import React, { useState, useEffect, useCallback, useRef, useMemo, memo, useTransition, useDeferredValue, lazy, Suspense } from "react";
import { formatDateMDY } from './utils/dateFormat';
import { cn } from "@/lib/utils";
// Feb 2026 — `recharts` import removed from App.js: it was imported but
// NEVER actually used in this file (`BarChart3` here refers to the
// lucide-react icon, not a chart).  Removing the import drops the
// entire recharts library from the initial bundle (~300KB+ of JS).
// AssessmentProgressChart.jsx still imports recharts directly — but
// that component is loaded lazily via React.lazy on demand.
import ReactDOM from "react-dom";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import "./App.css";
// Feb 2026 — react-signature-canvas wrapped in LazySignaturePad
// so the library downloads only when a signature dialog opens.
// See /app/frontend/src/components/LazySignaturePad.jsx
import SignatureCanvas from './components/LazySignaturePad';
import { formatMrn, mrnMatches } from './utils/formatMrn';
import { matrixBaseRows, matrixCellValue } from './utils/matrixRows';
import { SignatureDisplay } from './components/shared/SignatureDisplay';
import { MultiSelectFilter } from './components/shared/MultiSelectFilter';
import MedicalRecordsRequestForm from './MedicalRecordsRequestForm';
// Quota-resilient localStorage writes — preference setItem calls fire
// on every login/render, but if localStorage is over the browser quota
// (typically from accumulated encrypted note drafts), a bare setItem
// throws QuotaExceededError and crashes render. safeLocalStorageSetItem
// catches the quota error, purges oldest note drafts, retries once.
import { safeLocalStorageSetItem } from './utils/safeStorage';
import { AddonCptPicker } from './components/shared/AddonCptPicker';
import { CptCombobox } from './components/shared/CptCombobox';
import { CopayReminderBanner } from './components/shared/CopayReminderBanner';
const MedicalRecordsManagerDashboard = lazy(() => import('./MedicalRecordsManagerDashboard'));
const BatchHistoryPage = lazy(() => import('./BatchHistoryPage'));
const BatchTab = lazy(() => import('./components/features/BatchTab'));
const ERATab = lazy(() => import('./components/features/ERATab'));
// Bulk Appointment Import — CSV/XLSX → calendar.  Lives inside the
// Admin Portal as its own sub-tab (Feb 2026 — user explicitly asked
// for it NOT on the Schedule tab).  Lazy-loaded so the parser +
// preview table don't ship until the admin opens the tab.
const BulkAppointmentImportPanel = lazy(() => import('./components/features/BulkAppointmentImportPanel'));
// Admin Portal — Auto-Submit Schedule for closed claim batches
// (NEHEN/Availity).  Lazy-loaded so it doesn't ship in the initial
// JS bundle for non-admin users.
const AutoSubmitScheduleSettings = lazy(() => import('./components/features/AutoSubmitScheduleSettings'));

// MOVED TO: /src/constants/index.js - APP_VERSION
// DELETE THIS COMMENT AFTER VERIFYING BUILD
// Feb 2026 — V2 route-split shell.  See /layouts/DashboardLayoutV2.jsx
const DashboardLayoutV2 = lazy(() => import('./layouts/DashboardLayoutV2'));
const AuthorizationsRoute = lazy(() => import('./routes/AuthorizationsRoute'));
const BatchRoute = lazy(() => import('./routes/BatchRoute'));
const BillingRoute = lazy(() => import('./routes/BillingRoute'));
const CalendarRoute = lazy(() => import('./routes/CalendarRoute'));
const ClaimsWorkshopRoute = lazy(() => import('./routes/ClaimsWorkshopRoute'));
const ERAManagementRoute = lazy(() => import('./routes/ERAManagementRoute'));
const ClientBillsRoute = lazy(() => import('./routes/ClientBillsRoute'));
const LocationsAdminRoute = lazy(() => import('./routes/LocationsAdminRoute'));
const CardOnFileAdminRoute = lazy(() => import('./routes/CardOnFileAdminRoute'));
const StatementSettingsRoute = lazy(() => import('./routes/StatementSettingsRoute'));
const PlatformBillingRoute = lazy(() => import('./routes/PlatformBillingRoute'));
const BillingDirectoryAdminRoute = lazy(() => import('./routes/BillingDirectoryAdminRoute'));
const ServiceTypesAdminRoute = lazy(() => import('./routes/ServiceTypesAdminRoute'));
const POSCodesAdminRoute = lazy(() => import('./routes/POSCodesAdminRoute'));
const ClientLabelsAdminRoute = lazy(() => import('./routes/ClientLabelsAdminRoute'));
const ChartReviewConfigRoute = lazy(() => import('./routes/ChartReviewConfigRoute'));
const CPTCodesAdminRoute = lazy(() => import('./routes/CPTCodesAdminRoute'));
const ManageInsuranceAdminRoute = lazy(() => import('./routes/ManageInsuranceAdminRoute'));
const FormsAdminRoute = lazy(() => import('./routes/FormsAdminRoute'));
// Shared rich Send-Statement dialog — replaces the legacy inline
// implementation below (preview iframe, CC field, history accordion,
// print-to-PDF).  Used by both the patient-chart Billing tab and the
// V2 Client Bills tab so workflow stays identical.
import StatementDialog from './components/billing/StatementDialog';
const EligibilityRoute = lazy(() => import('./routes/EligibilityRoute'));
const GroupsRoute = lazy(() => import('./routes/GroupsRoute'));
const MedicalRecordsRoute = lazy(() => import('./routes/MedicalRecordsRoute'));
const MedicationManagementRoute = lazy(() => import('./routes/MedicationManagementRoute'));
const PayrollRoute = lazy(() => import('./routes/PayrollRoute'));
// Multitenancy chunk 2 (Jun 2026) — Platform Admin control-plane portal.
const PlatformPortal = lazy(() => import('./components/pages/PlatformPortal'));
const TenantPublicPortal = lazy(() => import('./components/pages/TenantPublicPortal'));
import ImpersonationBanner from './components/v2/ImpersonationBanner';
const ReportsRoute = lazy(() => import('./routes/ReportsRoute'));
const TasksRoute = lazy(() => import('./routes/TasksRoute'));
const DocumentDeadlineRulesRoute = lazy(() => import('./routes/DocumentDeadlineRulesRoute'));
const AppointmentRemindersRoute = lazy(() => import('./routes/AppointmentRemindersRoute'));
const ConflictRulesAdminRoute = lazy(() => import('./routes/ConflictRulesAdminRoute'));
const DocumentReminderSettingsRoute = lazy(() => import('./routes/DocumentReminderSettingsRoute'));
const RoutingRulesAdminRoute = lazy(() => import('./routes/RoutingRulesAdminRoute'));
const WaitlistRoute = lazy(() => import('./routes/WaitlistRoute'));
const StaffDirectoryRoute = lazy(() => import('./routes/StaffDirectoryRoute'));
const ActiveClientsRoute = lazy(() => import('./routes/ActiveClientsRoute'));
const AdminClaimInvestigateRoute = lazy(() => import('./routes/AdminClaimInvestigateRoute'));
// EDISettingsAdminRoute retired Sep 2026 — EDI settings moved to the platform portal.
const AdminRoute = lazy(() => import('./routes/AdminRoute'));
const ProfileRoute = lazy(() => import('./routes/ProfileRoute'));
const SurveyDashboardRoute = lazy(() => import('./routes/SurveyDashboardRoute'));
const UsersAdminRoute = lazy(() => import('./routes/UsersAdminRoute'));
const RolesAdminRoute = lazy(() => import('./routes/RolesAdminRoute'));
const NoteSignatureSettingsRoute = lazy(() => import('./routes/NoteSignatureSettingsRoute'));
const FirmCalendarAdminRoute = lazy(() => import('./routes/FirmCalendarAdminRoute'));
const AutoSubmitScheduleRoute = lazy(() => import('./routes/AutoSubmitScheduleRoute'));
const PaymentRemindersRoute = lazy(() => import('./routes/PaymentRemindersRoute'));
const ClientPortalSettingsRoute = lazy(() => import('./routes/ClientPortalSettingsRoute'));
const EligibilitySettingsRoute = lazy(() => import('./routes/EligibilitySettingsRoute'));
const WaitlistCommunicationRoute = lazy(() => import('./routes/WaitlistCommunicationRoute'));
const ClientBillingSettingsRoute = lazy(() => import('./routes/ClientBillingSettingsRoute'));
const IntakeBundlesRoute = lazy(() => import('./routes/IntakeBundlesRoute'));
const ManageLicensesRoute = lazy(() => import('./routes/ManageLicensesRoute'));
const SystemSettingsRoute = lazy(() => import('./routes/SystemSettingsRoute'));
const OrganizationSettingsRoute = lazy(() => import('./routes/OrganizationSettingsRoute'));
const InternalApptTypesAdminRoute = lazy(() => import('./routes/InternalApptTypesAdminRoute'));
const NoteAutomationRoute = lazy(() => import('./routes/NoteAutomationRoute'));
const ClientDemographicsRoute = lazy(() => import('./routes/ClientDemographicsRoute'));
const BrandingRouteV2 = lazy(() => import('./routes/BrandingRoute'));
const AccessSecurityRoute = lazy(() => import('./routes/AccessSecurityRoute'));
const SecurityAuditRoute = lazy(() => import('./routes/SecurityAuditRoute'));
const MessagingToolsRoute = lazy(() => import('./routes/MessagingToolsRoute'));
const IntakeFormConfigRoute = lazy(() => import('./routes/IntakeFormConfigRoute'));

// Phase 3e.1 (Feb 2026) — IntakeWizardProvider lifted from
// DashboardLayoutV2 to App.js root.  Eager-imported (not lazy) because
// the provider mounts as part of the root render and would race React
// Suspense if lazy-loaded.
import { IntakeWizardProvider, useIntakeWizard } from './contexts/IntakeWizardContext';
import { ChartActionsProvider } from './context/ChartActionsContext';
import { MedicationReferralDialog } from './components/features/MedicationReferralDialog';
import UserNotificationSettingsDialog from './components/features/UserNotificationSettingsDialog';

const ClaimsWorkshop = lazy(() => import('./components/features/ClaimsWorkshop'));
const CareVaultAtlas = lazy(() => import('./CareVaultAtlas'));
// Feb 2026 — lazy-loaded so recharts (~300KB) only downloads when the
// client-chart Assessment view actually mounts the chart.
const AssessmentProgressChart = lazy(() => import('./components/AssessmentProgressChart').then(m => ({ default: m.AssessmentProgressChart })));
// Feb 2026 — Assessment tab components moved out of App.js to
// `components/features/AssessmentsTabContent.jsx` so the V2 client
// chart can mount the exact same code without duplicating ~740 lines.
import {
  AssessmentsTabContent,
  NewAssessmentButton,
  RecentAssessmentScores,
} from './components/features/AssessmentsTabContent';
import { ReferenceNotePanel } from './ReferenceNotePanel';
import { AssessmentScoreCard, ScoreBadge, ScoreTrend, SeverityLegend } from './components/AssessmentScoreDisplay';
// Lazy-loaded heavy feature components — only fetched when user opens the relevant tab/dialog
const GroupNoteCreationDialog = lazy(() => import('./components/features/GroupNoteCreationDialog').then(m => ({ default: m.GroupNoteCreationDialog })));
const IntakeWizardDialog = lazy(() => import('./components/features/IntakeWizardDialog'));
const ChangeRequestDialog = lazy(() => import('./components/features/ChangeRequestDialog'));
const AssignProvidersDialog = lazy(() => import('./components/features/AssignProvidersDialog'));
const ClientChartDocumentsTab = lazy(() => import('./components/features/ClientChartDocumentsTab').then(m => ({ default: m.ClientChartDocumentsTab })));
const AppointmentColorLegend = lazy(() => import('./components/features/AppointmentColorLegend').then(m => ({ default: m.AppointmentColorLegend })));
const MedsVitalsTab = lazy(() => import('./components/features/MedsVitalsTab').then(m => ({ default: m.MedsVitalsTab })));
const WaitlistTab = lazy(() => import('./components/features/WaitlistTab').then(m => ({ default: m.WaitlistTab })));
const ClientInsuranceDialog = lazy(() => import('./components/features/ClientInsuranceDialog').then(m => ({ default: m.ClientInsuranceDialog })));
const InsuranceCardViewer = lazy(() => import('./components/features/InsuranceCardViewer').then(m => ({ default: m.InsuranceCardViewer })));
const InsuranceMigrationDialog = lazy(() => import('./components/features/InsuranceMigrationDialog'));
const GroupMembersDialog = lazy(() => import('./components/features/groups/GroupMembersDialog').then(m => ({ default: m.GroupMembersDialog })));
const EligibilityTab = lazy(() => import('./components/features/EligibilityTab').then(m => ({ default: m.EligibilityTab })));
const DiagnosesTab = lazy(() => import('./components/features/DiagnosesTab').then(m => ({ default: m.DiagnosesTab })));
const AddEditGroupDialog = lazy(() => import('./components/features/AddEditGroupDialog').then(m => ({ default: m.AddEditGroupDialog })));
const AuthorizationsTab = lazy(() => import('./components/features/AuthorizationsTab').then(m => ({ default: m.AuthorizationsTab })));
const BillingTab = lazy(() => import('./components/features/BillingTab').then(m => ({ default: m.BillingTab })));
const GroupsTab = lazy(() => import('./components/features/groups/GroupsTab').then(m => ({ default: m.GroupsTab })));
const DeletedDocumentsManager = lazy(() => import('./components/features/DeletedDocumentsManager'));
const UserArchiveModal = lazy(() => import('./components/features/UserArchiveModal'));
const CurriculumLibraryDialog = lazy(() => import('./components/features/CurriculumLibraryDialog').then(m => ({ default: m.CurriculumLibraryDialog })));
const GroupAttendanceSnapshot = lazy(() => import('./components/features/GroupAttendanceSnapshot').then(m => ({ default: m.GroupAttendanceSnapshot })));
const MedicationManagementTab = lazy(() => import('./components/features/MedicationManagementTab').then(m => ({ default: m.MedicationManagementTab })));
const SupervisionTab = lazy(() => import('./components/features/SupervisionTab').then(m => ({ default: m.SupervisionTab })));
const LegacyTreatmentPlanImport = lazy(() => import('./components/features/LegacyTreatmentPlanImport'));
import { GroupApprovalDialog } from './components/features/groups/GroupApprovalDialog';
import { GroupRequestDialog } from './components/features/groups/GroupRequestDialog';
import { GroupFormerMembersDialog } from './components/features/groups/GroupFormerMembersDialog';
import { AttendanceTrackingDialog } from './components/features/groups/AttendanceTrackingDialog';
import { ChangeHistoryModal } from './components/features/ChangeHistoryModal';
import { AppointmentLinkSelect } from './components/features/AppointmentLinkSelect';
import { NoteDetailDialog } from './components/features/NoteDetailDialog';
import V2NoteHost from './components/features/V2NoteHost';
import FormBuilderDialog from './components/features/FormBuilderDialog';
import { AssessmentQuickSend } from './components/features/AssessmentQuickSend';
import { FormFileUploadField } from './components/shared/FormFileUploadField';
import { FormsManagementDialog, FormTypeDialog } from './components/features/FormsAdminDialogs';
import { SignatureConfirmDialog, CorrectionsDialog, NotBillableDialog, CloseNoteWarningModal } from './components/features/note-dialogs/NoteCompanionDialogs';
import { LinkGoalsDialog } from './components/features/note-dialogs/LinkGoalsDialog';
import { NextStepsDialog } from './components/features/note-dialogs/NextStepsDialog';
import { BreakTime } from './components/features/BreakTime';
// ClaimViewer is a heavy 958-line component that renders the HCFA-1500
// preview / 837P / PDF.  Used only in conditional dialog flows below
// (note-review preview at line ~25160) — so we lazy-load it to keep it
// out of the initial bundle.  Wrapped in Suspense at the call site.
const ClaimViewer = lazy(() => import('./components/features/ClaimViewer').then(m => ({ default: m.ClaimViewer })));
import { PayerModifierRulesEditor } from './components/features/PayerModifierRulesEditor';
import FirmEventChips from './components/features/FirmEventChips';
import useFirmCalendarEvents from './hooks/useFirmCalendarEvents';
const ClaimsTab = lazy(() => import('./components/features/ClaimsTab'));
const AuthorizationsMgmtTab = lazy(() => import('./components/features/AuthorizationsMgmtTab'));
import { AddDiagnosisDialog } from './components/features/AddDiagnosisDialog';
import { TreatmentPlanClinicalContext } from './components/features/TreatmentPlanClinicalContext';
import { TreatmentPlanClinicalContextReadOnly } from './components/features/TreatmentPlanClinicalContextReadOnly';
import { DebouncedTextarea, DebouncedInput } from './components/ui/debounced-textarea';
import { SendToClientDialog } from './components/features/SendToClientDialog';
import { NeedsCorrectionsDialog } from './components/features/NeedsCorrectionsDialog';
import { TimeInput } from './components/ui/time-input';
import { AddEditMedicationDialog } from './components/features/AddEditMedicationDialog';
import { BillingProvider } from './context/BillingContext';
import { TasksSectionWrapper } from './components/features/TasksSectionWrapper';
import { AdminPortalWrapper } from './components/features/AdminPortalWrapper';
import { BillingTabWrapper } from './components/features/BillingTabWrapper';
import { VirtualizedNotesList } from './components/features/VirtualizedNotesList';
import { ExternalReviewersPanel } from './components/features/ExternalReviewersPanel';
import { AppointmentScheduleDialog } from './components/features/AppointmentScheduleDialog';
import { SeriesScopeDialog } from './components/features/SeriesScopeDialog';
// Lazy-load tab / section components. These render inside the main Suspense
// boundary wrapping <Tabs> (see ~L21089), so no extra Suspense wrapper needed
// at the usage sites; the existing fallback handles them during chunk load.
// CptCodesTab tab removed Feb 2026 — will be replaced by a Reports tab.
// The CptCodesTab component file is still available at
// ./components/features/CptCodesTab and can be re-mounted from a
// future "CPT Management" admin sub-screen if needed.
// const CptCodesTab = lazy(() => import('./components/features/CptCodesTab').then(m => ({ default: m.CptCodesTab })));
const AlertsTab = lazy(() => import('./components/features/AlertsTab').then(m => ({ default: m.AlertsTab })));
const ClientChartBillingTab = lazy(() => import('./components/features/ClientChartBillingTab').then(m => ({ default: m.ClientChartBillingTab })));
import { PaymentTimelineCard } from './components/features/PaymentTimelineCard';
import { LegacyMarkBilledDialog } from './components/features/LegacyMarkBilledDialog';
import { ContactFormDialog } from './components/features/ContactFormDialog';
import { OrganizationFormDialog } from './components/features/OrganizationFormDialog';
import {
  EditLocationDialog,
  ManageLocationsDialog,
} from './components/features/LocationDialogs';
// Lazy-load large dialog components. We must mount these CONDITIONALLY on
// their `open` state (see usage sites) so the chunk only downloads when the
// user actually opens the dialog.
const PostPaymentDialog = lazy(() => import('./components/features/PostPaymentDialog').then(m => ({ default: m.PostPaymentDialog })));
const ClientPostPaymentDialog = lazy(() => import('./components/features/ClientPostPaymentDialog').then(m => ({ default: m.ClientPostPaymentDialog })));
const StaffDirectory = lazy(() => import('./components/features/StaffDirectory').then(m => ({ default: m.StaffDirectory })));
const SurveyDashboard = lazy(() => import('./components/features/SurveyDashboard').then(m => ({ default: m.SurveyDashboard })));
import PageLoader from './components/ui/PageLoader';

// ── Lazy-loaded route components ──────────────────────────────────────
const HelpModule = lazy(() => import('./modules/help'));
const PayrollConfigPanel = lazy(() => import('./components/features/PayrollConfigPanel'));
const PayrollTab = lazy(() => import('./components/features/PayrollTab'));
import SystemStatusOverlay from './components/features/SystemStatusOverlay';
import { renderSimpleField, renderTextInputField, renderSelectionField } from './components/shared/FormFieldRenderer';
import { CreateTaskFieldWidget } from './components/shared/CreateTaskFieldWidget';
import { AppointmentHoverCard } from './components/shared/AppointmentHoverCard';
import { AppointmentLinkedItemsPanel } from './components/shared/AppointmentLinkedItemsPanel';
// Lazy-load large page-level routes — only reached via direct URL visits
// (client portals, self-service pages), not on staff dashboard load.
const WaitlistSelfService = lazy(() => import('./components/pages/WaitlistSelfService'));
const AcceptClientsPage = lazy(() => import('./components/pages/AcceptClientsPage'));
const PublicPortal = lazy(() => import('./components/pages/PublicPortal'));
const ClientPortal = lazy(() => import('./components/pages/ClientPortal'));
import BreakPage from './components/pages/BreakPage';
import PrintNoteView from './components/print/PrintNoteView';
import FormFieldRendererSandbox from './components/forms/FormFieldRendererSandbox';
import FormFieldRenderer from './components/forms/FormFieldRenderer';
import { PrepareClaimDialog } from './components/features/PrepareClaimDialog';
import { LinkExistingClaimDialog } from './components/features/LinkExistingClaimDialog';
import { MedicationReferralDetailsDialog } from './components/features/MedicationReferralDetailsDialog';
import { TaskDetailsDialog, TaskCompletionNoteDialog } from './components/features/TaskDetailsDialog';
import { ManageInsuranceDialog, InsuranceFormDialogs } from './components/features/InsuranceAdminDialogs';
import { buildNoteReviewData } from './utils/noteReview';
import BetaGate from './components/auth/BetaGate';
import Login from './components/auth/Login';
import InactivityWarningModal from './components/common/InactivityWarningModal';
import { ProfileDropdown, ProfileModal, ProfileAvatar } from './components/Profile';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import { refreshAuthToken } from './hooks/useTokenRefresh';
import { safeArray, safeFilter, safeFind, safeMap, safeLength, safeIncludes } from './utils/safeAccessors';
import { useWebSocket } from './hooks/useWebSocket';
import { setupRequestDeduplication } from './utils/requestDeduplication';
import { staticTextBorderStyle, staticTextImageClass } from './utils/staticTextStyle';
import { readStaticData, bustStaticDataKey } from './utils/staticDataCache';
import { fmtLocalDate } from './utils/insuranceDates';
import { sortClientsByName, formatClientNameOrdered } from './utils/nameFormatter';
import { formatRoleName, formatClientStatus } from './utils/displayFormat';

// Helper used by fetchStaticData() to persist a fresh static-data
// response into the SWR cache.  Intentionally bust-then-write so
// the timestamp resets — readers will then treat the entry as
// fresh (within the 5-minute soft TTL).
function bustAndWriteStaticData(key, value) {
  bustStaticDataKey(key);
  try {
    sessionStorage.setItem(`sdc:v1:${key}`, JSON.stringify({ t: Date.now(), d: value }));
  } catch { /* quota or storage disabled */ }
}
import { generateIntakePdf } from './utils/intakePdfGenerator';
import { createIntakePermissions } from './utils/intakePermissions';
// Note autosave with journaling and draft recovery
import { useNoteAutosaveUI } from './components/NoteAutosaveWrapper';
import { SaveIndicator } from './components/SaveIndicator';
import { DraftRecoveryBanner } from './components/DraftRecoveryModal';
import { AUPModal } from './components/AUPModal';
import { NoteHistoryPanel } from './components/notes/NoteHistoryPanel';
const LegacyImportAdmin = lazy(() => import('./pages/LegacyImportAdmin'));
const FirmCalendarAdmin = lazy(() => import('./components/features/admin/FirmCalendarAdmin'));
import { PollCacheMetricsCard } from './components/features/admin/PollCacheMetricsCard';
import { NehenDiagnosticsCard } from './components/features/NehenDiagnosticsCard';
const PayerAuditLog = lazy(() => import('./components/admin/PayerAuditLog'));
const EligibilityStatusTracker = lazy(() => import('./components/eligibility/EligibilityStatusTracker'));
import { ConsentAffirmationDisplay } from './components/consent/ConsentAffirmationDisplay';
import { ConsentLivePreview } from './components/consent/ConsentLivePreview';

// Messaging components
import { useMessaging, MessagesDropdown } from './components/features/messaging';
import NotificationBell from './components/features/NotificationBell';
import NotificationManagerDialog from './components/features/NotificationManagerDialog';

// Feature Contexts - separate state to prevent unnecessary re-renders
import { StaticDataProvider, useStaticData } from './context/StaticDataContext';
import { UIProvider, useUI } from './context/UIContext';
import { NotificationsProvider, useNotificationsActions } from './context/NotificationsContext';
import { ClientsProvider, useClients } from './context/ClientsContext';
import { AppointmentsProvider, useAppointments } from './context/AppointmentsContext';
import { DashboardCalendarBridgeProvider } from './context/DashboardCalendarBridgeContext';
import AppointmentsTab from './components/features/AppointmentsTab';
import { useCalendarDrag as useCalendarDragHook } from './hooks/useCalendarDrag';
import { useCurrentTime } from './hooks/useCurrentTime';
import { useCalendarPermissions } from './hooks/useCalendarPermissions';
import {
  getStatusBadgeColor,
  getBillingStatusBorderColor,
  getServiceTypeCategoryColor,
  isTelehealthAppointment,
  getCalendarDays as getCalendarDaysUtil,
} from './utils/calendar';
import { NotesProvider, useNotes } from './context/NotesContext';
import { ImpersonationProvider, useImpersonation } from './context/ImpersonationContext';

// Auth Context - imported from separate module for better organization
import { AuthProvider, useAuth } from './context/AuthContext';
import { openAuthenticatedTab } from './utils/authHelpers';

// Date utilities - extracted from App.js for reusability
import {
  formatDateForFilename,
  formatDateStringForDisplay,
  getTodayLocalDate,
  parseUTCTimestamp,
  formatMessageTimestamp,
  formatRelativeTime
} from './utils/dateHelpers';

// API utilities - extracted from App.js for reusability
import {
  getApiErrorMessage,
  generateDownloadFilename,
  filenameFromResponse
} from './utils/apiHelpers';

// Application constants - extracted from App.js for reusability
import {
  APP_VERSION,
  CLIENT_STATUSES,
  SYMPTOM_OPTIONS,
  URGENT_SYMPTOMS,
  LANGUAGE_OPTIONS,
  NON_INPUT_FIELD_TYPES,
  PROVIDER_DEFAULT_TABS,
  OFFICE_DEFAULT_TABS,
  DEFAULT_TABS,
  OFFICE_DEFAULT_PERMISSIONS,
  PROVIDER_DEFAULT_PERMISSIONS,
  SUPERVISOR_DEFAULT_PERMISSIONS,
  RECEPTION_DEFAULT_PERMISSIONS,
  INTERN_DEFAULT_PERMISSIONS
} from './constants';

// General utility helpers - extracted from App.js for reusability
import {
  isSuperAdmin,
  isGroupFacilitator,
  getOptionLabel,
  getEffectivePosCodes,
  formatStateForConsent
} from './utils/helpers';
import { resolveNoteTemplate, mergeNoteContentWithTemplate } from './utils/noteTemplateMerge';

// Note-related helpers - extracted from App.js
import { getNoteDisplayDate } from './utils/noteHelpers';

// Client/Insurance helpers - extracted from App.js
import {
  getClientPrimaryInsurance,
  getClientAllInsurances
} from './utils/clientHelpers';

// Document helpers - extracted from App.js
import { generateDocumentDownloadFilename } from './utils/documentHelpers';

// User/Provider display helpers - extracted from App.js (formatProviderName).
// Single source of truth for "Name, Credentials" rendering used by every
// provider dropdown/picker/badge across the app.
import { formatProviderName } from './utils/userHelpers';
// Client filter query-string builder — used by both client-fetch paths
// (refreshClients and refreshDynamicData).  Centralizes the
// Include/Exclude "complement strategy" so the two paths can't drift.
import { buildClientQueryParams } from './utils/clientFilterParams';

// User-busy service — module-level signal that lets background pollers
// skip their tick while the user is actively loading a chart or
// interacting with a heavy modal.  See Workstream B notes in
// /app/frontend/src/utils/userBusyService.js.
import { isUserBusy } from './utils/userBusyService';
import { useMarkBusyWhile } from './hooks/useUserBusy';

// Chart-bundle session cache — Feb 2026, persists the SWR cache
// to sessionStorage so hard browser refreshes still render the
// chart instantly.  Quota-fail-safe (purges old keys + retries on
// QuotaExceededError).
import {
  getCachedBundle,
  setCachedBundle,
  clearChartBundleCache,
} from './utils/chartBundleCache';

// Legacy/Admin Tools
import { InterventionLibrarySelector } from './components/features/InterventionLibrarySelector';
import { DecryptionTool } from './components/admin/DecryptionTool';
import { UUIDResolver } from './components/admin/UUIDResolver';
import { UUIDLookup } from './components/admin/UUIDLookup';
import { SupervisorSignatureBackfillCard } from './components/admin/SupervisorSignatureBackfillCard';
import { AssessmentPdfRegenCard } from './components/admin/AssessmentPdfRegenCard';
import { BulkClientOperationsCard } from './components/admin/BulkClientOperationsCard';
import LegacyNoteImport from './components/admin/LegacyNoteImport';

// Re-export useAuth for backwards compatibility (some components may import from App)
export { useAuth };

// Make openAuthenticatedTab available globally for components that use it
window.openAuthenticatedTab = openAuthenticatedTab;

// Setup request deduplication to prevent duplicate API calls
// Using response caching mode (safer - doesn't abort requests)
setupRequestDeduplication(axios);

// Set default timeout - 30 seconds (prevents hanging requests in production)
axios.defaults.timeout = 30000;

// ETag client-side cache — reduces bandwidth and parse cost for hot pollers
// (notifications, announcements, messages) and static config endpoints.
// See /app/frontend/src/utils/etagCache.js for HIPAA-safety notes.
import {
  etagRequestInterceptor,
  etagResponseInterceptor,
  etagResponseInterceptor304,
  etagErrorInterceptor,
  clearEtagCache as _clearEtagCache,
} from './utils/etagCache';

axios.interceptors.request.use(etagRequestInterceptor);

// Request timing interceptor - logs slow requests to help diagnose production issues
axios.interceptors.request.use((config) => {
  config.metadata = { startTime: Date.now() };
  return config;
});

axios.interceptors.response.use(
  (response) => {
    const duration = Date.now() - (response.config?.metadata?.startTime || Date.now());
    const url = response.config?.url?.replace(/\?.*/, '') || 'unknown'; // Remove query params for cleaner logs
    if (duration > 2000) {
      console.warn(`[SLOW] ${url} took ${duration}ms`);
    }
    // ETag cache: capture 200 + substitute body on 304-as-success
    return etagResponseInterceptor304(etagResponseInterceptor(response));
  },
  async (error) => {
    // 304 may arrive as an error depending on axios validateStatus config.
    // Let the etagErrorInterceptor transparently recover from the cache.
    try {
      if (error?.response?.status === 304) {
        return await etagErrorInterceptor(error);
      }
    } catch {
      // fall through to normal error path
    }
    // Handle cases where error.config might not exist (e.g., network errors, aborted requests)
    const startTime = error?.config?.metadata?.startTime;
    const duration = startTime ? Date.now() - startTime : 0;
    const url = error?.config?.url?.replace(/\?.*/, '') || 'unknown';
    const status = error?.response?.status || (error?.code === 'ERR_CANCELED' ? 'cancelled' : 'network error');
    
    // Don't log cancelled/duplicate requests as errors
    if (error?.code !== 'ERR_CANCELED') {
      console.error(`[API ERROR] ${url} failed after ${duration}ms - Status: ${status}`);
    }
    
    // Handle 401 — attempt a silent token refresh + retry BEFORE ever
    // redirecting to login (Jun 2026 session-stability fix).  The previous
    // behavior hard-redirected on ANY 401, which booted actively-working
    // users on transient failures before the refresh interceptor could run.
    if (error?.response?.status === 401) {
      const requestUrl = error?.config?.url || '';
      const isLoginPage = window.location.pathname === '/login';
      const isAuthMeCall = requestUrl.includes('/auth/me');
      // Never try to refresh-or-redirect for the auth endpoints themselves.
      const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/refresh');
      // Multitenancy chunk 2 — the Platform Portal (/platform) has its own
      // login + token scope. A 401 there must never dump the admin onto the
      // tenant login page; platform-token expiry just clears platform_token
      // and lands back on /platform's own login form.
      const isPlatformApiCall = requestUrl.includes('/api/platform/');
      const isPlatformPage = window.location.pathname.startsWith('/platform');
      // Client-facing portal contexts use their own magic-link/session
      // tokens — a 401 there must NEVER redirect the visitor to the STAFF
      // login page; the portal components render their own expiry UI.
      const PORTAL_PATH_PREFIXES = ['/client-portal', '/portal', '/p/', '/communication-preferences', '/waitlist/', '/accept-clients/'];
      const isPortalContext =
        PORTAL_PATH_PREFIXES.some((p) => window.location.pathname.startsWith(p)) ||
        requestUrl.includes('/client-portal/') ||
        requestUrl.includes('/public/');

      if (isPlatformApiCall || isPlatformPage) {
        const isPlatformLoginCall = requestUrl.includes('/api/platform/auth/login');
        if (!isPlatformLoginCall) {
          sessionStorage.removeItem('platform_token');
          if (!isPlatformPage) window.location.href = '/platform';
          else window.location.reload();
        }
        // wrong-password on the platform login form: let the component
        // render its inline error — no redirect.
      } else if (!isLoginPage && !isAuthMeCall && !isAuthEndpoint && !isPortalContext) {
        const originalRequest = error.config;
        if (originalRequest && !originalRequest._retry && !originalRequest._skipInterceptor) {
          originalRequest._retry = true;
          try {
            const newToken = await refreshAuthToken();
            if (newToken) {
              // Session is fine — retry the original request transparently.
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
            // Definitive: refresh endpoint rejected the token (or no token
            // at all) — the session is genuinely invalid.  Only NOW do we
            // clear state and redirect.
            void 0 && console.log('[AUTH] Session definitively invalid - redirecting to login');
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('impersonatedUserId');
            sessionStorage.removeItem('lastActivityTimestamp');
            delete axios.defaults.headers.common['Authorization'];
            // Clear ETag cache on forced sign-out so a future user on the same
            // tab never sees cached PHI from the previous session.
            try { _clearEtagCache(); } catch { /* ignore */ }
            window.location.href = '/login?reason=session_expired';
          } catch (refreshErr) {
            // Transient refresh failure (network/5xx) — the session may be
            // perfectly valid.  Surface the original error to the caller;
            // do NOT log the user out.
          }
        }
        // If _retry was already set: the request already got a fresh token
        // and STILL returned 401 — that's an endpoint-specific 401, not a
        // session problem.  Let the caller handle it; no redirect.
      }
    }

    // Feb 2026 — External Reviewer write-block (`ExternalReviewerReadOnlyMiddleware`).
    // The backend returns 403 with `error_code: 'EXTERNAL_REVIEWER_READ_ONLY'` for
    // any disallowed write. Surface a friendly, consistent toast so the
    // reviewer doesn't see a generic "failed to save" error from each
    // calling component.
    if (
      error?.response?.status === 403 &&
      error?.response?.data?.error_code === 'EXTERNAL_REVIEWER_READ_ONLY'
    ) {
      try {
        const { toast } = require('sonner');
        toast.error('Read-only review session', {
          description:
            error.response.data?.detail ||
            'External reviewer accounts are read-only.',
          id: 'external-reviewer-readonly', // dedupe across burst write attempts
        });
      } catch {
        /* ignore — sonner may not be loaded yet */
      }
    }
    return Promise.reject(error);
  }
);

// Import UI components
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter, DialogClose, getOpenDialogCount } from "./components/ui/dialog.jsx";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./components/ui/alert-dialog";
import { Form } from "./components/ui/form";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./components/ui/accordion";
import { Switch } from "./components/ui/switch";
import { Calendar } from "./components/ui/calendar";
import { Textarea } from "./components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "./components/ui/alert";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import DateCalculatorPopover from "./components/features/DateCalculatorPopover";
import { ClientListSkeleton } from "./components/ui/ClientListSkeleton";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./components/ui/hover-card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./components/ui/command";
import { Checkbox } from "./components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./components/ui/dropdown-menu";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./components/ui/collapsible";
// Drag and Drop
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Custom components
import NoteTextBackups from './components/admin/NoteTextBackups';
import BrokenNotesDiagnostic from './components/admin/BrokenNotesDiagnostic';
import CptDescriptionDiagnostic from './components/admin/CptDescriptionDiagnostic';
import IntakeBundleDocNumberBackfillCard from './components/features/IntakeBundleDocNumberBackfillCard';
// PHI-fix May 2026 — admin viewer for snapshot mismatch events.
import SnapshotMismatchAuditPanel from './components/admin/SnapshotMismatchAuditPanel';
// Shared signature renderer — handles data: / https / gcs:// raw
// schemes consistently.  Replaces the inline ``<img>`` patterns
// that broke when a note's stored ``provider_signature`` carried
// the ``gcs://`` scheme directly.  May 2026 production fix.
import { SignatureImage } from './components/shared/SignatureImage';
import { extractNoteTextContent } from './utils/noteTextExtractor';
import { useNoteLifecycleController, getV2NoteReturnPath } from './hooks/useNoteLifecycleController';
import { useClientEligibility } from './hooks/useClientEligibility';
import { useClaimsQueue } from './hooks/useClaimsQueue';
import { useIPrescribe } from './hooks/useIPrescribe';
import {
  ELIGIBILITY_DISPLAY,
  getEligibilityDisplay as getEligibilityDisplayShared,
  getDOSEligibilityDisplay as sharedGetDOSEligibilityDisplay,
  isClientSelfPay,
  EligibilityBadge,
  eligibilityTooltip as eligibilityTooltipShared,
} from './utils/eligibilityDisplay';

// Icons
import { 
  User, 
  Calendar as CalendarIcon, 
  FileText, 
  Folder,
  Settings, 
  LogOut,
  Plus,
  Edit,
  Edit2,
  Eye,
  EyeOff,
  Shield,
  Users,
  Clock,
  Phone,
  Mail,
  Video,
  Trash2,
  Archive,
  RefreshCw,
  Undo2,
  Download,
  Building,
  Tag,
  Upload,
  Filter,
  Save,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  X,
  Pencil,
  Bell,
  BellRing,
  BellOff,
  CreditCard,
  GripVertical,
  History,
  AlertCircle,
  AlertTriangle,
  ZoomIn,
  ZoomOut,
  Search,
  UserPlus,
  Check,
  ChevronsUpDown,
  XCircle,
  MessageSquare,
  Pill,
  Copy,
  Columns2,
  CheckCircle,
  CheckCircle2,
  ThumbsUp,
  Star,
  Lock,
  Unlock,
  MapPin,
  WifiOff,
  Key,
  Globe,
  ExternalLink,
  Link,
  UserCheck,
  UserCircle,
  RotateCcw,
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  Send,
  Printer,
  MoreVertical,
  DollarSign,
  Info,
  Sparkles,
  Loader,
  Loader2,
  Activity,
  Database,
  ClipboardList,
  Maximize2,
  CheckSquare,
  GitMerge,
  ArrowLeftRight,
  ArrowUpDown,
  Hash,
  Package,
  Briefcase,
  Target,
  BookOpen,
  BarChart3,
  Monitor,
  GraduationCap,
  Home,
  List,
  ListOrdered,
  Indent,
  Outdent,
  LayoutGrid,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Code2,
  ClipboardPaste,
  Heart,
  FileJson,
  Wrench,
  FileArchive,
  FileCheck,
  HelpCircle,
  Stethoscope,
  CalendarPlus,
  Receipt,
  Link2,
  Lightbulb,
  Ban,
  Home as HomeIcon,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios blob-response errors hide the backend detail inside a Blob —
// parse it so PDF download failures show the real reason (Jun 2026).
const pdfBlobErrDetail = async (error, fallback) => {
  try {
    const d = error?.response?.data;
    if (d instanceof Blob) {
      const parsed = JSON.parse(await d.text());
      return parsed?.detail || fallback;
    }
    return d?.detail || fallback;
  } catch {
    return fallback;
  }
};

// ============================================================================
// UTILITY FUNCTIONS - Now imported from /src/utils/helpers.js:
// - isSuperAdmin(user)
// - isGroupFacilitator(user, groups)
// - getOptionLabel(opt)
// - getEffectivePosCodes(organizations, posCodes)
// - formatStateForConsent(stateName)
// ============================================================================
// ADDITIONAL UTILITY FUNCTIONS - Now imported from various utils/ files:
// - getNoteDisplayDate(note, appointments) -> /src/utils/noteHelpers.js
// - getClientPrimaryInsurance(client, insurancesList) -> /src/utils/clientHelpers.js
// - getClientAllInsurances(client, insurancesList) -> /src/utils/clientHelpers.js
// - generateDocumentDownloadFilename(doc, client) -> /src/utils/documentHelpers.js
// ============================================================================

// Disable console.log in production for performance
const originalConsoleLog = console.log;
console.log = (...args) => {
  // Only log in development or for critical messages (auth, errors)
  if (process.env.NODE_ENV === 'development' || 
      args.some(arg => typeof arg === 'string' && (arg.includes('[AUTH]') || arg.includes('❌')))) {
    originalConsoleLog(...args);
  }
};

// Suppress ResizeObserver errors (harmless browser quirk that can block operations)
const resizeObserverErr = window.console.error;
window.console.error = (...args) => {
  if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
    return; // Ignore ResizeObserver errors
  }
  resizeObserverErr(...args);
};

// Utility function to migrate flat form fields to row groups for grid layout
// Used by Dashboard, PublicPortal, and other components that render forms
const migrateFormFieldsToRowGroups = (formFields) => {
  if (!formFields || formFields.length === 0) {
    return [{ id: 'row_empty_default', fields: [] }];
  }
  
  const rowGroups = [];
  let currentRowFields = [];
  let currentRowWidth = 0;
  
  const createRowId = (fields) => {
    // Create stable row ID based on field IDs in the row
    if (fields.length === 0) return 'row_empty';
    return `row_${fields.map(f => f.id).join('_')}`;
  };
  
  formFields.forEach((field, index) => {
    const fieldWidth = parseInt(field.width || '100');
    
    // Full-width fields (100%) always get their own row
    if (fieldWidth >= 100) {
      // Save current row if it has fields
      if (currentRowFields.length > 0) {
        rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
      }
      // Add full-width field as its own row
      rowGroups.push({ id: createRowId([field]), fields: [field] });
      // Start new row
      currentRowFields = [];
      currentRowWidth = 0;
    } else if (currentRowWidth + fieldWidth > 100) {
      // Would exceed 100%, start new row
      if (currentRowFields.length > 0) {
        rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
      }
      currentRowFields = [field];
      currentRowWidth = fieldWidth;
    } else {
      // Add to current row
      currentRowFields.push(field);
      currentRowWidth += fieldWidth;
    }
  });
  
  // Don't forget the last row
  if (currentRowFields.length > 0) {
    rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
  }
  
  // Ensure at least one empty row if nothing was added
  if (rowGroups.length === 0) {
    rowGroups.push({ id: 'row_empty_default', fields: [] });
  }
  
  return rowGroups;
};

// Centralized Client Status List
// MOVED TO: /src/constants/index.js - CLIENT_STATUSES
// DELETE THIS COMMENT AFTER VERIFYING BUILD

// Memoized field label input to prevent focus loss during typing
// Uses DebouncedInput for autosave with debounce delay
const FieldLabelInput = React.memo(({ fieldId, initialValue, onChange, className, placeholder }) => {
  const handleChange = useCallback((e) => {
    onChange(fieldId, { label: e.target.value });
  }, [fieldId, onChange]);
  
  return (
    <DebouncedInput
      value={initialValue || ''}
      onChange={handleChange}
      debounceMs={400}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      placeholder={placeholder}
    />
  );
}, (prevProps, nextProps) => {
  // Re-render if fieldId changes OR if initialValue changes externally
  return prevProps.fieldId === nextProps.fieldId && prevProps.initialValue === nextProps.initialValue;
});
FieldLabelInput.displayName = 'FieldLabelInput';

// Sortable Field Item Component for Drag and Drop
// Manages its own expand state to prevent re-renders from parent
// NOTE: We accept children for expanded content but use custom memo comparison
// to prevent re-renders when only children reference changes
const SortableFieldItem = React.memo(({ 
  field, 
  availableFieldTypes, 
  updateFormField, 
  removeFormField, 
  duplicateFormField, 
  moveFieldToSection, 
  allSections, 
  currentSectionId, 
  children,  // Accept children for expanded content
  renderExpandedContent  // Legacy prop - prefer children
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Check if field is non-editable (auto-populated data)
  // client_diagnoses_selector is interactive, not auto-populated
  const fieldTypeInfo = availableFieldTypes.find(t => t.type === field.type);
  const isNonEditable = field.type !== 'client_diagnoses_selector' && field.type !== 'add_contacts' && field.type !== 'pan_select' && (
    ['client_data', 'org_data', 'clinical', 'special'].includes(fieldTypeInfo?.category) || 
    field.type?.startsWith('client_') || 
    ['service_dropdown', 'group_dropdown', 'treatment_plan_goals', 'consent_affirmation'].includes(field.type)
  );

  // Render WYSIWYG preview of the field component
  const renderFieldPreview = () => {
    // Non-editable fields get special purple styling
    if (isNonEditable) {
      return (
        <div className="flex-1 h-8 bg-purple-50 rounded border border-purple-200 flex items-center px-3 text-xs text-purple-700">
          <span className="mr-2">📥</span>
          {field.label || fieldTypeInfo?.label || field.type}
          <span className="ml-auto text-purple-400 text-[10px]">auto-populated</span>
        </div>
      );
    }

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return <Input disabled placeholder={field.label || 'Text input'} className="h-8 text-sm bg-white flex-1" />;
      
      case 'textarea':
        return <DebouncedTextarea disabled placeholder={field.label || 'Text area'} className="h-10 text-sm bg-white flex-1 resize-none" rows={1} />;
      
      case 'date':
        return <Input type="date" disabled className="h-8 text-sm bg-white flex-1" />;
      
      case 'time':
        return <Input type="time" disabled className="h-8 text-sm bg-white flex-1" />;
      
      case 'select':
        return (
          <Select disabled>
            <SelectTrigger className="h-8 text-sm bg-white flex-1">
              <SelectValue placeholder={getOptionLabel(field.options?.[0]) || 'Select option'} />
            </SelectTrigger>
          </Select>
        );
      
      case 'multiselect':
        return (
          <div className="flex flex-wrap gap-1 flex-1 items-center min-h-[32px]">
            {(field.options || ['Option 1', 'Option 2']).slice(0, 4).map((opt, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                ☐ {getOptionLabel(opt)}
              </span>
            ))}
            {(field.options?.length || 0) > 4 && <span className="text-xs text-gray-400">+{field.options.length - 4} more</span>}
          </div>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center gap-2 h-8">
            <input type="checkbox" disabled className="h-4 w-4" />
            <span className="text-sm text-gray-600">{field.label || 'Checkbox'}</span>
          </div>
        );
      
      case 'checkbox_group':
        return (
          <div className="flex flex-wrap gap-x-4 gap-y-1 flex-1 items-center min-h-[32px]">
            {(field.options || ['Option 1', 'Option 2']).slice(0, 4).map((opt, i) => (
              <label key={i} className="inline-flex items-center text-sm">
                <input type="checkbox" disabled className="h-3.5 w-3.5 mr-1" />
                {getOptionLabel(opt)}
              </label>
            ))}
            {(field.options?.length || 0) > 4 && <span className="text-xs text-gray-400">+{field.options.length - 4} more</span>}
          </div>
        );
      
      case 'radio':
        return (
          <div className="flex flex-wrap gap-x-4 gap-y-1 flex-1 items-center min-h-[32px]">
            {(field.options || ['Option 1', 'Option 2']).slice(0, 4).map((opt, i) => (
              <label key={i} className="inline-flex items-center text-sm">
                <input type="radio" disabled name={`preview-${field.id}`} className="h-3.5 w-3.5 mr-1" />
                {getOptionLabel(opt)}
              </label>
            ))}
            {(field.options?.length || 0) > 4 && <span className="text-xs text-gray-400">+{field.options.length - 4} more</span>}
          </div>
        );
      
      case 'heading':
        const HeadingTag = field.headingLevel || 'h2';
        const headingStyles = {
          h1: 'text-xl font-bold',
          h2: 'text-lg font-semibold',
          h3: 'text-base font-medium'
        };
        return (
          <span 
            className={`${headingStyles[HeadingTag] || headingStyles.h2} flex-1`}
            style={{ color: field.fontColor || '#000', textAlign: field.textAlign || 'left' }}
          >
            {field.label || 'Heading'}
          </span>
        );
      
      case 'static_text':
        // Show content preview, truncated if too long
        const staticContent = typeof field.content === 'string' ? field.content : '';
        // Strip HTML tags for preview display
        const strippedContent = staticContent.replace(/<[^>]+>/g, '');
        const staticPreview = strippedContent.length > 100 ? strippedContent.substring(0, 100) + '...' : strippedContent;
        return (
          <div className="flex-1 bg-gray-50 border border-gray-200 rounded p-2">
            {staticPreview ? (
              <span className="text-sm text-gray-600 whitespace-pre-wrap">{staticPreview}</span>
            ) : (
              <span className="text-sm text-gray-400 italic">Click to add static text content...</span>
            )}
          </div>
        );
      
      case 'divider':
        return <hr className="flex-1 border-gray-300" />;
      
      case 'client_visibility_divider':
        return (
          <div className="flex-1 flex items-center justify-center py-2">
            <div className="flex-1 border-t-2 border-dashed border-teal-400"></div>
            <span className="px-3 text-xs text-teal-600 font-medium whitespace-nowrap">✂️ CLIENT SUMMARY START</span>
            <div className="flex-1 border-t-2 border-dashed border-teal-400"></div>
          </div>
        );
      
      case 'spacer':
        return <div className="flex-1 h-6 bg-gray-100 rounded border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">Spacer</div>;
      
      case 'signature':
        return <div className="flex-1 h-8 bg-gray-100 rounded border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-500">✍️ Signature</div>;

      case 'pan_select':
        return (
          <div className="flex-1 flex items-center gap-2 min-h-[32px]">
            <div className="flex-1 h-8 bg-white rounded border border-gray-300 flex items-center px-3 text-xs text-gray-500">Select existing PAN or write custom need…</div>
            <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 whitespace-nowrap">→ Treatment Plan</span>
          </div>
        );
      
      case 'send_assessment':
        return <div className="flex-1 h-8 bg-purple-50 rounded border border-dashed border-purple-300 flex items-center justify-center text-xs text-purple-600">📤 {field.label || 'Send Assessment'} (prompt at note time)</div>;
      
      case 'client_diagnoses_selector':
        return (
          <div className="flex-1 h-8 bg-teal-50 rounded border border-teal-200 flex items-center px-3 text-xs text-teal-700">
            <span className="mr-2">☑️</span>
            {field.label || 'Client Diagnoses (Selectable)'}
            <span className="ml-auto text-teal-500 text-[10px]">select from client diagnoses</span>
          </div>
        );
      
      default:
        return <div className="flex-1 h-8 bg-gray-100 rounded border flex items-center px-2 text-xs text-gray-500">{field.label || field.type}</div>;
    }
  };

  // Background color based on field type
  const bgClass = isNonEditable ? 'bg-purple-50 border-purple-200' : 'bg-gray-50';

  return (
    <div ref={setNodeRef} style={style} className={`border rounded ${bgClass} ${isExpanded ? 'p-3' : 'p-2'}`}>
      {/* Two-row WYSIWYG Layout */}
      <div className="space-y-1.5">
        {/* Row 1: Field Label + Actions */}
        <div className="flex items-center gap-2">
          {/* Drag Handle */}
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex-shrink-0">
            <GripVertical className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </div>
          
          {/* Field Label - Editable (wider now) */}
          {/* Field type badge - shown for ALL field types */}
          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0 ${
            isNonEditable ? 'text-purple-500 bg-purple-50' : 'text-gray-400 bg-gray-100'
          }`}>
            {fieldTypeInfo?.label || field.type}
          </span>
          
          {/* Field Label - Editable for most fields */}
          {!['divider', 'spacer', 'client_visibility_divider'].includes(field.type) && !isNonEditable && (
            <FieldLabelInput
              fieldId={field.id}
              initialValue={field.label}
              onChange={updateFormField}
              className={`font-medium text-sm h-7 flex-1 ${field.type === 'heading' ? 'font-bold text-base' : ''}`}
              placeholder={field.type === 'heading' ? 'Heading text...' : field.type === 'static_text' ? 'Static text label...' : `Enter ${(fieldTypeInfo?.label || field.type || 'field').toLowerCase()} label...`}
            />
          )}
          {/* Non-editable fields show label as static text */}
          {isNonEditable && (
            <span className="font-medium text-sm flex-1 text-purple-700">
              {field.label || fieldTypeInfo?.label || field.type}
            </span>
          )}
          {/* Layout-only fields (divider, spacer) - no label needed */}
          {['divider', 'spacer', 'client_visibility_divider'].includes(field.type) && !isNonEditable && (
            <span className="font-medium text-sm flex-1 text-gray-500">
              {field.label || fieldTypeInfo?.label || field.type}
            </span>
          )}
          
          {/* Required indicator */}
          {field.required && !['divider', 'spacer', 'heading', 'static_text', 'client_visibility_divider'].includes(field.type) && (
            <span className="text-red-500 text-sm font-bold flex-shrink-0" title="Required">*</span>
          )}
          
          {/* Action Buttons - always visible, won't wrap */}
          <div className="flex items-center gap-0.5 flex-shrink-0 ml-auto">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => duplicateFormField(field.id)}
              type="button"
              className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              title="Duplicate Field"
            >
              <Copy className="h-3 w-3" />
            </Button>
            
            {/* Move to Section - Icon with dropdown */}
            {allSections && allSections.length > 1 && (
              <div className="relative">
                <select
                  className="absolute inset-0 opacity-0 cursor-pointer w-6 h-6"
                  onChange={(e) => {
                    if (e.target.value && e.target.value !== currentSectionId) {
                      moveFieldToSection(field.id, e.target.value);
                    }
                  }}
                  value=""
                  title="Move to section"
                >
                  <option value="">Move field...</option>
                  {allSections.filter(s => s.header.id !== currentSectionId).map(section => (
                    <option key={section.header.id} value={section.header.id}>
                      {section.header.label || 'Fields'}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="ghost"
                  type="button"
                  className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100 pointer-events-none"
                  title="Move to section"
                >
                  <ArrowUpDown className="h-3 w-3" />
                </Button>
              </div>
            )}
            
            <Button
              size="sm"
              variant="ghost"
              onClick={() => removeFormField(field.id)}
              type="button"
              className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
              title="Remove Field"
            >
              <X className="h-3 w-3" />
            </Button>
            
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsExpanded(prev => !prev)}
              type="button"
              className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-200"
              title={isExpanded ? 'Collapse options' : 'Expand options'}
            >
              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
        
        {/* Row 2: WYSIWYG Field Preview */}
        <div className="pl-6">
          {renderFieldPreview()}
        </div>
      </div>
      
      {/* Expanded Content - Additional Options */}
      {isExpanded && (
        <div className="mt-3 pt-3 border-t">
          {/* Required checkbox - in expanded mode */}
          {!['divider', 'spacer', 'heading', 'static_text', 'client_visibility_divider'].includes(field.type) && (
            <div className="flex items-center space-x-2 mb-3">
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) => updateFormField(field.id, { required: e.target.checked })}
                className="h-4 w-4"
                id={`required-${field.id}`}
              />
              <label htmlFor={`required-${field.id}`} className="text-xs font-medium cursor-pointer">Required Field</label>
            </div>
          )}
          {/* Carry Forward toggle - value-bearing input fields only */}
          {['text', 'textarea', 'email', 'phone', 'date', 'time', 'select', 'multiselect', 'checkbox', 'checkbox_group', 'radio', 'matrix'].includes(field.type) && (
            <div className="flex items-center space-x-2 mb-3">
              <input
                type="checkbox"
                checked={!!field.is_carry_forward}
                onChange={(e) => updateFormField(field.id, { is_carry_forward: e.target.checked })}
                className="h-4 w-4"
                id={`carry-forward-${field.id}`}
                data-testid={`carry-forward-toggle-${field.id}`}
              />
              <label htmlFor={`carry-forward-${field.id}`} className="text-xs font-medium cursor-pointer">
                Carry Forward
                <span className="text-gray-500 font-normal ml-1">(pre-fill from this client's most recent completed note by the same provider)</span>
              </label>
            </div>
          )}
          {/* Render children (preferred) or legacy renderExpandedContent callback */}
          {children}
          {renderExpandedContent && renderExpandedContent(field, updateFormField)}
        </div>
      )}
    </div>
  );
});
SortableFieldItem.displayName = 'SortableFieldItem';

// Sortable Section Component for Drag and Drop
const SortableSection = ({ section, availableFieldTypes, updateFormField, removeFormField, duplicateFormField, moveFieldToSection, allSections, children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.header.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sectionColor = section.header.backgroundColor || '#3b82f6'; // Default blue

  return (
    <div ref={setNodeRef} className="border-2 rounded-lg p-4" 
      style={{ 
        ...style, 
        borderColor: sectionColor,
        backgroundColor: `${sectionColor}10`
      }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2 flex-1">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
            <GripVertical className="h-6 w-6" style={{ color: sectionColor }} />
          </div>
          {isEditingLabel ? (
            <DebouncedInput
              value={section.header.label || ''}
              onChange={(e) => updateFormField(section.header.id, { label: e.target.value })}
              onBlur={() => setIsEditingLabel(false)}
              onKeyDown={(e) => e.key === 'Enter' && setIsEditingLabel(false)}
              autoFocus
              className="font-semibold text-sm uppercase tracking-wide bg-white border rounded px-2 py-1 flex-1 min-h-[32px]"
              style={{ borderColor: sectionColor, color: sectionColor }}
              placeholder="Section Name (optional)"
            />
          ) : (
            <div 
              className="font-semibold text-sm uppercase tracking-wide cursor-pointer hover:opacity-80 px-2 py-1 rounded min-h-[32px] min-w-[150px] flex items-center gap-2 border border-transparent hover:border-gray-300 transition-all"
              style={{ color: sectionColor }}
              onClick={() => setIsEditingLabel(true)}
              title="Click to edit section name"
            >
              📋 {section.header.label || '(Click to name section)'}
              <Pencil className="h-3 w-3 opacity-50" />
            </div>
          )}
          <Badge style={{ backgroundColor: `${sectionColor}20`, color: sectionColor }}>
            {section.fields.length} field{section.fields.length !== 1 ? 's' : ''}
          </Badge>
          {/* Color picker */}
          <div className="flex items-center gap-1">
            <input
              type="color"
              value={sectionColor}
              onChange={(e) => updateFormField(section.header.id, { backgroundColor: e.target.value })}
              className="w-6 h-6 rounded cursor-pointer border-0"
              title="Section color"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsCollapsed(!isCollapsed)}
            type="button"
          >
            {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
          {section.header.id !== 'default' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => removeFormField(section.header.id)}
              type="button"
              className="text-red-500"
            >
              ✕
            </Button>
          )}
        </div>
      </div>
      {!isCollapsed && (
        <div className="space-y-2 ml-8">
          {children}
        </div>
      )}
    </div>
  );
};

// Droppable Row Component for Form Builder - allows dropping fields into rows
const DroppableRow = ({ rowId, rowIndex, children, isEmpty }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: rowId,
  });
  
  return (
    <div 
      ref={setNodeRef}
      className={`relative border border-dashed rounded-lg p-2 transition-colors ${
        isOver 
          ? 'border-blue-500 bg-blue-50' 
          : isEmpty 
            ? 'border-gray-300 bg-gray-50/50' 
            : 'border-gray-300 bg-gray-50/30'
      }`}
    >
      {/* Row Label */}
      <div className={`absolute -top-2 left-2 px-1 text-xs ${isOver ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-400'}`}>
        Row {rowIndex + 1} {isOver && '(Drop here)'}
      </div>
      
      {children}
    </div>
  );
};

// Error Boundary to catch React errors and prevent white screens
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] React error caught:', error, errorInfo);
    // Auto-reload on ChunkLoadError (stale deployment cache)
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
      console.warn('[ErrorBoundary] ChunkLoadError detected — auto-reloading');
      window.location.reload();
      return;
    }
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-8">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-lg text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h1>
            <p className="text-gray-600 mb-4">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors"
              >
                Refresh Page
              </button>
              <button
                onClick={() => {
                  // Clear all storage to reset state completely
                  sessionStorage.clear();
                  localStorage.removeItem('session_transfer_token');
                  localStorage.removeItem('session_transfer_timestamp');
                  window.location.href = '/login';
                }}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Clear Session & Login
              </button>
            </div>
            {/* Always show error details to help debug production issues */}
            {this.state.error && (
              <details className="mt-4 text-left text-sm text-red-600 bg-red-50 p-4 rounded">
                <summary className="cursor-pointer font-medium">Error Details (click to expand)</summary>
                <pre className="mt-2 overflow-auto whitespace-pre-wrap">{this.state.error.toString()}</pre>
                {this.state.errorInfo && (
                  <pre className="mt-2 overflow-auto whitespace-pre-wrap text-xs">{this.state.errorInfo.componentStack}</pre>
                )}
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Module-level flag to track if Dashboard has ever loaded successfully
// This persists across component unmounts/remounts
let dashboardHasLoadedOnce = false;

// Helper function to check if note content has a client_visibility_divider field
const noteHasClientVisibilityDivider = (noteContent) => {
  try {
    const content = typeof noteContent === 'string' ? JSON.parse(noteContent) : noteContent;
    
    // Check sections-based content
    if (content?.sections) {
      for (const section of content.sections) {
        for (const field of section.fields || []) {
          if (field.type === 'client_visibility_divider' || field.field_type === 'client_visibility_divider') {
            return true;
          }
        }
      }
    }
    
    // Check flat fields-based content
    if (content?.fields) {
      for (const field of content.fields) {
        if (field.type === 'client_visibility_divider' || field.field_type === 'client_visibility_divider') {
          return true;
        }
      }
    }
    
    return false;
  } catch {
    return false;
  }
};

// Helper function to download note content as a text file for recovery
const downloadNoteContentAsText = (noteContent, noteInfo) => {
  try {
    const { extractNoteTextContent } = require('./utils/noteTextExtractor');
    const textContent = extractNoteTextContent(noteContent, noteInfo);
    
    if (!textContent || textContent.trim().length === 0) {
      return false;
    }
    
    // Create filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    const clientName = (noteInfo?.client_name || 'Unknown').replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `Note_Recovery_${clientName}_${timestamp}.txt`;
    
    // Create and download the file
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (e) {
    console.error('[downloadNoteContentAsText] Error:', e);
    return false;
  }
};

// Client Pagination Controls with right-click page jump feature
const ClientPaginationControls = ({ currentPage, totalPages, onPageChange, disabled, showPageSize = false, pageSize, onPageSizeChange }) => {
  const [jumpOpen, setJumpOpen] = useState(false);
  const [jumpValue, setJumpValue] = useState('');
  
  const handleJump = () => {
    const page = parseInt(jumpValue, 10);
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      setJumpOpen(false);
      setJumpValue('');
    }
  };
  
  const handleContextMenu = (e) => {
    e.preventDefault();
    setJumpValue(currentPage.toString());
    setJumpOpen(true);
  };
  
  return (
    <div className="flex items-center gap-1">
      <Popover open={jumpOpen} onOpenChange={setJumpOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            onContextMenu={handleContextMenu}
            disabled={currentPage === 1 || disabled}
            title="Left-click: Previous page | Right-click: Jump to page"
          >
            ←
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3" align="start">
          <div className="space-y-3">
            <div className="text-sm font-medium">Jump to Page</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={totalPages}
                value={jumpValue}
                onChange={(e) => setJumpValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                className="w-20 px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Page #"
                autoFocus
              />
              <span className="text-sm text-gray-500">of {totalPages}</span>
              <Button size="sm" onClick={handleJump} disabled={!jumpValue}>
                Go
              </Button>
            </div>
            <div className="flex gap-1 pt-1 border-t">
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex-1 text-xs"
                onClick={() => { onPageChange(1); setJumpOpen(false); }}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex-1 text-xs"
                onClick={() => { onPageChange(totalPages); setJumpOpen(false); }}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      
      <span 
        className="text-sm px-2 cursor-pointer hover:bg-gray-100 rounded py-1"
        onClick={() => { setJumpValue(currentPage.toString()); setJumpOpen(true); }}
        title="Click to jump to a specific page"
      >
        Page {currentPage} of {totalPages}
      </span>
      
      <Popover open={false}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            onContextMenu={handleContextMenu}
            disabled={currentPage >= totalPages || disabled}
            title="Left-click: Next page | Right-click: Jump to page"
          >
            →
          </Button>
        </PopoverTrigger>
      </Popover>
    </div>
  );
};

// Dashboard Component  
// Phase 3B-1 (Jun 2026): V2 pages open notes at /note/:id?return=/v2/...
// so closing the editor lands back on the exact V2 page that opened it
// (instead of stranding the user on the legacy dashboard).  Only /v2
// paths are honored to avoid open-redirect misuse.
// getV2NoteReturnPath moved to hooks/useNoteLifecycleController (Phase 3B-2 step 1).

const Dashboard = ({ fullscreenClientId = null, fullscreenNoteId = null, initialClientId = null }) => {
  const { user, setUser, logout, createUser, refreshUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Phase 3e.2 (Feb 2026) — access the V2 IntakeWizardProvider so the
  // `{ skipNavigation: true }` call sites can delegate to it instead of
  // mutating legacy's inline state.  Sites that DO navigate (admin
  // "Add Client", duplicate "Use This Client", URL-driven loaders)
  // remain on the inline path until Phase 3e.3 collapses the state
  // owner entirely.
  const intakeWizard = useIntakeWizard();
  
  // Get state from contexts (prevents re-renders across unrelated features)
  const {
    providers, setProviders,
    locations, setLocations,
    posCodes, setPosCodes,
    availableStatuses, setAvailableStatuses,
    users, setUsers,
    roles, setRoles,
    organizations, setOrganizations,
    cptCodes, setCptCodes,
    cptBundles, setCptBundles,
    formTemplates, setFormTemplates,
    formTypes, setFormTypes,
    serviceTypes, setServiceTypes,
    intakeBundles, setIntakeBundles,
    allGroups, setAllGroups,
    brandingSettings, setBrandingSettings,
    staticDataLoaded, setStaticDataLoaded,
  } = useStaticData();

  // Resolve the user's role name from role_id → roles lookup.
  // This is the authoritative source; user.role (denormalized) can be stale/null.
  const userRoleName = React.useMemo(() => {
    if (user?.role_id && roles?.length) {
      const role = roles.find(r => r.id === user.role_id);
      if (role?.name) return role.name.toLowerCase();
    }
    return (user?.role || '').toLowerCase();
  }, [user?.role_id, user?.role, roles]);
  
  const {
    activeTab, setActiveTab,
    adminInnerTab, setAdminInnerTab,
    tasks, setTasks,
    tasksAssignedByMe, setTasksAssignedByMe,
    stats, setStats,
    alertCounts, setAlertCounts,
    isLoading, setIsLoading,
    resetUIState,
  } = useUI();

  // Notification state lives in NotificationsContext (Feb 2026).
  // Dashboard subscribes to ACTIONS only — actions are stable, so
  // Dashboard never re-renders when notifications poll comes back.
  const {
    setNotifications,
    setUnreadCount,
    setShowNotifications,
    fetchNotifications,
  } = useNotificationsActions();
  
  // Theme state - initialize from user preference or localStorage
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // First check localStorage for instant load
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    // Default to light
    return false;
  });

  // Apply theme class to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Safety net: detect and fix stale pointer-events:none on <body>.
  // Radix UI modal dialogs set body { pointer-events: none } and restore it on close.
  // If cleanup races with React re-renders, pointer-events can get stuck, making all
  // inputs unclickable until page refresh. This listener detects clicks that "fall through"
  // to the document (possible only when body pointer-events is being overridden by a
  // portal element's pointer-events:auto) and cleans up if no modal dialog is actually open.
  useEffect(() => {
    const handler = () => {
      if (document.body.style.pointerEvents === 'none') {
        // Check if any Radix modal dialog is actually open
        const openModals = document.querySelectorAll('[role="dialog"][data-state="open"]');
        if (openModals.length === 0) {
          console.warn('[PointerEvents Safety Net] Removing stale pointer-events:none from body');
          document.body.style.pointerEvents = '';
        }
      }
    };
    // Use capture phase to detect clicks even when pointer-events:none is set on body
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);

  // Sync theme with user preference from database
  useEffect(() => {
    if (user?.theme_preference) {
      const prefersDark = user.theme_preference === 'dark';
      setIsDarkMode(prefersDark);
      safeLocalStorageSetItem('theme', user.theme_preference);
    }
  }, [user?.theme_preference]);

  // Handle invitation response from email link
  useEffect(() => {
    const handleInvitationResponse = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const appointmentId = urlParams.get('respond_invitation');
      const response = urlParams.get('response');
      
      if (appointmentId && response && user?.id) {
        try {
          // Clear URL params first
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Make API call to respond
          const result = await axios.post(
            `${API}/appointments/${appointmentId}/respond-invitation?response=${response}`
          );
          
          if (response === 'accepted') {
            toast.success("You've accepted the meeting invitation!");
          } else {
            toast.success("You've declined the meeting invitation.");
          }
          
          // Targeted refresh: only appointments changed
          refreshAppointments(true);
        } catch (error) {
          console.error("Error responding to invitation:", error);
          toast.error(error.response?.data?.detail || "Failed to respond to invitation");
        }
      }
    };
    
    if (user?.id) {
      handleInvitationResponse();
    }
  }, [user?.id]);

  // Feb 2026 — V2 layout sends users here via /dashboard?openNotifications=1
  // when they click the V2 header bell.  Pop the manager open and clean
  // the URL.
  useEffect(() => {
    if (!user?.id) return;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('openNotifications') === '1') {
        setShowNotificationManager(true);
        urlParams.delete('openNotifications');
        const search = urlParams.toString();
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + (search ? `?${search}` : '')
        );
      }
    } catch {
      // URL parsing edge cases — silent fail, the bell still works from legacy
    }
  }, [user?.id]);


  // Toggle theme and save to database
  const handleToggleTheme = async () => {
    const newTheme = isDarkMode ? 'light' : 'dark';
    setIsDarkMode(!isDarkMode);
    safeLocalStorageSetItem('theme', newTheme);
    
    try {
      await axios.post(`${API}/auth/theme-preference`, { theme: newTheme });
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };
  
  // Wrapper logout function that also resets UI state
  const handleLogout = () => {
    // Set flag FIRST before any state changes
    sessionStorage.setItem('intentionalLogout', 'true');
    sessionStorage.removeItem('lastLoggedInUserId');
    
    // Clear all state
    resetUIState();
    clearClientChartData();
    clearImpersonation();
    // Flush ETag cache so PHI from the prior session cannot leak into a
    // subsequent user's polls on the same browser tab.
    try { _clearEtagCache(); } catch { /* ignore */ }
    // Wipe the chart-bundle sessionStorage cache too — same rationale:
    // no PHI ciphertext should linger after explicit logout.  Tab close
    // would also clear sessionStorage automatically, but a user who
    // logs out then signs back in as someone else would otherwise see
    // the previous account's encrypted blobs (un-decryptable, but
    // still wasteful to keep around).
    try { clearChartBundleCache(); } catch { /* ignore */ }
    
    // Clear auth (this will trigger ProtectedRoute redirect, but our flag is already set)
    logout();
    
    // Force navigate to clean login URL (will override ProtectedRoute's redirect)
    window.location.href = '/login';
  };
  
  // Aliases removed: setUnreadCount + setShowNotifications now come
  // directly from useNotificationsActions() above (Feb 2026).
  
  const {
    clients, setClients,
    allClientsForLookup, allClientsForLookupRef, setAllClientsForLookup,
    selectedClient, setSelectedClient,
    showClientDetail, setShowClientDetail,
    clientDetailLoading, setClientDetailLoading,
    clientStatusFilter, setClientStatusFilter,
    clientStatusFilterMode, setClientStatusFilterMode,
    clientProviderFilter, setClientProviderFilter,
    clientProviderFilterMode, setClientProviderFilterMode,
    clientLabels, setClientLabels,
    selectedLabelFilter, setSelectedLabelFilter,
    selectedLabelFilterMode, setSelectedLabelFilterMode,
    selectedInsuranceFilter, setSelectedInsuranceFilter,
    selectedInsuranceFilterMode, setSelectedInsuranceFilterMode,
    clientSearch, setClientSearch,
    clientViewMode, setClientViewMode,
    clientsPerPage, setClientsPerPage,
    clientsCurrentPage, setClientsCurrentPage,
    clientsSortColumn, setClientsSortColumn,
    clientsSortDirection, setClientsSortDirection,
    clientNameFormat, setClientNameFormat,
    clientsTotalCount, setClientsTotalCount,
    clientsTotalPages, setClientsTotalPages,
    clientsLastUpdated, setClientsLastUpdated,
    clientsLoading, setClientsLoading,
    clientDetailTab, setClientDetailTab,
    clientHeaderCollapsed, setClientHeaderCollapsed,
    clientNotes, setClientNotes,
    clientNotesHasMore, setClientNotesHasMore,
    clientNotesTotalCount, setClientNotesTotalCount,
    clientDocuments, setClientDocuments,
    clientDiagnoses, setClientDiagnoses,
    clientMedications, setClientMedications,
    clientAppointments, setClientAppointments,
    clientAuthorizations, setClientAuthorizations,
    clientEligibility, setClientEligibility,
    clientAlerts, setClientAlerts,
    clientContacts, setClientContacts,
    clientReleases, setClientReleases,
    clientVitals, setClientVitals,
    clientGroups, setClientGroups,
    clientMDTReviews, setClientMDTReviews,
    clientEnrollments, setClientEnrollments,
    clientInsurances, setClientInsurances,
    clientBillingSummary, setClientBillingSummary,
    clientClaims, setClientClaims,
    clientPaymentHistory, setClientPaymentHistory,
    clientServiceEnrollments, setClientServiceEnrollments,
    pendingIntakeClients,
    waitlistStatusClients,
    activeClients,
    nonArchivedClients,
    clearClientChartData,
  } = useClients();

  // Workstream B — Pause background polling while the user is loading
  // a chart.  isUserBusy() is checked by every background setInterval
  // poll above; this hook flips that flag on/off as the chart-load
  // lifecycle runs (see setClientDetailLoading(true/false) in
  // handleClientClick).  Reference-counted, so concurrent modals stack
  // safely.
  useMarkBusyWhile('chart-load', clientDetailLoading);

  // Workstream B — Billing team explicitly asked for poll suppression
  // while they're actively working in any billing surface.  Treat
  // whole-tab residence as a busy condition so the 8 background polls
  // back off until they navigate away.  Polls fire as soon as they
  // pop into a non-work tab (e.g. dashboard for notifications).
  //
  // Feb 2026 follow-up: user requested the same treatment for every
  // other "work" tab — schedule, groups, waitlist, CPT, auths,
  // medical records, payroll, notes, medication-management.  All of
  // them are surfaces where the user is actively reading / writing
  // data and a background poll mid-typing is felt as lag.
  const _workTabActive = (
    activeTab === 'billing' ||
    activeTab === 'billing-management' ||
    activeTab === 'era-management' ||
    activeTab === 'claims-workshop' ||
    activeTab === 'eligibility' ||
    activeTab === 'appointments' ||         // Schedule
    activeTab === 'groups' ||
    activeTab === 'waitlist' ||
    activeTab === 'authorizations' ||       // Auths
    activeTab === 'medical-records' ||      // Records
    activeTab === 'payroll' ||
    activeTab === 'notes' ||
    activeTab === 'medication-management'
  );
  useMarkBusyWhile('work-tab', _workTabActive);

  // Inner-chart Billing tab — same rationale.  When a biller is deep
  // in a client's per-claim history / payments timeline, suppress
  // background polls until they navigate out.
  useMarkBusyWhile(
    'client-chart-billing-tab',
    showClientDetail && clientDetailTab === 'billing'
  );
  
  // Load client name format preference from localStorage on login
  useEffect(() => {
    if (user?.id) {
      const savedFormat = localStorage.getItem(`clientNameFormat_${user.id}`);
      if (savedFormat && (savedFormat === 'first_last' || savedFormat === 'last_first')) {
        setClientNameFormat(savedFormat);
      }
    }
  }, [user?.id, setClientNameFormat]);
  
  // CRITICAL: Reset UI state when a DIFFERENT user logs in
  // This prevents state from the previous user's session bleeding into the new user's session
  // We use sessionStorage to track the previous user across page refreshes
  useEffect(() => {
    if (!user?.id) return;
    
    const previousUserId = sessionStorage.getItem('lastLoggedInUserId');
    
    if (previousUserId && previousUserId !== user.id) {
      // Different user logged in - reset UI state
      void 0 && console.log('[Session] Different user logged in (was:', previousUserId, ', now:', user.id, ') - resetting UI state');
      resetUIState();
    }
    
    // Always update the stored user ID
    sessionStorage.setItem('lastLoggedInUserId', user.id);
  }, [user?.id, resetUIState]);
  
  // Save client name format preference when it changes
  useEffect(() => {
    if (user?.id && clientNameFormat) {
      safeLocalStorageSetItem(`clientNameFormat_${user.id}`, clientNameFormat);
    }
  }, [user?.id, clientNameFormat]);
  
  const {
    appointments, setAppointments,
    selectedAppointment, setSelectedAppointment,
    calendarDate, setCalendarDate,
    calendarView, setCalendarView,
    calendarInterval, setCalendarInterval,
    selectedProviders, setSelectedProviders,
    selectedProvidersMode, setSelectedProvidersMode,
    showAppointmentForm, setShowAppointmentForm,
    editingAppointment, setEditingAppointment,
    appointmentFormData, setAppointmentFormData,
    resetAppointmentForm,
    appointmentSortAsc, setAppointmentSortAsc,
    showPastAppointments, setShowPastAppointments,
    showCanceledAppointments, setShowCanceledAppointments,
    appointmentSearch, setAppointmentSearch,
    appointmentContextMenu, setAppointmentContextMenu,
    draggedAppointment, setDraggedAppointment,
    resizingAppointment, setResizingAppointment,
    undoStack, setUndoStack,
    attendanceAppointment, setAttendanceAppointment,
    addAttendeeAppointment, setAddAttendeeAppointment,
    cptCodeUsage, setCptCodeUsage,
    groupAppointments, setGroupAppointments,
    showPastGroupAppointments, setShowPastGroupAppointments,
    groupNoteAppointment, setGroupNoteAppointment,
    addToFutureAppointments, setAddToFutureAppointments,
    // Jun 17 2026 — Group Notes dialog state migrated into
    // AppointmentsContext (was 5 useState() declarations in App.js).
    // Defaults preserved exactly; no behavior change.
    showGroupNoteDialog, setShowGroupNoteDialog,
    groupNoteGroup, setGroupNoteGroup,
    groupNoteCurriculumSession, setGroupNoteCurriculumSession,
    groupNoteExistingNotes, setGroupNoteExistingNotes,
    groupNoteMode, setGroupNoteMode,
  } = useAppointments();
  
  const {
    notes, setNotes,
    myTodoNotes, setMyTodoNotes,
    selectedNote, setSelectedNote,
    notesDateRange, setNotesDateRange,
    notesCustomStartDate, setNotesCustomStartDate,
    notesCustomEndDate, setNotesCustomEndDate,
    notesClientFilter, setNotesClientFilter,
    notesStatusFilter, setNotesStatusFilter,
    showNoteDetail, setShowNoteDetail,
    showCompletedNotes, setShowCompletedNotes,
    showActiveNotes, setShowActiveNotes,
    noteContextMenu, setNoteContextMenu,
    noteDetailHeaderCollapsed, setNoteDetailHeaderCollapsed,
    isEditingNote, setIsEditingNote,
    editedNoteContent, setEditedNoteContent,
    noteJustSigned, setNoteJustSigned,
    selectedNoteStatus, setSelectedNoteStatus,
    isSavingNote, setIsSavingNote,
    noteCreationError, setNoteCreationError,
    autosaveBlockedReason, setAutosaveBlockedReason,
    selectedNotes, setSelectedNotes,
    selectedClinicalNotes, setSelectedClinicalNotes,
    showAddNote, setShowAddNote,
    showCreateNoteDialog, setShowCreateNoteDialog,
    createNoteClientId, setCreateNoteClientId,
    createNoteAppointmentId, setCreateNoteAppointmentId,
    selectedNoteProvider, setSelectedNoteProvider,
    selectedNoteClients, setSelectedNoteClients,
    noteDialogHeaderCollapsed, setNoteDialogHeaderCollapsed,
    noteClientLocked, setNoteClientLocked,
    selectedNoteGroupId, setSelectedNoteGroupId,
    newNoteFormData, setNewNoteFormData,
    notesReadyForBilling, setNotesReadyForBilling,
    selectedNoteForReview, setSelectedNoteForReview,
    noteReviewData, setNoteReviewData,
    showNoteReviewDialog, setShowNoteReviewDialog,
    notesSentForCorrection, setNotesSentForCorrection,
    activeNotesPage, setActiveNotesPage,
    activeNotesPageSize, setActiveNotesPageSize,
    billedNotesPage, setBilledNotesPage,
    billedNotesPageSize, setBilledNotesPageSize,
    pendingDeletes, setPendingDeletes,
    resetNoteForm,
  } = useNotes();
  
  const {
    impersonatedUserId,
    impersonatedUser,
    isActualAdmin,
    availableUsers,
    loadingUsers,
    intentionalStopImpersonation,
    initializeAdmin,
    fetchUsersOnDemand,
    startImpersonation,
    stopImpersonation,
    clearImpersonation,
    setImpersonatedUserId,
    setIntentionalStopImpersonation,
  } = useImpersonation();
  
  // Compute active notes count for header display (must match filter logic in Tasks tab)
  const activeNotesCount = useMemo(() => {
    if (!notes || !user) return 0;
    
    let accessibleNotes = notes;
    if (userRoleName === 'provider' || userRoleName === 'intern' || (user?.is_provider && !user?.role)) {
      accessibleNotes = notes.filter(n => n.provider_id === user.id);
    } else if (userRoleName === 'supervisor' || user?.is_supervisor) {
      const superviseeIds = (users || []).filter(u => u.supervisor_ids?.includes(user.id)).map(u => u.id);
      accessibleNotes = notes.filter(n => n.provider_id === user.id || superviseeIds.includes(n.provider_id));
    }
    
    const todoNoteIds = new Set((myTodoNotes || []).map(n => n.id));
    accessibleNotes = accessibleNotes.filter(n => !todoNoteIds.has(n.id));
    
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    
    return accessibleNotes.filter(n => {
      if (n.status === 'draft' || !n.status) return false;
      if (n.is_locked || n.status === 'billed' || n.status === 'paid') return false;
      if (n.status === 'completed' && !n.is_billable) return false;
      if (n.status === 'signed_ready_for_billing' && !n.is_billable) return false;
      const noteDate = new Date(n.created_at);
      return noteDate >= ninetyDaysAgo;
    }).length;
  }, [notes, myTodoNotes, user, users]);
  
  // Don't render dashboard until user is loaded
  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
        <p className="text-gray-600">Loading dashboard...</p>
      </div>
    );
  }
  
  // AUP (Acceptable Use Policy) state and handler
  const [showAUPModal, setShowAUPModal] = useState(false);
  const [aupLoading, setAupLoading] = useState(false);
  
  // Update AUP modal visibility when user data loads.
  // IMPORTANT: never prompt during impersonation. The actual human in the
  // seat is the admin, who has already accepted the AUP. Showing the modal
  // when "viewing as" a different user would (a) interrupt every navigation
  // because the impersonated user's acceptance state is fetched fresh on
  // each refreshUser() call, and (b) record the acceptance against the
  // wrong record (accept-aup uses the real admin, not the impersonatee, so
  // the impersonated user's flag never flips and the modal loops). When
  // impersonation stops the admin's own aup_accepted_at is consulted again.
  //
  // Also suppress during the brief stop-impersonation transition window:
  // setImpersonatedUserId(null) is synchronous but refreshUser() is async,
  // so for ~50-200ms `impersonatedUserId === null` AND `user` is still the
  // impersonated user (with aup_accepted_at: null) → modal flashed on screen.
  // The `intentionalStopImpersonation` flag is already set during this
  // window and reset 100ms after refreshUser resolves.
  useEffect(() => {
    if (user && !impersonatedUserId && !intentionalStopImpersonation) {
      // Only show modal if user exists AND hasn't accepted AUP yet AND we
      // are not impersonating someone else AND not mid-transition.
      setShowAUPModal(!user.aup_accepted_at);
    } else {
      setShowAUPModal(false);
    }
  }, [user?.aup_accepted_at, impersonatedUserId, intentionalStopImpersonation]);
  
  const handleAcceptAUP = async () => {
    setAupLoading(true);
    try {
      await axios.post(`${API}/auth/accept-aup`);
      await refreshUser(); // Refresh user data to get updated aup_accepted_at
      setShowAUPModal(false);
      toast.success('Thank you for accepting the Acceptable Use Policy');
    } catch (error) {
      console.error('Failed to accept AUP:', error);
      toast.error('Failed to record policy acceptance. Please try again.');
    } finally {
      setAupLoading(false);
    }
  };
  
  // Helper function to open Google Meet in a small popup window positioned in the corner
  // This allows users to stay on the call while working in the main app
  const openMeetPopup = (meetLink) => {
    if (!meetLink) return;

    // Feb 2026 — Doxy.me provider routing
    // The link we store is the provider's PUBLIC waiting-room URL
    // (e.g., ``https://doxy.me/dr-smith``).  When the PROVIDER clicks
    // "Start Session" from inside this app, they want to land on
    // their own dashboard / sign-in page — not the patient-facing
    // waiting room.  Doxy's ``/sign-in`` page auto-redirects to the
    // dashboard if the provider is already authenticated, and shows
    // the login form if not, which is exactly the UX the user asked
    // for ("take them to their login page if not logged in, home if
    // they are").  We don't gate on currentUser.id === provider_id
    // because in-app Start Session clicks are always made by the
    // provider (or someone acting on their behalf — schedulers /
    // admins also benefit from landing on the dashboard rather than
    // the public waiting room).
    let popupLink = meetLink;
    try {
      const host = new URL(meetLink).hostname.replace(/^www\./, '');
      if (host.includes('doxy.me')) {
        popupLink = 'https://doxy.me/sign-in';
      }
    } catch { /* fall through with original link */ }

    // Smaller window size for better multitasking
    const width = 480;
    const height = 360;
    // Position in bottom-right corner of screen
    const left = window.screen.width - width - 20;
    const top = window.screen.height - height - 100;

    const popup = window.open(
      popupLink,
      'TelehealthSession_' + Date.now(),
      `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
    );

    if (popup) {
      popup.focus();
    } else {
      // Popup blocked — fall back to a normal new tab; if that's blocked
      // too, surface the link so the user can open it manually.
      const tab = window.open(popupLink, '_blank', 'noopener');
      if (!tab) {
        window.prompt('Popups are blocked — copy the meeting link below:', popupLink);
      }
    }

    return popup;
  };
  
  // Use transition for non-urgent state updates (helps with voice-to-text performance)
  const [isPending, startTransition] = useTransition();
  
  // Inactivity timeout (HIPAA compliance) - using extracted hook
  const {
    showInactivityWarning,
    inactivityCountdown,
    handleContinueSession,
    handleInactivityLogout: baseInactivityLogout
  } = useInactivityTimeout(logout);
  
  // Wrap inactivity logout to also reset UI state
  const handleInactivityLogout = () => {
    resetUIState();
    clearClientChartData();
    navigate('/login?reason=inactivity');
    baseInactivityLogout();
  };
  
  // Note: notifications, unreadNotifications, showNotificationsPanel moved to NotificationsContext (Feb 2026)
  const [showNotificationManager, setShowNotificationManager] = useState(false);
  // Notification manager filter/sort/selection state moved into the
  // NotificationManagerDialog component itself (Feb 2026).
  
  // Fullscreen client mode
  const [isFullscreenClientMode, setIsFullscreenClientMode] = useState(!!fullscreenClientId);
  
  // Staff messaging state
  const [showStaffMessages, setShowStaffMessages] = useState(false);
  const [staffMessageThreads, setStaffMessageThreads] = useState([]);
  const [selectedStaffThread, setSelectedStaffThread] = useState(null);
  const [staffThreadMessages, setStaffThreadMessages] = useState([]);
  const [staffReplyText, setStaffReplyText] = useState('');
  const [sendingStaffReply, setSendingStaffReply] = useState(false);
  const [loadingStaffMessages, setLoadingStaffMessages] = useState(false);
  const [unreadStaffMessagesCount, setUnreadStaffMessagesCount] = useState(0);
  const [showForwardDialog, setShowForwardDialog] = useState(false);
  const [forwardToUserId, setForwardToUserId] = useState('');
  const [forwardingMessage, setForwardingMessage] = useState(false);
  const [openMessageMenu, setOpenMessageMenu] = useState(null); // Track which message menu is open
  const [urgentWaitlistClearedTrigger, setUrgentWaitlistClearedTrigger] = useState(0); // Trigger refetch when urgent waitlist is cleared

  // WebSocket handlers for real-time updates
  const wsHandlers = useMemo(() => ({
    onNewMessage: (threadId, message) => {
      void 0 && console.log('[WebSocket] New message received for thread:', threadId);
      // Add message to current thread if viewing it
      setStaffThreadMessages(prev => {
        // Only add if we're viewing this thread and message isn't already there
        if (prev.length > 0 && prev[0]?.thread_id === threadId) {
          const exists = prev.some(m => m.id === message.id);
          if (!exists) {
            return [...prev, message];
          }
        }
        return prev;
      });
      // Update thread list to show new message preview
      setStaffMessageThreads(prev => prev.map(thread => {
        if (thread.id === threadId) {
          return {
            ...thread,
            last_message_at: message.created_at,
            last_message_preview: message.message?.substring(0, 100) || '',
            unread_count_staff: (thread.unread_count_staff || 0) + 1
          };
        }
        return thread;
      }));
      // Increment unread count
      setUnreadStaffMessagesCount(prev => prev + 1);
    },
    onNotification: (notification) => {
      void 0 && console.log('[WebSocket] Notification received:', notification);
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },
    onTaskUpdate: (action, taskData) => {
      void 0 && console.log('[WebSocket] Task update:', action, taskData?.id);
      if (taskData?.id && taskData?.status) {
        // Optimistically update task status in both lists
        const updateTaskInList = (list) =>
          list.map(t => t.id === taskData.id ? { ...t, status: taskData.status, updated_at: new Date().toISOString() } : t);
        
        setTasks(prev => updateTaskInList(prev));
        setTasksAssignedByMe(prev => updateTaskInList(prev));
        
        // Show toast for auto-completed tasks
        if (action === 'auto_completed') {
          const task = tasks.find(t => t.id === taskData.id) || tasksAssignedByMe.find(t => t.id === taskData.id);
          if (task) {
            toast.success(`Task auto-completed: ${task.title}`);
          }
        }
      }
    },
    onAppointmentUpdate: (action, appointmentData) => {
      void 0 && console.log('[WebSocket] Appointment update:', action, appointmentData?.id);
      if (action === 'created' || action === 'created_batch') {
        // Refresh appointments list
        setAppointments(prev => {
          if (action === 'created_batch' && appointmentData?.appointments) {
            return [...prev, ...appointmentData.appointments];
          } else if (appointmentData?.id) {
            // Avoid duplicates
            const exists = prev.some(a => a.id === appointmentData.id);
            if (!exists) {
              return [...prev, appointmentData];
            }
          }
          return prev;
        });
      } else if (action === 'updated' && appointmentData?.id) {
        setAppointments(prev => prev.map(apt => 
          apt.id === appointmentData.id ? { ...apt, ...appointmentData } : apt
        ));
      } else if (action === 'deleted' && appointmentData?.id) {
        setAppointments(prev => prev.filter(apt => apt.id !== appointmentData.id));
      }
    },
    onUrgentWaitlistCleared: (clientId) => {
      void 0 && console.log('[WebSocket] Urgent waitlist cleared for client:', clientId);
      // Remove urgent_waitlist notifications for this client from local state
      setNotifications(prev => prev.filter(n => !(n.type === 'urgent_waitlist' && n.client_id === clientId)));
      // Update unread count (decrement for any unread urgent notifications for this client)
      setUnreadCount(prev => Math.max(0, prev - 1));
      // Trigger a refetch of notifications and data
      setUrgentWaitlistClearedTrigger(prev => prev + 1);
    },
    onStaffAnnouncement: (announcement) => {
      void 0 && console.log('[WebSocket] New staff announcement:', announcement.subject);
      setStaffAnnouncements(prev => [announcement, ...prev]);
      setUnreadAnnouncementsCount(prev => prev + 1);
      toast.info(`New announcement: ${announcement.subject}`, { duration: 5000 });
    }
  }), []);

  // WebSocket connection for real-time updates
  const { isConnected: wsConnected } = useWebSocket(
    sessionStorage.getItem('token'),
    wsHandlers,
    !!user // Only connect when user is logged in
  );

  // Log WebSocket connection status changes (for debugging)
  useEffect(() => {
    if (user) {
      void 0 && console.log(`[WebSocket] Connection status: ${wsConnected ? '✅ Connected' : '❌ Disconnected'}`);
    }
  }, [wsConnected, user]);

  // Auto-refresh appointments when tab becomes visible (to catch updates from other sessions)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && user) {
        void 0 && console.log('[Tab Focus] Refreshing appointments...');
        try {
          // Feb 2026: pass an explicit -12 / +3-month window centered on
          // Tab Focus refresh — 6-week window around calendarDate
          // (matches the lazy refresh strategy in `refreshAppointments`).
          // Was previously a 15-month preload which was the dominant
          // memory + render cascade on tab returns.
          const anchor = calendarDate || new Date();
          const winStart = new Date(anchor);
          winStart.setDate(winStart.getDate() - 21);
          const winEnd = new Date(anchor);
          winEnd.setDate(winEnd.getDate() + 21);
          const fmt = (d) => d.toISOString().split('T')[0];
          const response = await axios.get(
            `${API}/appointments?summary=true&start_date=${fmt(winStart)}&end_date=${fmt(winEnd)}`,
          );
          setAppointments(response.data || []);
          void 0 && console.log('[Tab Focus] Appointments refreshed');
        } catch (error) {
          console.error('[Tab Focus] Failed to refresh appointments:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, calendarDate]);

  // Listen for session-expired events from autosave
  useEffect(() => {
    const handleSessionExpired = (event) => {
      console.error('[Session] Session expired detected:', event.detail);
      toast.error(
        <div>
          <strong>Session Expired</strong>
          <p className="text-sm mt-1">Your session has expired. Please save your work locally and log in again.</p>
        </div>,
        { duration: 10000 }
      );
      // Redirect to login after a short delay
      setTimeout(() => {
        window.location.href = '/login?reason=session_expired';
      }, 3000);
    };
    
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, []);

  // Claims Workshop state - MOVED TO ClaimsTab.jsx

  // ERA Management state - MOVED TO ERATab.jsx


  const [threadToForward, setThreadToForward] = useState(null); // Track which thread to forward
  const [forwardNote, setForwardNote] = useState(''); // Note to include when forwarding
  const [showStaffNewMessageDialog, setShowStaffNewMessageDialog] = useState(false);
  const [staffNewMessageClientId, setStaffNewMessageClientId] = useState('');
  const [staffNewMessageSubject, setStaffNewMessageSubject] = useState('');
  const [staffNewMessageText, setStaffNewMessageText] = useState('');
  const [staffNewMessageTopic, setStaffNewMessageTopic] = useState('general');
  const [sendingStaffNewMessage, setSendingStaffNewMessage] = useState(false);
  
  // Staff-to-staff messaging
  const [showStaffToStaffMessageDialog, setShowStaffToStaffMessageDialog] = useState(false);
  const [staffToStaffRecipientId, setStaffToStaffRecipientId] = useState('');
  const [staffToStaffRecipientName, setStaffToStaffRecipientName] = useState('');
  const [staffToStaffSubject, setStaffToStaffSubject] = useState('');
  const [staffToStaffMessage, setStaffToStaffMessage] = useState('');
  const [sendingStaffToStaffMessage, setSendingStaffToStaffMessage] = useState(false);

  // Mar 2026 — V2 → legacy hand-off for the Staff Directory's
  // "Send Message" action.  V2 sets `v2_pending_staff_message` in
  // sessionStorage then navigates to `/?tab=messaging`; this effect
  // unpacks it and opens the existing dialog with the recipient
  // pre-filled.  Will be removed when the messaging dialog itself is
  // migrated to a V2 native mount.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('v2_pending_staff_message');
      if (!raw) return;
      sessionStorage.removeItem('v2_pending_staff_message');
      const payload = JSON.parse(raw);
      if (payload && payload.recipient_id) {
        setStaffToStaffRecipientId(payload.recipient_id);
        setStaffToStaffRecipientName(payload.recipient_name || '');
        setStaffToStaffSubject('');
        setStaffToStaffMessage('');
        setShowStaffToStaffMessageDialog(true);
      }
    } catch {}
  }, []);
  
  // Message from chart
  const [showMessageFromChartDialog, setShowMessageFromChartDialog] = useState(false);
  const [messageFromChartClientId, setMessageFromChartClientId] = useState('');
  const [messageFromChartClientName, setMessageFromChartClientName] = useState('');
  const [accessibleClients, setAccessibleClients] = useState([]);
  
  // New messaging features
  const [messageRecipientType, setMessageRecipientType] = useState('client'); // 'client', 'staff', or 'all_staff'
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [showBroadcastConfirmation, setShowBroadcastConfirmation] = useState(false);
  const [broadcastClientCount, setBroadcastClientCount] = useState(0);
  const [showRecallDialog, setShowRecallDialog] = useState(false);
  const [recallThreadId, setRecallThreadId] = useState(null);
  const [recallReason, setRecallReason] = useState('');
  const [recallingMessage, setRecallingMessage] = useState(false);
  
  // Staff announcements
  const [staffAnnouncements, setStaffAnnouncements] = useState([]);
  const [showStaffAnnouncementsDialog, setShowStaffAnnouncementsDialog] = useState(false);
  const [unreadAnnouncementsCount, setUnreadAnnouncementsCount] = useState(0);
  
  // Archived messages
  const [showArchivedMessagesDialog, setShowArchivedMessagesDialog] = useState(false);
  const [archivedSearchClientName, setArchivedSearchClientName] = useState('');
  const [archivedSearchStaffName, setArchivedSearchStaffName] = useState('');
  const [archivedSearchStartDate, setArchivedSearchStartDate] = useState('');
  const [archivedSearchEndDate, setArchivedSearchEndDate] = useState('');
  const [archivedSearchKeyword, setArchivedSearchKeyword] = useState('');
  const [archivedMessageResults, setArchivedMessageResults] = useState([]);
  const [searchingArchived, setSearchingArchived] = useState(false);
  const [selectedArchivedThread, setSelectedArchivedThread] = useState(null);
  const [archivedThreadMessages, setArchivedThreadMessages] = useState([]);
  
  // Drag and Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance before activation
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle dragging fields (including between sections)
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setActiveFormFields((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };
  
  // Enhanced drag handler for row-aware drag and drop
  const handleRowDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) return;
    
    const activeId = active.id;
    const overId = over.id;
    
    if (activeId === overId) return;
    
    // Check if dropping onto a row (row IDs start with "row_")
    const isDropOnRow = String(overId).startsWith('row_');
    
    setActiveFormFields((items) => {
      const activeIndex = items.findIndex((item) => item.id === activeId);
      
      if (activeIndex === -1) return items;
      
      if (isDropOnRow) {
        // Dropping onto a row - need to find where in the flat array this row's fields are
        const sections = groupFieldsIntoSectionsAndRows(items);
        
        // Find which row we're dropping into
        let targetRowFields = [];
        let insertAfterIndex = -1;
        
        for (const section of sections) {
          for (const row of section.rowGroups || []) {
            if (row.id === overId) {
              targetRowFields = row.fields;
              // Insert at the end of this row's fields
              if (targetRowFields.length > 0) {
                const lastFieldInRow = targetRowFields[targetRowFields.length - 1];
                insertAfterIndex = items.findIndex(item => item.id === lastFieldInRow.id);
              } else {
                // Empty row - find where this row would be in the sequence
                // For now, just keep the field where it is
                insertAfterIndex = activeIndex;
              }
              break;
            }
          }
        }
        
        if (insertAfterIndex !== -1 && insertAfterIndex !== activeIndex) {
          // Move the field
          const newItems = [...items];
          const [movedField] = newItems.splice(activeIndex, 1);
          
          // Adjust insert index if we removed from before the insert point
          const adjustedIndex = activeIndex < insertAfterIndex ? insertAfterIndex : insertAfterIndex + 1;
          newItems.splice(adjustedIndex, 0, movedField);
          
          // Apply smart auto-split to the target row
          // First, recalculate sections with the new arrangement
          const newSections = groupFieldsIntoSectionsAndRows(newItems);
          
          // Find the row containing our moved field and apply smart split
          for (const section of newSections) {
            for (const row of section.rowGroups || []) {
              const fieldInRow = row.fields.find(f => f.id === movedField.id);
              if (fieldInRow && row.fields.length > 1) {
                // Apply smart split to this row
                const smartWidths = calculateSmartWidths(row.fields.length);
                row.fields.forEach((f, idx) => {
                  const fieldIdx = newItems.findIndex(item => item.id === f.id);
                  if (fieldIdx !== -1) {
                    newItems[fieldIdx] = { ...newItems[fieldIdx], width: smartWidths[idx] };
                  }
                });
                toast.success(`Field moved! Row adjusted to ${smartWidths.join('/')}% layout`);
                break;
              }
            }
          }
          
          return newItems;
        }
      } else {
        // Dropping onto another field - standard reorder
        const overIndex = items.findIndex((item) => item.id === overId);
        if (overIndex !== -1) {
          return arrayMove(items, activeIndex, overIndex);
        }
      }
      
      return items;
    });
  };

  // Group fields by sections for the form builder UI
  const groupFieldsBySections = (fields) => {
    const sections = [];
    let currentSection = null;
    
    fields.forEach((field) => {
      if (field.type === 'section_header') {
        // Start a new section
        currentSection = {
          header: field,
          fields: []
        };
        sections.push(currentSection);
      } else {
        // Add field to current section or create a default section
        if (!currentSection) {
          currentSection = {
            header: { id: 'default', type: 'section_header', label: 'Fields' },
            fields: []
          };
          sections.push(currentSection);
        }
        currentSection.fields.push(field);
      }
    });
    
    return sections;
  };
  
  // Enhanced grouping: Sections → Row Groups → Fields
  // This is used for display in form builder and rendering
  const groupFieldsIntoSectionsAndRows = (fields) => {
    const sections = [];
    let currentSection = null;
    
    fields.forEach((field) => {
      if (field.type === 'section_header') {
        // Start a new section with empty rowGroups
        currentSection = {
          header: field,
          fields: [], // Keep for compatibility
          rowGroups: []
        };
        sections.push(currentSection);
      } else {
        // Add field to current section or create a default section
        if (!currentSection) {
          currentSection = {
            header: { id: 'default', type: 'section_header', label: 'Fields' },
            fields: [],
            rowGroups: []
          };
          sections.push(currentSection);
        }
        currentSection.fields.push(field);
      }
    });
    
    // Now convert each section's fields into rowGroups
    sections.forEach(section => {
      section.rowGroups = migrateFormFieldsToRowGroups(section.fields);
    });
    
    return sections;
  };
  
  // Flatten sections with rowGroups back to flat array for saving
  const flattenSectionsWithRowGroups = (sections) => {
    const flattened = [];
    sections.forEach(section => {
      if (section.header.id !== 'default') {
        flattened.push(section.header);
      }
      // Flatten rowGroups to fields
      if (section.rowGroups) {
        section.rowGroups.forEach(row => {
          flattened.push(...(row.fields || []));
        });
      } else {
        flattened.push(...(section.fields || []));
      }
    });
    return flattened;
  };

  // Flatten sections back to flat array for saving
  const flattenSections = (sections) => {
    const flattened = [];
    sections.forEach(section => {
      if (section.header.id !== 'default') {
        flattened.push(section.header);
      }
      flattened.push(...section.fields);
    });
    return flattened;
  };

  // ============ ROW GROUP HELPERS ============
  
  // Migrate old section structure (fields[]) to new structure (rowGroups[])
  const migrateSectionToRowGroups = (section) => {
    // If section already has rowGroups, return as-is
    if (section.rowGroups && section.rowGroups.length > 0) {
      return section;
    }
    
    // Convert flat fields array to rowGroups based on width
    const fields = section.fields || [];
    if (fields.length === 0) {
      return {
        ...section,
        rowGroups: [{ id: `row_empty_${section.header?.id || 'default'}`, fields: [] }]
      };
    }
    
    // Helper to create stable row ID from fields
    const createRowId = (rowFields) => {
      if (rowFields.length === 0) return `row_empty_${section.header?.id || 'default'}`;
      return `row_${rowFields.map(f => f.id).join('_')}`;
    };
    
    // Group fields into rows based on cumulative width
    const rowGroups = [];
    let currentRowFields = [];
    let currentRowWidth = 0;
    
    fields.forEach((field, index) => {
      const fieldWidth = parseInt(field.width || '100');
      
      // Full-width fields always start a new row
      if (fieldWidth === 100) {
        // Save current row if it has fields
        if (currentRowFields.length > 0) {
          rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
        }
        // Add full-width field as its own row
        rowGroups.push({ id: createRowId([field]), fields: [field] });
        // Start new row
        currentRowFields = [];
        currentRowWidth = 0;
      } else if (currentRowWidth + fieldWidth > 100) {
        // Would exceed 100%, start new row
        if (currentRowFields.length > 0) {
          rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
        }
        currentRowFields = [field];
        currentRowWidth = fieldWidth;
      } else {
        // Add to current row
        currentRowFields.push(field);
        currentRowWidth += fieldWidth;
      }
    });
    
    // Don't forget the last row
    if (currentRowFields.length > 0) {
      rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
    }
    
    return {
      ...section,
      rowGroups,
      fields: [] // Clear old fields array
    };
  };
  
  // Migrate all sections to use rowGroups
  const migrateSectionsToRowGroups = (sections) => {
    return sections.map(migrateSectionToRowGroups);
  };
  
  // Flatten rowGroups back to fields array (for backward compatibility when saving)
  const flattenRowGroupsToFields = (section) => {
    if (!section.rowGroups) return section.fields || [];
    return section.rowGroups.flatMap(row => row.fields || []);
  };
  
  // Convert rowGroups back to flat form_fields array for saving to DB
  const flattenRowGroupsToFormFields = (rowGroups) => {
    if (!rowGroups) return [];
    return rowGroups.flatMap(row => row.fields || []);
  };
  
  // Smart auto-split: Calculate optimal widths when fields are added to a row
  const calculateSmartWidths = (fieldCount) => {
    switch (fieldCount) {
      case 1: return ['100'];
      case 2: return ['50', '50'];
      case 3: return ['33', '33', '33'];
      case 4: return ['25', '25', '25', '25'];
      default: return Array(fieldCount).fill(Math.floor(100 / fieldCount).toString());
    }
  };
  
  // Apply smart auto-split to a row's fields
  const applySmartSplitToRow = (rowFields) => {
    if (!rowFields || rowFields.length === 0) return rowFields;
    const widths = calculateSmartWidths(rowFields.length);
    return rowFields.map((field, index) => ({
      ...field,
      width: widths[index] || '100'
    }));
  };
  
  // Add a field to a specific row with smart auto-split
  const addFieldToRow = (newField, sectionIndex, rowIndex) => {
    // Get current sections with row groups
    const sectionsWithRows = groupFieldsIntoSectionsAndRows(activeFormFields);
    
    if (!sectionsWithRows[sectionIndex] || !sectionsWithRows[sectionIndex].rowGroups[rowIndex]) {
      // Fallback: just add to activeFormFields
      setActiveFormFields(prev => [...prev, { ...newField, width: '100' }]);
      return;
    }
    
    // Get the target row
    const targetRow = sectionsWithRows[sectionIndex].rowGroups[rowIndex];
    const newRowFields = [...targetRow.fields, newField];
    
    // Apply smart split
    const updatedFields = applySmartSplitToRow(newRowFields);
    
    // Rebuild the flat array with updated widths
    const newActiveFields = [];
    sectionsWithRows.forEach((section, sIdx) => {
      // Add section header if not default
      if (section.header.id !== 'default') {
        newActiveFields.push(section.header);
      }
      // Add fields from each row
      section.rowGroups.forEach((row, rIdx) => {
        if (sIdx === sectionIndex && rIdx === rowIndex) {
          // This is the target row - use updated fields
          newActiveFields.push(...updatedFields);
        } else {
          newActiveFields.push(...row.fields);
        }
      });
    });
    
    setActiveFormFields(newActiveFields);
    toast.success(`Added field to row (auto-adjusted widths to ${calculateSmartWidths(newRowFields.length).join('/')}%)`);
  };
  
  // Add a new empty row after a specific row
  const addNewRowAfter = (sectionIndex, rowIndex) => {
    // This doesn't need to modify data structure - it's handled by adding a 100% field
    // The migration function will put it in its own row
    toast.info('Add a field to create a new row');
  };
  
  // Get all fields from sections with rowGroups (flattened)
  const getAllFieldsFromSections = (sections) => {
    return sections.flatMap(section => flattenRowGroupsToFields(section));
  };
  
  // Add a new row group to a section
  const addRowGroupToSection = (sectionId) => {
    setActiveFormSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        const newRowGroup = { id: `row_${Date.now()}`, fields: [] };
        return {
          ...section,
          rowGroups: [...(section.rowGroups || []), newRowGroup]
        };
      }
      return section;
    }));
  };
  
  // Remove a row group from a section
  const removeRowGroup = (sectionId, rowGroupId) => {
    setActiveFormSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        const newRowGroups = (section.rowGroups || []).filter(row => row.id !== rowGroupId);
        // Ensure at least one row group exists
        if (newRowGroups.length === 0) {
          newRowGroups.push({ id: `row_${Date.now()}`, fields: [] });
        }
        return { ...section, rowGroups: newRowGroups };
      }
      return section;
    }));
  };
  
  // Move a field to a different row group
  const moveFieldToRowGroup = (fieldId, fromSectionId, fromRowId, toSectionId, toRowId) => {
    setActiveFormSections(prev => {
      let fieldToMove = null;
      
      // First pass: find and remove the field
      const sectionsWithoutField = prev.map(section => {
        if (section.id === fromSectionId) {
          return {
            ...section,
            rowGroups: (section.rowGroups || []).map(row => {
              if (row.id === fromRowId) {
                const field = row.fields.find(f => f.id === fieldId);
                if (field) fieldToMove = field;
                return {
                  ...row,
                  fields: row.fields.filter(f => f.id !== fieldId)
                };
              }
              return row;
            })
          };
        }
        return section;
      });
      
      if (!fieldToMove) return prev;
      
      // Second pass: add field to target row
      return sectionsWithoutField.map(section => {
        if (section.id === toSectionId) {
          return {
            ...section,
            rowGroups: (section.rowGroups || []).map(row => {
              if (row.id === toRowId) {
                return {
                  ...row,
                  fields: [...row.fields, fieldToMove]
                };
              }
              return row;
            })
          };
        }
        return section;
      });
    });
  };
  
  // Render fields in a row group with proper grid layout
  const renderRowGroupFields = (rowGroup, renderFieldFn, isEditable = false) => {
    if (!rowGroup || !rowGroup.fields || rowGroup.fields.length === 0) {
      return null;
    }
    
    // Calculate grid columns based on field widths
    // Using CSS Grid with 12-column system for precise alignment
    const getGridColSpan = (width) => {
      const w = parseInt(width || '100');
      if (w >= 100) return 12;
      if (w >= 75) return 9;   // 75% = 9 columns
      if (w >= 66) return 8;   // 66% = 8 columns
      if (w >= 50) return 6;
      if (w >= 33) return 4;
      if (w >= 25) return 3;
      return Math.round(w / 100 * 12) || 12;
    };
    
    return (
      <div 
        key={rowGroup.id} 
        className="grid grid-cols-12 gap-3 mb-3"
        data-row-id={rowGroup.id}
      >
        {rowGroup.fields.map((field, index) => {
          const colSpan = getGridColSpan(field.width);
          return (
            <div 
              key={field.id} 
              className={`col-span-12 md:col-span-${colSpan}`}
              style={{ gridColumn: `span ${colSpan} / span ${colSpan}` }}
            >
              {renderFieldFn(field, index, rowGroup.fields)}
            </div>
          );
        })}
      </div>
    );
  };
  
  // ============ END ROW GROUP HELPERS ============

  // Handle dragging entire sections
  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const sections = groupFieldsBySections(activeFormFields);
      const oldIndex = sections.findIndex((section) => section.header.id === active.id);
      const newIndex = sections.findIndex((section) => section.header.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedSections = arrayMove(sections, oldIndex, newIndex);
        setActiveFormFields(flattenSections(reorderedSections));
      }
    }
  };

  // Helper function to format date for datetime-local input
  const formatLocalDateTime = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  
  // Note: clients state is now in ClientsContext
  // Note: appointments state is now in AppointmentsContext
  // Note: notes and myTodoNotes are now in NotesContext
  
  // Note: memoized client lists (pendingIntakeClients, etc.) are now in ClientsContext
  
  // Note: clientLabels and selectedLabelFilter are now in ClientsContext
  const [showClientLabelsDialog, setShowClientLabelsDialog] = useState(false);
  const [newLabelData, setNewLabelData] = useState({ name: '', category: 'general', color: '#3b82f6' });
  
  // Bulk Communications State
  const [showBulkCommunicationsDialog, setShowBulkCommunicationsDialog] = useState(false);
  const [showBulkCommHistoryDialog, setShowBulkCommHistoryDialog] = useState(false);
  const [showMessageTemplatesDialog, setShowMessageTemplatesDialog] = useState(false);
  const [bulkCommFilters, setBulkCommFilters] = useState({
    statuses: ['active'],
    provider_ids: [],
    service_type_ids: [],
    label_ids: [],
    last_appointment_from: '',
    last_appointment_to: ''
  });
  const [bulkCommSubject, setBulkCommSubject] = useState('');
  const [bulkCommMessage, setBulkCommMessage] = useState('');
  const [bulkCommSendEmail, setBulkCommSendEmail] = useState(false);
  const [bulkCommSendSms, setBulkCommSendSms] = useState(false);
  const [bulkCommPreview, setBulkCommPreview] = useState(null);
  const [bulkCommHistory, setBulkCommHistory] = useState([]);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({ name: '', subject: '', message: '' });
  const [sendingBulkComm, setSendingBulkComm] = useState(false);
  const [loadingBulkCommPreview, setLoadingBulkCommPreview] = useState(false);
  
  // Documentation Deadlines State
  const [incompleteDocumentation, setIncompleteDocumentation] = useState([]);
  const [loadingDocDeadlines, setLoadingDocDeadlines] = useState(false);
  
  // Expiring Releases State
  const [expiringReleases, setExpiringReleases] = useState([]);
  const [loadingExpiringReleases, setLoadingExpiringReleases] = useState(false);
  
  // Task Assignment System State
  // Note: tasks and tasksAssignedByMe are now in UIContext (fetched on-demand when Tasks tab opens)
  const [treatmentPlansDueForReview, setTreatmentPlansDueForReview] = useState([]);
  const [myDraftPlans, setMyDraftPlans] = useState([]);
  const [teamDraftPlans, setTeamDraftPlans] = useState([]);
  const [isTeamOversightExpanded, setIsTeamOversightExpanded] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  // Feb 2026 — Completion-note dialog state.  When ``completionDialogTask``
  // is set, a small Dialog pops up letting the assignee leave a note
  // ("scheduled for next Tuesday") that flows to the assigner's
  // confirmation notification.  Null when not in flight.
  const [completionDialogTask, setCompletionDialogTask] = useState(null);
  const [completionDialogNote, setCompletionDialogNote] = useState('');
  const [completionDialogSubmitting, setCompletionDialogSubmitting] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Feb 2026 — Global event bridges for the CreateTaskFieldWidget on
  // signed notes.  The widget itself can't reach App-level state
  // (setSelectedTask, setEditingAppointment, setActiveTab), so it
  // dispatches typed window events and we translate them here:
  //   1. ``openTaskDrawer({ task_id })`` → fetch + open the same
  //      Task dialog the Tasks list uses (line ~59741).
  //   2. ``rescheduleFromTask({ appointment_id, task_id })`` → load
  //      the linked appointment, switch to the calendar tab, and
  //      open the appointment editor so the user can reschedule.
  useEffect(() => {
    const handleOpenTaskDrawer = async (e) => {
      try {
        const taskId = e?.detail?.task_id;
        if (!taskId) return;
        const r = await axios.get(`${API}/tasks/${taskId}`);
        if (r?.data) setSelectedTask(r.data);
      } catch (err) {
        toast.error('Could not load that task.');
      }
    };
    const handleRescheduleFromTask = async (e) => {
      try {
        const apptId = e?.detail?.appointment_id;
        const taskId = e?.detail?.task_id;
        // Mar 2026 — many reschedule tasks have no appointment_id
        // (legacy create_task path didn't mirror it).  Fall back to
        // a CREATE-mode appointment dialog pre-filled from the task
        // (client_id, target date, provider notes banner).
        let task = null;
        if (taskId) {
          try {
            const tr = await axios.get(`${API}/tasks/${taskId}`);
            task = tr?.data || null;
          } catch (_err) { /* non-fatal — fall back to plain create */ }
        }
        // Path A: task points at a real appointment → EDIT mode.
        if (apptId) {
          try {
            const r = await axios.get(`${API}/appointments/${apptId}`);
            if (r?.data) {
              setEditingAppointment(r.data);
              setAppointmentFormData((prev) => ({
                ...(prev || {}),
                ...(r.data || {}),
                _reschedule_context: task ? {
                  task_id: task.id,
                  action_type: task.action_type,
                  reschedule_in_label: task.reschedule_in_label,
                  target_appointment_date: task.target_appointment_date,
                  source_appointment_date: task.source_appointment_date,
                  description: task.description,
                  source_note_snippet: task.source_note_snippet,
                  client_phone: task.client_phone,
                } : undefined,
              }));
              setShowAppointmentForm(true);
              setActiveTab('calendar');
              return;
            }
          } catch (_err) { /* fall through to create-mode */ }
        }
        // Path B: no appointment yet — open CREATE-mode pre-filled
        // from the task.  Requires a client_id at minimum.
        if (!task || !task.client_id) {
          toast.error('Could not open the appointment dialog — task has no linked client.');
          return;
        }
        const targetDate = task.target_appointment_date
          ? String(task.target_appointment_date).slice(0, 10)
          : null;
        const baseStart = targetDate ? `${targetDate}T10:00` : '';
        const baseEnd = targetDate ? `${targetDate}T11:00` : '';
        setEditingAppointment(null);
        setAppointmentFormData({
          client_id: task.client_id,
          appointment_type: 'client_session',
          start_time: baseStart,
          end_time: baseEnd,
          location: '',
          service_type: '',
          provider_id: '',
          _reschedule_context: {
            task_id: task.id,
            action_type: task.action_type,
            reschedule_in_label: task.reschedule_in_label,
            target_appointment_date: task.target_appointment_date,
            source_appointment_date: task.source_appointment_date,
            description: task.description,
            source_note_snippet: task.source_note_snippet,
            client_phone: task.client_phone,
          },
        });
        setShowAppointmentForm(true);
        setActiveTab('calendar');
      } catch (err) {
        toast.error('Could not load that appointment.');
      }
    };
    window.addEventListener('openTaskDrawer', handleOpenTaskDrawer);
    window.addEventListener('rescheduleFromTask', handleRescheduleFromTask);
    return () => {
      window.removeEventListener('openTaskDrawer', handleOpenTaskDrawer);
      window.removeEventListener('rescheduleFromTask', handleRescheduleFromTask);
    };
    // eslint-disable-next-line
  }, []);
  
  // System health tracking for debug overlay
  const [dataHealth, setDataHealth] = useState({
    staticData: 'pending',   // 'pending' | 'loading' | 'success' | 'error'
    dynamicData: 'pending',  // 'pending' | 'loading' | 'success' | 'error'
    retryCount: 0,
    failedEndpoints: [],
    staticDetails: {},       // { users: 10, formTemplates: 5, ... }
    dynamicDetails: {}       // { clients: 100, appointments: 50, ... }
  });
  
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    priority: 'normal',
    assigned_to: '',
    client_id: '',
    due_date: ''
  });
  const [taskComment, setTaskComment] = useState('');
  const [taskClientSearch, setTaskClientSearch] = useState('');
  const [taskClientResults, setTaskClientResults] = useState([]);
  const [taskClientSearching, setTaskClientSearching] = useState(false);
  
  // Task edit mode state (for task creator to edit details)
  const [isEditingTask, setIsEditingTask] = useState(false);
  const [taskEditData, setTaskEditData] = useState({});
  
  // Email verification state for tasks
  const [emailVerifyMode, setEmailVerifyMode] = useState('verify'); // 'verify', 'update', 'invalid'
  const [emailVerifyNewEmail, setEmailVerifyNewEmail] = useState('');
  
  // Collapsible section states
  const [isTaskManagementExpanded, setIsTaskManagementExpanded] = useState(true);
  const [isMyTodoExpanded, setIsMyTodoExpanded] = useState(true);
  
  // Completed tasks toggle
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [tasksRefreshing, setTasksRefreshing] = useState(false);
  
  // Unified task filter states
  const [taskTypeFilter, setTaskTypeFilter] = useState('all'); // 'all', 'clinical', 'administrative', 'billing', 'compliance'
  const [taskUrgencyFilter, setTaskUrgencyFilter] = useState('all'); // 'all', 'overdue', 'due_today', 'due_this_week', 'upcoming'
  const [taskSourceFilter, setTaskSourceFilter] = useState('all'); // 'all', 'manual', 'automated'
  const [taskOwnershipFilter, setTaskOwnershipFilter] = useState('all'); // 'all', 'assigned_to_me', 'i_assigned'
  
  // Supervisor task filter (for supervisors to filter their own work vs supervisee work)
  const [supervisorTaskFilter, setSupervisorTaskFilter] = useState('all'); // 'all', 'my_work', 'supervisee_work'
  const [taskSortBy, setTaskSortBy] = useState('newest'); // 'newest', 'oldest', 'priority', 'provider', 'date', 'type'
  const [taskViewMode, setTaskViewMode] = useState('card'); // 'card' or 'list'
  const [tasksPerPage, setTasksPerPage] = useState(20); // 10, 20, 50, 100, or 'all'
  const [tasksCurrentPage, setTasksCurrentPage] = useState(1);
  
  // Rules management state
  const [cptConflictRules, setCptConflictRules] = useState([]);
  const [insuranceCptRestrictions, setInsuranceCptRestrictions] = useState([]);
  const [showRulesManagementDialog, setShowRulesManagementDialog] = useState(false);
  const [showConflictRuleDialog, setShowConflictRuleDialog] = useState(false);
  const [showRestrictionDialog, setShowRestrictionDialog] = useState(false);
  const [editingConflictRule, setEditingConflictRule] = useState(null);
  const [editingRestriction, setEditingRestriction] = useState(null);
  const [conflictRuleForm, setConflictRuleForm] = useState({
    rule_name: '',
    cpt_codes: [],
    description: '',
    can_override: false,
    max_per_day: 1,
    is_active: true
  });
  const [restrictionForm, setRestrictionForm] = useState({
    insurance_ids: [],
    specific_plans: [],
    cpt_codes: [],
    description: '',
    allow_direct_billing: false,
    allow_secondary_fallback: true,
    is_active: true
  });

  // Jun 2026 — CPT Billing Rules (add-on / override / replace).  These
  // are payer × CPT × credential-aware rules that mutate the claim
  // line at submission time (e.g., "add U1 modifier when 90837 is
  // billed to Medicaid as a nurse").  Backend model: `CPTBillingRule`;
  // engine: `core/billing_rules_engine.py`; endpoints: `/api/billing/rules/cpt`.
  const [cptBillingRules, setCptBillingRules] = useState([]);
  const [showCptBillingRuleDialog, setShowCptBillingRuleDialog] = useState(false);
  const [editingCptBillingRule, setEditingCptBillingRule] = useState(null);
  const [cptBillingRuleForm, setCptBillingRuleForm] = useState({
    rule_name: '',
    description: '',
    cpt_code: '',
    // Feb 2026 — Per-variant targeting (multi-select).  When this
    // array is non-empty, the rule applies ONLY to the listed CPT
    // variants (by row id).  When empty, the rule applies to ALL
    // variants sharing ``cpt_code`` (legacy code-level matching).
    cpt_code_ids: [],
    insurance_name: '',
    provider_credential: '',
    action_type: 'modify',
    add_modifiers: [],
    add_modifiers_position: 'append',  // Feb 2026 — "append" | "prepend"
    remove_modifiers: [],
    override_modifiers: [],
    replacement_cpt_code: '',
    block_reason: '',
    priority: 0,
    notes: '',
    is_active: true,
  });
  
  // Note: clientDocuments is now in ClientsContext
  const [alerts, setAlerts] = useState([]);
  const [unreadAlertCounts, setUnreadAlertCounts] = useState({});
  const [showUploadDocument, setShowUploadDocument] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentDescription, setDocumentDescription] = useState('');
  const [documentFormType, setDocumentFormType] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]); // Store multiple selected files before upload
  const [duplicateFileWarnings, setDuplicateFileWarnings] = useState([]); // Track files that already exist
  const [suggestedCategory, setSuggestedCategory] = useState(null); // Auto-suggested category from filename
  const [editingDocumentFormType, setEditingDocumentFormType] = useState(null); // Document being edited
  const [newDocumentFormType, setNewDocumentFormType] = useState(''); // New form type value

  // Note: users, providers, cptCodes, posCodes, availableStatuses, formTemplates, formTypes, intakeBundles
  // are now in StaticDataContext (accessed via useStaticData hook at top of Dashboard)
  
  // Note: clientGroups is now in ClientsContext
  const [serviceRequestTemplates, setServiceRequestTemplates] = useState([]);
  const [showArchivedTemplates, setShowArchivedTemplates] = useState('active'); // 'active', 'archived', or 'all'
  const [formTemplateSearchQuery, setFormTemplateSearchQuery] = useState('');
  const [formTemplateSortBy, setFormTemplateSortBy] = useState('alphabetical'); // 'alphabetical', 'form_type', 'newest', 'oldest'
  const [formTemplateFilterType, setFormTemplateFilterType] = useState('all'); // 'all' or specific form type id
  const [formTemplateItemsPerPage, setFormTemplateItemsPerPage] = useState(10);
  const [formTemplateCurrentPage, setFormTemplateCurrentPage] = useState(1);
  const [showManageFormTypes, setShowManageFormTypes] = useState(false);
  const [formTypeFormData, setFormTypeFormData] = useState({ name: '', description: '', sort_order: 0 });
  const [editingFormType, setEditingFormType] = useState(null);
  const [showManageFormTypesList, setShowManageFormTypesList] = useState(false);
  const [showFormsManagementDialog, setShowFormsManagementDialog] = useState(false);
  const [formsManagementTab, setFormsManagementTab] = useState('templates');
  
  // Intake Bundle state
  // Note: intakeBundles is now in StaticDataContext
  const [showManageIntakeBundles, setShowManageIntakeBundles] = useState(false);
  const [editingIntakeBundle, setEditingIntakeBundle] = useState(null);
  const [intakeBundleFormData, setIntakeBundleFormData] = useState({
    name: '',
    service_type_ids: [],
    trigger_cpt_code_ids: [],  // Changed to use CPT code IDs instead of code strings
    age_min: null,
    age_max: null,
    documents: []
  });
  // Which Document #N picker popover is currently open (for the
  // searchable + alphabetized template selector inside the Manage
  // Intake Bundles dialog — Feb 2026 UX fix).
  const [intakeBundleDocPickerOpenIdx, setIntakeBundleDocPickerOpenIdx] = useState(null);
  const [intakeBundleAsmtPickerOpenIdx, setIntakeBundleAsmtPickerOpenIdx] = useState(null);

  // Note: clientDiagnoses, clientMedications, clientAuthorizations, clientEligibility are now in ClientsContext
  const [showArchivedDiagnoses, setShowArchivedDiagnoses] = useState(false);
  const [showDiscontinuedMeds, setShowDiscontinuedMeds] = useState(false);
  const [expandedAuthId, setExpandedAuthId] = useState(null);
  
  // Version Conflict Resolution - when two tabs/devices edit the same note
  const [versionConflictData, setVersionConflictData] = useState(null); // { noteId, localVersion, serverVersion, localData }
  const [showVersionConflictDialog, setShowVersionConflictDialog] = useState(false);
  const [serverVersionContent, setServerVersionContent] = useState(null); // Fetched server content for comparison
  
  // Authorization Management (system-wide) — local UI state moved to AuthorizationsMgmtTab
  const [allAuthorizations, setAllAuthorizations] = useState([]);
  
  // MDT Chart Reviews State
  // Safely render any value as text — prevents React error #31 (objects as children)
  const safeText = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
    if (typeof val === 'object') {
      if (Array.isArray(val)) return val.map(safeText).join(', ');
      return Object.entries(val).map(([k, v]) => `${k}: ${safeText(v)}`).join(', ');
    }
    return String(val);
  };
  const [mdtPendingActions, setMdtPendingActions] = useState([]);
  
  // Tasks tab view mode toggle (card vs list)
  const [tasksNotesViewMode, setTasksNotesViewMode] = useState('card');
  const [billedNotesViewMode, setBilledNotesViewMode] = useState('card');
  // Tasks tab notes filters
  const [tasksNotesClientSearch, setTasksNotesClientSearch] = useState('');
  const [tasksNotesProviderFilter, setTasksNotesProviderFilter] = useState('all');
  
  // Note: stats is now in UIContext
  const [loading, setLoading] = useState(!dashboardHasLoadedOnce); // Only true on first ever load
  const [lastWaitlistRefresh, setLastWaitlistRefresh] = useState(null);
  
  // Helper function to change dashboard tab AND update URL
  //
  // Feb 2026 — wrapped the state update in `startTransition`.
  // Tab switches in this 87k-line Dashboard force React to walk the
  // entire conditional `{activeTab === 'X' && <…/>}` tree and mount
  // the newly-active tab's children synchronously.  That was the
  // dominant contributor to the 8.8 s INP measurement on a "click a
  // sidebar tab" interaction — the browser couldn't paint until
  // reconciliation finished.
  //
  // With startTransition, React marks the activeTab update as
  // *non-urgent*: the click highlight and any cheap visual feedback
  // paint immediately, then React works on the heavy reconciliation
  // in the background and commits when ready.  The URL update via
  // `navigate(...)` stays outside the transition so the address bar
  // changes instantly (otherwise back-button history would lag).
  const changeTab = useCallback((newTab) => {
    if (!showClientDetail) {
      navigate(`/dashboard?view=${newTab}`, { replace: true });
    }
    startTransition(() => {
      setActiveTab(newTab);
    });
  }, [setActiveTab, showClientDetail, navigate, startTransition]);

  // Helper function to open an appointment AND update URL
  const openAppointment = useCallback((appointment) => {
    if (!appointment) return;
    setEditingAppointment(appointment);
    setShowAppointmentForm(true);
    // Update URL with appointmentId
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('view', 'appointments');
    urlParams.set('appointmentId', appointment.id);
    navigate(`/dashboard?${urlParams.toString()}`, { replace: true });
  }, [setEditingAppointment, setShowAppointmentForm, navigate]);

  // Helper function to close appointment AND update URL
  const closeAppointment = useCallback(() => {
    setEditingAppointment(null);
    setShowAppointmentForm(false);
    // Remove appointmentId from URL but keep view
    navigate(`/dashboard?view=appointments`, { replace: true });
  }, [setEditingAppointment, setShowAppointmentForm, navigate]);

  const [isRefreshingWaitlist, setIsRefreshingWaitlist] = useState(false);
  const [portalSettings, setPortalSettings] = useState(null);

  // Notes for the currently-edited appointment, fetched on demand. This
  // backs the "Create Group Notes" vs "View Group Notes" button logic —
  // the global `notes` state only contains the currently-selected client's
  // notes (via summary=true), so when the user opens a group appointment
  // from the Schedule tab, the per-member group notes aren't in that list
  // and the button would incorrectly say "Create" even when notes exist.
  // appointmentNotesCache + its fetch effect MIGRATED INTO
  // AppointmentScheduleDialog (Stage 10 part 3).

  const [showPortalLinkModal, setShowPortalLinkModal] = useState(false);
  const [showManageClientPortal, setShowManageClientPortal] = useState(false);
  const [generatedPortalLink, setGeneratedPortalLink] = useState(null);
  const [showSendPortalLinkDialog, setShowSendPortalLinkDialog] = useState(false);
  const [portalLinkClientToSend, setPortalLinkClientToSend] = useState(null);
  const [portalLinkCustomMessage, setPortalLinkCustomMessage] = useState('');
  
  // TOS/NPP Editor state
  const [showTosEditor, setShowTosEditor] = useState(false);
  const [showNppEditor, setShowNppEditor] = useState(false);
  const [tosEditorContent, setTosEditorContent] = useState('');
  const [tosEditorVersion, setTosEditorVersion] = useState('');
  const [nppEditorContent, setNppEditorContent] = useState('');
  const [nppEditorVersion, setNppEditorVersion] = useState('');
  const [savingLegalDoc, setSavingLegalDoc] = useState(false);
  
  // Eligibility automation settings
  const [eligibilitySettings, setEligibilitySettings] = useState(null);
  // Per-client 271 fetch state: { [clientId]: { loading: bool, lastChecked: Date|null } }
  const [eligibility271State, setEligibility271State] = useState({});
  // Date-of-service override for eligibility check
  const [eligibilityCheckDOS, setEligibilityCheckDOS] = useState('');
  // Discrepancy review modal
  const [showDiscrepancyReview, setShowDiscrepancyReview] = useState(false);
  const [discrepancyData, setDiscrepancyData] = useState(null); // { checkId, clientId, clientName, discrepancies }

  // ── Client-chart eligibility (extracted to a hook Feb 2026) ──
  // Apply an eligibility result optimistically everywhere in the UI.
  // Hoisted above the hook call so the hook can register it as a
  // post-fetch sync callback (was previously defined inline ~line
  // 16118).  Pure state-mutation — no async work.
  //
  // `insuranceIndex` defaults to 0 (primary). For secondary/tertiary
  // (index 1/2), we DON'T overwrite the primary_eligibility_status /
  // copay / deductible fields — those belong to the primary plan.
  // Instead we patch the matching entry in the client's `insurances[]`
  // array (sorted-by-priority, same mapping as the backend's 270 handler)
  // so the per-card badge on that plan reflects the in-flight / settled
  // status without clobbering primary data.
  const applyEligibilityResult = useCallback((clientId, { status, copayAmounts, deductible, coinsurance, lastChecked }, insuranceIndex = 0) => {
    const now = lastChecked || new Date().toISOString();
    const patchClient = (prev) => {
      if (!prev || prev.id !== clientId) return prev;
      if (!insuranceIndex || insuranceIndex === 0) {
        // Primary: patch the top-level eligibility fields (legacy behavior)
        return {
          ...prev,
          primary_eligibility_status: status,
          last_eligibility_check: now,
          ...(copayAmounts != null && { eligibility_copay_amounts: copayAmounts }),
          ...(deductible != null && { eligibility_deductible: deductible }),
          ...(coinsurance != null && { eligibility_coinsurance: coinsurance }),
        };
      }
      // Secondary / tertiary: patch the matching insurance entry.
      // Match the backend's active-by-priority ordering (routes/eligibility.py:733-736).
      const insurances = Array.isArray(prev.insurances) ? prev.insurances : [];
      const activeSorted = insurances
        .map((ins, origIdx) => ({ ins, origIdx }))
        .filter(x => x.ins?.status === 'active')
        .sort((a, b) => (a.ins.priority || 99) - (b.ins.priority || 99));
      const target = activeSorted[insuranceIndex];
      if (!target) return prev;
      const nextInsurances = insurances.map((ins, origIdx) =>
        origIdx === target.origIdx
          ? { ...ins, eligibility_status: status, eligibility_checked_at: now }
          : ins
      );
      return { ...prev, insurances: nextInsurances };
    };
    setSelectedClient(prev => patchClient(prev));
    setClients(prev => prev.map(c => patchClient(c)));
  }, [setSelectedClient, setClients]);

  const {
    eligibilityDetail,
    eligibilityDetailLoading,
    eligibilityHistory,
    eligibilityDetailMap,
    activeEligibilityCheckId,
    setEligibilityDetail,
    setEligibilityHistory,
    setEligibilityDetailMap,
    setActiveEligibilityCheckId,
    safeEligibilityDetail,
    safeEligibilityHistory,
    fetchEligibilityDetail,
    handleEligibilityCheck,
    resetForClient: resetEligibilityForClient,
  } = useClientEligibility({ selectedClient, applyEligibilityResult });
  
  // Payment reminder automation settings
  const [paymentReminderSettings, setPaymentReminderSettings] = useState(null);
  const [showPaymentReminderSettings, setShowPaymentReminderSettings] = useState(false);
  
  // Service Enrollment Diagnostic state
  const [enrollmentDiagnostic, setEnrollmentDiagnostic] = useState(null);
  const [enrollmentDiagnosticLoading, setEnrollmentDiagnosticLoading] = useState(false);
  const [selectedMigrationServiceType, setSelectedMigrationServiceType] = useState('');
  const [enrollmentMigrationRunning, setEnrollmentMigrationRunning] = useState(false);
  
  // UUID Display Issues Diagnostic state
  const [uuidDiagnostic, setUuidDiagnostic] = useState(null);
  const [uuidDiagnosticLoading, setUuidDiagnosticLoading] = useState(false);
  const [uuidCodeDiagnostic, setUuidCodeDiagnostic] = useState(null);
  const [uuidCodeDiagnosticLoading, setUuidCodeDiagnosticLoading] = useState(false);
  const [uuidFixRunning, setUuidFixRunning] = useState(false);
  
  // Fetch payment reminder settings
  const fetchPaymentReminderSettings = useCallback(async () => {
    if (isSuperAdmin(user) || user?.is_billing_manager) {
      try {
        const response = await axios.get(`${API}/payment-reminder-settings`);
        setPaymentReminderSettings(response.data);
      } catch (error) {
        void 0 && console.log('No payment reminder settings found, will use defaults');
        setPaymentReminderSettings({
          enabled: false,
          min_balance: 10.0,
          reminder_interval_days: 7,
          include_payment_link: true,
          send_time: '09:00'
        });
      }
    }
  }, [user]);
  
  // Fetch eligibility automation settings
  const fetchEligibilitySettings = useCallback(async () => {
    if (isSuperAdmin(user)) {
      try {
        const response = await axios.get(`${API}/auto-eligibility-settings`);
        setEligibilitySettings(response.data);
      } catch (error) {
        // Settings may not exist yet - that's OK
        void 0 && console.log('No eligibility settings found, will use defaults');
        setEligibilitySettings({
          enabled: false,
          check_time: '06:00',
          check_times: ['06:00'],
          days_ahead: 0,
          create_task_on_failure: true,
          notify_scheduler: true,
          notify_client_portal: true,
          stale_hours: 12
        });
      }
    }
  }, [user]);
  
  // Eligibility settings dialog state
  const [showEligibilitySettings, setShowEligibilitySettings] = useState(false);
  
  // Fetch portal settings
  const fetchPortalSettings = useCallback(async () => {
    if (isSuperAdmin(user)) {
      try {
        const response = await axios.get(`${API}/admin/client-portal-settings`);
        setPortalSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch portal settings:', error);
      }
    }
  }, [user]);
  
  // Note: Impersonation state is now in ImpersonationContext
  
  // Note: selectedClient, showClientDetail, clientDetailTab, clientHeaderCollapsed are now in ClientsContext
  const [viewingReleaseInline, setViewingReleaseInline] = useState(null); // Release being viewed inline
  const [clientContextMenu, setClientContextMenu] = useState({ show: false, x: 0, y: 0, clientId: null }); // Right-click context menu
  // Collapse states - persist in localStorage per user so they survive logout
  const [dashboardHeaderCollapsed, setDashboardHeaderCollapsed] = useState(false);
  // Jun 2026 — admin portal practice-wide overview (moved from the general
  // dashboard header; regular users now see appointments + tasks only).
  const [adminOverviewCollapsed, setAdminOverviewCollapsed] = useState(false);
  
  // Load collapse states from localStorage when user changes
  useEffect(() => {
    if (user?.id) {
      const savedClient = localStorage.getItem(`clientHeaderCollapsed_${user.id}`);
      const savedDashboard = localStorage.getItem(`dashboardHeaderCollapsed_${user.id}`);
      const savedAdminOverview = localStorage.getItem(`adminOverviewCollapsed_${user.id}`);
      setClientHeaderCollapsed(savedClient === 'true');
      setDashboardHeaderCollapsed(savedDashboard === 'true');
      setAdminOverviewCollapsed(savedAdminOverview === 'true');
    }
  }, [user?.id]);
  
  // Persist collapse states to localStorage when they change (per user)
  useEffect(() => {
    if (user?.id) {
      safeLocalStorageSetItem(`clientHeaderCollapsed_${user.id}`, clientHeaderCollapsed);
    }
  }, [clientHeaderCollapsed, user?.id]);
  
  useEffect(() => {
    if (user?.id) {
      safeLocalStorageSetItem(`dashboardHeaderCollapsed_${user.id}`, dashboardHeaderCollapsed);
    }
  }, [dashboardHeaderCollapsed, user?.id]);
  
  useEffect(() => {
    if (user?.id) {
      safeLocalStorageSetItem(`adminOverviewCollapsed_${user.id}`, adminOverviewCollapsed);
    }
  }, [adminOverviewCollapsed, user?.id]);
  
  // Change History Modal state
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [changeHistoryEntity, setChangeHistoryEntity] = useState({ type: null, id: null, name: null });
  
  // Survey Dashboard state
  const [showSurveyDashboard, setShowSurveyDashboard] = useState(false);
  
  // Context menus for appointments, claims, notes (right-click)
  // Note: appointmentContextMenu is now in AppointmentsContext
  // Note: noteContextMenu is now in NotesContext
  const [claimContextMenu, setClaimContextMenu] = useState({ show: false, x: 0, y: 0, claim: null });
  
  // Document viewer state
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentZoom, setDocumentZoom] = useState(100); // Zoom level for document viewer
  const [documentToDelete, setDocumentToDelete] = useState(null);
  
  // Fetch document content when viewingDocument changes
  useEffect(() => {
    const fetchDocumentContent = async () => {
      // Clean up previous blob URL when document changes
      if (documentBlobUrl) {
        window.URL.revokeObjectURL(documentBlobUrl);
        setDocumentBlobUrl(null);
      }
      
      if (!viewingDocument) {
        setDocumentLoading(false);
        return;
      }
      
      setDocumentLoading(true);
      try {
        // Try the fast path first (no responseType specified → axios auto-
        // parses JSON). If the backend returned {download_url, filename},
        // skip the blob entirely and point the viewer iframe at the signed
        // GCS URL (clinical + legacy-import docs, Tier 1 PDF optimization).
        // If the backend returned raw bytes (regular encrypted uploads),
        // fall through to the legacy blob handling.
        const probeResponse = await axios.get(
          `${API}/documents/${viewingDocument.id}/download?inline=true`,
          {
            // Accept JSON (signed URL) OR octet-stream (proxy-streamed bytes).
            // When axios sees a binary content-type it returns a Blob automatically
            // even without responseType:'blob'. So this one call handles both.
            responseType: 'blob',
            // Tell axios NOT to choke on JSON error bodies — we peek at the
            // first few bytes to decide which path we're on.
          }
        );

        const contentType = probeResponse.headers['content-type'] || '';

        if (contentType.includes('application/json')) {
          // Signed URL path — parse the JSON out of the blob, then point at
          // the direct GCS URL (browser will fetch bytes straight from
          // Google, no backend hop). 2-3× faster on big clinical PDFs.
          const text = await probeResponse.data.text();
          const payload = JSON.parse(text);
          if (payload?.download_url) {
            setDocumentBlobUrl(payload.download_url);
            void 0 && console.log('[Document] Signed URL path:', viewingDocument.filename);
          } else {
            throw new Error('signed URL response missing download_url');
          }
        } else {
          // Proxy-stream path (encrypted regular uploads). Same behavior as before.
          const blobType = contentType || viewingDocument.file_type || 'application/octet-stream';
          const blob = new Blob([probeResponse.data], { type: blobType });
          const url = window.URL.createObjectURL(blob);
          setDocumentBlobUrl(url);
          void 0 && console.log('[Document] Proxy-stream path:', viewingDocument.filename, 'type:', blobType);
        }
      } catch (error) {
        console.error('Failed to fetch document content:', error);
        toast.error('Failed to load document');
      } finally {
        setDocumentLoading(false);
      }
    };
    
    fetchDocumentContent();
  }, [viewingDocument?.id]); // Only re-run when document ID changes
  const [showAssignProviders, setShowAssignProviders] = useState(false);
  const [selectedProvidersForAssignment, setSelectedProvidersForAssignment] = useState([]);
  const [providerServiceAssignments, setProviderServiceAssignments] = useState({}); // {providerId: [serviceIds]}
  const [pendingServiceEnrollments, setPendingServiceEnrollments] = useState({}); // For new clients: {providerId: [serviceIds]}
  const [availableServices, setAvailableServices] = useState([]);
  const [existingEnrollments, setExistingEnrollments] = useState([]);
  // Note: clientEnrollments is now in ClientsContext
  const [enrollmentsLoaded, setEnrollmentsLoaded] = useState(false);
  const [showTransferServiceDialog, setShowTransferServiceDialog] = useState(false);
  const [transferEnrollment, setTransferEnrollment] = useState(null);
  const [newProviderForTransfer, setNewProviderForTransfer] = useState('');
  
  // Cache for service enrollments per client to avoid repeated fetches
  const enrollmentsCacheRef = React.useRef({});
  
  // Cache for user signature URLs (GCS signed URLs)
  const signatureCacheRef = React.useRef({});
  
  // Request ID ref to prevent race conditions in fetchData
  const fetchRequestIdRef = React.useRef(0);
  
  // Ref to track active tab without causing fetchData to recreate on tab changes
  const activeTabRef = React.useRef(activeTab);
  React.useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);
  
  // Guard to prevent duplicate note creation (double-click protection)
  const isCreatingNoteRef = React.useRef(false);
  
  // Performance: Track whether a heavy editing dialog is open.
  // When true, background polling intervals skip their work to avoid
  // triggering re-renders while the user is actively typing.
  const isEditingDialogOpenRef = React.useRef(false);
  React.useEffect(() => {
    isEditingDialogOpenRef.current = showNoteDetail || showAddNote;
  }, [showNoteDetail, showAddNote]);
  
  // Helper function to get signature URL (handles GCS signed URLs)
  const getSignatureUrl = useCallback(async (userId) => {
    if (!userId) return null;
    
    // Check cache first
    if (signatureCacheRef.current[userId] !== undefined) {
      // Cache hit — includes negative results (null when the user has no
      // signature or was deleted). Negative caching prevents the frontend
      // from hammering /api/users/{id}/signature every render when a note
      // references a historical author whose signature image was never set.
      return signatureCacheRef.current[userId];
    }

    try {
      const response = await axios.get(`${API}/users/${userId}/signature`);
      const signatureUrl = response.data?.signature_url ?? null;
      // Cache everything — positive AND null — for 30 min (signed URLs
      // expire after 60 min). For null results this prevents repeat 200s
      // on every component render that needs the signature.
      signatureCacheRef.current[userId] = signatureUrl;
      setTimeout(() => {
        delete signatureCacheRef.current[userId];
      }, 30 * 60 * 1000);
      return signatureUrl;
    } catch (error) {
      // On network/unknown failure, also negative-cache for 5 min so we
      // don't retry on every render of the same note.
      signatureCacheRef.current[userId] = null;
      setTimeout(() => {
        delete signatureCacheRef.current[userId];
      }, 5 * 60 * 1000);
      console.error('Failed to fetch signature URL:', error);
      return null;
    }
  }, []);
  
  // State for signature URLs being displayed
  const [loadedSignatures, setLoadedSignatures] = useState({});
  
  // Alert state
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [alertFormData, setAlertFormData] = useState({
    content: '',
    is_banner: false,
    tagged_users: []
  });
  // Note: clientAlerts is now in ClientsContext
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [showAlertsDropdown, setShowAlertsDropdown] = useState(null); // Track which client's alerts dropdown is open
  
  // Note: pendingDeletes for notes is now in NotesContext
  
  // Routing Rules state
  const [routingRules, setRoutingRules] = useState([]);
  const [reminderSettings, setReminderSettings] = useState(null);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  
  // Admin Dialog Navigation State
  const [adminJumpToTarget, setAdminJumpToTarget] = useState(null);
  const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] = useState(false);
  const [currentAdminDialogDirty, setCurrentAdminDialogDirty] = useState(false);
  const [pendingAdminNavigation, setPendingAdminNavigation] = useState(null);
  
  // Billing state
  // claimsQueue, claimsQueueRefreshKey, claimsSortColumn,
  // claimsSortDirection now live in useClaimsQueue (instantiated
  // below once billingProviderFilter / billingStatusFilter are
  // declared).
  const [currentBatch, setCurrentBatch] = useState(null);
  const [batchHistory, setBatchHistory] = useState([]);
  const [showBatchHistory, setShowBatchHistory] = useState(true);
  const [viewingBatch, setViewingBatch] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showClaimDetailDialog, setShowClaimDetailDialog] = useState(false);
  const [showReturnClaimDialog, setShowReturnClaimDialog] = useState(false);
  const [returnClaimNotes, setReturnClaimNotes] = useState('');
  const [claimSearchTerm, setClaimSearchTerm] = useState('');
  const [claimFilterProvider, setClaimFilterProvider] = useState('');
  const [claimFilterStatus, setClaimFilterStatus] = useState('');
  
  const [showRoutingRuleForm, setShowRoutingRuleForm] = useState(false);
  const [showRoutingRulesManager, setShowRoutingRulesManager] = useState(false); // Full rules manager dialog
  const [editingRoutingRule, setEditingRoutingRule] = useState(null);
  const [routingRuleFormData, setRoutingRuleFormData] = useState({
    rule_name: '',
    description: '',
    priority: 0,
    scope: 'global',
    provider_id: null,
    template_id: null,
    conditions: [],
    require_signature: true,
    route_to: 'first_supervisor',
    specific_supervisor_id: null,
    is_active: true
  });
  const [naturalLanguageRule, setNaturalLanguageRule] = useState('');
  const [parsingRule, setParsingRule] = useState(false);
  const [ruleConflicts, setRuleConflicts] = useState(null);
  const [validatingRules, setValidatingRules] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [runningBackfill, setRunningBackfill] = useState(false);
  
  // Password Reset state
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [showPasswordResetManager, setShowPasswordResetManager] = useState(false);
  const [selectedPasswordResetRequest, setSelectedPasswordResetRequest] = useState(null);
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  const [passwordResetFormData, setPasswordResetFormData] = useState({
    user_id: '',
    new_password: '',
    confirm_password: '',
    send_email: true,
    require_change_on_login: true
  });
  
  // Client Merge state
  const [showClientMergeDialog, setShowClientMergeDialog] = useState(false);
  const [mergeSourceClient, setMergeSourceClient] = useState(null);
  const [mergeTargetClient, setMergeTargetClient] = useState(null);
  const [mergePreview, setMergePreview] = useState(null);
  const [mergeFieldOverrides, setMergeFieldOverrides] = useState({});
  const [mergeLoading, setMergeLoading] = useState(false);
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  const [selectedDuplicatePairs, setSelectedDuplicatePairs] = useState([]); // Track selected pairs for batch merge
  const [swappedPairs, setSwappedPairs] = useState({}); // Track which pairs have been swapped (key: pairIdx, value: true if swapped)
  const [showDuplicatesDialog, setShowDuplicatesDialog] = useState(false);
  const [mergeHistory, setMergeHistory] = useState([]);
  const [showMergeHistoryDialog, setShowMergeHistoryDialog] = useState(false);
  const [dismissedDuplicates, setDismissedDuplicates] = useState([]);
  const [showDismissedDuplicates, setShowDismissedDuplicates] = useState(false);
  
  // Note: Calendar state is now in AppointmentsContext (calendarDate, calendarView, appointmentSortAsc, 
  // showPastAppointments, appointmentSearch, calendarInterval, draggedAppointment, resizingAppointment,
  // showCanceledAppointments, undoStack, cptCodeUsage)
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreenCalendar, setIsFullscreenCalendar] = useState(false); // Fullscreen calendar mode
  
  // Update current time every minute for the calendar indicator
  // Skip when editing dialog is open to avoid re-renders while typing
  useEffect(() => {
    const timer = setInterval(() => {
      if (!isEditingDialogOpenRef.current) {
        setCurrentTime(new Date());
      }
    }, 60000); // Update every minute
    
    return () => clearInterval(timer);
  }, []);
  // Calendar drag/resize/undo logic — extracted into a hook so the
  // Dashboard scope no longer owns 8 inline closures and ~140 lines
  // of drag/drop wiring. The same hook is reusable by V2
  // CalendarRoute once it mounts `<AppointmentsTab />` natively.
  const {
    dragOverSlot, setDragOverSlot,
    addToUndoStack, handleUndo,
    handleDragStart, handleDragOver, handleDrop, handleDragLeave,
    handleAppointmentDragEnd,
  } = useCalendarDragHook({
    // refreshAppointments is defined later in this function (line
    // ~11650). The closure captures the binding lazily, so the
    // forward reference is safe.
    refreshAppointments: (full) => refreshAppointments(full),
  });
  // Note: selectedProviders is now in AppointmentsContext
  
  // CPT Code management
  // Note: cptCodeUsage is now in AppointmentsContext
  const [cptCodeFilter, setCptCodeFilter] = useState('active'); // 'active', 'archived', 'all'
  const [cptCodeSearch, setCptCodeSearch] = useState('');
  const [cptCodeSort, setCptCodeSort] = useState('code'); // 'code', 'name', 'duration', 'category'
  
  // CPT Code form state
  const [showCptForm, setShowCptForm] = useState(false);
  const [showCptImportDialog, setShowCptImportDialog] = useState(false);
  const [showDirectoryDialog, setShowDirectoryDialog] = useState(false); // Staff Directory dialog
  const [cptImportMode, setCptImportMode] = useState('create_and_update');  // Default to create & update
  const [cptFormData, setCptFormData] = useState({
    code: '',
    display_name: '',
    description: '',
    category: '',
    bundle_ids: [],
    billing_type: 'insurance',
    base_price: null,  // Base price for billing (insurance rate)
    // Self-Pay Pricing
    self_pay_discount_type: 'none',  // "none", "percentage", "fixed_amount", "fixed_rate"
    self_pay_discount_percent: null,
    self_pay_discount_amount: null,
    self_pay_rate: null,
    units: 1,
    duration_minutes: 45,
    minutes_per_unit: 45,
    allows_multiple_units: true,
    is_group_session: false,
    default_group_id: '',
    default_template_id: '',  // Template to auto-create note from
    min_duration: null,
    max_duration: null,
    min_age: null,
    max_age: null,
    pos_codes: [],
    modifiers: [],
    color: '#3B82F6',  // Default blue color
    is_addon_code: false,  // Add-on code configuration
    requires_primary_codes: [],  // List of CPT code IDs this add-on requires
    requires_authorization: false,  // If true, scheduling requires valid authorization
    allow_authorization: false,  // If true, auth picker is shown but optional
    allows_overlap: false  // If true, can overlap other appointments whose CPT also allows it
  });
  
  // Form template state
  const [showFormTemplateBuilder, setShowFormTemplateBuilder] = useState(false);
  const [formTemplateData, setFormTemplateData] = useState({
    name: '',
    description: '',
    form_type: 'intake',
    form_fields: [],
    billable: false,
    requires_provider_signature: true,
    requires_supervisor_signature: false,
    allow_client_signature: false,
    require_client_signature: false,
    is_scored_assessment: false,
    is_group_template: false,
    is_discharge_form: false,
    available_in_quick_create: true,
    allow_external_download: false, // Allow downloading completed forms for external fax/send
    allow_print_for_signature: false, // Show print for signature button
    allow_send_to_client: false, // Show send to client button
    scoring_config: {
      max_score: 0,
      severity_ranges: [],
      clinical_alerts: []
    }
  });
  const [activeFormFields, setActiveFormFields] = useState([]);
  // Tracks the note id currently having its diagnoses refreshed (for
  // the "Refresh diagnoses" button on Comp Assessments). Stores the
  // note id while in-flight so the button can show a spinner without
  // accidentally disabling refresh on other notes.
  const [isRefreshingDiagnoses, setIsRefreshingDiagnoses] = useState(null);
  
  // Ref always holds latest fields for save operations (avoids stale closure issues)
  const activeFormFieldsRef = useRef(activeFormFields);
  useEffect(() => { activeFormFieldsRef.current = activeFormFields; }, [activeFormFields]);
  
  // Ref for formTemplateData to avoid re-binding keyboard handler on every change
  const formTemplateDataRef = useRef(formTemplateData);
  useEffect(() => { formTemplateDataRef.current = formTemplateData; }, [formTemplateData]);
  
  // Use deferred value for heavy preview rendering - keeps typing responsive
  const deferredFormFields = useDeferredValue(activeFormFields);
  
  // Memoize the grouped sections to prevent unnecessary re-renders during typing
  // This prevents focus loss when editing field labels
  const memoizedGroupedSections = useMemo(() => {
    return groupFieldsIntoSectionsAndRows(activeFormFields);
  }, [activeFormFields]);
  
  const deferredGroupedSections = useMemo(() => {
    return groupFieldsIntoSectionsAndRows(deferredFormFields);
  }, [deferredFormFields]);
  
  // Field context menu state (for right-click to pick section)
  const [fieldContextMenu, setFieldContextMenu] = useState({ show: false, x: 0, y: 0, fieldType: null });
  
  // Ref to track static text editor content to prevent loss on re-renders
  // Key: field.id, Value: { content: string, initialized: boolean }
  const staticTextEditorCache = useRef(new Map());

  // Client form state
  const [showClientForm, setShowClientForm] = useState(false);
  const [showClientExportDialog, setShowClientExportDialog] = useState(false);
  const [clientExportFilters, setClientExportFilters] = useState({
    status: 'all',
    startDate: '',
    endDate: '',
    providerId: 'all'
  });
  const [exportColumnGroups, setExportColumnGroups] = useState([]);
  const [exportSelectedCols, setExportSelectedCols] = useState(new Set());
  const [showEditClientForm, setShowEditClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showClientPreview, setShowClientPreview] = useState(false);
  const [previewClientLoading, setPreviewClientLoading] = useState(false);
  const [showInactiveSection, setShowInactiveSection] = useState(false);
  const [previewClient, setPreviewClient] = useState(null);
  const [showRequestInfoDialog, setShowRequestInfoDialog] = useState(false);
  const [showDenyDialog, setShowDenyDialog] = useState(false);
  const [showActivateWithProviderDialog, setShowActivateWithProviderDialog] = useState(false);
  const [clientToActivate, setClientToActivate] = useState(null);
  const [selectedProviderForActivation, setSelectedProviderForActivation] = useState('');
  const [selectedServiceForActivation, setSelectedServiceForActivation] = useState('');
  const [selectedClientForAction, setSelectedClientForAction] = useState(null);
  const [infoRequestNotes, setInfoRequestNotes] = useState('');
  const [denyReason, setDenyReason] = useState('');
  const [clientFormData, setClientFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    primary_insurance: '',
    secondary_insurance: '',
    tertiary_insurance: '',
    assigned_providers: [],
    email_consent: true,  // Default opted-in for email
    sms_consent: false,   // Default opted-out for SMS (requires explicit consent)
    status: '',           // Optional: if set to 'waitlist', creates client directly on waitlist
    pharmacy_name: '',
    pharmacy_phone: '',
    pharmacy_fax: '',
    pharmacy_address: ''
  });

  // Effect to populate clientFormData when editingClient is set (for edit dialog)
  useEffect(() => {
    if (editingClient && showEditClientForm) {
      setClientFormData({
        full_name: editingClient.full_name || '',
        email: editingClient.email || '',
        phone: editingClient.phone || '',
        date_of_birth: editingClient.date_of_birth || '',
        primary_insurance: editingClient.primary_insurance || '',
        secondary_insurance: editingClient.secondary_insurance || '',
        tertiary_insurance: editingClient.tertiary_insurance || '',
        assigned_providers: editingClient.assigned_providers || [],
        email_consent: editingClient.email_consent !== undefined ? editingClient.email_consent : true,
        sms_consent: editingClient.sms_consent !== undefined ? editingClient.sms_consent : false,
        pharmacy_name: editingClient.pharmacy_name || '',
        pharmacy_phone: editingClient.pharmacy_phone || '',
        pharmacy_fax: editingClient.pharmacy_fax || '',
        pharmacy_address: editingClient.pharmacy_address || ''
      });
    }
  }, [editingClient, showEditClientForm]);

  // Note: showAppointmentForm, editingAppointment, appointmentFormData, attendanceAppointment are now in AppointmentsContext
  // Sep 2026 — recurring-series scope modal (replaces stacked confirms)
  const [showSeriesScopeDialog, setShowSeriesScopeDialog] = useState(false);
  const seriesScopeResolverRef = useRef(null);
  const resolveSeriesScope = (scope) => {
    setShowSeriesScopeDialog(false);
    if (seriesScopeResolverRef.current) {
      seriesScopeResolverRef.current(scope);
      seriesScopeResolverRef.current = null;
    }
  };
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});
  const [directAttendanceGroup, setDirectAttendanceGroup] = useState(null); // For attendance without appointment
  const [directAttendanceDate, setDirectAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  // reminderSectionExpanded migrated INTO AppointmentScheduleDialog (Stage 10 part 2).
  // Reschedule-response + counter-proposal state migrated INTO AppointmentScheduleDialog (Stage 10 part 2).
  
  // Track applicable intake bundles for current appointment
  const [applicableIntakeBundles, setApplicableIntakeBundles] = useState([]);
  
  // Vitals tracking state
  const [vitalReadings, setVitalReadings] = useState([]);
  const [showVitalForm, setShowVitalForm] = useState(false);
  const [vitalFormData, setVitalFormData] = useState({
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    oxygen_saturation: '',
    pulse: '',
    respiratory_rate: '',
    temperature: '',
    height_feet: '',
    height_inches: '',
    weight: '',
    notes: '',
    set_reminder: false,
    reminder_type: 'next_appointment',
    reminder_days: '30'
  });
  const [vitalsTimeFilter, setVitalsTimeFilter] = useState('30'); // days
  const [vitalsChartView, setVitalsChartView] = useState('combined'); // 'combined' or 'individual'
  const [showMedicationReferralHistory, setShowMedicationReferralHistory] = useState(true); // Collapsible section

  // Set default provider based on user role when user or form visibility changes
  useEffect(() => {
    if (user && showAppointmentForm && !editingAppointment) {
      // For new appointments (not editing), only default to current user if they're a provider
      // Admin/office should select a provider instead of defaulting to themselves
      const updates = {};
      if (user?.is_provider) {
        updates.provider_id = user.id;
        // Also set default location and POS if user has them
        if (user.default_location) {
          updates.location = user.default_location;
        }
        if (user.default_pos_code) {
          updates.pos_code = user.default_pos_code;
        }
      }

      // Feb 28, 2026 — V2 Tasks deep-link prefill: a "Reschedule X"
      // task bounced here with `window.__v2_prefill_client__` set so
      // the form pre-selects the right client.  One-shot — clear
      // immediately so subsequent "Schedule Appointment" button
      // clicks don't accidentally re-pre-fill.
      if (window.__v2_prefill_client__) {
        updates.client_id = window.__v2_prefill_client__;
        window.__v2_prefill_client__ = null;
      }

      if (Object.keys(updates).length > 0) {
        setAppointmentFormData(prev => ({
          ...prev,
          ...updates
        }));
      }
    }
  }, [user, showAppointmentForm, editingAppointment]);

  // Populate form data when editing an existing appointment — MIGRATED
  // INTO AppointmentScheduleDialog (Stage 10 part 3). Removing the
  // Dashboard-level effect avoids double-population (both mounts share
  // the same `appointmentFormData` via AppointmentsContext).

  // Sync provider defaults when provider_id changes (after initial render)
  useEffect(() => {
    if (!appointmentFormData.provider_id || !showAppointmentForm) return;
    
    // Find the selected provider
    const selectedProvider = providers?.find(p => p.id === appointmentFormData.provider_id) || 
                             users?.find(u => u.id === appointmentFormData.provider_id);
    
    if (!selectedProvider) return;
    
    // Only update location/POS/telehealth_modality if they're empty (user hasn't manually selected yet)
    setAppointmentFormData(prev => {
      const updates = {};
      
      // Only prefill location if it's currently empty and provider has a default
      if (!prev.location && selectedProvider.default_location) {
        updates.location = selectedProvider.default_location;
      }
      
      // Only prefill POS if it's currently empty and provider has a default
      if (!prev.pos_code && selectedProvider.default_pos_code) {
        updates.pos_code = selectedProvider.default_pos_code;
      }
      
      // Only prefill telehealth modality if:
      // 1. It's currently empty
      // 2. Provider has a default telehealth modality
      // 3. The POS code is a telehealth code (02 or 10)
      const posCode = updates.pos_code || prev.pos_code;
      const isTelehealthPOS = posCode === '02' || posCode === '10';
      if (!prev.telehealth_modality && selectedProvider.default_telehealth_modality && isTelehealthPOS) {
        updates.telehealth_modality = selectedProvider.default_telehealth_modality;
      }
      
      // Only update if there are changes
      if (Object.keys(updates).length > 0) {
        void 0 && console.log('🔧 Syncing provider defaults:', updates);
        return { ...prev, ...updates };
      }
      
      return prev;
    });
    
    // Fetch clients assigned to this provider if not enough are loaded
    // This ensures the client dropdown shows all the provider's clients
    const fetchProviderClients = async () => {
      const providerId = appointmentFormData.provider_id;
      const currentlyLoaded = allClientsForLookup?.filter(c => 
        c.assigned_providers?.includes(providerId)
      ) || [];
      
      // If we have very few clients for this provider, fetch them specifically
      if (currentlyLoaded.length < 20) {
        try {
          void 0 && console.log(`📥 Fetching clients for provider ${providerId}...`);
          const response = await axios.get(`${API}/clients?assigned_to=${providerId}&fields=minimal&include_archived=true&page_size=500`);
          const providerClients = Array.isArray(response.data) ? response.data : (response.data.clients || []);
          
          if (providerClients.length > 0) {
            // Merge with existing clients (avoiding duplicates)
            setAllClientsForLookup(prev => {
              const existingIds = new Set(prev.map(c => c.id));
              const newClients = providerClients.filter(c => !existingIds.has(c.id));
              if (newClients.length > 0) {
                void 0 && console.log(`✅ Added ${newClients.length} new clients for provider ${providerId}`);
                return [...prev, ...newClients];
              }
              return prev;
            });
          }
        } catch (error) {
          console.error('Failed to fetch provider clients:', error);
        }
      }
    };
    
    fetchProviderClients();
    
  }, [appointmentFormData.provider_id, appointmentFormData.pos_code, providers, users, showAppointmentForm, allClientsForLookup, setAllClientsForLookup]);

  // User form state (Admin only)
  const [showUserForm, setShowUserForm] = useState(false);
  const [showUserNotifSettings, setShowUserNotifSettings] = useState(false);
  const [showUserList, setShowUserList] = useState(false);
  const [userViewMode, setUserViewMode] = useState('list'); // 'list' or 'grid'
  const [insuranceViewMode, setInsuranceViewMode] = useState('list'); // 'list' or 'grid'
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userSortMode, setUserSortMode] = useState('name'); // 'name' or 'role'
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: '',
    password: '',
    showPassword: false,
    autoGeneratePassword: true,  // Default to auto-generate for new users
    first_name: '',
    last_name: '',
    full_name: '',  // Keep for backward compatibility
    role_id: '',
    phone: '',
    title: '',
    department: '',
    credentials: '',
    is_provider: false,
    is_prescriber: false,
    is_medication_manager: false,
    is_waitlist_manager: false,
    is_biller: false,
    is_billing_manager: false,
    is_medical_records_manager: false,
    is_authorization_manager: false,
    is_payroll_manager: false,
    is_era_manager: false,
    is_scheduler: false,
    is_external_reviewer: false,
    scheduling_for: [],
    npi: '',
    taxonomy_code: '',
    license_status: 'Licensed',
    medicare_approved: false,
    supervisor_ids: [],
    assigned_billers: [],
    assigned_cpt_codes: [],
    assigned_cpt_bundles: [],
    assigned_pos_codes: [],
    assigned_locations: [],
    default_location: null,
    default_pos_code: null,
    default_telehealth_modality: null,
    default_template_id: '',
    custom_telehealth_url: '',
    disable_appointment_reminders: false,
    can_archive_clients: false,
    can_archive_users: false,
    can_generate_client_portal_links: true,
    can_qa_audit: false,
    group_admin: false,
    client_visibility: 'own_only',
    is_archived: false,
    welcome_email_cc: ''  // CC admin on welcome email
  });
  
  // Helper function to reset user form data
  const resetUserFormData = () => {
    setUserFormData({
      email: '',
      password: '',
      showPassword: false,
      first_name: '',
      last_name: '',
      full_name: '',
      role_id: '',
      phone: '',
      title: '',
      department: '',
      credentials: '',
      is_provider: false,
      is_prescriber: false,
      is_medication_manager: false,
      is_waitlist_manager: false,
      is_biller: false,
      is_billing_manager: false,
      is_medical_records_manager: false,
      is_authorization_manager: false,
      is_payroll_manager: false,
    is_era_manager: false,
      is_scheduler: false,
      is_external_reviewer: false,
      scheduling_for: [],
      npi: '',
      taxonomy_code: '',
      employee_id: '',
      license_status: 'Licensed',
      medicare_approved: false,
      supervisor_ids: [],
      assigned_billers: [],
      assigned_cpt_codes: [],
      assigned_cpt_bundles: [],
      assigned_pos_codes: [],
      assigned_locations: [],
      default_location: null,
      default_pos_code: null,
      default_telehealth_modality: null,
      default_template_id: '',
      custom_telehealth_url: '',
      disable_appointment_reminders: false,
      can_archive_clients: false,
      can_archive_users: false,
      can_generate_client_portal_links: true,
      can_qa_audit: false,
      group_admin: false,
      client_visibility: 'own_only',
      is_archived: false,
      autoGeneratePassword: true,  // Default to auto-generate for new users
      welcome_email_cc: ''  // CC admin on welcome email
    });
    setEditingUser(null);
  };

  // Note: clientStatusFilter, clientViewMode, clientsPerPage, clientsCurrentPage, clientsSortColumn, 
  // clientsSortDirection, clientProviderFilter, clientSearch are now in ClientsContext
  const [clientFilter, setClientFilter] = useState('all');
  const [collapsedClientStatusSections, setCollapsedClientStatusSections] = useState({}); // Track collapsed status sections in card view
  const [showArchivedUsers, setShowArchivedUsers] = useState(false);
  const [superAdminCount, setSuperAdminCount] = useState(0); // For "Last Man Standing" protection
  const [adminAiEnabled, setAdminAiEnabled] = useState(false); // AI features kill switch
  // Per-appointment auto-note kill switch (Feb 2026 user request).
  // Default true to preserve historical behaviour for orgs that
  // never visit the setting.
  const [autoNoteOnScheduleEnabled, setAutoNoteOnScheduleEnabled] = useState(true);
  const [showArchiveModal, setShowArchiveModal] = useState(false); // User archive workflow modal
  const [userToArchive, setUserToArchive] = useState(null); // User being archived

  // Deleted Notes Recovery (Admin)
  const [showDeletedNotesDialog, setShowDeletedNotesDialog] = useState(false);
  const [deletedNotes, setDeletedNotes] = useState([]);
  const [deletedNotesLoading, setDeletedNotesLoading] = useState(false);
  const [deletedNotesClientFilter, setDeletedNotesClientFilter] = useState('');

  // Fetch deleted notes for recovery
  const fetchDeletedNotes = async (clientId = null) => {
    setDeletedNotesLoading(true);
    try {
      const params = clientId ? `?client_id=${clientId}` : '';
      const response = await axios.get(`${API}/admin/deleted-notes${params}`);
      setDeletedNotes(response.data.deleted_notes || []);
    } catch (error) {
      console.error('Failed to fetch deleted notes:', error);
      toast.error('Failed to fetch deleted notes');
    } finally {
      setDeletedNotesLoading(false);
    }
  };

  // Restore a deleted note
  const handleRestoreNote = async (noteId) => {
    try {
      await axios.post(`${API}/admin/restore-note/${noteId}`);
      toast.success('Note restored successfully');
      // Refresh the list
      fetchDeletedNotes(deletedNotesClientFilter || null);
    } catch (error) {
      console.error('Failed to restore note:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore note');
    }
  };

  // Note Reassignment (Admin - for fixing cross-contamination)
  const [showReassignNoteDialog, setShowReassignNoteDialog] = useState(false);
  const [reassignNoteId, setReassignNoteId] = useState('');
  const [reassignNoteData, setReassignNoteData] = useState(null);
  const [reassignTargetClientId, setReassignTargetClientId] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignClientSearch, setReassignClientSearch] = useState('');
  const [reassignClientResults, setReassignClientResults] = useState([]);
  // Recent reassignment audit feed — shown directly below the panel so an
  // admin who clicks the wrong target client notices within minutes
  // instead of waiting for a billing cycle to surface the misclick.
  const [reassignAuditEvents, setReassignAuditEvents] = useState([]);
  const [reassignAuditLoading, setReassignAuditLoading] = useState(false);

  const fetchReassignAudit = async () => {
    setReassignAuditLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reassign-note/audit?limit=20`);
      setReassignAuditEvents(Array.isArray(r.data?.events) ? r.data.events : []);
    } catch (e) {
      console.error('Failed to fetch reassign audit:', e);
      // Soft-fail — admin can still reassign even if audit fetch fails.
      setReassignAuditEvents([]);
    } finally {
      setReassignAuditLoading(false);
    }
  };

  // Look up note for reassignment
  const lookupNoteForReassign = async () => {
    if (!reassignNoteId.trim()) {
      toast.error('Please enter a Note ID');
      return;
    }
    setReassignLoading(true);
    try {
      const response = await axios.get(`${API}/notes/${reassignNoteId.trim()}`);
      setReassignNoteData(response.data);
      toast.success('Note found');
    } catch (error) {
      console.error('Failed to find note:', error);
      if (error.response?.status === 404) {
        toast.error('Note not found. Check the Note ID and try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to find note');
      }
      setReassignNoteData(null);
    } finally {
      setReassignLoading(false);
    }
  };

  // Search clients for reassignment target
  const searchClientsForReassign = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setReassignClientResults([]);
      return;
    }
    try {
      // GET /api/clients returns the paginated shape `{ clients, pagination }`
      // — read `.clients` (with a fallback to a flat-array response for any
      // legacy proxy that still flattens). The earlier `response.data || []`
      // was setting the whole {clients, pagination} OBJECT into state, so
      // `reassignClientResults.length` was undefined and the dropdown never
      // rendered (user-reported as "lets me find the note but not search
      // the client to reassign it to").
      // Use `fields=minimal` so the response stays small (reassign UI only
      // needs id/name/MRN to render the picker rows).
      const response = await axios.get(
        `${API}/clients?search=${encodeURIComponent(searchTerm)}&fields=minimal&page_size=10`
      );
      const list = Array.isArray(response.data?.clients)
        ? response.data.clients
        : Array.isArray(response.data)
          ? response.data
          : [];
      setReassignClientResults(list);
    } catch (error) {
      console.error('Failed to search clients:', error);
      setReassignClientResults([]);
    }
  };

  // Reassign note to new client
  const handleReassignNote = async () => {
    if (!reassignNoteData?.id || !reassignTargetClientId) {
      toast.error('Please select a note and target client');
      return;
    }
    setReassignLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/reassign-note/${reassignNoteData.id}?new_client_id=${reassignTargetClientId}`
      );
      toast.success(
        <div>
          <strong>Note reassigned successfully</strong>
          <p className="text-sm">From: {response.data.from_client?.name}</p>
          <p className="text-sm">To: {response.data.to_client?.name}</p>
        </div>
      );
      // Reset form
      setReassignNoteId('');
      setReassignNoteData(null);
      setReassignTargetClientId('');
      setReassignClientSearch('');
      setReassignClientResults([]);
      // Refresh the audit feed so the just-completed reassignment
      // appears at the top immediately (and the admin can sanity-check
      // they picked the right target client).
      fetchReassignAudit();
    } catch (error) {
      console.error('Failed to reassign note:', error);
      toast.error(error.response?.data?.detail || 'Failed to reassign note');
    } finally {
      setReassignLoading(false);
    }
  };

  // Waitlist Sort & Filter State
  // Default to 'newest' so just-added clients appear at the top of
  // long waitlist sections without the admin needing to hunt for them
  // through pagination.  Persisted to localStorage so an explicit
  // 'oldest' preference sticks across reloads.
  const [waitlistSortBy, setWaitlistSortBy] = useState(() => {
    try {
      return localStorage.getItem('waitlistSortBy') || 'newest';
    } catch (e) {
      return 'newest';
    }
  });
  // Separate status filter for waitlist tab - does NOT affect main client list
  const [waitlistStatusFilter, setWaitlistStatusFilter] = useState(['pending_intake', 'corrections_requested', 'waitlist']);
  const [waitlistStatusFilterMode, setWaitlistStatusFilterMode] = useState('include');
  // Separate search for waitlist tab - does NOT affect main client list
  const [waitlistSearch, setWaitlistSearch] = useState('');
  const [waitlistFilters, setWaitlistFilters] = useState({
    // All filter fields are arrays so they can be used with the shared
    // MultiSelectFilter (Include/Exclude) component.  `urgentOnly`
    // stays a boolean — it's a single-state toggle, not a list.
    providerPreference: [], // ['Female', 'Male', 'Either']
    ageGroup: [],           // ['adult', 'child']
    languages: [],          // Array of selected languages
    serviceType: [],        // Array of service type IDs
    gender: [],             // ['male', 'female', 'non-binary', 'other']
    location: [],           // Array of location IDs
    insurance: [],          // Array of insurance IDs
    modality: [],           // ['telehealth', 'in_person', 'either']
    symptoms: [],           // Array of selected symptoms
    urgentOnly: false       // Show only urgent clients
  });
  // Companion modes for the Include/Exclude toggle on each waitlist
  // filter (Feb 2026 — universal Include/Exclude standardization).
  const [waitlistFilterModes, setWaitlistFilterModes] = useState({
    providerPreference: 'include',
    ageGroup: 'include',
    languages: 'include',
    serviceType: 'include',
    gender: 'include',
    location: 'include',
    insurance: 'include',
    modality: 'include',
    symptoms: 'include',
  });
  const [showWaitlistFilters, setShowWaitlistFilters] = useState(false);
  const [waitlistItemsPerSection, setWaitlistItemsPerSection] = useState(5); // 5, 10, 20, 50, 100, or 'all'
  
  // Symptom options for filtering
  // Symptom and language options moved to /src/constants/index.js
  // Import: SYMPTOM_OPTIONS, URGENT_SYMPTOMS, LANGUAGE_OPTIONS
  const symptomOptions = SYMPTOM_OPTIONS;
  const urgentSymptoms = URGENT_SYMPTOMS;
  const languageOptions = LANGUAGE_OPTIONS;
  
  // State for urgent escalation dialog
  const [showUrgentEscalationDialog, setShowUrgentEscalationDialog] = useState(false);
  const [urgentEscalationClient, setUrgentEscalationClient] = useState(null);
  const [selectedSupervisorsForUrgent, setSelectedSupervisorsForUrgent] = useState([]);
  const [urgentReason, setUrgentReason] = useState('');
  const [supervisorsList, setSupervisorsList] = useState([]);

  // Intake Wizard State
  const [showIntakeWizard, setShowIntakeWizard] = useState(false);
  const [intakeClientId, setIntakeClientId] = useState(null);
  const [intakeStep, setIntakeStep] = useState(1);
  const [intakeData, setIntakeData] = useState({});
  const [intakeLastSaved, setIntakeLastSaved] = useState(null);
  const [intakeAutoSaving, setIntakeAutoSaving] = useState(false);
  const [intakeValidationErrors, setIntakeValidationErrors] = useState({});
  const [isCreatingClient, setIsCreatingClient] = useState(false); // Prevent duplicate client creation
  const [pendingIntakeInsurances, setPendingIntakeInsurances] = useState([]); // Insurances to create after client creation
  
  // Duplicate check state
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [intakePotentialDuplicates, setIntakePotentialDuplicates] = useState([]);
  const [pendingClientData, setPendingClientData] = useState(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [revealedSSNs, setRevealedSSNs] = useState({}); // Track which SSNs are revealed
  const [realtimeDuplicateWarnings, setRealtimeDuplicateWarnings] = useState([]); // Real-time warnings during form fill
  const [checkingRealtimeDuplicates, setCheckingRealtimeDuplicates] = useState(false);

  // Intake Form Configuration State (Admin)
  const [intakeFormConfig, setIntakeFormConfig] = useState(null);
  const [showFormConfigDialog, setShowFormConfigDialog] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [editingField, setEditingField] = useState(null);

  // ─────────────────────────────────────────────────────────────────
  // Phase 3a of B2 IntakeWizard extraction (Feb 2026): the six
  // permission helpers (`hasPermission`, `isAdmin`,
  // `canEditFieldCategory`, `getFieldConfig`, `isFieldVisible`,
  // `isFieldRequired`) used to be defined inline at lines ~7020,
  // ~7097, ~7103, ~18285, ~18296, ~18302.  They're now sourced from
  // the shared `createIntakePermissions` factory so legacy and V2
  // share a single implementation.  Zero behavior change.
  // ─────────────────────────────────────────────────────────────────
  const __intakePerms = React.useMemo(
    () => createIntakePermissions({ user, roles, userRoleName, intakeFormConfig }),
    [user, roles, userRoleName, intakeFormConfig],
  );
  const hasPermission = __intakePerms.hasPermission;
  const isAdmin = __intakePerms.isAdmin;
  const canEditFieldCategory = __intakePerms.canEditFieldCategory;
  const getFieldConfig = __intakePerms.getFieldConfig;
  const isFieldVisible = __intakePerms.isFieldVisible;
  const isFieldRequired = __intakePerms.isFieldRequired;

  // Form builder state
  const [activeFormSections, setActiveFormSections] = useState([
    { 
      id: 'section_1', 
      title: 'General Information', 
      rowGroups: [
        { id: 'row_1', fields: [] }
      ],
      // Keep fields for backward compatibility during migration
      fields: [] 
    }
  ]);
  const [showFormPreview, setShowFormPreview] = useState(false);
  const [isFieldPaletteCollapsed, setIsFieldPaletteCollapsed] = useState(false);
  const [expandedFieldIds, setExpandedFieldIds] = useState({});
  const [formHeaderCollapsed, setFormHeaderCollapsed] = useState(true);
  const [availableFieldTypes, setAvailableFieldTypes] = useState([
    // Input Fields
    { type: 'text', label: 'Text Input', icon: '📝', category: 'input' },
    { type: 'textarea', label: 'Text Area', icon: '📄', category: 'input' },
    { type: 'email', label: 'Email', icon: '📧', category: 'input' },
    { type: 'phone', label: 'Phone', icon: '📞', category: 'input' },
    { type: 'date', label: 'Date', icon: '📅', category: 'input' },
    { type: 'time', label: 'Time', icon: '🕐', category: 'input' },
    { type: 'select', label: 'Dropdown', icon: '⬇️', category: 'input' },
    { type: 'multiselect', label: 'Multi-Select Dropdown', icon: '☰', category: 'input' },
    { type: 'checkbox', label: 'Checkbox', icon: '☑️', category: 'input' },
    { type: 'checkbox_group', label: 'Checkbox Group', icon: '☑️', category: 'input' },
    { type: 'radio', label: 'Radio Buttons', icon: '🔘', category: 'input' },
    { type: 'matrix', label: 'Matrix/Grid', icon: '⊞', category: 'input' },
    { type: 'file', label: 'File Upload', icon: '📎', category: 'input' },
    { type: 'signature', label: 'Signature Field', icon: '✍️', category: 'input' },
    { type: 'send_assessment', label: 'Send Assessment', icon: '📤', category: 'input' },
    // Layout & Formatting
    { type: 'heading', label: 'Heading Text', icon: '📌', category: 'layout' },
    { type: 'section_header', label: 'Section Header', icon: '📋', category: 'layout' },
    { type: 'static_text', label: 'Static Text', icon: '📃', category: 'layout' },
    { type: 'divider', label: 'Horizontal Line', icon: '➖', category: 'layout' },
    { type: 'client_visibility_divider', label: 'Client Summary Divider', icon: '✂️', category: 'layout' },
    { type: 'spacer', label: 'Vertical Space', icon: '⬍', category: 'layout' },
    { type: 'container', label: 'Shaded Box', icon: '▢', category: 'layout' },
    { type: 'row_placeholder', label: 'Empty Row', icon: '⊡', category: 'layout' },
    // Client Data Fields (Auto-fill)
    { type: 'client_name', label: 'Client Name', icon: '👤', category: 'client_data' },
    { type: 'client_dob', label: 'Date of Birth', icon: '🎂', category: 'client_data' },
    { type: 'client_age', label: 'Client Age', icon: '🔢', category: 'client_data' },
    { type: 'client_email', label: 'Email Address', icon: '📧', category: 'client_data' },
    { type: 'client_phone', label: 'Phone Number', icon: '📞', category: 'client_data' },
    { type: 'client_address', label: 'Full Address', icon: '🏠', category: 'client_data' },
    { type: 'client_diagnoses', label: 'Client Diagnoses (All)', icon: '🏥', category: 'client_data' },
    { type: 'client_diagnoses_selector', label: 'Client Diagnoses (Selectable)', icon: '☑️', category: 'client_data' },
    { type: 'client_medications', label: 'Medications Table', icon: '💊', category: 'client_data' },
    { type: 'client_allergies', label: 'Allergies', icon: '⚠️', category: 'client_data' },
    { type: 'client_insurance', label: 'Insurance Info', icon: '🏥', category: 'client_data' },
    { type: 'client_providers', label: 'Assigned Providers', icon: '👨‍⚕️', category: 'client_data' },
    { type: 'client_intake_date', label: 'Date of Intake', icon: '📅', category: 'client_data' },
    { type: 'client_last_appointment', label: 'Most Recent Appointment', icon: '📆', category: 'client_data' },
    { type: 'client_next_appointment', label: 'Next Appointment', icon: '📅', category: 'client_data' },
    { type: 'client_vitals', label: 'Most Recent Vitals', icon: '❤️', category: 'client_data' },
    { type: 'add_contacts', label: 'Add Contact (to chart)', icon: '📇', category: 'client_data' },
    // Organization Data Fields (Auto-fill)
    { type: 'service_dropdown', label: 'Service Selection', icon: '🎯', category: 'org_data' },
    { type: 'location_dropdown', label: 'Location Selection', icon: '📍', category: 'org_data' },
    { type: 'provider_dropdown', label: 'Provider Selection', icon: '👨‍⚕️', category: 'org_data' },
    { type: 'group_dropdown', label: 'Group Selection', icon: '👥', category: 'org_data' },
    { type: 'current_provider_name', label: 'Current Provider Name', icon: '👤', category: 'org_data' },
    { type: 'current_user_name', label: 'Current User Name', icon: '🧑‍💻', category: 'org_data' },
    { type: 'current_date', label: 'Today\'s Date', icon: '📅', category: 'org_data' },
    // Certificate Fields
    { type: 'certificate_title', label: 'Certificate Title', icon: '🎓', category: 'certificate' },
    { type: 'certificate_recipient', label: 'Recipient Name', icon: '👤', category: 'certificate' },
    { type: 'certificate_completion_text', label: 'Completion Text', icon: '📜', category: 'certificate' },
    { type: 'certificate_signature_line', label: 'Signature Line', icon: '✍️', category: 'certificate' },
    { type: 'certificate_date', label: 'Certificate Date', icon: '📅', category: 'certificate' },
    { type: 'certificate_logo', label: 'Organization Logo', icon: '🏢', category: 'certificate' },
    // Special Fields
    { type: 'insurance', label: 'Insurance Info', icon: '🏥', category: 'special' },
    { type: 'emergency_contact', label: 'Emergency Contact', icon: '🚨', category: 'special' },
    // Clinical Components (Progress Notes)
    { type: 'treatment_plan_goals', label: 'Treatment Plan Goals', icon: '🎯', category: 'clinical' },
    { type: 'pan_select', label: 'PAN Selector (feeds Treatment Plan)', icon: '🧭', category: 'clinical' },
    { type: 'consent_affirmation', label: 'Consent Affirmation', icon: '✅', category: 'clinical' },
    // Workflow Triggers (Feb 2026)
    // create_task generates a real Task on note save — used for
    // reschedule requests, lab-orders, follow-up needs.  Field
    // designer configures default assignee_type + due offset.
    { type: 'create_task', label: 'Create Task on Save', icon: '📋', category: 'clinical' }
  ]);

  // Provider dashboard state
  const [nextAppointment, setNextAppointment] = useState(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]); // Today and tomorrow's appointments
  const [selectedUpcomingIndex, setSelectedUpcomingIndex] = useState(0); // For navigating through upcoming

  // Calendar interaction state
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [showQuickSchedule, setShowQuickSchedule] = useState(false);

  // State monitoring removed after debugging

  // Medical management state
  const [showDiagnosisForm, setShowDiagnosisForm] = useState(false);
  const [showAllDiagnoses, setShowAllDiagnoses] = useState(false); // Toggle for showing all diagnoses vs only current provider's
  const [showMedicationForm, setShowMedicationForm] = useState(false);
  const [showAuthorizationForm, setShowAuthorizationForm] = useState(false);
  const [showEligibilityCheck, setShowEligibilityCheck] = useState(false);
  const [selectedInsuranceForCheck, setSelectedInsuranceForCheck] = useState(null);
  const [insuranceSearchTerm, setInsuranceSearchTerm] = useState(''); // For filtering insurances
  // Note: showNoteDetail, showCompletedNotes, showActiveNotes are now in NotesContext
  const [showChangeRequestDetail, setShowChangeRequestDetail] = useState(false);
  const [selectedChangeRequest, setSelectedChangeRequest] = useState(null);
  
  // Note: activeNotesPageSize, activeNotesPage, billedNotesPageSize, billedNotesPage are now in NotesContext
  
  // State for cancel future appointments confirmation dialog
  const [showCancelFutureApptsDialog, setShowCancelFutureApptsDialog] = useState(false);
  const [cancelFutureApptsData, setCancelFutureApptsData] = useState(null); // {clientId, clientName, status, futureApptsCount, reason}
  
  // Note: selectedNote, isEditingNote, editedNoteContent, noteDetailHeaderCollapsed are now in NotesContext
  const [editedNoteConsent, setEditedNoteConsent] = useState(null); // For editing consent on existing notes
  const [noteLinkedAppointment, setNoteLinkedAppointment] = useState(null); // Store linked appointment for selected note (fetched directly if not in appointments list)
  const [showEditContextMenu, setShowEditContextMenu] = useState(false);
  const [editContextMenuPos, setEditContextMenuPos] = useState({ x: 0, y: 0 });
  const [linkedFormsAdded, setLinkedFormsAdded] = useState({}); // Track which linked forms have been added {formName: noteId}
  const [returnToCreateNote, setReturnToCreateNote] = useState(null); // Store context to return to Create Note dialog
  const [showSignatureConfirm, setShowSignatureConfirm] = useState(false);
  const [signatureAction, setSignatureAction] = useState(null);
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString());
  const [signWithoutDate, setSignWithoutDate] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [signaturePadFor, setSignaturePadFor] = useState(null); // 'provider' or 'supervisor'
  
  /**
   * URL-BASED NOTE NAVIGATION
   * Navigate to a note using the URL path. This makes noteId authoritative from the URL.
   * The fullscreenNoteId handler will load the note data.
   * Note: Unsaved changes check is handled by the openNote function if needed.
   */
  const navigateToNote = useCallback((noteId) => {
    if (!noteId) {
      console.warn('[navigateToNote] No noteId provided');
      return;
    }
    
    // Clear current note state before navigating
    setIsEditingNote(false);
    setEditedNoteContent(null);
    setEditedNoteConsent(null);
    setNoteLinkedAppointment(null);
    setLinkedGoals([]);
    
    navigate(`/note/${noteId}`);
  }, [navigate]);
  
  /**
   * CLOSE NOTE - Navigate back from note URL
   * If we're on a /note/:noteId URL, navigate back to dashboard or previous page
   */
  const closeNote = useCallback(() => {
    // SAVE TEXT BACKUP before clearing state
    if (selectedNote?.id && editedNoteContent) {
      try {
        const textContent = extractNoteTextContent(editedNoteContent, selectedNote);
        if (textContent && textContent.trim().length > 0) {
          axios.post(`${API}/notes/${selectedNote.id}/text-backup`, {
            text_content: textContent,
            client_id: selectedNote.client_id,
            client_name: selectedNote.client_name,
            template_name: selectedNote.template_name || selectedNote.form_name
          }, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
          }).catch(err => {
            console.warn('Failed to save text backup:', err);
          });
        }
      } catch (e) {
        console.warn('Error creating text backup:', e);
      }
    }
    
    // SUCCESS CLEANUP: Clear local draft when closing note
    // This prevents stale drafts from haunting the note later
    noteEditingAutosave?.clearLocalDraft?.();
    
    // Clear note state
    setIsEditingNote(false);
    setEditedNoteContent(null);
    setEditedNoteConsent(null);
    setNoteLinkedAppointment(null);
    setLinkedGoals([]);
    setSelectedNote(null);
    setShowNoteDetail(false);
    
    // Update URL based on current context - remove noteId but preserve tab
    if (showClientDetail && selectedClient?.id) {
      // Get current tab from URL or state
      const urlParams = new URLSearchParams(window.location.search);
      const currentTab = urlParams.get('tab') || clientDetailTab;
      const tabParam = currentTab && currentTab !== 'overview' ? `?tab=${currentTab}` : '';
      navigate(`/client/${selectedClient.id}${tabParam}`, { replace: true });
    } else if (window.location.pathname.startsWith('/note/')) {
      // Standalone note URL — back to the V2 page that opened it, else dashboard
      navigate(getV2NoteReturnPath() || '/dashboard', { replace: true });
    } else {
      // Clear noteId from dashboard URL
      navigate(getV2NoteReturnPath() || '/dashboard', { replace: true });
    }
  }, [navigate, showClientDetail, selectedClient, clientDetailTab, selectedNote, editedNoteContent]);
  

  // Helper function to open note intelligently
  const openNote = async (note, forceViewMode = false) => {
    // EXIT GUARD: Check for REAL unsaved changes on current note before opening a new one
    // Use hasRealUnsavedChanges to avoid false warnings when content matches server
    // CRITICAL: Fall back to false, not hasUnsavedChanges flag, to prevent false positives
    const hasRealChanges = noteEditingAutosave?.hasRealUnsavedChanges?.() ?? false;
    if (showNoteDetail && isEditingNote && hasRealChanges && selectedNote?.id !== note.id) {
      const shouldProceed = window.confirm(
        'You have unsaved changes on the current note. Are you sure you want to open a different note?\n\n' +
        'Your changes are being auto-saved, but the latest edits may not be saved yet.'
      );
      if (!shouldProceed) {
        return; // Don't open the new note
      }
      // Force a final save (fire and forget)
      noteEditingAutosave.forceSave().catch(e => console.warn('Final save failed:', e));
    }
    
    // CRITICAL: Disable editing mode FIRST to stop autosave from firing
    // This prevents the autosave from trying to save cleared/partial state
    setIsEditingNote(false);
    
    // Now safe to clear previous note state
    setEditedNoteContent(null);
    setEditedNoteConsent(null);
    setNoteLinkedAppointment(null); // Clear previous linked appointment
    setLinkedGoals([]);
    
    // Workstream C3 (Feb 2026) — single bundled fetch replaces the
    // 4 sequential awaits this dialog used to fire (note + linked
    // appointment + client diagnoses + form templates).  Fan-out
    // happens server-side via asyncio.gather.
    //
    // We still allow the templates piece to come from the local
    // 60s-TTL cache via fetchFormTemplates() — but the bundle gives us
    // a fresh server snapshot if the cache is cold.
    let fullNote = note;
    let bundleAppt = null;
    let bundleDiagnoses = null;
    let bundleMedications = null;
    let bundleTemplates = null;
    void 0 && console.log('[openNote] Opening note:', note.id);

    try {
      void 0 && console.log('[openNote] Fetching note open-bundle...');
      const bundleResp = await axios.get(`${API}/notes/${note.id}/open-bundle`);
      const bundle = bundleResp.data || {};
      if (bundle.note && bundle.note.id) {
        fullNote = bundle.note;
      } else {
        // Jun 2026 DATA-PRESERVATION: an empty bundle body (ETag/304
        // edge, proxy hiccup) must NOT let us proceed with a summary
        // row that lacks `content` — that path blank-initialized the
        // editor and autosave then wiped the real note. Throw so the
        // fallback below fetches the full note directly.
        throw new Error('open-bundle returned no note');
      }
      bundleAppt = bundle.linked_appointment || null;
      bundleDiagnoses = Array.isArray(bundle.client_diagnoses) ? bundle.client_diagnoses : null;
      // May 2026 PHI-fix: bundle now returns the note client's
      // medications scoped server-side.  Mirrors the diagnoses
      // pattern so the auto-populate snapshot can NEVER pick up
      // another patient's meds from stale global ``clientMedications``
      // state — root cause of the production cross-contamination
      // report.
      bundleMedications = Array.isArray(bundle.client_medications) ? bundle.client_medications : null;
      bundleTemplates = Array.isArray(bundle.form_templates) ? bundle.form_templates : null;
      void 0 && console.log('[openNote] Bundle: note set, diagnoses=%d, medications=%d, templates=%d',
        bundleDiagnoses?.length ?? 0, bundleMedications?.length ?? 0, bundleTemplates?.length ?? 0);
    } catch (error) {
      console.error('Failed to fetch note open-bundle:', error);
      // Fallback path — bundle endpoint unreachable.  Fan out the
      // legacy individual fetches in parallel with **isolated**
      // per-promise failure handling (allSettled).  A 404 on the
      // linked-appointment fetch, or a temporary blip on the
      // diagnoses fetch, must NOT lock a clinician out of their note.
      const fallbackAptId = note.appointment_id;
      const fallbackClientId = note.client_id;
      const [noteRes, apptRes, diagnosesRes, medicationsRes] = await Promise.allSettled([
        axios.get(`${API}/notes/${note.id}`),
        fallbackAptId
          ? axios.get(`${API}/appointments/${fallbackAptId}`)
          : Promise.resolve({ data: null }),
        fallbackClientId
          ? axios.get(`${API}/clients/${fallbackClientId}/diagnoses?include_archived=true`)
          : Promise.resolve({ data: [] }),
        // PHI-fix: also fetch the note client's medications directly
        // on the fallback path so the snapshot stays scoped to the
        // correct patient even when open-bundle is unreachable.
        fallbackClientId
          ? axios.get(`${API}/clients/${fallbackClientId}/medications`)
          : Promise.resolve({ data: [] }),
      ]);
      if (noteRes.status === 'fulfilled' && noteRes.value?.data) {
        fullNote = noteRes.value.data;
      } else if (!note.content && !note.fields) {
        toast.error('Failed to load note details');
        return;
      } else {
        console.warn('[openNote] Using cached note data due to fetch failure');
      }
      if (apptRes.status === 'fulfilled' && apptRes.value?.data) {
        bundleAppt = apptRes.value.data;
      }
      if (diagnosesRes.status === 'fulfilled' && Array.isArray(diagnosesRes.value?.data)) {
        bundleDiagnoses = diagnosesRes.value.data;
      }
      if (medicationsRes.status === 'fulfilled' && Array.isArray(medicationsRes.value?.data)) {
        bundleMedications = medicationsRes.value.data;
      }
      // Templates intentionally not refetched here — the cached
      // fetchFormTemplates() call below will hit its 60s TTL or
      // make a fresh request as needed.  Keeps the fallback path
      // lightweight.
    }

    // Linked appointment — bundle returned it server-side.  Cross-
    // reference the local appointments list as a fallback when the
    // bundle didn't populate (e.g., legacy notes with stale
    // appointment_id pointing at a deleted appointment).
    let linkedApt = bundleAppt;
    if (!linkedApt && fullNote.appointment_id) {
      linkedApt = appointments.find(a => a.id === fullNote.appointment_id) || null;
    }
    setNoteLinkedAppointment(linkedApt || null);

    // Diagnoses — bundle returned them.  Merge with existing state for
    // OTHER clients (don't clobber chart data for an unrelated client).
    const noteClientId = fullNote.client_id;
    let freshDiagnoses = clientDiagnoses || [];
    if (noteClientId && bundleDiagnoses !== null) {
      const otherClientDx = (clientDiagnoses || []).filter(d => d.client_id !== noteClientId);
      freshDiagnoses = [...otherClientDx, ...bundleDiagnoses];
      if (freshDiagnoses.length !== (clientDiagnoses?.length || 0)) {
        setClientDiagnoses(freshDiagnoses);
      }
    }

    // Medications — same merge pattern as diagnoses.  Critical PHI
    // fix (May 2026): the note open-bundle now returns the medications
    // for the NOTE's client_id, scoped server-side.  We merge them into
    // ``clientMedications`` so the chart state for the unrelated
    // client we may currently be viewing isn't clobbered, and we keep
    // a local ``freshMedications`` array — scoped to the note — for
    // the auto-populate snapshot helper below to filter on.  Without
    // this we'd be reading whatever stale meds happen to be in the
    // global ``clientMedications`` state (the LAST opened chart),
    // which is the root cause of the production cross-contamination
    // report.
    let freshMedications = clientMedications || [];
    if (noteClientId && bundleMedications !== null) {
      const otherClientMeds = (clientMedications || []).filter(m => m.client_id !== noteClientId);
      freshMedications = [...otherClientMeds, ...bundleMedications];
      if (freshMedications.length !== (clientMedications?.length || 0)) {
        setClientMedications(freshMedications);
      }
    }

    // Templates — bundle carries the full list, but we still let
    // fetchFormTemplates() update its TTL cache so subsequent opens
    // can short-circuit purely client-side.
    let freshTemplates;
    if (bundleTemplates && bundleTemplates.length > 0) {
      // Hydrate the cache + state from the bundle without an extra
      // HTTP call.
      fetchFormTemplatesCacheRef.current = { templates: bundleTemplates, ts: Date.now() };
      setFormTemplates(bundleTemplates);
      freshTemplates = bundleTemplates;
    } else {
      freshTemplates = await fetchFormTemplates() || formTemplates;
    }
    
    setSelectedNote(fullNote);
    setShowNoteDetail(true);
    
    // URL-BASED NOTE TRACKING: Update URL to reflect current context
    // If viewing note within client chart, use client URL with noteId param
    // If standalone note view, navigate to /note/[id]
    if (showClientDetail && selectedClient?.id) {
      // Within client chart - update URL with noteId query param
      navigate(`/client/${selectedClient.id}?noteId=${fullNote.id}`, { replace: true });
    } else if (!window.location.pathname.startsWith('/note/')) {
      // Standalone view - add noteId as query param to current URL
      navigate(`/dashboard?noteId=${fullNote.id}`, { replace: true });
    }
    
    // Merge logic shared with V2NoteHost — see utils/noteTemplateMerge.js
    // (field merge keeps matrix structure + prefers client portal values).
    
    // Parse note content for editing
    let contentLoadFailed = false;
    try {
      const noteData = JSON.parse(fullNote.content);
      // Check if noteData has actual content (fields or sections)
      if (noteData && (noteData.fields?.length > 0 || noteData.sections?.length > 0)) {
        // Resolve the template (incl. historical version snapshot when the
        // note was authored against an older template version) via the
        // shared helper — same code path as V2NoteHost.
        let template = await resolveNoteTemplate({
          noteData, fullNote, templates: freshTemplates, api: API,
        });

        // Fetch PANs if this is a Comprehensive Assessment (needed for pan_select fields)
        if (template?.name?.includes('Comprehensive Assessment') || fullNote.template_name?.includes('Comprehensive Assessment')) {
          try {
            const pansRes = await axios.get(`${API}/treatment-planning/pans`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            });
            setAvailablePANs(pansRes.data);
            void 0 && console.log('[openNote] Fetched PANs for Comprehensive Assessment:', pansRes.data.length);
          } catch (error) {
            console.error('[openNote] Error fetching PANs:', error);
          }
        }
        
        // Merge saved fields with template definitions — shared helper
        // (sections branch, template-ordered flat fields, extras preserved,
        // template_id/template_name carried from the note).
        const mergedContent = mergeNoteContentWithTemplate(noteData, fullNote, template);
        
        setEditedNoteContent(mergedContent);
      } else {
        // Empty content object - initialize from template (only for new/draft notes)
        throw new Error('Empty content structure');
      }
    } catch (e) {
      // Jun 2026 DATA-PRESERVATION: if the note HAS content but it
      // failed to parse (corruption / truncated fetch), blank-initializing
      // from the template is how notes were getting wiped — autosave
      // persisted the blank over the real content. Open VIEW-ONLY instead.
      // Jun 2026 (2) — plain-text content is NOT corruption: legacy /
      // auto-created notes stored a plain string. Only content that
      // LOOKS like JSON ({ or [ prefix) but fails to parse is treated
      // as corrupt; plain text is loaded as legacy content below.
      const rawContent = typeof fullNote.content === 'string' ? fullNote.content.trim() : '';
      const isEmptyStructure = e && e.message === 'Empty content structure';
      const looksLikeJson = rawContent.startsWith('{') || rawContent.startsWith('[');
      let legacyPlainText = null;
      if (rawContent && !isEmptyStructure && !['{}', 'null', '""', '[]'].includes(rawContent)) {
        if (looksLikeJson) {
          console.error('[openNote] Content parse failed for a note with real content — opening view-only to protect it:', e);
          toast.error("This note could not be fully loaded, so it opened in view-only mode to protect its content. Try reopening it.");
          setEditedNoteContent(null);
          contentLoadFailed = true;
        } else {
          // Plain-text legacy content — initialize from the template
          // below and carry the text into the first free-text field.
          legacyPlainText = rawContent;
        }
      }
      if (!contentLoadFailed) {
      // Content is empty or invalid - initialize from template
      
      // For legacy imported notes, use form_data to populate field values
      const legacyFormData = fullNote.form_data || {};
      const isLegacyNote = fullNote.is_legacy_import === true;
      if (isLegacyNote) {
        void 0 && console.log('[openNote] Legacy imported note detected, form_data keys:', Object.keys(legacyFormData));
      }
      
      // Fetch PANs if this is a Comprehensive Assessment (needed for pan_select fields)
      if (fullNote.template_name?.includes('Comprehensive Assessment')) {
        try {
          const pansRes = await axios.get(`${API}/treatment-planning/pans`, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
          });
          setAvailablePANs(pansRes.data);
          void 0 && console.log('[openNote] Fetched PANs for Comprehensive Assessment (catch block):', pansRes.data.length);
        } catch (error) {
          console.error('[openNote] Error fetching PANs:', error);
        }
      }
      
      // Helper to get auto-populated field value for client data fields
      const getAutoPopulatedValue = (field) => {
        const client = allClientsForLookup?.find(c => c.id === noteClientId) || clients?.find(c => c.id === noteClientId);
        
        switch (field.type) {
          case 'client_name':
            return client?.full_name || '';
          case 'client_dob':
            const dobVal = client?.dob || client?.date_of_birth;
            return dobVal ? new Date(dobVal).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
          case 'client_age':
            const dob = client?.dob || client?.date_of_birth;
            const ageYears = dob ? Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000)) : null;
            return ageYears !== null ? `${ageYears} years old` : '';
          case 'client_email':
            return client?.email || '';
          case 'client_phone':
            return client?.phone || '';
          case 'client_address':
            return [client?.address || client?.street || client?.address_line1, client?.address_line2, client?.city, client?.state, client?.zipcode || client?.zip || client?.zip_code].filter(Boolean).join(', ') || '';
          case 'client_allergies':
            return client?.allergies || 'No known allergies';
          case 'client_diagnoses':
            // Use freshDiagnoses which was loaded earlier in openNote
            const clientDx = freshDiagnoses.filter(d => d.client_id === noteClientId && d.is_active);
            void 0 && console.log('[openNote] Auto-populating diagnoses:', clientDx.length, 'found for client', noteClientId);
            return clientDx.length > 0 ? JSON.stringify(clientDx.map(dx => ({
              id: dx.id,
              // Stamp client_id on every snapshot row so the renderer
              // (specialty/index.js) can detect a cross-patient leak
              // and refuse to display it.  PHI-fix May 2026 — same
              // pattern as the medication snapshot fix.
              client_id: dx.client_id || noteClientId,
              code: dx.code || dx.diagnosis_code,
              description: dx.description || dx.diagnosis_description,
              diagnosis_tag: dx.diagnosis_tag,
              start_date: dx.start_date,
              assigned_by_name: dx.assigned_by_name || 'Unknown',
              provider_names: dx.provider_names || [],
              is_primary: dx.is_primary
            }))) : '';
          case 'client_medications':
            // PHI-fix May 2026 — MUST filter by ``client_id ===
            // noteClientId`` so the snapshot can never include
            // another patient's medications when this note happens
            // to be initialized while a different chart is loaded
            // in the global state.  Mirrors the diagnoses path
            // immediately above.  Without this filter the snapshot
            // grabs whatever was last loaded into ``clientMedications``
            // (the LAST OPENED chart), which is the root cause of the
            // production cross-contamination report.
            const clientMeds = freshMedications.filter(m => m.client_id === noteClientId && m.is_active === true);
            void 0 && console.log('[openNote] Auto-populating medications:', clientMeds.length, 'found for client', noteClientId);
            return clientMeds.length > 0 ? JSON.stringify(clientMeds.map(med => ({
              id: med.id,
              // Stamp client_id on every snapshot row so the renderer
              // (specialty/index.js) can detect a cross-patient leak
              // and refuse to display it.  PHI-fix May 2026.
              client_id: med.client_id || noteClientId,
              medication_name: med.medication_name,
              dosage: med.dosage,
              frequency: med.frequency,
              prescriber: med.prescriber,
              purpose: med.purpose
            }))) : '';
          default:
            return null; // Return null to indicate field should use default_value
        }
      };
      
      // Always try to initialize from the note's template_id, regardless of status
      // This fixes the bug where signed comprehensive assessments showed as empty
      // IMPORTANT: Use fullNote (fetched data) not note (original param which may be summary data)
      // IMPORTANT: Use freshTemplates (returned from fetchFormTemplates) not stale formTemplates state
      if (fullNote.template_id) {
        // Find the template and initialize fields
        const template = freshTemplates.find(t => t.id === fullNote.template_id);
        if (template && template.form_fields && template.form_fields.length > 0) {
          const initializedContent = {
            template_id: fullNote.template_id,
            template_name: template.name || fullNote.template_name,
            fields: template.form_fields.map(field => {
              // For legacy notes, use form_data value first
              if (legacyFormData[field.id] !== undefined) {
                return { ...field, value: legacyFormData[field.id] };
              }
              
              // Check if this is an auto-populated field type (client_diagnoses, client_name, etc.)
              const autoValue = getAutoPopulatedValue(field);
              if (autoValue !== null && autoValue !== '') {
                return { ...field, value: autoValue };
              }
              
              // Fall back to default_value
              return { ...field, value: field.default_value || '' };
            })
          };
          void 0 && console.log(`[openNote] Initialized ${initializedContent.fields.length} fields from template: ${template.name}${isLegacyNote ? ' (with legacy form_data)' : ''}`);
          // Carry legacy plain-text content into the first empty
          // free-text field so it isn't lost (Jun 2026).
          if (legacyPlainText) {
            const target = initializedContent.fields.find(f =>
              ['textarea', 'text', 'richtext', 'narrative'].includes(f.type) && !f.value);
            if (target) {
              target.value = legacyPlainText;
            } else {
              initializedContent.fields.push({
                id: 'legacy_plain_text', label: 'Imported Note Text',
                type: 'textarea', value: legacyPlainText,
              });
            }
          }
          setEditedNoteContent(initializedContent);
        } else {
          console.error('[openNote] Template not found or has no fields:', fullNote.template_id);
          setEditedNoteContent({
            fields: legacyPlainText
              ? [{ id: 'legacy_plain_text', label: 'Imported Note Text', type: 'textarea', value: legacyPlainText }]
              : [],
            template_id: fullNote.template_id,
            template_name: fullNote.template_name,
          });
        }
      } else {
        console.error('[openNote] Note has no template_id, cannot initialize fields');
        setEditedNoteContent({
          fields: legacyPlainText
            ? [{ id: 'legacy_plain_text', label: 'Imported Note Text', type: 'textarea', value: legacyPlainText }]
            : [],
        });
      }
      }
    }
    
    // Auto-enter edit mode if:
    // 1. Note is not locked
    // 2. User can edit the note
    // 3. Not forcing view mode (for preview)
    // 4. Or note is a draft (always editable)
    // IMPORTANT: Use fullNote for status checks since note may be summary data
    const isDraft = fullNote.status === 'draft';
    const canEdit = canEditNote(fullNote);
    
    if (!forceViewMode && !contentLoadFailed && fullNote.status !== 'locked' && fullNote.is_locked !== true && (canEdit || (isDraft && (!fullNote.provider_id || fullNote.provider_id === user?.id)))) {
      setIsEditingNote(true);
      // Initialize edited consent from note's existing consent, or preload from linked appointment
      if (fullNote.telehealth_consent?.pos_code) {
        setEditedNoteConsent(fullNote.telehealth_consent);
      } else if (fullNote.appointment_id && linkedApt) {
        // Try to preload from linked appointment (use linkedApt which was fetched earlier)
        if (linkedApt.pos_code) {
          const allPosCodes = getEffectivePosCodes(organizations, posCodes);
          const posObj = allPosCodes.find(p => p.code === linkedApt.pos_code);
          setEditedNoteConsent({
            provider_location: linkedApt.location_id || null,
            pos_code: linkedApt.pos_code,
            pos_description: posObj?.description || '',
            modality: linkedApt.telehealth_modality
              || (linkedApt.telehealth_modifier === '93' ? 'audio_only'
                : linkedApt.telehealth_modifier === '95' ? 'audio_visual'
                : (linkedApt.google_meet_link ? 'audio_visual' : 'in_person'))
          });
        } else {
          setEditedNoteConsent({ provider_location: null, pos_code: null, pos_description: null, modality: null });
        }
      } else {
        setEditedNoteConsent({ provider_location: null, pos_code: null, pos_description: null, modality: null });
      }
      // Initialize linked goals from note's existing goals
      setLinkedGoals(fullNote.linked_goals || []);
    } else {
      setIsEditingNote(false);
      setEditedNoteConsent(null);
      setLinkedGoals(fullNote.linked_goals || []);
    }
  };
  const sigPadRef = useRef(null);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  const [showSignatureAdvanceDialog, setShowSignatureAdvanceDialog] = useState(false);
  const [signedNoteForAdvancement, setSignedNoteForAdvancement] = useState(null);
  // Mar 2026 — CPT × insurance restriction lookup for the post-sign
  // signature-advance dialog (provider flow).  Same logic as the
  // supervisor's Next Steps dialog — if the CPT is restricted for the
  // client's primary insurance, suppress the "Ready for Billing" CTA
  // and force "Mark Complete" instead, since the backend guardrail
  // would otherwise reject the billing transition.
  const [advanceCptBlocked, setAdvanceCptBlocked] = useState(false);
  // Jun 2026 — missing-CPT detection for the same dialog.  Billable
  // notes without a CPT (note, _billing, or linked appointment) can't
  // be marked ready for billing (backend guard 400s) — providers were
  // punting them to supervisors instead of adding the CPT.
  const [advanceCptMissing, setAdvanceCptMissing] = useState(false);
  useEffect(() => {
    if (!showSignatureAdvanceDialog || !signedNoteForAdvancement?.id) {
      setAdvanceCptBlocked(false);
      setAdvanceCptMissing(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const note = signedNoteForAdvancement;
        const cpt = note.cpt_code || note._billing?.cpt_code || note.cpt_code_id;
        if (!cpt || !note.client_id) {
          if (!cancelled) setAdvanceCptBlocked(false);
          // Mirror the backend guard: the linked appointment's CPT also
          // satisfies the requirement — check it before flagging missing.
          if (note.billable && !cpt) {
            let apptHasCpt = false;
            if (note.appointment_id) {
              try {
                const apptResp = await axios.get(`${API}/appointments/${note.appointment_id}`);
                apptHasCpt = !!(apptResp?.data?.cpt_code_id || apptResp?.data?.cpt_code);
              } catch { /* treat as no CPT */ }
            }
            if (!cancelled) setAdvanceCptMissing(!apptHasCpt);
          } else if (!cancelled) {
            setAdvanceCptMissing(false);
          }
          return;
        }
        if (!cancelled) setAdvanceCptMissing(false);
        const resp = await axios.post(`${API}/validate-insurance-cpt`, {
          client_id: note.client_id,
          cpt_code: cpt,
        });
        if (!cancelled) setAdvanceCptBlocked(!!resp?.data?.primary_blocked);
      } catch {
        if (!cancelled) setAdvanceCptBlocked(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showSignatureAdvanceDialog, signedNoteForAdvancement?.id, signedNoteForAdvancement?.cpt_code, signedNoteForAdvancement?.client_id]);
  const [showSignatureContextMenu, setShowSignatureContextMenu] = useState(false);
  const [signatureContextMenuPos, setSignatureContextMenuPos] = useState({ x: 0, y: 0 });
  const [signatureContextMenuType, setSignatureContextMenuType] = useState(null); // 'provider' or 'supervisor'
  
  // Treatment plan signature state
  const [signaturePlanId, setSignaturePlanId] = useState(null);
  const [clientSignature, setClientSignature] = useState(null);
  const [showStaffSignatureDialog, setShowStaffSignatureDialog] = useState(false);
  const [staffSignatureType, setStaffSignatureType] = useState(null);
  const [staffSignature, setStaffSignature] = useState(null);

  // Feb 2026 — `showOptionalAuthPicker` migrated INTO AppointmentScheduleDialog (Stage 10 part 2).

  // iframe-safe confirm() replacement (Feb 2026).
  //
  // Native window.confirm() is silently suppressed inside the Emergent
  // preview iframe sandbox — clicking "Remove signature" appeared to
  // do nothing because the confirm() returned false without ever
  // rendering a dialog.  Production works because no sandbox.  We now
  // route through a promise-backed AlertDialog so the UX is identical
  // in both environments.
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    destructive: false,
    // Set when a caller awaits confirmAction(); resolved on Confirm/Cancel.
    resolver: null,
  });
  // Returns a promise that resolves to true (Confirm) / false (Cancel).
  const confirmAction = (message, opts = {}) => {
    return new Promise((resolve) => {
      setConfirmState({
        open: true,
        title: opts.title || 'Please confirm',
        message: message || '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        destructive: !!opts.destructive,
        resolver: resolve,
      });
    });
  };
  const _closeConfirm = (answer) => {
    setConfirmState((prev) => {
      if (prev.resolver) prev.resolver(answer);
      return { ...prev, open: false, resolver: null };
    });
  };
  
  // Treatment plan editing and versioning state
  const [showEditTypeDialog, setShowEditTypeDialog] = useState(false);
  const [planToEdit, setPlanToEdit] = useState(null);
  const [expandedVersionHistory, setExpandedVersionHistory] = useState(new Set());
  const [versionHistoryData, setVersionHistoryData] = useState({});
  const [archivedPlansExpanded, setArchivedPlansExpanded] = useState(false); // Collapsed by default
  
  // Major review goal progress state
  const [majorReviewGoalProgress, setMajorReviewGoalProgress] = useState([]);
  const [majorReviewNotes, setMajorReviewNotes] = useState('');
  const [majorReviewDate, setMajorReviewDate] = useState('');
  
  // Treatment plan review interface state
  const [showReviewInterface, setShowReviewInterface] = useState(false);
  const [reviewMode, setReviewMode] = useState(null); // 'minor' or 'major'
  const [originalPlan, setOriginalPlan] = useState(null);
  const [editedPlan, setEditedPlan] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isCreatingReview, setIsCreatingReview] = useState(false);
  
  // Debug: Log when review interface state changes
  useEffect(() => {
    if (showReviewInterface) {
      void 0 && console.log('  - originalPlan:', originalPlan);
      void 0 && console.log('  - editedPlan:', editedPlan);
      void 0 && console.log('  - reviewMode:', reviewMode);
    }
  }, [showReviewInterface, originalPlan, editedPlan, reviewMode]);
  // Note: noteJustSigned, selectedNoteStatus are now in NotesContext
  const [showNextStepsDialog, setShowNextStepsDialog] = useState(false);
  // Mar 2026 — CPT × insurance restriction lookup for the post-sign
  // Next Steps dialog.  When the note's CPT is restricted for the
  // client's primary insurance (e.g., Medicare/90882), the dialog
  // shows "Mark as Complete" instead of "Send to Billing" since the
  // billing transition will be rejected by the backend guardrail.
  const [nextStepsCptBlocked, setNextStepsCptBlocked] = useState(false);
  // Re-check CPT × insurance restriction when the Next Steps dialog
  // opens for a fresh note.  We hit the same /api/validate-insurance-cpt
  // endpoint the scheduler uses (Feb 2026 logic) so the supervisor
  // sees the right next-step CTA without us duplicating the rule logic
  // client-side.
  useEffect(() => {
    if (!showNextStepsDialog || !selectedNote?.id) {
      setNextStepsCptBlocked(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cpt = selectedNote.cpt_code
          || selectedNote._billing?.cpt_code
          || selectedNote.cpt_code_id;
        const clientId = selectedNote.client_id;
        if (!cpt || !clientId) {
          if (!cancelled) setNextStepsCptBlocked(false);
          return;
        }
        const resp = await axios.post(`${API}/validate-insurance-cpt`, {
          client_id: clientId,
          cpt_code: cpt,
        });
        if (!cancelled) {
          setNextStepsCptBlocked(!!resp?.data?.primary_blocked);
        }
      } catch {
        if (!cancelled) setNextStepsCptBlocked(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showNextStepsDialog, selectedNote?.id, selectedNote?.cpt_code, selectedNote?.client_id]);
  const [showCorrectionsDialog, setShowCorrectionsDialog] = useState(false);
  const [correctionsReason, setCorrectionsReason] = useState('');
  const [showNoteHistory, setShowNoteHistory] = useState(false);
  const [showSendToClientDialog, setShowSendToClientDialog] = useState(false);
  // clientMessage state moved to SendToClientDialog (component-local)
  // — typing no longer triggers a full-App re-render.
  const [showAdminOverrideDialog, setShowAdminOverrideDialog] = useState(false);
  const [adminOverrideReason, setAdminOverrideReason] = useState('');
  const [showScheduleNextDialog, setShowScheduleNextDialog] = useState(false);
  const [scheduleNextData, setScheduleNextData] = useState({
    timeframe: '2_weeks',
    customDate: '',
    notes: ''
  });
  const [overrideTargetStatus, setOverrideTargetStatus] = useState(null);
  // Note: selectedNotes, selectedClinicalNotes are now in NotesContext
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showNextItemDialog, setShowNextItemDialog] = useState(false);
  
  // Additional Information section
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  
  // Service History section
  const [showServiceHistory, setShowServiceHistory] = useState(false);
  
  // Overview tab collapsible sections
  const [showOverviewAppointments, setShowOverviewAppointments] = useState(false);
  const [showOverviewNotes, setShowOverviewNotes] = useState(false);
  
  // Client insurance editing state (for Overview tab)
  const [showEditClientInsurance, setShowEditClientInsurance] = useState(false);
  const [editingInsuranceType, setEditingInsuranceType] = useState(null); // 'primary', 'secondary', or 'tertiary'
  
  // Client "since" date editing (for backdating imported clients)
  const [showEditClientSinceDialog, setShowEditClientSinceDialog] = useState(false);
  const [editingClientSinceDate, setEditingClientSinceDate] = useState('');
  
  const [clientInsuranceFormData, setClientInsuranceFormData] = useState({
    insurance_name: '',
    member_id: '',
    group_number: '',
    relationship_to_insured: 'self',
    insured_name: '',
    insured_ssn: '',
    insured_dob: '',
    effective_date: '',
    end_date: ''
  });
  
  // New Insurance Management System state
  // Note: Insurance form state and handlers are now in ClientInsuranceDialog component
  const [showInsuranceManagement, setShowInsuranceManagement] = useState(false);
  const [managingInsuranceForClient, setManagingInsuranceForClient] = useState(null);
  // Prefill payload handed to ClientInsuranceDialog when the user
  // clicks "Add to chart" on a 271's "Other Active Coverage" row —
  // saves them retyping the payer name, member ID, group #, and
  // effective dates.  Cleared by the dialog after it consumes it.
  const [insuranceDialogPrefill, setInsuranceDialogPrefill] = useState(null);
  // Note: clientInsurances is now in ClientsContext
  const [selectedClientInsurances, setSelectedClientInsurances] = useState([]); // For client detail view
  
  // Insurance Card Viewing state
  const [viewingInsuranceCards, setViewingInsuranceCards] = useState(null); // { type, front, back }
  const [showInsuranceCardView, setShowInsuranceCardView] = useState(false);

  // Client Billing & Claims state
  // Note: clientBillingSummary, clientClaims, clientPaymentHistory are now in ClientsContext
  const [expandedClaimVersions, setExpandedClaimVersions] = useState(new Set());
  const [showPostPaymentDialog, setShowPostPaymentDialog] = useState(false);
  const [postPaymentTarget, setPostPaymentTarget] = useState(null); // { claim, initialAction }
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showStatementDialog, setShowStatementDialog] = useState(false);
  const [showReprocessDialog, setShowReprocessDialog] = useState(false); // eslint-disable-line no-unused-vars -- Reprocess Claims button removed Feb 2026; useState retained so other useState/hook ordering isn't disturbed
  const [sendingStatement, setSendingStatement] = useState(false);
  // Feb 2026 — uncontrolled ref for the CC field.  The Send Statement
  // dialog lives inside App.js (84K lines) so every controlled-input
  // keystroke re-renders the entire app tree and feels frozen.  We
  // bypass the React state pipeline by holding the value in a ref
  // and only reading it when Preview / Send actually fires.
  const statementCcEmailsRef = React.useRef(null);
  const [statementCcEmails, setStatementCcEmails] = useState('');
  // Preview state — feature added Feb 2026 so the biller can verify
  // the rendered statement before it hits the patient's inbox.
  const [statementPreview, setStatementPreview] = useState(null);
  const [previewingStatement, setPreviewingStatement] = useState(false);
  // Statement history viewer — feature added Feb 2026 so office staff
  // can audit when statements have been sent to a client without
  // digging through the Gmail sent folder.  Loaded lazily when the
  // user expands the "Statement History" pane in the Send dialog.
  const [statementHistory, setStatementHistory] = useState(null);
  const [loadingStatementHistory, setLoadingStatementHistory] = useState(false);
  const [showStatementHistory, setShowStatementHistory] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentType, setPaymentType] = useState('copay');
  const [paymentNotes, setPaymentNotes] = useState('');

  // Treatment Planning state
  const [treatmentPlans, setTreatmentPlans] = useState([]);
  const [loadingTreatmentPlans, setLoadingTreatmentPlans] = useState(false);
  const [showCreatePlanDialog, setShowCreatePlanDialog] = useState(false);
  const [planFieldsCollapsed, setPlanFieldsCollapsed] = useState(false);
  const [editingPlanForMinorEdit, setEditingPlanForMinorEdit] = useState(null); // Track plan being edited for minor edit
  const [isMajorReviewEdit, setIsMajorReviewEdit] = useState(false); // Track if we're doing a major review via the editor
  const [duplicatePlanDetected, setDuplicatePlanDetected] = useState(null); // Track duplicate plan for redirect
  const [selectedTreatmentPlan, setSelectedTreatmentPlan] = useState(null);
  const [showTreatmentPlanDialog, setShowTreatmentPlanDialog] = useState(false);
  const [holdFromClient, setHoldFromClient] = useState(false); // Hold treatment plan from auto-sending to client
  const [availablePANs, setAvailablePANs] = useState([]);
  const [availableGoals, setAvailableGoals] = useState([]);
  const [availableObjectives, setAvailableObjectives] = useState([]);
  const [showAddPANDialog, setShowAddPANDialog] = useState(false);
  const [showAddGoalDialog, setShowAddGoalDialog] = useState(false);
  const [showCustomizeGoalDialog, setShowCustomizeGoalDialog] = useState(false);
  const [selectedGoalTemplate, setSelectedGoalTemplate] = useState(null);
  const [goalCustomization, setGoalCustomization] = useState({
    customized_text: '',
    client_goal_description: '',
    client_barriers: '',
    client_strengths: '',
    duration_days: 90, // Default 90 days
    custom_duration: '',
    notes: ''
  });
  const [showAddObjectiveDialog, setShowAddObjectiveDialog] = useState(false);
  const [selectedPANForGoal, setSelectedPANForGoal] = useState(null);
  const [selectedGoalForObjective, setSelectedGoalForObjective] = useState(null);
  const [showEditObjectiveDialog, setShowEditObjectiveDialog] = useState(false);
  const [objectiveToEdit, setObjectiveToEdit] = useState(null);
  const [showEditGoalDialog, setShowEditGoalDialog] = useState(false);
  const [goalToEdit, setGoalToEdit] = useState(null);
  const [showVersionHistoryDialog, setShowVersionHistoryDialog] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [isSavingGoal, setIsSavingGoal] = useState(false);
  const [isSavingObjective, setIsSavingObjective] = useState(false);
  const [showEditPlanSettingsDialog, setShowEditPlanSettingsDialog] = useState(false);
  const [planSettingsToEdit, setPlanSettingsToEdit] = useState(null);
  const [reviewDateEditPlan, setReviewDateEditPlan] = useState(null);
  const [reviewDateEditValue, setReviewDateEditValue] = useState('');
  const [showCloseGoalDialog, setShowCloseGoalDialog] = useState(false);
  const [goalToClose, setGoalToClose] = useState(null);
  const [goalCloseReason, setGoalCloseReason] = useState('');
  const [goalCloseNotes, setGoalCloseNotes] = useState('');
  const [newPlanServiceType, setNewPlanServiceType] = useState('');
  const [newPlanProviderId, setNewPlanProviderId] = useState('');
  const [newPlanVisitFrequency, setNewPlanVisitFrequency] = useState('');
  const [newPlanStartDate, setNewPlanStartDate] = useState('');
  const [isLegacyPlan, setIsLegacyPlan] = useState(false); // Legacy plan - skip signature requirement
  // Note: clientServiceEnrollments is now in ClientsContext
  const [panCategoryFilter, setPANCategoryFilter] = useState('');
  const [panSearchQuery, setPANSearchQuery] = useState('');
  // Custom PAN state
  const [showCustomPANInput, setShowCustomPANInput] = useState(false);
  const [customPANName, setCustomPANName] = useState('');
  const [customPANDescription, setCustomPANDescription] = useState('');
  const [customPANCategory, setCustomPANCategory] = useState('');
  // Streamlined dialog state
  const [planPANs, setPlanPANs] = useState([]); // [{pan_id, pan_name, goals: [{goal_id, goal_text, objectives: []}]}]
  const [expandedPANs, setExpandedPANs] = useState(new Set()); // Track which PANs are expanded
  const [expandedPlans, setExpandedPlans] = useState(new Set()); // Track which treatment plans are expanded
  const [clinicalContextCollapsed, setClinicalContextCollapsed] = useState(new Set()); // Track collapsed clinical context per plan
  const [expandedGoals, setExpandedGoals] = useState(new Set()); // Track which goals are expanded
  // Legacy treatment plan import
  const [showLegacyImport, setShowLegacyImport] = useState(false);
  // Signature workflow state (declarations moved to line ~1488 to avoid duplication)
  const [signatureType, setSignatureType] = useState(null); // 'provider', 'supervisor', 'client'
  // Inline form states
  const [showInlinePANSelector, setShowInlinePANSelector] = useState(false);
  const [showInlinePANGoalSelector, setShowInlinePANGoalSelector] = useState(null); // panIdx
  const [showInlineObjectiveForm, setShowInlineObjectiveForm] = useState(null); // "panIdx-goalIdx"
  const [editingObjectiveIndex, setEditingObjectiveIndex] = useState(null); // Index of objective being edited in inline form
  const [interventionFormKey, setInterventionFormKey] = useState(0); // Key to force re-render of intervention selects
  const [showInterventionLibrary, setShowInterventionLibrary] = useState(false);
  const [interventionLibraryTarget, setInterventionLibraryTarget] = useState(null); // { panIdx, goalIdx }
  const [pendingInterventions, setPendingInterventions] = useState({}); // Map of "panIdx-goalIdx" -> interventions array
  const [showEditInterventionLibrary, setShowEditInterventionLibrary] = useState(false); // For edit objective dialog
  const [showAddObjectiveInterventionLibrary, setShowAddObjectiveInterventionLibrary] = useState(false); // For add objective dialog
  const [inlineInterventionSelector, setInlineInterventionSelector] = useState(null); // "panIdx-goalIdx" key or null
  const [interventionCategories, setInterventionCategories] = useState([]);
  const [interventionSearchQuery, setInterventionSearchQuery] = useState('');
  const [interventionCategoriesLoading, setInterventionCategoriesLoading] = useState(false);
  const [addObjectiveInterventions, setAddObjectiveInterventions] = useState([]); // Interventions for add objective form
  const [editingInterventionIdx, setEditingInterventionIdx] = useState(null); // Index of intervention being edited inline
  
  // Refs for auto-scrolling in treatment plan dialog
  const panSectionRef = useRef(null);
  const inlinePANSelectorRef = useRef(null);
  const goalSelectorRef = useRef(null);
  const objectiveFormRef = useRef(null);
  const treatmentPlanDialogRef = useRef(null);
  
  const [objectiveFormData, setObjectiveFormData] = useState({
    responsible_party: 'client',
    objective_text: '',
    intervention: '',
    custom_intervention: '',
    treatment_description: '',
    treatment_modality: '',
    frequency: ''
  });

  // Treatment plan metadata state (medications, diagnoses, supports, etc.)
  const DEFAULT_SUPPORT_ROLES = [
    'PCP', 'Psychiatrist/Psych NP', 'Therapist (outpatient)', 'Therapist (In Home/Behavioral)',
    'TM', 'TT&S', 'DCF Worker', 'ICC', 'School (include grade)',
    'Probation Officer', 'Parent/Guardian', 'Emergency Contact', 'Guardian Ad Litem'
  ];
  const [tpMedications, setTpMedications] = useState([]);
  const [tpDiagnoses, setTpDiagnoses] = useState([]);
  const [tpLevelOfCareNotes, setTpLevelOfCareNotes] = useState('');
  const [tpTraumaReports, setTpTraumaReports] = useState('None reported');
  const [tpSupportsNoneReported, setTpSupportsNoneReported] = useState(false);
  const [tpSupports, setTpSupports] = useState([]);
  const [tpMetadataLoaded, setTpMetadataLoaded] = useState(false);
  const [tpClinicalContextCollapsed, setTpClinicalContextCollapsed] = useState(true);

  // Sync treatment plan snapshots when client data changes (e.g. after adding via existing dialogs)
  useEffect(() => {
    if (!showCreatePlanDialog) return;
    const clientId = editingPlanForMinorEdit?.client_id || selectedClient?.id;
    if (!clientId) return;
    const activeMeds = (clientMedications || []).filter(m => m.client_id === clientId && m.is_active === true);
    setTpMedications(activeMeds.map(m => ({
      name: m.medication_name || m.name || '',
      dosage: m.dosage || '',
      frequency: m.frequency || '',
      prescriber: m.prescriber || '',
      purpose: m.purpose || '',
      status: 'active'
    })));
    const activeDiags = (clientDiagnoses || []).filter(d => d.client_id === clientId && d.is_active !== false);
    setTpDiagnoses(activeDiags.map(d => ({
      code: d.code || d.diagnosis_code || '',
      description: d.description || d.diagnosis_description || d.diagnosis_name || '',
      status: d.status || 'active',
      is_primary: d.is_primary || false,
      assigned_by: d.assigned_by || '',
      provider_names: d.provider_names || [],
      assigned_by_name: d.assigned_by_name || ''
    })));
  // eslint-disable-next-line
  }, [clientDiagnoses, clientMedications]);
  // Note: serviceTypes is now in StaticDataContext
  const [showServiceTypeDialog, setShowServiceTypeDialog] = useState(false);
  const [showManageServiceTypesDialog, setShowManageServiceTypesDialog] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState(null);
  const [serviceTypeFormData, setServiceTypeFormData] = useState({
    name: '',
    display_name: '',
    short_name: '',
    description: '',
    required_documents: [],
    requirements: '',
    notes: '',
    is_active: true,
    requires_discharge: true
  });
  
  // Document Deadline Rules state
  const [documentRules, setDocumentRules] = useState([]);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [showManageRulesDialog, setShowManageRulesDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleServiceFilter, setRuleServiceFilter] = useState('all');
  
  // Custom Goal/Objective form state
  const [customGoalText, setCustomGoalText] = useState('');
  const [clientGoalDescription, setClientGoalDescription] = useState('');
  const [goalBarriers, setGoalBarriers] = useState('');
  const [goalStrengths, setGoalStrengths] = useState('');
  const [goalDuration, setGoalDuration] = useState(90); // Default 90 days
  const [customGoalDuration, setCustomGoalDuration] = useState('');
  const [goalNotes, setGoalNotes] = useState('');
  const [customObjectiveText, setCustomObjectiveText] = useState('');
  const [objectiveIntervention, setObjectiveIntervention] = useState('');
  const [objectiveFrequency, setObjectiveFrequency] = useState('');
  const [objectiveModality, setObjectiveModality] = useState('');
  

  const [holdInEscrow, setHoldInEscrow] = useState(false);
  const [selectedClaimsForPayment, setSelectedClaimsForPayment] = useState([]);
  const [selectedClaimsForInvoice, setSelectedClaimsForInvoice] = useState([]);
  const [selectedClaimsForReprocess, setSelectedClaimsForReprocess] = useState([]);
  

  
  // Contact management state
  const [showAddContact, setShowAddContact] = useState(false);
  const [showEditContact, setShowEditContact] = useState(false);
  const [showInactiveReasonDialog, setShowInactiveReasonDialog] = useState(false);
  const [inactiveClientId, setInactiveClientId] = useState(null);
  const [inactiveReason, setInactiveReason] = useState('');
  const [showBillingReviewDialog, setShowBillingReviewDialog] = useState(false);
  const [billingReviewClientId, setBillingReviewClientId] = useState(null);
  const [billingReviewNotes, setBillingReviewNotes] = useState('');
  const [showServiceDisenrollmentDialog, setShowServiceDisenrollmentDialog] = useState(false);
  const [dischargeCheckInfo, setDischargeCheckInfo] = useState(null);
  const [dischargeTaskInfo, setDischargeTaskInfo] = useState(null);  // Info from discharge_required task for pre-populating discharge form
  const [selectedServicesToDisenroll, setSelectedServicesToDisenroll] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  
  // Release of Information state
  // Note: clientReleases is now in ClientsContext
  const [showCreateRelease, setShowCreateRelease] = useState(false);
  const [releaseClientData, setReleaseClientData] = useState(null); // Store client data for release dialog
  const [showArchivedReleases, setShowArchivedReleases] = useState(false); // Toggle for archived releases section
  const [showReleaseDetail, setShowReleaseDetail] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState(null);
  
  const [releaseFormData, setReleaseFormData] = useState({
    direction: 'both',
    linked_contact_id: '',
    recipient_name: '',
    contact_person: '',
    address: '',
    suite: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    phone_ext: '',
    fax: '',
    email: '',
    date_range_type: 'all',
    date_from: '',
    date_to: '',
    info_entire_medical: false,
    info_non_medical: false,
    info_progress_therapy: false,
    info_progress_psychiatric: false,
    info_assessments: false,
    info_psych_testing: false,
    info_treatment_summary: false,
    info_discharge_summary: false,
    info_school_records: false,
    info_attendance_only: false,
    info_insurance_claims: false,
    info_substance: false,
    info_substance_description: '',
    info_other: false,
    info_other_description: '',
    purpose_assessment: false,
    purpose_continuity: false,
    purpose_substance: false,
    purpose_substance_description: '',
    purpose_treatment_summary: false,
    purpose_discharge_summary: false,
    purpose_school_records: false,
    purpose_attendance_only: false,
    purpose_insurance: false,
    purpose_other: false,
    purpose_other_text: '',
    purpose_other_description: '',
    expiration_type: 'default_90_days',
    expiration_date: '',
    expiration_event: '',
    member_refused: false,
    verbal_consent: false,
    client_instructions: '',
    special_reason: ''
  });
  
  // Change Request state
  const [showChangeRequestDialog, setShowChangeRequestDialog] = useState(false);
  const [changeRequestSection, setChangeRequestSection] = useState(null);
  const [changeRequestFields, setChangeRequestFields] = useState([]);
  const [changeRequestReason, setChangeRequestReason] = useState('');
  const [changeRequestProposedChanges, setChangeRequestProposedChanges] = useState({});
  
  // Location management state
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [showRequestGroupDialog, setShowRequestGroupDialog] = useState(false);
  const [pendingGroupRequests, setPendingGroupRequests] = useState([]);
  const [requestGroupData, setRequestGroupData] = useState({
    client_id: '',
    group_id: '',
    request_reason: ''
  });
  
  // Group Approval Dialog state
  const [showGroupApprovalDialog, setShowGroupApprovalDialog] = useState(false);
  const [groupApprovalRequest, setGroupApprovalRequest] = useState(null);
  const [groupApprovalData, setGroupApprovalData] = useState({
    start_date: '',
    add_to_future_appointments: true
  });
  
  // Medication Management state
  const [showMedicationReferralDialog, setShowMedicationReferralDialog] = useState(false);
  const [showMedicationManagerDashboard, setShowMedicationManagerDashboard] = useState(false);
  const [selectedReferralForDetails, setSelectedReferralForDetails] = useState(null);
  const [editingReferralFormData, setEditingReferralFormData] = useState(null); // For editing form data in response mode
  const [showApprovalProviderDropdown, setShowApprovalProviderDropdown] = useState(null); // stores request.id when showing dropdown
  const [selectedMedReferralProvider, setSelectedMedReferralProvider] = useState(''); // Selected provider for med referral approval
  const [selectedMedReferralService, setSelectedMedReferralService] = useState(''); // Selected service for med referral approval
  const [customPromptDialog, setCustomPromptDialog] = useState({ open: false, title: '', placeholder: '', onSubmit: null }); // For styled prompts
  const [medicationReferralTemplate, setMedicationReferralTemplate] = useState(null);
  const [medicationReferralRequests, setMedicationReferralRequests] = useState([]);
  const [medicationReferralFormData, setMedicationReferralFormData] = useState({});
  const [medicationManagerUsers, setMedicationManagerUsers] = useState([]);
  const [waitlistClients, setWaitlistClients] = useState([]); // Clients approved but not assigned
  const [dedicatedWaitlistClients, setDedicatedWaitlistClients] = useState([]); // Separate waitlist data (doesn't affect Client Management)
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [viewingReferral, setViewingReferral] = useState(null); // Currently viewing medication referral
  const [showMedicationReferralView, setShowMedicationReferralView] = useState(false); // Show medication referral dialog
  const [selectedMedicationManagers, setSelectedMedicationManagers] = useState([]);
  const [medicationReferralClient, setMedicationReferralClient] = useState(null); // Store client for referral form
  const [generatingAISummary, setGeneratingAISummary] = useState(false);

  // ── V2 → Legacy bridge for the medication referral dialog ─────
  // The V2 chart's native Meds & Vitals tab (Phase 2E) can't mount
  // the ~1,250-line referral dialog inline — it depends on too much
  // App-root scope.  The V2 wrapper dispatches `v2:open-medication-
  // referral` with the prefilled client + formData + selected
  // managers, and we forward to the existing state setters here.
  // Phase 2 of the migration will extract the dialog and remove
  // this bridge.
  //
  // Note: we don't trigger `fetchMedicationReferralTemplate` here —
  // App.js loads the template list on mount (via the formTemplates
  // flow), and the dialog's existing onOpenChange handles the
  // missing-template fallback fetch.
  useEffect(() => {
    const onOpenReferral = (e) => {
      const { client: refClient, formData, managers } = e.detail || {};
      if (refClient) setMedicationReferralClient(refClient);
      if (formData) setMedicationReferralFormData(formData);
      if (managers) setSelectedMedicationManagers(managers);
      setShowMedicationReferralDialog(true);
    };
    const onOpenDetails = (e) => {
      const { referral } = e.detail || {};
      if (referral) setSelectedReferralForDetails(referral);
    };
    window.addEventListener('v2:open-medication-referral', onOpenReferral);
    window.addEventListener('v2:open-referral-details', onOpenDetails);
    return () => {
      window.removeEventListener('v2:open-medication-referral', onOpenReferral);
      window.removeEventListener('v2:open-referral-details', onOpenDetails);
    };
  }, []);
  
  // Medication Waitlist Filters
  const [medWaitlistSearch, setMedWaitlistSearch] = useState('');
  const [medWaitlistSort, setMedWaitlistSort] = useState('date_desc'); // date_desc, date_asc, name_asc, name_desc
  const [medWaitlistStatusFilter, setMedWaitlistStatusFilter] = useState(['pending', 'approved', 'needs_more_info', 'denied']); // All statuses by default
  const [medWaitlistItemsPerSection, setMedWaitlistItemsPerSection] = useState(25);
  const [isRefreshingMedWaitlist, setIsRefreshingMedWaitlist] = useState(false);
  
  // Note: brandingSettings is now in StaticDataContext
  
  // 2FA/Security settings (Admin)
  const [twoFAOrgSettings, setTwoFAOrgSettings] = useState({
    require_2fa_for_all: false,
    require_2fa_for_admins: false,
    require_2fa_for_providers: false,
    require_2fa_for_supervisors: false,
    require_2fa_for_office: false,
    require_2fa_for_reception: false,
    require_2fa_for_interns: false,
    allow_email_backup: true,
    allow_recovery_codes: true
  });
  const [show2FAUserManagement, setShow2FAUserManagement] = useState(false);
  const [selectedUserFor2FA, setSelectedUserFor2FA] = useState(null);
  const [user2FAStatus, setUser2FAStatus] = useState(null);
  
  // Audit Settings (Admin - HIPAA Compliance)
  const [auditSettings, setAuditSettings] = useState({
    verbose_audit_logging: false,
    chart_access_window_minutes: 30
  });
  
  // Domain Whitelist Settings (Admin)
  const [domainWhitelistSettings, setDomainWhitelistSettings] = useState({
    enabled: false,
    allowed_domains: []
  });
  const [newDomainInput, setNewDomainInput] = useState('');
  
  // Beta Gate Settings (Admin)
  const [betaGateSettings, setBetaGateSettings] = useState({
    beta_enabled: false,
    beta_message: 'Please enter your invite code to continue.'
  });
  const [betaGateLoading, setBetaGateLoading] = useState(false);
  
  // Billing Settings (Admin)
  const [billingSettings, setBillingSettings] = useState({
    billing_enabled: true
  });
  const [billingSettingsLoading, setBillingSettingsLoading] = useState(false);
  
  // Google Calendar Integration Status
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({
    status: 'loading',
    is_mock: true
  });
  
  // Backup Status (Admin)
  const [backupStatus, setBackupStatus] = useState({
    status: 'loading',
    last_backup: null,
    backup_status: 'unknown',
    bucket_names: [],
  });
  const [runningBackup, setRunningBackup] = useState(false);
  
  // Security Dashboard State (Admin)
  const [showSecurityDashboard, setShowSecurityDashboard] = useState(false);
  const [securityStats, setSecurityStats] = useState(null);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [securityLogsLoading, setSecurityLogsLoading] = useState(false);
  const [securityLogsTotal, setSecurityLogsTotal] = useState(0);
  const [securityLogsPage, setSecurityLogsPage] = useState(1);
  const [securityFilter, setSecurityFilter] = useState('all'); // 'all', 'high-risk', 'chart-access', 'failed-login'
  const [securityClientSearch, setSecurityClientSearch] = useState('');
  const [securityUserFilter, setSecurityUserFilter] = useState('');
  const [securityDateRange, setSecurityDateRange] = useState({ start: '', end: '' });
  const [securityClientReport, setSecurityClientReport] = useState(null);
  const [securityClientReportLoading, setSecurityClientReportLoading] = useState(false);
  const [showHIPAAReportModal, setShowHIPAAReportModal] = useState(false);
  const [hipaaReportClientId, setHipaaReportClientId] = useState('');
  const [hipaaReportDays, setHipaaReportDays] = useState(365);
  const [archiveStats, setArchiveStats] = useState(null);
  const [integrityStatus, setIntegrityStatus] = useState(null);
  const [integrityVerifying, setIntegrityVerifying] = useState(false);
  
  // Data Recovery Tools (Super Admin)
  const [showDecryptionTool, setShowDecryptionTool] = useState(false);
  const [showUUIDResolver, setShowUUIDResolver] = useState(false);
  const [showUUIDLookup, setShowUUIDLookup] = useState(false);
  const [showLegacyNoteImport, setShowLegacyNoteImport] = useState(false);
  const [legacyTemplateFixResult, setLegacyTemplateFixResult] = useState(null);
  const [legacyTemplateFixLoading, setLegacyTemplateFixLoading] = useState(false);
  
  // Legacy Notes Completion (Admin)
  const [legacyNotesCompletionResult, setLegacyNotesCompletionResult] = useState(null);
  const [legacyNotesCompletionLoading, setLegacyNotesCompletionLoading] = useState(false);
  const [legacyNotesCompletionStatus, setLegacyNotesCompletionStatus] = useState(null);
  
  // Waitlist Settings (Admin)
  const [showWaitlistCommunicationSettings, setShowWaitlistCommunicationSettings] = useState(false);
  const [waitlistSettings, setWaitlistSettings] = useState({
    enabled: true,
    check_in_frequency_days: 14,
    include_remove_link: true,
    include_update_info_link: true,
    include_confirm_link: true,
    max_check_ins: 6,
    auto_remove_after_no_response: 0,
    email_subject: "Are you still interested in services?",
    email_intro: "We wanted to check in with you about your place on our waitlist.",
    welcome_email_enabled: true,
    welcome_email_subject: "Welcome to Our Waitlist",
    welcome_email_body: `You have been added to our waitlist for services.

**What to expect:**
- We will reach out periodically to confirm you're still interested in services
- When a provider becomes available and you've been assigned, you'll hear directly from them

**While you wait:**
- You can remove yourself from the waitlist at any time using the link below
- If your insurance or contact information changes, please update it so we have accurate records

**Update Your Information:** {{update_link}}

**Remove Yourself from Waitlist:** {{remove_link}}

If you have any questions, please don't hesitate to contact us.

Thank you for your patience.`
  });
  
  // Billing state (additional)
  const [claimDetail, setClaimDetail] = useState(null);
  const [billingProviderFilter, setBillingProviderFilter] = useState(() => {
    return localStorage.getItem('billingProviderFilter') || 'all';
  });
  // Include/exclude mode for provider filter — shared across BillingContext via localStorage
  const [billingProviderFilterMode, setBillingProviderFilterMode] = useState(() => {
    return localStorage.getItem('billingProviderFilterMode') === 'exclude' ? 'exclude' : 'include';
  });
  const [billingStatusFilter, setBillingStatusFilter] = useState('ready_for_billing');
  const [billingSearchTerm, setBillingSearchTerm] = useState('');
  const [showClaimDetail, setShowClaimDetail] = useState(false);
  const [loadingClaimDetail, setLoadingClaimDetail] = useState(false);

  // ── Claims Queue (extracted to a hook Feb 2026) ──────────────────
  // Just the data layer.  Dialog state, bulk-select sets, claim
  // handlers all stay inline since they're tightly coupled to specific
  // JSX flows (see useClaimsQueue.js header for the rationale).
  const {
    claimsQueue,
    claimsQueueRefreshKey,
    claimsSortColumn,
    claimsSortDirection,
    loadingClaims,
    setClaimsQueue,
    setClaimsSortColumn,
    setClaimsSortDirection,
    setLoadingClaims,
    fetchClaimsQueue,
  } = useClaimsQueue({ user, isSuperAdmin, billingProviderFilter, billingStatusFilter });
  // Note: notesReadyForBilling, selectedNoteForReview, noteReviewData, showNoteReviewDialog, notesSentForCorrection are now in NotesContext
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
  const [showSendBackDialog, setShowSendBackDialog] = useState(false);
  // Feb 2026 — non-billable status (Worcester clinic request).
  // Distinct from "Send Back for Corrections" (which goes to the
  // provider): this is a terminal billing-side decision —
  // timely-filing expired, no coverage, non-covered service, missing
  // auth, write-off, or other.  Off provider todo + biller queue.
  // Any biller can mark or restore.  See NoteStatus.NOT_BILLABLE
  // and the not_billable_reason / not_billable_comment fields the
  // backend stamps in routes/notes.put on transition.
  const [showNotBillableDialog, setShowNotBillableDialog] = useState(false);
  const [notBillableReason, setNotBillableReason] = useState('');
  const [notBillableComment, setNotBillableComment] = useState('');
  // Mar 2026 — generic target note for the Mark Non-Billable dialog.
  // Default source is `noteReviewData?.note` (Prepare Claim flow);
  // signature next-steps dialogs override this so the same dialog
  // can mark any selected note as non-billable.
  const [notBillableTargetNote, setNotBillableTargetNote] = useState(null);
  // correctionReason state moved to NeedsCorrectionsDialog (component-local).
  const [billingPreviewClaimId, setBillingPreviewClaimId] = useState(null);
  const [dryRunPreviewData, setDryRunPreviewData] = useState(null);
  const [loadingDryRun, setLoadingDryRun] = useState(false);
  // Service line edit state for Prepare Claim dialog
  const [editingServiceLine, setEditingServiceLine] = useState(false);
  const [serviceLineEdits, setServiceLineEdits] = useState({});
  const [savingServiceLine, setSavingServiceLine] = useState(false);
  // Insurance management in Prepare Claim dialog
  const [showPrepareInsuranceMgmt, setShowPrepareInsuranceMgmt] = useState(false);
  // Legacy-bill single note from Prepare Claim dialog
  const [showSingleLegacyMarkDialog, setShowSingleLegacyMarkDialog] = useState(false);
  // "Link to Existing Claim" — used when a note is orphaned from a real
  // claim (legacy data corruption) and the biller wants to re-attach
  // instead of creating a duplicate.  See backend
  // /api/billing/notes/{note_id}/candidate-claims for the picker source.
  const [showLinkExistingClaimDialog, setShowLinkExistingClaimDialog] = useState(false);
  const [candidateClaims, setCandidateClaims] = useState([]);
  const [candidateClaimsLoading, setCandidateClaimsLoading] = useState(false);
  const [linkingClaimId, setLinkingClaimId] = useState(null);
  const [manualClaimNumber, setManualClaimNumber] = useState('');
  
  // Billing filters with persistence
  const [billingClientFilter, setBillingClientFilter] = useState(() => {
    try {
      const stored = localStorage.getItem('billingClientFilter');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) { /* legacy string */ }
    const legacy = localStorage.getItem('billingClientFilter');
    if (legacy && legacy !== 'all') return [legacy];
    return [];
  });
  const [billingClientFilterMode, setBillingClientFilterMode] = useState(() => {
    return localStorage.getItem('billingClientFilterMode') === 'exclude' ? 'exclude' : 'include';
  });
  const [billingInsuranceFilter, setBillingInsuranceFilter] = useState(() => {
    // Stored as JSON array; tolerate legacy 'all' / single-string values
    // gracefully so an upgrade doesn't strand existing users with a
    // filter that the popover can't render.
    try {
      const stored = localStorage.getItem('billingInsuranceFilter');
      if (stored) {
        if (stored.startsWith('[')) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) return parsed;
        }
        if (stored !== 'all') return [stored];
      }
    } catch (e) { /* fall through */ }
    return [];
  });
  const [billingDateFrom, setBillingDateFrom] = useState(() => {
    return localStorage.getItem('billingDateFrom') || '';
  });
  // Eligibility Management state — moved to EligibilityTab.jsx (self-contained)
  
  const [billingDateTo, setBillingDateTo] = useState(() => {
    return localStorage.getItem('billingDateTo') || '';
  });

  // Mar 2026 — billing-list sort lifted from BillingTab into App.js so
  // the "Next Claim" / "Previous Claim" navigation inside the Prepare
  // Claim dialog can walk the SAME sorted+filtered list the biller
  // sees on screen.  Bug report: clicking Next Claim used to jump to
  // a different clinician's claim because the dialog navigated by
  // Mongo insertion order (no sort), ignoring the column-header sort.
  // See `sortedFilteredBillingNotes` below and BillingTab.jsx props.
  const [billingSortColumn, setBillingSortColumn] = useState(null);
  const [billingSortDirection, setBillingSortDirection] = useState('asc');
  
  // Note: Notes archive search state (notesDateRange, notesCustomStartDate, notesCustomEndDate, notesClientFilter, notesStatusFilter) 
  // are now in NotesContext
  const [showArchivedGroups, setShowArchivedGroups] = useState(false);
  const [expandedNoteFormTypes, setExpandedNoteFormTypes] = useState({}); // Track which form type sections are expanded

  // Reference note panel (side-by-side comparison)
  const [showReferencePanel, setShowReferencePanel] = useState(false);
  
  // Note dialog size presets — persists in sessionStorage
  const [noteDialogSize, setNoteDialogSize] = useState(() => sessionStorage.getItem('noteDialogSize') || 'full');
  const updateNoteDialogSize = (size) => {
    setNoteDialogSize(size);
    sessionStorage.setItem('noteDialogSize', size);
  };
  const NOTE_SIZE_PRESETS = {
    'split': { width: '50vw', label: 'Split', icon: 'columns' },
    'medium': { width: '75vw', label: '75%', icon: 'square' },
    'full': { width: '95vw', label: 'Full', icon: 'maximize' },
  };
  const [showCreateNoteRefPanel, setShowCreateNoteRefPanel] = useState(false);

  
  // Note creation state
  // Note: showAddNote, selectedNoteProvider, selectedNoteClients, newNoteFormData, 
  // noteDialogHeaderCollapsed, noteClientLocked, selectedNoteGroupId are now in NotesContext
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isGroupNote, setIsGroupNote] = useState(false);
  // Note: selectedAppointment is now in AppointmentsContext
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [templateSearchOpen, setTemplateSearchOpen] = useState(false);
  const [autosaveNoteId, setAutosaveNoteId] = useState(null); // Track the draft note ID for autosave
  const autosaveNoteIdRef = useRef(null); // SYNC ref to prevent race condition on save
  const [lastAutosave, setLastAutosave] = useState(null); // Track last autosave time
  const [isAutosaving, setIsAutosaving] = useState(false); // Show autosave indicator
  const [curriculumSessionData, setCurriculumSessionData] = useState(null); // Curriculum session data for group notes
  
  // Template change warning state
  const [showTemplateChangeWarning, setShowTemplateChangeWarning] = useState(false);
  const [pendingTemplateChange, setPendingTemplateChange] = useState(null); // Stores the template user wants to switch to
  const isNoteFormDirtyRef = useRef(false); // Track if user has typed in the form
  
  // Close note warning state (for Add Note dialog)
  const [showCloseNoteWarning, setShowCloseNoteWarning] = useState(false);
  
  // Close note warning state (for editing existing notes)
  const [showCloseEditingNoteWarning, setShowCloseEditingNoteWarning] = useState(false);
  
  // Handler for updating form data that also sets dirty flag
  const updateNewNoteFormData = useCallback((updater) => {
    isNoteFormDirtyRef.current = true; // Mark as dirty when user types
    setNewNoteFormData(updater);
  }, [setNewNoteFormData]);

  // Carry Forward: pre-fill flagged fields from the client's most recent
  // completed note by the same provider using the same template.
  const carryForwardKeyRef = useRef(null);
  useEffect(() => {
    if (!showAddNote) { carryForwardKeyRef.current = null; return; }
    if (!selectedTemplate?.id || autosaveNoteId) return;
    if (!selectedTemplate.form_fields?.some(f => f.is_carry_forward)) return;
    const clientId = selectedNoteClients?.length === 1 ? selectedNoteClients[0] : null;
    if (!clientId) return;
    const providerId = selectedNoteProvider || user?.id;
    const key = `${selectedTemplate.id}:${clientId}:${providerId}`;
    if (carryForwardKeyRef.current === key) return;
    carryForwardKeyRef.current = key;
    axios.get(`${API}/notes/carry-forward`, {
      params: { client_id: clientId, template_id: selectedTemplate.id, ...(providerId ? { provider_id: providerId } : {}) }
    }).then(res => {
      const values = res.data?.values || {};
      if (!Object.keys(values).length) return;
      setNewNoteFormData(prev => {
        const next = { ...(prev || {}) };
        let changed = false;
        Object.entries(values).forEach(([fid, val]) => {
          if (next[fid] === undefined || next[fid] === null || next[fid] === '') {
            next[fid] = val;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }).catch(() => {});
  }, [showAddNote, selectedTemplate, selectedNoteClients, selectedNoteProvider, autosaveNoteId, user?.id, setNewNoteFormData]);

  // ─── Plan B: Lifted note-edit helpers ──────────────────────────────
  // These three helpers were originally defined deep inside the JSX
  // render block (around line 53400). Lifting them here + wrapping in
  // useCallback gives them STABLE function identities across App
  // re-renders, which (a) prevents wasteful re-renders of the note
  // form renderer subtree and (b) is a prerequisite for the future
  // React.memo wrapping (Plan C). Behavior is byte-identical to the
  // legacy inline definitions — only memoization changes.

  // Cheap "did this value actually change?" check used by the field
  // bridges below. Avoids the expensive double JSON.stringify the
  // legacy bridge did on every keystroke flush. Falls back to a
  // shallow object comparison only for non-primitive values, since
  // string textarea values (the 95% case) are reference-equal when
  // unchanged.
  const _bridgeValueChanged = useCallback((a, b) => {
    if (a === b) return false;
    // null/undefined or primitive change → definitely different
    if (a == null || b == null) return true;
    if (typeof a !== 'object' || typeof b !== 'object') return true;
    // Both are objects/arrays. Use shallow key/length compare. If a
    // deep change is missed, the next keystroke will re-trigger this
    // check anyway — and for our field-value shape (strings, simple
    // arrays, small objects), shallow is sufficient. The legacy code
    // used JSON.stringify which is also lossy for Date/undefined and
    // significantly slower.
    if (Array.isArray(a) !== Array.isArray(b)) return true;
    if (Array.isArray(a)) {
      if (a.length !== b.length) return true;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return true;
      return false;
    }
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return true;
    for (const k of ka) if (a[k] !== b[k]) return true;
    return false;
  }, []);

  // Updates a single field value inside `editedNoteContent`. Works
  // with both the modern `sections[].fields[]` and legacy `fields[]`
  // shapes. Stable identity (only depends on setEditedNoteContent,
  // which React guarantees is stable).
  const updateFieldValueLifted = useCallback((fieldId, newValue) => {
    setEditedNoteContent(prev => {
      if (!prev) {
        console.warn('[updateFieldValue] No editedNoteContent, returning early');
        return prev;
      }
      // Sections-based structure
      if (prev.sections) {
        let fieldFound = false;
        const updatedSections = prev.sections.map(section => ({
          ...section,
          fields: section.fields?.map(f => {
            if (f.id === fieldId) {
              fieldFound = true;
              return { ...f, value: newValue };
            }
            return f;
          })
        }));
        if (!fieldFound) {
          console.warn('[updateFieldValue] Field not found in sections:', fieldId);
        }
        return { ...prev, sections: updatedSections };
      }
      // Flat fields structure
      if (prev.fields) {
        const existingFieldIndex = prev.fields.findIndex(f => f.id === fieldId);
        let updatedFields;
        if (existingFieldIndex >= 0) {
          updatedFields = prev.fields.map(f =>
            f.id === fieldId ? { ...f, value: newValue } : f
          );
        } else {
          updatedFields = [...prev.fields, { id: fieldId, value: newValue }];
        }
        return { ...prev, fields: updatedFields };
      }
      console.warn('[updateFieldValue] editedNoteContent has neither sections nor fields');
      return prev;
    });
  }, [setEditedNoteContent]);

  // Bridge: handles both legacy (fieldId, value) and new (updaterFn)
  // call signatures from the form renderer. Reads current data fresh
  // from `editedNoteContent` on every invocation so it never operates
  // on stale closures.
  const handleFieldChangeLifted = useCallback((updaterOrFieldId, maybeValue) => {
    if (typeof updaterOrFieldId === 'function') {
      // New style: updater function like (prev) => ({ ...prev, [k]: v })
      const currentData = {};
      if (editedNoteContent?.fields) {
        editedNoteContent.fields.forEach(f => { if (f.id) currentData[f.id] = f.value; });
      } else if (editedNoteContent?.sections) {
        editedNoteContent.sections.forEach(section => {
          (section.fields || []).forEach(f => { if (f.id) currentData[f.id] = f.value; });
        });
      }
      const newData = updaterOrFieldId(currentData);
      for (const key of Object.keys(newData)) {
        if (_bridgeValueChanged(newData[key], currentData[key])) {
          updateFieldValueLifted(key, newData[key]);
        }
      }
    } else {
      // Legacy: direct (fieldId, value) call
      updateFieldValueLifted(updaterOrFieldId, maybeValue);
    }
  }, [editedNoteContent, updateFieldValueLifted, _bridgeValueChanged]);

  // Bridge for components that call setNewNoteFormData(updater) but
  // are actually editing an existing note. Routes the update through
  // updateFieldValueLifted so editedNoteContent stays the source of truth.
  const setNewNoteFormDataBridge = useCallback((updater) => {
    if (typeof updater !== 'function') return;
    const currentData = {};
    if (editedNoteContent?.fields) {
      editedNoteContent.fields.forEach(f => { if (f.id) currentData[f.id] = f.value; });
    } else if (editedNoteContent?.sections) {
      editedNoteContent.sections.forEach(section => {
        (section.fields || []).forEach(f => { if (f.id) currentData[f.id] = f.value; });
      });
    }
    const newData = updater(currentData);
    for (const key of Object.keys(newData)) {
      if (_bridgeValueChanged(newData[key], currentData[key])) {
        updateFieldValueLifted(key, newData[key]);
      }
    }
  }, [editedNoteContent, updateFieldValueLifted, _bridgeValueChanged]);
  // ─── End lifted helpers ────────────────────────────────────────────
  
  // Track when form data becomes dirty (user has typed something)
  // Use a ref to store the initial form data after template selection
  const initialFormDataRef = useRef(null);
  
  // Reset initial form data ref when template changes
  useEffect(() => {
    if (selectedTemplate) {
      // When template changes, we'll set initialFormDataRef in a setTimeout
      // to capture the form state after defaults are applied
      setTimeout(() => {
        initialFormDataRef.current = JSON.stringify(newNoteFormData);
        isNoteFormDirtyRef.current = false;
        void 0 && console.log('[DirtyTracking] Initial form data captured for template:', selectedTemplate?.name);
      }, 100);
    }
  }, [selectedTemplate?.id]);
  
  // Track actual user changes by comparing with initial state
  useEffect(() => {
    // Skip if no form data or no initial state captured yet
    if (!newNoteFormData || !initialFormDataRef.current) return;
    
    // Compare current state with initial state
    const currentState = JSON.stringify(newNoteFormData);
    const hasChanged = currentState !== initialFormDataRef.current;
    
    // Only log and update if there's an actual change in dirty state
    if (hasChanged !== isNoteFormDirtyRef.current) {
      void 0 && console.log('[DirtyTracking] Form dirty state changed:', {
        hasChanged,
        keysCount: Object.keys(newNoteFormData).length,
        wasAlreadyDirty: isNoteFormDirtyRef.current
      });
      isNoteFormDirtyRef.current = hasChanged;
    }
  }, [newNoteFormData]);
  
  // Handler for template change warning - Keep Current Note
  const handleKeepCurrentNote = useCallback(() => {
    setShowTemplateChangeWarning(false);
    setPendingTemplateChange(null);
  }, []);
  
  // Handler for template change warning - Start New Note
  const handleStartNewNoteWithTemplate = useCallback(async () => {
    if (!pendingTemplateChange) return;
    
    // Current note is already saved via autosave (if autosaveNoteId exists)
    // Just reset state and switch to new template
    
    // Fetch fresh template data
    let freshTemplate;
    try {
      const response = await axios.get(`${API}/form-templates/${pendingTemplateChange.id}`);
      freshTemplate = response.data;
    } catch (error) {
      console.error('Error fetching fresh template:', error);
      freshTemplate = pendingTemplateChange;
    }
    
    // Reset state for new note
    setAutosaveNoteId(null); // Clear noteId so lazy creation can work for new note
    autosaveNoteIdRef.current = null; // Also clear ref
    isNoteFormDirtyRef.current = false; // Reset dirty flag
    setNewNoteFormData({}); // Clear form data
    setLinkedGoals([]); // Clear linked goals
    
    // Update URL to remove old note ID
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('autosave_note_id');
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      console.warn('Failed to update URL:', e);
    }
    
    // Set new template
    setSelectedTemplate(freshTemplate);
    setIsGroupNote(freshTemplate.is_group_template || false);
    
    // Initialize form data with defaults from new template
    const initialFormData = {};
    if (freshTemplate && freshTemplate.form_fields) {
      freshTemplate.form_fields.forEach(field => {
        if (field.default_value !== undefined && field.default_value !== null && field.default_value !== '') {
          if (field.type === 'date' && field.default_value === 'today') {
            initialFormData[field.id] = new Date().toISOString().split('T')[0];
          } else if (field.type === 'time' && field.default_value === 'now') {
            initialFormData[field.id] = new Date().toTimeString().slice(0, 5);
          } else {
            initialFormData[field.id] = field.default_value;
          }
        }
      });
    }
    setNewNoteFormData(initialFormData);
    
    // IMPORTANT: Reset dirty flag and initialFormDataRef for new note
    // The new tracking system will capture initial state when template changes
    setTimeout(() => {
      isNoteFormDirtyRef.current = false;
      initialFormDataRef.current = JSON.stringify(initialFormData);
      void 0 && console.log('[TemplateChange] Reset dirty tracking for new note');
    }, 0);
    
    // Close modal
    setShowTemplateChangeWarning(false);
    setPendingTemplateChange(null);
    
    toast.success('Your previous note was saved as a draft. Starting new note.');
  }, [pendingTemplateChange, setNewNoteFormData]);
  
  // Link Treatment Plan Goals state
  const [showLinkGoalsDialog, setShowLinkGoalsDialog] = useState(false);
  const [availableTreatmentPlanGoals, setAvailableTreatmentPlanGoals] = useState([]);
  const [linkedGoals, setLinkedGoals] = useState([]); // Goals linked to current note
  const [loadingTreatmentPlanGoals, setLoadingTreatmentPlanGoals] = useState(false);
  
  // Telehealth consent state for progress notes
  const [telehealthConsent, setTelehealthConsent] = useState({
    provider_location: null,
    pos_code: null,
    pos_description: null,
    modality: null
  });
  
  // Add attendee to appointment state
  // Note: addAttendeeAppointment is now in AppointmentsContext
  const [showAddAttendeeDialog, setShowAddAttendeeDialog] = useState(false);
  const [addAttendeeSearch, setAddAttendeeSearch] = useState('');
  
  // Group management state
  const [showGroupMembers, setShowGroupMembers] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMemberSearch, setGroupMemberSearch] = useState('');
  const [showAttendanceSnapshot, setShowAttendanceSnapshot] = useState(false);
  const [attendanceSnapshotGroup, setAttendanceSnapshotGroup] = useState(null);
  const [groups, setGroups] = useState([
    {
      id: '1',
      name: 'Anxiety Support Group',
      type: 'support',
      facilitator_id: 'provider1',
      facilitator_name: 'Dr. Sarah Johnson',
      location_id: '1',
      location_name: 'Main Office - Room 2B',
      day: 'tuesday',
      start_time: '14:00',
      end_time: '15:00',
      description: 'Weekly support group for anxiety management and coping strategies',
      members: [
        { id: 'client1', name: 'John Smith', joined: '2024-03-15', sessions_attended: 12, total_sessions: 14, status: 'active' },
        { id: 'client2', name: 'Jane Doe', joined: '2024-03-22', sessions_attended: 11, total_sessions: 14, status: 'active' }
      ],
      status: 'active',
      created_at: '2024-03-01'
    },
    {
      id: '2', 
      name: 'Teen Therapy Group',
      type: 'therapy',
      facilitator_id: 'provider2',
      facilitator_name: 'Dr. Mike Wilson',
      location_id: '2',
      location_name: 'Satellite Office - Group Room A',
      day: 'thursday',
      start_time: '16:00',
      end_time: '17:00',
      description: 'Therapeutic group sessions for teenagers dealing with various challenges',
      members: [
        { id: 'client3', name: 'Alex Johnson', joined: '2024-02-10', sessions_attended: 15, total_sessions: 16, status: 'active' },
        { id: 'client4', name: 'Sam Davis', joined: '2024-02-17', sessions_attended: 14, total_sessions: 16, status: 'active' },
        { id: 'client5', name: 'Riley Chen', joined: '2024-03-01', sessions_attended: 10, total_sessions: 12, status: 'active' }
      ],
      status: 'active',
      created_at: '2024-02-01'
    },
    {
      id: '3',
      name: 'CBT Skills Group',
      type: 'skills',
      facilitator_id: 'provider1',
      facilitator_name: 'Dr. Sarah Johnson', 
      location_id: '1',
      location_name: 'Main Office - Room 1A',
      day: 'monday',
      start_time: '10:00',
      end_time: '11:30',
      description: 'Cognitive Behavioral Therapy skills training group',
      members: [
        { id: 'client6', name: 'Taylor Brown', joined: '2024-01-10', sessions_attended: 8, total_sessions: 8, status: 'completed' }
      ],
      status: 'completed',
      created_at: '2024-01-01'
    }
  ]);
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    group_type: '',
    service_type_id: '',
    facilitator_id: '',
    cpt_code_id: '',
    curriculum_id: '',
    location_id: '',
    schedule_day: '',
    start_time: '',
    end_time: '',
    modality: 'in_person',
    status: 'active',
    total_sessions: null,
    current_session: 1,
    session_type: 'fixed',
    sessions_start_date: '',
    members: [],
    client_ids: [],
    assign_facilitator_as_provider: false
  });
  const [editingGroup, setEditingGroup] = useState(null);
  // Note: groupAppointments, showPastGroupAppointments are now in AppointmentsContext
  const [groupClientSearch, setGroupClientSearch] = useState('');
  const [showFormerMembers, setShowFormerMembers] = useState(false);
  const [selectedGroupForMembers, setSelectedGroupForMembers] = useState(null);
  const [showRemovalDialog, setShowRemovalDialog] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removalReason, setRemovalReason] = useState('');
  
  // Client chart appointments tab - collapsible sections
  const [showClientUpcomingAppts, setShowClientUpcomingAppts] = useState(true);
  const [showClientPastAppts, setShowClientPastAppts] = useState(false);
  const [showAllUpcomingAppts, setShowAllUpcomingAppts] = useState(false);
  const [showAllPastAppts, setShowAllPastAppts] = useState(false);
  
  // Reset "show all" states when client changes
  useEffect(() => {
    setShowAllUpcomingAppts(false);
    setShowAllPastAppts(false);
  }, [selectedClient?.id]);
  
  // Clear note state when switching clients to prevent stale data
  useEffect(() => {
    setSelectedNote(null);
    setShowNoteDetail(false);
    setIsEditingNote(false);
  }, [selectedClient?.id]);
  
  const [removalNotes, setRemovalNotes] = useState('');
  // Note: addToFutureAppointments is now in AppointmentsContext
  const [removeFromFutureStartDate, setRemoveFromFutureStartDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Legacy group form fields (for backward compatibility with existing UI)
  const [legacyGroupFormData, setLegacyGroupFormData] = useState({
    name: '',
    type: '',
    facilitator_id: '',
    location_id: '',
    day: '',
    start_time: '',
    end_time: '',
    description: '',
    members: []
  });

  // Organization and Insurance data (Backend-connected)
  // Note: organizations is now in StaticDataContext
  const [insurances, setInsurances] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  
  // Roles and Permissions data
  // Note: roles is now in StaticDataContext
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [showManageRoles, setShowManageRoles] = useState(false);
  const [showAddEditRole, setShowAddEditRole] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    visible_tabs: []
  });
  
  // CPT Bundles data
  // Note: cptBundles is now in StaticDataContext
  const [showBundleForm, setShowBundleForm] = useState(false);
  
  // Send to Client Modal state
  const [showSendToClientModal, setShowSendToClientModal] = useState(false);
  const [sendToClientNote, setSendToClientNote] = useState(null);
  const [sendToClientOptions, setSendToClientOptions] = useState({
    sendEmail: true,
    addToPortal: true,
    message: ''
  });
  const [sendingToClient, setSendingToClient] = useState(false);
  
  // Helper function to replace variables in static text with actual client data
  const replaceClientVariables = (text, clientData) => {
    if (!text || !clientData) return text;
    
    // Use case-insensitive regex with word boundaries to catch variables regardless of surrounding characters
    let result = text;
    
    // Helper to safely replace - handles cases where variable is adjacent to punctuation
    const safeReplace = (str, varName, value) => {
      // Match {{variable}} with optional whitespace inside braces
      const regex = new RegExp(`\\{\\{\\s*${varName}\\s*\\}\\}`, 'gi');
      return str.replace(regex, value || '');
    };
    
    result = safeReplace(result, 'client_name', clientData.full_name);
    result = safeReplace(result, 'client_first_name', clientData.first_name);
    result = safeReplace(result, 'client_last_name', clientData.last_name);
    result = safeReplace(result, 'client_dob', formatDateMDY(clientData.date_of_birth || clientData.dob));
    result = safeReplace(result, 'client_age', (clientData.date_of_birth || clientData.dob) ? Math.floor((new Date() - new Date(clientData.date_of_birth || clientData.dob)) / (365.25 * 24 * 60 * 60 * 1000)).toString() : '');
    result = safeReplace(result, 'client_email', clientData.email);
    result = safeReplace(result, 'client_phone', clientData.phone);
    result = safeReplace(result, 'client_address', [clientData.address || clientData.street || clientData.address_line1, clientData.address_line2, clientData.city, clientData.state, clientData.zipcode || clientData.zip || clientData.zip_code].filter(Boolean).join(', '));
    result = safeReplace(result, 'client_streetaddress', clientData.street || clientData.address || clientData.address_line1);
    result = safeReplace(result, 'client_city', clientData.city);
    result = safeReplace(result, 'client_state', clientData.state);
    result = safeReplace(result, 'client_zipcode', clientData.zipcode || clientData.zip || clientData.zip_code);
    result = safeReplace(result, 'client_SSNunmasked', clientData.ssn);
    result = safeReplace(result, 'client_mrn', clientData.mrn);
    result = safeReplace(result, 'today', new Date().toLocaleDateString());
    result = safeReplace(result, 'provider_name', user?.name);
    result = safeReplace(result, 'current_date', new Date().toLocaleDateString());
    
    return result;
  };
  const [editingBundle, setEditingBundle] = useState(null);
  const [bundleFormData, setBundleFormData] = useState({
    name: '',
    description: '',
    cpt_code_ids: []
  });
  
  // Organization form state
  const [organizationFormData, setOrganizationFormData] = useState({
    name: '',
    organization_type: '',
    tax_id: '',
    taxonomy_code: '',
    npis: [],
    availity_submitter_id: ''
  });
  
  // Insurance form state
  const [insuranceFormData, setInsuranceFormData] = useState({
    name: '',
    abbreviation: '', // Short name for display (e.g., "BCBS", "UHC")
    payer_id: '',
    secondary_identifier: '',
    plans: [],
    payer_billing_types: [],
    assigned_npis: [],
    authorization_exemptions: [],  // CPT codes exempt from auth requirement
    modifier_rules: [],  // Per-payer modifier overrides (e.g. BCBS: SA→HO for 90834)
    contact_info: {
      phone: '',
      email: '',
      address: ''
    },
    // Timely Filing Configuration
    initial_filing_days: 90,
    corrected_claim_days: 90,
    appeal_deadline_days: 60,
    timely_filing_notes: '',
    // Payer Profile — Eligibility 270 Configuration
    rendering_npi_type: 'organization',
    requires_taxonomy: false,
    search_strategy: 'subscriber_dependent',
    strip_member_id_suffix: true,
    strip_member_id_prefix: '',
    include_provider_address: true,
    send_zip_plus4: false,
    // Claim Formatting (Advanced) — May 2026.  Null = use legacy routing.
    default_billing_entity_type: null,
    box31_signature: null,  // null = auto, 'facility', 'rendering_provider'
    suppress_2310b_for_facility_billing: false,  // Feb 2026 — Tufts-style per-payer toggle to omit 2310B for facility billing
    suppress_service_facility_loop: false,  // Jun 2026 — per-payer toggle to omit the Service Facility loop (2310C/2310D) on outbound EDI claims
    insurance_type_code: null,  // null = auto-detect (CI default; MC for Medicaid name match). Per-payer X12 SBR09 override.
    // Per-payer 2310B Rendering Provider override (BCBS-style org-on-2310B).
    // Null / { enabled: false } = use standard rendering rules.
    loop_2310b_override: null,
    clearinghouse: 'availity'
  });

  // Dialog states for Organization and Insurance management
  const [showAddOrganization, setShowAddOrganization] = useState(false);
  const [showEditOrganization, setShowEditOrganization] = useState(false);
  const [showAddInsurance, setShowAddInsurance] = useState(false);
  const [showEditInsurance, setShowEditInsurance] = useState(false);
  const [showArchivedInsurances, setShowArchivedInsurances] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [editingInsurance, setEditingInsurance] = useState(null);
  const [migratingInsurance, setMigratingInsurance] = useState(null);  // {id, name} when migration dialog open
  
  // Additional management dialogs
  const [showManageCptCodes, setShowManageCptCodes] = useState(false);
  // V2 → legacy bridge: when the V2 /v2/admin/cpt-codes route bounces
  // back here (Bundles / Add / Edit / CSV-Import buttons), it stashes
  // an intent object in sessionStorage['v2_legacy_cpt_intent'].  The
  // dialog reads it on open and applies the appropriate tab / inner
  // dialog.  See CPTCodesAdminRoute.jsx::openLegacyCptDialog.
  const [cptDialogTab, setCptDialogTab] = useState('codes');

  // Consume the V2 → legacy intent when the CPT dialog opens.  We can't
  // do this inside `<Dialog onOpenChange>` because Radix only fires
  // onOpenChange on USER interaction, not on programmatic `open` flips
  // (which is how the URL-param effect opens it).
  useEffect(() => {
    if (!showManageCptCodes) return;
    try {
      const raw = sessionStorage.getItem('v2_legacy_cpt_intent');
      if (!raw) return;
      const intent = JSON.parse(raw);
      sessionStorage.removeItem('v2_legacy_cpt_intent');
      // Ignore stale intents (>30s old) so back-button / refresh
      // doesn't re-trigger.
      if (!intent || Date.now() - (intent.ts || 0) > 30000) return;
      if (intent.tab === 'bundles') {
        setCptDialogTab('bundles');
        // The tab's onValueChange fires fetchCptBundles, but in this
        // path we set state directly so fetch the bundles list
        // explicitly.
        try { fetchCptBundles && fetchCptBundles(); } catch (e) {}
      } else {
        setCptDialogTab('codes');
      }
      // editId / add / csvImport are advisory hints — for now we just
      // land on the right tab.  Future passes can preselect the edit
      // row / pop the add panel.
    } catch (e) {
      // Ignore — sessionStorage failures shouldn't block the dialog.
    }
  }, [showManageCptCodes]);
  const [showProvidersDropdown, setShowProvidersDropdown] = useState(null); // Store client ID instead of boolean
  const [editingCptCode, setEditingCptCode] = useState(null);
  const [editingFormTemplate, setEditingFormTemplate] = useState(null);
  const [showGroupForm, setShowGroupForm] = useState(false);
  
  // Admin management views
  const [showManageLocations, setShowManageLocations] = useState(false);
  const [showDeletedDocuments, setShowDeletedDocuments] = useState(false);
  const [showManagePOS, setShowManagePOS] = useState(false);
  const [newPOSCode, setNewPOSCode] = useState('');
  const [newPOSLocation, setNewPOSLocation] = useState('');
  const [showManageLicenses, setShowManageLicenses] = useState(false);
  const [showOrgConfig, setShowOrgConfig] = useState(false);
  const [showManageInsurance, setShowManageInsurance] = useState(false);

  // V2 → legacy bridge: drain any sessionStorage intent set by
  // ManageInsuranceAdminRoute.jsx::openLegacyInsuranceDialog().  v1
  // just clears the key so it doesn't linger; future passes can
  // wire editId / deleteId into preselecting / pre-deleting the
  // target payer inside the legacy editor.
  useEffect(() => {
    if (!showManageInsurance) return;
    try {
      sessionStorage.removeItem('v2_legacy_insurance_intent');
    } catch (e) {
      // ignore
    }
  }, [showManageInsurance]);

  // V2 → legacy bridge for User Management.  Same shape as the
  // insurance bridge above — v1 just clears the key so a refresh
  // doesn't re-fire.  Future passes can read intent.action /
  // intent.user_id to pre-select an Edit / Archive / Reset row.
  useEffect(() => {
    if (!showUserList) return;
    try {
      sessionStorage.removeItem('v2_legacy_user_intent');
    } catch (e) {
      // ignore
    }
  }, [showUserList]);
  // Auto-submit recurring schedule admin dialog (NEHEN/Availity).
  // Backend gates by billing-manager role; the underlying component
  // silently no-ops on 403 so we don't need a frontend role check.
  const [showAutoSubmitSchedule, setShowAutoSubmitSchedule] = useState(false);
  const [showPayerAuditLog, setShowPayerAuditLog] = useState(false);
  const [showCurriculumLibrary, setShowCurriculumLibrary] = useState(false);
  const [showFirmCalendarDialog, setShowFirmCalendarDialog] = useState(false);
  
  // Group Notes Dialog state
  // Jun 17 2026 — All 5 fields (showGroupNoteDialog, groupNoteGroup,
  // groupNoteCurriculumSession, groupNoteExistingNotes, groupNoteMode)
  // were migrated into AppointmentsContext alongside the already-there
  // groupNoteAppointment.  Destructured at the top of this component
  // — see the useAppointments() block above.  Kept this banner so
  // future readers don't grep here and think the state is missing.
  
  // Curriculum Library state
  const [curricula, setCurricula] = useState([]);
  
  // Location management state
  // Note: locations is now in StaticDataContext
  const [locationProviderCounts, setLocationProviderCounts] = useState({}); // Map of location_id to provider count
  const [showEditLocation, setShowEditLocation] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showLocationProviders, setShowLocationProviders] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [viewingProfileUserId, setViewingProfileUserId] = useState(null);
  const [profileStartInEdit, setProfileStartInEdit] = useState(false);
  const [locationFormData, setLocationFormData] = useState({
    name: '',
    location_type: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    fax: '',
    edi_location_qualifier: '77',
    edi_secondary_id_type: '',
    edi_secondary_id_value: '',
  });
  const [locationProviders, setLocationProviders] = useState([]); // Read-only list for display

  // diagnosisFormData state moved to AddDiagnosisDialog component
  // medicationFormData state moved to AddEditMedicationDialog component

  const [editingMedication, setEditingMedication] = useState(null);


  const [authorizationFormData, setAuthorizationFormData] = useState({
    service_type: '',
    cpt_codes: [],
    provider_ids: [],
    total_units: 0,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '',
    insurance_provider: '',
    authorization_number: '',
    notes: '',
    editing_id: null
  });

  const [contactFormData, setContactFormData] = useState({
    name: '',
    relationship: '',
    title_agency: '',
    phone: '',
    fax: '',
    email: '',
    can_receive_info: false,
    release_on_file: false,
    is_minor_or_legal_guardian: false,
    release_id: '',
    end_date: '',
    discharge_date: '',
    auto_calculate_end_date: true,
    lives_in_same_household: false,
    contact_address: '',
    contact_city: '',
    contact_state: '',
    contact_zipcode: ''
  });

  const [eligibilityFormData, setEligibilityFormData] = useState({
    insurance_provider: '',
    is_eligible: true,
    benefits_remaining: '',
    copay_amount: '',
    deductible_met: false,
    notes: ''
  });

  // Helper function to validate required fields before signing
  const validateRequiredFieldsForSigning = async () => {
    if (!selectedNote?.content) {
      return { valid: true, emptyFields: [] }; // No content to validate
    }
    
    try {
      const noteContent = typeof selectedNote.content === 'string' 
        ? JSON.parse(selectedNote.content) 
        : selectedNote.content;
      
      // Get all fields from the note content
      let allFields = [];
      if (noteContent.sections && Array.isArray(noteContent.sections)) {
        noteContent.sections.forEach(section => {
          if (section.fields) {
            allFields = allFields.concat(section.fields);
          }
        });
      } else if (noteContent.fields && Array.isArray(noteContent.fields)) {
        allFields = noteContent.fields;
      }
      
      // Also check editedNoteContent if we're in edit mode
      let editedFields = [];
      if (editedNoteContent) {
        if (editedNoteContent.sections && Array.isArray(editedNoteContent.sections)) {
          editedNoteContent.sections.forEach(section => {
            if (section.fields) {
              editedFields = editedFields.concat(section.fields);
            }
          });
        } else if (editedNoteContent.fields && Array.isArray(editedNoteContent.fields)) {
          editedFields = editedNoteContent.fields;
        }
      }
      
      // Prefer editedNoteContent (live editing state) over selectedNote.content (last-saved server state)
      // Only fall back to allFields when editedNoteContent is null (not in edit mode / no edits made)
      const fieldsToCheck = editedNoteContent ? editedFields : allFields;
      
      // Get client diagnoses for validation of diagnosis fields
      // IMPORTANT: Fetch fresh diagnoses to ensure we have the latest data
      const noteClientId = selectedNote?.client_id;
      let activeClientDiagnoses = clientDiagnoses.filter(d => 
        d.client_id === noteClientId && d.is_active !== false
      );
      
      // If no diagnoses found in state but we have a client, try fetching fresh
      // This handles cases where diagnoses weren't loaded or state is stale
      if (activeClientDiagnoses.length === 0 && noteClientId) {
        try {
          void 0 && console.log('[validateRequiredFieldsForSigning] No diagnoses in state, fetching for client:', noteClientId);
          const dxResponse = await axios.get(`${API}/clients/${noteClientId}/diagnoses`);
          if (dxResponse.data && Array.isArray(dxResponse.data)) {
            activeClientDiagnoses = dxResponse.data.filter(d => d.is_active !== false);
            void 0 && console.log('[validateRequiredFieldsForSigning] Fetched', activeClientDiagnoses.length, 'active diagnoses');
          }
        } catch (error) {
          console.warn('[validateRequiredFieldsForSigning] Could not fetch diagnoses:', error.message);
        }
      }
      
      // Find required fields that are empty
      void 0 && console.log('[validateRequiredFieldsForSigning] Checking', fieldsToCheck.length, 'fields');
      void 0 && console.log('[validateRequiredFieldsForSigning] Active client diagnoses:', activeClientDiagnoses.length);
      
      const emptyRequiredFields = fieldsToCheck.filter(field => {
        // Skip non-required fields
        if (!field.required) return false;
        
        // Skip non-input fields (headers, dividers, etc.) - using constant from /src/constants/index.js
        if (NON_INPUT_FIELD_TYPES.includes(field.type)) return false;
        
        // Special handling for client_diagnoses fields - check if client has diagnoses
        if (field.type === 'client_diagnoses' || field.type === 'client_diagnoses_selector') {
          void 0 && console.log('[validateRequiredFieldsForSigning] ========================================');
          void 0 && console.log('[validateRequiredFieldsForSigning] Found diagnosis field:', field.type);
          void 0 && console.log('[validateRequiredFieldsForSigning] Field label:', field.label);
          void 0 && console.log('[validateRequiredFieldsForSigning] Field value type:', typeof field.value);
          void 0 && console.log('[validateRequiredFieldsForSigning] Field value:', field.value);
          void 0 && console.log('[validateRequiredFieldsForSigning] Client has', activeClientDiagnoses.length, 'active diagnoses in state');
          void 0 && console.log('[validateRequiredFieldsForSigning] ========================================');

          // ── Hardening (Feb 2026 production bug) ─────────────────
          // Some note templates prefill diagnoses as a snapshot wrapped
          // in an object (e.g., {diagnoses: [...]}, {selected: [...]},
          // {items: [...]}). The old check only accepted raw arrays or
          // JSON-stringified arrays and fell through to the stale
          // `activeClientDiagnoses` state check — which could be
          // empty for a split second after switching clients, causing
          // "no diagnosis" to fire on a field the provider could
          // visibly see populated. _extractDiagnosisList unwraps every
          // shape we've seen in production:
          //   - an Array directly
          //   - a JSON-stringified array
          //   - an object with {diagnoses|selected|items|list|codes}
          //   - any non-empty string (last-resort — trust UX: if the
          //     provider sees content, treat the field as populated)
          const _extractDiagnosisList = (v) => {
            if (v === null || v === undefined) return null;
            if (Array.isArray(v)) return v;
            if (typeof v === 'string') {
              const s = v.trim();
              if (!s) return null;
              try {
                const p = JSON.parse(s);
                return _extractDiagnosisList(p);
              } catch {
                // Non-JSON non-empty string — treat as a populated
                // value; field isn't empty even if we can't parse it.
                return ['__unparsed__'];
              }
            }
            if (typeof v === 'object') {
              for (const key of ['diagnoses', 'selected', 'items', 'list', 'codes', 'selectedCodes']) {
                if (Array.isArray(v[key])) return v[key];
              }
              // Bare object with no recognized list key: if it has any
              // non-empty string properties, treat as populated (e.g.
              // {primary: 'F32.9'} from some legacy templates).
              if (Object.values(v).some(x =>
                (Array.isArray(x) && x.length > 0)
                || (typeof x === 'string' && x.trim() !== '')
              )) return ['__object_populated__'];
            }
            return null;
          };

          // If client has ANY active diagnoses, the field is valid regardless of whether
          // selections have been saved to the note. The diagnoses exist and will display.
          if (activeClientDiagnoses.length > 0) {
            void 0 && console.log('[validateRequiredFieldsForSigning] ✅ Client has live diagnoses - field is VALID');
            return false; // Not empty - valid
          }

          // Client state is empty — trust the field's saved/prefilled
          // value. Covers both client_diagnoses (display-only) AND
          // client_diagnoses_selector (interactive).
          const extracted = _extractDiagnosisList(field.value);
          if (extracted && extracted.length > 0) {
            void 0 && console.log('[validateRequiredFieldsForSigning] ✅ Field value has', extracted.length, 'prefilled diagnosis entries - field is VALID');
            return false;
          }
          void 0 && console.log('[validateRequiredFieldsForSigning] ❌ No live diagnoses AND no prefilled value → field is EMPTY');
          return true;  // Empty → required field violation
        }
        
        // Check if field has conditional visibility - if hidden, skip validation
        if (field.conditionalField) {
          const parentField = fieldsToCheck.find(f => f.id === field.conditionalField);
          if (parentField) {
            const parentValue = parentField.value || '';
            const operator = field.conditionalOperator || 'equals';
            const targetValue = field.conditionalValue;
            
            let isVisible = false;
            if (operator === 'equals') {
              // checkbox_group stores an array — check inclusion
              isVisible = Array.isArray(parentValue) ? parentValue.includes(targetValue) : parentValue === targetValue;
            } else if (operator === 'not_equals') {
              isVisible = Array.isArray(parentValue) ? !parentValue.includes(targetValue) : parentValue !== targetValue;
            } else if (operator === 'contains') {
              if (Array.isArray(parentValue)) {
                isVisible = parentValue.includes(targetValue);
              } else {
                isVisible = String(parentValue || '').includes(targetValue);
              }
            } else if (operator === 'not_empty') {
              isVisible = Array.isArray(parentValue) ? parentValue.length > 0 : !!parentValue;
            } else {
              isVisible = targetValue ? (Array.isArray(parentValue) ? parentValue.includes(targetValue) : parentValue === targetValue) : !!parentValue;
            }
            
            // If field is conditionally hidden, skip validation
            if (!isVisible) return false;
          }
        }
        
        // Check if field value is empty
        const value = field.value;
        if (value === undefined || value === null || value === '') return true;
        if (Array.isArray(value) && value.length === 0) return true;
        if (typeof value === 'string' && value.trim() === '') return true;
        
        return false;
      });
      
      return {
        valid: emptyRequiredFields.length === 0,
        emptyFields: emptyRequiredFields.map(f => f.label || f.id)
      };
    } catch (error) {
      console.error('Error validating required fields:', error);
      return { valid: true, emptyFields: [] }; // Don't block on validation error
    }
  };

  // Signature handling functions
  const handleSignNote = async (signType, withDate = true, customDate = null, forceSign = false) => {
    void 0 && console.log('[handleSignNote] Called with:', { signType, withDate, customDate, forceSign });
    try {
      // CRITICAL: Check that the note actually exists in the database before trying to sign
      if (!selectedNote?.id) {
        toast.error(
          <div>
            <strong>Cannot sign - note not saved</strong>
            <p className="text-sm mt-1">This note hasn't been saved to the server yet. Please wait for autosave to complete or try refreshing the page.</p>
          </div>,
          { duration: 10000 }
        );
        return;
      }
      
      // Validate required fields before signing (unless force sign is enabled)
      if (!forceSign) {
        void 0 && console.log('[handleSignNote] forceSign is FALSE - running validation');
        const validation = await validateRequiredFieldsForSigning();
        if (!validation.valid) {
          const fieldList = validation.emptyFields.slice(0, 5).join('\n• ');
          const moreCount = validation.emptyFields.length > 5 ? `\n• ...and ${validation.emptyFields.length - 5} more` : '';
          toast.error(
            <div>
              <strong>Cannot sign note - required fields are empty:</strong>
              <div className="mt-2 text-sm">• {fieldList}{moreCount}</div>
              <div className="mt-2 text-xs text-gray-500">
                Hold Shift and click Sign to force signature anyway
              </div>
            </div>,
            { duration: 8000 }
          );
          return;
        }
      } else {
        void 0 && console.log('[handleSignNote] Force sign enabled - skipping validation');
        toast.info('Force signing - validation bypassed', { duration: 3000 });
      }
      
      // Check if user has a saved signature
      if (!user.signature_image) {
        // First time signing - show signature pad
        setSignaturePadFor(signType);
        setShowSignaturePad(true);
        return;
      }

      // SAFEGUARD: Verify note exists on server before signing
      // This catches cases where autosave failed silently
      try {
        const verifyResponse = await axios.get(`${API}/notes/${selectedNote.id}`);
        const serverNote = verifyResponse.data;
        
        // CROSS-CONTAMINATION CHECK: Verify note belongs to the client we're viewing
        // Only applies when actively inside a client chart (showClientDetail = true)
        // If opened from task list, selectedClient may be stale from a previous chart view
        if (showClientDetail && selectedClient?.id && serverNote.client_id && serverNote.client_id !== selectedClient.id) {
          toast.error(
            <div>
              <strong>⚠️ Client Mismatch Detected</strong>
              <p className="text-sm mt-1">This note is associated with a different client than the one you're viewing.</p>
              <p className="text-sm mt-1">Note's client: {serverNote.client_name || serverNote.client_id}</p>
              <p className="text-sm mt-1">Current view: {selectedClient.full_name || selectedClient.id}</p>
              <p className="text-sm mt-2 text-red-600">Please contact your administrator to resolve this data issue before signing.</p>
            </div>,
            { duration: 20000 }
          );
          return;
        }
      } catch (verifyError) {
        if (verifyError.response?.status === 404) {
          // CRITICAL: Auto-save text backup on "note not found" error for data recovery
          void 0 && console.log('[TextBackup:NoteNotFound] Auto-saving backup on 404 error');
          if (selectedNote?.id && editedNoteContent) {
            try {
              const textContent = extractNoteTextContent(editedNoteContent, selectedNote);
              if (textContent && textContent.trim().length > 0) {
                axios.post(`${API}/notes/${selectedNote.id}/text-backup`, {
                  text_content: textContent,
                  client_id: selectedNote.client_id,
                  client_name: selectedNote.client_name,
                  template_name: selectedNote.template_name || selectedNote.form_name
                }, {
                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                }).then(() => {
                  void 0 && console.log('[TextBackup:NoteNotFound] Backup saved successfully');
                }).catch(err => {
                  console.warn('[TextBackup:NoteNotFound] Backup failed:', err);
                });
              }
            } catch (e) {
              console.warn('[TextBackup:NoteNotFound] Error creating backup:', e);
            }
          }
          
          toast.error(
            <div>
              <strong>Note not found on server</strong>
              <p className="text-sm mt-1">This note may not have been saved properly.</p>
              <button 
                className="mt-2 px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                onClick={() => {
                  if (downloadNoteContentAsText(editedNoteContent, selectedNote)) {
                    toast.success('Note content downloaded!');
                  } else {
                    toast.error('No content to download');
                  }
                }}
              >
                Download My Work
              </button>
              <p className="text-xs mt-2 text-gray-400">Click above to save your work before refreshing</p>
            </div>,
            { duration: 30000 }
          );
          return;
        }
        // Other errors - continue and let the sign attempt handle it
      }

      // User has saved signature - apply it immediately
      const signatureData = {
        [`${signType}_signed_by`]: user.id,
        [`${signType}_signed_at`]: withDate ? (customDate || new Date().toISOString()) : null,
        force_sign: forceSign  // Pass force_sign to backend to bypass validation
      };

      try {
        await axios.put(`${API}/notes/${selectedNote.id}`, signatureData);
        void 0 && console.log('Note signed successfully');
        toast.success(`Signed as ${signType}${forceSign ? ' (force signed)' : ''}`);
        setNoteJustSigned(true); // Mark that note was just signed
        // Optimistically update clientNotes so the document list reflects the signature
        updateNoteStatusOptimistically(selectedNote.id, selectedNote.status, {
          [`${signType}_signed_by`]: user.id,
          [`${signType}_signed_at`]: signatureData[`${signType}_signed_at`],
          [`${signType}_signed_date_hidden`]: !withDate
        });
      } catch (error) {
        console.error('Failed to sign note:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
        const statusCode = error.response?.status;
        
        if (statusCode === 404) {
          // CRITICAL: Auto-save text backup on "note not found" error for data recovery
          void 0 && console.log('[TextBackup:SignNotFound] Auto-saving backup on 404 error');
          if (selectedNote?.id && editedNoteContent) {
            try {
              const textContent = extractNoteTextContent(editedNoteContent, selectedNote);
              if (textContent && textContent.trim().length > 0) {
                axios.post(`${API}/notes/${selectedNote.id}/text-backup`, {
                  text_content: textContent,
                  client_id: selectedNote.client_id,
                  client_name: selectedNote.client_name,
                  template_name: selectedNote.template_name || selectedNote.form_name
                }, {
                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                }).then(() => {
                  void 0 && console.log('[TextBackup:SignNotFound] Backup saved successfully');
                }).catch(err => {
                  console.warn('[TextBackup:SignNotFound] Backup failed:', err);
                });
              }
            } catch (e) {
              console.warn('[TextBackup:SignNotFound] Error creating backup:', e);
            }
          }
          
          toast.error(
            <div>
              <strong>Note not found</strong>
              <p className="text-sm mt-1">This note may have been deleted or moved.</p>
              <button 
                className="mt-2 px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                onClick={() => {
                  if (downloadNoteContentAsText(editedNoteContent, selectedNote)) {
                    toast.success('Note content downloaded!');
                  } else {
                    toast.error('No content to download');
                  }
                }}
              >
                Download My Work
              </button>
              <p className="text-xs mt-2 text-gray-400">Note ID: {selectedNote.id}</p>
            </div>,
            { duration: 30000 }
          );
        } else if (statusCode === 403) {
          toast.error(`Permission denied: ${errorMessage}`, { duration: 8000 });
        } else if (statusCode === 400) {
          toast.error(`Cannot sign: ${errorMessage}`, { duration: 8000 });
        } else {
          toast.error(`Failed to sign note: ${errorMessage}`, { duration: 8000 });
        }
        return;
      }

      refreshNotes(); refreshTodos();
      
      // Refresh the selected note and show advancement dialog
      try {
        const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
        setSelectedNote(updatedNote.data);
        
        // Show advancement dialog to let provider choose next status (send to supervisor, etc.)
        if (signType === 'provider') {
          setSignedNoteForAdvancement(updatedNote.data);
          setShowSignatureAdvanceDialog(true);
          // Clear noteJustSigned since we're showing the advancement dialog
          // This prevents the duplicate "Next Steps" dialog when closing
          setNoteJustSigned(false);
        }
      } catch (error) {
        console.error('Failed to refresh note display:', error);
        // Don't show error since signing succeeded
      }
    } catch (error) {
      console.error('Unexpected sign note error:', error);
      toast.error(error.response?.data?.detail || `Failed to sign note`);
    }
  };

  const handleSaveSignature = async () => {
    if (sigPadRef.current.isEmpty()) {
      toast.error('Please provide a signature first');
      return;
    }
    
    // Validate required fields before signing
    const validation = await validateRequiredFieldsForSigning();
    if (!validation.valid) {
      const fieldList = validation.emptyFields.slice(0, 5).join('\n• ');
      const moreCount = validation.emptyFields.length > 5 ? `\n• ...and ${validation.emptyFields.length - 5} more` : '';
      toast.error(
        <div>
          <strong>Cannot sign note - required fields are empty:</strong>
          <div className="mt-2 text-sm">• {fieldList}{moreCount}</div>
        </div>,
        { duration: 8000 }
      );
      return;
    }

    try {
      // Get signature as base64 image
      const signatureImage = sigPadRef.current.toDataURL();
      
      // Save to user profile using the new endpoint
      try {
        await axios.put(`${API}/users/me/signature`, {
          signature_image: signatureImage
        });
        void 0 && console.log('Signature saved to profile');
      } catch (error) {
        console.error('Failed to save signature to profile:', error);
        toast.error('Failed to save signature to your profile');
        return;
      }

      // Update local user state
      setUser(prev => ({ ...prev, signature_image: signatureImage }));

      // Now apply the signature to the note
      const signatureData = {
        [`${signaturePadFor}_signed_by`]: user.id,
        [`${signaturePadFor}_signed_at`]: new Date().toISOString()
      };

      try {
        await axios.put(`${API}/notes/${selectedNote.id}`, signatureData);
        void 0 && console.log('Note signed successfully');
      } catch (error) {
        console.error('Failed to sign note:', error);
        toast.error('Signature saved, but failed to sign note');
        return;
      }
      
      toast.success('Signature saved and note signed!');
      setShowSignaturePad(false);
      
      // Optimistically update the selected note to show signature immediately
      if (selectedNote) {
        setSelectedNote(prev => ({
          ...prev,
          [`${signaturePadFor}_signed_by`]: user.id,
          [`${signaturePadFor}_signed_at`]: new Date().toISOString()
        }));
        updateNoteStatusOptimistically(selectedNote.id, selectedNote.status, {
          [`${signaturePadFor}_signed_by`]: user.id,
          [`${signaturePadFor}_signed_at`]: new Date().toISOString()
        });
      }

      // Show advancement dialog (non-critical — don't fail the whole flow)
      try {
        const updatedNoteForDialog = await axios.get(`${API}/notes/${selectedNote.id}`);
        setSignedNoteForAdvancement(updatedNoteForDialog.data);
        // Close the note detail first so the advancement dialog isn't hidden behind it
        setShowNoteDetail(false);
        setShowSignatureAdvanceDialog(true);
        setNoteJustSigned(false);
      } catch (dialogErr) {
        console.warn('Could not load advancement dialog:', dialogErr);
        // Signature was saved successfully — don't mislead the user
      }
      
      refreshNotes(); refreshTodos();
      
      // Refresh the selected note (non-critical, don't fail if this errors)
      try {
        const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
        setSelectedNote(updatedNote.data);
      } catch (error) {
        console.error('Failed to refresh note display:', error);
        // Don't show error to user since the important operations succeeded
      }
    } catch (error) {
      console.error('Unexpected signature save error:', error);
      toast.error('Failed to save signature');
    }
  };

  const handleClearSignaturePad = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
    }
  };

  const handleClearMySavedSignature = async () => {
    if (!window.confirm('Are you sure you want to clear your saved signature? You will need to create a new one the next time you sign a note.')) {
      return;
    }

    try {
      await axios.delete(`${API}/users/me/signature`);
      
      // Update local user state
      setUser(prev => ({ ...prev, signature_image: null }));
      
      toast.success('Your signature has been cleared');
    } catch (error) {
      console.error('Clear signature error:', error);
      toast.error('Failed to clear signature');
    }
  };

  const handleUnsignNote = async (signType) => {
    // Check if current user is the one who signed (super admins can
    // override — May 2026 user request: "i can't seem to remove
    // signatures").  Backend will also accept the request from any
    // billing-admin / super-admin via the existing note-edit ACL.
    const signedBy = selectedNote[`${signType}_signed_by`];
    if (signedBy !== user.id && !isSuperAdmin(user)) {
      toast.error('You can only clear your own signature');
      return;
    }

    // Build confirmation message based on note status and sign type
    let confirmMessage = `Are you sure you want to remove your ${signType} signature from this note?`;
    let consequences = [];
    
    const noteStatus = selectedNote.status;
    
    if (signType === 'provider') {
      // Provider removing signature
      if (noteStatus === 'pending_supervisor_signature' || noteStatus === 'sent_to_supervisor') {
        consequences.push('• The supervisor signature request will be cancelled');
      }
      if (noteStatus === 'sent_to_client_for_signature' || noteStatus === 'pending_client_signature') {
        consequences.push('• The client signature request will be pulled back');
        consequences.push('• The client will no longer see this note in their portal');
      }
      if (selectedNote.supervisor_signed_at) {
        consequences.push('• The supervisor\'s signature will also be removed');
      }
      if (consequences.length > 0) {
        consequences.push('• The note will return to draft status');
      }
    } else if (signType === 'supervisor') {
      // Supervisor removing signature
      if (noteStatus === 'sent_to_client_for_signature' || noteStatus === 'pending_client_signature') {
        consequences.push('• The client signature request will be pulled back');
        consequences.push('• The client will no longer see this note in their portal');
        consequences.push('• The note will return to provider-signed status');
      }
    }
    
    if (consequences.length > 0) {
      confirmMessage += '\n\nThis will also:\n' + consequences.join('\n');
    }

    // iframe-safe confirm (replaces window.confirm which Emergent's
    // preview sandbox silently suppresses).  Production behaviour
    // is identical — same Yes/No semantics.
    const ok = await confirmAction(confirmMessage, {
      title: `Remove ${signType} signature?`,
      confirmLabel: 'Remove Signature',
      destructive: true,
    });
    if (!ok) {
      return;
    }

    try {
      const unsignData = {
        [`${signType}_signed_by`]: null,
        [`${signType}_signed_at`]: null,
        [`${signType}_signed_date_hidden`]: null
      };

      await axios.put(`${API}/notes/${selectedNote.id}`, unsignData);
      toast.success('Signature removed from note');
      refreshNotes(); refreshTodos();
      
      // Refresh the selected note
      const updatedNote = await axios.get(`${API}/notes/${selectedNote.id}`);
      setSelectedNote(updatedNote.data);
    } catch (error) {
      console.error('Unsign note error:', error);
      toast.error('Failed to remove signature');
    }
  };

  // ========== DATA FETCHING FUNCTIONS (declared after state, before useEffect) ==========

  // Memoize the filtered billing notes list for navigation in the Prepare Claim dialog
  const filteredBillingNotes = useMemo(() => {
    return notesReadyForBilling.filter(note => {
      if (Array.isArray(billingProviderFilter) && billingProviderFilter.length > 0) {
        const inList = billingProviderFilter.includes(note.provider_id);
        if (billingProviderFilterMode === 'exclude' ? inList : !inList) return false;
      }
      if (Array.isArray(billingClientFilter) && billingClientFilter.length > 0) {
        const inList = billingClientFilter.includes(note.client_id);
        if (billingClientFilterMode === 'exclude' ? inList : !inList) return false;
      }
      // billingInsuranceFilter is now always an array (May 2026 audit).
      // Tolerate the legacy 'all' / single-string shapes for forward
      // compatibility during the transition.
      const insuranceFilterArr = Array.isArray(billingInsuranceFilter)
        ? billingInsuranceFilter
        : (billingInsuranceFilter && billingInsuranceFilter !== 'all'
            ? [billingInsuranceFilter] : []);
      if (insuranceFilterArr.length > 0) {
        const noteClient = allClientsForLookupRef.current?.find(c => c.id === note.client_id);
        const clientInsurance = getClientPrimaryInsurance(noteClient, insurances);
        const clientPayerName = clientInsurance?.company;
        if (!noteClient || !clientPayerName) return false;
        // Match parent OR any sub-plan in the family for ANY selected
        // insurance.  User report (Feb 2026): selecting "Tufts" on the
        // Billing tab filter excluded Tufts One Care clients because
        // the client's primary_insurance company is set to the sub-plan
        // name.
        const clientPayerLower = clientPayerName.toLowerCase();
        const anyMatch = insuranceFilterArr.some((selected) => {
          if (clientPayerName === selected) return true;
          const parentDoc = (insurances || []).find(
            (i) => (i.name || '').toLowerCase() === selected.toLowerCase()
          );
          const planNames = (parentDoc?.plans || []).map((p) =>
            typeof p === 'string' ? p : (p && p.name) || ''
          ).filter(Boolean);
          return planNames.some(
            (n) => n.toLowerCase() === clientPayerLower
          );
        });
        if (!anyMatch) return false;
      }
      if (billingDateFrom && note.date_of_service < billingDateFrom) return false;
      if (billingDateTo && note.date_of_service > billingDateTo) return false;
      return true;
    });
  }, [notesReadyForBilling, billingProviderFilter, billingProviderFilterMode, billingClientFilter, billingClientFilterMode, billingInsuranceFilter, billingDateFrom, billingDateTo, insurances]);

  // Mar 2026 — apply the BillingTab column sort here so navigation
  // helpers (Next/Prev Claim in the Prepare Claim dialog) walk the
  // EXACT same order the biller sees in the on-screen list.  Mirror
  // of the `sortedNotes` useMemo in BillingTab.jsx.  Kept identical so
  // future changes only need to land in one place — if you change
  // sort keys here, also update BillingTab.jsx (and ideally extract
  // both into a shared helper).
  const sortedFilteredBillingNotes = useMemo(() => {
    if (!billingSortColumn) return filteredBillingNotes;
    const list = [...filteredBillingNotes];
    list.sort((a, b) => {
      let aVal, bVal;
      switch (billingSortColumn) {
        case 'client':    aVal = a._client_name || ''; bVal = b._client_name || ''; break;
        case 'mrn':       aVal = a._client_mrn || ''; bVal = b._client_mrn || ''; break;
        case 'insurance': aVal = a._client_insurance || ''; bVal = b._client_insurance || ''; break;
        case 'provider':  aVal = a._provider_name || ''; bVal = b._provider_name || ''; break;
        case 'date':      aVal = a.date_of_service || ''; bVal = b.date_of_service || ''; break;
        case 'deadline': {
          const calc = (note) => {
            const iso = note.date_of_service;
            if (!iso) return Number.POSITIVE_INFINITY;
            const s = String(iso).slice(0, 10);
            const [y, m, d] = s.split('-').map(Number);
            if (!y || !m || !d) return Number.POSITIVE_INFINITY;
            const dos = new Date(y, m - 1, d);
            const deadline = new Date(dos.getTime() + 90 * 86400000);
            const today = new Date(); today.setHours(0, 0, 0, 0);
            return Math.floor((deadline - today) / 86400000);
          };
          aVal = calc(a); bVal = calc(b); break;
        }
        case 'cpt':       aVal = a._billing?.cpt_code || ''; bVal = b._billing?.cpt_code || ''; break;
        case 'charges':   aVal = Number(a._billing?.charges) || 0; bVal = Number(b._billing?.charges) || 0; break;
        default: return 0;
      }
      if (typeof aVal === 'number') return billingSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      return billingSortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
    return list;
  }, [filteredBillingNotes, billingSortColumn, billingSortDirection]);
  
  // `hasPermission` — moved to the consolidated `createIntakePermissions`
  // call near the top of this component (Phase 3a, Feb 2026).
  
  // Helper to check if user can see a specific tab based on role settings
  // Multitenancy — per-tenant module gates for the LEGACY dashboard tabs.
  const [tenantEntitlements, setTenantEntitlements] = useState(null);
  // Jun 2026 — Tools/Diagnostics/Bulk Import moved to the platform portal.
  // Tenant admins only see those tabs when the tenant's transition flag
  // (tool_access.tenant_admin_enabled) is on, or during platform
  // impersonation sessions.
  const [tenantToolAccess, setTenantToolAccess] = useState({ enabled: true, tools: true, bulkImport: true });
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/me/entitlements`)
      .then((r) => {
        setTenantEntitlements(r.data?.entitlements || {});
        const ta = r.data?.tool_access || {};
        const grants = ta.grants || {};
        const base = r.data?.impersonation === true || ta.tenant_admin_enabled === true;
        setTenantToolAccess({
          tools: base || grants.tools === true || grants.diagnostics === true,
          bulkImport: base || grants.bulk_import === true,
          enabled: base || Object.values(grants).some(Boolean),
        });
      })
      .catch(() => setTenantEntitlements({}));
  }, [user?.id]);

  // Applied before any role logic (including super admin) so a module the
  // platform admin switched off disappears for everyone in the tenant.
  const LEGACY_TAB_FEATURE_MAP = {
    'billing': 'billing',
    'billing-management': 'billing',
    'claims-workshop': 'billing',
    'era-management': 'era',
    'payroll': 'payroll',
    'eligibility': 'eligibility',
    'waitlist': 'waitlist',
    'medical-records': 'medical_records',
    'groups': 'groups',
    'medication-management': 'medication_management',
    'authorizations': 'authorizations',
    'reports': 'reports',
    'tasks': 'tasks',
  };
  const tenantHasFeature = (f) => !tenantEntitlements || tenantEntitlements[f] !== false;

  const canSeeTab = (tabId) => {
    if (!user) return false;
    
    const gatedFeature = LEGACY_TAB_FEATURE_MAP[tabId];
    if (gatedFeature && tenantEntitlements && tenantEntitlements[gatedFeature] === false) return false;
    
    // Clients tab is always visible for all users
    if (tabId === 'clients') return true;
    
    // Group administrators can always see the groups tab
    if (tabId === 'groups' && user.group_admin) return true;
    
    // Super admins can see all tabs
    if (isSuperAdmin(user)) return true;
    
    // Check if user's role has this tab in visible_tabs
    const userRole = (roles || []).find(r => r.id === user.role_id);
    
    // If role is not found (role_id is null/missing), apply default restrictions based on user type
    if (!userRole) {
      // Providers without a role_id should only see appointments by default
      if (user.is_provider || userRoleName === 'provider' || userRoleName === 'supervisor' || userRoleName === 'intern') {
        return PROVIDER_DEFAULT_TABS.includes(tabId);
      }
      // Office/reception without role_id - limited access
      if (userRoleName === 'office' || userRoleName === 'reception' || userRoleName === 'office staff') {
        return OFFICE_DEFAULT_TABS.includes(tabId);
      }
      // Admin role (legacy) - full access
      if (userRoleName === 'admin') {
        return true;
      }
      // Unknown role - restrictive default (appointments and clients only)
      return DEFAULT_TABS.includes(tabId);
    }
    
    // If visible_tabs is empty or undefined, show all tabs (backward compatibility for configured roles)
    if (!userRole.visible_tabs || userRole.visible_tabs.length === 0) return true;
    return userRole.visible_tabs.includes(tabId);
  };
  
  // `isAdmin` and `canEditFieldCategory` — moved to the consolidated
  // `createIntakePermissions` call near the top of this component
  // (Phase 3a, Feb 2026).
  
  // Helper to lookup a user/provider by ID - checks both users and providers arrays
  const findUserOrProvider = useCallback((userId) => {
    if (!userId) return null;
    // Check users first (more complete data)
    const fromUsers = users?.find(u => u.id === userId);
    if (fromUsers) return fromUsers;
    // Fallback to providers array
    const fromProviders = providers?.find(p => p.id === userId);
    if (fromProviders) return fromProviders;
    return null;
  }, [users, providers]);

  // Helper to lookup a client by ID - uses ref for allClientsForLookup to avoid re-render cascades
  const findClientById = useCallback((clientId) => {
    if (!clientId) return null;
    // If clientId is actually an object (legacy support), return it directly
    if (typeof clientId === 'object' && clientId.id) return clientId;
    // Check selectedClient first (most reliable when in a client chart)
    if (selectedClient?.id === clientId) {
      return selectedClient;
    }
    // Check allClientsForLookup via ref (no dependency = no re-render cascade)
    const fromLookup = allClientsForLookupRef.current?.find(c => c.id === clientId);
    if (fromLookup) {
      return fromLookup;
    }
    // Fallback to filtered clients array
    const fromClients = clients?.find(c => c.id === clientId);
    if (fromClients) {
      return fromClients;
    }
    return null;
  }, [selectedClient, clients]);

  // Feb 27, 2026 — Notes-tab INP fix (8.5s → fast).  The Notes tab
  // rendered hundreds of rows each running
  //   `allClientsForLookup.find(c => c.id === note.client_id)` and
  //   `users.find(u => u.id === note.provider_id)`
  // per row.  With ~3k clients + ~50 users + ~500 notes that's
  // ~1.5M comparisons per Notes-tab render — multiplied by every
  // keystroke in the filter inputs.  Pre-building O(1) lookup Maps
  // here cuts that to a constant ~500 Map.get()s per render.
  // The two refs below also feed the virtualized notes list.
  const clientsByIdMap = useMemo(() => {
    const m = new Map();
    (allClientsForLookup || []).forEach((c) => {
      if (c?.id) m.set(c.id, c);
    });
    return m;
  }, [allClientsForLookup]);

  const usersByIdMap = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => {
      if (u?.id) m.set(u.id, u);
    });
    return m;
  }, [users]);

  // Same idea for the Clients-tab table rows: each row was doing
  //   `clientLabels.find(l => l.id === labelId)` for every label and
  //   `(insurances || []).find(ins => ins.name === primaryIns.company || ...)`
  //   on every render.  With ~50 labels and ~80 insurances and ~50
  // visible client rows we were burning ~5k Array.find iterations per
  // render of the table.  Build the lookup Maps once.
  const clientLabelsByIdMap = useMemo(() => {
    const m = new Map();
    (clientLabels || []).forEach((l) => {
      if (l?.id) m.set(l.id, l);
    });
    return m;
  }, [clientLabels]);

  const insurancesByNameLowerMap = useMemo(() => {
    const m = new Map();
    (insurances || []).forEach((ins) => {
      if (ins?.name) m.set(ins.name.toLowerCase(), ins);
    });
    return m;
  }, [insurances]);

  // Helper to check if user can edit a note
  const canEditNote = (note) => {
    if (!user || !note) return false;
    
    // 1. Admin, Super Admin, Office Staff can edit any note
    if (isSuperAdmin(user) || userRoleName === 'admin' || userRoleName === 'office' || userRoleName === 'office staff' || userRoleName === 'biller') return true;
    
    // 2. Provider can edit their own notes
    if (note.provider_id === user.id || note.created_by === user.id) return true;
    
    // 3. Supervisor can edit notes of their supervisees
    if (userRoleName === 'supervisor' || user?.is_supervisor) {
      // Check if note's provider is in the user's supervisee list
      const noteProvider = users.find(u => u.id === note.provider_id);
      if (noteProvider) {
        // Check if current user is the supervisor
        if (noteProvider.supervisor_id === user.id) return true;
        // Check supervisor_ids array
        if (noteProvider.supervisor_ids && noteProvider.supervisor_ids.includes(user.id)) return true;
      }
    }
    
    // 4. Note: Providers can VIEW other providers' notes for shared clients, but NOT edit
    // This is handled by returning false here - they can see but not modify
    
    return false;
  };
  
  // Helper function to find schedulers for a provider
  const getSchedulersForProvider = (providerId) => {
    if (!users || !providerId) return [];
    return users.filter(u => 
      u.is_scheduler && 
      u.scheduling_for && 
      u.scheduling_for.includes(providerId)
    );
  };
  
  // Helper function to check if user can VIEW a note (more permissive than edit)
  const canViewNote = (note) => {
    if (!user || !note) return false;
    
    // Anyone who can edit can also view
    if (canEditNote(note)) return true;
    
    // Providers can view notes from shared clients (other providers' notes)
    // This is controlled by what notes are fetched from the API based on client_visibility
    // If the note was returned by the API, the user has permission to view it
    return true;
  };
  
  // Helper to check if user can lock/unlock notes
  const canLockNote = (note) => {
    if (!user || !note) return false;
    // Admin / Super Admin always allowed.
    if (isSuperAdmin(user)) return true;
    // Hardcoded legacy fallback: historical roles that always had lock/unlock.
    if (userRoleName === 'office' || userRoleName === 'office staff' || userRoleName === 'biller') {
      return true;
    }
    // New role-based check: the `lock_unlock_notes` permission on the user's
    // Role record. Enables granular control (e.g., enable for Supervisors
    // without hardcoding the name).
    const perms = (user.permissions || user.role_permissions || user.effective_permissions || []);
    if (Array.isArray(perms) && perms.includes('lock_unlock_notes')) return true;
    return false;
  };
  
  // Admin Dialog Jump To Configuration (defined after all state declarations)
  // Note: Org Setup is intentionally excluded as it's a master setting
  const adminDialogConfig = useMemo(() => [
    { id: 'users', label: 'User Management', icon: Users, setter: setShowUserList },
    { id: 'roles', label: 'Role Management', icon: Shield, setter: setShowManageRoles },
    { id: 'locations', label: 'Locations', icon: MapPin, setter: setShowManageLocations },
    { id: 'pos', label: 'POS Codes', icon: Hash, setter: setShowManagePOS },
    { id: 'serviceTypes', label: 'Service Types', icon: Briefcase, setter: setShowManageServiceTypesDialog },
    { id: 'cpt', label: 'CPT Codes & Bundles', icon: FileText, setter: setShowManageCptCodes },
    { id: 'intakeBundles', label: 'Intake Bundles', icon: Package, setter: setShowManageIntakeBundles },
    { id: 'labels', label: 'Client Labels', icon: Tag, setter: setShowClientLabelsDialog },
    { id: 'forms', label: 'Forms & Types', icon: ClipboardList, setter: setShowFormsManagementDialog },
    { id: 'routing', label: 'Routing Rules', icon: GitMerge, setter: setShowRoutingRulesManager },
    { id: 'rulesManagement', label: 'Insurance/CPT Conflict Rules', icon: Shield, setter: setShowRulesManagementDialog },
    { id: 'reminders', label: 'Appointment Reminders', icon: Bell, setter: setShowReminderDialog },
    { id: 'documentDeadlines', label: 'Document Deadline Rules', icon: Clock, setter: setShowManageRulesDialog },
    { id: 'paymentReminders', label: 'Payment Reminders', icon: DollarSign, setter: setShowPaymentReminderSettings },
    { id: 'waitlistCommunication', label: 'Waitlist Communication', icon: Clock, setter: setShowWaitlistCommunicationSettings },
    { id: 'clientSurveys', label: 'Client Surveys', icon: BarChart3, setter: setShowSurveyDashboard },
    { id: 'clientPortal', label: 'Client Portal', icon: Globe, setter: setShowManageClientPortal },
    { id: 'eligibility', label: 'Eligibility Settings', icon: CheckCircle, setter: setShowEligibilitySettings },
    { id: 'insurance', label: 'Manage Insurance', icon: CreditCard, setter: setShowManageInsurance },
    { id: 'autoSubmitSchedule', label: 'Auto-Submit Schedule', icon: Clock, setter: setShowAutoSubmitSchedule },
    { id: 'licenses', label: 'Manage Licenses', icon: FileText, setter: setShowManageLicenses },
    { id: 'firmCalendar', label: 'Firm Calendar', icon: CalendarIcon, setter: setShowFirmCalendarDialog },
  ], []);
  
  // Handle Admin Dialog Navigation with unsaved changes check
  const handleAdminJumpTo = useCallback((targetId, isDirty = false) => {
    const targetDialog = adminDialogConfig.find(d => d.id === targetId);
    if (!targetDialog) return;
    
    if (isDirty) {
      setPendingAdminNavigation(targetDialog);
      setShowUnsavedChangesWarning(true);
    } else {
      // Close all admin dialogs first
      adminDialogConfig.forEach(d => d.setter(false));
      // Open the target dialog
      setTimeout(() => targetDialog.setter(true), 100);
    }
  }, [adminDialogConfig]);
  
  // Confirm navigation after saving or discarding
  const confirmAdminNavigation = useCallback((shouldSave = false) => {
    if (pendingAdminNavigation) {
      // Close all admin dialogs
      adminDialogConfig.forEach(d => d.setter(false));
      setShowUnsavedChangesWarning(false);
      // Open target dialog
      setTimeout(() => pendingAdminNavigation.setter(true), 100);
      setPendingAdminNavigation(null);
      setCurrentAdminDialogDirty(false);
    }
  }, [adminDialogConfig, pendingAdminNavigation]);
  
  // Cancel navigation
  const cancelAdminNavigation = useCallback(() => {
    setShowUnsavedChangesWarning(false);
    setPendingAdminNavigation(null);
  }, []);

  // Mar 2026 — V2 Admin deep-link handler.
  // When a user clicks a card on the V2 Admin hub (/v2/admin), we
  // route them back to the legacy dashboard with URL params:
  //   ?view=admin&adminInnerTab=setup&adminDialog=cpt
  // This effect:
  //   1) flips the legacy inner tab (`setAdminInnerTab`)
  //   2) opens the requested dialog via the existing setter map
  //   3) cleans the param off the URL so a back-nav doesn't re-open
  //      the dialog over and over
  // Zero-risk shim — only fires when the param is present, otherwise
  // the legacy admin tab behaves exactly as before.
  useEffect(() => {
    if (activeTab !== 'admin') return;
    const sp = new URLSearchParams(window.location.search);
    const innerTabParam = sp.get('adminInnerTab');
    const dialogParam = sp.get('adminDialog');
    let touched = false;
    if (innerTabParam && innerTabParam !== adminInnerTab) {
      setAdminInnerTab(innerTabParam);
      touched = true;
    }
    if (dialogParam) {
      const target = adminDialogConfig.find((d) => d.id === dialogParam);
      if (target) {
        // Defer slightly so the inner tab swap renders first.
        setTimeout(() => target.setter(true), 80);
      }
      touched = true;
    }
    if (touched) {
      sp.delete('adminInnerTab');
      sp.delete('adminDialog');
      const newSearch = sp.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [activeTab, adminInnerTab, setAdminInnerTab, adminDialogConfig]);
  
  // Load client enrollments when selectedClient changes (with caching)
  useEffect(() => {
    if (selectedClient?.id) {
      const clientId = selectedClient.id;
      
      // Check cache first
      if (enrollmentsCacheRef.current[clientId]) {
        setClientEnrollments(enrollmentsCacheRef.current[clientId]);
        setEnrollmentsLoaded(true);
        return;
      }
      
      const loadEnrollments = async () => {
        try {
          // Fetch ALL enrollments including inactive/past ones for service history
          const response = await axios.get(`${API}/clients/${clientId}/service-enrollments?include_inactive=true`, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
          });
          // Cache the result (all enrollments, not just active)
          enrollmentsCacheRef.current[clientId] = response.data;
          setClientEnrollments(response.data);
        } catch (error) {
          console.error('Failed to load client enrollments:', error);
          setClientEnrollments([]);
        } finally {
          setEnrollmentsLoaded(true);
        }
      };
      setEnrollmentsLoaded(false);
      loadEnrollments();
    } else {
      setClientEnrollments([]);
      setEnrollmentsLoaded(false);
    }
  }, [selectedClient?.id]);
  
  // Helper to invalidate enrollment cache for a client (call after adding/removing enrollments)
  const invalidateEnrollmentCache = useCallback((clientId) => {
    if (clientId) {
      delete enrollmentsCacheRef.current[clientId];
    }
  }, []);

  // Centralized reload for client enrollments.  Always fetches with
  // include_inactive=true so the Service History card (which renders
  // Active + Pending + Past from a single `clientEnrollments` array)
  // keeps showing past episodes after a disenrollment / transfer.
  // Previously every mutation site had its own ad-hoc refresh that
  // fetched the default (active-only) endpoint and then filtered the
  // result down further before stomping `clientEnrollments` — this
  // caused (a) "Past Services" to disappear after a disenroll and
  // (b) the Edit Client dialog's separate `existingEnrollments` state
  // to drift out of sync with the Client Chart header.  Routing all
  // post-mutation refreshes through this helper fixes both.
  const reloadClientEnrollments = useCallback(async (clientId) => {
    if (!clientId) return [];
    try {
      const response = await axios.get(
        `${API}/clients/${clientId}/service-enrollments?include_inactive=true`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      const data = response.data || [];
      enrollmentsCacheRef.current[clientId] = data;
      setClientEnrollments(data);
      // Keep the Edit Client "Manage Services" dialog's local
      // existingEnrollments state in sync as well — it derives the
      // currently-assigned provider/service grid from active+assigned
      // entries.
      setExistingEnrollments(data);
      return data;
    } catch (error) {
      console.error('Failed to reload client enrollments:', error);
      return [];
    }
  }, [setClientEnrollments]);

  // Real-time duplicate check - DISABLED due to memory constraints
  // The duplicate check will run on form submission instead
  // TODO: Re-enable after App.js is refactored into smaller components

  // fetchNotifications moved to NotificationsContext (Feb 2026).  It
  // is consumed via useNotificationsActions() at the top of Dashboard.
  
  // Fetch CPT conflict rules and insurance restrictions
  const fetchRules = useCallback(async () => {
    if (!user) return;
    try {
      const [conflictRulesRes, restrictionsRes, cptBillingRulesRes] = await Promise.all([
        axios.get(`${API}/cpt-conflict-rules`),
        axios.get(`${API}/insurance-cpt-restrictions`),
        // Jun 2026 — also pull the modifier-aware CPT billing rules so
        // the admin's "Insurance/CPT Conflict Rules" dialog can manage
        // them inline.  Server filters to active=true by default;
        // pass is_active=null to see soft-deleted history too.
        axios.get(`${API}/billing/rules/cpt?is_active=true`),
      ]);
      setCptConflictRules(conflictRulesRes.data || []);
      setInsuranceCptRestrictions(restrictionsRes.data || []);
      setCptBillingRules(cptBillingRulesRes.data?.rules || []);
    } catch (error) {
      console.error('Error fetching rules:', error);
    }
  }, [user]);
  
  // ────────────────────────────────────────────────────────────
  // Lazy-load deferred static data (Feb 2026 perf pass).
  //
  // The following resources are NOT fetched on Dashboard mount
  // anymore — they are loaded only when the user opens a tab or
  // dialog that needs them.  Each fetcher is gated by a ref so
  // it runs at most once per session (no race, no refetch loop).
  //
  // Triggers:
  //   • cpt-codes / cpt-bundles      → entering Billing-adjacent
  //                                    tabs OR opening a note
  //                                    editor / appointment form
  //   • cpt-conflict-rules + insurance-cpt-restrictions
  //                                  → opening a note editor
  //                                    (where validation matters)
  //   • form-templates / form-types  → entering Medical Records
  //                                    tab OR opening client chart
  //                                    OR opening a note editor
  //   • intake-bundles               → entering Admin tab
  //                                    (intake setup)
  //   • treatment-planning/service-types
  //                                  → entering client chart
  //                                    OR opening note editor
  //   • curricula                    → entering Groups tab
  //
  // Why refs (not state) for the "already-loaded" flags:
  //   refs don't trigger re-renders, and we don't want a useEffect
  //   to flip a state value that re-renders the whole 87k-line
  //   Dashboard.
  // ────────────────────────────────────────────────────────────
  const lazyLoadedRef = useRef({
    cpt: false,
    cptRules: false,
    forms: false,
    intakeBundles: false,
    serviceTypes: false,
    curricula: false,
  });

  const ensureCptCodesLoaded = useCallback(async () => {
    if (!user || lazyLoadedRef.current.cpt) return;
    lazyLoadedRef.current.cpt = true;
    try {
      const [codesRes, bundlesRes] = await Promise.all([
        axios.get(`${API}/cpt-codes`),
        axios.get(`${API}/cpt-bundles`),
      ]);
      setCptCodes(codesRes.data || []);
      setCptBundles(bundlesRes.data || []);
    } catch (error) {
      // Reset the flag so a retry can happen later
      lazyLoadedRef.current.cpt = false;
      console.error('[LAZY] cpt-codes/bundles load failed:', error);
    }
  }, [user]);

  const ensureCptRulesLoaded = useCallback(async () => {
    if (!user || lazyLoadedRef.current.cptRules) return;
    lazyLoadedRef.current.cptRules = true;
    try {
      await fetchRules();
    } catch (error) {
      lazyLoadedRef.current.cptRules = false;
      console.error('[LAZY] cpt rules load failed:', error);
    }
  }, [user, fetchRules]);

  const ensureFormsLoaded = useCallback(async (isAdminScope = false) => {
    if (!user || lazyLoadedRef.current.forms) return;
    lazyLoadedRef.current.forms = true;
    try {
      const url = isAdminScope
        ? `${API}/form-templates?include_archived=true`
        : `${API}/form-templates`;
      const [templatesRes, typesRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/form-types`),
      ]);
      setFormTemplates(templatesRes.data || []);
      setFormTypes(typesRes.data || []);
    } catch (error) {
      lazyLoadedRef.current.forms = false;
      console.error('[LAZY] forms load failed:', error);
    }
  }, [user]);

  const ensureIntakeBundlesLoaded = useCallback(async () => {
    // Feb 2026: dropped the `isSuperAdmin(user)` restriction.  Backend
    // /api/intake-bundles only requires authentication; the gate here
    // prevented supervisors / providers from ever seeing the
    // appointment-form intake bundle picker even though their backend
    // call would have succeeded.  Now any authenticated user can
    // lazy-load — same as users, providers, cptCodes, etc.
    if (!user || lazyLoadedRef.current.intakeBundles) return;
    lazyLoadedRef.current.intakeBundles = true;
    try {
      const res = await axios.get(`${API}/intake-bundles`);
      setIntakeBundles(res.data || []);
    } catch (error) {
      lazyLoadedRef.current.intakeBundles = false;
      console.error('[LAZY] intake-bundles load failed:', error);
    }
  }, [user]);

  const ensureServiceTypesLoaded = useCallback(async () => {
    if (!user || lazyLoadedRef.current.serviceTypes) return;
    lazyLoadedRef.current.serviceTypes = true;
    try {
      const res = await axios.get(`${API}/treatment-planning/service-types`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      });
      setServiceTypes(res.data || []);
    } catch (error) {
      lazyLoadedRef.current.serviceTypes = false;
      console.error('[LAZY] service-types load failed:', error);
    }
  }, [user]);

  const ensureCurriculaLoaded = useCallback(async () => {
    if (!user || lazyLoadedRef.current.curricula) return;
    lazyLoadedRef.current.curricula = true;
    try {
      const res = await axios.get(`${API}/curricula`);
      setCurricula(res.data || []);
    } catch (error) {
      lazyLoadedRef.current.curricula = false;
      console.error('[LAZY] curricula load failed:', error);
    }
  }, [user]);

  // Tab- and dialog-driven loader.  Each lazy fetch is gated by a
  // ref so this effect can fire freely without spamming the network.
  useEffect(() => {
    if (!user) return;

    // CPT codes/bundles — billing, claims, batch, ERA, and any
    // place a clinician picks a CPT code (notes, appointments).
    // Feb 2026 — also loaded for the admin tab so the CPT Billing
    // Rules picker can list every variant.  Without this the
    // multi-select dropdown rendered as empty when opening Setup →
    // CPT Billing Rules cold (no other tab had pre-warmed cptCodes).
    const cptTabs = new Set([
      'billing', 'billing-management', 'batch', 'claims',
      'era', 'eligibility', 'authorizations', 'admin',
    ]);
    if (cptTabs.has(activeTab) || showAppointmentForm || showAddNote || showNoteDetail || showClientDetail) {
      ensureCptCodesLoaded();
    }

    // Note-save validation rules — only when actually editing/saving notes.
    if (showAddNote || showNoteDetail) {
      ensureCptRulesLoaded();
    }

    // Form templates/types — medical records, client chart, intake.
    if (activeTab === 'medical-records' || activeTab === 'admin' || showClientDetail || showAddNote) {
      ensureFormsLoaded(activeTab === 'admin');
    }

    // Admin-only resources.
    if (activeTab === 'admin') {
      ensureIntakeBundlesLoaded();
    }
    // Intake bundles are also needed inside the appointment form so
    // providers can attach a bundle when scheduling — load on demand.
    if (showAppointmentForm) {
      ensureIntakeBundlesLoaded();
    }

    // Treatment planning service types — opening a client chart or note editor.
    if (showClientDetail || showAddNote || showNoteDetail || activeTab === 'admin') {
      ensureServiceTypesLoaded();
    }

    // Curricula picker — Groups tab.
    if (activeTab === 'groups') {
      ensureCurriculaLoaded();
    }
  }, [
    user, activeTab, showClientDetail, showAppointmentForm,
    showAddNote, showNoteDetail,
    ensureCptCodesLoaded, ensureCptRulesLoaded, ensureFormsLoaded,
    ensureIntakeBundlesLoaded, ensureServiceTypesLoaded, ensureCurriculaLoaded,
  ]);

  // Set default calendar view based on user role
  useEffect(() => {
    if (user && selectedProviders.length === 0) {
      // Providers and supervisors default to their own calendar
      if (userRoleName === 'provider' || userRoleName === 'supervisor') {
        setSelectedProviders([user.id]);
      } else {
        // Admin, office staff, billers see all by default
        setSelectedProviders(['all']);
      }
    }
  }, [user, selectedProviders.length]);
  
  // Fetch service types for admin portal
  const fetchServiceTypes = useCallback(async (force = false) => {
    if (!user) return;
    // Note: Removed serviceTypes.length from dependency array to prevent infinite loop
    // The force parameter allows manual refetch when needed
    try {
      const response = await axios.get(`${API}/treatment-planning/service-types`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      setServiceTypes(response.data);
    } catch (error) {
      console.error('Error fetching service types:', error);
    }
  }, [user]);
  
  // Fetch document deadline rules
  const fetchDocumentRules = useCallback(async (force = false) => {
    if (!user) return;
    // Note: Removed documentRules.length from dependency array to prevent infinite loop
    // The force parameter allows manual refetch when needed
    try {
      const response = await axios.get(`${API}/document-deadline-rules`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      setDocumentRules(response.data);
    } catch (error) {
      console.error('Error fetching document rules:', error);
    }
  }, [user]);
  
  // Fetch service types and rules when admin tab is accessed
  useEffect(() => {
    
    if (user && activeTab === 'admin') {
      void 0 && console.log('📥 Loading admin data...');
      
      // Force fetch when admin tab opens
      const loadAdminData = async () => {
        try {
          void 0 && console.log('Fetching from API...');
          const [servicesRes, rulesRes, aiToggleRes, autoNoteRes] = await Promise.all([
            axios.get(`${API}/treatment-planning/service-types`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            }),
            axios.get(`${API}/document-deadline-rules`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            }),
            axios.get(`${API}/admin/tools/ai-toggle`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            }).catch(() => ({ data: { ai_features_enabled: false } })),
            axios.get(`${API}/admin/tools/auto-note-toggle`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            }).catch(() => ({ data: { auto_create_notes_on_schedule: true } })),
          ]);
          
          setServiceTypes(servicesRes.data);
          setDocumentRules(rulesRes.data);
          setAdminAiEnabled(aiToggleRes.data.ai_features_enabled || false);
          setAutoNoteOnScheduleEnabled(autoNoteRes.data.auto_create_notes_on_schedule !== false);
          void 0 && console.log('Admin data loaded:', servicesRes.data.length, 'services,', rulesRes.data.length, 'rules, AI:', aiToggleRes.data.ai_features_enabled);
        } catch (error) {
          console.error('Error loading admin data:', error);
        }
      };
      
      loadAdminData();
    }
  }, [user, activeTab]);

  // Handler for opening Rules Management Dialog
  const handleOpenRulesManagement = useCallback(() => {
    void 0 && console.log('🔧🔧🔧 handleOpenRulesManagement CALLED! 🔧🔧🔧');
    setShowRulesManagementDialog(true);
    fetchRules(); // Fetch latest rules when opening dialog
  }, [fetchRules]);
  
  // DEBUG: Log when handler is created
  void 0 && console.log('✅ handleOpenRulesManagement created:', typeof handleOpenRulesManagement);

  // dismissNotification moved to NotificationsContext (Feb 2026).

  // Staff messaging functions
  const lastMessageCheckRef = useRef({ unread: 0, lastMessageAt: null });
  
  const fetchStaffMessageThreads = useCallback(async (showLoading = true) => {
    if (!user) return;
    try {
      if (showLoading) setLoadingStaffMessages(true);
      const response = await axios.get(`${API}/staff/messages`);
      // Feb 2026 perf fix — preserve reference when poll returns the
      // same thread list to avoid unnecessary App.js re-renders.  We
      // compare by id + unread_count_staff + last_message_at since
      // those are the fields the message UI reacts to.
      const next = response.data || [];
      const unread = next.reduce((sum, t) => sum + (t.unread_count_staff || 0), 0);
      setStaffMessageThreads(prev => {
        if (prev.length !== next.length) return next;
        for (let i = 0; i < prev.length; i++) {
          if (prev[i].id !== next[i].id) return next;
          if (prev[i].unread_count_staff !== next[i].unread_count_staff) return next;
          if (prev[i].last_message_at !== next[i].last_message_at) return next;
        }
        return prev;
      });
      setUnreadStaffMessagesCount(prev => prev === unread ? prev : unread);

      // Update the check reference
      const lastMessage = next[0];
      lastMessageCheckRef.current = {
        unread,
        lastMessageAt: lastMessage?.last_message_at || null
      };
    } catch (error) {
      console.error('Error fetching staff messages:', error);
    } finally {
      if (showLoading) setLoadingStaffMessages(false);
    }
  }, [user]);

  // Lightweight check for new messages - only fetches full data if something changed
  const checkForNewMessages = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/staff/messages/check`);
      const { unread_count, last_message_at } = response.data;
      
      // Only refetch if unread count or last message changed
      if (unread_count !== lastMessageCheckRef.current.unread || 
          last_message_at !== lastMessageCheckRef.current.lastMessageAt) {
        void 0 && console.log('[Messages] Change detected, refetching...');
        await fetchStaffMessageThreads(false);
      }
    } catch (error) {
      console.error('Error checking messages:', error);
    }
  }, [user, fetchStaffMessageThreads]);

  const fetchStaffThreadMessages = async (threadId) => {
    try {
      const response = await axios.get(`${API}/staff/messages/${threadId}`);
      setSelectedStaffThread(response.data.thread);
      setStaffThreadMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching thread messages:', error);
      toast.error('Failed to load messages');
    }
  };

  // Fetch staff announcements
  const fetchStaffAnnouncements = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/staff/announcements`);
      // Feb 2026 perf fix — preserve reference when content matches
      // so the periodic poll doesn't force an App.js re-render every
      // 15-60s with identical data.  Compare by id + is_read since
      // those are the only fields the UI uses.
      const next = response.data || [];
      const nextUnread = next.filter(a => !a.is_read).length;
      setStaffAnnouncements(prev => {
        if (prev.length !== next.length) return next;
        for (let i = 0; i < prev.length; i++) {
          if (prev[i].id !== next[i].id) return next;
          if (prev[i].is_read !== next[i].is_read) return next;
        }
        return prev;
      });
      setUnreadAnnouncementsCount(prev => prev === nextUnread ? prev : nextUnread);
    } catch (error) {
      console.error('Error fetching staff announcements:', error);
    }
  }, [user]);

  // Mark announcement as read
  const markAnnouncementRead = async (announcementId) => {
    try {
      await axios.post(`${API}/staff/announcements/${announcementId}/read`);
      setStaffAnnouncements(prev => prev.map(a => 
        a.id === announcementId ? { ...a, is_read: true } : a
      ));
      setUnreadAnnouncementsCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking announcement read:', error);
    }
  };

  const sendStaffReply = async () => {
    if (!staffReplyText.trim() || !selectedStaffThread) return;
    
    const messageText = staffReplyText;
    setSendingStaffReply(true);
    
    // Optimistic UI update - add message to local state immediately
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      thread_id: selectedStaffThread.id,
      sender_id: user.id,
      sender_name: user.full_name,
      sender_type: 'staff',
      message: messageText,
      created_at: new Date().toISOString(),
      attachments: []
    };
    setStaffThreadMessages(prev => [...prev, optimisticMessage]);
    setStaffReplyText(''); // Clear input immediately for better UX
    
    try {
      await axios.post(
        `${API}/staff/messages/${selectedStaffThread.id}/reply`,
        {
          message: messageText,
          attachments: []
        }
      );
      toast.success('Reply sent');
      // Refresh to get the real message with correct ID
      fetchStaffThreadMessages(selectedStaffThread.id);
      fetchStaffMessageThreads(false); // Refresh list without loading spinner
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
      // Remove optimistic message on failure
      setStaffThreadMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
      setStaffReplyText(messageText); // Restore the message text
    } finally {
      setSendingStaffReply(false);
    }
  };

  // Fetch staff messages - initial load and periodic check for changes
  useEffect(() => {
    if (user) {
      fetchStaffMessageThreads(true); // Show loading on initial fetch
      fetchStaffAnnouncements(); // Also fetch announcements
      // Poll less frequently when WebSocket is connected (WebSocket handles real-time)
      // Poll every 15s if WS disconnected, every 60s if connected (as backup)
      const pollInterval = wsConnected ? 60000 : 15000;
      const interval = setInterval(() => {
        if (!document.hidden && !isEditingDialogOpenRef.current && !isUserBusy()) {
          checkForNewMessages();
        }
      }, pollInterval);
      return () => clearInterval(interval);
    }
  }, [user, fetchStaffMessageThreads, fetchStaffAnnouncements, checkForNewMessages, wsConnected]);

  // Debug Rules Management Dialog state
  useEffect(() => {
    void 0 && console.log('🔧 showRulesManagementDialog changed to:', showRulesManagementDialog);
  }, [showRulesManagementDialog]);

  // Diagnoses are now delivered by the chart-bundle endpoint
  // (workstream C1, Feb 2026).  The legacy "fetch when empty"
  // follow-up effect that lived here used to fire a redundant
  // /clients/{id}/diagnoses request on every chart open and is no
  // longer needed.

  const fetchAccessibleClients = async () => {
    try {
      const response = await axios.get(`${API}/staff/accessible-clients`);
      setAccessibleClients(response.data || []);
      setBroadcastClientCount(response.data?.length || 0);
    } catch (error) {
      console.error('Error fetching accessible clients:', error);
    }
  };


  // Fetch Claims Workshop data (compact view for tab)

  // fetchWorkshopClaims and fetchWorkshopSummary moved to ClaimsTab.jsx

  // ── Centralized eligibility status display — delegated to shared
  // utility at /utils/eligibilityDisplay.jsx.  Local alias kept to
  // avoid touching the many call sites at once; underlying data now
  // lives in a single module used by EligibilityTab, Client* dialogs,
  // ClientPortal, etc.
  const getEligibilityDisplay = getEligibilityDisplayShared;

  // Feb 2026 — Moved into /utils/eligibilityDisplay.jsx as
  // `eligibilityTooltip` so V2 (`ActiveClientsRoute`) and legacy can
  // never drift again.  The local arrow form is kept as a thin
  // delegate so existing call sites still work (`eligibilityTooltip(c)`).
  const eligibilityTooltip = eligibilityTooltipShared;

  // DOS-aware: checks the appointment date against coverage_effective → coverage_end
  // Feb 2026 — delegated to the shared utility (`utils/eligibilityDisplay.jsx`)
  // so the symmetric 7-day staleness rule is applied identically here,
  // on V2 surfaces, on the calendar tile, on the eligibility tab and
  // in the Claims Workshop.  Inline duplicate previously diverged and
  // showed "Coverage Inactive" on future-dated appts when the verdict
  // really should have been neutral "Last verified active on {date}".
  const getDOSEligibilityDisplay = (client, appointmentDateStr) =>
    sharedGetDOSEligibilityDisplay(client, appointmentDateStr);

  // Eligibility Indicator Component - reusable across client cards, headers, and calendar
  const EligibilityIndicator = ({ client, size = 'sm', showTooltip = true, checkDetail = null }) => {
    if (!client) return null;

    // Self-pay short-circuit: render the blue "Self Pay" badge directly
    // (no stale-check decoration, no tooltip about last-checked dates,
    // since there's nothing to verify).
    const selfPay = isClientSelfPay(client);
    const status = selfPay ? 'self_pay' : (client.primary_eligibility_status || 'not_checked');
    const lastChecked = selfPay ? null : (client.primary_eligibility_checked || client.last_eligibility_check);
    const { icon, color, bg, label } = getEligibilityDisplay(status);

    let hoursAgo = null;
    let isStale = true;
    if (lastChecked) {
      hoursAgo = Math.round((new Date() - new Date(lastChecked)) / 3600000);
      isStale = hoursAgo >= 24;
    }

    const displayColor = (status === 'verified' || status === 'active') && isStale
      ? 'text-yellow-600' : color;
    const displayBg = (status === 'verified' || status === 'active') && isStale
      ? 'bg-yellow-50' : bg;
    const displayLabel = (status === 'verified' || status === 'active') && isStale
      ? 'Eligible (Stale)' : label;

    const tooltipContent = (() => {
      if (!lastChecked) return `${displayLabel} · never checked`;
      const d = new Date(lastChecked);
      const dateStr = isNaN(d.getTime()) ? lastChecked : (() => {
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        return `${mm}/${dd}/${yyyy} ${hh}:${mi}`;
      })();
      const dosStr = checkDetail?.date_of_service || client.last_eligibility_dos;
      const dosLine = dosStr ? ` · DOS ${dosStr}` : ' · General (no DOS)';
      return `${displayLabel} · checked ${hoursAgo}h ago (${dateStr})${dosLine}`;
    })();

    const sizeClasses = size === 'sm' ? 'text-xs px-1.5 py-0.5' : 'text-sm px-2 py-1';

    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full ${sizeClasses} ${displayBg} ${displayColor} font-medium cursor-help`}
        title={showTooltip ? eligibilityTooltip(client, checkDetail) : undefined}
      >
        {icon}
        {size !== 'sm' && <span>{displayLabel}</span>}
      </span>
    );
  };


  // Helper function to build unified task list from all sources
  const buildUnifiedTaskList = () => {
    const unifiedTasks = [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeekEnd = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    // Helper to determine urgency
    const getUrgency = (dueDate) => {
      if (!dueDate) return 'upcoming';
      const due = new Date(dueDate);
      if (due < today) return 'overdue';
      if (due.toDateString() === today.toDateString()) return 'due_today';
      if (due <= thisWeekEnd) return 'due_this_week';
      return 'upcoming';
    };
    
    // Helper to check if item is supervisee work (supervision duties, not direct client service)
    const isSuperviseeWork = (providerId, clientProviderId) => {
      if (!user?.is_supervisor) return false;
      // If the item's provider is not the current user, it's supervisee work
      return providerId !== user.id && clientProviderId !== user.id;
    };
    
    // 1. Manual tasks assigned to me (tasks comes from UIContext)
    tasks?.forEach(task => {
      if (!showCompletedTasks && (task.status === 'completed' || task.status === 'dismissed' || task.status === 'cancelled')) return;
      
      // Determine task type - preserve special types like discharge_required
      const taskType = task.type === 'discharge_required' ? 'discharge_required' : 'manual_task';
      const badgeColor = task.type === 'discharge_required' ? 'bg-amber-500' : 'bg-blue-500';
      const badgeText = task.type === 'discharge_required' ? 'DISCHARGE REQUIRED' : (task.category?.toUpperCase() || 'TASK');
      
      unifiedTasks.push({
        id: `manual-assigned-${task.id}`,
        originalId: task.id,
        type: taskType,
        category: task.type === 'discharge_required' ? 'clinical' : (task.category || 'other'),
        title: task.title,
        description: task.description,
        clientName: task.client_name,
        clientId: task.client_id,
        clientPhone: task.client_phone,
        rescheduleInLabel: task.reschedule_in_label,
        targetAppointmentDate: task.target_appointment_date,
        sourceAppointmentDate: task.source_appointment_date,
        rebookedAppointmentId: task.rebooked_appointment_id,
        rebookedAppointmentStartTime: task.rebooked_appointment_start_time,
        providerId: task.assigned_to,
        providerName: users?.find(u => u.id === task.assigned_by)?.full_name,
        assignedBy: task.assigned_by,
        assignedByName: users?.find(u => u.id === task.assigned_by)?.full_name,
        status: task.status,
        priority: task.priority || 'normal',
        dueDate: task.due_date,
        urgency: getUrgency(task.due_date),
        createdAt: task.created_at,
        source: 'manual',
        ownership: 'assigned_to_me',
        isSuperviseeWork: false,
        originalData: task,
        badgeColor: badgeColor,
        badgeText: badgeText,
        // Action link data
        actionType: task.action_type,
        actionData: task.action_data,
        appointmentId: task.appointment_id,
        // Enrollment data for discharge tasks
        enrollmentId: task.enrollment_id,
        serviceTypeName: task.service_type_name,
      });
    });
    
    // 2. Manual tasks I assigned to others (tasksAssignedByMe comes from UIContext)
    // Skip tasks assigned to myself - they're already in the "assigned to me" list above
    tasksAssignedByMe?.forEach(task => {
      if (!showCompletedTasks && (task.status === 'completed' || task.status === 'dismissed' || task.status === 'cancelled')) return;
      // Skip self-assigned tasks to avoid duplicates
      if (task.assigned_to === task.assigned_by) return;
      unifiedTasks.push({
        id: `manual-created-${task.id}`,
        originalId: task.id,
        type: 'manual_task',
        category: task.category || 'other',
        title: task.title,
        description: task.description,
        clientName: task.client_name,
        clientId: task.client_id,
        clientPhone: task.client_phone,
        rescheduleInLabel: task.reschedule_in_label,
        targetAppointmentDate: task.target_appointment_date,
        sourceAppointmentDate: task.source_appointment_date,
        rebookedAppointmentId: task.rebooked_appointment_id,
        rebookedAppointmentStartTime: task.rebooked_appointment_start_time,
        providerId: task.assigned_to,
        providerName: users?.find(u => u.id === task.assigned_to)?.full_name,
        assignedTo: task.assigned_to,
        assignedToName: users?.find(u => u.id === task.assigned_to)?.full_name,
        status: task.status,
        priority: task.priority || 'normal',
        dueDate: task.due_date,
        urgency: getUrgency(task.due_date),
        createdAt: task.created_at,
        source: 'manual',
        ownership: 'i_assigned',
        isSuperviseeWork: false,
        originalData: task,
        badgeColor: 'bg-indigo-500',
        badgeText: 'ASSIGNED BY ME',
        // Action link data
        actionType: task.action_type,
        actionData: task.action_data,
      });
    });
    
    // 3. Draft notes and clinical documentation from myTodoNotes
    myTodoNotes?.forEach(item => {
      if (item.is_dismissed) return;
      
      const isChangeRequest = item.notification_type === 'change_request';
      const isGroupRequest = item.requested_by && item.group_id && !item.notification_type;
      const isMedicationRequest = item.notification_type === 'medication_referral';
      const isTreatmentPlan = item.item_type === 'treatment_plan' || item.notification_type === 'treatment_plan_signature';
      const isTreatmentPlanCorrections = item.item_type === 'treatment_plan_corrections';
      
      let taskType = 'clinical_note';
      let category = 'clinical';
      let badgeColor = 'bg-teal-500';
      let badgeText = item.status?.replace(/_/g, ' ').toUpperCase() || 'DRAFT';
      // Client name for title - badge already shows task type
      // Use allClientsForLookup to avoid filter bleeding from Client Management
      // Feb 27, 2026 — Detect both UUIDs AND raw encrypted PHI blobs.
      // Some legacy `change_request` rows persisted the Fernet
      // ciphertext under `client_name` (no spaces, no commas, very
      // long base64-looking).  We mirror the backend's
      // `_looks_encrypted` heuristic and the UUID check together so
      // a stale cache entry on the user's end still falls back to a
      // real client name from `allClientsForLookup`.
      let clientName = item.client_name;
      const isUUID = clientName && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(clientName);
      const looksEncrypted = clientName
        && typeof clientName === 'string'
        && clientName.length >= 40
        && !clientName.includes(' ')
        && !clientName.includes(',');
      if (!clientName || isUUID || looksEncrypted) {
        // Lookup client name from available sources
        const foundClient = allClientsForLookup?.find(c => c.id === item.client_id) || clients?.find(c => c.id === item.client_id);
        clientName = foundClient?.full_name || foundClient?.first_name ? `${foundClient.first_name || ''} ${foundClient.last_name || ''}`.trim() : (looksEncrypted ? 'Client' : (clientName || 'Client'));
      }
      let title = clientName;
      // Description should provide context, not repeat the task type
      let description = item.date_of_service ? `Session ${formatDateStringForDisplay(item.date_of_service)}` : '';
      
      if (isChangeRequest) {
        taskType = 'change_request';
        category = 'administrative';
        badgeColor = item.status === 'pending' ? 'bg-blue-500' : item.status === 'approved' ? 'bg-green-500' : 'bg-red-500';
        badgeText = 'CHANGE REQUEST';
        title = clientName;
        description = item.change_description || 'Change requested';
      } else if (isGroupRequest) {
        taskType = 'group_request';
        category = 'administrative';
        badgeColor = item.status === 'approved' ? 'bg-green-500' : 'bg-red-500';
        badgeText = 'GROUP REQUEST';
        title = clientName;
        description = item.group_name ? `Requesting to join ${item.group_name}` : 'Group enrollment request';
      } else if (isMedicationRequest) {
        taskType = 'medication_referral';
        category = 'clinical';
        badgeColor = 'bg-purple-500';
        badgeText = 'MEDICATION REFERRAL';
        title = clientName;
        description = 'Medication management referral';
      } else if (isTreatmentPlanCorrections) {
        taskType = 'treatment_plan_corrections';
        category = 'clinical';
        badgeColor = 'bg-orange-500';
        badgeText = 'CORRECTIONS NEEDED';
        title = clientName;
        description = item.needs_corrections_reason || 'Corrections requested';
      } else if (isTreatmentPlan) {
        taskType = 'treatment_plan_signature';
        category = 'clinical';
        badgeColor = 'bg-indigo-500';
        badgeText = 'SIGNATURE REQUIRED';
        title = clientName;
        description = 'Treatment plan ready for signature';
      } else {
        // Regular clinical note - use template/form name in description
        description = item.template_name || item.form_type_name || (item.date_of_service ? `Session ${formatDateStringForDisplay(item.date_of_service)}` : 'Draft note');
        
        // Update badge based on note status
        if (item.status === 'sent_to_supervisor') {
          // Jun 2026 fix — supervisors reported their sign queue said
          // "DRAFT"/"SENT TO SUPERVISOR" for notes awaiting THEIR
          // signature.  When the current user is the assigned
          // supervisor and hasn't signed yet, say what they need to
          // do: SIGNATURE REQUIRED.
          const needsMySignature =
            (item.assigned_supervisor === user?.id && !item.supervisor_signed_at) ||
            (item.assigned_supervisor_2 === user?.id && !item.supervisor_2_signed_at);
          badgeColor = needsMySignature ? 'bg-indigo-500' : 'bg-purple-500';
          badgeText = needsMySignature ? 'SIGNATURE REQUIRED' : 'SENT TO SUPERVISOR';
        } else if (item.status === 'pending_supervisor_signature') {
          // Feb 2026 billing-driven soft-route — was falling through
          // to the generic DRAFT badge (user-reported bug).
          const needsMySignature =
            (item.assigned_supervisor === user?.id && !item.supervisor_signed_at) ||
            (item.assigned_supervisor_2 === user?.id && !item.supervisor_2_signed_at);
          badgeColor = 'bg-indigo-500';
          badgeText = needsMySignature ? 'SIGNATURE REQUIRED' : 'PENDING SUPERVISOR SIGNATURE';
        } else if (item.status === 'needs_corrections') {
          badgeColor = 'bg-orange-500';
          badgeText = 'NEEDS CORRECTIONS';
        } else {
          // Default: draft
          badgeColor = 'bg-teal-500';
          badgeText = 'DRAFT';
        }
      }
      
      unifiedTasks.push({
        id: `todo-${item.id}`,
        originalId: item.id,
        type: taskType,
        category,
        title,
        description,
        clientName,
        clientId: item.client_id,
        providerId: item.provider_id,
        providerName: findUserOrProvider(item.provider_id)?.full_name,
        status: item.status,
        priority: item.corrections_requested_at ? 'urgent' : 'normal',
        dueDate: item.due_date,
        appointmentId: item.appointment_id,
        appointmentDate: item.appointment_date || item.date_of_service,
        urgency: item.corrections_requested_at ? 'overdue' : getUrgency(item.due_date),
        createdAt: item.created_at,
        source: 'automated',
        ownership: 'assigned_to_me',
        isSuperviseeWork: isSuperviseeWork(item.provider_id, item.created_by),
        originalData: item,
        badgeColor,
        badgeText,
        // Correction reason for notes sent back by biller
        correctionsReason: item.needs_corrections_reason,
        correctionsRequestedAt: item.corrections_requested_at,
        correctionsRequestedBy: item.corrections_requested_by,
        correctionsRequestedByName: item.corrections_requested_by_name,
      });
    });
    
    // 4. Treatment plans due for review
    treatmentPlansDueForReview?.forEach(plan => {
      const reviewDate = new Date(plan.next_review_date + (plan.next_review_date.includes('T') ? '' : 'T12:00:00'));
      const daysUntilReview = Math.ceil((reviewDate - today) / (1000 * 60 * 60 * 24));
      const isOverdue = daysUntilReview < 0;
      
      // Get client name - fallback to looking up from clients array if backend didn't provide it
      // Use allClientsForLookup to avoid filter bleeding from Client Management
      let clientName = plan.client_name;
      if (!clientName || clientName.match(/^[0-9a-f-]{36}$/i)) {
        // client_name is missing or looks like a UUID, try to look it up
        const client = allClientsForLookup?.find(c => c.id === plan.client_id) || clients?.find(c => c.id === plan.client_id);
        clientName = client?.full_name || client?.first_name ? `${client.first_name || ''} ${client.last_name || ''}`.trim() : 'Unknown Client';
      }
      
      unifiedTasks.push({
        id: `tp-review-${plan.id}`,
        originalId: plan.id,
        type: 'treatment_plan_review',
        category: 'clinical',
        title: clientName || 'Client',
        description: isOverdue 
          ? `Review ${Math.abs(daysUntilReview)} days overdue`
          : `Review due ${reviewDate.toLocaleDateString()}`,
        clientName: clientName,
        clientId: plan.client_id,
        providerId: plan.provider_id,
        providerName: findUserOrProvider(plan.provider_id)?.full_name,
        status: isOverdue ? 'overdue' : 'pending',
        priority: isOverdue ? 'urgent' : daysUntilReview <= 7 ? 'normal' : 'low',
        dueDate: plan.next_review_date,
        urgency: isOverdue ? 'overdue' : daysUntilReview <= 7 ? 'due_this_week' : 'upcoming',
        createdAt: plan.created_at,
        source: 'automated',
        ownership: 'assigned_to_me',
        isSuperviseeWork: isSuperviseeWork(plan.provider_id, plan.created_by),
        originalData: plan,
        badgeColor: isOverdue ? 'bg-red-600' : daysUntilReview <= 7 ? 'bg-orange-600' : 'bg-yellow-600',
        badgeText: isOverdue ? 'OVERDUE REVIEW' : 'REVIEW DUE',
      });
    });
    
    // 5. Incomplete documentation / scheduled reviews
    incompleteDocumentation?.forEach(doc => {
      const hoursOverdue = doc.hours_overdue || 0;
      const clientName = doc.client_name || 'Client';
      
      unifiedTasks.push({
        id: `doc-${doc.id || doc.client_id}-${doc.document_type}`,
        originalId: doc.id,
        type: 'incomplete_documentation',
        category: 'compliance',
        title: clientName,
        description: doc.date_of_service 
          ? `Session ${doc.date_of_service}${hoursOverdue > 0 ? ` • ${hoursOverdue}h overdue` : ''}`
          : (doc.message || doc.rule_name || 'Documentation needed'),
        clientName: doc.client_name,
        clientId: doc.client_id,
        providerId: doc.provider_id,
        providerName: findUserOrProvider(doc.provider_id)?.full_name,
        status: 'pending',
        priority: doc.urgency === 'urgent' ? 'urgent' : doc.urgency === 'warning' ? 'normal' : 'low',
        dueDate: doc.next_due_date,
        urgency: doc.urgency === 'urgent' ? 'overdue' : doc.urgency === 'warning' ? 'due_this_week' : 'upcoming',
        createdAt: doc.created_at,
        source: 'automated',
        ownership: 'assigned_to_me',
        isSuperviseeWork: isSuperviseeWork(doc.provider_id, doc.created_by),
        originalData: doc,
        badgeColor: doc.urgency === 'urgent' ? 'bg-red-600' : doc.urgency === 'warning' ? 'bg-orange-600' : 'bg-cyan-600',
        badgeText: doc.document_type?.toUpperCase() || 'DOCUMENTATION',
      });
    });
    
    // 6. MDT Pending Actions (these are supervisor work by definition)
    mdtPendingActions?.forEach(mdt => {
      const providerName = findUserOrProvider(mdt.provider_id)?.full_name;
      unifiedTasks.push({
        id: `mdt-${mdt.id}`,
        originalId: mdt.id,
        type: 'mdt_review',
        category: 'supervision',
        title: mdt.client_name || 'Client',
        description: providerName ? `Review for ${providerName}` : 'Multidisciplinary team review',
        clientName: mdt.client_name,
        clientId: mdt.client_id,
        providerId: mdt.provider_id,
        providerName: providerName,
        status: 'pending',
        priority: 'normal',
        dueDate: mdt.due_date,
        urgency: getUrgency(mdt.due_date),
        createdAt: mdt.created_at,
        source: 'automated',
        ownership: 'assigned_to_me',
        isSuperviseeWork: true,
        originalData: mdt,
        badgeColor: 'bg-violet-500',
        badgeText: 'MDT REVIEW',
      });
    });
    
    // Re-categorize supervisee work as "supervision" category
    // This applies to notes, treatment plans, etc. that belong to supervisees
    unifiedTasks.forEach(task => {
      if (task.isSuperviseeWork && task.category !== 'supervision') {
        task.category = 'supervision';
      }
    });
    
    return unifiedTasks;
  };

  // getCARCDescription moved to ERATab.jsx

  // Treatment plans cache with client-based memoization
  const [treatmentPlansCache, setTreatmentPlansCache] = useState(new Map());
  const [treatmentPlansLastUpdated, setTreatmentPlansLastUpdated] = useState(new Map());
  
  // Cache cleanup - remove old entries to prevent memory leaks
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const maxAge = 5 * 60 * 1000; // 5 minutes
      
      setTreatmentPlansCache(prev => {
        const newCache = new Map();
        prev.forEach((value, key) => {
          const lastUpdated = treatmentPlansLastUpdated.get(key) || 0;
          if (now - lastUpdated < maxAge) {
            newCache.set(key, value);
          }
        });
        return newCache;
      });
      
      setTreatmentPlansLastUpdated(prev => {
        const newUpdated = new Map();
        prev.forEach((value, key) => {
          if (now - value < maxAge) {
            newUpdated.set(key, value);
          }
        });
        return newUpdated;
      });
    }, 2 * 60 * 1000); // Clean every 2 minutes
    
    return () => clearInterval(cleanupInterval);
  }, [treatmentPlansLastUpdated]);
  
  // Cache invalidation helper
  const invalidateTreatmentPlansCache = useCallback((clientId) => {
    setTreatmentPlansCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(clientId);
      return newCache;
    });
    setTreatmentPlansLastUpdated(prev => {
      const newUpdated = new Map(prev);
      newUpdated.set(clientId, Date.now());
      return newUpdated;
    });
  }, []);

  // Memoized fetch function - only fetches if not cached or cache is stale
  const fetchTreatmentPlans = useCallback(async (clientId, forceRefresh = false) => {
    try {
      // Check cache first (unless force refresh)
      const cached = treatmentPlansCache.get(clientId);
      const lastUpdated = treatmentPlansLastUpdated.get(clientId) || 0;
      const isStale = Date.now() - lastUpdated > 30000; // Cache for 30 seconds
      
      if (!forceRefresh && cached && !isStale) {
        void 0 && console.log('🚀 Using cached treatment plans for client:', clientId);
        setTreatmentPlans(cached);
        return;
      }
      
      void 0 && console.log('📡 Fetching fresh treatment plans for client:', clientId);
      setLoadingTreatmentPlans(true);
      
      // Fetch treatment plans (include archived to show in separate section)
      const treatmentPlansResponse = await axios.get(`${API}/clients/${clientId}/treatment-plans?include_archived=true`);
      const plans = treatmentPlansResponse.data;
      
      // Update cache
      setTreatmentPlansCache(prev => {
        const newCache = new Map(prev);
        newCache.set(clientId, plans);
        return newCache;
      });
      setTreatmentPlansLastUpdated(prev => {
        const newUpdated = new Map(prev);
        newUpdated.set(clientId, Date.now());
        return newUpdated;
      });
      
      setTreatmentPlans(plans);
      
      // Auto-expand all PANs and goals for a readable default view
      const panKeys = new Set();
      const goalKeys = new Set();
      for (const plan of plans) {
        if (plan.pans) {
          for (const pan of plan.pans) {
            panKeys.add(`${plan.id}-${pan.id}`);
            if (pan.goals) {
              for (const goal of pan.goals) {
                goalKeys.add(`${plan.id}-${pan.id}-${goal.id}`);
              }
            }
          }
        }
      }
      setExpandedPANs(panKeys);
      setExpandedGoals(goalKeys);
      
      // Fetch service types only if not already loaded
      if (serviceTypes.length === 0) {
        const serviceTypesResponse = await axios.get(`${API}/treatment-planning/service-types`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
        });
        setServiceTypes(serviceTypesResponse.data);
      }
    } catch (error) {
      console.error('Error fetching treatment plans:', error);
      toast.error('Failed to load treatment plans');
    } finally {
      setLoadingTreatmentPlans(false);
    }
  }, [treatmentPlansCache, treatmentPlansLastUpdated, serviceTypes.length]);

  // Enhanced function for when treatment plans are modified
  const refetchTreatmentPlans = useCallback(async (clientId) => {
    invalidateTreatmentPlansCache(clientId);
    await fetchTreatmentPlans(clientId, true); // Force refresh
  }, [invalidateTreatmentPlansCache, fetchTreatmentPlans]);

  // Load treatment plans when treatment-plans tab is opened
  useEffect(() => {
    if (clientDetailTab === 'treatment-plans' && selectedClient?.id) {
      fetchTreatmentPlans(selectedClient.id);
    }
  }, [clientDetailTab, selectedClient?.id]);

  // Load vitals and medication referrals when medications tab is opened
  useEffect(() => {
    if (clientDetailTab === 'medications' && selectedClient?.id) {
      const fetchVitals = async () => {
        try {
          const response = await axios.get(`${API}/clients/${selectedClient.id}/vitals`);
          setVitalReadings(response.data);
        } catch (error) {
          console.error('Failed to load vitals:', error);
        }
      };
      fetchVitals();
      // Fetch medication referral requests (needed for all users to see referral status)
      if (medicationReferralRequests.length === 0) {
        fetchMedicationReferralRequests();
      }
    }
  }, [clientDetailTab, selectedClient?.id]);

  // Fetch client vitals when creating a note (for client_vitals field type snapshot)
  useEffect(() => {
    const fetchClientVitalsForNote = async () => {
      if (selectedNoteClients?.length > 0) {
        const clientId = selectedNoteClients[0];
        try {
          const response = await axios.get(`${API}/clients/${clientId}/vitals`);
          setClientVitals(response.data || []);
        } catch (error) {
          console.error('Failed to load client vitals for note:', error);
          setClientVitals([]);
        }
      }
    };
    fetchClientVitalsForNote();
  }, [selectedNoteClients]);

  // Fetch treatment plan goals for linking to progress notes
  const fetchTreatmentPlanGoals = async (clientId) => {
    if (!clientId) return;
    setLoadingTreatmentPlanGoals(true);
    try {
      const response = await axios.get(`${API}/clients/${clientId}/treatment-plan-goals`);
      setAvailableTreatmentPlanGoals(response.data || []);
    } catch (error) {
      console.error('Error fetching treatment plan goals:', error);
      setAvailableTreatmentPlanGoals([]);
    } finally {
      setLoadingTreatmentPlanGoals(false);
    }
  };

  // Fetch client billing data
  const fetchClientBilling = async (clientId) => {
    try {
      const [summaryRes, claimsRes, paymentsRes] = await Promise.allSettled([
        axios.get(`${API}/clients/${clientId}/billing-summary`),
        axios.get(`${API}/clients/${clientId}/claims-history`),
        axios.get(`${API}/clients/${clientId}/payment-history`)
      ]);
      
      if (summaryRes.status === 'fulfilled') setClientBillingSummary(summaryRes.value.data);
      if (claimsRes.status === 'fulfilled') setClientClaims(claimsRes.value.data.claims || []);
      if (paymentsRes.status === 'fulfilled') setClientPaymentHistory(paymentsRes.value.data.payments || []);
    } catch (error) {
      console.error('Error fetching client billing:', error);
    }
  };

  // Sort claims for billing tab
  const sortClientClaims = (column) => {
    const newDirection = claimsSortColumn === column && claimsSortDirection === 'asc' ? 'desc' : 'asc';
    setClaimsSortColumn(column);
    setClaimsSortDirection(newDirection);
    
    const sorted = [...clientClaims].sort((a, b) => {
      let aVal, bVal;
      
      switch(column) {
        case 'claim_number':
          aVal = a.claim_number || '';
          bVal = b.claim_number || '';
          break;
        case 'date_of_service':
          aVal = a.date_of_service || '';
          bVal = b.date_of_service || '';
          break;
        case 'provider_name':
          aVal = a.provider_name || '';
          bVal = b.provider_name || '';
          break;
        case 'insurance':
          aVal = a.primary_insurance || '';
          bVal = b.primary_insurance || '';
          break;
        case 'billed':
          aVal = a.total_charged_amount || 0;
          bVal = b.total_charged_amount || 0;
          break;
        case 'paid':
          aVal = (a.primary_paid_amount || 0) + (a.secondary_paid_amount || 0) + (a.self_pay_received || 0);
          bVal = (b.primary_paid_amount || 0) + (b.secondary_paid_amount || 0) + (b.self_pay_received || 0);
          break;
        case 'balance':
          aVal = a.patient_balance || 0;
          bVal = b.patient_balance || 0;
          break;
        case 'insurance_balance': {
          // Mirror the per-claim outstanding-AR calculation in the table.
          // Kept inline (rather than imported) so sort doesn't drag in the
          // React component module; stays pure-data.
          const OPEN = new Set([
            'billed', 'submitted', 'resubmitted', 'billed_legacy',
            'acknowledged', 'pending_payment', 'ready_for_secondary',
            'takeback_received', 'takeback_rebilled_pending', 'takeback_unresolved',
          ]);
          const TAKEBACK = new Set([
            'takeback_received', 'takeback_rebilled_pending', 'takeback_unresolved',
          ]);
          const insBalance = (c) => {
            const s = (c.status || '').toLowerCase();
            if (!OPEN.has(s)) return 0;
            const charged = Number(c.total_charged_amount || 0);
            if (TAKEBACK.has(s)) {
              const net = c.financials_cache?.net_balance;
              if (net !== undefined && net !== null) return Math.max(0, Number(net));
              return charged;
            }
            const paid = Number(c.primary_paid_amount || c.paid_amount || 0) + Number(c.secondary_paid_amount || 0);
            return Math.max(0, charged - paid);
          };
          aVal = insBalance(a);
          bVal = insBalance(b);
          break;
        }
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'cpt':
          aVal = a.service_lines?.[0]?.cpt_code || '';
          bVal = b.service_lines?.[0]?.cpt_code || '';
          break;
        case 'pos':
          aVal = a.place_of_service || '';
          bVal = b.place_of_service || '';
          break;
        case 'secondary_balance':
          aVal = a.secondary_balance || 0;
          bVal = b.secondary_balance || 0;
          break;
        case 'contractual_adj': {
          // Charge − Allowed when primary has adjudicated (allowed > 0
          // AND allowed < charged). Otherwise 0 so claims with no
          // adjustment sort to the bottom by default.
          const adj = (c) => {
            const charged = Number(c.total_charged_amount || 0);
            const allowed = Number(c.primary_allowed_amount || 0);
            return (allowed > 0 && allowed < charged) ? (charged - allowed) : 0;
          };
          aVal = adj(a);
          bVal = adj(b);
          break;
        }
        default:
          return 0;
      }
      
      if (typeof aVal === 'string') {
        return newDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else {
        return newDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });
    
    setClientClaims(sorted);
  };

  // Load billing data when billing tab is opened
  // Uses a visibility-based approach to always refresh when tab is clicked
  const [billingRefreshKey, setBillingRefreshKey] = useState(0);
  useEffect(() => {
    if (clientDetailTab === 'billing' && selectedClient?.id) {
      // Clear stale billing data immediately to prevent PHI flash from prior client
      setClientBillingSummary(null);
      setClientClaims([]);
      setClientPaymentHistory([]);
      fetchClientBilling(selectedClient.id);
    }
  }, [clientDetailTab, selectedClient?.id, billingRefreshKey]);

  // Fetch full appointments when viewing client chart appointments tab
  useEffect(() => {
    if (clientDetailTab === 'appointments' && selectedClient?.id && showClientDetail) {
      // Ensure we have all appointments for this client, not just the 7-day window
      refreshAppointments(true);
    }
  }, [clientDetailTab, selectedClient?.id, showClientDetail]);

  // Ref to track if we're currently saving (to prevent concurrent saves)
  const isSavingNewNoteRef = useRef(false);
  // Ref to track the last saved form data hash (to avoid unnecessary saves)
  const lastSavedFormHashRef = useRef(null);
  // Ref to track the template ID being autosaved (to prevent race conditions)
  const autosaveTemplateIdRef = useRef(null);
  // Ref to track the client ID(s) being autosaved (to prevent race conditions)
  const autosaveClientIdsRef = useRef(null);
  
  // Save a text backup for a NEW note that hasn't been saved yet (no note ID)
  // Called on dialog close and on every save failure to prevent content loss
  // MUST be defined after all state variables it references are initialized
  const saveUnsavedDraftBackup = useCallback(() => {
    try {
      if (!newNoteFormData || Object.keys(newNoteFormData).length === 0) return;
      const clientId = selectedNoteClients?.[0] || null;
      const clientName = clientId
        ? (allClientsForLookup?.find(c => c.id === clientId)?.full_name || null)
        : null;
      const lines = [];
      if (clientName) lines.push(`Client: ${clientName}`);
      if (selectedTemplate?.name) lines.push(`Form: ${selectedTemplate.name}`);
      lines.push(`Date: ${new Date().toLocaleString()}`);
      lines.push(`Status: UNSAVED DRAFT${autosaveNoteId ? ` (draft ID: ${autosaveNoteId})` : ' - never saved'}`);
      lines.push('---');
      const fields = selectedTemplate?.form_fields || [];
      for (const field of fields) {
        const value = newNoteFormData[field.id];
        if (value === undefined || value === null || value === '') continue;
        if (Array.isArray(value) && value.length === 0) continue;
        const label = field.label || field.id;
        if (Array.isArray(value)) lines.push(`${label}: ${value.join(', ')}`);
        else if (typeof value === 'string' && value.trim()) lines.push(`${label}: ${value.trim()}`);
        else if (typeof value === 'boolean') lines.push(`${label}: ${value ? 'Yes' : 'No'}`);
      }
      if (telehealthConsent?.pos_code) {
        lines.push(`Consent POS: ${telehealthConsent.pos_code}${telehealthConsent.pos_description ? ' - ' + telehealthConsent.pos_description : ''}`);
        lines.push(`Consent Modality: ${telehealthConsent.modality || ''}`);
      }
      const textContent = lines.join('\n');
      if (!textContent.trim()) return;
      const token = sessionStorage.getItem('token');
      if (!token) return;
      if (autosaveNoteId) {
        axios.post(`${API}/notes/${autosaveNoteId}/text-backup`, {
          text_content: textContent, client_id: clientId, client_name: clientName, template_name: selectedTemplate?.name
        }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      } else {
        axios.post(`${API}/notes/unsaved-draft-backup`, {
          text_content: textContent, client_id: clientId, client_name: clientName, template_name: selectedTemplate?.name, draft_note_id: null
        }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      }
    } catch (e) { /* silent fail — best-effort backup */ }
  }, [newNoteFormData, selectedNoteClients, selectedTemplate, autosaveNoteId, telehealthConsent, allClientsForLookup]);

  // Helper function to create/update draft for Add Note dialog
  // CRITICAL: This function must NEVER silently fail. All failures must be visible to user.
  const saveNewNoteDraft = useCallback(async (isImmediate = false, retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // ms - 1 second between retries
    
    // Retry logic for concurrent save lock
    if (isSavingNewNoteRef.current) {
      if (retryCount < MAX_RETRIES) {
        void 0 && console.log(`[AUTOSAVE] Another save in progress, retry ${retryCount + 1}/${MAX_RETRIES}...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return saveNewNoteDraft(isImmediate, retryCount + 1);
      } else {
        // Max retries exceeded - LOUD failure with friendly message
        console.error('[AUTOSAVE] Max retries exceeded for concurrent save lock');
        setAutosaveBlockedReason('Another save is in progress - please wait');
        toast.error('Another save is still in progress. Please wait a moment and try again.');
        return;
      }
    }
    
    // Check prerequisites - these are expected states, not errors
    if (!showAddNote) {
      return; // Dialog not open, nothing to save
    }
    
    if (!selectedTemplate) {
      // Not an error - user hasn't selected template yet
      return;
    }
    
    if (!selectedNoteClients?.length) {
      // Not an error - user hasn't selected client yet
      return;
    }
    
    if (!selectedNoteProvider) {
      // Not an error - user hasn't selected provider yet
      return;
    }
    
    // LOUD FAILURE: Template mismatch - this indicates data corruption risk
    if (autosaveNoteId && autosaveTemplateIdRef.current && autosaveTemplateIdRef.current !== selectedTemplate.id) {
      const errorMsg = `The note template was changed after the draft was created. Please close this dialog and start a new note to avoid mixing content from different templates.`;
      console.error('[AUTOSAVE] CRITICAL:', errorMsg);
      setAutosaveBlockedReason('Template Changed - Please Close & Restart');
      setNoteCreationError(errorMsg);
      return;
    }
    
    // CLIENT MISMATCH HANDLING: If the draft was created for a different client,
    // we must NOT update it (would put content in wrong chart). Instead:
    // 1. Abandon the stale draft
    // 2. Create a new draft for the current client
    // 3. Warn the user
    if (autosaveNoteId && autosaveClientIdsRef.current) {
      const currentClientIds = JSON.stringify(selectedNoteClients.sort());
      const savedClientIds = JSON.stringify(autosaveClientIdsRef.current.sort());
      if (currentClientIds !== savedClientIds) {
        // Log the mismatch for debugging/tracing
        const currentClient = allClientsForLookup.find(c => c.id === selectedNoteClients[0]);
        const savedClientId = autosaveClientIdsRef.current[0];
        const savedClient = allClientsForLookup.find(c => c.id === savedClientId);
        
        console.warn('[AUTOSAVE] CRITICAL: Client mismatch detected! Abandoning stale draft and creating new one.', {
          staleNoteId: autosaveNoteId,
          staleClientId: savedClientId,
          staleClientName: savedClient?.full_name || 'Unknown',
          currentClientId: selectedNoteClients[0],
          currentClientName: currentClient?.full_name || 'Unknown',
        });
        
        // CRITICAL: Clear the stale draft reference - do NOT update it
        // This prevents content from going to the wrong client's chart
        setAutosaveNoteId(null);
        autosaveNoteIdRef.current = null;
        autosaveClientIdsRef.current = [...selectedNoteClients]; // Reset to current client
        lastSavedFormHashRef.current = null; // Force new save
        
        // Warn the user (only once per incident to avoid toast spam)
        if (!mismatchNotifiedRef.current) {
          toast.warning(
            `Note was started for ${savedClient?.full_name || 'another client'}. Creating new draft for ${currentClient?.full_name || 'current client'}.`,
            { duration: 4000 }
          );
          mismatchNotifiedRef.current = true;
        }
        
        // Continue to create a NEW draft below (autosaveNoteId is now null)
      }
    }
    
    // Clear any previous blocked reason since we passed the checks
    if (autosaveBlockedReason) {
      setAutosaveBlockedReason(null);
    }
    
    // Check if there's any form data at all - silent skip is OK here (nothing to save)
    const hasFormData = newNoteFormData && Object.keys(newNoteFormData).length > 0;
    if (!hasFormData) {
      return;
    }
    
    // Check if form data actually changed - silent skip is OK here (optimization)
    const currentHash = JSON.stringify(newNoteFormData);
    if (currentHash === lastSavedFormHashRef.current && autosaveNoteId) {
      return;
    }
    
    try {
      isSavingNewNoteRef.current = true;
      setIsAutosaving(true);
      
      // Track which template and clients we're autosaving for
      autosaveTemplateIdRef.current = selectedTemplate.id;
      autosaveClientIdsRef.current = [...selectedNoteClients];
      
      // Helper function to get client data field value for snapshot
      const getClientDataValueForAutosave = (field) => {
        const client = allClientsForLookup.find(c => c.id === selectedNoteClients[0]);
        if (!client) return '';
        
        switch (field.type) {
          case 'client_name':
            return `${client.first_name || ''} ${client.last_name || ''}`.trim();
          case 'client_dob':
            return client.date_of_birth || '';
          case 'client_age':
            if (!client.date_of_birth) return '';
            const dob = new Date(client.date_of_birth);
            const today = new Date();
            let age = today.getFullYear() - dob.getFullYear();
            const monthDiff = today.getMonth() - dob.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
            return age.toString();
          case 'client_gender':
            return client.gender || '';
          case 'client_mrn':
            return client.mrn || '';
          case 'client_phone':
            return client.phone || '';
          case 'client_email':
            return client.email || '';
          case 'client_address':
            return [client.address || client.street || client.address_line1, client.address_line2, client.city, client.state, client.zipcode || client.zip || client.zip_code]
              .filter(Boolean).join(', ');
          default:
            return '';
        }
      };
      
      // Build note content from form data
      const noteContent = {
        fields: selectedTemplate.form_fields?.map(field => ({
          id: field.id,
          label: field.label,
          type: field.type,
          value: ['client_name', 'client_dob', 'client_age', 'client_gender', 'client_mrn', 'client_phone', 'client_email', 'client_address'].includes(field.type)
            ? getClientDataValueForAutosave(field)
            : (newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : (field.value !== undefined ? field.value : ''))
        })) || []
      };
      
      if (autosaveNoteId) {
        // Update existing draft - use PUT (not PATCH) as backend only has PUT endpoint
        try {
          // Get client name for tracing
          const primaryClient = allClientsForLookup.find(c => c.id === selectedNoteClients[0]);
          const clientNameDisplay = primaryClient?.full_name || 'Unknown';
          const clientMrnDisplay = primaryClient?.mrn || 'No MRN';
          
          const updatedNoteResponse = await axios.put(`${API}/notes/${autosaveNoteId}`, {
            content: JSON.stringify(noteContent),
            updated_at: new Date().toISOString(),
            // TRACE: Include client info for server-side logging
            client_name_display: clientNameDisplay,
            client_mrn_display: clientMrnDisplay
          });
          void 0 && console.log('[AUTOSAVE] Draft updated at', new Date().toLocaleTimeString(), 'for client:', clientNameDisplay);
          
          // CRITICAL: Update the note in clientNotes with fresh content
          // This ensures when user re-opens the note, they see the latest saved content
          if (updatedNoteResponse.data) {
            setClientNotes(prev => prev.map(n => 
              n.id === autosaveNoteId ? { ...n, content: JSON.stringify(noteContent), updated_at: new Date().toISOString() } : n
            ));
          }
        } catch (putError) {
          // CRITICAL FALLBACK: If PUT fails with 404, draft was deleted externally
          // Clear stale ID and recreate on next autosave cycle
          if (putError.response?.status === 404) {
            console.warn('[AUTOSAVE] Draft not found (404), clearing stale ID:', autosaveNoteId);
            setAutosaveNoteId(null);
            autosaveNoteIdRef.current = null;
            // Clear URL param too
            const url = new URL(window.location.href);
            if (url.searchParams.has('draftNote')) {
              url.searchParams.delete('draftNote');
              window.history.replaceState({}, '', url.toString());
            }
            // Allow next autosave cycle to create a new draft
            lastSavedFormHashRef.current = null;
          } else {
            throw putError;
          }
        }
      } else {
        // Create new draft - LAZY AUTO-CREATION on first save
        const response = await axios.post(`${API}/notes`, {
          client_id: isGroupNote ? null : selectedNoteClients[0],
          client_ids: isGroupNote ? selectedNoteClients : undefined,
          provider_id: selectedNoteProvider,
          template_id: selectedTemplate.id,
          template_name: selectedTemplate.name,
          content: JSON.stringify(noteContent),
          status: 'draft',
          date_of_service: getTodayLocalDate(),
          is_group_note: isGroupNote || false
        });
        const newDraft = response.data;
        setAutosaveNoteId(newDraft.id);
        autosaveNoteIdRef.current = newDraft.id; // SYNC ref to prevent race condition
        void 0 && console.log('[AUTOSAVE] Draft CREATED at', new Date().toLocaleTimeString(), '- ID:', newDraft.id);
        
        // Update URL with draft ID so refresh preserves the state
        const url = new URL(window.location.href);
        url.searchParams.set('draftNote', newDraft.id);
        window.history.replaceState({}, '', url.toString());
        void 0 && console.log('[AUTOSAVE] URL updated with draft ID');
        
        // OPTIMISTIC UPDATE: Add the new draft to clientNotes immediately
        // so it appears in the Documents list without refresh
        if (newDraft.client_id || (newDraft.client_ids && newDraft.client_ids.length > 0)) {
          setClientNotes(prev => {
            // Avoid duplicates
            if (prev.some(n => n.id === newDraft.id)) return prev;
            return [newDraft, ...prev];
          });
        }
        
        // Show toast on first creation
        toast.success('Draft saved', { duration: 1500 });
      }
      
      lastSavedFormHashRef.current = currentHash;
      setLastAutosave(new Date());
      
      // CLEAR any previous blocked reason on successful save
      // This ensures a previous glitch message doesn't linger after user fixes the issue
      if (autosaveBlockedReason) {
        setAutosaveBlockedReason(null);
      }
      if (noteCreationError) {
        setNoteCreationError(null);
      }
    } catch (error) {
      console.error('[AUTOSAVE] Failed:', error);
      // LOUD FAILURE: Show error to user instead of silently failing
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      setAutosaveBlockedReason(`Save Failed - Please Try Again`);
      toast.error(`Your note could not be saved: ${errorMsg}. Please try again or copy your content.`, { duration: 5000 });
    } finally {
      isSavingNewNoteRef.current = false;
      setIsAutosaving(false);
    }
  }, [showAddNote, selectedTemplate, selectedNoteClients, selectedNoteProvider, newNoteFormData, autosaveNoteId, isGroupNote, allClientsForLookup, autosaveBlockedReason]);
  
  // Close note dialog handlers (must be after saveNewNoteDraft)
  // Auto-backup whenever a save error is set — covers ALL 8+ error call sites at once
  useEffect(() => {
    if (noteCreationError && showAddNote) {
      saveUnsavedDraftBackup();
    }
  }, [noteCreationError]);

  const handleCloseNoteDiscard = useCallback(() => {
    setShowCloseNoteWarning(false);
    
    // Save backup BEFORE clearing state — user is discarding, preserve what they typed
    saveUnsavedDraftBackup();
    
    // CRITICAL: Clear state IMMEDIATELY to prevent race conditions
    // Previously this was in setTimeout(0) which caused cross-contamination
    setNoteDialogHeaderCollapsed(false);
    setNoteClientLocked(false);
    setCurriculumSessionData(null);
    setSelectedNoteGroupId(null);
    isNoteFormDirtyRef.current = false;
    initialFormDataRef.current = null;
    setNewNoteFormData({});
    setSelectedTemplate(null);
    setSelectedNoteClients([]);
    setSelectedNoteProvider(null);
    setAutosaveNoteId(null);
    autosaveNoteIdRef.current = null; // CRITICAL: Clear ref to prevent stale references
    setLastAutosave(null);
    lastSavedFormHashRef.current = null;
    autosaveTemplateIdRef.current = null;
    autosaveClientIdsRef.current = null;
    setAutosaveBlockedReason(null); // Clear any blocked reason
    setNoteCreationError(null); // Clear any creation error
    
    // Clear URL param
    const url = new URL(window.location.href);
    if (url.searchParams.has('draftNote')) {
      url.searchParams.delete('draftNote');
      window.history.replaceState({}, '', url.toString());
    }
    
    // Close dialog LAST after state is cleared
    setShowAddNote(false);
  }, []);
  
  const handleCloseNoteSaveAndClose = useCallback(async () => {
    try {
      await saveNewNoteDraft(false);
      toast.success('Draft saved');
    } catch (e) {
      console.warn('Failed to save:', e);
    }
    setShowCloseNoteWarning(false);
    
    // CRITICAL: Clear state IMMEDIATELY to prevent race conditions
    // Previously this was in setTimeout(0) which caused cross-contamination
    setNoteDialogHeaderCollapsed(false);
    setNoteClientLocked(false);
    setCurriculumSessionData(null);
    setSelectedNoteGroupId(null);
    isNoteFormDirtyRef.current = false;
    initialFormDataRef.current = null;
    setNewNoteFormData({});
    setSelectedTemplate(null);
    setSelectedNoteClients([]);
    setSelectedNoteProvider(null);
    setAutosaveNoteId(null);
    autosaveNoteIdRef.current = null; // CRITICAL: Clear ref to prevent stale references
    setLastAutosave(null);
    lastSavedFormHashRef.current = null;
    autosaveTemplateIdRef.current = null;
    autosaveClientIdsRef.current = null;
    setAutosaveBlockedReason(null); // Clear any blocked reason
    setNoteCreationError(null); // Clear any creation error
    
    // Clear URL param
    const url = new URL(window.location.href);
    if (url.searchParams.has('draftNote')) {
      url.searchParams.delete('draftNote');
      window.history.replaceState({}, '', url.toString());
    }
    
    // Close dialog LAST after state is cleared
    setShowAddNote(false);
  }, [saveNewNoteDraft]);
  
  // LAZY AUTO-CREATION: Debounced effect that triggers on form data changes
  // Creates draft on first keystroke (500ms debounce) and updates on subsequent changes (2 second debounce)
  useEffect(() => {
    // Debug logging
    void 0 && console.log('[LazyAutoCreate] Effect triggered:', {
      showAddNote,
      hasTemplate: !!selectedTemplate,
      templateName: selectedTemplate?.name,
      clientsCount: selectedNoteClients?.length,
      hasProvider: !!selectedNoteProvider,
      formDataKeys: Object.keys(newNoteFormData || {}),
      autosaveNoteId
    });
    
    if (!showAddNote) {
      void 0 && console.log('[LazyAutoCreate] Skipping - dialog not open');
      return;
    }
    if (!selectedTemplate) {
      void 0 && console.log('[LazyAutoCreate] Skipping - no template selected');
      return;
    }
    if (!selectedNoteClients?.length) {
      void 0 && console.log('[LazyAutoCreate] Skipping - no clients selected');
      return;
    }
    if (!selectedNoteProvider) {
      void 0 && console.log('[LazyAutoCreate] Skipping - no provider selected');
      return;
    }
    
    // Check if there's any form data at all (even just one field)
    const hasFormData = newNoteFormData && Object.keys(newNoteFormData).length > 0;
    if (!hasFormData) {
      void 0 && console.log('[LazyAutoCreate] Skipping - no form data');
      return;
    }
    
    // Use shorter debounce for first save (creating draft), longer for updates
    const debounceTime = autosaveNoteId ? 2000 : 500;
    void 0 && console.log('[LazyAutoCreate] Scheduling save in', debounceTime, 'ms');
    
    const debounceTimer = setTimeout(() => {
      void 0 && console.log('[LazyAutoCreate] Executing save now');
      saveNewNoteDraft(!autosaveNoteId); // isImmediate = true for first save
    }, debounceTime);
    
    return () => clearTimeout(debounceTimer);
  }, [newNoteFormData, showAddNote, selectedTemplate, selectedNoteClients, selectedNoteProvider, autosaveNoteId, saveNewNoteDraft]);

  // Backup autosave interval (every 30 seconds) - catches any missed saves
  useEffect(() => {
    // Only autosave if:
    // 1. Create Note dialog is open
    // 2. A template is selected
    // 3. There's form data to save
    // 4. A client is selected
    // 5. A provider is selected
    if (!showAddNote || !selectedTemplate || !selectedNoteClients?.length || !selectedNoteProvider) {
      return;
    }
    
    const hasFormData = newNoteFormData && Object.keys(newNoteFormData).length > 0;
    if (!hasFormData) {
      return;
    }
    
    const autosaveInterval = setInterval(() => {
      saveNewNoteDraft(false);
    }, 30000); // Backup autosave every 30 seconds
    
    return () => clearInterval(autosaveInterval);
  }, [showAddNote, selectedTemplate, selectedNoteClients, selectedNoteProvider, newNoteFormData, saveNewNoteDraft]);

  // CRITICAL: Validate client match when Add Note dialog opens
  // Catches mismatches IMMEDIATELY instead of waiting for autosave
  const clientValidationDoneRef = useRef(false);
  const mismatchNotifiedRef = useRef(false); // Prevent repeated mismatch toasts
  
  // Reset validation flags when template changes (new note type = fresh validation)
  useEffect(() => {
    if (selectedTemplate) {
      clientValidationDoneRef.current = false;
      mismatchNotifiedRef.current = false;
    }
  }, [selectedTemplate?.id]);
  
  useEffect(() => {
    if (!showAddNote) {
      clientValidationDoneRef.current = false; // Reset when dialog closes
      mismatchNotifiedRef.current = false; // Reset mismatch notification flag
      return;
    }
    
    // Only run validation once per dialog open to prevent cascade
    if (clientValidationDoneRef.current) return;
    
    // Skip validation for group notes (multiple clients selected)
    if (selectedNoteClients?.length > 1) {
      void 0 && console.log('[NOTE_DIALOG] Skipping URL validation for group note');
      clientValidationDoneRef.current = true;
      return;
    }
    
    // Get client ID from URL (the chart we're viewing)
    const url = new URL(window.location.href);
    const pathMatch = url.pathname.match(/\/client\/([^\/\?]+)/);
    const urlClientId = pathMatch ? pathMatch[1] : null;
    
    // If we're in a client chart and have a different client selected for the note
    if (urlClientId && selectedNoteClients?.length > 0) {
      const selectedClientId = selectedNoteClients[0];
      
      if (urlClientId !== selectedClientId) {
        const urlClient = allClientsForLookup.find(c => c.id === urlClientId);
        const selectedClientObj = allClientsForLookup.find(c => c.id === selectedClientId);
        
        console.error('[NOTE_DIALOG] CRITICAL: Client mismatch on dialog open!', {
          urlClientId,
          urlClientName: urlClient?.full_name || 'Unknown',
          selectedClientId,
          selectedClientName: selectedClientObj?.full_name || 'Unknown'
        });
        
        // Auto-correct to the URL's client (the chart we're viewing)
        setSelectedNoteClients([urlClientId]);
        
        toast.warning(
          `Corrected note client to ${urlClient?.full_name || 'current chart'}. Was incorrectly set to ${selectedClientObj?.full_name || 'different client'}.`,
          { duration: 5000 }
        );
      }
    }
    
    clientValidationDoneRef.current = true;
  }, [showAddNote, selectedNoteClients, allClientsForLookup]);

  // Reset autosave state when dialog closes
  useEffect(() => {
    if (!showAddNote) {
      setAutosaveNoteId(null);
      autosaveNoteIdRef.current = null; // CRITICAL: Clear ref too to prevent stale references
      setLastAutosave(null);
      lastSavedFormHashRef.current = null; // Clear the hash to prevent stale saves
      autosaveTemplateIdRef.current = null; // Clear template tracking
      autosaveClientIdsRef.current = null; // Clear client tracking
    }
  }, [showAddNote]);

  // Restore draft from URL on page load (handles browser refresh)
  // Use a ref to ensure we only attempt restoration once per session
  const draftRestoreAttemptedRef = useRef(false);
  
  useEffect(() => {
    const restoreDraftFromUrl = async () => {
      // Only attempt once per session to prevent multiple restorations
      if (draftRestoreAttemptedRef.current) {
        void 0 && console.log('[DraftRestore] Already attempted restoration this session, skipping');
        return;
      }
      
      const url = new URL(window.location.href);
      const draftNoteId = url.searchParams.get('draftNote');
      
      if (!draftNoteId) return;
      
      // Mark as attempted
      draftRestoreAttemptedRef.current = true;
      
      void 0 && console.log('[DraftRestore] Found draft ID in URL:', draftNoteId);
      
      try {
        // Fetch the draft note
        const response = await axios.get(`${API}/notes/${draftNoteId}`);
        const draftNote = response.data;
        
        if (!draftNote || draftNote.status !== 'draft') {
          void 0 && console.log('[DraftRestore] Note not found or not a draft, clearing URL');
          url.searchParams.delete('draftNote');
          window.history.replaceState({}, '', url.toString());
          return;
        }
        
        void 0 && console.log('[DraftRestore] Restoring draft:', draftNote.template_name);
        
        // Set up the Add Note dialog state
        setAutosaveNoteId(draftNote.id);
        autosaveNoteIdRef.current = draftNote.id; // SYNC ref for race condition prevention
        
        // Set client(s)
        if (draftNote.is_group_note && draftNote.client_ids?.length) {
          setSelectedNoteClients(draftNote.client_ids);
          setIsGroupNote(true);
        } else if (draftNote.client_id) {
          setSelectedNoteClients([draftNote.client_id]);
          setIsGroupNote(false);
        }
        
        // Set provider
        if (draftNote.provider_id) {
          setSelectedNoteProvider(draftNote.provider_id);
        }
        
        // Find and set template
        if (draftNote.template_id && formTemplates?.length) {
          const template = formTemplates.find(t => t.id === draftNote.template_id);
          if (template) {
            setSelectedTemplate(template);
          }
        }
        
        // Parse and restore form data from content
        if (draftNote.content) {
          try {
            const content = typeof draftNote.content === 'string' 
              ? JSON.parse(draftNote.content) 
              : draftNote.content;
            
            if (content.fields) {
              const formData = {};
              content.fields.forEach(field => {
                if (field.id && field.value !== undefined) {
                  formData[field.id] = field.value;
                }
              });
              setNewNoteFormData(formData);
              
              // Mark as dirty since we have restored content
              setTimeout(() => {
                initialFormDataRef.current = JSON.stringify(formData);
                isNoteFormDirtyRef.current = false;
              }, 100);
            }
          } catch (e) {
            console.warn('[DraftRestore] Failed to parse content:', e);
          }
        }
        
        // Open the dialog
        setShowAddNote(true);
        toast.info('Restored your draft note', { duration: 3000 });
        
      } catch (error) {
        console.error('[DraftRestore] Failed to restore draft:', error);
        // Clear invalid draft ID from URL
        url.searchParams.delete('draftNote');
        window.history.replaceState({}, '', url.toString());
      }
    };
    
    // Only run once when formTemplates are loaded (needed to set the template)
    if (formTemplates?.length > 0) {
      restoreDraftFromUrl();
    }
  }, [formTemplates]); // Run when templates are loaded

  // Note Editing Autosave with Journaling System
  // Uses the new /api/notes/{id}/autosave endpoint for versioned history
  // NOW captures FULL form state: content, consent, and treatment plan goals
  // Build createNoteData for lazy auto-creation (new notes only)
  // This allows autosave to create the note on first keystroke
  const createNoteData = useMemo(() => {
    // Only provide createNoteData when we're in "new note" mode (no selectedNote.id yet)
    // AND the Add Note dialog is open with a template selected
    if (selectedNote?.id) return null;
    
    // Must have required fields to create a note
    if (!selectedNoteProvider || selectedNoteClients.length === 0) return null;
    
    // Get template_id from selectedTemplate (for Add Note dialog) OR selectedNote.template_id (for editing)
    // FIX: Use selectedTemplate?.id for new notes from Add Note dialog
    const templateId = selectedTemplate?.id || selectedNote?.template_id || null;
    const templateName = selectedTemplate?.name || (templateId ? formTemplates.find(t => t.id === templateId)?.name : null) || null;
    
    void 0 && console.log('[createNoteData] Building:', {
      hasSelectedNote: !!selectedNote?.id,
      selectedTemplateId: selectedTemplate?.id,
      selectedTemplateName: selectedTemplate?.name,
      selectedNoteTemplateId: selectedNote?.template_id,
      finalTemplateId: templateId,
      provider: selectedNoteProvider,
      clients: selectedNoteClients.length
    });
    
    return {
      client_id: isGroupNote ? null : selectedNoteClients[0],
      client_ids: isGroupNote ? selectedNoteClients : null,
      provider_id: selectedNoteProvider,
      template_id: templateId,
      template_name: templateName,
      is_group_note: isGroupNote,
    };
  }, [selectedNote?.id, selectedNote?.template_id, selectedTemplate?.id, selectedTemplate?.name, selectedNoteProvider, selectedNoteClients, isGroupNote, formTemplates]);

  // Callback when lazy creation succeeds - update selectedNote with the new ID
  const handleLazyNoteCreated = useCallback((newNote) => {
    void 0 && console.log('[Lazy Create] Note created:', newNote.id);
    // CRITICAL (Jun 2026): set the SYNC ref first — handleCreateClinicalNote
    // reads autosaveNoteIdRef.current to decide create-vs-update.  Setting
    // only React state left a window where the Create button POSTed a
    // duplicate draft because the state hadn't re-rendered yet.
    autosaveNoteIdRef.current = newNote.id;
    setSelectedNote(newNote);
    setAutosaveNoteId(newNote.id);
    
    // Update clientNotes if viewing client chart
    if (newNote.client_id) {
      setClientNotes(prev => [newNote, ...prev]);
    }
    
    // Show subtle success indicator
    toast.success('Note auto-saved', { duration: 2000 });
  }, []);

  // Callback to update selectedNote's ID after lazy creation
  const setSelectedNoteId = useCallback((newId) => {
    setSelectedNote(prev => prev ? { ...prev, id: newId } : { id: newId });
  }, []);

  // Debug: Log when editedNoteContent changes
  useEffect(() => {
    if (editedNoteContent) {
      const sampleField = editedNoteContent.fields?.[0];
      void 0 && console.log('[App] editedNoteContent changed:', {
        fieldsCount: editedNoteContent.fields?.length || 0,
        sampleFieldId: sampleField?.id,
        sampleFieldValue: sampleField?.value?.substring?.(0, 30) || sampleField?.value
      });
    }
  }, [editedNoteContent]);

  // DEBUG: Log autosave config on each render
  const autosaveEnabled = isEditingNote && 
             editedNoteContent !== null && 
             (editedNoteContent.fields?.length > 0 || editedNoteContent.sections?.length > 0) &&
             (!!selectedNote?.id || !!createNoteData);
  
  // URL-BASED NOTE ID: Use noteId from URL as the authoritative source
  // Priority: fullscreenNoteId (path param) > URL query param > selectedNote.id
  // This ensures localStorage keys are 100% specific to the note in the URL
  const urlQueryNoteId = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('noteId');
    } catch {
      return null;
    }
  }, [window.location.search]);
  
  const urlNoteId = fullscreenNoteId || urlQueryNoteId || selectedNote?.id;
  
  const noteEditingAutosave = useNoteAutosaveUI({
    noteId: urlNoteId,
    setNoteId: setSelectedNoteId,           // NEW: For lazy creation
    formData: editedNoteContent,
    consentData: editedNoteConsent,         // Include telehealth consent
    linkedGoals: linkedGoals,               // Include treatment plan goals
    // NEW: Include metadata (date_of_service, times, billing info)
    metadata: selectedNote ? {
      date_of_service: selectedNote.date_of_service,
      start_time: selectedNote.start_time,
      end_time: selectedNote.end_time,
      units: selectedNote.units,
      cpt_code: selectedNote.cpt_code,
      pos_code: selectedNote.pos_code,
      modality: selectedNote.modality,
      ready_for_billing: selectedNote.ready_for_billing,
    } : null,
    // UPDATED: Enable autosave only when we have actual content to save
    // This prevents saving {} when note is still initializing
    enabled: autosaveEnabled,
    // OPTIMISTIC LOCKING: Pass the note's current version to prevent lost updates
    initialVersion: selectedNote?.current_version || null,
    // TIMESTAMP COMPARISON: Pass server's updated_at for draft comparison
    serverUpdatedAt: selectedNote?.updated_at || null,
    // NEW: Data for lazy auto-creation
    createNoteData: createNoteData,
    onNoteCreated: handleLazyNoteCreated,
    // NEW: LOUD FAILURE HANDLERS - Show errors in UI
    onSaveError: setAutosaveBlockedReason,
    onSaveCriticalError: setNoteCreationError,
    // NEW: Client/Provider names for recovery banner display
    // Shows "Found unsaved changes for [Client Name]" in the recovery prompt
    clientName: selectedNote?.client_name || null,
    providerName: users?.find(u => u.id === selectedNote?.provider_id)?.full_name || null,
    // NEW: Client ID for cross-contamination prevention (verified server-side)
    clientId: selectedNote?.client_id || selectedNoteClients?.[0] || null,
    // Jun 2026: user explicitly saved this note empty → prompt instead of auto-restore
    noteEmptyManuallySaved: selectedNote?.empty_content_manually_saved || false,
    onFormDataRestore: (restoredState) => {
      void 0 && console.log('[Draft Recovery] Restoring form state:', Object.keys(restoredState));
      // Restore FULL form state when draft recovery is triggered
      if (restoredState.formData) {
        setEditedNoteContent(restoredState.formData);
      }
      if (restoredState.consentData !== undefined) {
        setEditedNoteConsent(restoredState.consentData);
      }
      if (restoredState.linkedGoals) {
        setLinkedGoals(restoredState.linkedGoals);
      }
      // Show toast notification for draft restoration (version restores
      // already toast "Note restored to version X" from the history panel)
      if (restoredState.source !== 'version_restore') {
        toast.success('Draft restored! Your unsaved changes have been recovered.', {
          duration: 4000,
          icon: '📄',
        });
      }
    },
    onVersionConflict: async (conflict) => {
      // SIMPLIFIED CONFLICT HANDLING - COMPLETELY SILENT
      // Just update the version number so the next save works
      // No toasts, no merging, no fetching server data
      // The user's edits take priority - they can always refresh if they want server version
      
      console.warn('[Version Conflict] Silent handling - just updating version:', conflict);
      
      try {
        // Update the version in React state
        if (conflict.noteId === selectedNote?.id && conflict.serverVersion) {
          setSelectedNote(prev => prev ? { 
            ...prev, 
            current_version: conflict.serverVersion 
          } : prev);
          // Also update the hook's internal version ref
          noteEditingAutosave?.setVersion?.(conflict.serverVersion);
        }
        
        // Clear the conflict state so future saves work
        noteEditingAutosave?.clearConflict?.();
        
      } catch (error) {
        console.error('[Conflict] Failed to handle conflict:', error);
      }
    },
  });

  // BACKUP AUTOSAVE - DISABLED
  // This was causing save loops by calling forceSave every 5 seconds
  // The useNoteAutosave hook handles autosaving - this backup is not needed
  /*
  useEffect(() => {
    if (!isEditingNote || !selectedNote?.id || !editedNoteContent) {
      return;
    }
    
    const hasFields = (editedNoteContent?.fields?.length > 0) || 
                      (editedNoteContent?.sections?.some(s => s.fields?.length > 0));
    if (!hasFields) {
      return;
    }
    
    void 0 && console.log('⏰ [Backup Autosave] Setting up 5-second interval for note:', selectedNote?.id);
    
    const backupInterval = setInterval(() => {
      void 0 && console.log('⏰ [Backup Autosave] Interval triggered - calling forceSave');
      noteEditingAutosave?.forceSave?.().then(result => {
        void 0 && console.log('⏰ [Backup Autosave] forceSave result:', result);
      }).catch(err => {
        console.error('⏰ [Backup Autosave] forceSave error:', err);
      });
    }, 5000);
    
    return () => {
      void 0 && console.log('⏰ [Backup Autosave] Clearing interval');
      clearInterval(backupInterval);
    };
  }, [isEditingNote, selectedNote?.id, editedNoteContent?.fields?.length, editedNoteContent?.sections?.length]);
  */

  // Function to refresh client notes (called after note creation/edit/delete)
  // IMPORTANT: This is defined early because closeNoteWithCheck and other callbacks depend on it
  const refreshClientNotes = useCallback(async () => {
    if (selectedClient?.id) {
      try {
        const response = await axios.get(`${API}/notes?client_id=${selectedClient.id}&start_date=2020-01-01&end_date=2030-12-31&summary=true`);
        setClientNotes(response.data || []);
      } catch (error) {
        console.error('Error refreshing client notes:', error);
      }
    }
  }, [selectedClient?.id]);

  // CRITICAL: Prevent data loss when provider closes tab or navigates away
  // Uses ref pattern to avoid listener cycling on every keystroke
  
  // Helper function to close note with unsaved changes check
  // ── Phase 3B-2 step 1 (Jun 2026): note close/discard/save-and-close
  // lifecycle handlers extracted VERBATIM to hooks/useNoteLifecycleController
  // so the upcoming V2-native note host reuses the exact same behavior.
  const {
    closeNoteWithCheck,
    handleCloseEditingNoteKeepEditing,
    handleCloseEditingNoteDiscard,
    handleCloseEditingNoteSaveAndClose,
  } = useNoteLifecycleController({
    isEditingNote, selectedNote, editedNoteContent, noteJustSigned, showNoteDetail,
    showClientDetail, selectedClient, clientDetailTab,
    noteEditingAutosave, navigate, refreshClientNotes,
    setIsEditingNote, setEditedNoteContent, setEditedNoteConsent, setLinkedGoals,
    setSelectedNote, setShowNoteDetail, setShowCloseEditingNoteWarning,
    setShowNextStepsDialog, setNoteJustSigned,
  });

  // CROSS-CONTAMINATION PREVENTION: When the provider navigates to a different client
  // while a note is open in edit mode, show a dialog asking what to do.
  // Never auto-close — let the provider decide.
  const prevClientIdRef = useRef(selectedClient?.id);
  // Ref for syncOpenChart in refreshClients (avoids adding selectedClient to dependency array)
  const selectedClientIdRef = useRef(selectedClient?.id);
  useEffect(() => { selectedClientIdRef.current = selectedClient?.id; }, [selectedClient?.id]);
  const [clientMismatchInfo, setClientMismatchInfo] = useState(null); // {prevId, prevName, newId, newName}
  useEffect(() => {
    const prevId = prevClientIdRef.current;
    const newId = selectedClient?.id;
    prevClientIdRef.current = newId;

    // Only act when client actually changes (not on initial mount)
    if (!prevId || !newId || prevId === newId) return;

    // If a note is open in edit mode, show the mismatch dialog
    if (isEditingNote && selectedNote?.id) {
      void 0 && console.log(`[ClientChange Safety] Client changed from ${prevId} to ${newId} while editing note ${selectedNote.id}`);
      setClientMismatchInfo({
        prevId,
        prevName: selectedNote.client_name || 'the previous client',
        newId,
        newName: selectedClient?.full_name || selectedClient?.first_name || 'the new client',
        noteId: selectedNote.id,
        templateName: selectedNote.template_name || selectedNote.form_name || 'Progress Note',
      });
    }
  }, [selectedClient?.id]);

  // Handler: Keep editing — navigate back to the original client
  const handleMismatchKeepEditing = useCallback(() => {
    if (clientMismatchInfo?.prevId) {
      navigate(`/client/${clientMismatchInfo.prevId}?tab=notes`, { replace: true });
      // Restore the previous client in state so the chart reloads
      // The note editor stays open — no data lost
    }
    setClientMismatchInfo(null);
  }, [clientMismatchInfo, navigate]);

  // Handler: Save and close — flush save to original client, then close editor
  const handleMismatchSaveAndClose = useCallback(async () => {
    // Flush any pending autosave (saves to the original client's note)
    if (noteEditingAutosave?.flushSave) {
      try {
        await noteEditingAutosave.flushSave();
      } catch (e) {
        console.warn('[ClientChange Safety] Flush save failed:', e);
      }
    }

    // Create text backup for extra safety
    if (selectedNote?.id && editedNoteContent) {
      try {
        const textContent = extractNoteTextContent(editedNoteContent, selectedNote);
        if (textContent && textContent.trim().length > 0) {
          axios.post(`${API}/notes/${selectedNote.id}/text-backup`, {
            text_content: textContent,
            client_id: selectedNote.client_id,
            client_name: selectedNote.client_name,
            template_name: selectedNote.template_name || selectedNote.form_name
          }, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
          }).catch(() => {});
        }
      } catch (e) {
        // Silent
      }
    }

    // Close the note editor
    setIsEditingNote(false);
    setTimeout(() => {
      setEditedNoteContent(null);
      setEditedNoteConsent(null);
      setLinkedGoals([]);
      setSelectedNote(null);
    }, 100);
    setShowNoteDetail(false);
    setClientMismatchInfo(null);

    toast.success('Note saved and closed.', { duration: 3000 });
  }, [noteEditingAutosave, selectedNote, editedNoteContent]);

  // Handler: Move note to new client
  const handleMismatchMoveNote = useCallback(async () => {
    if (!selectedNote?.id || !clientMismatchInfo?.newId) return;

    // Flush save first (save current content)
    if (noteEditingAutosave?.flushSave) {
      try {
        await noteEditingAutosave.flushSave();
      } catch (e) {
        console.warn('[ClientChange Safety] Flush save before move failed:', e);
      }
    }

    try {
      // Update the note's client_id to the new client
      await axios.put(`${API}/notes/${selectedNote.id}`, {
        client_id: clientMismatchInfo.newId,
        client_ids: [clientMismatchInfo.newId],
      }, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });

      // Update the selectedNote in state
      setSelectedNote(prev => prev ? {
        ...prev,
        client_id: clientMismatchInfo.newId,
        client_name: clientMismatchInfo.newName,
      } : prev);

      setClientMismatchInfo(null);
      toast.success(`Note moved to ${clientMismatchInfo.newName}. You can continue editing.`, { duration: 5000 });
    } catch (e) {
      console.error('[ClientChange Safety] Move note failed:', e);
      toast.error('Failed to move note. Your work is saved — please close the note manually.');
      setClientMismatchInfo(null);
    }
  }, [selectedNote, clientMismatchInfo, noteEditingAutosave]);

  // Helper function to navigate between notes in the note dialog
  // Extracted from render IIFE to prevent React Error #300 (state update during render)
  const handleNavigateToNote = useCallback(async (note) => {
    if (!note) return;
    
    const saveStatus = noteEditingAutosave?.saveStatus;
    
    try {
      // CRITICAL: Check for ERROR or OFFLINE_CACHED states - data NOT synced to server!
      if (saveStatus === 'error' || saveStatus === 'offline_cached') {
        const shouldNavigate = window.confirm(
          '⚠️ WARNING: Your changes are NOT saved on the server!\n\n' +
          'Your internet connection may be lost or there was a server error.\n\n' +
          'If you switch notes now, YOUR WORK WILL BE LOST.\n\n' +
          'Please copy your work before switching, or click Cancel and try the Retry button.'
        );
        if (!shouldNavigate) {
          return; // Don't navigate
        }
      } else if (isEditingNote) {
        // CRITICAL FIX: Use hasRealUnsavedChanges() for content comparison
        // This checks for meaningful differences using deep equality
        const hasRealChanges = noteEditingAutosave?.hasRealUnsavedChanges?.() ?? false;
        if (hasRealChanges) {
          // Check for unsaved changes before navigating (synchronous!)
          const shouldNavigate = window.confirm(
            'You have unsaved changes on this note. Are you sure you want to switch to a different note?\n\n' +
            'Your changes are being auto-saved, but the latest edits may not be saved yet.'
          );
          if (!shouldNavigate) {
            return; // Don't navigate
          }
          // Force a final save (fire and forget)
          noteEditingAutosave.forceSave?.().catch(e => console.warn('Final save failed:', e));
        }
      }
      
      // Use openNote to properly load and merge the note with template
      // This ensures proper rendering with template field definitions
      await openNote(note, true); // forceViewMode=true since we're navigating to view
      
    } catch (error) {
      console.error('[handleNavigateToNote] Error:', error);
      toast.error('Failed to load note');
    }
  }, [isEditingNote, noteEditingAutosave, openNote]);

  // 1. Create a ref to hold the latest values without re-triggering the effect
  const dirtyStateRef = useRef({ 
    isEditingNote, 
    editedNoteContent, 
    status: noteEditingAutosave.saveStatus,
    hasRealUnsavedChanges: noteEditingAutosave.hasRealUnsavedChanges
  });

  // 2. Keep the ref updated (cheap operation, doesn't cycle the listener)
  useEffect(() => {
    dirtyStateRef.current = { 
      isEditingNote, 
      editedNoteContent, 
      status: noteEditingAutosave.saveStatus,
      hasRealUnsavedChanges: noteEditingAutosave.hasRealUnsavedChanges
    };
  }, [isEditingNote, editedNoteContent, noteEditingAutosave.saveStatus, noteEditingAutosave.hasRealUnsavedChanges]);

  // 3. The Guard - Only runs ONCE on mount, listener stays attached for component lifetime
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const { isEditingNote, editedNoteContent, status, hasRealUnsavedChanges } = dirtyStateRef.current;
      
      const isSaving = status === 'saving';
      const hasOfflineData = status === 'offline_cached';
      const hasError = status === 'error';  // RED state - save failed
      const hasConflict = status === 'conflict';  // Version conflict
      
      // SYNC-AWARE GUARD: Use hasRealUnsavedChanges() for meaningful comparison
      // This uses cached lastSavedContentRef - no API calls, no browser hang
      // Only block close if there are REAL differences from last saved state
      let hasRealChanges = false;
      try {
        hasRealChanges = hasRealUnsavedChanges?.() ?? false;
      } catch (err) {
        // If function throws, fall back to simple check
        hasRealChanges = editedNoteContent !== null && status !== 'saved' && status !== 'idle';
      }
      
      // Warn user if: saving in progress, offline/error state, or REAL unsaved changes
      // The hasRealChanges check uses deep equality against lastSavedContentRef
      if (isEditingNote && (isSaving || hasOfflineData || hasError || hasConflict || hasRealChanges)) {
        e.preventDefault();
        e.returnValue = ''; // Modern browsers show their own generic message
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []); // Empty array = listener stays attached for the life of the component

  // Listen for catastrophic loss events from autosave
  useEffect(() => {
    const handleCatastrophicLoss = (event) => {
      const { noteId, lossPercent } = event.detail;
      toast.error(
        `Save blocked: this change removes ${lossPercent}% of the note's saved content. ` +
        `Your data is safe. If intentional, click "Save Blocked — Review" to save anyway.`,
        { duration: 15000 }
      );
      console.error('[Catastrophic Loss Protection] Blocked save for note', noteId, 'loss:', lossPercent, '%');
    };

    window.addEventListener('autosave-catastrophic-loss', handleCatastrophicLoss);
    return () => window.removeEventListener('autosave-catastrophic-loss', handleCatastrophicLoss);
  }, []);

  // Workshop data now loaded by ClaimsTab.jsx directly


  const hasClientAccess = (client) => {
    if (!user) return false;
    
    // Admins and super admins have access to all
    if (isSuperAdmin(user)) return true;
    
    // Provider has access to their own clients
    if (userRoleName === 'provider' && client.provider_id === user.id) return true;
    
    // Users with is_provider flag (like supervisors who are also providers)
    if (user?.is_provider && client.provider_id === user.id) return true;
    
    // Scheduler has access to clients of providers they schedule for
    if (user?.is_scheduler && client.provider_id) {
      if (safeIncludes(user.scheduling_for, client.provider_id)) return true;
    }
    
    // Biller has access to clients they bill for
    if (user?.is_biller) {
      if (safeIncludes(client.assigned_billers, user.id)) return true;
    }
    
    // Supervisor has access to clients of providers they supervise
    // Find supervisees - users who have this user as their supervisor
    if (userRoleName === 'supervisor' && client.provider_id) {
      const superviseeIds = safeFilter(users, u => safeIncludes(u.supervisor_ids, user.id))
        .map(u => u.id);
      if (superviseeIds.includes(client.provider_id)) return true;
    }
    
    return false;
  };

  const forwardStaffMessage = async () => {
    const threadToUse = threadToForward || selectedStaffThread;
    if (!forwardToUserId || !threadToUse) {
      toast.error('Please select a staff member');
      return;
    }
    
    setForwardingMessage(true);
    try {
      const params = new URLSearchParams({
        forward_to_user_id: forwardToUserId
      });
      if (forwardNote.trim()) {
        params.append('forward_note', forwardNote.trim());
      }
      
      const response = await axios.post(
        `${API}/staff/messages/${threadToUse.id}/forward?${params.toString()}`
      );
      toast.success(response.data.message || 'Message forwarded successfully');
      setShowForwardDialog(false);
      setForwardToUserId('');
      setForwardNote('');
      setThreadToForward(null);
      setOpenMessageMenu(null);
      fetchStaffMessageThreads(false); // Refresh message list
    } catch (error) {
      console.error('Error forwarding message:', error);
      toast.error('Failed to forward message');
    } finally {
      setForwardingMessage(false);
    }
  };

  const recallStaffMessage = async () => {
    if (!recallReason.trim()) {
      toast.error('Please provide a reason for recall');
      return;
    }
    
    setRecallingMessage(true);
    try {
      const params = new URLSearchParams({
        recall_reason: recallReason
      });
      
      const response = await axios.post(
        `${API}/staff/messages/${recallThreadId}/recall?${params.toString()}`
      );
      toast.success(response.data.message || 'Message recalled');
      setShowRecallDialog(false);
      setRecallReason('');
      setRecallThreadId(null);
      fetchStaffMessageThreads(false); // Refresh list
    } catch (error) {
      console.error('Error recalling message:', error);
      toast.error(error.response?.data?.detail || 'Failed to recall message');
    } finally {
      setRecallingMessage(false);
    }
  };

  const sendBroadcastMessage = async () => {
    if (!staffNewMessageSubject.trim() || !staffNewMessageText.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSendingStaffNewMessage(true);
    try {
      const params = new URLSearchParams({
        subject: staffNewMessageSubject,
        message: staffNewMessageText,
        topic: staffNewMessageTopic
      });
      
      const response = await axios.post(`${API}/staff/messages/broadcast?${params.toString()}`);
      toast.success(response.data.message || 'Broadcast sent successfully');
      setShowStaffNewMessageDialog(false);
      setShowBroadcastConfirmation(false);
      setStaffNewMessageClientId('');
      setStaffNewMessageSubject('');
      setStaffNewMessageText('');
      setStaffNewMessageTopic('general');
      fetchStaffMessageThreads(false);
    } catch (error) {
      console.error('Error sending broadcast:', error);
      toast.error('Failed to send broadcast message');
    } finally {
      setSendingStaffNewMessage(false);
    }
  };

  // Fetch notifications periodically (reduced frequency when WebSocket is connected)
  useEffect(() => {
    if (user) {
      fetchNotifications();
      // Poll less frequently when WebSocket is connected (WebSocket handles real-time)
      // Poll every 30s if WS disconnected, every 2 minutes if connected (as backup)
      const pollInterval = wsConnected ? 120000 : 30000;
      const interval = setInterval(() => {
        if (!document.hidden && !isEditingDialogOpenRef.current && !isUserBusy()) {
          fetchNotifications();
        }
      }, pollInterval);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications, wsConnected]);

  // Handle fullscreen client mode - auto-load client when fullscreenClientId is provided
  useEffect(() => {
    if (fullscreenClientId && user) {
      setIsFullscreenClientMode(true);
      // Auto-load the client
      handleClientClick(fullscreenClientId);
    }
  }, [fullscreenClientId, user]);

  // Handle URL-based dashboard view - set activeTab from URL query param
  useEffect(() => {
    if (user && !initialClientId) {
      const urlParams = new URLSearchParams(window.location.search);
      const viewFromUrl = urlParams.get('view');
      
      if (viewFromUrl && viewFromUrl !== activeTab) {
        setActiveTab(viewFromUrl);
      }
      
      // Feb 28, 2026 — V2 Tasks deep-link support.
      // The new /v2/tasks workspace bounces back to the legacy
      // dashboard with these params per task type so the original
      // dialogs fire without rewriting them.  Each handler clears
      // the param off the URL after use so refresh / back-button
      // doesn't re-trigger.
      const openClientFromUrl = urlParams.get('open_client');
      const openNoteFromUrl = urlParams.get('open_note');
      const taskActionFromUrl = urlParams.get('action');
      const taskTabFromUrl = urlParams.get('tab');

      if (openClientFromUrl && user) {
        const handleOpenClient = async () => {
          try {
            const response = await axios.get(`${API}/clients/${openClientFromUrl}`);
            if (response.data) {
              // Calendar / appointment scheduling deep-link.
              //
              // Feb 28, 2026 — V2 of this dispatcher.  We stash the
              // target client in `__v2_prefill_client__` and set a
              // `__v2_pending_open_appointment__` flag, then strip
              // the URL params and navigate to the calendar.  A
              // separate `activeTab`-watcher effect (defined below)
              // consumes the pending-open flag once
              // `activeTab === 'appointments'` is committed and the
              // calendar tree has had a paint to mount its dialog
              // host.  Firing `setShowAppointmentForm(true)` inline
              // from this async handler was racy — the dialog flashed
              // or never opened because the scheduler wasn't mounted
              // yet.
              if (viewFromUrl === 'appointments') {
                window.__v2_prefill_client__ = openClientFromUrl;
                setPendingOpenAppointment(true);
                // Strip the deep-link params so refresh doesn't loop.
                const cleanParams = new URLSearchParams(window.location.search);
                cleanParams.delete('open_client');
                cleanParams.delete('action');
                cleanParams.delete('tab');
                const cleanSearch = cleanParams.toString();
                navigate(`/dashboard${cleanSearch ? `?${cleanSearch}` : ''}`, { replace: true });
                return;
              }
              // Client chart deep-link with optional tab + action.
              setFullscreenClientId(openClientFromUrl);
              if (taskTabFromUrl) {
                setClientDetailTab(taskTabFromUrl);
              }
              if (taskActionFromUrl === 'discharge') {
                setTimeout(() => {
                  if (typeof setShowDischargeForm === 'function') {
                    setShowDischargeForm(true);
                  }
                }, 250);
              }
              // Strip the deep-link params so refresh doesn't loop.
              const cleanParams = new URLSearchParams(window.location.search);
              cleanParams.delete('open_client');
              cleanParams.delete('action');
              cleanParams.delete('tab');
              const cleanSearch = cleanParams.toString();
              navigate(`/dashboard${cleanSearch ? `?${cleanSearch}` : ''}`, { replace: true });
            }
          } catch (error) {
            console.error('[URL Navigation] Failed to load client:', error);
          }
        };
        handleOpenClient();
      }

      if (openNoteFromUrl && user) {
        const handleOpenNoteFromUrl = async () => {
          try {
            const r = await axios.get(`${API}/notes/${openNoteFromUrl}`);
            if (r.data && typeof openNote === 'function') {
              openNote(r.data);
            }
            const cleanParams = new URLSearchParams(window.location.search);
            cleanParams.delete('open_note');
            const cleanSearch = cleanParams.toString();
            navigate(`/dashboard${cleanSearch ? `?${cleanSearch}` : ''}`, { replace: true });
          } catch (error) {
            console.error('[URL Navigation] Failed to load note:', error);
          }
        };
        handleOpenNoteFromUrl();
      }

      // Jul 2026 — V2 Documents tab "Add Note" deep-link.  Lands with
      // the Add Note dialog ALREADY OPEN and locked to the client, so
      // V2 users don't have to hunt for the button after the bounce.
      // Mirrors ClientChartDocumentsTab.handleAddNoteClick exactly.
      const addNoteClientFromUrl = urlParams.get('add_note_client');
      if (addNoteClientFromUrl && user) {
        (async () => {
          try {
            const r = await axios.get(`${API}/clients/${addNoteClientFromUrl}`);
            if (r.data) {
              setSelectedNoteStatus('draft');
              const roleName = (user?.role || '').toLowerCase();
              if ((roleName === 'provider' || roleName === 'intern' || roleName === 'supervisor') && user?.is_provider) {
                setSelectedNoteProvider(user.id);
              } else {
                setSelectedNoteProvider(null);
              }
              setSelectedNoteClients([addNoteClientFromUrl]);
              setIsGroupNote(false);
              setNoteClientLocked(true);
              setShowAddNote(true);
            }
          } catch (error) {
            console.error('[URL Navigation] Failed to open add-note:', error);
          } finally {
            const cleanParams = new URLSearchParams(window.location.search);
            cleanParams.delete('add_note_client');
            const cleanSearch = cleanParams.toString();
            navigate(`/dashboard${cleanSearch ? `?${cleanSearch}` : ''}`, { replace: true });
          }
        })();
      }

      // Feb 28, 2026 — V2 Tasks card-click deep-link.  Real /api/tasks
      // rows in V2 bounce to /dashboard?view=notes&open_task=<id> so
      // the same legacy Task Details dialog (with Discussion thread,
      // Withdraw, Mark In Progress, etc.) opens.  We fetch the task
      // and stuff it into `selectedTask` — exactly what the legacy
      // unified-feed click handler does at App.js:41281.
      const openTaskFromUrl = urlParams.get('open_task');
      if (openTaskFromUrl && user) {
        (async () => {
          try {
            const r = await axios.get(`${API}/tasks/${openTaskFromUrl}`);
            if (r.data) {
              const cleanParams = new URLSearchParams(window.location.search);
              cleanParams.delete('open_task');
              const cleanSearch = cleanParams.toString();
              navigate(`/dashboard${cleanSearch ? `?${cleanSearch}` : ''}`, { replace: true });
              // Defer to next tick so any tab-change mount effects in
              // the legacy view settle before we flip the dialog on —
              // mirrors the V2 appointments deep-link pattern.
              setTimeout(() => {
                setSelectedTask(r.data);
                setShowTaskDetail(true);
              }, 300);
            }
          } catch (error) {
            console.error('[URL Navigation] Failed to load task:', error);
            toast.error('Could not open that task.');
          }
        })();
      }

      // Feb 28, 2026 — V2 Tasks change-request deep-link.  Synthesized
      // change_request rows bounce here so the legacy review dialog
      // (defined at App.js:61482) opens.
      const openChangeRequestFromUrl = urlParams.get('open_change_request');
      if (openChangeRequestFromUrl && user) {
        (async () => {
          try {
            const r = await axios.get(`${API}/change-requests/${openChangeRequestFromUrl}`);
            const cleanParams = new URLSearchParams(window.location.search);
            cleanParams.delete('open_change_request');
            const cleanSearch = cleanParams.toString();
            navigate(`/dashboard${cleanSearch ? `?${cleanSearch}` : ''}`, { replace: true });
            if (r.data) {
              setTimeout(() => {
                setSelectedChangeRequest(r.data);
                setShowChangeRequestDetail(true);
              }, 300);
            }
          } catch (error) {
            console.error('[URL Navigation] Failed to load change request:', error);
            toast.error('Could not open that change request.');
          }
        })();
      }

      // Also check for appointmentId in URL
      const appointmentIdFromUrl = urlParams.get('appointmentId');
      if (appointmentIdFromUrl && !editingAppointment) {
        // Load the appointment
        const loadAppointmentFromUrl = async () => {
          try {
            const response = await axios.get(`${API}/appointments/${appointmentIdFromUrl}`);
            if (response.data) {
              setEditingAppointment(response.data);
              setShowAppointmentForm(true);
              if (viewFromUrl !== 'appointments') {
                setActiveTab('appointments');
              }
            }
          } catch (error) {
            console.error('[URL Navigation] Failed to load appointment:', error);
            // Remove invalid appointmentId from URL
            navigate(`/dashboard?view=appointments`, { replace: true });
          }
        };
        loadAppointmentFromUrl();
      }

      // V2 → Dashboard bouncer support: when /v2/calendar's slot-click
      // bouncer lands on the Dashboard with `?newAppointmentStart=<iso>`,
      // open a fresh appointment form prefilled with that start time.
      // The dialog itself still lives on the Dashboard; this URL hook
      // is the bridge that makes V2's bouncer feel native.
      const rescheduleTaskIdFromUrl = urlParams.get('reschedule_task_id');
      if (rescheduleTaskIdFromUrl && !editingAppointment && !showAppointmentForm) {
        // Mar 2026 — V2 Tasks panel can deep-link to legacy with this
        // param when the user isn't in V2 shell.  Re-use the same
        // `rescheduleFromTask` window event the create_task widget
        // fires so the dialog opens with the provider's reschedule
        // context banner (notes, target date, phone).
        window.dispatchEvent(new CustomEvent('rescheduleFromTask', {
          detail: { task_id: rescheduleTaskIdFromUrl },
        }));
        // Clean the URL so a refresh doesn't reopen.
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('reschedule_task_id');
        window.history.replaceState({}, '', cleanUrl.toString());
      }

      const newApptStartFromUrl = urlParams.get('newAppointmentStart');
      if (newApptStartFromUrl && !editingAppointment && !showAppointmentForm) {
        try {
          const startDate = new Date(newApptStartFromUrl);
          if (!Number.isNaN(startDate.getTime())) {
            const endDate = new Date(startDate.getTime() + 15 * 60 * 1000);
            const fmt = (d) => {
              const y = d.getFullYear();
              const m = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              const h = String(d.getHours()).padStart(2, '0');
              const min = String(d.getMinutes()).padStart(2, '0');
              return `${y}-${m}-${day}T${h}:${min}`;
            };
            setAppointmentFormData(prev => ({
              ...prev,
              start_time: fmt(startDate),
              end_time: fmt(endDate),
            }));
            setShowAppointmentForm(true);
            if (viewFromUrl !== 'appointments') {
              setActiveTab('appointments');
            }
            // Clean the URL so a refresh doesn't reopen the dialog.
            const cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.delete('newAppointmentStart');
            window.history.replaceState({}, '', cleanUrl.toString());
          }
        } catch (err) {
          console.error('[URL Navigation] Bad newAppointmentStart:', err);
        }
      }
      
      // Also check for groupId in URL
      const groupIdFromUrl = urlParams.get('groupId');
      const modeFromUrl = urlParams.get('mode');
      
      if (groupIdFromUrl && !editingGroup && !showAttendanceDialog && !showAttendanceSnapshot && !showGroupMembers) {
        // Load the group
        const loadGroupFromUrl = async () => {
          try {
            const response = await axios.get(`${API}/client-groups/${groupIdFromUrl}`);
            if (response.data) {
              if (modeFromUrl === 'attendance') {
                // Open attendance dialog
                handleDirectAttendance(response.data);
              } else if (modeFromUrl === 'history') {
                // Open group history/attendance snapshot
                handleGroupHistory(response.data);
              } else if (modeFromUrl === 'members') {
                // Open group members dialog
                handleGroupMembers(response.data);
              } else {
                // Open group edit dialog
                handleEditClientGroup(response.data);
              }
              if (viewFromUrl !== 'groups') {
                setActiveTab('groups');
              }
            }
          } catch (error) {
            console.error('[URL Navigation] Failed to load group:', error);
            // Remove invalid groupId from URL
            navigate(`/dashboard?view=groups`, { replace: true });
          }
        };
        loadGroupFromUrl();
      }
      
      // Also check for waitlistId in URL
      const waitlistIdFromUrl = urlParams.get('waitlistId');
      const waitlistModeFromUrl = urlParams.get('mode');
      
      if (waitlistIdFromUrl && viewFromUrl === 'waitlist' && !showClientPreview && !showIntakeWizard) {
        // Load the waitlist client
        const loadWaitlistFromUrl = async () => {
          try {
            const response = await axios.get(`${API}/clients/${waitlistIdFromUrl}`);
            if (response.data) {
              if (waitlistModeFromUrl === 'edit') {
                // Phase 3e.3a — delegate to V2 provider for the edit flow.
                intakeWizard.open(waitlistIdFromUrl);
              } else {
                // Default to preview mode
                setPreviewClient(response.data);
                setShowClientPreview(true);
              }
              if (viewFromUrl !== 'waitlist') {
                setActiveTab('waitlist');
              }
            }
          } catch (error) {
            console.error('[URL Navigation] Failed to load waitlist client:', error);
            // Remove invalid waitlistId from URL
            navigate(`/dashboard?view=waitlist`, { replace: true });
          }
        };
        loadWaitlistFromUrl();
      }
    }
  }, [user, initialClientId]);

  // Feb 28, 2026 — V2 Tasks "Reschedule" deep-link consumer.
  //
  // When the V2 Tasks tab bounces a Reschedule/Schedule task to
  // /dashboard?view=appointments&open_client=XYZ, the upstream deep
  // link handler stashes:
  //   - window.__v2_prefill_client__ — picked up by the form-data
  //     prefill effect once the dialog opens.
  //   - setPendingOpenAppointment(true) — consumed HERE.
  //
  // We wait for `activeTab === 'appointments'` to commit before
  // firing `setShowAppointmentForm(true)`.  Without this guard the
  // dialog flashed or failed to open because the scheduler component
  // wasn't mounted yet (it only renders when the appointments tab is
  // active).  An extra `requestAnimationFrame` defers the open one
  // paint further so any sibling mount-time form resets settle first.
  const [pendingOpenAppointment, setPendingOpenAppointment] = useState(false);
  useEffect(() => {
    if (activeTab !== 'appointments' || !user) return;
    if (!pendingOpenAppointment) return;
    // Mimic the manual "Schedule Appointment" button handler so the
    // dialog reliably opens with the deep-linked client pre-filled.
    // Defer the open + clear via setTimeout(0) so React commits the
    // current render (activeTab='appointments') before we toggle the
    // dialog — without the deferral the dialog could mount before the
    // calendar tree settles.  We deliberately do NOT return a cleanup
    // here because the pendingOpenAppointment flip below re-runs this
    // effect — a cleanup would cancel the open we just queued.
    const defaultProviderId = (userRoleName === 'office' || userRoleName === 'reception')
      ? (selectedProviders?.length === 1 && selectedProviders[0] !== 'all' ? selectedProviders[0] : '')
      : (user?.id || '');
    const prefillClient = window.__v2_prefill_client__ || '';
    window.__v2_prefill_client__ = null;
    setPendingOpenAppointment(false);
    setTimeout(() => {
      setAppointmentFormData({
        client_id: prefillClient,
        client_ids: [],
        selected_group_id: '',
        provider_id: defaultProviderId,
        start_time: '',
        end_time: '',
        service_type: '',
        appointment_type: 'client_session',
        status: 'scheduled',
        is_billable: true,
        is_payroll_eligible: false,
        units: 1,
        location: '',
        notes: '',
        cpt_code_id: '',
        is_recurring: false,
        recurrence_pattern: '',
        recurrence_count: '',
        recurrence_end_date: '',
        selectedCptMeta: null,
      });
      setEditingAppointment(null);
      setShowAppointmentForm(true);
    }, 0);
  }, [activeTab, user, pendingOpenAppointment, userRoleName, selectedProviders, setAppointmentFormData, setEditingAppointment, setShowAppointmentForm]);

  // Handle URL-based client navigation - auto-load client when initialClientId is provided
  // Use a ref to track if user has manually closed the chart (to prevent auto-reopening)
  const userClosedChartRef = React.useRef(false);
  // Feb 2026 — track whether we've already honoured the ?mode=edit URL
  // param this page load.  Without this guard the intake wizard
  // re-opened whenever selectedClient.id flipped (e.g., after the
  // simple Edit Client dialog saved and re-fetched the client), even
  // though the user had already finished with the wizard.  One-shot
  // semantics: clear it only when the user closes the wizard.
  const didAutoOpenIntakeRef = React.useRef(false);
  // Mar 2026 — One-shot guard for V2 → legacy release-form deep links
  // (`/client/:id?tab=release-form&releaseId=…`).  Same rationale as
  // `didAutoOpenIntakeRef`: prevent the URL effect from re-hydrating
  // the release form every time `selectedClient.id` cycles.
  const didAutoOpenReleaseRef = React.useRef(false);
  // Mar 2026 — One-shot guard for V2 → legacy treatment-plan deep
  // links (`/client/:id?tab=treatment-plans&action=new|open`).  V2
  // Plan tab navigates here when the user wants to create or open a
  // plan in the full legacy editor.  Guard prevents re-firing on
  // `selectedClient.id` cycles.
  const didAutoOpenPlanRef = React.useRef(false);
  
  // Reset the closed flag when initialClientId changes (new client URL)
  useEffect(() => {
    userClosedChartRef.current = false;
    didAutoOpenIntakeRef.current = false;
    didAutoOpenReleaseRef.current = false;
    didAutoOpenPlanRef.current = false;
  }, [initialClientId]);
  
  useEffect(() => {
    // Skip if user manually closed the chart
    if (userClosedChartRef.current) {
      return;
    }
    
    // Skip if client is already loaded with the correct ID (prevents flash on navigation)
    if (selectedClient?.id === initialClientId) {
      return;
    }
    
    // If we have an initialClientId from URL and user is logged in, load the client
    if (initialClientId && user) {
      // Read tab and mode from URL query params
      const urlParams = new URLSearchParams(window.location.search);
      const tabFromUrl = urlParams.get('tab') || 'overview';
      const modeFromUrl = urlParams.get('mode');
      const releaseIdFromUrl = urlParams.get('releaseId');
      const releaseActionFromUrl = urlParams.get('action');
      
      // Load client with the tab from URL, skip URL update since we're already at the right URL
      handleClientClick(initialClientId, { initialTab: tabFromUrl, skipUrlUpdate: true });
      
      // If mode=edit, open the intake wizard for editing after client loads.
      // Feb 2026 — guard with ``didAutoOpenIntakeRef`` so we only auto-
      // open once per page load.  Without this, the simple "Edit Client"
      // dialog save (which doesn't touch ``mode``) would briefly flicker
      // the intake wizard because this useEffect re-fires when
      // ``selectedClient.id`` settles back to its prior value after the
      // ``setSelectedClient(null) → setSelectedClient(<reloaded>)``
      // round-trip the save flow does.
      if (modeFromUrl === 'edit' && !didAutoOpenIntakeRef.current) {
        didAutoOpenIntakeRef.current = true;
        // Small delay to ensure client data is loaded first
        setTimeout(() => {
          // Phase 3e.3a — delegate to V2 provider.
          intakeWizard.open(initialClientId);
        }, 500);
      }

      // Mar 2026 — V2 Contacts tab deep-links here with
      // `?tab=release-form&releaseId=X` (or `&action=new`) when the
      // user clicks "Edit"/"New Release" on a release card.  The
      // legacy release-form lives in App.js and is driven by
      // `releaseFormData` + `selectedRelease`; hydrate them here so
      // the form opens already populated for the right release.
      if (tabFromUrl === 'release-form' && !didAutoOpenReleaseRef.current) {
        didAutoOpenReleaseRef.current = true;
        if (releaseIdFromUrl) {
          (async () => {
            try {
              const res = await axios.get(`${API}/releases/${releaseIdFromUrl}`);
              const full = res.data || null;
              if (full) {
                setReleaseFormData((prev) => ({ ...prev, ...full }));
                setSelectedRelease(full);
              }
            } catch (err) {
              console.warn('Failed to auto-load release from URL:', err);
              toast.error('Failed to load release for editing');
            }
          })();
        } else if (releaseActionFromUrl === 'new') {
          // Start the form fresh.  Mirrors the legacy "New Release"
          // button's reset payload (see App.js ~line 45994).
          setReleaseFormData({
            direction: 'both',
            linked_contact_id: '',
            recipient_name: '',
            contact_person: '',
            address: '',
            suite: '',
            city: '',
            state: '',
            zip_code: '',
            phone: '',
            phone_ext: '',
            fax: '',
            email: '',
            date_range_type: 'all',
            date_from: '',
            date_to: '',
            info_entire_medical: false,
            info_non_medical: false,
            info_progress_therapy: false,
            info_progress_psychiatric: false,
            info_assessments: false,
            info_psych_testing: false,
            info_treatment_summary: false,
            info_discharge_summary: false,
            info_school_records: false,
            info_attendance_only: false,
            info_insurance_claims: false,
            info_substance: false,
            info_substance_description: '',
            info_other: false,
            info_other_description: '',
            purpose_assessment: false,
            purpose_continuity: false,
            purpose_substance: false,
            purpose_substance_description: '',
            purpose_treatment_summary: false,
            purpose_discharge_summary: false,
            purpose_school_records: false,
            purpose_attendance_only: false,
            purpose_insurance: false,
            purpose_other: false,
            purpose_other_description: '',
            expiration_type: 'default_90_days',
            expiration_date: '',
            expiration_event: '',
            member_refused: false,
            verbal_consent: false,
            special_reason: '',
          });
          setSelectedRelease(null);
        }
      }

      // Mar 2026 — V2 Plan tab deep-link
      // `?tab=treatment-plans&action=new|open[&planId=…]`.  The V2
      // Plan list-view bridges every write/edit action through this
      // URL because the legacy chart still owns the heavy Treatment
      // Plan editor / signing dialogs (Phase A of the Plan-tab
      // migration; Phase B will lift the dialogs natively).  For
      // `action=new` we dispatch a window event after a short delay
      // so the legacy "New Treatment Plan" button's existing onClick
      // handler can drive the same data-loading + dialog-open flow
      // it always has — avoids duplicating ~80 lines of pre-fetch +
      // dialog-state setup here.
      if (tabFromUrl === 'treatment-plans' && !didAutoOpenPlanRef.current) {
        const planAction = urlParams.get('action');
        if (planAction === 'new') {
          didAutoOpenPlanRef.current = true;
          // Poll for the legacy "New Treatment Plan" button to mount
          // (chart open → tab switch → button render is asynchronous);
          // dispatch the open-event as soon as it shows up.  Bail
          // after ~5 s so we don't leak a setInterval.
          let attempts = 0;
          const interval = setInterval(() => {
            attempts += 1;
            const btn = document.querySelector('[data-testid="legacy-new-treatment-plan-btn"]');
            if (btn) {
              clearInterval(interval);
              try {
                window.dispatchEvent(new CustomEvent('legacy:open-new-treatment-plan', {
                  detail: { clientId: initialClientId },
                }));
              } catch { /* noop */ }
            } else if (attempts >= 25) {
              clearInterval(interval);
            }
          }, 200);
        } else if (planAction === 'open') {
          // No-op: legacy chart already lands on the Plan tab; user
          // sees the plan card right there and can click into it.
          // The `planId` URL param is reserved for Phase B when we
          // auto-scroll/highlight a specific card.
          didAutoOpenPlanRef.current = true;
        }
      }
    }
  }, [initialClientId, user, selectedClient?.id]);
  
  // Function to close client chart and mark as user-closed.
  // Jun 2026 (user request) — return to the captured origin view
  // (e.g. Claims Workshop stores it in sessionStorage before opening
  // the chart) instead of hardcoding the dashboard; the default
  // dashboard behavior only applies when no origin was recorded.
  const handleCloseClientChart = useCallback(() => {
    userClosedChartRef.current = true;
    setSelectedClient(null);
    setShowClientDetail(false);
    if (isFullscreenClientMode) {
      setIsFullscreenClientMode(false);
    }
    let origin = null;
    try {
      origin = sessionStorage.getItem('chartReturnTo');
      if (origin) sessionStorage.removeItem('chartReturnTo');
    } catch { /* noop */ }
    if (origin && origin.startsWith('/') && !origin.startsWith('//')) {
      navigate(origin, { replace: true });
    } else {
      // Default: stay on the dashboard, preserving the active tab.
      // replaceState (not navigate) avoids re-triggering data-fetch
      // effects tied to router navigation.
      window.history.replaceState({}, '', `/dashboard?view=${activeTab}`);
    }
  }, [navigate, isFullscreenClientMode, activeTab]);

  // Handle fullscreen note mode - auto-load note when fullscreenNoteId is provided
  // Jun 2026 (user report: "CPT edit flashes then reverts"): this effect
  // depends on `formTemplates`, and hitting Edit runs openNote() which
  // refreshes formTemplates — the effect then RE-RAN, refetched the note
  // and reset isEditingNote(false), kicking the user out of edit ~1s in.
  // Gate the body so it only loads once per fullscreenNoteId.
  const loadedFullscreenNoteIdRef = useRef(null);
  useEffect(() => {
    if (!fullscreenNoteId) {
      loadedFullscreenNoteIdRef.current = null;
    }
    const loadFullscreenNote = async () => {
      void 0 && console.log('[Fullscreen] Checking fullscreen note load:', { fullscreenNoteId, hasUser: !!user });
      if (fullscreenNoteId && user && loadedFullscreenNoteIdRef.current !== fullscreenNoteId) {
        loadedFullscreenNoteIdRef.current = fullscreenNoteId;
        try {
          const token = sessionStorage.getItem('token');
          void 0 && console.log('[Fullscreen] Loading note:', fullscreenNoteId);
          
          // Load form templates if not already loaded (needed for note rendering)
          if (!formTemplates || formTemplates.length === 0) {
            const templatesResponse = await axios.get(`${API}/form-templates`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setFormTemplates(templatesResponse.data || []);
          }
          
          const response = await axios.get(`${API}/notes/${fullscreenNoteId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          void 0 && console.log('[Fullscreen] Note loaded:', response.data?.id);
          // Jun 2026 DATA-PRESERVATION: clear any leftover edit state from a
          // previously edited note — this route bypasses openNote, and stale
          // isEditingNote/editedNoteContent let a manual save write another
          // note's (or empty) content onto this one.
          setIsEditingNote(false);
          setEditedNoteContent(null);
          setSelectedNote(response.data);
          setShowNoteDetail(true);
          // Jun 2026 — this route bypasses openNote, so fetch the linked
          // appointment directly (the global appointments list only covers a
          // narrow date window, causing false "Appointment not found").
          setNoteLinkedAppointment(null);
          if (response.data?.appointment_id) {
            try {
              const aptRes = await axios.get(`${API}/appointments/${response.data.appointment_id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (aptRes.data) setNoteLinkedAppointment(aptRes.data);
            } catch (aptErr) {
              console.warn('[Fullscreen] Linked appointment fetch failed:', aptErr?.response?.status);
            }
          }
        } catch (error) {
          console.error('[Fullscreen] Error loading note:', error);
          // Allow a retry on the next effect run if the load failed.
          loadedFullscreenNoteIdRef.current = null;
          toast.error('Failed to load note');
        }
      }
    };
    loadFullscreenNote();
  }, [fullscreenNoteId, user, formTemplates]);

  // Track if we've loaded from URL to prevent re-loading after close
  const hasLoadedFromUrlRef = useRef(false);
  
  // Handle URL query params - load note if noteId is in URL (only on initial load)
  useEffect(() => {
    const loadNoteFromUrl = async () => {
      if (!user) return;
      
      const urlParams = new URLSearchParams(window.location.search);
      const noteIdFromUrl = urlParams.get('noteId');
      
      // Only load if we have a noteId in URL and haven't loaded yet
      if (noteIdFromUrl && !hasLoadedFromUrlRef.current) {
        hasLoadedFromUrlRef.current = true; // Mark as loaded to prevent re-opening
        try {
          const token = sessionStorage.getItem('token');
          const response = await axios.get(`${API}/notes/${noteIdFromUrl}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data) {
            // Jun 2026 DATA-PRESERVATION: same stale-edit-state guard as the
            // fullscreen route — this path also bypasses openNote.
            setIsEditingNote(false);
            setEditedNoteContent(null);
            setSelectedNote(response.data);
            setShowNoteDetail(true);
            // Jun 2026 — same bypass as fullscreen route: fetch linked
            // appointment directly so the header doesn't show "not found".
            setNoteLinkedAppointment(null);
            if (response.data.appointment_id) {
              try {
                const aptRes = await axios.get(`${API}/appointments/${response.data.appointment_id}`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (aptRes.data) setNoteLinkedAppointment(aptRes.data);
              } catch (aptErr) {
                console.warn('[URL Navigation] Linked appointment fetch failed:', aptErr?.response?.status);
              }
            }
          }
        } catch (error) {
          console.error('[URL Navigation] Failed to load note:', error);
          // Clear invalid noteId from URL
          const url = new URL(window.location.href);
          url.searchParams.delete('noteId');
          window.history.replaceState({}, '', url.toString());
        }
      }
      
      // Reset the flag when noteId is removed from URL (allows future loads)
      if (!noteIdFromUrl) {
        hasLoadedFromUrlRef.current = false;
      }
    };
    loadNoteFromUrl();
  }, [user]);

  // Note: staticDataLoaded is now in StaticDataContext

  // Fetch form templates - can be called after creating/updating templates
  // Workstream C3 (Feb 2026) — module-level cache for form templates.
  // 60s TTL.  Lives as a ref so re-renders don't reset it.  Bypassed
  // by passing skipCache=true (used after template imports).
  const fetchFormTemplatesCacheRef = useRef(null);

  const fetchFormTemplates = useCallback(async (skipCache = false) => {
    const token = sessionStorage.getItem('token');
    if (!token) return [];

    // Workstream C3 (Feb 2026) — 60s TTL cache to stop the per-
    // note-open refetch.  Templates only change when an admin edits
    // them; serving stale-by-60s during a clinician's note-writing
    // burst is the right tradeoff vs hammering the endpoint on every
    // openNote().  skipCache=true bypasses (used after imports).
    if (!skipCache) {
      const cached = fetchFormTemplatesCacheRef.current;
      if (cached && (Date.now() - cached.ts) < 60_000) {
        // Keep React state in sync in case caller relied on side-effect
        if (formTemplates !== cached.templates) {
          setFormTemplates(cached.templates);
        }
        return cached.templates;
      }
    }

    try {
      // Always fetch with include_archived=true so we have all templates
      // The UI filters by showArchivedTemplates state
      // Use nocache=true after imports to bypass browser cache
      const cacheParam = skipCache ? '&nocache=true' : '';
      const response = await axios.get(`${API}/form-templates?include_archived=true${cacheParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const templates = response.data || [];
      setFormTemplates(templates);
      fetchFormTemplatesCacheRef.current = { templates, ts: Date.now() };
      void 0 && console.log('[TEMPLATES] Refreshed form templates:', templates.length, skipCache ? '(cache bypassed)' : '');
      return templates; // Return templates so callers can use them immediately
    } catch (error) {
      console.error('[TEMPLATES] Failed to fetch form templates:', error);
      return [];
    }
  }, [formTemplates]);

  // Helper function to fetch with retry
  const fetchWithRetry = useCallback(async (url, maxRetries = 3, delay = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await axios.get(url);
        return response;
      } catch (error) {
        console.warn(`[RETRY] Attempt ${attempt}/${maxRetries} failed for ${url}:`, error.message);
        if (attempt === maxRetries) {
          console.error(`[RETRY] All ${maxRetries} attempts failed for ${url}`);
          return { data: [], _failed: true, _url: url };
        }
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
    return { data: [], _failed: true, _url: url };
  }, []);

  // Fetch static/rarely-changing data - only called once on login
  const fetchStaticData = useCallback(async () => {
    // Guard: Must have fully authenticated user with ID
    if (!user || !user.id) {
      void 0 && console.log('[PERF] No user or user.id, skipping fetchStaticData');
      return;
    }
    
    // Ensure auth header is set before making API calls
    const token = sessionStorage.getItem('token');
    if (!token) {
      void 0 && console.log('[PERF] No token found, skipping fetchStaticData');
      return;
    }
    
    // Always ensure Authorization header is set (fixes race condition on login)
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    void 0 && console.log('[PERF] Fetching static data (one-time)... User role:', user?.role, 'is_provider:', user?.is_provider, 'isSuperAdmin:', isSuperAdmin(user));
    
    // Update health status to loading
    setDataHealth(prev => ({ ...prev, staticData: 'loading', failedEndpoints: [] }));
    
    // Track failed requests for retry notification
    const failedRequests = [];
    
    try {
      // ─────────────────────────────────────────────────────────────
      // Named-map static fetch (Feb 2026 deferred-load refactor).
      //
      // The old code packed everything into an array indexed by
      // `responses[idx + N]` per role, which made deferring any one
      // entry a footgun.  This is now a single map of `name → axios
      // Promise`; assigned by name so adding/removing is one line
      // each.
      //
      // What stays EAGER on mount (essential to render initial
      // Dashboard chrome and any tab the user can immediately click):
      //   - providers, locations, pos-codes, appointment-statuses
      //   - organizations (used in chrome on most layouts)
      //   - users (admin/provider/office need it for the team picker)
      //   - client-groups (sidebar count badges, used widely)
      //   - staff-list (provider/office sidebars)
      //
      // What is now DEFERRED (loaded lazily via the tab-activation
      // effect below — see `useEffect(() => { ... }, [activeTab,
      // showClientDetail, showAppointmentForm, showNoteEditor])`):
      //   - cpt-codes, cpt-bundles                (notes + billing)
      //   - cpt-conflict-rules                    (note save validation)
      //   - insurance-cpt-restrictions            (note save validation)
      //   - form-templates, form-types            (intake / documents)
      //   - intake-bundles                        (admin intake setup)
      //   - treatment-planning/service-types      (TP flows)
      //   - curricula                             (group curricula picker)
      // ─────────────────────────────────────────────────────────────
      const fetchTargets = {
        providers: `${API}/providers`,
        locations: `${API}/locations`,
        posCodes: `${API}/pos-codes`,
        appointmentStatuses: `${API}/config/appointment-statuses`,
      };

      // Role-specific essential additions
      // Log the role detection for debugging
      const roleCheck = {
        role: user?.role,
        is_provider: user?.is_provider,
        isSuperAdmin: isSuperAdmin(user),
        willMatch: isSuperAdmin(user) ? 'superadmin' 
          : (userRoleName === 'provider' || user?.is_provider) ? 'provider' 
          : (userRoleName === 'office' || userRoleName === 'reception') ? 'office/reception'
          : 'other'
      };
      void 0 && console.log('[PERF] Role check for data fetch:', roleCheck);
      
      if (isSuperAdmin(user)) {
        fetchTargets.organizations = `${API}/organizations`;
        fetchTargets.users = `${API}/users?include_archived=true`;
        fetchTargets.clientGroups = `${API}/client-groups`;
      } else if (userRoleName === 'provider' || user?.is_provider) {
        fetchTargets.organizations = `${API}/organizations`;
        fetchTargets.clientGroups = `${API}/client-groups`;
        fetchTargets.users = `${API}/staff-list`;
      } else if (userRoleName === 'office' || userRoleName === 'reception') {
        fetchTargets.organizations = `${API}/organizations`;
        fetchTargets.clientGroups = `${API}/client-groups`;
        fetchTargets.users = `${API}/staff-list`;
      } else {
        // Other roles (supervisor, biller, admin, etc.) - get basic data they need
        fetchTargets.organizations = `${API}/organizations`;
        fetchTargets.users = `${API}/staff-list`;
      }

      const fetchNames = Object.keys(fetchTargets);
      const fetchUrls = Object.values(fetchTargets);

      // Feb 2026 — SWR cache pass.
      //
      // For each named endpoint, paint the cached value to React
      // state IMMEDIATELY (synchronous sessionStorage read).  The
      // dashboard chrome therefore mounts with full data BEFORE the
      // first network round-trip even completes — drastically
      // dropping LCP on warm reloads.  Then revalidate in the
      // background; when the fresh response lands we run the same
      // setter again to swap in fresh data.  See utils/staticDataCache.js.
      const setterByName = {
        providers: (val) => setProviders(val || []),
        locations: (val) => setLocations(val || []),
        posCodes: (val) => setPosCodes(val || []),
        appointmentStatuses: (val) => setAvailableStatuses((val && val.statuses) ? val.statuses : []),
        organizations: (val) => setOrganizations(val || []),
        users: (val) => setUsers(val || []),
        clientGroups: (val) => setClientGroups(val || []),
      };
      fetchNames.forEach((name) => {
        const cached = readStaticData(name);
        if (cached !== null && setterByName[name]) {
          try { setterByName[name](cached); } catch { /* unmounted */ }
        }
      });

      const responses = await Promise.all(fetchUrls.map((u) => fetchWithRetry(u)));
      const byName = {};
      fetchNames.forEach((name, i) => { byName[name] = responses[i]; });
      
      // Check for failed requests
      responses.forEach(r => {
        if (r._failed) {
          failedRequests.push(r._url);
        }
      });
      
      void 0 && console.log('[PERF] All static data requests completed. Failed:', failedRequests.length);
      
      // Set common static data + persist into sessionStorage for the
      // next page load.  Skip writing on failure — keep the previous
      // (stale) cache so the next session has SOMETHING to paint.
      if (!byName.providers?._failed) {
        setProviders(byName.providers?.data || []);
        bustAndWriteStaticData('providers', byName.providers?.data || []);
      }
      if (!byName.locations?._failed) {
        setLocations(byName.locations?.data || []);
        bustAndWriteStaticData('locations', byName.locations?.data || []);
      }
      if (!byName.posCodes?._failed) {
        setPosCodes(byName.posCodes?.data || []);
        bustAndWriteStaticData('posCodes', byName.posCodes?.data || []);
      }
      if (!byName.appointmentStatuses?._failed) {
        const statuses = byName.appointmentStatuses?.data?.statuses || [];
        setAvailableStatuses(statuses);
        bustAndWriteStaticData('appointmentStatuses', byName.appointmentStatuses?.data || { statuses });
      }

      // Fetch provider counts for each location (in background, non-blocking)
      const locationData = byName.locations?.data || [];
      if (locationData.length > 0) {
        Promise.all(
          locationData.map(async (location) => {
            try {
              const providerResponse = await axios.get(`${API}/locations/${location.id}/providers`);
              return { id: location.id, count: providerResponse.data.length };
            } catch (error) {
              return { id: location.id, count: 0 };
            }
          })
        ).then(results => {
          const counts = {};
          results.forEach(r => { counts[r.id] = r.count; });
          setLocationProviderCounts(counts);
        });
      }

      // Role-specific data assignment (by name — order-independent)
      // Same SWR-write pattern: persist on success so the next page
      // load gets an instant paint.
      if (byName.organizations && !byName.organizations?._failed) {
        setOrganizations(byName.organizations?.data || []);
        bustAndWriteStaticData('organizations', byName.organizations?.data || []);
      }
      if (byName.users && !byName.users?._failed) {
        setUsers(byName.users?.data || []);
        bustAndWriteStaticData('users', byName.users?.data || []);
      }
      if (byName.clientGroups && !byName.clientGroups?._failed) {
        setClientGroups(byName.clientGroups?.data || []);
        bustAndWriteStaticData('clientGroups', byName.clientGroups?.data || []);
      }
      // Note: cptCodes, cptBundles, formTemplates, formTypes, intakeBundles,
      // and serviceTypes are no longer fetched here — see the lazy
      // tab-activation effect below.

      void 0 && console.log('[PERF] Static data loaded successfully.');
      
      // Update health status with details
      setDataHealth(prev => ({
        ...prev,
        staticData: failedRequests.length > 0 ? 'warning' : 'success',
        failedEndpoints: failedRequests,
        retryCount: prev.retryCount + failedRequests.length,
        staticDetails: {
          providers: byName.providers?.data?.length || 0,
          locations: byName.locations?.data?.length || 0,
          users: byName.users?.data?.length || 0,
        }
      }));
      
      setStaticDataLoaded(true);
      
      // Notify user if some data failed to load
      if (failedRequests.length > 0) {
        console.error('[PERF] Some data failed to load after retries:', failedRequests);
        toast.error(`Some data failed to load. Please refresh the page if you notice missing information.`, {
          duration: 8000,
          action: {
            label: 'Refresh',
            onClick: () => window.location.reload()
          }
        });
      }
    } catch (error) {
      console.error('[PERF] Failed to fetch static data:', error);
      
      // Update health status to error
      setDataHealth(prev => ({
        ...prev,
        staticData: 'error',
        failedEndpoints: [...prev.failedEndpoints, 'static-data-batch']
      }));
      
      toast.error('Failed to load application data. Please refresh the page.', {
        duration: 10000,
        action: {
          label: 'Refresh',
          onClick: () => window.location.reload()
        }
      });
      // Don't block the app - set loaded anyway so dynamic data can still load
      setStaticDataLoaded(true);
    }
  }, [user, fetchWithRetry]);
  
  // Failsafe: If static data hasn't loaded after 30 seconds, force it to continue
  // This prevents the app from being stuck forever if something goes wrong
  useEffect(() => {
    if (user?.id && !staticDataLoaded) {
      const failsafeTimer = setTimeout(() => {
        if (!staticDataLoaded) {
          console.warn('[PERF] Failsafe triggered - static data load timeout after 30s');
          toast.warning('Some data is taking longer than expected to load. The app will continue with available data.', {
            duration: 8000
          });
          setStaticDataLoaded(true);
        }
      }, 30000); // 30 second failsafe
      
      return () => clearTimeout(failsafeTimer);
    }
  }, [user?.id, staticDataLoaded]);

  // Fetch all clients the user has permission to see (no UI filters)
  // This is used for lookups throughout the app (Groups, Appointments, Notes, etc.)
  const fetchAllClientsForLookup = useCallback(async (retryCount = 0) => {
    if (!user || !user.id) {
      void 0 && console.log('[LOOKUP] No user, skipping fetchAllClientsForLookup');
      return;
    }
    
    const token = sessionStorage.getItem('token');
    if (!token) {
      void 0 && console.log('[LOOKUP] No token, skipping fetchAllClientsForLookup');
      return;
    }
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    try {
      void 0 && console.log('[LOOKUP] Fetching clients for lookup (lightweight)...');
      
      // Fetch clients in batches for better performance.
      // Include archived clients so historical claims/appointments still resolve to a client.
      // First batch: 1000 (was 500) — fewer round trips for typical practices,
      // and the dropdown is fully populated faster.
      const firstBatchResponse = await axios.get(`${API}/clients?page_size=1000&fields=minimal&include_archived=true`);
      
      let firstBatch = [];
      let totalCount = 0;
      
      if (Array.isArray(firstBatchResponse.data)) {
        firstBatch = firstBatchResponse.data;
        totalCount = firstBatch.length;
      } else if (firstBatchResponse.data?.clients) {
        firstBatch = firstBatchResponse.data.clients;
        // Backend returns pagination metadata under ``pagination.*``
        // (since the HYBRID DISCOVERY refactor).  The older flat
        // ``total_count`` is kept as a backwards-compat fallback, but
        // it's no longer populated — so relying on it alone pinned
        // ``totalCount`` to ``firstBatch.length`` (= 1000) for any
        // practice with more than 1000 clients, silently skipping the
        // background pagination loop below.  Result: dropdown only
        // ever showed A-through-~R.  Read pagination.total_count first.
        totalCount =
          firstBatchResponse.data.pagination?.total_count ??
          firstBatchResponse.data.total_count ??
          firstBatch.length;
      }
      
      // Set first batch immediately so UI is responsive
      void 0 && console.log('[LOOKUP] Loaded first batch:', firstBatch.length, 'clients, total:', totalCount);
      setAllClientsForLookup(firstBatch);
      
      // If there are more clients, fetch remaining in background
      if (totalCount > 1000) {
        void 0 && console.log('[LOOKUP] Fetching remaining clients in background...');
        
        // Fetch remaining pages in background without blocking.
        // Start IMMEDIATELY (was 100ms delay) — eliminates a window where the
        // Billing-tab dropdown shows only the first batch while pagination
        // hasn't started yet.
        setTimeout(async () => {
          try {
            let allClients = [...firstBatch];
            let page = 2;
            const pageSize = 1000;  // Match first batch size — fewer round trips
            
            // Max 50,000 clients (was 10,000) — defensive ceiling for very
            // large practices; will short-circuit on empty page anyway.
            while (allClients.length < totalCount && page <= 50) {
              const response = await axios.get(`${API}/clients?page=${page}&page_size=${pageSize}&fields=minimal&include_archived=true`);
              
              let pageClients = [];
              if (Array.isArray(response.data)) {
                pageClients = response.data;
              } else if (response.data?.clients) {
                pageClients = response.data.clients;
              }
              
              if (pageClients.length === 0) break;
              
              allClients = [...allClients, ...pageClients];
              void 0 && console.log('[LOOKUP] Loaded page', page, '- total:', allClients.length);
              page++;
            }
            
            // Single state update after all pages loaded (avoids N re-renders during pagination)
            setAllClientsForLookup(allClients);
            
            void 0 && console.log('[LOOKUP] Background fetch complete:', allClients.length, 'clients');
          } catch (bgError) {
            console.error('[LOOKUP] Background fetch error:', bgError);
          }
        }, 0);
      }
    } catch (error) {
      console.error('[LOOKUP] Failed to fetch clients for lookup:', error);
      // Retry up to 3 times
      if (retryCount < 3) {
        void 0 && console.log(`[LOOKUP] Retrying... (attempt ${retryCount + 1}/3)`);
        setTimeout(() => fetchAllClientsForLookup(retryCount + 1), 1500 * (retryCount + 1));
      } else {
        toast.error('Failed to load client data. Some features may not work correctly.', {
          duration: 8000,
          action: {
            label: 'Retry',
            onClick: () => fetchAllClientsForLookup(0)
          }
        });
      }
    }
  }, [user, setAllClientsForLookup]);

  const fetchData = useCallback(async (options = {}) => {
    // options.fullAppointments: if true, fetch full date range; if false/undefined, fetch only next 7 days for header
    // options.refreshClients: if true, bypass server cache for fresh data
    const { fullAppointments = false, refreshClients = false } = options;
    
    // Guard: Must have fully authenticated user with ID to fetch data
    if (!user || !user.id) {
      void 0 && console.log('[PERF] No user or user.id, skipping fetchData');
      return;
    }
    
    // Ensure auth header is set before making API calls
    const token = sessionStorage.getItem('token');
    if (!token) {
      void 0 && console.log('[PERF] No token found, skipping fetchData');
      return;
    }
    
    // Always ensure Authorization header is set (fixes race condition on login)
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Increment request ID to track this specific request
    const currentRequestId = ++fetchRequestIdRef.current;
    void 0 && console.log(`[PERF] Fetching dynamic data... (request #${currentRequestId})${refreshClients ? ' [REFRESH]' : ''}`);
    void 0 && console.log(`[PERF] Current filters - Status: ${JSON.stringify(clientStatusFilter)}, Provider: ${JSON.stringify(clientProviderFilter)}`);
    
    // Set loading state for clients and update health status
    setClientsLoading(true);
    setDataHealth(prev => ({ ...prev, dynamicData: 'loading' }));
    
    try {
      // Build client filter params via the shared helper.  All
      // Include/Exclude complement logic, sub-plan expansion, and
      // page/sort handling now live in one place (clientFilterParams.js).
      const clientParams = buildClientQueryParams({
        refresh: refreshClients,
        clientStatusFilter,
        clientStatusFilterMode,
        clientProviderFilter,
        clientProviderFilterMode,
        selectedLabelFilter,
        selectedLabelFilterMode,
        selectedInsuranceFilter,
        selectedInsuranceFilterMode,
        clientSearch,
        clientsCurrentPage,
        clientsPerPage,
        clientsSortColumn,
        clientsSortDirection,
        userId: user?.id,
        users,
        clientLabels,
        insurances,
        CLIENT_STATUSES,
      });
      void 0 && console.log('[API CALL] Building request - Page:', clientsCurrentPage, 'Search:', clientSearch, 'PerPage:', clientsPerPage);

      // Build notes query params with date range
      const notesParams = new URLSearchParams();
      if (notesDateRange === 'custom' && notesCustomStartDate && notesCustomEndDate) {
        notesParams.append('start_date', notesCustomStartDate);
        notesParams.append('end_date', notesCustomEndDate);
      } else if (notesDateRange !== 'all') {
        // Calculate start date based on selected range
        const daysAgo = parseInt(notesDateRange);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - daysAgo);
        notesParams.append('start_date', startDate.toISOString().split('T')[0]);
      }
      // Add other filters
      if (notesClientFilter && notesClientFilter !== 'all') notesParams.append('client_id', notesClientFilter);
      if (notesStatusFilter && notesStatusFilter !== 'all') notesParams.append('status', notesStatusFilter);

      // Build appointments query - limit to next 7 days on initial load for performance
      // Full range is loaded when Schedule tab is opened or when explicitly requested
      let appointmentsUrl = `${API}/appointments`;
      
      // Auto-enable full appointments when on schedule/calendar tab to prevent data loss.
      // Feb 2026 — User reported missing appointments from "early May" and
      // "March" even with the ±3-month window in place.  Root cause: the
      // tab name is inconsistent across the codebase — most setActiveTab
      // calls use 'appointments' but at least two flows (line 2684 +
      // 57847) use 'calendar'.  When the user lands on a 'calendar'-named
      // tab, ``activeTabRef.current === 'appointments'`` was false, so
      // the fetch fell into the tight 3-DAY window (yesterday/today/
      // tomorrow) and silently clipped everything else.  Accept BOTH
      // tab names; also accept the legacy 'schedule' variant.
      const calendarTabs = new Set(['appointments', 'calendar', 'schedule']);
      const shouldFetchFullAppointments = fullAppointments || calendarTabs.has(activeTabRef.current);
      
      if (!shouldFetchFullAppointments) {
        // Tight date range: yesterday, today, tomorrow (3 days) for header "Next Appointment".
        // The dashboard "Upcoming Appointments" widget does NOT rely on this
        // window — it has its own dedicated 90-day own-schedule fetch below.
        const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        appointmentsUrl = `${API}/appointments?start_date=${yesterday}&end_date=${tomorrow}&summary=true`;
      } else {
        // For calendar view, fetch the anchor MONTH's full visible
        // grid: 1st of month −7 days (previous-month preview row)
        // through end of month +14 days (next-month preview row).
        // The old ±21-days-around-anchor window clipped the end of
        // the month whenever the anchor sat near the 1st (e.g. month
        // navigation lands on the 1st → June 23-30 vanished).
        // When the user navigates beyond this range the lazy refetch
        // path in `refreshAppointments` re-windows around the new
        // anchor — Google-Calendar-style dynamic range fetching.
        const anchor = calendarDate || new Date();
        const winStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        winStart.setDate(winStart.getDate() - 7);
        const winEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
        winEnd.setDate(winEnd.getDate() + 14);
        const fmt = (d) => d.toISOString().split('T')[0];
        appointmentsUrl = `${API}/appointments?summary=true&start_date=${fmt(winStart)}&end_date=${fmt(winEnd)}`;
        // Seed the skip-guard so the post-mount refreshAppointments
        // effect doesn't immediately duplicate this exact fetch.
        loadedAppointmentRangeRef.current = { start: winStart, end: winEnd };
      }

      // Dynamic data only - static data is loaded separately via fetchStaticData
      // PERF OPTIMIZATION: Only fetch essential data on login.
      // Notes, dashboard stats, reviews-due, draft plans are deferred to their tabs.
      const clientRequestTimeout = 30000; // 30 second timeout for clients
      const promises = [
        axios.get(`${API}/clients?${clientParams.toString()}&summary=true`, { timeout: clientRequestTimeout }).catch(e => { 
          console.error('[PERF] Failed: clients', e.message); 
          if (e.code === 'ECONNABORTED') {
            console.error('[PERF] Client request timed out - dataset may be too large');
          }
          return { data: null, _failed: true }; // Mark as failed, not empty
        }),
        axios.get(appointmentsUrl).catch(e => { console.error('[PERF] Failed: appointments', e.message); return { data: [] }; }),
        // Notes deferred to Notes tab — start with empty, refreshNotes() called when tab opens
        Promise.resolve({ data: [], _deferred: true }),
        axios.get(`${API}/my-todo`).catch(e => { console.error('[PERF] Failed: my-todo', e.message); return { data: [], _failed: true }; }),
        axios.get(`${API}/alerts/unread-counts`).catch(e => { console.error('[PERF] Failed: alerts/unread-counts', e.message); return { data: {} }; }),
        // Dashboard stats deferred — refreshStats() called when Dashboard tab opens
        Promise.resolve({ data: {}, _deferred: true }),
        // Treatment plan reviews deferred — fetched when Dashboard renders
        Promise.resolve({ data: [], _deferred: true }),
        // Firm-calendar events for the next 30 days so they can appear in the
        // "Upcoming Appointments" overview alongside real appointments.
        axios.get(`${API}/firm-calendar/events`, {
          params: {
            start: new Date().toISOString().slice(0, 10),
            end: new Date(Date.now() + 30 * 86400e3).toISOString().slice(0, 10),
          },
        }).catch(e => { console.error('[PERF] Failed: firm-calendar', e.message); return { data: { events: [] } }; }),
        // Own upcoming schedule for the dashboard "Upcoming Appointments"
        // widget (Jun 2026).  The shared appointments fetch above uses a
        // tight 3-day window; this dedicated per-user 30-day summary
        // fetch feeds the widget and is tiny + tenant/role-scoped
        // server-side.  Includes appointments the user is INVITED to
        // (invited_user_ids), not just ones they own.  Fetched for all
        // staff — admins merge it with org internal events below.
        axios.get(`${API}/appointments`, {
              params: {
                provider_id: user.id,
                start_date: new Date().toISOString().slice(0, 10),
                end_date: new Date(Date.now() + 30 * 86400e3).toISOString().slice(0, 10),
                summary: true,
              },
            }).catch(e => { console.error('[PERF] Failed: own-upcoming', e.message); return { data: [] }; }),
      ];

      // Draft plans deferred — fetched when Dashboard tab opens
      // (Previously fetched on every login for all providers)

      const responses = await Promise.all(promises);
      
      // CRITICAL: Check if this request is still the latest one
      // If a newer request was started, discard these results to prevent race conditions
      if (currentRequestId !== fetchRequestIdRef.current) {
        void 0 && console.log(`[PERF] Discarding stale response (request #${currentRequestId}, current #${fetchRequestIdRef.current})`);
        return;
      }
      
      // Process dynamic data responses
      // Handle new paginated response format
      const clientsResponse = responses[0];
      const clientsFailed = clientsResponse._failed === true;
      
      void 0 && console.log('[CLIENTS DEBUG] Raw response:', JSON.stringify(clientsResponse).substring(0, 500));
      
      // Handle both old format (array) and new format (object with clients + pagination)
      let newClients = [];
      let paginationData = null;
      
      if (clientsResponse.data) {
        void 0 && console.log('[CLIENTS DEBUG] Response data type:', typeof clientsResponse.data, Array.isArray(clientsResponse.data) ? 'isArray' : 'isObject');
        if (Array.isArray(clientsResponse.data)) {
          // Old format - just an array of clients
          newClients = clientsResponse.data;
          void 0 && console.log('[CLIENTS DEBUG] Old format - array of', newClients.length, 'clients');
        } else if (clientsResponse.data.clients) {
          // New paginated format
          newClients = clientsResponse.data.clients || [];
          paginationData = clientsResponse.data.pagination;
          void 0 && console.log('[CLIENTS DEBUG] New format - paginated,', newClients.length, 'clients, pagination:', paginationData);
        } else {
          void 0 && console.log('[CLIENTS DEBUG] Unknown format - keys:', Object.keys(clientsResponse.data));
        }
      } else {
        void 0 && console.log('[CLIENTS DEBUG] No data in response');
      }
      
      void 0 && console.log(`[PERF] Request #${currentRequestId} - Clients API: ${clientsFailed ? 'FAILED' : 'success'}, received ${newClients.length} clients${paginationData ? ` (page ${paginationData.page}/${paginationData.total_pages}, total: ${paginationData.total_count})` : ''}`);
      
      setClients(prev => {
        // If API call failed, always preserve existing data
        if (clientsFailed) {
          console.warn(`[PERF] Request #${currentRequestId} - Clients API failed, preserving ${prev.length} existing clients`);
          return prev;
        }
        
        // Check if this request is still the latest
        if (currentRequestId !== fetchRequestIdRef.current) {
          void 0 && console.log(`[PERF] Request #${currentRequestId} is stale (current is #${fetchRequestIdRef.current}), ignoring response`);
          return prev;
        }
        
        // Always accept the API response - even if empty (could be legitimate filter result)
        return newClients;
      });
      
      // Update pagination state if we have pagination data
      // Only update if this is still the current request
      if (paginationData && currentRequestId === fetchRequestIdRef.current) {
        setClientsTotalCount(paginationData.total_count);
        setClientsTotalPages(paginationData.total_pages);
        
        // If cache was refreshed while on page 2+, sync the page number
        // This handles the case where user stayed on page for a while and cache expired
        if (paginationData.cache_refreshed && paginationData.page !== clientsCurrentPage) {
          void 0 && console.log(`[CACHE] Cache refreshed - syncing page from ${clientsCurrentPage} to ${paginationData.page}`);
          setClientsCurrentPage(paginationData.page);
        }
      }
      
      // Update last updated timestamp
      if (currentRequestId === fetchRequestIdRef.current) {
        setClientsLastUpdated(new Date());
      }
      
      // Smart appointment update: only replace if fetching full range, 
      // otherwise merge to avoid losing calendar data
      const newAppointments = responses[1].data || [];
      if (shouldFetchFullAppointments) {
        setAppointments(newAppointments);
      } else {
        setAppointments(prev => {
          const newIds = new Set(newAppointments.map(a => a.id));
          const existingOutsideWindow = prev.filter(a => !newIds.has(a.id));
          return [...newAppointments, ...existingOutsideWindow];
        });
      }
      
      // Only update notes if the API call succeeded - preserve existing on failure
      if (!responses[2]._failed) {
        setNotes(responses[2].data || []);
      } else {
        console.warn('[PERF] Notes API failed, preserving existing data');
      }
      
      // Only update myTodoNotes if the API call succeeded - preserve existing on failure
      // This prevents tasks from disappearing when client filters change
      if (!responses[3]._failed) {
        setMyTodoNotes(responses[3].data || []);
      } else {
        console.warn('[PERF] my-todo API failed, preserving existing tasks');
      }
      
      setUnreadAlertCounts(responses[4].data || {});
      setStats(responses[5].data || {});
      // Tasks are now fetched on-demand when Notes tab is opened
      setTreatmentPlansDueForReview(responses[6]?.data || []);
      
      // Draft plans are no longer fetched here - deferred to dashboard tab

      // Build synthetic "appointment" objects for firm-calendar events (closures,
      // meetings, holidays) so they surface in the Upcoming Appointments widget.
      // Uses 9AM local for all-day events so they sort naturally at the start
      // of the day (or specific start_time when set). Shape matches what the
      // widget's `appointment_type === 'firm_event'` branch expects.
      const firmEventsRaw = responses[7]?.data?.events || [];
      const synthFirmEvents = firmEventsRaw
        .filter(ev => !ev._is_shadow)  // skip weekend-shadow chips
        .map(ev => {
          const ymd = ev.observe_weekend_shift && ev._actual_date
            ? ev.occurrence_date   // already the observed (shifted) date
            : ev.occurrence_date;
          const [y, m, d] = ymd.split('-').map(Number);
          const startT = ev.all_day ? '09:00' : (ev.start_time || '09:00');
          const endT = ev.all_day ? '17:00' : (ev.end_time || '10:00');
          const [sh, sm] = startT.split(':').map(Number);
          const [eh, em] = endT.split(':').map(Number);
          const startDt = new Date(y, m - 1, d, sh, sm);
          const endDt = new Date(y, m - 1, d, eh, em);
          return {
            id: `firm-${ev.id}-${ev.occurrence_date}`,
            appointment_type: 'firm_event',
            title: ev.title,
            description: ev.description,
            start_time: startDt.toISOString(),
            end_time: endDt.toISOString(),
            status: 'scheduled',
            all_day: !!ev.all_day,
            blocks_booking: !!ev.blocks_booking,
            firm_event_color: ev.color || '#0ea5e9',
            firm_event_type: ev.type_name,
            is_observed: !!ev._is_observed,
          };
        });

      // Find next appointment and build upcoming list.
      // Jun 2026 fix — providers reported an empty "Upcoming Appointments"
      // widget: (a) the data branch only ran for the exact role name
      // 'provider' even though the widget renders for supervisor/intern/
      // reception too (and tenants rename provider roles), and (b) it fed
      // off the 3-day shared window.  Now widget-eligible users get their
      // own dedicated 30-day feed (responses[8]).
      const _isOfficeOrAdmin = isSuperAdmin(user) || userRoleName === 'office';
      const _widgetEligible = userRoleName === 'provider' || user?.is_provider ||
        ['supervisor', 'intern', 'reception'].includes(userRoleName);
      if (_widgetEligible && !_isOfficeOrAdmin) {
        const userAppointments = (responses[8]?.data || []).filter(apt =>
          (apt.provider_id === user.id || (apt.invited_user_ids || []).includes(user.id)) &&
          new Date(apt.start_time) > new Date() &&
          apt.status !== 'cancelled'
        );
        // Merge firm events (future only)
        const combined = [...userAppointments, ...synthFirmEvents.filter(e => new Date(e.start_time) > new Date())]
          .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

        setUpcomingAppointments(combined.slice(0, 10));
        setNextAppointment(combined.length > 0 ? combined[0] : null);
      } else if (_isOfficeOrAdmin) {
        // Admin/office: own + invited appointments (30-day dedicated feed)
        // merged with near-term internal events from the shared window.
        // Jun 2026 — invited appointments (e.g. a provider's personal
        // time the admin accepted an invite to) now show here too.
        const ownOrInvited = (responses[8]?.data || []).filter(apt =>
          (apt.provider_id === user.id || (apt.invited_user_ids || []).includes(user.id)) &&
          new Date(apt.start_time) > new Date() &&
          apt.status !== 'cancelled'
        );
        const internalEvents = (responses[1].data || []).filter(apt =>
          apt.provider_id === user.id &&
          new Date(apt.start_time) > new Date() &&
          apt.status !== 'cancelled' &&
          ['internal', 'supervision', 'block_time', 'vacation', 'sick_day', 'meeting'].includes(apt.appointment_type)
        );
        const _seen = new Map();
        [...ownOrInvited, ...internalEvents].forEach(a => _seen.set(a.id, a));
        const combined = [..._seen.values(), ...synthFirmEvents.filter(e => new Date(e.start_time) > new Date())]
          .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

        setUpcomingAppointments(combined.slice(0, 10));
        setNextAppointment(combined.length > 0 ? combined[0] : null);
      }
      
      void 0 && console.log('[PERF] Dynamic data loaded successfully');
      
      // Update health status to success with counts
      setDataHealth(prev => ({
        ...prev,
        dynamicData: 'success',
        dynamicDetails: {
          clients: responses[0]?.data?.length || 0,
          appointments: responses[1]?.data?.length || 0,
          notes: responses[2]?.data?.length || 0
        }
      }));
    } catch (error) {
      console.error('[PERF] Failed to fetch dynamic data:', error);
      setDataHealth(prev => ({ ...prev, dynamicData: 'error', failedEndpoints: [...prev.failedEndpoints, 'dynamic'] }));
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
      setClientsLoading(false);
      // Mark that dashboard has loaded at least once - prevents full-screen loader on navigation
      dashboardHasLoadedOnce = true;
    }
  }, [user, clientStatusFilter, clientProviderFilter, selectedLabelFilter, selectedInsuranceFilter, clientSearch, clientsCurrentPage, clientsPerPage, clientsSortColumn, clientsSortDirection, notesDateRange, notesCustomStartDate, notesCustomEndDate, notesClientFilter, notesStatusFilter, setClientsLoading]);

  // ── Targeted fetchers (Phase 1 perf optimization) ──────────────────────────
  // These replace full fetchData() calls where only one dataset changed.
  // fetchData() is kept for login, initial load, and filter changes.

  const refreshClients = useCallback(async (opts = {}) => {
    if (!user?.id) return;
    try {
      // Query-string is built by the shared helper so this path and
      // refreshDynamicData below stay in lock-step on include/exclude
      // semantics, sub-plan expansion, and pagination/sort.
      const params = buildClientQueryParams({
        refresh: opts.refresh,
        clientStatusFilter,
        clientStatusFilterMode,
        clientProviderFilter,
        clientProviderFilterMode,
        selectedLabelFilter,
        selectedLabelFilterMode,
        selectedInsuranceFilter,
        selectedInsuranceFilterMode,
        clientSearch,
        clientsCurrentPage,
        clientsPerPage,
        clientsSortColumn,
        clientsSortDirection,
        userId: user?.id,
        users,
        clientLabels,
        insurances,
        CLIENT_STATUSES,
      });
      params.append('summary', 'true');

      const res = await axios.get(`${API}/clients?${params.toString()}`);
      if (Array.isArray(res.data)) {
        setClients(res.data);
      } else if (res.data?.clients) {
        setClients(res.data.clients);
        if (res.data.pagination) {
          setClientsTotalCount(res.data.pagination.total_count);
          setClientsTotalPages(res.data.pagination.total_pages);
        }
      }
      setClientsLastUpdated(new Date());

      // Surgical Sync: If a client chart is open and caller requested it,
      // re-fetch the FULL client record (not the summary from the list)
      if (opts.syncOpenChart && selectedClientIdRef.current) {
        try {
          const fullRes = await axios.get(`${API}/clients/${selectedClientIdRef.current}`);
          setSelectedClient(fullRes.data);
        } catch (syncErr) {
          // Non-critical — chart keeps current data
          console.warn('[refreshClients] syncOpenChart failed:', syncErr.message);
        }
      }
    } catch (e) {
      console.error('[refreshClients] Failed:', e.message);
    }
  }, [user?.id, clientStatusFilter, clientStatusFilterMode, clientProviderFilter, clientProviderFilterMode, selectedLabelFilter, selectedLabelFilterMode, selectedInsuranceFilter, selectedInsuranceFilterMode, clientSearch, clientsCurrentPage, clientsPerPage, clientsSortColumn, clientsSortDirection, users, clientLabels, insurances]);

  const refreshNotes = useCallback(async () => {
    if (!user?.id) return;
    try {
      const params = new URLSearchParams();
      if (notesDateRange === 'custom' && notesCustomStartDate && notesCustomEndDate) {
        params.append('start_date', notesCustomStartDate);
        params.append('end_date', notesCustomEndDate);
      } else if (notesDateRange !== 'all') {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(notesDateRange));
        params.append('start_date', startDate.toISOString().split('T')[0]);
      }
      if (notesClientFilter && notesClientFilter !== 'all') params.append('client_id', notesClientFilter);
      if (notesStatusFilter && notesStatusFilter !== 'all') params.append('status', notesStatusFilter);
      params.append('summary', 'true');
      const res = await axios.get(`${API}/notes?${params.toString()}`);
      setNotes(res.data || []);
    } catch (e) {
      console.error('[refreshNotes] Failed:', e.message);
    }
  }, [user?.id, notesDateRange, notesCustomStartDate, notesCustomEndDate, notesClientFilter, notesStatusFilter]);

  // Feb 2026 perf fix — "everything is slow after clicking Schedule".
  //
  // The old `refreshAppointments(true)` path fetched a 15-MONTH window
  // (-12 → +3 months around calendarDate).  For a busy practice that's
  // ~22,500 appointments dropped into a single React state array, which
  // (a) holds enormous memory pressure, (b) cascades re-renders to
  // every consumer of `appointments`, and (c) re-fires whenever
  // calendarDate moves.  The result was the Schedule tab poisoning
  // every other tab the user navigated to afterward.
  //
  // New strategy — lazy windowed fetch:
  //   * Fetch a 6-week window centered on the visible month (~3 weeks
  //     before / 3 weeks after).  Calendar grids show 4-6 weeks at
  //     a time, so this matches the actually-visible data exactly.
  //   * Track the currently-loaded range in `loadedAppointmentRangeRef`.
  //   * On `calendarDate` change, only refetch if the new visible
  //     window falls OUTSIDE the loaded range.  Navigating one month
  //     forward inside the same loaded buffer = zero new fetches.
  //   * Historical / year-end review users navigate the calendar to
  //     the month they want; the fetch lazy-expands to fit.  No more
  //     "preload everything upfront just in case" behavior.
  const loadedAppointmentRangeRef = useRef({ start: null, end: null });

  const refreshAppointments = useCallback(async (full = false) => {
    if (!user?.id) return;
    try {
      const shouldFull = full || ['appointments', 'calendar', 'schedule'].includes(activeTabRef.current);
      let url = `${API}/appointments?summary=true`;
      if (!shouldFull) {
        const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
        const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
        url = `${API}/appointments?start_date=${yesterday}&end_date=${tomorrow}&summary=true`;
      } else {
        // Compute the anchor MONTH's full visible grid window:
        // 1st of month −7 days (previous-month preview row) through
        // end of month +14 days (next-month preview row).  This is
        // the exact range a month grid can render, and week/day views
        // are always inside it.  The old ±21-days-around-anchor
        // window clipped the end of the month whenever the anchor sat
        // near the 1st (month navigation keeps the day-of-month, and
        // jump-to-date can land anywhere) — late-month appointments
        // silently vanished from the grid.
        const anchor = calendarDate || new Date();
        const winStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        winStart.setDate(winStart.getDate() - 7);
        const winEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
        winEnd.setDate(winEnd.getDate() + 14);

        // Skip refetch if the visible window is already inside the
        // currently-loaded range.  Avoids the cascade of double fires
        // when calendarDate nudges by a day (recurrence picker, etc.)
        // or when activeTab toggles but the window hasn't actually
        // moved.
        const loaded = loadedAppointmentRangeRef.current;
        if (loaded.start && loaded.end &&
            winStart >= loaded.start && winEnd <= loaded.end) {
          return;
        }

        const fmt = (d) => d.toISOString().split('T')[0];
        url = `${API}/appointments?summary=true&start_date=${fmt(winStart)}&end_date=${fmt(winEnd)}`;
        loadedAppointmentRangeRef.current = { start: winStart, end: winEnd };
      }
      const res = await axios.get(url);
      setAppointments(res.data || []);
    } catch (e) {
      console.error('[refreshAppointments] Failed:', e.message);
    }
  }, [user?.id, calendarDate]);

  const refreshStats = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setStats(res.data || {});
    } catch (e) {
      console.error('[refreshStats] Failed:', e.message);
    }
  }, [user?.id]);

  // Jun 2026 — practice-wide stats now live in the Admin portal overview;
  // the stats fetch is deferred off the login path, so load them when the
  // admin tab opens.
  useEffect(() => {
    if (activeTab === 'admin' && user?.id) {
      refreshStats();
    }
  }, [activeTab, user?.id, refreshStats]);

  const refreshAlerts = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/alerts/unread-counts`);
      setUnreadAlertCounts(res.data || {});
    } catch (e) {
      console.error('[refreshAlerts] Failed:', e.message);
    }
  }, [user?.id]);

  const refreshTodos = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/my-todo`);
      setMyTodoNotes(res.data || []);
    } catch (e) {
      console.error('[refreshTodos] Failed:', e.message);
    }
  }, [user?.id]);

  // Effect to refresh data when urgent waitlist is cleared via WebSocket
  useEffect(() => {
    if (urgentWaitlistClearedTrigger > 0 && user) {
      void 0 && console.log('[WebSocket] Refreshing data after urgent waitlist cleared');
      refreshClients(); refreshStats(); refreshAlerts();
      fetchNotifications();
    }
  }, [urgentWaitlistClearedTrigger]);

  // Fetch password reset requests (admin only)
  const fetchPasswordResetRequests = useCallback(async () => {
    if (!isSuperAdmin(user)) return;
    
    try {
      void 0 && console.log('Fetching password reset requests...');
      const response = await axios.get(`${API}/admin/password-reset-requests`);
      const data = response?.data || [];
      void 0 && console.log('Password reset requests:', data);
      void 0 && console.log('Total requests:', data.length);
      void 0 && console.log('Pending requests:', data.filter(r => r.status === 'pending').length);
      setPasswordResetRequests(data);
    } catch (error) {
      console.error("Failed to fetch password reset requests:", error);
    }
  }, [user]);

  // Poll for password reset requests every 5 minutes if admin
  //
  // Feb 2026 — reduced from 30s → 5 min.  The 30s cadence was hammering
  // the backend Motor pool for what is, in practice, a slow-moving admin
  // queue (password resets are typed by humans, not generated by a
  // pipeline).  Notifications still flow through the WebSocket + 30s
  // /notifications poll so admins still see new requests in near
  // real-time via the bell — this dedicated poll only refreshes the
  // sidebar Pending count and the admin-tab list.
  useEffect(() => {
    if (isSuperAdmin(user)) {
      fetchPasswordResetRequests();
      const interval = setInterval(() => {
        if (!document.hidden && !isEditingDialogOpenRef.current && !isUserBusy()) fetchPasswordResetRequests();
      }, 300000); // 5 minutes
      return () => clearInterval(interval);
    }
  }, [user, fetchPasswordResetRequests]);

  // Fetch incomplete documentation deadlines for providers
  const fetchIncompleteDocumentation = useCallback(async () => {
    if (!user?.is_provider && userRoleName !== 'provider' && !isSuperAdmin(user)) return;
    
    try {
      setLoadingDocDeadlines(true);
      const response = await axios.get(`${API}/documentation-deadlines/my-incomplete`);
      setIncompleteDocumentation(response.data || []);
    } catch (error) {
      console.warn('Failed to fetch incomplete documentation:', error);
      setIncompleteDocumentation([]);
    } finally {
      setLoadingDocDeadlines(false);
    }
  }, [user]);
  
  // Fetch incomplete documentation on login and periodically (every 5 minutes)
  useEffect(() => {
    if (user?.is_provider || userRoleName === 'provider' || isSuperAdmin(user)) {
      fetchIncompleteDocumentation();
      const interval = setInterval(() => {
        if (!document.hidden && !isEditingDialogOpenRef.current && !isUserBusy()) fetchIncompleteDocumentation();
      }, 300000); // 5 minutes
      return () => clearInterval(interval);
    }
  }, [user, fetchIncompleteDocumentation]);

  // (Jun 2026) Scheduled document reviews (CANS, Comp Assessments, etc.)
  // moved to the V2 Tasks panel — the old fetch here stored results in
  // state that was never rendered.  See TasksPanel.jsx source #6.

  // Fetch expiring releases for providers
  const fetchExpiringReleases = useCallback(async () => {
    if (!user?.is_provider && userRoleName !== 'provider' && !isSuperAdmin(user)) return;
    
    try {
      setLoadingExpiringReleases(true);
      const response = await axios.get(`${API}/releases/expiring-soon`);
      setExpiringReleases(response.data || []);
    } catch (error) {
      console.warn('Failed to fetch expiring releases:', error);
      setExpiringReleases([]);
    } finally {
      setLoadingExpiringReleases(false);
    }
  }, [user]);
  
  // Fetch expiring releases on login and periodically (every 10 minutes)
  useEffect(() => {
    if (user?.is_provider || userRoleName === 'provider' || isSuperAdmin(user)) {
      fetchExpiringReleases();
      const interval = setInterval(() => {
        if (!document.hidden && !isEditingDialogOpenRef.current && !isUserBusy()) fetchExpiringReleases();
      }, 600000); // 10 minutes
      return () => clearInterval(interval);
    }
  }, [user, fetchExpiringReleases]);

  // Fetch routing rules
  const fetchRoutingRules = useCallback(async () => {
    if (!isSuperAdmin(user)) return;
    
    try {
      const response = await axios.get(`${API}/routing-rules`);
      setRoutingRules(response.data);
    } catch (error) {
      console.error('Failed to fetch routing rules:', error);
    }
  }, [user]);

  const fetchReminderSettings = useCallback(async () => {
    if (!isSuperAdmin(user)) return;
    
    try {
      const response = await axios.get(`${API}/reminder-settings`);
      setReminderSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch reminder settings:', error);
    }
  }, [user]);

  const fetchPendingGroupRequests = useCallback(async () => {
    if (!isSuperAdmin(user) && !user?.group_admin) return;
    try {
      const response = await axios.get(`${API}/group-admission-requests?status=pending`);
      setPendingGroupRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch pending group requests:", error);
    }
  }, [user]);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/organizations`);
      setOrganizations(response.data);
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
      toast.error("Failed to load organizations");
    }
  }, []);

  const fetchInsurances = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/insurances`);
      setInsurances(response.data);
    } catch (error) {
      console.error("Failed to fetch insurances:", error);
      toast.error("Failed to load insurances");
    }
  }, []);
  
  const fetchCurricula = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/curricula`);
      setCurricula(response.data);
    } catch (error) {
      console.error("Failed to fetch curricula:", error);
    }
  }, []);
  
  // Fetch curricula when user logs in (for group curriculum assignment)
  //
  // Feb 2026 — gated to ONLY fire when the Groups tab is open, or
  // when a group editor dialog is up.  Curricula are not needed on
  // dashboard load.  The shared `ensureCurriculaLoaded` ref-gate
  // (set above) handles dedup so this effect is safe.
  useEffect(() => {
    if (!user) return;
    if (activeTab !== 'groups') return;
    if (!(isSuperAdmin(user) || userRoleName === 'provider' || user?.is_provider)) return;
    fetchCurricula();
  }, [user, activeTab, userRoleName, fetchCurricula]);
  
  // Fetch curriculum session data when a group is selected for note creation
  const fetchCurriculumSessionForGroup = useCallback(async (groupId) => {
    if (!groupId) {
      setCurriculumSessionData(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/client-groups/${groupId}/current-session`);
      if (response.data.session_data) {
        setCurriculumSessionData(response.data);
      } else {
        setCurriculumSessionData(null);
      }
    } catch (error) {
      console.error("Failed to fetch curriculum session:", error);
      setCurriculumSessionData(null);
    }
  }, []);
  
  const fetchRoles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      toast.error("Failed to load roles");
    }
  }, []);

  const fetchIntakeFormConfig = useCallback(async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/config/intake-form`);
      setIntakeFormConfig(response.data);
    } catch (error) {
      console.error("Failed to fetch intake form config:", error);
      // Retry silently up to 2 times before showing error toast
      if (retryCount < 2) {
        void 0 && console.log('[IntakeFormConfig] Retrying...', retryCount + 1);
        setTimeout(() => fetchIntakeFormConfig(retryCount + 1), 1000);
      } else {
        toast.error("Failed to load form configuration");
      }
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/permissions`);
      setAvailablePermissions(response.data);
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      toast.error("Failed to load permissions");
    }
  }, []);


  const fetchClientAlerts = useCallback(async (clientId) => {
    if (!clientId) return;
    try {
      setLoadingAlerts(true);
      const response = await axios.get(`${API}/clients/${clientId}/alerts`);
      setClientAlerts(response.data);
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
      toast.error("Failed to load alerts");
    } finally {
      setLoadingAlerts(false);
    }
  }, []);

  // Medication Management fetch functions
  // Jun 2026 — tolerant match: the template may hold the legacy slug
  // 'medication_referral' OR the display name 'Medication Referral'
  // (the only value selectable in the form builder's type dropdown).
  const isMedReferralTemplate = useCallback((t) =>
    t.is_active && (t.form_type || '').toLowerCase().replace(/[\s_]+/g, '_') === 'medication_referral', []);
  const fetchMedicationReferralTemplate = useCallback(async () => {
    // First check if we already have formTemplates loaded
    if (formTemplates && formTemplates.length > 0) {
      const medTemplate = formTemplates.find(isMedReferralTemplate);
      if (medTemplate) {
        setMedicationReferralTemplate(medTemplate);
        return;
      }
    }
    // Only fetch if not already loaded
    try {
      const response = await axios.get(`${API}/form-templates`);
      // Look for medication referral template
      const medTemplate = response.data.find(isMedReferralTemplate);
      if (medTemplate) {
        setMedicationReferralTemplate(medTemplate);
      } else {
        console.warn("No active medication referral template found");
      }
    } catch (error) {
      console.error("Failed to fetch medication referral template:", error);
    }
  }, [formTemplates, isMedReferralTemplate]);

  const fetchMedicationReferralRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/medication-referral-requests`);
      setMedicationReferralRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch medication referral requests:", error);
      toast.error("Failed to load medication referral requests");
    }
  }, []);

  const fetchMedicationManagers = useCallback(async () => {
    // First check if we already have users loaded
    if (users && users.length > 0) {
      const managers = users.filter(u => u.is_medication_manager && u.is_provider && u.is_prescriber);
      setMedicationManagerUsers(managers);
      return;
    }
    // Only fetch if not already loaded
    try {
      const response = await axios.get(`${API}/users?include_archived=false`);
      // Filter for users who are medication managers AND prescribers
      const managers = response.data.filter(u => u.is_medication_manager && u.is_provider && u.is_prescriber);
      setMedicationManagerUsers(managers);
    } catch (error) {
      console.error("Failed to fetch medication managers:", error);
    }
  }, [users]);

  const fetchBrandingSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/branding-settings`);
      void 0 && console.log('📌 Branding settings fetched:', response.data);
      // Only update if we got valid data, otherwise keep defaults
      if (response.data && typeof response.data === 'object') {
        setBrandingSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch branding settings:", error);
      // Keep default branding settings on error
    }
  }, []);

  // Fetch super admin count for "Last Man Standing" protection
  const fetchSuperAdminCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/super-admin-count`);
      setSuperAdminCount(response.data.count || 0);
      void 0 && console.log('👤 Super admin count:', response.data.count);
    } catch (error) {
      console.error("Failed to fetch super admin count:", error);
    }
  }, []);

  // Fetch 2FA organization settings (Admin only)
  const fetch2FASettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/2fa-settings`);
      setTwoFAOrgSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch 2FA settings:", error);
    }
  }, []);

  // Fetch Audit Settings (Super Admin only - HIPAA)
  const fetchAuditSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/audit/settings`);
      setAuditSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch audit settings:", error);
    }
  }, []);

  // Fetch Domain Whitelist Settings (Admin only)
  const fetchDomainWhitelistSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/domain-whitelist-settings`);
      setDomainWhitelistSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch domain whitelist settings:", error);
    }
  }, []);

  // Fetch Beta Gate Settings (Admin only)
  const fetchBetaGateSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/beta/status`);
      setBetaGateSettings({
        beta_enabled: response.data.beta_enabled || false,
        beta_message: response.data.beta_message || 'Please enter your invite code to continue.'
      });
    } catch (error) {
      console.error("Failed to fetch beta gate settings:", error);
    }
  }, []);

  // Toggle Beta Gate (Admin only)
  const handleToggleBetaGate = async (enabled) => {
    setBetaGateLoading(true);
    try {
      await axios.post(`${API}/beta/settings`, {
        beta_enabled: enabled,
        beta_message: betaGateSettings.beta_message
      });
      setBetaGateSettings(prev => ({ ...prev, beta_enabled: enabled }));
      toast.success(enabled ? 'Beta gate enabled - users will need an invite code' : 'Beta gate disabled - all users can access login');
    } catch (error) {
      console.error("Failed to update beta gate settings:", error);
      toast.error("Failed to update beta gate settings");
    } finally {
      setBetaGateLoading(false);
    }
  };

  // Fetch Billing Settings (Admin only)
  const fetchBillingSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/billing/settings`);
      setBillingSettings({
        billing_enabled: response.data.billing_enabled ?? true
      });
    } catch (error) {
      console.error("Failed to fetch billing settings:", error);
    }
  }, []);

  // Fetch Google Calendar Integration Status
  const fetchGoogleCalendarStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/integrations/google-calendar/status`);
      setGoogleCalendarStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch Google Calendar status:", error);
      setGoogleCalendarStatus({ status: 'error', is_mock: true });
    }
  }, []);

  // Fetch Backup Status (Admin only)
  const fetchBackupStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/health`);
      setBackupStatus({
        status: 'loaded',
        last_backup: response.data.last_backup,
        backup_status: response.data.backup_status || 'unknown'
      });
    } catch (error) {
      console.error("Failed to fetch backup status:", error);
      setBackupStatus({ status: 'error', last_backup: null, backup_status: 'error' });
    }
  }, []);

  // Toggle Billing Features (Admin only)
  const handleToggleBilling = async (enabled) => {
    setBillingSettingsLoading(true);
    try {
      await axios.put(`${API}/billing/settings`, {
        billing_enabled: enabled
      });
      setBillingSettings(prev => ({ ...prev, billing_enabled: enabled }));
      toast.success(enabled ? 'Billing features enabled' : 'Billing features disabled - submission and reminders are turned off');
    } catch (error) {
      console.error("Failed to update billing settings:", error);
      toast.error("Failed to update billing settings");
    } finally {
      setBillingSettingsLoading(false);
    }
  };

  // Fix Legacy Template Form Types (Admin only)
  const handleFixLegacyTemplates = async () => {
    setLegacyTemplateFixLoading(true);
    setLegacyTemplateFixResult(null);
    try {
      const response = await axios.post(`${API}/admin/fix-legacy-templates`);
      setLegacyTemplateFixResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Failed to fix legacy templates:", error);
      toast.error("Failed to fix legacy templates: " + (error.response?.data?.detail || error.message));
      setLegacyTemplateFixResult({ error: error.response?.data?.detail || error.message });
    } finally {
      setLegacyTemplateFixLoading(false);
    }
  };

  // Handle bulk completion of legacy notes (adds signatures, sets status to completed)
  const handleBulkCompleteLegacyNotes = async () => {
    setLegacyNotesCompletionLoading(true);
    setLegacyNotesCompletionResult(null);
    setLegacyNotesCompletionStatus(null);
    try {
      const response = await axios.post(`${API}/notes/bulk-complete-legacy`);
      setLegacyNotesCompletionResult(response.data);
      if (response.data.status === 'processing') {
        toast.success(`Started processing ${response.data.total_count} legacy notes in background`);
        // Start polling for status
        pollLegacyNotesCompletionStatus();
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error("Failed to start legacy notes completion:", error);
      toast.error("Failed to start legacy notes completion: " + (error.response?.data?.detail || error.message));
      setLegacyNotesCompletionResult({ error: error.response?.data?.detail || error.message });
    } finally {
      setLegacyNotesCompletionLoading(false);
    }
  };

  // Poll for legacy notes completion status
  const pollLegacyNotesCompletionStatus = async () => {
    try {
      const response = await axios.get(`${API}/notes/bulk-complete-legacy/status`);
      setLegacyNotesCompletionStatus(response.data);
      
      if (response.data.status === 'processing') {
        // Continue polling every 3 seconds
        setTimeout(pollLegacyNotesCompletionStatus, 3000);
      } else if (response.data.status === 'completed') {
        toast.success(`Completed! Updated ${response.data.updated_count} legacy notes`);
      } else if (response.data.status === 'failed') {
        toast.error('Legacy notes completion failed: ' + response.data.error);
      }
    } catch (error) {
      console.error("Failed to fetch legacy notes completion status:", error);
    }
  };

  // Check legacy notes completion status on load (if there was a previous run)
  const checkLegacyNotesCompletionStatus = async () => {
    try {
      const response = await axios.get(`${API}/notes/bulk-complete-legacy/status`);
      if (response.data.status && response.data.status !== 'no_operation') {
        setLegacyNotesCompletionStatus(response.data);
        if (response.data.status === 'processing') {
          pollLegacyNotesCompletionStatus();
        }
      }
    } catch (error) {
      // Silently ignore - no previous operation
    }
  };

  // Fetch Security Dashboard Stats (Super Admin only)
  const fetchSecurityStats = useCallback(async (days = 7) => {
    try {
      const response = await axios.get(`${API}/admin/audit/stats?days=${days}`);
      setSecurityStats(response.data);
    } catch (error) {
      console.error("Failed to fetch security stats:", error);
    }
  }, []);

  // Fetch Security Audit Logs with filters
  const fetchSecurityLogs = useCallback(async (page = 1, filters = {}) => {
    setSecurityLogsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', 25);
      
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.action) params.append('action', filters.action);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      // Handle high-risk filter
      if (filters.highRiskOnly) {
        params.append('action', 'VIEW_SSN,export,export_csv,download,generate_bundle,reveal_ssn');
      }
      
      const response = await axios.get(`${API}/admin/audit/logs?${params.toString()}`);
      setSecurityLogs(response.data.logs || []);
      setSecurityLogsTotal(response.data.total || 0);
      setSecurityLogsPage(page);
    } catch (error) {
      console.error("Failed to fetch security logs:", error);
      setSecurityLogs([]);
    } finally {
      setSecurityLogsLoading(false);
    }
  }, []);

  // Derive the active security-log filters (quick filter + user dropdown)
  const buildSecurityFilters = (overrides = {}) => {
    const base = { filter: securityFilter, userId: securityUserFilter, ...overrides };
    const f = {};
    if (base.filter === 'high-risk') f.highRiskOnly = true;
    else if (base.filter === 'chart-access') f.action = 'view_chart';
    else if (base.filter === 'login') f.action = 'login,login_failed,account_locked,login_2fa_email,login_2fa_totp';
    if (base.userId && base.userId !== 'all') f.user_id = base.userId;
    return f;
  };
  const fetchClientAuditHistory = useCallback(async (clientId, days = 365) => {
    setSecurityClientReportLoading(true);
    try {
      // Use the /full endpoint to get both MongoDB and GCS archive data
      const response = await axios.get(`${API}/admin/audit/logs/client/${clientId}/full?days=${days}`);
      setSecurityClientReport(response.data);
    } catch (error) {
      console.error("Failed to fetch client audit history:", error);
      toast.error("Failed to generate client audit report");
      setSecurityClientReport(null);
    } finally {
      setSecurityClientReportLoading(false);
    }
  }, []);

  // Fetch Archive Stats (Super Admin only)
  const fetchArchiveStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/audit/archive/stats`);
      setArchiveStats(response.data);
    } catch (error) {
      console.error("Failed to fetch archive stats:", error);
    }
  }, []);

  // Fetch Integrity Status (Super Admin only)
  const fetchIntegrityStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/audit/integrity/status`);
      setIntegrityStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch integrity status:", error);
    }
  }, []);

  // Verify Audit Integrity (Super Admin only)
  const verifyAuditIntegrity = useCallback(async (hours = 24) => {
    setIntegrityVerifying(true);
    try {
      const response = await axios.get(`${API}/admin/audit/integrity/verify?hours=${hours}`);
      setIntegrityStatus(prev => ({ ...prev, verification: response.data }));
      if (response.data.valid) {
        toast.success(`Integrity verified: ${response.data.entries_checked} entries checked, no tampering detected`);
      } else {
        toast.error(`INTEGRITY VIOLATION DETECTED: ${response.data.error}`);
      }
      return response.data;
    } catch (error) {
      console.error("Failed to verify integrity:", error);
      toast.error("Failed to verify audit integrity");
    } finally {
      setIntegrityVerifying(false);
    }
  }, []);

  // Fetch Waitlist Settings (Admin only)
  const fetchWaitlistSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/waitlist-settings`);
      setWaitlistSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch waitlist settings:", error);
    }
  }, []);

  // Fetch user's 2FA status (Admin viewing another user)
  const fetchUser2FAStatus = useCallback(async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/2fa-status`);
      setUser2FAStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch user 2FA status:", error);
      toast.error("Failed to fetch 2FA status");
    }
  }, []);

  // Reset 2FA for a user (Admin only)
  const resetUser2FA = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-2fa`);
      toast.success("2FA has been reset for this user");
      fetchUser2FAStatus(userId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset 2FA");
    }
  };

  // Require 2FA setup for a user (Admin only)
  const requireUser2FA = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/require-2fa`);
      toast.success("User will be required to set up 2FA on next login");
      // Update local editingUser state so UI reflects the change immediately
      if (editingUser && editingUser.id === userId) {
        setEditingUser(prev => ({ ...prev, requires_2fa_setup: true }));
      }
      fetchUser2FAStatus(userId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to require 2FA");
    }
  };

  const fetchClientLabels = useCallback(async () => {
    // Jun 2026 — retry with backoff; a silently-failed one-shot fetch
    // left billing-label dots blank until a full reload.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await axios.get(`${API}/client-labels`);
        setClientLabels(response.data);
        return;
      } catch (error) {
        console.error(`Failed to fetch client labels (attempt ${attempt + 1}/3):`, error);
        await new Promise((res) => setTimeout(res, 700 * (attempt + 1)));
      }
    }
  }, []);

  // Bulk Communications Functions
  const fetchBulkCommPreview = useCallback(async (filters) => {
    setLoadingBulkCommPreview(true);
    try {
      const response = await axios.post(`${API}/bulk-communications/preview`, filters, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      setBulkCommPreview(response.data);
    } catch (error) {
      console.error("Failed to fetch bulk comm preview:", error);
      toast.error("Failed to preview recipients");
    } finally {
      setLoadingBulkCommPreview(false);
    }
  }, []);

  const fetchBulkCommHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bulk-communications/history`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      setBulkCommHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch bulk comm history:", error);
    }
  }, []);

  const fetchMessageTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/message-templates`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      setMessageTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch message templates:", error);
    }
  }, []);

  const sendBulkCommunication = async () => {
    if (!bulkCommSubject.trim() || !bulkCommMessage.trim()) {
      toast.error("Please enter a subject and message");
      return;
    }
    if (!bulkCommPreview || bulkCommPreview.total_recipients === 0) {
      toast.error("No recipients match your filters");
      return;
    }

    setSendingBulkComm(true);
    try {
      const response = await axios.post(`${API}/bulk-communications/send`, {
        subject: bulkCommSubject,
        message: bulkCommMessage,
        filters: bulkCommFilters,
        send_email: bulkCommSendEmail,
        send_sms: bulkCommSendSms
      }, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      
      toast.success(response.data.message);
      setShowBulkCommunicationsDialog(false);
      setBulkCommSubject('');
      setBulkCommMessage('');
      setBulkCommSendEmail(false);
      setBulkCommSendSms(false);
      setBulkCommPreview(null);
      fetchBulkCommHistory();
    } catch (error) {
      console.error("Failed to send bulk communication:", error);
      toast.error(error.response?.data?.detail || "Failed to send communication");
    } finally {
      setSendingBulkComm(false);
    }
  };

  const saveMessageTemplate = async () => {
    if (!templateFormData.name.trim() || !templateFormData.subject.trim() || !templateFormData.message.trim()) {
      toast.error("Please fill in all template fields");
      return;
    }

    try {
      if (editingTemplate) {
        await axios.put(`${API}/message-templates/${editingTemplate.id}`, templateFormData, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
        });
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/message-templates`, templateFormData, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
        });
        toast.success("Template created");
      }
      
      setEditingTemplate(null);
      setTemplateFormData({ name: '', subject: '', message: '' });
      fetchMessageTemplates();
    } catch (error) {
      console.error("Failed to save template:", error);
      toast.error("Failed to save template");
    }
  };

  const deleteMessageTemplate = async (templateId) => {
    if (!confirm("Are you sure you want to delete this template?")) return;
    
    try {
      await axios.delete(`${API}/message-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      toast.success("Template deleted");
      fetchMessageTemplates();
    } catch (error) {
      console.error("Failed to delete template:", error);
      toast.error("Failed to delete template");
    }
  };

  const fetchWaitlistClients = useCallback(async () => {
    try {
      // Fetch approved medication referrals that don't have an assigned provider
      const response = await axios.get(`${API}/medication-referral-requests`);
      const approvedWithoutProvider = response.data.filter(r => 
        r.status === 'approved' && 
        (!r.assigned_provider_id || r.assigned_provider_id === null)
      );
      setWaitlistClients(approvedWithoutProvider);
    } catch (error) {
      console.error("Failed to fetch waitlist clients:", error);
    }
  }, []);
  
  // Dedicated fetch for Waitlist tab - doesn't affect Client Management filters
  const fetchDedicatedWaitlistClients = useCallback(async () => {
    if (!user) return;
    
    try {
      setWaitlistLoading(true);
      const response = await axios.get(`${API}/clients`, {
        params: { 
          status_filter: 'pending_intake,corrections_requested,waitlist',
          page_size: 1000,  // Fetch all waitlist clients (server caps summary at 1000)
          summary: true    // Use summary fields for faster response
        }
      });
      // Handle both paginated response {clients: [...]} and direct array response
      const clientsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.clients || []);
      setDedicatedWaitlistClients(clientsData);
      void 0 && console.log('📋 Waitlist clients loaded:', clientsData.length);
    } catch (error) {
      console.error("Failed to fetch waitlist clients:", error);
      setDedicatedWaitlistClients([]);
    } finally {
      setWaitlistLoading(false);
    }
  }, [user]);
  
  const fetchServiceRequestTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/service-request-templates`);
      setServiceRequestTemplates(response.data || []);
    } catch (error) {
      console.error("Failed to fetch service request templates:", error);
      setServiceRequestTemplates([]);
    }
  }, []);

  // Billing fetch functions
  const fetchNotesReadyForBilling = useCallback(async () => {
    if (!user?.is_biller && !user?.is_billing_manager && !isSuperAdmin(user)) {
      return;
    }
    
    setLoadingNotes(true);
    try {
      const response = await axios.get(`${API}/billing/notes-ready`);
      void 0 && console.log("Fetched notes ready for billing:", response.data?.length || 0);
      setNotesReadyForBilling(response.data || []);
    } catch (error) {
      console.error("Failed to fetch notes ready for billing:", error);
      toast.error("Failed to load notes ready for billing");
    } finally {
      setLoadingNotes(false);
    }
  }, [user]);

  const fetchNotesSentForCorrection = useCallback(async () => {
    if (!user?.is_biller && !user?.is_billing_manager && !isSuperAdmin(user)) {
      return;
    }
    
    try {
      // List view: request summary projection (status, doc_number, client_id,
      // dates, counts) — not the 8 KB content blob per note.
      const response = await axios.get(`${API}/notes?status=needs_corrections&limit=1000&start_date=2020-01-01&summary=true`);
      const notes = response.data || [];
      
      // Batch-fetch client info for all correction notes
      const clientIds = [...new Set(notes.map(n => n.client_id).filter(Boolean))];
      const clientMap = {};
      if (clientIds.length > 0) {
        try {
          // Feb 2026 perf fix: was N+1 — one GET /clients/{id} per row,
          // serialized server-side by the Mongo pool (3.3s × 16 rows ≈
          // 53s waterfall in prod logs).  Now a single bulk fetch via
          // the new ids= query param.  Chunk into 200-id batches to
          // match the server's hard cap.
          const chunks = [];
          for (let i = 0; i < clientIds.length; i += 200) {
            chunks.push(clientIds.slice(i, i + 200));
          }
          const responses = await Promise.all(
            chunks.map(chunk =>
              axios
                .get(`${API}/clients?summary=true&ids=${chunk.join(',')}&page_size=200&include_archived=true`)
                .then(r => r.data?.clients || [])
                .catch(() => [])
            )
          );
          responses.flat().forEach(c => { if (c) clientMap[c.id] = c; });
        } catch (e) {
          console.warn('[fetchNotesSentForCorrection] Client batch fetch error:', e);
        }
      }
      
      // Enrich notes with client info
      notes.forEach(note => {
        const client = clientMap[note.client_id];
        if (client) {
          note._client_name = client.full_name || `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Unknown';
          note._client_mrn = client.mrn || '';
          const ins = (client.insurances || []).find(i => i.priority === 1 && i.status === 'active') || (client.insurances || []).find(i => i.status === 'active');
          note._client_insurance = ins?.company || client.primary_insurance || 'Self Pay';
          note._client_is_self_pay = !ins?.company && (!client.primary_insurance || ['Self Pay','SELF PAY','SELF-PAY','NONE',''].includes((client.primary_insurance || '').toUpperCase()));
        }
        // Enrich provider name
        const prov = users.find(u => u.id === note.provider_id) || providers.find(p => p.id === note.provider_id);
        if (prov) note._provider_name = prov.full_name || '';
      });
      
      setNotesSentForCorrection(notes);
    } catch (error) {
      console.error("Failed to fetch notes sent for correction:", error);
    }
  }, [user, users, providers]);

  // fetchClaimsQueue moved to useClaimsQueue hook (instantiated above).

  const fetchCurrentBatch = useCallback(async () => {
    if (!user?.is_billing_manager && !isSuperAdmin(user)) return;
    
    try {
      const response = await axios.get(`${API}/batches/current`);
      setCurrentBatch(response.data.batch);
    } catch (error) {
      console.error("Failed to fetch current batch:", error);
    }
  }, [user]);

  const fetchBatchHistory = useCallback(async () => {
    if (!user?.is_billing_manager && !isSuperAdmin(user)) return;
    
    try {
      // Get first and last day of current month
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      
      const startDate = firstDayOfMonth.toISOString().split('T')[0];
      const endDate = lastDayOfMonth.toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/batches/history`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          per_page: 100
        }
      });
      // Only show closed batches in history (open batch shows in Current Batch section)
      const closedBatches = response.data.batches.filter(b => b.status === 'closed');
      setBatchHistory(closedBatches);
    } catch (error) {
      console.error("Failed to fetch batch history:", error);
    }
  }, [user]);

  const viewBatchDetails = async (batchId) => {
    try {
      const response = await axios.get(`${API}/batches/${batchId}/claims`);
      setViewingBatch(response.data.batch);
      setViewingBatchClaims(response.data.claims);
      setShowBatchDetailsDialog(true);
    } catch (error) {
      console.error("Failed to fetch batch details:", error);
      toast.error("Failed to load batch details");
    }
  };

  // Fetch batch history when billing management tab is opened
  useEffect(() => {
    if (user && activeTab === 'billing-management') {
      fetchBatchHistory();
    }
  }, [user, activeTab, fetchBatchHistory]);

  // Fetch all authorizations when authorization management tab is opened
  const fetchAllAuthorizations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/authorizations`);
      setAllAuthorizations(response.data);
    } catch (error) {
      console.error("Failed to fetch authorizations:", error);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    // Fetch on tab open (for the Auths tab itself) AND on initial
    // login when the user is an auth manager / super admin — so the
    // dashboard To-Do widget ("Auths Expiring ≤14d / 15–30d") can
    // render without requiring the user to visit the Auths tab
    // first.  Without this, the widget would always render as empty
    // on the home view because `allAuthorizations` stayed `[]`.
    const needsAuths = activeTab === 'authorizations'
      || user.is_authorization_manager
      || isSuperAdmin(user);
    if (needsAuths) {
      fetchAllAuthorizations();
      // Feb 2026 — only fetch service types if the user is actively
      // ON the authorizations tab (the Add-Authorization dialog needs
      // them).  Skipping this on dashboard load.
      if (activeTab === 'authorizations' && serviceTypes.length === 0) {
        axios.get(`${API}/treatment-planning/service-types`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
        }).then(res => setServiceTypes(res.data)).catch(e => console.error('Failed to load service types:', e));
      }
    }
  }, [user, activeTab, fetchAllAuthorizations]);

  // Eligibility appointment fetching — moved to EligibilityTab.jsx

  const fetchClaimDetail = useCallback(async (claimId) => {
    try {
      setLoadingClaimDetail(true); // Show loading indicator
      const response = await axios.get(`${API}/claims/${claimId}`);
      setClaimDetail(response.data);
      setShowClaimDetail(true);
    } catch (error) {
      console.error("Failed to fetch claim detail:", error);
      toast.error("Failed to load claim details");
    } finally {
      setLoadingClaimDetail(false); // Hide loading indicator
    }
  }, []);

  const fetchNoteForReview = useCallback(async (noteId) => {
    try {
      // Jun 2026 — assembly logic extracted to utils/noteReview.js so the
      // V2 host (PrepareClaimHostV2) shares the exact same code path.
      const data = await buildNoteReviewData(noteId, { findClientById });
      setNoteReviewData(data);
      setShowNoteReviewDialog(true);
    } catch (error) {
      console.error("Failed to fetch note for review:", error);
      toast.error("Failed to load note details");
    }
  }, [findClientById]);


  // ========== END DATA FETCHING FUNCTIONS ==========

  // Navigation helpers for Prepare Claim dialog (must be after fetchNoteForReview)
  // Mar 2026 — walk `sortedFilteredBillingNotes` (filters + biller's
  // column sort) instead of plain `filteredBillingNotes` so Next/Prev
  // mirror what the biller sees on screen.  Originally the dialog
  // walked filter-only order which surfaced random clinicians.
  const navigateToNextBillingNote = () => {
    const nextIdx = Math.min(currentNoteIndex, sortedFilteredBillingNotes.length - 1);
    if (nextIdx >= 0 && sortedFilteredBillingNotes[nextIdx]) {
      setCurrentNoteIndex(nextIdx);
      setSelectedNoteForReview(sortedFilteredBillingNotes[nextIdx]);
      fetchNoteForReview(sortedFilteredBillingNotes[nextIdx].id);
      setBillingPreviewClaimId(null);
      setDryRunPreviewData(null);
    } else {
      setShowNoteReviewDialog(false);
      setBillingPreviewClaimId(null);
      setDryRunPreviewData(null);
    }
  };

  const navigateToPrevBillingNote = () => {
    const prevIdx = currentNoteIndex > 0 ? currentNoteIndex - 1 : sortedFilteredBillingNotes.length - 1;
    if (sortedFilteredBillingNotes[prevIdx]) {
      setCurrentNoteIndex(prevIdx);
      setSelectedNoteForReview(sortedFilteredBillingNotes[prevIdx]);
      fetchNoteForReview(sortedFilteredBillingNotes[prevIdx].id);
      setBillingPreviewClaimId(null);
      setDryRunPreviewData(null);
    }
  };

  const navigateToNextBillingNoteNoRemove = () => {
    const nextIdx = currentNoteIndex < sortedFilteredBillingNotes.length - 1 ? currentNoteIndex + 1 : 0;
    if (sortedFilteredBillingNotes[nextIdx]) {
      setCurrentNoteIndex(nextIdx);
      setSelectedNoteForReview(sortedFilteredBillingNotes[nextIdx]);
      fetchNoteForReview(sortedFilteredBillingNotes[nextIdx].id);
      setBillingPreviewClaimId(null);
      setDryRunPreviewData(null);
    }
  };

  // Fetch static data once when user logs in (not on filter changes)
  useEffect(() => {
    // Only fetch when user is fully authenticated with an ID and role assignment
    // Use role_id (always set) instead of role (can be null/stale)
    // Jun 2026 (user request) — don't trust the shared loaded flag
    // alone: the V2 bootstrap sets it after fetching only its lean
    // slice, so also verify the legacy-only lists (posCodes,
    // organizations) are actually populated before skipping.
    if (user?.id && (user?.role_id || user?.role)
        && (!staticDataLoaded || posCodes.length === 0 || organizations.length === 0)) {
      void 0 && console.log('[PERF] User fully loaded, fetching static data. Role:', userRoleName, 'is_provider:', user.is_provider);
      fetchStaticData();
    }
  }, [user?.id, user?.role_id, user?.role, user?.is_provider, staticDataLoaded, posCodes.length, organizations.length, fetchStaticData, userRoleName]);

  // Fetch dynamic data AFTER static data is loaded to avoid race conditions
  // Only runs once per session - subsequent navigations use cached data
  // Also re-fetches roles if they failed to load (roles.length === 0)
  useEffect(() => {
    // Only fetch when user is fully authenticated AND static data has loaded
    // AND we haven't already loaded the data (prevents refetch on navigation)
    if (user?.id && staticDataLoaded && !dashboardHasLoadedOnce) {
      void 0 && console.log('[PERF] Static data loaded, now fetching dynamic data...');
      fetchData();
      fetchAllClientsForLookup(); // Fetch all clients for lookups (no UI filters)
      fetchRoles(); // Fetch roles for all users (needed for permission checks)
      fetchClientLabels(); // Fetch client labels for filtering
      fetchIntakeFormConfig(); // Fetch intake form config for Additional Information section
      // Fetch super admin count for "Last Man Standing" protection (admin only)
      if (isSuperAdmin(user) || userRoleName === 'admin') {
        fetchSuperAdminCount();
      }
      // Note: fetchMedicationReferralTemplate will run after formTemplates is loaded (via its useEffect dependency)
      // Note: Other fetches moved to tab-specific useEffects for performance
    }
  }, [user?.id, staticDataLoaded]);

  // Recovery: if roles failed to load or are empty, retry once when user is authenticated
  useEffect(() => {
    if (user?.id && staticDataLoaded && dashboardHasLoadedOnce && (!roles || roles.length === 0)) {
      console.warn('[Permissions] Roles array is empty after initial load — retrying fetchRoles');
      fetchRoles();
    }
  }, [user?.id, staticDataLoaded, dashboardHasLoadedOnce, roles?.length]);

  // Branding runs independently on every login — not gated by dashboardHasLoadedOnce
  // so a failed first fetch (network hiccup) doesn't permanently hide the logo
  useEffect(() => {
    if (user?.id) {
      fetchBrandingSettings();
    }
  }, [user?.id]);

  // Fallback: Ensure client labels are loaded even if the main fetch effect missed it
  // This handles race conditions where dashboardHasLoadedOnce might be set before labels are fetched
  useEffect(() => {
    if (user?.id && staticDataLoaded && clientLabels.length === 0) {
      void 0 && console.log('[Labels] Fetching client labels (fallback)...');
      fetchClientLabels();
    }
  }, [user?.id, staticDataLoaded, clientLabels.length, fetchClientLabels]);

  // Set medication referral template once formTemplates is loaded (avoids duplicate API call)
  useEffect(() => {
    if (formTemplates && formTemplates.length > 0 && !medicationReferralTemplate) {
      const medTemplate = formTemplates.find(isMedReferralTemplate);
      if (medTemplate) {
        setMedicationReferralTemplate(medTemplate);
      }
    }
  }, [formTemplates, medicationReferralTemplate, isMedReferralTemplate]);

  // Fetch Groups tab data only when Groups tab is active
  useEffect(() => {
    if (user?.id && activeTab === 'groups') {
      fetchPendingGroupRequests();
    }
  }, [user?.id, activeTab]);

  // Fetch Medications tab data only when Medication Management tab is active (initial load only)
  useEffect(() => {
    if (user?.id && activeTab === 'medication-management') {
      // Always fetch templates/managers as they're lightweight
      fetchMedicationReferralTemplate();
      fetchMedicationManagers();
      fetchServiceRequestTemplates();
      // Only fetch requests/waitlist on initial load (when empty)
      if (medicationReferralRequests.length === 0) {
        fetchMedicationReferralRequests();
      }
      if (user?.is_medication_manager) {
        if (waitlistClients.length === 0) {
          fetchWaitlistClients();
        }
      }
    }
  }, [user?.id, activeTab]);

  // Fetch Waitlist tab data only when Waitlist tab is active (initial load only)
  useEffect(() => {
    if (user?.id && activeTab === 'waitlist') {
      if (user?.is_medication_manager && waitlistClients.length === 0) {
        fetchWaitlistClients();
      }
      // Fetch insurances for waitlist filters if not already loaded
      if (insurances.length === 0) {
        fetchInsurances();
      }
    }
  }, [user?.id, activeTab]);

  // Fetch insurances for the Client Management filter pill (multi-select).
  // The Insurances admin page also fetches them, but this view loads first
  // for most users and needs the carrier list to populate the dropdown.
  useEffect(() => {
    if (user?.id && activeTab === 'clients' && insurances.length === 0) {
      fetchInsurances();
    }
  }, [user?.id, activeTab]);

  // Fetch Billing tab data only when Billing tab is active
  useEffect(() => {
    if (user?.id && activeTab === 'billing') {
      if (user?.is_billing_manager || user?.is_biller || isSuperAdmin(user)) {
        fetchClaimsQueue();
        fetchCurrentBatch();
        fetchNotesReadyForBilling();
        fetchNotesSentForCorrection();
      }
    }
  }, [user?.id, activeTab]);

  // Listen for bulk legacy-billing marks from the Billing Tab and drop those
  // notes from the in-memory list (backend already flipped their status to
  // 'billed' so they no longer belong in Notes Ready for Billing).
  useEffect(() => {
    const handler = (e) => {
      const removedIds = e.detail?.removedIds || [];
      if (!removedIds.length) return;
      const removedSet = new Set(removedIds);
      setNotesReadyForBilling(prev => prev.filter(n => !removedSet.has(n.id)));
      // Also refresh claims queue so the newly-created stubs show up there
      fetchClaimsQueue();
    };
    window.addEventListener('billingTabNotesUpdated', handler);
    return () => window.removeEventListener('billingTabNotesUpdated', handler);
  }, [fetchClaimsQueue]);

  // Fetch Tasks data only when Notes tab is active (on-demand loading)
  // Tasks are displayed within the Notes tab, so fetch when that tab is opened
  const refreshTasks = useCallback(async () => {
    if (!user?.id) return;
    setTasksRefreshing(true);
    try {
      void 0 && console.log('[Tasks] Fetching tasks...');
      const [assignedToMe, assignedByMe] = await Promise.all([
        axios.get(`${API}/tasks?assigned_to_me=true`).catch(e => ({ data: [] })),
        axios.get(`${API}/tasks?assigned_by_me=true`).catch(e => ({ data: [] }))
      ]);
      setTasks(assignedToMe.data || []);
      setTasksAssignedByMe(assignedByMe.data || []);
      void 0 && console.log('[Tasks] Tasks loaded:', assignedToMe.data?.length, assignedByMe.data?.length);
    } catch (error) {
      console.error('[Tasks] Failed to fetch tasks:', error);
    } finally {
      setTasksRefreshing(false);
    }
  }, [user?.id]);
  
  useEffect(() => {
    if (user?.id && activeTab === 'notes') {
      refreshTasks();
      // Lazy-load notes on first visit to Notes tab
      if (!notes || notes.length === 0) {
        refreshNotes();
      }
    }
  }, [user?.id, activeTab, refreshTasks]);

  // Optimistic task status update helper
  // Updates local state immediately, then syncs with backend.
  // Feb 2026 — ``completionNote`` is forwarded to the backend when
  // marking a task ``completed`` so the assigner's confirmation
  // notification can quote the outcome ("scheduled for next Tuesday
  // at 2 PM").  Empty / undefined ⇒ no note.
  const updateTaskStatusOptimistic = useCallback(async (taskId, newStatus, options = {}) => {
    const { skipToast = false, onSuccess, onError, completionNote } = options;
    
    // Find the task in both lists
    const taskInAssigned = tasks.find(t => t.id === taskId);
    const taskInAssignedByMe = tasksAssignedByMe.find(t => t.id === taskId);
    const originalTask = taskInAssigned || taskInAssignedByMe;
    
    const statusLabels = {
      'completed': 'completed',
      'in_progress': 'marked as in progress',
      'dismissed': 'dismissed',
      'cancelled': 'cancelled'
    };

    if (!originalTask) {
      // Jun 2026 — task not in local lists (deep-linked open_task from
      // V2 before refreshTasks resolves, or a supervisee task that
      // lives in neither list).  Previously this silently did NOTHING
      // — the dialog closed but the backend never heard about the
      // completion.  Persist the change directly instead.
      try {
        const payload = { status: newStatus };
        if (newStatus === 'completed' && completionNote && completionNote.trim()) {
          payload.completion_note = completionNote.trim();
        }
        await axios.put(`${API}/tasks/${taskId}`, payload);
        if (!skipToast) toast.success(`Task ${statusLabels[newStatus] || newStatus}`);
        if (newStatus === 'completed' || newStatus === 'dismissed') {
          fetchNotifications();
        }
        if (onSuccess) onSuccess();
        return true;
      } catch (error) {
        console.error('[Tasks] Failed to update task status:', error);
        toast.error('Failed to update task. Please try again.');
        if (onError) onError(error);
        return false;
      }
    }
    
    // Store original status for rollback
    const originalStatus = originalTask.status;
    
    // Optimistically update local state
    const updateTaskInList = (list) => 
      list.map(t => t.id === taskId ? { ...t, status: newStatus, updated_at: new Date().toISOString() } : t);
    
    setTasks(prev => updateTaskInList(prev));
    setTasksAssignedByMe(prev => updateTaskInList(prev));
    
    // Show immediate feedback
    if (!skipToast) {
      toast.success(`Task ${statusLabels[newStatus] || newStatus}`);
    }
    
    try {
      // Sync with backend.  Pass completion_note alongside status
      // only when actually completing — backend ignores it otherwise.
      const payload = { status: newStatus };
      if (newStatus === 'completed' && completionNote && completionNote.trim()) {
        payload.completion_note = completionNote.trim();
      }
      await axios.put(`${API}/tasks/${taskId}`, payload);
      
      // Update notifications count if task was completed/dismissed
      if (newStatus === 'completed' || newStatus === 'dismissed') {
        fetchNotifications();
      }
      
      if (onSuccess) onSuccess();
      return true;
    } catch (error) {
      console.error('[Tasks] Failed to update task status:', error);
      
      // Rollback optimistic update
      const rollbackTaskInList = (list) =>
        list.map(t => t.id === taskId ? { ...t, status: originalStatus } : t);
      
      setTasks(prev => rollbackTaskInList(prev));
      setTasksAssignedByMe(prev => rollbackTaskInList(prev));
      
      toast.error('Failed to update task. Please try again.');
      if (onError) onError(error);
      return false;
    }
  }, [tasks, tasksAssignedByMe, fetchNotifications]);

  // Fetch billing settings on initial load for ALL users (to hide/show billing tabs)
  useEffect(() => {
    if (user?.id) {
      fetchBillingSettings();
    }
  }, [user?.id]);

  // Fetch Admin tab data only when Admin tab is active (in addition to existing admin useEffect)
  useEffect(() => {
    if (user?.id && activeTab === 'admin') {
      if (isSuperAdmin(user)) {
        fetchOrganizations();
        fetchPermissions();
        fetchIntakeFormConfig();
        fetchInsurances();
        fetch2FASettings();
        fetchWaitlistSettings();
        fetchAuditSettings();
        fetchDomainWhitelistSettings();
        fetchBetaGateSettings();
        fetchBillingSettings();
        fetchGoogleCalendarStatus();
        fetchBackupStatus();
      }
    }
  }, [user?.id, activeTab]);

  // Fetch appointments when Schedule tab opens OR calendarDate moves
  // outside the already-loaded window.
  //
  // Collapsed from two useEffects into one (Feb 2026 perf fix) — the
  // previous structure had a redundant pair: one on (user, activeTab),
  // one on (user, activeTab, calendarDate, refreshAppointments).
  // Because `refreshAppointments` itself depends on calendarDate, the
  // second effect was firing twice on every tab switch.  Now one
  // effect, and `refreshAppointments` is responsible for its own
  // dedupe (skips the fetch when the window is already loaded).
  //
  // Jul 2026 — accept all three tab-name variants ('appointments' /
  // 'calendar' / 'schedule'); same inconsistency already handled by
  // the initial-load path.  With only 'appointments' checked, landing
  // on a 'schedule'-named tab meant month/week navigation NEVER
  // refetched — the calendar silently showed only the login-time
  // window instead of window-fetching like Google Calendar.
  useEffect(() => {
    if (user?.id && ['appointments', 'calendar', 'schedule'].includes(activeTab)) {
      refreshAppointments(true);
    }
  }, [user?.id, activeTab, calendarDate, refreshAppointments]);


  // Fetch alerts when selectedClient changes
  useEffect(() => {
    if (selectedClient?.id) {
      fetchClientAlerts(selectedClient.id);
    }
  }, [selectedClient?.id, fetchClientAlerts]);


  // Keyboard shortcut listener for Ctrl+Z (undo) and Alt+T (create task)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Close context menus on Escape
      if (e.key === 'Escape') {
        if (showEditContextMenu) {
          e.preventDefault();
          setShowEditContextMenu(false);
          return;
        }
        if (showSignatureContextMenu) {
          e.preventDefault();
          setShowSignatureContextMenu(false);
          return;
        }
      }
      
      // Debug: Log all key presses to help troubleshoot (remove after testing)
      if (e.altKey || e.metaKey) {
        void 0 && console.log('Key pressed:', {
          key: e.key,
          code: e.code,
          altKey: e.altKey,
          metaKey: e.metaKey,
          ctrlKey: e.ctrlKey,
          shiftKey: e.shiftKey
        });
      }
      
      // Check for Ctrl+S or Cmd+S (Mac) - Save Progress in Form Builder
      //
      // Feb 2026 — only intercept Cmd+S when the form-template builder
      // is actually open AND the event didn't originate in a text
      // field.  Otherwise users typing in a textarea could accidentally
      // trigger the form-builder save (and we'd preventDefault the
      // browser's "Save Page" which, while rarely wanted, isn't ours
      // to steal globally).
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && showFormTemplateBuilder) {
        const t = e.target;
        const isTextField = t && (
          t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable
        );
        if (isTextField) return;
        e.preventDefault();

        // Check if form builder is open
        if (showFormTemplateBuilder && activeFormFieldsRef.current.length > 0) {
          void 0 && console.log('✓ Cmd/Ctrl+S detected - saving form builder progress');
          
          // First, sync any static text from cache to state before saving
          const cache = staticTextEditorCache.current;
          const updatedFields = activeFormFieldsRef.current.map(field => {
            if (field.type === 'static_text') {
              const cached = cache.get(field.id);
              if (cached && cached.hasUserEdits && cached.content !== field.content) {
                return { ...field, content: cached.content };
              }
            }
            return field;
          });
          
          // Trigger save
          (async () => {
            try {
              const templateData = {
                ...formTemplateDataRef.current,
                form_fields: updatedFields
              };
              
              if (editingFormTemplate) {
                await axios.put(`${API}/form-templates/${editingFormTemplate.id}`, templateData);
                // Update local state with synced content
                setActiveFormFields(updatedFields);
                // Clear user edits flags in cache after successful save
                cache.forEach((value, key) => {
                  cache.set(key, { ...value, hasUserEdits: false });
                });
                toast.success("✓ Saved! (⌘S)");
              } else if (formTemplateDataRef.current.name) {
                const response = await axios.post(`${API}/form-templates`, templateData);
                setEditingFormTemplate(response.data);
                setActiveFormFields(updatedFields);
                toast.success("✓ Template created and saved! (⌘S)");
              } else {
                toast.error("Please enter a template name first");
              }
              // Form templates don't need fetchData - they're static data
            } catch (error) {
              toast.error(error.response?.data?.detail || "Failed to save");
            }
          })();
        }
        return;
      }
      
      // Check for Ctrl+Z or Cmd+Z (Mac) - Undo
      //
      // Feb 2026 — only intercept Cmd+Z when the user is NOT editing
      // a text field.  Without this guard the global handler stole
      // native browser undo from every textarea/input across the
      // app — including ClientEditDialog, progress notes, the search
      // bar — wiping the per-element undo stack the browser
      // maintains for free.
      //
      // We allow native undo whenever the event originates inside
      // any text-editable surface: <input>, <textarea>, or any
      // element with contenteditable=true (rich-text editors).
      // The form-template builder undo (handleUndo) is still
      // available everywhere else and via its own button.
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        const t = e.target;
        const isTextField =
          t && (
            t.tagName === 'INPUT' ||
            t.tagName === 'TEXTAREA' ||
            t.isContentEditable
          );
        if (!isTextField) {
          e.preventDefault();
          handleUndo();
        }
        // else — let the browser handle native undo on the text field.
      }
      
      // Check for Alt+T or Option+T (Mac) - Create Task
      // On Mac, Option key is altKey, so this should work
      if (e.altKey && (e.key === 't' || e.key === 'T') && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault();
        void 0 && console.log('✓ Alt+T detected - opening Create Task dialog');
        // Only open if user is logged in
        if (user) {
          setShowCreateTask(true);
        }
      }
      
      // Check for Alt+R or Option+R (Mac) - Open Rules Management
      if (e.altKey && (e.key === 'r' || e.key === 'R') && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault();
        void 0 && console.log('✓ Alt+R detected - opening Rules Management dialog');
        // Only open if user is admin/super admin
        if (user && (isSuperAdmin(user))) {
          handleOpenRulesManagement();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [undoStack, user, handleOpenRulesManagement, showFormTemplateBuilder, editingFormTemplate, showEditContextMenu, showSignatureContextMenu]); // Re-bind when dependencies change — activeFormFields accessed via ref to avoid re-binds on every keystroke

  // Auto-fetch data when filters change - debounced to prevent rapid re-fetches
  // Auto-fetch data when filters, pagination, or search change
  const initialFetchDone = useRef(false);
  const prevFiltersRef = useRef({ status: null, provider: null, label: null });
  
  useEffect(() => {
    // Only fetch if user is fully authenticated with an ID
    if (user?.id) {
      // Check if filters actually changed (not just a re-render).
      // Mode changes count as filter changes — flipping Include→Exclude
      // with a non-empty selection produces a different effective query.
      const filtersChanged = 
        prevFiltersRef.current.status !== JSON.stringify(clientStatusFilter) ||
        prevFiltersRef.current.statusMode !== clientStatusFilterMode ||
        prevFiltersRef.current.provider !== JSON.stringify(clientProviderFilter) ||
        prevFiltersRef.current.providerMode !== clientProviderFilterMode ||
        prevFiltersRef.current.label !== JSON.stringify(selectedLabelFilter) ||
        prevFiltersRef.current.labelMode !== selectedLabelFilterMode ||
        prevFiltersRef.current.insurance !== JSON.stringify(selectedInsuranceFilter) ||
        prevFiltersRef.current.insuranceMode !== selectedInsuranceFilterMode;
      
      const debounceTimer = setTimeout(() => {
        void 0 && console.log('[FETCH TRIGGER] Filters changed:', filtersChanged, 'Page:', clientsCurrentPage, 'Status:', clientStatusFilter);
        
        // Force refresh on initial mount OR when filters change
        const shouldRefresh = !initialFetchDone.current || filtersChanged;
        
        if (!initialFetchDone.current) {
          void 0 && console.log('[FETCH] Initial mount - forcing cache refresh');
          initialFetchDone.current = true;
        }
        
        if (filtersChanged) {
          void 0 && console.log('[FETCH] Filters changed - forcing cache refresh');
          prevFiltersRef.current = {
            status: JSON.stringify(clientStatusFilter),
            statusMode: clientStatusFilterMode,
            provider: JSON.stringify(clientProviderFilter),
            providerMode: clientProviderFilterMode,
            label: JSON.stringify(selectedLabelFilter),
            labelMode: selectedLabelFilterMode,
            insurance: JSON.stringify(selectedInsuranceFilter),
            insuranceMode: selectedInsuranceFilterMode
          };
        }
        
        refreshClients({ refresh: shouldRefresh });
      }, 150); // 150ms debounce - quick but prevents double-fetches
      
      return () => clearTimeout(debounceTimer);
    }
  }, [clientStatusFilter, clientStatusFilterMode, clientProviderFilter, clientProviderFilterMode, selectedLabelFilter, selectedLabelFilterMode, selectedInsuranceFilter, selectedInsuranceFilterMode, clientsCurrentPage, clientsPerPage, clientSearch, user?.id, refreshClients]);

  // Dedicated effect for sort-column / direction changes on the Client
  // Management list view.  The "auto-fetch" effect above includes
  // `refreshClients` in its deps (which itself depends on the sort
  // fields), so in theory it covers sort changes — but empirically the
  // chain doesn't fire for header clicks (May 2026 user report:
  // "Sort columns on Client Management list don't seem to be working").
  // Explicitly watching the sort fields here guarantees a refetch on
  // every header click without disturbing the debounced filter logic.
  const _sortFetchSkipRef = useRef(true);
  useEffect(() => {
    if (!user?.id) return;
    // Skip the very first run so we don't double-fetch on initial mount.
    if (_sortFetchSkipRef.current) {
      _sortFetchSkipRef.current = false;
      return;
    }
    refreshClients();
  }, [clientsSortColumn, clientsSortDirection, user?.id, refreshClients]);
  
  // Separate effect for search - resets to page 1 when search changes
  // Note: We use a ref to avoid re-triggering on every render
  const prevSearchRef = useRef(clientSearch);
  useEffect(() => {
    // Only reset page if search actually changed (not on initial render)
    if (prevSearchRef.current !== clientSearch && clientSearch !== undefined) {
      setClientsCurrentPage(1);
    }
    prevSearchRef.current = clientSearch;
  }, [clientSearch, setClientsCurrentPage]);

  // Claims are now fetched on-demand in BillingContext when Billing tab is opened
  // The useEffect below is removed to prevent initial load fetch

  // Save billing filters to localStorage
  useEffect(() => {
    safeLocalStorageSetItem('billingProviderFilter', billingProviderFilter);
  }, [billingProviderFilter]);

  useEffect(() => {
    // Persist as JSON array so the load-side can rehydrate without
    // guessing between legacy string forms and modern array forms.
    safeLocalStorageSetItem(
      'billingClientFilter',
      JSON.stringify(Array.isArray(billingClientFilter) ? billingClientFilter : []),
    );
  }, [billingClientFilter]);

  useEffect(() => {
    safeLocalStorageSetItem(
      'billingInsuranceFilter',
      JSON.stringify(Array.isArray(billingInsuranceFilter) ? billingInsuranceFilter : []),
    );
  }, [billingInsuranceFilter]);

  useEffect(() => {
    safeLocalStorageSetItem('billingDateFrom', billingDateFrom);
  }, [billingDateFrom]);

  useEffect(() => {
    safeLocalStorageSetItem('billingDateTo', billingDateTo);
  }, [billingDateTo]);

  // Persist notes pagination settings to localStorage (per user)
  useEffect(() => {
    if (user?.id) {
      safeLocalStorageSetItem(`activeNotesPageSize_${user.id}`, activeNotesPageSize.toString());
    }
  }, [activeNotesPageSize, user]);

  useEffect(() => {
    if (user?.id) {
      safeLocalStorageSetItem(`billedNotesPageSize_${user.id}`, billedNotesPageSize.toString());
    }
  }, [billedNotesPageSize, user]);

  // Load user-specific notes preferences when user logs in
  useEffect(() => {
    if (user?.id) {
      const savedActivePageSize = localStorage.getItem(`activeNotesPageSize_${user.id}`);
      const savedBilledPageSize = localStorage.getItem(`billedNotesPageSize_${user.id}`);
      const savedShowActiveNotes = localStorage.getItem(`showActiveNotes_${user.id}`);
      
      if (savedActivePageSize) {
        const parsed = savedActivePageSize === 'all' ? 'all' : parseInt(savedActivePageSize, 10);
        setActiveNotesPageSize(parsed);
      }
      if (savedBilledPageSize) {
        const parsed = savedBilledPageSize === 'all' ? 'all' : parseInt(savedBilledPageSize, 10);
        setBilledNotesPageSize(parsed);
      }
      if (savedShowActiveNotes !== null) {
        setShowActiveNotes(savedShowActiveNotes === 'true');
      }
    }
  }, [user?.id]);

  // Save showActiveNotes preference per user
  useEffect(() => {
    if (user?.id) {
      safeLocalStorageSetItem(`showActiveNotes_${user.id}`, showActiveNotes.toString());
    }
  }, [showActiveNotes, user]);

  // Fetch dedicated waitlist data when Waitlist tab is active
  // This doesn't modify clientStatusFilter, so Client Management stays unaffected
  useEffect(() => {
    // Fetch waitlist data whenever tab becomes active
    // This ensures new clients are shown
    if (activeTab === 'waitlist' && user && !waitlistLoading) {
      fetchDedicatedWaitlistClients();
    }
  }, [activeTab, user]); // Removed dedicatedWaitlistClients.length - always fetch on tab switch

  // Phase 3e.2 (Feb 2026) — listen for IntakeWizard saves that fire
  // through the V2 IntakeWizardProvider (mounted at App.js root since
  // Phase 3e.1) and refresh the App.js-scope data caches.  This lets
  // call sites that delegated to the V2 provider (the `skipNavigation`
  // sites migrated in 3e.2) get the same refresh semantics as the
  // remaining inline-state sites.
  useEffect(() => {
    const onV2IntakeSaved = () => {
      try { refreshClients?.(); } catch (_) { /* noop */ }
      try { refreshStats?.(); } catch (_) { /* noop */ }
      try { fetchDedicatedWaitlistClients?.(); } catch (_) { /* noop */ }
    };
    window.addEventListener('v2:intake-saved', onV2IntakeSaved);
    // Feb 2026 Phase 1G — V2 chart inline edits (label/status/
    // service-enrollment) broadcast `client:updated`. Refresh the
    // legacy Client Management list so the cards stay 1-to-1 with
    // what the V2 chart shows. Backend has already committed and
    // synced `assigned_providers`; this just refetches the in-
    // memory snapshot.
    const onClientUpdated = () => {
      try { refreshClients?.(); } catch (_) { /* noop */ }
    };
    window.addEventListener('client:updated', onClientUpdated);
    return () => {
      window.removeEventListener('v2:intake-saved', onV2IntakeSaved);
      window.removeEventListener('client:updated', onClientUpdated);
    };
  }, [refreshClients, refreshStats, fetchDedicatedWaitlistClients]);

  // Auto-save intake draft every 60 seconds
  useEffect(() => {
    // Don't auto-save in edit mode or if no client ID
    if (!showIntakeWizard || !intakeClientId || editingClient) return;
    // NOTE: autosave now runs inside IntakeWizardDialog using its own local
    // formData (the dialog owns draft state for typing-lag perf). Keeping this
    // effect stubbed here so the dep-array-heavy block doesn't reappear.
    return;
    // eslint-disable-next-line no-unreachable
    const autoSaveInterval = setInterval(async () => {
      try {
        setIntakeAutoSaving(true);
        await axios.post(`${API}/clients/${intakeClientId}/intake-draft`, {
          current_step: 1, // No longer using steps
          data: intakeData
        });
        setIntakeLastSaved(new Date());
      } catch (error) {
        console.error('Auto-save failed:', error);
      } finally {
        setIntakeAutoSaving(false);
      }
    }, 60000); // 60 seconds

    return () => clearInterval(autoSaveInterval);
  }, [showIntakeWizard, intakeClientId, intakeData, editingClient]);

  // Fetch portal settings for admin
  useEffect(() => {
    if (isSuperAdmin(user)) {
      fetchPortalSettings();
      fetchEligibilitySettings();
      fetchPaymentReminderSettings();
    }
  }, [user, fetchPortalSettings, fetchEligibilitySettings, fetchPaymentReminderSettings]);

  // Auto-refresh waitlist every 60 seconds when waitlist tab is active
  useEffect(() => {
    if (activeTab !== 'waitlist') return;
    
    const refreshInterval = setInterval(async () => {
      if (isEditingDialogOpenRef.current || isUserBusy()) return;
      try {
        refreshClients(); refreshStats();
        setLastWaitlistRefresh(new Date());
      } catch (error) {
        console.error('Auto-refresh failed:', error);
      }
    }, 30000); // 30 seconds - reduced from 60s for better responsiveness

    return () => clearInterval(refreshInterval);
  }, [activeTab, refreshClients, refreshStats]);

  // Initialize impersonation for admin users (no API call - just check admin status)
  useEffect(() => {
    if (user && isSuperAdmin(user) && !isActualAdmin) {
      void 0 && console.log('🎭 User is admin - initializing impersonation context');
      initializeAdmin();
    }
  }, [user, isActualAdmin, initializeAdmin]);

  // Security check: If user is on admin tab but isn't an admin, redirect to dashboard
  // This handles cases where a non-admin user logs in while the previous admin session
  // left the UI state on the admin tab
  useEffect(() => {
    if (user && activeTab === 'admin' && !isSuperAdmin(user)) {
      void 0 && console.log('[Security] Non-admin user on admin tab - redirecting to dashboard');
      setActiveTab('dashboard');
    }
  }, [user, activeTab, setActiveTab]);

  // Fetch routing rules when admin tab is opened
  useEffect(() => {
    if (activeTab === 'admin') {
      fetchRoutingRules();
      fetchReminderSettings();
    }
  }, [activeTab, fetchRoutingRules, fetchReminderSettings]);

  // Lazy-load deferred data when relevant tabs open
  // Dashboard stats, treatment plan reviews, and draft plans are deferred from initial login
  const deferredDataLoadedRef = React.useRef(false);
  useEffect(() => {
    if (!user?.id || deferredDataLoadedRef.current) return;
    // Load deferred data on first meaningful interaction (any tab change or after 3s)
    const loadDeferred = () => {
      if (deferredDataLoadedRef.current) return;
      deferredDataLoadedRef.current = true;
      void 0 && console.log('[PERF] Loading deferred data (stats, reviews, drafts)');
      refreshStats();
      // Fetch treatment plan reviews
      axios.get(`${API}/treatment-plans/my-reviews-due`).then(r => setTreatmentPlansDueForReview(r.data || [])).catch(() => {});
      // Fetch draft plans
      if (userRoleName === 'provider' || user?.is_provider) {
        axios.get(`${API}/treatment-plans/my-draft-plans`).then(r => setMyDraftPlans(r.data || [])).catch(() => {});
      }
      if (user?.is_supervisor) {
        axios.get(`${API}/treatment-plans/team-draft-plans`).then(r => setTeamDraftPlans(r.data || [])).catch(() => {});
      }
    };
    // Load after 3 second delay (user is reading the page) or on tab change
    const timer = setTimeout(loadDeferred, 3000);
    return () => clearTimeout(timer);
  }, [user?.id]);

  // Helper function to format client name with preferred name
  // Last name is ALWAYS uppercase for consistency across the app
  // Format examples: "Emily Grace CHEN" or "CHEN, Emily Grace"
  const formatClientName = (client) => {
    if (!client) return '';
    const displayName = formatClientNameOrdered(client, clientNameFormat);
    // Add preferred name if different from first name
    if (client.preferred_name && client.preferred_name !== client.first_name) {
      return `${displayName || 'Unknown'} (${client.preferred_name})`;
    }
    return displayName || 'Unknown';
  };

  // Fetch client documents - LAZY LOAD (only when documents tab or related feature is active)
  // Skipped when the chart-bundle endpoint (workstream C1) has
  // already populated documents for this client.  Ref-checked so a
  // doc upload / delete that calls this effect indirectly still
  // refetches.
  const clientDocumentsRef = useRef(clientDocuments);
  useEffect(() => { clientDocumentsRef.current = clientDocuments; }, [clientDocuments]);
  useEffect(() => {
    const fetchClientDocuments = async () => {
      // Fetch documents whenever client is selected - needed for multiple tabs
      if (selectedClient?.id) {
        // Skip if chart-bundle just populated documents for this client.
        if (chartBundleClientIdRef.current === selectedClient.id) return;
        // chart-bundle pre-fill check: if we already have docs whose
        // client_id matches the current client, skip the redundant fetch.
        const existing = clientDocumentsRef.current;
        const alreadyHaveDocsForThisClient =
          Array.isArray(existing) &&
          existing.length > 0 &&
          existing.every(d => d.client_id === selectedClient.id);
        if (alreadyHaveDocsForThisClient) return;
        try {
          const response = await axios.get(`${API}/clients/${selectedClient.id}/documents`);
          // Filter out documents that are pending delete
          const filteredDocs = response.data.filter(doc => !pendingDeletes[`doc-${doc.id}`]);
          setClientDocuments(filteredDocs);
        } catch (error) {
          console.error('Error fetching client documents:', error);
          // Show error to user so they know something went wrong
          if (error.response?.status === 500) {
            toast.error('Failed to load documents. Please try refreshing.');
          }
        }
      } else {
        setClientDocuments([]);
      }
    };
    fetchClientDocuments();
  }, [selectedClient?.id]);

  // Workstream C1 (Feb 2026) — when chart-bundle populates the chart
  // for a client, stamp this ref so the "selectedClient.id changed"
  // follow-up effects can skip their redundant per-resource fetches
  // even when the bundle returned an empty array (e.g. clients with
  // 0 notes — where the "alreadyHaveNotesForThisClient" guard below
  // would otherwise fail and trigger a refetch).
  const chartBundleClientIdRef = useRef(null);
  // Per-collection JSON snapshots of the last applied chart bundle —
  // lets applyBundle skip setStates whose data didn't change (Jun 2026
  // render-thrash fix: prevents care-team/insurance subtree remounts on
  // the SWR fresh swap and repeat opens).
  const lastBundleAppliedRef = useRef({});

  // ETag cache for chart-bundle revisits.  Map<clientId, {etag, payload}>
  // — when set, the next /chart-bundle request for the same client
  // includes ``If-None-Match`` so the server can short-circuit with
  // 304 Not Modified.  Bumped on every 200 response.
  //
  // SWR session-persistence (Feb 2026): on first read for a given
  // clientId we hydrate the entry from sessionStorage if a previous
  // tab session cached it.  Lets a hard browser refresh render the
  // chart instantly while the background revalidation runs.  See
  // utils/chartBundleCache.js for the quota-aware encrypt/decrypt
  // layer.
  const chartBundleEtagCacheRef = useRef(new Map());

  const _readCachedEntry = useCallback((clientId) => {
    // Prefer the in-memory ref (latest)
    const mem = chartBundleEtagCacheRef.current.get(clientId);
    if (mem) return mem;
    // Fall through to sessionStorage hydrate
    const persisted = getCachedBundle(clientId);
    if (persisted && persisted.etag && persisted.payload) {
      chartBundleEtagCacheRef.current.set(clientId, {
        etag: persisted.etag,
        payload: persisted.payload,
      });
      return chartBundleEtagCacheRef.current.get(clientId);
    }
    return null;
  }, []);

  const _writeCacheEntry = useCallback((clientId, etag, payload) => {
    chartBundleEtagCacheRef.current.set(clientId, { etag, payload });
    // Best-effort: persist to sessionStorage for hard-refresh survival.
    // The setter is quota-fail-safe — never throws.
    setCachedBundle(clientId, { etag, payload });
  }, []);

  /**
   * Wrap axios.get(/chart-bundle) so we transparently send
   * ``If-None-Match`` on revisits and surface the cached payload on
   * 304.  Returns ``{ data, status }`` so callers can branch on 304
   * to skip redundant state writes (we still set them, but cheaper:
   * no JSON.parse, no ~22KB transfer).
   */
  const fetchChartBundle = useCallback(async (clientId) => {
    const cached = _readCachedEntry(clientId);
    const headers = cached?.etag ? { 'If-None-Match': cached.etag } : {};
    const resp = await axios.get(`${API}/clients/${clientId}/chart-bundle`, {
      headers,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
    });
    if (resp.status === 304 && cached?.payload) {
      return { data: cached.payload, status: 304 };
    }
    const newEtag = resp.headers?.etag || resp.headers?.ETag;
    if (newEtag) {
      _writeCacheEntry(clientId, newEtag, resp.data);
    }
    return { data: resp.data, status: 200 };
  }, [_readCachedEntry, _writeCacheEntry]);

  /**
   * Stale-while-revalidate variant — when we already have a cached
   * payload for the client, return it IMMEDIATELY (synchronous
   * resolution) and fire a background ``If-None-Match`` validation
   * fetch.  When the background fetch resolves with a 200 (data
   * changed) we surface the fresh data via the ``onFresh`` callback.
   *
   * Pattern from Feb 2026 user feedback: *"show the cached bundle
   * instantly while a fresh fetch runs in the background, then swap
   * if the ETag changes."*
   *
   * Returns ``{ data, status, isStale }`` where:
   *   - status: 200 if no cache existed (regular fetch)
   *             200-stale if we returned cache while revalidating
   *             304 if cache existed AND server confirmed unchanged
   *
   * The caller is expected to use ``data`` to render the chart, and
   * (optionally) re-render when ``onFresh`` fires.
   */
  const fetchChartBundleSWR = useCallback(async (clientId, onFresh) => {
    const cached = _readCachedEntry(clientId);

    // Cold cache — regular fetch (still benefits from ETag if a
    // service worker / browser cached it).
    if (!cached?.payload) {
      return fetchChartBundle(clientId);
    }

    // Warm cache — fire the validation fetch in the background but
    // resolve immediately with the cached payload.  The background
    // fetch updates the cache + invokes onFresh if data changed.
    (async () => {
      try {
        const resp = await axios.get(`${API}/clients/${clientId}/chart-bundle`, {
          headers: { 'If-None-Match': cached.etag },
          validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
        });
        if (resp.status === 304) {
          // Server confirmed cache is fresh — no-op
          return;
        }
        // Server returned new data — update cache + notify caller
        const newEtag = resp.headers?.etag || resp.headers?.ETag;
        if (newEtag) {
          _writeCacheEntry(clientId, newEtag, resp.data);
        }
        if (typeof onFresh === 'function') {
          try { onFresh(resp.data); } catch { /* caller error — ignore */ }
        }
      } catch {
        // Background revalidation failure is silent — user is
        // already looking at the cached data.
      }
    })();

    return { data: cached.payload, status: 200, isStale: true };
  }, [fetchChartBundle, _readCachedEntry, _writeCacheEntry]);

  // Fetch client notes — LAZY LOAD (newest 100 on chart open; rest pulled
  // on search or chevron expand via loadAllClientNotes below).
  //
  // Option A: don't ship every historical note over the wire just to render
  // a client's chart. The 100 newest cover the common case; if the biller
  // searches or opens a form-type section whose older notes weren't in the
  // first batch, loadAllClientNotes() fetches the rest in the background.
  // Ref to the latest clientNotes so the fetch effect can read current value
  // WITHOUT being in the dependency array (which would re-fire the effect
  // on every save/lock/etc and cause a refetch loop).
  const clientNotesRef = useRef(clientNotes);
  useEffect(() => { clientNotesRef.current = clientNotes; }, [clientNotes]);

  useEffect(() => {
    const fetchClientNotes = async () => {
      const shouldFetchNotes = clientDetailTab === 'notes' || clientDetailTab === 'overview';
      if (selectedClient?.id && shouldFetchNotes) {
        // Skip if chart-bundle just populated notes for this same client
        // (handles the empty-notes case the next guard can't).
        if (chartBundleClientIdRef.current === selectedClient.id) {
          return;
        }
        // Skip the refetch if handleClientClick has ALREADY populated the
        // notes for this exact client. Without this, a slow second fetch
        // with a smaller `limit` clobbers the wider initial fetch (~100ms
        // after open), making older notes "flash and disappear" on the
        // Documents tab. handleClientClick fetches up to limit=1000 with
        // the 2020–2030 date range, which is the canonical full set.
        const existing = clientNotesRef.current;
        const alreadyHaveNotesForThisClient =
          Array.isArray(existing) &&
          existing.length > 0 &&
          existing.every(n => n.client_id === selectedClient.id);
        if (alreadyHaveNotesForThisClient) {
          return;
        }
        try {
          // CRITICAL: include wide date range (2020-2030) — without it the
          // backend defaults to last 30 days only (server.py:13865), which
          // was causing the "flash and disappear" race: handleClientClick's
          // call with date range returned ALL notes; this effect's call
          // (no range) returned only last 30; whichever resolved last won.
          // limit=1000 — wire-size bounded; lazy-load via X-Has-More for
          // clients with >1000 notes.
          const response = await axios.get(
            `${API}/notes?client_id=${selectedClient.id}&start_date=2020-01-01&end_date=2030-12-31&limit=1000&summary=true`
          );
          setClientNotes(response.data || []);
          // Headers exposed via CORS expose_headers (server.py). Axios
          // normalizes to lowercase.
          const total = parseInt(
            response.headers['x-total-count'] || (response.data || []).length,
            10,
          );
          const hasMore = (response.headers['x-has-more'] || 'false').toLowerCase() === 'true';
          setClientNotesTotalCount(Number.isNaN(total) ? (response.data || []).length : total);
          setClientNotesHasMore(hasMore);
        } catch (error) {
          console.error('Error fetching client notes:', error);
          if (error.response?.status === 500) {
            toast.error('Failed to load notes. Please try refreshing.');
          }
        }
      } else if (!selectedClient?.id) {
        setClientNotes([]);
        setClientNotesHasMore(false);
        setClientNotesTotalCount(0);
      }
    };
    fetchClientNotes();
  }, [selectedClient?.id, clientDetailTab, setClientNotes, setClientNotesHasMore, setClientNotesTotalCount]);

  // Lazy-load the remainder when the user triggers an action that needs
  // the full history (search box focus, form-type chevron expand, etc.).
  // Idempotent: no-op when already loaded or a load is in flight.
  const loadingAllClientNotesRef = useRef(false);
  const loadAllClientNotes = useCallback(async () => {
    if (!selectedClient?.id) return;
    if (!clientNotesHasMore) return;
    if (loadingAllClientNotesRef.current) return;
    loadingAllClientNotesRef.current = true;
    try {
      const response = await axios.get(
        `${API}/notes?client_id=${selectedClient.id}&start_date=2020-01-01&end_date=2030-12-31&limit=5000&summary=true`
      );
      if (Array.isArray(response.data)) {
        setClientNotes(response.data);
        setClientNotesHasMore(false);
        setClientNotesTotalCount(response.data.length);
      }
    } catch (error) {
      console.error('Error loading all client notes:', error);
    } finally {
      loadingAllClientNotesRef.current = false;
    }
  }, [selectedClient?.id, clientNotesHasMore, setClientNotes, setClientNotesHasMore, setClientNotesTotalCount]);

  // Fetch client insurances when client is selected.
  // Skipped when chart-bundle (workstream C1) has already populated
  // insurances for the current client.
  const selectedClientInsurancesRef = useRef(selectedClientInsurances);
  useEffect(() => { selectedClientInsurancesRef.current = selectedClientInsurances; }, [selectedClientInsurances]);
  useEffect(() => {
    const fetchClientInsurances = async () => {
      if (selectedClient?.id) {
        // Skip if chart-bundle just populated insurances for this client.
        if (chartBundleClientIdRef.current === selectedClient.id) return;
        const existing = selectedClientInsurancesRef.current;
        const alreadyHave =
          Array.isArray(existing) &&
          existing.length > 0 &&
          existing.every(i => i.client_id === selectedClient.id);
        if (alreadyHave) return;
        try {
          const response = await axios.get(`${API}/clients/${selectedClient.id}/insurances`);
          setSelectedClientInsurances(response.data);
        } catch (error) {
          console.error('Error fetching client insurances:', error);
          setSelectedClientInsurances([]);
        }
      } else {
        setSelectedClientInsurances([]);
      }
    };
    fetchClientInsurances();
  }, [selectedClient?.id]);

  // Fetch client authorizations - LAZY LOAD (only when authorizations tab or billing tab)
  // Skipped when chart-bundle (workstream C1) has already populated
  // authorizations for the current client.
  const clientAuthorizationsRef = useRef(clientAuthorizations);
  useEffect(() => { clientAuthorizationsRef.current = clientAuthorizations; }, [clientAuthorizations]);
  useEffect(() => {
    const fetchClientAuthorizations = async () => {
      const shouldFetchAuth = clientDetailTab === 'authorizations' || 
                              clientDetailTab === 'billing' ||
                              clientDetailTab === 'overview';  // Keep for overview summary
      
      if (selectedClient?.id && shouldFetchAuth) {
        // Skip if chart-bundle just populated authorizations for this client.
        if (chartBundleClientIdRef.current === selectedClient.id) {
          // Still ensure serviceTypes are loaded for the dropdown
          if (serviceTypes.length === 0) {
            try {
              const serviceTypesResponse = await axios.get(`${API}/treatment-planning/service-types`);
              setServiceTypes(serviceTypesResponse.data);
            } catch { /* non-critical */ }
          }
          return;
        }
        const existing = clientAuthorizationsRef.current;
        const alreadyHave =
          Array.isArray(existing) &&
          existing.length > 0 &&
          existing.every(a => a.client_id === selectedClient.id);
        if (alreadyHave) {
          // Still ensure serviceTypes are loaded for the dropdown
          if (serviceTypes.length === 0) {
            try {
              const serviceTypesResponse = await axios.get(`${API}/treatment-planning/service-types`);
              setServiceTypes(serviceTypesResponse.data);
            } catch { /* non-critical */ }
          }
          return;
        }
        try {
          // Also fetch service types if not loaded
          if (serviceTypes.length === 0) {
            const serviceTypesResponse = await axios.get(`${API}/treatment-planning/service-types`);
            setServiceTypes(serviceTypesResponse.data);
          }
          
          const response = await axios.get(`${API}/authorizations/${selectedClient.id}`);
          setClientAuthorizations(response.data);
        } catch (error) {
          console.error('Error fetching client authorizations:', error);
          setClientAuthorizations([]);
        }
      } else if (!selectedClient?.id) {
        setClientAuthorizations([]);
      }
    };
    fetchClientAuthorizations();
  }, [selectedClient?.id, clientDetailTab]);

  // Fetch client releases - LAZY LOAD (only when relevant tab is active)
  useEffect(() => {
    const fetchClientReleases = async () => {
      // Releases are shown in contacts tab, releases tab, and documents tab
      const shouldFetchReleases = clientDetailTab === 'contacts' || clientDetailTab === 'releases' || clientDetailTab === 'documents';
      
      if (selectedClient?.id && shouldFetchReleases) {
        try {
          const response = await axios.get(`${API}/releases?client_id=${selectedClient.id}`);
          setClientReleases(response.data);
        } catch (error) {
          console.error('Error fetching client releases:', error);
          setClientReleases([]);
        }
      } else if (!selectedClient?.id) {
        setClientReleases([]);
      }
    };
    fetchClientReleases();
  }, [selectedClient?.id, clientDetailTab]);

  // Fetch client MDT reviews - LAZY LOAD (only when mdt-reviews tab is active)
  useEffect(() => {
    const fetchClientMDTReviews = async () => {
      const shouldFetchMDT = clientDetailTab === 'mdt-reviews' || clientDetailTab === 'supervision';
      
      if (selectedClient?.id && shouldFetchMDT) {
        try {
          const response = await axios.get(`${API}/clients/${selectedClient.id}/mdt-reviews`);
          setClientMDTReviews(response.data);
        } catch (error) {
          console.error('Error fetching client MDT reviews:', error);
          setClientMDTReviews([]);
        }
      } else if (!selectedClient?.id) {
        setClientMDTReviews([]);
      }
    };
    fetchClientMDTReviews();
  }, [selectedClient?.id, clientDetailTab]);

  // Fetch my pending MDT actions (for clinicians)
  const fetchMDTPendingActions = useCallback(async () => {
    if (!user?.is_provider && userRoleName !== 'provider' && userRoleName !== 'intern') return;
    
    try {
      const response = await axios.get(`${API}/mdt-reviews/my-pending`);
      setMdtPendingActions(response.data || []);
    } catch (error) {
      console.warn('Failed to fetch MDT pending actions:', error);
      setMdtPendingActions([]);
    }
  }, [user]);

  useEffect(() => {
    if (user?.is_provider || userRoleName === 'provider' || userRoleName === 'intern') {
      fetchMDTPendingActions();
      const interval = setInterval(() => {
        if (!isEditingDialogOpenRef.current && !isUserBusy()) fetchMDTPendingActions();
      }, 300000); // 5 minutes
      return () => clearInterval(interval);
    }
  }, [user, fetchMDTPendingActions]);

  // ── V2 Client Chart event bridges (Phase 2I, Feb 2026) ────────────
  // The V2 Alerts + Supervision tabs are now native and dispatch
  // window events when they need a legacy-owned dialog or refresh:
  //   • `alerts:changed`        → refresh global unread badge
  //   • `mdt:pending-changed`   → refresh MDT pending badge
  //   • `v2:open-note`          → open NoteDetail dialog
  //   • `v2:view-document`      → open DocumentViewer dialog
  // Ref-stable handlers so the listeners attach ONCE per session and
  // don't churn on every render (openNote / fetchData / etc. are not
  // memoized at the App-root scope).
  const v2BridgeRefs = React.useRef({});
  v2BridgeRefs.current.fetchData = fetchData;
  v2BridgeRefs.current.fetchMDTPendingActions = fetchMDTPendingActions;
  v2BridgeRefs.current.openNote = openNote;
  v2BridgeRefs.current.setViewingDocument = setViewingDocument;
  v2BridgeRefs.current.fetchPendingGroupRequests = fetchPendingGroupRequests;
  // Mar 2026 — `handleClientClick` and `selectedClient` are declared
  // LATER in this component (handleClientClick ~line 18524).  Reading
  // them at top-level render produces a TDZ ReferenceError on the
  // Dashboard route ("Cannot access 'handleClientClick' before
  // initialization").  Defer those two ref-writes to a post-render
  // effect so by the time the bridge listeners read them, both
  // identifiers are bound.
  useEffect(() => {
    const onAlertsChanged = () => {
      try { v2BridgeRefs.current.fetchData?.(); } catch (_) { /* no-op */ }
    };
    const onMDTChanged = () => {
      try { v2BridgeRefs.current.fetchMDTPendingActions?.(); } catch (_) { /* no-op */ }
    };
    const onGroupsChanged = () => {
      // Mirrors the GroupRequestDialog's existing onSuccess hook in
      // App.js so the legacy chart's pending-requests pill stays in
      // sync after a V2 chart-initiated request.
      try { v2BridgeRefs.current.fetchPendingGroupRequests?.(); } catch (_) { /* no-op */ }
    };
    const onContactsChanged = () => {
      // V2 Contacts tab self-refreshes via its own `fetchAll()` after
      // mutations.  The legacy chart's open client doc also carries
      // contacts inline, but legacy + V2 are not usually open at the
      // same time so we don't proactively refresh legacy here — it'll
      // pick up the new state on next navigation.  Earlier attempt to
      // call handleClientClick here produced a TDZ ReferenceError
      // because that function is declared LATER in this component
      // body.  Keep this listener side-effect-free.
      try {
        // Optional: nudge any global side panels that listen.
        if (typeof window !== 'undefined') {
          // Intentional no-op slot — the dispatchEvent above carries
          // the detail payload; downstream listeners can consume it.
        }
      } catch (_) { /* no-op */ }
    };
    const onOpenNote = (ev) => {
      const note = ev?.detail?.note;
      if (note) {
        try { v2BridgeRefs.current.openNote?.(note); } catch (_) { /* no-op */ }
      }
    };
    const onViewDocument = (ev) => {
      const doc = ev?.detail?.doc;
      if (doc) {
        try { v2BridgeRefs.current.setViewingDocument?.(doc); } catch (_) { /* no-op */ }
      }
    };
    // Mar 2026 — V2 Plan tab bridge.  When the V2 Plan list-view's
    // "New Treatment Plan" button is clicked we navigate the user to
    // `/client/:id?tab=treatment-plans&action=new`.  The URL effect
    // higher up fires this window event ~700 ms after mount so we
    // know the legacy chart + Plan tab have fully rendered.  We
    // simulate a click on the existing legacy button to reuse its
    // (~200-line) data-fetch + dialog-open logic verbatim instead of
    // duplicating it.  The legacy button carries
    // `data-testid="legacy-new-treatment-plan-btn"`.
    const onOpenNewPlan = () => {
      try {
        const btn = document.querySelector('[data-testid="legacy-new-treatment-plan-btn"]');
        if (btn && typeof btn.click === 'function') {
          btn.click();
        }
      } catch (_) { /* no-op */ }
    };
    window.addEventListener('alerts:changed', onAlertsChanged);
    window.addEventListener('mdt:pending-changed', onMDTChanged);
    window.addEventListener('groups:changed', onGroupsChanged);
    window.addEventListener('contacts:changed', onContactsChanged);
    window.addEventListener('v2:open-note', onOpenNote);
    window.addEventListener('v2:view-document', onViewDocument);
    window.addEventListener('legacy:open-new-treatment-plan', onOpenNewPlan);
    return () => {
      window.removeEventListener('alerts:changed', onAlertsChanged);
      window.removeEventListener('mdt:pending-changed', onMDTChanged);
      window.removeEventListener('groups:changed', onGroupsChanged);
      window.removeEventListener('contacts:changed', onContactsChanged);
      window.removeEventListener('v2:open-note', onOpenNote);
      window.removeEventListener('v2:view-document', onViewDocument);
      window.removeEventListener('legacy:open-new-treatment-plan', onOpenNewPlan);
    };
    // Listeners attached once; refs above always read the latest handler.
  }, []);

  // Save Intake Form Configuration
  const saveIntakeFormConfig = async () => {
    try {
      await axios.put(`${API}/config/intake-form`, intakeFormConfig);
      toast.success("Form configuration saved successfully!");
      setShowFormConfigDialog(false);
    } catch (error) {
      console.error("Failed to save form config:", error);
      toast.error("Failed to save configuration");
    }
  };

  // Update field in configuration
  const updateFieldInConfig = (stepId, fieldId, updates) => {
    setIntakeFormConfig(prev => ({
      ...prev,
      steps: prev.steps.map(step => {
        if (step.step_id === stepId) {
          return {
            ...step,
            fields: step.fields.map(field => 
              field.field_id === fieldId ? { ...field, ...updates } : field
            )
          };
        }
        return step;
      })
    }));
  };

  // Reorder field in configuration
  const reorderField = (stepId, fieldId, direction) => {
    setIntakeFormConfig(prev => ({
      ...prev,
      steps: prev.steps.map(step => {
        if (step.step_id === stepId) {
          const fields = [...step.fields];
          const index = fields.findIndex(f => f.field_id === fieldId);
          if (index === -1) return step;
          
          const newIndex = direction === 'up' ? index - 1 : index + 1;
          if (newIndex < 0 || newIndex >= fields.length) return step;
          
          // Swap
          [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];
          
          // Update order numbers
          fields.forEach((f, i) => f.order = i + 1);
          
          return { ...step, fields };
        }
        return step;
      })
    }));
  };

  // Add option to select field
  const addOptionToField = (stepId, fieldId, newOption) => {
    updateFieldInConfig(stepId, fieldId, {
      options: [...(intakeFormConfig.steps
        .find(s => s.step_id === stepId)?.fields
        .find(f => f.field_id === fieldId)?.options || []), newOption]
    });
  };

  // Remove option from select field
  const removeOptionFromField = (stepId, fieldId, optionIndex) => {
    const field = intakeFormConfig.steps
      .find(s => s.step_id === stepId)?.fields
      .find(f => f.field_id === fieldId);
    
    if (field?.options) {
      const newOptions = field.options.filter((_, i) => i !== optionIndex);
      updateFieldInConfig(stepId, fieldId, { options: newOptions });
    }
  };
  // Fetch CPT Bundles from backend
  const fetchCptBundles = async () => {
    try {
      const response = await axios.get(`${API}/cpt-bundles`);
      setCptBundles(response.data || []);
    } catch (error) {
      console.error("Failed to fetch CPT bundles:", error);
      toast.error("Failed to load CPT bundles");
      setCptBundles([]);
    }
  };
  
  // Bundle Management Handlers
  const handleCreateBundle = async (e) => {
    e.preventDefault();
    void 0 && console.log("Creating bundle with data:", bundleFormData);
    try {
      if (editingBundle) {
        void 0 && console.log("Updating bundle:", editingBundle.id);
        const response = await axios.put(`${API}/cpt-bundles/${editingBundle.id}`, bundleFormData);
        void 0 && console.log("Update response:", response.data);
        toast.success("Bundle updated successfully");
      } else {
        void 0 && console.log("Creating new bundle");
        const response = await axios.post(`${API}/cpt-bundles`, bundleFormData);
        void 0 && console.log("Create response:", response.data);
        toast.success("Bundle created successfully");
      }
      setShowBundleForm(false);
      setBundleFormData({ name: '', description: '', cpt_code_ids: [] });
      setEditingBundle(null);
      fetchCptBundles();
    } catch (error) {
      console.error("Bundle save error:", error.response?.data);
      // Handle Pydantic validation errors
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const errorMsg = error.response.data.detail.map(err => err.msg).join(', ');
        toast.error(errorMsg);
      } else {
        toast.error(error.response?.data?.detail || "Failed to save bundle");
      }
    }
  };
  
  const handleDeleteBundle = async (bundleId) => {
    try {
      await axios.delete(`${API}/cpt-bundles/${bundleId}`);
      toast.success("Bundle deleted successfully");
      fetchCptBundles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete bundle");
    }
  };

  // Create Organization
  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/organizations`, organizationFormData);
      toast.success("Organization created successfully");
      setShowAddOrganization(false);
      setOrganizationFormData({
        name: '',
        organization_type: '',
        tax_id: '',
        taxonomy_code: '',
        npis: [],
        availity_submitter_id: ''
      });
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create organization");
    }
  };

  // Update Organization
  const handleUpdateOrganization = async (e) => {
    e.preventDefault();
    if (!editingOrganization) return;
    
    try {
      // PUT returns the saved Organization doc — apply it locally
      // BEFORE clearing the dialog so reopening Edit reads from the
      // fresh state immediately, even if `fetchOrganizations()`
      // hasn't completed yet (Feb 2026 — user reported "save, close,
      // open and it's gone" because the list was stale).
      const resp = await axios.put(`${API}/organizations/${editingOrganization.id}`, organizationFormData);
      const updated = resp.data;
      setOrganizations((prev) =>
        Array.isArray(prev)
          ? prev.map((o) => (o.id === editingOrganization.id ? { ...o, ...updated } : o))
          : prev
      );
      toast.success("Organization updated successfully");
      setShowEditOrganization(false);
      setEditingOrganization(null);
      // Background refetch in case the response missed any derived
      // fields — non-blocking.
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update organization");
    }
  };

  // Create Insurance

  // Archive User - Opens the archive workflow modal
  const handleArchiveUser = useCallback((userItem) => {
    void 0 && console.log('Archive user called for:', userItem.full_name);
    setUserToArchive(userItem);
    setShowArchiveModal(true);
  }, []);

  // Called when archive workflow completes successfully
  const handleArchiveComplete = useCallback((archivedUser) => {
    void 0 && console.log('Archive completed for:', archivedUser.full_name);
    setShowArchiveModal(false);
    setUserToArchive(null);
    setShowUserForm(false);
    setEditingUser(null);
    refreshClients(); refreshStats();
  }, []);

  // Restore User with useCallback for stable reference
  const handleRestoreUser = useCallback(async (userItem) => {
    void 0 && console.log('Restore user called for:', userItem.full_name); // Debug log
    try {
      if (userItem && userItem.id) {
        void 0 && console.log('Making API call to restore user:', userItem.id); // Debug log
        await axios.patch(`${API}/users/${userItem.id}/restore`);
        toast.success(`${userItem.full_name} restored successfully`);
        refreshClients(); refreshStats();
      }
    } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to restore user");
    }
  }, []);

  // Delete User permanently
  const handleDeleteUser = async (userId) => {
    void 0 && console.log('handleDeleteUser called with userId:', userId);
    try {
      void 0 && console.log('Making DELETE request to:', `${API}/users/${userId}`);
      await axios.delete(`${API}/users/${userId}`);
      void 0 && console.log('Delete successful');
      toast.success("User permanently deleted");
      setShowUserForm(false);
      setEditingUser(null);
      refreshNotes(); refreshClients(); refreshStats();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };
  
  // Role Management Handlers
  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, roleFormData);
        toast.success("Role updated successfully");
      } else {
        await axios.post(`${API}/roles`, roleFormData);
        toast.success("Role created successfully");
      }
      setShowAddEditRole(false);
      setRoleFormData({ name: '', description: '', permissions: [], visible_tabs: [] });
      setEditingRole(null);
      await fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save role");
    }
  };
  
  const handleDeleteRole = async (roleId) => {
    try {
      await axios.delete(`${API}/roles/${roleId}`);
      toast.success("Role deleted successfully");
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete role");
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    
    // Prevent double-submission
    if (isCreatingClient) {
      void 0 && console.log('Already creating client, ignoring duplicate call');
      return;
    }
    
    setIsCreatingClient(true);
    try {
      const response = await axios.post(`${API}/clients`, clientFormData);
      const newClient = response.data;
      
      toast.success(clientFormData.status === 'waitlist' ? "Client added to waitlist" : "Client created successfully");
      setShowClientForm(false);
      setClientFormData({
        full_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        primary_insurance: '',
        secondary_insurance: '',
        tertiary_insurance: '',
        assigned_providers: [],
        email_consent: true,
        sms_consent: false,
        status: ''
      });
      
      // Optimistic update: immediately add to waitlist if applicable
      const effectiveStatus = clientFormData.status || 'pending_intake'; // Default is pending_intake
      if (['waitlist', 'pending_intake', 'corrections_requested'].includes(effectiveStatus)) {
        setDedicatedWaitlistClients(prev => [newClient, ...prev]);
      }
      
      // Also add to main clients list optimistically
      setClients(prev => [newClient, ...prev]);
      
      // Background refresh to ensure data consistency
      refreshClients(); refreshStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create client");
    } finally {
      setIsCreatingClient(false);
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    
    if (!editingClient || !editingClient.id) {
      toast.error("No client selected for editing.");
      return;
    }
    
    try {
      await axios.put(`${API}/clients/${editingClient.id}`, clientFormData);
      toast.success("Client updated successfully");
      setShowEditClientForm(false);
      setEditingClient(null);
      setClientFormData({
        full_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        primary_insurance: '',
        secondary_insurance: '',
        tertiary_insurance: '',
        assigned_providers: [],
        email_consent: true,
        sms_consent: false,
        status: ''
      });
      
      // Optimistically update the client in the current list instead of refetching
      // This preserves the current page position
      setClients(prevClients => 
        prevClients.map(c => 
          c.id === editingClient.id 
            ? { ...c, ...clientFormData }
            : c
        )
      );
      
      // Also update allClientsForLookup for consistency
      setAllClientsForLookup(prevClients =>
        prevClients.map(c =>
          c.id === editingClient.id
            ? { ...c, ...clientFormData }
            : c
        )
      );
      
      // Also refresh waitlist if status involves waitlist
      if (clientFormData.status === 'waitlist' || clientFormData.status === 'pending_intake' || 
          editingClient?.status === 'waitlist' || editingClient?.status === 'pending_intake') {
        fetchDedicatedWaitlistClients();
      }
      
    } catch (error) {
      console.error("Error updating client:", error);
      let errorMessage = "Failed to update client";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || err.message || "Validation error").join(", ");
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  // Insurance Management Handlers
  const handleOpenInsuranceManagement = (client) => {
    setManagingInsuranceForClient(client);
    setShowInsuranceManagement(true);
  };

  // Add a 271-reported "Other Active Coverage" entry to the currently-
  // selected client's chart.  Maps the parsed Loop 2120C dict onto
  // the ClientInsuranceDialog form schema and opens the dialog with
  // the values pre-populated — biller can confirm/edit and Save.
  const handleAddOtherPayerToChart = (otherPayer) => {
    if (!selectedClient) {
      toast.error('No client selected — open the chart first.');
      return;
    }
    if (!otherPayer) return;
    setInsuranceDialogPrefill({
      company: otherPayer.name || '',
      plan: otherPayer.plan_name || '',
      member_id: otherPayer.member_id || '',
      group_number: otherPayer.group_number || '',
      start_date: otherPayer.effective_date || '',
      end_date: otherPayer.termination_date || '',
      // Default — the user will pick relationship/MSP type in the form
      // if it's anything other than "self" / "12" (Working Aged Spouse).
      relationship_to_insured: 'self',
      msp_type_code: '12',
    });
    setManagingInsuranceForClient(selectedClient);
    setShowInsuranceManagement(true);
  };

  // Optimistic insurance update handler - refreshes insurance data without full fetchData
  const handleInsuranceUpdate = async (clientId, optimisticInsurances) => {
    if (!clientId) return;

    // Jun 2026 — optimistic path.  When ClientInsuranceDialog hands us
    // the freshly-edited list (member_id change, new priority, archive
    // toggle, etc.), apply it to every consumer state slice WITHOUT
    // waiting on the server.  The dialog then calls this again with no
    // payload after its own refetch resolves, replacing the optimistic
    // rows with authoritative server data (so any backend-only fields
    // — derived flags, audit timestamps — still land correctly).
    const applyInsurances = (updatedInsurances) => {
      if (selectedClient?.id === clientId) {
        setSelectedClientInsurances(updatedInsurances);
      }
      setClients(prevClients =>
        prevClients.map(c =>
          c.id === clientId ? { ...c, insurances: updatedInsurances } : c
        )
      );
      setAllClientsForLookup(prevClients =>
        prevClients.map(c =>
          c.id === clientId ? { ...c, insurances: updatedInsurances } : c
        )
      );
      if (selectedClient?.id === clientId) {
        setSelectedClient(prev => prev ? { ...prev, insurances: updatedInsurances } : prev);
      }
      if (editingClient?.id === clientId) {
        setEditingClient(prev => prev ? { ...prev, insurances: updatedInsurances } : prev);
      }
    };

    if (Array.isArray(optimisticInsurances)) {
      applyInsurances(optimisticInsurances);
      return;
    }

    try {
      // Fetch fresh insurance data for this client (authoritative pass).
      const response = await axios.get(`${API}/clients/${clientId}/insurances`);
      const updatedInsurances = response.data || [];
      applyInsurances(updatedInsurances);

      // 5. Update editingClient if the Edit Client / Intake Wizard dialog
      //    is currently open for this client. Without this the dialog
      //    keeps showing the pre-edit insurance list until the user closes
      //    and reopens it — confusing because they JUST changed it via
      //    the nested Manage Insurances dialog.
      // (Handled by `applyInsurances` above for both paths.)
    } catch (error) {
      console.error('[Insurance Update] Error fetching updated insurances:', error);
      // Fallback to full refresh if optimistic update fails
      refreshClients({ syncOpenChart: true });
    }
  };

  // Note: Insurance CRUD handlers (handleAddInsuranceClick, handleEditInsuranceClick, handleSaveClientInsurance,
  // handleArchiveInsurance, handleReinstateInsurance, handleUploadInsuranceCardFile, drag handlers) 
  // have been moved to ClientInsuranceDialog component



  // V2 → legacy pickup: WaitlistRoute (and other V2 routes) bounce
  // back to /dashboard with `?openIntake=new|<clientId>` whenever the
  // user clicks "Add to Waitlist".  The intake wizard dialog still
  // lives here on the legacy host, so we pick the param up on mount
  // and dispatch into handleOpenIntakeWizard.
  // Edit-mode (`openIntake=<clientId>`) is also handled so the same
  // bounce works for "Edit referral" / "Re-open intake" actions.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const openIntake = params.get('openIntake');
    if (!openIntake) return;
    // Strip the param immediately so a back-button trip doesn't
    // re-fire the wizard.
    const url = new URL(window.location.href);
    url.searchParams.delete('openIntake');
    window.history.replaceState({}, '', url.toString());

    if (openIntake === 'new') {
      let mode = 'waitlist';
      try {
        const raw = sessionStorage.getItem('v2_pending_open_intake');
        if (raw) {
          const payload = JSON.parse(raw);
          if (payload?.mode) mode = payload.mode;
          sessionStorage.removeItem('v2_pending_open_intake');
        }
      } catch (_) {
        // ignore — sessionStorage may be unavailable
      }
      // Phase 3e.3a — delegate to V2 provider.  Maps the legacy
      // (null, mode) signature to the provider's (clientId, defaultStatus).
      intakeWizard.open(null, mode);
    } else {
      // Phase 3e.3a — delegate to V2 provider.
      intakeWizard.open(openIntake);
    }
  }, [user, intakeWizard]);

  // V2 → legacy bounce handler for Billing dialog opens.
  // BillingRoute (and BatchRoute) bounce to /dashboard?view=billing&review_note=<id>
  // or &open_claim=<id> when the user clicks a row that opens a legacy
  // dialog.  Strip the params on entry so a back-button trip doesn't
  // re-fire the dialog.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const reviewNoteId = params.get('review_note');
    const openClaimId = params.get('open_claim');
    if (!reviewNoteId && !openClaimId) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('review_note');
    url.searchParams.delete('open_claim');
    window.history.replaceState({}, '', url.toString());

    if (reviewNoteId) {
      try {
        fetchNoteForReview(reviewNoteId);
      } catch (e) {
        console.warn('[v2 bounce] fetchNoteForReview failed:', e);
      }
    } else if (openClaimId) {
      // Reuse the same fetcher — the review dialog opens claim-aware
      // when given a claim's source-note id.  If the caller passed a
      // raw claim id we fall back to fetching the claim detail.
      (async () => {
        try {
          const r = await axios.get(`${API}/claims/${openClaimId}`);
          if (r.data?.source_note_id) {
            await fetchNoteForReview(r.data.source_note_id);
          } else if (typeof setSelectedClaim === 'function') {
            setSelectedClaim(r.data);
          }
        } catch (e) {
          console.warn('[v2 bounce] open_claim fetch failed:', e);
        }
      })();
    }
  }, [user]);


  // Intake Wizard Handlers
  const handleOpenIntakeWizard = async (clientId = null, defaultStatus = null, options = {}) => {
    const { skipNavigation = false } = options;
    
    if (clientId) {
      try {
        // Edit mode - fetch fresh client data to ensure decryption
        const response = await axios.get(`${API}/clients/${clientId}`);
        const client = response.data;
        
        // Also fetch medications for PDF generation
        let medications = [];
        try {
          const medsResponse = await axios.get(`${API}/clients/${clientId}/medications`);
          medications = medsResponse.data || [];
        } catch (e) {
          console.warn('Could not fetch medications:', e);
        }
        
        // Update URL to include mode=edit for shareability (unless skipped for waitlist)
        if (!skipNavigation) {
          // If we're on dashboard, navigate to client page with mode=edit
          // If we're already on client page, just add mode=edit
          const currentPath = window.location.pathname;
          if (currentPath.includes('/client/')) {
            // Already on client page - just add mode=edit
            const url = new URL(window.location.href);
            url.searchParams.set('mode', 'edit');
            navigate(`${url.pathname}${url.search}`, { replace: true });
          } else {
            // On dashboard or elsewhere - navigate to client page with mode=edit
            navigate(`/client/${clientId}?mode=edit`, { replace: true });
          }
        }
        
        // Helper to parse full_name into separate fields if they're not already populated
        // This allows staff to review and correct the parsed values
        const parseFullName = (fullName) => {
          if (!fullName || typeof fullName !== 'string') return { first: '', middle: '', last: '' };
          
          const parts = fullName.trim().split(/\s+/);
          
          if (parts.length === 1) {
            // Single name - put in first name
            return { first: parts[0], middle: '', last: '' };
          } else if (parts.length === 2) {
            // First Last
            return { first: parts[0], middle: '', last: parts[1] };
          } else {
            // First [Middle...] Last - assume last word is last name, rest is first/middle
            const first = parts[0];
            const last = parts[parts.length - 1];
            const middle = parts.slice(1, -1).join(' ');
            return { first, middle, last };
          }
        };
        
        // If separate name fields are empty but full_name exists, auto-populate as suggestion
        let firstName = client.first_name || '';
        let middleName = client.middle_name || '';
        let lastName = client.last_name || '';
        
        if (!firstName && !lastName && client.full_name) {
          const parsed = parseFullName(client.full_name);
          firstName = parsed.first;
          middleName = parsed.middle;
          lastName = parsed.last;
        }
        
        setIntakeData({
          // Step 1: Demographics
          first_name: firstName,
          middle_name: middleName,
          last_name: lastName,
          preferred_name: client.preferred_name || '',
          full_name: client.full_name || '',
          date_of_birth: client.date_of_birth || '',
          ssn: client.ssn || '',
          gender: client.gender || '',
          marital_status: client.marital_status || '',
          status: client.status || 'pending_intake',
          occupation: client.occupation || '',
          race: client.race || '',
          ethnicity: client.ethnicity || '',
          language: client.language || '',
          language_other: client.language_other || '',
          address_line1: client.address_line1 || '',
          address_line2: client.address_line2 || '',
          city: client.city || '',
          state: client.state || '',
          zipcode: client.zipcode || '',
          // Insurance address (separate from mailing)
          use_separate_insurance_address: client.use_separate_insurance_address || false,
          insurance_address_line1: client.insurance_address_line1 || '',
          insurance_address_line2: client.insurance_address_line2 || '',
          insurance_city: client.insurance_city || '',
          insurance_state: client.insurance_state || '',
          insurance_zipcode: client.insurance_zipcode || '',
          phone: client.phone || '',
          phone_secondary: client.phone_secondary || '',
          email: client.email || '',
          // Step 2: Guardian info
          has_guardian: client.has_guardian || false,
          is_minor_or_guardian_present: client.is_minor_or_guardian_present || false,
          guardian_name: client.guardian_name || '',
          guardian_contact: client.guardian_contact || '',
          guardian_phone: client.guardian_phone || '',
          guardian_email: client.guardian_email || '',
          guardian_same_household: client.guardian_same_household || false,
          // Step 3: Referral
          referral_source: client.referral_source || '',
          referral_contact: client.referral_contact || '',
          referral_organization: client.referral_organization || '',
          referral_phone: client.referral_phone || '',
          referral_email: client.referral_email || '',
          referral_type: client.referral_type || '',
          reason_for_referral: client.reason_for_referral || '',
          symptoms_checklist: client.symptoms_checklist || [],
          // Step 4: Preferences
          service_requested: client.service_requested || '',
          preferred_mode: client.preferred_mode || '',
          preferred_location: client.preferred_location || '',
          preferred_provider_type: client.preferred_provider_type || '',
          ok_to_call_times: client.ok_to_call_times || '',
          pets_in_home: client.pets_in_home || '',
          pets_description: client.pets_description || '',
          // Step 5: Medical
          has_pcp: client.has_pcp || '',
          pcp_name: client.pcp_name || '',
          pcp_phone: client.pcp_phone || '',
          pcp_address: client.pcp_address || '',
          last_physical_date: client.last_physical_date || '',
          medical_alerts: client.medical_alerts || '',
          allergies: client.allergies || '',
          interpreter_needed: client.interpreter_needed || false,
          // Step 6: Substance recovery
          requesting_substance_services: client.requesting_substance_services || '',
          requires_24q_assessment: client.requires_24q_assessment || '',
          assessment_date: client.assessment_date || '',
          recorded_bac: client.recorded_bac || '',
          // Pharmacy info
          pharmacy_name: client.pharmacy_name || '',
          pharmacy_phone: client.pharmacy_phone || '',
          pharmacy_fax: client.pharmacy_fax || '',
          pharmacy_address: client.pharmacy_address || '',
          // Medications will be fetched separately
          // Step 7: Insurance
          // Determine payment_type from insurances array
          payment_type: (() => {
            const activeInsurances = (client.insurances || []).filter(ins => ins.status === 'active');
            return activeInsurances.length > 0 ? 'has_insurance' : 'self_pay';
          })(),
          primary_insurance: client.primary_insurance || '',
          primary_member_id: client.primary_member_id || '',
          primary_group_number: client.primary_group_number || '',
          primary_relationship_to_insured: client.primary_relationship_to_insured || '',
          primary_insured_name: client.primary_insured_name || '',
          primary_insured_ssn: client.primary_insured_ssn || '',
          primary_insured_dob: client.primary_insured_dob || '',
          primary_card_front: client.primary_card_front || '',
          primary_card_back: client.primary_card_back || '',
          has_secondary_insurance: client.secondary_insurance ? true : false,
          secondary_insurance: client.secondary_insurance || '',
          secondary_member_id: client.secondary_member_id || '',
          secondary_group_number: client.secondary_group_number || '',
          secondary_relationship_to_insured: client.secondary_relationship_to_insured || '',
          secondary_insured_name: client.secondary_insured_name || '',
          secondary_insured_ssn: client.secondary_insured_ssn || '',
          secondary_insured_dob: client.secondary_insured_dob || '',
          secondary_card_front: client.secondary_card_front || '',
          secondary_card_back: client.secondary_card_back || '',
          has_tertiary_insurance: client.tertiary_insurance ? true : false,
          tertiary_insurance: client.tertiary_insurance || '',
          tertiary_member_id: client.tertiary_member_id || '',
          tertiary_group_number: client.tertiary_group_number || '',
          tertiary_relationship_to_insured: client.tertiary_relationship_to_insured || '',
          tertiary_insured_name: client.tertiary_insured_name || '',
          tertiary_insured_ssn: client.tertiary_insured_ssn || '',
          tertiary_insured_dob: client.tertiary_insured_dob || '',
          tertiary_card_front: client.tertiary_card_front || '',
          tertiary_card_back: client.tertiary_card_back || '',
          // Step 8: Emergency contacts - convert from array to individual fields
          emergency_contact_1_name: client.emergency_contacts?.[0]?.name || '',
          emergency_contact_1_relationship: client.emergency_contacts?.[0]?.relationship || '',
          emergency_contact_1_relationship_other: '',
          emergency_contact_1_phone: client.emergency_contacts?.[0]?.phone || '',
          emergency_contact_1_email: client.emergency_contacts?.[0]?.email || '',
          emergency_contact_2_name: client.emergency_contacts?.[1]?.name || '',
          emergency_contact_2_relationship: client.emergency_contacts?.[1]?.relationship || '',
          emergency_contact_2_relationship_other: '',
          emergency_contact_2_phone: client.emergency_contacts?.[1]?.phone || '',
          emergency_contact_2_email: client.emergency_contacts?.[1]?.email || '',
          // Provider assignment - now array
          assigned_providers: client.assigned_providers || []
        });
        // March 2026 fix: merge any unsaved intake draft on top of the
        // persisted client snapshot so reopening the wizard shows the
        // user's in-progress edits.  Without this, "Save Draft" + close +
        // reopen appeared to wipe the data because `intake_draft.data`
        // lived on the client doc but was never re-read by this handler.
        if (client.intake_draft && client.intake_draft.data && typeof client.intake_draft.data === 'object') {
          setIntakeData(prev => ({ ...prev, ...client.intake_draft.data }));
        }
        if (client.intake_last_saved) {
          setIntakeLastSaved(new Date(client.intake_last_saved));
        } else {
          setIntakeLastSaved(null);
        }
        // Add medications to the client object for PDF generation
        const clientWithMeds = { ...client, medications };
        setEditingClient(clientWithMeds);
        setIntakeStep(1);
        setShowIntakeWizard(true);
      } catch (error) {
        console.error("Error loading client for edit:", error);
        toast.error("Failed to load client data");
      }
    } else {
      // New client - reset form with optional default status
      setIntakeData(defaultStatus ? { status: defaultStatus } : {});
      setEditingClient(null);
      setIntakeStep(1);
      setShowIntakeWizard(true);
    }
  };

  const handleCloseIntakeWizard = () => {
    setShowIntakeWizard(false);
    setIntakeClientId(null);
    setIntakeStep(1);
    setIntakeData({});
    setIntakeLastSaved(null);
    setEditingClient(null);
    setRealtimeDuplicateWarnings([]); // Clear real-time duplicate warnings
    setPendingIntakeInsurances([]); // Clear pending insurances
    
    // Remove mode=edit from URL if present
    const url = new URL(window.location.href);
    if (url.searchParams.get('mode') === 'edit') {
      url.searchParams.delete('mode');
      // If on client page, stay there without mode param
      // If URL has no search params left, navigate cleanly
      const newSearch = url.search || '';
      navigate(`${url.pathname}${newSearch}`, { replace: true });
    }
  };

  const handleIntakeFieldChange = (field, value) => {
    setIntakeData(prevData => {
      const newData = {
        ...prevData,
        [field]: value
      };
      
      // Auto-update full_name when first, middle, or last name changes
      if (field === 'first_name' || field === 'middle_name' || field === 'last_name') {
        const firstName = field === 'first_name' ? value : (prevData.first_name || '');
        const middleName = field === 'middle_name' ? value : (prevData.middle_name || '');
        const lastName = field === 'last_name' ? value : (prevData.last_name || '');
        newData.full_name = `${firstName} ${middleName} ${lastName}`.trim().replace(/\s+/g, ' ');
      }
      
      return newData;
    });
  };

  const handleSaveIntakeDraft = async (dataOverride = null) => {
    // Prefer draft data passed from the dialog (live local state) over the
    // stale parent `intakeData` which is only seeded on dialog open.
    const data = dataOverride && typeof dataOverride === 'object' ? dataOverride : intakeData;
    try {
      if (!intakeClientId) {
        // Create client first with pending_intake status
        const response = await axios.post(`${API}/clients`, {
          full_name: data.full_name || 'Pending',
          first_name: data.first_name || '',
          middle_name: data.middle_name || '',
          last_name: data.last_name || '',
          status: 'pending_intake',
          created_by: user.id
        });
        setIntakeClientId(response.data.id);
        
        // Save draft
        await axios.post(`${API}/clients/${response.data.id}/intake-draft`, {
          current_step: 1, // No longer using steps
          data: data
        });
      } else {
        await axios.post(`${API}/clients/${intakeClientId}/intake-draft`, {
          current_step: 1, // No longer using steps
          data: data
        });
      }
      setIntakeLastSaved(new Date());
      // Mirror back to intakeData so other App.js consumers see the latest.
      setIntakeData(data);
      toast.success("Draft saved successfully");
    } catch (error) {
      toast.error("Failed to save draft");
    }
  };

  const handleGenerateIntakePDF = async (dataOverride = null) => {
    // Prefer draft data passed from the dialog over stale parent state
    const data = (dataOverride && typeof dataOverride === 'object') ? dataOverride : intakeData;
    if (dataOverride && typeof dataOverride === 'object') {
      setIntakeData(dataOverride);
    }
    try {
      // Ensure locations are loaded before generating PDF
      let currentLocations = locations;
      if (!locations || locations.length === 0) {
        console.warn('[PDF] Locations not loaded, fetching...');
        try {
          const locResp = await axios.get(`${API}/locations`);
          if (locResp.data) {
            setLocations(locResp.data);
            currentLocations = locResp.data;
          }
        } catch (e) {
          console.error('[PDF] Failed to fetch locations:', e);
        }
      }
      
      // Ensure branding settings are loaded for header/footer
      let currentBrandingSettings = brandingSettings;
      if (!brandingSettings || !brandingSettings.company_name) {
        console.warn('[PDF] Branding settings not loaded, fetching...');
        try {
          const brandingResp = await axios.get(`${API}/admin/branding-settings`);
          if (brandingResp.data) {
            setBrandingSettings(brandingResp.data);
            currentBrandingSettings = brandingResp.data;
          }
        } catch (e) {
          console.error('[PDF] Failed to fetch branding settings:', e);
        }
      }
      
      // CRITICAL: Ensure intake form config is loaded for full PDF generation
      let currentIntakeFormConfig = intakeFormConfig;
      if (!intakeFormConfig || !intakeFormConfig.steps) {
        console.warn('[PDF] Intake form config not loaded, fetching...');
        try {
          const configResp = await axios.get(`${API}/config/intake-form`);
          if (configResp.data) {
            setIntakeFormConfig(configResp.data);
            currentIntakeFormConfig = configResp.data;
          }
        } catch (e) {
          console.error('[PDF] Failed to fetch intake form config:', e);
        }
      }
      
      // Generate PDF using the utility function
      // PERF: Use `data` (live from dialog) not `intakeData` (parent state, potentially stale).
      const { blob: pdfBlob, fileName, currentDate } = await generateIntakePdf({
        intakeData: data,
        editingClient,
        intakeFormConfig: currentIntakeFormConfig,
        locations: currentLocations,
        brandingSettings: currentBrandingSettings
      });
      
      // Determine client ID (either from editing client or intake client)
      const clientId = editingClient?.id || intakeClientId;
      
      if (!clientId) {
        toast.error('Please save the client first before generating PDF');
        return;
      }
      
      // Upload document to backend
      const formData = new FormData();
      formData.append('file', pdfBlob, fileName);
      formData.append('description', `Referral Form (${currentDate})`);
      formData.append('form_type', 'Intake Paperwork');
      
      await axios.post(`${API}/clients/${clientId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`PDF generated and saved to Documents: ${fileName}`);
      
      // Documents are client-specific, no global fetchData needed
      
      // If we're viewing this client's details, also refresh documents
      if (selectedClient && selectedClient.id === clientId) {
        const documentsResponse = await axios.get(`${API}/clients/${clientId}/documents`);
        setClientDocuments(documentsResponse.data);
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Keyword-to-category mapping for auto-categorization
  const categoryKeywords = {
    'ROI': ['roi', 'release of information', 'release information'],
    'Consent': ['consent', 'informed consent', 'consent form', 'consent to treat'],
    'Progress Notes': ['progress note', 'progress notes', 'session note', 'clinical note'],
    'Treatment Plan': ['treatment plan', 'tx plan', 'treatment planning'],
    'Assessment': ['assessment', 'evaluation', 'cans', 'phq', 'gad', 'screening'],
    'Intake': ['intake', 'admission', 'new client', 'initial'],
    'Authorization': ['authorization', 'auth', 'prior auth', 'pa form'],
    'Insurance': ['insurance', 'eligibility', 'coverage', 'benefits'],
    'Discharge': ['discharge', 'termination', 'closure', 'closing'],
    'Referral': ['referral', 'refer'],
    'Medical Records': ['medical record', 'health record', 'chart'],
    'Correspondence': ['letter', 'correspondence', 'communication'],
    'Legal': ['legal', 'court', 'subpoena', 'custody'],
    'Safety Plan': ['safety plan', 'crisis plan', 'safety'],
    'Lab Results': ['lab', 'labs', 'laboratory', 'blood work', 'test results'],
    'Prescription': ['prescription', 'rx', 'medication', 'script'],
    'IEP': ['iep', 'individualized education', '504'],
    'School Records': ['school', 'academic', 'report card', 'transcript'],
  };

  const suggestCategoryFromFilename = (filename) => {
    const lowerFilename = filename.toLowerCase();
    
    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      for (const keyword of keywords) {
        if (lowerFilename.includes(keyword)) {
          // Find matching form type from available formTypes
          const matchingFormType = formTypes?.find(ft => 
            ft.name.toLowerCase().includes(category.toLowerCase()) ||
            category.toLowerCase().includes(ft.name.toLowerCase())
          );
          return matchingFormType?.name || null;
        }
      }
    }
    return null;
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Check file sizes (100MB limit per file - increased from 25MB)
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      toast.error(`${oversizedFiles.length} file(s) exceed 100MB limit. Please choose smaller files.`);
      return;
    }

    // Check for duplicate filenames against existing client documents
    const existingFilenames = (clientDocuments || []).map(doc => doc.filename?.toLowerCase());
    const duplicates = files.filter(file => existingFilenames.includes(file.name.toLowerCase()));
    
    if (duplicates.length > 0) {
      setDuplicateFileWarnings(duplicates.map(f => f.name));
    } else {
      setDuplicateFileWarnings([]);
    }

    // Auto-suggest category from first file's name (if no category selected yet)
    if (!documentFormType && files.length > 0) {
      const suggested = suggestCategoryFromFilename(files[0].name);
      if (suggested) {
        setSuggestedCategory(suggested);
        // Auto-fill the category dropdown
        setDocumentFormType(suggested);
        toast.info(`Auto-suggested category: ${suggested}`, { duration: 3000 });
      } else {
        setSuggestedCategory(null);
      }
    }

    // Store the selected files
    setSelectedFiles(files);
  };

  const handleConfirmUpload = async () => {
    if (selectedFiles.length === 0) return;

    // Create ghost/loading documents for optimistic UI
    const ghostDocs = selectedFiles.map((file, index) => ({
      id: `uploading-${Date.now()}-${index}`,
      filename: file.name,
      file_type: file.type || 'application/octet-stream',
      file_size: file.size,
      form_type: documentFormType || null,
      uploaded_at: new Date().toISOString(),
      isUploading: true, // Flag to show loading state
      doc_number: null
    }));

    try {
      setUploadingDocument(true);
      
      // Optimistic update - show ghost documents immediately
      setClientDocuments(prev => [...ghostDocs, ...prev]);
      
      const formData = new FormData();
      
      // Add all files
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      // Add form type if selected
      if (documentFormType) {
        formData.append('form_type', documentFormType);
      }
      
      const response = await axios.post(`${API}/clients/${selectedClient.id}/documents/bulk`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Get uploaded documents from response (includes doc_number)
      const uploadedDocs = response.data.documents || [];
      const failedUploads = response.data.failed || [];
      
      // Replace ghost documents with real ones from response
      setClientDocuments(prev => {
        // Remove ghost documents
        const withoutGhosts = prev.filter(d => !d.isUploading);
        // Add real uploaded documents at the beginning
        return [...uploadedDocs, ...withoutGhosts];
      });
      
      if (failedUploads.length > 0) {
        toast.error(`${failedUploads.length} file(s) failed to upload — ${failedUploads[0].reason}`, { duration: 10000 });
      }
      if (uploadedDocs.length > 0) {
        toast.success(`${uploadedDocs.length} document(s) uploaded successfully!`);
      }
      
      // Reset form
      setShowUploadDocument(false);
      setDocumentDescription('');
      setDocumentFormType('');
      setSelectedFiles([]);
      setDuplicateFileWarnings([]);
      setSuggestedCategory(null);
      
    } catch (error) {
      console.error('Error uploading documents:', error);
      
      // Remove ghost documents on error
      setClientDocuments(prev => prev.filter(d => !d.isUploading));
      
      toast.error('Failed to upload documents: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleUpdateDocumentFormType = async (documentId, newFormType) => {
    try {
      const formData = new FormData();
      formData.append('form_type', newFormType || '');
      
      await axios.put(`${API}/documents/${documentId}`, formData);
      
      toast.success('Document category updated');
      
      // Documents are client-specific - refresh client docs if viewing
      if (selectedClient?.id) {
        try {
          const docsRes = await axios.get(`${API}/clients/${selectedClient.id}/documents`);
          setClientDocuments(docsRes.data || []);
        } catch (e) { /* non-critical */ }
      }
      
      // Reset editing state
      setEditingDocumentFormType(null);
      setNewDocumentFormType('');
      
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Failed to update document');
    }
  };

  const handleCompleteIntake = async (dataOverride = null) => {
    void 0 && console.log('=== handleCompleteIntake called ===');
    
    // Prevent double-submission
    if (intakeAutoSaving) {
      void 0 && console.log('Already submitting, ignoring duplicate call');
      return;
    }
    
    // PERF: the dialog owns its own draft state. When it passes `dataOverride`,
    // it's the live/latest form data — we mirror it to parent state so the
    // rest of the handler reads the latest values via the ref.
    const data = (dataOverride && typeof dataOverride === 'object') ? dataOverride : intakeData;
    if (dataOverride && typeof dataOverride === 'object') {
      setIntakeData(dataOverride);
    }
    
    // Validate all required fields before submission
    const fieldsToValidate = ['date_of_birth', 'phone', 'zipcode', 'email', 'ssn'];
    let hasErrors = false;
    const newErrors = {};
    
    fieldsToValidate.forEach(field => {
      const value = data[field];
      let error = null;
      
      switch(field) {
        case 'email':
          error = validateEmail(value);
          break;
        case 'phone':
          error = validatePhone(value);
          break;
        case 'zipcode':
          error = validateZipCode(value);
          break;
        case 'ssn':
          error = validateSSN(value);
          break;
        case 'date_of_birth':
          error = validateDateOfBirth(value);
          break;
      }
      
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });
    
    if (hasErrors) {
      setIntakeValidationErrors(newErrors);
      toast.error("Please fix validation errors before submitting");
      return;
    }
    
    // Show loading state
    setIntakeAutoSaving(true);
    
    try {
      // Prepare client data with proper field mapping
      // PERF: Use `data` (live from dialog) not `intakeData` (parent state, potentially stale).
      const clientData = {
        ...data,
        // Preserve the status from data if set (e.g., 'waitlist'), otherwise default to 'active'
        status: data.status || 'active',
        intake_completed: true
      };
      
      // Ensure assigned_providers is an array (already handled in form)
      if (!clientData.assigned_providers) {
        clientData.assigned_providers = [];
      }
      
      // Convert emergency contact fields to emergency_contacts array
      const emergencyContacts = [];
      
      // Emergency Contact 1
      if (data.emergency_contact_1_name) {
        emergencyContacts.push({
          name: data.emergency_contact_1_name,
          relationship: data.emergency_contact_1_relationship === 'Other' 
            ? data.emergency_contact_1_relationship_other || 'Other'
            : data.emergency_contact_1_relationship || '',
          phone: data.emergency_contact_1_phone || null,
          email: data.emergency_contact_1_email || null,
          can_receive_info: false,
          release_on_file: false
        });
      }
      
      // Emergency Contact 2
      if (data.emergency_contact_2_name) {
        emergencyContacts.push({
          name: data.emergency_contact_2_name,
          relationship: data.emergency_contact_2_relationship === 'Other'
            ? data.emergency_contact_2_relationship_other || 'Other'
            : data.emergency_contact_2_relationship || '',
          phone: data.emergency_contact_2_phone || null,
          email: data.emergency_contact_2_email || null,
          can_receive_info: false,
          release_on_file: false
        });
      }
      
      if (emergencyContacts.length > 0) {
        clientData.emergency_contacts = emergencyContacts;
      }
      
      // Remove individual contact fields from clientData
      delete clientData.emergency_contact_1_name;
      delete clientData.emergency_contact_1_relationship;
      delete clientData.emergency_contact_1_relationship_other;
      delete clientData.emergency_contact_1_phone;
      delete clientData.emergency_contact_1_email;
      delete clientData.emergency_contact_2_name;
      delete clientData.emergency_contact_2_relationship;
      delete clientData.emergency_contact_2_relationship_other;
      delete clientData.emergency_contact_2_phone;
      delete clientData.emergency_contact_2_email;
      
      // Remove medications from client data - they're stored separately via /api/clients/{id}/medications
      // Keeping them here would cause validation issues on the backend
      delete clientData.medications;
      
      let clientId;
      
      if (editingClient) {
        // Update existing client
        await axios.put(`${API}/clients/${editingClient.id}`, clientData);
        clientId = editingClient.id;
        
        // If guardian info exists, create/update contact
        void 0 && console.log('Checking guardian info:', {
          has_guardian: data.has_guardian,
          guardian_name: data.guardian_name,
          guardian_phone: data.guardian_phone,
          guardian_email: data.guardian_email
        });
        
        if (data.has_guardian && data.guardian_name) {
          try {
            void 0 && console.log('Creating/updating guardian contact for client:', clientId);
            
            const guardianContact = {
              name: data.guardian_name,
              relationship: 'parent',
              phone: data.guardian_phone || null,
              email: data.guardian_email || null,
              can_receive_info: false,
              release_on_file: false,
              is_minor_or_legal_guardian: true,
              lives_in_same_household: data.guardian_same_household || false
            };
            
            void 0 && console.log('Guardian contact data:', guardianContact);
            
            // Check if guardian contact already exists (by name)
            const existingContacts = await axios.get(`${API}/clients/${clientId}/contacts`);
            void 0 && console.log('Existing contacts:', existingContacts.data);
            
            const existingGuardian = existingContacts.data.find(c => 
              c.name === data.guardian_name && (c.relationship === 'parent' || c.relationship === 'guardian')
            );
            
            if (existingGuardian) {
              // Update existing guardian contact
              void 0 && console.log('Updating existing guardian contact:', existingGuardian.id);
              await axios.put(`${API}/contacts/${existingGuardian.id}`, guardianContact);
              void 0 && console.log('Guardian contact updated successfully');
            } else {
              // Create new guardian contact
              void 0 && console.log('Creating new guardian contact');
              await axios.post(`${API}/clients/${clientId}/contacts`, guardianContact);
              void 0 && console.log('Guardian contact created successfully');
            }
            
            // If guardian should also be emergency contact, add them
            if (data.guardian_is_emergency_contact) {
              void 0 && console.log('Adding guardian as emergency contact');
              const emergencyContact = {
                name: data.guardian_name,
                relationship: 'parent',
                phone: data.guardian_phone || null,
                email: data.guardian_email || null
              };
              
              // Check if emergency contact already exists
              const existingEmergencyContacts = await axios.get(`${API}/clients/${clientId}/emergency-contacts`);
              const existingEmergency = existingEmergencyContacts.data.find(c => 
                c.name === data.guardian_name && (c.relationship === 'parent' || c.relationship === 'guardian')
              );
              
              if (!existingEmergency) {
                await axios.post(`${API}/clients/${clientId}/emergency-contacts`, emergencyContact);
                void 0 && console.log('Guardian added as emergency contact successfully');
              } else {
                void 0 && console.log('Guardian already exists as emergency contact');
              }
            }
          } catch (contactError) {
            console.error('Failed to create/update guardian contact:', contactError);
            console.error('Error response:', contactError.response?.data);
            // Don't fail the whole operation if contact creation fails
          }
        } else {
          void 0 && console.log('Guardian info not complete, skipping contact creation');
        }
        
        toast.success("Client updated successfully!");
        handleCloseIntakeWizard();
        refreshClients({ syncOpenChart: true }); refreshStats();
      } else {
        // Check for duplicates before creating new client
        void 0 && console.log('Checking for duplicates...', {
          full_name: clientData.full_name,
          date_of_birth: clientData.date_of_birth
        });
        
        setCheckingDuplicates(true);
        
        try {
          const params = new URLSearchParams();
          if (clientData.full_name) params.append('full_name', clientData.full_name);
          if (clientData.date_of_birth) params.append('date_of_birth', clientData.date_of_birth);
          if (clientData.ssn) params.append('ssn', clientData.ssn);
          if (clientData.email) params.append('email', clientData.email);
          if (clientData.phone) params.append('phone', clientData.phone);
          
          const duplicateCheck = await axios.post(`${API}/clients/check-duplicates?${params.toString()}`, {}, {
            timeout: 30000 // 30 second timeout
          });
          
          setCheckingDuplicates(false);
          void 0 && console.log('Duplicate check result:', duplicateCheck.data);
          
          if (duplicateCheck.data && duplicateCheck.data.has_duplicates) {
            // Show duplicate warning dialog
            void 0 && console.log('Duplicates found, showing warning dialog');
            setIntakePotentialDuplicates(duplicateCheck.data.duplicates);
            setPendingClientData(clientData);
            setShowDuplicateWarning(true);
            return; // Stop here and wait for user decision
          } else {
            // No duplicates, proceed with creation
            void 0 && console.log('No duplicates, creating client');
          }
        } catch (duplicateError) {
          setCheckingDuplicates(false);
          console.error('Duplicate check failed:', duplicateError);
          // If duplicate check fails, proceed anyway with a warning
          toast.warning('Unable to check for duplicates, proceeding with client creation.');
        }
        
        // Create client
        void 0 && console.log('Creating client');
        const response = await axios.post(`${API}/clients`, clientData);
        clientId = response.data.id;
        
        // If guardian info exists, create contact
        void 0 && console.log('Checking guardian info for new client:', {
          has_guardian: data.has_guardian,
          guardian_name: data.guardian_name,
          guardian_phone: data.guardian_phone,
          guardian_email: data.guardian_email
        });
        
        if (data.has_guardian && data.guardian_name) {
          try {
            void 0 && console.log('Creating guardian contact for new client:', clientId);
            
            const guardianContact = {
              name: data.guardian_name,
              relationship: 'parent',
              phone: data.guardian_phone || null,
              email: data.guardian_email || null,
              can_receive_info: false,
              release_on_file: false,
              is_minor_or_legal_guardian: true,
              lives_in_same_household: data.guardian_same_household || false
            };
            
            void 0 && console.log('Guardian contact data:', guardianContact);
            await axios.post(`${API}/clients/${clientId}/contacts`, guardianContact);
            void 0 && console.log('Guardian contact created successfully');
            
            // If guardian should also be emergency contact, add them
            if (data.guardian_is_emergency_contact) {
              void 0 && console.log('Adding guardian as emergency contact for new client');
              const emergencyContact = {
                name: data.guardian_name,
                relationship: 'Parent/Guardian',
                phone: data.guardian_phone || null,
                email: data.guardian_email || null
              };
              
              await axios.post(`${API}/clients/${clientId}/emergency-contacts`, emergencyContact);
              void 0 && console.log('Guardian added as emergency contact successfully');
            }
          } catch (contactError) {
            console.error('Failed to create guardian contact:', contactError);
            console.error('Error response:', contactError.response?.data);
            // Don't fail the whole operation if contact creation fails
          }
        } else {
          void 0 && console.log('Guardian info not complete for new client, skipping contact creation');
        }
        
        // Create service enrollments if any were pending
        const pendingEnrollments = data.pending_service_enrollments || pendingServiceEnrollments;
        if (pendingEnrollments && Object.keys(pendingEnrollments).length > 0) {
          void 0 && console.log('Creating pending service enrollments for new client:', clientId, pendingEnrollments);
          
          for (const providerId of Object.keys(pendingEnrollments)) {
            const services = pendingEnrollments[providerId] || [];
            for (const serviceId of services) {
              try {
                await axios.post(`${API}/clients/${clientId}/service-enrollments`, {
                  provider_id: providerId,
                  service_type: serviceId,
                  visit_frequency: "weekly"
                }, {
                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                });
                void 0 && console.log(`Created service enrollment: ${providerId} - ${serviceId}`);
              } catch (enrollmentError) {
                // Handle duplicate enrollment error gracefully
                if (enrollmentError.response?.status === 400 || enrollmentError.response?.status === 409) {
                  void 0 && console.log(`Skipping duplicate enrollment: ${providerId} - ${serviceId}`);
                } else {
                  console.error('Failed to create service enrollment:', enrollmentError);
                }
              }
            }
          }
          
          // Clear pending enrollments
          setPendingServiceEnrollments({});
        }
        
        // Create insurance policies if any were added during intake
        if (pendingIntakeInsurances && pendingIntakeInsurances.length > 0) {
          void 0 && console.log('Creating pending insurances for new client:', clientId, pendingIntakeInsurances);
          
          for (const insurance of pendingIntakeInsurances) {
            try {
              await axios.post(`${API}/clients/${clientId}/insurances`, insurance, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
              });
              void 0 && console.log(`Created insurance: ${insurance.company} (${insurance.priority})`);
            } catch (insuranceError) {
              console.error('Failed to create insurance:', insuranceError);
              // Don't fail the whole operation if insurance creation fails
              toast.warning(`Failed to add insurance: ${insurance.company}. You can add it from the client's profile.`);
            }
          }
          
          // Clear pending insurances
          setPendingIntakeInsurances([]);
        }
        
        // Create medications if any were added during intake
        if (data.medications && data.medications.length > 0) {
          void 0 && console.log('Creating medications for new client:', clientId, data.medications);
          
          for (const medication of data.medications) {
            if (medication.name) { // Only create if medication has a name
              try {
                await axios.post(`${API}/clients/${clientId}/medications`, {
                  name: medication.name,
                  dosage: medication.dosage || '',
                  frequency: medication.frequency || '',
                  prescriber: medication.prescriber || '',
                  is_active: true,
                  start_date: new Date().toISOString().split('T')[0]
                }, {
                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                });
                void 0 && console.log(`Created medication: ${medication.name}`);
              } catch (medError) {
                console.error('Failed to create medication:', medError);
                // Don't fail the whole operation if medication creation fails
              }
            }
          }
        }
        
        toast.success(
          data.status === 'waitlist' 
            ? "Client added to waitlist successfully!" 
            : "Client intake completed successfully!"
        );
        handleCloseIntakeWizard();
        refreshClients({ syncOpenChart: true }); refreshStats();
        // Also refresh waitlist if client was added to waitlist
        if (data.status === 'waitlist' || data.status === 'pending_intake') {
          fetchDedicatedWaitlistClients();
        }
      }
    } catch (error) {
      console.error("Error saving client:", error);
      // Handle validation errors properly — include field name so users know which field is missing/invalid
      let errorMessage = "Failed to save client";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors: include the field name from `loc` path
          errorMessage = detail.map(err => {
            const field = Array.isArray(err.loc) && err.loc.length > 0
              ? err.loc[err.loc.length - 1]
              : null;
            // Humanize snake_case -> Title Case
            const label = field
              ? String(field).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
              : null;
            return label ? `${label}: ${err.msg}` : err.msg;
          }).join("; ");
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      // Always hide loading state
      setIntakeAutoSaving(false);
    }
  };
  
  const handleCreateClientAnyway = async () => {
    // Prevent double-submission
    if (isCreatingClient) {
      void 0 && console.log('Already creating client, ignoring duplicate call');
      return;
    }
    
    setIsCreatingClient(true);
    try {
      const response = await axios.post(`${API}/clients`, pendingClientData);
      const clientId = response.data.id;
      
      // Create service enrollments if any were pending
      const pendingEnrollments = intakeData.pending_service_enrollments || pendingServiceEnrollments;
      if (pendingEnrollments && Object.keys(pendingEnrollments).length > 0) {
        void 0 && console.log('Creating pending service enrollments for new client (duplicate bypass):', clientId, pendingEnrollments);
        
        for (const providerId of Object.keys(pendingEnrollments)) {
          const services = pendingEnrollments[providerId] || [];
          for (const serviceId of services) {
            try {
              await axios.post(`${API}/clients/${clientId}/service-enrollments`, {
                provider_id: providerId,
                service_type: serviceId,
                visit_frequency: "weekly"
              }, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
              });
              void 0 && console.log(`Created service enrollment: ${providerId} - ${serviceId}`);
            } catch (enrollmentError) {
              if (enrollmentError.response?.status === 400 || enrollmentError.response?.status === 409) {
                void 0 && console.log(`Skipping duplicate enrollment: ${providerId} - ${serviceId}`);
              } else {
                console.error('Failed to create service enrollment:', enrollmentError);
              }
            }
          }
        }
        
        // Clear pending enrollments
        setPendingServiceEnrollments({});
      }
      
      // Create insurance policies if any were added during intake (duplicate bypass)
      if (pendingIntakeInsurances && pendingIntakeInsurances.length > 0) {
        void 0 && console.log('Creating pending insurances for new client (duplicate bypass):', clientId, pendingIntakeInsurances);
        
        for (const insurance of pendingIntakeInsurances) {
          try {
            await axios.post(`${API}/clients/${clientId}/insurances`, insurance, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            });
            void 0 && console.log(`Created insurance: ${insurance.company} (${insurance.priority})`);
          } catch (insuranceError) {
            console.error('Failed to create insurance:', insuranceError);
            toast.warning(`Failed to add insurance: ${insurance.company}. You can add it from the client's profile.`);
          }
        }
        
        // Clear pending insurances
        setPendingIntakeInsurances([]);
      }
      
      toast.success("Client created successfully!");
      setShowDuplicateWarning(false);
      setPendingClientData(null);
      setIntakePotentialDuplicates([]);
      handleCloseIntakeWizard();
      refreshClients({ syncOpenChart: true }); refreshStats();
      // Also refresh waitlist if client was added to waitlist
      if (pendingClientData?.status === 'waitlist' || pendingClientData?.status === 'pending_intake') {
        fetchDedicatedWaitlistClients();
      }
    } catch (error) {
      console.error("Error creating client:", error);
      // Handle validation errors properly
      let errorMessage = "Failed to create client";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg).join(", ");
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsCreatingClient(false);
    }
  };

  const handleUploadInsuranceCard = async (insuranceType, side, file) => {
    try {
      if (!intakeClientId) {
        toast.error("Please save draft first");
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('insurance_type', insuranceType);
      formData.append('side', side);

      const response = await axios.post(
        `${API}/clients/${intakeClientId}/insurance-card`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update intake data with file path
      handleIntakeFieldChange(`${insuranceType}_card_${side}`, response.data.file_path);
      toast.success(`${insuranceType} card ${side} uploaded successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload card");
    }
  };

  // Validate CPT conflict rules
  const validateCPTConflicts = (cptCode, clientId, appointmentDate) => {
    if (!cptCode || !clientId || !appointmentDate) return { valid: true };
    
    const appointmentDay = new Date(appointmentDate).toDateString();
    
    // Find all applicable conflict rules for this CPT code
    const applicableRules = cptConflictRules.filter(rule => 
      rule.is_active && rule.cpt_codes.includes(cptCode)
    );
    
    if (applicableRules.length === 0) return { valid: true };
    
    // Check for conflicts with existing appointments
    for (const rule of applicableRules) {
      const maxPerDay = rule.max_per_day || 1;
      
      // Get all appointments for this client on the same day with conflicting CPT codes.
      // Feb 2026 — exclude cancelled/no_show statuses so a slot that was vacated
      // by a cancellation doesn't keep blocking new bookings of the same CPT.
      const conflictingAppts = appointments.filter(apt => {
        if (apt.client_id !== clientId) return false;
        if (editingAppointment && apt.id === editingAppointment.id) return false; // Skip self
        if (apt.status === 'cancelled' || apt.status === 'client_cancelled' || apt.status === 'no_show') return false;
        
        const aptDay = new Date(apt.start_time).toDateString();
        if (aptDay !== appointmentDay) return false;
        
        // Extract CPT code from service_type
        const aptCptCode = apt.service_type?.split(' - ')[0]?.trim();
        return rule.cpt_codes.includes(aptCptCode);
      });
      
      // Check if number of existing appointments meets or exceeds the max_per_day limit
      if (conflictingAppts.length >= maxPerDay) {
        const limitMsg = maxPerDay === 1 
          ? 'This client already has an appointment with this service on' 
          : `This client already has ${conflictingAppts.length} appointment(s) with this service (max ${maxPerDay}/day) on`;
        return {
          valid: false,
          canOverride: rule.can_override,
          message: `⚠️ CPT Code Limit\n\n${rule.rule_name}\n\n${limitMsg} ${appointmentDay}.\n\n${rule.description || ''}`,
          rule: rule
        };
      }
    }
    
    // Check 365-day restrictions ONLY for diagnostic evaluations (90791/90792)
    // Only apply this check if the CPT code being scheduled is actually 90791 or 90792
    if (cptCode === '90791' || cptCode === '90792') {
      const diagnosticRule = applicableRules.find(rule => 
        !rule.can_override && (rule.cpt_codes.includes('90791') || rule.cpt_codes.includes('90792'))
      );
      
      if (diagnosticRule) {
        const oneYearAgo = new Date(appointmentDate);
        oneYearAgo.setDate(oneYearAgo.getDate() - 365);
        
        const recentDiagnostic = appointments.find(apt => {
          if (apt.client_id !== clientId) return false;
          if (editingAppointment && apt.id === editingAppointment.id) return false;
          
          const aptDate = new Date(apt.start_time);
          if (aptDate < oneYearAgo) return false;
          
          const aptCptCode = apt.service_type?.split(' - ')[0]?.trim();
          // Only check for 90791 or 90792, not other codes in the rule
          return aptCptCode === '90791' || aptCptCode === '90792';
        });
        
        if (recentDiagnostic) {
          const lastDate = new Date(recentDiagnostic.start_time).toLocaleDateString();
          return {
            valid: false,
            canOverride: false,
            message: `⚠️ 365-Day Restriction\n\n${diagnosticRule.rule_name}\n\nThis client's last ${cptCode} was on ${lastDate}.\n\nCannot schedule another ${cptCode} until ${new Date(new Date(recentDiagnostic.start_time).getTime() + 365*24*60*60*1000).toLocaleDateString()}.`,
            rule: diagnosticRule
          };
        }
      }
    }
    
    return { valid: true };
  };
  
  // Validate insurance-CPT restrictions
  const validateInsuranceRestrictions = (cptCode, clientId) => {
    if (!cptCode || !clientId) return { valid: true };
    
    const client = allClientsForLookup.find(c => c.id === clientId);
    if (!client) return { valid: true };
    
    // Get client's primary insurance ID
    // Client can have insurance_id directly OR insurances[] array with company names
    let primaryInsuranceId = client.insurance_id;
    let secondaryInsuranceId = client.secondary_insurance;
    
    // If using insurances array, look up the insurance ID by company name
    if (!primaryInsuranceId && client.insurances && client.insurances.length > 0) {
      const primaryIns = client.insurances.find(ins => ins.priority === 1 || ins.status === 'active');
      if (primaryIns) {
        // Try to find matching insurance in our insurances list by company name
        const matchedInsurance = insurances.find(ins => 
          ins.name?.toLowerCase().includes(primaryIns.company?.toLowerCase()) ||
          primaryIns.company?.toLowerCase().includes(ins.name?.toLowerCase())
        );
        if (matchedInsurance) {
          primaryInsuranceId = matchedInsurance.id;
        }
      }
      
      // Also check for secondary insurance in the array
      const secondaryIns = client.insurances.find(ins => ins.priority === 2);
      if (secondaryIns) {
        const matchedSecondary = insurances.find(ins => 
          ins.name?.toLowerCase().includes(secondaryIns.company?.toLowerCase()) ||
          secondaryIns.company?.toLowerCase().includes(ins.name?.toLowerCase())
        );
        if (matchedSecondary) {
          secondaryInsuranceId = matchedSecondary.id;
        }
      }
    }
    
    // Check primary insurance restriction
    let primaryRestriction = null;
    if (primaryInsuranceId) {
      const primaryRestrictions = insuranceCptRestrictions.filter(restriction => {
        if (!restriction.is_active) return false;
        if (!restriction.insurance_ids.includes(primaryInsuranceId)) return false;
        if (!restriction.cpt_codes.includes(cptCode)) return false;
        
        // Check plan-level restrictions
        if (!restriction.specific_plans || restriction.specific_plans.length === 0) {
          return true;
        }
        
        if (client.plan_name) {
          return restriction.specific_plans.some(plan => 
            client.plan_name.toLowerCase().includes(plan.toLowerCase())
          );
        }
        
        return false;
      });
      
      if (primaryRestrictions.length > 0) {
        primaryRestriction = primaryRestrictions[0];
      }
    }
    
    // Check secondary insurance restriction
    let secondaryRestriction = null;
    if (secondaryInsuranceId) {
      const secondaryRestrictions = insuranceCptRestrictions.filter(restriction => {
        if (!restriction.is_active) return false;
        if (!restriction.insurance_ids.includes(secondaryInsuranceId)) return false;
        if (!restriction.cpt_codes.includes(cptCode)) return false;
        
        // Check plan-level restrictions for secondary
        if (!restriction.specific_plans || restriction.specific_plans.length === 0) {
          return true;
        }
        
        if (client.secondary_plan_name) {
          return restriction.specific_plans.some(plan => 
            client.secondary_plan_name.toLowerCase().includes(plan.toLowerCase())
          );
        }
        
        return false;
      });
      
      if (secondaryRestrictions.length > 0) {
        secondaryRestriction = secondaryRestrictions[0];
      }
    }
    
    // Decision logic
    if (primaryRestriction && !secondaryRestriction && secondaryInsuranceId) {
      // Primary blocked but secondary allows
      // Check if the rule allows secondary fallback
      if (primaryRestriction.allow_secondary_fallback !== false) {
        // Secondary fallback allowed - can bill secondary
        const primaryInsurance = insurances.find(i => i.id === primaryInsuranceId);
        const secondaryInsurance = insurances.find(i => i.id === secondaryInsuranceId);
        
        return {
          valid: true,
          billSecondary: true,
          message: `ℹ️ Primary Insurance Does Not Cover This Service\n\nPrimary: ${primaryInsurance?.name || 'Client insurance'}\nSecondary: ${secondaryInsurance?.name || 'Secondary insurance'}\n\n${primaryRestriction.description}\n\n✓ However, secondary insurance (${secondaryInsurance?.name}) does cover this service. This will be billed to secondary insurance.`
        };
      } else {
        // Secondary fallback NOT allowed by this rule - treat as if no secondary
        const primaryInsurance = insurances.find(i => i.id === primaryInsuranceId);
        
        let planInfo = '';
        if (primaryRestriction.specific_plans && primaryRestriction.specific_plans.length > 0) {
          planInfo = `\nPlan(s): ${primaryRestriction.specific_plans.join(', ')}`;
        }
        
        return {
          valid: false,
          allowDirectBilling: primaryRestriction.allow_direct_billing,
          message: `⚠️ Insurance Does Not Cover This Service\n\nInsurance: ${primaryInsurance?.name || 'Client insurance'}${planInfo}\nCPT Code: ${cptCode}\n\n${primaryRestriction.description}\n\nNote: This restriction does not allow secondary insurance fallback.`,
          restriction: primaryRestriction
        };
      }
    }
    
    if (primaryRestriction && secondaryRestriction) {
      // Both blocked - check if either allows direct billing
      const primaryInsurance = insurances.find(i => i.id === primaryInsuranceId);
      const secondaryInsurance = insurances.find(i => i.id === secondaryInsuranceId);
      
      const allowDirectBilling = primaryRestriction.allow_direct_billing || (secondaryRestriction && secondaryRestriction.allow_direct_billing);
      
      let planInfo = '';
      if (primaryRestriction.specific_plans && primaryRestriction.specific_plans.length > 0) {
        planInfo = `\nPrimary Plan(s): ${primaryRestriction.specific_plans.join(', ')}`;
      }
      if (secondaryRestriction && secondaryRestriction.specific_plans && secondaryRestriction.specific_plans.length > 0) {
        planInfo += `\nSecondary Plan(s): ${secondaryRestriction.specific_plans.join(', ')}`;
      }
      
      return {
        valid: false,
        allowDirectBilling: allowDirectBilling,
        message: `⚠️ Neither Insurance Covers This Service\n\nPrimary: ${primaryInsurance?.name || 'Client insurance'}\nSecondary: ${secondaryInsurance?.name || 'Secondary insurance'}${planInfo}\nCPT Code: ${cptCode}\n\n${primaryRestriction.description}`,
        restriction: primaryRestriction
      };
    }
    
    if (primaryRestriction && !client.secondary_insurance) {
      // Primary blocked, no secondary
      const primaryInsurance = insurances.find(i => i.id === primaryInsuranceId);
      
      let planInfo = '';
      if (primaryRestriction.specific_plans && primaryRestriction.specific_plans.length > 0) {
        planInfo = `\nPlan(s): ${primaryRestriction.specific_plans.join(', ')}`;
      }
      
      return {
        valid: false,
        allowDirectBilling: primaryRestriction.allow_direct_billing,
        message: `⚠️ Insurance Does Not Cover This Service\n\nInsurance: ${primaryInsurance?.name || 'Client insurance'}${planInfo}\nCPT Code: ${cptCode}\n\n${primaryRestriction.description}`,
        restriction: primaryRestriction
      };
    }
    
    return { valid: true };
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    // Captured here so the post-save reschedule-task-closer (below)
    // can read it from BOTH the edit and create branches without
    // redeclaring `const response` in each block.  Mar 2026.
    let savedAppointmentForReschedule = null;
    
    void 0 && console.log('handleCreateAppointment called with editingAppointment:', editingAppointment);
    
    try {
      // Validate CLIENT_SESSION appointments require provider + client + CPT
      if (appointmentFormData.appointment_type === 'client_session') {
        if (!appointmentFormData.provider_id) {
          toast.error("Client billable sessions require a provider");
          return;
        }
        if (!appointmentFormData.client_id) {
          toast.error("Client billable sessions require a client");
          return;
        }
        if (!appointmentFormData.service_type) {
          toast.error("Client billable sessions require a CPT code");
          return;
        }
        // Require location for all billable appointments (including telehealth - for billing purposes)
        if (!appointmentFormData.location) {
          toast.error("Client billable sessions require a location for billing purposes");
          return;
        }
      }
      
      // Validate GROUP appointments require provider and group selection
      if (appointmentFormData.appointment_type === 'group') {
        if (!appointmentFormData.provider_id) {
          toast.error("Group sessions require a provider");
          return;
        }
        if (!appointmentFormData.selected_group_id) {
          toast.error("Group sessions require selecting a group");
          return;
        }
        // Require location for all group appointments (including telehealth - for billing purposes)
        if (!appointmentFormData.location) {
          toast.error("Group sessions require a location for billing purposes");
          return;
        }
      }
      
      const startTime = new Date(appointmentFormData.start_time);
      const endTime = new Date(appointmentFormData.end_time);
      
      // Validate recurring appointment settings
      if (appointmentFormData.is_recurring) {
        if (!appointmentFormData.recurrence_pattern) {
          toast.error("Please select a recurrence pattern");
          return;
        }
        if (!appointmentFormData.recurrence_count && !appointmentFormData.recurrence_end_date) {
          toast.error("Please specify either number of occurrences or an end date");
          return;
        }
      }
      
      // Validate that client sessions don't cross midnight
      if (appointmentFormData.appointment_type === 'client_session') {
        if (startTime.toDateString() !== endTime.toDateString()) {
          toast.error("Client appointments cannot cross midnight. Please adjust the end time.");
          return;
        }
        
        // Validate CPT code duration range
        if (appointmentFormData.selectedCptMeta) {
          const durationMinutes = Math.round((endTime - startTime) / 60000);
          // Prefer the appointment's exact CPT row — duplicate codes with
          // different limits make code-text matching ambiguous (Jun 2026).
          const currentCpt = (appointmentFormData.cpt_code_id && cptCodes?.find(c => c.id === appointmentFormData.cpt_code_id))
            || cptCodes?.find(c => c.code === appointmentFormData.selectedCptMeta.code);
          const validation = validateCPTDuration(durationMinutes, currentCpt);
          
          if (!validation.valid) {
            // Show error with suggestion
            if (validation.suggestedCode) {
              const useCorrectCode = window.confirm(
                `${validation.message}\n\nWould you like to change to ${validation.suggestedCode.code} instead?\n\n` +
                `Click OK to switch to ${validation.suggestedCode.code}, or Cancel to manually adjust the duration.`
              );
              
              if (useCorrectCode) {
                // Auto-switch to suggested CPT code
                const suggestedServiceType = `${validation.suggestedCode.display_name || validation.suggestedCode.code} - ${validation.suggestedCode.description}`;
                setAppointmentFormData(prev => ({
                  ...prev,
                  service_type: suggestedServiceType,
                  selectedCptMeta: {
                    allows_multiple_units: validation.suggestedCode.allows_multiple_units,
                    minutes_per_unit: validation.suggestedCode.minutes_per_unit || validation.suggestedCode.duration_minutes || 60,
                    code: validation.suggestedCode.code || validation.suggestedCode.display_name
                  }
                }));
                toast.info(`Changed to ${validation.suggestedCode.code}`);
                return; // Don't submit yet, let user review
              } else {
                return; // User wants to manually adjust
              }
            } else {
              // No suggestion available, just block
              toast.error(validation.message);
              return;
            }
          }
        }
      }
      
      // Track if appointment should be non-billable due to insurance restriction override
      let isNonBillable = false;
      
      // Validate CPT conflict rules for client sessions
      if (appointmentFormData.appointment_type === 'client_session' && appointmentFormData.service_type) {
        const cptCode = appointmentFormData.service_type.split(' - ')[0]?.trim();
        const conflictCheck = validateCPTConflicts(
          cptCode,
          appointmentFormData.client_id,
          appointmentFormData.start_time
        );
        
        if (!conflictCheck.valid) {
          if (conflictCheck.canOverride) {
            // Admin allows override - show warning and let user decide
            const proceed = window.confirm(
              `${conflictCheck.message}\n\n` +
              `⚠️ The administrator has allowed this rule to be overridden.\n\n` +
              `Do you want to proceed with scheduling anyway?\n\n` +
              `Click OK to proceed, or Cancel to reschedule to a different day.`
            );
            if (!proceed) return;
          } else {
            // Admin does NOT allow override - hard block
            toast.error(conflictCheck.message, { duration: 6000 });
            return;
          }
        }
        
        // Validate insurance restrictions
        const insuranceCheck = validateInsuranceRestrictions(
          cptCode,
          appointmentFormData.client_id
        );
        
        if (!insuranceCheck.valid) {
          if (insuranceCheck.allowDirectBilling) {
            // Can schedule but it's NON-BILLABLE
            const proceed = window.confirm(
              `${insuranceCheck.message}\n\n` +
              `⚠️ IMPORTANT: This will be a NON-BILLABLE encounter.\n\n` +
              `You can schedule this appointment for record-keeping purposes, ` +
              `but it will NOT generate a billable claim.\n\n` +
              `Do you want to proceed with this non-billable appointment?\n\n` +
              `Click OK to add to schedule (non-billable), or Cancel to choose a different service.`
            );
            if (!proceed) return;
            
            // Mark as non-billable
            isNonBillable = true;
            toast.warning('⚠️ This appointment will be marked as NON-BILLABLE', { duration: 5000 });
          } else {
            // Cannot schedule at all - hard block
            toast.error(
              `${insuranceCheck.message}\n\nThis service cannot be scheduled for this client's insurance.`,
              { duration: 6000 }
            );
            return;
          }
        } else if (insuranceCheck.billSecondary) {
          // Primary doesn't cover but secondary does - inform user
          toast.info(insuranceCheck.message, { duration: 7000 });
        }
      }
      
      // Check for conflicts with other appointments for the same provider
      // Only check conflicts if times have changed OR creating new appointment
      const timeChanged = editingAppointment && (
        new Date(editingAppointment.start_time).getTime() !== startTime.getTime() ||
        new Date(editingAppointment.end_time).getTime() !== endTime.getTime()
      );
      
      if (!editingAppointment || timeChanged) {
        // Per-CPT overlap allowance — conflict is skipped ONLY when BOTH the
        // new appointment's CPT and the existing appointment's CPT have
        // allows_overlap enabled (mirrors backend rule).
        const _cptAllowsOverlap = (cptId, serviceType) => {
          const list = cptCodes || [];
          let c = cptId ? list.find(x => x.id === cptId) : null;
          if (!c && serviceType) {
            const prefix = String(serviceType).split(' - ')[0].trim();
            c = list.find(x => x.code === prefix || x.display_name === prefix);
          }
          return !!(c && c.allows_overlap);
        };
        const newApptAllowsOverlap = _cptAllowsOverlap(appointmentFormData.cpt_code_id, appointmentFormData.service_type);
        const conflictingApt = appointments.find(apt => {
          // Skip if checking against the appointment being edited (compare as strings to handle type differences)
          if (editingAppointment && String(apt.id) === String(editingAppointment.id)) {
            void 0 && console.log('Skipping self-conflict check for appointment:', apt.id);
            return false;
          }

          // Cancelled / no-show appointments don't hold the slot — the
          // backend's overlap check already excludes them, so the FE
          // pre-flight must match.  Jun 2026 fix — non-admin users were
          // being blocked client-side from scheduling over a cancelled
          // slot even though the backend would have allowed it.
          const aptStatus = (apt.status || '').toLowerCase();
          if (['cancelled', 'canceled', 'client_cancelled',
               'no_show', 'late_cancel', 'rescheduled'].includes(aptStatus)) {
            return false;
          }

          // Check same provider (all appointments should have provider_id now)
          const checkProviderId = appointmentFormData.provider_id || user.id;
          if (apt.provider_id !== checkProviderId) return false;
          
          // BOTH CPTs allow overlap → not a conflict
          if (newApptAllowsOverlap && _cptAllowsOverlap(apt.cpt_code_id, apt.service_type)) return false;
          
          const aptStart = new Date(apt.start_time);
          const aptEnd = new Date(apt.end_time);
          
          // Check if times overlap
          const hasOverlap = (startTime < aptEnd && endTime > aptStart);
          
          if (hasOverlap) {
            void 0 && console.log('Conflict detected:', {
              conflictId: apt.id,
              editingId: editingAppointment?.id,
              conflictTime: `${aptStart} - ${aptEnd}`,
              newTime: `${startTime} - ${endTime}`
            });
          }
          
          return hasOverlap;
        });
        
        if (conflictingApt) {
          const conflictClient = allClientsForLookup.find(c => c.id === conflictingApt.client_id);
          toast.error(
            `Conflict: Provider already has appointment with ${conflictClient?.full_name || 'another client'} at this time. ` +
            `Please schedule or reschedule appointment for a different time.`
          );
          return;
        }
      }
      
      const appointmentData = {
        ...appointmentFormData,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        is_billable: !isNonBillable // Set to false if insurance restriction was overridden
      };

      // Jun 2026 (user report) — units must ALWAYS ship as a valid
      // positive integer.  A cleared number input holds '' which the
      // backend rejects ("unable to parse string as an integer").
      {
        const unitsInt = parseInt(appointmentData.units, 10);
        appointmentData.units = Number.isInteger(unitsInt) && unitsInt >= 1 ? unitsInt : 1;
      }
      // Same guard for the other optional-int field.
      if (appointmentData.session_number === '' || appointmentData.session_number === null) {
        delete appointmentData.session_number;
      }
      
      // For non-client appointments without a provider_id, set it to current user
      if (!appointmentData.provider_id && appointmentFormData.appointment_type !== 'client_session' && appointmentFormData.appointment_type !== 'group') {
        appointmentData.provider_id = user.id;
      }
      
      // Remove frontend-only metadata before sending to backend
      delete appointmentData.selectedCptMeta;
      // Jun 2026 — internal appointments must ALWAYS ship their managed
      // type id so the selection persists; every other type explicitly
      // clears it so a stale selection can't linger on the record.
      appointmentData.internal_type_id = ['supervision', 'internal'].includes(appointmentData.appointment_type)
        ? (appointmentFormData.internal_type_id || null)
        : null;
      // Jun 2026 — internal appointments must ALWAYS ship their managed
      // type id so the selection persists; every other type explicitly
      // clears it so a stale selection can't linger on the record.
      appointmentData.internal_type_id = ['supervision', 'internal'].includes(appointmentData.appointment_type)
        ? (appointmentFormData.internal_type_id || null)
        : null;
      // Mar 2026 — `_reschedule_context` is FE-only state used by the
      // dialog's reschedule banner + the post-save task-closer.  Don't
      // ship it to the backend (Pydantic would just drop it, but
      // explicit is safer).
      delete appointmentData._reschedule_context;
      
      void 0 && console.log('Submitting appointment with data:', appointmentData);
      
      // Clean up recurring fields if not recurring
      if (!appointmentData.is_recurring) {
        delete appointmentData.recurrence_pattern;
        delete appointmentData.recurrence_end_date;
        delete appointmentData.recurrence_count;
        delete appointmentData.recurring_days;
        delete appointmentData.monthly_mode;
      } else {
        if (appointmentData.recurrence_pattern !== 'monthly') {
          delete appointmentData.monthly_mode;
        }
        // If recurring, remove empty values
        if (!appointmentData.recurrence_count || appointmentData.recurrence_count === '') {
          delete appointmentData.recurrence_count;
        } else {
          // Coerce to a valid positive integer or drop it.
          const rc = parseInt(appointmentData.recurrence_count, 10);
          if (Number.isInteger(rc) && rc >= 1) {
            appointmentData.recurrence_count = rc;
          } else {
            delete appointmentData.recurrence_count;
          }
        }
        if (!appointmentData.recurrence_end_date || appointmentData.recurrence_end_date === '') {
          delete appointmentData.recurrence_end_date;
        } else if (isNaN(new Date(appointmentData.recurrence_end_date).getTime())) {
          toast.error("Recurrence end date is incomplete — please pick a full date.");
          return;
        }
      }
      
      if (editingAppointment) {
        // Recurring-series scope prompt (Feb 2026 user request:
        // "If I update 1 I want it to ask if I want to update all").
        // Only ask when the appointment is part of a series AND the
        // user actually changed something that's meaningful to
        // propagate (times, duration, provider, service_type,
        // location).  Status is intentionally excluded — status
        // changes are always scoped to the single appointment
        // (series-wide cancel has its own dedicated action).
        // We skip the prompt for trivial
        // metadata-only edits (notes/etc.) to keep the dialog out
        // of the way.
        const isPartOfSeries = !!editingAppointment.parent_appointment_id;
        const propagateKeys = [
          'start_time', 'end_time', 'service_type', 'cpt_code_id',
          'provider_id', 'location', 'pos_code',
          'telehealth_modality', 'is_billable', 'is_payroll_eligible',
        ];
        const propagatableChanged = propagateKeys.some((k) => {
          const a = appointmentData[k];
          const b = editingAppointment[k];
          // Only treat as "changed" when we actually sent the key
          // AND the value is different.
          return (a !== undefined) && (a !== null) && (a !== b);
        });
        if (isPartOfSeries && propagatableChanged) {
          // Sep 2026 — proper 3-option modal (was two stacked
          // window.confirm prompts). Cancel aborts the save.
          const scope = await new Promise((resolve) => {
            seriesScopeResolverRef.current = resolve;
            setShowSeriesScopeDialog(true);
          });
          if (!scope) return;
          appointmentData.apply_to = scope;
        }
        // Update existing appointment
        void 0 && console.log('Updating appointment:', editingAppointment.id);
        void 0 && console.log('Appointment data being sent:', appointmentData);
        const response = await axios.patch(`${API}/appointments/${editingAppointment.id}`, appointmentData);
        const updatedAppointment = response.data;
        void 0 && console.log('Update response:', updatedAppointment);

        // OPTIMISTIC UPDATE: Immediately update in local state for instant UI feedback.
        //
        // Feb 2026 bug: the PATCH /appointments/{id} endpoint only returned
        // a ``{message: ...}`` envelope, NOT the full appointment doc.
        // Spreading that onto the cached row left every field we just
        // changed (addon_cpt_code_ids, status, notes, units, etc.) at
        // its STALE pre-save value, so the next time the user opened
        // this row the form re-hydrated from the cached stale data —
        // looking like "the save didn't take" even though the DB had
        // the right values.  Fix on the BACKEND: PATCH now returns
        // ``{...message, appointment: <full doc>}``.  Fix HERE: prefer
        // the full ``appointment`` field when present, fall back to
        // spreading ``appointmentData`` so older backends keep working.
        const serverAppt = updatedAppointment?.appointment;
        setAppointments(prev => prev.map(apt =>
          apt.id === editingAppointment.id
            ? (serverAppt ? { ...apt, ...serverAppt } : { ...apt, ...appointmentData, ...updatedAppointment })
            : apt
        ));

        if (updatedAppointment.recurring_siblings_updated > 0) {
          toast.success(
            `Appointment updated · ${updatedAppointment.recurring_siblings_updated} other `
            + `appointment${updatedAppointment.recurring_siblings_updated === 1 ? '' : 's'} `
            + `in the series also updated.`
          );
        } else {
          toast.success("Appointment updated successfully");
        }
        
        // Background refresh to ensure data consistency (won't override optimistic update immediately)
        refreshAppointments(true);
        
        // If no-show was marked and a note was created, open it
        if (updatedAppointment.no_show_note_id) {
          try {
            const noteResponse = await axios.get(`${API}/notes/${updatedAppointment.no_show_note_id}`);
            setSelectedNote(noteResponse.data);
            setShowNoteDialog(true);
            toast.info("Please complete the No-Show/Cancellation form");
          } catch (noteError) {
            console.error('Failed to load no-show note:', noteError);
          }
        }
      } else {
        // Create new appointment
        const response = await axios.post(`${API}/appointments`, appointmentData);
        const newAppointment = response.data;
        const newAppointmentId = newAppointment.id;
        savedAppointmentForReschedule = newAppointment;
        
        // OPTIMISTIC UPDATE: Immediately add to local state for instant UI feedback
        setAppointments(prev => [...prev, newAppointment]);
        
        // Pre-calculate appointment date for use in different branches
        const appointmentDate = new Date(appointmentData.start_time);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const dayAfterTomorrow = new Date(today);
        dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
        appointmentDate.setHours(0, 0, 0, 0);
        
        // Create intake documents if bundle was selected
        if (appointmentFormData.intake_bundle_id && appointmentFormData.client_id) {
          try {
            const selectedBundle = intakeBundles.find(b => b.id === appointmentFormData.intake_bundle_id);
            if (selectedBundle) {
              // Jun 2026 — route through the SAME server-side creation
              // path as post-scheduling sends
              // (POST /appointments/{id}/send-intake-bundle): ONE
              // creation path with ONE dedup rule (server skips
              // creation when notes already exist for this
              // client+bundle), structured-JSON note content, and the
              // single consolidated client email.  The old client-side
              // documents.map loop double-created notes when a bundle
              // had already been sent.  prepare_only skips the email
              // when no doc is flagged auto_send_to_client.
              const shouldSendCount = (selectedBundle.documents || []).filter(d => d.auto_send_to_client).length;
              const bundleResp = await axios.post(
                `${API}/appointments/${newAppointmentId}/send-intake-bundle`,
                { bundle_id: selectedBundle.id, prepare_only: shouldSendCount === 0 },
              );
              const sentToClientCount = bundleResp.data?.documents_sent || 0;
              let message = `Appointment scheduled! Intake bundle "${selectedBundle.name}" prepared`;
              if (sentToClientCount > 0) {
                message += `. ONE consolidated email sent to client covering ${sentToClientCount} document(s).`;
              }
              toast.success(message, { duration: 5000 });
            }
          } catch (error) {
            console.error('Error creating intake documents:', error);
            toast.warning('Appointment created, but the intake bundle could not be prepared. You can send it from the appointment menu.');
          }
        } else {
          // Check if appointment is today or tomorrow - auto-create note
          if (appointmentDate >= today && appointmentDate < dayAfterTomorrow) {
            // Org-level kill switch (Feb 2026 user request: "Can I
            // have a way to turn that off? It's creating chaos.").
            // GET'ing this every create is acceptable — the
            // endpoint is a single Mongo lookup and only fires on
            // a today/tomorrow appointment.  If the lookup fails
            // for any reason we fall back to "enabled" so the
            // historical behaviour is preserved.
            let autoNoteEnabled = true;
            try {
              const tog = await axios.get(`${API}/admin/auto-note-toggle`);
              autoNoteEnabled = tog.data?.auto_create_notes_on_schedule !== false;
            } catch (e) {
              void 0 && console.log('auto-note-toggle lookup failed, defaulting to enabled');
            }
            if (autoNoteEnabled) {
              // Appointment is today or tomorrow - try to auto-create note
              try {
                const noteResponse = await axios.post(`${API}/appointments/${newAppointmentId}/auto-create-note`);
                if (noteResponse.data.note_id) {
                  toast.success(`Appointment scheduled and draft note created using ${noteResponse.data.template_name}`);
                }
              } catch (error) {
                // Note creation failed - could be no CPT code or no template
                if (error.response?.status === 400) {
                  // This is expected if CPT has no template
                  void 0 && console.log('Note not auto-created:', error.response?.data?.detail);
                }
                // Don't show error to user - just means no template assigned
              }
            }
          }
        }
        
        // Auto-switch calendar to show the provider whose appointment was just created
        // This ensures the new appointment is visible immediately
        if (!selectedProviders.includes('all') && !selectedProviders.includes(appointmentData.provider_id)) {
          setSelectedProviders([appointmentData.provider_id]);
          const providerName = providers.find(p => p.id === appointmentData.provider_id)?.full_name || 'provider';
          toast.success(`Appointment scheduled for ${providerName} - switched calendar view to show it`);
        } else if (appointmentDate < today || appointmentDate >= dayAfterTomorrow) {
          // Only show this if we didn't already show the note creation message
          toast.success("Appointment scheduled successfully");
        }
      }
      
      setShowAppointmentForm(false);
      setShowQuickSchedule(false); // Close quick schedule dialog too

      // ── Jun 2026 — copay reminder for providers after scheduling ──
      // Fire-and-forget: if the client has a copay (Manage Insurance)
      // that isn't collected for the booked DOS, nudge the provider to
      // remind the client to call the office to pay.
      if (!editingAppointment && appointmentData.client_id) {
        const dosStr = String(appointmentData.start_time || '').slice(0, 10);
        axios.get(`${API}/clients/${appointmentData.client_id}/copay-status`, { params: { date: dosStr } })
          .then((r) => {
            const d = r.data || {};
            if ((d.copay || 0) > 0 && !d.collected) {
              const cName = allClientsForLookup.find(c => c.id === appointmentData.client_id)?.full_name || 'This client';
              toast.info(`${cName} has a $${Number(d.copay).toFixed(2)} copay — remind them to call the office to pay.`, { duration: 8000 });
            }
          })
          .catch(() => {});
      }

      setEditingAppointment(null);

      // ── Mar 2026 — auto-complete the originating reschedule task ──
      // If this dialog was opened from a Reschedule task, close the
      // loop by posting a "Rebooked for X" comment and flipping the
      // task to `completed` so the scheduler doesn't have to bounce
      // back to the Tasks tab and mark it manually.  Fire-and-forget
      // — failures don't block the appointment save.
      try {
        const ctx = appointmentFormData?._reschedule_context;
        if (ctx?.task_id) {
          const { completeRescheduleTaskAfterBooking } = await import('./utils/rescheduleTaskCompletion');
          const r = await completeRescheduleTaskAfterBooking({
            ctx,
            newAppointment: savedAppointmentForReschedule,
            apiBase: API,
            axios,
          });
          if (r.commentOk && r.statusOk) {
            toast.success('Reschedule task closed automatically');
          } else if (r.commentOk || r.statusOk) {
            toast.info('Appointment booked — task partially closed (see Tasks tab)');
          }
        }
      } catch (closeErr) {
        // Non-fatal — the appointment was saved successfully.
        console.warn('[reschedule] auto-close task failed:', closeErr);
      }

      setApplicableIntakeBundles([]); // Clear intake bundles
      setAppointmentFormData({
        client_id: '',
        client_ids: [],
        client_attendance: {},
        selected_group_id: '',
        session_number: null,
        provider_id: user?.id || '',
        start_time: '',
        end_time: '',
        service_type: '',
        cpt_code_id: '',
        appointment_type: 'client_session',
        status: 'scheduled',
        is_billable: true,
        is_payroll_eligible: false,
        units: 1,
        location: '',
        notes: '',
        is_recurring: false,
        recurrence_pattern: '',
        recurrence_end_date: '',
        recurrence_count: '',
        recurring_days: [],
        intake_bundle_id: '',
        authorization_id: null
      });
    } catch (error) {
      console.error("Appointment creation error:", error.response?.data);
      let errorMessage = "Failed to schedule appointment";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Handle Pydantic validation errors
          errorMessage = detail.map(err => {
            const field = err.loc?.join('.') || 'field';
            return `${field}: ${err.msg || err.message}`;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user - set is_active based on is_archived status.
        // Strip transient UI-only fields (e.g. _meet_space_loading) so
        // they don't get included in the API payload.
        const { _meet_space_loading, ...rest } = userFormData;
        const updateData = {
          ...rest,
          is_active: !userFormData.is_archived
        };
        const response = await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success("User updated successfully");
        
        // Update local users state with the updated user data
        setUsers(prevUsers => prevUsers.map(u => 
          u.id === editingUser.id ? { ...u, ...response.data } : u
        ));
      } else {
        // Create user with role_id
        const response = await axios.post(`${API}/auth/create-user`, userFormData);
        toast.success("User created successfully");
        
        // Add new user to local state
        if (response.data) {
          setUsers(prevUsers => [...prevUsers, response.data]);
        }
      }
      setShowUserForm(false);
      setUserFormData({
        email: '',
        password: '',
        full_name: '',
        role_id: '',
        phone: '',
        title: '',
        department: '',
        credentials: '',
        license_status: 'Licensed',
        medicare_approved: false,
        supervisor_ids: [],
        assigned_billers: [],
        assigned_cpt_codes: [],
        assigned_cpt_bundles: [],
        assigned_pos_codes: [],
        assigned_locations: [],
        default_location: null,
        default_pos_code: null,
        default_telehealth_modality: null,
        default_template_id: '',
        custom_telehealth_url: '',
        disable_appointment_reminders: false,
        can_archive_clients: false,
        can_archive_users: false,
        can_qa_audit: false,
        group_admin: false,
        client_visibility: 'own_only',
        is_archived: false,
        is_provider: false,
        is_medication_manager: false,
        is_waitlist_manager: false,
        is_biller: false,
        is_billing_manager: false,
        is_medical_records_manager: false,
        is_payroll_manager: false,
    is_era_manager: false,
        is_scheduler: false,
        scheduling_for: []
      });
      setEditingUser(null);
      refreshClients(); refreshStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  // Password Reset Handlers
  const handleApprovePasswordReset = async (requestId, customPassword = null) => {
    try {
      const response = await axios.post(`${API}/admin/password-reset-requests/${requestId}/approve`, 
        customPassword ? { custom_password: customPassword } : {}
      );
      
      const tempPassword = response.data.temp_password;
      
      // Show success with temp password in a more prominent way
      toast.success("Password reset approved!", {
        duration: 15000,
        description: (
          <div className="mt-2">
            <p className="font-semibold mb-1">Temporary Password:</p>
            <div className="flex items-center space-x-2 bg-white p-2 rounded border">
              <code className="text-sm font-mono flex-1">{tempPassword}</code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(tempPassword);
                  toast.success("Password copied to clipboard!");
                }}
                className="text-teal-600 hover:text-teal-700 text-xs underline"
              >
                Copy
              </button>
            </div>
            <p className="text-xs mt-2 text-gray-600">Share this with the user via secure channel</p>
          </div>
        )
      });
      
      void 0 && console.log("Temp password:", tempPassword);
      fetchPasswordResetRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve password reset");
    }
  };

  const handleDenyPasswordReset = async (requestId, reason = null) => {
    try {
      await axios.post(`${API}/admin/password-reset-requests/${requestId}/deny`, {
        reason: reason
      });
      toast.success("Password reset request denied");
      fetchPasswordResetRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deny password reset");
    }
  };

  // Client Merge Handlers
  const fetchPotentialDuplicates = async () => {
    try {
      void 0 && console.log("Fetching potential duplicates...");
      setMergeLoading(true);
      const response = await axios.get(`${API}/admin/clients/search-duplicates`);
      void 0 && console.log("Duplicates response:", response.data);
      setPotentialDuplicates(response.data.potential_duplicates || []);
      void 0 && console.log("Set potentialDuplicates, about to set dialog true");
      // Also fetch dismissed duplicates so the accordion is ready
      try {
        const dismissedResp = await axios.get(`${API}/admin/clients/dismissed-duplicates`);
        setDismissedDuplicates(dismissedResp.data || []);
      } catch (e) {
        console.error("Failed to fetch dismissed duplicates:", e);
        setDismissedDuplicates([]);
      }
      return true;
    } catch (error) {
      console.error("Failed to search for duplicates:", error);
      toast.error("Failed to search for duplicates");
      return false;
    } finally {
      setMergeLoading(false);
    }
  };

  const handleUndismissDuplicate = async (pairKey) => {
    try {
      await axios.delete(`${API}/admin/clients/dismissed-duplicates/${encodeURIComponent(pairKey)}`);
      toast.success("Pair restored - will appear in next duplicate search");
      setDismissedDuplicates(prev => prev.filter(d => d.pair_key !== pairKey));
      // Re-run duplicate search so the newly un-dismissed pair appears if still duplicate
      fetchPotentialDuplicates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore dismissed pair");
    }
  };

  const fetchMergeHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients/merge/history`);
      setMergeHistory(response.data || []);
    } catch (error) {
      toast.error("Failed to fetch merge history");
    }
  };

  const handlePreviewMerge = async () => {
    if (!mergeSourceClient || !mergeTargetClient) {
      toast.error("Please select both source and target clients");
      return;
    }
    
    try {
      setMergeLoading(true);
      const response = await axios.post(`${API}/admin/clients/merge/preview?source_client_id=${mergeSourceClient.id}&target_client_id=${mergeTargetClient.id}`);
      setMergePreview(response.data);
      setMergeFieldOverrides({});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview merge");
    } finally {
      setMergeLoading(false);
    }
  };

  const handleExecuteMerge = async () => {
    if (!mergeSourceClient || !mergeTargetClient) {
      toast.error("Please select both clients");
      return;
    }
    
    try {
      setMergeLoading(true);
      const response = await axios.post(`${API}/admin/clients/merge`, {
        source_client_id: mergeSourceClient.id,
        target_client_id: mergeTargetClient.id,
        keep_source_name: mergeFieldOverrides.full_name === 'source',
        keep_source_mrn: mergeFieldOverrides.mrn === 'source',
        field_overrides: mergeFieldOverrides
      });
      
      toast.success(response.data.message);
      
      // Reset state
      setMergeSourceClient(null);
      setMergeTargetClient(null);
      setMergePreview(null);
      setMergeFieldOverrides({});
      setShowClientMergeDialog(false);
      
      // Note: fetchClients is not a separate function, clients will refresh on next page load
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to merge clients");
    } finally {
      setMergeLoading(false);
    }
  };

  const handleAdminResetPassword = async (userId, customPassword = null) => {
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/reset-password`,
        customPassword ? { custom_password: customPassword } : {}
      );
      toast.success("Password reset successfully");
      
      // Show temp password to admin
      const tempPassword = response.data.temp_password;
      toast.info(`Temporary password: ${tempPassword}`, { 
        duration: 10000,
        description: "Share this with the user via secure channel"
      });
      
      void 0 && console.log("Temp password for user:", tempPassword);
      return tempPassword;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
      throw error;
    }
  };

  const handleArchiveClient = async (clientId, reason = '', cancelFutureAppts = false) => {
    // Optimistic update - immediately update the client in the local state
    const previousClients = [...clients];
    const previousSelectedClient = selectedClient ? { ...selectedClient } : null;
    setClients(prev => prev.map(c => 
      c.id === clientId ? { ...c, status: 'archived' } : c
    ));
    if (selectedClient?.id === clientId) {
      setSelectedClient(prev => prev ? { ...prev, status: 'archived' } : prev);
    }
    
    try {
      const response = await axios.patch(`${API}/clients/${clientId}/status`, {
        status: 'archived',
        discharge_reason: reason,
        cancel_future_appointments: cancelFutureAppts
      });
      
      let message = "Client archived successfully";
      if (response.data.appointments_cancelled > 0) {
        message += ` (${response.data.appointments_cancelled} future appointments cancelled)`;
      }
      toast.success(message);
    } catch (error) {
      // Rollback optimistic update on error
      setClients(previousClients);
      if (previousSelectedClient && selectedClient?.id === clientId) {
        setSelectedClient(previousSelectedClient);
      }
      toast.error(error.response?.data?.detail || "Failed to archive client");
    }
  };

  // Check for future appointments before archiving/discharging
  const checkFutureAppointmentsAndUpdateStatus = async (clientId, clientName, newStatus, reason = '') => {
    try {
      // Check for future appointments
      const response = await axios.get(`${API}/clients/${clientId}/future-appointments-count`);
      const futureCount = response.data.count;
      
      if (futureCount > 0) {
        // Show confirmation dialog
        setCancelFutureApptsData({
          clientId,
          clientName,
          status: newStatus,
          futureApptsCount: futureCount,
          reason
        });
        setShowCancelFutureApptsDialog(true);
      } else {
        // No future appointments, proceed directly
        if (newStatus === 'archived') {
          await handleArchiveClient(clientId, reason, false);
        } else {
          // Optimistic update for discharged status
          const previousClients = [...clients];
          const previousSelectedClient = selectedClient ? { ...selectedClient } : null;
          setClients(prev => prev.map(c => 
            c.id === clientId ? { ...c, status: newStatus } : c
          ));
          if (selectedClient?.id === clientId) {
            setSelectedClient(prev => prev ? { ...prev, status: newStatus } : prev);
          }
          
          try {
            await axios.patch(`${API}/clients/${clientId}/status`, {
              status: newStatus,
              discharge_reason: reason,
              cancel_future_appointments: false
            });
            toast.success(`Client moved to ${newStatus}`);
          } catch (innerError) {
            // Rollback on error
            setClients(previousClients);
            if (previousSelectedClient && selectedClient?.id === clientId) {
              setSelectedClient(previousSelectedClient);
            }
            throw innerError;
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update client status");
    }
  };

  const handleRestoreClient = async (clientId) => {
    // Optimistic update - restore to active
    const previousClients = [...clients];
    const previousSelectedClient = selectedClient ? { ...selectedClient } : null;
    
    setClients(prev => prev.map(c => 
      c.id === clientId ? { ...c, status: 'active' } : c
    ));
    
    if (selectedClient?.id === clientId) {
      setSelectedClient(prev => prev ? { ...prev, status: 'active' } : prev);
    }
    
    try {
      await axios.patch(`${API}/clients/${clientId}/restore`);
      toast.success("Client restored successfully");
      // Don't call fetchData() - optimistic update already shows correct state
    } catch (error) {
      // Rollback on error
      setClients(previousClients);
      if (previousSelectedClient && selectedClient?.id === clientId) {
        setSelectedClient(previousSelectedClient);
      }
      toast.error(error.response?.data?.detail || "Failed to restore client");
    }
  };

  // Duplicate functions removed - using the ones with confirmation dialogs above

  const handleUpdateClientStatus = async (clientId, status, clientName = '') => {
    // If setting to inactive, show reason dialog
    if (status === 'inactive') {
      setInactiveClientId(clientId);
      setShowInactiveReasonDialog(true);
      return;
    }
    
    // If setting to discharged, check for multi-provider scenario first
    if (status === 'discharged') {
      try {
        const checkRes = await axios.get(`${API}/clients/${clientId}/discharge-check`);
        const dischargeInfo = checkRes.data;
        
        // If client has multiple providers, show service disenrollment dialog instead
        if (dischargeInfo.has_multiple_providers) {
          // Store the discharge check info for the dialog
          setDischargeCheckInfo(dischargeInfo);
          setShowServiceDisenrollmentDialog(true);
          return;
        }
        
        // If single provider but has multiple services, ask which to disenroll
        if (dischargeInfo.current_provider_enrollments?.length > 1) {
          setDischargeCheckInfo(dischargeInfo);
          setShowServiceDisenrollmentDialog(true);
          return;
        }
        
        // Single provider, single service or no services - proceed with practice-wide discharge
        // But first check if requirements are met
        if (dischargeInfo.error_message) {
          toast.error(dischargeInfo.error_message);
          return;
        }
        
      } catch (error) {
        console.error('Error checking discharge eligibility:', error);
        // If check fails, show error and don't proceed - don't want to accidentally discharge
        toast.error('Unable to check discharge eligibility. Please try again.');
        return;
      }
      
      // Proceed with normal discharge flow (future appointments check, etc.)
      const client = allClientsForLookup.find(c => c.id === clientId);
      const name = clientName || client?.full_name || 'Client';
      await checkFutureAppointmentsAndUpdateStatus(clientId, name, status);
      return;
    }
    
    // If setting to archived, check for future appointments first
    if (status === 'archived') {
      const client = allClientsForLookup.find(c => c.id === clientId);
      const name = clientName || client?.full_name || 'Client';
      await checkFutureAppointmentsAndUpdateStatus(clientId, name, status);
      return;
    }
    
    // Optimistic update - immediately update UI
    const previousClients = [...clients];
    const previousSelectedClient = selectedClient ? { ...selectedClient } : null;
    
    // Update in clients list
    setClients(prev => prev.map(c => 
      c.id === clientId ? { ...c, status } : c
    ));
    
    // Also update selectedClient if it's the same client
    if (selectedClient?.id === clientId) {
      setSelectedClient(prev => prev ? { ...prev, status } : prev);
    }
    
    try {
      await axios.patch(`${API}/clients/${clientId}/status`, {
        status
      });
      toast.success(`Client moved to ${status}`);
      // Don't call fetchData() - optimistic update already shows correct state
      // Calling fetchData() immediately can cause race condition where stale data overwrites the update
    } catch (error) {
      // Rollback on error
      setClients(previousClients);
      if (previousSelectedClient && selectedClient?.id === clientId) {
        setSelectedClient(previousSelectedClient);
      }
      toast.error(error.response?.data?.detail || "Failed to update client status");
    }
  };
  
  const handleConfirmInactive = async () => {
    if (!inactiveReason.trim()) {
      toast.error("Please provide a reason for marking client as inactive");
      return;
    }
    
    const clientId = inactiveClientId;
    
    // Optimistic update
    const previousClients = [...clients];
    const previousSelectedClient = selectedClient ? { ...selectedClient } : null;
    
    setClients(prev => prev.map(c => 
      c.id === clientId ? { ...c, status: 'inactive', inactive_reason: inactiveReason, inactivated_by: user?.id, inactivated_at: new Date().toISOString() } : c
    ));
    
    if (selectedClient?.id === clientId) {
      setSelectedClient(prev => prev ? { ...prev, status: 'inactive', inactive_reason: inactiveReason, inactivated_by: user?.id, inactivated_at: new Date().toISOString() } : prev);
    }
    
    // Close dialog immediately for snappy UX
    setShowInactiveReasonDialog(false);
    setInactiveClientId(null);
    const savedReason = inactiveReason;
    setInactiveReason('');
    
    try {
      await axios.patch(`${API}/clients/${clientId}/status`, {
        status: 'inactive',
        inactive_reason: savedReason
      });
      
      toast.success("Client marked as inactive");
    } catch (error) {
      // Rollback on error
      setClients(previousClients);
      if (previousSelectedClient && selectedClient?.id === clientId) {
        setSelectedClient(previousSelectedClient);
      }
      toast.error(error.response?.data?.detail || "Failed to update client status");
    }
  };

  const handleCompleteBillingReview = async (balanceCleared = true) => {
    try {
      await axios.post(`${API}/clients/${billingReviewClientId}/billing-review-complete`, {
        notes: billingReviewNotes,
        balance_cleared: balanceCleared
      });
      
      toast.success(balanceCleared 
        ? "Billing review completed - client is ready for archive" 
        : "Billing review marked - outstanding balance noted"
      );
      setShowBillingReviewDialog(false);
      setBillingReviewClientId(null);
      setBillingReviewNotes('');
      refreshClients({ syncOpenChart: true }); refreshStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete billing review");
    }
  };

  // Helper function to validate appointment duration against CPT code ranges
  const validateCPTDuration = (durationMinutes, currentCptCode) => {
    if (!currentCptCode) return { valid: true, message: null, suggestedCode: null };
    
    const min = currentCptCode.min_duration;
    const max = currentCptCode.max_duration;
    
    // If no min/max set, any duration is valid
    if (!min && !max) return { valid: true, message: null, suggestedCode: null };
    
    // Check if duration is within range
    if (min && durationMinutes < min) {
      // Find shorter CPT code suggestion
      const shorterCode = cptCodes?.find(code => 
        code.max_duration && code.max_duration >= durationMinutes && 
        code.min_duration && code.min_duration <= durationMinutes
      );
      return {
        valid: false,
        message: `Duration ${durationMinutes} min is too short for ${currentCptCode.code} (min ${min} min). ${shorterCode ? `Consider using ${shorterCode.code} instead.` : 'Please adjust duration or select different CPT code.'}`,
        suggestedCode: shorterCode
      };
    }
    
    if (max && durationMinutes > max) {
      // Find longer CPT code suggestion
      const longerCode = cptCodes?.find(code => 
        code.min_duration && code.min_duration <= durationMinutes && 
        code.max_duration && code.max_duration >= durationMinutes
      );
      return {
        valid: false,
        message: `Duration ${durationMinutes} min is too long for ${currentCptCode.code} (max ${max} min). ${longerCode ? `Consider using ${longerCode.code} instead.` : 'Extended sessions require documentation of medical necessity.'}`,
        suggestedCode: longerCode
      };
    }
    
    return { valid: true, message: null, suggestedCode: null };
  };

  // Helper function to suggest appropriate CPT code based on duration
  const suggestCPTCodeByDuration = (durationMinutes) => {
    if (!durationMinutes || !cptCodes) return null;
    
    // Find CPT code whose range includes this duration
    const matchingCode = cptCodes.find(code => {
      if (!code.min_duration || !code.max_duration) return false;
      return durationMinutes >= code.min_duration && durationMinutes <= code.max_duration;
    });
    
    return matchingCode;
  };

  // Undo functionality — moved into useCalendarDrag hook (Stage 4).
  // addToUndoStack/handleUndo are now returned from the hook above
  // and exported via the bridge below for the inline calendar.

  // canEditAppointment / canResizeAppointment moved into
  // hooks/useCalendarPermissions (Stage 7).  Dashboard still
  // consumes them — destructured here — because the Fullscreen
  // Calendar Dialog and a few non-calendar bits reference them.
  const {
    canEditAppointment,
    canResizeAppointment,
  } = useCalendarPermissions();

  // Check if current user can edit a treatment plan
  const canEditTreatmentPlan = (plan) => {
    if (!plan || !user) return false;
    
    // Admins and super admins can edit any treatment plan
    if (user.role?.toLowerCase() === 'admin' || isSuperAdmin(user)) {
      return true;
    }
    
    // The assigned provider can edit their own treatment plan
    if (plan.assigned_provider_id === user.id) {
      return true;
    }
    
    // Supervisors can edit their supervisees' treatment plans
    // Look up the assigned provider and check if current user is their supervisor
    const assignedProvider = users?.find(u => u.id === plan.assigned_provider_id);
    if (assignedProvider && assignedProvider.supervisor_ids?.includes(user.id)) {
      return true;
    }
    
    // Also check if user is marked as a supervisor and has this provider in their supervisees list
    if (user?.is_supervisor) {
      // Calculate supervisees dynamically from users array
      const superviseeIds = users?.filter(u => u.supervisor_ids?.includes(user.id)).map(u => u.id) || [];
      if (superviseeIds.includes(plan.assigned_provider_id)) {
        return true;
      }
    }
    
    // Otherwise, cannot edit
    return false;
  };

  // Calendar click handler
  const handleCalendarSlotClick = (date, hour, minute) => {
    void 0 && console.log('📅 handleCalendarSlotClick called:', { date, hour, minute });
    const selectedDateTime = new Date(date);
    selectedDateTime.setHours(hour, minute, 0, 0);
    
    const endDateTime = new Date(selectedDateTime.getTime() + 15 * 60 * 1000); // Default 15 minutes
    
    // Format both times for datetime-local input (local time)
    const formatLocalDateTime = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    setSelectedTimeSlot(selectedDateTime);
    setAppointmentFormData(prev => ({
      ...prev,
      start_time: formatLocalDateTime(selectedDateTime),
      end_time: formatLocalDateTime(endDateTime)
    }));
    void 0 && console.log('✅ Setting showQuickSchedule to true');
    setShowQuickSchedule(true);
  };

  // Form builder functions
  const addFormSection = () => {
    const newSection = {
      id: `section_${Date.now()}`,
      title: 'New Section',
      rowGroups: [
        { id: `row_${Date.now()}`, fields: [] }
      ],
      fields: [] // Keep for backward compatibility
    };
    setActiveFormSections([...activeFormSections, newSection]);
  };

  const addFormField = (fieldType, sectionId = null) => {
    // Base field structure
    const newField = {
      id: `field_${Date.now()}`,
      type: fieldType.type,
      // Leave label blank for user-editable fields so users must name them
      // Layout/special fields keep their type label as a default
      label: ['heading', 'static_text', 'divider', 'spacer', 'client_visibility_divider'].includes(fieldType.type) 
        ? fieldType.label 
        : '',
      required: false,
      // Layout properties
      width: '100', // percentage: 100, 50, 33, 25, or custom
      indentLevel: 0, // 0-3 for left padding
      backgroundColor: '',
      borderColor: '',
      textColor: '',
    };
    
    // Type-specific properties
    if (fieldType.type === 'text' || fieldType.type === 'textarea' || fieldType.type === 'email' || fieldType.type === 'phone') {
      newField.placeholder = 'Enter your response here...';
      newField.default_value = ''; // Default text value
    }
    
    if (fieldType.type === 'select' || fieldType.type === 'checkbox_group') {
      newField.options = ['Option 1', 'Option 2', 'Option 3'];
      newField.default_value = ''; // Default selected option
    }
    
    if (fieldType.type === 'multiselect') {
      newField.default_value = []; // Default selected options array
    }
    
    if (fieldType.type === 'service_dropdown') {
      newField.default_value = []; // Default selected services array (multi-select)
    }
    
    if (fieldType.type === 'radio') {
      newField.options = ['Yes', 'No']; // Better default for radio buttons
      newField.default_value = ''; // Default selected radio option
    }
    
    if (fieldType.type === 'checkbox') {
      newField.default_value = false; // Default checkbox state
    }
    
    if (fieldType.type === 'date') {
      newField.default_value = ''; // Default date value (can be 'today' or specific date)
    }
    
    // Organization data dropdowns - populate with live data
    if (fieldType.type === 'group_dropdown') {
      // Note: groups come from clientGroups - filter to only active ones (check both is_active and status)
      newField.options = (clientGroups || []).filter(g => g.status === 'active' || g.is_active === true || (g.is_active !== false && !g.status)).map(g => g.name);
      newField.default_value = '';
    }
    
    if (fieldType.type === 'heading') {
      newField.label = 'Section Heading';
      newField.headingLevel = 'h2'; // h1, h2, h3
      newField.textAlign = 'left'; // left, center, right
    }
    
    if (fieldType.type === 'static_text') {
      newField.label = 'Instructions or information';
      newField.content = 'Enter your text here. This text cannot be edited by form users.';
      newField.isBold = false;
      newField.isItalic = false;
      newField.borderEnabled = false;
      newField.borderThickness = 'thin';
      newField.borderColor = '#d1d5db';
      newField.imageAlign = 'left';
    }
    
    if (fieldType.type === 'divider') {
      newField.label = 'Divider';
      newField.thickness = 1; // pixels
      newField.color = '#cccccc';
    }
    
    if (fieldType.type === 'spacer') {
      newField.label = 'Spacer';
      newField.height = 20; // pixels
    }
    
    if (fieldType.type === 'container') {
      newField.label = 'Shaded Box';
      newField.containerFields = []; // Fields inside this container
      newField.padding = 16;
      newField.borderRadius = 8;
      newField.borderWidth = 1;
    }
    
    if (fieldType.type === 'row_placeholder') {
      newField.label = 'Empty Row';
      newField.width = '100'; // Always full width
      newField.isPlaceholder = true; // Mark as placeholder (hidden in preview/view)
    }
    
    // Client Data Fields (read-only, auto-populated)
    if (fieldType.category === 'client_data') {
      // client_diagnoses_selector is interactive, not auto-populated
      if (fieldType.type === 'client_diagnoses_selector') {
        newField.isReadOnly = false;
        newField.autoPopulate = false;
        newField.label = 'Client Diagnoses (Selectable)';
        newField.showColumns = ['code', 'description', 'date'];
      } else if (fieldType.type === 'add_contacts') {
        // Interactive button field — adds chart contacts from the note.
        newField.isReadOnly = false;
        newField.autoPopulate = false;
        newField.label = 'Contacts Added This Session';
      } else {
        newField.isReadOnly = true;
        newField.autoPopulate = true;
      }
      
      if (fieldType.type === 'client_diagnoses') {
        newField.label = 'Client Diagnoses';
        newField.showColumns = ['code', 'description', 'tag', 'date'];
      }
      
      if (fieldType.type === 'client_medications') {
        newField.label = 'Current Medications';
        newField.showColumns = ['name', 'dosage', 'frequency', 'prescriber', 'purpose'];
      }
      
      if (fieldType.type === 'client_insurance') {
        newField.label = 'Insurance Information';
        newField.showColumns = ['type', 'company', 'member_id', 'group'];
      }
      
      if (fieldType.type === 'client_providers') {
        newField.label = 'Assigned Providers';
        newField.showColumns = ['name', 'role', 'specialty'];
      }
    }
    
    // Add to activeFormFields
    if (sectionId && sectionId !== 'default') {
      // Insert after the last field in the specified section
      setActiveFormFields(fields => {
        const newFields = [...fields];
        
        // Find the section header index
        const sectionIndex = newFields.findIndex(f => f.id === sectionId);
        if (sectionIndex === -1) {
          // Section not found, append to end
          return [...newFields, newField];
        }
        
        // Find the next section header or end of array
        let insertIndex = newFields.length;
        for (let i = sectionIndex + 1; i < newFields.length; i++) {
          if (newFields[i].type === 'section_header') {
            insertIndex = i;
            break;
          }
        }
        
        // Insert before the next section (or at end)
        newFields.splice(insertIndex, 0, newField);
        return newFields;
      });
    } else if (sectionId === 'default') {
      // Default section (no section headers) - find the first section_header or append to end
      setActiveFormFields(fields => {
        const newFields = [...fields];
        const firstSectionIndex = newFields.findIndex(f => f.type === 'section_header');
        if (firstSectionIndex === -1) {
          // No sections at all, append to end
          return [...newFields, newField];
        } else {
          // Insert before the first section header
          newFields.splice(firstSectionIndex, 0, newField);
          return newFields;
        }
      });
    } else {
      // No section specified, append to end
      setActiveFormFields(fields => [...fields, newField]);
    }
    toast.success(`Added "${fieldType.label}" field`);
  };

  const removeFormField = (fieldId) => {
    const fieldToRemove = activeFormFields.find(f => f.id === fieldId);
    setActiveFormFields(fields => fields.filter(field => field.id !== fieldId));
    if (fieldToRemove) {
      toast.success(`Removed "${fieldToRemove.label}" field`);
    }
  };
  
  const moveFieldToSection = (fieldId, targetSectionId) => {
    const sections = groupFieldsBySections(activeFormFields);
    
    // Find the field and remove it from current section
    let fieldToMove = null;
    let newSections = sections.map(section => {
      const fieldInSection = section.fields.find(f => f.id === fieldId);
      if (fieldInSection) {
        fieldToMove = fieldInSection;
        return {
          ...section,
          fields: section.fields.filter(f => f.id !== fieldId)
        };
      }
      return section;
    });
    
    if (!fieldToMove) return;
    
    // Add field to target section
    newSections = newSections.map(section => {
      if (section.header.id === targetSectionId) {
        return {
          ...section,
          fields: [...section.fields, fieldToMove]
        };
      }
      return section;
    });
    
    // Update activeFormFields
    setActiveFormFields(flattenSections(newSections));
  };
  
  const duplicateFormField = (fieldId) => {
    const fieldToDuplicate = activeFormFields.find(field => field.id === fieldId);
    if (fieldToDuplicate) {
      const duplicatedField = {
        ...fieldToDuplicate,
        id: `field_${Date.now()}`,
        label: fieldToDuplicate.label  // Keep the same label - each instance is its own thing
      };
      
      const fieldIndex = activeFormFields.findIndex(field => field.id === fieldId);
      const newFields = [...activeFormFields];
      newFields.splice(fieldIndex + 1, 0, duplicatedField);
      setActiveFormFields(newFields);
      
      toast.success("Field duplicated successfully");
    }
  };

  const updateFormField = useCallback((fieldId, updates) => {
    setActiveFormFields(fields => fields.map(field => 
      field.id === fieldId ? { ...field, ...updates } : field
    ));
  }, []);

  // Auto-sync static text content from cache to state every 5 seconds while form builder is open
  // This prevents content loss when React re-renders the component
  useEffect(() => {
    if (!showFormTemplateBuilder) return;
    
    const syncStaticTextToState = () => {
      const cache = staticTextEditorCache.current;
      let hasChanges = false;
      
      // Check if any cached items have user edits before triggering a state update
      cache.forEach((cached) => {
        if (cached.hasUserEdits) hasChanges = true;
      });
      
      if (!hasChanges) return;
      
      // Batch all field updates into a single setActiveFormFields call
      setActiveFormFields(fields => {
        let changed = false;
        const updated = fields.map(field => {
          if (field.type === 'static_text') {
            const cached = cache.get(field.id);
            if (cached && cached.hasUserEdits && cached.content !== field.content) {
              changed = true;
              return { ...field, content: cached.content };
            }
          }
          return field;
        });
        return changed ? updated : fields;
      });
    };
    
    // Sync every 5 seconds
    const intervalId = setInterval(syncStaticTextToState, 5000);
    
    // Also sync when user switches away from the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        syncStaticTextToState();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showFormTemplateBuilder]);

  const updateSection = (sectionId, updates) => {
    setActiveFormSections(sections => sections.map(section => 
      section.id === sectionId ? { ...section, ...updates } : section
    ));
  };

  // ── Eligibility client-chart state (data lives in useClientEligibility
  //    hook; only the UI-dialog open/close booleans + 270/271 preview
  //    state stay here since they're chart-screen UI concerns).

  const [showEligibilityHistory, setShowEligibilityHistory] = useState(false);
  const [showEligibilityBenefits, setShowEligibilityBenefits] = useState(false);

  // 270 Preview state (client chart)
  const [show270Preview, setShow270Preview] = useState(false);
  const [preview270Content, setPreview270Content] = useState('');
  const [preview270Meta, setPreview270Meta] = useState({});
  const [loading270Preview, setLoading270Preview] = useState(false);
  // 271 Details state (client chart)
  const [show271Details, setShow271Details] = useState(false);
  const [details271, setDetails271] = useState(null);
  const [loading271Details, setLoading271Details] = useState(false);

  // 270 Preview handlers (client chart context)
  const handleView270 = async (checkId) => {
    setLoading270Preview(true);
    setShow270Preview(true);
    try {
      const response = await axios.get(`${API}/eligibility/preview-270/${checkId}`);
      setPreview270Content(response.data.file_content || '');
      setPreview270Meta({
        control_number: response.data.control_number,
        insurance_name: response.data.insurance_name,
        payer_id: response.data.payer_id,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate 270 preview');
      setShow270Preview(false);
    }
    setLoading270Preview(false);
  };

  const handleDownload270File = () => {
    if (!preview270Content) return;
    const blob = new Blob([preview270Content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `270-${preview270Meta.control_number || 'preview'}.x12`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      if (document.body.contains(a)) document.body.removeChild(a);
    }, 100);
  };

  const handleCopy270 = () => {
    navigator.clipboard.writeText(preview270Content).then(() => {
      toast.success('270 payload copied to clipboard');
    });
  };

  const handleView271Details = async (checkId) => {
    setLoading271Details(true);
    setShow271Details(true);
    try {
      const response = await axios.get(`${API}/eligibility/status/${checkId}`);
      setDetails271(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load 271 details');
      setShow271Details(false);
    }
    setLoading271Details(false);
  };

  const handleDownload271Responses = async (specificClientId = null, forceRefresh = false) => {
    // If we already have a completed response for this client, just reload from DB (skip SFTP)
    if (specificClientId && !forceRefresh) {
      const existing = eligibility271State[specificClientId];
      const alreadyReceived = ['verified', 'active', 'rejected', 'inactive'].includes(
        selectedClient?.primary_eligibility_status
      );
      if (alreadyReceived && existing?.lastChecked) {
        const ago = Math.round((Date.now() - existing.lastChecked.getTime()) / 60000);
        const agoLabel = ago < 1 ? 'just now' : ago === 1 ? '1 min ago' : `${ago} min ago`;
        await fetchEligibilityDetail(specificClientId);
        toast.info(`Showing cached result (verified ${agoLabel}). Click "Refresh Results" again to re-check Availity.`, { duration: 5000 });
        setEligibility271State(prev => ({
          ...prev,
          [specificClientId]: { ...prev[specificClientId], lastChecked: new Date() }
        }));
        return;
      }
    }
    // Enforce 30-second cooldown per client
    if (specificClientId) {
      const existing = eligibility271State[specificClientId];
      if (existing?.lastChecked) {
        const secondsSince = (Date.now() - existing.lastChecked.getTime()) / 1000;
        if (secondsSince < 30) {
          toast.info(`Checked ${Math.round(secondsSince)}s ago — please wait ${Math.round(30 - secondsSince)}s before checking again.`, { duration: 4000 });
          return;
        }
      }
      setEligibility271State(prev => ({ ...prev, [specificClientId]: { loading: true, lastChecked: prev[specificClientId]?.lastChecked || null } }));
    }
    try {
      const clientName = specificClientId && selectedClient?.id === specificClientId 
        ? selectedClient.full_name || selectedClient.preferred_name 
        : null;
      
      toast.info(
        clientName 
          ? `Checking for ${clientName}'s 271 response...` 
          : 'Downloading 271 responses from Availity...', 
        { duration: 3000 }
      );
      
      // Add client_id as query parameter if specified
      const url = specificClientId 
        ? `${API}/billing/availity/download-271-responses?client_id=${specificClientId}`
        : `${API}/billing/availity/download-271-responses`;
      
      const response = await axios.post(url);
      
      const { files_downloaded, files_processed, processed_checks, unmatched_files, errors, client_filter_debug } = response.data;
      
      if (files_processed > 0) {
        // Show success with details
        let message = specificClientId 
          ? `Found and processed ${clientName}'s eligibility response!\n\n`
          : `Successfully processed ${files_processed} of ${files_downloaded} response file(s)\n\n`;
        
        if (processed_checks && processed_checks.length > 0) {
          message += 'Updated eligibility for:\n';
          processed_checks.forEach(check => {
            message += `• ${check.client_name} - ${check.coverage_status}\n`;
            if (check.has_errors) {
              message += `  Has errors: ${check.error_messages?.join(', ') || 'See details'}\n`;
            }
            if (check.discrepancies_count > 0) {
              message += `  ${check.discrepancies_count} discrepancy(ies) found\n`;
            }
          });
        }
        
        if (unmatched_files && unmatched_files.length > 0 && !specificClientId) {
          message += `\n${unmatched_files.length} file(s) couldn't be matched:\n`;
          unmatched_files.forEach(file => {
            message += `• ${file.filename} - ${file.reason}\n`;
          });
        }
        
        if (errors && errors.length > 0) {
          message += `\n${errors.length} error(s) occurred during processing`;
        }
        
        toast.success(message, { duration: 15000 });
        
        // If any processed check has discrepancies, open the review modal
        const checksWithDiscrepancies = processed_checks?.filter(c => c.discrepancies_count > 0);
        if (checksWithDiscrepancies?.length > 0 && specificClientId) {
          const firstCheck = checksWithDiscrepancies[0];
          // Re-fetch the full check to get parsed discrepancy detail
          try {
            const histRes = await axios.get(`${API}/clients/${specificClientId}/eligibility-history`);
            const fullCheck = histRes.data.find(c => c.id === firstCheck.check_id);
            if (fullCheck?.discrepancies?.length > 0) {
              setDiscrepancyData({
                checkId: firstCheck.check_id,
                clientId: specificClientId,
                clientName: clientName,
                discrepancies: fullCheck.discrepancies,
              });
              setShowDiscrepancyReview(true);
            }
          } catch { /* best effort */ }
        }

        // Refresh client data and auto-load eligibility detail
        if (selectedClient?.id) {
          // Optimistic update — don't wait for full client re-fetch
          if (specificClientId && processed_checks?.length > 0) {
            const first = processed_checks[0];
            const coverageToStatus = { active: 'verified', inactive: 'inactive', rejected: 'rejected' };
            applyEligibilityResult(specificClientId, {
              status: coverageToStatus[first.coverage_status] || first.coverage_status,
              lastChecked: new Date().toISOString(),
            });
          }
          handleClientClick(selectedClient.id);
          fetchEligibilityDetail(selectedClient.id);
        }
      } else if (files_downloaded === 0) {
        toast.info(
          specificClientId
            ? `No 271 response files found on Availity SFTP yet. Availity may still be processing the request.`
            : 'No 271 response files available yet. Availity may still be processing your request.',
          { duration: 8000 }
        );
      } else if (files_downloaded > 0 && files_processed === 0) {
        if (specificClientId) {
          toast.info(
            `No 271 response found for ${clientName} yet. Availity may still be processing the request.`,
            { duration: 8000 }
          );
        } else {
          let message = `Downloaded ${files_downloaded} file(s) but couldn't match them to any pending check.\n\n`;
          if (unmatched_files && unmatched_files.length > 0) {
            unmatched_files.forEach(file => {
              message += `• ${file.filename}\n  Control#: ${file.control_number || 'N/A'}\n  Reason: ${file.reason}\n`;
            });
          }
          toast.warning(message, { duration: 15000 });
        }
      } else {
        toast.info('No new 271 responses to process.', { duration: 5000 });
      }
      
    } catch (error) {
      console.error('Failed to download 271 responses:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to download responses';
      toast.error(errorMsg);
    } finally {
      if (specificClientId) {
        setEligibility271State(prev => ({
          ...prev,
          [specificClientId]: { loading: false, lastChecked: new Date() }
        }));
      }
    }
  };

  // Eligibility management functions — moved to EligibilityTab.jsx

  const handleClientClick = async (clientId, options = {}) => {
    const { initialTab = 'overview', fetchAuthorizations = false, skipUrlUpdate = false } = options;
    
    // Clear stale billing data immediately to prevent PHI flash from prior client
    setClientBillingSummary(null);
    setClientClaims([]);
    setClientPaymentHistory([]);

    // Check if we already have this client's basic data in memory
    const cachedClient = allClientsForLookup?.find(c => c.id === clientId) || clients?.find(c => c.id === clientId);
    
    // If we have cached client data, show chart immediately with it
    // This prevents the loading screen flash for clients already in memory
    if (cachedClient) {
      setSelectedClient(cachedClient);
      setShowClientDetail(true);
      setClientDetailTab(initialTab);

      // Stamp the bundle marker SYNCHRONOUSLY — the per-resource
      // useEffects (insurances / enrollments / notes / authorizations)
      // fire on selectedClient change, i.e. BEFORE the bundle resolves.
      // Without the early stamp they race the bundle with their own
      // fetches, and the duplicate arrivals re-replace the arrays →
      // visible churn / remounts while the chart is loading (Jun 2026
      // render-thrash fix).  Reset to null in the catch below so a
      // failed bundle doesn't permanently block those fallbacks.
      chartBundleClientIdRef.current = clientId;
      
      // Update URL immediately
      if (!skipUrlUpdate) {
        const tabParam = initialTab !== 'overview' ? `?tab=${initialTab}` : '';
        navigate(`/client/${clientId}${tabParam}`, { replace: true });
      }
      
      // Fetch additional data in background (insurances, diagnoses, etc.)
      // These will fill in as they load - no blocking
      try {
        // Workstream C1 + SWR (Feb 2026) — single chart-bundle
        // request replaces the 6-8 parallel fetches that used to
        // fire on the cached-client path.  Same bundle endpoint the
        // uncached path uses; server-side fans out via
        // asyncio.gather.  Goes through fetchChartBundleSWR so a
        // warm cache renders the chart INSTANTLY (zero network
        // wait) while a background If-None-Match validation runs;
        // the onFresh callback below swaps in new data only if the
        // server's ETag actually changed.
        //
        // Build a single "applyBundle" closure that we call:
        //   - once synchronously with the (possibly stale) cached data
        //   - again from onFresh if the background fetch returned new data
        const applyBundle = (bundle) => {
          if (!bundle) return;
          // Per-collection identity guard (Jun 2026 render-thrash fix):
          // applyBundle runs twice (cached apply + SWR fresh swap) and the
          // fresh data is usually identical for most collections.  Skipping
          // unchanged setStates prevents the full teardown/remount of the
          // care-team / insurance subtrees that made the chart feel like it
          // "re-renders constantly" and dropped clicks/keystrokes.
          const snap = lastBundleAppliedRef.current;
          if (snap.clientId !== clientId) {
            lastBundleAppliedRef.current = { clientId };
          }
          const applyIfChanged = (key, setter, value) => {
            const j = JSON.stringify(value);
            if (lastBundleAppliedRef.current[key] === j) return;
            lastBundleAppliedRef.current[key] = j;
            setter(value);
          };
          setSelectedClient(prev => {
            if (prev?.id !== clientId) {
              void 0 && console.log(`[handleClientClick] Discarding stale data for ${clientId}, current client is ${prev?.id}`);
              return prev; // Don't update, client changed
            }
            applyIfChanged('diagnoses', setClientDiagnoses, bundle.diagnoses || []);
            applyIfChanged('medications', setClientMedications, bundle.medications || []);
            applyIfChanged('appointments', setClientAppointments, bundle.appointments || []);
            applyIfChanged('insurances', setSelectedClientInsurances, bundle.insurances || []);
            applyIfChanged('alerts', setClientAlerts, bundle.alerts || []);
            applyIfChanged('vitals', setClientVitals, bundle.vitals || []);
            applyIfChanged('authorizations', setClientAuthorizations, bundle.authorizations || []);
            if (bundle.enrollments) applyIfChanged('enrollments', setClientEnrollments, bundle.enrollments);
            chartBundleClientIdRef.current = clientId;
            return prev;
          });
          // Always apply notes + documents from the bundle.  The bundle
          // ALWAYS carries them and stamping chartBundleClientIdRef
          // blocks the follow-up per-resource useEffects from
          // refetching.  Previously gated on initialTab — caused a
          // bug where opening a chart via the Billing tab (e.g. from
          // Claims Workshop) left the Documents tab empty because
          // neither notes nor documents were applied AND the
          // follow-up effects were short-circuited by the ref stamp.
          setSelectedClient(prev => {
            if (prev?.id !== clientId) return prev;
            applyIfChanged('notes', setClientNotes, bundle.notes || []);
            setClientNotesTotalCount(bundle?._meta?.notes_total ?? (bundle.notes || []).length);
            setClientNotesHasMore(!!bundle?._meta?.notes_has_more);
            applyIfChanged('documents', setClientDocuments, bundle.documents || []);
            return prev;
          });
          setSelectedClient(prev => {
            if (prev?.id !== clientId) return prev;
            const next = bundle.client || prev;
            // Keep the same object identity when nothing changed so
            // [selectedClient]-dep effects don't re-fire on the swap.
            if (next !== prev && JSON.stringify(next) === JSON.stringify(prev)) return prev;
            return next;
          });
        };

        const { data: bundle } = await fetchChartBundleSWR(clientId, (freshBundle) => {
          // Background revalidation returned fresh data — swap in.
          void 0 && console.log(`[handleClientClick] SWR: swapping in fresh data for ${clientId}`);
          applyBundle(freshBundle);
        });
        applyBundle(bundle);

      } catch (error) {
        console.error('Error fetching client details:', error);
        // Bundle failed — unblock the per-resource fallback effects.
        if (chartBundleClientIdRef.current === clientId) {
          chartBundleClientIdRef.current = null;
        }
      }

      // Authorizations are already in the bundle; skip the extra
      // /authorizations/{client_id} fetch we used to make for the
      // ``initialTab === 'authorizations'`` and ``fetchAuthorizations``
      // paths.  The bundle's authorizations array + the
      // chartBundleClientIdRef short-circuit in the follow-up effect
      // cover both cases.

      return;
    }
    
    // No cached data - need to fetch everything with loading screen
    setSelectedClient(null);
    setClientDiagnoses([]);
    setClientMedications([]);
    setClientAppointments([]);
    setClientAuthorizations([]);
    setClientEligibility(null);
    setClientNotes([]);
    setClientDocuments([]);
    setSelectedClientInsurances([]);
    setClientDetailLoading(true);
    setShowClientDetail(true);
    
    try {
      // Workstream C1 (Feb 2026) — single bundled chart-open request.
      // Replaces the legacy 6-9 parallel fetches with one round-trip;
      // server-side does the fan-out via asyncio.gather.  See
      // /app/backend/routes/chart_bundle.py.  Goes through
      // fetchChartBundle so revisits hit the ETag/304 cache.
      const { data: bundle } = await fetchChartBundle(clientId);

      // For uncached path, we set everything at once - no race condition here
      // because we showed loading screen and cleared all data first
      setSelectedClient(bundle.client);
      resetEligibilityForClient();
      setShowEligibilityHistory(false);
      setShowEligibilityBenefits(false);
      // Auto-load eligibility detail for any client that has had a check
      const newClient = bundle.client;
      if (newClient?.primary_eligibility_status && newClient.primary_eligibility_status !== 'not_checked') {
        fetchEligibilityDetail(newClient.id);
      }
      setClientDiagnoses(bundle.diagnoses || []);
      setClientMedications(bundle.medications || []);
      setClientAppointments(bundle.appointments || []);
      setSelectedClientInsurances(bundle.insurances || []);
      setClientVitals(bundle.vitals || []);

      // Notes / documents — always apply from the bundle so the
      // Documents tab is populated regardless of which tab the chart
      // opened on (Billing, Eligibility, etc.).  The bundle always
      // carries them and stamping ``chartBundleClientIdRef`` below
      // blocks the per-resource useEffects from refetching.  Earlier
      // version gated these on initialTab which caused empty
      // Documents tab when entering via Billing (Claims Workshop).
      setClientNotes(bundle.notes || []);
      setClientNotesTotalCount(bundle?._meta?.notes_total ?? (bundle.notes || []).length);
      setClientNotesHasMore(!!bundle?._meta?.notes_has_more);
      setClientDocuments(bundle.documents || []);

      // Authorizations — bundle always carries them too, so the inner
      // tab-driven fetch will find them already present and skip its
      // refetch.  Setting here regardless keeps both initialTab variants
      // green without an extra HTTP call.
      setClientAuthorizations(bundle.authorizations || []);

      // Enrollments + alerts — also delivered by the bundle.  These were
      // previously fetched in their own useEffect on selectedClient
      // change; setting them here means those effects no-op via cache.
      if (bundle.enrollments) setClientEnrollments(bundle.enrollments);
      if (bundle.alerts) setClientAlerts(bundle.alerts);

      // Stamp the chart-bundle-populated client id so the per-resource
      // follow-up useEffects (documents/insurances/notes/authorizations)
      // skip their redundant fetches.  Handles the empty-array case
      // ("zero notes / docs / etc.") that the per-effect "alreadyHave"
      // guards can't catch.
      chartBundleClientIdRef.current = bundle.client?.id || clientId;

      setClientDetailTab(initialTab);

      if (!skipUrlUpdate) {
        const tabParam = initialTab !== 'overview' ? `?tab=${initialTab}` : '';
        navigate(`/client/${clientId}${tabParam}`, { replace: true });
      }
    } catch (error) {
      toast.error("Failed to load client details");
      setShowClientDetail(false);
    } finally {
      setClientDetailLoading(false);
    }
  };

  // Helper function to get appointments for a specific date
  const getAppointmentsForDate = (date, appointmentsList = appointments) => {
    return appointmentsList.filter(apt => {
      const aptDate = new Date(apt.start_time);
      return aptDate.toDateString() === date.toDateString();
    });
  };

  // Helper function to get calendar days for current month
  // Helper: build the 6×7 Month-view grid. The pure version lives in
  // utils/calendar.js — we wrap it so callers don't have to pass
  // `calendarDate` every time.
  const getCalendarDays = () => getCalendarDaysUtil(calendarDate);

  // Firm-wide calendar events (holidays, all-staff meetings). Pulls a window
  // wide enough for any of the schedule views (±45 days around calendarDate).
  // Backend expands recurring events into concrete occurrences.
  const firmWindowStart = new Date(calendarDate || new Date()); firmWindowStart.setDate(firmWindowStart.getDate() - 45);
  const firmWindowEnd = new Date(calendarDate || new Date()); firmWindowEnd.setDate(firmWindowEnd.getDate() + 45);
  const { getForDate: getFirmEventsForDate } = useFirmCalendarEvents(firmWindowStart, firmWindowEnd);

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    
    // Check if it's a valid date format (YYYY-MM-DD or parseable date)
    const birthDate = new Date(dateOfBirth);
    if (isNaN(birthDate.getTime())) return null;  // Invalid date returns null
    
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    // Return null for obviously invalid ages (e.g., negative or > 150)
    if (age < 0 || age > 150) return null;
    
    return age;
  };


  // Format date to MM/DD/YYYY
  const formatDateMMDDYYYY = (dateString) => {
    if (!dateString) return null;
    
    // Parse date string as local date to avoid timezone conversion
    // Input format: YYYY-MM-DD
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      // Validate it looks like a date (YYYY-MM-DD)
      if (parts.length === 3 && parts[0].length === 4 && !isNaN(parseInt(parts[0]))) {
        const [year, month, day] = parts;
        return `${month}/${day}/${year}`;
      }
    }
    
    // Fallback for other date formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;  // Invalid date returns null
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    // Sanity check year range
    if (year < 1900 || year > 2100) return null;
    
    return `${month}/${day}/${year}`;
  };

  // Helper function to format 24-hour time to 12-hour AM/PM format
  const formatTimeTo12Hour = (timeString) => {
    if (!timeString) return null;

    // Feb 2026 — robust input handling.  The biller's "Prepare a Claim"
    // dialog reads `note.start_time` / `note.end_time` which is now an
    // ISO UTC timestamp (e.g. "2026-06-19T02:00:00.000Z") since the
    // service-line editor writes `start_time_iso`.  The original
    // implementation only knew "HH:MM" and silently parsed "2026-06-19T02"
    // by stopping at the dash → hours=2026 → 2026%12=10 → "10:00 PM",
    // making every modern claim display 10 PM regardless of the real
    // time.  Detect an ISO string up front and route it through the
    // Date parser so we get the user's local clock face.
    const str = String(timeString);
    if (str.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(str)) {
      const d = new Date(str);
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
      }
      return str;
    }

    // Handle HH:MM format (24-hour)
    const parts = str.split(':');
    if (parts.length < 2) return str;

    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];

    if (isNaN(hours)) return str;

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12

    return `${hours}:${minutes} ${ampm}`;
  };

  // Helper function to format provider name with credentials
  // formatProviderName(provider) is imported from ./utils/userHelpers.js.
  // Renders "Name, Credentials" with graceful fallback when full_name
  // or credentials are missing.

  // Validation functions for intake form
  const validateEmail = (email) => {
    if (!email) return null; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const validatePhone = (phone) => {
    if (!phone) return 'Phone number is required';
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    // Check if we have 10 digits (US) or 11 digits (US with country code)
    if (digitsOnly.length === 10 || digitsOnly.length === 11) {
      return null;
    }
    return 'Please enter a valid phone number (10 digits)';
  };

  const validateZipCode = (zipcode) => {
    if (!zipcode) return 'Zip code is required';
    // Allow 5 digits or 9 digits with optional hyphen (12345 or 12345-6789)
    const zipRegex = /^\d{5}(-?\d{4})?$/;
    if (!zipRegex.test(zipcode)) {
      return 'Please enter a valid zip code (5 or 9 digits)';
    }
    return null;
  };

  const validateSSN = (ssn) => {
    if (!ssn) return null; // SSN is optional
    // Check for XXX-XX-XXXX format
    const ssnRegex = /^\d{3}-\d{2}-\d{4}$/;
    if (!ssnRegex.test(ssn)) {
      return 'Please enter SSN in format XXX-XX-XXXX';
    }
    return null;
  };

  const validateDateOfBirth = (dob) => {
    if (!dob) return 'Date of birth is required';
    
    // Check if it's a valid date
    const date = new Date(dob);
    if (isNaN(date.getTime())) {
      return 'Please enter a valid date';
    }
    
    // Check if date is not in the future
    const today = new Date();
    if (date > today) {
      return 'Date of birth cannot be in the future';
    }
    
    // Check age is reasonable (0-120 years)
    const age = calculateAge(dob);
    if (age < 0 || age > 120) {
      return 'Please enter a valid date of birth';
    }
    
    return null;
  };

  // Handle field validation on blur
  const handleIntakeFieldValidation = (fieldName, value) => {
    let error = null;
    
    switch(fieldName) {
      case 'email':
        error = validateEmail(value);
        break;
      case 'phone':
        error = validatePhone(value);
        break;
      case 'zipcode':
        error = validateZipCode(value);
        break;
      case 'ssn':
        error = validateSSN(value);
        break;
      case 'date_of_birth':
        error = validateDateOfBirth(value);
        break;
      default:
        break;
    }
    
    setIntakeValidationErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };


  // `getFieldConfig`, `isFieldVisible`, `isFieldRequired` — moved to
  // the consolidated `createIntakePermissions` call near the top of
  // this component (Phase 3a, Feb 2026).
  
  // Get dropdown options for a field
  const getFieldOptions = (fieldId) => {
    const fieldConfig = getFieldConfig(fieldId);
    return fieldConfig && fieldConfig.options ? fieldConfig.options : [];
  };

  // Helper function to get provider names from IDs
  const getProviderNames = (providerIds) => {
    if (!providerIds || providerIds.length === 0) return 'None assigned';
    return providerIds
      .map(id => {
        const provider = users.find(u => u.id === id);
        return provider ? provider.full_name : id;
      })
      .join(', ');
  };

  // Helper function to format time from 24-hour to 12-hour format
  const formatTime12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper function to get accessible providers based on user role
  const getAccessibleProviders = (appointmentType = null) => {
    if (!user) return [];
    
    // Determine if this appointment type requires a provider (billable sessions)
    const requiresProvider = appointmentType === 'client_session' || appointmentType === 'group';
    
    // Early return if users not loaded yet
    if (!Array.isArray(users)) {
      void 0 && console.log('⚠️ Users not loaded yet, returning empty array');
      return [];
    }
    
    // Admin and Office staff can see all providers or all users depending on appointment type
    if (isSuperAdmin(user) || userRoleName === 'office') {
      if (requiresProvider) {
        // For billable appointments, only show providers
        const providers = users.filter(u => u.is_provider === true);
        void 0 && console.log('✅ Returning providers for admin/super admin:', providers.length);
        return providers;
      } else {
        // For non-billable appointments (block time, vacation, etc.), show all users
        void 0 && console.log('✅ Returning all users for non-billable:', users?.length);
        return users || [];
      }
    }
    
    // Providers can see themselves (default scheduling is for themselves)
    if (userRoleName === 'provider' || user?.is_provider) {
      // Check if this provider supervises others
      const supervisees = users.filter(u => 
        u.supervisor_ids && u.supervisor_ids.includes(user.id) && u.is_provider === true
      );
      
      // If they have supervisees, show themselves + supervisees (but default to themselves)
      if (supervisees.length > 0) {
        return [user, ...supervisees];
      }
      
      // Otherwise just themselves
      return [user];
    }
    
    // Default: empty array
    return [];
  };

  const getAccessibleClients = (providerId = null) => {
    if (!user) return [];
    
    // Use allClientsForLookup to avoid filter bleeding from Client Management
    const clientList = allClientsForLookup?.length > 0 ? allClientsForLookup : (clients || []);
    
    // Filter out archived and merged clients first
    const activeClients = clientList.filter(c => c.status !== 'archived' && c.status !== 'merged');
    
    let filteredClients;
    
    // If a provider is selected, filter by that provider's assigned clients
    // This prevents scheduling a provider with a client they don't have access to
    if (providerId) {
      filteredClients = activeClients.filter(c => {
        const hasProvider = c.assigned_providers && c.assigned_providers.includes(providerId);
        return hasProvider;
      });
      void 0 && console.log('Filtering clients for provider:', providerId, '- found:', filteredClients.length);
    } else {
      // If no provider selected yet, show all clients for admins/office
      // or own assigned clients for providers
      const userRole = user?.role?.toLowerCase();
      
      if (isSuperAdmin(user) || 
          userRole === 'office' || 
          userRole === 'office staff' || 
          userRole === 'reception' ||
          userRole === 'biller' ||
          userRole === 'admin') {
        void 0 && console.log('Admin/Office - no provider selected yet, returning all', activeClients.length, 'clients');
        filteredClients = activeClients;
      } else if (user?.is_provider || 
          userRole === 'provider' || 
          userRole === 'intern' ||
          userRole === 'supervisor') {
        // Providers/Interns see their assigned clients
        filteredClients = activeClients.filter(c => 
          c.assigned_providers && c.assigned_providers.includes(user.id)
        );
        void 0 && console.log('Provider/Intern - returning own assigned clients:', filteredClients.length);
      } else {
        // Default: return all active clients (fallback for unknown roles)
        void 0 && console.log('Unknown role, returning all active clients:', activeClients.length);
        filteredClients = activeClients;
      }
    }
    
    // Jun 2026 — alphabetize by "LASTNAME, First" (the display order)
    // so the scheduling dropdown is scannable (user report).
    filteredClients = sortClientsByName(filteredClients);
    
    // When editing an appointment, always include the current appointment's client
    // This ensures the dropdown shows the correct client even if provider assignment changed
    // or the client is no longer assigned to the selected provider
    // Check both appointmentFormData.client_id and editingAppointment.client_id to handle timing issues
    const currentClientId = appointmentFormData?.client_id || editingAppointment?.client_id;
    if (editingAppointment && currentClientId) {
      const isCurrentClientInList = filteredClients.some(c => c.id === currentClientId);
      if (!isCurrentClientInList) {
        // Search in multiple sources to find the client:
        // 1. allClientsForLookup (includes archived, might still be loading)
        // 2. clients array (filtered by status in Client Management)
        // 3. Fall back to creating a minimal client object from appointment data
        const fullClientList = allClientsForLookup?.length > 0 ? allClientsForLookup : [];
        let currentClient = fullClientList.find(c => c.id === currentClientId);
        
        // If not in allClientsForLookup, check the main clients array
        if (!currentClient && clients?.length > 0) {
          currentClient = clients.find(c => c.id === currentClientId);
        }
        
        if (currentClient) {
          filteredClients = [currentClient, ...filteredClients];
          void 0 && console.log('Added current appointment client to list:', currentClient.full_name, 'status:', currentClient.status);
        } else {
          // Last resort: if we have client info on the appointment itself, create a minimal entry
          if (editingAppointment.client_name) {
            const minimalClient = {
              id: currentClientId,
              full_name: editingAppointment.client_name,
              first_name: editingAppointment.client_name?.split(' ')[0] || '',
              last_name: editingAppointment.client_name?.split(' ').slice(1).join(' ') || '',
              status: 'unknown'
            };
            filteredClients = [minimalClient, ...filteredClients];
            void 0 && console.log('Created minimal client entry from appointment:', minimalClient.full_name);
          } else {
            console.warn('Could not find client for appointment:', currentClientId, 
              'allClientsForLookup:', allClientsForLookup?.length, 
              'clients:', clients?.length);
          }
        }
      }
    }
    
    return filteredClients;
  };

  // iPrescribe — shared with V2 Meds & Vitals via the useIPrescribe
  // hook (extracted Phase 3 of the migration).  Same gate, same URL
  // resolution; keep `getIPrescribeUrl` as a thin wrapper for any
  // legacy callers still threading the URL through.
  const { canAccess: canAccessIPrescribe, open: openIPrescribe, url: _iprescribeUrl } = useIPrescribe();
  const getIPrescribeUrl = () => _iprescribeUrl;

  // Get clients visible to the current user for client management tab
  const getVisibleClientsForManagement = useMemo(() => {
    if (!user) return [];
    
    void 0 && console.log('[CLIENT VISIBILITY] User role:', user?.role, 'User ID:', user?.id, 'Clients count:', clients?.length);
    
    // Admin, super admin, office staff, billers, QA auditors can see all clients
    if (isSuperAdmin(user) || 
        userRoleName === 'admin' ||
        userRoleName === 'office' || userRoleName === 'office staff' ||
        user?.is_biller || user?.is_billing_manager ||
        user?.can_qa_audit) {
      void 0 && console.log('[CLIENT VISIBILITY] Admin/Office/Biller/QA - returning all', clients?.length, 'clients');
      return safeArray(clients);
    }
    
    // Supervisors can see their own clients + supervisees' clients
    if (userRoleName === 'supervisor') {
      // Find supervisees - users who have this user in their supervisor_ids array
      const superviseeIds = safeFilter(users, u => safeIncludes(u.supervisor_ids, user.id))
        .map(u => u.id);
      const filtered = safeFilter(clients, c => {
        const assignedProviders = safeArray(c.assigned_providers);
        // Client is assigned to this supervisor OR to any of their supervisees
        return assignedProviders.includes(user.id) || 
               assignedProviders.some(pid => superviseeIds.includes(pid));
      });
      void 0 && console.log('[CLIENT VISIBILITY] Supervisor - filtered to', filtered.length, 'clients');
      return filtered;
    }
    
    // Regular providers can only see their own assigned clients
    if (userRoleName === 'provider' || user?.is_provider) {
      const filtered = safeFilter(clients, c => 
        safeIncludes(c.assigned_providers, user.id)
      );
      void 0 && console.log('[CLIENT VISIBILITY] Provider - user.id:', user.id);
      void 0 && console.log('[CLIENT VISIBILITY] Provider - clients from API:', clients?.length);
      void 0 && console.log('[CLIENT VISIBILITY] Provider - filtered to', filtered.length, 'clients');
      if (clients?.length > 0 && filtered.length === 0) {
        void 0 && console.log('[CLIENT VISIBILITY] First client assigned_providers:', clients[0]?.assigned_providers);
      }
      return filtered;
    }
    
    // Default: empty array
    void 0 && console.log('[CLIENT VISIBILITY] Default - no matching role, returning empty');
    return [];
  }, [user, clients, users]);

  const getAccessibleCPTCodes = (providerId = null) => {
    if (!user) return [];
    
    // If a provider is selected, ALWAYS filter by that provider's assigned CPT codes/bundles
    // This prevents admins from scheduling a provider with a CPT code they don't have access to
    if (providerId) {
      void 0 && console.log('Filtering CPT codes for provider:', providerId);
      
      // Find the provider to get their individually assigned CPT codes
      const provider = users.find(u => u.id === providerId) || providers.find(p => p.id === providerId);
      const individualCptIds = provider?.assigned_cpt_codes || provider?.cpt_codes || [];
      void 0 && console.log('Provider individual CPT codes:', individualCptIds.length);
      
      // Get bundles assigned to this provider (check both directions)
      // 1. Bundles where provider is in bundle.assigned_provider_ids
      // 2. Bundle IDs in provider.assigned_cpt_bundles
      const providerBundleIds = provider?.assigned_cpt_bundles || [];
      const assignedBundles = (cptBundles || []).filter(bundle =>
        (bundle.assigned_provider_ids && bundle.assigned_provider_ids.includes(providerId)) ||
        providerBundleIds.includes(bundle.id)
      );
      
      void 0 && console.log('Provider has bundles:', assignedBundles.map(b => b.name));
      
      // Get all CPT code IDs from assigned bundles
      const cptIdsFromBundles = assignedBundles.flatMap(bundle => bundle.cpt_code_ids || []);
      
      void 0 && console.log('CPT IDs from bundles:', cptIdsFromBundles.length);
      
      // Combine individual CPT codes and bundle CPT codes
      const allAssignedCptIds = new Set([...individualCptIds, ...cptIdsFromBundles]);
      
      // Return CPT codes that are individually assigned OR in the assigned bundles
      const filteredCpts = (cptCodes || []).filter(cpt => 
        allAssignedCptIds.has(cpt.id)
      );
      
      void 0 && console.log('Filtered CPT codes (individual + bundles):', filteredCpts.length);
      // Admin override: when an admin (or super admin) is auto-selected
      // as the appointment owner and has no CPT assignments, fall back
      // to the full catalog so the picker isn't empty.  Server-side CPT
      // eligibility still enforces correctness on save.
      if (filteredCpts.length === 0 && (isAdmin() || user?.is_super_admin)) {
        return cptCodes || [];
      }
      return filteredCpts;
    }
    
    // If no provider selected yet:
    // - Admin/Office can see all CPT codes
    // - Providers see only their CPT codes from bundles + individual assignments
    if (isAdmin() || userRoleName === 'office') {
      void 0 && console.log('Admin/Office - no provider selected yet, returning all CPT codes');
      return cptCodes || [];
    }
    
    if (userRoleName === 'provider') {
      // Get individually assigned CPT codes
      const individualCptIds = user.assigned_cpt_codes || user.cpt_codes || [];
      
      // Get bundles assigned to this provider (check both directions)
      const userBundleIds = user.assigned_cpt_bundles || [];
      const assignedBundles = (cptBundles || []).filter(bundle =>
        (bundle.assigned_provider_ids && bundle.assigned_provider_ids.includes(user.id)) ||
        userBundleIds.includes(bundle.id)
      );
      
      // Get all CPT code IDs from assigned bundles
      const cptIdsFromBundles = assignedBundles.flatMap(bundle => bundle.cpt_code_ids || []);
      
      // Combine individual CPT codes and bundle CPT codes
      const allAssignedCptIds = new Set([...individualCptIds, ...cptIdsFromBundles]);
      
      // Return CPT codes that are individually assigned OR in the assigned bundles
      return (cptCodes || []).filter(cpt => 
        allAssignedCptIds.has(cpt.id)
      );
    }
    
    // Default: empty array
    return [];
  };

  // Helper function to get CPT codes assigned to a provider
  const getProviderCptCodes = (providerId) => {
    if (!providerId) {
      void 0 && console.log('No provider ID provided');
      return [];
    }
    
    const provider = users.find(u => u.id === providerId);
    if (!provider) {
      void 0 && console.log('Provider not found:', providerId);
      return [];
    }
    
    void 0 && console.log('Provider found:', provider.full_name);
    void 0 && console.log('Assigned CPT codes:', provider.assigned_cpt_codes);
    void 0 && console.log('Assigned CPT bundles:', provider.assigned_cpt_bundles);
    void 0 && console.log('Total CPT codes available:', cptCodes.length);
    void 0 && console.log('Total CPT bundles available:', cptBundles.length);
    
    // Get CPT codes from assigned_cpt_codes and assigned_cpt_bundles
    const assignedCodeIds = provider.assigned_cpt_codes || [];
    const assignedBundleIds = provider.assigned_cpt_bundles || [];
    
    // Get all CPT codes that match the assigned codes
    let availableCodes = cptCodes.filter(code => 
      assignedCodeIds.includes(code.id) && code.is_active
    );
    
    void 0 && console.log('Direct CPT codes found:', availableCodes.length);
    
    // Also get CPT codes from bundles
    const bundleCptIds = [];
    assignedBundleIds.forEach(bundleId => {
      const bundle = cptBundles.find(b => b.id === bundleId);
      if (bundle) {
        void 0 && console.log('Found bundle:', bundle.name, 'with', bundle.cpt_code_ids?.length, 'CPT codes');
        bundleCptIds.push(...(bundle.cpt_code_ids || []));
      }
    });
    
    void 0 && console.log('Bundle CPT IDs:', bundleCptIds);
    
    // Add bundle CPT codes
    const bundleCodes = cptCodes.filter(code => 
      bundleCptIds.includes(code.id) && code.is_active
    );
    
    void 0 && console.log('Bundle CPT codes found:', bundleCodes.length);
    
    // Combine and remove duplicates
    const allCodes = [...availableCodes, ...bundleCodes];
    const uniqueCodes = allCodes.filter((code, index, self) => 
      index === self.findIndex(c => c.id === code.id)
    );
    
    void 0 && console.log('Total unique CPT codes:', uniqueCodes.length);
    
    return uniqueCodes;
  };

  // Drag and drop handlers — moved into useCalendarDrag hook (Stage 4).
  // The handlers are now returned from the hook at the top of this
  // component and surfaced via the bridge for the inline calendar
  // and used directly by the Fullscreen Calendar Dialog below.

  const handleCreateCptCode = async (e) => {
    e.preventDefault();
    try {
      if (editingCptCode) {
        // Update existing CPT code — optimistically merge the form data
        // into local state so the price/display-name change shows up
        // immediately. Previously we only called refreshClients/refreshStats
        // which don't touch the CPT list, so the UI showed stale values
        // until the user hit refresh.
        const { data: updated } = await axios.put(`${API}/cpt-codes/${editingCptCode.id}`, cptFormData);
        setCptCodes(prev => prev.map(c =>
          c.id === editingCptCode.id ? { ...c, ...cptFormData, ...(updated || {}) } : c
        ));
        toast.success("CPT Code updated successfully");
        setEditingCptCode(null);
      } else {
        // Create new CPT code — optimistically push onto the list so it
        // appears at the top of the admin table without a refetch.
        const { data: created } = await axios.post(`${API}/cpt-codes`, cptFormData);
        if (created && created.id) {
          setCptCodes(prev => [created, ...prev]);
        }
        toast.success("CPT Code created successfully");
      }
      
      setShowCptForm(false);
      setCptFormData({
        code: '',
        display_name: '',
        description: '',
        category: '',
        bundle_ids: [],
        billing_type: 'insurance',
        units: 1,
        duration_minutes: 45,
        minutes_per_unit: 45,
        allows_multiple_units: true,
        is_group_session: false,
        default_group_id: '',
        default_template_id: '',
        min_duration: null,
        max_duration: null,
        min_age: null,
        max_age: null,
        pos_codes: [],
        modifiers: [],
        color: '#3B82F6',
        is_addon_code: false,
        requires_primary_codes: [],
        requires_authorization: false
      });
      refreshClients(); refreshStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingCptCode ? 'update' : 'create'} CPT code`);
    }
  };

  const handleArchiveCptCode = async (cptCodeId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await axios.patch(`${API}/cpt-codes/${cptCodeId}`, { is_active: newStatus });
      // Optimistic update - update local state immediately
      setCptCodes(prev => prev.map(c => c.id === cptCodeId ? { ...c, is_active: newStatus } : c));
      toast.success(`CPT Code ${newStatus ? 'activated' : 'archived'} successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update CPT code status");
    }
  };

  const handleDeleteCptCode = async (cptCodeId, cptCodeName) => {
    // Check if CPT code is in use
    try {
      const response = await axios.get(`${API}/cpt-codes/${cptCodeId}/usage`);
      const { appointment_count, can_delete } = response.data;
      
      if (!can_delete) {
        toast.error(`Cannot delete ${cptCodeName}: ${appointment_count} appointment(s) are using this code. Please archive instead to preserve appointment history.`);
        return;
      }
      
      // If no appointments, proceed with deletion
      if (window.confirm(`Are you sure you want to permanently delete CPT code ${cptCodeName}? This action cannot be undone.`)) {
        await axios.delete(`${API}/cpt-codes/${cptCodeId}`);
        // Optimistic update - remove from local state immediately
        setCptCodes(prev => prev.filter(c => c.id !== cptCodeId));
        toast.success("CPT Code deleted successfully");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete CPT code");
    }
  };

  const checkCptCodeUsage = async () => {
    if (!cptCodes) return;
    
    const usageData = {};
    for (const code of cptCodes) {
      try {
        const response = await axios.get(`${API}/cpt-codes/${code.id}/usage`);
        usageData[code.id] = response.data;
      } catch (error) {
        console.error(`Failed to check usage for ${code.code}:`, error);
      }
    }
    setCptCodeUsage(usageData);
  };

  const handleCreateFormTemplate = async (e) => {
    e.preventDefault();
    try {
      // Calculate max_score if this is a scored assessment
      let scoringConfig = formTemplateData.scoring_config || {};
      if (formTemplateData.is_scored_assessment) {
        let maxScore = 0;
        activeFormFields.forEach(field => {
          if (field.is_scored && field.score_values) {
            const fieldMax = Math.max(...Object.values(field.score_values));
            maxScore += fieldMax;
          }
        });
        scoringConfig = { ...scoringConfig, max_score: maxScore };
      }
      
      const templateData = {
        ...formTemplateData,
        form_fields: activeFormFieldsRef.current,
        scoring_config: scoringConfig
      };
      
      if (editingFormTemplate) {
        // Update existing form template
        await axios.put(`${API}/form-templates/${editingFormTemplate.id}`, templateData);
        // Optimistically update local state
        setFormTemplates(prev => prev.map(t => 
          t.id === editingFormTemplate.id ? { ...t, ...templateData } : t
        ));
        toast.success("Form template updated successfully");
        setEditingFormTemplate(null);
      } else {
        // Create new form template
        const response = await axios.post(`${API}/form-templates`, templateData);
        // Optimistically add to local state
        setFormTemplates(prev => [...prev, response.data]);
        toast.success("Form template created successfully");
      }
      
      setShowFormTemplateBuilder(false);
      setFormTemplateData({
        name: '',
        description: '',
        form_type: '',
        form_fields: [],
        billable: false,
        requires_provider_signature: true,
        requires_supervisor_signature: false,
        allow_client_signature: false,
        require_client_signature: false,
        is_scored_assessment: false,
        is_group_template: false,
        available_in_quick_create: true,
        allow_external_download: false,
        allow_print_for_signature: false,
        allow_send_to_client: false,
        scoring_config: { max_score: 0, severity_ranges: [], clinical_alerts: [] }
      });
      setActiveFormFields([]);
      // Refresh form templates specifically (not all data) - skip cache after save
      fetchFormTemplates(true);
      // Return to the Forms Management dialog after saving
      setShowFormsManagementDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save form template");
    }
  };

  const handleDeleteFormTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to archive this form template?")) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/form-templates/${templateId}`);
      // Optimistically update local state - mark as archived or remove
      setFormTemplates(prev => prev.filter(t => t.id !== templateId));
      // Backend returns a message indicating if it was archived due to usage
      toast.success(response.data.message || "Form template archived successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive form template");
      // Refresh to restore state on error
      fetchFormTemplates(true);
    }
  };

  const duplicateFormTemplate = async (template) => {
    try {
      const duplicatedTemplate = {
        name: `${template.name} (Copy)`,
        description: template.description || '',
        form_type: template.form_type,
        form_fields: template.form_fields || [],
        billable: template.billable || false,
        requires_provider_signature: template.requires_provider_signature !== false,
        requires_supervisor_signature: template.requires_supervisor_signature || false,
        allow_client_signature: template.allow_client_signature || false,
        require_client_signature: template.require_client_signature || false,
        available_in_quick_create: template.available_in_quick_create !== false
      };
      
      const response = await axios.post(`${API}/form-templates`, duplicatedTemplate);
      // Optimistically add to local state
      setFormTemplates(prev => [...prev, response.data]);
      toast.success("Form template duplicated successfully");
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error(error.response?.data?.detail || "Failed to duplicate form template");
    }
  };

  // Handler to update form field values during note creation
  // Uses startTransition for better performance with rapid input (e.g., voice-to-text)
  const handleNewNoteFieldChange = useCallback((fieldId, value) => {
    startTransition(() => {
      setNewNoteFormData(prev => ({
        ...prev,
        [fieldId]: value
      }));
    });
  }, [startTransition]);

  // Handle adding a linked form from a comprehensive assessment or other triggering form
  const handleAddLinkedForm = async (linkedFormConfig, sourceNote) => {
    try {
      if (!linkedFormConfig.linked_template_id) {
        toast.error(`Template "${linkedFormConfig.form_name}" not found in system`);
        return;
      }
      
      // Get the template
      const templateResponse = await axios.get(`${API}/form-templates/${linkedFormConfig.linked_template_id}`);
      const template = templateResponse.data;
      
      // Prepare initial content from template fields
      const initialContent = {
        template_id: template.id,
        template_name: template.name,
        fields: template.form_fields.map(field => ({
          ...field,
          value: field.type === 'checkbox_group' || field.type === 'multiselect' ? [] : ''
        }))
      };
      
      // Create the note for the same client
      const noteData = {
        client_id: sourceNote.client_id,
        template_id: template.id,
        template_name: template.name,
        content: JSON.stringify(initialContent),
        status: 'draft',
        provider_id: user.id,
        billable: template.billable || false,
        requires_supervisor_signature: template.requires_supervisor_signature || false,
        source_note_id: sourceNote.id, // Link back to the source assessment
        source_note_name: sourceNote.template_name || 'Assessment'
      };
      
      const response = await axios.post(`${API}/notes`, noteData);
      const newNote = response.data;
      
      // Create a task if configured
      if (linkedFormConfig.create_task) {
        const client = allClientsForLookup.find(c => c.id === sourceNote.client_id);
        const taskData = {
          title: `Complete ${linkedFormConfig.form_name} for ${client?.full_name || 'Client'}`,
          description: `This form was triggered from ${sourceNote.template_name || 'an assessment'}. Please complete and sign.`,
          assigned_to: user.id,
          client_id: sourceNote.client_id,
          source_note_id: newNote.id,
          due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
          priority: 'medium',
          status: 'pending'
        };
        await axios.post(`${API}/tasks`, taskData);
      }
      
      // Track that this linked form was added
      setLinkedFormsAdded(prev => ({
        ...prev,
        [linkedFormConfig.form_name]: newNote.id
      }));
      
      toast.success(`${linkedFormConfig.form_name} added to client's chart${linkedFormConfig.create_task ? ' and task created' : ''}`);
      refreshNotes(); refreshTodos(); // Refresh to show new note
      
    } catch (error) {
      console.error('Error adding linked form:', error);
      toast.error(`Failed to add ${linkedFormConfig.form_name}: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleCreateClinicalNote = async (noteContent, templateId = null, providerId = null, clientIds = [], isGroup = false, appointmentId = null, initialStatus = 'draft', manualServiceDetails = null) => {
    // DOUBLE-CLICK PROTECTION: Prevent duplicate note creation
    if (isCreatingNoteRef.current) {
      void 0 && console.log('[Note Create] Already creating a note, ignoring duplicate request');
      return null;
    }
    
    // Filter out any empty/invalid client IDs
    const validClientIds = (clientIds || []).filter(id => id && typeof id === 'string' && id.trim());
    
    // Validate client selection
    if (!isGroup && validClientIds.length === 0) {
      toast.error("Please select at least one client");
      return null;
    }
    
    if (isGroup && validClientIds.length < 2) {
      toast.error("Group notes require at least 2 clients");
      return null;
    }
    
    // Validate provider selection
    if (!providerId) {
      toast.error("Please select a provider for this note");
      return null;
    }
    
    try {
      // Set creation flag to prevent duplicate requests
      isCreatingNoteRef.current = true;
      
      // Refresh templates to ensure we have latest version
      await fetchFormTemplates();
      
      // Get template info if template was used (use refreshed templates)
      const template = templateId ? formTemplates.find(t => t.id === templateId) : null;
      
      const noteData = {
        // For group notes: use client_ids array, for individual: use client_id
        ...(isGroup ? {
          client_ids: validClientIds,
          is_group_note: true
        } : {
          client_id: validClientIds[0],  // Single client
          is_group_note: false
        }),
        provider_id: providerId,  // Include selected provider
        content: JSON.stringify(noteContent),
        appointment_id: appointmentId && appointmentId !== 'none' ? appointmentId : null,  // Optional appointment link
        template_id: templateId,
        title: template ? template.name : null,
        billable: template ? template.billable || false : false,
        status: initialStatus,  // Use the selected initial status
        linked_goals: linkedGoals,  // Include linked treatment plan goals
        telehealth_consent: telehealthConsent?.pos_code ? telehealthConsent : null,  // Include telehealth consent affirmation
        // Manual service details when no appointment is linked
        ...(manualServiceDetails && (!appointmentId || appointmentId === 'none') ? {
          date_of_service: manualServiceDetails.dos || null,
          start_time: manualServiceDetails.start_time || null,
          end_time: manualServiceDetails.end_time || null,
          units: manualServiceDetails.units || null,
          cpt_code: manualServiceDetails.cpt || null,
          cpt_code_id: manualServiceDetails.cpt_code_id || null,
          pos_code: manualServiceDetails.pos_code || null,
          modality: manualServiceDetails.modality || null
        } : {})
      };
      
      let response;
      
      // If there's an autosaved draft, update it instead of creating a new note
      // CRITICAL: Use ref (autosaveNoteIdRef) for synchronous check to prevent race condition
      // where state hasn't updated yet but autosave already created a note
      const draftIdToUpdate = autosaveNoteIdRef.current || autosaveNoteId;
      
      if (draftIdToUpdate) {
        void 0 && console.log('[NOTE] Updating autosaved draft:', draftIdToUpdate);
        try {
          response = await axios.put(`${API}/notes/${draftIdToUpdate}`, noteData);
        } catch (putError) {
          // CRITICAL FALLBACK: If PUT fails with 404, the draft may have been deleted
          // or never existed (race condition). Fall back to POST to prevent data loss.
          if (putError.response?.status === 404) {
            console.warn('[NOTE] Draft not found (404), falling back to POST to prevent data loss:', draftIdToUpdate);
            response = await axios.post(`${API}/notes`, noteData);
          } else {
            // Re-throw non-404 errors (e.g., 500, network errors)
            throw putError;
          }
        }
        // Clear the autosave ID since we've now properly saved
        setAutosaveNoteId(null);
        autosaveNoteIdRef.current = null;
        setLastAutosave(null);
        // CRITICAL: Clean up URL to prevent "draft restored" message on next dialog open
        const url = new URL(window.location.href);
        if (url.searchParams.has('draftNote')) {
          url.searchParams.delete('draftNote');
          window.history.replaceState({}, '', url.toString());
        }
      } else {
        response = await axios.post(`${API}/notes`, noteData);
      }
      
      toast.success(`Clinical note ${draftIdToUpdate ? 'saved' : 'created'} successfully (Status: ${initialStatus.replace(/_/g, ' ').toUpperCase()})${isGroup ? ' for ' + clientIds.length + ' clients' : ''}`);
      
      // Optimistic update: Add the new note to the local state immediately
      if (response?.data) {
        const newNote = response.data;
        // Add to notes list if it's for the current view
        setNotes(prevNotes => {
          // Check if note already exists (shouldn't, but just in case)
          const exists = prevNotes.some(n => n.id === newNote.id);
          if (exists) {
            return prevNotes.map(n => n.id === newNote.id ? newNote : n);
          }
          // Add to beginning of list (most recent first)
          return [newNote, ...prevNotes];
        });
        
        // CRITICAL: Also update clientNotes for the Documents tab
        // This is separate from the global notes list
        setClientNotes(prevClientNotes => {
          const exists = prevClientNotes.some(n => n.id === newNote.id);
          if (exists) {
            return prevClientNotes.map(n => n.id === newNote.id ? newNote : n);
          }
          return [newNote, ...prevClientNotes];
        });
        
        // Also update myTodoNotes if it's assigned to current user
        if (newNote.provider_id === user?.id || newNote.provider_signed_by === user?.id) {
          setMyTodoNotes(prevTodo => {
            const exists = prevTodo.some(n => n.id === newNote.id);
            if (exists) {
              return prevTodo.map(n => n.id === newNote.id ? newNote : n);
            }
            return [newNote, ...prevTodo];
          });
        }
      }
      
      // Also refresh data in background to ensure consistency
      refreshNotes(); refreshTodos();
      
      // CRITICAL: Refresh client notes if viewing a client's chart
      // This ensures the Documents tab shows the newly created note
      if (selectedClient?.id && response?.data?.client_id === selectedClient.id) {
        refreshClientNotes();
      }
      
      setLinkedGoals([]);
      setTelehealthConsent({ provider_location: null, pos_code: null, pos_description: null, modality: null });  // Reset telehealth consent
      
      return response.data; // Return the created note
    } catch (error) {
      // Jun 2026 — duplicate-note adoption: a 409 here means a previous
      // attempt (e.g. before a network timeout) actually created the note.
      // Open the existing note instead of dead-ending with an error.
      const dupDetail = error.response?.status === 409 ? error.response?.data?.detail : null;
      if (dupDetail && typeof dupDetail === 'object' && dupDetail.existing_note_id) {
        try {
          const { data: existingNote } = await axios.get(`${API}/notes/${dupDetail.existing_note_id}`);
          toast.info('A note already exists for this appointment — opening your existing note.');
          refreshNotes(); refreshTodos();
          return existingNote;
        } catch { /* fall through to generic error */ }
      }
      console.error("Error creating clinical note:", error);
      const _d = error.response?.data?.detail;
      toast.error(typeof _d === 'object' ? (_d?.message || 'Failed to create clinical note') : (_d || "Failed to create clinical note"));
      return null;
    } finally {
      // Always clear the creation flag
      isCreatingNoteRef.current = false;
    }
  };

  // Form field reordering helpers
  const moveFieldUp = (fieldId) => {
    setActiveFormFields(fields => {
      const index = fields.findIndex(f => f.id === fieldId);
      if (index > 0) {
        const newFields = [...fields];
        [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
        return newFields;
      }
      return fields;
    });
  };

  const moveFieldDown = (fieldId) => {
    setActiveFormFields(fields => {
      const index = fields.findIndex(f => f.id === fieldId);
      if (index < fields.length - 1) {
        const newFields = [...fields];
        [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
        return newFields;
      }
      return fields;
    });
  };


  // Form Type Management handlers
  const handleCreateFormType = async (e) => {
    e.preventDefault();
    
    try {
      if (editingFormType) {
        // Update existing
        await axios.put(`${API}/form-types/${editingFormType.id}`, formTypeFormData);
        // Optimistically update local state
        setFormTypes(prev => prev.map(t => 
          t.id === editingFormType.id ? { ...t, ...formTypeFormData } : t
        ));
        toast.success("Form type updated successfully");
      } else {
        // Create new
        const response = await axios.post(`${API}/form-types`, formTypeFormData);
        // Optimistically add to local state
        setFormTypes(prev => [...prev, response.data]);
        toast.success("Form type created successfully");
      }
      
      setShowManageFormTypes(false);
      setFormTypeFormData({ name: '', description: '', sort_order: 0 });
      setEditingFormType(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save form type");
      // Form types are static data - no global fetchData needed
    }
  };

  const handleDeleteFormType = async (formTypeId) => {
    if (!window.confirm("Are you sure you want to delete this form type? This won't affect existing forms.")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/form-types/${formTypeId}`);
      // Optimistically remove from local state
      setFormTypes(prev => prev.filter(t => t.id !== formTypeId));
      toast.success("Form type deleted successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete form type");
      // Refresh on error
      refreshNotes(); refreshClients(); refreshStats();
    }
  };

  const handleMoveFormTypeUp = async (formTypeId) => {
    const currentIndex = formTypes.findIndex(ft => ft.id === formTypeId);
    if (currentIndex <= 0) return;
    
    const currentType = formTypes[currentIndex];
    const previousType = formTypes[currentIndex - 1];
    
    // Optimistically update local state first
    setFormTypes(prev => {
      const newTypes = [...prev];
      [newTypes[currentIndex], newTypes[currentIndex - 1]] = [newTypes[currentIndex - 1], newTypes[currentIndex]];
      return newTypes;
    });
    
    try {
      // Swap sort orders
      await axios.put(`${API}/form-types/${currentType.id}`, {
        name: currentType.name,
        description: currentType.description,
        sort_order: previousType.sort_order
      });
      
      await axios.put(`${API}/form-types/${previousType.id}`, {
        name: previousType.name,
        description: previousType.description,
        sort_order: currentType.sort_order
      });
      
      toast.success("Form type order updated");
    } catch (error) {
      toast.error("Failed to update order");
      // Revert form types optimistic update on error
      setFormTypes(prev => {
        const newTypes = [...prev];
        const idx = newTypes.findIndex(ft => ft.id === formTypeId);
        if (idx > 0) [newTypes[idx], newTypes[idx - 1]] = [newTypes[idx - 1], newTypes[idx]];
        return newTypes;
      });
    }
  };

  const handleMoveFormTypeDown = async (formTypeId) => {
    const currentIndex = formTypes.findIndex(ft => ft.id === formTypeId);
    if (currentIndex >= formTypes.length - 1) return;
    
    const currentType = formTypes[currentIndex];
    const nextType = formTypes[currentIndex + 1];
    
    // Optimistically update local state first
    setFormTypes(prev => {
      const newTypes = [...prev];
      [newTypes[currentIndex], newTypes[currentIndex + 1]] = [newTypes[currentIndex + 1], newTypes[currentIndex]];
      return newTypes;
    });
    
    try {
      // Swap sort orders
      await axios.put(`${API}/form-types/${currentType.id}`, {
        name: currentType.name,
        description: currentType.description,
        sort_order: nextType.sort_order
      });
      
      await axios.put(`${API}/form-types/${nextType.id}`, {
        name: nextType.name,
        description: nextType.description,
        sort_order: currentType.sort_order
      });
      
      toast.success("Form type order updated");
    } catch (error) {
      toast.error("Failed to update order");
      refreshNotes(); refreshClients(); refreshStats(); // Revert on error
    }
  };





  // Contact management handlers
  //
  // Mar 2026 — refactored: previously `handleCreateContact` and
  // `handleUpdateContact` had ~85% identical bodies (validation,
  // payload prep, form reset, error parsing) with only the HTTP verb
  // and URL differing.  Hoisted the shared pieces into three small
  // helpers so the two outer handlers are now slim glue.
  //   • EMPTY_CONTACT_FORM    — reset shape (was inlined twice)
  //   • buildContactPayload   — null-coerce optional dates / release_id
  //   • extractContactError   — turn an axios error into a human msg
  //                              (handles Pydantic detail arrays)
  // This sets up the V2 Contacts tab migration: a single shared
  // ContactForm + one unified save handler is much easier to lift
  // out of App.js than two near-duplicates.

  const EMPTY_CONTACT_FORM = React.useMemo(() => ({
    name: '',
    relationship: '',
    title_agency: '',
    phone: '',
    fax: '',
    email: '',
    can_receive_info: false,
    release_on_file: false,
    is_minor_or_legal_guardian: false,
    release_id: '',
    end_date: '',
    discharge_date: '',
    auto_calculate_end_date: true,
    lives_in_same_household: false,
    contact_address: '',
    contact_city: '',
    contact_state: '',
    contact_zipcode: '',
  }), []);

  const buildContactPayload = (formData) => ({
    ...formData,
    // Empty strings → null so the backend treats absent values as
    // genuinely unset rather than as an invalid empty string.
    email: formData.email || null,
    phone: formData.phone || null,
    discharge_date: formData.discharge_date || null,
    end_date: formData.end_date || null,
    release_id: formData.release_id || null,
  });

  const extractContactError = (error, fallback) => {
    if (error?.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation errors → join the msgs
        return detail.map(e => e.msg || e.message || 'Validation error').join(', ');
      }
      if (typeof detail === 'string') return detail;
    }
    return error?.message || fallback;
  };

  const handleCreateContact = async (e) => {
    e.preventDefault();
    if (!selectedClient?.id) {
      toast.error('No client selected. Please select a client first.');
      return;
    }
    if (!contactFormData.name || !contactFormData.relationship) {
      toast.error('Please fill in all required fields (Name and Relationship).');
      return;
    }
    try {
      await axios.post(
        `${API}/clients/${selectedClient.id}/contacts`,
        buildContactPayload(contactFormData),
      );
      setContactFormData(EMPTY_CONTACT_FORM);
      setShowAddContact(false);
      toast.success('Contact added successfully');
      handleClientClick(selectedClient.id, { initialTab: 'contacts' });
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error(extractContactError(error, 'Failed to add contact'));
    }
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    if (!selectedClient?.id) {
      toast.error('No client selected. Please select a client first.');
      return;
    }
    if (!editingContact?.id) {
      toast.error('No contact selected for editing.');
      return;
    }
    if (!contactFormData.name || !contactFormData.relationship) {
      toast.error('Please fill in all required fields (Name and Relationship).');
      return;
    }
    try {
      const payload = buildContactPayload(contactFormData);
      await axios.put(
        `${API}/clients/${selectedClient.id}/contacts/${editingContact.id}`,
        payload,
      );
      // If this is a parent / guardian contact, ALSO sync the client's
      // guardian fields so the legacy guardian banner stays accurate.
      // Best-effort: if this sync 500s we still show the success toast
      // because the contact itself saved correctly.
      if (contactFormData.relationship === 'parent' || contactFormData.relationship === 'guardian') {
        try {
          await axios.put(`${API}/clients/${selectedClient.id}`, {
            has_guardian: true,
            guardian_name: contactFormData.name,
            guardian_phone: contactFormData.phone || '',
            guardian_email: contactFormData.email || '',
            guardian_same_household: contactFormData.lives_in_same_household,
          });
        } catch (syncErr) {
          console.error('Failed to sync guardian fields to client:', syncErr);
        }
      }
      setContactFormData(EMPTY_CONTACT_FORM);
      setShowEditContact(false);
      setEditingContact(null);
      toast.success('Contact updated successfully');
      handleClientClick(selectedClient.id, { initialTab: 'contacts' });
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error(extractContactError(error, 'Failed to update contact'));
    }
  };

  // Mar 2026 — single source of truth for opening the Edit Contact
  // dialog seeded with an existing contact's values.  Previously this
  // ~17-line `setContactFormData({...})` block was inlined inside the
  // onClick of TWO different "Edit" buttons in the contacts list, AND
  // the second copy was missing `lives_in_same_household` +
  // `contact_address/city/state/zipcode` — so editing a contact from
  // that location silently wiped those fields.  Pulling the seed into
  // one helper eliminates the duplication AND patches that latent bug.
  const openEditContact = (contact) => {
    setEditingContact(contact);
    setContactFormData({
      ...EMPTY_CONTACT_FORM,
      name: contact.name || '',
      relationship: contact.relationship || '',
      title_agency: contact.title_agency || '',
      phone: contact.phone || '',
      fax: contact.fax || '',
      email: contact.email || '',
      can_receive_info: contact.can_receive_info || false,
      release_on_file: contact.release_on_file || false,
      is_minor_or_legal_guardian: contact.is_minor_or_legal_guardian || false,
      release_id: contact.release_id || '',
      end_date: contact.end_date ? contact.end_date.split('T')[0] : '',
      discharge_date: contact.discharge_date ? contact.discharge_date.split('T')[0] : '',
      auto_calculate_end_date: contact.auto_calculate_end_date !== undefined ? contact.auto_calculate_end_date : true,
      lives_in_same_household: contact.lives_in_same_household || false,
      contact_address: contact.contact_address || '',
      contact_city: contact.contact_city || '',
      contact_state: contact.contact_state || '',
      contact_zipcode: contact.contact_zipcode || '',
    });
    setShowEditContact(true);
  };

  const handleDeleteContact = async (clientId, contactId) => {
    if (!clientId || !contactId) {
      toast.error("Invalid client or contact ID.");
      return;
    }
    
    if (!confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      await axios.delete(`${API}/clients/${clientId}/contacts/${contactId}`);
      toast.success("Contact deleted successfully");
      
      // Refresh client data but stay on contacts tab
      handleClientClick(clientId, { initialTab: 'contacts' });
    } catch (error) {
      console.error("Error deleting contact:", error);
      let errorMessage = "Failed to delete contact";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Handle Pydantic validation errors
          errorMessage = detail.map(err => err.msg || err.message || "Validation error").join(", ");
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  // Client Group Management Handlers
  const handleCreateClientGroup = async (e) => {
    e.preventDefault();
    
    if (!groupFormData.name || !groupFormData.name.trim()) {
      toast.error("Group name is required");
      return;
    }
    
    try {
      // Convert client_ids to members format
      const members = (groupFormData.client_ids || []).map(clientId => ({
        client_id: clientId,
        joined_date: new Date().toISOString(),
        status: "active",
        sessions_attended: 0,
        total_sessions: 0,
        notes: ""
      }));
      
      const response = await axios.post(`${API}/client-groups`, {
        name: groupFormData.name.trim(),
        description: groupFormData.description || '',
        group_type: groupFormData.group_type || null,
        service_type_id: groupFormData.service_type_id || null,
        facilitator_id: groupFormData.facilitator_id || null,
        facilitator_ids: (groupFormData.facilitator_ids && groupFormData.facilitator_ids.length)
          ? groupFormData.facilitator_ids
          : (groupFormData.facilitator_id ? [groupFormData.facilitator_id] : []),
        cpt_code_id: groupFormData.cpt_code_id || null,
        curriculum_id: groupFormData.curriculum_id || null,
        location_id: groupFormData.location_id || null,
        schedule_day: groupFormData.schedule_day || null,
        start_time: groupFormData.start_time || null,
        end_time: groupFormData.end_time || null,
        modality: groupFormData.modality || 'in_person',
        status: groupFormData.status || 'active',
        total_sessions: groupFormData.total_sessions || null,
        current_session: groupFormData.current_session || 1,
        session_type: groupFormData.session_type || 'fixed',
        sessions_start_date: groupFormData.sessions_start_date || null,
        members: members,
        client_ids: groupFormData.client_ids || [],
        assign_facilitator_as_provider: groupFormData.assign_facilitator_as_provider || false
      });
      
      toast.success("Client group created successfully");
      
      // Optimistic update: Add the new group to local state immediately
      if (response.data) {
        setClientGroups(prevGroups => [...prevGroups, response.data]);
      }
      
      setShowAddGroup(false);
      
      // Reset form
      setGroupFormData({
        name: '',
        description: '',
        group_type: '',
        service_type_id: '',
        facilitator_id: '',
        location_id: '',
        schedule_day: '',
        start_time: '',
        end_time: '',
        modality: 'in_person',
        status: 'active',
        total_sessions: null,
        current_session: 1,
        session_type: 'fixed',
        sessions_start_date: '',
        members: [],
        client_ids: [],
        assign_facilitator_as_provider: false
      });
      
      // Refresh data
      refreshStats();
    } catch (error) {
      console.error("Error creating client group:", error);
      toast.error(getApiErrorMessage(error, "Failed to create client group"));
    }
  };

  const handleUpdateClientGroup = async (e) => {
    e.preventDefault();
    
    if (!editingGroup || !editingGroup.id) {
      toast.error("No group selected for editing");
      return;
    }
    
    if (!groupFormData.name || !groupFormData.name.trim()) {
      toast.error("Group name is required");
      return;
    }
    
    try {
      // Convert client_ids to members format (preserve existing member data)
      const existingMembers = editingGroup.members || [];
      const selectedClientIds = groupFormData.client_ids || [];
      
      // Truly new clients (not in existingMembers at all)
      const newClientIds = selectedClientIds.filter(
        id => !existingMembers.some(m => m.client_id === id)
      );
      
      const newMembers = newClientIds.map(clientId => {
        // Resolve the client name upfront. Server-side will also re-resolve
        // if we miss, but including it here means the UI never flickers to
        // "Unknown Client" on optimistic render, and older servers without
        // the name-resolution fallback still behave correctly.
        const c = (clients || []).find(x => x.id === clientId);
        const resolvedName = c?.full_name || (c?.first_name || c?.last_name ? `${c?.first_name || ''} ${c?.last_name || ''}`.trim() : '') || '';
        return {
          client_id: clientId,
          client_name: resolvedName || undefined,
          joined_date: new Date().toISOString(),
          status: "active",
          sessions_attended: 0,
          total_sessions: 0,
          notes: ""
        };
      });
      
      // For existing members: keep those still selected, reactivate removed ones
      const retainedMembers = existingMembers
        .filter(m => selectedClientIds.includes(m.client_id))
        .map(m => {
          // If the member was removed but is now re-selected, reactivate them
          if (m.status === 'removed' || m.status === 'discharged') {
            return { ...m, status: 'active', exit_date: null, exit_reason: null, exit_notes: null };
          }
          return m;
        });
      
      // Also preserve removed members NOT in the selection (history)
      const removedNotSelected = existingMembers.filter(
        m => (m.status === 'removed' || m.status === 'discharged') && !selectedClientIds.includes(m.client_id)
      );
      
      const allMembers = [...retainedMembers, ...newMembers, ...removedNotSelected];
      
      // Set is_active based on status: archived groups are inactive
      const isActive = groupFormData.status !== 'archived';

      // Helper: PUT the payload. The backend now refuses to silently
      // drop active members (Feb 2026 incident — a stale form wiped a
      // group's roster). On HTTP 409 it returns the names of members
      // that would be removed; we surface that to the operator and let
      // them explicitly confirm before retrying with the override flag.
      const submitGroupUpdate = async (confirmMemberChanges) => {
        const params = confirmMemberChanges ? '?confirm_member_changes=true' : '';
        await axios.put(`${API}/client-groups/${editingGroup.id}${params}`, {
          name: groupFormData.name.trim(),
          description: groupFormData.description || '',
          group_type: groupFormData.group_type || null,
          service_type_id: groupFormData.service_type_id || null,
          facilitator_id: groupFormData.facilitator_id || null,
          facilitator_ids: (groupFormData.facilitator_ids && groupFormData.facilitator_ids.length)
            ? groupFormData.facilitator_ids
            : (groupFormData.facilitator_id ? [groupFormData.facilitator_id] : []),
          cpt_code_id: groupFormData.cpt_code_id || null,
          curriculum_id: groupFormData.curriculum_id || null,
          location_id: groupFormData.location_id || null,
          schedule_day: groupFormData.schedule_day || null,
          start_time: groupFormData.start_time || null,
          end_time: groupFormData.end_time || null,
          modality: groupFormData.modality || 'in_person',
          status: groupFormData.status || 'active',
          is_active: isActive,
          total_sessions: groupFormData.total_sessions || null,
          current_session: groupFormData.current_session || 1,
          session_type: groupFormData.session_type || 'fixed',
          sessions_start_date: groupFormData.sessions_start_date || null,
          members: allMembers,
          client_ids: groupFormData.client_ids || [],
          assign_facilitator_as_provider: groupFormData.assign_facilitator_as_provider || false
        });
      };

      try {
        await submitGroupUpdate(false);
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (
          err.response?.status === 409
          && detail
          && typeof detail === 'object'
          && detail.error === 'member_changes_not_confirmed'
        ) {
          const names = (detail.removed_names || []).join(', ');
          const proceed = window.confirm(
            `⚠ This save would remove ${detail.removed_count} active member`
            + `${detail.removed_count > 1 ? 's' : ''} from this group:\n\n`
            + `${names}${(detail.removed_names || []).length < detail.removed_count ? '…' : ''}\n\n`
            + 'If that was NOT intentional, click Cancel and reopen the edit '
            + 'dialog to make sure the member list is loaded.\n\n'
            + 'Click OK to confirm removal and save anyway.'
          );
          if (!proceed) {
            // Bail out of the whole save flow — keep the dialog open
            // so the operator can correct.
            toast.info('Save cancelled. Reopen the edit dialog to recheck the member list.');
            return;
          }
          // Retry with the explicit override.
          await submitGroupUpdate(true);
        } else {
          throw err;
        }
      }
      
      // If "add to future appointments" is checked, add new members to all future appointments
      if (addToFutureAppointments && newClientIds.length > 0) {
        let totalAppointmentsUpdated = 0;
        for (const clientId of newClientIds) {
          try {
            const addResponse = await axios.post(
              `${API}/client-groups/${editingGroup.id}/members`,
              {
                client_id: clientId,
                joined_date: new Date().toISOString(),
                status: "active",
                sessions_attended: 0
              },
              { params: { add_to_future_appointments: true } }
            );
            totalAppointmentsUpdated += addResponse.data.appointments_updated || 0;
          } catch (addError) {
            console.error(`Error adding ${clientId} to future appointments:`, addError);
          }
        }
        if (totalAppointmentsUpdated > 0) {
          toast.success(`Added ${newClientIds.length} member(s) to ${totalAppointmentsUpdated} future appointment(s)`);
        }
      }
      
      toast.success("Client group updated successfully");
      
      // Optimistic update: Update local state immediately
      setClientGroups(prevGroups => prevGroups.map(g => {
        if (g.id === editingGroup.id) {
          return {
            ...g,
            name: groupFormData.name.trim(),
            description: groupFormData.description || '',
            group_type: groupFormData.group_type || null,
            service_type_id: groupFormData.service_type_id || null,
            facilitator_id: groupFormData.facilitator_id || null,
            facilitator_ids: (groupFormData.facilitator_ids && groupFormData.facilitator_ids.length)
              ? groupFormData.facilitator_ids
              : (groupFormData.facilitator_id ? [groupFormData.facilitator_id] : []),
            cpt_code_id: groupFormData.cpt_code_id || null,
            curriculum_id: groupFormData.curriculum_id || null,
            location_id: groupFormData.location_id || null,
            schedule_day: groupFormData.schedule_day || null,
            start_time: groupFormData.start_time || null,
            end_time: groupFormData.end_time || null,
            modality: groupFormData.modality || 'in_person',
            status: groupFormData.status || 'active',
            is_active: isActive,
            total_sessions: groupFormData.total_sessions || null,
            current_session: groupFormData.current_session || 1,
            session_type: groupFormData.session_type || 'fixed',
            sessions_start_date: groupFormData.sessions_start_date || null,
            members: allMembers,
            client_ids: groupFormData.client_ids || [],
            assign_facilitator_as_provider: groupFormData.assign_facilitator_as_provider || false
          };
        }
        return g;
      }));
      
      setEditingGroup(null);
      setShowAddGroup(false);
      setAddToFutureAppointments(false);
      // Update URL to remove groupId
      navigate(`/dashboard?view=groups`, { replace: true });
      
      // Reset form
      setGroupFormData({
        name: '',
        description: '',
        group_type: '',
        service_type_id: '',
        facilitator_id: '',
        location_id: '',
        schedule_day: '',
        start_time: '',
        end_time: '',
        modality: 'in_person',
        status: 'active',
        total_sessions: null,
        current_session: 1,
        session_type: 'fixed',
        members: [],
        client_ids: [],
        assign_facilitator_as_provider: false
      });
      
      // Note: Not calling fetchData() here since we already did optimistic update above
      // This prevents race conditions where fetchData() returns stale data
    } catch (error) {
      console.error("Error updating client group:", error);
      toast.error(getApiErrorMessage(error, "Failed to update client group"));
    }
  };

  const handleCompleteGroup = async (groupId, groupName) => {
    if (!confirm(`Mark "${groupName}" as completed? All members will be marked as successfully completed.`)) {
      return;
    }
    
    try {
      // Get the group
      const group = clientGroups.find(g => g.id === groupId);
      if (!group) return;
      
      // Mark all active members as completed
      const updatedMembers = (group.members || []).map(m => {
        if (m.status === 'active') {
          return {
            ...m,
            status: 'completed',
            exit_reason: 'completed_successfully',
            exit_date: new Date().toISOString()
          };
        }
        return m;
      });
      
      await axios.put(`${API}/client-groups/${groupId}?confirm_member_changes=true`, {
        ...group,
        status: 'completed',
        members: updatedMembers,
        client_ids: [] // Clear active client_ids
      });
      
      toast.success("Group marked as completed");
      setShowAddGroup(false);
      setEditingGroup(null);
      // Update URL to remove groupId
      navigate(`/dashboard?view=groups`, { replace: true });
      refreshStats();
    } catch (error) {
      console.error("Error completing group:", error);
      toast.error(getApiErrorMessage(error, "Failed to complete group"));
    }
  };

  const handleArchiveGroup = async (groupId, groupName) => {
    if (!confirm(`Archive "${groupName}"? It will be moved to archived groups.`)) {
      return;
    }
    
    try {
      // Use dedicated archive endpoint - no need to send entire group object
      await axios.post(`${API}/client-groups/${groupId}/archive`);
      
      toast.success("Group archived");
      setShowAddGroup(false);
      setEditingGroup(null);
      // Update URL to remove groupId
      navigate(`/dashboard?view=groups`, { replace: true });
      refreshClients(); refreshStats();
    } catch (error) {
      console.error("Error archiving group:", error);
      toast.error(getApiErrorMessage(error, "Failed to archive group"));
    }
  };

  const handleDeleteClientGroup = async (groupId, groupName) => {
    // Check if group has appointments or notes before allowing delete
    try {
      const groupAppts = (appointments || []).filter(apt => apt.selected_group_id === groupId || apt.group_id === groupId);
      const groupNotes = (notes || []).filter(n => n.group_id === groupId);
      
      if (groupAppts.length > 0 || groupNotes.length > 0) {
        toast.error(
          `Cannot delete group "${groupName}" - it has ${groupAppts.length} appointments and ${groupNotes.length} notes. ` +
          `Please archive the group instead by setting status to "Archived".`
        );
        return;
      }
    } catch (error) {
      console.error('Error checking group usage:', error);
    }
    
    if (!confirm(`Are you sure you want to permanently delete the group "${groupName}"? This cannot be undone.`)) {
      return;
    }
    
    // Double confirmation for safety
    if (!confirm(`FINAL CONFIRMATION: Delete "${groupName}"? Type DELETE to confirm.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/client-groups/${groupId}`);
      toast.success("Client group deleted successfully");
      
      // Refresh data
      refreshStats();
    } catch (error) {
      console.error("Error deleting client group:", error);
      toast.error(getApiErrorMessage(error, "Failed to delete client group"));
    }
  };

  const handleEditClientGroup = async (group) => {
    setEditingGroup(group);
    setShowPastGroupAppointments(false);
    
    // Jun 2026 fix (user bug): the groups list can be stale — e.g. a Meet
    // link generated in a previous dialog session lives in the DB but not
    // in the list object, so reopening "lost" it. Hydrate from the server.
    try {
      const fresh = await axios.get(`${API}/client-groups/${group.id}`);
      if (fresh.data?.id) setEditingGroup((g) => (g && g.id === group.id ? { ...g, ...fresh.data } : g));
    } catch { /* keep the list object if the fetch fails */ }
    
    // Update URL with groupId
    navigate(`/dashboard?view=groups&groupId=${group.id}`, { replace: true });
    
    // Fetch appointments for this group
    try {
      // Feb 2026 perf fix: was loading the entire ±30d appointments
      // window and filtering client-side.  Use the new group_id
      // filter + summary mode to ship only what we need.
      const response = await axios.get(`${API}/appointments?group_id=${group.id}&summary=true`);
      const groupAppts = response.data || [];
      // Sort by start_time
      groupAppts.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      setGroupAppointments(groupAppts);
    } catch (error) {
      console.error('Error fetching group appointments:', error);
      setGroupAppointments([]);
    }
    
    // Extract client_ids ONLY from active members — this is the source of truth.
    // group.client_ids may be stale if add/remove/reactivate didn't sync perfectly.
    const client_ids = (group.members || [])
      .filter(m => m.status === 'active')
      .map(m => m.client_id);
    
    setGroupFormData({
      name: group.name || '',
      description: group.description || '',
      group_type: group.group_type || '',
      service_type_id: group.service_type_id || '',
      facilitator_id: group.facilitator_id || '',
      facilitator_ids: (group.facilitator_ids && group.facilitator_ids.length)
        ? group.facilitator_ids
        : (group.facilitator_id ? [group.facilitator_id] : []),
      cpt_code_id: group.cpt_code_id || '',
      curriculum_id: group.curriculum_id || '',
      location_id: group.location_id || '',
      schedule_day: group.schedule_day || '',
      start_time: group.start_time || '',
      end_time: group.end_time || '',
      modality: group.modality || 'in_person',
      status: group.status || 'active',
      total_sessions: group.total_sessions || null,
      current_session: group.current_session || 1,
      session_type: group.session_type || 'fixed',
      sessions_start_date: group.sessions_start_date || '',
      members: group.members || [],
      client_ids: client_ids,
      assign_facilitator_as_provider: group.assign_facilitator_as_provider || false
    });
    setShowAddGroup(true);
  };

  // Generate and save attendance history PDF when member is removed from group
  const generateAndSaveAttendancePdf = async (clientId, groupId, groupName, memberData) => {
    try {
      // Fetch attendance history for the group
      const attendanceResponse = await axios.get(`${API}/client-groups/${groupId}/attendance-history`);
      const attendanceData = attendanceResponse.data;
      
      // Filter sessions where this client has attendance data
      const clientSessions = attendanceData.sessions
        .filter(session => session.attendance && session.attendance[clientId])
        .map(session => ({
          date: new Date(session.date).toLocaleDateString(),
          session_number: session.session_number,
          status: session.attendance[clientId]
        }));
      
      // Calculate summary stats - match all possible attendance status values from backend
      const totalSessions = clientSessions.length;
      const presentSessions = clientSessions.filter(s => {
        const status = (s.status || '').toLowerCase();
        return status === 'present' || status === 'attended' || status === 'late' || status === 'present - late';
      }).length;
      const absentSessions = clientSessions.filter(s => {
        const status = (s.status || '').toLowerCase();
        return status === 'absent' || status === 'no_show' || status === 'no show' || status === 'noshow';
      }).length;
      const attendanceRate = totalSessions > 0 ? Math.round((presentSessions / totalSessions) * 100) : 0;
      
      // Get client name
      const client = allClientsForLookup.find(c => c.id === clientId);
      const clientName = client?.full_name || 'Unknown Client';
      
      // Generate PDF using jsPDF
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Group Attendance History', 105, 20, { align: 'center' });
      
      // Client and group info
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      let y = 35;
      doc.text(`Client: ${clientName}`, 20, y);
      y += 8;
      doc.text(`Group: ${groupName}`, 20, y);
      y += 8;
      doc.text(`Member From: ${memberData.start_date ? fmtLocalDate(memberData.start_date) : 'N/A'}`, 20, y);
      y += 8;
      doc.text(`Member Until: ${memberData.exit_date ? new Date(memberData.exit_date).toLocaleDateString() : new Date().toLocaleDateString()}`, 20, y);
      y += 8;
      doc.text(`Exit Reason: ${memberData.exit_reason?.replace(/_/g, ' ') || 'N/A'}`, 20, y);
      if (memberData.exit_notes) {
        y += 8;
        doc.text(`Exit Notes: ${memberData.exit_notes}`, 20, y);
      }
      
      // Summary stats
      y += 15;
      doc.setFont('helvetica', 'bold');
      doc.text('Attendance Summary', 20, y);
      doc.setFont('helvetica', 'normal');
      y += 8;
      doc.text(`Total Sessions: ${totalSessions}`, 20, y);
      y += 6;
      doc.text(`Present: ${presentSessions}`, 20, y);
      y += 6;
      doc.text(`Absent/No-Show: ${absentSessions}`, 20, y);
      y += 6;
      doc.text(`Attendance Rate: ${attendanceRate}%`, 20, y);
      
      // Session details table
      y += 15;
      doc.setFont('helvetica', 'bold');
      doc.text('Session Details', 20, y);
      y += 8;
      
      // Table header
      doc.setFillColor(240, 240, 240);
      doc.rect(20, y - 5, 170, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.text('Date', 25, y);
      doc.text('Session #', 80, y);
      doc.text('Status', 130, y);
      y += 8;
      
      // Table rows
      doc.setFont('helvetica', 'normal');
      clientSessions.forEach((session, index) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        // Alternate row colors
        if (index % 2 === 0) {
          doc.setFillColor(250, 250, 250);
          doc.rect(20, y - 5, 170, 7, 'F');
        }
        doc.text(session.date, 25, y);
        doc.text(String(session.session_number || '-'), 80, y);
        doc.text(session.status || '-', 130, y);
        y += 7;
      });
      
      // Footer
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 285, { align: 'center' });
      
      // Convert to blob
      const pdfBlob = doc.output('blob');
      
      // Create file name
      const sanitizedGroupName = groupName.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `Attendance_History_${sanitizedGroupName}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Upload to client's documents
      const formData = new FormData();
      formData.append('file', pdfBlob, fileName);
      formData.append('document_name', `Group Attendance History - ${groupName}`);
      formData.append('category', 'group_records');
      formData.append('auto_generated', 'true');
      
      await axios.post(`${API}/clients/${clientId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      void 0 && console.log('Attendance PDF saved to client documents:', fileName);
      return true;
    } catch (error) {
      console.error('Error generating attendance PDF:', error);
      throw error;
    }
  };

  const handleRemoveMemberFromGroup = (groupId, member) => {
    setMemberToRemove({ groupId, member });
    setRemovalReason('');
    setRemovalNotes('');
    setShowRemovalDialog(true);
  };

  const handleConfirmRemoval = async () => {
    if (!memberToRemove || !removalReason) {
      toast.error("Please select a removal reason");
      return;
    }

    try {
      const group = clientGroups.find(g => g.id === memberToRemove.groupId);
      if (!group) {
        toast.error('Group not found');
        return;
      }

      // Update the member's status to removed and add exit info
      const updatedMembers = group.members.map(m => {
        if (m.client_id === memberToRemove.member.client_id) {
          return {
            ...m,
            status: 'removed',
            exit_reason: removalReason,
            exit_notes: removalNotes || null,
            exit_date: removeFromFutureStartDate // Use the selected date for exit_date
          };
        }
        return m;
      });

      // Update client_ids to only include active members
      const activeClientIds = updatedMembers
        .filter(m => m.status === 'active')
        .map(m => m.client_id);

      const response = await axios.put(`${API}/client-groups/${memberToRemove.groupId}`, {
        ...group,
        members: updatedMembers,
        client_ids: activeClientIds
      });

      // Always remove from future appointments starting the day AFTER the last session date
      try {
        // Calculate the day after the last session date
        const lastSessionDate = new Date(removeFromFutureStartDate);
        lastSessionDate.setDate(lastSessionDate.getDate() + 1);
        const removeStartDate = lastSessionDate.toISOString().split('T')[0];
        
        const removeResponse = await axios.post(
          `${API}/client-groups/${memberToRemove.groupId}/members/${memberToRemove.member.client_id}/remove-from-future-appointments`,
          null,
          { params: { start_date: removeStartDate } }
        );
        if (removeResponse.data.appointments_updated > 0) {
          toast.success(`Removed from ${removeResponse.data.appointments_updated} scheduled session(s) after ${removeFromFutureStartDate}`);
        }
      } catch (removeError) {
        console.error("Error removing from future appointments:", removeError);
        toast.warning("Member removed from group, but failed to update scheduled sessions");
      }

      // Update the editingGroup state immediately with the updated members
      if (editingGroup && editingGroup.id === memberToRemove.groupId) {
        const updatedEditingGroup = {
          ...editingGroup,
          members: updatedMembers,
          client_ids: activeClientIds
        };
        setEditingGroup(updatedEditingGroup);
        
        // Also update the form data
        setGroupFormData(prev => ({
          ...prev,
          members: updatedMembers,
          client_ids: activeClientIds
        }));
      }
      
      // Generate and save attendance history PDF to client's documents
      try {
        await generateAndSaveAttendancePdf(
          memberToRemove.member.client_id,
          memberToRemove.groupId,
          group.name,
          memberToRemove.member
        );
        toast.success("Attendance history PDF saved to client's documents");
      } catch (pdfError) {
        console.error("Error generating attendance PDF:", pdfError);
        toast.warning("Member removed, but failed to generate attendance history PDF");
      }
      
      // Refresh clients to update group membership badges
      await refreshClients(); refreshStats();
      
      toast.success("Member removed from group");
      setShowRemovalDialog(false);
      setMemberToRemove(null);
      setRemovalReason('');
      setRemovalNotes('');
      setRemoveFromFutureStartDate(new Date().toISOString().split('T')[0]);
    } catch (error) {
      console.error("Error removing member from group:", error);
      toast.error(getApiErrorMessage(error, "Failed to remove member from group"));
    }
  };

  const handleGraduateMember = async (groupId, clientId) => {
    if (!confirm('Are you sure you want to graduate this member? They will be marked as completed.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/client-groups/${groupId}/members/${clientId}/graduate`);
      toast.success("Member graduated successfully");
      refreshClients(); refreshStats();
      
      // Update selectedGroup if it's open
      if (selectedGroup && selectedGroup.id === groupId) {
        const updated = clientGroups.find(g => g.id === groupId);
        if (updated) {
          setSelectedGroup(updated);
        }
      }
    } catch (error) {
      console.error("Error graduating member:", error);
      toast.error(error.response?.data?.detail || "Failed to graduate member");
    }
  };

  const handleUpdateMemberAttendance = async (groupId, clientId, sessionsAttended, totalSessions) => {
    try {
      await axios.patch(`${API}/client-groups/${groupId}/members/${clientId}`, {
        sessions_attended: sessionsAttended,
        total_sessions: totalSessions
      });
      toast.success("Attendance updated");
      // Attendance is group-specific, no global fetchData needed
    } catch (error) {
      console.error("Error updating attendance:", error);
      toast.error(error.response?.data?.detail || "Failed to update attendance");
    }
  };

  const handleAddClientToGroup = (clientId) => {
    if (!groupFormData.client_ids.includes(clientId)) {
      setGroupFormData({
        ...groupFormData,
        client_ids: [...groupFormData.client_ids, clientId]
      });
    }
  };

  const handleOpenAttendanceDialog = (appointment) => {
    setAttendanceAppointment(appointment);
    
    // Initialize attendance data from existing client_attendance field
    const initialAttendance = {};
    if (appointment.client_attendance) {
      Object.keys(appointment.client_attendance).forEach(clientId => {
        initialAttendance[clientId] = appointment.client_attendance[clientId];
      });
    } else {
      // Find the group and initialize all members as 'pending'
      const group = clientGroups.find(g => g.id === appointment.selected_group_id);
      if (group) {
        group.client_ids.forEach(clientId => {
          initialAttendance[clientId] = 'pending';
        });
      }
    }
    
    setAttendanceData(initialAttendance);
    setShowAttendanceDialog(true);
  };
  
  const handleSaveAttendance = async () => {
    // Handle direct attendance mode (no appointment)
    if (directAttendanceGroup && !attendanceAppointment) {
      try {
        const group = directAttendanceGroup;
        
        // Update sessions_attended for each member based on attendance
        const updatedMembers = group.members.map(member => {
          if (member.status !== 'active') return member;
          
          // Increment total_sessions for all active members
          const newTotalSessions = (member.total_sessions || 0) + 1;
          
          // If member attended this session, also increment sessions_attended
          if (attendanceData[member.client_id] === 'attended') {
            return {
              ...member,
              sessions_attended: (member.sessions_attended || 0) + 1,
              total_sessions: newTotalSessions
            };
          }
          
          return {
            ...member,
            total_sessions: newTotalSessions
          };
        });
        
        // Update the group with new session counts
        await axios.put(`${API}/client-groups/${group.id}`, {
          ...group,
          members: updatedMembers
        });
        
        toast.success(`Attendance recorded for ${directAttendanceDate}`);
        setShowAttendanceDialog(false);
        setDirectAttendanceGroup(null);
        setAttendanceData({});
        refreshStats();
      } catch (error) {
        console.error('Error saving direct attendance:', error);
        toast.error(error.response?.data?.detail || "Failed to save attendance");
      }
      return;
    }
    
    // Standard appointment-based attendance
    if (!attendanceAppointment) return;
    
    try {
      // Use the dedicated attendance endpoint
      await axios.put(`${API}/appointments/${attendanceAppointment.id}/attendance`, {
        client_attendance: attendanceData
      });
      
      // Also update the appointment status to completed
      // IMPORTANT: Include the new attendance data to avoid overwriting what we just saved
      await axios.put(`${API}/appointments/${attendanceAppointment.id}`, {
        ...attendanceAppointment,
        client_attendance: attendanceData,  // Use the NEW attendance data, not the stale one
        status: 'completed'
      });
      
      // Update sessions_attended for each member who attended
      const group = clientGroups.find(g => g.id === attendanceAppointment.selected_group_id);
      if (group) {
        const updatedMembers = group.members.map(member => {
          // If member attended this session, increment their sessions_attended
          if (attendanceData[member.client_id] === 'attended') {
            return {
              ...member,
              sessions_attended: (member.sessions_attended || 0) + 1
            };
          }
          return member;
        });
        
        // Update the group with new sessions_attended counts
        await axios.put(`${API}/client-groups/${group.id}`, {
          ...group,
          members: updatedMembers
        });
      }
      
      // Update the editing appointment state so attendance persists if user reopens
      if (editingAppointment && editingAppointment.id === attendanceAppointment.id) {
        setEditingAppointment(prev => ({
          ...prev,
          client_attendance: attendanceData,
          status: 'completed'
        }));
      }
      
      // Also update the appointment form data
      setAppointmentFormData(prev => ({
        ...prev,
        client_attendance: attendanceData,
        status: 'completed'
      }));
      
      // Update the appointments list optimistically
      setAppointments(prev => prev.map(apt => 
        apt.id === attendanceAppointment.id 
          ? { ...apt, client_attendance: attendanceData, status: 'completed' }
          : apt
      ));
      
      toast.success("Attendance saved and sessions updated successfully");
      setShowAttendanceDialog(false);
      // Don't call fetchData() - local state already updated, API already saved
      // Calling fetchData() can cause race condition where stale data overwrites the update
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error(error.response?.data?.detail || "Failed to save attendance");
    }
  };

  // Handler to open group history/attendance snapshot
  const handleGroupHistory = (group) => {
    setAttendanceSnapshotGroup(group);
    setShowAttendanceSnapshot(true);
    // Update URL with history mode
    navigate(`/dashboard?view=groups&groupId=${group.id}&mode=history`, { replace: true });
  };

  // Handler to open group members dialog
  const handleGroupMembers = (group) => {
    setSelectedGroup(group);
    setShowGroupMembers(true);
    // Update URL with members mode
    navigate(`/dashboard?view=groups&groupId=${group.id}&mode=members`, { replace: true });
  };

  // Handler to close group members dialog and clear URL
  const handleCloseGroupMembers = (open) => {
    setShowGroupMembers(open);
    if (!open) {
      // Clear the URL when closing the dialog
      navigate(`/dashboard?view=groups`, { replace: true });
    }
  };

  // Handler to open waitlist client preview with URL
  const handleWaitlistPreview = async (client) => {
    // Show loading state immediately
    setPreviewClientLoading(true);
    setShowClientPreview(true);
    // Update URL with preview mode
    navigate(`/dashboard?view=waitlist&waitlistId=${client.id}&mode=preview`, { replace: true });
    
    try {
      // Fetch fresh client data
      const response = await axios.get(`${API}/clients/${client.id}`);
      setPreviewClient(response.data);
    } catch (error) {
      console.error('Failed to load client preview:', error);
      // Fall back to passed client data
      setPreviewClient(client);
    } finally {
      setPreviewClientLoading(false);
    }
  };

  // Handler to close waitlist preview and clear URL
  const handleCloseWaitlistPreview = (open) => {
    setShowClientPreview(open);
    if (!open) {
      // Clear the URL when closing the preview
      navigate(`/dashboard?view=waitlist`, { replace: true });
    }
  };

  // Handler to open waitlist client edit (intake wizard) with URL
  const handleWaitlistEdit = async (clientId) => {
    // Update URL to show edit mode (but stay on waitlist view, not client dashboard)
    navigate(`/dashboard?view=waitlist&waitlistId=${clientId}&mode=edit`, { replace: true });
    // Close preview first
    setShowClientPreview(false);
    // Phase 3e.2 — delegate to V2 provider (its open() doesn't navigate).
    // Legacy refreshers fire via the `v2:intake-saved` listener added above.
    await intakeWizard.open(clientId);
  };

  // Handler to close intake wizard and clear waitlist URL if applicable
  const handleCloseIntakeWizardWithUrl = (open) => {
    setShowIntakeWizard(open);
    if (!open) {
      // Check if we came from waitlist view
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('view') === 'waitlist') {
        navigate(`/dashboard?view=waitlist`, { replace: true });
      }
    }
  };

  // Handler to open direct attendance dialog (no appointment)
  const handleDirectAttendance = (group) => {
    setDirectAttendanceGroup(group);
    setDirectAttendanceDate(new Date().toISOString().split('T')[0]);
    // Initialize attendance data with all active members as 'pending'
    const initialAttendance = {};
    (group.members || []).forEach(m => {
      if (m.status === 'active' || !m.status) {
        initialAttendance[m.client_id] = 'pending';
      }
    });
    setAttendanceData(initialAttendance);
    setAttendanceAppointment(null); // Clear any appointment
    setShowAttendanceDialog(true);
    // Update URL with attendance mode
    navigate(`/dashboard?view=groups&groupId=${group.id}&mode=attendance`, { replace: true });
  };

  // Handler to open group note dialog (no appointment)
  const handleDirectGroupNote = async (group) => {
    // Get active members from the group
    const activeMembers = (group.members || [])
      .filter(m => m.status === 'active' || !m.status)
      .map(m => m.client_id);
    
    if (activeMembers.length === 0) {
      toast.error("No active members in this group");
      return;
    }
    
    // Fetch curriculum session data if group has a curriculum
    let curriculumData = null;
    if (group.curriculum_id) {
      try {
        const response = await axios.get(`${API}/client-groups/${group.id}/current-session`);
        curriculumData = response.data;
      } catch (error) {
        void 0 && console.log('No curriculum session data available');
      }
    }
    
    // Set up for GroupNoteCreationDialog (same as calendar flow)
    setGroupNoteAppointment(null); // No appointment yet - user will select or create
    setGroupNoteGroup(group);
    setGroupNoteCurriculumSession(curriculumData);
    setGroupNoteExistingNotes([]);
    setGroupNoteMode('create');
    setShowGroupNoteDialog(true);
    
    toast.info(`Creating group note for ${group.name}`);
  };

  const handleScheduleGroupSessions = async (groupId) => {
    try {
      const group = clientGroups.find(g => g.id === groupId);
      if (!group) {
        toast.error("Group not found");
        return;
      }
      
      // Validate required fields
      if (!group.schedule_day || !group.start_time || !group.end_time || !group.total_sessions) {
        toast.error("Group must have schedule day, start time, end time, and total sessions to schedule appointments");
        return;
      }
      
      if (!group.facilitator_id) {
        toast.error("Group must have a facilitator assigned to schedule appointments");
        return;
      }
      
      if (!group.cpt_code_id) {
        toast.error("Group must have a CPT code assigned to schedule appointments");
        return;
      }
      
      // Get the CPT code details
      const cptCode = cptCodes.find(c => c.id === group.cpt_code_id);
      if (!cptCode) {
        toast.error("Selected CPT code not found");
        return;
      }
      
      // Get current appointments for conflict checking
      const currentAppointments = appointments || [];
      
      // Map day names to numbers (0 = Sunday, 1 = Monday, etc.)
      const dayMap = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };
      const targetDay = dayMap[group.schedule_day.toLowerCase()];
      
      if (targetDay === undefined) {
        toast.error("Invalid schedule day");
        return;
      }
      
      // Use group's start_date if set, otherwise find next occurrence of target day
      let startDate;
      if (group.sessions_start_date) {
        startDate = new Date(group.sessions_start_date);
      } else {
        startDate = new Date();
        while (startDate.getDay() !== targetDay) {
          startDate.setDate(startDate.getDate() + 1);
        }
      }
      
      // Determine POS code based on group modality
      const posCode = group.modality === 'telehealth' ? '02' : '11';
      
      // Generate proposed session dates
      const proposedSessions = [];
      for (let i = 0; i < group.total_sessions; i++) {
        const sessionDate = new Date(startDate);
        sessionDate.setDate(startDate.getDate() + (i * 7)); // Weekly sessions
        
        const [hours, minutes] = group.start_time.split(':');
        sessionDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        const [endHours, endMinutes] = group.end_time.split(':');
        const endDate = new Date(sessionDate);
        endDate.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
        
        proposedSessions.push({
          sessionNumber: i + 1,
          startTime: sessionDate,
          endTime: endDate
        });
      }
      
      // Check for conflicts with facilitator's existing appointments
      const facilitatorAppointments = currentAppointments.filter(
        apt => apt.provider_id === group.facilitator_id && apt.status !== 'cancelled'
      );
      
      const conflicts = [];
      for (const session of proposedSessions) {
        const sessionStart = session.startTime.getTime();
        const sessionEnd = session.endTime.getTime();
        
        for (const existing of facilitatorAppointments) {
          const existingStart = new Date(existing.start_time).getTime();
          const existingEnd = new Date(existing.end_time).getTime();
          
          // Check for overlap
          if (sessionStart < existingEnd && sessionEnd > existingStart) {
            const conflictingGroup = clientGroups.find(g => g.id === existing.selected_group_id);
            conflicts.push({
              date: session.startTime.toLocaleDateString(),
              time: session.startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              conflictWith: conflictingGroup?.name || existing.service_type || 'Another appointment'
            });
            break;
          }
        }
      }
      
      // If conflicts exist, show them and stop
      if (conflicts.length > 0) {
        const conflictList = conflicts.map(c => `• ${c.date} at ${c.time} (${c.conflictWith})`).join('\n');
        toast.error(
          `Cannot schedule: ${conflicts.length} conflict${conflicts.length > 1 ? 's' : ''} found.\n\n${conflictList}\n\nPlease reschedule or cancel the conflicting appointments first.`,
          { duration: 10000 }
        );
        return;
      }
      
      // No conflicts - create all appointments
      const appointmentsToCreate = proposedSessions.map(session => ({
        title: `${group.name} - Group Session`,
        provider_id: group.facilitator_id,
        start_time: session.startTime.toISOString(),
        end_time: session.endTime.toISOString(),
        appointment_type: 'group',
        selected_group_id: group.id,
        session_number: session.sessionNumber,
        service_type: cptCode.code,
        cpt_code_id: group.cpt_code_id,
        notes: `${group.name} - Session ${session.sessionNumber} of ${group.total_sessions}`,
        status: 'scheduled',
        client_ids: group.client_ids || [],
        pos_code: posCode,
        location_id: group.location_id,
        google_meet_link: group.google_meet_link || null
      }));
      
      // If telehealth group doesn't have a Meet link yet, generate one first
      if (posCode === '02' && !group.google_meet_link) {
        try {
          const meetResponse = await axios.post(`${API}/client-groups/${group.id}/generate-meet-link`);
          const groupMeetLink = meetResponse.data.meet_link;
          appointmentsToCreate.forEach(apt => apt.google_meet_link = groupMeetLink);
          toast.success('Generated group Meet link');
        } catch (error) {
          console.error('Error generating group Meet link:', error);
        }
      }
      
      // Create all appointments
      let successCount = 0;
      for (const apt of appointmentsToCreate) {
        try {
          await axios.post(`${API}/appointments`, apt);
          successCount++;
        } catch (error) {
          console.error('Error creating appointment:', error);
          toast.error(`Failed to create session ${apt.session_number}: ${error.response?.data?.detail || error.message}`);
        }
      }
      
      if (successCount === appointmentsToCreate.length) {
        toast.success(`Successfully scheduled all ${successCount} group sessions starting ${startDate.toLocaleDateString()}`);
      } else if (successCount > 0) {
        toast.warning(`Scheduled ${successCount} of ${appointmentsToCreate.length} sessions`);
      }
      
      refreshAppointments(true);
    } catch (error) {
      console.error('Error scheduling group sessions:', error);
      toast.error(error.response?.data?.detail || "Failed to schedule group sessions");
    }
  };

  const handleReactivateMember = async (clientId) => {
    if (!editingGroup) return;
    
    try {
      const group = clientGroups.find(g => g.id === editingGroup.id);
      if (!group) return;
      
      // Find the removed member and reactivate them
      const updatedMembers = group.members.map(m => {
        if (m.client_id === clientId && m.status !== 'active') {
          return {
            ...m,
            status: 'active',
            exit_date: null,
            exit_reason: null,
            exit_notes: null
          };
        }
        return m;
      });
      
      // Add back to client_ids if not already there
      const activeClientIds = [...new Set([
        ...group.client_ids,
        clientId
      ])];
      
      await axios.put(`${API}/client-groups/${editingGroup.id}`, {
        ...group,
        members: updatedMembers,
        client_ids: activeClientIds
      });
      
      // Update local state
      setEditingGroup({
        ...group,
        members: updatedMembers,
        client_ids: activeClientIds
      });
      
      setGroupFormData(prev => ({
        ...prev,
        members: updatedMembers,
        client_ids: activeClientIds
      }));
      
      await refreshClients();
      
      toast.success("Member reactivated in group");
    } catch (error) {
      console.error('Error reactivating member:', error);
      toast.error(error.response?.data?.detail || "Failed to reactivate member");
    }
  };

  const handleRemoveClientFromGroup = (clientId) => {
    // If we're editing a group, use the removal dialog
    if (editingGroup) {
      const member = editingGroup.members?.find(m => m.client_id === clientId);
      // Use allClientsForLookup to avoid filter bleeding from Client Management
      const client = allClientsForLookup?.find(c => c.id === clientId) || clients?.find(c => c.id === clientId);
      
      if (member && client) {
        setMemberToRemove({ 
          groupId: editingGroup.id, 
          member: { ...member, full_name: client.full_name }
        });
        setRemovalReason('');
        setRemovalNotes('');
        setShowRemovalDialog(true);
      } else {
        toast.error('Unable to find member information');
      }
    } else {
      // For new groups, just remove from the list
      setGroupFormData({
        ...groupFormData,
        client_ids: groupFormData.client_ids.filter(id => id !== clientId)
      });
    }
  };

  // Location Management Handlers
  const handleCreateLocation = async (e) => {
    e.preventDefault();
    
    try {
      // Use the already-fetched first organization
      const orgId = organizations?.[0]?.id;
      
      if (!orgId) {
        toast.error("No organization found. Please create an organization first in Admin → Configure.");
        return;
      }
      
      const response = await axios.post(`${API}/locations`, {
        ...locationFormData,
        organization_id: orgId
      });
      
      toast.success("Location created successfully");
      setShowAddLocation(false);
      
      // Reset form
      setLocationFormData({
        name: '',
        type: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        fax: '',
        license: '',
        license_expiry: ''
      });
      setLocationProviders([]); // Clear read-only providers list
      
      // Locations are static data - no global fetchData needed
    } catch (error) {
      console.error("Error creating location:", error);
      toast.error(error.response?.data?.detail || "Failed to create location");
    }
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    
    if (!editingLocation) {
      toast.error("No location selected for editing");
      return;
    }
    
    try {
      // PUT returns the updated Location doc — use it to refresh the
      // local list so reopening the Edit dialog shows the new value
      // immediately.  Without this, the list stays stale and looks
      // like the save didn't take (user-reported, Feb 2026).
      const resp = await axios.put(`${API}/locations/${editingLocation.id}`, locationFormData);
      const updated = resp.data;

      setLocations((prev) =>
        Array.isArray(prev)
          ? prev.map((l) => (l.id === editingLocation.id ? { ...l, ...updated } : l))
          : prev
      );

      toast.success("Location updated successfully");
      setShowEditLocation(false);
      setEditingLocation(null);
      
      // Reset form
      setLocationFormData({
        name: '',
        location_type: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        fax: '',
      });
      setLocationProviders([]); // Clear read-only providers list

      // Belt-and-suspenders re-fetch so any field the PUT response
      // didn't echo back (e.g. derived flags) lands too.
      try {
        const fresh = await axios.get(`${API}/locations`);
        if (Array.isArray(fresh.data)) setLocations(fresh.data);
      } catch (_) { /* non-fatal — local state is already updated */ }
    } catch (error) {
      console.error("Error updating location:", error);
      toast.error(error.response?.data?.detail || "Failed to update location");
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!confirm('Are you sure you want to delete this location?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/locations/${locationId}`);
      // Drop the deleted row from local state so the list updates
      // without a full refetch (Feb 2026 — was leaving stale rows).
      setLocations((prev) => Array.isArray(prev) ? prev.filter((l) => l.id !== locationId) : prev);
      toast.success("Location deleted successfully");
    } catch (error) {
      console.error("Error deleting location:", error);
      toast.error(error.response?.data?.detail || "Failed to delete location");
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'super admin':
      case 'admin': return 'bg-red-100 text-red-800';
      case 'provider': return 'bg-blue-100 text-blue-800';
      case 'supervisor': return 'bg-purple-100 text-purple-800';
      case 'office': return 'bg-yellow-100 text-yellow-800';
      case 'reception': return 'bg-orange-100 text-orange-800';
      case 'intern': return 'bg-teal-100 text-teal-800';
      case 'client': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // getStatusBadgeColor, getBillingStatusBorderColor,
  // getServiceTypeCategoryColor, isTelehealthAppointment moved to
  // utils/calendar.js (Stage 5).

  // Treatment plan status display helper
  const getTreatmentPlanStatusDisplay = (status) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'active': return 'Active';
      case 'pending_signatures': return 'Pending Signatures';
      case 'sent_to_client_for_signature': return 'Sent to Client';
      case 'fully_signed': return 'Fully Signed';
      case 'needs_corrections': return 'Needs Corrections';
      case 'archived': return 'Archived';
      case 'inactive': return 'Inactive';
      default: return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  // Helper function to format status labels dynamically
  const formatStatusLabel = (note) => {
    if (!note.status) return 'DRAFT';
    if (note.status === 'client_declined') return 'CLIENT DECLINED';

    // Mar 2026 — when a note is marked non-billable as a DUPLICATE,
    // surface that distinction in the badge so users don't have to
    // open the note to know why it's out of the billing queue.
    if (note.status === 'not_billable' && note.not_billable_reason === 'duplicate_note') {
      return 'DUPLICATE';
    }
    if (note.status === 'not_billable' && note.not_billable_reason === 'cpt_restricted_for_insurance') {
      return 'NOT BILLABLE (CPT)';
    }

    // When the note's status is a "container" status that hides its actual
    // pipeline state (locked / completed), prefer the linked claim's status
    // if we have one. This is what billers want to see — "PAID", "DENIED",
    // "BILLED (LEGACY)" — not just "LOCKED". Lock state is shown separately
    // via the small lock chip next to the badge.
    const ambiguousStatuses = new Set(['locked', 'completed']);
    if (ambiguousStatuses.has(note.status) && note._claim_status) {
      const cs = String(note._claim_status).toLowerCase();
      if (cs === 'billed_legacy') return 'BILLED (LEGACY)';
      if (cs === 'ready_for_secondary') return 'PARTIAL — AWAITING 2°';
      if (cs === 'crossed_to_secondary') return 'CROSSED TO 2° — NO REBILL';
      if (cs === 'takeback_received') return 'TAKE-BACK';
      if (cs === 'takeback_unresolved') return 'TAKE-BACK UNRESOLVED';
      if (cs === 'partially_paid') return 'PARTIALLY PAID';
      return cs.replace(/_/g, ' ').toUpperCase();
    }

    // For locked notes with no linked claim, fall back to a meaningful label
    // based on whether the note was ever supposed to be billed:
    //   • Not billable → "COMPLETED" (the encounter is closed; no claim coming)
    //   • Billable     → "SIGNED READY FOR BILLING" (mirrors the canonical
    //                     pipeline status that precedes claim creation)
    // Lock state itself is conveyed by the separate red lock chip.
    if (note.status === 'locked' && !note._claim_status) {
      if (note.billable === false) return 'COMPLETED';
      return 'SIGNED READY FOR BILLING';
    }

    // Special handling for sent_to_supervisor status
    if (note.status === 'sent_to_supervisor') {
      const allSignaturesComplete = note.signatures_required?.length > 0 &&
        note.signatures_required?.length === note.signatures_completed?.length;
      
      if (allSignaturesComplete) {
        return 'SIGNED';
      }
      return 'SENT TO SUPERVISOR';
    }
    
    // Special handling for client signature statuses
    if (note.status === 'sent_to_client_for_signature') {
      return 'SENT TO CLIENT FOR SIGNATURE';
    }
    
    if (note.status === 'client_completed') {
      return 'CLIENT SIGNED';
    }
    
    // All other statuses - just format normally
    return note.status.replace(/_/g, ' ').toUpperCase();
  };

  // ============================================
  // APPOINTMENT VISUAL STYLING HELPERS — moved to utils/calendar.js
  // ============================================
  //
  // `getBillingStatusBorderColor`, `getServiceTypeCategoryColor`,
  // and `isTelehealthAppointment` are now pure functions in
  // utils/calendar.js (Stage 5 of the Calendar refactor). They are
  // imported directly by App.js and AppointmentsTab.jsx — the bridge
  // no longer needs to ferry them around.

  // Handle note deletion with undo option (30 seconds)
  const handleDeleteNoteWithUndo = (noteId, noteData = null) => {
    // If already pending delete, just confirm it immediately
    if (pendingDeletes[noteId]) {
      clearTimeout(pendingDeletes[noteId].timeoutId);
      const newPending = { ...pendingDeletes };
      delete newPending[noteId];
      setPendingDeletes(newPending);
      
      // Actually delete from backend
      axios.delete(`${API}/notes/${noteId}`)
        .then(() => {
          toast.success('Note deleted successfully');
          refreshNotes(); refreshTodos();
        })
        .catch((error) => {
          const errorMsg = error.response?.data?.detail || 'Failed to delete note';
          toast.error(errorMsg);
        });
      return;
    }
    
    // Remove from UI immediately for better UX (optimistic update)
    setNotes(prevNotes => prevNotes.filter(n => n.id !== noteId));
    // Also remove from clientNotes for client chart view
    setClientNotes(prevNotes => prevNotes.filter(n => n.id !== noteId));
    
    // Set up delayed actual delete (30 seconds)
    const timeoutId = setTimeout(() => {
      // Actually delete from backend after 30 seconds
      axios.delete(`${API}/notes/${noteId}`)
        .then(() => {
          // Remove from pending deletes
          setPendingDeletes(prev => {
            const newPending = { ...prev };
            delete newPending[noteId];
            return newPending;
          });
        })
        .catch((error) => {
          const errorMsg = error.response?.data?.detail || 'Failed to delete note';
          toast.error(errorMsg);
          // Restore note on error
          refreshNotes(); refreshTodos();
        });
    }, 30000); // 30 seconds
    
    // Store in pending deletes
    setPendingDeletes(prev => ({
      ...prev,
      [noteId]: { timeoutId, noteData }
    }));
    
    // Show toast with undo button using action property
    toast.success('Note deleted', {
      duration: 30000,
      action: {
        label: 'UNDO',
        onClick: () => {
          void 0 && console.log('UNDO clicked for note:', noteId); // Debug log
          // Cancel the deletion
          clearTimeout(timeoutId);
          setPendingDeletes(prev => {
            const newPending = { ...prev };
            delete newPending[noteId];
            return newPending;
          });
          // Restore the note in all views
          refreshNotes(); refreshTodos();
          refreshClientNotes(); // Also refresh client chart notes
          toast.success('Note restored');
        }
      }
    });
  };

  // Optimistically update note status in all relevant state arrays
  const updateNoteStatusOptimistically = (noteId, newStatus, additionalFields = {}) => {
    const updateNote = (note) => {
      if (note.id === noteId) {
        return { ...note, status: newStatus, ...additionalFields };
      }
      return note;
    };

    // Update in main notes array
    setNotes(prevNotes => prevNotes.map(updateNote));
    
    // Update in clientNotes if present
    setClientNotes(prevNotes => prevNotes.map(updateNote));
    
    // Update selectedNote if it's the one being changed
    if (selectedNote?.id === noteId) {
      setSelectedNote(prev => prev ? { ...prev, status: newStatus, ...additionalFields } : prev);
    }
    
    // OPTIMISTIC TASK UPDATE: Update status in myTodoNotes or remove if no longer tracked
    const todoStatuses = ['draft', 'needs_corrections', 'sent_to_supervisor'];
    if (!todoStatuses.includes(newStatus)) {
      setMyTodoNotes(prev => {
        if (!prev) return prev;
        return prev.filter(n => n.id !== noteId);
      });
    } else {
      setMyTodoNotes(prev => {
        if (!prev) return prev;
        return prev.map(n => n.id === noteId ? { ...n, status: newStatus, ...additionalFields } : n);
      });
    }
    
    // Handle billing-specific arrays (NotesContext) based on status change
    if (newStatus === 'signed_ready_for_billing') {
      setNotesReadyForBilling(prev => {
        const exists = prev.some(n => n.id === noteId);
        if (!exists) {
          const note = notes.find(n => n.id === noteId) || clientNotes.find(n => n.id === noteId);
          if (note) return [...prev, { ...note, status: newStatus, ...additionalFields }];
        }
        return prev.map(updateNote);
      });
      setNotesSentForCorrection(prev => prev.filter(n => n.id !== noteId));
    } else if (newStatus === 'needs_corrections') {
      setNotesSentForCorrection(prev => {
        const exists = prev.some(n => n.id === noteId);
        if (!exists) {
          const note = notes.find(n => n.id === noteId) || clientNotes.find(n => n.id === noteId);
          if (note) return [...prev, { ...note, status: newStatus, ...additionalFields }];
        }
        return prev.map(updateNote);
      });
      setNotesReadyForBilling(prev => prev.filter(n => n.id !== noteId));
    } else {
      setNotesReadyForBilling(prev => prev.filter(n => n.id !== noteId));
      setNotesSentForCorrection(prev => prev.filter(n => n.id !== noteId));
    }
    
    // Dispatch event for BillingContext (separate context, separate state)
    const noteData = notes.find(n => n.id === noteId) || clientNotes.find(n => n.id === noteId);
    window.dispatchEvent(new CustomEvent('noteStatusChanged', { 
      detail: { noteId, newStatus, noteData: noteData ? { ...noteData, status: newStatus, ...additionalFields } : null } 
    }));
  };

  // Handle document deletion with undo option (30 seconds)
  const handleDeleteDocumentWithUndo = (docId, docData = null) => {
    // If already pending delete, just confirm it immediately
    if (pendingDeletes[`doc-${docId}`]) {
      clearTimeout(pendingDeletes[`doc-${docId}`].timeoutId);
      const newPending = { ...pendingDeletes };
      delete newPending[`doc-${docId}`];
      setPendingDeletes(newPending);
      
      // Actually delete from backend
      axios.delete(`${API}/documents/${docId}`)
        .then(() => {
          toast.success('Document deleted successfully');
          // Documents are client-specific, no global fetchData needed
        })
        .catch((error) => {
          const errorMsg = error.response?.data?.detail || 'Failed to delete document';
          toast.error(errorMsg);
        });
      return;
    }
    
    // Remove from UI immediately for better UX
    setClientDocuments(prevDocs => prevDocs.filter(d => d.id !== docId));
    
    // Set up delayed actual delete (30 seconds)
    const timeoutId = setTimeout(() => {
      // Actually delete from backend after 30 seconds
      axios.delete(`${API}/documents/${docId}`)
        .then(() => {
          // Remove from pending deletes
          setPendingDeletes(prev => {
            const newPending = { ...prev };
            delete newPending[`doc-${docId}`];
            return newPending;
          });
        })
        .catch((error) => {
          const errorMsg = error.response?.data?.detail || 'Failed to delete document';
          toast.error(errorMsg);
          // Restore document on error - re-fetch client documents
          if (selectedClient?.id) {
            axios.get(`${API}/clients/${selectedClient.id}/documents`).then(r => setClientDocuments(r.data || []));
          }
        });
    }, 30000); // 30 seconds
    
    // Store in pending deletes
    setPendingDeletes(prev => ({
      ...prev,
      [`doc-${docId}`]: { timeoutId, docData }
    }));
    
    // Show toast with undo button using action property
    toast.success('Document deleted', {
      duration: 30000,
      action: {
        label: 'UNDO',
        onClick: () => {
          void 0 && console.log('UNDO clicked for document:', docId); // Debug log
          // Cancel the deletion
          clearTimeout(timeoutId);
          setPendingDeletes(prev => {
            const newPending = { ...prev };
            delete newPending[`doc-${docId}`];
            return newPending;
          });
          // Restore the document - re-fetch client documents
          if (selectedClient?.id) {
            axios.get(`${API}/clients/${selectedClient.id}/documents`).then(r => setClientDocuments(r.data || []));
          }
          toast.success('Document restored');
        }
      }
    });
  };


  // Only show full-screen loader on the very first app load
  // After that, data refreshes happen silently in the background
  if (loading && !dashboardHasLoadedOnce) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
        <p className="text-gray-600">Loading your session...</p>
      </div>
    );
  }

  // Admin Jump To Dropdown Component
  const AdminJumpToDropdown = ({ currentDialogId, isDirty = false }) => {
    const otherDialogs = adminDialogConfig.filter(d => d.id !== currentDialogId);
    
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1">
            <ArrowLeftRight className="h-3 w-3" />
            Jump to
            <ChevronDown className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-1" align="end">
          <div className="text-xs font-medium text-gray-500 px-2 py-1.5">Navigate to:</div>
          <div className="max-h-80 overflow-y-auto">
            {otherDialogs.map((dialog) => (
              <button
                key={dialog.id}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-gray-100 text-left"
                onClick={() => handleAdminJumpTo(dialog.id, isDirty)}
              >
                <dialog.icon className="h-4 w-4 text-gray-500" />
                {dialog.label}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  // Document Print Header Component (only visible when printing)
  const DocumentPrintHeader = () => {
    const primaryLocation = locations?.[0] || {};
    
    return (
      <div className="print-document-header hidden print:block mb-6">
        <div className="flex items-start justify-between border-b-2 border-gray-800 pb-3">
          {/* Left side - Logo */}
          <div className="flex items-center gap-3">
            {brandingSettings?.company_logo_url && (
              <img 
                src={brandingSettings?.company_logo_url.startsWith('/api/') 
                  ? `${process.env.REACT_APP_BACKEND_URL}${brandingSettings?.company_logo_url}`
                  : brandingSettings?.company_logo_url
                } 
                alt={brandingSettings?.company_name || 'Company Logo'} 
                className="h-12 max-w-[100px] object-contain"
              />
            )}
          </div>
          
          {/* Right side - Company Info */}
          <div className="text-right text-sm">
            <div className="font-bold text-lg">
              {brandingSettings?.company_display_name || brandingSettings?.company_name || 'Healthcare Practice'}
            </div>
            {primaryLocation.address_line1 && (
              <div className="text-gray-700">
                {primaryLocation.address_line1}
                {primaryLocation.address_line2 && `, ${primaryLocation.address_line2}`}
              </div>
            )}
            {(primaryLocation.city || primaryLocation.state || primaryLocation.zip_code) && (
              <div className="text-gray-700">
                {[primaryLocation.city, primaryLocation.state, primaryLocation.zip_code].filter(Boolean).join(', ')}
              </div>
            )}
            {primaryLocation.phone && (
              <div className="text-gray-600">
                Phone: {primaryLocation.phone}
                {primaryLocation.fax && ` | Fax: ${primaryLocation.fax}`}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Document Print Footer Component (only visible when printing)
  const DocumentPrintFooter = () => {
    return (
      <div className="print-document-footer hidden print:block mt-8 pt-3 border-t border-gray-400 text-center text-xs text-gray-600">
        {brandingSettings?.company_website && (
          <div>{brandingSettings?.company_website}</div>
        )}
        {brandingSettings?.company_motto && (
          <div className="italic mt-1">{brandingSettings?.company_motto}</div>
        )}
      </div>
    );
  };

  return (
    <ChartActionsProvider
      value={{
        // Open the legacy Add/Edit Medication form for a given
        // client.  Sets the selected client first because the form
        // reads `selectedClient` from Dashboard scope to know who
        // to attach the new med to.
        openAddMedication: (c) => {
          if (c) setSelectedClient(c);
          setShowMedicationForm(true);
        },
        // Same shape for the Add Diagnosis form.
        openAddDiagnosis: (c) => {
          if (c) setSelectedClient(c);
          setShowDiagnosisForm(true);
        },
        // Optimistic UI sync — push a freshly-created medication
        // into the active meds list without waiting for a full
        // re-fetch.  Phase 2 (Med Referral Dialog extraction) will
        // call this from inside the dialog so the new entry shows
        // up immediately in the Meds tab table behind it.
        appendMedication: (med) => {
          if (!med) return;
          setClientMedications((prev) => Array.isArray(prev) ? [...prev, med] : [med]);
        },
      }}
    >
    <div className="min-h-screen bg-gray-50 dark:bg-[#1A1A1A]" onClick={() => {
      if (clientContextMenu.show) setClientContextMenu({ show: false, x: 0, y: 0, clientId: null });
      if (appointmentContextMenu.show) setAppointmentContextMenu({ show: false, x: 0, y: 0, appointment: null });
      if (claimContextMenu.show) setClaimContextMenu({ show: false, x: 0, y: 0, claim: null });
      if (noteContextMenu.show) setNoteContextMenu({ show: false, x: 0, y: 0, note: null });
    }}>
      {/* Feb 27, 2026 — External Reviewer read-only banner.
          Persistent across every screen so the auditor knows the UI
          intentionally has fewer write affordances, and the org has
          a constant visual reminder of what the session is for. */}
      {user?.is_external_reviewer && (
        <div
          className="bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2 text-sm flex items-center justify-center gap-2"
          data-testid="external-reviewer-banner"
        >
          <Eye className="h-4 w-4" />
          <span>
            <strong>Read-only review session.</strong> You have view-only access to the clients explicitly granted to your account.
            Contact your administrator for any changes.
          </span>
        </div>
      )}
      {/* Acceptable Use Policy Modal - Must accept on first login */}
      <AUPModal
        isOpen={showAUPModal}
        onAccept={handleAcceptAUP}
        isLoading={aupLoading}
      />
      
      {/* Inactivity Warning Modal */}
      <InactivityWarningModal
        open={showInactivityWarning}
        countdown={inactivityCountdown}
        onContinue={handleContinueSession}
        onLogout={handleInactivityLogout}
      />
      
      {/* Client Context Menu (right-click on folder icon) */}
      {clientContextMenu.show && (
        <div 
          className="fixed bg-white rounded-lg shadow-lg border py-1 z-[100]"
          style={{ top: clientContextMenu.y, left: clientContextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
            onClick={() => {
              handleClientClick(clientContextMenu.clientId);
              setClientContextMenu({ show: false, x: 0, y: 0, clientId: null });
            }}
          >
            <Folder className="h-4 w-4" />
            Open Client Chart
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-teal-600"
            onClick={() => {
              sessionStorage.setItem('activeClientId', clientContextMenu.clientId);
              window.openAuthenticatedTab('/activeclient');
              setClientContextMenu({ show: false, x: 0, y: 0, clientId: null });
            }}
          >
            <ExternalLink className="h-4 w-4" />
            Open in New Window
          </button>
          <div className="border-t my-1"></div>
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-gray-600"
            onClick={() => {
              const client = allClientsForLookup.find(c => c.id === clientContextMenu.clientId);
              setChangeHistoryEntity({
                type: 'client',
                id: clientContextMenu.clientId,
                name: client?.full_name || 'Client'
              });
              setShowChangeHistory(true);
              setClientContextMenu({ show: false, x: 0, y: 0, clientId: null });
            }}
          >
            <History className="h-4 w-4" />
            View Change History
          </button>
        </div>
      )}
      
      {/* Appointment Context Menu (right-click) */}
      {appointmentContextMenu.show && (
        <div 
          className="fixed bg-white rounded-lg shadow-lg border py-1 z-[100]"
          style={{ top: appointmentContextMenu.y, left: appointmentContextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-gray-600"
            onClick={() => {
              const appt = appointmentContextMenu.appointment;
              const client = allClientsForLookup.find(c => c.id === appt?.client_id);
              setChangeHistoryEntity({
                type: 'appointment',
                id: appt?.id,
                name: client?.full_name || appt?.title || 'Appointment'
              });
              setShowChangeHistory(true);
              setAppointmentContextMenu({ show: false, x: 0, y: 0, appointment: null });
            }}
          >
            <History className="h-4 w-4" />
            View Change History
          </button>
        </div>
      )}
      
      {/* Claim Context Menu (right-click) */}
      {claimContextMenu.show && (
        <div 
          className="fixed bg-white rounded-lg shadow-lg border py-1 z-[100]"
          style={{ top: claimContextMenu.y, left: claimContextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-gray-600"
            onClick={() => {
              const claim = claimContextMenu.claim;
              setChangeHistoryEntity({
                type: 'claim',
                id: claim?.id,
                name: `Claim #${claim?.claim_number || claim?.id?.slice(0,8)}`
              });
              setShowChangeHistory(true);
              setClaimContextMenu({ show: false, x: 0, y: 0, claim: null });
            }}
          >
            <History className="h-4 w-4" />
            View Change History
          </button>
        </div>
      )}
      
      {/* Note Context Menu (right-click) */}
      {noteContextMenu.show && (
        <div 
          className="fixed bg-white rounded-lg shadow-lg border py-1 z-[100]"
          style={{ top: noteContextMenu.y, left: noteContextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-gray-600"
            onClick={() => {
              const note = noteContextMenu.note;
              setChangeHistoryEntity({
                type: 'note',
                id: note?.id,
                name: note?.title || note?.note_type || 'Note'
              });
              setShowChangeHistory(true);
              setNoteContextMenu({ show: false, x: 0, y: 0, note: null });
            }}
          >
            <History className="h-4 w-4" />
            View Change History
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-purple-600"
            onClick={async () => {
              const note = noteContextMenu.note;
              setNoteContextMenu({ show: false, x: 0, y: 0, note: null });
              
              if (!note?.id) {
                toast.error('No note selected');
                return;
              }
              
              try {
                // Fetch full note data
                const fullNoteResponse = await axios.get(`${API}/notes/${note.id}`, {
                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                });
                const sourceNote = fullNoteResponse.data;
                
                if (!sourceNote) {
                  toast.error('Could not fetch note data');
                  return;
                }
                
                // Create duplicate with ALL relevant fields from source note
                const duplicateData = {
                  client_id: sourceNote.client_id,
                  client_ids: sourceNote.client_ids || [],
                  is_group_note: sourceNote.is_group_note || false,
                  provider_id: sourceNote.provider_id || user?.id,
                  template_id: sourceNote.template_id,
                  content: sourceNote.content || '',
                  status: 'draft', // Always create as draft
                  note_type: sourceNote.note_type,
                  form_type_name: sourceNote.form_type_name || sourceNote.form_type,
                  title: sourceNote.title,
                  date_of_service: sourceNote.date_of_service,
                  start_time: sourceNote.start_time,
                  end_time: sourceNote.end_time,
                  units: sourceNote.units,
                  cpt_code: sourceNote.cpt_code,
                  billable: sourceNote.billable || false,
                  linked_goals: sourceNote.linked_goals || [],
                  telehealth_consent: sourceNote.telehealth_consent,
                  require_client_signature: sourceNote.require_client_signature || false,
                  allow_client_signature: sourceNote.allow_client_signature || false,
                  requires_supervisor_signature: sourceNote.requires_supervisor_signature || false,
                  assigned_supervisor: sourceNote.assigned_supervisor || null
                };
                
                const response = await axios.post(`${API}/notes`, duplicateData, {
                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                });
                toast.success('Note duplicated successfully');
                refreshNotes(); refreshTodos();
                // Open the new note properly via openNote() to initialize template fields
                await openNote(response.data);
              } catch (error) {
                console.error('Error duplicating note:', error);
                toast.error('Failed to duplicate note: ' + (error.response?.data?.detail || error.message));
              }
            }}
          >
            <Copy className="h-4 w-4" />
            Duplicate Note
          </button>
        </div>
      )}
      
      {/* Header - Sticky at top */}
      <header className="bg-white dark:bg-[#242424] shadow-sm border-b dark:border-[#404040] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              {/* CareVaultPro Logo - Clickable to go home */}
              <TooltipProvider delayDuration={100}>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <div 
                      className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        // Clear any open client/note views first
                        setSelectedClient(null);
                        setShowClientDetail(false);
                        setSelectedNote(null);
                        setShowNoteDetail(false);
                        
                        // Smart default tab based on role
                        let defaultTab;
                        if (isSuperAdmin(user) || userRoleName === 'office' || userRoleName === 'office staff') {
                          defaultTab = 'clients';
                        } else if (user?.is_biller || user?.is_billing_manager) {
                          defaultTab = 'billing';
                        } else {
                          // Providers, supervisors go to tasks
                          defaultTab = 'notes';
                        }
                        
                        setActiveTab(defaultTab);
                        // Reset URL to dashboard with default view
                        navigate(`/dashboard?view=${defaultTab}`, { replace: true });
                      }}
                    >
                      <Shield className="h-8 w-8 text-teal-600 mr-2" />
                      <span className="text-xl font-bold text-gray-900">CareVaultPro</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-teal-600 text-white font-medium">
                    <p>Take me Home</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
              
              {/* Company Branding - Show if configured */}
              {brandingSettings?.show_company_branding && (brandingSettings?.company_logo_url || brandingSettings?.company_name || brandingSettings?.company_display_name) && (
                <>
                  <div className="mx-4 h-8 w-px bg-gray-300" /> {/* Divider */}
                  <div className="flex items-center gap-3">
                    {brandingSettings?.company_logo_url && (
                      <img 
                        src={brandingSettings?.company_logo_url.startsWith('/api/') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${brandingSettings?.company_logo_url}`
                          : brandingSettings?.company_logo_url
                        } 
                        alt={brandingSettings?.company_name || 'Company Logo'} 
                        className="h-8 max-w-[120px] object-contain"
                      />
                    )}
                    {(brandingSettings?.company_display_name || brandingSettings?.company_name) && (
                      <span className="text-lg font-semibold text-gray-700">
                        {brandingSettings?.company_display_name || brandingSettings?.company_name}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDirectoryDialog(true)}
                data-testid="providers-header-button"
              >
                <Shield className="h-4 w-4 mr-2" />
                Directory
              </Button>
              
              {/* Impersonation UI (Admin Only) - Always show if actual user is admin and feature enabled */}
              {isActualAdmin && brandingSettings?.view_as_user_enabled !== false && (
                <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-300">
                  {impersonatedUser && (
                    <Badge className="bg-orange-500 text-white px-2 py-1 flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      Viewing as: {impersonatedUser.first_name} {impersonatedUser.last_name}
                    </Badge>
                  )}
                  <Select
                    value={impersonatedUserId || ''}
                    onValueChange={async (value) => {
                      if (value === 'stop') {
                        await stopImpersonation(refreshUser, fetchData);
                      } else {
                        await startImpersonation(value, refreshUser, fetchData);
                      }
                    }}
                    onOpenChange={(open) => {
                      // Fetch users on-demand when dropdown is opened
                      if (open) {
                        fetchUsersOnDemand();
                      }
                    }}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="View as user..." />
                    </SelectTrigger>
                    <SelectContent>
                      {impersonatedUserId && (
                        <SelectItem value="stop" className="font-semibold text-orange-600">
                          ✕ Stop Impersonating
                        </SelectItem>
                      )}
                      {loadingUsers ? (
                        <div className="p-2 text-center text-gray-500 text-sm">
                          Loading users...
                        </div>
                      ) : !Array.isArray(availableUsers) || availableUsers.length === 0 ? (
                        <div className="p-2 text-center text-gray-500 text-sm">
                          No users available
                        </div>
                      ) : (
                        (availableUsers || [])
                          .filter(u => u.id !== user?.id)
                          .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                          .map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.first_name} {u.last_name} ({formatRoleName(u.role)})
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* iPrescribe Quick Launch - Global Access for Prescribers */}
              {brandingSettings?.iprescribe_enabled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openIPrescribe}
                  disabled={!canAccessIPrescribe()}
                  className={`relative ${
                    canAccessIPrescribe()
                      ? 'text-purple-600 hover:text-purple-800 hover:bg-purple-50 border-purple-200'
                      : 'text-gray-300 cursor-not-allowed border-gray-200'
                  }`}
                  title={canAccessIPrescribe()
                    ? 'Launch iPrescribe (Dr. First)'
                    : 'Requires prescribing authority'}
                >
                  <Pill className="h-4 w-4" />
                </Button>
              )}
              
              {/* CareVault Atlas - Help Center */}
              <TooltipProvider delayDuration={100}>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('/atlas', '_blank')}
                      className="text-teal-600 hover:text-teal-800 hover:bg-teal-50 border-teal-200"
                      data-testid="atlas-button"
                    >
                      <BookOpen className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-teal-600 text-white font-medium">
                    <p>CareVault Atlas - Help Center</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>

              {/* V2 Dashboard preview — strangler-pattern shell that
                  lazy-loads each migrated tab as its own route. Opens
                  in the same tab so we don't lose React Router state.
                  Hidden behind a small "V2" pill instead of a full
                  button to keep the legacy nav from getting busy. */}
              <TooltipProvider delayDuration={100}>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/v2')}
                      className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 border-indigo-200 font-semibold text-xs px-2"
                      data-testid="v2-preview-button"
                    >
                      V2
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-indigo-600 text-white font-medium">
                    <p>Try the new V2 Dashboard (preview)</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>

              
              {/* Notifications Bell - extracted to NotificationBell.jsx (Feb 2026) */}
              <NotificationBell
                setShowNotificationManager={setShowNotificationManager}
                setEditingAppointment={setEditingAppointment}
                setShowAppointmentForm={setShowAppointmentForm}
                changeTab={changeTab}
                setActiveTab={setActiveTab}
                setSelectedChangeRequest={setSelectedChangeRequest}
                setShowChangeRequestDetail={setShowChangeRequestDetail}
                setSelectedTask={setSelectedTask}
                setShowTaskDetail={setShowTaskDetail}
                setSelectedClient={setSelectedClient}
                setShowClientDetail={setShowClientDetail}
                setClientDetailTab={setClientDetailTab}
                setPreviewClient={setPreviewClient}
                setShowClientPreview={setShowClientPreview}
                setSelectedNote={setSelectedNote}
                setShowNoteDetail={setShowNoteDetail}
                setSelectedReferralForDetails={setSelectedReferralForDetails}
              />
              
                            {/* Messages Button - Using MessagesDropdown component */}
              <MessagesDropdown
                user={user}
                users={users}
                showStaffMessages={showStaffMessages}
                setShowStaffMessages={setShowStaffMessages}
                staffMessageThreads={staffMessageThreads}
                selectedStaffThread={selectedStaffThread}
                staffThreadMessages={staffThreadMessages}
                staffReplyText={staffReplyText}
                setStaffReplyText={setStaffReplyText}
                sendingStaffReply={sendingStaffReply}
                loadingStaffMessages={loadingStaffMessages}
                unreadStaffMessagesCount={unreadStaffMessagesCount}
                openMessageMenu={openMessageMenu}
                setOpenMessageMenu={setOpenMessageMenu}
                staffAnnouncements={staffAnnouncements}
                unreadAnnouncementsCount={unreadAnnouncementsCount}
                onMarkAnnouncementRead={markAnnouncementRead}
                sendStaffReply={sendStaffReply}
                fetchStaffThreadMessages={fetchStaffThreadMessages}
                openThread={(thread) => {
                  setSelectedStaffThread(thread);
                  fetchStaffThreadMessages(thread.id);
                }}
                closeThread={() => {
                  setSelectedStaffThread(null);
                  setStaffThreadMessages([]);
                  setStaffReplyText('');
                }}
                onNewMessage={() => setShowStaffNewMessageDialog(true)}
                setThreadToForward={setThreadToForward}
                setShowForwardDialog={setShowForwardDialog}
                setRecallThreadId={setRecallThreadId}
                setShowRecallDialog={setShowRecallDialog}
              />
              
              <div className="flex gap-2 items-center">
                {(isSuperAdmin(user)) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => changeTab('admin')}
                    data-testid="admin-button"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Admin
                  </Button>
                )}
                
                {/* Profile Dropdown - replaces simple logout button */}
                <ProfileDropdown
                  user={user}
                  onLogout={handleLogout}
                  onOpenProfile={() => {
                    setViewingProfileUserId(user?.id);
                    setShowProfileModal(true);
                  }}
                  isDarkMode={isDarkMode}
                  onToggleTheme={handleToggleTheme}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Profile Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          setViewingProfileUserId(null);
          setProfileStartInEdit(false);
        }}
        userId={viewingProfileUserId}
        currentUserId={user?.id}
        canManageAccepting={['admin', 'super admin'].includes((user?.role || '').toLowerCase())}
        canEditProfile={['admin', 'super admin'].includes((user?.role || '').toLowerCase())}
        startInEdit={profileStartInEdit}
        onProfileUpdated={(updatedProfile) => {
          const targetUserId = viewingProfileUserId || user?.id;
          // Optimistically update the users list (directory, etc.)
          if (updatedProfile?.profile_pic_url) {
            setUsers(prev => prev.map(u => 
              u.id === targetUserId 
                ? { ...u, profile_pic_url: updatedProfile.profile_pic_url } 
                : u
            ));
          }
          // Refresh own user object (header avatar)
          if (targetUserId === user?.id) {
            setUser(prev => prev ? { ...prev, ...updatedProfile } : prev);
            refreshUser();
          }
        }}
      />

      {/* Staff Directory Dialog — lazy mounted only when opened. */}
      <Dialog open={showDirectoryDialog} onOpenChange={setShowDirectoryDialog}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-teal-600" />
              Staff Directory
            </DialogTitle>
            <DialogDescription>
              View and contact team members
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {showDirectoryDialog && (
              <Suspense fallback={<div className="p-6 text-center text-sm text-gray-500">Loading directory…</div>}>
                <StaffDirectory
                  users={users}
                  currentUser={user}
                  onViewProfile={(userId) => {
                    setProfileStartInEdit(false);
                    setViewingProfileUserId(userId);
                    setShowProfileModal(true);
                  }}
                  onEditProfile={['admin', 'super admin'].includes((user?.role || '').toLowerCase()) ? (userId) => {
                    setProfileStartInEdit(true);
                    setViewingProfileUserId(userId);
                    setShowProfileModal(true);
                  } : undefined}
                  onSendMessage={(userId, userName) => {
                    setShowStaffToStaffMessageDialog(true);
                    setStaffToStaffRecipientId(userId);
                    setStaffToStaffRecipientName(userName);
                  }}
                />
              </Suspense>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 dark:text-gray-100">
        {/* Dashboard Header - Collapsible.
            Jun 2026 restructure: the regular dashboard shows upcoming
            appointments + tasks ONLY.  The practice-wide summary metrics
            (total users/clients/appointments/documents) moved to the
            Admin portal overview (?view=admin) below — so this header is
            hidden while the admin tab is active. */}
        {activeTab !== 'admin' && (isAdmin() || userRoleName === 'provider' || isSuperAdmin(user) || userRoleName === 'office' || userRoleName === 'supervisor' || userRoleName === 'intern' || userRoleName === 'reception') && (
          <div className="mb-6">
            {/* Toggle button row */}
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-700">
                {dashboardHeaderCollapsed ? 'Dashboard Overview' : ''}
              </h2>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDashboardHeaderCollapsed(!dashboardHeaderCollapsed)}
                className="h-8 px-3 text-gray-500 hover:text-teal-600"
                title={dashboardHeaderCollapsed ? "Expand dashboard header" : "Collapse dashboard header for more workspace"}
                data-testid="dashboard-header-collapse-toggle"
              >
                {dashboardHeaderCollapsed ? (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    <span className="text-xs">Show Overview</span>
                  </>
                ) : (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    <span className="text-xs">Hide Overview</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Jun 2026 — practice-wide summary metrics moved to the Admin
            portal overview (?view=admin).  See TabsContent value="admin". */}

        {/* Provider/Admin/Office Dashboard - Upcoming Appointments - Collapsible */}
        {activeTab !== 'admin' && !dashboardHeaderCollapsed && (userRoleName === 'provider' || isSuperAdmin(user) || userRoleName === 'office' || userRoleName === 'supervisor' || userRoleName === 'intern' || userRoleName === 'reception') && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="md:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2" />
                    Upcoming Appointments
                  </div>
                  {upcomingAppointments.length > 1 && (
                    <div className="flex items-center gap-2 text-sm font-normal text-gray-500">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 w-7 p-0"
                        disabled={selectedUpcomingIndex === 0}
                        onClick={() => setSelectedUpcomingIndex(prev => Math.max(0, prev - 1))}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <span>{selectedUpcomingIndex + 1} / {upcomingAppointments.length}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 w-7 p-0"
                        disabled={selectedUpcomingIndex >= upcomingAppointments.length - 1}
                        onClick={() => setSelectedUpcomingIndex(prev => Math.min(upcomingAppointments.length - 1, prev + 1))}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingAppointments.length > 0 ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        {upcomingAppointments[selectedUpcomingIndex]?.appointment_type === 'firm_event' && (
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: upcomingAppointments[selectedUpcomingIndex]?.firm_event_color || '#0ea5e9' }}
                            aria-hidden
                          />
                        )}
                        {upcomingAppointments[selectedUpcomingIndex]?.appointment_type === 'firm_event'
                          ? `${upcomingAppointments[selectedUpcomingIndex]?.title}${upcomingAppointments[selectedUpcomingIndex]?.is_observed ? ' (Observed)' : ''}`
                          : upcomingAppointments[selectedUpcomingIndex]?.client_id
                          ? (findClientById(upcomingAppointments[selectedUpcomingIndex]?.client_id)?.full_name || 'Unknown Client')
                          : (upcomingAppointments[selectedUpcomingIndex]?.group_id || upcomingAppointments[selectedUpcomingIndex]?.selected_group_id)
                            ? (clientGroups.find(g => g.id === (upcomingAppointments[selectedUpcomingIndex]?.group_id || upcomingAppointments[selectedUpcomingIndex]?.selected_group_id))?.name || upcomingAppointments[selectedUpcomingIndex]?.title || 'Group Session')
                            : (upcomingAppointments[selectedUpcomingIndex]?.appointment_type === 'block_time' ? 'Personal Time' :
                               upcomingAppointments[selectedUpcomingIndex]?.appointment_type === 'supervision' ? 'Supervision' :
                               upcomingAppointments[selectedUpcomingIndex]?.appointment_type === 'internal' ? 'Internal Meeting' :
                               upcomingAppointments[selectedUpcomingIndex]?.appointment_type === 'vacation' ? 'Vacation Day' :
                               upcomingAppointments[selectedUpcomingIndex]?.appointment_type === 'sick_day' ? 'Sick Day' :
                               upcomingAppointments[selectedUpcomingIndex]?.appointment_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()))
                        }
                      </h3>
                      <p className="text-gray-600">
                        {upcomingAppointments[selectedUpcomingIndex]?.appointment_type === 'firm_event'
                          ? (upcomingAppointments[selectedUpcomingIndex]?.firm_event_type || 'Firm Calendar')
                          : upcomingAppointments[selectedUpcomingIndex]?.service_type}
                      </p>
                      <p className="text-sm text-gray-500">
                        {parseUTCTimestamp(upcomingAppointments[selectedUpcomingIndex]?.start_time)?.toLocaleDateString()}
                        {upcomingAppointments[selectedUpcomingIndex]?.appointment_type === 'firm_event' && upcomingAppointments[selectedUpcomingIndex]?.all_day ? (
                          <> · All day</>
                        ) : (
                          <>
                            {' at '}
                            {parseUTCTimestamp(upcomingAppointments[selectedUpcomingIndex]?.start_time)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {' - '}
                            {parseUTCTimestamp(upcomingAppointments[selectedUpcomingIndex]?.end_time)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </>
                        )}
                      </p>
                      {upcomingAppointments[selectedUpcomingIndex]?.appointment_type === 'firm_event' && upcomingAppointments[selectedUpcomingIndex]?.description && (
                        <p className="text-xs text-gray-500 mt-1 italic">{upcomingAppointments[selectedUpcomingIndex].description}</p>
                      )}
                    </div>
                    <div className="space-x-2">
                      <Button 
                        size="sm" 
                        onClick={() => changeTab('appointments')}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        View Calendar
                      </Button>
                      {upcomingAppointments[selectedUpcomingIndex]?.google_meet_link && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.preventDefault();
                            openMeetPopup(upcomingAppointments[selectedUpcomingIndex]?.google_meet_link);
                          }}
                        >
                          <Video className="h-4 w-4 mr-1" />
                          Join
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">No upcoming appointments for today or tomorrow</p>
                )}
              </CardContent>
            </Card>
            
            {/* My Clients card for providers, To Do Items for others */}
            {user?.is_provider ? (
              <Card>
                <CardHeader>
                  <CardTitle>My Clients</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Active Clients</span>
                      <span className="font-semibold">
                        {(allClientsForLookup || []).filter(c => c.assigned_providers?.includes(user?.id) && c.status === 'active').length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">On Waitlist</span>
                      <span className="font-semibold">
                        {(allClientsForLookup || []).filter(c => c.assigned_providers?.includes(user?.id) && c.status === 'waitlist').length}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>📋 To Do Items</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {user?.is_waitlist_manager && (
                      <div 
                        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded"
                        onClick={() => changeTab('waitlist')}
                      >
                        <span className="text-sm text-gray-600">Referrals Awaiting Approval</span>
                        <span className="font-semibold text-red-600">
                          {pendingIntakeClients.length}
                        </span>
                      </div>
                    )}
                    {user?.is_medication_manager && (
                      <div 
                        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded"
                        onClick={() => changeTab('medications')}
                      >
                        <span className="text-sm text-gray-600">Pending Med Intakes</span>
                        <span className="font-semibold text-orange-600">
                          {myTodoNotes?.filter(note => note.notification_type === 'medication_referral').length || 0}
                        </span>
                      </div>
                    )}
                    {(user?.is_authorization_manager || isSuperAdmin(user)) && (() => {
                      // Auth Expiry Snapshot — Feb 2026 addition.
                      // Surfaces authorizations whose remaining window
                      // is short, so billers/auth managers don't have
                      // to scroll the Auths tab every morning to
                      // figure out who's at risk of falling off cover.
                      //
                      // Two buckets:
                      //   • Expiring in ≤14 days  → red, urgent.
                      //   • Expiring in 15–30 days → amber, plan ahead.
                      // Only includes auths that aren't already
                      // expired/depleted (the Auths tab already
                      // separates those into the chevron below).
                      const isActive = (a) => a.status !== 'expired' && a.status !== 'depleted';
                      const days = (a) => Number(a.days_until_expiration);
                      const expiringSoon = (allAuthorizations || []).filter(
                        a => isActive(a) && Number.isFinite(days(a)) && days(a) > 0 && days(a) <= 14
                      );
                      const expiringLater = (allAuthorizations || []).filter(
                        a => isActive(a) && Number.isFinite(days(a)) && days(a) > 14 && days(a) <= 30
                      );
                      if (expiringSoon.length === 0 && expiringLater.length === 0) return null;
                      return (
                        <>
                          {expiringSoon.length > 0 && (
                            <div
                              className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded"
                              onClick={() => changeTab('authorizations')}
                              data-testid="todo-auths-expiring-soon"
                              title="Authorizations expiring in the next 14 days"
                            >
                              <span className="text-sm text-gray-600">Auths Expiring ≤ 14d</span>
                              <span className="font-semibold text-red-600">
                                {expiringSoon.length}
                              </span>
                            </div>
                          )}
                          {expiringLater.length > 0 && (
                            <div
                              className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded"
                              onClick={() => changeTab('authorizations')}
                              data-testid="todo-auths-expiring-later"
                              title="Authorizations expiring in 15–30 days"
                            >
                              <span className="text-sm text-gray-600">Auths Expiring 15–30d</span>
                              <span className="font-semibold text-amber-600">
                                {expiringLater.length}
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {!user?.is_waitlist_manager && !user?.is_medication_manager && !user?.is_authorization_manager && !isSuperAdmin(user) && (
                      <p className="text-sm text-gray-500">No pending items</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Tabs */}
        <Suspense fallback={<div className="flex items-center justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>}>
        <Tabs value={activeTab} onValueChange={changeTab} className="space-y-6">
          <TabsList className="flex flex-col w-full h-auto gap-1 p-1">
            {/* Row 1: Core tabs - always visible */}
            <div className="flex flex-wrap w-full gap-1 justify-start">
              <TabsTrigger value="clients" data-testid="clients-tab" className="flex-1 min-w-fit">
                <User className="h-4 w-4 mr-2" />
                Clients
              </TabsTrigger>
              {canSeeTab('appointments') && (
                <TabsTrigger value="appointments" data-testid="appointments-tab" className="flex-1 min-w-fit">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Schedule
                </TabsTrigger>
              )}
              <TabsTrigger value="notes" data-testid="notes-tab" className="flex-1 min-w-fit">
                <FileText className="h-4 w-4 mr-2" />
                Tasks
                {myTodoNotes && myTodoNotes.filter(note => {
                  // Exclude medication referrals for medication managers (they see them in their own tab)
                  if (note.notification_type === 'medication_referral' && user?.is_medication_manager) {
                    return false;
                  }
                  return true;
                }).length > 0 && (
                  <Badge className="ml-2 bg-teal-600 text-white">
                    {myTodoNotes.filter(note => {
                      if (note.notification_type === 'medication_referral' && user?.is_medication_manager) {
                        return false;
                      }
                      return true;
                    }).length}
                  </Badge>
                )}
              </TabsTrigger>
              {/* Groups tab - visibility controlled by role settings */}
              {canSeeTab('groups') && (
                <TabsTrigger value="groups" data-testid="groups-tab" className="flex-1 min-w-fit">
                  <Users className="h-4 w-4 mr-2" />
                  Groups
                </TabsTrigger>
              )}
              {user?.is_medication_manager && tenantHasFeature('medication_management') && (
                <TabsTrigger value="medication-management" data-testid="medication-management-tab" className="flex-1 min-w-fit">
                  <Pill className="h-4 w-4 mr-2" />
                  Meds
                  {medicationReferralRequests.filter(r => r.status === 'pending').length > 0 && (
                    <Badge className="ml-2 bg-purple-600 text-white">
                      {medicationReferralRequests.filter(r => r.status === 'pending').length}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
              {user?.is_waitlist_manager && tenantHasFeature('waitlist') && (
                <TabsTrigger value="waitlist" data-testid="waitlist-tab" className="flex-1 min-w-fit">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Waitlist
                  {pendingIntakeClients.length > 0 && (
                    <Badge className="ml-2 bg-orange-600 text-white">
                      {pendingIntakeClients.length}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
              {/* CPT Tab removed Feb 2026 — will be replaced by a Reports tab. */}
              {/* Authorization Management Tab */}
              {(user?.is_authorization_manager || isSuperAdmin(user)) && (
                <TabsTrigger value="authorizations" data-testid="authorizations-tab" className="flex-1 min-w-fit">
                  <Shield className="h-4 w-4 mr-2" />
                  Auths
                  {allAuthorizations.filter(a => a.status === 'low_units' || a.status === 'warning').length > 0 && (
                    <Badge className="ml-1 bg-amber-500 text-white text-xs">
                      {allAuthorizations.filter(a => a.status === 'low_units' || a.status === 'warning').length}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
            </div>
            {/* Row 2: Billing/Admin tabs - conditional on billing/admin roles
                OR office users whose role-permission system grants them
                a tab in this row.  Without the `canSeeTab(...)` clause,
                office users with `eligibility` in their role's
                `visible_tabs` (the default for `OFFICE_DEFAULT_TABS`)
                got blocked by this outer guard — they never saw the
                Eligibility tab even though the inner gate said yes.
                Reported May 2026: "office user can't see eligibility tab,
                seems to be always drifting" — drift came from this
                conflict (flipping is_biller would temporarily reveal it). */}
            {(user?.is_billing_manager || user?.is_biller || user?.is_medical_records_manager || user?.is_payroll_manager || isSuperAdmin(user) || userRoleName === 'admin' || canSeeTab('eligibility')) && (
              <div className="flex flex-wrap w-full gap-1 justify-start">
                {/* Eligibility Tab - controlled by role permission */}
                {canSeeTab('eligibility') && (
                  <TabsTrigger value="eligibility" data-testid="eligibility-tab" className="flex-1 min-w-fit">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Eligibility
                  </TabsTrigger>
                )}
                {/* Billing Tab - controlled at user level (is_biller) AND billing enabled */}
                {billingSettings.billing_enabled && tenantHasFeature('billing') && (user?.is_biller || isSuperAdmin(user) || userRoleName === 'admin') && (
                  <TabsTrigger value="billing" data-testid="billing-tab" className="flex-1 min-w-fit">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Billing
                  </TabsTrigger>
                )}
                {/* Billing Management Tab - Super Admin only AND billing enabled */}
                {billingSettings.billing_enabled && tenantHasFeature('billing') && (isSuperAdmin(user)) && (
                  <TabsTrigger value="billing-management" data-testid="billing-management-tab" className="flex-1 min-w-fit">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Batch
                  </TabsTrigger>
                )}
                {/* ERA Management Tab - Super Admin only AND billing enabled */}
                {billingSettings.billing_enabled && tenantHasFeature('era') && (isSuperAdmin(user) || user?.is_era_manager) && (
                  <TabsTrigger value="era-management" data-testid="era-management-tab" className="flex-1 min-w-fit">
                    <FileText className="h-4 w-4 mr-2" />
                    ERA
                  </TabsTrigger>
                )}
                {/* Claims Workshop Tab - Super Admin only AND billing enabled */}
                {billingSettings.billing_enabled && tenantHasFeature('billing') && (isSuperAdmin(user)) && (
                  <TabsTrigger value="claims-workshop" data-testid="claims-workshop-tab" className="flex-1 min-w-fit">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Claims
                  </TabsTrigger>
                )}
                {/* Medical Records Tab - controlled at user level (is_medical_records_manager) */}
                {(user?.is_medical_records_manager || isSuperAdmin(user)) && tenantHasFeature('medical_records') && (
                  <TabsTrigger value="medical-records" data-testid="medical-records-tab" className="flex-1 min-w-fit">
                    <FileText className="h-4 w-4 mr-2" />
                    Records
                  </TabsTrigger>
                )}
                {/* Payroll Tab - Admins, Super Admins, or Payroll Managers */}
                {(user?.is_payroll_manager || isSuperAdmin(user)) && tenantHasFeature('payroll') && (
                  <TabsTrigger value="payroll" data-testid="payroll-tab" className="flex-1 min-w-fit">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Payroll
                  </TabsTrigger>
                )}
              </div>
            )}
          </TabsList>

          {/* Groups Tab */}
          <TabsContent value="groups">
            <GroupsTab
              user={user}
              clientGroups={clientGroups}
              clients={clients}
              allClientsForLookup={allClientsForLookup}
              providers={providers}
              users={users}
              locations={locations}
              curricula={curricula}
              pendingGroupRequests={pendingGroupRequests}
              showArchivedGroups={showArchivedGroups}
              setShowArchivedGroups={setShowArchivedGroups}
              setShowCurriculumLibrary={setShowCurriculumLibrary}
              setShowRequestGroupDialog={setShowRequestGroupDialog}
              setShowAddGroup={setShowAddGroup}
              setShowGroupMembers={setShowGroupMembers}
              setShowAttendanceSnapshot={setShowAttendanceSnapshot}
              setShowGroupApprovalDialog={setShowGroupApprovalDialog}
              setSelectedGroup={setSelectedGroup}
              setAttendanceSnapshotGroup={setAttendanceSnapshotGroup}
              setGroupApprovalRequest={setGroupApprovalRequest}
              setGroupApprovalData={setGroupApprovalData}
              handleEditClientGroup={handleEditClientGroup}
              onGroupHistory={handleGroupHistory}
              onGroupMembers={handleGroupMembers}
              fetchData={fetchData}
              fetchPendingGroupRequests={fetchPendingGroupRequests}
              onEnterAttendance={handleDirectAttendance}
              onAddGroupNote={handleDirectGroupNote}
            />
          </TabsContent>

          {/* Billing Tab (For Billers - assigned to providers) */}
          {/* Wrapped with BillingTabWrapper for crash isolation - errors won't crash the entire app */}
          {(user?.is_biller || isSuperAdmin(user)) && (
            <TabsContent value="billing">
              <BillingProvider>
                <BillingTabWrapper
                  users={users}
                  providers={providers}
                  clients={allClientsForLookup}
                  insurances={insurances}
                  currentBatch={currentBatch}
                  user={user}
                  fetchCurrentBatch={fetchCurrentBatch}
                  fetchNoteForReview={fetchNoteForReview}
                  setCurrentNoteIndex={setCurrentNoteIndex}
                  setSelectedNoteForReview={setSelectedNoteForReview}
                  setSelectedClaim={setSelectedClaim}
                  // Mar 2026 — hoisted billing list sort lives in App.js
                  // so Next/Prev Claim navigation can walk the SAME order
                  // the biller sees on screen.  BillingTab uses these
                  // props instead of its own local state.
                  sortColumn={billingSortColumn}
                  setSortColumn={setBillingSortColumn}
                  sortDirection={billingSortDirection}
                  setSortDirection={setBillingSortDirection}
                  onViewNote={(note) => {
                    // Open the note with proper content initialization
                    openNote(note);
                  }}
                />
              </BillingProvider>
            </TabsContent>
          )}

          {/* Billing Management Tab (Batch Management) */}
          {(user?.is_billing_manager || isSuperAdmin(user)) && (
            <TabsContent value="billing-management">
              <Suspense fallback={<PageLoader label="Loading Batch Management..." />}>
              <BatchTab user={user} billingSettings={billingSettings} onEditClaim={(noteId) => fetchNoteForReview(noteId)} claimsQueueRefreshKey={claimsQueueRefreshKey} />
              </Suspense>
            </TabsContent>
          )}


          {/* Claims Workshop Tab */}
          {(user?.is_billing_manager || isSuperAdmin(user)) && (
            <TabsContent value="claims-workshop">
              <Suspense fallback={<PageLoader label="Loading Claims..." />}>
              <ClaimsTab user={user} onClaimContextMenu={setClaimContextMenu} />
              </Suspense>
            </TabsContent>
          )}


          {/* Edit button now opens full ClaimsWorkshop with pre-selected claim */}

          {/* Claim Detail Dialog */}
          <Dialog open={showClaimDetailDialog} onOpenChange={setShowClaimDetailDialog}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>📋 Claim Details</DialogTitle>
                <DialogDescription>
                  Review claim information before adding to batch
                </DialogDescription>
              </DialogHeader>
              
              {selectedClaim && (
                <div className="space-y-6">
                  {/* Claim Summary */}
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 font-medium">Claim Number</p>
                        <p className="font-mono text-sm font-semibold text-teal-700">
                          {selectedClaim.claim?.claim_number || selectedClaim.claim?.id?.substring(0, 8)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Date of Service</p>
                        <p className="font-semibold">
                          {selectedClaim.claim?.date_of_service 
                            ? fmtLocalDate(selectedClaim.claim.date_of_service) 
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Place of Service</p>
                        <p className="font-semibold">
                          {selectedClaim.claim?.place_of_service || 'N/A'}
                          {selectedClaim.claim?.place_of_service === '11' && ' (Office)'}
                          {selectedClaim.claim?.place_of_service === '02' && ' (Telehealth)'}
                          {selectedClaim.claim?.place_of_service === '12' && ' (Home)'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Total Amount</p>
                        <p className="font-semibold text-green-700">
                          ${(selectedClaim.claim?.total_charged_amount || 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Status</p>
                        <Badge className="bg-blue-600 text-white">
                          {selectedClaim.claim?.status === 'ready_for_secondary' ? 'Partial Paid – Awaiting Secondary' : selectedClaim.claim?.status === 'crossed_to_secondary' ? 'Crossed to Secondary – No Rebill' : selectedClaim.claim?.status?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Client Information */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <span className="text-blue-600">👤</span> Client Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600">Name</p>
                        <p className="font-semibold">{selectedClaim.client?.first_name} {(selectedClaim.client?.last_name || '').toUpperCase()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">MRN</p>
                        <p className="font-mono text-sm">{formatMrn(selectedClaim.client?.mrn)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Date of Birth</p>
                        <p>{selectedClaim.client?.date_of_birth 
                          ? fmtLocalDate(selectedClaim.client.date_of_birth) 
                          : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Gender</p>
                        <p>{selectedClaim.client?.gender || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Insurance Information */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <span className="text-yellow-600">🏥</span> Insurance Information
                    </h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 font-medium">Primary Insurance</p>
                          <p className="font-semibold">{selectedClaim.claim?.primary_insurance || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 font-medium">Subscriber ID / Member ID</p>
                          <p className="font-mono text-sm font-semibold text-blue-700">
                            {selectedClaim.claim?.subscriber_id || selectedClaim.claim?.primary_member_id || 'N/A'}
                          </p>
                        </div>
                        {selectedClaim.claim?.group_number && (
                          <div>
                            <p className="text-sm text-gray-600 font-medium">Group Number</p>
                            <p className="font-mono text-sm">{selectedClaim.claim.group_number}</p>
                          </div>
                        )}
                        {selectedClaim.claim?.secondary_insurance && (
                          <div>
                            <p className="text-sm text-gray-600 font-medium">Secondary Insurance</p>
                            <p className="font-semibold">{selectedClaim.claim.secondary_insurance}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Provider Information - continuing Claim Detail Dialog */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <span className="text-green-600">👨‍⚕️</span> Provider Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600">Provider Name</p>
                        <p className="font-semibold">{selectedClaim.provider?.full_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">NPI</p>
                        <p className="font-mono text-sm">{selectedClaim.provider?.npi || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>


          {/* ERA Dialogs moved to ERATab.jsx */}

          {/* Return Claim for Corrections Dialog */}
          <Dialog open={showReturnClaimDialog} onOpenChange={setShowReturnClaimDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>🔄 Return Claim for Corrections</DialogTitle>
                <DialogDescription>
                  Send this claim back to billing for corrections
                </DialogDescription>
              </DialogHeader>
              
              {selectedClaim && (
                <div className="space-y-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="text-sm space-y-1">
                      <p><strong>Claim:</strong> {selectedClaim.claim_number || selectedClaim.id?.substring(0, 8)}</p>
                      <p><strong>Client:</strong> {selectedClaim.client_name}</p>
                      <p><strong>Amount:</strong> ${(selectedClaim.total_charged_amount || 0).toFixed(2)}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Return <span className="text-red-500">*</span>
                    </label>
                    <DebouncedTextarea
                      value={returnClaimNotes}
                      onChange={(e) => setReturnClaimNotes(e.target.value)}
                      placeholder="Explain what needs to be corrected (e.g., incorrect CPT code, missing authorization, wrong date of service, etc.)"
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Be specific so the biller knows exactly what to fix
                    </p>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowReturnClaimDialog(false);
                    setSelectedClaim(null);
                    setReturnClaimNotes('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!returnClaimNotes.trim()) {
                      toast.error("Please provide a reason for returning this claim");
                      return;
                    }

                    try {
                      await axios.post(`${API}/claims/${selectedClaim.id}/return-for-corrections`, {
                        notes: returnClaimNotes
                      });
                      toast.success("Claim returned to billing for corrections");
                      setShowReturnClaimDialog(false);
                      setSelectedClaim(null);
                      setReturnClaimNotes('');
                      fetchClaimsQueue();
                    } catch (error) {
                      console.error("Failed to return claim:", error);
                      toast.error(error.response?.data?.detail || "Failed to return claim");
                    }
                  }}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  disabled={!returnClaimNotes.trim()}
                >
                  Return Claim
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ERA Management Tab (For Billing Managers) */}
          {(user?.is_billing_manager || isSuperAdmin(user)) && (
            <TabsContent value="era-management">
              <Suspense fallback={<PageLoader label="Loading ERA Management..." />}>
              <ERATab user={user} allClientsForLookup={allClientsForLookup} />
              </Suspense>
            </TabsContent>
          )}

          {/* Authorization Management Tab — Extracted to AuthorizationsMgmtTab */}
          {(user?.is_authorization_manager || isSuperAdmin(user)) && (
            <TabsContent value="authorizations">
              <Suspense fallback={<PageLoader label="Loading Authorizations..." />}>
              <AuthorizationsMgmtTab
                allAuthorizations={allAuthorizations}
                fetchAllAuthorizations={fetchAllAuthorizations}
                allClientsForLookup={allClientsForLookup}
                clients={clients}
                users={users}
                cptCodes={cptCodes}
                serviceTypes={serviceTypes}
                setServiceTypes={setServiceTypes}
                insurances={insurances}
                handleClientClick={handleClientClick}
                setEditingAppointment={setEditingAppointment}
                setShowAppointmentForm={setShowAppointmentForm}
                setClientDetailTab={setClientDetailTab}
              />
              </Suspense>

            </TabsContent>
          )}

          {/* Eligibility Management Tab - Self-contained Component */}
          <EligibilityTab
            handleDownload271Responses={handleDownload271Responses}
            onClientClick={handleClientClick}
            onReviewDiscrepancies={(checkId, clientId, clientName, discrepancies) => {
              setDiscrepancyData({ checkId, clientId, clientName, discrepancies });
              setShowDiscrepancyReview(true);
            }}
          />

          {/* Medical Records Tab */}
          {(user?.is_medical_records_manager || user?.is_billing_manager || isSuperAdmin(user)) && (
            <TabsContent value="medical-records">
              <Suspense fallback={<PageLoader label="Loading Medical Records..." />}>
              <MedicalRecordsManagerDashboard API={API} user={user} brandingSettings={brandingSettings} locations={locations} />
              </Suspense>
            </TabsContent>
          )}

          {/* Payroll Tab */}
          <TabsContent value="payroll">
            <Suspense fallback={<div className="flex items-center justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>}>
              <PayrollTab user={user} />
            </Suspense>
          </TabsContent>


          {/* Claim Detail Dialog with HCFA-1500 Preview */}
          <Dialog open={showClaimDetail} onOpenChange={setShowClaimDetail}>
            <DialogContent className="max-w-[1100px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Claim Preview - HCFA-1500 Professional Claim Form</DialogTitle>
                <DialogDescription>
                  Preview claim in standard HCFA-1500 format
                </DialogDescription>
              </DialogHeader>
              
              {loadingClaimDetail ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading claim preview...</p>
                  </div>
                </div>
              ) : claimDetail && (
                <div className="space-y-6">
                  {/* Claim Information Header */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 font-medium">Claim ID</p>
                        <p className="font-mono text-xs">{claimDetail.claim?.id?.substring(0, 8)}...</p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Post Date</p>
                        <p className="font-semibold">{claimDetail.claim?.post_date ? new Date(claimDetail.claim.post_date).toLocaleDateString() : 'Not posted yet'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Date of Service</p>
                        <p className="font-semibold">{claimDetail.claim?.date_of_service ? fmtLocalDate(claimDetail.claim.date_of_service) : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Status</p>
                        <Badge className={
                          claimDetail.claim?.status === 'billed' ? 'bg-green-600 text-white' :
                          claimDetail.claim?.status === 'ready_for_billing' ? 'bg-blue-600 text-white' :
                          claimDetail.claim?.status === 'ready_for_secondary' ? 'bg-amber-500 text-white' :
                          claimDetail.claim?.status === 'crossed_to_secondary' ? 'bg-purple-600 text-white' :
                          'bg-gray-600 text-white'
                        }>
                          {claimDetail.claim?.status === 'ready_for_secondary' ? 'Partial Paid – Awaiting Secondary' : claimDetail.claim?.status === 'crossed_to_secondary' ? 'Crossed to Secondary – No Rebill' : claimDetail.claim?.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  {/* Claim recovery toolbar (Feb 2026): "Restore
                      Balance from Ledger" + a small explainer for the
                      `claim_created` status leak. Billing managers /
                      admins only. */}
                  {(isSuperAdmin(user) || user?.is_billing_manager) && claimDetail.claim?.id && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                      <RefreshCw className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                      <div className="flex-1 text-xs text-amber-900">
                        <div className="font-semibold mb-0.5">Recovery tools</div>
                        <div className="text-amber-800">
                          If this claim's balance looks wrong (e.g. stuck at <code className="bg-white px-1 rounded">claim_created</code> with no payments after an unpost), rebuild it from the append-only payment ledger. No new money events are emitted — display fields only.
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="claim-restore-balance-from-ledger-btn"
                        className="text-xs border-amber-400 text-amber-900 hover:bg-amber-100 shrink-0"
                        onClick={async () => {
                          const ok = window.confirm(
                            "Restore this claim's balance from the payment ledger?\n\n"
                            + "Replays every non-voided money event (payments, reversals, corrections) "
                            + "and writes the correct patient_balance / primary_paid_amount / etc. "
                            + "back to the claim. Also heals a stuck `claim_created` status. "
                            + "No new money events are emitted."
                          );
                          if (!ok) return;
                          try {
                            const resp = await axios.post(
                              `${API}/claims/${claimDetail.claim.id}/restore-balance-from-ledger`
                            );
                            if (resp.data?.restored) {
                              const pb = resp.data?.restored_fields?.patient_balance;
                              toast.success(
                                `Balance restored from ledger${typeof pb === 'number' ? `: $${pb.toFixed(2)}` : ''}.`
                              );
                            } else {
                              toast.info(resp.data?.message || 'Nothing to restore from ledger.');
                            }
                            // Re-fetch the claim detail so the dialog
                            // shows the restored fields immediately.
                            if (typeof fetchClaimDetail === 'function') {
                              fetchClaimDetail(claimDetail.claim.id);
                            }
                          } catch (err) {
                            const msg = err?.response?.data?.detail || err.message || 'Restore failed';
                            toast.error(`Restore failed: ${msg}`);
                          }
                        }}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Restore Balance from Ledger
                      </Button>
                    </div>
                  )}

                  {/* Phase 4 — Payment Timeline (append-only ledger derived) */}
                  {claimDetail.claim?.id && (
                    <PaymentTimelineCard
                      claimId={claimDetail.claim.id}
                      claimNumber={claimDetail.claim.claim_number}
                    />
                  )}

                  {/* HCFA-1500 Form - Professional Claim Format - RED INK VERSION */}
                  <div className="bg-white p-3" style={{
                    fontFamily: 'Courier New, monospace', 
                    fontSize: '8.5px',
                    border: '2px solid #CC0000',
                    color: '#000000',
                    lineHeight: '1.2'
                  }}>
                    {/* Header */}
                    <div className="flex justify-between items-start mb-1 pb-1" style={{borderBottom: '1px solid #CC0000'}}>
                      <div>
                        <div style={{fontSize: '11px', fontWeight: 'bold', color: '#CC0000'}}>HEALTH INSURANCE CLAIM FORM</div>
                        <div style={{fontSize: '7px', color: '#CC0000'}}>APPROVED BY NATIONAL UNIFORM CLAIM COMMITTEE (NUCC) 02/12</div>
                      </div>
                      <div className="text-right">
                        <div style={{fontSize: '9px', fontWeight: 'bold', color: '#CC0000'}}>PICA</div>
                        <div style={{fontSize: '7px', color: '#CC0000'}}>PICA</div>
                      </div>
                    </div>

                    {/* Box 1-1a: Insurance Type & Insured's ID */}
                    <div className="grid grid-cols-2 gap-0" style={{borderBottom: '1px solid #CC0000'}}>
                      {/* Box 1: Insurance Type */}
                      <div className="p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>
                          1. MEDICARE &nbsp;&nbsp; MEDICAID &nbsp;&nbsp; TRICARE CHAMPVA &nbsp;&nbsp; GROUP HEALTH PLAN &nbsp;&nbsp; FECA BLK LUNG &nbsp;&nbsp; OTHER
                        </div>
                        <div className="flex gap-2" style={{fontSize: '8px'}}>
                          <label className="flex items-center gap-1">
                            <span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', textAlign: 'center'}}>
                              {claimDetail.claim?.primary_insurance?.toLowerCase().includes('medicare') ? 'X' : ''}
                            </span>
                            <span style={{color: '#CC0000', fontSize: '7px'}}>MEDICARE</span>
                          </label>
                          <label className="flex items-center gap-1">
                            <span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', textAlign: 'center'}}>
                              {claimDetail.claim?.primary_insurance?.toLowerCase().includes('medicaid') ? 'X' : ''}
                            </span>
                            <span style={{color: '#CC0000', fontSize: '7px'}}>MEDICAID</span>
                          </label>
                          <label className="flex items-center gap-1">
                            <span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', textAlign: 'center'}}>
                              {!claimDetail.claim?.primary_insurance?.toLowerCase().includes('medicare') && !claimDetail.claim?.primary_insurance?.toLowerCase().includes('medicaid') ? 'X' : ''}
                            </span>
                            <span style={{color: '#CC0000', fontSize: '7px'}}>OTHER</span>
                          </label>
                        </div>
                      </div>
                      
                      {/* Box 1a: Insured's ID */}
                      <div className="p-1">
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>1a. INSURED'S I.D. NUMBER (For Program in Item 1)</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>{(claimDetail.claim?.primary_member_id || '').toUpperCase()}</div>
                      </div>
                    </div>

                    {/* Box 2-4: Patient & Insured Names */}
                    <div className="grid grid-cols-12 gap-0" style={{borderBottom: '1px solid #CC0000'}}>
                      {/* Box 2: Patient Name */}
                      <div className="col-span-6 p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>2. PATIENT'S NAME (Last Name, First Name, Middle Initial)</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>
                          {claimDetail.client ? `${(claimDetail.client.last_name || '').toUpperCase()}, ${(claimDetail.client.first_name || '').toUpperCase()}${claimDetail.client.middle_initial ? `, ${claimDetail.client.middle_initial.toUpperCase()}` : ''}` : ''}
                        </div>
                      </div>
                      
                      {/* Box 3: Patient DOB & Sex */}
                      <div className="col-span-3 p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>3. PATIENT'S BIRTH DATE</div>
                        <div style={{fontSize: '8px'}}>
                          {claimDetail.client?.date_of_birth ? 
                            (() => {
                              const dob = new Date(claimDetail.client.date_of_birth);
                              const mm = dob.toLocaleDateString('en-US', {month: '2-digit'});
                              const dd = dob.toLocaleDateString('en-US', {day: '2-digit'});
                              const yy = dob.toLocaleDateString('en-US', {year: '2-digit'});
                              return `${mm} ${dd} ${yy}`;
                            })()
                            : ''}
                        </div>
                        <div style={{fontSize: '6px', color: '#CC0000', marginTop: '4px'}}>
                          SEX <span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', marginLeft: '4px', textAlign: 'center', color: '#000'}}>M</span> <span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', marginLeft: '4px', textAlign: 'center', color: '#000'}}>F</span>
                        </div>
                      </div>
                      
                      {/* Box 4: Insured's Name */}
                      <div className="col-span-3 p-1">
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>4. INSURED'S NAME (Last Name, First Name, Middle Initial)</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>
                          {claimDetail.client ? `${(claimDetail.client.last_name || '').toUpperCase()}, ${(claimDetail.client.first_name || '').toUpperCase()}` : 'SELF'}
                        </div>
                      </div>
                    </div>

                    {/* Box 5-7: Addresses */}
                    <div className="grid grid-cols-2 gap-0" style={{borderBottom: '1px solid #CC0000'}}>
                      {/* Box 5: Patient Address */}
                      <div className="p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>5. PATIENT'S ADDRESS (No., Street)</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>{(claimDetail.client?.address || '').toUpperCase()}</div>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginTop: '4px', marginBottom: '2px'}}>CITY</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>{(claimDetail.client?.city || '').toUpperCase()}</div>
                        <div className="grid grid-cols-2 gap-1 mt-1">
                          <div>
                            <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000'}}>STATE</div>
                            <div style={{fontSize: '9px', fontWeight: 'bold'}}>{(claimDetail.client?.state || '').toUpperCase()}</div>
                          </div>
                          <div>
                            <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000'}}>ZIP CODE</div>
                            <div style={{fontSize: '9px', fontWeight: 'bold'}}>{claimDetail.client?.zip_code || claimDetail.client?.zipcode || ''}</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Box 7: Insured's Address */}
                      <div className="p-1">
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>7. INSURED'S ADDRESS (No., Street)</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>SAME AS PATIENT</div>
                      </div>
                    </div>

                    {/* Box 6, 8, 9-11: Patient Relationship, Reserved, Other Insurance */}
                    <div className="grid grid-cols-2 gap-0" style={{borderBottom: '1px solid #CC0000'}}>
                      <div className="p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>6. PATIENT RELATIONSHIP TO INSURED</div>
                        <div className="flex gap-3" style={{fontSize: '7px'}}>
                          <label><span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', marginRight: '4px', textAlign: 'center'}}>X</span>Self</label>
                          <label><span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', marginRight: '4px', textAlign: 'center'}}></span>Spouse</label>
                          <label><span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', marginRight: '4px', textAlign: 'center'}}></span>Child</label>
                          <label><span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', marginRight: '4px', textAlign: 'center'}}></span>Other</label>
                        </div>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginTop: '8px', marginBottom: '2px'}}>9. OTHER INSURED'S NAME (Last Name, First Name, Middle Initial)</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>{claimDetail.claim?.secondary_insurance ? 'SEE SECONDARY' : ''}</div>
                      </div>
                      <div className="p-1">
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>8. RESERVED FOR NUCC USE</div>
                        <div style={{height: '20px'}}></div>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>11. INSURED'S POLICY GROUP OR FECA NUMBER</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>{(claimDetail.claim?.primary_group_number || claimDetail.claim?.primary_member_id || '').toUpperCase()}</div>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginTop: '4px', marginBottom: '2px'}}>11d. INSURANCE PLAN NAME OR PROGRAM NAME</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>{(claimDetail.claim?.primary_insurance || '').toUpperCase()}</div>
                      </div>
                    </div>

                    {/* Box 12-13: Signatures */}
                    <div className="grid grid-cols-2 gap-0" style={{borderBottom: '1px solid #CC0000'}}>
                      <div className="p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>12. PATIENT'S OR AUTHORIZED PERSON'S SIGNATURE</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold', marginTop: '8px'}}>SIGNATURE ON FILE</div>
                        <div style={{fontSize: '7px', marginTop: '4px'}}>DATE: {new Date(claimDetail.claim?.created_at || Date.now()).toLocaleDateString('en-US')}</div>
                      </div>
                      <div className="p-1">
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>13. INSURED'S OR AUTHORIZED PERSON'S SIGNATURE</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold', marginTop: '8px'}}>SIGNATURE ON FILE</div>
                      </div>
                    </div>

                    {/* Box 14-21: Dates and Diagnosis */}
                    <div className="grid grid-cols-12 gap-0" style={{borderBottom: '1px solid #CC0000'}}>
                      <div className="col-span-3 p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>14. DATE OF CURRENT ILLNESS, INJURY, OR PREGNANCY (LMP)</div>
                        <div style={{fontSize: '8px', fontWeight: 'bold'}}>
                          {/* Left blank - typically not used for mental health/therapy claims */}
                        </div>
                      </div>
                      <div className="col-span-3 p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>17. NAME OF REFERRING PROVIDER OR OTHER SOURCE</div>
                        <div style={{fontSize: '8px'}}></div>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginTop: '8px'}}>17b. NPI</div>
                      </div>
                      <div className="col-span-6 p-1">
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>21. DIAGNOSIS OR NATURE OF ILLNESS OR INJURY (Relate A-L to service line below 24E)</div>
                        <div style={{fontSize: '6px', color: '#CC0000', marginBottom: '2px'}}>ICD Ind: <span style={{fontSize: '8px', fontWeight: 'bold', color: '#000'}}>0</span></div>
                        <div className="grid grid-cols-4 gap-1">
                          {claimDetail.claim?.diagnosis_codes?.slice(0, 12).map((code, idx) => (
                            <div key={idx} style={{fontSize: '8px'}}>
                              <span style={{fontWeight: 'bold', color: '#CC0000'}}>{String.fromCharCode(65 + idx)}.</span> <span style={{fontWeight: 'bold', color: '#000'}}>{(String(code || '')).toUpperCase()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Box 24: Service Lines - THE MAIN SECTION */}
                    <div style={{borderBottom: '2px solid #CC0000'}}>
                      <div className="grid grid-cols-12 gap-0" style={{backgroundColor: '#f5f5f5', borderBottom: '1px solid #CC0000'}}>
                        <div className="col-span-2 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '5px', fontWeight: 'bold', color: '#CC0000', textAlign: 'center'}}>24A. DATE(S) OF SERVICE<br/>MM DD YY</div>
                        <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '5px', fontWeight: 'bold', color: '#CC0000', textAlign: 'center'}}>24B.<br/>PLACE OF<br/>SERVICE</div>
                        <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '5px', fontWeight: 'bold', color: '#CC0000', textAlign: 'center'}}>24C.<br/>EMG</div>
                        <div className="col-span-2 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '5px', fontWeight: 'bold', color: '#CC0000', textAlign: 'center'}}>24D. PROCEDURES, SERVICES,<br/>OR SUPPLIES<br/>CPT/HCPCS MODIFIER</div>
                        <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '5px', fontWeight: 'bold', color: '#CC0000', textAlign: 'center'}}>24E.<br/>DIAGNOSIS<br/>POINTER</div>
                        <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '5px', fontWeight: 'bold', color: '#CC0000', textAlign: 'center'}}>24F.<br/>$ CHARGES</div>
                        <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '5px', fontWeight: 'bold', color: '#CC0000', textAlign: 'center'}}>24G.<br/>DAYS OR<br/>UNITS</div>
                        <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '5px', fontWeight: 'bold', color: '#CC0000', textAlign: 'center'}}>24H.<br/>EPSDT<br/>Family Plan</div>
                        <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '5px', fontWeight: 'bold', color: '#CC0000', textAlign: 'center'}}>24I.<br/>ID.<br/>QUAL</div>
                        <div className="col-span-1 p-1" style={{fontSize: '5px', fontWeight: 'bold', color: '#CC0000', textAlign: 'center'}}>24J.<br/>RENDERING<br/>PROVIDER ID #</div>
                      </div>
                      
                      {/* Service Line Rows (up to 6) */}
                      {[0, 1, 2, 3, 4, 5].map((rowIdx) => {
                        const line = claimDetail.claim?.service_lines?.[rowIdx];
                        return (
                          <div key={rowIdx} className="grid grid-cols-12 gap-0" style={{borderBottom: '1px solid #CC0000', minHeight: '28px'}}>
                            <div className="col-span-2 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '8px', fontWeight: 'bold'}}>
                              {line?.date_from ? 
                                (() => {
                                  const dateFrom = new Date(line.date_from);
                                  const mm = dateFrom.toLocaleDateString('en-US', {month: '2-digit'});
                                  const dd = dateFrom.toLocaleDateString('en-US', {day: '2-digit'});
                                  const yy = dateFrom.toLocaleDateString('en-US', {year: '2-digit'});
                                  return `${mm} ${dd} ${yy}`;
                                })()
                                : ''}
                            </div>
                            <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', textAlign: 'center', fontSize: '9px', fontWeight: 'bold'}}>
                              {line?.place_of_service || ''}
                            </div>
                            <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '8px', textAlign: 'center', fontWeight: 'bold'}}>
                              {line?.emergency ? 'Y' : ''}
                            </div>
                            <div className="col-span-2 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '9px', fontWeight: 'bold'}}>
                              {line ? `${(line.cpt_code || '').toUpperCase()} ${(line.modifiers || []).join(' ').toUpperCase()}`.trim() : ''}
                            </div>
                            <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', textAlign: 'center', fontSize: '8px', fontWeight: 'bold'}}>
                              {line?.diagnosis_pointers?.map(num => String.fromCharCode(64 + num)).join(',') || (line ? 'A' : '')}
                            </div>
                            <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', textAlign: 'right', fontSize: '9px', fontWeight: 'bold'}}>
                              {line ? `${(line.charges || 0).toFixed(2)}` : ''}
                            </div>
                            <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', textAlign: 'center', fontSize: '8px', fontWeight: 'bold'}}>
                              {line?.units || ''}
                            </div>
                            <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '7px'}}></div>
                            <div className="col-span-1 p-1" style={{borderRight: '1px solid #CC0000', fontSize: '7px'}}></div>
                            <div className="col-span-1 p-1" style={{fontSize: '7px', fontWeight: 'bold'}}>
                              {claimDetail.provider?.npi || ''}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Box 25-28: Federal Tax ID and Totals */}
                    <div className="grid grid-cols-12 gap-0" style={{borderBottom: '1px solid #CC0000'}}>
                      <div className="col-span-3 p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>25. FEDERAL TAX I.D. NUMBER</div>
                        <div className="flex gap-2" style={{fontSize: '7px'}}>
                          <label><span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', marginRight: '2px', textAlign: 'center'}}></span> SSN</label>
                          <label><span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', marginRight: '2px', textAlign: 'center'}}>X</span> EIN</label>
                        </div>
                        <div style={{fontSize: '9px', fontWeight: 'bold', marginTop: '4px'}}>{(organizations?.[0]?.tax_id || '').toUpperCase()}</div>
                      </div>
                      <div className="col-span-3 p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>26. PATIENT'S ACCOUNT NO.</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>{(claimDetail.client?.mrn || '').toUpperCase()}</div>
                      </div>
                      <div className="col-span-3 p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>27. ACCEPT ASSIGNMENT?</div>
                        <div style={{fontSize: '7px'}}>
                          <label className="mr-3"><span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', marginRight: '2px', textAlign: 'center'}}>X</span> YES</label>
                          <label><span style={{border: '1px solid #CC0000', width: '10px', height: '10px', display: 'inline-block', marginRight: '2px', textAlign: 'center'}}></span> NO</label>
                        </div>
                      </div>
                      <div className="col-span-3 p-1">
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>28. TOTAL CHARGE</div>
                        <div style={{fontSize: '14px', fontWeight: 'bold'}}>$ {(claimDetail.claim?.total_charged_amount || 0).toFixed(2)}</div>
                      </div>
                    </div>

                    {/* Box 29-33: Provider Info */}
                    <div className="grid grid-cols-2 gap-0">
                      <div className="p-1" style={{borderRight: '1px solid #CC0000'}}>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>31. SIGNATURE OF PHYSICIAN OR SUPPLIER INCLUDING DEGREES OR CREDENTIALS</div>
                        <div style={{marginTop: '8px', fontSize: '9px', fontWeight: 'bold'}}>SIGNATURE ON FILE</div>
                        <div style={{fontSize: '8px', marginTop: '4px', fontWeight: 'bold'}}>
                          {(claimDetail.provider?.full_name || '').toUpperCase()}{claimDetail.provider?.credentials ? `, ${claimDetail.provider.credentials.toUpperCase()}` : ''}
                        </div>
                        <div style={{fontSize: '7px', marginTop: '4px'}}>DATE: {new Date().toLocaleDateString('en-US')}</div>
                      </div>
                      <div className="p-1">
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginBottom: '2px'}}>32. SERVICE FACILITY LOCATION INFORMATION</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>{(organizations?.[0]?.name || '').toUpperCase()}</div>
                        <div style={{fontSize: '8px', fontWeight: 'bold'}}>{(organizations?.[0]?.address || '').toUpperCase()}</div>
                        <div style={{fontSize: '8px', fontWeight: 'bold'}}>{organizations?.[0]?.city ? `${organizations[0].city.toUpperCase()}, ${organizations[0].state?.toUpperCase() || ''} ${organizations[0].zip_code || ''}` : ''}</div>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginTop: '4px'}}>a. NPI: <span style={{fontSize: '8px', color: '#000'}}>{organizations?.[0]?.npi || ''}</span></div>
                        
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginTop: '8px', marginBottom: '2px'}}>33. BILLING PROVIDER INFO & PH #</div>
                        <div style={{fontSize: '9px', fontWeight: 'bold'}}>{(claimDetail.provider?.full_name || '').toUpperCase()}{claimDetail.provider?.credentials ? `, ${claimDetail.provider.credentials.toUpperCase()}` : ''}</div>
                        <div style={{fontSize: '8px', fontWeight: 'bold'}}>{(organizations?.[0]?.address || '').toUpperCase()}</div>
                        <div style={{fontSize: '8px', fontWeight: 'bold'}}>{organizations?.[0]?.city ? `${organizations[0].city.toUpperCase()}, ${organizations[0].state?.toUpperCase() || ''} ${organizations[0].zip_code || ''}` : ''}</div>
                        <div style={{fontSize: '7px', marginTop: '2px'}}>({organizations?.[0]?.phone || claimDetail.provider?.phone || ''})</div>
                        <div style={{fontSize: '6px', fontWeight: 'bold', color: '#CC0000', marginTop: '2px'}}>a. NPI: <span style={{fontSize: '8px', color: '#000'}}>{claimDetail.provider?.npi || ''}</span></div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-2" style={{fontSize: '6px', color: '#666', display: 'flex', justifyContent: 'space-between'}}>
                      <div>NUCC Instruction Manual available at: www.nucc.org</div>
                      <div>APPROVED OMB-0938-1197 FORM 1500 (02-12)</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 justify-between">
                    <div>
                      {claimDetail.claim?.status === 'READY_FOR_BILLING' && (
                        <Button 
                          variant="destructive" 
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this claim? The note will be returned to "Ready for Billing" status and you can recreate the claim if needed.')) {
                              try {
                                await axios.delete(`${API}/claims/${claimDetail.claim.id}`);
                                toast.success('Claim deleted successfully. Note returned to billing queue.');
                                setShowClaimDetail(false);
                                fetchClaimsQueue();
                                fetchNotesReadyForBilling();
                              } catch (error) {
                                console.error('Failed to delete claim:', error);
                                toast.error(error.response?.data?.detail || 'Failed to delete claim');
                              }
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Claim
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowClaimDetail(false)}>
                        Close
                      </Button>
                      <Button variant="outline" disabled>
                        <FileText className="h-4 w-4 mr-2" />
                        Generate PDF
                        <Badge className="ml-2 bg-yellow-600 text-white text-xs">Coming Soon</Badge>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Note Preview Dialog - For billers to preview before creating claim */}
          <PrepareClaimDialog ctx={{
            allClientsForLookup,
            appointments,
            availablePANs,
            billingPreviewClaimId,
            clientDiagnoses,
            clientGroups,
            clientMedications,
            clientVitals,
            cptCodes,
            currentNoteIndex,
            dryRunPreviewData,
            editingServiceLine,
            fetchClaimsQueue,
            fetchClientBilling,
            fetchNoteForReview,
            fetchTreatmentPlanGoals,
            filteredBillingNotes,
            findClientById,
            formTemplates,
            formatDateStringForDisplay,
            formatStatusLabel,
            formatTimeTo12Hour,
            getClientAllInsurances,
            getEffectivePosCodes,
            getSignatureUrl,
            handleClientClick,
            insurances,
            linkedGoals,
            linkingClaimId,
            loadedSignatures,
            loadingDryRun,
            locations,
            navigate,
            navigateToNextBillingNoteNoRemove,
            navigateToPrevBillingNote,
            newNoteFormData,
            noteReviewData,
            openAppointment,
            openNote,
            organizations,
            parseUTCTimestamp,
            posCodes,
            providers,
            savingServiceLine,
            selectedClient,
            selectedNote,
            selectedNoteClients,
            serviceLineEdits,
            serviceTypes,
            sortedFilteredBillingNotes,
            telehealthConsent,
            user,
            users,
            setAppointments,
            setBillingPreviewClaimId,
            setClientDetailTab,
            setCurrentNoteIndex,
            setDryRunPreviewData,
            setEditingMedication,
            setEditingServiceLine,
            setLinkedGoals,
            setLinkingClaimId,
            setLoadedSignatures,
            setLoadingDryRun,
            setManagingInsuranceForClient,
            setNewNoteFormData,
            setNotBillableComment,
            setNotBillableReason,
            setNoteReviewData,
            setNotes,
            setNotesReadyForBilling,
            setSavingServiceLine,
            setSelectedClient,
            setSelectedNoteForReview,
            setServiceLineEdits,
            setShowClientDetail,
            setShowDiagnosisForm,
            setShowInsuranceManagement,
            setShowLinkExistingClaimDialog,
            setShowLinkGoalsDialog,
            setShowMedicationForm,
            setShowNotBillableDialog,
            setShowNoteReviewDialog,
            setShowSendBackDialog,
            setShowSingleLegacyMarkDialog,
            setTelehealthConsent,
            showNoteReviewDialog,
            API,
          }} />

          {/* Single-note "Mark as Billed in Legacy System" — triggered from
              the Prepare Claim dialog. Re-uses the same backend endpoint as
              the bulk flow on the main Billing tab. On success, removes the
              note from the billing queue (mirrors Create Claim post-actions)
              and advances to the next note if there is one. */}
          <LegacyMarkBilledDialog
            open={showSingleLegacyMarkDialog}
            onOpenChange={setShowSingleLegacyMarkDialog}
            noteIds={noteReviewData?.note?.id ? [noteReviewData.note.id] : []}
            onSuccess={(data) => {
              const processedNoteId = noteReviewData?.note?.id;
              if (!processedNoteId) return;
              // Optimistic cleanup — mirrors Create Claim path.
              setNotesReadyForBilling(prev => prev.filter(n => n.id !== processedNoteId));
              setNotes(prev => prev.map(n => n.id === processedNoteId
                ? { ...n, status: 'billed_legacy', is_locked: true }
                : n));
              window.dispatchEvent(new CustomEvent('noteStatusChanged', {
                detail: {
                  noteId: processedNoteId,
                  newStatus: 'billed_legacy',
                  noteData: { ...noteReviewData.note, status: 'billed_legacy', is_locked: true },
                }
              }));
              if (typeof fetchClaimsQueue === 'function') {
                try { fetchClaimsQueue(); } catch (e) { /* ignore */ }
              }
              // Advance to next note in the biller's sorted+filtered view
              // (Mar 2026 — was filteredBillingNotes only, which surfaced random clinicians).
              const nextIdx = Math.min(currentNoteIndex, sortedFilteredBillingNotes.length - 2);
              const nextNote = nextIdx >= 0 ? sortedFilteredBillingNotes.filter(n => n.id !== processedNoteId)[nextIdx] : null;
              if (nextNote) {
                setCurrentNoteIndex(nextIdx);
                setSelectedNoteForReview(nextNote);
                fetchNoteForReview(nextNote.id);
                setBillingPreviewClaimId(null);
                setDryRunPreviewData(null);
              } else {
                setShowNoteReviewDialog(false);
              }
            }}
          />

          {/* "Link to Existing Claim" — recovery dialog for orphaned
              notes whose claim_id is null in the DB but a real claim
              exists.  Loads candidate claims by note_id / appointment_id
              / client+DOS and lets the biller pick the right one. */}
          <LinkExistingClaimDialog
            open={showLinkExistingClaimDialog}
            onOpenChange={setShowLinkExistingClaimDialog}
            noteId={noteReviewData?.note?.id}
            onLinked={(processedNoteId) => {
              // Drop from billing queue and close, like Create Claim does.
              setNotesReadyForBilling(prev => prev.filter(n => n.id !== processedNoteId));
              setShowLinkExistingClaimDialog(false);
              setShowNoteReviewDialog(false);
            }}
          />

          {/* Send Back for Corrections Dialog — extracted to its own
              component. Typing in the reason textarea no longer triggers
              a full-App re-render (was a major source of typing lag). */}
          <NeedsCorrectionsDialog
            open={showSendBackDialog}
            onOpenChange={setShowSendBackDialog}
            note={noteReviewData?.note}
            currentUserId={user?.id}
            onSuccess={(movedNote, correctionReason) => {
              setShowNoteReviewDialog(false);
              // Optimistic: move from ready-for-billing to corrections
              if (movedNote) {
                setNotesReadyForBilling(prev => prev.filter(n => n.id !== movedNote.id));
                setNotesSentForCorrection(prev => {
                  if (prev.some(n => n.id === movedNote.id)) return prev;
                  return [...prev, { ...movedNote, status: 'needs_corrections', needs_corrections_reason: correctionReason }];
                });
                // Also notify BillingContext
                window.dispatchEvent(new CustomEvent('noteStatusChanged', {
                  detail: { noteId: movedNote.id, newStatus: 'needs_corrections', noteData: { ...movedNote, status: 'needs_corrections', needs_corrections_reason: correctionReason } }
                }));
              }
            }}
          />

          {/* Mark Non-Billable Dialog — Feb 2026 (Worcester clinic).
              Terminal billing-side status for notes that won't be
              billed.  Reason is required (matches the
              `NotBillableReason` enum on the backend).  Comment is
              optional free-text.  Any biller / billing-manager /
              admin can mark; same group can restore.  On confirm we
              PATCH the note to status='not_billable' with the reason
              + comment; the backend stamps the audit fields. */}
          <NotBillableDialog ctx={{
            API, notBillableComment, notBillableReason, notBillableTargetNote,
            noteReviewData, refreshNotes, refreshTodos, setNotBillableComment,
            setNotBillableReason, setNotBillableTargetNote, setShowNotBillableDialog,
            setShowNoteReviewDialog, showNotBillableDialog, updateNoteStatusOptimistically,
          }} />

          <Suspense fallback={null}>
            <ClaimViewer
              claimId={billingPreviewClaimId}
              noteId={!billingPreviewClaimId ? noteReviewData?.note?.id : undefined}
              open={!!dryRunPreviewData}
              onClose={() => setDryRunPreviewData(null)}
              previewData={dryRunPreviewData}
            />
          </Suspense>

          {/* VERSION CONFLICT RESOLUTION DIALOG - Blocking dialog when two tabs/devices edit same note */}
          <Dialog 
            open={showVersionConflictDialog} 
            onOpenChange={(open) => {
              // Don't allow closing by clicking outside - must choose an action
              if (!open && versionConflictData) {
                // Only close if user explicitly chose an action
                return;
              }
              setShowVersionConflictDialog(open);
            }}
          >
            <DialogContent className="max-w-2xl" onPointerDownOutside={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="h-5 w-5" />
                  Version Conflict Detected
                </DialogTitle>
                <DialogDescription className="text-base">
                  Someone else (or another browser tab) saved changes to this note while you were editing.
                  Your changes have NOT been saved yet. Please choose how to proceed:
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Conflict Summary */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-amber-800">Your Version:</span>
                      <span className="ml-2">v{versionConflictData?.localVersion || '?'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-amber-800">Server Version:</span>
                      <span className="ml-2">v{versionConflictData?.serverVersion || '?'}</span>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="space-y-3">
                  {/* Option 1: Keep My Changes (Force Save) */}
                  <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-green-700 flex items-center gap-2">
                          <Save className="h-4 w-4" />
                          Keep My Changes
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Overwrite the server version with your current edits. 
                          The other person's changes will be saved in history.
                        </p>
                      </div>
                      <Button
                        onClick={async () => {
                          try {
                            // Force save with the new version
                            const response = await axios.put(`${API}/notes/${versionConflictData.noteId}`, {
                              content: JSON.stringify(versionConflictData.localData),
                              force_version_override: true,
                            });
                            
                            // Update local version
                            if (response.data.current_version) {
                              noteEditingAutosave.setVersion(response.data.current_version);
                            }
                            
                            toast.success('Your changes have been saved');
                            setShowVersionConflictDialog(false);
                            setVersionConflictData(null);
                            setServerVersionContent(null);
                            noteEditingAutosave.clearConflict();
                          } catch (error) {
                            toast.error('Failed to save changes: ' + (error.response?.data?.detail || error.message));
                          }
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Save My Version
                      </Button>
                    </div>
                  </div>
                  
                  {/* Option 2: Load Server Version */}
                  <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-700 flex items-center gap-2">
                          <RefreshCw className="h-4 w-4" />
                          Load Server Version
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Discard your changes and load the latest version from the server.
                          Your current edits will be lost.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            // Re-fetch and reload the note
                            const response = await axios.get(`${API}/notes/${versionConflictData.noteId}`);
                            const serverNote = response.data;
                            
                            // Update the selected note and re-initialize content
                            setSelectedNote(serverNote);
                            if (serverNote.content) {
                              try {
                                const content = JSON.parse(serverNote.content);
                                setEditedNoteContent(content);
                              } catch (e) {
                                setEditedNoteContent({ fields: [] });
                              }
                            }
                            
                            // Update version
                            if (serverNote.current_version) {
                              noteEditingAutosave.setVersion(serverNote.current_version);
                            }
                            
                            toast.info('Loaded server version');
                            setShowVersionConflictDialog(false);
                            setVersionConflictData(null);
                            setServerVersionContent(null);
                            noteEditingAutosave.clearConflict();
                          } catch (error) {
                            toast.error('Failed to load server version');
                          }
                        }}
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        Load Server
                      </Button>
                    </div>
                  </div>
                  
                  {/* Option 3: Copy My Text & Refresh */}
                  <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-purple-700 flex items-center gap-2">
                          <Copy className="h-4 w-4" />
                          Copy My Text & Refresh
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Copy your edits to clipboard, then load the server version.
                          You can manually paste and merge your changes.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            // Copy local data to clipboard
                            const textContent = JSON.stringify(versionConflictData.localData, null, 2);
                            await navigator.clipboard.writeText(textContent);
                            toast.success('Your edits copied to clipboard');
                            
                            // Then load server version
                            const response = await axios.get(`${API}/notes/${versionConflictData.noteId}`);
                            const serverNote = response.data;
                            
                            setSelectedNote(serverNote);
                            if (serverNote.content) {
                              try {
                                const content = JSON.parse(serverNote.content);
                                setEditedNoteContent(content);
                              } catch (e) {
                                setEditedNoteContent({ fields: [] });
                              }
                            }
                            
                            if (serverNote.current_version) {
                              noteEditingAutosave.setVersion(serverNote.current_version);
                            }
                            
                            setShowVersionConflictDialog(false);
                            setVersionConflictData(null);
                            setServerVersionContent(null);
                            noteEditingAutosave.clearConflict();
                          } catch (error) {
                            toast.error('Failed to copy or refresh');
                          }
                        }}
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        Copy & Refresh
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Warning */}
                <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
                  <strong>Note:</strong> Your unsaved changes are preserved in your browser's local storage.
                  If you accidentally choose "Load Server Version", you can recover your edits from the 
                  draft recovery prompt when you reopen the note.
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Signature Advancement Dialog - rendered above Note Detail Dialog */}
          <Dialog open={showSignatureAdvanceDialog} onOpenChange={setShowSignatureAdvanceDialog}>
            <DialogContent className="max-w-md z-[100]" overlayClassName="z-[99]">
              <DialogHeader>
                <DialogTitle>✅ Note Signed Successfully!</DialogTitle>
                <DialogDescription>
                  What would you like to do next with this note?
                </DialogDescription>
              </DialogHeader>
              
              {signedNoteForAdvancement && (
                <div className="space-y-3">
                  {/* Client Info */}
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm font-semibold">
                      {allClientsForLookup.find(c => c.id === signedNoteForAdvancement.client_id)?.first_name}{' '}
                      {allClientsForLookup.find(c => c.id === signedNoteForAdvancement.client_id)?.last_name}
                    </p>
                    <p className="text-xs text-gray-600">{signedNoteForAdvancement.title}</p>
                  </div>
                  
                  {/* Mar 2026 — CPT × insurance restriction notice.
                      When the client's primary insurance blocks this
                      CPT, surface a warning so the user understands
                      why "Ready for Billing" isn't offered. */}
                  {advanceCptBlocked && (
                    <div
                      data-testid="provider-advance-cpt-blocked-banner"
                      className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
                    >
                      <strong>CPT restricted:</strong> This note's CPT code is not
                      billable to the client's primary insurance.  Mark Complete
                      or Mark Non-Billable instead — sending to billing will be
                      rejected.
                    </div>
                  )}
                  
                  {/* Jun 2026 — missing CPT notice.  The #1 cause of
                      "can't mark ready for billing" → providers punt
                      to the supervisor instead of adding the CPT. */}
                  {advanceCptMissing && (
                    <div
                      data-testid="provider-advance-cpt-missing-banner"
                      className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
                    >
                      <strong>Missing CPT code:</strong> This note can't go to
                      billing until a CPT code is added. Choose <em>Keep as
                      Draft</em>, add the CPT code to the note (or its linked
                      appointment), then sign and advance it. Sending it to
                      your supervisor will <em>not</em> add the CPT.
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="space-y-2">
                    {/* Keep as Draft */}
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        setShowSignatureAdvanceDialog(false);
                        setSignedNoteForAdvancement(null);
                        setShowNoteDetail(false);
                      }}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Keep as Draft
                      <span className="ml-auto text-xs text-gray-500">Review later</span>
                    </Button>
                    
                    {/* Send to Client (if requires client signature) */}
                    {(signedNoteForAdvancement.require_client_signature || signedNoteForAdvancement.requires_client_signature) && (
                      <Button
                        className="w-full justify-start bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                          setSelectedNote(signedNoteForAdvancement);
                          setShowSignatureAdvanceDialog(false);
                          setSignedNoteForAdvancement(null);
                          setShowSendToClientDialog(true);
                        }}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Send to Client for Signature
                        <span className="ml-auto text-xs">Required</span>
                      </Button>
                    )}
                    
                    {/* Send to Supervisor - Show when:
                        1. The note requires supervisor signature (routing determined), OR
                        2. The note has an assigned supervisor, OR
                        3. User has a supervisor assigned on their profile
                    */}
                    {(signedNoteForAdvancement.requires_supervisor_signature || 
                      signedNoteForAdvancement.assigned_supervisor ||
                      user?.supervisor_id || 
                      (user?.supervisor_ids && user?.supervisor_ids.length > 0)) && (
                      <Button
                        className={`w-full justify-start ${signedNoteForAdvancement.requires_supervisor_signature ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'}`}
                        onClick={async () => {
                          const noteId = signedNoteForAdvancement.id;
                          // Jun 2026 — when supervision is OPTIONAL and the
                          // CPT is missing, confirm first: the right move is
                          // adding the CPT, not filling the supervisor queue.
                          if (advanceCptMissing && !signedNoteForAdvancement.requires_supervisor_signature) {
                            const ok = await confirmAction(
                              "This note has no CPT code, so it can't be billed even after your supervisor signs. Add the CPT code to the note instead (Keep as Draft → edit → sign). Send to supervisor anyway?",
                              { title: "Missing CPT code", confirmLabel: "Send anyway", cancelLabel: "Go back" }
                            );
                            if (!ok) return;
                          }
                          // Optimistically update status and remove from tasks list
                          updateNoteStatusOptimistically(noteId, 'sent_to_supervisor');
                          try {
                            await axios.put(`${API}/notes/${noteId}`, {
                              status: 'sent_to_supervisor'
                            });
                            toast.success("Note sent to supervisor for review");
                            setShowSignatureAdvanceDialog(false);
                            setSignedNoteForAdvancement(null);
                            setShowNoteDetail(false);
                            refreshNotes(); refreshTodos();
                          } catch (error) {
                            toast.error("Failed to send to supervisor");
                            // On failure, refetch to restore the note to the list
                            refreshNotes(); refreshTodos();
                          }
                        }}
                      >
                        <UserCheck className="h-4 w-4 mr-2" />
                        Send to Supervisor
                        <span className="ml-auto text-xs">{signedNoteForAdvancement.requires_supervisor_signature ? 'Required' : 'Optional'}</span>
                      </Button>
                    )}
                    
                    {/* Mark as Complete (non-billable OR CPT restricted for client's insurance,
                        and client signature is NOT required) */}
                    {(!signedNoteForAdvancement.billable || advanceCptBlocked) && 
                     !signedNoteForAdvancement.require_client_signature &&
                     !signedNoteForAdvancement.requires_client_signature && (
                      <Button
                        className="w-full justify-start bg-green-600 hover:bg-green-700"
                        data-testid="provider-advance-mark-complete"
                        onClick={async () => {
                          const noteId = signedNoteForAdvancement.id;
                          // Optimistically update status and remove from tasks list
                          updateNoteStatusOptimistically(noteId, 'completed');
                          try {
                            await axios.put(`${API}/notes/${noteId}`, {
                              status: 'completed'
                            });
                            toast.success("Note marked as complete");
                            setShowSignatureAdvanceDialog(false);
                            setSignedNoteForAdvancement(null);
                            refreshNotes(); refreshTodos();
                          } catch (error) {
                            toast.error("Failed to mark as complete");
                            // On failure, refetch to restore the note to the list
                            refreshNotes(); refreshTodos();
                          }
                        }}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Mark as Complete
                        <span className="ml-auto text-xs text-gray-200">{advanceCptBlocked ? 'CPT restricted' : 'Non-billable'}</span>
                      </Button>
                    )}
                    
                    {/* Ready for Billing (billable, no supervision required by routing rules,
                        AND CPT is not restricted for the client's primary insurance) */}
                    {signedNoteForAdvancement.billable && 
                     !advanceCptBlocked &&
                     !signedNoteForAdvancement.requires_supervisor_signature &&
                     !signedNoteForAdvancement.require_client_signature &&
                     !signedNoteForAdvancement.requires_client_signature && (
                      <Button
                        className="w-full justify-start bg-teal-600 hover:bg-teal-700"
                        onClick={async () => {
                          const noteId = signedNoteForAdvancement.id;
                          // Optimistically update status and remove from tasks list
                          updateNoteStatusOptimistically(noteId, 'signed_ready_for_billing');
                          try {
                            const resp = await axios.put(`${API}/notes/${noteId}`, {
                              status: 'signed_ready_for_billing'
                            });
                            // Backend may have soft-routed to a
                            // supervisor instead of advancing to
                            // ready-for-billing — read back the actual
                            // status so the toast tells the user what
                            // really happened.  Otherwise admins see
                            // "Note ready for billing" but the note is
                            // sitting in the supervisor's queue.
                            const newStatus = resp?.data?.status;
                            if (newStatus === 'pending_supervisor_signature') {
                              toast.success("Routed to supervisor for signature", {
                                description: "The supervisor was notified and the note will auto-advance to ready-for-billing once they sign.",
                              });
                            } else {
                              toast.success("Note ready for billing");
                            }
                            setShowSignatureAdvanceDialog(false);
                            setSignedNoteForAdvancement(null);
                            setShowNoteDetail(false);
                            refreshNotes(); refreshTodos();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Failed to mark ready for billing");
                            // Close advancement dialog so user can fix the issue in the note
                            setShowSignatureAdvanceDialog(false);
                            setSignedNoteForAdvancement(null);
                            refreshNotes(); refreshTodos();
                          }
                        }}
                      >
                        <DollarSign className="h-4 w-4 mr-2" />
                        Mark Ready for Billing
                        <span className="ml-auto text-xs text-gray-200">Billable</span>
                      </Button>
                    )}

                    {/* Mar 2026 — Mark Non-Billable escape hatch.
                        Available whenever the client signature step
                        isn't pending, so providers can flag duplicate
                        or CPT-restricted notes directly from the
                        post-sign dialog without bouncing to Prepare
                        Claim. */}
                    {!signedNoteForAdvancement.require_client_signature &&
                     !signedNoteForAdvancement.requires_client_signature && (
                      <Button
                        variant="outline"
                        className="w-full justify-start border-slate-500 text-slate-700 hover:bg-slate-50"
                        data-testid="provider-advance-mark-not-billable"
                        onClick={() => {
                          setNotBillableTargetNote(signedNoteForAdvancement);
                          setNotBillableReason(advanceCptBlocked ? 'cpt_restricted_for_insurance' : '');
                          setNotBillableComment('');
                          setShowSignatureAdvanceDialog(false);
                          setSignedNoteForAdvancement(null);
                          setShowNotBillableDialog(true);
                        }}
                      >
                        <Ban className="h-4 w-4 mr-2" />
                        Mark Non-Billable
                        <span className="ml-auto text-xs text-gray-500">Duplicate / Restricted</span>
                      </Button>
                    )}
                  </div>
                  
                  <p className="text-xs text-gray-500 text-center mt-4">
                    You can change the status later from the note details
                  </p>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Medication Management Tab (Medication Managers only - based on user setup) */}
          {user?.is_medication_manager && (
            <TabsContent value="medication-management">
              <Suspense fallback={<div className="flex items-center justify-center p-12"><RefreshCw className="h-6 w-6 animate-spin text-gray-400" /></div>}>
              <MedicationManagementTab
                medicationReferralRequests={medicationReferralRequests}
                waitlistClients={waitlistClients}
                clients={clients}
                medicationManagerUsers={medicationManagerUsers}
                serviceTypes={serviceTypes}
                user={user}
                medWaitlistSearch={medWaitlistSearch}
                setMedWaitlistSearch={setMedWaitlistSearch}
                medWaitlistStatusFilter={medWaitlistStatusFilter}
                setMedWaitlistStatusFilter={setMedWaitlistStatusFilter}
                medWaitlistSort={medWaitlistSort}
                setMedWaitlistSort={setMedWaitlistSort}
                medWaitlistItemsPerSection={medWaitlistItemsPerSection}
                setMedWaitlistItemsPerSection={setMedWaitlistItemsPerSection}
                isRefreshingMedWaitlist={isRefreshingMedWaitlist}
                setIsRefreshingMedWaitlist={setIsRefreshingMedWaitlist}
                showApprovalProviderDropdown={showApprovalProviderDropdown}
                setShowApprovalProviderDropdown={setShowApprovalProviderDropdown}
                fetchMedicationReferralRequests={fetchMedicationReferralRequests}
                fetchWaitlistClients={fetchWaitlistClients}
                fetchData={fetchData}
                formatClientName={formatClientName}
                setSelectedClient={setSelectedClient}
                setClientDetailTab={setClientDetailTab}
                setShowClientDetail={setShowClientDetail}
                setCustomPromptDialog={setCustomPromptDialog}
                setSelectedReferralForDetails={setSelectedReferralForDetails}
                clientNameFormat={clientNameFormat}
                setClientNameFormat={setClientNameFormat}
              />
              </Suspense>
            </TabsContent>
          )}

          {/* Waitlist Tab */}
          <TabsContent value="waitlist">
            <WaitlistTab
              clients={dedicatedWaitlistClients}
              loading={waitlistLoading}
              user={user}
              locations={locations}
              insurances={insurances}
              serviceTypes={serviceTypes}
              languageOptions={languageOptions}
              symptomOptions={symptomOptions}
              providers={providers}
              supervisorsList={supervisorsList}
              setSupervisorsList={setSupervisorsList}
              clientSearch={waitlistSearch}
              setClientSearch={setWaitlistSearch}
              clientStatusFilter={waitlistStatusFilter}
              setClientStatusFilter={setWaitlistStatusFilter}
              clientStatusFilterMode={waitlistStatusFilterMode}
              setClientStatusFilterMode={setWaitlistStatusFilterMode}
              waitlistSortBy={waitlistSortBy}
              setWaitlistSortBy={(v) => {
                setWaitlistSortBy(v);
                try { safeLocalStorageSetItem('waitlistSortBy', v); } catch (e) { /* noop */ }
              }}
              waitlistItemsPerSection={waitlistItemsPerSection}
              setWaitlistItemsPerSection={setWaitlistItemsPerSection}
              waitlistFilters={waitlistFilters}
              setWaitlistFilters={setWaitlistFilters}
              waitlistFilterModes={waitlistFilterModes}
              setWaitlistFilterModes={setWaitlistFilterModes}
              showWaitlistFilters={showWaitlistFilters}
              setShowWaitlistFilters={setShowWaitlistFilters}
              isRefreshingWaitlist={isRefreshingWaitlist}
              setIsRefreshingWaitlist={setIsRefreshingWaitlist}
              lastWaitlistRefresh={lastWaitlistRefresh}
              setLastWaitlistRefresh={setLastWaitlistRefresh}
              showInactiveSection={showInactiveSection}
              setShowInactiveSection={setShowInactiveSection}
              selectedClientForAction={selectedClientForAction}
              setSelectedClientForAction={setSelectedClientForAction}
              showDenyDialog={showDenyDialog}
              setShowDenyDialog={setShowDenyDialog}
              denyReason={denyReason}
              setDenyReason={setDenyReason}
              showRequestInfoDialog={showRequestInfoDialog}
              setShowRequestInfoDialog={setShowRequestInfoDialog}
              infoRequestNotes={infoRequestNotes}
              setInfoRequestNotes={setInfoRequestNotes}
              showActivateWithProviderDialog={showActivateWithProviderDialog}
              setShowActivateWithProviderDialog={setShowActivateWithProviderDialog}
              selectedProviderForActivation={selectedProviderForActivation}
              setSelectedProviderForActivation={setSelectedProviderForActivation}
              clientToActivate={clientToActivate}
              setClientToActivate={setClientToActivate}
              showUrgentEscalationDialog={showUrgentEscalationDialog}
              setShowUrgentEscalationDialog={setShowUrgentEscalationDialog}
              urgentEscalationClient={urgentEscalationClient}
              setUrgentEscalationClient={setUrgentEscalationClient}
              urgentReason={urgentReason}
              setUrgentReason={setUrgentReason}
              selectedSupervisorsForUrgent={selectedSupervisorsForUrgent}
              setSelectedSupervisorsForUrgent={setSelectedSupervisorsForUrgent}
              previewClient={previewClient}
              setPreviewClient={setPreviewClient}
              showClientPreview={showClientPreview}
              setShowClientPreview={setShowClientPreview}
              onWaitlistPreview={handleWaitlistPreview}
              onWaitlistEdit={handleWaitlistEdit}
              fetchData={fetchDedicatedWaitlistClients}
              formatClientName={formatClientName}
              setSelectedClient={setSelectedClient}
              setClientDetailTab={setClientDetailTab}
              setShowClientDetail={setShowClientDetail}
              handleClientClick={handleClientClick}
              setEditingClient={setEditingClient}
              setShowEditClientForm={setShowEditClientForm}
              handleOpenIntakeWizard={handleOpenIntakeWizard}
              clientNameFormat={clientNameFormat}
              setClientNameFormat={setClientNameFormat}
            />
          </TabsContent>

          <TabsContent value="admin">
            <AdminPortalWrapper user={user}>
              {activeTab === 'admin' && (
              <>
              {/* Practice-wide overview — moved here from the general
                  dashboard header (Jun 2026).  Same metrics, same data
                  source (GET /dashboard/stats, tenant-scoped), still
                  collapsible.  Regular users now see appointments +
                  tasks only on their dashboard. */}
              <div className="mb-6" data-testid="admin-overview-summary">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-gray-700">Practice Overview</h2>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setAdminOverviewCollapsed(!adminOverviewCollapsed)}
                    className="h-8 px-3 text-gray-500 hover:text-teal-600"
                    title={adminOverviewCollapsed ? 'Show practice-wide metrics' : 'Hide practice-wide metrics'}
                    data-testid="admin-overview-collapse-toggle"
                  >
                    {adminOverviewCollapsed ? (
                      <>
                        <ChevronDown className="h-4 w-4 mr-1" />
                        <span className="text-xs">Show Overview</span>
                      </>
                    ) : (
                      <>
                        <ChevronUp className="h-4 w-4 mr-1" />
                        <span className="text-xs">Hide Overview</span>
                      </>
                    )}
                  </Button>
                </div>
                {!adminOverviewCollapsed && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="admin-overview-stats-grid">
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center">
                          <Users className="h-8 w-8 text-teal-600" />
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Users</p>
                            <p className="text-2xl font-bold text-gray-900" data-testid="admin-stat-total-users">{stats?.total_users || 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center">
                          <User className="h-8 w-8 text-blue-600" />
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Active Clients</p>
                            <p className="text-2xl font-bold text-gray-900" data-testid="admin-stat-total-clients">{stats?.total_clients || 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center">
                          <CalendarIcon className="h-8 w-8 text-green-600" />
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Appointments</p>
                            <p className="text-2xl font-bold text-gray-900" data-testid="admin-stat-total-appointments">{stats?.total_appointments || 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center">
                          <FileText className="h-8 w-8 text-purple-600" />
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Documents</p>
                            <p className="text-2xl font-bold text-gray-900" data-testid="admin-stat-total-notes">{stats?.total_notes || 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
              <Tabs value={adminInnerTab} onValueChange={setAdminInnerTab} className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="setup" data-testid="admin-setup-tab">
                    <Settings className="h-4 w-4 mr-2" />
                    Setup
                  </TabsTrigger>
                  {tenantToolAccess.tools && (
                  <TabsTrigger value="tools" data-testid="admin-tools-tab">
                    <Wrench className="h-4 w-4 mr-2" />
                    Admin Tools & Security
                  </TabsTrigger>
                  )}
                  <TabsTrigger value="help" data-testid="admin-help-tab">
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Admin Help Center
                  </TabsTrigger>
                  <TabsTrigger value="payroll" data-testid="admin-payroll-tab">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Payroll Config
                  </TabsTrigger>
                  {/* Bulk Appointment Import — its own dedicated tab
                      per Feb 2026 user request: "I actually want this
                      whole thing on the portal not the calendar.
                      Also, it should be its own thing outside of [the
                      existing tabs]." */}
                  {tenantToolAccess.bulkImport && (
                  <TabsTrigger value="bulk-import" data-testid="admin-bulk-import-tab">
                    <Upload className="h-4 w-4 mr-2" />
                    Bulk Import
                  </TabsTrigger>
                  )}
                </TabsList>
                
                {/* Setup Tab - Contains all existing admin cards */}
                <TabsContent value="setup">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* User Management */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg">User Management</CardTitle>
                      {(isSuperAdmin(user)) && (
                        <div className="flex flex-wrap gap-2">
                          <Dialog open={showUserForm} onOpenChange={(open) => {
                            if (!open) {
                              // When closing, reset the form
                              resetUserFormData();
                            }
                            setShowUserForm(open);
                          }}>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm" 
                                className="bg-teal-600 hover:bg-teal-700"
                                onClick={() => {
                                  resetUserFormData();
                                  fetchRoles();
                                }}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Add User
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>{editingUser ? 'Edit User Account' : 'Create New User Account'}</DialogTitle>
                              </DialogHeader>
                              
                              {/* CRITICAL: Last Super Admin Warning Banner - Shows at TOP of dialog */}
                              {editingUser && isSuperAdmin(editingUser) && superAdminCount <= 1 && (
                                <div className="bg-red-100 border-2 border-red-500 rounded-lg p-4 mb-4" data-testid="last-superadmin-warning">
                                  <div className="flex items-center gap-3">
                                    <div className="bg-red-500 rounded-full p-2">
                                      <AlertTriangle className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                      <h3 className="font-bold text-red-800 text-lg">⚠️ LAST SUPER ADMIN ACCOUNT</h3>
                                      <p className="text-red-700 text-sm mt-1">
                                        This is the <strong>only Super Admin</strong> in the system. Role changes, deactivation, and deletion are <strong>blocked</strong> to maintain system access.
                                      </p>
                                      <p className="text-red-600 text-xs mt-2">
                                        To make changes, first create another Super Admin account.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              <form onSubmit={handleCreateUser} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="user_first_name">First Name *</Label>
                              <DebouncedInput
                                id="user_first_name"
                                value={userFormData.first_name}
                                onChange={(e) => setUserFormData(prev => ({...prev, first_name: e.target.value}))}
                                required
                                data-testid="user-first-name"
                              />
                            </div>
                            <div>
                              <Label htmlFor="user_last_name">Last Name *</Label>
                              <DebouncedInput
                                id="user_last_name"
                                value={userFormData.last_name}
                                onChange={(e) => setUserFormData(prev => ({...prev, last_name: e.target.value}))}
                                required
                                data-testid="user-last-name"
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="user_email">Email Address *</Label>
                              <DebouncedInput
                                id="user_email"
                                type="email"
                                value={userFormData.email}
                                onChange={(e) => setUserFormData(prev => ({...prev, email: e.target.value}))}
                                required
                                data-testid="user-email"
                              />
                            </div>
                            <div>
                              <Label htmlFor="user_password">Password {editingUser ? "(Leave blank to keep current)" : ""}</Label>
                              {!editingUser && (
                                <div className="flex items-center gap-2 mt-1 mb-2">
                                  <Checkbox
                                    id="auto_generate_password"
                                    checked={userFormData.autoGeneratePassword}
                                    onCheckedChange={(checked) => setUserFormData(prev => ({
                                      ...prev, 
                                      autoGeneratePassword: checked,
                                      password: checked ? '' : prev.password
                                    }))}
                                  />
                                  <label htmlFor="auto_generate_password" className="text-sm text-gray-600 cursor-pointer">
                                    Auto-generate secure password (recommended)
                                  </label>
                                </div>
                              )}
                              {(!userFormData.autoGeneratePassword || editingUser) && (
                                <div className="relative">
                                  <DebouncedInput
                                    id="user_password"
                                    type={userFormData.showPassword ? "text" : "password"}
                                    value={userFormData.password}
                                    onChange={(e) => setUserFormData(prev => ({...prev, password: e.target.value}))}
                                    required={false}
                                    placeholder={editingUser ? "Leave blank to keep current password" : "Enter password manually"}
                                    className="pr-10"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setUserFormData(prev => ({...prev, showPassword: !prev.showPassword}))}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                  >
                                    {userFormData.showPassword ? (
                                      <EyeOff className="h-4 w-4" />
                                    ) : (
                                      <Eye className="h-4 w-4" />
                                    )}
                                  </button>
                                </div>
                              )}
                              {!editingUser && !userFormData.autoGeneratePassword && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Min 12 characters with uppercase, lowercase, number, and special character (!@#$%^&*)
                                </p>
                              )}
                              {!editingUser && userFormData.autoGeneratePassword && (
                                <p className="text-xs text-green-600 mt-1">
                                  ✓ A secure 14-character password will be auto-generated and sent to the user via welcome email
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* CC Welcome Email Option - Only show when creating new user */}
                          {!editingUser && (
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                              <Label htmlFor="welcome_email_cc" className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                CC Welcome Email To (Optional)
                              </Label>
                              <p className="text-xs text-blue-600 dark:text-blue-300 mb-2">
                                Select an admin to receive a copy of the welcome email for verification
                              </p>
                              <Select
                                value={userFormData.welcome_email_cc || 'none'}
                                onValueChange={(value) => setUserFormData(prev => ({...prev, welcome_email_cc: value === 'none' ? '' : value}))}
                              >
                                <SelectTrigger id="welcome_email_cc" className="bg-white dark:bg-gray-800">
                                  <SelectValue placeholder="Select admin to CC..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No CC</SelectItem>
                                  {(users || [])
                                    .filter(u => u.role === 'super admin' || u.role === 'admin' || isSuperAdmin(u))
                                    .map(admin => (
                                      <SelectItem key={admin.id} value={admin.email}>
                                        {admin.full_name || `${admin.first_name} ${admin.last_name}`} ({admin.email})
                                      </SelectItem>
                                    ))
                                  }
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="user_phone">Phone Number</Label>
                              <DebouncedInput
                                id="user_phone"
                                type="tel"
                                value={userFormData.phone}
                                onChange={(e) => setUserFormData(prev => ({...prev, phone: e.target.value}))}
                                data-testid="user-phone"
                                placeholder="(555) 123-4567"
                              />
                            </div>
                            <div>
                              <Label htmlFor="user_title">Title</Label>
                              <DebouncedInput
                                id="user_title"
                                value={userFormData.title}
                                onChange={(e) => setUserFormData(prev => ({...prev, title: e.target.value}))}
                                data-testid="user-title"
                                placeholder="e.g., Licensed Clinical Social Worker"
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="user_department">Department</Label>
                              <DebouncedInput
                                id="user_department"
                                value={userFormData.department}
                                onChange={(e) => setUserFormData(prev => ({...prev, department: e.target.value}))}
                                data-testid="user-department"
                                placeholder="e.g., Mental Health Services"
                              />
                            </div>
                            <div>
                              <Label htmlFor="user_credentials">Credentials</Label>
                              <DebouncedInput
                                id="user_credentials"
                                value={userFormData.credentials}
                                onChange={(e) => setUserFormData(prev => ({...prev, credentials: e.target.value}))}
                                data-testid="user-credentials"
                                placeholder="e.g., LCSW, PhD, MD"
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="user_role">Role *</Label>
                              <Select 
                                value={userFormData.role_id} 
                                onValueChange={(value) => {
                                  // Check if this is the last super admin trying to change role
                                  if (editingUser && isSuperAdmin(editingUser) && superAdminCount <= 1) {
                                    toast.error("Cannot change role of the last remaining super admin.");
                                    return;
                                  }
                                  // Find the selected role
                                  const selectedRole = (roles || []).find(r => r.id === value);
                                  // Automatically set is_provider=true for clinical roles
                                  const isClinicalRole = selectedRole && ['Provider', 'Supervisor', 'Intern'].includes(selectedRole.name);
                                  setUserFormData(prev => ({
                                    ...prev, 
                                    role_id: value,
                                    is_provider: isClinicalRole ? true : prev.is_provider
                                  }));
                                }}
                                disabled={editingUser && isSuperAdmin(editingUser) && superAdminCount <= 1}
                              >
                                <SelectTrigger data-testid="user-role" disabled={editingUser && isSuperAdmin(editingUser) && superAdminCount <= 1}>
                                  <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(roles || []).map(role => (
                                    <SelectItem key={role.id} value={role.id}>
                                      {role.name}
                                      {role.is_default && ' (Default)'}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label htmlFor="employee_id">Employee ID</Label>
                              <DebouncedInput
                                id="employee_id"
                                value={userFormData.employee_id || ''}
                                onChange={(e) => setUserFormData(prev => ({...prev, employee_id: e.target.value}))}
                                placeholder="e.g., 1001"
                                data-testid="user-employee-id-input"
                              />
                              <p className="text-xs text-gray-500 mt-1">For payroll</p>
                            </div>
                          </div>
                          
                          {/* License Status and Medicare Credentialing */}
                          {userFormData.role_id && (
                            <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <h3 className="font-semibold text-sm text-blue-900">Credentialing & Licensing</h3>
                              <div className="grid grid-cols-2 gap-4">
                                {/* Only show "Also a Provider" checkbox for non-clinical roles */}
                                {(() => {
                                  const selectedRole = (roles || []).find(r => r.id === userFormData.role_id);
                                  const isClinicalRole = selectedRole && ['Provider', 'Supervisor', 'Intern'].includes(selectedRole.name);
                                  return !isClinicalRole;
                                })() && (
                                  <div>
                                    <Label htmlFor="is_provider" className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        id="is_provider"
                                        checked={userFormData.is_provider || false}
                                        onChange={(e) => setUserFormData(prev => ({...prev, is_provider: e.target.checked}))}
                                        className="h-4 w-4 rounded border-gray-300"
                                      />
                                      <span className="font-semibold">Also a Provider</span>
                                    </Label>
                                    <p className="text-xs text-gray-600 mt-1">
                                      Check this if the user provides direct clinical services (required to create notes).
                                    </p>
                                  </div>
                                )}
                                
                                {/* Show license/credential fields if role is clinical OR if is_provider is checked */}
                                {(() => {
                                  const selectedRole = (roles || []).find(r => r.id === userFormData.role_id);
                                  const isClinicalRole = selectedRole && ['Provider', 'Supervisor', 'Intern'].includes(selectedRole.name);
                                  return isClinicalRole || userFormData.is_provider;
                                })() && (
                                  <div className="space-y-4 pl-6 border-l-2 border-teal-200">
                                    <p className="text-sm font-semibold text-teal-700">Provider Credentials</p>
                                    
                                    <div>
                                      <Label htmlFor="npi">NPI (National Provider Identifier)</Label>
                                      <DebouncedInput
                                        id="npi"
                                        value={userFormData.npi || ''}
                                        onChange={(e) => setUserFormData(prev => ({...prev, npi: e.target.value}))}
                                        placeholder="10-digit NPI number"
                                        maxLength="10"
                                      />
                                      <p className="text-xs text-gray-600 mt-1">
                                        Required for billing and insurance claims
                                      </p>
                                    </div>
                                    
                                    <div>
                                      <Label htmlFor="taxonomy_code">Taxonomy Code</Label>
                                      <DebouncedInput
                                        id="taxonomy_code"
                                        value={userFormData.taxonomy_code || ''}
                                        onChange={(e) => setUserFormData(prev => ({...prev, taxonomy_code: e.target.value}))}
                                        placeholder="e.g., 207R00000X"
                                        maxLength="10"
                                      />
                                      <p className="text-xs text-gray-600 mt-1">
                                        Provider specialty code for claims submission (Box 24i on CMS-1500)
                                      </p>
                                    </div>
                                    
                                    <div>
                                      <Label htmlFor="license_status">License Status *</Label>
                                      <Select
                                        value={userFormData.license_status || 'Licensed'}
                                        onValueChange={(value) => setUserFormData(prev => ({...prev, license_status: value}))}
                                      >
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="Licensed">Licensed</SelectItem>
                                          <SelectItem value="Provisionally Licensed">Provisionally Licensed</SelectItem>
                                          <SelectItem value="Intern">Intern</SelectItem>
                                          <SelectItem value="Unlicensed">Unlicensed</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <p className="text-xs text-gray-600 mt-1">
                                        Interns/Unlicensed require supervisor signatures
                                      </p>
                                    </div>
                                    
                                    <div>
                                      <Label htmlFor="medicare_approved" className="flex items-center gap-2">
                                        Medicare Credentialed
                                        <input
                                          type="checkbox"
                                          id="medicare_approved"
                                          checked={userFormData.medicare_approved || false}
                                          onChange={(e) => setUserFormData(prev => ({...prev, medicare_approved: e.target.checked}))}
                                          className="h-4 w-4 rounded border-gray-300"
                                        />
                                      </Label>
                                      <p className="text-xs text-gray-600 mt-1">
                                        Can bill Medicare independently without supervisor
                                      </p>
                                    </div>
                                    
                                    <div>
                                      <Label htmlFor="is_prescriber" className="flex items-center gap-2">
                                        Prescribing Provider
                                        <input
                                          type="checkbox"
                                          id="is_prescriber"
                                          checked={userFormData.is_prescriber || false}
                                          onChange={(e) => setUserFormData(prev => ({...prev, is_prescriber: e.target.checked}))}
                                          className="h-4 w-4 rounded border-gray-300"
                                        />
                                      </Label>
                                      <p className="text-xs text-gray-600 mt-1">
                                        Can prescribe medications (PNP, NP, MD, DO, PA, PMHNP) - filters med management dropdowns
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Provider-specific fields - Show for all roles for now */}
                          {userFormData.role_id && (
                            <>
                              {/* Show assignments based on user type */}
                              {editingUser && (
                                <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-md p-4 space-y-3">
                                  <div>
                                    <h4 className="font-semibold text-sm text-blue-900 mb-2">Current Assignments:</h4>
                                    
                                    {/* Show who they supervise - For ALL users */}
                                    <div className="mb-2">
                                      <span className="text-sm font-medium text-gray-700">Supervising: </span>
                                      {(() => {
                                        const supervisees = providers.filter(p => 
                                          p.supervisor_ids && p.supervisor_ids.includes(editingUser.id)
                                        );
                                        return supervisees.length > 0 ? (
                                          <span className="text-sm text-gray-600">
                                            {supervisees.map(s => s.full_name).join(', ')}
                                          </span>
                                        ) : (
                                          <span className="text-sm text-gray-500 italic">None</span>
                                        );
                                      })()}
                                    </div>
                                    
                                    {/* Show biller and scheduler assignments - Only for Office staff and Admins */}
                                    {(editingUser.role?.toLowerCase() === 'office' || 
                                      isSuperAdmin(editingUser)) && (
                                      <>
                                        {/* Show who they're a biller for */}
                                        <div className="mb-2">
                                          <span className="text-sm font-medium text-gray-700">Assigned as Biller for: </span>
                                          {(() => {
                                            const billerFor = providers.filter(p => 
                                              p.assigned_billers && p.assigned_billers.includes(editingUser.id)
                                            );
                                            return billerFor.length > 0 ? (
                                              <span className="text-sm text-gray-600">
                                                {billerFor.map(s => s.full_name).join(', ')}
                                              </span>
                                            ) : (
                                              <span className="text-sm text-gray-500 italic">None</span>
                                            );
                                          })()}
                                        </div>
                                        
                                        {/* Show who they're a scheduler for */}
                                        <div>
                                          <span className="text-sm font-medium text-gray-700">Assigned as Scheduler for: </span>
                                          {(() => {
                                            const schedulerFor = providers.filter(p => 
                                              editingUser.scheduling_for && editingUser.scheduling_for.includes(p.id)
                                            );
                                            return schedulerFor.length > 0 ? (
                                              <span className="text-sm text-gray-600">
                                                {schedulerFor.map(s => s.full_name).join(', ')}
                                              </span>
                                            ) : (
                                              <span className="text-sm text-gray-500 italic">None</span>
                                            );
                                          })()}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="supervisors">Supervisors (Multiple)</Label>
                                <div className="space-y-2">
                                  <Select
                                    onValueChange={(value) => {
                                      if (value !== 'none') {
                                        // Add supervisor if not already selected
                                        if (!(userFormData.supervisor_ids || []).includes(value)) {
                                          setUserFormData(prev => ({
                                            ...prev,
                                            supervisor_ids: [...(prev.supervisor_ids || []), value]
                                          }));
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Click to add supervisors" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">Select a supervisor</SelectItem>
                                      {(users || [])
                                        .filter(u => {
                                          // Filter for users with Supervisor role only
                                          const roleObj = (roles || []).find(r => r.id === u.role_id);
                                          const roleName = roleObj?.name?.toLowerCase() || '';
                                          // Include: Supervisors (by role name) or Super Admins
                                          return (
                                            isSuperAdmin(u) || 
                                            roleName.includes('supervisor')
                                          );
                                        })
                                        .filter(u => !(userFormData.supervisor_ids || []).includes(u.id))
                                        .map(supervisor => (
                                          <SelectItem key={supervisor.id} value={supervisor.id}>
                                            {supervisor.first_name} {supervisor.last_name}
                                          </SelectItem>
                                        ))
                                      }
                                    </SelectContent>
                                  </Select>
                                  
                                  {/* Show selected supervisors as removable badges */}
                                  {(userFormData.supervisor_ids || []).length > 0 ? (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {(userFormData.supervisor_ids || []).map(supId => {
                                        const supervisor = (users || []).find(p => p.id === supId);
                                        return supervisor ? (
                                          <Badge key={supId} variant="secondary" className="flex items-center gap-1">
                                            {supervisor.first_name} {supervisor.last_name}
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setUserFormData(prev => ({
                                                  ...prev,
                                                  supervisor_ids: (prev.supervisor_ids || []).filter(id => id !== supId)
                                                }));
                                              }}
                                              className="ml-1 hover:bg-gray-300 rounded-full"
                                            >
                                              ×
                                            </button>
                                          </Badge>
                                        ) : null;
                                      })}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-500">No supervisors selected</p>
                                  )}
                                </div>
                              </div>
                              <div>
                                <Label htmlFor="billers">Assigned Billers</Label>
                                <div className="space-y-2">
                                  <Select
                                    onValueChange={(value) => {
                                      if (value !== 'none') {
                                        // Add biller if not already selected
                                        if (!userFormData.assigned_billers.includes(value)) {
                                          setUserFormData(prev => ({
                                            ...prev,
                                            assigned_billers: [...(prev.assigned_billers || []), value]
                                          }));
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Click to add billers" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">Select a biller</SelectItem>
                                      {(users || []).filter(u => {
                                        const roleLower = (u.role || '').toLowerCase();
                                        return (
                                          u.is_biller === true ||
                                          u.is_billing_manager === true ||
                                          isSuperAdmin(u) ||
                                          roleLower === 'office' ||
                                          roleLower === 'biller'
                                        );
                                      }).map(biller => (
                                        <SelectItem key={biller.id} value={biller.id}>
                                          {biller.full_name} ({biller.role}{biller.is_biller ? ' - Biller' : ''}{biller.is_billing_manager ? ' - Billing Mgr' : ''})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  
                                  {/* Show selected billers as removable badges */}
                                  {userFormData.assigned_billers && userFormData.assigned_billers.length > 0 ? (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {userFormData.assigned_billers.map(billerId => {
                                        const biller = users.find(u => u.id === billerId);
                                        return biller ? (
                                          <Badge key={billerId} variant="secondary" className="flex items-center gap-1">
                                            {biller.full_name}
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setUserFormData(prev => ({
                                                  ...prev,
                                                  assigned_billers: prev.assigned_billers.filter(id => id !== billerId)
                                                }));
                                              }}
                                              className="ml-1 hover:bg-gray-300 rounded-full"
                                            >
                                              ×
                                            </button>
                                          </Badge>
                                        ) : null;
                                      })}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-500">No billers selected</p>
                                  )}
                                </div>
                              </div>
                              
                              {/* CPT Code Assignment - Only for Providers */}
                              {userFormData.is_provider && (
                              <div className="col-span-2 space-y-4 p-4 border rounded-lg bg-gray-50">
                                <h4 className="font-medium text-sm">Assign CPT Codes</h4>
                                
                                {/* Bundle Assignment */}
                                <div>
                                  <Label className="text-sm">CPT Bundles</Label>
                                  <div className="max-h-32 overflow-y-auto p-2 border rounded bg-white space-y-1">
                                    {cptBundles?.map(bundle => (
                                      <label key={bundle.id} className="flex items-center space-x-2 text-sm">
                                        <input
                                          type="checkbox"
                                          checked={userFormData.assigned_cpt_bundles?.includes(bundle.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setUserFormData(prev => ({
                                                ...prev,
                                                assigned_cpt_bundles: [...(prev.assigned_cpt_bundles || []), bundle.id]
                                              }));
                                            } else {
                                              setUserFormData(prev => ({
                                                ...prev,
                                                assigned_cpt_bundles: (prev.assigned_cpt_bundles || []).filter(id => id !== bundle.id)
                                              }));
                                            }
                                          }}
                                          className="rounded"
                                        />
                                        <span>{bundle.name} ({bundle.cpt_code_ids?.length || 0} codes)</span>
                                      </label>
                                    ))}
                                    {(!cptBundles || cptBundles.length === 0) && (
                                      <p className="text-xs text-gray-500">No bundles available</p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Individual CPT Code Assignment */}
                                <div>
                                  <Label className="text-sm">Individual CPT Codes</Label>
                                  <div className="max-h-32 overflow-y-auto p-2 border rounded bg-white space-y-1">
                                    {cptCodes?.map(code => (
                                      <label key={code.id} className="flex items-center space-x-2 text-sm">
                                        <input
                                          type="checkbox"
                                          checked={userFormData.assigned_cpt_codes?.includes(code.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setUserFormData(prev => ({
                                                ...prev,
                                                assigned_cpt_codes: [...(prev.assigned_cpt_codes || []), code.id]
                                              }));
                                            } else {
                                              setUserFormData(prev => ({
                                                ...prev,
                                                assigned_cpt_codes: (prev.assigned_cpt_codes || []).filter(id => id !== code.id)
                                              }));
                                            }
                                          }}
                                          className="rounded"
                                        />
                                        <span>{code.code} - {code.display_name || code.description.substring(0, 30)}</span>
                                      </label>
                                    ))}
                                    {(!cptCodes || cptCodes.length === 0) && (
                                      <p className="text-xs text-gray-500">No CPT codes available</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              )}
                              
                              {/* Location and POS Code Assignment - Only for Providers */}
                              {userFormData.is_provider && (
                              <div className="col-span-2 space-y-4 p-4 border rounded-lg bg-blue-50">
                                <h4 className="font-medium text-sm">Assign Locations & POS Codes</h4>
                                
                                {/* Location Assignment */}
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <Label className="text-sm">Locations</Label>
                                    <div className="space-x-2">
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 text-xs"
                                        onClick={() => {
                                          const allLocationNames = (locations || []).map(loc => loc.name);
                                          setUserFormData(prev => ({...prev, assigned_locations: allLocationNames}));
                                        }}
                                      >
                                        Select All
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 text-xs"
                                        onClick={() => setUserFormData(prev => ({...prev, assigned_locations: []}))}
                                      >
                                        Clear All
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="max-h-32 overflow-y-auto p-2 border rounded bg-white space-y-1">
                                    {[...(locations || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((location) => (
                                      <label key={location.id} className="flex items-center space-x-2 text-sm">
                                        <input
                                          type="checkbox"
                                          checked={userFormData.assigned_locations?.includes(location.name)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setUserFormData(prev => ({
                                                ...prev,
                                                assigned_locations: [...(prev.assigned_locations || []), location.name]
                                              }));
                                            } else {
                                              setUserFormData(prev => ({
                                                ...prev,
                                                assigned_locations: (prev.assigned_locations || []).filter(loc => loc !== location.name)
                                              }));
                                            }
                                          }}
                                          className="rounded"
                                        />
                                        <span>{location.name}</span>
                                      </label>
                                    ))}
                                    {(!locations || locations.length === 0) && (
                                      <p className="text-xs text-gray-500">No locations configured. Add locations in Settings → Manage Locations</p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* POS Code Assignment */}
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <Label className="text-sm">POS Codes</Label>
                                    <div className="space-x-2">
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 text-xs"
                                        onClick={() => {
                                          const allPosCodes = getEffectivePosCodes(organizations, posCodes).map(pos => pos.code);
                                          setUserFormData(prev => ({...prev, assigned_pos_codes: allPosCodes}));
                                        }}
                                      >
                                        Select All
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 text-xs"
                                        onClick={() => setUserFormData(prev => ({...prev, assigned_pos_codes: []}))}
                                      >
                                        Clear All
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="max-h-32 overflow-y-auto p-2 border rounded bg-white space-y-1">
                                    {getEffectivePosCodes(organizations, posCodes).map((pos, idx) => (
                                      <label key={idx} className="flex items-center space-x-2 text-sm">
                                        <input
                                          type="checkbox"
                                          checked={userFormData.assigned_pos_codes?.includes(pos.code)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setUserFormData(prev => ({
                                                ...prev,
                                                assigned_pos_codes: [...(prev.assigned_pos_codes || []), pos.code]
                                              }));
                                            } else {
                                              setUserFormData(prev => ({
                                                ...prev,
                                                assigned_pos_codes: (prev.assigned_pos_codes || []).filter(code => code !== pos.code)
                                              }));
                                            }
                                          }}
                                          className="rounded"
                                        />
                                        <span>{pos.code} - {pos.description}</span>
                                      </label>
                                    ))}
                                    {getEffectivePosCodes(organizations, posCodes).length === 0 && (
                                      <p className="text-xs text-gray-500">No POS codes configured. Add POS codes in Settings → Manage POS Codes</p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Default Location + POS Combo */}
                                {(userFormData.assigned_locations?.length > 0 && userFormData.assigned_pos_codes?.length > 0) && (
                                  <div className="pt-4 border-t border-blue-200">
                                    <Label className="text-sm font-semibold text-blue-900">Default Location & POS</Label>
                                    <p className="text-xs text-gray-600 mb-2">Auto-selected when scheduling appointments for this provider</p>
                                    <Select
                                      value={userFormData.default_location && userFormData.default_pos_code ? `${userFormData.default_pos_code}|${userFormData.default_location}` : ''}
                                      onValueChange={(value) => {
                                        if (value) {
                                          const [pos_code, location] = value.split('|');
                                          setUserFormData(prev => ({...prev, default_location: location, default_pos_code: pos_code}));
                                        } else {
                                          setUserFormData(prev => ({...prev, default_location: null, default_pos_code: null}));
                                        }
                                      }}
                                    >
                                      <SelectTrigger className="bg-white">
                                        <SelectValue placeholder="No default set (optional)" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">No Default</SelectItem>
                                        {(() => {
                                          const combinations = [];
                                          const allPosCodes = getEffectivePosCodes(organizations, posCodes);
                                          userFormData.assigned_pos_codes?.forEach(posCode => {
                                            const posInfo = allPosCodes.find(p => p.code === posCode);
                                            if (posInfo) {
                                              userFormData.assigned_locations?.forEach(location => {
                                                combinations.push({
                                                  value: `${posCode}|${location}`,
                                                  label: `${posCode} - ${posInfo.description} at ${location}`
                                                });
                                              });
                                            }
                                          });
                                          return combinations.map(combo => (
                                            <SelectItem key={combo.value} value={combo.value}>
                                              {combo.label}
                                            </SelectItem>
                                          ));
                                        })()}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                                
                                {/* Default Telehealth Modality */}
                                {userFormData.assigned_pos_codes?.some(code => code === '02' || code === '10') && (
                                  <div className="pt-4 border-t border-blue-200">
                                    <Label className="text-sm font-semibold text-blue-900">Default Telehealth Modality</Label>
                                    <p className="text-xs text-gray-600 mb-2">Auto-selected when scheduling telehealth appointments (POS 02 or 10)</p>
                                    <Select
                                      value={userFormData.default_telehealth_modality || ''}
                                      onValueChange={(value) => {
                                        setUserFormData(prev => ({...prev, default_telehealth_modality: value === 'none' ? null : value}));
                                      }}
                                    >
                                      <SelectTrigger className="bg-white">
                                        <SelectValue placeholder="No default set (optional)" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">No Default</SelectItem>
                                        <SelectItem value="audio_visual">🎥 Audio/Visual (Video Call)</SelectItem>
                                        <SelectItem value="audio_only">📞 Audio Only (Phone Call)</SelectItem>
                                      </SelectContent>
                                    </Select>

                                    {/* Feb 2026 — Custom Telehealth Room URL.
                                        When set, this provider's telehealth appointments
                                        use the URL below INSTEAD of an auto-generated
                                        Google Meet link.  Their appointment reminders +
                                        portal both link to this URL.  Use for Doxy.me /
                                        personal Zoom / persistent rooms. */}
                                    <div className="mt-4">
                                      <Label className="text-sm font-semibold text-blue-900">
                                        Custom Telehealth Room URL
                                      </Label>
                                      <p className="text-xs text-gray-600 mb-2">
                                        Optional. If set, this URL is used on every telehealth appointment for this provider
                                        (Doxy.me, personal Zoom, etc.), and Google Meet creation is skipped.
                                      </p>
                                      <Input
                                        type="url"
                                        placeholder="https://doxy.me/dr-smith"
                                        value={userFormData.custom_telehealth_url || ''}
                                        onChange={(e) => setUserFormData(prev => ({
                                          ...prev,
                                          custom_telehealth_url: e.target.value,
                                        }))}
                                        onBlur={(e) => {
                                          // Light validation — must look like an HTTP(S) URL.
                                          // Empty/whitespace strings are treated as "unset".
                                          const v = (e.target.value || '').trim();
                                          if (v && !/^https?:\/\//i.test(v)) {
                                            toast.error('Telehealth URL must start with http:// or https://');
                                          }
                                          setUserFormData(prev => ({
                                            ...prev,
                                            custom_telehealth_url: v || null,
                                          }));
                                        }}
                                        className="bg-white"
                                        data-testid="user-custom-telehealth-url-input"
                                      />
                                    </div>

                                    {/* Persistent Google Meet Room (Feb 2026).
                                        Provider clicks "Generate" → backend
                                        hits Meet REST API v2 to create a
                                        reusable space → URL is stored on the
                                        user record AND mirrored into
                                        custom_telehealth_url so the existing
                                        appointment flow picks it up.  Refresh
                                        deletes + recreates (new link).
                                        Remove deletes the Google space and
                                        clears the local fields. */}
                                    {editingUser && (
                                      <div className="mt-4 p-3 rounded-md border border-emerald-200 bg-emerald-50/60">
                                        <div className="flex items-center justify-between mb-2">
                                          <Label className="text-sm font-semibold text-emerald-900">
                                            Persistent Google Meet Room
                                          </Label>
                                          {userFormData.meet_space_uri && (
                                            <span className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold bg-emerald-100 px-2 py-0.5 rounded">
                                              Active
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-600 mb-2">
                                          Generate a single Google Meet room this provider reuses on every telehealth appointment. Restricted access + waiting room — only invitees join without knocking. The link is saved in the Custom Telehealth URL field above.
                                        </p>
                                        {userFormData.meet_space_uri ? (
                                          <>
                                            <div className="flex items-center gap-2 mb-2">
                                              <Input
                                                type="text"
                                                readOnly
                                                value={userFormData.meet_space_uri}
                                                className="bg-white text-xs"
                                                data-testid="user-meet-space-uri"
                                                onFocus={(e) => e.target.select()}
                                              />
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                data-testid="user-meet-space-copy"
                                                onClick={() => {
                                                  navigator.clipboard.writeText(userFormData.meet_space_uri);
                                                  toast.success('Meet link copied');
                                                }}
                                              >
                                                Copy
                                              </Button>
                                            </div>
                                            <div className="flex gap-2">
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="border-amber-300 text-amber-800 hover:bg-amber-50"
                                                disabled={!!userFormData._meet_space_loading}
                                                data-testid="user-meet-space-refresh"
                                                onClick={async () => {
                                                  const ok = await confirmAction(
                                                    'Refreshing will permanently invalidate the current Meet link and generate a brand-new one. Anyone who has the old link will not be able to join future appointments. Continue?',
                                                    { title: 'Refresh persistent Meet room?', confirmLabel: 'Refresh', destructive: true }
                                                  );
                                                  if (!ok) return;
                                                  setUserFormData(prev => ({ ...prev, _meet_space_loading: true }));
                                                  try {
                                                    const res = await axios.post(`${API}/users/${editingUser.id}/meet-space/refresh`);
                                                    setUserFormData(prev => ({
                                                      ...prev,
                                                      meet_space_uri: res.data.meet_space_uri,
                                                      meet_space_resource_name: res.data.meet_space_resource_name,
                                                      meet_space_created_at: res.data.meet_space_created_at,
                                                      custom_telehealth_url: res.data.meet_space_uri,
                                                      _meet_space_loading: false,
                                                    }));
                                                    toast.success('New Meet room generated — old link is dead');
                                                  } catch (err) {
                                                    setUserFormData(prev => ({ ...prev, _meet_space_loading: false }));
                                                    toast.error(err.response?.data?.detail || 'Failed to refresh Meet room');
                                                  }
                                                }}
                                              >
                                                {userFormData._meet_space_loading ? 'Working…' : 'Refresh'}
                                              </Button>
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="border-red-300 text-red-700 hover:bg-red-50"
                                                disabled={!!userFormData._meet_space_loading}
                                                data-testid="user-meet-space-remove"
                                                onClick={async () => {
                                                  const ok = await confirmAction(
                                                    'Remove this persistent Meet room? The link will be invalidated and future appointments will fall back to per-appointment Meet links (or your manually set Telehealth URL).',
                                                    { title: 'Remove persistent Meet room?', confirmLabel: 'Remove', destructive: true }
                                                  );
                                                  if (!ok) return;
                                                  setUserFormData(prev => ({ ...prev, _meet_space_loading: true }));
                                                  try {
                                                    await axios.delete(`${API}/users/${editingUser.id}/meet-space`);
                                                    setUserFormData(prev => ({
                                                      ...prev,
                                                      meet_space_uri: null,
                                                      meet_space_resource_name: null,
                                                      meet_space_created_at: null,
                                                      // Backend only clears custom_telehealth_url if it was the
                                                      // Meet URI — mirror that locally.
                                                      custom_telehealth_url: prev.custom_telehealth_url === prev.meet_space_uri ? null : prev.custom_telehealth_url,
                                                      _meet_space_loading: false,
                                                    }));
                                                    toast.success('Persistent Meet room removed');
                                                  } catch (err) {
                                                    setUserFormData(prev => ({ ...prev, _meet_space_loading: false }));
                                                    toast.error(err.response?.data?.detail || 'Failed to remove Meet room');
                                                  }
                                                }}
                                              >
                                                Remove
                                              </Button>
                                            </div>
                                          </>
                                        ) : (
                                          <Button
                                            type="button"
                                            size="sm"
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                            disabled={!!userFormData._meet_space_loading}
                                            data-testid="user-meet-space-generate"
                                            onClick={async () => {
                                              setUserFormData(prev => ({ ...prev, _meet_space_loading: true }));
                                              try {
                                                const res = await axios.post(`${API}/users/${editingUser.id}/meet-space/generate`);
                                                setUserFormData(prev => ({
                                                  ...prev,
                                                  meet_space_uri: res.data.meet_space_uri,
                                                  meet_space_resource_name: res.data.meet_space_resource_name,
                                                  meet_space_created_at: res.data.meet_space_created_at,
                                                  custom_telehealth_url: res.data.meet_space_uri,
                                                  _meet_space_loading: false,
                                                }));
                                                toast.success('Persistent Meet room created — share the link with confidence');
                                              } catch (err) {
                                                setUserFormData(prev => ({ ...prev, _meet_space_loading: false }));
                                                toast.error(err.response?.data?.detail || 'Failed to generate Meet room');
                                              }
                                            }}
                                          >
                                            {userFormData._meet_space_loading ? 'Generating…' : 'Generate persistent Meet room'}
                                          </Button>
                                        )}
                                        {!editingUser?.email && (
                                          <p className="text-xs text-amber-700 mt-2">⚠️ Save the user first — Meet generation needs the email on file.</p>
                                        )}
                                      </div>
                                    )}

                                    {/* Feb 2026 — Disable Appointment Reminders.
                                        Admin-managed master kill-switch.  When
                                        enabled, the automated reminder scheduler
                                        skips ALL channels (email / SMS / in-app /
                                        voice) for ANY appointment where THIS user
                                        is the rendering provider — regardless of
                                        client-level opt-ins.  Used when a provider
                                        handles reminders themselves out-of-band,
                                        is on leave, etc.  Re-enable by unchecking. */}
                                    <div className="mt-4 p-3 rounded-md border border-amber-200 bg-amber-50/60">
                                      <label className="flex items-start gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          className="mt-0.5 h-4 w-4"
                                          checked={!!userFormData.disable_appointment_reminders}
                                          onChange={(e) => setUserFormData(prev => ({
                                            ...prev,
                                            disable_appointment_reminders: e.target.checked,
                                          }))}
                                          data-testid="user-disable-appointment-reminders"
                                        />
                                        <span className="flex-1">
                                          <span className="text-sm font-semibold text-amber-900">
                                            Disable all appointment reminders for this provider
                                          </span>
                                          <p className="text-xs text-amber-800 mt-0.5">
                                            When checked, no automated reminders (email, SMS, in-app, voice) will be sent for any appointment where this provider is the rendering clinician — even if individual clients have reminders enabled. Toggle off any time to resume reminders.
                                          </p>
                                        </span>
                                      </label>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Default Note Template */}
                                <div className="pt-4 border-t border-blue-200">
                                  <Label className="text-sm font-semibold text-blue-900">Default Note Template</Label>
                                  <p className="text-xs text-gray-600 mb-2">Auto-selected when creating notes (can be changed)</p>
                                  <Select
                                    value={userFormData.default_template_id || ''}
                                    onValueChange={(value) => {
                                      setUserFormData(prev => ({...prev, default_template_id: value === 'none' ? null : value}));
                                    }}
                                  >
                                    <SelectTrigger className="bg-white">
                                      <SelectValue placeholder="No default set (optional)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">No Default</SelectItem>
                                      {(formTemplates || [])
                                        .filter(t => t.is_active !== false && t.available_in_quick_create !== false)
                                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                        .map(template => (
                                          <SelectItem key={template.id} value={template.id}>
                                            {template.name} {template.is_group_template && '(Group)'}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              )}
                            </div>
                            </>
                          )}
                          
                          {/* Archive permissions */}
                          <div className="space-y-2">
                            <Label>Archive Permissions</Label>
                            <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="can_archive_clients"
                                  checked={userFormData.can_archive_clients}
                                  onChange={(e) => setUserFormData(prev => ({...prev, can_archive_clients: e.target.checked}))}
                                  data-testid="user-can-archive-clients"
                                />
                                <Label htmlFor="can_archive_clients" className="text-sm">Can archive clients</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="can_archive_users"
                                  checked={userFormData.can_archive_users}
                                  onChange={(e) => setUserFormData(prev => ({...prev, can_archive_users: e.target.checked}))}
                                  data-testid="user-can-archive-users"
                                />
                                <Label htmlFor="can_archive_users" className="text-sm">Can archive users</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="group_admin"
                                  checked={userFormData.group_admin}
                                  onChange={(e) => setUserFormData(prev => ({...prev, group_admin: e.target.checked}))}
                                  data-testid="user-group-admin"
                                />
                                <Label htmlFor="group_admin" className="text-sm">Group Administrator</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="is_medication_manager"
                                  checked={userFormData.is_medication_manager || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, is_medication_manager: e.target.checked}))}
                                  data-testid="user-medication-manager"
                                />
                                <Label htmlFor="is_medication_manager" className="text-sm">Medication Manager</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="is_waitlist_manager"
                                  checked={userFormData.is_waitlist_manager || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, is_waitlist_manager: e.target.checked}))}
                                  data-testid="user-waitlist-manager"
                                />
                                <Label htmlFor="is_waitlist_manager" className="text-sm">Waitlist Manager</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="is_scheduler"
                                  checked={userFormData.is_scheduler || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, is_scheduler: e.target.checked}))}
                                  data-testid="user-scheduler"
                                />
                                <Label htmlFor="is_scheduler" className="text-sm">Scheduler</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="is_biller"
                                  checked={userFormData.is_biller || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, is_biller: e.target.checked}))}
                                  data-testid="user-biller"
                                />
                                <Label htmlFor="is_biller" className="text-sm">Biller</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="is_billing_manager"
                                  checked={userFormData.is_billing_manager || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, is_billing_manager: e.target.checked}))}
                                  data-testid="user-billing-manager"
                                />
                                <Label htmlFor="is_billing_manager" className="text-sm">Billing Manager</Label>
                              </div>
                              {/* Feb 27, 2026 — External Reviewer:
                                  strict read-only PHI access for
                                  outside auditors.  See `User.is_external_reviewer`
                                  in models/user.py for the full
                                  contract. */}
                              <div className="flex items-center space-x-2 col-span-2 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                <input
                                  type="checkbox"
                                  id="is_external_reviewer"
                                  checked={userFormData.is_external_reviewer || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, is_external_reviewer: e.target.checked}))}
                                  data-testid="user-external-reviewer"
                                />
                                <Label htmlFor="is_external_reviewer" className="text-sm font-medium text-amber-900">
                                  External Reviewer
                                  <span className="block text-xs font-normal text-amber-700">
                                    Read-only PHI access. User will only see clients explicitly granted to them on individual client charts.
                                  </span>
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="is_medical_records_manager"
                                  checked={userFormData.is_medical_records_manager || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, is_medical_records_manager: e.target.checked}))}
                                  data-testid="user-medical-records-manager"
                                />
                                <Label htmlFor="is_medical_records_manager" className="text-sm">Medical Records Manager</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="is_payroll_manager"
                                  checked={userFormData.is_payroll_manager || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, is_payroll_manager: e.target.checked}))}
                                  data-testid="user-payroll-manager"
                                />
                                <Label htmlFor="is_payroll_manager" className="text-sm">Payroll Manager</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="is_era_manager"
                                  checked={userFormData.is_era_manager || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, is_era_manager: e.target.checked}))}
                                  data-testid="user-era-manager"
                                />
                                <Label htmlFor="is_era_manager" className="text-sm">ERA Manager</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="is_authorization_manager"
                                  checked={userFormData.is_authorization_manager || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, is_authorization_manager: e.target.checked}))}
                                  data-testid="user-authorization-manager"
                                />
                                <Label htmlFor="is_authorization_manager" className="text-sm">Authorization Manager</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="can_generate_client_portal_links"
                                  checked={userFormData.can_generate_client_portal_links || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, can_generate_client_portal_links: e.target.checked}))}
                                  data-testid="user-can-generate-portal-links"
                                />
                                <Label htmlFor="can_generate_client_portal_links" className="text-sm">Can Generate Client Portal Links</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="can_qa_audit"
                                  checked={userFormData.can_qa_audit || false}
                                  onChange={(e) => setUserFormData(prev => ({...prev, can_qa_audit: e.target.checked}))}
                                  data-testid="user-can-qa-audit"
                                />
                                <Label htmlFor="can_qa_audit" className="text-sm text-indigo-700 font-medium">QA Auditor Access</Label>
                              </div>
                            </div>
                            
                            {/* Scheduler Assignment - Show only if is_scheduler is checked */}
                            {userFormData.is_scheduler && (
                              <div>
                                <Label className="text-sm font-medium">Schedule & Message For (Providers)</Label>
                                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded p-2">
                                  {providers && providers.length > 0 ? (
                                    providers.map(provider => (
                                      <div key={provider.id} className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          id={`schedule_for_${provider.id}`}
                                          checked={(userFormData.scheduling_for || []).includes(provider.id)}
                                          onChange={(e) => {
                                            const currentScheduling = userFormData.scheduling_for || [];
                                            if (e.target.checked) {
                                              setUserFormData(prev => ({
                                                ...prev, 
                                                scheduling_for: [...currentScheduling, provider.id]
                                              }));
                                            } else {
                                              setUserFormData(prev => ({
                                                ...prev, 
                                                scheduling_for: currentScheduling.filter(id => id !== provider.id)
                                              }));
                                            }
                                          }}
                                        />
                                        <Label htmlFor={`schedule_for_${provider.id}`} className="text-sm font-normal">
                                          {formatProviderName(provider)}
                                        </Label>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-sm text-gray-500">No providers available</p>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  This scheduler will receive appointment notifications AND client messages for selected providers. They can view and reply to client messages on behalf of these providers.
                                </p>
                              </div>
                            )}
                            
                            <div>
                              <Label htmlFor="client_visibility" className="text-sm font-medium">Client Visibility</Label>
                              <Select
                                value={userFormData.client_visibility}
                                onValueChange={(value) => setUserFormData(prev => ({...prev, client_visibility: value}))}
                              >
                                <SelectTrigger data-testid="user-client-visibility">
                                  <SelectValue placeholder="Select client visibility" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="own_only">Own Clients Only</SelectItem>
                                  <SelectItem value="all_clients">All Clients</SelectItem>
                                  <SelectItem value="own_and_supervisees">Own + Supervisees' Clients</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {/* User Status Toggle - only show when editing */}
                          {editingUser && (
                            <div className="space-y-3">
                              {/* Super Admin Protection Notice */}
                              {isSuperAdmin(editingUser) && (
                                <div className="p-4 bg-amber-50 rounded-md border border-amber-200">
                                  <div className="flex items-start gap-2">
                                    <Shield className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <p className="text-sm font-medium text-amber-900">Super Admin Account Protection</p>
                                      <p className="text-xs text-amber-700 mt-1">
                                        {superAdminCount <= 1 
                                          ? "This is the last super admin account. Role changes and deactivation are disabled to maintain system access."
                                          : "Super admin accounts cannot be deleted directly. To delete, first change the role to a lower tier (e.g., Admin or Provider)."}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              <div className="space-y-2 p-4 bg-gray-50 rounded-md border">
                                <Label htmlFor="user_status" className="text-sm font-medium">User Status</Label>
                                <div className="flex items-center space-x-3">
                                  <span className={`text-sm font-medium ${userFormData.is_archived ? 'text-gray-500' : 'text-green-600'}`}>
                                    Active
                                  </span>
                                  <Switch
                                    id="user_status"
                                    checked={userFormData.is_archived}
                                    onCheckedChange={(checked) => {
                                      if (checked && editingUser && !editingUser.is_archived) {
                                        // Opening archive modal instead of direct toggle
                                        handleArchiveUser(editingUser);
                                      } else if (!checked && editingUser?.is_archived) {
                                        // Restoring user
                                        handleRestoreUser(editingUser);
                                      } else {
                                        setUserFormData(prev => ({...prev, is_archived: checked}));
                                      }
                                    }}
                                    data-testid="user-status-toggle"
                                    disabled={isSuperAdmin(editingUser) && superAdminCount <= 1}
                                  />
                                  <span className={`text-sm font-medium ${userFormData.is_archived ? 'text-red-600' : 'text-gray-500'}`}>
                                    Inactive
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500">
                                  {isSuperAdmin(editingUser) && superAdminCount <= 1
                                    ? "Cannot deactivate the last remaining super admin."
                                    : userFormData.is_archived 
                                      ? "Inactive users cannot log in and won't appear in active user lists." 
                                      : "Active users can log in and access the system."}
                                </p>
                              </div>
                              
                              {/* Delete User Section - Disabled for Super Admins */}
                              <div className={`p-4 rounded-md border ${isSuperAdmin(editingUser) ? 'bg-gray-100 border-gray-200' : 'bg-red-50 border-red-200'}`}>
                                <Label className={`text-sm font-medium ${isSuperAdmin(editingUser) ? 'text-gray-500' : 'text-red-900'}`}>Danger Zone</Label>
                                <p className={`text-xs mt-1 mb-3 ${isSuperAdmin(editingUser) ? 'text-gray-500' : 'text-red-700'}`}>
                                  {isSuperAdmin(editingUser)
                                    ? "Super admin accounts cannot be deleted directly. Change the role to a lower tier first to enable deletion."
                                    : "Permanently delete this user. This action cannot be undone. All user data will be removed from the system."}
                                </p>
                                <button
                                  type="button"
                                  disabled={isSuperAdmin(editingUser)}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isSuperAdmin(editingUser)) {
                                      toast.error("Cannot delete super admin accounts. Change the role first.");
                                      return;
                                    }
                                    if (window.confirm(`⚠️ PERMANENT DELETE WARNING\n\nAre you absolutely sure you want to permanently delete ${editingUser.full_name}?\n\nThis will:\n• Remove all user data permanently\n• Cannot be undone\n• User will be completely removed from the system\n\nType 'DELETE' in the next prompt to confirm.`)) {
                                      const confirmation = prompt(`Type 'DELETE' to confirm permanent deletion of ${editingUser.full_name}:`);
                                      if (confirmation === 'DELETE') {
                                        handleDeleteUser(editingUser.id);
                                      } else {
                                        toast.error("Deletion cancelled - confirmation text did not match");
                                      }
                                    }
                                  }}
                                  className={`inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 ${
                                    isSuperAdmin(editingUser) 
                                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                      : 'bg-red-600 hover:bg-red-700 text-white'
                                  }`}
                                  style={{ pointerEvents: 'auto' }}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Permanently Delete User
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {/* 2FA Management — only shown when editing an existing user */}
                          {editingUser && (isSuperAdmin(user) || userRoleName === 'admin') && (
                            <div className="border rounded-lg p-4 bg-gray-50">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-medium flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-blue-600" />
                                    Two-Factor Authentication
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {editingUser.two_factor_enabled
                                      ? '2FA is currently enabled for this user.'
                                      : editingUser.requires_2fa_setup || user2FAStatus?.requires_2fa_setup
                                      ? '2FA setup required on next login.'
                                      : '2FA is not set up for this user.'}
                                  </p>
                                </div>
                                {editingUser.two_factor_enabled ? (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 border-red-200"
                                    onClick={() => {
                                      if (window.confirm(`Reset 2FA for ${editingUser.full_name || editingUser.email}? They will need to set it up again on next login.`)) {
                                        resetUser2FA(editingUser.id);
                                      }
                                    }}
                                  >
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                    Reset 2FA
                                  </Button>
                                ) : (editingUser.requires_2fa_setup || user2FAStatus?.requires_2fa_setup) ? (
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                                    Pending Setup
                                  </Badge>
                                ) : (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => requireUser2FA(editingUser.id)}
                                  >
                                    <Shield className="h-4 w-4 mr-2" />
                                    Require 2FA Setup
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="flex justify-end space-x-2">
                            {editingUser && (
                              <Button
                                type="button"
                                variant="outline"
                                className="mr-auto"
                                onClick={() => setShowUserNotifSettings(true)}
                                data-testid="v2-user-edit-notification-settings-btn"
                              >
                                <BellRing className="h-4 w-4 mr-1.5" />
                                Notification Settings
                              </Button>
                            )}
                            <Button type="button" variant="outline" onClick={() => setShowUserForm(false)}>
                              Cancel
                            </Button>
                            <Button type="submit" data-testid="create-user-submit" className="bg-teal-600 hover:bg-teal-700">
                              {editingUser ? 'Update User' : 'Create User'}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                    {editingUser && (
                      <UserNotificationSettingsDialog
                        open={showUserNotifSettings}
                        onClose={() => setShowUserNotifSettings(false)}
                        userId={editingUser.id}
                        userName={editingUser.full_name || editingUser.email || ''}
                      />
                    )}
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => setShowUserList(true)}
                      data-testid="manage-all-users"
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      Manage Users
                    </Button>
                  </div>
                  )}
                </div>
                </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">Total Users: {stats?.total_users || 0}</p>
                    <div className="space-y-2 mt-2">
                      {users.slice(0, 3).map((userItem) => (
                        <div key={userItem.id} className="flex items-center justify-between text-sm">
                          <span>{userItem.full_name}</span>
                          <Badge className={getRoleBadgeColor(roles?.find(r => r.id === userItem.role_id)?.name || userItem.role)} variant="outline">
                            {roles?.find(r => r.id === userItem.role_id)?.name || userItem.role || 'No Role'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                    
                    {/* Password Reset Requests Section (within same card) */}
                    <div className="mt-6 pt-6 border-t" data-password-resets>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-sm">Password Reset Requests</h3>
                        {passwordResetRequests.filter(r => r.status === 'pending').length > 0 && (
                          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">
                            {passwordResetRequests.filter(r => r.status === 'pending').length}
                          </span>
                        )}
                      </div>
                      
                      {passwordResetRequests.filter(r => r.status === 'pending').length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          <p className="text-xs">No pending requests</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {passwordResetRequests.filter(r => r.status === 'pending').slice(0, 3).map(request => (
                            <div key={request.id} className="border p-3 rounded bg-gray-50">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <p className="font-semibold text-sm">{request.user_name}</p>
                                  <p className="text-xs text-gray-600">{request.email}</p>
                                  <p className="text-xs text-gray-500">
                                    {new Date(request.created_at).toLocaleDateString()} {new Date(request.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </p>
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                <Button 
                                  size="sm"
                                  onClick={() => handleApprovePasswordReset(request.id)}
                                  className="bg-green-600 hover:bg-green-700 text-xs flex-1"
                                >
                                  Approve
                                </Button>
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDenyPasswordReset(request.id)}
                                  className="text-xs flex-1"
                                >
                                  Deny
                                </Button>
                              </div>
                            </div>
                          ))}
                          {passwordResetRequests.filter(r => r.status === 'pending').length > 3 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full text-xs mt-2"
                              onClick={() => changeTab('users')}
                            >
                              View All ({passwordResetRequests.filter(r => r.status === 'pending').length} total)
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Forms & Types Management */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg">Forms & Types</CardTitle>
                      {(isSuperAdmin(user)) && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="default"
                            onClick={() => {
                              setEditingFormTemplate(null);
                              setFormTemplateData({
                                name: '',
                                description: '',
                                form_type: '',
                                fields: [],
                                is_active: true,
                                is_group_template: false
                              });
                              setActiveFormFields([]);
                              // Clear static text editor cache when starting fresh
                              staticTextEditorCache.current.clear();
                              setShowFormTemplateBuilder(true);
                            }}
                            data-testid="add-form-btn"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Form
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setShowFormsManagementDialog(true)}
                            data-testid="manage-forms-types-btn"
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Manage
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Form Templates</span>
                        <Badge variant="secondary">{formTemplates.length}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Form Types</span>
                        <Badge variant="secondary">{formTypes.length}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Form Template Builder Dialog — extracted to components/features/FormBuilderDialog.jsx (V2 Forms migration, Jun 2026) */}
                <FormBuilderDialog
                  open={showFormTemplateBuilder}
                  onOpenChange={setShowFormTemplateBuilder}
                  editingTemplate={editingFormTemplate}
                  initialData={formTemplateData}
                  initialFields={activeFormFields}
                  formTypes={formTypes}
                  setFormTemplates={setFormTemplates}
                  fetchFormTemplates={fetchFormTemplates}
                  onCancelToManagement={() => { setShowFormTemplateBuilder(false); setShowFormsManagementDialog(true); }}
                  headerExtras={<AdminJumpToDropdown currentDialogId="forms" isDirty={currentAdminDialogDirty} />}
                />

                {/* Intake Bundles */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg">Intake Bundles</CardTitle>
                      {(isSuperAdmin(user)) && (
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            size="sm" 
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => {
                              setIntakeBundleFormData({
                                name: '',
                                service_type_ids: [],
                                trigger_cpt_code_ids: [],
                                age_min: null,
                                age_max: null,
                                documents: []
                              });
                              setEditingIntakeBundle(null);
                              setShowManageIntakeBundles(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Bundle
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setShowManageIntakeBundles(true)}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Manage Bundles
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {intakeBundles.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Folder className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-sm">No intake bundles configured</p>
                        <p className="text-xs mt-1">Create bundles to automate client intake workflows</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {intakeBundles.slice(0, 3).map((bundle) => (
                          <div key={bundle.id} className="border p-3 rounded bg-gray-50 hover:bg-gray-100">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold text-sm">{bundle.name}</p>
                                <p className="text-xs text-gray-600 mt-1">
                                  {bundle.documents?.length || 0} document{bundle.documents?.length !== 1 ? 's' : ''}
                                  {bundle.assessments?.length > 0 ? ` · ${bundle.assessments.length} assessment${bundle.assessments.length !== 1 ? 's' : ''}` : ''}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingIntakeBundle(bundle);
                                  setIntakeBundleFormData(bundle);
                                  setShowManageIntakeBundles(true);
                                }}
                              >
                                <Edit2 className="h-3 w-3 mr-1" />
                                Edit
                              </Button>
                            </div>
                          </div>
                        ))}
                        {intakeBundles.length > 3 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full text-xs mt-2"
                            onClick={() => setShowManageIntakeBundles(true)}
                          >
                            View All ({intakeBundles.length} total)
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* CPT Codes Management */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg">CPT Codes</CardTitle>
                      {(isSuperAdmin(user)) && (
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            size="sm" 
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => {
                              // Reset form for new CPT code
                              setCptFormData({
                                code: '',
                                display_name: '',
                                description: '',
                                category: '',
                                bundle_ids: [],
                                billing_type: 'insurance',
                                base_price: null,
                                self_pay_discount_type: 'none',
                                self_pay_discount_percent: null,
                                self_pay_discount_amount: null,
                                self_pay_rate: null,
                                units: 1,
                                duration_minutes: 45,
                                minutes_per_unit: 45,
                                allows_multiple_units: true,
                                is_group_session: false,
                                default_group_id: '',
                                default_template_id: '',
                                min_duration: null,
                                max_duration: null,
                                min_age: null,
                                max_age: null,
                                pos_codes: [],
                                modifiers: [],
                                color: '#3B82F6',
                                is_addon_code: false,
                                requires_primary_codes: [],
                                requires_authorization: false
                              });
                              setEditingCptCode(null);
                              setShowCptForm(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add CPT Code
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => setShowManageCptCodes(true)}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Manage CPT Codes
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">Active CPT Codes: {cptCodes.length}</p>
                      {cptCodes.slice(0, 3).map((cpt) => (
                        <div key={cpt.id} className="flex items-center justify-between text-sm">
                          <span>{cpt.code}</span>
                          <Badge variant="outline">{cpt.category}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* System Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">System Settings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Beta Gate + Billing toggles moved to the platform
                          portal (Sep 2026) — superadmin-only controls. */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm">HIPAA Audit Logs</span>
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Google Meet Integration</span>
                        <Badge className={googleCalendarStatus.is_mock ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}>
                          {googleCalendarStatus.status === 'loading' ? 'Checking...' : 
                           googleCalendarStatus.is_mock ? 'Mock Mode' : 'Active'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium">Backup Status</span>
                          {backupStatus.last_backup && (
                            <p className="text-xs text-gray-500">
                              Last backup: {new Date(backupStatus.last_backup).toLocaleString()}
                            </p>
                          )}
                          {Array.isArray(backupStatus.bucket_names) && backupStatus.bucket_names.length > 0 && (
                            <p className="text-[11px] text-gray-500 mt-0.5">
                              Destinations: {backupStatus.bucket_names.join(' · ')}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={
                            backupStatus.backup_status === 'success' ? "bg-green-100 text-green-800" :
                            backupStatus.backup_status?.includes('stale') ? "bg-amber-100 text-amber-800" :
                            backupStatus.backup_status === 'no_backups' ? "bg-red-100 text-red-800" :
                            backupStatus.status === 'loading' ? "bg-gray-100 text-gray-600" :
                            "bg-blue-100 text-blue-800"
                          }>
                            {backupStatus.status === 'loading' ? 'Checking...' :
                             backupStatus.backup_status === 'success' ? 'Healthy' :
                             backupStatus.backup_status === 'no_backups' ? 'No Backups!' :
                             backupStatus.backup_status?.includes('stale') ? 'Stale' :
                             backupStatus.backup_status || 'Unknown'}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={fetchBackupStatus}
                            className="h-7 px-2"
                            title="Refresh backup status"
                            data-testid="backup-refresh-btn"
                          >
                            <RefreshCw className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="default"
                            className="h-7 px-2 bg-purple-600 hover:bg-purple-700"
                            title="Run a full backup right now (writes to all configured destinations)"
                            data-testid="backup-run-now-btn"
                            disabled={runningBackup}
                            onClick={async () => {
                              if (!window.confirm('Run a full backup now? This uploads every critical collection to all configured GCS destinations.')) return;
                              try {
                                setRunningBackup(true);
                                const r = await axios.post(`${API}/admin/backup/run`);
                                const d = r.data || {};
                                const dests = (d.destination_buckets || []).join(', ') || 'default';
                                if (d.status === 'success') {
                                  toast.success(`Backup OK: ${d.collections_backed_up} collections · ${d.total_documents} docs · to ${dests}`);
                                } else if (d.status === 'partial') {
                                  toast.warning(`Backup partial: ${d.collections_backed_up} ok, ${d.collections_failed || 0} failed · to ${dests}`);
                                } else {
                                  toast.error(`Backup failed: ${d.error || d.status || 'unknown'}`);
                                }
                                fetchBackupStatus();
                              } catch (err) {
                                toast.error(`Backup failed: ${err.response?.data?.detail || err.message}`);
                              } finally {
                                setRunningBackup(false);
                              }
                            }}
                          >
                            {runningBackup
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <>Run Now</>}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Client Satisfaction Surveys */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Client Surveys
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">Satisfaction surveys & feedback</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setShowSurveyDashboard(true)}>
                      <BarChart3 className="h-4 w-4 mr-1" />
                      Manage Client Surveys
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p>• View survey responses & analytics</p>
                      <p>• Manage survey templates</p>
                      <p>• Track client satisfaction trends</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Company Branding Settings - Compact Card with Dialog */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Building className="h-5 w-5" />
                        Company Branding
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">Customize your company logo and name</p>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline">
                          <Settings className="h-4 w-4 mr-1" />
                          Manage
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <Building className="h-5 w-5" />
                            Company Branding Settings
                          </DialogTitle>
                          <DialogDescription>
                            Customize your company logo and name displayed in the header
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label htmlFor="company_name">Company Name (Internal)</Label>
                            <DebouncedInput
                              id="company_name"
                              value={brandingSettings?.company_name || ''}
                              onChange={(e) => setBrandingSettings(prev => ({ ...prev, company_name: e.target.value }))}
                              placeholder="Your Company Name"
                              className="mt-1"
                            />
                            <p className="text-xs text-gray-500 mt-1">Used for internal reference and alt text</p>
                          </div>
                          
                          <div>
                            <Label htmlFor="company_display_name">Display Name (Shown in Header)</Label>
                            <DebouncedInput
                              id="company_display_name"
                              value={brandingSettings?.company_display_name || ''}
                              onChange={(e) => setBrandingSettings(prev => ({ ...prev, company_display_name: e.target.value }))}
                              placeholder="e.g., CAC WORCESTER"
                              className="mt-1"
                            />
                            <p className="text-xs text-gray-500 mt-1">This text appears next to your logo in the header</p>
                          </div>
                          
                          <div>
                            <Label htmlFor="company_logo_url">Company Logo</Label>
                            <div className="flex gap-2 mt-1">
                              <DebouncedInput
                                id="company_logo_url"
                                value={brandingSettings?.company_logo_url || ''}
                                onChange={(e) => setBrandingSettings(prev => ({ ...prev, company_logo_url: e.target.value }))}
                                placeholder="Logo URL or upload below"
                                className="flex-1"
                              />
                              <div className="relative">
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    
                                    try {
                                      const formData = new FormData();
                                      formData.append('file', file);
                                      
                                      const response = await axios.post(`${API}/admin/upload-logo`, formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                      });
                                      
                                      if (response.data.logo_url) {
                                        setBrandingSettings(prev => ({ ...prev, company_logo_url: response.data.logo_url }));
                                        toast.success("Logo uploaded successfully!");
                                      }
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || "Failed to upload logo");
                                    }
                                  }}
                                />
                                <Button type="button" variant="outline" className="whitespace-nowrap">
                                  <Upload className="h-4 w-4 mr-2" />Upload
                                </Button>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Upload a logo (PNG, JPG, GIF, WebP, SVG) or enter a URL</p>
                          </div>
                          
                          {/* Logo Preview */}
                          {(brandingSettings?.company_logo_url || brandingSettings?.company_display_name) && (
                            <div className="p-4 bg-gray-50 rounded-lg">
                              <Label className="text-sm text-gray-600 mb-2 block">Header Preview:</Label>
                              <div className="flex items-center gap-3">
                                <Shield className="h-8 w-8 text-teal-600" />
                                <span className="text-xl font-bold text-gray-900">CareVaultPro</span>
                                <div className="h-8 w-px bg-gray-300" />
                                {brandingSettings?.company_logo_url && (
                                  <img 
                                    src={brandingSettings?.company_logo_url} 
                                    alt="Company Logo Preview" 
                                    className="h-8 max-w-[120px] object-contain"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                {brandingSettings?.company_display_name && (
                                  <span className="text-lg font-semibold text-gray-700">{brandingSettings?.company_display_name}</span>
                                )}
                              </div>
                            </div>
                          )}
                          
                          <div className="border-t pt-4 mt-4">
                            <Label className="text-sm font-medium text-gray-700 mb-3 block">Document Print Settings</Label>
                            <p className="text-xs text-gray-500 mb-3">These appear on printed/exported documents (header & footer)</p>
                            
                            <div className="space-y-3">
                              <div>
                                <Label htmlFor="company_website">Website URL</Label>
                                <DebouncedInput
                                  id="company_website"
                                  value={brandingSettings?.company_website || ''}
                                  onChange={(e) => setBrandingSettings(prev => ({ ...prev, company_website: e.target.value }))}
                                  placeholder="e.g., www.yourorganization.com"
                                  className="mt-1"
                                />
                              </div>
                              
                              <div>
                                <Label htmlFor="company_motto">Company Motto/Tagline</Label>
                                <DebouncedInput
                                  id="company_motto"
                                  value={brandingSettings?.company_motto || ''}
                                  onChange={(e) => setBrandingSettings(prev => ({ ...prev, company_motto: e.target.value }))}
                                  placeholder="e.g., Caring for our community since 1985"
                                  className="mt-1"
                                />
                              </div>
                              
                              <div>
                                <Label htmlFor="company_state">Organization State</Label>
                                <Select
                                  value={brandingSettings?.company_state || ''}
                                  onValueChange={(value) => setBrandingSettings(prev => ({ ...prev, company_state: value }))}
                                >
                                  <SelectTrigger className="mt-1">
                                    <SelectValue placeholder="Select state..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Alabama">Alabama</SelectItem>
                                    <SelectItem value="Alaska">Alaska</SelectItem>
                                    <SelectItem value="Arizona">Arizona</SelectItem>
                                    <SelectItem value="Arkansas">Arkansas</SelectItem>
                                    <SelectItem value="California">California</SelectItem>
                                    <SelectItem value="Colorado">Colorado</SelectItem>
                                    <SelectItem value="Connecticut">Connecticut</SelectItem>
                                    <SelectItem value="Delaware">Delaware</SelectItem>
                                    <SelectItem value="Florida">Florida</SelectItem>
                                    <SelectItem value="Georgia">Georgia</SelectItem>
                                    <SelectItem value="Hawaii">Hawaii</SelectItem>
                                    <SelectItem value="Idaho">Idaho</SelectItem>
                                    <SelectItem value="Illinois">Illinois</SelectItem>
                                    <SelectItem value="Indiana">Indiana</SelectItem>
                                    <SelectItem value="Iowa">Iowa</SelectItem>
                                    <SelectItem value="Kansas">Kansas</SelectItem>
                                    <SelectItem value="Kentucky">Kentucky</SelectItem>
                                    <SelectItem value="Louisiana">Louisiana</SelectItem>
                                    <SelectItem value="Maine">Maine</SelectItem>
                                    <SelectItem value="Maryland">Maryland</SelectItem>
                                    <SelectItem value="Massachusetts">Massachusetts</SelectItem>
                                    <SelectItem value="Michigan">Michigan</SelectItem>
                                    <SelectItem value="Minnesota">Minnesota</SelectItem>
                                    <SelectItem value="Mississippi">Mississippi</SelectItem>
                                    <SelectItem value="Missouri">Missouri</SelectItem>
                                    <SelectItem value="Montana">Montana</SelectItem>
                                    <SelectItem value="Nebraska">Nebraska</SelectItem>
                                    <SelectItem value="Nevada">Nevada</SelectItem>
                                    <SelectItem value="New Hampshire">New Hampshire</SelectItem>
                                    <SelectItem value="New Jersey">New Jersey</SelectItem>
                                    <SelectItem value="New Mexico">New Mexico</SelectItem>
                                    <SelectItem value="New York">New York</SelectItem>
                                    <SelectItem value="North Carolina">North Carolina</SelectItem>
                                    <SelectItem value="North Dakota">North Dakota</SelectItem>
                                    <SelectItem value="Ohio">Ohio</SelectItem>
                                    <SelectItem value="Oklahoma">Oklahoma</SelectItem>
                                    <SelectItem value="Oregon">Oregon</SelectItem>
                                    <SelectItem value="Pennsylvania">Pennsylvania</SelectItem>
                                    <SelectItem value="Rhode Island">Rhode Island</SelectItem>
                                    <SelectItem value="South Carolina">South Carolina</SelectItem>
                                    <SelectItem value="South Dakota">South Dakota</SelectItem>
                                    <SelectItem value="Tennessee">Tennessee</SelectItem>
                                    <SelectItem value="Texas">Texas</SelectItem>
                                    <SelectItem value="Utah">Utah</SelectItem>
                                    <SelectItem value="Vermont">Vermont</SelectItem>
                                    <SelectItem value="Virginia">Virginia</SelectItem>
                                    <SelectItem value="Washington">Washington</SelectItem>
                                    <SelectItem value="West Virginia">West Virginia</SelectItem>
                                    <SelectItem value="Wisconsin">Wisconsin</SelectItem>
                                    <SelectItem value="Wyoming">Wyoming</SelectItem>
                                    <SelectItem value="District of Columbia">District of Columbia</SelectItem>
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-gray-500 mt-1">
                                  Used for telehealth consent and state-specific compliance text.
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Email Settings Section */}
                          <div className="border-t pt-4 mt-4">
                            <h4 className="font-medium mb-3 flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              Email Settings
                            </h4>
                            <div className="space-y-3">
                              <div>
                                <Label>No-Reply Email Address</Label>
                                <div className="mt-1 p-2 bg-gray-100 rounded border text-sm text-gray-700 font-mono">
                                  {brandingSettings?.system_email || 'no-reply@example.com'}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Configured via environment variable. Contact system administrator to change.
                                </p>
                              </div>
                              <div>
                                <Label htmlFor="noreply_display_name">Email Display Name</Label>
                                <DebouncedInput
                                  id="noreply_display_name"
                                  value={brandingSettings?.noreply_display_name || ''}
                                  onChange={(e) => setBrandingSettings(prev => ({ ...prev, noreply_display_name: e.target.value }))}
                                  placeholder="e.g., Your Organization (No Reply)"
                                  className="mt-1"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Name shown in email "From" field. Defaults to company name if not set.
                                </p>
                              </div>
                              <div>
                                <Label htmlFor="admin_contact_email">Admin Contact Email</Label>
                                <DebouncedInput
                                  id="admin_contact_email"
                                  type="email"
                                  value={brandingSettings?.admin_contact_email || ''}
                                  onChange={(e) => setBrandingSettings(prev => ({ ...prev, admin_contact_email: e.target.value }))}
                                  placeholder="e.g., admin@yourorg.com"
                                  className="mt-1"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Contact email shown in welcome emails for new users. Users will reach out here for help.
                                </p>
                              </div>
                              <div className="flex items-center space-x-2 pt-2">
                                <input
                                  type="checkbox"
                                  id="welcome_email_enabled"
                                  checked={brandingSettings?.welcome_email_enabled !== false}
                                  onChange={(e) => setBrandingSettings(prev => ({ ...prev, welcome_email_enabled: e.target.checked }))}
                                />
                                <Label htmlFor="welcome_email_enabled">Send welcome emails to new users</Label>
                              </div>
                              <p className="text-xs text-gray-500 -mt-1 ml-6">
                                When enabled, new users will receive a welcome email with login instructions.
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="show_branding"
                              checked={brandingSettings?.show_company_branding !== false}
                              onChange={(e) => setBrandingSettings(prev => ({ ...prev, show_company_branding: e.target.checked }))}
                            />
                            <Label htmlFor="show_branding">Show company branding in header</Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="view_as_user_enabled"
                              checked={brandingSettings?.view_as_user_enabled !== false}
                              onChange={(e) => setBrandingSettings(prev => ({ ...prev, view_as_user_enabled: e.target.checked }))}
                            />
                            <Label htmlFor="view_as_user_enabled">Enable "View as User" feature (Admin)</Label>
                          </div>
                          
                          <Button
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/admin/branding-settings`, brandingSettings);
                                toast.success('Branding settings saved');
                              } catch (error) {
                                toast.error('Failed to save branding settings');
                              }
                            }}
                            className="w-full bg-teal-600 hover:bg-teal-700"
                          >
                            Save Branding Settings
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      {brandingSettings?.company_display_name ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Configured: {brandingSettings?.company_display_name}</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-4 w-4 text-amber-500" />
                          <span>Not configured</span>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Security & 2FA Settings - Compact Card with Dialog */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Security Settings (2FA)
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">Two-factor authentication requirements</p>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline">
                          <Settings className="h-4 w-4 mr-1" />
                          Manage
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <Shield className="h-5 w-5" />
                            Security Settings (2FA)
                          </DialogTitle>
                          <DialogDescription>
                            Configure two-factor authentication requirements for your organization
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          {/* 2FA Requirements */}
                          <div className="space-y-3">
                            <Label className="font-medium">2FA Requirements</Label>
                            
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">Require 2FA for All Users</p>
                                <p className="text-xs text-gray-500">All staff must set up 2FA to access the system</p>
                              </div>
                              <Switch
                                checked={twoFAOrgSettings.require_2fa_for_all}
                                onCheckedChange={(checked) => setTwoFAOrgSettings(prev => ({ 
                                  ...prev, 
                                  require_2fa_for_all: checked,
                                  require_2fa_for_admins: checked ? true : prev.require_2fa_for_admins,
                                  require_2fa_for_providers: checked ? true : prev.require_2fa_for_providers,
                                  require_2fa_for_supervisors: checked ? true : prev.require_2fa_for_supervisors,
                                  require_2fa_for_office: checked ? true : prev.require_2fa_for_office,
                                  require_2fa_for_reception: checked ? true : prev.require_2fa_for_reception,
                                  require_2fa_for_interns: checked ? true : prev.require_2fa_for_interns
                                }))}
                              />
                            </div>
                            
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">Require 2FA for Admins</p>
                                <p className="text-xs text-gray-500">Admin users must use 2FA</p>
                              </div>
                              <Switch
                                checked={twoFAOrgSettings.require_2fa_for_admins}
                                onCheckedChange={(checked) => setTwoFAOrgSettings(prev => ({ ...prev, require_2fa_for_admins: checked }))}
                                disabled={twoFAOrgSettings.require_2fa_for_all}
                              />
                            </div>
                            
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">Require 2FA for Providers</p>
                                <p className="text-xs text-gray-500">Clinical providers must use 2FA</p>
                              </div>
                              <Switch
                                checked={twoFAOrgSettings.require_2fa_for_providers}
                                onCheckedChange={(checked) => setTwoFAOrgSettings(prev => ({ ...prev, require_2fa_for_providers: checked }))}
                                disabled={twoFAOrgSettings.require_2fa_for_all}
                              />
                            </div>
                            
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">Require 2FA for Supervisors</p>
                                <p className="text-xs text-gray-500">Supervisors must use 2FA</p>
                              </div>
                              <Switch
                                checked={twoFAOrgSettings.require_2fa_for_supervisors}
                                onCheckedChange={(checked) => setTwoFAOrgSettings(prev => ({ ...prev, require_2fa_for_supervisors: checked }))}
                                disabled={twoFAOrgSettings.require_2fa_for_all}
                              />
                            </div>
                            
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">Require 2FA for Office Staff</p>
                                <p className="text-xs text-gray-500">Office staff must use 2FA</p>
                              </div>
                              <Switch
                                checked={twoFAOrgSettings.require_2fa_for_office}
                                onCheckedChange={(checked) => setTwoFAOrgSettings(prev => ({ ...prev, require_2fa_for_office: checked }))}
                                disabled={twoFAOrgSettings.require_2fa_for_all}
                              />
                            </div>
                            
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">Require 2FA for Reception</p>
                                <p className="text-xs text-gray-500">Reception staff must use 2FA</p>
                              </div>
                              <Switch
                                checked={twoFAOrgSettings.require_2fa_for_reception}
                                onCheckedChange={(checked) => setTwoFAOrgSettings(prev => ({ ...prev, require_2fa_for_reception: checked }))}
                                disabled={twoFAOrgSettings.require_2fa_for_all}
                              />
                            </div>
                            
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">Require 2FA for Interns</p>
                                <p className="text-xs text-gray-500">Interns must use 2FA</p>
                              </div>
                              <Switch
                                checked={twoFAOrgSettings.require_2fa_for_interns}
                                onCheckedChange={(checked) => setTwoFAOrgSettings(prev => ({ ...prev, require_2fa_for_interns: checked }))}
                                disabled={twoFAOrgSettings.require_2fa_for_all}
                              />
                            </div>
                          </div>
                          
                          {/* 2FA Options */}
                          <div className="space-y-3 pt-4 border-t">
                            <Label className="font-medium">Allowed 2FA Methods</Label>
                            
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">Allow Email OTP Backup</p>
                                <p className="text-xs text-gray-500">Users can verify via email if authenticator unavailable</p>
                              </div>
                              <Switch
                                checked={twoFAOrgSettings.allow_email_backup}
                                onCheckedChange={(checked) => setTwoFAOrgSettings(prev => ({ ...prev, allow_email_backup: checked }))}
                              />
                            </div>
                            
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">Allow Recovery Codes</p>
                                <p className="text-xs text-gray-500">Users receive one-time backup codes</p>
                              </div>
                              <Switch
                                checked={twoFAOrgSettings.allow_recovery_codes}
                                onCheckedChange={(checked) => setTwoFAOrgSettings(prev => ({ ...prev, allow_recovery_codes: checked }))}
                              />
                            </div>
                          </div>
                          
                          <Button
                            onClick={async () => {
                              try {
                                const formData = new FormData();
                                formData.append('require_2fa_for_all', twoFAOrgSettings.require_2fa_for_all);
                                formData.append('require_2fa_for_admins', twoFAOrgSettings.require_2fa_for_admins);
                                formData.append('require_2fa_for_providers', twoFAOrgSettings.require_2fa_for_providers);
                                formData.append('require_2fa_for_supervisors', twoFAOrgSettings.require_2fa_for_supervisors);
                                formData.append('require_2fa_for_office', twoFAOrgSettings.require_2fa_for_office);
                                formData.append('require_2fa_for_reception', twoFAOrgSettings.require_2fa_for_reception);
                                formData.append('require_2fa_for_interns', twoFAOrgSettings.require_2fa_for_interns);
                                formData.append('allow_email_backup', twoFAOrgSettings.allow_email_backup);
                                formData.append('allow_recovery_codes', twoFAOrgSettings.allow_recovery_codes);
                                
                                await axios.put(`${API}/admin/2fa-settings`, formData);
                                toast.success('Security settings saved');
                              } catch (error) {
                                toast.error('Failed to save security settings');
                              }
                            }}
                            className="w-full bg-teal-600 hover:bg-teal-700"
                          >
                            Save Security Settings
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      {twoFAOrgSettings.require_2fa_for_all ? (
                        <>
                          <Lock className="h-4 w-4 text-green-500" />
                          <span>2FA required for all users</span>
                        </>
                      ) : (twoFAOrgSettings.require_2fa_for_admins || twoFAOrgSettings.require_2fa_for_providers) ? (
                        <>
                          <Lock className="h-4 w-4 text-amber-500" />
                          <span>2FA required for some roles</span>
                        </>
                      ) : (
                        <>
                          <Unlock className="h-4 w-4 text-gray-400" />
                          <span>2FA optional</span>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Domain Whitelist Settings */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Domain Whitelist
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">
                        {domainWhitelistSettings.enabled 
                          ? `Restricted to ${domainWhitelistSettings.allowed_domains.length} domain(s)` 
                          : 'All email domains allowed'}
                      </p>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline">
                          <Settings className="h-4 w-4 mr-1" />
                          Manage
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <Mail className="h-5 w-5" />
                            Domain Whitelist Settings
                          </DialogTitle>
                          <DialogDescription>
                            Restrict user registration to specific email domains
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          {/* Enable/Disable Toggle */}
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-sm">Enable Domain Whitelist</p>
                              <p className="text-xs text-gray-500">Only allow user creation with approved email domains</p>
                            </div>
                            <Switch
                              checked={domainWhitelistSettings.enabled}
                              onCheckedChange={(checked) => setDomainWhitelistSettings(prev => ({ 
                                ...prev, 
                                enabled: checked 
                              }))}
                            />
                          </div>

                          {/* Domain List */}
                          {domainWhitelistSettings.enabled && (
                            <div className="space-y-3">
                              <Label className="font-medium">Allowed Domains</Label>
                              
                              {/* Add Domain Input */}
                              <div className="flex gap-2">
                                <div className="relative flex-1">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                                  <DebouncedInput
                                    placeholder="example.com"
                                    value={newDomainInput}
                                    onChange={(e) => setNewDomainInput(e.target.value.toLowerCase().replace('@', ''))}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && newDomainInput.trim()) {
                                        e.preventDefault();
                                        const domain = newDomainInput.trim().toLowerCase();
                                        if (domain.includes('.') && !domainWhitelistSettings.allowed_domains.includes(domain)) {
                                          setDomainWhitelistSettings(prev => ({
                                            ...prev,
                                            allowed_domains: [...prev.allowed_domains, domain]
                                          }));
                                          setNewDomainInput('');
                                        }
                                      }
                                    }}
                                    className="pl-7"
                                  />
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    const domain = newDomainInput.trim().toLowerCase();
                                    if (domain.includes('.') && !domainWhitelistSettings.allowed_domains.includes(domain)) {
                                      setDomainWhitelistSettings(prev => ({
                                        ...prev,
                                        allowed_domains: [...prev.allowed_domains, domain]
                                      }));
                                      setNewDomainInput('');
                                    }
                                  }}
                                  disabled={!newDomainInput.trim() || !newDomainInput.includes('.')}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>

                              {/* Domain List */}
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {domainWhitelistSettings.allowed_domains.length === 0 ? (
                                  <p className="text-sm text-gray-500 italic p-3 bg-yellow-50 rounded-lg">
                                    No domains added yet. Add at least one domain to enable whitelist.
                                  </p>
                                ) : (
                                  domainWhitelistSettings.allowed_domains.map((domain, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 bg-white border rounded-lg">
                                      <span className="text-sm font-mono">@{domain}</span>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => {
                                          setDomainWhitelistSettings(prev => ({
                                            ...prev,
                                            allowed_domains: prev.allowed_domains.filter((_, i) => i !== index)
                                          }));
                                        }}
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          )}

                          {/* Info Box */}
                          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-xs text-blue-800">
                              <strong>Note:</strong> When enabled, new user accounts can only be created with email addresses from the whitelisted domains. Existing users are not affected.
                            </p>
                          </div>

                          {/* Save Button */}
                          <Button
                            className="w-full"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/admin/domain-whitelist-settings`, domainWhitelistSettings);
                                toast.success('Domain whitelist settings saved');
                              } catch (error) {
                                toast.error('Failed to save domain whitelist settings');
                              }
                            }}
                          >
                            Save Domain Whitelist Settings
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm">
                      {domainWhitelistSettings.enabled ? (
                        <div className="flex flex-wrap gap-1">
                          {domainWhitelistSettings.allowed_domains.slice(0, 3).map((domain, i) => (
                            <span key={i} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                              @{domain}
                            </span>
                          ))}
                          {domainWhitelistSettings.allowed_domains.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                              +{domainWhitelistSettings.allowed_domains.length - 3} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">Whitelist disabled - all domains allowed</span>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* HIPAA Audit Settings - Super Admin Only */}
                {isSuperAdmin(user) && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        HIPAA Audit Logging
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">Chart access tracking for compliance</p>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline">
                          <Settings className="h-4 w-4 mr-1" />
                          Manage
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            HIPAA Audit Logging Settings
                          </DialogTitle>
                          <DialogDescription>
                            Configure how chart access is logged for HIPAA compliance
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-800">
                              <strong>Smart Filter (Default):</strong> Logs the first time each user accesses a client's chart within a 30-minute window. This meets HIPAA "Accounting of Disclosures" requirements while minimizing database bloat.
                            </p>
                          </div>
                          
                          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="text-sm text-amber-800">
                              <strong>Note:</strong> High-value actions (SSN reveals, exports, downloads) are <strong>always</strong> logged regardless of this setting.
                            </p>
                          </div>
                          
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-sm">Verbose Audit Logging</p>
                              <p className="text-xs text-gray-500">When enabled, logs every single chart access (may increase database size significantly)</p>
                            </div>
                            <Switch
                              checked={auditSettings.verbose_audit_logging}
                              onCheckedChange={(checked) => setAuditSettings(prev => ({ 
                                ...prev, 
                                verbose_audit_logging: checked
                              }))}
                              data-testid="verbose-audit-toggle"
                            />
                          </div>
                          
                          <div className="text-sm text-gray-600 p-3 bg-gray-50 rounded-lg">
                            <p><strong>Access Window:</strong> {auditSettings.chart_access_window_minutes || 30} minutes</p>
                            <p className="text-xs text-gray-500 mt-1">Time before a repeat access is logged (when Smart Filter is active)</p>
                          </div>
                          
                          <Button
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/admin/audit/settings`, {
                                  verbose_audit_logging: auditSettings.verbose_audit_logging
                                });
                                toast.success('Audit settings saved');
                              } catch (error) {
                                toast.error('Failed to save audit settings');
                              }
                            }}
                            className="w-full bg-teal-600 hover:bg-teal-700"
                            data-testid="save-audit-settings-btn"
                          >
                            Save Audit Settings
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      {auditSettings.verbose_audit_logging ? (
                        <>
                          <FileText className="h-4 w-4 text-amber-500" />
                          <span>Verbose mode • Logging all accesses</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Smart filter • 30-min window</span>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
                )}

                {/* Security Dashboard - Super Admin Only */}
                {isSuperAdmin(user) && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5 text-red-600" />
                        Security Dashboard
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">Audit logs, high-risk actions & HIPAA reports</p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setShowSecurityDashboard(true);
                        setSecurityFilter('all');
                        setSecurityUserFilter('');
                        fetchSecurityStats(7);
                        fetchSecurityLogs(1, {});
                        fetchArchiveStats();
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Open Dashboard
                    </Button>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Activity className="h-4 w-4 text-teal-500" />
                      <span>View audit trail & generate compliance reports</span>
                    </div>
                  </CardContent>
                </Card>
                )}

                {/* Security Dashboard Dialog */}
                <Dialog open={showSecurityDashboard} onOpenChange={setShowSecurityDashboard}>
                  <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-xl">
                        <Shield className="h-6 w-6 text-red-600" />
                        Security Dashboard
                      </DialogTitle>
                      <DialogDescription>
                        HIPAA-compliant audit trail • High-risk action monitoring • Disclosure reports
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                      {/* Red Flag Section - High Priority Alerts */}
                      {securityStats && (securityStats.high_value_actions > 0) && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <h3 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
                            <AlertTriangle className="h-5 w-5" />
                            High-Risk Actions (Last {securityStats.period_days} days)
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-white p-3 rounded border border-red-100">
                              <p className="text-2xl font-bold text-red-600">{securityStats.high_value_actions}</p>
                              <p className="text-xs text-gray-600">SSN Reveals / Exports</p>
                            </div>
                            {securityStats.actions_by_type?.filter(a => 
                              ['VIEW_SSN', 'reveal_ssn', 'export', 'export_csv', 'generate_bundle'].includes(a.action)
                            ).slice(0, 3).map((action, idx) => (
                              <div key={idx} className="bg-white p-3 rounded border border-red-100">
                                <p className="text-xl font-bold text-red-600">{action.count}</p>
                                <p className="text-xs text-gray-600 truncate">{action.action.replace('_', ' ')}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Stats Overview */}
                      {securityStats && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-4 bg-gray-50 rounded-lg border">
                            <p className="text-3xl font-bold text-gray-800">{securityStats.total_actions?.toLocaleString()}</p>
                            <p className="text-sm text-gray-600">Total Actions</p>
                          </div>
                          <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                            <p className="text-3xl font-bold text-teal-700">{securityStats.chart_accesses?.toLocaleString()}</p>
                            <p className="text-sm text-gray-600">Chart Accesses</p>
                          </div>
                          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-3xl font-bold text-red-600">{securityStats.high_value_actions}</p>
                            <p className="text-sm text-gray-600">High-Risk Actions</p>
                          </div>
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-3xl font-bold text-blue-700">{securityStats.top_users?.length || 0}</p>
                            <p className="text-sm text-gray-600">Active Users</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Quick Filters & Search */}
                      <div className="p-4 bg-gray-50 rounded-lg border space-y-4">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant={securityFilter === 'all' ? 'default' : 'outline'}
                            onClick={() => {
                              setSecurityFilter('all');
                              fetchSecurityLogs(1, buildSecurityFilters({ filter: 'all' }));
                            }}
                          >
                            All Actions
                          </Button>
                          <Button
                            size="sm"
                            variant={securityFilter === 'high-risk' ? 'default' : 'outline'}
                            className={securityFilter === 'high-risk' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-300'}
                            onClick={() => {
                              setSecurityFilter('high-risk');
                              fetchSecurityLogs(1, buildSecurityFilters({ filter: 'high-risk' }));
                            }}
                          >
                            <AlertTriangle className="h-4 w-4 mr-1" />
                            High-Risk Only
                          </Button>
                          <Button
                            size="sm"
                            variant={securityFilter === 'chart-access' ? 'default' : 'outline'}
                            onClick={() => {
                              setSecurityFilter('chart-access');
                              fetchSecurityLogs(1, buildSecurityFilters({ filter: 'chart-access' }));
                            }}
                          >
                            Chart Access
                          </Button>
                          <Button
                            size="sm"
                            variant={securityFilter === 'login' ? 'default' : 'outline'}
                            onClick={() => {
                              setSecurityFilter('login');
                              fetchSecurityLogs(1, buildSecurityFilters({ filter: 'login' }));
                            }}
                          >
                            Logins
                          </Button>
                          <div className="ml-auto min-w-[220px]">
                            <Select
                              value={securityUserFilter || 'all'}
                              onValueChange={(v) => {
                                const uid = v === 'all' ? '' : v;
                                setSecurityUserFilter(uid);
                                fetchSecurityLogs(1, buildSecurityFilters({ userId: uid }));
                              }}
                            >
                              <SelectTrigger className="bg-white h-9" data-testid="security-user-filter">
                                <SelectValue placeholder="All users" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All users</SelectItem>
                                {(users || [])
                                  .slice()
                                  .sort((a, b) => (a.full_name || `${a.first_name || ''} ${a.last_name || ''}`).localeCompare(b.full_name || `${b.first_name || ''} ${b.last_name || ''}`))
                                  .map(u => (
                                    <SelectItem key={u.id} value={u.id}>
                                      {(u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email)}{u.email ? ` — ${u.email}` : ''}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* HIPAA Disclosure Report Section */}
                        <div className="pt-3 border-t">
                          <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            Generate HIPAA Disclosure Report
                          </h4>
                          <div className="flex flex-wrap gap-2 items-end">
                            <div className="flex-1 min-w-[200px]">
                              <Label className="text-xs text-gray-500">Client ID or Search</Label>
                              <div className="flex gap-2">
                                <Select value={hipaaReportClientId} onValueChange={setHipaaReportClientId}>
                                  <SelectTrigger className="bg-white">
                                    <SelectValue placeholder="Select client..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(allClientsForLookup?.length > 0 ? allClientsForLookup : clients || []).slice(0, 100).map(c => (
                                      <SelectItem key={c.id} value={c.id}>
                                        {formatClientName(c)} {c.mrn ? `(${c.mrn})` : ''}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="w-32">
                              <Label className="text-xs text-gray-500">Days Back</Label>
                              <Select value={String(hipaaReportDays)} onValueChange={(v) => setHipaaReportDays(Number(v))}>
                                <SelectTrigger className="bg-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="90">90 days</SelectItem>
                                  <SelectItem value="365">1 year</SelectItem>
                                  <SelectItem value="730">2 years</SelectItem>
                                  <SelectItem value="1825">5 years</SelectItem>
                                  <SelectItem value="3650">10 years</SelectItem>
                                  <SelectItem value="7300">20 years</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Button
                              onClick={() => {
                                if (!hipaaReportClientId) {
                                  toast.error('Please select a client');
                                  return;
                                }
                                fetchClientAuditHistory(hipaaReportClientId, hipaaReportDays);
                                setShowHIPAAReportModal(true);
                              }}
                              className="bg-teal-600 hover:bg-teal-700"
                              disabled={!hipaaReportClientId}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Generate Report
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Audit Logs Table */}
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-100 px-4 py-2 border-b flex justify-between items-center">
                          <h4 className="font-medium text-sm">
                            Audit Log ({securityLogsTotal.toLocaleString()} entries)
                          </h4>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => fetchSecurityLogs(securityLogsPage, buildSecurityFilters())}
                            disabled={securityLogsLoading}
                            data-testid="security-refresh-logs"
                          >
                            <RefreshCw className={`h-4 w-4 ${securityLogsLoading ? 'animate-spin' : ''}`} />
                          </Button>
                        </div>
                        
                        <div className="max-h-[400px] overflow-y-auto">
                          {securityLogsLoading ? (
                            <div className="flex items-center justify-center py-12">
                              <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                            </div>
                          ) : securityLogs.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                              No audit logs found
                            </div>
                          ) : (
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="text-left px-4 py-2 font-medium">Timestamp</th>
                                  <th className="text-left px-4 py-2 font-medium">User</th>
                                  <th className="text-left px-4 py-2 font-medium">Action</th>
                                  <th className="text-left px-4 py-2 font-medium">Resource</th>
                                  <th className="text-left px-4 py-2 font-medium">Details</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {securityLogs.map((log, idx) => {
                                  const isHighRisk = ['VIEW_SSN', 'reveal_ssn', 'export', 'export_csv', 'generate_bundle', 'download'].includes(log.action);
                                  return (
                                    <tr key={log.id || idx} className={isHighRisk ? 'bg-red-50' : 'hover:bg-gray-50'}>
                                      <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">
                                        {new Date(log.timestamp).toLocaleString()}
                                      </td>
                                      <td className="px-4 py-2">
                                        <span className="font-medium">{log.user_name || log.user_id}</span>
                                      </td>
                                      <td className="px-4 py-2">
                                        <span className={`inline-flex items-center gap-1 ${isHighRisk ? 'text-red-600 font-medium' : ''}`}>
                                          {isHighRisk && <AlertTriangle className="h-3 w-3" />}
                                          {log.action}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2 text-gray-600">
                                        {log.resource_type}: {log.resource_id?.substring(0, 8)}...
                                      </td>
                                      <td className="px-4 py-2 text-xs text-gray-500 max-w-[200px] truncate">
                                        {log.details?.client_name || log.details?.action || JSON.stringify(log.details || {}).substring(0, 50)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                        
                        {/* Pagination */}
                        {securityLogsTotal > 25 && (
                          <div className="px-4 py-2 border-t bg-gray-50 flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              Page {securityLogsPage} of {Math.ceil(securityLogsTotal / 25)}
                            </span>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={securityLogsPage <= 1}
                                data-testid="security-prev-page"
                                onClick={() => fetchSecurityLogs(securityLogsPage - 1, buildSecurityFilters())}
                              >
                                Previous
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={securityLogsPage >= Math.ceil(securityLogsTotal / 25)}
                                data-testid="security-next-page"
                                onClick={() => fetchSecurityLogs(securityLogsPage + 1, buildSecurityFilters())}
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Top Users Section */}
                      {securityStats?.top_users && securityStats.top_users.length > 0 && (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-gray-100 px-4 py-2 border-b">
                            <h4 className="font-medium text-sm">Most Active Users (Last {securityStats.period_days} days)</h4>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                              {securityStats.top_users.slice(0, 5).map((u, idx) => (
                                <div key={idx} className="text-center p-2 bg-gray-50 rounded">
                                  <p className="font-medium text-sm truncate">{u.user_name}</p>
                                  <p className="text-xl font-bold text-teal-600">{u.count}</p>
                                  <p className="text-xs text-gray-500">actions</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Archive Status Section */}
                      {archiveStats && (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-blue-50 px-4 py-2 border-b border-blue-200">
                            <h4 className="font-medium text-sm flex items-center gap-2 text-blue-800">
                              <Database className="h-4 w-4" />
                              GCS Archive Status (20-Year Retention)
                            </h4>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div className="text-center p-2 bg-gray-50 rounded">
                                <p className="text-xl font-bold text-blue-600">{archiveStats.total_archive_files || 0}</p>
                                <p className="text-xs text-gray-500">Archive Files</p>
                              </div>
                              <div className="text-center p-2 bg-gray-50 rounded">
                                <p className="text-xl font-bold text-blue-600">{(archiveStats.total_logs_archived || 0).toLocaleString()}</p>
                                <p className="text-xs text-gray-500">Logs Archived</p>
                              </div>
                              <div className="text-center p-2 bg-gray-50 rounded">
                                <p className="text-xl font-bold text-blue-600">{archiveStats.total_size_mb || 0} MB</p>
                                <p className="text-xs text-gray-500">Storage Used</p>
                              </div>
                              <div className="text-center p-2 bg-gray-50 rounded">
                                <p className="text-xl font-bold text-blue-600">{archiveStats.retention_days || 90}d</p>
                                <p className="text-xs text-gray-500">Archive After</p>
                              </div>
                            </div>
                            {archiveStats.oldest_archive_date && (
                              <p className="text-xs text-gray-500 mt-3 text-center">
                                Archive range: {archiveStats.oldest_archive_date} to {archiveStats.newest_archive_date}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>

                {/* HIPAA Disclosure Report Modal */}
                <Dialog open={showHIPAAReportModal} onOpenChange={setShowHIPAAReportModal}>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-teal-600" />
                        HIPAA Accounting of Disclosures Report
                      </DialogTitle>
                      <DialogDescription>
                        Complete audit history for the selected client
                      </DialogDescription>
                    </DialogHeader>
                    
                    {securityClientReportLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                        <span className="ml-2">Generating report...</span>
                      </div>
                    ) : securityClientReport ? (
                      <div className="space-y-4 py-4">
                        {/* Report Header */}
                        <div className="p-4 bg-gray-50 rounded-lg border">
                          <h3 className="font-semibold text-lg">{securityClientReport.client_name}</h3>
                          {securityClientReport.client_mrn && (
                            <p className="text-sm text-gray-600">MRN: {formatMrn(securityClientReport.client_mrn)}</p>
                          )}
                          <p className="text-sm text-gray-600">Client ID: {securityClientReport.client_id}</p>
                          <p className="text-sm text-gray-600 mt-2">
                            Report Period: Last {securityClientReport.days_covered} days • 
                            Total Accesses: {securityClientReport.total}
                          </p>
                          {securityClientReport.includes_archive && (
                            <div className="mt-2 inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              <Database className="h-3 w-3" />
                              Includes archived data ({securityClientReport.from_archive} from GCS, {securityClientReport.from_mongodb} recent)
                            </div>
                          )}
                        </div>
                        
                        {/* Export Buttons */}
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              // Generate CSV
                              const headers = ['Date/Time', 'User', 'Role', 'Action', 'Details'];
                              const rows = securityClientReport.logs.map(log => [
                                new Date(log.timestamp).toLocaleString(),
                                log.user_name || log.user_id,
                                log.user_role || 'N/A',
                                log.action,
                                JSON.stringify(log.details || {})
                              ]);
                              const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
                              const blob = new Blob([csv], { type: 'text/csv' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `HIPAA_Disclosure_${securityClientReport.client_name?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
                              a.click();
                              URL.revokeObjectURL(url);
                              toast.success('CSV downloaded');
                            }}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Export CSV
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              // Generate printable HTML report in new window
                              const printWindow = window.open('', '_blank');
                              const report = securityClientReport;
                              
                              const tableRows = report.logs.map(log => {
                                const isHighRisk = ['VIEW_SSN', 'reveal_ssn', 'export', 'export_csv', 'generate_bundle'].includes(log.action);
                                return `
                                  <tr style="${isHighRisk ? 'background-color: #FEF2F2;' : ''}">
                                    <td style="padding: 8px; border: 1px solid #ddd; font-size: 12px;">${new Date(log.timestamp).toLocaleString()}</td>
                                    <td style="padding: 8px; border: 1px solid #ddd;">${log.user_name || log.user_id}</td>
                                    <td style="padding: 8px; border: 1px solid #ddd;">${log.user_role || 'N/A'}</td>
                                    <td style="padding: 8px; border: 1px solid #ddd; ${isHighRisk ? 'color: #DC2626; font-weight: bold;' : ''}">${isHighRisk ? '⚠️ ' : ''}${log.action}</td>
                                    <td style="padding: 8px; border: 1px solid #ddd; font-size: 11px; max-width: 200px; overflow: hidden;">${JSON.stringify(log.details || {}).substring(0, 50)}</td>
                                  </tr>
                                `;
                              }).join('');
                              
                              printWindow.document.write(`
                                <!DOCTYPE html>
                                <html>
                                <head>
                                  <title>HIPAA Accounting of Disclosures - ${report.client_name}</title>
                                  <style>
                                    body { font-family: Arial, sans-serif; padding: 20px; }
                                    h1 { color: #0D9488; margin-bottom: 5px; }
                                    .header { border-bottom: 2px solid #0D9488; padding-bottom: 15px; margin-bottom: 20px; }
                                    .meta { color: #666; font-size: 14px; margin: 5px 0; }
                                    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                                    th { background-color: #F3F4F6; padding: 10px 8px; border: 1px solid #ddd; text-align: left; font-size: 13px; }
                                    .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
                                    .archive-badge { background: #DBEAFE; color: #1E40AF; padding: 4px 8px; border-radius: 4px; font-size: 12px; display: inline-block; margin-top: 10px; }
                                    @media print { body { padding: 0; } }
                                  </style>
                                </head>
                                <body>
                                  <div class="header">
                                    <h1>HIPAA Accounting of Disclosures Report</h1>
                                    <p class="meta"><strong>Client:</strong> ${report.client_name}</p>
                                    ${report.client_mrn ? `<p class="meta"><strong>MRN:</strong> ${report.client_mrn}</p>` : ''}
                                    <p class="meta"><strong>Client ID:</strong> ${report.client_id}</p>
                                    <p class="meta"><strong>Report Period:</strong> Last ${report.days_covered} days</p>
                                    <p class="meta"><strong>Total Access Records:</strong> ${report.total}</p>
                                    ${report.includes_archive ? `<span class="archive-badge">📁 Includes archived data (${report.from_archive} from archive, ${report.from_mongodb} recent)</span>` : ''}
                                  </div>
                                  
                                  <table>
                                    <thead>
                                      <tr>
                                        <th>Date/Time</th>
                                        <th>Accessed By</th>
                                        <th>Role</th>
                                        <th>Action</th>
                                        <th>Details</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      ${tableRows || '<tr><td colspan="5" style="text-align: center; padding: 20px;">No access records found</td></tr>'}
                                    </tbody>
                                  </table>
                                  
                                  <div class="footer">
                                    <p><strong>Report Generated:</strong> ${new Date().toLocaleString()}</p>
                                    <p><strong>Generated By:</strong> ${user?.full_name || user?.email || 'Administrator'}</p>
                                    <p style="margin-top: 10px; font-style: italic;">
                                      This report is generated for HIPAA Accounting of Disclosures compliance purposes (45 CFR § 164.528). 
                                      All access to protected health information (PHI) is logged and audited.
                                    </p>
                                  </div>
                                </body>
                                </html>
                              `);
                              
                              printWindow.document.close();
                              printWindow.focus();
                              
                              // Trigger print after content loads
                              setTimeout(() => {
                                printWindow.print();
                              }, 250);
                            }}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Print Report
                          </Button>
                        </div>
                        
                        {/* Disclosure Log Table */}
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="text-left px-4 py-2 font-medium">Date/Time</th>
                                <th className="text-left px-4 py-2 font-medium">Accessed By</th>
                                <th className="text-left px-4 py-2 font-medium">Role</th>
                                <th className="text-left px-4 py-2 font-medium">Action</th>
                                <th className="text-left px-4 py-2 font-medium">Details</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {securityClientReport.logs.map((log, idx) => {
                                const isHighRisk = ['VIEW_SSN', 'reveal_ssn', 'export', 'export_csv', 'generate_bundle'].includes(log.action);
                                return (
                                  <tr key={idx} className={isHighRisk ? 'bg-red-50' : ''}>
                                    <td className="px-4 py-2 whitespace-nowrap text-xs">
                                      {new Date(log.timestamp).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-2 font-medium">{log.user_name || log.user_id}</td>
                                    <td className="px-4 py-2 text-gray-600">{log.user_role || 'N/A'}</td>
                                    <td className="px-4 py-2">
                                      <span className={isHighRisk ? 'text-red-600 font-medium' : ''}>
                                        {isHighRisk && '⚠️ '}{log.action}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-500 max-w-[150px] truncate">
                                      {JSON.stringify(log.details || {}).substring(0, 40)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        
                        {securityClientReport.logs.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            No audit records found for this client in the specified period.
                          </div>
                        )}
                        
                        {/* Report Footer */}
                        <div className="p-4 bg-gray-50 rounded-lg border text-xs text-gray-600">
                          <p><strong>Report Generated:</strong> {new Date().toLocaleString()}</p>
                          <p><strong>Generated By:</strong> {user?.full_name || user?.email}</p>
                          <p className="mt-2 italic">
                            This report is generated for HIPAA Accounting of Disclosures compliance purposes. 
                            All access to protected health information (PHI) is logged and audited.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        Select a client and click "Generate Report" to view their audit history.
                      </div>
                    )}
                  </DialogContent>
                </Dialog>

                {/* Waitlist Communication Settings - Compact Card with Dialog */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Waitlist Communication
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">Welcome &amp; check-in emails</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setShowWaitlistCommunicationSettings(true)}>
                      <Settings className="h-4 w-4 mr-1" />
                      Manage
                    </Button>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      {waitlistSettings.enabled ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Enabled • Every {waitlistSettings.check_in_frequency_days || 14} days</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-gray-400" />
                          <span>Disabled</span>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Waitlist Communication Settings Dialog */}
                <Dialog open={showWaitlistCommunicationSettings} onOpenChange={setShowWaitlistCommunicationSettings}>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader className="flex flex-row items-center justify-between">
                      <div>
                        <DialogTitle className="flex items-center gap-2">
                          <Clock className="h-5 w-5" />
                          Waitlist Communication Settings
                        </DialogTitle>
                        <DialogDescription>
                          Configure welcome and check-in emails for waitlist clients
                        </DialogDescription>
                      </div>
                      <AdminJumpToDropdown currentDialogId="waitlistCommunication" isDirty={currentAdminDialogDirty} />
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {/* Welcome Email Section */}
                          <div className="border rounded-lg p-4 bg-blue-50">
                            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              Welcome Email (Sent When Added to Waitlist)
                            </h3>
                            
                            <div className="space-y-4">
                              <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                <div>
                                  <p className="font-medium text-sm">Enable Welcome Email</p>
                                  <p className="text-xs text-gray-500">Send email when client is first added to waitlist</p>
                                </div>
                                <Switch
                                  checked={waitlistSettings.welcome_email_enabled}
                                  onCheckedChange={(checked) => setWaitlistSettings(prev => ({ ...prev, welcome_email_enabled: checked }))}
                                />
                              </div>

                              {waitlistSettings.welcome_email_enabled && (
                                <>
                                  <div className="space-y-2">
                                    <Label className="font-medium">Subject Line</Label>
                                    <DebouncedInput
                                      value={waitlistSettings.welcome_email_subject}
                                      onChange={(e) => setWaitlistSettings(prev => ({ ...prev, welcome_email_subject: e.target.value }))}
                                      placeholder="Welcome to Our Waitlist"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <Label className="font-medium">Email Body</Label>
                                    <p className="text-xs text-gray-500">
                                      Available variables: {`{{client_name}}, {{company_name}}, {{update_link}}, {{remove_link}}, {{mrn}}`}
                                    </p>
                                    <DebouncedTextarea
                                      className="w-full p-3 border rounded-md text-sm font-mono bg-white"
                                      rows={12}
                                      value={waitlistSettings.welcome_email_body}
                                      onChange={(e) => setWaitlistSettings(prev => ({ ...prev, welcome_email_body: e.target.value }))}
                                      placeholder="Enter your welcome email template..."
                                    />
                                  </div>

                                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                    <p className="text-xs text-yellow-800">
                                      <strong>Tip:</strong> Use **text** for bold formatting. The links for updating info and removing from waitlist will be automatically included based on your settings below.
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Check-in Email Section */}
                          <div className="border rounded-lg p-4 bg-teal-50">
                            <h3 className="font-semibold text-teal-900 mb-3 flex items-center gap-2">
                              <RefreshCw className="h-4 w-4" />
                              Check-in Emails (Periodic Follow-ups)
                            </h3>
                            
                            <div className="space-y-4">
                              {/* Enable/Disable Automated Emails */}
                              <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                <div>
                                  <p className="font-medium text-sm">Enable Automated Check-ins</p>
                                  <p className="text-xs text-gray-500">Automatically email waitlist clients to confirm interest</p>
                                </div>
                                <Switch
                                  checked={waitlistSettings.enabled}
                                  onCheckedChange={(checked) => setWaitlistSettings(prev => ({ ...prev, enabled: checked }))}
                                />
                              </div>

                              {waitlistSettings.enabled && (
                                <>
                                  {/* Check-in Frequency */}
                                  <div className="space-y-2">
                                    <Label className="font-medium">Check-in Frequency (days)</Label>
                                    <p className="text-xs text-gray-500">How often to send &quot;Are you still interested?&quot; emails</p>
                                    <Input
                                      type="number"
                                      min="7"
                                      max="90"
                                      value={waitlistSettings.check_in_frequency_days}
                                      onChange={(e) => setWaitlistSettings(prev => ({ ...prev, check_in_frequency_days: parseInt(e.target.value) || 14 }))}
                                      className="w-32"
                                    />
                                  </div>

                              {/* Max Check-ins */}
                              <div className="space-y-2">
                                <Label className="font-medium">Maximum Check-ins</Label>
                                <p className="text-xs text-gray-500">Stop sending after this many unanswered emails (0 = unlimited)</p>
                                <Input
                                  type="number"
                                  min="0"
                                  max="20"
                                  value={waitlistSettings.max_check_ins}
                                  onChange={(e) => setWaitlistSettings(prev => ({ ...prev, max_check_ins: parseInt(e.target.value) || 0 }))}
                                  className="w-32"
                                />
                              </div>

                              {/* Email Content */}
                                  <div className="space-y-2">
                                    <Label className="font-medium">Email Subject</Label>
                                    <DebouncedInput
                                      value={waitlistSettings.email_subject}
                                      onChange={(e) => setWaitlistSettings(prev => ({ ...prev, email_subject: e.target.value }))}
                                      placeholder="Are you still interested in services?"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <Label className="font-medium">Email Introduction</Label>
                                    <DebouncedTextarea
                                      className="w-full p-2 border rounded-md text-sm"
                                      rows={2}
                                      value={waitlistSettings.email_intro}
                                      onChange={(e) => setWaitlistSettings(prev => ({ ...prev, email_intro: e.target.value }))}
                                      placeholder="We wanted to check in with you about your place on our waitlist."
                                    />
                                  </div>

                                  {/* Self-Service Links */}
                                  <div className="space-y-3 pt-4 border-t">
                                    <Label className="font-medium">Self-Service Options in Emails</Label>
                                    
                                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                      <div>
                                        <p className="font-medium text-sm">Include &quot;Confirm Interest&quot; Link</p>
                                        <p className="text-xs text-gray-500">Clients can confirm they want to stay on waitlist</p>
                                      </div>
                                      <Switch
                                        checked={waitlistSettings.include_confirm_link}
                                        onCheckedChange={(checked) => setWaitlistSettings(prev => ({ ...prev, include_confirm_link: checked }))}
                                      />
                                    </div>

                                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                      <div>
                                        <p className="font-medium text-sm">Include &quot;Remove Me&quot; Link</p>
                                        <p className="text-xs text-gray-500">Clients can remove themselves from waitlist</p>
                                      </div>
                                      <Switch
                                        checked={waitlistSettings.include_remove_link}
                                        onCheckedChange={(checked) => setWaitlistSettings(prev => ({ ...prev, include_remove_link: checked }))}
                                      />
                                    </div>

                                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                      <div>
                                        <p className="font-medium text-sm">Include &quot;Update Info&quot; Link</p>
                                        <p className="text-xs text-gray-500">Clients can update their contact information</p>
                                      </div>
                                      <Switch
                                        checked={waitlistSettings.include_update_info_link}
                                        onCheckedChange={(checked) => setWaitlistSettings(prev => ({ ...prev, include_update_info_link: checked }))}
                                      />
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <Button
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/admin/waitlist-settings`, waitlistSettings);
                                toast.success('Waitlist settings saved');
                                setShowWaitlistCommunicationSettings(false);
                              } catch (error) {
                                toast.error('Failed to save waitlist settings');
                              }
                            }}
                            className="w-full bg-teal-600 hover:bg-teal-700"
                          >
                            Save Waitlist Settings
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                {/* Bulk Communications Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Mail className="h-5 w-5 text-teal-600" />
                        Bulk Communications
                      </CardTitle>
                      <Button 
                        size="sm" 
                        className="bg-teal-600 hover:bg-teal-700"
                        onClick={() => {
                          setBulkCommFilters({
                            statuses: ['active'],
                            provider_ids: [],
                            service_type_ids: [],
                            label_ids: [],
                            last_appointment_from: '',
                            last_appointment_to: ''
                          });
                          setBulkCommSubject('');
                          setBulkCommMessage('');
                          setBulkCommSendEmail(false);
                          setBulkCommSendSms(false);
                          setBulkCommPreview(null);
                          fetchMessageTemplates();
                          setShowBulkCommunicationsDialog(true);
                        }}
                        data-testid="send-bulk-comm-btn"
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Send Message
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-2">
                    <p className="text-sm text-gray-600">
                      Send announcements to filtered groups of clients via portal, email, or SMS.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          fetchBulkCommHistory();
                          setShowBulkCommHistoryDialog(true);
                        }}
                      >
                        <Clock className="h-4 w-4 mr-1" />
                        View History
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          fetchMessageTemplates();
                          setShowMessageTemplatesDialog(true);
                        }}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Templates
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Client Labels Management */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Tag className="h-5 w-5" />
                        Client Labels
                      </CardTitle>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          className="bg-teal-600 hover:bg-teal-700"
                          onClick={() => {
                            setNewLabelData({ name: '', category: 'general', color: '#3b82f6' });
                            setShowClientLabelsDialog(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Label
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setShowClientLabelsDialog(true)}
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Manage Labels
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">Total Labels: {clientLabels.length}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {clientLabels.slice(0, 5).map(label => (
                        <span 
                          key={label.id}
                          className="px-2 py-1 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: label.color }}
                        >
                          {label.name}
                        </span>
                      ))}
                      {clientLabels.length > 5 && (
                        <span className="text-xs text-gray-500 self-center">+{clientLabels.length - 5} more</span>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Note Automation */}
                {(isSuperAdmin(user)) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Note Automation</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                          Auto-create draft notes for today's appointments based on CPT code templates
                        </p>
                        <Button
                          onClick={async () => {
                            try {
                              const response = await axios.post(`${API}/appointments/auto-create-notes-for-today`);
                              toast.success(
                                `Notes created: ${response.data.notes_created}, ` +
                                `Already exist: ${response.data.notes_already_exist}, ` +
                                `No template: ${response.data.no_template}`
                              );
                              refreshAppointments(true); refreshStats();
                            } catch (error) {
                              toast.error('Failed to auto-create notes');
                            }
                          }}
                          className="w-full"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Create Notes for Today's Appointments
                        </Button>
                        <p className="text-xs text-gray-500">
                          This will create draft notes for all appointments scheduled today that have CPT codes with assigned templates.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Quick Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">System Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Total Users</span>
                        <span className="font-semibold">{stats?.total_users || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Active Clients</span>
                        <span className="font-semibold">{stats?.total_clients || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">This Month Appointments</span>
                        <span className="font-semibold">{stats?.total_appointments || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Intake Form Configuration */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Intake Form Configuration</CardTitle>
                    <Dialog open={showFormConfigDialog} onOpenChange={setShowFormConfigDialog}>
                      <DialogTrigger asChild>
                        <Button 
                          size="sm" 
                          className="bg-teal-600 hover:bg-teal-700"
                          onClick={() => {
                            setShowFormConfigDialog(true);
                            if (!intakeFormConfig) fetchIntakeFormConfig();
                          }}
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Configure
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Configure Intake Form</DialogTitle>
                          <p className="text-sm text-gray-600 mt-2">
                            Manage form fields grouped by sections. The intake form displays as a single scrollable page.
                          </p>
                        </DialogHeader>

                        {intakeFormConfig && (
                          <div className="mt-4 space-y-6">
                            {/* Section-based Layout */}
                            <Tabs defaultValue={intakeFormConfig.steps?.[0]?.step_id || "basic_info"} className="w-full">
                              <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 mb-4">
                                {intakeFormConfig.steps?.map(section => (
                                  <TabsTrigger key={section.step_id} value={section.step_id}>
                                    {section.step_name}
                                  </TabsTrigger>
                                ))}
                              </TabsList>

                              {intakeFormConfig.steps?.map(section => (
                                <TabsContent key={section.step_id} value={section.step_id} className="space-y-4">
                                  <div className="border-b pb-3">
                                    <div className="flex justify-between items-start mb-2">
                                      <div>
                                        <h3 className="text-lg font-semibold">{section.step_name}</h3>
                                        <p className="text-sm text-gray-600">{section.description}</p>
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          // Add new field to this section
                                          const newField = {
                                            field_id: `field_${Date.now()}`,
                                            field_name: 'New Field',
                                            field_type: 'text',
                                            required: false,
                                            visible: true,
                                            show_on_public_portal: true,
                                            placeholder: '',
                                            options: []
                                          };
                                          const updatedConfig = {...intakeFormConfig};
                                          const sectionIndex = updatedConfig.steps.findIndex(s => s.step_id === section.step_id);
                                          updatedConfig.steps[sectionIndex].fields.push(newField);
                                          setIntakeFormConfig(updatedConfig);
                                        }}
                                      >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add Field
                                      </Button>
                                    </div>
                                    {/* Section-level Public Portal Toggle */}
                                    <div className="flex items-center space-x-2 mt-2 p-2 bg-teal-50 rounded">
                                      <Checkbox
                                        id={`section-public-${section.step_id}`}
                                        checked={section.show_on_public_portal !== false}
                                        onCheckedChange={(checked) => {
                                          const updatedConfig = {...intakeFormConfig};
                                          const sectionIndex = updatedConfig.steps.findIndex(s => s.step_id === section.step_id);
                                          updatedConfig.steps[sectionIndex].show_on_public_portal = checked;
                                          setIntakeFormConfig(updatedConfig);
                                        }}
                                      />
                                      <Label htmlFor={`section-public-${section.step_id}`} className="text-sm cursor-pointer text-teal-700 font-medium">
                                        <Globe className="h-4 w-4 inline mr-1" />
                                        Show this entire section on Public Portal
                                      </Label>
                                    </div>
                                  </div>

                                  {/* Fields List */}
                                  <div className="space-y-3">
                                    {section.fields?.map((field, index) => (
                                      field.system_required ? (
                                        <div key={field.field_id} className="border rounded-lg p-4 bg-gray-50 flex items-center justify-between" data-testid={`locked-field-${field.field_id}`}>
                                          <div className="flex items-center gap-3">
                                            <Lock className="h-4 w-4 text-gray-400" />
                                            <div>
                                              <p className="text-sm font-medium text-gray-700">{field.field_name}</p>
                                              <p className="text-[11px] text-gray-500">System required — managed by CareVaultPro. This field can&apos;t be edited or removed.</p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            {field.required && <Badge variant="outline" className="text-[10px]">Required</Badge>}
                                            <Badge className="bg-gray-200 text-gray-700 text-[10px]">Always visible</Badge>
                                          </div>
                                        </div>
                                      ) : (
                                      <div key={field.field_id} className="border rounded-lg p-4 space-y-3 bg-white hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1 grid grid-cols-3 gap-4">
                                            {/* Field Name */}
                                            <div>
                                              <Label className="text-xs text-gray-600 font-semibold">Field Label</Label>
                                              <DebouncedInput
                                                value={field.field_name}
                                                onChange={(e) => updateFieldInConfig(section.step_id, field.field_id, { field_name: e.target.value })}
                                                className="mt-1"
                                                placeholder="e.g., Full Name"
                                              />
                                            </div>

                                            {/* Field Type */}
                                            <div>
                                              <Label className="text-xs text-gray-600 font-semibold">Field Type</Label>
                                              <Select
                                                value={field.field_type}
                                                onValueChange={(value) => updateFieldInConfig(section.step_id, field.field_id, { field_type: value })}
                                              >
                                                <SelectTrigger className="mt-1">
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="text">Text Input</SelectItem>
                                                  <SelectItem value="email">Email</SelectItem>
                                                  <SelectItem value="phone">Phone Number</SelectItem>
                                                  <SelectItem value="date">Date Picker</SelectItem>
                                                  <SelectItem value="select">Dropdown</SelectItem>
                                                  <SelectItem value="textarea">Text Area</SelectItem>
                                                  <SelectItem value="checkbox">Checkbox</SelectItem>
                                                  <SelectItem value="radio">Radio Buttons</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            </div>

                                            {/* Field Settings */}
                                            <div className="space-y-2">
                                              <Label className="text-xs text-gray-600 font-semibold">Settings</Label>
                                              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                                <div className="flex items-center space-x-2">
                                                  <Checkbox
                                                    id={`required-${field.field_id}`}
                                                    checked={field.required}
                                                    onCheckedChange={(checked) => updateFieldInConfig(section.step_id, field.field_id, { required: checked })}
                                                  />
                                                  <Label htmlFor={`required-${field.field_id}`} className="text-xs cursor-pointer">Required</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                  <Checkbox
                                                    id={`visible-${field.field_id}`}
                                                    checked={field.visible}
                                                    onCheckedChange={(checked) => updateFieldInConfig(section.step_id, field.field_id, { visible: checked })}
                                                  />
                                                  <Label htmlFor={`visible-${field.field_id}`} className="text-xs cursor-pointer">Visible</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                  <Checkbox
                                                    id={`public-${field.field_id}`}
                                                    checked={field.show_on_public_portal !== false}
                                                    onCheckedChange={(checked) => updateFieldInConfig(section.step_id, field.field_id, { show_on_public_portal: checked })}
                                                  />
                                                  <Label htmlFor={`public-${field.field_id}`} className="text-xs cursor-pointer text-teal-600">Show on Public Portal</Label>
                                                </div>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Action Buttons */}
                                          <div className="flex items-center space-x-2 ml-4">
                                            {/* Reorder Buttons */}
                                            <div className="flex flex-col space-y-1">
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => reorderField(section.step_id, field.field_id, 'up')}
                                                disabled={index === 0}
                                                className="h-7 w-7 p-0"
                                                title="Move up"
                                              >
                                                <ChevronUp className="h-4 w-4" />
                                              </Button>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => reorderField(section.step_id, field.field_id, 'down')}
                                                disabled={index === section.fields.length - 1}
                                                className="h-7 w-7 p-0"
                                                title="Move down"
                                              >
                                                <ChevronDown className="h-4 w-4" />
                                              </Button>
                                            </div>
                                            {/* Delete Button */}
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => {
                                                if (window.confirm(`Delete field "${field.field_name}"?`)) {
                                                  const updatedConfig = {...intakeFormConfig};
                                                  const sectionIndex = updatedConfig.steps.findIndex(s => s.step_id === section.step_id);
                                                  updatedConfig.steps[sectionIndex].fields = updatedConfig.steps[sectionIndex].fields.filter(f => f.field_id !== field.field_id);
                                                  setIntakeFormConfig(updatedConfig);
                                                }
                                              }}
                                              className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                                              title="Delete field"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </div>
                                        </div>

                                        {/* Dropdown Options Editor (for select/radio fields) */}
                                        {(field.field_type === 'select' || field.field_type === 'radio') && (
                                          <div className="border-t pt-3 mt-3 bg-gray-50 -m-4 p-4 rounded-b-lg">
                                            <Label className="text-xs text-gray-600 font-semibold mb-2 block">
                                              {field.field_type === 'select' ? 'Dropdown' : 'Radio'} Options
                                            </Label>
                                            <div className="space-y-2">
                                              {field.options?.map((option, optIndex) => (
                                                <div key={optIndex} className="flex items-center space-x-2">
                                                  <DebouncedInput
                                                    value={option}
                                                    onChange={(e) => {
                                                      const newOptions = [...field.options];
                                                      newOptions[optIndex] = e.target.value;
                                                      updateFieldInConfig(section.step_id, field.field_id, { options: newOptions });
                                                    }}
                                                    className="flex-1"
                                                    placeholder={`Option ${optIndex + 1}`}
                                                  />
                                                  <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => removeOptionFromField(section.step_id, field.field_id, optIndex)}
                                                    className="h-8 w-8 p-0"
                                                  >
                                                    <X className="h-4 w-4" />
                                                  </Button>
                                                </div>
                                              ))}
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => addOptionToField(section.step_id, field.field_id, 'New Option')}
                                                className="w-full"
                                              >
                                                <Plus className="h-4 w-4 mr-1" />
                                                Add Option
                                              </Button>
                                            </div>
                                          </div>
                                        )}

                                        {/* Placeholder (for text-based fields) */}
                                        {(field.field_type === 'text' || field.field_type === 'email' || field.field_type === 'phone' || field.field_type === 'textarea') && (
                                          <div className="border-t pt-3 mt-3">
                                            <Label className="text-xs text-gray-600 font-semibold">Placeholder Text</Label>
                                            <DebouncedInput
                                              value={field.placeholder || ''}
                                              onChange={(e) => updateFieldInConfig(section.step_id, field.field_id, { placeholder: e.target.value })}
                                              className="mt-1"
                                              placeholder="e.g., Enter your full name"
                                            />
                                          </div>
                                        )}
                                      </div>
                                      )
                                    ))}
                                    
                                    {section.fields?.length === 0 && (
                                      <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                                        <p className="mb-2">No fields in this section</p>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            const newField = {
                                              field_id: `field_${Date.now()}`,
                                              field_name: 'New Field',
                                              field_type: 'text',
                                              required: false,
                                              visible: true,
                                              placeholder: '',
                                              options: []
                                            };
                                            const updatedConfig = {...intakeFormConfig};
                                            const sectionIndex = updatedConfig.steps.findIndex(s => s.step_id === section.step_id);
                                            updatedConfig.steps[sectionIndex].fields.push(newField);
                                            setIntakeFormConfig(updatedConfig);
                                          }}
                                        >
                                          <Plus className="h-4 w-4 mr-1" />
                                          Add First Field
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </TabsContent>
                              ))}
                            </Tabs>

                            {/* Save Button */}
                            <div className="flex justify-between items-center pt-4 border-t">
                              <p className="text-sm text-gray-600">
                                <strong>Note:</strong> Changes affect the intake form structure. Save carefully.
                              </p>
                              <div className="flex space-x-2">
                                <Button variant="outline" onClick={() => setShowFormConfigDialog(false)}>
                                  Cancel
                                </Button>
                                <Button onClick={saveIntakeFormConfig} className="bg-teal-600 hover:bg-teal-700">
                                  Save Configuration
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {!intakeFormConfig && (
                          <div className="text-center py-8">
                            <p className="text-gray-600">Loading configuration...</p>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">
                      Customize the intake form fields organized by sections. The form displays as a single scrollable page for clients.
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span>Sections</span>
                        <span className="font-semibold">{intakeFormConfig?.steps?.length || 9}</span>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span>Total Fields</span>
                        <span className="font-semibold">
                          {intakeFormConfig?.steps?.reduce((acc, step) => acc + step.fields.length, 0) || '60+'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span>Required Fields</span>
                        <span className="font-semibold">
                          {intakeFormConfig?.steps.reduce((acc, step) => 
                            acc + step.fields.filter(f => f.required).length, 0) || 8}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Organization & Location Management */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Organization Settings</CardTitle>
                    <Button
                      size="sm"
                      className="bg-teal-600 hover:bg-teal-700"
                      onClick={() => setShowOrgConfig(true)}
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      Configure
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-3 border rounded-lg">
                      <Label className="text-sm font-medium text-gray-700">Organization Name</Label>
                      <p className="text-sm">{organizations?.[0]?.name || 'Not configured'}</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <Label className="text-sm font-medium text-gray-700">Tax ID / NPI</Label>
                      <p className="text-sm">
                        {organizations?.[0]?.tax_id ? `Tax ID: ${organizations[0].tax_id}` : ''}
                        {organizations?.[0]?.tax_id && organizations?.[0]?.npis?.length > 0 ? ' | ' : ''}
                        {organizations?.[0]?.npis?.length > 0 ? `NPI: ${organizations[0].npis[0]}` : ''}
                        {!organizations?.[0]?.tax_id && (!organizations?.[0]?.npis || organizations[0].npis.length === 0) ? 'Not configured' : ''}
                      </p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <Label className="text-sm font-medium text-gray-700">Active Locations</Label>
                      <p className="text-sm">{locations.length} locations configured</p>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mt-2"
                        onClick={() => setShowManageLocations(true)}
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Manage Locations
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* POS Code Management */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg">POS (Place of Service) Codes</CardTitle>
                      {(isSuperAdmin(user)) && (
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            size="sm" 
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => setShowManagePOS(true)}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add POS Code
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setShowManagePOS(true)}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Manage POS Codes
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">{(getEffectivePosCodes(organizations, posCodes)).length || 0} POS codes configured</p>
                  </CardContent>
                </Card>

                {/* License Management */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg">Licenses & Certifications</CardTitle>
                      {(isSuperAdmin(user)) && (
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            size="sm" 
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => {
                              setEditingLicense && setEditingLicense(null);
                              setShowManageLicenses(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add License
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setShowManageLicenses(true)}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Manage Licenses
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">2 active licenses, 1 expiring soon</p>
                  </CardContent>
                </Card>

                {/* Client Portal */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg flex items-center">
                        <Globe className="h-5 w-5 mr-2 text-teal-600" />
                        Client Portal
                      </CardTitle>
                      {(isSuperAdmin(user)) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowManageClientPortal(true)}
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Manage
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-gray-600">
                      Public portal for service requests and client access
                    </p>
                    <div className="bg-gray-50 p-3 rounded border">
                      <p className="text-xs text-gray-600 mb-2">Portal URL:</p>
                      <div className="flex items-center space-x-2">
                        <code className="text-sm bg-white px-2 py-1 rounded border flex-1 truncate">
                          {window.location.origin}/portal
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/portal`);
                            toast.success("Portal URL copied to clipboard!");
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => window.openAuthenticatedTab('/portal')}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Visit Portal
                      </Button>
                      {user?.is_waitlist_manager && (
                        <Button
                          size="sm"
                          className="flex-1 bg-orange-600 hover:bg-orange-700"
                          onClick={() => changeTab('waitlist')}
                        >
                          View Waitlist
                          {pendingIntakeClients.length > 0 && (
                            <Badge className="ml-2 bg-white text-orange-600">
                              {pendingIntakeClients.length}
                            </Badge>
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Manage Client Portal Dialog */}
                <Dialog open={showManageClientPortal} onOpenChange={setShowManageClientPortal}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader className="flex flex-row items-center justify-between">
                      <div>
                        <DialogTitle className="flex items-center gap-2">
                          <Globe className="h-5 w-5 text-teal-600" />
                          Manage Client Portal
                        </DialogTitle>
                        <DialogDescription>
                          Configure portal link settings and client access options
                        </DialogDescription>
                      </div>
                      <AdminJumpToDropdown currentDialogId="clientPortal" isDirty={currentAdminDialogDirty} />
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {/* Portal URL and Quick Actions */}
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-xs text-gray-600 mb-2">Portal URL:</p>
                        <div className="flex items-center space-x-2 mb-3">
                          <code className="text-sm bg-white px-2 py-1 rounded border flex-1 truncate">
                            {window.location.origin}/portal
                          </code>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/portal`);
                              toast.success("Portal URL copied to clipboard!");
                            }}
                          >
                            Copy
                          </Button>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => window.openAuthenticatedTab('/portal')}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Visit Portal
                          </Button>
                          {user?.is_waitlist_manager && (
                            <Button
                              size="sm"
                              className="flex-1 bg-orange-600 hover:bg-orange-700"
                              onClick={() => {
                                setShowManageClientPortal(false);
                                setActiveTab('waitlist');
                              }}
                            >
                              View Waitlist
                              {pendingIntakeClients.length > 0 && (
                                <Badge className="ml-2 bg-white text-orange-600">
                                  {pendingIntakeClients.length}
                                </Badge>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Portal Settings */}
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-semibold mb-3">Portal Settings</h4>
                        <div className="space-y-4">
                          <div>
                            <Label className="text-sm font-medium">Magic Link Expiration</Label>
                            <Select 
                              value={String(portalSettings?.link_expiration_hours || 24)}
                              onValueChange={async (value) => {
                                try {
                                  await axios.post(`${API}/admin/client-portal-settings`, {
                                    link_expiration_hours: parseFloat(value),
                                    one_time_use: portalSettings?.one_time_use !== false,
                                    min_reschedule_notice_hours: portalSettings?.min_reschedule_notice_hours || 24
                                  });
                                  setPortalSettings({ ...portalSettings, link_expiration_hours: parseFloat(value) });
                                  toast.success('Portal settings updated');
                                } catch (error) {
                                  toast.error('Failed to update settings');
                                }
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0.25">15 minutes</SelectItem>
                                <SelectItem value="0.5">30 minutes</SelectItem>
                                <SelectItem value="1">1 hour</SelectItem>
                                <SelectItem value="2">2 hours</SelectItem>
                                <SelectItem value="4">4 hours</SelectItem>
                                <SelectItem value="8">8 hours</SelectItem>
                                <SelectItem value="24">24 hours</SelectItem>
                                <SelectItem value="72">72 hours (3 days)</SelectItem>
                                <SelectItem value="168">7 days</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 mt-1">How long portal links remain valid</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <Label className="text-sm font-medium">One-Time Use Links</Label>
                              <p className="text-xs text-gray-500">Links become invalid after first use</p>
                            </div>
                            <Button
                              size="sm"
                              variant={portalSettings?.one_time_use !== false ? "default" : "outline"}
                              onClick={async () => {
                                try {
                                  const newValue = portalSettings?.one_time_use === false;
                                  await axios.post(`${API}/admin/client-portal-settings`, {
                                    link_expiration_hours: portalSettings?.link_expiration_hours || 24,
                                    one_time_use: newValue,
                                    min_reschedule_notice_hours: portalSettings?.min_reschedule_notice_hours || 24
                                  });
                                  setPortalSettings({ ...portalSettings, one_time_use: newValue });
                                  toast.success(newValue ? 'One-time use enabled' : 'One-time use disabled');
                                } catch (error) {
                                  toast.error('Failed to update settings');
                                }
                              }}
                            >
                              {portalSettings?.one_time_use !== false ? 'Enabled' : 'Disabled'}
                            </Button>
                          </div>
                          <div>
                            <Label className="text-sm font-medium">Minimum Reschedule Notice</Label>
                            <Select 
                              value={String(portalSettings?.min_reschedule_notice_hours || 24)}
                              onValueChange={async (value) => {
                                try {
                                  await axios.post(`${API}/admin/client-portal-settings`, {
                                    link_expiration_hours: portalSettings?.link_expiration_hours || 24,
                                    one_time_use: portalSettings?.one_time_use !== false,
                                    min_reschedule_notice_hours: parseInt(value)
                                  });
                                  setPortalSettings({ ...portalSettings, min_reschedule_notice_hours: parseInt(value) });
                                  toast.success('Reschedule notice period updated');
                                } catch (error) {
                                  toast.error('Failed to update settings');
                                }
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">No restriction</SelectItem>
                                <SelectItem value="12">12 hours</SelectItem>
                                <SelectItem value="24">24 hours (1 day)</SelectItem>
                                <SelectItem value="48">48 hours (2 days)</SelectItem>
                                <SelectItem value="72">72 hours (3 days)</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 mt-1">How far in advance clients must request reschedule</p>
                          </div>
                        </div>
                      </div>

                      {/* Legal Documents (TOS/NPP) */}
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-semibold mb-3">Legal Documents</h4>
                        <p className="text-xs text-gray-500 mb-3">Manage Terms of Use and Notice of Privacy Practices shown to clients in the portal</p>
                        <div className="space-y-3">
                          <Button
                            variant="outline"
                            className="w-full justify-between"
                            onClick={() => setShowTosEditor(true)}
                          >
                            <span className="flex items-center gap-2">
                              <FileText className="h-4 w-4" />
                              Edit Terms of Use
                            </span>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full justify-between"
                            onClick={() => setShowNppEditor(true)}
                          >
                            <span className="flex items-center gap-2">
                              <Shield className="h-4 w-4" />
                              Edit Notice of Privacy Practices
                            </span>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Insurance Management */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg">Insurance Companies</CardTitle>
                      {(isSuperAdmin(user)) && (
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            size="sm" 
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => {
                              setEditingInsurance(null);
                              setShowAddInsurance(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Insurance
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => setShowManageInsurance(true)}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Manage Insurance
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      {insurances?.filter(ins => ins.is_active).length || 0} active companies, {insurances?.length || 0} total
                    </p>
                  </CardContent>
                </Card>

                {/* Conflict Rules */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Conflict Rules
                      </CardTitle>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          className="bg-teal-600 hover:bg-teal-700"
                          onClick={() => {
                            setShowRulesManagementDialog(true);
                            fetchRules();
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Rule
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setShowRulesManagementDialog(true);
                            fetchRules();
                          }}
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Manage Rules
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">CPT Conflict Rules</span>
                        <Badge variant="outline">{cptConflictRules.filter(r => r.is_active).length} active</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Insurance Restrictions</span>
                        <Badge variant="outline">{insuranceCptRestrictions.filter(r => r.is_active).length} active</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Eligibility & Batch Automation Settings */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5 text-green-600" />
                        Eligibility &amp; Batch Automation
                      </CardTitle>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowEligibilitySettings(true)}
                        data-testid="elig-manage-settings-btn"
                      >
                        <Settings className="h-4 w-4 mr-1" />
                        Manage Eligibility
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* On/Off toggle — clearly labeled so admins can flip
                        scheduled eligibility checks without hunting through
                        the settings dialog. Uses Shadcn Switch so it's
                        obviously interactive (the previous ✓/○ button was
                        being mistaken for a status badge). */}
                    <div className="flex items-center justify-between gap-3 p-3 mb-3 rounded-md border bg-gray-50">
                      <div className="flex flex-col">
                        <Label
                          htmlFor="auto-eligibility-toggle"
                          className="text-sm font-medium cursor-pointer"
                        >
                          Automated eligibility checks
                        </Label>
                        <span className="text-xs text-gray-500">
                          {eligibilitySettings?.enabled
                            ? 'Scheduled 270 checks are running on the configured schedule.'
                            : 'No scheduled 270 checks will run until this is turned on.'}
                        </span>
                      </div>
                      <Switch
                        id="auto-eligibility-toggle"
                        data-testid="auto-eligibility-toggle"
                        checked={!!eligibilitySettings?.enabled}
                        onCheckedChange={async (checked) => {
                          try {
                            await axios.put(`${API}/auto-eligibility-settings`, {
                              ...eligibilitySettings,
                              enabled: checked,
                            });
                            setEligibilitySettings({ ...(eligibilitySettings || {}), enabled: checked });
                            toast.success(
                              checked
                                ? 'Eligibility automation enabled'
                                : 'Eligibility automation disabled'
                            );
                          } catch (error) {
                            toast.error('Failed to update settings');
                          }
                        }}
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      {eligibilitySettings?.enabled 
                        ? `Runs daily at ${(eligibilitySettings?.check_times?.length > 0 ? eligibilitySettings.check_times.join(', ') : eligibilitySettings?.check_time) || '06:00'} for ${
                          eligibilitySettings?.check_scope === 'today' ? "today's appointments" :
                          eligibilitySettings?.check_scope === 'tomorrow' ? "tomorrow's appointments" :
                          eligibilitySettings?.check_scope === 'today_tomorrow' ? "today & tomorrow" :
                          eligibilitySettings?.check_scope === 'next_3_days' ? "next 3 days" :
                          eligibilitySettings?.check_scope === 'this_week' ? "this week" :
                          eligibilitySettings?.check_scope === 'next_week' ? "next 2 weeks" :
                          "today's appointments"
                        }`
                        : 'Automatic eligibility checks are disabled'}
                    </p>

                    {/* ─── Batch Automation section ─────────────────────
                        Recurring auto-submission of closed claim batches
                        to NEHEN / Availity. Uses the same dialog wired
                        elsewhere (showAutoSubmitSchedule) so the card
                        stays small and admins have one Manage button per
                        sub-domain. */}
                    <div className="border-t border-gray-200 mt-4 pt-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Send className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-semibold text-gray-900">
                            Batch Submission Schedule
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowAutoSubmitSchedule(true)}
                          data-testid="batch-automation-manage-btn"
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Manage Batch Schedule
                        </Button>
                      </div>
                      <p className="text-sm text-gray-600">
                        Configure recurring auto-submission of closed 837P
                        batches to NEHEN (TriZetto / Gateway EDI) and
                        Availity SFTP. Per-clearinghouse day-of-week +
                        time-of-day picker; missing-credential warnings
                        surface inline.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Eligibility Settings Dialog */}
                <Dialog open={showEligibilitySettings} onOpenChange={setShowEligibilitySettings}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader className="flex flex-row items-center justify-between">
                      <DialogTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-green-600" />
                        Eligibility Automation Settings
                      </DialogTitle>
                      <AdminJumpToDropdown currentDialogId="eligibility" isDirty={currentAdminDialogDirty} />
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Schedule Settings */}
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm font-medium">Scheduled Check Times</Label>
                          <p className="text-xs text-gray-500 mb-2">Add multiple times to run eligibility checks throughout the day</p>
                          
                          {/* List of scheduled times */}
                          <div className="space-y-2">
                            {(() => {
                              const times = eligibilitySettings?.check_times?.length > 0 
                                ? eligibilitySettings.check_times 
                                : [eligibilitySettings?.check_time || '06:00'];
                              return times.map((time, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <Input
                                  type="time"
                                  value={time || '06:00'}
                                  onChange={async (e) => {
                                    try {
                                      const newTimes = [...times];
                                      newTimes[index] = e.target.value;
                                      await axios.put(`${API}/auto-eligibility-settings`, {
                                        ...eligibilitySettings,
                                        check_times: newTimes,
                                        check_time: newTimes[0] // Keep legacy field in sync
                                      });
                                      setEligibilitySettings({ ...eligibilitySettings, check_times: newTimes, check_time: newTimes[0] });
                                      toast.success('Check time updated');
                                    } catch (error) {
                                      toast.error('Failed to update');
                                    }
                                  }}
                                  className="w-32"
                                />
                                {times.length > 1 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={async () => {
                                      try {
                                        const newTimes = times.filter((_, i) => i !== index);
                                        await axios.put(`${API}/auto-eligibility-settings`, {
                                          ...eligibilitySettings,
                                          check_times: newTimes,
                                          check_time: newTimes[0] // Keep legacy field in sync
                                        });
                                        setEligibilitySettings({ ...eligibilitySettings, check_times: newTimes, check_time: newTimes[0] });
                                        toast.success('Check time removed');
                                      } catch (error) {
                                        toast.error('Failed to remove time');
                                      }
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            ));
                            })()}
                          </div>
                          
                          {/* Add new time button */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={async () => {
                              try {
                                const currentTimes = eligibilitySettings?.check_times?.length > 0 
                                  ? eligibilitySettings.check_times 
                                  : [eligibilitySettings?.check_time || '06:00'];
                                const newTimes = [...currentTimes, '12:00'];
                                
                                // Get organization_id if missing
                                let orgId = eligibilitySettings?.organization_id;
                                if (!orgId && organizations?.length > 0) {
                                  orgId = organizations[0].id;
                                }
                                
                                const updateData = {
                                  ...eligibilitySettings,
                                  organization_id: orgId,
                                  check_times: newTimes,
                                  check_time: newTimes[0]
                                };
                                void 0 && console.log('Adding new time, sending:', updateData);
                                const response = await axios.put(`${API}/auto-eligibility-settings`, updateData);
                                void 0 && console.log('Response:', response.data);
                                setEligibilitySettings({ ...eligibilitySettings, check_times: newTimes, check_time: newTimes[0] });
                                toast.success('New check time added');
                              } catch (error) {
                                console.error('Failed to add time:', error.response?.data || error);
                                toast.error(error.response?.data?.detail || 'Failed to add time');
                              }
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Another Time
                          </Button>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium">Check Eligibility For</Label>
                          <p className="text-xs text-gray-500 mb-1">Which appointments to check at each scheduled time</p>
                          <Select
                            value={eligibilitySettings?.check_scope || 'today'}
                            onValueChange={async (value) => {
                              try {
                                // Map scope to days_ahead for backward compatibility
                                const daysMap = { 'today': 0, 'tomorrow': 1, 'today_tomorrow': 1, 'next_3_days': 2, 'this_week': 6, 'next_week': 13 };
                                await axios.put(`${API}/auto-eligibility-settings`, {
                                  ...eligibilitySettings,
                                  check_scope: value,
                                  days_ahead: daysMap[value] || 0
                                });
                                setEligibilitySettings({ ...eligibilitySettings, check_scope: value, days_ahead: daysMap[value] || 0 });
                                toast.success('Check scope updated');
                              } catch (error) {
                                console.error('Failed to update:', error);
                                toast.error('Failed to update');
                              }
                            }}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select what to check..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="today">Today's appointments only</SelectItem>
                              <SelectItem value="tomorrow">Tomorrow's appointments only</SelectItem>
                              <SelectItem value="today_tomorrow">Today & tomorrow</SelectItem>
                              <SelectItem value="next_3_days">Next 3 days</SelectItem>
                              <SelectItem value="this_week">This week (7 days)</SelectItem>
                              <SelectItem value="next_week">Next 2 weeks</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Days of week — multi-select day filter so you
                            can run, say, only Mondays. Empty = every day. */}
                        <div>
                          <Label className="text-sm font-medium">Run on these days</Label>
                          <p className="text-xs text-gray-500 mb-2">
                            Local time, weekday selection. Tap to toggle. Empty = run every day.
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {[
                              { idx: 0, label: 'Mon' }, { idx: 1, label: 'Tue' },
                              { idx: 2, label: 'Wed' }, { idx: 3, label: 'Thu' },
                              { idx: 4, label: 'Fri' }, { idx: 5, label: 'Sat' },
                              { idx: 6, label: 'Sun' },
                            ].map(({ idx, label }) => {
                              const days = eligibilitySettings?.days_of_week || [0,1,2,3,4];
                              const active = days.includes(idx);
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  data-testid={`elig-dow-${label.toLowerCase()}`}
                                  onClick={async () => {
                                    try {
                                      const next = active
                                        ? days.filter(d => d !== idx)
                                        : [...days, idx].sort((a, b) => a - b);
                                      await axios.put(`${API}/auto-eligibility-settings`, {
                                        ...eligibilitySettings,
                                        days_of_week: next,
                                      });
                                      setEligibilitySettings({ ...eligibilitySettings, days_of_week: next });
                                      toast.success('Schedule days updated');
                                    } catch {
                                      toast.error('Failed to update');
                                    }
                                  }}
                                  className={`px-3 py-1 rounded-full text-xs border transition ${
                                    active
                                      ? 'bg-green-600 text-white border-green-600'
                                      : 'bg-white text-gray-600 border-gray-300 hover:border-green-400'
                                  }`}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Timezone — what zone the check_times are
                            interpreted in. Stored as IANA name. */}
                        <div>
                          <Label className="text-sm font-medium">Timezone</Label>
                          <p className="text-xs text-gray-500 mb-2">
                            Scheduled times above are interpreted in this zone.
                          </p>
                          <Select
                            value={eligibilitySettings?.timezone || 'America/New_York'}
                            onValueChange={async (value) => {
                              try {
                                await axios.put(`${API}/auto-eligibility-settings`, {
                                  ...eligibilitySettings,
                                  timezone: value,
                                });
                                setEligibilitySettings({ ...eligibilitySettings, timezone: value });
                                toast.success('Timezone updated');
                              } catch {
                                toast.error('Failed to update');
                              }
                            }}
                          >
                            <SelectTrigger className="mt-1" data-testid="elig-timezone-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="America/New_York">Eastern (New York)</SelectItem>
                              <SelectItem value="America/Chicago">Central (Chicago)</SelectItem>
                              <SelectItem value="America/Denver">Mountain (Denver)</SelectItem>
                              <SelectItem value="America/Phoenix">Mountain — Arizona (no DST)</SelectItem>
                              <SelectItem value="America/Los_Angeles">Pacific (Los Angeles)</SelectItem>
                              <SelectItem value="America/Anchorage">Alaska (Anchorage)</SelectItem>
                              <SelectItem value="Pacific/Honolulu">Hawaii (Honolulu)</SelectItem>
                              <SelectItem value="UTC">UTC</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Notification Options */}
                      <div className="border rounded-lg p-3">
                        <Label className="text-sm font-medium mb-2 block">When Eligibility is Inactive:</Label>
                        <div className="space-y-2">
                          <label className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={eligibilitySettings?.create_task_on_failure !== false}
                              onChange={async (e) => {
                                try {
                                  await axios.put(`${API}/auto-eligibility-settings`, {
                                    ...eligibilitySettings,
                                    create_task_on_failure: e.target.checked
                                  });
                                  setEligibilitySettings({ ...eligibilitySettings, create_task_on_failure: e.target.checked });
                                } catch (error) {
                                  toast.error('Failed to update');
                                }
                              }}
                              className="rounded"
                            />
                            <span>Create task for provider</span>
                          </label>
                          <label className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={eligibilitySettings?.notify_scheduler !== false}
                              onChange={async (e) => {
                                try {
                                  await axios.put(`${API}/auto-eligibility-settings`, {
                                    ...eligibilitySettings,
                                    notify_scheduler: e.target.checked
                                  });
                                  setEligibilitySettings({ ...eligibilitySettings, notify_scheduler: e.target.checked });
                                } catch (error) {
                                  toast.error('Failed to update');
                                }
                              }}
                              className="rounded"
                            />
                            <span>Notify billing/scheduler team</span>
                          </label>
                          <label className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={eligibilitySettings?.notify_client_portal === true}
                              onChange={async (e) => {
                                try {
                                  await axios.put(`${API}/auto-eligibility-settings`, {
                                    ...eligibilitySettings,
                                    notify_client_portal: e.target.checked
                                  });
                                  setEligibilitySettings({ ...eligibilitySettings, notify_client_portal: e.target.checked });
                                } catch (error) {
                                  toast.error('Failed to update');
                                }
                              }}
                              className="rounded"
                            />
                            <span>Notify client via portal</span>
                          </label>
                        </div>
                      </div>
                      
                      {/* Stale Threshold */}
                      <div>
                        <Label className="text-sm font-medium">Re-check After (Hours)</Label>
                        <Select
                          value={String(eligibilitySettings?.stale_hours || 12)}
                          onValueChange={async (value) => {
                            try {
                              await axios.put(`${API}/auto-eligibility-settings`, {
                                ...eligibilitySettings,
                                stale_hours: parseInt(value)
                              });
                              setEligibilitySettings({ ...eligibilitySettings, stale_hours: parseInt(value) });
                              toast.success('Stale threshold updated');
                            } catch (error) {
                              toast.error('Failed to update');
                            }
                          }}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="12">12 hours</SelectItem>
                            <SelectItem value="24">24 hours</SelectItem>
                            <SelectItem value="48">48 hours</SelectItem>
                            <SelectItem value="72">72 hours</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">Skip clients checked within this window</p>
                      </div>
                    </div>
                    <div className="flex justify-end mt-4">
                      <Button variant="outline" onClick={() => setShowEligibilitySettings(false)}>
                        Close
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Payment Reminders Automation */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CreditCard className="h-5 w-5 text-amber-600" />
                        Payment Reminders
                      </CardTitle>
                      {(isSuperAdmin(user) || user?.is_billing_manager) && (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            className={paymentReminderSettings?.enabled ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 hover:bg-gray-500'}
                            onClick={async () => {
                              try {
                                const newEnabled = !paymentReminderSettings?.enabled;
                                await axios.put(`${API}/payment-reminder-settings`, {
                                  ...paymentReminderSettings,
                                  enabled: newEnabled
                                });
                                setPaymentReminderSettings({ ...paymentReminderSettings, enabled: newEnabled });
                                toast.success(newEnabled ? 'Payment reminders enabled' : 'Payment reminders disabled');
                              } catch (error) {
                                toast.error('Failed to update settings');
                              }
                            }}
                          >
                            {paymentReminderSettings?.enabled ? '✓ Enabled' : '○ Disabled'}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              fetchPaymentReminderSettings();
                              setShowPaymentReminderSettings(true);
                            }}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Manage Settings
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      {paymentReminderSettings?.enabled 
                        ? `Sends reminders every ${paymentReminderSettings?.day_of_week_name || 'Friday'} for balances over $${paymentReminderSettings?.min_balance || 10}`
                        : 'Automatic payment reminders are disabled'}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Reminders include Square payment links and show self-pay discounts when applicable
                    </p>
                  </CardContent>
                </Card>

                {/* Payment Reminder Settings Dialog */}
                <Dialog open={showPaymentReminderSettings} onOpenChange={setShowPaymentReminderSettings}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader className="flex flex-row items-center justify-between">
                      <div>
                        <DialogTitle className="flex items-center gap-2">
                          <CreditCard className="h-5 w-5 text-amber-600" />
                          Payment Reminder Settings
                        </DialogTitle>
                        <DialogDescription>
                          Configure automated payment reminders sent to clients with outstanding balances
                        </DialogDescription>
                      </div>
                      <AdminJumpToDropdown currentDialogId="paymentReminders" isDirty={currentAdminDialogDirty} />
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Enable/Disable Toggle */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <Label className="font-medium">Automated Reminders</Label>
                          <p className="text-xs text-gray-500">Send daily payment reminder emails</p>
                        </div>
                        <Switch
                          checked={paymentReminderSettings?.enabled || false}
                          onCheckedChange={async (checked) => {
                            try {
                              await axios.put(`${API}/payment-reminder-settings`, {
                                ...paymentReminderSettings,
                                enabled: checked
                              });
                              setPaymentReminderSettings({ ...paymentReminderSettings, enabled: checked });
                              toast.success(checked ? 'Reminders enabled' : 'Reminders disabled');
                            } catch (error) {
                              toast.error('Failed to update');
                            }
                          }}
                        />
                      </div>

                      {/* Settings Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium">Minimum Balance ($)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="5"
                            value={paymentReminderSettings?.min_balance || 10}
                            onChange={async (e) => {
                              const value = parseFloat(e.target.value);
                              try {
                                await axios.put(`${API}/payment-reminder-settings`, {
                                  ...paymentReminderSettings,
                                  min_balance: value
                                });
                                setPaymentReminderSettings({ ...paymentReminderSettings, min_balance: value });
                                toast.success('Minimum balance updated');
                              } catch (error) {
                                toast.error('Failed to update');
                              }
                            }}
                            className="mt-1"
                          />
                          <p className="text-xs text-gray-500 mt-1">Only send for balances above this amount</p>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Reminder Interval (days)</Label>
                          <Select
                            value={String(paymentReminderSettings?.reminder_interval_days || 7)}
                            onValueChange={async (value) => {
                              try {
                                await axios.put(`${API}/payment-reminder-settings`, {
                                  ...paymentReminderSettings,
                                  reminder_interval_days: parseInt(value)
                                });
                                setPaymentReminderSettings({ ...paymentReminderSettings, reminder_interval_days: parseInt(value) });
                                toast.success('Reminder interval updated');
                              } catch (error) {
                                toast.error('Failed to update');
                              }
                            }}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="3">Every 3 days</SelectItem>
                              <SelectItem value="7">Weekly (7 days)</SelectItem>
                              <SelectItem value="14">Bi-weekly (14 days)</SelectItem>
                              <SelectItem value="30">Monthly (30 days)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 mt-1">Days between reminders per client</p>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Send On Day</Label>
                          <Select
                            value={String(paymentReminderSettings?.day_of_week ?? 4)}
                            onValueChange={async (value) => {
                              const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                              try {
                                await axios.put(`${API}/payment-reminder-settings`, {
                                  ...paymentReminderSettings,
                                  day_of_week: parseInt(value),
                                  day_of_week_name: dayNames[parseInt(value)]
                                });
                                setPaymentReminderSettings({ 
                                  ...paymentReminderSettings, 
                                  day_of_week: parseInt(value),
                                  day_of_week_name: dayNames[parseInt(value)]
                                });
                                toast.success(`Reminders will be sent on ${dayNames[parseInt(value)]}s`);
                              } catch (error) {
                                toast.error('Failed to update');
                              }
                            }}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">Monday</SelectItem>
                              <SelectItem value="1">Tuesday</SelectItem>
                              <SelectItem value="2">Wednesday</SelectItem>
                              <SelectItem value="3">Thursday</SelectItem>
                              <SelectItem value="4">Friday</SelectItem>
                              <SelectItem value="5">Saturday</SelectItem>
                              <SelectItem value="6">Sunday</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 mt-1">Invoices/statements sent weekly on this day</p>
                        </div>
                      </div>

                      {/* Options */}
                      <div className="space-y-3 pt-2 border-t">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm font-medium">Include Payment Link</Label>
                            <p className="text-xs text-gray-500">Add Square payment link in emails</p>
                          </div>
                          <Switch
                            checked={paymentReminderSettings?.include_payment_link !== false}
                            onCheckedChange={async (checked) => {
                              try {
                                await axios.put(`${API}/payment-reminder-settings`, {
                                  ...paymentReminderSettings,
                                  include_payment_link: checked
                                });
                                setPaymentReminderSettings({ ...paymentReminderSettings, include_payment_link: checked });
                              } catch (error) {
                                toast.error('Failed to update');
                              }
                            }}
                          />
                        </div>
                      </div>

                      {/* Info Box */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                        <p className="font-medium mb-1">📧 What's included in reminders:</p>
                        <ul className="list-disc list-inside text-xs space-y-1">
                          <li>Outstanding balance amount</li>
                          <li>Recent account activity summary</li>
                          <li>Secure payment link (Square)</li>
                          <li>Self-pay discounts (when applicable)</li>
                        </ul>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowPaymentReminderSettings(false)}>
                        Close
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Document Deadline Rules */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <FileText className="h-5 w-5 text-purple-600" />
                          Document Deadline Rules
                        </CardTitle>
                        <p className="text-sm text-gray-600 mt-1">
                          Configure when documents are due based on service type
                        </p>
                      </div>
                      {(isSuperAdmin(user)) && (
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            size="sm" 
                            className="bg-purple-600 hover:bg-purple-700"
                            onClick={() => {
                              setEditingRule(null);
                              setShowRuleDialog(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Rule
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowManageRulesDialog(true)}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Manage Rules
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {documentRules.length === 0 ? (
                      <p className="text-sm text-gray-500">No rules configured yet</p>
                    ) : (
                      <div className="space-y-2">
                        {/* Show first 3 rules as preview */}
                        <div className="space-y-1">
                          {documentRules.filter(r => r.is_active).slice(0, 3).map(rule => (
                            <div key={rule.id} className="flex items-center gap-2 text-sm">
                              <Badge variant="outline" className="text-xs">
                                {rule.document_type}
                              </Badge>
                              <span className="text-gray-600 truncate">{rule.rule_name}</span>
                            </div>
                          ))}
                          {documentRules.filter(r => r.is_active).length > 3 && (
                            <p className="text-xs text-gray-500">
                              +{documentRules.filter(r => r.is_active).length - 3} more rules...
                            </p>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 pt-1 border-t">
                          {documentRules.filter(r => r.is_active).length} active, {documentRules.filter(r => !r.is_active).length} inactive
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Service Types Library - Compact View */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="h-5 w-5 text-teal-600" />
                        Service Types
                      </CardTitle>
                      {(isSuperAdmin(user)) && (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => {
                              setEditingServiceType(null);
                              setShowManageServiceTypesDialog(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowManageServiceTypesDialog(true)}
                            data-testid="manage-service-types-btn"
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Manage
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {serviceTypes.length > 0 ? (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {serviceTypes.filter(st => st.is_active).slice(0, 5).map(st => (
                            <Badge 
                              key={st.id} 
                              variant="secondary"
                              className="text-sm py-1 px-2"
                            >
                              {st.display_name}
                              {st.short_name && <span className="ml-1 text-xs opacity-70">({st.short_name})</span>}
                            </Badge>
                          ))}
                          {serviceTypes.filter(st => st.is_active).length > 5 && (
                            <Badge variant="outline" className="text-sm py-1 px-2">
                              +{serviceTypes.filter(st => st.is_active).length - 5} more
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {serviceTypes.filter(st => st.is_active).length} active, {serviceTypes.filter(st => !st.is_active).length} inactive
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No service types configured</p>
                    )}
                  </CardContent>
                </Card>

                {/* Signature Routing Rules */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <CardTitle className="text-lg">Signature Routing Rules</CardTitle>
                        <p className="text-sm text-gray-600 mt-1">Configure signature requirements</p>
                      </div>
                      {(isSuperAdmin(user)) && (
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            size="sm" 
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => {
                              setEditingRoutingRule(null);
                              setShowRoutingRulesManager(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Rule
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowRoutingRulesManager(true)}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Manage Rules
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      {routingRules.filter(r => r.is_active).length} active rules, {routingRules.length} total
                    </p>
                  </CardContent>
                </Card>


                {/* Client Demographics CSV */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Client Demographics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="p-3 border rounded-lg">
                      <Label className="text-sm font-medium text-gray-700">Import/Export Clients</Label>
                      <p className="text-sm text-gray-600 mb-3">Download client demographics or bulk import new clients via CSV</p>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={async () => {
                            try {
                              void 0 && console.log('[ClientTemplate] Requesting template download...');
                              const response = await axios.get(`${API}/clients/template/csv`, {
                                responseType: 'blob'
                              });
                              void 0 && console.log('[ClientTemplate] Response received, size:', response.data.size);
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', 'clients_template.csv');
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                              toast.success("Template downloaded!");
                            } catch (error) {
                              console.error('[ClientTemplate] Download failed:', error.response?.data || error.message);
                              toast.error(error.response?.data?.detail || "Failed to download template");
                            }
                          }}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download Template
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setShowClientExportDialog(true)}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Export Clients
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => document.getElementById('admin-client-csv-upload').click()}
                        >
                          <Upload className="h-3 w-3 mr-1" />
                          Upload CSV
                        </Button>
                        <input
                          id="admin-client-csv-upload"
                          type="file"
                          accept=".csv"
                          style={{ display: 'none' }}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            try {
                              const response = await axios.post(`${API}/clients/import/csv`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              
                              const { created, skipped, errors } = response.data;
                              
                              // Show detailed results
                              if (errors.length === 0) {
                                toast.success(`✅ Success! ${created} client${created !== 1 ? 's' : ''} added${skipped > 0 ? `, ${skipped} already existed` : ''}`, { duration: 5000 });
                              } else {
                                // Show errors in plain English
                                let errorMessage = `Import finished with some issues:\n\n`;
                                if (created > 0) errorMessage += `✅ ${created} client${created !== 1 ? 's' : ''} added successfully\n`;
                                if (skipped > 0) errorMessage += `⏭️ ${skipped} skipped (already in system)\n`;
                                errorMessage += `\n❌ ${errors.length} problem${errors.length !== 1 ? 's' : ''} found:\n\n`;
                                
                                errors.forEach((err, idx) => {
                                  if (idx < 10) { // Show first 10 errors
                                    errorMessage += `• ${err}\n\n`;
                                  }
                                });
                                if (errors.length > 10) {
                                  errorMessage += `... and ${errors.length - 10} more issue${errors.length - 10 !== 1 ? 's' : ''}. Check the browser console for the full list.`;
                                  console.error('All import errors:', errors);
                                }
                                
                                toast.error(errorMessage, { duration: 15000 });
                              }
                              
                              e.target.value = '';
                            } catch (error) {
                              console.error('CSV upload error:', error);
                              toast.error(error.response?.data?.detail || "Failed to upload CSV");
                              e.target.value = '';
                            }
                          }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Roles & Permissions Management */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg">Roles & Permissions</CardTitle>
                      {(isSuperAdmin(user)) && (
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            size="sm" 
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => {
                              setEditingRole(null);
                              fetchPermissions();
                              setShowManageRoles(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Role
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              fetchRoles();
                              fetchPermissions();
                              setShowManageRoles(true);
                            }}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Manage Roles
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">{roles?.length || 0} roles defined</p>
                  </CardContent>
                </Card>

                {/* Archived Messages Search */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Archived Message Search</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">Search messages archived after 180 days</p>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setShowArchivedMessagesDialog(true)}
                          className="flex-1"
                        >
                          <Search className="h-4 w-4 mr-2" />
                          Search Archived
                        </Button>
                        
                        <Button
                          variant="outline"
                          onClick={async () => {
                            if (!window.confirm('Archive all messages older than 180 days? This action cannot be undone.')) {
                              return;
                            }
                            
                            try {
                              const response = await axios.post(`${API}/admin/archive-old-messages`);
                              const { archived_count } = response.data;
                              
                              if (archived_count > 0) {
                                toast.success(`Successfully archived ${archived_count} message thread${archived_count !== 1 ? 's' : ''}`);
                              } else {
                                toast.info('No messages older than 180 days found');
                              }
                            } catch (error) {
                              console.error('Error archiving messages:', error);
                              toast.error('Failed to archive messages');
                            }
                          }}
                          className="flex-1"
                        >
                          <Archive className="h-4 w-4 mr-2" />
                          Archive Old
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Appointment Reminders Card - Compact Card with Dialog */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Bell className="h-5 w-5" />
                        Appointment Reminders
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">Configure automatic appointment reminders</p>
                    </div>
                    <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline">
                          <Settings className="h-4 w-4 mr-1" />
                          Manage
                        </Button>
                      </DialogTrigger>
                            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                              <DialogHeader className="flex flex-row items-start justify-between">
                                <div>
                                  <DialogTitle>Appointment Reminder Settings</DialogTitle>
                                  <DialogDescription>
                                    Configure automatic reminders with custom messages and placeholders
                                  </DialogDescription>
                                </div>
                                <AdminJumpToDropdown currentDialogId="reminders" isDirty={currentAdminDialogDirty} />
                              </DialogHeader>
                              
                              {reminderSettings ? (
                              <>
                              <div className="space-y-6 py-4">
                                {/* Enable/Disable Toggle */}
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div>
                                    <p className="font-semibold">Reminders Enabled</p>
                                    <p className="text-xs text-gray-600">Send automatic appointment reminders</p>
                                  </div>
                                  <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={reminderSettings.enabled}
                                      onChange={(e) => setReminderSettings({...reminderSettings, enabled: e.target.checked})}
                                      className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                                  </label>
                                </div>

                                {/* Quick Settings */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <p className="font-semibold text-sm">Reminder Intervals</p>
                                    <div className="flex flex-col space-y-2">
                                      <label className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          checked={reminderSettings.reminder_48_hours}
                                          disabled={!reminderSettings.enabled}
                                          onChange={(e) => setReminderSettings({...reminderSettings, reminder_48_hours: e.target.checked})}
                                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded disabled:opacity-50"
                                        />
                                        <span className="text-sm">48 Hours Before</span>
                                      </label>
                                      <label className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          checked={reminderSettings.reminder_2_hours}
                                          disabled={!reminderSettings.enabled}
                                          onChange={(e) => setReminderSettings({...reminderSettings, reminder_2_hours: e.target.checked})}
                                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded disabled:opacity-50"
                                        />
                                        <span className="text-sm">2 Hours Before</span>
                                      </label>
                                      <label className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          checked={reminderSettings.reminder_5_minutes}
                                          disabled={!reminderSettings.enabled}
                                          onChange={(e) => setReminderSettings({...reminderSettings, reminder_5_minutes: e.target.checked})}
                                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded disabled:opacity-50"
                                        />
                                        <span className="text-sm">5 Minutes Before</span>
                                      </label>
                                    </div>
                                  </div>

                                  <div className="space-y-2">
                                    <p className="font-semibold text-sm">Delivery Methods</p>
                                    <div className="flex flex-col space-y-2">
                                      <label className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          checked={reminderSettings.send_email}
                                          disabled={!reminderSettings.enabled}
                                          onChange={(e) => setReminderSettings({...reminderSettings, send_email: e.target.checked})}
                                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded disabled:opacity-50"
                                        />
                                        <span className="text-sm">Email</span>
                                      </label>
                                      <label className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          checked={reminderSettings.send_in_app}
                                          disabled={!reminderSettings.enabled}
                                          onChange={(e) => setReminderSettings({...reminderSettings, send_in_app: e.target.checked})}
                                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded disabled:opacity-50"
                                        />
                                        <span className="text-sm">In-App</span>
                                      </label>
                                      <label className="flex items-center space-x-2 opacity-50">
                                        <input type="checkbox" disabled className="h-4 w-4" />
                                        <span className="text-sm">SMS (Coming Soon)</span>
                                      </label>
                                    </div>
                                  </div>
                                </div>

                                {/* Signature Auto Nudge (Jun 2026) — independent of appointment reminders */}
                                <div className="space-y-2 border-t pt-4">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-semibold text-sm">Signature Reminders (Auto Nudge)</p>
                                      <p className="text-xs text-gray-500">Automatically re-email clients a signing link when a document sits unsigned. Repeats until signed.</p>
                                    </div>
                                    <label className="flex items-center space-x-2">
                                      <input
                                        type="checkbox"
                                        checked={!!reminderSettings.signature_auto_nudge_enabled}
                                        onChange={(e) => setReminderSettings({...reminderSettings, signature_auto_nudge_enabled: e.target.checked})}
                                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                        data-testid="signature-auto-nudge-toggle"
                                      />
                                      <span className="text-sm">{reminderSettings.signature_auto_nudge_enabled ? 'On' : 'Off'}</span>
                                    </label>
                                  </div>
                                  {reminderSettings.signature_auto_nudge_enabled && (
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm">Remind after</span>
                                      <Input
                                        type="number"
                                        min="1"
                                        max="90"
                                        className="w-20 h-8"
                                        value={reminderSettings.signature_nudge_after_days ?? 7}
                                        onChange={(e) => setReminderSettings({...reminderSettings, signature_nudge_after_days: e.target.value})}
                                        data-testid="signature-nudge-days-input"
                                      />
                                      <span className="text-sm">days without a signature</span>
                                    </div>
                                  )}
                                </div>

                                {/* Telehealth Button Text */}
                                <div className="space-y-2">
                                  <Label htmlFor="telehealth_button_text">Telehealth Button Text</Label>
                                  <DebouncedInput
                                    id="telehealth_button_text"
                                    value={reminderSettings.telehealth_button_text || 'Join Now'}
                                    onChange={(e) => setReminderSettings({...reminderSettings, telehealth_button_text: e.target.value})}
                                    disabled={!reminderSettings.enabled}
                                    placeholder="Join Now"
                                  />
                                  <p className="text-xs text-gray-500">Text displayed on the button/link for telehealth appointments</p>
                                </div>

                                {/* Additional Instructions (Optional) */}
                                <div className="space-y-3 border-t pt-6">
                                  <div>
                                    <Label htmlFor="additional_instructions" className="text-base font-semibold flex items-center gap-2">
                                      <MessageSquare className="h-4 w-4" />
                                      Additional Instructions (Optional)
                                    </Label>
                                    <p className="text-xs text-gray-600 mt-1 mb-3">
                                      This message will appear in <strong>all</strong> appointment reminders. Great for office closures, special announcements, or general instructions.
                                    </p>
                                  </div>
                                  <DebouncedTextarea
                                    id="additional_instructions"
                                    value={reminderSettings.additional_instructions || ''}
                                    onChange={(e) => setReminderSettings({...reminderSettings, additional_instructions: e.target.value})}
                                    disabled={!reminderSettings.enabled}
                                    rows={4}
                                    className="w-full p-3 border rounded-md text-sm disabled:opacity-50 focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                                    placeholder="e.g., Our office will be closed Dec 24-26 for the holidays. Please call for urgent matters."
                                  />
                                  <p className="text-xs text-gray-500">
                                    Leave blank if you don't need any additional message in reminders.
                                  </p>
                                </div>
                              </div>

                              <DialogFooter className="flex gap-2">
                                <Button
                                  variant="outline"
                                  onClick={async () => {
                                    try {
                                      const response = await axios.post(`${API}/send-appointment-reminders`);
                                      toast.success(response.data.message);
                                    } catch (error) {
                                      toast.error('Failed to send reminders');
                                    }
                                  }}
                                >
                                  <Bell className="h-4 w-4 mr-2" />
                                  Test Send
                                </Button>
                                <Button
                                  onClick={async () => {
                                    try {
                                      await axios.put(`${API}/reminder-settings`, reminderSettings);
                                      toast.success('All settings saved successfully!');
                                      setShowReminderDialog(false);
                                      fetchReminderSettings();
                                    } catch (error) {
                                      toast.error('Failed to save settings');
                                    }
                                  }}
                                  className="bg-teal-600 hover:bg-teal-700"
                                >
                                  <Save className="h-4 w-4 mr-2" />
                                  Save Changes
                                </Button>
                              </DialogFooter>
                              </>
                              ) : (
                                <div className="flex items-center justify-center py-8">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mr-3"></div>
                                  <span className="text-gray-500">Loading settings...</span>
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                  </CardHeader>
                  <CardContent>
                    {reminderSettings ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Badge className={reminderSettings.enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                            {reminderSettings.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                          {reminderSettings.enabled && (
                            <span className="text-sm text-gray-600">
                              {[reminderSettings.reminder_48_hours && '48h', reminderSettings.reminder_2_hours && '2h', reminderSettings.reminder_5_minutes && '5min'].filter(Boolean).join(', ')} • {[reminderSettings.send_email && 'Email', reminderSettings.send_in_app && 'In-App'].filter(Boolean).join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                        <span className="text-sm text-gray-500">Loading...</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Firm Calendar */}
                <Card data-testid="firm-calendar-admin-card">
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5 text-sky-600" />
                        Firm Calendar
                      </CardTitle>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowFirmCalendarDialog(true)}
                        data-testid="firm-calendar-manage-btn"
                      >
                        <Settings className="h-4 w-4 mr-1" />
                        Manage
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      Shared events visible to all users — holidays, all-staff meetings, trainings. Supports recurring events, role targeting, and optional payroll auto-fill.
                    </p>
                  </CardContent>
                </Card>

                  </div>
                </TabsContent>

                {/* Admin Tools & Security Tab */}
                {tenantToolAccess.tools && (
                <TabsContent value="tools">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Poll Cache Hit Rate — opens a dialog on demand */}
                    <PollCacheMetricsCard />
                    {/* NEHEN / TriZetto SOAP + SFTP diagnostic buttons */}
                    <NehenDiagnosticsCard />
                    {/* Mar 2026 — Claim Investigator: triple-source
                        diagnostic for billing-ledger desync.  Lets
                        admins paste a claim ID, see which of (claim
                        doc / claim_payment_events / ERA enrichment)
                        is lying, and fire the existing recompute +
                        reconcile endpoints to repair. */}
                    <Card className="border-blue-200" data-testid="claim-investigator-card">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Search className="h-5 w-5 text-blue-600" />
                          Claim Investigator
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <p className="text-xs text-gray-600">
                          Diagnose desync between a claim's doc-level totals, the append-only payment ledger, and the ERA enrichment math. Includes per-claim Repair Actions and a "Scan recent ERAs" tile for triaging the whole backlog at once.
                        </p>
                        <ul className="text-xs text-gray-500 list-disc pl-5 space-y-1">
                          <li>Read-only diagnostic — won't mutate anything until you click a Repair button.</li>
                          <li>Repair Actions wire existing, tested, idempotent endpoints.</li>
                          <li>Use this BEFORE bulk history cleanup so you fix the root cause first.</li>
                        </ul>
                        <Button
                          type="button"
                          size="sm"
                          data-testid="claim-investigator-open-btn"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => { window.location.href = '/v2/admin/claim-investigate'; }}
                        >
                          Open Claim Investigator
                        </Button>
                      </CardContent>
                    </Card>
                    {/* Supervisor Signature Backfill — Feb 2026 one-shot
                        migration to recognize historical supervisor
                        signatures that landed in additional_signatures[]
                        instead of the dedicated supervisor_* fields. */}
                    <SupervisorSignatureBackfillCard />
                    {/* Portal Assessment Severity & PDF Fix — Jun 2026 one-shot:
                        corrects severities mis-scored by the old band-key bug
                        and regenerates the affected chart PDFs. */}
                    <AssessmentPdfRegenCard />
                    {/* Bulk Client Operations — Feb 2026: bulk-set
                        gender for clients missing it, plus rule-based
                        bulk labeling (e.g. "tag everyone missing
                        email"). Driven by /api/admin/clients/* */}
                    <BulkClientOperationsCard />
                    {/* AI Features Kill Switch */}
                    <Card className="border-red-200" data-testid="ai-toggle-card">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Shield className="h-5 w-5 text-red-600" />
                          AI Features Control
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="p-3 border rounded-lg">
                          <Label className="text-sm font-medium text-gray-700">AI / LLM Features</Label>
                          <p className="text-xs text-gray-500 mt-1">
                            Enable or disable all AI-powered features (clinical summaries, claims analysis, routing rule validation). 
                            When disabled, all AI endpoints return a 403 error. HIPAA-compliant de-identification is applied when enabled.
                          </p>
                          <div className="flex items-center gap-3 mt-3">
                            <Button
                              type="button"
                              size="sm"
                              data-testid="ai-toggle-btn"
                              variant={adminAiEnabled ? "destructive" : "default"}
                              className={!adminAiEnabled ? "bg-green-600 hover:bg-green-700" : ""}
                              onClick={async () => {
                                const newState = !adminAiEnabled;
                                const confirmMsg = newState 
                                  ? 'Enable AI features? All LLM calls use HIPAA-compliant de-identification.' 
                                  : 'Disable ALL AI features? No data will be sent to any LLM.';
                                if (!window.confirm(confirmMsg)) return;
                                try {
                                  const res = await axios.post(`${API}/admin/tools/ai-toggle`, { enabled: newState });
                                  setAdminAiEnabled(res.data.ai_features_enabled);
                                  toast.success(res.data.message);
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to toggle AI features');
                                }
                              }}
                            >
                              {adminAiEnabled ? (
                                <><XCircle className="h-3 w-3 mr-1" /> Disable AI</>
                              ) : (
                                <><CheckCircle className="h-3 w-3 mr-1" /> Enable AI</>
                              )}
                            </Button>
                            <Badge variant={adminAiEnabled ? "default" : "secondary"} className={adminAiEnabled ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"} data-testid="ai-toggle-status">
                              {adminAiEnabled ? "ENABLED" : "DISABLED"}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    {/* Auto-Create Notes on Schedule Kill Switch
                        Feb 2026 user request: "Can I have a way to
                        turn that off? It's creating chaos." */}
                    <Card data-testid="auto-note-toggle-card">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <FileText className="h-5 w-5 text-indigo-600" />
                          Auto-Create Notes on Schedule
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="p-3 border rounded-lg">
                          <Label className="text-sm font-medium text-gray-700">Auto-Create Notes on Schedule</Label>
                          <p className="text-xs text-gray-500 mt-1">
                            When ON, a draft note is automatically created for any new appointment scheduled for today or tomorrow
                            (using the default template assigned to the CPT). When OFF, providers create notes manually.
                            The admin-triggered &ldquo;Auto-create notes for today&rdquo; sweeper button is unaffected.
                          </p>
                          <div className="flex items-center gap-3 mt-3">
                            <Button
                              type="button"
                              size="sm"
                              data-testid="auto-note-toggle-btn"
                              variant={autoNoteOnScheduleEnabled ? "destructive" : "default"}
                              className={!autoNoteOnScheduleEnabled ? "bg-green-600 hover:bg-green-700" : ""}
                              onClick={async () => {
                                const newState = !autoNoteOnScheduleEnabled;
                                const confirmMsg = newState
                                  ? 'Re-enable auto-note creation for today/tomorrow appointments?'
                                  : 'Disable auto-note creation? New today/tomorrow appointments will NOT auto-generate a draft note.';
                                if (!window.confirm(confirmMsg)) return;
                                try {
                                  const res = await axios.post(`${API}/admin/tools/auto-note-toggle`, { enabled: newState });
                                  setAutoNoteOnScheduleEnabled(res.data.auto_create_notes_on_schedule !== false);
                                  toast.success(res.data.message);
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to toggle auto-note creation');
                                }
                              }}
                            >
                              {autoNoteOnScheduleEnabled ? (
                                <><XCircle className="h-3 w-3 mr-1" /> Disable</>
                              ) : (
                                <><CheckCircle className="h-3 w-3 mr-1" /> Enable</>
                              )}
                            </Button>
                            <Badge
                              variant={autoNoteOnScheduleEnabled ? "default" : "secondary"}
                              className={autoNoteOnScheduleEnabled ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}
                              data-testid="auto-note-toggle-status"
                            >
                              {autoNoteOnScheduleEnabled ? "ENABLED" : "DISABLED"}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    {/* Client Merge Tool */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="h-5 w-5 text-orange-600" />
                          Client Data Management
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="p-3 border rounded-lg">
                          <Label className="text-sm font-medium text-gray-700">Archive Merged Clients</Label>
                          <p className="text-xs text-gray-500 mt-1">Move all clients with status "merged" to the clients_archived_merges collection to keep the client list clean</p>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={async () => {
                              if (!window.confirm('Move all merged clients to the archive collection? This removes them from the main clients collection.')) return;
                              try {
                                const response = await axios.post(`${API}/admin/clients/archive-merged`);
                                toast.success(response.data.message);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to archive merged clients');
                              }
                            }}
                          >
                            <Archive className="h-3 w-3 mr-1" />
                            Archive Merged Clients
                          </Button>
                        </div>
                        <div className="p-3 border rounded-lg">
                          <Label className="text-sm font-medium text-gray-700">Find & Merge Duplicates</Label>
                          <p className="text-xs text-gray-500 mt-1">Scan for clients with matching name, DOB, or SSN and merge them</p>
                          <Dialog onOpenChange={(open) => { if (!open) { setSelectedDuplicatePairs([]); setSwappedPairs({}); } }}>
                            <DialogTrigger asChild>
                              <Button 
                                type="button"
                                size="sm"
                                variant="outline"
                                className="mt-2"
                                onClick={() => {
                                  void 0 && console.log("Search Duplicates button clicked!");
                                  toast.info("Searching for duplicates...");
                                  setSelectedDuplicatePairs([]);
                                  setSwappedPairs({});
                                  fetchPotentialDuplicates();
                                }}
                              >
                                <Search className="h-3 w-3 mr-1" />
                                Search Duplicates
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                  <Search className="h-5 w-5" />
                                  Potential Duplicate Clients
                                </DialogTitle>
                              </DialogHeader>
                              
                              {mergeLoading ? (
                                <div className="flex justify-center py-8">
                                  <Loader className="h-8 w-8 animate-spin" />
                                </div>
                              ) : potentialDuplicates.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                                  <p>No potential duplicates found</p>
                                  <p className="text-sm">All clients appear to be unique</p>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {/* Header with Select All and Batch Merge */}
                                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                      <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={selectedDuplicatePairs.length === potentialDuplicates.length && potentialDuplicates.length > 0}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setSelectedDuplicatePairs(potentialDuplicates.map((_, idx) => idx));
                                            } else {
                                              setSelectedDuplicatePairs([]);
                                            }
                                          }}
                                          className="rounded border-gray-300"
                                        />
                                        <span className="text-sm font-medium">Select All</span>
                                      </label>
                                      <span className="text-sm text-gray-600">
                                        {selectedDuplicatePairs.length} of {potentialDuplicates.length} pairs selected
                                      </span>
                                    </div>
                                    
                                    {selectedDuplicatePairs.length > 0 && (
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <Button
                                            size="sm"
                                            className="bg-orange-600 hover:bg-orange-700"
                                          >
                                            <GitMerge className="h-4 w-4 mr-1" />
                                            Merge {selectedDuplicatePairs.length} Selected Pair{selectedDuplicatePairs.length > 1 ? 's' : ''}
                                          </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent className="max-w-2xl">
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>Confirm Batch Merge</AlertDialogTitle>
                                            <AlertDialogDescription asChild>
                                              <div className="space-y-3">
                                                <p>Are you sure you want to merge <strong>{selectedDuplicatePairs.length} client pair{selectedDuplicatePairs.length > 1 ? 's' : ''}</strong>?</p>
                                                
                                                <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-2">
                                                  {selectedDuplicatePairs.map((pairIdx) => {
                                                    const pair = potentialDuplicates[pairIdx];
                                                    const isSwapped = swappedPairs[pairIdx];
                                                    const sourceClient = isSwapped ? pair.client2 : pair.client1;
                                                    const targetClient = isSwapped ? pair.client1 : pair.client2;
                                                    return (
                                                      <div key={pairIdx} className="p-2 bg-gray-50 rounded text-sm border">
                                                        <div className="flex items-center gap-2">
                                                          <div className="flex-1 text-red-600">
                                                            <span className="line-through">{sourceClient.full_name}</span>
                                                            <span className="text-xs text-gray-500 ml-1">
                                                              ({sourceClient.mrn || 'No MRN'} • {sourceClient.date_of_birth || 'No DOB'})
                                                            </span>
                                                          </div>
                                                          <span className="text-gray-400">→</span>
                                                          <div className="flex-1 text-green-600 font-medium">
                                                            {targetClient.full_name}
                                                            <span className="text-xs text-gray-500 ml-1 font-normal">
                                                              ({targetClient.mrn || 'No MRN'} • {targetClient.date_of_birth || 'No DOB'})
                                                            </span>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                                
                                                <p className="text-sm text-orange-600 mt-3">
                                                  ⚠️ For each pair, the strikethrough client will be archived and their records transferred to the green client. This action cannot be undone.
                                                </p>
                                              </div>
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                              className="bg-orange-600 hover:bg-orange-700"
                                              onClick={async () => {
                                                try {
                                                  setMergeLoading(true);
                                                  let successCount = 0;
                                                  let failCount = 0;
                                                  
                                                  for (const pairIdx of selectedDuplicatePairs) {
                                                    const pair = potentialDuplicates[pairIdx];
                                                    // Respect swap state for each pair
                                                    const isSwapped = swappedPairs[pairIdx];
                                                    const sourceClient = isSwapped ? pair.client2 : pair.client1;
                                                    const targetClient = isSwapped ? pair.client1 : pair.client2;
                                                    
                                                    try {
                                                      await axios.post(`${API}/admin/clients/merge`, {
                                                        source_client_id: sourceClient.id,
                                                        target_client_id: targetClient.id
                                                      });
                                                      successCount++;
                                                    } catch (error) {
                                                      console.error(`Failed to merge ${sourceClient.full_name}:`, error);
                                                      failCount++;
                                                    }
                                                  }
                                                  
                                                  if (successCount > 0) {
                                                    toast.success(`Successfully merged ${successCount} client pair${successCount > 1 ? 's' : ''}`);
                                                  }
                                                  if (failCount > 0) {
                                                    toast.error(`Failed to merge ${failCount} pair${failCount > 1 ? 's' : ''}`);
                                                  }
                                                  
                                                  setSelectedDuplicatePairs([]);
                                                  setSwappedPairs({});
                                                  // Refresh duplicates list after merge
                                                  fetchPotentialDuplicates();
                                                } catch (error) {
                                                  console.error('Batch merge failed:', error);
                                                  toast.error('Batch merge failed');
                                                } finally {
                                                  setMergeLoading(false);
                                                }
                                              }}
                                            >
                                              {mergeLoading ? <Loader className="h-4 w-4 mr-2 animate-spin" /> : <GitMerge className="h-4 w-4 mr-2" />}
                                              Confirm Batch Merge
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    )}
                                  </div>
                                  
                                  {/* Cluster warning if any 3+ copy groups exist */}
                                  {potentialDuplicates.some(p => p.cluster_size >= 3) && (
                                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                      <div className="flex items-center gap-2 text-amber-800">
                                        <span className="text-lg">⚠️</span>
                                        <span className="font-medium">Multiple copies detected!</span>
                                      </div>
                                      <p className="text-sm text-amber-700 mt-1">
                                        Some clients have 3 or more duplicate records. Look for pairs marked with the red "X copies found" badge - 
                                        you may need to merge multiple times to consolidate all copies into one record.
                                      </p>
                                    </div>
                                  )}
                                  
                                  <p className="text-sm text-gray-600">
                                    Found {potentialDuplicates.length} potential duplicate pairs based on matching name, DOB, SSN, email, or phone.
                                  </p>
                                  
                                  {/* Duplicate Pairs List */}
                                  {potentialDuplicates.map((pair, idx) => {
                                    // Determine which client is source (archived) and which is target (kept) based on swap state
                                    const isSwapped = swappedPairs[idx];
                                    const sourceClient = isSwapped ? pair.client2 : pair.client1;
                                    const targetClient = isSwapped ? pair.client1 : pair.client2;
                                    
                                    return (
                                    <div key={idx} className={`p-4 border rounded-lg ${selectedDuplicatePairs.includes(idx) ? 'border-orange-400 bg-orange-50' : ''}`}>
                                      <div className="flex items-start gap-3">
                                        {/* Checkbox */}
                                        <input
                                          type="checkbox"
                                          checked={selectedDuplicatePairs.includes(idx)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setSelectedDuplicatePairs(prev => [...prev, idx]);
                                            } else {
                                              setSelectedDuplicatePairs(prev => prev.filter(i => i !== idx));
                                            }
                                          }}
                                          className="mt-1 rounded border-gray-300 text-orange-600"
                                        />
                                        
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                                            <Badge variant="outline" className="bg-yellow-50">
                                              Match Score: {pair.match_score}
                                            </Badge>
                                            {pair.cluster_size && (
                                              <Badge variant="destructive" className="text-xs">
                                                ⚠️ {pair.cluster_size} copies found
                                              </Badge>
                                            )}
                                            {pair.match_reasons.map((reason, i) => (
                                              <Badge key={i} variant="secondary" className="text-xs">
                                                {reason}
                                              </Badge>
                                            ))}
                                          </div>
                                          
                                          <div className="grid grid-cols-[1fr,auto,1fr] gap-2 items-center">
                                            {/* Source Client (will be archived) */}
                                            <div className="p-3 bg-red-50 rounded text-sm border border-red-200">
                                              <div className="flex items-center justify-between mb-1">
                                                <p className="text-xs text-red-600 font-medium">Will be ARCHIVED:</p>
                                                <Badge variant="outline" className="text-xs capitalize bg-white">
                                                  {sourceClient.status?.replace(/_/g, ' ') || 'Unknown'}
                                                </Badge>
                                              </div>
                                              <div className="font-medium">{sourceClient.full_name}</div>
                                              <div className="text-gray-600">MRN: {formatMrn(sourceClient.mrn) || 'N/A'}</div>
                                              <div className="text-gray-600">DOB: {sourceClient.date_of_birth || 'N/A'}</div>
                                            </div>
                                            
                                            {/* Swap Button */}
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="px-2"
                                              onClick={() => {
                                                setSwappedPairs(prev => ({
                                                  ...prev,
                                                  [idx]: !prev[idx]
                                                }));
                                              }}
                                              title="Swap which client to keep"
                                            >
                                              <ArrowLeftRight className="h-4 w-4" />
                                            </Button>
                                            
                                            {/* Target Client (will be kept) */}
                                            <div className="p-3 bg-green-50 rounded text-sm border border-green-200">
                                              <div className="flex items-center justify-between mb-1">
                                                <p className="text-xs text-green-600 font-medium">Will be KEPT:</p>
                                                <Badge variant="outline" className="text-xs capitalize bg-white">
                                                  {targetClient.status?.replace(/_/g, ' ') || 'Unknown'}
                                                </Badge>
                                              </div>
                                              <div className="font-medium">{targetClient.full_name}</div>
                                              <div className="text-gray-600">MRN: {formatMrn(targetClient.mrn) || 'N/A'}</div>
                                              <div className="text-gray-600">DOB: {targetClient.date_of_birth || 'N/A'}</div>
                                            </div>
                                          </div>
                                          
                                          {/* Individual Merge Button */}
                                          <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="mt-3"
                                              >
                                                <GitMerge className="h-3 w-3 mr-1" />
                                                Merge This Pair
                                              </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent className="max-w-2xl">
                                              <AlertDialogHeader>
                                                <AlertDialogTitle>Confirm Client Merge</AlertDialogTitle>
                                                <AlertDialogDescription asChild>
                                                  <div className="space-y-4">
                                                    <p>Are you sure you want to merge these clients?</p>
                                                    
                                                    {/* Client Cards */}
                                                    <div className="grid grid-cols-2 gap-4">
                                                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                                                        <p className="text-xs text-red-600 font-medium mb-1">Will be ARCHIVED:</p>
                                                        <p className="font-medium text-gray-900">{sourceClient.full_name}</p>
                                                        <p className="text-sm text-gray-600">MRN: {formatMrn(sourceClient.mrn) || 'N/A'}</p>
                                                        <p className="text-sm text-gray-600">DOB: {sourceClient.date_of_birth || 'N/A'}</p>
                                                        <p className="text-sm text-gray-600 capitalize">Status: {sourceClient.status?.replace(/_/g, ' ') || 'N/A'}</p>
                                                      </div>
                                                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                                        <p className="text-xs text-green-600 font-medium mb-1">Will be KEPT:</p>
                                                        <p className="font-medium text-gray-900">{targetClient.full_name}</p>
                                                        <p className="text-sm text-gray-600">MRN: {formatMrn(targetClient.mrn) || 'N/A'}</p>
                                                        <p className="text-sm text-gray-600">DOB: {targetClient.date_of_birth || 'N/A'}</p>
                                                        <p className="text-sm text-gray-600 capitalize">Status: {targetClient.status?.replace(/_/g, ' ') || 'N/A'}</p>
                                                      </div>
                                                    </div>
                                                    
                                                    {/* Info about what gets transferred */}
                                                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                                      <p className="text-xs text-blue-700 font-medium mb-1">📋 What happens when you merge:</p>
                                                      <ul className="text-sm text-blue-600 list-disc list-inside space-y-1">
                                                        <li>All appointments are transferred to the kept client</li>
                                                        <li>All documents and files are moved</li>
                                                        <li>All clinical notes become part of the kept client's chart</li>
                                                        <li>All claims and billing records are transferred</li>
                                                        <li>The archived client is hidden from all views</li>
                                                      </ul>
                                                    </div>
                                                    
                                                    {/* Warning */}
                                                    <p className="text-sm text-orange-600">
                                                      ⚠️ Records will appear as if they always belonged to {targetClient.full_name}. This action is logged for audit but cannot be undone.
                                                    </p>
                                                  </div>
                                                </AlertDialogDescription>
                                              </AlertDialogHeader>
                                              <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction
                                                  className="bg-orange-600 hover:bg-orange-700"
                                                  onClick={async () => {
                                                    try {
                                                      setMergeLoading(true);
                                                      const response = await axios.post(`${API}/admin/clients/merge`, {
                                                        source_client_id: sourceClient.id,
                                                        target_client_id: targetClient.id
                                                      });
                                                      toast.success(`Successfully merged! ${response.data.total_records_migrated || 0} records transferred.`);
                                                      fetchPotentialDuplicates();
                                                    } catch (error) {
                                                      console.error('Merge failed:', error);
                                                      toast.error(error.response?.data?.detail || 'Failed to merge clients');
                                                    } finally {
                                                      setMergeLoading(false);
                                                    }
                                                  }}
                                                >
                                                  <GitMerge className="h-4 w-4 mr-2" />
                                                  Confirm Merge
                                                </AlertDialogAction>
                                              </AlertDialogFooter>
                                            </AlertDialogContent>
                                          </AlertDialog>
                                          
                                          {/* Dismiss Button - mark as not duplicates */}
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="mt-3 ml-2 text-gray-500 hover:text-gray-700"
                                            onClick={async () => {
                                              try {
                                                await axios.post(`${API}/admin/clients/dismissed-duplicates`, {
                                                  client_id_1: pair.client1.id,
                                                  client_id_2: pair.client2.id,
                                                  reason: "Manually dismissed - not duplicates"
                                                });
                                                toast.success("Pair dismissed - will not appear in future searches");
                                                // Remove from local state immediately
                                                setPotentialDuplicates(prev => prev.filter((_, i) => i !== idx));
                                                setSelectedDuplicatePairs(prev => prev.filter(i => i !== idx).map(i => i > idx ? i - 1 : i));
                                                // Optimistically add to dismissed list so the accordion reflects it
                                                const sortedIds = [pair.client1.id, pair.client2.id].sort();
                                                const pairKey = `${sortedIds[0]}:${sortedIds[1]}`;
                                                setDismissedDuplicates(prev => [
                                                  {
                                                    pair_key: pairKey,
                                                    client_id_1: sortedIds[0],
                                                    client_id_2: sortedIds[1],
                                                    client1: sortedIds[0] === pair.client1.id ? pair.client1 : pair.client2,
                                                    client2: sortedIds[0] === pair.client1.id ? pair.client2 : pair.client1,
                                                    dismissed_by_name: user?.full_name || "You",
                                                    dismissed_at: new Date().toISOString(),
                                                    reason: "Manually dismissed - not duplicates",
                                                  },
                                                  ...prev,
                                                ]);
                                              } catch (error) {
                                                console.error('Dismiss failed:', error);
                                                toast.error(error.response?.data?.detail || 'Failed to dismiss pair');
                                              }
                                            }}
                                            title="Mark as not duplicates - hide from search"
                                          >
                                            <XCircle className="h-3 w-3 mr-1" />
                                            Not Duplicates
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Dismissed Duplicates Accordion - shown at bottom of dialog */}
                              {dismissedDuplicates.length > 0 && (
                                <div className="mt-6 border-t pt-4" data-testid="dismissed-duplicates-section">
                                  <button
                                    type="button"
                                    onClick={() => setShowDismissedDuplicates(v => !v)}
                                    className="w-full flex items-center justify-between text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors"
                                    data-testid="dismissed-duplicates-toggle"
                                  >
                                    <div className="flex items-center gap-2">
                                      <ChevronDown
                                        className={`h-4 w-4 text-gray-500 transition-transform ${showDismissedDuplicates ? 'rotate-180' : ''}`}
                                      />
                                      <span className="text-sm font-medium text-gray-700">
                                        Previously Dismissed Pairs ({dismissedDuplicates.length})
                                      </span>
                                    </div>
                                    <span className="text-xs text-gray-500">
                                      Pairs marked as &ldquo;not duplicates&rdquo;
                                    </span>
                                  </button>

                                  {showDismissedDuplicates && (
                                    <div className="mt-3 space-y-2" data-testid="dismissed-duplicates-list">
                                      {dismissedDuplicates.map((d) => {
                                        const c1 = d.client1 || { id: d.client_id_1, full_name: d.client_id_1, mrn: '—' };
                                        const c2 = d.client2 || { id: d.client_id_2, full_name: d.client_id_2, mrn: '—' };
                                        return (
                                          <div
                                            key={d.pair_key}
                                            className="flex items-center justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                                            data-testid={`dismissed-pair-${d.pair_key}`}
                                          >
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2 text-gray-700">
                                                <span className="font-medium truncate">{c1.full_name || c1.id}</span>
                                                <span className="text-gray-400">↔</span>
                                                <span className="font-medium truncate">{c2.full_name || c2.id}</span>
                                              </div>
                                              <div className="text-xs text-gray-500 mt-1">
                                                MRN {formatMrn(c1.mrn) || '—'} · MRN {formatMrn(c2.mrn) || '—'}
                                                {d.dismissed_by_name && (
                                                  <> · dismissed by {d.dismissed_by_name}</>
                                                )}
                                                {d.dismissed_at && (
                                                  <> · {new Date(d.dismissed_at).toLocaleDateString()}</>
                                                )}
                                              </div>
                                              {d.reason && (
                                                <div className="text-xs text-gray-500 italic mt-0.5 truncate">
                                                  &ldquo;{d.reason}&rdquo;
                                                </div>
                                              )}
                                            </div>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="shrink-0"
                                              onClick={() => handleUndismissDuplicate(d.pair_key)}
                                              data-testid={`undismiss-pair-${d.pair_key}`}
                                              title="Restore this pair - will appear in duplicate search again"
                                            >
                                              <RotateCcw className="h-3 w-3 mr-1" />
                                              Undismiss
                                            </Button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                        </div>
                        
                        <div className="p-3 border rounded-lg">
                          <Label className="text-sm font-medium text-gray-700">Merge History</Label>
                          <p className="text-xs text-gray-500 mt-1">View past client merge operations (Audited)</p>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="mt-2"
                                onClick={() => {
                                  fetchMergeHistory();
                                }}
                              >
                                <History className="h-3 w-3 mr-1" />
                                View History
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                  <History className="h-5 w-5" />
                                  Client Merge History
                                </DialogTitle>
                              </DialogHeader>
                              
                              {mergeHistory.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                  <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                  <p>No merge history found</p>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {mergeHistory.map((log) => (
                                    <div key={log.id} className="p-4 border rounded-lg">
                                      <div className="flex justify-between items-start mb-2">
                                        <div>
                                          <div className="font-medium">
                                            {log.source_client_name} → {log.target_client_name}
                                          </div>
                                          <div className="text-sm text-gray-600">
                                            MRN {log.source_mrn || 'N/A'} merged into MRN {log.target_mrn || 'N/A'}
                                          </div>
                                        </div>
                                        <Badge variant="outline" className="text-xs">
                                          {new Date(log.merged_at).toLocaleDateString()}
                                        </Badge>
                                      </div>
                                      
                                      <div className="text-sm text-gray-600">
                                        Merged by: {log.merged_by_name}
                                      </div>
                                      
                                      <div className="mt-2 pt-2 border-t">
                                        <div className="text-sm font-medium mb-1">Records Migrated:</div>
                                        <div className="flex flex-wrap gap-2">
                                          {Object.entries(log.records_merged || {}).map(([type, count]) => (
                                            <Badge key={type} variant="secondary" className="text-xs">
                                              {type}: {count}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Service Enrollment Diagnostic Tool */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Service Enrollment Diagnostic</CardTitle>
                        <p className="text-sm text-gray-500">Find and fix mismatches between assigned providers and service enrollments</p>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <Button
                          onClick={async () => {
                            setEnrollmentDiagnosticLoading(true);
                            try {
                              const response = await axios.get(`${API}/admin/diagnose-enrollment-mismatches`, {
                                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                              });
                              setEnrollmentDiagnostic(response.data);
                            } catch (error) {
                              toast.error('Failed to run diagnostic');
                              console.error(error);
                            } finally {
                              setEnrollmentDiagnosticLoading(false);
                            }
                          }}
                          disabled={enrollmentDiagnosticLoading}
                          className="w-full"
                        >
                          {enrollmentDiagnosticLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Running Diagnostic...
                            </>
                          ) : (
                            <>
                              <Search className="h-4 w-4 mr-2" />
                              Run Diagnostic
                            </>
                          )}
                        </Button>

                        {enrollmentDiagnostic && (
                          <div className="space-y-4 border-t pt-4">
                            {/* Summary */}
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="p-2 bg-gray-50 rounded">
                                <span className="text-gray-500">Clients with Providers:</span>
                                <span className="font-medium ml-2">{enrollmentDiagnostic.summary?.total_clients_with_assigned_providers}</span>
                              </div>
                              <div className="p-2 bg-gray-50 rounded">
                                <span className="text-gray-500">Total Relationships:</span>
                                <span className="font-medium ml-2">{enrollmentDiagnostic.summary?.total_assigned_provider_relationships}</span>
                              </div>
                              <div className="p-2 bg-green-50 rounded">
                                <span className="text-green-700">With Enrollments:</span>
                                <span className="font-medium ml-2 text-green-700">{enrollmentDiagnostic.summary?.relationships_with_enrollments}</span>
                              </div>
                              <div className={`p-2 rounded ${enrollmentDiagnostic.summary?.relationships_missing_enrollments > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                                <span className={enrollmentDiagnostic.summary?.relationships_missing_enrollments > 0 ? 'text-amber-700' : 'text-green-700'}>Missing Enrollments:</span>
                                <span className={`font-medium ml-2 ${enrollmentDiagnostic.summary?.relationships_missing_enrollments > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                                  {enrollmentDiagnostic.summary?.relationships_missing_enrollments}
                                </span>
                              </div>
                            </div>

                            {/* Mismatches List */}
                            {enrollmentDiagnostic.mismatches?.length > 0 && (
                              <div className="space-y-2">
                                <h4 className="font-medium text-sm">Mismatches Found ({enrollmentDiagnostic.total_mismatches}):</h4>
                                <div className="max-h-40 overflow-y-auto border rounded p-2 text-xs space-y-1">
                                  {enrollmentDiagnostic.mismatches.map((m, idx) => (
                                    <div key={idx} className="flex justify-between items-center p-1 hover:bg-gray-50 rounded">
                                      <span>{m.client_name} <span className="text-gray-400">({m.client_status})</span></span>
                                      <span className="text-gray-500">→ {m.provider_name}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Migration Controls */}
                            {enrollmentDiagnostic.summary?.relationships_missing_enrollments > 0 && (
                              <div className="border-t pt-4 space-y-3">
                                <h4 className="font-medium text-sm">Create Missing Enrollments</h4>
                                <div>
                                  <Label className="text-xs text-gray-500">Default Service Type for Migration</Label>
                                  <Select value={selectedMigrationServiceType} onValueChange={setSelectedMigrationServiceType}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select a service type..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {enrollmentDiagnostic.available_service_types?.map(st => (
                                        <SelectItem key={st.id} value={st.name}>{st.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={async () => {
                                      if (!selectedMigrationServiceType) {
                                        toast.error('Please select a service type first');
                                        return;
                                      }
                                      setEnrollmentMigrationRunning(true);
                                      try {
                                        const response = await axios.post(
                                          `${API}/admin/migrate-assigned-providers-to-enrollments?dry_run=true&default_service_type=${encodeURIComponent(selectedMigrationServiceType)}`,
                                          {},
                                          { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }}
                                        );
                                        toast.success(`Dry run complete: Would create ${response.data.enrollments_to_create} enrollments`);
                                      } catch (error) {
                                        toast.error('Dry run failed');
                                        console.error(error);
                                      } finally {
                                        setEnrollmentMigrationRunning(false);
                                      }
                                    }}
                                    disabled={enrollmentMigrationRunning || !selectedMigrationServiceType}
                                  >
                                    Preview (Dry Run)
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-amber-600 hover:bg-amber-700"
                                    onClick={async () => {
                                      if (!selectedMigrationServiceType) {
                                        toast.error('Please select a service type first');
                                        return;
                                      }
                                      if (!window.confirm(`This will create ${enrollmentDiagnostic.summary.relationships_missing_enrollments} enrollments with service type "${selectedMigrationServiceType}". Continue?`)) {
                                        return;
                                      }
                                      setEnrollmentMigrationRunning(true);
                                      try {
                                        const response = await axios.post(
                                          `${API}/admin/migrate-assigned-providers-to-enrollments?dry_run=false&default_service_type=${encodeURIComponent(selectedMigrationServiceType)}`,
                                          {},
                                          { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }}
                                        );
                                        toast.success(`Migration complete: Created ${response.data.enrollments_created} enrollments`);
                                        if (response.data.errors?.length > 0) {
                                          toast.warning(`${response.data.errors.length} errors occurred`);
                                        }
                                        // Re-run diagnostic to update the view
                                        const diagResponse = await axios.get(`${API}/admin/diagnose-enrollment-mismatches`, {
                                          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                        });
                                        setEnrollmentDiagnostic(diagResponse.data);
                                      } catch (error) {
                                        toast.error('Migration failed');
                                        console.error(error);
                                      } finally {
                                        setEnrollmentMigrationRunning(false);
                                      }
                                    }}
                                    disabled={enrollmentMigrationRunning || !selectedMigrationServiceType}
                                  >
                                    {enrollmentMigrationRunning ? 'Running...' : 'Run Migration'}
                                  </Button>
                                </div>
                              </div>
                            )}

                            {enrollmentDiagnostic.summary?.relationships_missing_enrollments === 0 && (
                              <div className="p-3 bg-green-50 rounded-lg text-green-700 text-sm flex items-center gap-2">
                                <CheckCircle className="h-4 w-4" />
                                All assigned providers have corresponding service enrollments!
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Duplicate Enrollment Cleanup Tool */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Enrollment Data Cleanup</CardTitle>
                        <p className="text-sm text-gray-500">Find duplicates and normalize service type data</p>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Button
                          onClick={async () => {
                            try {
                              const response = await axios.get(`${API}/admin/diagnose-duplicate-enrollments`, {
                                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                              });
                              if (response.data.duplicate_sets === 0) {
                                toast.success('No duplicate enrollments found!');
                              } else {
                                const confirmCleanup = window.confirm(
                                  `Found ${response.data.duplicate_sets} sets of duplicate enrollments.\n\n` +
                                  `Would you like to clean them up? (This will keep the oldest enrollment in each set)`
                                );
                                if (confirmCleanup) {
                                  const cleanupRes = await axios.post(
                                    `${API}/admin/cleanup-duplicate-enrollments?dry_run=false`,
                                    {},
                                    { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }}
                                  );
                                  toast.success(`Cleaned up ${cleanupRes.data.deleted} duplicate enrollments`);
                                }
                              }
                            } catch (error) {
                              toast.error('Failed to check for duplicates');
                              console.error(error);
                            }
                          }}
                          variant="outline"
                          className="w-full"
                        >
                          <Search className="h-4 w-4 mr-2" />
                          Find & Clean Duplicates
                        </Button>
                        <Button
                          onClick={async () => {
                            try {
                              // First check what would be normalized
                              const dryRunRes = await axios.post(
                                `${API}/admin/normalize-enrollment-service-types?dry_run=true`,
                                {},
                                { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }}
                              );
                              
                              if (dryRunRes.data.would_update === 0) {
                                toast.success('All service types are already normalized!');
                                return;
                              }
                              
                              const confirmNormalize = window.confirm(
                                `Found ${dryRunRes.data.would_update} enrollments with service type names instead of IDs.\n\n` +
                                `Normalize them for consistent data storage?`
                              );
                              
                              if (confirmNormalize) {
                                const normalizeRes = await axios.post(
                                  `${API}/admin/normalize-enrollment-service-types?dry_run=false`,
                                  {},
                                  { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }}
                                );
                                toast.success(`Normalized ${normalizeRes.data.updated} enrollments`);
                              }
                            } catch (error) {
                              toast.error('Failed to normalize service types');
                              console.error(error);
                            }
                          }}
                          variant="outline"
                          className="w-full"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Normalize Service Types
                        </Button>
                      </CardContent>
                    </Card>

                    {/* UUID Display Issues Diagnostic Tool */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">UUID Display Issues</CardTitle>
                        <p className="text-sm text-gray-500">Find and fix places where UUIDs are showing instead of human-readable names</p>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <Button
                          onClick={async () => {
                            setUuidDiagnosticLoading(true);
                            try {
                              const response = await axios.get(`${API}/admin/diagnose-uuid-display-issues`, {
                                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                              });
                              setUuidDiagnostic(response.data);
                              if (response.data.total_issues === 0) {
                                toast.success('No UUID display issues found!');
                              }
                            } catch (error) {
                              toast.error('Failed to run UUID diagnostic');
                              console.error(error);
                            } finally {
                              setUuidDiagnosticLoading(false);
                            }
                          }}
                          disabled={uuidDiagnosticLoading}
                          className="w-full"
                        >
                          {uuidDiagnosticLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Scanning...
                            </>
                          ) : (
                            <>
                              <Search className="h-4 w-4 mr-2" />
                              Scan Database for UUID Issues
                            </>
                          )}
                        </Button>

                        <Button
                          onClick={async () => {
                            setUuidCodeDiagnosticLoading(true);
                            try {
                              const response = await axios.get(`${API}/admin/diagnose-code-uuid-issues`, {
                                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                              });
                              setUuidCodeDiagnostic(response.data);
                              if (response.data.total_issues === 0) {
                                toast.success('No code-level UUID issues found!');
                              }
                            } catch (error) {
                              toast.error('Failed to analyze code');
                              console.error(error);
                            } finally {
                              setUuidCodeDiagnosticLoading(false);
                            }
                          }}
                          disabled={uuidCodeDiagnosticLoading}
                          variant="outline"
                          className="w-full"
                        >
                          {uuidCodeDiagnosticLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                              Analyzing Code...
                            </>
                          ) : (
                            <>
                              <Code2 className="h-4 w-4 mr-2" />
                              Analyze Code for UUID Patterns
                            </>
                          )}
                        </Button>

                        {/* Code Analysis Results */}
                        {uuidCodeDiagnostic && (
                          <div className="space-y-4 border-t pt-4">
                            <div className={`p-3 rounded-lg ${uuidCodeDiagnostic.total_issues > 0 ? 'bg-purple-50' : 'bg-green-50'}`}>
                              <div className={`font-medium ${uuidCodeDiagnostic.total_issues > 0 ? 'text-purple-700' : 'text-green-700'}`}>
                                {uuidCodeDiagnostic.summary}
                              </div>
                              {uuidCodeDiagnostic.recommendation && (
                                <p className="text-sm text-gray-600 mt-1">{uuidCodeDiagnostic.recommendation}</p>
                              )}
                            </div>

                            {uuidCodeDiagnostic.issues_by_pattern?.map((patternGroup, idx) => (
                              <div key={idx} className="border rounded-lg overflow-hidden">
                                <div className={`p-3 flex justify-between items-center ${
                                  patternGroup.severity === 'high' ? 'bg-red-50' : 
                                  patternGroup.severity === 'medium' ? 'bg-yellow-50' : 'bg-gray-50'
                                }`}>
                                  <div>
                                    <span className="font-medium">{patternGroup.pattern}</span>
                                    <span className="ml-2 text-sm text-gray-500">({patternGroup.count} instances)</span>
                                  </div>
                                  <Badge className={
                                    patternGroup.severity === 'high' ? 'bg-red-100 text-red-800' :
                                    patternGroup.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                                  }>
                                    {patternGroup.severity}
                                  </Badge>
                                </div>
                                <div className="p-3 text-sm text-gray-600">
                                  <p className="mb-2">{patternGroup.description}</p>
                                  <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {patternGroup.locations?.slice(0, 5).map((loc, locIdx) => (
                                      <div key={locIdx} className="bg-gray-900 text-gray-100 p-2 rounded text-xs font-mono">
                                        <div className="text-gray-400 mb-1">Line {loc.line}:</div>
                                        <div className="text-green-400">{loc.code}</div>
                                        {loc.suggestion && (
                                          <div className="mt-2 text-yellow-300 border-t border-gray-700 pt-2">
                                            💡 Fix: {loc.suggestion}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {uuidDiagnostic && (
                          <div className="space-y-4 border-t pt-4">
                            {/* Summary */}
                            <div className={`p-3 rounded-lg ${uuidDiagnostic.total_issues > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                              <div className={`font-medium ${uuidDiagnostic.total_issues > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                                {uuidDiagnostic.total_issues > 0 
                                  ? `Found ${uuidDiagnostic.total_issues} UUID display issues`
                                  : 'No UUID display issues found!'
                                }
                              </div>
                            </div>

                            {/* Issues by Collection */}
                            {uuidDiagnostic.issues?.length > 0 && (
                              <div className="space-y-3">
                                {uuidDiagnostic.issues.map((issue, idx) => (
                                  <div key={idx} className="border rounded-lg p-3 space-y-2">
                                    <div className="flex justify-between items-center">
                                      <div>
                                        <span className="font-medium text-sm">{issue.collection}</span>
                                        <span className="text-gray-500 text-xs ml-2">({issue.count} issues)</span>
                                      </div>
                                      {issue.fix_available && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-7 text-xs"
                                          disabled={uuidFixRunning}
                                          onClick={async () => {
                                            if (!window.confirm(`Fix ${issue.count} UUID display issues in ${issue.collection}?`)) return;
                                            setUuidFixRunning(true);
                                            try {
                                              const res = await axios.post(
                                                `${API}/admin/fix-uuid-display-issues?collection=${issue.collection}&dry_run=false`,
                                                {},
                                                { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }}
                                              );
                                              toast.success(`Fixed ${res.data.fixed} records in ${issue.collection}`);
                                              // Re-run diagnostic
                                              const diagRes = await axios.get(`${API}/admin/diagnose-uuid-display-issues`, {
                                                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                              });
                                              setUuidDiagnostic(diagRes.data);
                                            } catch (error) {
                                              toast.error('Failed to fix UUID issues');
                                              console.error(error);
                                            } finally {
                                              setUuidFixRunning(false);
                                            }
                                          }}
                                        >
                                          {uuidFixRunning ? 'Fixing...' : 'Fix'}
                                        </Button>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500">{issue.issue_type}</div>
                                    {issue.items?.length > 0 && (
                                      <div className="max-h-32 overflow-y-auto bg-gray-50 rounded p-2 text-xs space-y-1">
                                        {issue.items.slice(0, 5).map((item, i) => (
                                          <div key={i} className="flex justify-between text-gray-600">
                                            <span className="truncate flex-1">{item.title || item.name || item.task_id || item.notification_id || item.enrollment_id}</span>
                                            <span className="text-amber-600 ml-2 truncate max-w-[150px]" title={item.uuid || item.value}>
                                              {item.should_be || item.value || 'UUID'}
                                            </span>
                                          </div>
                                        ))}
                                        {issue.items.length > 5 && (
                                          <div className="text-gray-400">...and {issue.items.length - 5} more</div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* PDF Bulk Import Tool */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-600" />
                          PDF Bulk Import
                        </CardTitle>
                        <p className="text-sm text-gray-500">Upload PDFs to match and import into client charts</p>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-xs text-gray-500">
                          Upload PDFs with filenames like: <code className="bg-gray-100 px-1 py-0.5 rounded">John Smith - Progress Note - 01-15-2024.pdf</code>
                        </p>
                        <p className="text-xs text-gray-500">
                          The tool will automatically match files to clients, detect duplicates, and categorize documents.
                        </p>
                        <Button
                          onClick={() => window.open('/api/pdf-import/', '_blank')}
                          className="w-full"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Open PDF Import Tool
                        </Button>
                      </CardContent>
                    </Card>
                    
                    {/* Note Text Backup Search - Recovery Tool */}
                    {isSuperAdmin(user) && <NoteTextBackups />}
                    
                    {/* Deleted Notes Recovery - Super Admin Only */}
                    {isSuperAdmin(user) && (
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Trash2 className="h-5 w-5 text-red-500" />
                            Deleted Notes Recovery
                          </CardTitle>
                          <Dialog open={showDeletedNotesDialog} onOpenChange={(open) => {
                            setShowDeletedNotesDialog(open);
                            if (open) fetchDeletedNotes();
                          }}>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50"
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                View & Restore
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                  <Trash2 className="h-5 w-5 text-red-500" />
                                  Deleted Notes Recovery
                                </DialogTitle>
                                <p className="text-sm text-gray-500 mt-1">
                                  Notes that were soft-deleted can be restored here. Hard-deleted notes (from before this feature) cannot be recovered.
                                </p>
                              </DialogHeader>
                              
                              <div className="space-y-4">
                                {/* Live client-side search across already-fetched notes.
                                    Matches client name, MRN, provider name, and template/title
                                    so admins can find a deleted note without knowing the client ID. */}
                                <div className="flex items-center gap-2">
                                  <Label className="shrink-0">Search:</Label>
                                  <DebouncedInput
                                    placeholder="Filter by client name, MRN, provider, or template…"
                                    value={deletedNotesClientFilter}
                                    onChange={(e) => setDeletedNotesClientFilter(e.target.value)}
                                    className="flex-1 max-w-md"
                                    data-testid="deleted-notes-search"
                                  />
                                  {deletedNotesClientFilter && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setDeletedNotesClientFilter('')}
                                      data-testid="deleted-notes-search-clear"
                                    >
                                      Clear
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => fetchDeletedNotes()}
                                    title="Reload list from server"
                                    data-testid="deleted-notes-refresh"
                                  >
                                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                    Refresh
                                  </Button>
                                </div>

                                {/* Deleted Notes List — filtered client-side to the visible subset. */}
                                {(() => {
                                  const q = (deletedNotesClientFilter || '').trim().toLowerCase();
                                  const filtered = !q ? deletedNotes : deletedNotes.filter(n =>
                                    (n.client_name || '').toLowerCase().includes(q)
                                    || (n.client_mrn || '').toLowerCase().includes(q)
                                    || (n.provider_name || '').toLowerCase().includes(q)
                                    || (n.template_name || '').toLowerCase().includes(q)
                                    || (n.title || '').toLowerCase().includes(q)
                                  );
                                  if (deletedNotesLoading) {
                                    return (
                                      <div className="flex items-center justify-center py-8">
                                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                        <span className="ml-2 text-gray-500">Loading deleted notes...</span>
                                      </div>
                                    );
                                  }
                                  if (deletedNotes.length === 0) {
                                    return (
                                      <div className="text-center py-8 text-gray-500">
                                        <Trash2 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                                        <p>No deleted notes found</p>
                                        <p className="text-sm">Notes deleted before soft-delete was implemented cannot be recovered here.</p>
                                      </div>
                                    );
                                  }
                                  if (filtered.length === 0) {
                                    return (
                                      <div className="text-center py-8 text-gray-500">
                                        <p>No deleted notes match "{deletedNotesClientFilter}"</p>
                                        <p className="text-xs mt-1">Try a shorter query or clear the search.</p>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
                                      {filtered.map((note) => (
                                        <div key={note.id} className="p-3 hover:bg-gray-50 flex items-center justify-between" data-testid={`deleted-note-row-${note.id}`}>
                                          <div className="flex-1">
                                            <div className="font-medium text-sm">
                                              {note.template_name || note.title || 'Untitled Note'}
                                            </div>
                                            <div className="text-xs text-gray-500 space-x-2">
                                              <span>Client: {note.client_name || 'Unknown Client'}</span>
                                              {note.client_mrn && <><span>•</span><span>MRN: {formatMrn(note.client_mrn)}</span></>}
                                              <span>•</span>
                                              <span>Provider: {note.provider_name || note.provider_id || 'Unknown'}</span>
                                              <span>•</span>
                                              <span>Deleted: {note.deleted_at ? new Date(note.deleted_at).toLocaleString() : 'Unknown'}</span>
                                              {note.deleted_by_name && <><span>•</span><span>By: {note.deleted_by_name}</span></>}
                                            </div>
                                            {note.deletion_reason && (
                                              <div className="text-xs text-red-500 mt-1">
                                                Reason: {note.deletion_reason}
                                              </div>
                                            )}
                                          </div>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-green-300 text-green-600 hover:bg-green-50"
                                            onClick={() => handleRestoreNote(note.id)}
                                          >
                                            <RotateCcw className="h-4 w-4 mr-1" />
                                            Restore
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}

                                <div className="text-xs text-gray-400 mt-2">
                                  {(() => {
                                    const q = (deletedNotesClientFilter || '').trim().toLowerCase();
                                    const filteredCount = !q ? deletedNotes.length : deletedNotes.filter(n =>
                                      (n.client_name || '').toLowerCase().includes(q)
                                      || (n.client_mrn || '').toLowerCase().includes(q)
                                      || (n.provider_name || '').toLowerCase().includes(q)
                                      || (n.template_name || '').toLowerCase().includes(q)
                                      || (n.title || '').toLowerCase().includes(q)
                                    ).length;
                                    return q
                                      ? `Showing ${filteredCount} of ${deletedNotes.length} deleted notes`
                                      : `Showing ${deletedNotes.length} deleted note${deletedNotes.length !== 1 ? 's' : ''}`;
                                  })()}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-gray-500">
                            Recover notes that were accidentally deleted or auto-removed by the system.
                            Only soft-deleted notes (after this feature was added) can be restored.
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Deleted Documents Recovery - Admin Only */}
                    {isAdmin() && (
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <FileText className="h-5 w-5 text-amber-500" />
                            Deleted Documents
                          </CardTitle>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-amber-300 text-amber-600 hover:bg-amber-50"
                            onClick={() => setShowDeletedDocuments(true)}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            View & Restore
                          </Button>
                        </CardHeader>
                        <CardContent className="text-sm text-gray-600">
                          <p>
                            Recover accidentally deleted client documents. Documents are kept for 30 days before permanent deletion.
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Note Reassignment - Super Admin Only (for cross-contamination fixes) */}
                    {isSuperAdmin(user) && (
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <ArrowRightLeft className="h-5 w-5 text-orange-500" />
                            Note Reassignment
                          </CardTitle>
                          <Dialog open={showReassignNoteDialog} onOpenChange={(open) => {
                            setShowReassignNoteDialog(open);
                            if (open) {
                              // Refresh the audit feed every time the dialog
                              // opens so admins see the freshest reassignments
                              // (cheap call: capped at 20 recent rows).
                              fetchReassignAudit();
                            } else {
                              // Reset form when closing
                              setReassignNoteId('');
                              setReassignNoteData(null);
                              setReassignTargetClientId('');
                              setReassignClientSearch('');
                              setReassignClientResults([]);
                            }
                          }}>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-orange-300 text-orange-600 hover:bg-orange-50"
                              >
                                <ArrowRightLeft className="h-4 w-4 mr-1" />
                                Reassign Note
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                  <ArrowRightLeft className="h-5 w-5 text-orange-500" />
                                  Reassign Note to Different Client
                                </DialogTitle>
                                <p className="text-sm text-gray-500 mt-1">
                                  Use this to fix cross-contamination issues where a note ended up in the wrong client's chart.
                                </p>
                              </DialogHeader>
                              
                              <div className="space-y-4">
                                {/* Step 1: Find the note */}
                                <div className="space-y-2">
                                  <Label className="font-semibold">Step 1: Find the Note</Label>
                                  <div className="flex items-center gap-2">
                                    <DebouncedInput 
                                      placeholder="Enter Note ID..."
                                      value={reassignNoteId}
                                      onChange={(e) => setReassignNoteId(e.target.value)}
                                      className="flex-1"
                                    />
                                    <Button 
                                      onClick={lookupNoteForReassign}
                                      disabled={reassignLoading || !reassignNoteId.trim()}
                                    >
                                      {reassignLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                                      Look Up
                                    </Button>
                                  </div>
                                  <p className="text-xs text-gray-400">
                                    You can find the Note ID in the note dialog header or from the audit logs
                                  </p>
                                </div>

                                {/* Show found note */}
                                {reassignNoteData && (
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                                    <div className="flex items-center gap-2">
                                      <FileText className="h-4 w-4 text-blue-500" />
                                      <span className="font-medium">Note Found</span>
                                    </div>
                                    <div className="text-sm space-y-1">
                                      <div><strong>Title:</strong> {reassignNoteData.template_name || reassignNoteData.title || 'Untitled'}</div>
                                      <div><strong>Current Client:</strong> {reassignNoteData.client_name || reassignNoteData.client_id}</div>
                                      <div><strong>Status:</strong> {reassignNoteData.status}</div>
                                      <div><strong>Created:</strong> {reassignNoteData.created_at ? new Date(reassignNoteData.created_at).toLocaleString() : 'Unknown'}</div>
                                    </div>
                                  </div>
                                )}

                                {/* Step 2: Select target client */}
                                {reassignNoteData && (
                                  <div className="space-y-2">
                                    <Label className="font-semibold">Step 2: Select Correct Client</Label>
                                    <DebouncedInput 
                                      placeholder="Search for client by name or MRN (3+ chars for partial name match)..."
                                      value={reassignClientSearch}
                                      onChange={(e) => {
                                        setReassignClientSearch(e.target.value);
                                        searchClientsForReassign(e.target.value);
                                      }}
                                      data-testid="reassign-client-search-input"
                                    />
                                    
                                    {/* Client search results */}
                                    {reassignClientResults.length > 0 ? (
                                      <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto" data-testid="reassign-client-results-list">
                                        {reassignClientResults.map((client) => (
                                          <div 
                                            key={client.id} 
                                            className={`p-2 hover:bg-gray-50 cursor-pointer flex items-center justify-between ${
                                              reassignTargetClientId === client.id ? 'bg-green-50 border-l-4 border-green-500' : ''
                                            }`}
                                            onClick={() => {
                                              setReassignTargetClientId(client.id);
                                              setReassignClientSearch(client.full_name || `${client.first_name} ${client.last_name}`);
                                            }}
                                          >
                                            <div>
                                              <div className="font-medium text-sm">{client.full_name || `${client.first_name} ${client.last_name}`}</div>
                                              <div className="text-xs text-gray-500">MRN: {formatMrn(client.mrn)}</div>
                                            </div>
                                            {reassignTargetClientId === client.id && (
                                              <Check className="h-4 w-4 text-green-500" />
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      // Three empty-state cases — show explicit feedback so the user
                                      // knows whether the search ran or whether they need to type more.
                                      // Previously this was a silent "nothing renders" which the user
                                      // reported as "I type and nothing happens" (Feb 2026).
                                      reassignClientSearch.length === 0 ? null :
                                      reassignClientSearch.length < 2 ? (
                                        <div className="text-xs text-gray-500 italic px-1" data-testid="reassign-search-hint-typing">
                                          Type 2+ characters to search…
                                        </div>
                                      ) : reassignClientSearch.length < 3 ? (
                                        <div className="text-xs text-gray-500 italic px-1" data-testid="reassign-search-hint-typing">
                                          Type 3+ characters for partial name matching, or enter a full MRN.
                                        </div>
                                      ) : (
                                        <div className="text-xs text-gray-500 italic px-1" data-testid="reassign-search-no-results">
                                          No clients found matching "{reassignClientSearch}". Try a different name or the patient's MRN.
                                        </div>
                                      )
                                    )}

                                    {reassignTargetClientId && (
                                      <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-sm">
                                        <strong>Target Client Selected:</strong> {reassignClientSearch}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Confirm and execute */}
                                {reassignNoteData && reassignTargetClientId && (
                                  <div className="space-y-3">
                                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                                      <div className="font-medium text-orange-800 mb-2">⚠️ Confirm Reassignment</div>
                                      <div className="text-sm text-orange-700">
                                        <p>This will move the note from:</p>
                                        <p className="mt-1"><strong>From:</strong> {reassignNoteData.client_name || reassignNoteData.client_id}</p>
                                        <p><strong>To:</strong> {reassignClientSearch}</p>
                                        <p className="mt-2 text-xs">This action is logged and can be audited.</p>
                                      </div>
                                    </div>
                                    <Button 
                                      onClick={handleReassignNote}
                                      disabled={reassignLoading}
                                      className="w-full bg-orange-600 hover:bg-orange-700"
                                    >
                                      {reassignLoading ? (
                                        <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                                      ) : (
                                        <><ArrowRightLeft className="h-4 w-4 mr-2" /> Reassign Note</>
                                      )}
                                    </Button>
                                  </div>
                                )}

                                {/* Recent Reassignments — sanity-check feed.
                                    A misclick on the wrong target client is
                                    the highest-risk failure mode for this
                                    tool; surfacing the last 20 reassignments
                                    inline lets the admin spot it within
                                    seconds (e.g., "wait, that note was
                                    supposed to go to Smith, not Jones"). */}
                                <div className="border-t pt-4 mt-2" data-testid="reassign-audit-section">
                                  <div className="flex items-center justify-between mb-2">
                                    <Label className="font-semibold text-sm">Recent reassignments (last 20)</Label>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      onClick={fetchReassignAudit}
                                      disabled={reassignAuditLoading}
                                      data-testid="reassign-audit-refresh-btn"
                                      className="h-7 px-2"
                                    >
                                      {reassignAuditLoading ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <RefreshCw className="h-3.5 w-3.5" />
                                      )}
                                    </Button>
                                  </div>
                                  {reassignAuditLoading && reassignAuditEvents.length === 0 ? (
                                    <div className="text-xs text-gray-500 italic">Loading…</div>
                                  ) : reassignAuditEvents.length === 0 ? (
                                    <div className="text-xs text-gray-500 italic" data-testid="reassign-audit-empty">
                                      No reassignments yet. Once you complete one, it'll show up here so you can verify the right client was picked.
                                    </div>
                                  ) : (
                                    <div
                                      className="border rounded-lg divide-y max-h-[260px] overflow-y-auto bg-white"
                                      data-testid="reassign-audit-list"
                                    >
                                      {reassignAuditEvents.map((e, i) => (
                                        <div
                                          key={e.id || `${e.note_id}-${i}`}
                                          className="p-2.5 text-xs hover:bg-gray-50"
                                          data-testid={`reassign-audit-row-${i}`}
                                        >
                                          <div className="flex items-center justify-between gap-2">
                                            <div className="font-medium text-gray-900 truncate">
                                              {e.doc_number ? (
                                                <span className="font-mono text-orange-700">{e.doc_number}</span>
                                              ) : (
                                                <span className="font-mono text-gray-500">{(e.note_id || '').slice(0, 8) || '—'}</span>
                                              )}
                                            </div>
                                            <div className="text-[10px] text-gray-500 whitespace-nowrap">
                                              {e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}
                                            </div>
                                          </div>
                                          <div className="mt-1 flex items-center gap-1.5 text-gray-700">
                                            <span className="text-gray-500">From:</span>
                                            <span className="truncate">{e.from_client?.name || e.from_client?.id || '—'}</span>
                                            <ArrowRightLeft className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                            <span className="text-gray-500">To:</span>
                                            <span className="truncate font-medium">{e.to_client?.name || e.to_client?.id || '—'}</span>
                                          </div>
                                          <div className="mt-0.5 text-[10px] text-gray-500">
                                            by {e.reassigned_by_name || 'unknown'}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-gray-500">
                            Fix cross-contamination by moving a note from one client's chart to another.
                            Use when a note was accidentally saved to the wrong client.
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Data Recovery Tools - Super Admin Only */}
                    {user?.is_super_admin && (
                      <Card className="border-amber-200 bg-amber-50/30">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2 text-amber-900">
                              <Shield className="h-5 w-5" />
                              Data Recovery Tools
                            </CardTitle>
                            <p className="text-sm text-amber-700 mt-1">Super Admin only - All usage is logged</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-amber-300 text-amber-800 hover:bg-amber-100"
                            onClick={() => setShowDecryptionTool(true)}
                          >
                            <Key className="h-4 w-4 mr-1" />
                            Decryption Tool
                          </Button>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm text-amber-800">
                            <p>• Decrypt PHI data from database for troubleshooting</p>
                            <p>• Verify encrypted data integrity</p>
                            <p>• Data recovery assistance</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Decryption Tool Dialog */}
                    <DecryptionTool 
                      open={showDecryptionTool} 
                      onOpenChange={setShowDecryptionTool} 
                    />

                    {/* UUID Resolver Tool (Super Admin Only) */}
                    {user?.is_super_admin && (
                      <Card className="border-blue-200 bg-blue-50/50">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2 text-blue-900">
                              <FileJson className="h-5 w-5" />
                              UUID Resolver
                            </CardTitle>
                            <p className="text-sm text-blue-700 mt-1">Convert database UUIDs to human-readable names</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-blue-300 text-blue-800 hover:bg-blue-100"
                            onClick={() => setShowUUIDResolver(true)}
                          >
                            <Search className="h-4 w-4 mr-1" />
                            UUID Resolver
                          </Button>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm text-blue-800">
                            <p>• Resolve client_id, provider_id, and other UUIDs to names</p>
                            <p>• Paste any JSON document from the database</p>
                            <p>• Makes database documents human-readable</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* UUID Resolver Dialog */}
                    <UUIDResolver 
                      open={showUUIDResolver} 
                      onOpenChange={setShowUUIDResolver} 
                    />

                    {/* UUID Lookup Tool (Super Admin Only) */}
                    {user?.is_super_admin && (
                      <Card className="border-purple-200 bg-purple-50/50">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2 text-purple-900">
                              <Search className="h-5 w-5" />
                              UUID Lookup
                            </CardTitle>
                            <p className="text-sm text-purple-700 mt-1">Find UUIDs by searching for names</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-purple-300 text-purple-800 hover:bg-purple-100"
                            onClick={() => setShowUUIDLookup(true)}
                          >
                            <Search className="h-4 w-4 mr-1" />
                            UUID Lookup
                          </Button>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm text-purple-800">
                            <p>• Search for clients or users by name</p>
                            <p>• Get their UUID for database queries</p>
                            <p>• Reverse lookup - name to UUID</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* UUID Lookup Dialog */}
                    <UUIDLookup 
                      open={showUUIDLookup} 
                      onOpenChange={setShowUUIDLookup} 
                    />

                    {/* Legacy Data Import Tool (SFTP) - Super Admin Only */}
                    {user?.is_super_admin && (
                      <Card className="border-blue-200 bg-blue-50/50">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2 text-blue-900">
                              <FileArchive className="h-5 w-5" />
                              Legacy Data Import (SFTP)
                            </CardTitle>
                            <p className="text-sm text-blue-700 mt-1">Import client documents from legacy EMR via SFTP</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-blue-300 text-blue-800 hover:bg-blue-100"
                            onClick={() => window.open('/admin/legacy-import', '_blank')}
                            data-testid="legacy-import-card-btn"
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Open Import Tool
                          </Button>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm text-blue-800">
                            <p>• Connect to SFTP server and download client ZIPs</p>
                            <p>• Auto-convert Word docs to PDF</p>
                            <p>• Match clients by name/DOB with fuzzy matching</p>
                            <p>• Content-based deduplication (SHA-256)</p>
                            <p>• Archive unmatched clients for review</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Legacy Note Import Tool (Super Admin Only) */}
                    {user?.is_super_admin && (
                      <Card className="border-emerald-200 bg-emerald-50/50">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2 text-emerald-900">
                              <Upload className="h-5 w-5" />
                              Legacy Note Import
                            </CardTitle>
                            <p className="text-sm text-emerald-700 mt-1">Import notes from your previous EMR system</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                            onClick={() => setShowLegacyNoteImport(true)}
                          >
                            <Upload className="h-4 w-4 mr-1" />
                            Import Notes
                          </Button>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm text-emerald-800">
                            <p>• Bulk upload .txt files from legacy EMR exports</p>
                            <p>• Auto-detect note types (Progress Notes, Family Consults)</p>
                            <p>• Match clients by name and DOB</p>
                            <p>• Download report of unmatched files</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Legacy Note Import Dialog */}
                    <Dialog open={showLegacyNoteImport} onOpenChange={setShowLegacyNoteImport}>
                      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                        <LegacyNoteImport />
                      </DialogContent>
                    </Dialog>
                  </div>
                  
                  {/* Broken Notes Diagnostic - Full Width at Bottom */}
                  {isSuperAdmin(user) && (
                    <div className="mt-6">
                      <BrokenNotesDiagnostic />
                    </div>
                  )}

                  {/* CPT Description Mismatch Diagnostic (Jun 2026) */}
                  {isSuperAdmin(user) && (
                    <div className="mt-6">
                      <CptDescriptionDiagnostic />
                    </div>
                  )}

                  {/* Intake-Bundle DOC-ID Backfill (Mar 2026) - one-click
                      admin action for bundle notes created before the
                      auto-doc_number fix. */}
                  {isSuperAdmin(user) && (
                    <div className="mt-6">
                      <IntakeBundleDocNumberBackfillCard />
                    </div>
                  )}

                  {/* Snapshot Mismatch Audit - PHI compliance dashboard
                      (May 2026).  Surfaces notes where the chart-context
                      snapshot renderer detected stale data belonging to
                      another patient.  Super-admin only — gated for
                      HIPAA-officer use. */}
                  {isSuperAdmin(user) && (
                    <div className="mt-6">
                      <SnapshotMismatchAuditPanel />
                    </div>
                  )}
                </TabsContent>
                )}

                <TabsContent value="help">
                  <Suspense fallback={<PageLoader label="Loading Admin Help Center..." />}>
                    <HelpModule />
                  </Suspense>
                </TabsContent>
                <TabsContent value="payroll">
                  <Suspense fallback={<PageLoader label="Loading Payroll Configuration..." />}>
                    <PayrollConfigPanel user={user} />
                  </Suspense>
                </TabsContent>
                {tenantToolAccess.bulkImport && (
                <TabsContent value="bulk-import">
                  <Suspense fallback={<PageLoader label="Loading Bulk Import..." />}>
                    <BulkAppointmentImportPanel />
                  </Suspense>
                </TabsContent>
                )}
              </Tabs>
              </>
              )}
            </AdminPortalWrapper>
          </TabsContent>

          {/* Users Tab (Admin Only) */}
          {isAdmin() && (
            <TabsContent value="users">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>User Management</CardTitle>
                  {/* Button triggers the global User Form dialog defined in Admin section */}
                  <Button 
                    data-testid="add-user-button" 
                    className="bg-teal-600 hover:bg-teal-700"
                    onClick={() => {
                      setEditingUser(null);
                      resetUserFormData();
                      setShowUserForm(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[...users].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')).map((userItem) => (
                      <Card key={userItem.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg flex items-center gap-2">
                              {userItem.full_name}
                              {userItem.disable_appointment_reminders && (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border border-amber-300 bg-amber-100 text-amber-800"
                                  title="All automated appointment reminders are disabled for this provider"
                                  data-testid={`reminders-off-badge-${userItem.id}`}
                                >
                                  <BellOff className="h-3 w-3" />
                                  Reminders OFF
                                </span>
                              )}
                            </h3>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <div className="flex items-center">
                                <Mail className="h-4 w-4 mr-1" />
                                {userItem.email}
                              </div>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              Created: {new Date(userItem.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge className={getRoleBadgeColor(roles?.find(r => r.id === userItem.role_id)?.name || userItem.role)}>
                              {roles?.find(r => r.id === userItem.role_id)?.name || userItem.role || 'No Role'}
                            </Badge>
                            <Badge className={userItem.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {userItem.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            {/* 2FA Status Badge */}
                            <Badge 
                              className={userItem.two_factor_enabled ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}
                              title={userItem.two_factor_enabled ? '2FA Enabled' : '2FA Not Set Up'}
                            >
                              <Shield className="h-3 w-3 mr-1" />
                              {userItem.two_factor_enabled ? '2FA' : 'No 2FA'}
                            </Badge>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={async () => {
                                if (window.confirm(`Reset password for ${userItem.full_name}? A temporary password will be generated and sent to their email.`)) {
                                  try {
                                    const tempPassword = await handleAdminResetPassword(userItem.id);
                                    alert(`Temporary password for ${userItem.full_name}: ${tempPassword}\n\nThis password has been sent to ${userItem.email}`);
                                  } catch (error) {
                                    // Error already shown by handler
                                  }
                                }
                              }}
                              title="Reset Password"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            {/* 2FA Management Button */}
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" title="2FA Options">
                                  <Shield className="h-4 w-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-56">
                                <div className="space-y-2">
                                  <p className="text-sm font-medium">2FA Management</p>
                                  {userItem.two_factor_enabled ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="w-full text-red-600 hover:text-red-700"
                                      onClick={() => {
                                        if (window.confirm(`Reset 2FA for ${userItem.full_name}? They will need to set it up again on next login.`)) {
                                          resetUser2FA(userItem.id);
                                        }
                                      }}
                                    >
                                      <RotateCcw className="h-4 w-4 mr-2" />
                                      Reset 2FA
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="w-full"
                                      onClick={() => requireUser2FA(userItem.id)}
                                    >
                                      <Shield className="h-4 w-4 mr-2" />
                                      Require 2FA Setup
                                    </Button>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                            <Button variant="outline" size="sm" data-testid={`view-user-${userItem.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                    
                    {users.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        No users found. Create your first user account to get started.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Password Reset Requests Section */}
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span>Password Reset Requests</span>
                    {passwordResetRequests.filter(r => r.status === 'pending').length > 0 && (
                      <span className="bg-red-500 text-white text-sm px-2 py-1 rounded">
                        {passwordResetRequests.filter(r => r.status === 'pending').length} Pending
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {passwordResetRequests.filter(r => r.status === 'pending').length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">No pending password reset requests</p>
                      <p className="text-xs mt-1">Requests will appear here when users submit them</p>
                    </div>
                  ) : (
                    passwordResetRequests.filter(r => r.status === 'pending').map(request => (
                      <div key={request.id} className="border p-4 rounded mb-3 bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold">{request.user_name}</p>
                            <p className="text-sm text-gray-600">{request.email} • {request.user_role}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Requested {new Date(request.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              size="sm"
                              onClick={() => handleApprovePasswordReset(request.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Approve
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => handleDenyPasswordReset(request.id)}
                            >
                              Deny
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Forms Tab (Admin Only) */}
          {isAdmin() && (
            <TabsContent value="forms">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Form Templates & Intake Forms</CardTitle>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="show-archived-forms-tab"
                        checked={showArchivedTemplates}
                        onCheckedChange={setShowArchivedTemplates}
                      />
                      <Label htmlFor="show-archived-forms-tab" className="text-sm cursor-pointer whitespace-nowrap">
                        Show Archived
                      </Label>
                    </div>
                    {/* Button triggers the global Form Builder dialog defined in Admin section */}
                    <Button 
                      data-testid="add-form-template-button" 
                      className="bg-teal-600 hover:bg-teal-700"
                      onClick={() => {
                        setEditingFormTemplate(null);
                        setFormTemplateData({
                          name: '',
                          form_type: 'intake',
                          description: '',
                          billable: false,
                          requires_provider_signature: false,
                          requires_supervisor_signature: false,
                          allow_client_signature: false,
                          require_client_signature: false,
                          assessment_config: null,
                          form_fields: []
                        });
                        setActiveFormFields([]);
                        // Clear static text editor cache when starting fresh
                        staticTextEditorCache.current.clear();
                        setShowFormTemplateBuilder(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Form Template
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const jsonInput = prompt('Paste your external form JSON here:');
                        if (jsonInput) {
                          const name = prompt('Enter a name for this form template:', 'Imported Form');
                          if (name) {
                            axios.post(`${API}/form-templates/import-external`, {
                              form_json: jsonInput,
                              name: name,
                              description: 'Imported from external system',
                              form_type: 'clinical_note'
                            }).then(response => {
                              toast.success(`Form "${name}" imported with ${response.data.field_count} fields!`);
                              // Static data - no global refresh needed
                            }).catch(error => {
                              toast.error(error.response?.data?.detail || 'Failed to import form');
                              console.error('Import error:', error);
                            });
                          }
                        }
                      }}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Import External JSON
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        // Create a file input to load JSON file
                        const fileInput = document.createElement('input');
                        fileInput.type = 'file';
                        fileInput.accept = '.json,application/json';
                        fileInput.onchange = async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          try {
                            const text = await file.text();
                            const jsonData = JSON.parse(text);
                            
                            const response = await axios.post(`${API}/form-templates/import-native`, {
                              template_json: jsonData
                            });
                            toast.success(`Form "${response.data.name}" imported with ${response.data.field_count} fields!`);
                            // Static data - no global refresh needed
                          } catch (error) {
                            if (error instanceof SyntaxError) {
                              toast.error('Invalid JSON file');
                            } else {
                              toast.error(error.response?.data?.detail || 'Failed to import form');
                            }
                            console.error('Import error:', error);
                          }
                        };
                        fileInput.click();
                      }}
                    >
                      <FileJson className="h-4 w-4 mr-2" />
                      Import Native JSON
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {formTemplates
                      .filter(template => showArchivedTemplates ? true : template.is_active !== false)
                      .map((template) => (
                      <Card key={template.id} className={`p-4 ${template.is_active === false ? 'bg-gray-50 border-gray-300' : ''}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{template.name}</h3>
                              {template.is_system && (
                                <Badge className="bg-teal-100 text-teal-800 border-teal-300">
                                  System Form
                                </Badge>
                              )}
                              {template.is_active === false && (
                                <Badge className="bg-gray-100 text-gray-800 border-gray-300">
                                  Archived
                                </Badge>
                              )}
                            </div>
                            <p className="text-gray-600 text-sm">{template.description}</p>
                            <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                              <Badge variant="outline">{template.form_type}</Badge>
                              {template.billable && <Badge className="bg-green-100 text-green-800">Billable</Badge>}
                              <span>Created: {new Date(template.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {template.is_active !== false ? (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  data-testid={`edit-template-${template.id}`}
                                  onClick={() => {
                                    setFormTemplateData({
                                      name: template.name,
                                      description: template.description,
                                      form_type: template.form_type,
                                      form_fields: template.form_fields || [],
                                      billable: template.billable || false,
                                      requires_provider_signature: template.requires_provider_signature !== false,
                                      requires_supervisor_signature: template.requires_supervisor_signature || false,
                                      allow_client_signature: template.allow_client_signature || false,
                                      require_client_signature: template.require_client_signature || false,
                                      is_scored_assessment: template.is_scored_assessment || false,
                                      is_group_template: template.is_group_template || false,
                                      available_in_quick_create: template.available_in_quick_create !== false,
                                      allow_external_download: template.allow_external_download || false,
                                      download_requires_signature: template.download_requires_signature !== false,
                                      allow_print_for_signature: template.allow_print_for_signature || false,
                                      allow_send_to_client: template.allow_send_to_client || false,
                                      scoring_config: template.scoring_config || { max_score: 0, severity_ranges: [], clinical_alerts: [] },
                                      status_notifications: template.status_notifications || []
                                    });
                                    setActiveFormFields(template.form_fields || []);
                                    setEditingFormTemplate(template);
                                    // Clear cache to load fresh from template
                                    staticTextEditorCache.current.clear();
                                    setShowFormTemplateBuilder(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700"
                                  onClick={() => duplicateFormTemplate(template)}
                                >
                                  <Copy className="h-4 w-4 mr-1" />
                                  Duplicate
                                </Button>
                                {!template.is_system && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-orange-600 hover:text-orange-700"
                                  onClick={() => handleDeleteFormTemplate(template.id)}
                                >
                                  <Archive className="h-4 w-4 mr-1" />
                                  Archive
                                </Button>
                                )}
                              </>
                            ) : (
                              <div className="flex items-center space-x-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-green-600 hover:text-green-700"
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/form-templates/${template.id}/restore`, {}, {
                                        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
                                      });
                                      toast.success("Form template restored successfully");
                                      fetchFormTemplates(true); // Skip cache after restore
                                    } catch (error) {
                                      console.error('Error restoring template:', error);
                                      toast.error("Failed to restore form template");
                                    }
                                  }}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Restore
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={async () => {
                                    if (!window.confirm("Are you sure you want to PERMANENTLY delete this template? This cannot be undone.")) {
                                      return;
                                    }
                                    try {
                                      await axios.delete(`${API}/form-templates/${template.id}/permanent`, {
                                        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
                                      });
                                      toast.success("Form template permanently deleted");
                                      fetchFormTemplates(true); // Skip cache after delete
                                    } catch (error) {
                                      console.error('Error permanently deleting template:', error);
                                      toast.error(error.response?.data?.detail || "Failed to permanently delete form template");
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete Forever
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                    
                    {formTemplates.filter(t => showArchivedTemplates ? true : t.is_active !== false).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        {showArchivedTemplates 
                          ? "No form templates found."
                          : "No active form templates. Enable 'Show Archived' to see archived templates."}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Clients Tab */}
          <TabsContent value="clients">
            <Card>
              <CardHeader>
                <div className="flex flex-row items-center justify-between">
                  <CardTitle>Client Management</CardTitle>
                  <div className="flex items-center space-x-2">
                    {hasPermission('create_clients') && (
                      <>
                        <Button 
                          onClick={() => intakeWizard.open(null) /* Phase 3e.3a — V2 provider */}
                          variant="default"
                          data-testid="add-client-button"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Client
                        </Button>
                        
                        {/* Client form dialog moved to global dialogs section */}
                      </>
                )}

                {/* Edit Client Dialog - MOVED TO GLOBAL DIALOGS SECTION */}
              </div>
            </div>

                
                {/* Client Filters — standardized on MultiSelectFilter (May 2026).
                    All four filters share trigger height, popover layout,
                    search, persistent label cache, and inline-X clear.
                    Status + Labels also support Include/Exclude. */}
                <div className="flex items-center gap-4 mt-4 flex-wrap">
                  {/* Status Filter */}
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">Status:</Label>
                    <div className="w-52">
                      <MultiSelectFilter
                        items={(CLIENT_STATUSES || []).map(s => ({ id: s.value, label: s.label }))}
                        selected={clientStatusFilter}
                        onChange={setClientStatusFilter}
                        mode={clientStatusFilterMode}
                        onModeChange={setClientStatusFilterMode}
                        entity="status"
                        emptyLabel="All Statuses"
                        triggerTestId="client-status-filter"
                        popoverWidth={260}
                      />
                    </div>
                  </div>
                  
                  {/* Provider Filter — standardized on MultiSelectFilter.
                      Role-aware visibility is preserved by adapting the
                      items[] list and sublabels:
                        - All roles see "My Clients" + their own name
                        - Admin/office/biller/billing-manager + supervisors
                          + all_clients also see "Unassigned"
                        - Supervisors see their supervisees (purple ⭐ sublabel)
                        - Admin/office/biller/billing-manager + all_clients
                          + can_qa_audit see "Other Providers"
                      Backend tokens unchanged: 'mine', 'unassigned',
                      'provider_<id>'. */}
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">Provider:</Label>
                    <div className="w-52">
                      {(() => {
                        const allProviders = (users || []).filter(u =>
                          u.role === 'provider' || u.role === 'supervisor' || u.is_provider === true
                        );
                        const supervisees = allProviders.filter(u =>
                          Array.isArray(u.supervisor_ids) && u.supervisor_ids.includes(user?.id)
                        ).sort((a, b) =>
                          ((a.last_name || '').localeCompare(b.last_name || '')) ||
                          ((a.first_name || '').localeCompare(b.first_name || ''))
                        );
                        const otherProviders = allProviders.filter(u =>
                          !u.supervisor_ids?.includes(user?.id) && u.id !== user?.id
                        ).sort((a, b) =>
                          ((a.first_name || '').localeCompare(b.first_name || '')) ||
                          ((a.last_name || '').localeCompare(b.last_name || ''))
                        );
                        const canSeeAllClients = isSuperAdmin(user)
                          || userRoleName === 'office' || userRoleName === 'office staff'
                          || user?.is_biller || user?.is_billing_manager
                          || userRoleName === 'admin'
                          || user?.client_visibility === 'all_clients'
                          || user?.can_qa_audit;
                        const canSeeUnassigned = canSeeAllClients || userRoleName === 'supervisor';

                        const items = [];
                        items.push({ id: 'mine', label: 'My Clients', sublabel: 'me' });
                        if (canSeeUnassigned) {
                          items.push({ id: 'unassigned', label: 'Unassigned', sublabel: 'no provider assigned' });
                        }
                        for (const p of supervisees) {
                          items.push({
                            id: `provider_${p.id}`,
                            label: formatProviderName(p) || 'Unknown',
                            sublabel: 'supervisee',
                          });
                        }
                        if (canSeeAllClients) {
                          for (const p of otherProviders) {
                            items.push({
                              id: `provider_${p.id}`,
                              label: formatProviderName(p) || 'Unknown',
                              sublabel: 'provider',
                            });
                          }
                        }
                        return (
                          <MultiSelectFilter
                            items={items}
                            selected={clientProviderFilter}
                            onChange={setClientProviderFilter}
                            mode={clientProviderFilterMode}
                            onModeChange={setClientProviderFilterMode}
                            entity="provider"
                            emptyLabel="All Providers"
                            shortcuts={[
                              { label: 'My Clients', ids: ['mine'], testId: 'client-provider-filter-shortcut-mine' },
                              ...(supervisees.length > 0
                                ? [{ label: 'My Supervisees', ids: supervisees.map(p => `provider_${p.id}`), testId: 'client-provider-filter-shortcut-supervisees' }]
                                : []),
                            ]}
                            triggerTestId="client-provider-filter"
                            popoverWidth={320}
                          />
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* Label Filter — multi-select with Include/Exclude.
                      Color dots restored for visual parity with the
                      pre-standardization popover. */}
                  {clientLabels.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">Labels:</Label>
                      <div className="w-52">
                        <MultiSelectFilter
                          items={clientLabels.map(l => ({ id: l.id, label: l.name, color: l.color }))}
                          selected={selectedLabelFilter}
                          onChange={setSelectedLabelFilter}
                          mode={selectedLabelFilterMode}
                          onModeChange={setSelectedLabelFilterMode}
                          entity="label"
                          emptyLabel="All Labels"
                          triggerTestId="client-label-filter"
                          popoverWidth={260}
                        />
                      </div>
                    </div>
                  )}

                  {/* Insurance Filter — multi-select with Include/Exclude.
                      "Self Pay" is a synthetic option that matches
                      clients with no active insurance subdocument.
                      Selecting a parent carrier auto-includes all its
                      sub-plans at query time (Tufts → Tufts One Care,
                      Tufts Medicare, etc.).  Mirrors BillingTab. */}
                  {/* Insurance Filter — multi-select with Include/Exclude.
                      "Self Pay" is a synthetic option that matches
                      clients with no active insurance subdocument.
                      Selecting a parent carrier auto-includes all its
                      sub-plans at query time (Tufts → Tufts One Care,
                      Tufts Medicare, etc.).  Mirrors BillingTab. */}
                  {((insurances || []).filter(i => (i.status || 'active') !== 'archived').length > 0) && (
                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">Insurance:</Label>
                      <div className="w-56">
                        <MultiSelectFilter
                          items={[
                            { id: '__self_pay__', label: 'Self Pay (no insurance)', sublabel: 'synthetic' },
                            ...((insurances || [])
                              .filter(i => (i.status || 'active') !== 'archived')
                              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                              .map(i => ({
                                id: i.name,
                                label: i.name,
                                sublabel: i.payer_id || undefined,
                              }))
                            ),
                          ]}
                          selected={selectedInsuranceFilter}
                          onChange={setSelectedInsuranceFilter}
                          mode={selectedInsuranceFilterMode}
                          onModeChange={setSelectedInsuranceFilterMode}
                          entity="insurance"
                          emptyLabel="All Insurance"
                          triggerTestId="insurance-filter-trigger"
                          popoverWidth={300}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Search Box - Full Width */}
                <div className="mt-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <DebouncedInput
                      type="text"
                      placeholder="Search by name, email, phone, DOB, MRN, or insurance..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="w-full pl-10 pr-10"
                    />
                    {clientSearch && (
                      <button
                        onClick={() => setClientSearch('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* View Mode Toggle and Pagination Controls */}
                <div className="flex items-center justify-between mt-4">
                  {/* Left: View Toggle */}
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">View:</Label>
                    <div className="flex border rounded-md overflow-hidden">
                      <button
                        className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors ${
                          clientViewMode === 'card'
                            ? 'bg-teal-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        onClick={() => setClientViewMode('card')}
                      >
                        <LayoutGrid className="h-4 w-4" />
                        Cards
                      </button>
                      <button
                        className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors ${
                          clientViewMode === 'table'
                            ? 'bg-teal-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        onClick={() => setClientViewMode('table')}
                      >
                        <List className="h-4 w-4" />
                        List
                      </button>
                    </div>
                    
                    {/* Name Format Toggle */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs gap-1.5"
                      onClick={() => setClientNameFormat(prev => prev === 'first_last' ? 'last_first' : 'first_last')}
                      title={clientNameFormat === 'first_last' ? 'Switch to LAST, First' : 'Switch to First LAST'}
                      data-testid="name-format-toggle"
                    >
                      <ArrowLeftRight className="h-3.5 w-3.5" />
                      {clientNameFormat === 'first_last' ? 'First LAST' : 'LAST, First'}
                    </Button>
                  </div>
                  
                  {/* Middle: Showing count + Last Updated + Refresh */}
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                      {clientsTotalCount > 0 ? (
                        clientsPerPage === 'all' ? (
                          `${clientsTotalCount} client${clientsTotalCount !== 1 ? 's' : ''}`
                        ) : (
                          (() => {
                            const start = (clientsCurrentPage - 1) * clientsPerPage + 1;
                            const end = Math.min(clientsCurrentPage * clientsPerPage, clientsTotalCount);
                            return `${start}-${end} of ${clientsTotalCount} client${clientsTotalCount !== 1 ? 's' : ''}`;
                          })()
                        )
                      ) : clientsLoading ? 'Loading...' : 'No clients found'}
                    </span>
                    
                    {/* Last Updated + Refresh Button */}
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 border-l pl-3">
                      {clientsLastUpdated && (
                        <span title={clientsLastUpdated.toLocaleString()}>
                          Updated {formatRelativeTime(clientsLastUpdated)}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-gray-400 hover:text-teal-600"
                        onClick={() => {
                          // Reset to page 1 and force a full refresh
                          setClientsCurrentPage(1);
                          // Small delay to ensure state is updated
                          setTimeout(() => {
                            refreshClients({ refresh: true });
                          }, 50);
                        }}
                        disabled={clientsLoading}
                        title="Refresh client list"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${clientsLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Right: Pagination Controls */}
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Per page:</Label>
                    <Select 
                      value={String(clientsPerPage)} 
                      onValueChange={(value) => {
                        setClientsPerPage(value === 'all' ? 'all' : parseInt(value));
                        setClientsCurrentPage(1); // Reset to first page
                      }}
                    >
                      <SelectTrigger className="w-24 h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {clientsPerPage !== 'all' && clientsTotalPages > 0 && (
                      <ClientPaginationControls
                        currentPage={clientsCurrentPage}
                        totalPages={clientsTotalPages}
                        onPageChange={setClientsCurrentPage}
                        disabled={clientsLoading}
                      />
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Skeleton loading when fetching clients */}
                {clientsLoading && clients.length === 0 ? (
                  <ClientListSkeleton 
                    rowCount={10} 
                    viewMode={clientViewMode} 
                    showHeader={false}
                    showFilters={false}
                  />
                ) : (
                <div className={`space-y-4 ${clientsLoading ? 'opacity-60' : ''}`}>
                  {/* Loading indicator overlay for filter changes (when we have existing data) */}
                  {clientsLoading && clients.length > 0 && (
                    <div className="flex items-center justify-center py-2 text-sm text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600 mr-2"></div>
                      {clientSearch ? 'Searching...' : 'Updating...'}
                    </div>
                  )}
                  {(() => {
                    // With server-side pagination and search, clients are already filtered, sorted, and paginated
                    // No local filtering needed - just use the data directly
                    const displayClients = getVisibleClientsForManagement;
                    
                    // Server-side sorting, pagination, and search - just use displayClients directly
                    // (all filtering is now handled by the backend)
                    
                    // If table view, render table
                    if (clientViewMode === 'table') {
                      return (
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse text-sm">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                              <tr className="border-b border-gray-200">
                                {[
                                  { key: 'mrn', label: 'MRN', width: 'w-20' },
                                  { key: 'full_name', label: 'Name', width: '' },
                                  { key: 'date_of_birth', label: 'DOB', width: 'w-28' },
                                  { key: 'contact', label: 'Contact', width: 'w-20' },
                                  { key: 'labels', label: 'Labels', width: 'w-28' },
                                  { key: 'insurance', label: 'Ins', width: 'w-32' },
                                  { key: 'balance', label: 'Balance', width: 'w-24' },
                                  { key: 'providers', label: 'Provider', width: 'w-28' },
                                  { key: 'status', label: 'Status', width: 'w-24' },
                                  { key: 'actions', label: '', width: 'w-24' }
                                ].map(col => {
                                  // Sortable on the table view.  Backend support added
                                  // May 2026 for status / balance / insurance / labels /
                                  // providers — see server.py::get_clients sort dispatch.
                                  // "contact" and "actions" remain non-sortable (no useful
                                  // semantic ordering).
                                  const SORTABLE_KEYS = new Set([
                                    'mrn', 'full_name', 'date_of_birth',
                                    'status', 'balance', 'insurance', 'labels', 'providers',
                                  ]);
                                  const isSortable = SORTABLE_KEYS.has(col.key);
                                  return (
                                  <th 
                                    key={col.key}
                                    className={`px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider ${col.width} ${isSortable ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                                    data-testid={isSortable ? `client-sort-${col.key}` : undefined}
                                    onClick={() => {
                                      if (isSortable) {
                                        if (clientsSortColumn === col.key) {
                                          setClientsSortDirection(d => d === 'asc' ? 'desc' : 'asc');
                                        } else {
                                          setClientsSortColumn(col.key);
                                          setClientsSortDirection('asc');
                                        }
                                      }
                                    }}
                                  >
                                    <div className="flex items-center gap-1">
                                      {col.label}
                                      {isSortable && clientsSortColumn === col.key && (
                                        <span>{clientsSortDirection === 'asc' ? '↑' : '↓'}</span>
                                      )}
                                    </div>
                                  </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {displayClients.map((client) => (
                                <tr 
                                  key={client.id} 
                                  className="h-10 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors"
                                  onClick={() => handleClientClick(client.id)}
                                >
                                  {/* MRN */}
                                  <td className="px-2 text-gray-500 text-xs">
                                    {client.mrn ? client.mrn.replace('MRN-', '#') : '—'}
                                  </td>
                                  {/* Name + Eligibility */}
                                  <td className="px-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-gray-900 hover:text-blue-600">
                                        {formatClientName(client)}
                                      </span>
                                      <EligibilityIndicator client={client} size="sm" />
                                    </div>
                                  </td>
                                  {/* DOB + Age */}
                                  <td className="px-2 text-gray-600">
                                    {client.date_of_birth ? (
                                      <span title={formatDateMMDDYYYY(client.date_of_birth)}>
                                        {formatDateMMDDYYYY(client.date_of_birth)} <span className="text-gray-400">({calculateAge(client.date_of_birth)})</span>
                                      </span>
                                    ) : '—'}
                                  </td>
                                  {/* Contact Icons */}
                                  <td className="px-2" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center gap-1">
                                      {client.email && (
                                        <a href={`mailto:${client.email}`} title={client.email} onClick={(e) => e.stopPropagation()}>
                                          <Mail className="h-3.5 w-3.5 text-blue-500 hover:text-blue-700" />
                                        </a>
                                      )}
                                      {client.phone && (
                                        <a href={`tel:${client.phone}`} title={client.phone} onClick={(e) => e.stopPropagation()}>
                                          <Phone className="h-3.5 w-3.5 text-green-500 hover:text-green-700" />
                                        </a>
                                      )}
                                      {!client.email && !client.phone && <span className="text-gray-300">—</span>}
                                    </div>
                                  </td>
                                  {/* Labels */}
                                  <td className="px-2">
                                    <div className="flex flex-wrap gap-0.5">
                                      {(client.labels || []).slice(0, 2).map(labelId => {
                                        const label = clientLabelsByIdMap.get(labelId);
                                        if (!label) return null;
                                        return (
                                          <span
                                            key={labelId}
                                            className="inline-block w-2 h-2 rounded-full"
                                            style={{ backgroundColor: label.color }}
                                            title={label.name}
                                          />
                                        );
                                      })}
                                      {(client.labels || []).length > 2 && (
                                        <span className="text-xs text-gray-400">+{(client.labels || []).length - 2}</span>
                                      )}
                                    </div>
                                  </td>
                                  {/* Insurance - Abbreviated */}
                                  <td className="px-2 text-xs">
                                    {(() => {
                                      const activeIns = (client.insurances || []).filter(ins => ins.status === 'active');
                                      if (activeIns.length > 0) {
                                        const primaryIns = activeIns[0];
                                        // Look up insurance company to get custom abbreviation
                                        // (Map keyed by lowercase name → constant-time hit
                                        //  for both exact and case-insensitive matches.)
                                        const insuranceCompany = primaryIns.company
                                          ? insurancesByNameLowerMap.get(primaryIns.company.toLowerCase())
                                          : null;
                                        // Use custom abbreviation if set, otherwise fallback to common abbreviations
                                        let abbrevName = insuranceCompany?.abbreviation;
                                        if (!abbrevName) {
                                          abbrevName = (primaryIns.company || '')
                                            .replace(/Blue Cross Blue Shield/gi, 'BCBS')
                                            .replace(/United Healthcare/gi, 'UHC')
                                            .replace(/Anthem/gi, 'ANT')
                                            .replace(/Aetna/gi, 'AET')
                                            .replace(/Cigna/gi, 'CIG')
                                            .replace(/Humana/gi, 'HUM')
                                            .replace(/Kaiser/gi, 'KP')
                                            .replace(/Medicare/gi, 'MCARE')
                                            .replace(/Medicaid/gi, 'MCAID')
                                            .replace(/MassHealth/gi, 'MH')
                                            .replace(/ of .*/gi, '')
                                            .substring(0, 12);
                                        }
                                        const { icon: statusIcon, color: statusColor } = getEligibilityDisplay(client.primary_eligibility_status || primaryIns.eligibility_status);
                                        return (
                                          <span
                                            className={statusColor}
                                            title={`${primaryIns.company} - ${primaryIns.plan || 'N/A'}\n\n${eligibilityTooltip(client)}`}
                                          >
                                            {statusIcon} {abbrevName}{activeIns.length > 1 ? ` +${activeIns.length - 1}` : ''}
                                          </span>
                                        );
                                      }
                                      return <span className="text-gray-400">Self</span>;
                                    })()}
                                  </td>
                                  {/* Balance */}
                                  <td className="px-2 text-xs">
                                    {(() => {
                                      const balance = client.balance || 0;
                                      if (balance === 0) {
                                        return <span className="text-gray-400">$0.00</span>;
                                      } else if (balance > 0) {
                                        return <span className="text-red-600 font-medium">${balance.toFixed(2)}</span>;
                                      } else {
                                        return <span className="text-green-600">-${Math.abs(balance).toFixed(2)}</span>;
                                      }
                                    })()}
                                  </td>
                                  {/* Provider - Abbreviated */}
                                  <td className="px-2 text-xs text-gray-600">
                                    {(() => {
                                      if (client.assigned_providers?.length > 0) {
                                        const firstProvider = usersByIdMap.get(client.assigned_providers[0]);
                                        const name = firstProvider ? 
                                          (firstProvider.last_name || firstProvider.full_name?.split(' ').pop() || '?') : '?';
                                        const allNames = client.assigned_providers.map(pid => {
                                          const p = usersByIdMap.get(pid);
                                          return p?.full_name || 'Unknown';
                                        }).join('\n');
                                        return (
                                          <span title={allNames}>
                                            {name}{client.assigned_providers.length > 1 ? ` +${client.assigned_providers.length - 1}` : ''}
                                          </span>
                                        );
                                      }
                                      return <span className="text-gray-300">—</span>;
                                    })()}
                                  </td>
                                  {/* Status Badge */}
                                  <td className="px-2">
                                    <Badge variant="outline" className={`text-xs px-1.5 py-0 ${
                                      client.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
                                      client.status === 'pending_intake' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                      client.status === 'waitlist' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                      client.status === 'inactive' ? 'bg-gray-50 text-gray-600 border-gray-200' :
                                      'bg-red-50 text-red-700 border-red-200'
                                    }`}>
                                      {(client.status || 'unknown').replace('_', ' ')}
                                    </Badge>
                                  </td>
                                  {/* Actions - Match card view icons */}
                                  <td className="px-2" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="p-1 h-7 w-7"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleClientClick(client.id, { initialTab: 'notes' });
                                        }}
                                        title="View client documents"
                                      >
                                        <Folder className="h-4 w-4" />
                                      </Button>
                                      {(user?.is_biller || user?.is_billing_manager || isAdmin() || isSuperAdmin(user)) && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="p-1 h-7 w-7"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleClientClick(client.id, { initialTab: 'billing' });
                                          }}
                                          title="Open client billing"
                                          data-testid={`client-row-billing-${client.id}`}
                                        >
                                          <Receipt className="h-4 w-4" />
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="p-1 h-7 w-7"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Phase 3e.2 — V2 provider, no nav.
                                          intakeWizard.open(client.id);
                                        }}
                                        title="Edit client details"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                    
                    // Card view (existing code)
                    // Group clients by status
                    const groupedClients = displayClients.reduce((acc, client) => {
                      const status = client.status || 'unknown';
                      if (!acc[status]) {
                        acc[status] = [];
                      }
                      acc[status].push(client);
                      return acc;
                    }, {});
                    
                    // Define status display order and labels
                    const statusOrder = ['pending_intake', 'corrections_requested', 'active', 'waitlist', 'inactive', 'discharged', 'archived'];
                    const statusLabels = {
                      pending_intake: 'Pending Waitlist Approval',
                      corrections_requested: 'Corrections Requested',
                      active: 'Active',
                      waitlist: 'Waitlist',
                      inactive: 'Inactive',
                      discharged: 'Discharged',
                      archived: 'Archived'
                    };
                    
                    // Render grouped clients
                    const renderedGroups = statusOrder.map(status => {
                      const clientsInStatus = groupedClients[status] || [];
                      if (clientsInStatus.length === 0) return null;
                      
                      const isCollapsed = collapsedClientStatusSections[status] || false;
                      
                      return (
                        <div key={status} className="mb-6">
                          {/* Status Header - Clickable to expand/collapse */}
                          <div 
                            className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-gray-200 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition-colors"
                            onClick={() => setCollapsedClientStatusSections(prev => ({
                              ...prev,
                              [status]: !prev[status]
                            }))}
                            data-testid={`client-status-section-${status}`}
                          >
                            {isCollapsed ? (
                              <ChevronRight className="h-5 w-5 text-gray-500" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-500" />
                            )}
                            <h3 className="text-lg font-semibold text-gray-700">
                              {statusLabels[status]} Clients
                            </h3>
                            <span className="text-sm text-gray-500">({clientsInStatus.length})</span>
                          </div>
                          
                          {/* Clients in this status - Only show if not collapsed */}
                          {!isCollapsed && (
                          <div className="space-y-3">
                            {clientsInStatus.map((client) => (
                    <Card key={client.id} className="p-4" data-testid={`client-card-${client.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div>
                            {client.mrn && (
                              <div className="text-xs text-gray-500 mb-1">
                                MRN: #{client.mrn.replace('MRN-', '')}
                              </div>
                            )}
                            <div className="flex items-center space-x-2">
                              <h3 
                                className="font-semibold text-lg text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                onClick={() => handleClientClick(client.id)}
                                title="Open client file"
                              >
                                {formatClientName(client)}
                              </h3>
                              {client.date_of_birth && (
                                <span className="text-sm text-gray-600">
                                  {formatDateMMDDYYYY(client.date_of_birth) && (
                                    <>• DOB: {formatDateMMDDYYYY(client.date_of_birth)} {calculateAge(client.date_of_birth) !== null && `(${calculateAge(client.date_of_birth)})`}</>
                                  )}
                                </span>
                              )}
                            </div>
                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                              {client.email && (
                                <div className="flex items-center">
                                  <Mail className="h-4 w-4 mr-1" />
                                  <a href={`mailto:${client.email}`} className="hover:text-teal-600">
                                    {client.email}
                                  </a>
                                  {/* Email Status Indicator with Tooltip */}
                                  {client.email_status === 'invalid' && (
                                    <TooltipProvider>
                                      <UITooltip>
                                        <TooltipTrigger asChild>
                                          <span className="ml-1 cursor-help text-red-600 text-sm">⚠️</span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-xs bg-red-50 text-red-800 border border-red-200">
                                          <div className="text-xs">
                                            <p className="font-semibold mb-1">Email Bounced</p>
                                            <p>Delivery failed{client.email_last_bounce_date ? ` on ${new Date(client.email_last_bounce_date).toLocaleDateString()}` : ''}.</p>
                                            <p className="mt-1 text-red-600">Automated emails are suppressed. Please verify contact info.</p>
                                          </div>
                                        </TooltipContent>
                                      </UITooltip>
                                    </TooltipProvider>
                                  )}
                                  {client.email_status === 'unverified' && !client.suppress_automated_emails && (
                                    <TooltipProvider>
                                      <UITooltip>
                                        <TooltipTrigger asChild>
                                          <span className="ml-1 cursor-help text-yellow-600 text-sm">❓</span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-xs bg-yellow-50 text-yellow-800 border border-yellow-200">
                                          <div className="text-xs">
                                            <p className="font-semibold mb-1">Email Unverified</p>
                                            <p>This email address has not been verified yet.</p>
                                          </div>
                                        </TooltipContent>
                                      </UITooltip>
                                    </TooltipProvider>
                                  )}
                                  {client.suppress_automated_emails && client.email_status !== 'invalid' && (
                                    <TooltipProvider>
                                      <UITooltip>
                                        <TooltipTrigger asChild>
                                          <span className="ml-1 cursor-help text-gray-500 text-sm">🚫</span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-xs bg-gray-100 text-gray-700 border border-gray-300">
                                          <div className="text-xs">
                                            <p className="font-semibold mb-1">Emails Suppressed</p>
                                            <p>Automated emails will not be sent to this address.</p>
                                          </div>
                                        </TooltipContent>
                                      </UITooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              )}
                              {client.phone && (
                                <div className="flex items-center">
                                  <Phone className="h-4 w-4 mr-1" />
                                  <a href={`tel:${client.phone}`} className="hover:text-teal-600">
                                    {client.phone}
                                  </a>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 mt-2">
                              {/* Provider Count - Clickable to show providers */}
                              {client.assigned_providers?.length > 0 && (
                                <div className="relative">
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="h-6 text-xs px-2"
                                    onClick={() => {
                                      if (showProvidersDropdown === client.id) {
                                        setShowProvidersDropdown(null);
                                        setSelectedClient(null);
                                      } else {
                                        setSelectedClient(client);
                                        setShowProvidersDropdown(client.id);
                                      }
                                    }}
                                    title="Click to see assigned providers"
                                  >
                                    {client.assigned_providers.length} provider{client.assigned_providers.length !== 1 ? 's' : ''}
                                  </Button>
                                  
                                  {/* Providers Dropdown */}
                                  {showProvidersDropdown === client.id && (
                                    <div className="absolute top-8 left-0 z-50 bg-white border border-gray-200 rounded-md shadow-lg p-3 min-w-[280px]">
                                      <div className="text-xs font-medium text-gray-700 mb-2">Assigned Providers:</div>
                                      <div className="space-y-1">
                                        {client.assigned_providers.map((providerId, index) => {
                                          // Try to find provider in users array (O(1) Map lookup)
                                          const provider = usersByIdMap.get(providerId);
                                          // Also check client's provider_details if available (from backend enrichment)
                                          const providerFromDetails = client.provider_details?.find(p => p.id === providerId);
                                          const providerInfo = provider || providerFromDetails;
                                          
                                          return (
                                            <div key={index} className="text-xs text-gray-600 flex items-center">
                                              <User className="h-3 w-3 mr-1 flex-shrink-0" />
                                              {providerInfo ? (
                                                <>
                                                  {providerInfo.full_name}
                                                  {providerInfo.credentials && <span className="text-gray-500">, {providerInfo.credentials}</span>}
                                                </>
                                              ) : (
                                                <span className="text-gray-400 italic">Loading provider...</span>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        className="mt-2 text-xs h-6"
                                        onClick={() => {
                                          setShowProvidersDropdown(null);
                                          setSelectedClient(null);
                                        }}
                                      >
                                        Close
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Alerts - Show unread count with dropdown preview */}
                              {unreadAlertCounts && unreadAlertCounts[client.id] > 0 && (
                                <div className="relative">
                                  <Badge 
                                    variant="outline" 
                                    className="h-6 text-xs px-2 bg-amber-50 border-amber-300 text-amber-800 cursor-pointer hover:bg-amber-100 transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (showAlertsDropdown === client.id) {
                                        setShowAlertsDropdown(null);
                                      } else {
                                        setShowAlertsDropdown(client.id);
                                        // Fetch alerts for this client
                                        fetchClientAlerts(client.id);
                                      }
                                    }}
                                    title="Click to preview alerts"
                                  >
                                    <Bell className="h-3 w-3 mr-1" />
                                    {unreadAlertCounts && unreadAlertCounts[client.id]} unread alert{unreadAlertCounts && unreadAlertCounts[client.id] !== 1 ? 's' : ''}
                                  </Badge>
                                  
                                  {/* Alerts Dropdown Preview */}
                                  {showAlertsDropdown === client.id && (
                                    <div className="absolute top-8 left-0 z-50 bg-white border border-gray-200 rounded-md shadow-lg p-3 min-w-96 max-w-lg">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="text-sm font-semibold text-gray-700 flex items-center">
                                          <Bell className="h-4 w-4 mr-2" />
                                          Recent Alerts
                                        </div>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShowAlertsDropdown(null);
                                          }}
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                      
                                      {loadingAlerts ? (
                                        <div className="text-center py-4 text-gray-500 text-sm">Loading...</div>
                                      ) : clientAlerts.length === 0 ? (
                                        <div className="text-center py-4 text-gray-500 text-sm">No alerts</div>
                                      ) : (
                                        <div className="space-y-2 max-h-80 overflow-y-auto">
                                          {clientAlerts.slice(0, 3).map(alert => (
                                            <div key={alert.id} className={`p-2 rounded text-xs border ${
                                              alert.is_banner 
                                                ? 'bg-amber-50 border-amber-200' 
                                                : !alert.read_by?.[user.id] 
                                                ? 'bg-blue-50 border-blue-200' 
                                                : 'bg-gray-50 border-gray-200'
                                            }`}>
                                              <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                  {alert.is_banner && (
                                                    <div className="flex items-center space-x-1 mb-1">
                                                      <Shield className="h-3 w-3 text-amber-700" />
                                                      <span className="text-[10px] font-semibold text-amber-800 uppercase">Banner</span>
                                                    </div>
                                                  )}
                                                  <p className="font-medium text-gray-800 line-clamp-2">
                                                    {alert.content}
                                                  </p>
                                                  <div className="flex items-center space-x-2 mt-1 text-[10px] text-gray-600">
                                                    <span>{alert.created_by_name}</span>
                                                    <span>•</span>
                                                    <span>{new Date(alert.created_at).toLocaleDateString()}</span>
                                                    {!alert.read_by?.[user.id] && (
                                                      <>
                                                        <span>•</span>
                                                        <span className="text-blue-600 font-semibold">UNREAD</span>
                                                      </>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                          
                                          {clientAlerts.length > 3 && (
                                            <p className="text-[10px] text-gray-500 text-center mt-2">
                                              +{clientAlerts.length - 3} more alert{clientAlerts.length - 3 !== 1 ? 's' : ''}
                                            </p>
                                          )}
                                        </div>
                                      )}
                                      
                                      <Button
                                        size="sm"
                                        className="w-full mt-3 h-8 text-xs"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowAlertsDropdown(null);
                                          handleClientClick(client.id, { initialTab: 'alerts' });
                                        }}
                                      >
                                        View All Alerts
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Client Labels */}
                              {(client.labels || []).length > 0 && (
                                <div className="flex flex-wrap items-center gap-1">
                                  {(client.labels || []).slice(0, 3).map(labelId => {
                                    const label = clientLabelsByIdMap.get(labelId);
                                    if (!label) return null;
                                    return (
                                      <span
                                        key={labelId}
                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                                        style={{ 
                                          backgroundColor: label.color + '20',
                                          color: label.color,
                                          border: `1px solid ${label.color}40`
                                        }}
                                      >
                                        {label.name}
                                      </span>
                                    );
                                  })}
                                  {(client.labels || []).length > 3 && (
                                    <span className="text-xs text-gray-500">
                                      +{(client.labels || []).length - 3} more
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Insurance Information - Always show, display "Self Pay" if no insurance */}
                              <div className="text-xs text-gray-600 space-y-1">
                                <div className="flex items-center space-x-2">
                                  {/* Manage Insurance Icon */}
                                  {(user?.can_edit_client_status_insurance || hasPermission('edit_client_insurance') || isSuperAdmin(user)) && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenInsuranceManagement(client);
                                      }}
                                      title="Manage insurance"
                                      className="text-blue-600 hover:text-blue-700 h-5 w-5 p-0"
                                    >
                                      <CreditCard className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <div className="flex-1">
                                {(() => {
                                  // Use the new insurances array instead of old fields
                                  const activeInsurances = (client.insurances || [])
                                    .filter(ins => ins.status === 'active')
                                    .sort((a, b) => a.priority - b.priority);
                                  
                                  if (activeInsurances.length === 0) {
                                    return (
                                      <div className="flex items-center space-x-2">
                                        <span className="font-medium text-gray-700">Self Pay</span>
                                      </div>
                                    );
                                  }
                                  
                                  const priorityLabels = {1: 'Primary', 2: 'Secondary', 3: 'Tertiary', 4: 'Quaternary'};
                                  const primaryInsurance = activeInsurances[0];
                                  const additionalCount = activeInsurances.length - 1;
                                  
                                  return (
                                    <div className="flex items-center space-x-2">
                                      {/* Primary Insurance */}
                                      <span className="font-medium">{priorityLabels[primaryInsurance.priority] || 'Primary'}:</span>
                                      <span>{primaryInsurance.company}{primaryInsurance.plan ? ` - ${primaryInsurance.plan}` : ''}</span>
                                      {(() => {
                                        // Use client-level status (authoritative) — falls back to insurance-level
                                        const cardStatus = client.primary_eligibility_status || primaryInsurance.eligibility_status;
                                        if (!cardStatus || cardStatus === 'not_checked') return null;
                                        const { icon, color } = getEligibilityDisplay(cardStatus);
                                        return (
                                          <span className={`text-sm font-medium ${color}`}
                                            title={eligibilityTooltip(client)}>
                                            {icon}
                                          </span>
                                        );
                                      })()}
                                      
                                      {/* Additional Insurances Indicator with Tooltip */}
                                      {additionalCount > 0 && (
                                        <TooltipProvider delayDuration={100}>
                                          <UITooltip>
                                            <TooltipTrigger asChild>
                                              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded cursor-help hover:bg-gray-200">
                                                +{additionalCount} more
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="bg-white border shadow-lg p-2 max-w-xs">
                                              <div className="text-xs space-y-1">
                                                <div className="font-semibold text-gray-700 mb-1">All Insurances:</div>
                                                {activeInsurances.map((ins) => (
                                                  <div key={ins.id} className="flex items-center space-x-1">
                                                    <span className="font-medium text-gray-600">{priorityLabels[ins.priority] || `#${ins.priority}`}:</span>
                                                    <span className="text-gray-800">{ins.company}{ins.plan ? ` - ${ins.plan}` : ''}</span>
                                                    {ins.eligibility_status && (
                                                      <span className={`${
                                                        ins.eligibility_status === 'active' || ins.eligibility_status === 'verified' ? 'text-green-600' :
                                                        ins.eligibility_status === 'pending' ? 'text-yellow-600' : 'text-red-600'
                                                      }`}>
                                                        {getEligibilityDisplay(ins.eligibility_status).icon}
                                                      </span>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </TooltipContent>
                                          </UITooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  );
                                })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {/* Client Status Controls */}
                          
                          {/* Complete Intake Button - for pending intake clients */}
                          {client.status === 'pending_intake' && (isAdmin() || userRoleName === 'provider') && (
                            <Button 
                              variant="default"
                              size="sm"
                              onClick={() => intakeWizard.open(client.id) /* Phase 3e.2 — V2 provider */}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              title="Complete client intake"
                            >
                              Complete Intake
                            </Button>
                          )}
                          
                          {/* Billing Review for Discharged Clients */}
                          {client.status === 'discharged' && (
                            <>
                              {client.billing_review_status === 'pending' && (user?.is_biller || user?.is_billing_manager || isAdmin()) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setBillingReviewClientId(client.id);
                                    setShowBillingReviewDialog(true);
                                  }}
                                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                  title="Review billing before archive"
                                >
                                  <DollarSign className="h-4 w-4 mr-1" />
                                  Review Billing
                                </Button>
                              )}
                              {client.billing_review_status === 'pending' && !(user?.is_biller || user?.is_billing_manager || isAdmin()) && (
                                <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                                  Pending Billing Review
                                </Badge>
                              )}
                              {client.ready_for_archive && (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  Ready for Archive
                                </Badge>
                              )}
                            </>
                          )}
                          
                          <Select 
                            value={client.status || 'active'}
                            onValueChange={(value) => handleUpdateClientStatus(client.id, value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CLIENT_STATUSES.filter(s => {
                                // Hide archived unless user can archive
                                if (s.value === 'archived' && !user.can_archive_clients && !isSuperAdmin(user)) return false;
                                // Hide inactive unless user is supervisor, super admin, or waitlist manager
                                if (s.value === 'inactive' && !user?.is_supervisor && !user?.is_waitlist_manager && !isSuperAdmin(user)) return false;
                                return true;
                              }).map(status => (
                                <SelectItem key={status.value} value={status.value}>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleClientClick(client.id, { initialTab: 'notes' })}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setClientContextMenu({
                                show: true,
                                x: e.clientX,
                                y: e.clientY,
                                clientId: client.id
                              });
                            }}
                            data-testid={`view-client-${client.id}`}
                            title="View client documents"
                          >
                            <Folder className="h-4 w-4" />
                          </Button>
                          {(user?.is_biller || user?.is_billing_manager || isAdmin() || isSuperAdmin(user)) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleClientClick(client.id, { initialTab: 'billing' })}
                              data-testid={`client-card-billing-${client.id}`}
                              title="Open client billing"
                            >
                              <Receipt className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => intakeWizard.open(client.id) /* Phase 3e.2 — V2 provider */}
                            data-testid={`edit-client-${client.id}`}
                            title="Edit client details"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          
                          {client.status === 'archived' && (user.can_archive_clients || isAdmin()) && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleRestoreClient(client.id)}
                              className="text-green-600 hover:text-green-700"
                              title="Restore from archive"
                            >
                              ↺
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                          </div>
                          )}
                        </div>
                      );
                    });
                    
                    // Check if we have any results after filtering
                    const hasResults = renderedGroups.some(group => group !== null);
                    
                    if (!hasResults && (clientSearch || displayClients.length === 0)) {
                      return (
                        <div className="text-center py-12">
                          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No clients found</h3>
                          <p className="text-sm text-gray-500 mb-4">
                            {clientSearch 
                              ? `No clients match your search "${clientSearch}"`
                              : "No clients match the selected filters"}
                          </p>
                          {clientSearch && (
                            <p className="text-xs text-gray-400">
                              Try searching by name, email, phone, DOB, language, insurance company, or "self pay"
                            </p>
                          )}
                        </div>
                      );
                    }
                    
                    return renderedGroups;
                  })()}
                  
                  {(clients || []).length === 0 && !clientsLoading && (
                    <div className="text-center py-8 text-gray-500">
                      No clients found. Add your first client to get started.
                    </div>
                  )}
                  
                  {/* Bottom Pagination - visible when scrolling down */}
                  {clientsPerPage !== 'all' && clientsTotalPages > 0 && (clients || []).length > 0 && (
                    <div className="flex items-center justify-between pt-4 mt-4 border-t">
                      <span className="text-sm text-gray-600">
                        Showing {((clientsCurrentPage - 1) * parseInt(clientsPerPage)) + 1} - {Math.min(clientsCurrentPage * parseInt(clientsPerPage), clientsTotalCount)} of {clientsTotalCount} clients
                      </span>
                      <div className="flex items-center gap-4">
                        <Select value={clientsPerPage} onValueChange={(val) => { setClientsPerPage(val); setClientsCurrentPage(1); }}>
                          <SelectTrigger className="w-20 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                            <SelectItem value="200">200</SelectItem>
                          </SelectContent>
                        </Select>
                        <ClientPaginationControls
                          currentPage={clientsCurrentPage}
                          totalPages={clientsTotalPages}
                          onPageChange={setClientsCurrentPage}
                          disabled={clientsLoading}
                        />
                      </div>
                    </div>
                  )}
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <DashboardCalendarBridgeProvider
              value={{
                // ─── Stage 6: Bridge slimmed ───
                // AppointmentsTab now reads `user`, `userRoleName`,
                // `providers`, `clients`, `locations`,
                // `allClientsForLookup`, `clientGroups`, `cptCodes`,
                // `cptBundles`, `currentTime`, `notes` directly from
                // their canonical contexts/hooks. We only ferry
                // Dashboard-owned actions and helpers that need
                // Dashboard data.
                //
                // Quick-Schedule slot click stays Dashboard-owned
                // (uses Dashboard's `setShowAppointmentForm` etc.).
                handleCalendarSlotClick,
                // Action handlers
                openAppointment, openMeetPopup, openNote,
                refreshAppointments, refreshStats,
                // Display helpers (Dashboard-scope only).
                getCalendarDays,
                getDOSEligibilityDisplay, getFirmEventsForDate,
                eligibilityTooltip,
                // Dashboard-local UI state for fullscreen toggle
                isFullscreenCalendar, setIsFullscreenCalendar,
              }}
            >
              <AppointmentsTab />
            </DashboardCalendarBridgeProvider>
          </TabsContent>

          {/* Fullscreen Calendar Dialog */}
          <Dialog open={isFullscreenCalendar} onOpenChange={setIsFullscreenCalendar}>
            <DialogContent className="max-w-full w-full h-screen max-h-screen overflow-hidden rounded-none p-0 [&>button]:hidden">
              <div className="h-full flex flex-col">
                {/* Fullscreen Calendar Header */}
                <div className="flex items-center justify-between p-4 border-b bg-white">
                  <div className="flex items-center space-x-4">
                    <h2 className="text-xl font-bold">📅 Calendar</h2>
                    <Select 
                      value={selectedProviders[0] === user?.id ? 'mine' : (selectedProviders[0] || 'all')} 
                      onValueChange={(value) => {
                        if (value === 'mine') {
                          setSelectedProviders([user?.id]);
                        } else if (value === 'all') {
                          setSelectedProviders(['all']);
                        } else {
                          setSelectedProviders([value]);
                        }
                      }}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select calendar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mine">My Calendar</SelectItem>
                        <SelectItem value="all">All Providers</SelectItem>
                        {providers.map(provider => (
                          <SelectItem key={provider.id} value={provider.id}>
                            {provider.full_name}{provider.credentials ? `, ${provider.credentials}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <div className="flex items-center space-x-1">
                      <Button variant={calendarView === 'week' ? 'default' : 'outline'} size="sm" onClick={() => setCalendarView('week')}>Week</Button>
                      <Button variant={calendarView === 'day' ? 'default' : 'outline'} size="sm" onClick={() => setCalendarView('day')}>Day</Button>
                      <Button variant={calendarView === 'month' ? 'default' : 'outline'} size="sm" onClick={() => setCalendarView('month')}>Month</Button>
                      <Button variant={calendarView === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setCalendarView('list')}>List</Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm" onClick={() => setCalendarDate(prev => {
                      const newDate = new Date(prev);
                      if (calendarView === 'week') newDate.setDate(newDate.getDate() - 7);
                      else if (calendarView === 'day') newDate.setDate(newDate.getDate() - 1);
                      else if (calendarView === 'month') newDate.setMonth(newDate.getMonth() - 1);
                      return newDate;
                    })}>
                      ← Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setCalendarDate(new Date())}>Today</Button>
                    {/* Jump-to-date input — user request Feb 2026:
                        "can we have a jump to date box where we can
                        type the date we want the calendar to jump to".
                        Uses a native date picker so keyboard typing
                        AND clicking the picker icon both work without
                        adding another popover dependency.  Reflects
                        whatever date the calendar is currently anchored
                        to so the user knows where they are. */}
                    <input
                      type="date"
                      min="1970-01-01"
                      max="2100-12-31"
                      className="h-9 px-2 text-sm border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      value={(() => {
                        const d = calendarDate || new Date();
                        const y = d.getFullYear();
                        const m = String(d.getMonth() + 1).padStart(2, '0');
                        const dd = String(d.getDate()).padStart(2, '0');
                        return `${y}-${m}-${dd}`;
                      })()}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) return;
                        const [y, m, d] = v.split('-').map(Number);
                        if (!y || !m || !d) return;
                        if (y < 1970 || y > 2100) return;
                        setCalendarDate(new Date(y, m - 1, d));
                      }}
                      title="Jump to date"
                      data-testid="calendar-jump-to-date"
                    />
                    <Button variant="outline" size="sm" onClick={() => setCalendarDate(prev => {
                      const newDate = new Date(prev);
                      if (calendarView === 'week') newDate.setDate(newDate.getDate() + 7);
                      else if (calendarView === 'day') newDate.setDate(newDate.getDate() + 1);
                      else if (calendarView === 'month') newDate.setMonth(newDate.getMonth() + 1);
                      return newDate;
                    })}>
                      Next →
                    </Button>
                    <Button variant="default" size="sm" onClick={() => {
                      setEditingAppointment(null);
                      setShowAppointmentForm(true);
                    }}>
                      + Schedule Appointment
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setIsFullscreenCalendar(false)} title="Exit fullscreen">
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
                
                {/* Calendar Content - Full Height */}
                <div className="flex-1 overflow-auto p-4 bg-gray-50 relative">
                  {/* Drag Indicator Banner for Fullscreen */}
                  {draggedAppointment && (
                    <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3 animate-pulse">
                      <span className="text-xl">📅</span>
                      <div>
                        <p className="font-semibold">Dragging appointment</p>
                        <p className="text-sm text-blue-100">
                          {allClientsForLookup.find(c => c.id === draggedAppointment.client_id)?.full_name || draggedAppointment.service_type}
                        </p>
                      </div>
                      <span className="text-sm bg-blue-500 px-2 py-1 rounded">Drop to reschedule</span>
                    </div>
                  )}
                  
                  {calendarView === 'week' && (
                    <div className="bg-white rounded-lg shadow flex flex-col" style={{ height: 'calc(100vh - 180px)' }}>
                      {/* Week header - always visible above scroll area, no sticky needed */}
                      <div className="grid grid-cols-8 border-b bg-white z-10 flex-shrink-0">
                        <div className="p-2 text-center font-semibold border-r text-sm">Time</div>
                        {Array.from({ length: 7 }, (_, i) => {
                          const d = new Date(calendarDate);
                          d.setDate(d.getDate() - d.getDay() + i);
                          const isToday = d.toDateString() === new Date().toDateString();
                          return (
                            <div key={i} className={`p-2 text-center font-semibold border-r text-sm ${isToday ? 'bg-blue-100 text-blue-800' : ''}`}>
                              <div>{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i]}</div>
                              <div className="text-lg">{d.getDate()}</div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Time slots - scrolls independently, header stays put */}
                      <div className="overflow-y-auto flex-1 min-h-0">
                        {Array.from({ length: 48 }, (_, slotIdx) => {
                          const hour = Math.floor(slotIdx / 2) + (calendarInterval === 15 ? 6 : 6);
                          const minute = (slotIdx % 2) * 30;
                          const timeStr = `${hour % 12 || 12}:${minute.toString().padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
                          
                          return (
                            <div key={slotIdx} className="grid grid-cols-8 border-b min-h-[40px]">
                              <div className="p-1 text-xs text-gray-500 border-r text-right pr-2">{minute === 0 ? timeStr : ''}</div>
                              {Array.from({ length: 7 }, (_, dayIdx) => {
                                const dayDate = new Date(calendarDate);
                                dayDate.setDate(dayDate.getDate() - dayDate.getDay() + dayIdx);
                                dayDate.setHours(hour, minute, 0, 0);
                                
                                // Note: this secondary week-view lives OUTSIDE
                                // the main IIFE that builds slotApptMap /
                                // dayApptMap / clientByIdMap, so we can't use
                                // them here.  Keeping the original filter for
                                // correctness; the dominant calendar render
                                // (and the 81%-of-CPU hot path identified by
                                // the Chrome trace) is the primary week-view
                                // inside the IIFE above — that's the one the
                                // Map-bucketing fix targets.
                                const dayAppointments = appointments.filter(apt => {
                                  const aptDate = new Date(apt.start_time);
                                  return aptDate.toDateString() === dayDate.toDateString() &&
                                         aptDate.getHours() === hour &&
                                         Math.floor(aptDate.getMinutes() / 30) === Math.floor(minute / 30) &&
                                         (selectedProviders.includes('all') || selectedProviders.includes(apt.provider_id));
                                });
                                
                                return (
                                  <div 
                                    key={dayIdx} 
                                    className={`border-r p-0.5 relative min-h-[40px] hover:bg-gray-50 ${dragOverSlot?.date === dayDate.toISOString() ? 'bg-blue-200 ring-2 ring-blue-400' : ''}`}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      setDragOverSlot({ date: dayDate.toISOString(), hour, minute });
                                    }}
                                    onDragLeave={() => setDragOverSlot(null)}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      if (draggedAppointment) {
                                        handleAppointmentDrop(dayDate, draggedAppointment);
                                      }
                                      setDragOverSlot(null);
                                      setDraggedAppointment(null);
                                    }}
                                  >
                                    {dayAppointments.map(apt => {
                                      // Original .find pattern preserved here — Map
                                      // optimization can't reach into this secondary
                                      // view's scope (the IIFE that builds the Maps
                                      // is the primary week-view, above).
                                      const lookupClient = allClientsForLookup.find(c => c.id === apt.client_id);
                                      const clientDisplayName = lookupClient?.full_name || apt.client_name || apt.service_type;
                                      const serviceColor = getServiceTypeCategoryColor(apt, cptCodes);
                                      const billingBorder = getBillingStatusBorderColor(apt, notes);
                                      const isTelehealth = isTelehealthAppointment(apt);
                                      const isDraggable = (userRoleName === 'provider' || isAdmin()) && canResizeAppointment(apt);
                                      
                                      return (
                                        <div
                                          key={apt.id}
                                          className={`group relative text-xs p-1 pl-3 rounded hover:shadow-md mb-0.5 ${isDraggable ? 'cursor-grab active:cursor-grabbing hover:ring-2 hover:ring-blue-300' : 'cursor-pointer'} ${draggedAppointment?.id === apt.id ? 'ring-2 ring-blue-500 shadow-lg scale-105' : ''}`}
                                          style={{
                                            backgroundColor: draggedAppointment?.id === apt.id ? 'rgba(59, 130, 246, 0.3)' : `${serviceColor}20`,
                                            borderLeft: `3px solid ${billingBorder}`,
                                            color: isTelehealth ? '#4F46E5' : serviceColor
                                          }}
                                          draggable={isDraggable}
                                          onDragStart={(e) => handleDragStart(e, apt)}
                                          onDragEnd={handleAppointmentDragEnd}
                                          onClick={() => openAppointment(apt)}
                                          title={isDraggable ? 'Drag to reschedule' : ''}
                                        >
                                          {/* Drag handle */}
                                          {isDraggable && (
                                            <div className="absolute left-0.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <GripVertical className="h-3 w-3 text-gray-400" />
                                            </div>
                                          )}
                                          <div className="font-medium truncate">{clientDisplayName}</div>
                                          {isTelehealth && <Video className="h-3 w-3 inline" />}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {calendarView !== 'week' && (
                    <div className="text-center py-10 text-gray-500">
                      <p>Switch to Week view for full calendar display in fullscreen mode.</p>
                      <p className="text-sm mt-2">Other views coming soon!</p>
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Notes Tab - with To-Do at top */}
          <TabsContent value="notes">
            <div className="space-y-6">
              {/* Unified My Tasks Section - Wrapped with TasksSectionWrapper for crash isolation */}
              <TasksSectionWrapper>
              {(() => {
                const unifiedTasks = buildUnifiedTaskList();
                
                // Apply filters
                let filteredTasks = unifiedTasks.filter(task => {
                  // Type filter - handle "other" category specially
                  const taskCategory = task.category || 'other';
                  if (taskTypeFilter !== 'all') {
                    if (taskTypeFilter === 'other') {
                      if (['clinical', 'administrative', 'compliance', 'supervision'].includes(taskCategory)) return false;
                    } else if (taskCategory !== taskTypeFilter) {
                      return false;
                    }
                  }
                  if (taskUrgencyFilter !== 'all' && task.urgency !== taskUrgencyFilter) return false;
                  if (taskSourceFilter !== 'all' && task.source !== taskSourceFilter) return false;
                  if (taskOwnershipFilter !== 'all') {
                    if (taskOwnershipFilter === 'assigned_to_me' && task.ownership !== 'assigned_to_me') return false;
                    if (taskOwnershipFilter === 'i_assigned' && task.ownership !== 'i_assigned') return false;
                  }
                  return true;
                });
                
                // Sort tasks
                filteredTasks.sort((a, b) => {
                  if (taskSortBy === 'newest') {
                    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
                    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
                    return dateB - dateA; // Most recent first
                  } else if (taskSortBy === 'oldest') {
                    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
                    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
                    return dateA - dateB; // Oldest first
                  } else if (taskSortBy === 'priority') {
                    // Support all priority values: urgent/high (highest), normal/medium (middle), low (lowest)
                    const priorityOrder = { urgent: 0, high: 0, normal: 1, medium: 1, low: 2 };
                    return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
                  } else if (taskSortBy === 'date') {
                    const dateA = a.dueDate ? new Date(a.dueDate) : new Date('2099-12-31');
                    const dateB = b.dueDate ? new Date(b.dueDate) : new Date('2099-12-31');
                    return dateA - dateB;
                  } else if (taskSortBy === 'provider') {
                    return (a.providerName || '').localeCompare(b.providerName || '');
                  } else if (taskSortBy === 'type') {
                    return (a.type || '').localeCompare(b.type || '');
                  }
                  return 0;
                });
                
                // Pagination calculation
                const totalTaskPages = tasksPerPage === 'all' ? 1 : Math.ceil(filteredTasks.length / tasksPerPage);
                const paginatedTasks = tasksPerPage === 'all' 
                  ? filteredTasks 
                  : filteredTasks.slice((tasksCurrentPage - 1) * tasksPerPage, tasksCurrentPage * tasksPerPage);
                
                // Count for filter badges
                const counts = {
                  all: unifiedTasks.length,
                  clinical: unifiedTasks.filter(t => (t.category || 'other') === 'clinical').length,
                  administrative: unifiedTasks.filter(t => (t.category || 'other') === 'administrative').length,
                  compliance: unifiedTasks.filter(t => (t.category || 'other') === 'compliance').length,
                  supervision: unifiedTasks.filter(t => (t.category || 'other') === 'supervision').length,
                  other: unifiedTasks.filter(t => {
                    const cat = t.category || 'other';
                    return cat === 'other' || !['clinical', 'administrative', 'compliance', 'supervision'].includes(cat);
                  }).length,
                  overdue: unifiedTasks.filter(t => t.urgency === 'overdue').length,
                  manual: unifiedTasks.filter(t => t.source === 'manual').length,
                  automated: unifiedTasks.filter(t => t.source === 'automated').length,
                  assigned_to_me: unifiedTasks.filter(t => t.ownership === 'assigned_to_me').length,
                  i_assigned: unifiedTasks.filter(t => t.ownership === 'i_assigned').length,
                  my_work: unifiedTasks.filter(t => !t.isSuperviseeWork).length,
                  supervisee_work: unifiedTasks.filter(t => t.isSuperviseeWork).length,
                };
                
                return (
                  <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
                    <CardHeader>
                      <div className="flex flex-col gap-4">
                        {/* Header */}
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-3">
                            <CardTitle className="text-teal-900 text-xl">📋 My Tasks</CardTitle>
                            <Badge className="bg-teal-600 text-white text-lg px-3">{filteredTasks.length}</Badge>
                            {counts.overdue > 0 && (
                              <Badge className="bg-red-600 text-white animate-pulse">⚠️ {counts.overdue} Overdue</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* View Mode Toggle */}
                            <div className="flex rounded-md border overflow-hidden" data-testid="task-view-toggle">
                              <button
                                onClick={() => setTaskViewMode('card')}
                                className={`px-2.5 py-1.5 text-xs flex items-center gap-1 transition-colors ${
                                  taskViewMode === 'card'
                                    ? 'bg-teal-600 text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                                data-testid="task-view-cards"
                              >
                                <LayoutGrid className="h-3.5 w-3.5" />
                                Cards
                              </button>
                              <button
                                onClick={() => setTaskViewMode('list')}
                                className={`px-2.5 py-1.5 text-xs flex items-center gap-1 transition-colors ${
                                  taskViewMode === 'list'
                                    ? 'bg-teal-600 text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                                data-testid="task-view-list"
                              >
                                <List className="h-3.5 w-3.5" />
                                List
                              </button>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={refreshTasks}
                              disabled={tasksRefreshing}
                              title="Refresh tasks"
                              data-testid="refresh-tasks-btn"
                            >
                              <RefreshCw className={`h-4 w-4 ${tasksRefreshing ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button size="sm" onClick={() => setShowCreateTask(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="create-task-btn">
                              + Create Task
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setShowCompletedTasks(!showCompletedTasks)}
                              className={showCompletedTasks ? 'bg-gray-200' : ''} data-testid="toggle-completed-tasks-btn">
                              {showCompletedTasks ? '✓ Hide Completed' : 'Show Completed'}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Filter controls */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-white rounded-lg border border-teal-200">
                          <div>
                            <Label className="text-xs text-gray-600">Type</Label>
                            <Select value={taskTypeFilter} onValueChange={setTaskTypeFilter}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Types ({counts.all})</SelectItem>
                                <SelectItem value="clinical">🏥 Clinical ({counts.clinical})</SelectItem>
                                <SelectItem value="administrative">📋 Administrative ({counts.administrative})</SelectItem>
                                <SelectItem value="compliance">✓ Compliance ({counts.compliance})</SelectItem>
                                <SelectItem value="supervision">👥 Supervision ({counts.supervision})</SelectItem>
                                <SelectItem value="other">📌 Other ({counts.other})</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600">Urgency</Label>
                            <Select value={taskUrgencyFilter} onValueChange={setTaskUrgencyFilter}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="overdue">Overdue ({counts.overdue})</SelectItem>
                                <SelectItem value="due_today">🟠 Due Today</SelectItem>
                                <SelectItem value="due_this_week">🟡 Due This Week</SelectItem>
                                <SelectItem value="upcoming">🟢 Upcoming</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600">Source</Label>
                            <Select value={taskSourceFilter} onValueChange={setTaskSourceFilter}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Sources</SelectItem>
                                <SelectItem value="manual">📝 Manual ({counts.manual})</SelectItem>
                                <SelectItem value="automated">⚡ Automated ({counts.automated})</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600">Sort By</Label>
                            <Select value={taskSortBy} onValueChange={setTaskSortBy}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="newest">Most Recent</SelectItem>
                                <SelectItem value="oldest">Oldest</SelectItem>
                                <SelectItem value="priority">Priority</SelectItem>
                                <SelectItem value="date">Due Date</SelectItem>
                                <SelectItem value="provider">Provider</SelectItem>
                                <SelectItem value="type">Type</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Quick filter toggles */}
                        <div className="flex flex-wrap gap-3 items-center">
                          <div className="flex items-center gap-1 text-sm">
                            <span className="text-gray-600 mr-1">Show:</span>
                            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                              <button onClick={() => setTaskOwnershipFilter('all')}
                                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${taskOwnershipFilter === 'all' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`}>
                                All
                              </button>
                              <button onClick={() => setTaskOwnershipFilter('assigned_to_me')}
                                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${taskOwnershipFilter === 'assigned_to_me' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`}>
                                Assigned to Me ({counts.assigned_to_me})
                              </button>
                              <button onClick={() => setTaskOwnershipFilter('i_assigned')}
                                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${taskOwnershipFilter === 'i_assigned' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`}>
                                I Assigned ({counts.i_assigned})
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      {filteredTasks.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-lg border border-teal-200">
                          <div className="text-4xl mb-3">📭</div>
                          <p className="text-teal-700 font-medium">No matching tasks</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {taskTypeFilter !== 'all' && taskUrgencyFilter !== 'all' 
                              ? `No ${taskTypeFilter} tasks are ${taskUrgencyFilter.replace(/_/g, ' ')}.`
                              : 'No tasks match your current filter combination.'}
                          </p>
                          <div className="flex flex-wrap gap-2 justify-center mt-3">
                            {taskTypeFilter !== 'all' && (
                              <Badge className="bg-gray-200 text-gray-700">Type: {taskTypeFilter}</Badge>
                            )}
                            {taskUrgencyFilter !== 'all' && (
                              <Badge className="bg-gray-200 text-gray-700">Urgency: {taskUrgencyFilter.replace(/_/g, ' ')}</Badge>
                            )}
                            {taskSourceFilter !== 'all' && (
                              <Badge className="bg-gray-200 text-gray-700">Source: {taskSourceFilter}</Badge>
                            )}
                            {taskOwnershipFilter !== 'all' && (
                              <Badge className="bg-gray-200 text-gray-700">Show: {taskOwnershipFilter.replace(/_/g, ' ')}</Badge>
                            )}
                          </div>
                          {(taskTypeFilter !== 'all' || taskUrgencyFilter !== 'all' || taskSourceFilter !== 'all' || taskOwnershipFilter !== 'all') && (
                            <Button variant="link" className="mt-3 text-teal-600"
                              onClick={() => { setTaskTypeFilter('all'); setTaskUrgencyFilter('all'); setTaskSourceFilter('all'); setTaskOwnershipFilter('all'); }}>
                              Clear all filters
                            </Button>
                          )}
                        </div>
                      ) : taskViewMode === 'list' ? (
                        /* List View */
                        <div className="space-y-3">
                          <div className="border rounded-lg overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Type</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Priority</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Due Date</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Assigned</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {paginatedTasks.map((task) => {
                                const handleTaskClick = async () => {
                                  if (task.type === 'manual_task') {
                                    setSelectedTask(task.originalData);
                                    setEmailVerifyMode('verify');
                                    setEmailVerifyNewEmail('');
                                    setShowTaskDetail(true);
                                  } else if (task.type === 'clinical_note') {
                                    openNote(task.originalData);
                                  } else if (task.type === 'change_request') {
                                    setSelectedChangeRequest(task.originalData);
                                    setShowChangeRequestDetail(true);
                                  } else if (task.type === 'treatment_plan_signature' || task.type === 'treatment_plan_corrections' || task.type === 'treatment_plan_review') {
                                    try {
                                      const response = await axios.get(`${API}/clients/${task.clientId}`);
                                      setSelectedClient(response.data);
                                      setClientDetailTab('treatment-plans');
                                      setShowClientDetail(true);
                                    } catch (error) {
                                      toast.error('Failed to load client');
                                    }
                                  } else if (task.type === 'medication_referral') {
                                    setSelectedReferralForDetails(task.originalData);
                                  } else if (task.type === 'incomplete_documentation' || task.type === 'scheduled_review') {
                                    try {
                                      const response = await axios.get(`${API}/clients/${task.clientId}`);
                                      setSelectedClient(response.data);
                                      setClientDetailTab('clinical-docs');
                                      setShowClientDetail(true);
                                    } catch (error) {
                                      toast.error('Failed to load client');
                                    }
                                  } else if (task.type === 'mdt_review') {
                                    try {
                                      const response = await axios.get(`${API}/clients/${task.clientId}`);
                                      setSelectedClient(response.data);
                                      setShowClientDetail(true);
                                    } catch (error) {
                                      toast.error('Failed to load client');
                                    }
                                  } else if (task.type === 'discharge_required') {
                                    try {
                                      // Load client
                                      const response = await axios.get(`${API}/clients/${task.clientId}`);
                                      setSelectedClient(response.data);
                                      
                                      // Find discharge template
                                      const dischargeTemplate = formTemplates.find(t => t.is_discharge_form && t.is_active);
                                      
                                      if (dischargeTemplate) {
                                        // Pre-select client and provider
                                        setSelectedNoteClients([task.clientId]);
                                        const enrollmentProviderId = task.originalData?.enrollment_provider_id || task.originalData?.metadata?.provider_id || user?.id;
                                        setSelectedNoteProvider(enrollmentProviderId);
                                        setSelectedTemplate(dischargeTemplate);
                                        
                                        // Store enrollment info for the discharge form to use
                                        // This allows the form to pre-select the service enrollment
                                        setDischargeTaskInfo({
                                          enrollment_id: task.originalData?.enrollment_id,
                                          group_id: task.originalData?.group_id,
                                          group_name: task.originalData?.group_name,
                                          service_type: task.originalData?.service_type,
                                          service_type_name: task.originalData?.service_type_name,
                                          exit_reason: task.originalData?.metadata?.exit_reason,
                                          exit_notes: task.originalData?.metadata?.exit_notes,
                                          task_id: task.id
                                        });
                                        
                                        setShowAddNote(true);
                                        toast.info(`Creating discharge note for ${task.originalData?.group_name || 'group'} enrollment`);
                                      } else {
                                        // Fallback: open client notes tab if no discharge template found
                                        setClientDetailTab('notes');
                                        setShowClientDetail(true);
                                        toast.warning('No discharge template found. Please create a discharge note manually.');
                                      }
                                    } catch (error) {
                                      console.error('Error handling discharge task:', error);
                                      toast.error('Failed to load client');
                                    }
                                  }
                                };
                                return (
                                  <tr 
                                    key={task.id} 
                                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                                      task.urgency === 'overdue' ? 'bg-red-50' :
                                      task.urgency === 'due_today' ? 'bg-orange-50' : 'bg-white'
                                    }`}
                                    onClick={handleTaskClick}
                                    data-testid={`task-row-${task.id}`}
                                  >
                                    <td className={`px-4 py-3 border-l-2 ${
                                      (task.priority === 'urgent' || task.priority === 'high') ? 'border-l-red-300' :
                                      (task.priority === 'normal' || task.priority === 'medium') ? 'border-l-teal-400' :
                                      task.priority === 'low' ? 'border-l-gray-300' : 'border-l-teal-400'
                                    }`}>
                                      <div className="flex flex-col">
                                        <span className="font-medium text-gray-900 truncate max-w-xs">{task.title}</span>
                                        {task.description && (
                                          <span className="text-xs text-gray-500 truncate max-w-xs">{task.description}</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 hidden sm:table-cell">
                                      <Badge className={`${task.badgeColor} text-white text-xs`}>{task.badgeText}</Badge>
                                    </td>
                                    <td className="px-4 py-3 hidden md:table-cell">
                                      <Badge className={`text-xs ${
                                        task.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                                        task.priority === 'normal' ? 'bg-blue-100 text-blue-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {task.priority || 'normal'}
                                      </Badge>
                                    </td>
                                    <td className="px-4 py-3 text-sm hidden lg:table-cell">
                                      {task.dueDate ? (
                                        <span className={task.urgency === 'overdue' ? 'text-red-600 font-medium' : 'text-gray-600'}>
                                          {formatDateStringForDisplay(task.dueDate)}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm hidden lg:table-cell">
                                      {task.ownership === 'i_assigned' ? (
                                        <span className="text-indigo-600">→ {task.assignedToName}</span>
                                      ) : task.assignedByName ? (
                                        <span className="text-gray-600">From: {task.assignedByName}</span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        {task.urgency === 'overdue' && <Badge className="bg-red-600 text-white text-xs">OVERDUE</Badge>}
                                        {task.urgency === 'due_today' && <Badge className="bg-orange-500 text-white text-xs">TODAY</Badge>}
                                        {task.status && task.status !== 'pending' && task.status !== 'draft' && (
                                          <Badge className={`text-xs ${
                                            task.status === 'completed' ? 'bg-green-100 text-green-700' : 
                                            task.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' : 
                                            'bg-gray-100 text-gray-700'
                                          }`}>
                                            {task.status.replace(/_/g, ' ')}
                                          </Badge>
                                        )}
                                        {/* Inline complete button for pending/in-progress tasks */}
                                        {task.source === 'manual' && task.status !== 'completed' && task.status !== 'dismissed' && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              // Feb 2026 — open completion dialog
                                              // for note capture instead of firing
                                              // immediately, so the assigner sees
                                              // the outcome.
                                              setCompletionDialogTask({
                                                id: task.originalId,
                                                title: task.title,
                                                assigned_by_name: task.assigned_by_name,
                                              });
                                              setCompletionDialogNote('');
                                            }}
                                            className="p-1 rounded hover:bg-green-100 text-gray-400 hover:text-green-600 transition-colors"
                                            title="Mark complete"
                                            data-testid={`task-complete-${task.id}`}
                                          >
                                            <CheckCircle className="h-4 w-4" />
                                          </button>
                                        )}
                                        <ChevronRight className="h-4 w-4 text-gray-400" />
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                          {/* Pagination Controls for List View */}
                          <div className="flex items-center justify-between text-sm pt-2">
                            <span className="text-gray-500">
                              {tasksPerPage === 'all' 
                                ? `${filteredTasks.length} task${filteredTasks.length !== 1 ? 's' : ''}`
                                : `${Math.min((tasksCurrentPage - 1) * tasksPerPage + 1, filteredTasks.length)}-${Math.min(tasksCurrentPage * tasksPerPage, filteredTasks.length)} of ${filteredTasks.length}`
                              }
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">Per page:</span>
                              <Select 
                                value={String(tasksPerPage)} 
                                onValueChange={(v) => {
                                  setTasksPerPage(v === 'all' ? 'all' : parseInt(v));
                                  setTasksCurrentPage(1);
                                }}
                              >
                                <SelectTrigger className="w-20 h-7 text-xs" data-testid="tasks-per-page-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="10">10</SelectItem>
                                  <SelectItem value="20">20</SelectItem>
                                  <SelectItem value="50">50</SelectItem>
                                  <SelectItem value="100">100</SelectItem>
                                  <SelectItem value="all">All</SelectItem>
                                </SelectContent>
                              </Select>
                              
                              {tasksPerPage !== 'all' && totalTaskPages > 1 && (
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => setTasksCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={tasksCurrentPage === 1}
                                    data-testid="tasks-prev-page"
                                  >
                                    ←
                                  </Button>
                                  <span className="text-xs px-1">{tasksCurrentPage}/{totalTaskPages}</span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => setTasksCurrentPage(p => Math.min(totalTaskPages, p + 1))}
                                    disabled={tasksCurrentPage >= totalTaskPages}
                                    data-testid="tasks-next-page"
                                  >
                                    →
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Card View */
                        <>
                        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                          {paginatedTasks.map((task) => {
                            // Priority border colors (subtle): urgent/high = soft red, normal/medium = soft blue, low = soft gray
                            const priorityBorderClass = 
                              (task.priority === 'urgent' || task.priority === 'high') ? 'border-l-red-300' :
                              (task.priority === 'normal' || task.priority === 'medium') ? 'border-l-teal-400' :
                              task.priority === 'low' ? 'border-l-gray-300' : 'border-l-teal-400';
                            
                            return (
                            <div key={task.id}
                              className={`p-4 bg-white border-2 border-l-2 rounded-lg hover:shadow-md cursor-pointer transition-all ${priorityBorderClass} ${
                                task.urgency === 'overdue' ? 'border-t-red-300 border-r-red-300 border-b-red-300 bg-red-50' :
                                task.urgency === 'due_today' ? 'border-t-orange-300 border-r-orange-300 border-b-orange-300 bg-orange-50' :
                                task.isSuperviseeWork ? 'border-t-purple-300 border-r-purple-300 border-b-purple-300' :
                                task.ownership === 'i_assigned' ? 'border-t-indigo-300 border-r-indigo-300 border-b-indigo-300' : 'border-t-teal-200 border-r-teal-200 border-b-teal-200'
                              }`}
                              data-testid={`task-card-${task.id}`}
                              onClick={async () => {
                                // Handle click based on task type
                                if (task.type === 'manual_task') {
                                  setSelectedTask(task.originalData);
                                  setEmailVerifyMode('verify');
                                  setEmailVerifyNewEmail('');
                                  setShowTaskDetail(true);
                                } else if (task.type === 'clinical_note') {
                                  openNote(task.originalData);
                                } else if (task.type === 'change_request') {
                                  setSelectedChangeRequest(task.originalData);
                                  setShowChangeRequestDetail(true);
                                } else if (task.type === 'treatment_plan_signature' || task.type === 'treatment_plan_corrections' || task.type === 'treatment_plan_review') {
                                  try {
                                    const response = await axios.get(`${API}/clients/${task.clientId}`);
                                    setSelectedClient(response.data);
                                    setClientDetailTab('treatment-plans');
                                    setShowClientDetail(true);
                                  } catch (error) {
                                    toast.error('Failed to load client');
                                  }
                                } else if (task.type === 'medication_referral') {
                                  // Open the medication referral details dialog
                                  setSelectedReferralForDetails(task.originalData);
                                } else if (task.type === 'incomplete_documentation' || task.type === 'scheduled_review') {
                                  try {
                                    const response = await axios.get(`${API}/clients/${task.clientId}`);
                                    setSelectedClient(response.data);
                                    setClientDetailTab('clinical-docs');
                                    setShowClientDetail(true);
                                  } catch (error) {
                                    toast.error('Failed to load client');
                                  }
                                } else if (task.type === 'mdt_review') {
                                  try {
                                    const response = await axios.get(`${API}/clients/${task.clientId}`);
                                    setSelectedClient(response.data);
                                    setShowClientDetail(true);
                                  } catch (error) {
                                    toast.error('Failed to load client');
                                  }
                                } else if (task.type === 'discharge_required') {
                                  // Open discharge form with template pre-selected
                                  try {
                                    const response = await axios.get(`${API}/clients/${task.clientId}`);
                                    setSelectedClient(response.data);
                                    
                                    // Find discharge template
                                    const dischargeTemplate = formTemplates.find(t => t.is_discharge_form && t.is_active);
                                    
                                    if (dischargeTemplate) {
                                      setSelectedNoteClients([task.clientId]);
                                      const enrollmentProviderId = task.originalData?.enrollment_provider_id || task.originalData?.metadata?.provider_id || user?.id;
                                      setSelectedNoteProvider(enrollmentProviderId);
                                      setSelectedTemplate(dischargeTemplate);
                                      
                                      setDischargeTaskInfo({
                                        enrollment_id: task.originalData?.enrollment_id,
                                        group_id: task.originalData?.group_id,
                                        group_name: task.originalData?.group_name,
                                        service_type: task.originalData?.service_type,
                                        service_type_name: task.originalData?.service_type_name,
                                        exit_reason: task.originalData?.metadata?.exit_reason,
                                        exit_notes: task.originalData?.metadata?.exit_notes,
                                        task_id: task.id
                                      });
                                      
                                      setShowAddNote(true);
                                      toast.info(`Creating discharge note for ${task.originalData?.group_name || 'group'} enrollment`);
                                    } else {
                                      setClientDetailTab('notes');
                                      setShowClientDetail(true);
                                      toast.warning('No discharge template found. Please create a discharge note manually.');
                                    }
                                  } catch (error) {
                                    console.error('Error handling discharge task:', error);
                                    toast.error('Failed to load client');
                                  }
                                }
                              }}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <Badge className={`${task.badgeColor} text-white text-xs`}>{task.badgeText}</Badge>
                                    {task.urgency === 'overdue' && <Badge className="bg-red-600 text-white text-xs">OVERDUE</Badge>}
                                    {task.urgency === 'due_today' && <Badge className="bg-orange-500 text-white text-xs">DUE TODAY</Badge>}
                                    {task.isSuperviseeWork && <Badge className="bg-purple-500 text-white text-xs">👤 {task.providerName || 'Supervisee'}</Badge>}
                                    {task.ownership === 'i_assigned' && <Badge className="bg-indigo-100 text-indigo-700 text-xs">→ {task.assignedToName}</Badge>}
                                    {task.status && task.status !== 'pending' && task.status !== 'draft' && task.status !== 'needs_corrections' && task.status !== 'approved' && task.status !== 'denied' && (
                                      <Badge className={`text-xs ${task.status === 'completed' ? 'bg-green-100 text-green-700' : task.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
                                        {task.status.replace(/_/g, ' ')}
                                      </Badge>
                                    )}
                                  </div>
                                  <h4 className="font-semibold text-gray-900 truncate flex items-center gap-2">
                                    <span className="truncate">{task.title}</span>
                                    {task.rescheduleInLabel && task.clientPhone && (
                                      // Mar 2026 — for reschedule tasks, put the phone next
                                      // to the client name in the title row so the scheduler's
                                      // eye lands on the call-target first.
                                      <a
                                        href={`tel:${(task.clientPhone || '').replace(/[^\d+]/g, '')}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-sm text-teal-700 hover:underline font-normal whitespace-nowrap"
                                        data-testid={`task-client-phone-${task.id}`}
                                        title="Call client to reschedule"
                                      >
                                        📞 {task.clientPhone}
                                      </a>
                                    )}
                                  </h4>
                                  {task.description && (
                                    <p className="text-sm text-gray-600 mt-1 truncate">{task.description}</p>
                                  )}
                                  {/* Corrections Reason - prominently displayed for notes sent back */}
                                  {task.correctionsReason && (
                                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                                      <span className="font-semibold text-yellow-800">⚠️ Corrections Needed: </span>
                                      <span className="text-yellow-700">{task.correctionsReason}</span>
                                      {(task.correctionsRequestedByName || task.correctionsRequestedBy) && (
                                        <span className="text-yellow-600 text-xs ml-2">
                                          — {task.correctionsRequestedByName || users?.find(u => u.id === task.correctionsRequestedBy)?.full_name || 'Biller'}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {task.rescheduleInLabel ? (
                                    // Mar 2026 — reschedule tasks get a single
                                    // focused metadata row.  Drop the Created /
                                    // Due dates; the scheduler reads "Original
                                    // Session Date → Reschedule in X → Target
                                    // week of Y" as a single planning line.
                                    // Once auto-completed by the booking flow,
                                    // the row flips to green with the rebook
                                    // date so the auditor sees the outcome.
                                    (task.rebookedAppointmentStartTime && task.status === 'completed') ? (
                                      <div
                                        className="mt-2 px-2 py-1.5 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-900"
                                        data-testid={`task-rebooked-${task.id}`}
                                      >
                                        <span className="font-semibold">✓ Rebooked →</span>{' '}
                                        {(() => {
                                          try {
                                            const d = new Date(task.rebookedAppointmentStartTime);
                                            return `${d.toLocaleDateString()} at ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
                                          } catch (_e) { return String(task.rebookedAppointmentStartTime).slice(0, 16); }
                                        })()}
                                        {task.sourceAppointmentDate && (
                                          <span className="text-emerald-700 ml-2">
                                            · was {formatDateStringForDisplay(task.sourceAppointmentDate)}, provider asked {task.rescheduleInLabel}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <div
                                        className="mt-2 px-2 py-1.5 rounded bg-indigo-50 border border-indigo-200 text-xs text-indigo-900"
                                        data-testid={`task-reschedule-in-${task.id}`}
                                      >
                                        {task.sourceAppointmentDate && (
                                          <>
                                            <span className="font-semibold">Original Session Date:</span>{' '}
                                            {formatDateStringForDisplay(task.sourceAppointmentDate)}
                                            <span className="mx-2 text-indigo-400">·</span>
                                          </>
                                        )}
                                        <span className="font-semibold">Provider wants to reschedule in:</span>{' '}
                                        {task.rescheduleInLabel}
                                        {task.targetAppointmentDate && (
                                          <>
                                            <span className="mx-2 text-indigo-400">·</span>
                                            <span className="font-semibold">Target:</span>{' '}
                                            week of {formatDateStringForDisplay(task.targetAppointmentDate)}
                                          </>
                                        )}
                                      </div>
                                    )
                                  ) : (
                                    <div className="flex items-center gap-4 text-xs text-gray-500 mt-2 flex-wrap">
                                      {task.appointmentDate && <span className="text-blue-600">🗓️ Appt: {formatDateStringForDisplay(task.appointmentDate)}</span>}
                                      {task.sourceAppointmentDate && (
                                        <span className="text-indigo-700">🗓️ Session: {formatDateStringForDisplay(task.sourceAppointmentDate)}</span>
                                      )}
                                      {task.clientPhone && (
                                        <a
                                          href={`tel:${(task.clientPhone || '').replace(/[^\d+]/g, '')}`}
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-teal-700 hover:underline font-medium"
                                          data-testid={`task-client-phone-${task.id}`}
                                          title="Call client"
                                        >
                                          📞 {task.clientPhone}
                                        </a>
                                      )}
                                      {task.createdAt && <span>📝 Created: {formatDateStringForDisplay(task.createdAt)}</span>}
                                      {task.dueDate && <span className={task.urgency === 'overdue' ? 'text-red-600 font-medium' : ''}>📅 Due: {formatDateStringForDisplay(task.dueDate)}</span>}
                                      {task.assignedByName && task.ownership === 'assigned_to_me' && <span>From: {task.assignedByName}</span>}
                                      {task.source === 'automated' && <span className="text-cyan-600">⚡ Auto</span>}
                                    </div>
                                  )}
                                  {/* Action button for tasks with specific actions */}
                                  {task.actionType && task.clientId && (
                                    <div className="mt-3 pt-2 border-t border-gray-100">
                                      <Button
                                        size="sm"
                                        className="bg-teal-600 hover:bg-teal-700 text-white"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const clientResponse = await axios.get(`${API}/clients/${task.clientId}`);
                                            setSelectedClient(clientResponse.data);
                                            // Always store client ID, not the full object
                                            setSelectedNoteClients([clientResponse.data.id]);
                                            
                                            if (task.actionType === 'create_assessment' && task.actionData?.template_id) {
                                              // Open add note dialog with pre-selected template (use existing formTemplates if available)
                                              let template = formTemplates?.find(t => t.id === task.actionData.template_id);
                                              if (!template) {
                                                const templates = await axios.get(`${API}/form-templates`);
                                                template = templates.data.find(t => t.id === task.actionData.template_id);
                                              }
                                              if (template) {
                                                setSelectedTemplate(template);
                                              }
                                              setShowAddNote(true);
                                              toast.info(`Creating ${task.actionData.template_name || 'assessment'} for ${clientResponse.data.full_name}`);
                                            } else if (task.actionType === 'reschedule_appointment') {
                                              // Mar 2026 — dispatch the same window event the
                                              // create_task widget uses.  The shared listener
                                              // (App.js L2931+) handles BOTH the edit-mode
                                              // (appointment exists) and create-mode (legacy
                                              // task with no appointment_id) paths AND ferries
                                              // the provider's reschedule context (notes,
                                              // target date, phone) into the dialog banner.
                                              window.dispatchEvent(new CustomEvent('rescheduleFromTask', {
                                                detail: {
                                                  task_id: task.originalId || task.id,
                                                  appointment_id: task.appointmentId
                                                    || task.actionData?.appointment_id
                                                    || null,
                                                },
                                              }));
                                            } else if (task.actionType === 'create_note' || task.actionType === 'open_note') {
                                              // Open add note dialog
                                              setShowAddNote(true);
                                            } else if (task.actionType === 'open_treatment_plan') {
                                              setClientDetailTab('treatment-plans');
                                              setShowClientDetail(true);
                                            } else {
                                              setShowClientDetail(true);
                                            }
                                          } catch (error) {
                                            toast.error('Failed to load client');
                                          }
                                        }}
                                      >
                                        {task.actionType === 'create_assessment' && '📝 Create Assessment'}
                                        {task.actionType === 'reschedule_appointment' && '📅 Reschedule'}
                                        {(task.actionType === 'create_note' || task.actionType === 'open_note') && '📝 Create Document'}
                                        {task.actionType === 'open_treatment_plan' && '📋 Open Treatment Plan'}
                                        {!['create_assessment', 'reschedule_appointment', 'create_note', 'open_note', 'open_treatment_plan'].includes(task.actionType) && '→ Take Action'}
                                      </Button>
                                    </div>
                                  )}
                                  {/* Smart action for tasks with client but no specific action - detect from title */}
                                  {!task.actionType && task.clientId && task.type === 'manual_task' && (
                                    <div className="mt-3 pt-2 border-t border-gray-100 flex gap-2">
                                      {/* If task mentions creating/completing a document, assessment, or form */}
                                      {(task.title?.toLowerCase().match(/create|complete|fill|submit|assessment|form|document|note/) || 
                                        task.description?.toLowerCase().match(/create|complete|fill|submit|assessment|form|document|note/)) ? (
                                        <Button
                                          size="sm"
                                          className="bg-teal-600 hover:bg-teal-700 text-white"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                              const clientResponse = await axios.get(`${API}/clients/${task.clientId}`);
                                              setSelectedClient(clientResponse.data);
                                              // Always store client ID, not the full object
                                              setSelectedNoteClients([clientResponse.data.id]);
                                              setShowAddNote(true);
                                              toast.info(`Creating document for ${clientResponse.data.full_name}`);
                                            } catch (error) {
                                              toast.error('Failed to load client');
                                            }
                                          }}
                                        >
                                          📝 Create Document
                                        </Button>
                                      ) : (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="text-teal-700 border-teal-300"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                              const clientResponse = await axios.get(`${API}/clients/${task.clientId}`);
                                              setSelectedClient(clientResponse.data);
                                              setShowClientDetail(true);
                                            } catch (error) {
                                              toast.error('Failed to load client');
                                            }
                                          }}
                                        >
                                          👤 Open Client Chart
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
                              </div>
                            </div>
                          );
                          })}
                        </div>
                        {/* Pagination Controls for Card View */}
                        <div className="flex items-center justify-between text-sm pt-3">
                          <span className="text-gray-500">
                            {tasksPerPage === 'all' 
                              ? `${filteredTasks.length} task${filteredTasks.length !== 1 ? 's' : ''}`
                              : `${Math.min((tasksCurrentPage - 1) * tasksPerPage + 1, filteredTasks.length)}-${Math.min(tasksCurrentPage * tasksPerPage, filteredTasks.length)} of ${filteredTasks.length}`
                            }
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Per page:</span>
                            <Select 
                              value={String(tasksPerPage)} 
                              onValueChange={(v) => {
                                setTasksPerPage(v === 'all' ? 'all' : parseInt(v));
                                setTasksCurrentPage(1);
                              }}
                            >
                              <SelectTrigger className="w-20 h-7 text-xs" data-testid="tasks-per-page-select-card">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="10">10</SelectItem>
                                <SelectItem value="20">20</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                                <SelectItem value="100">100</SelectItem>
                                <SelectItem value="all">All</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            {tasksPerPage !== 'all' && totalTaskPages > 1 && (
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => setTasksCurrentPage(p => Math.max(1, p - 1))}
                                  disabled={tasksCurrentPage === 1}
                                  data-testid="tasks-prev-page-card"
                                >
                                  ←
                                </Button>
                                <span className="text-xs px-1">{tasksCurrentPage}/{totalTaskPages}</span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => setTasksCurrentPage(p => Math.min(totalTaskPages, p + 1))}
                                  disabled={tasksCurrentPage >= totalTaskPages}
                                  data-testid="tasks-next-page-card"
                                >
                                  →
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}
              </TasksSectionWrapper>

              {/* Team Oversight Section (Supervisors Only) */}
              {user?.is_supervisor && teamDraftPlans.length > 0 && (
                <Card className="border-purple-200 bg-purple-50">
                  <CardHeader 
                    className="cursor-pointer hover:bg-purple-100 transition"
                    onClick={() => setIsTeamOversightExpanded(!isTeamOversightExpanded)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-purple-900">
                          {isTeamOversightExpanded ? '▼' : '▶'} Team Oversight
                        </CardTitle>
                        <Badge className="bg-purple-600 text-white">
                          {teamDraftPlans.reduce((sum, provider) => sum + provider.draft_plans.length, 0)}
                        </Badge>
                        {teamDraftPlans.some(provider => provider.draft_plans.some(plan => plan.days_in_draft >= 7)) && (
                          <Badge className="bg-red-600 text-white">⚠️ Urgent</Badge>
                        )}
                      </div>
                      <span className="text-xs text-purple-700">Supervisees' draft plans</span>
                    </div>
                  </CardHeader>
                  {isTeamOversightExpanded && (
                    <CardContent className="space-y-4">
                      {teamDraftPlans.map(provider => (
                        <div key={provider.provider_id} className="space-y-2">
                          <div className="flex items-center gap-2 pb-2 border-b border-purple-200">
                            <span className="font-semibold text-purple-900">{provider.provider_name}</span>
                            <Badge className="bg-purple-200 text-purple-900">
                              {provider.draft_plans.length} {provider.draft_plans.length === 1 ? 'plan' : 'plans'}
                            </Badge>
                          </div>
                          {provider.draft_plans.map(plan => (
                            <div
                              key={plan.id}
                              className="ml-4 p-3 border-l-4 border-yellow-500 bg-white rounded-r cursor-pointer hover:bg-yellow-50 transition"
                              onClick={async () => {
                                try {
                                  const response = await axios.get(`${API}/clients/${plan.client_id}`);
                                  setSelectedClient(response.data);
                                  setClientDetailTab('treatment-plans');
                                  setShowClientDetail(true);
                                  toast.info(`Opening ${provider.provider_name}'s draft plan`);
                                } catch (error) {
                                  console.error('Error opening draft plan:', error);
                                  toast.error('Failed to load client');
                                }
                              }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <FileText className="h-4 w-4 text-yellow-600" />
                                    {plan.days_in_draft >= 7 ? (
                                      <Badge className="bg-red-600 text-white text-xs">⚠️ {plan.days_in_draft} days</Badge>
                                    ) : plan.days_in_draft >= 3 ? (
                                      <Badge className="bg-orange-600 text-white text-xs">{plan.days_in_draft} days</Badge>
                                    ) : (
                                      <Badge className="bg-yellow-600 text-white text-xs">{plan.days_in_draft} days</Badge>
                                    )}
                                  </div>
                                  <h4 className="font-semibold text-gray-900">{plan.client_name}</h4>
                                  <p className="text-sm text-gray-600">
                                    {plan.service_type_name || 'Treatment Plan'} - Version {plan.version_number}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Created: {new Date(plan.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="ml-2 border-purple-300 text-purple-700 hover:bg-purple-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.currentTarget.parentElement.parentElement.click();
                                  }}
                                >
                                  View →
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Notes Archive Search */}
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-blue-900">🔍 Search Notes Archive</CardTitle>
                    <span className="text-xs text-blue-700">Currently showing: Last {notesDateRange === 'all' ? 'All Time' : `${notesDateRange} days`}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Date Range */}
                    <div>
                      <Label className="text-sm text-gray-700">Date Range</Label>
                      <Select value={notesDateRange} onValueChange={setNotesDateRange}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7">Last 7 days</SelectItem>
                          <SelectItem value="30">Last 30 days</SelectItem>
                          <SelectItem value="90">Last 90 days</SelectItem>
                          <SelectItem value="all">All time</SelectItem>
                          <SelectItem value="custom">Custom range</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Client Filter */}
                    <div>
                      <Label className="text-sm text-gray-700">Client</Label>
                      <Select value={notesClientFilter} onValueChange={setNotesClientFilter}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="All clients" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All clients</SelectItem>
                          {[...clients].sort((a, b) => (a.last_name || a.full_name || '').localeCompare(b.last_name || b.full_name || '')).map(client => (
                            <SelectItem key={client.id} value={client.id}>
                              {formatClientName(client)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Status Filter */}
                    <div>
                      <Label className="text-sm text-gray-700">Status</Label>
                      <Select value={notesStatusFilter} onValueChange={setNotesStatusFilter}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All statuses</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent_to_supervisor">Sent to Supervisor</SelectItem>
                          <SelectItem value="needs_corrections">Needs Corrections</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="signed_ready_for_billing">Ready for Billing</SelectItem>
                          <SelectItem value="billed">Billed</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="denied">Denied</SelectItem>
                          <SelectItem value="locked">Locked</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Search Button */}
                    <div className="flex items-end">
                      <Button 
                        onClick={() => refreshNotes()}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        <Search className="h-4 w-4 mr-2" />
                        Search
                      </Button>
                    </div>
                  </div>
                  
                  {/* Custom Date Range */}
                  {notesDateRange === 'custom' && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label className="text-sm text-gray-700">Start Date</Label>
                        <Input 
                          type="date" 
                          value={notesCustomStartDate}
                          onChange={(e) => setNotesCustomStartDate(e.target.value)}
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-gray-700">End Date</Label>
                        <Input 
                          type="date" 
                          value={notesCustomEndDate}
                          onChange={(e) => setNotesCustomEndDate(e.target.value)}
                          className="bg-white"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Clear Filters */}
                  {(notesClientFilter !== 'all' || notesStatusFilter !== 'all' || notesDateRange !== '30') && (
                    <div className="mt-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setNotesDateRange('30');
                          setNotesClientFilter('all');
                          setNotesStatusFilter('all');
                          setNotesCustomStartDate('');
                          setNotesCustomEndDate('');
                        }}
                        className="text-blue-600 border-blue-600"
                      >
                        Clear Filters
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Active Clinical Notes Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-2 cursor-pointer flex-1"
                      onClick={() => setShowActiveNotes(!showActiveNotes)}
                    >
                      {showActiveNotes ? (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      )}
                      <div>
                        <CardTitle>📝 Active Clinical Notes</CardTitle>
                        <p className="text-sm text-gray-600">
                          Notes in progress or pending next steps ({activeNotesCount} active)
                        </p>
                      </div>
                    </div>
                    {/* View Mode Toggle */}
                    {showActiveNotes && (
                      <div className="flex border rounded-md">
                        <Button
                          variant={tasksNotesViewMode === 'card' ? 'default' : 'ghost'}
                          size="sm"
                          className="rounded-r-none"
                          onClick={(e) => { e.stopPropagation(); setTasksNotesViewMode('card'); }}
                        >
                          <LayoutGrid className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={tasksNotesViewMode === 'list' ? 'default' : 'ghost'}
                          size="sm"
                          className="rounded-l-none"
                          onClick={(e) => { e.stopPropagation(); setTasksNotesViewMode('list'); }}
                        >
                          <List className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                {showActiveNotes && (
                <CardContent>
                  {/* Search and Filter Controls */}
                  <div className="mb-4 flex flex-wrap items-center gap-3 pb-3 border-b">
                    <div className="flex items-center gap-2">
                      <Search className="h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by client name..."
                        value={tasksNotesClientSearch}
                        onChange={(e) => setTasksNotesClientSearch(e.target.value)}
                        className="text-sm border rounded px-2 py-1 w-48"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Provider:</span>
                      <select
                        value={tasksNotesProviderFilter}
                        onChange={(e) => setTasksNotesProviderFilter(e.target.value)}
                        className="text-sm border rounded px-2 py-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="all">All Providers</option>
                        {[...users].filter(u => u.role === 'Provider' || u.role === 'Intern').sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')).map(provider => (
                          <option key={provider.id} value={provider.id}>
                            {formatProviderName(provider)}
                          </option>
                        ))}
                      </select>
                    </div>
                    {(tasksNotesClientSearch || tasksNotesProviderFilter !== 'all') && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setTasksNotesClientSearch('');
                          setTasksNotesProviderFilter('all');
                        }}
                        className="text-gray-500"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                  {/* Bulk Actions Toolbar */}
                  {selectedNotes.length > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{selectedNotes.length} note{selectedNotes.length > 1 ? 's' : ''} selected</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedNotes([])}
                        >
                          Clear Selection
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        {canLockNote({}) && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                try {
                                  await Promise.all(selectedNotes.map(noteId =>
                                    axios.put(`${API}/notes/${noteId}`, {
                                      is_locked: true,
                                      locked_at: new Date().toISOString(),
                                      locked_by: user.id
                                    })
                                  ));
                                  toast.success(`${selectedNotes.length} note(s) locked`);
                                  setSelectedNotes([]);
                                  refreshNotes(); refreshTodos();
                                } catch (error) {
                                  toast.error('Failed to lock notes');
                                }
                              }}
                            >
                              <Lock className="h-4 w-4 mr-1" />
                              Lock
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                try {
                                  await Promise.all(selectedNotes.map(noteId =>
                                    axios.put(`${API}/notes/${noteId}`, {
                                      is_locked: false,
                                      unlocked_at: new Date().toISOString(),
                                      unlocked_by: user.id
                                    })
                                  ));
                                  toast.success(`${selectedNotes.length} note(s) unlocked`);
                                  setSelectedNotes([]);
                                  refreshNotes(); refreshTodos();
                                } catch (error) {
                                  toast.error('Failed to unlock notes');
                                }
                              }}
                            >
                              <Unlock className="h-4 w-4 mr-1" />
                              Unlock
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600"
                          onClick={async () => {
                            try {
                              await Promise.all(selectedNotes.map(noteId =>
                                axios.put(`${API}/notes/${noteId}`, {
                                  provider_signed_at: new Date().toISOString(),
                                  provider_signed_by: user.id
                                })
                              ));
                              toast.success(`${selectedNotes.length} note(s) signed`);
                              setSelectedNotes([]);
                              refreshNotes(); refreshTodos();
                            } catch (error) {
                              toast.error('Failed to sign notes');
                            }
                          }}
                        >
                          ✍️ Sign All
                        </Button>
                        {hasPermission('delete_notes') && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete ${selectedNotes.length} note(s)?`)) {
                                // Delete each note with undo capability
                                selectedNotes.forEach(noteId => {
                                  const note = notes.find(n => n.id === noteId);
                                  handleDeleteNoteWithUndo(noteId, note);
                                });
                                toast.success(`${selectedNotes.length} note(s) queued for deletion. Click UNDO to restore.`);
                                setSelectedNotes([]);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="space-y-6">
                    <React.Fragment>
                    {(() => {
                      // Filter notes based on role
                      let accessibleNotes = notes;
                      
                      // Check for Provider/Intern role OR legacy is_provider flag
                      if (userRoleName === 'provider' || userRoleName === 'intern' || (user?.is_provider && !user?.role)) {
                        accessibleNotes = notes.filter(n => n.provider_id === user.id);
                      }
                      else if (userRoleName === 'supervisor' || user?.is_supervisor) {
                        const superviseeIds = users.filter(u => 
                          u.supervisor_ids?.includes(user.id)
                        ).map(u => u.id);
                        accessibleNotes = notes.filter(n => 
                          n.provider_id === user.id || superviseeIds.includes(n.provider_id)
                        );
                      }
                      
                      // Filter out To-Do notes (already shown above)
                      const todoNoteIds = new Set(myTodoNotes.map(n => n.id));
                      accessibleNotes = accessibleNotes.filter(n => !todoNoteIds.has(n.id));
                      
                      if (accessibleNotes.length === 0 && myTodoNotes.length === 0) {
                        return (
                          <div className="text-center py-8 text-gray-500">
                            <FileText className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                            <p>No notes available</p>
                            <p className="text-sm mt-2">Click "Create Note" to add a new clinical note</p>
                          </div>
                        );
                      }
                      
                      // Separate active and billed/completed notes
                      // Only show notes from the last 90 days
                      const ninetyDaysAgo = new Date();
                      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
                      
                      // Apply search and provider filters
                      const searchLower = tasksNotesClientSearch.toLowerCase().trim();
                      const filterBySearchAndProvider = (note) => {
                        // Provider filter
                        if (tasksNotesProviderFilter !== 'all' && note.provider_id !== tasksNotesProviderFilter) {
                          return false;
                        }
                        // Client name search
                        if (searchLower) {
                          const noteClient = allClientsForLookup.find(c => c.id === note.client_id);
                          const clientName = noteClient?.full_name || noteClient?.first_name || note.client_name || '';
                          if (!clientName.toLowerCase().includes(searchLower)) {
                            // Also check group note clients
                            if (note.is_group_note && note.client_ids?.length > 0) {
                              const groupClientNames = note.client_ids
                                .map(id => allClientsForLookup.find(c => c.id === id))
                                .filter(Boolean)
                                .map(c => c.full_name || c.first_name || '')
                                .join(' ');
                              if (!groupClientNames.toLowerCase().includes(searchLower)) {
                                return false;
                              }
                            } else {
                              return false;
                            }
                          }
                        }
                        return true;
                      };
                      
                      const activeNotes = accessibleNotes.filter(n => {
                        // Apply search and provider filters first
                        if (!filterBySearchAndProvider(n)) {
                          return false;
                        }
                        
                        // Exclude draft notes (they should be in To-Do)
                        if (n.status === 'draft' || !n.status) {
                          return false;
                        }
                        
                        // Already billed/paid/locked goes to bottom
                        if (n.is_locked || n.status === 'billed' || n.status === 'paid') {
                          return false;
                        }
                        
                        // Non-billable completed notes go to bottom
                        if (n.status === 'completed' && !n.is_billable) {
                          return false;
                        }
                        
                        // Non-billable signed ready for billing goes to bottom (treated as completed)
                        if (n.status === 'signed_ready_for_billing' && !n.is_billable) {
                          return false;
                        }
                        
                        // Everything else (including billable completed/signed notes) stays active
                        const noteDate = new Date(n.created_at);
                        return noteDate >= ninetyDaysAgo;
                      });
                      
                      const billedNotes = accessibleNotes.filter(n => {
                        // Apply search and provider filters first
                        if (!filterBySearchAndProvider(n)) {
                          return false;
                        }
                        
                        // Billed, paid, or locked
                        const isBilledOrLocked = n.is_locked || n.status === 'billed' || n.status === 'paid';
                        
                        // Non-billable completed notes
                        const isNonBillableCompleted = (n.status === 'completed' || n.status === 'signed_ready_for_billing') && !n.is_billable;
                        
                        if (!(isBilledOrLocked || isNonBillableCompleted)) {
                          return false;
                        }
                        
                        // Only show from last 90 days
                        const noteDate = new Date(n.created_at);
                        return noteDate >= ninetyDaysAgo;
                      });
                      
                      const renderNote = (note, isLocked = false) => {
                        const noteClient = allClientsForLookup.find(c => c.id === note.client_id);
                        const noteProvider = users.find(u => u.id === note.provider_id);
                        
                        // Get all clients for group notes
                        const noteClients = note.is_group_note && note.client_ids?.length > 0
                          ? note.client_ids.map(id => allClientsForLookup.find(c => c.id === id)).filter(Boolean)
                          : noteClient ? [noteClient] : [];
                        
                        // Fallback to note.client_name if no clients found via lookup
                        const clientDisplayName = noteClients.length > 0 
                          ? (note.is_group_note 
                              ? noteClients.map(c => formatClientName(c)).join(', ')
                              : formatClientName(noteClients[0]))
                          : note.client_name || null;
                        
                        return (
                          <div
                            key={note.id}
                            className={`p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${isLocked ? 'bg-gray-50' : ''}`}
                            onClick={() => {
                              openNote(note);
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setNoteContextMenu({
                                show: true,
                                x: e.clientX,
                                y: e.clientY,
                                note: note
                              });
                            }}
                            title="Right-click for options"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={getStatusBadgeColor(note.status)}>
                                    {formatStatusLabel(note)}
                                  </Badge>
                                  {isLocked && (
                                    <Badge variant="outline" className="bg-gray-200 text-gray-700">
                                      🔒 LOCKED
                                    </Badge>
                                  )}
                                  {note.is_group_note && (
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                      GROUP ({noteClients.length})
                                    </Badge>
                                  )}
                                  {clientDisplayName ? (
                                    <span className="text-sm font-semibold">
                                      {clientDisplayName}
                                    </span>
                                  ) : (
                                    <span className="text-sm font-semibold text-red-600">
                                      ⚠️ No Client Assigned
                                    </span>
                                  )}
                                </div>
                                {/* Show template/form name - prefer template_name (frozen), fallback to title */}
                                {(note.template_name || note.title) && (
                                  <p className="text-sm font-medium text-teal-700 mb-1">
                                    {note.template_name || note.title}
                                  </p>
                                )}
                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                  {noteProvider && (
                                    <span>
                                      Provider: {noteProvider.full_name}
                                      {noteProvider.is_archived && <span className="text-red-600"> [Inactive]</span>}
                                    </span>
                                  )}
                                  <span>DOS: {formatDateStringForDisplay(note.date_of_service || note.created_at)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Download button for external fax/send - only show if template allows it */}
                                {(() => {
                                  const noteTemplate = formTemplates.find(t => t.id === note.template_id);
                                  const requiresSignature = noteTemplate?.download_requires_signature !== false;
                                  const isSignedOrLocked = note.status === 'signed_ready_for_billing' || note.status === 'completed' || note.status === 'locked' || note.is_locked;
                                  const canDownload = noteTemplate?.allow_external_download && 
                                    (!requiresSignature || isSignedOrLocked);
                                  if (canDownload) {
                                    return (
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="text-blue-600 hover:bg-blue-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Download the note as PDF
                                          const token = sessionStorage.getItem('token');
                                          window.open(`${API}/notes/${note.id}/wysiwyg-pdf?token=${token}`, '_blank');
                                        }}
                                        title="Download for external fax/send"
                                      >
                                        <Download className="h-4 w-4" />
                                      </Button>
                                    );
                                  }
                                  return null;
                                })()}
                                {/* Duplicate button - available for all notes */}
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-purple-600 hover:bg-purple-50"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      // Fetch full note data
                                      const fullNoteResponse = await axios.get(`${API}/notes/${note.id}`, {
                                        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                      });
                                      const sourceNote = fullNoteResponse.data;
                                      
                                      if (!sourceNote) {
                                        toast.error('Could not fetch note data');
                                        return;
                                      }
                                      
                                      // Create duplicate with ALL relevant fields from source note
                                      const duplicateData = {
                                        client_id: sourceNote.client_id,
                                        client_ids: sourceNote.client_ids || [],
                                        is_group_note: sourceNote.is_group_note || false,
                                        provider_id: sourceNote.provider_id || user?.id,
                                        template_id: sourceNote.template_id,
                                        content: sourceNote.content || '',
                                        status: 'draft', // Always create as draft
                                        note_type: sourceNote.note_type,
                                        form_type_name: sourceNote.form_type_name || sourceNote.form_type,
                                        title: sourceNote.title,
                                        date_of_service: sourceNote.date_of_service,
                                        start_time: sourceNote.start_time,
                                        end_time: sourceNote.end_time,
                                        units: sourceNote.units,
                                        cpt_code: sourceNote.cpt_code,
                                        billable: sourceNote.billable || false,
                                        linked_goals: sourceNote.linked_goals || [],
                                        telehealth_consent: sourceNote.telehealth_consent,
                                        require_client_signature: sourceNote.require_client_signature || false,
                                        allow_client_signature: sourceNote.allow_client_signature || false,
                                        requires_supervisor_signature: sourceNote.requires_supervisor_signature || false,
                                        assigned_supervisor: sourceNote.assigned_supervisor || null
                                      };
                                      
                                      const response = await axios.post(`${API}/notes`, duplicateData, {
                                        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                      });
                                      toast.success('Note duplicated successfully');
                                      refreshNotes(); refreshTodos();
                                      // Open the new note properly via openNote()
                                      await openNote(response.data);
                                    } catch (error) {
                                      console.error('Error duplicating note:', error);
                                      toast.error('Failed to duplicate note: ' + (error.response?.data?.detail || error.message));
                                    }
                                  }}
                                  title="Duplicate this note"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="outline">
                                  {isLocked ? 'View' : 'Edit'}
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      };
                      
                      return (
                        <>
                          {/* Pagination Controls for Active Notes */}
                          {activeNotes.length > 0 && (
                            <div className="flex items-center justify-between mb-4 pb-3 border-b">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Show:</span>
                                <select
                                  value={activeNotesPageSize}
                                  onChange={(e) => {
                                    const newSize = e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10);
                                    setActiveNotesPageSize(newSize);
                                    setActiveNotesPage(1);
                                  }}
                                  className="text-sm border rounded px-2 py-1"
                                >
                                  <option value={5}>5</option>
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                  <option value={100}>100</option>
                                  <option value="all">All</option>
                                </select>
                                <span className="text-sm text-gray-500">
                                  of {activeNotes.length} notes
                                </span>
                              </div>
                              {activeNotesPageSize !== 'all' && activeNotes.length > activeNotesPageSize && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setActiveNotesPage(p => Math.max(1, p - 1))}
                                    disabled={activeNotesPage === 1}
                                  >
                                    ← Prev
                                  </Button>
                                  <span className="text-sm text-gray-600">
                                    Page {activeNotesPage} of {Math.ceil(activeNotes.length / activeNotesPageSize)}
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setActiveNotesPage(p => Math.min(Math.ceil(activeNotes.length / activeNotesPageSize), p + 1))}
                                    disabled={activeNotesPage >= Math.ceil(activeNotes.length / activeNotesPageSize)}
                                  >
                                    Next →
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Active Notes */}
                          {activeNotes.length > 0 ? (
                            tasksNotesViewMode === 'list' ? (
                              /* List View — virtualized when > 50 rows */
                              (() => {
                                const pageSize = activeNotesPageSize === 'all' ? activeNotes.length : activeNotesPageSize;
                                const startIndex = (activeNotesPage - 1) * pageSize;
                                const paginatedNotes = activeNotes.slice(startIndex, startIndex + pageSize);
                                return (
                                  <VirtualizedNotesList
                                    notes={paginatedNotes}
                                    view="table"
                                    clientsByIdMap={clientsByIdMap}
                                    usersByIdMap={usersByIdMap}
                                    formatClientName={formatClientName}
                                    formatStatusLabel={formatStatusLabel}
                                    getStatusBadgeColor={getStatusBadgeColor}
                                    formatDateStringForDisplay={formatDateStringForDisplay}
                                    openNote={openNote}
                                    renderNote={renderNote}
                                  />
                                );
                              })()
                            ) : (
                              /* Card View — virtualized when > 50 rows */
                              (() => {
                                const pageSize = activeNotesPageSize === 'all' ? activeNotes.length : activeNotesPageSize;
                                const startIndex = (activeNotesPage - 1) * pageSize;
                                const paginatedNotes = activeNotes.slice(startIndex, startIndex + pageSize);
                                return (
                                  <VirtualizedNotesList
                                    notes={paginatedNotes}
                                    view="cards"
                                    clientsByIdMap={clientsByIdMap}
                                    usersByIdMap={usersByIdMap}
                                    formatClientName={formatClientName}
                                    formatStatusLabel={formatStatusLabel}
                                    getStatusBadgeColor={getStatusBadgeColor}
                                    formatDateStringForDisplay={formatDateStringForDisplay}
                                    openNote={openNote}
                                    renderNote={renderNote}
                                  />
                                );
                              })()
                            )
                          ) : (
                            <div className="text-center py-4 text-gray-500">
                              <p className="text-sm">No active notes</p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    </React.Fragment>
                  </div>
                </CardContent>
                )}
              </Card>

              {/* Billed/Paid/Locked Notes - Collapsible */}
              <React.Fragment>
              {(() => {
                // Filter notes based on role
                let accessibleNotes = notes;
                
                // Check for Provider/Intern role OR legacy is_provider flag
                if (userRoleName === 'provider' || userRoleName === 'intern' || (user?.is_provider && !user?.role)) {
                  accessibleNotes = notes.filter(n => n.provider_id === user.id);
                }
                else if (userRoleName === 'supervisor' || user?.is_supervisor) {
                  const superviseeIds = users.filter(u => 
                    u.supervisor_ids?.includes(user.id)
                  ).map(u => u.id);
                  accessibleNotes = notes.filter(n => 
                    n.provider_id === user.id || superviseeIds.includes(n.provider_id)
                  );
                }
                
                // Only show notes from last 90 days
                const ninetyDaysAgo = new Date();
                ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
                
                const billedNotes = accessibleNotes.filter(n => {
                  // Billed, paid, or locked
                  const isBilledOrLocked = n.is_locked || n.status === 'billed' || n.status === 'paid';
                  
                  // Non-billable completed notes
                  const isNonBillableCompleted = (n.status === 'completed' || n.status === 'signed_ready_for_billing') && !n.is_billable;
                  
                  if (!(isBilledOrLocked || isNonBillableCompleted)) {
                    return false;
                  }
                  
                  // Only show from last 90 days
                  const noteDate = new Date(n.created_at);
                  return noteDate >= ninetyDaysAgo;
                });
                
                if (billedNotes.length === 0) return null;
                
                const renderNote = (note, isCompleted = true) => {
                  const noteClient = allClientsForLookup.find(c => c.id === note.client_id);
                  const noteProvider = users.find(u => u.id === note.provider_id);
                  
                  // Get all clients for group notes
                  const noteClients = note.is_group_note && note.client_ids?.length > 0
                    ? note.client_ids.map(id => allClientsForLookup.find(c => c.id === id)).filter(Boolean)
                    : noteClient ? [noteClient] : [];
                  
                  // Fallback to note.client_name if no clients found via lookup
                  const clientDisplayName = noteClients.length > 0 
                    ? (note.is_group_note 
                        ? noteClients.map(c => formatClientName(c)).join(', ')
                        : formatClientName(noteClients[0]))
                    : note.client_name || null;
                  
                  return (
                    <div
                      key={note.id}
                      className={`p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${isCompleted ? 'bg-gray-50' : ''}`}
                      onClick={() => {
                        openNote(note);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getStatusBadgeColor(note.status)}>
                              {formatStatusLabel(note)}
                            </Badge>
                            {isCompleted && (
                              <Badge variant="outline" className="bg-gray-200 text-gray-700">
                                🔒 LOCKED
                              </Badge>
                            )}
                            {note.is_group_note && (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                GROUP ({noteClients.length})
                              </Badge>
                            )}
                            {clientDisplayName ? (
                              <span className="text-sm font-semibold">
                                {clientDisplayName}
                              </span>
                            ) : (
                              <span className="text-sm font-semibold text-red-600">
                                ⚠️ No Client Assigned
                              </span>
                            )}
                          </div>
                          {/* Show template/form name - prefer template_name (frozen), fallback to title */}
                          {(note.template_name || note.title) && (
                            <p className="text-sm font-medium text-teal-700 mb-1">
                              {note.template_name || note.title}
                            </p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            {noteProvider && (
                              <span>
                                Provider: {noteProvider.full_name}
                                {noteProvider.is_archived && <span className="text-red-600"> (Archived)</span>}
                              </span>
                            )}
                            <span>DOS: {formatDateStringForDisplay(note.date_of_service || note.created_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Duplicate button - available for all notes */}
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-purple-600 hover:bg-purple-50"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                // Fetch full note data
                                const fullNoteResponse = await axios.get(`${API}/notes/${note.id}`, {
                                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                });
                                const sourceNote = fullNoteResponse.data;
                                
                                if (!sourceNote) {
                                  toast.error('Could not fetch note data');
                                  return;
                                }
                                
                                // Create duplicate with ALL relevant fields from source note
                                const duplicateData = {
                                  client_id: sourceNote.client_id,
                                  client_ids: sourceNote.client_ids || [],
                                  is_group_note: sourceNote.is_group_note || false,
                                  provider_id: sourceNote.provider_id || user?.id,
                                  template_id: sourceNote.template_id,
                                  content: sourceNote.content || '',
                                  status: 'draft', // Always create as draft
                                  note_type: sourceNote.note_type,
                                  form_type_name: sourceNote.form_type_name || sourceNote.form_type,
                                  title: sourceNote.title,
                                  date_of_service: sourceNote.date_of_service,
                                  start_time: sourceNote.start_time,
                                  end_time: sourceNote.end_time,
                                  units: sourceNote.units,
                                  cpt_code: sourceNote.cpt_code,
                                  billable: sourceNote.billable || false,
                                  linked_goals: sourceNote.linked_goals || [],
                                  telehealth_consent: sourceNote.telehealth_consent,
                                  require_client_signature: sourceNote.require_client_signature || false,
                                  allow_client_signature: sourceNote.allow_client_signature || false,
                                  requires_supervisor_signature: sourceNote.requires_supervisor_signature || false,
                                  assigned_supervisor: sourceNote.assigned_supervisor || null
                                };
                                
                                const response = await axios.post(`${API}/notes`, duplicateData, {
                                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                });
                                toast.success('Note duplicated successfully');
                                refreshNotes(); refreshTodos();
                                // Open the new note properly via openNote()
                                await openNote(response.data);
                              } catch (error) {
                                console.error('Error duplicating note:', error);
                                toast.error('Failed to duplicate note: ' + (error.response?.data?.detail || error.message));
                              }
                            }}
                            title="Duplicate this note"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline">
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                };
                
                return (
                  <Card className="border-gray-300">
                    <CardHeader
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setShowCompletedNotes(!showCompletedNotes)}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          {showCompletedNotes ? (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-500" />
                          )}
                          <div>
                            <CardTitle className="text-gray-700">
                              💰 Billed/Paid/Locked Notes
                            </CardTitle>
                            <p className="text-sm text-gray-500">
                              Finalized records (last 90 days) - {billedNotes.length} notes
                            </p>
                          </div>
                        </div>
                        {/* View Mode Toggle */}
                        {showCompletedNotes && (
                          <div className="flex border rounded-md" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant={billedNotesViewMode === 'card' ? 'default' : 'ghost'}
                              size="sm"
                              className="rounded-r-none"
                              onClick={(e) => { e.stopPropagation(); setBilledNotesViewMode('card'); }}
                            >
                              <LayoutGrid className="h-4 w-4" />
                            </Button>
                            <Button
                              variant={billedNotesViewMode === 'list' ? 'default' : 'ghost'}
                              size="sm"
                              className="rounded-l-none"
                              onClick={(e) => { e.stopPropagation(); setBilledNotesViewMode('list'); }}
                            >
                              <List className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    {showCompletedNotes && (
                      <CardContent>
                        {/* Pagination Controls for Billed Notes */}
                        {billedNotes.length > 0 && (
                          <div className="flex items-center justify-between mb-4 pb-3 border-b">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600">Show:</span>
                              <select
                                value={billedNotesPageSize}
                                onChange={(e) => {
                                  const newSize = e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10);
                                  setBilledNotesPageSize(newSize);
                                  setBilledNotesPage(1);
                                }}
                                className="text-sm border rounded px-2 py-1"
                              >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value="all">All</option>
                              </select>
                              <span className="text-sm text-gray-500">
                                of {billedNotes.length} notes
                              </span>
                            </div>
                            {billedNotesPageSize !== 'all' && billedNotes.length > billedNotesPageSize && (
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setBilledNotesPage(p => Math.max(1, p - 1))}
                                  disabled={billedNotesPage === 1}
                                >
                                  ← Prev
                                </Button>
                                <span className="text-sm text-gray-600">
                                  Page {billedNotesPage} of {Math.ceil(billedNotes.length / billedNotesPageSize)}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setBilledNotesPage(p => Math.min(Math.ceil(billedNotes.length / billedNotesPageSize), p + 1))}
                                  disabled={billedNotesPage >= Math.ceil(billedNotes.length / billedNotesPageSize)}
                                >
                                  Next →
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                        {billedNotesViewMode === 'list' ? (
                          /* Table/List View — virtualized when > 50 rows */
                          (() => {
                            const pageSize = billedNotesPageSize === 'all' ? billedNotes.length : billedNotesPageSize;
                            const startIndex = (billedNotesPage - 1) * pageSize;
                            const paginatedNotes = billedNotes.slice(startIndex, startIndex + pageSize);
                            return (
                              <VirtualizedNotesList
                                notes={paginatedNotes}
                                view="table"
                                clientsByIdMap={clientsByIdMap}
                                usersByIdMap={usersByIdMap}
                                formatClientName={formatClientName}
                                formatStatusLabel={formatStatusLabel}
                                getStatusBadgeColor={getStatusBadgeColor}
                                formatDateStringForDisplay={formatDateStringForDisplay}
                                openNote={openNote}
                                renderNote={renderNote}
                              />
                            );
                          })()
                        ) : (
                          /* Card View — virtualized when > 50 rows */
                          (() => {
                            const pageSize = billedNotesPageSize === 'all' ? billedNotes.length : billedNotesPageSize;
                            const startIndex = (billedNotesPage - 1) * pageSize;
                            const paginatedNotes = billedNotes.slice(startIndex, startIndex + pageSize);
                            return (
                              <VirtualizedNotesList
                                notes={paginatedNotes}
                                view="cards"
                                clientsByIdMap={clientsByIdMap}
                                usersByIdMap={usersByIdMap}
                                formatClientName={formatClientName}
                                formatStatusLabel={formatStatusLabel}
                                getStatusBadgeColor={getStatusBadgeColor}
                                formatDateStringForDisplay={formatDateStringForDisplay}
                                openNote={openNote}
                                renderNote={renderNote}
                              />
                            );
                          })()
                        )}
                      </CardContent>
                    )}
                  </Card>
                );
              })()}
              </React.Fragment>
            </div>
          </TabsContent>


          {/* Providers Directory Tab */}
          <TabsContent value="providers">
            <StaffDirectory
              users={users}
              currentUser={user}
              onViewProfile={(userId) => {
                setProfileStartInEdit(false);
                setViewingProfileUserId(userId);
                setShowProfileModal(true);
              }}
              onEditProfile={['admin', 'super admin'].includes((user?.role || '').toLowerCase()) ? (userId) => {
                setProfileStartInEdit(true);
                setViewingProfileUserId(userId);
                setShowProfileModal(true);
              } : undefined}
              onSendMessage={(userId, userName) => {
                setShowStaffToStaffMessageDialog(true);
                setStaffToStaffRecipientId(userId);
                setStaffToStaffRecipientName(userName);
              }}
            />
          </TabsContent>

          {/* CPT Codes Tab removed Feb 2026 — will be replaced by a Reports tab. */}



          {/* Portal Tab */}
          <TabsContent value="portal">
            <Card>
              <CardHeader>
                <CardTitle>Security & Access Control</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Controlled Access System:</strong> Only authorized users created by administrators can access this system. All data is encrypted and access is logged for HIPAA compliance.
                    </AlertDescription>
                  </Alert>
                  
                  {isAdmin() && (
                    <div className="space-y-4">
                      <h3 className="font-semibold">Administrator Features</h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-600">
                        <li>Complete user account management (create, view, disable)</li>
                        <li>Full system oversight and statistics</li>
                        <li>Audit trail access for compliance reporting</li>
                        <li>Client and provider management</li>
                        <li>System configuration and security controls</li>
                        <li>HIPAA compliance monitoring and reporting</li>
                      </ul>
                    </div>
                  )}
                  
                  {userRoleName === 'provider' && (
                    <div className="space-y-4">
                      <h3 className="font-semibold">Provider Portal Features</h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-600">
                        <li>Secure client management with HIPAA compliance</li>
                        <li>Appointment scheduling with conflict checking</li>
                        <li>Clinical note documentation with workflow management</li>
                        <li>Google Meet integration for telehealth sessions</li>
                        <li>Role-based access to assigned clients only</li>
                        <li>Secure messaging and communication tools</li>
                      </ul>
                    </div>
                  )}

                  {userRoleName === 'client' && (
                    <div className="space-y-4">
                      <h3 className="font-semibold">Client Portal Access</h3>
                      <p>As a client, you have secure access to your medical information and can:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-600">
                        <li>View your appointment history and upcoming sessions</li>
                        <li>Access your clinical notes (when shared by providers)</li>
                        <li>Join telehealth appointments via secure video links</li>
                        <li>Communicate securely with your care team</li>
                        <li>View test results and treatment plans</li>
                      </ul>
                    </div>
                  )}

                  <div className="mt-6 p-4 bg-teal-50 rounded-lg">
                    <h4 className="font-semibold text-teal-800 mb-2">Security Notice</h4>
                    <p className="text-sm text-teal-700">
                      CareVaultPro EMR uses controlled access authentication. New users must be created by system administrators. 
                      All activities are logged for HIPAA compliance and security monitoring.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </Suspense>
      </main>

      {/* Intake Form Dialog - Global (moved outside tabs for accessibility from any tab) */}
      {/* Intake Form Dialog - Extracted to IntakeWizardDialog component */}
      <Suspense fallback={null}>
      <IntakeWizardDialog
        // Dialog state
        showIntakeWizard={showIntakeWizard}
        setShowIntakeWizard={handleCloseIntakeWizardWithUrl}
        
        // Client data
        editingClient={editingClient}
        intakeData={intakeData}
        intakeClientId={intakeClientId}
        intakeLastSaved={intakeLastSaved}
        intakeAutoSaving={intakeAutoSaving}
        intakeValidationErrors={intakeValidationErrors}
        
        // Duplicate checking
        checkingRealtimeDuplicates={checkingRealtimeDuplicates}
        setCheckingRealtimeDuplicates={setCheckingRealtimeDuplicates}
        realtimeDuplicateWarnings={realtimeDuplicateWarnings}
        setRealtimeDuplicateWarnings={setRealtimeDuplicateWarnings}
        
        // Change request state
        setShowChangeRequestDialog={setShowChangeRequestDialog}
        setChangeRequestProposedChanges={setChangeRequestProposedChanges}
        setChangeRequestSection={setChangeRequestSection}
        setChangeRequestFields={setChangeRequestFields}
        
        // Provider assignment state
        providers={providers}
        setProviders={setProviders}
        setSelectedProvidersForAssignment={setSelectedProvidersForAssignment}
        setShowAssignProviders={setShowAssignProviders}
        setAvailableServices={setAvailableServices}
        setExistingEnrollments={setExistingEnrollments}
        setProviderServiceAssignments={setProviderServiceAssignments}
        
        // Client selection
        setSelectedClient={setSelectedClient}
        setClientDetailTab={setClientDetailTab}
        setShowClientDetail={setShowClientDetail}
        
        // Reference data
        locations={locations}
        users={users}
        serviceTypes={serviceTypes}
        onViewChangeHistory={(client) => {
          setChangeHistoryEntity({ type: 'client', id: client.id, name: client.full_name || 'Client' });
          setShowChangeHistory(true);
        }}
        
        // Handlers
        handleIntakeFieldChange={handleIntakeFieldChange}
        handleIntakeFieldValidation={handleIntakeFieldValidation}
        handleCloseIntakeWizard={handleCloseIntakeWizard}
        handleSaveIntakeDraft={handleSaveIntakeDraft}
        handleGenerateIntakePDF={handleGenerateIntakePDF}
        handleCompleteIntake={handleCompleteIntake}
        handleOpenIntakeWizard={handleOpenIntakeWizard}
        handleOpenInsuranceManagement={handleOpenInsuranceManagement}
        
        // Permission/config helpers
        canEditFieldCategory={canEditFieldCategory}
        isFieldVisible={isFieldVisible}
        isFieldRequired={isFieldRequired}
        hasPermission={hasPermission}
        
        // Pending insurances for new client creation
        pendingInsurances={pendingIntakeInsurances}
        setPendingInsurances={setPendingIntakeInsurances}
        
        // Medication dialog props for editing existing client
        setShowMedicationForm={setShowMedicationForm}
        setEditingMedication={setEditingMedication}
        clientMedications={clientMedications}
      />


      {/* Appointment Form Dialog — extracted to AppointmentScheduleDialog (Calendar Stage 10). */}
      <SeriesScopeDialog open={showSeriesScopeDialog} onChoose={resolveSeriesScope} />
      <AppointmentScheduleDialog
        legacy={{
          isSuperAdmin,
          getEffectivePosCodes,
          closeAppointment,
          fetchAppointments: refreshAppointments,
          handleCreateAppointment,
          refreshAppointments,
          refreshNotes,
          refreshTodos,
          refreshStats,
          openMeetPopup,
          openNote,
          navigate,
          getAccessibleClients,
          getAccessibleProviders,
          getAccessibleCPTCodes,
          getDOSEligibilityDisplay,
          getEligibilityDisplay,
          eligibilityTooltip,
          handleEligibilityCheck,
          handleOpenAttendanceDialog,
          formatClientName,
          addToUndoStack,
          safeEligibilityDetail,
          applicableIntakeBundles,
          setApplicableIntakeBundles,
          setChangeHistoryEntity,
          setShowChangeHistory,
          addAttendeeSearch, setAddAttendeeSearch,
          showAddAttendeeDialog, setShowAddAttendeeDialog,
          showGroupNoteDialog, setShowGroupNoteDialog,
          groupNoteGroup, setGroupNoteGroup,
          groupNoteCurriculumSession, setGroupNoteCurriculumSession,
          groupNoteExistingNotes, setGroupNoteExistingNotes,
          groupNoteMode, setGroupNoteMode,
          allAuthorizations,
        }}
      />

      {/* Client Detail Dialog - Lazy mounted to avoid ~5500 lines of JSX evaluation when closed */}
      {(showClientDetail || isFullscreenClientMode) && (
      <Dialog 
        modal={false}
        open={showClientDetail || isFullscreenClientMode} 
        onOpenChange={(open) => {
        if (!open) {
          // Jun 2026 — single close path: handleCloseClientChart owns
          // origin-aware navigation (captured view or dashboard default),
          // user-closed marking, and fullscreen reset.
          handleCloseClientChart();
          return;
        }
        setShowClientDetail(open);
      }}>
        <DialogContent 
          key={selectedClient?.id || 'no-client'} 
          className={isFullscreenClientMode ? "max-w-full w-full h-screen max-h-screen overflow-y-auto rounded-none" : "max-w-7xl max-h-[90vh] overflow-y-auto"}
          onPointerDownOutside={(e) => {
            // If ANY other dialog is mounted on top, prevent closing the chart.
            // This catches sub-dialogs we didn't enumerate by state name
            // (file attach, service enrollments, any dynamically-opened modal).
            if (getOpenDialogCount() > 1) {
              e.preventDefault();
              return;
            }
            // Legacy explicit guards (kept as belt-and-suspenders)
            if (showNoteDetail || showAddNote || showMedicationForm || showAppointmentForm || 
                showAlertForm || showSignatureConfirm || showCorrectionsDialog || showSendToClientDialog || 
                showAddContact || showEditContact || showInactiveReasonDialog || showServiceDisenrollmentDialog || 
                showCancelFutureApptsDialog || showBillingReviewDialog || showCreateTask || showTaskDetail ||
                showAdminOverrideDialog || showMedicationReferralDialog || showMedicationManagerDashboard ||
                showEditClientForm || showIntakeWizard || showDiagnosisForm || showAuthorizationForm) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            if (getOpenDialogCount() > 1) {
              e.preventDefault();
              return;
            }
            if (showNoteDetail || showAddNote || showMedicationForm || showAppointmentForm || 
                showAlertForm || showSignatureConfirm || showCorrectionsDialog || showSendToClientDialog || 
                showAddContact || showEditContact || showInactiveReasonDialog || showServiceDisenrollmentDialog || 
                showCancelFutureApptsDialog || showBillingReviewDialog || showCreateTask || showTaskDetail ||
                showAdminOverrideDialog || showMedicationReferralDialog || showMedicationManagerDashboard ||
                showEditClientForm || showIntakeWizard || showDiagnosisForm || showAuthorizationForm) {
              e.preventDefault();
            }
          }}
        >
          <DialogTitle className="sr-only">
            Client Chart{selectedClient ? ` — ${selectedClient.full_name || selectedClient.first_name || ''}` : ''}
          </DialogTitle>
          <DialogDescription className="sr-only">Client chart details</DialogDescription>
          {/* Loading Overlay */}
          {clientDetailLoading && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
              <p className="text-gray-600 font-medium">Loading Client...</p>
            </div>
          )}
          {!clientDetailLoading && selectedClient && (
            <div className="space-y-6">
              {/* Client Header - Condensed Layout */}
              <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border">
                {/* Top bar - Collapsed: Single row with all info | Expanded: Just MRN + icons */}
                <div className={`flex items-center justify-between px-4 py-2 ${!clientHeaderCollapsed ? 'border-b border-gray-200/50' : ''}`}>
                  {/* Left side - MRN (+ Name + Insurance when collapsed) */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-600">
                      {selectedClient.mrn ? `MRN: #${selectedClient.mrn.replace('MRN-', '')}` : 'No MRN'}
                    </span>
                    {clientHeaderCollapsed && (
                      <>
                        <span className="text-gray-400">·</span>
                        <span className="font-bold text-gray-800">{formatClientName(selectedClient)}</span>
                        <span className="text-gray-400">·</span>
                        {(() => {
                          const activeInsurances = (selectedClientInsurances || [])
                            .filter(ins => ins.status === 'active')
                            .sort((a, b) => a.priority - b.priority);
                          
                          if (activeInsurances.length === 0) {
                            return <span className="text-gray-600">Self Pay</span>;
                          }
                          
                          const primary = activeInsurances[0];
                          return (
                            <>
                              <span className="text-gray-600">{primary.company}</span>
                              {(() => {
                                const s = selectedClient?.primary_eligibility_status || primary.eligibility_status;
                                if (!s || s === 'not_checked') return null;
                                const { icon, color, bg } = getEligibilityDisplay(s);
                                return <span className={`text-xs font-semibold ${color}`}>{icon}</span>;
                              })()}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                  
                  {/* Action Icons - Right */}
                  <div className="flex items-center gap-1">
                    {/* Email Icon (shown when collapsed) */}
                    {clientHeaderCollapsed && selectedClient.email && (
                      <a
                        href={`mailto:${selectedClient.email}`}
                        className="h-8 w-8 p-0 inline-flex items-center justify-center text-gray-500 hover:text-teal-600 hover:bg-gray-100 rounded-md"
                        title={`Email: ${selectedClient.email}`}
                      >
                        <Mail className="h-4 w-4" />
                      </a>
                    )}
                    {/* Phone Icon (shown when collapsed) */}
                    {clientHeaderCollapsed && selectedClient.phone && (
                      <a
                        href={`tel:${selectedClient.phone}`}
                        className="h-8 w-8 p-0 inline-flex items-center justify-center text-gray-500 hover:text-teal-600 hover:bg-gray-100 rounded-md"
                        title={`Call: ${selectedClient.phone}`}
                      >
                        <Phone className="h-4 w-4" />
                      </a>
                    )}
                    {/* Address Hover (shown when collapsed) — mirror
                        of the expanded-header HoverCard.  Same data
                        sources and copy affordance.  Feb 27, 2026. */}
                    {clientHeaderCollapsed && (() => {
                      const street = selectedClient.address || selectedClient.street || selectedClient.address_line1;
                      const line2 = selectedClient.address_line2;
                      const city = selectedClient.city;
                      const state = selectedClient.state;
                      const zip = selectedClient.zipcode || selectedClient.zip || selectedClient.zip_code;
                      const hasAddress = !!(street || city || state || zip);
                      const singleLine = [street, line2, [city, state].filter(Boolean).join(', '), zip].filter(Boolean).join(' ');
                      const multiLine = [street, line2, [city, state].filter(Boolean).join(', ') + (zip ? ` ${zip}` : '')].filter(Boolean);
                      return (
                        <HoverCard openDelay={200} closeDelay={150}>
                          <HoverCardTrigger asChild>
                            <button
                              type="button"
                              data-testid="client-address-hover-trigger-collapsed"
                              className={`h-8 w-8 p-0 inline-flex items-center justify-center rounded-md ${hasAddress ? 'text-gray-500 hover:text-teal-600 hover:bg-gray-100' : 'text-gray-300 cursor-help'}`}
                              title={hasAddress ? 'Hover to see address' : 'No address on file'}
                              aria-label="Client address"
                            >
                              <HomeIcon className="h-4 w-4" />
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent side="bottom" align="end" className="w-72">
                            {hasAddress ? (
                              <div className="space-y-2">
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                  Address
                                </div>
                                <div className="text-sm text-gray-800 space-y-0.5">
                                  {multiLine.map((line, i) => (
                                    <div key={i}>{line}</div>
                                  ))}
                                </div>
                                <div className="pt-1">
                                  <button
                                    type="button"
                                    data-testid="client-address-copy-btn-collapsed"
                                    className="w-full text-xs px-2 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 transition-colors"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        if (navigator?.clipboard?.writeText) {
                                          await navigator.clipboard.writeText(singleLine);
                                          toast.success('Address copied');
                                        } else {
                                          const ta = document.createElement('textarea');
                                          ta.value = singleLine;
                                          document.body.appendChild(ta);
                                          ta.select();
                                          document.execCommand('copy');
                                          document.body.removeChild(ta);
                                          toast.success('Address copied');
                                        }
                                      } catch (err) {
                                        toast.error('Failed to copy address');
                                      }
                                    }}
                                  >
                                    Copy address
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 italic">
                                No address on file for this client.
                              </div>
                            )}
                          </HoverCardContent>
                        </HoverCard>
                      );
                    })()}
                    {/* iPrescribe Icon */}
                    {brandingSettings?.iprescribe_enabled && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={openIPrescribe}
                        disabled={!canAccessIPrescribe()}
                        className={`h-8 w-8 p-0 ${
                          canAccessIPrescribe()
                            ? 'text-purple-600 hover:text-purple-800 hover:bg-purple-50' 
                            : 'text-gray-300 cursor-not-allowed'
                        }`}
                        title={canAccessIPrescribe() ? 'Open iPrescribe' : 'Requires prescribing authority'}
                      >
                        <Pill className="h-4 w-4" />
                      </Button>
                    )}
                    {/* Edit */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => intakeWizard.open(selectedClient.id) /* Phase 3e.3a — V2 provider */}
                      className="h-8 w-8 p-0 text-gray-500 hover:text-teal-600"
                      title="Edit client details"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {/* External Link */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        sessionStorage.setItem('activeClientId', selectedClient.id);
                        window.openAuthenticatedTab('/activeclient');
                      }}
                      className="h-8 w-8 p-0 text-gray-500 hover:text-teal-600"
                      title="Open in new window"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    {/* Message */}
                    {hasClientAccess(selectedClient) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setMessageFromChartClientId(selectedClient.id);
                          setMessageFromChartClientName(formatClientName(selectedClient));
                          setShowMessageFromChartDialog(true);
                        }}
                        className="h-8 w-8 p-0 text-gray-500 hover:text-teal-600"
                        title="Send message to client"
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                    )}
                    {/* Portal Link */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        if (!selectedClient?.email) {
                          toast.error('No email on file');
                          return;
                        }
                        try {
                          toast.loading('Sending portal link...', { id: 'portal-link' });
                          const response = await axios.post(`${API}/admin/generate-client-portal-link`, {
                            client_id: selectedClient.id,
                            send_email: true
                          });
                          if (response.data.email_sent) {
                            toast.success(`Portal link sent!`, { id: 'portal-link' });
                          } else {
                            toast.error('Email could not be sent', { id: 'portal-link' });
                            setGeneratedPortalLink(response.data);
                            setShowPortalLinkModal(true);
                          }
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to send portal link', { id: 'portal-link' });
                        }
                      }}
                      className="h-8 w-8 p-0 text-gray-500 hover:text-teal-600"
                      title="Send client portal link"
                    >
                      <Link className="h-4 w-4" />
                    </Button>
                    {/* View Portal (Admin only) */}
                    {isSuperAdmin(user) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          // Sync window.open first — popup blockers kill
                          // window.open called after an await (user report).
                          const w = window.open('', '_blank');
                          try {
                            const response = await axios.post(`${API}/admin/generate-client-portal-link`, {
                              client_id: selectedClient.id,
                              admin_preview: true
                            });
                            const url = response.data?.magic_link;
                            if (url) {
                              if (w) w.location = url; else window.open(url, '_blank');
                            } else {
                              w?.close();
                              toast.error('No portal link returned');
                            }
                          } catch (error) {
                            w?.close();
                            toast.error(error.response?.data?.detail || 'Failed to open portal preview');
                          }
                        }}
                        className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600"
                        title="View client portal (as client)"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    {/* Collapse/Expand Toggle */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setClientHeaderCollapsed(!clientHeaderCollapsed)}
                      className="h-8 w-8 p-0 text-gray-500 hover:text-teal-600 ml-2"
                      title={clientHeaderCollapsed ? "Expand header" : "Collapse header"}
                    >
                      {clientHeaderCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                
                {/* Main Header Content - Only shown when expanded */}
                {!clientHeaderCollapsed && (
                  <div className="px-4 py-4 space-y-1">
                    {/* EXPANDED: Two-column layout - Client info left, Care Team right */}
                    
                    {/* Row 1: Name + Status */}
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <h2 className="font-bold text-gray-800 text-2xl">
                          {formatClientName(selectedClient)}
                        </h2>
                        {/* Feb 27, 2026 — Quick address peek + copy.
                            User asked for a small house icon next to
                            the client name that opens an address
                            tooltip on hover.  HoverCard handles the
                            open-on-hover gesture; we add a copy button
                            inside so a single hover → click copies
                            the formatted address to the clipboard.
                            Falls back to a faded gray icon if no
                            address parts are on file (a hint that
                            this client has no address rather than a
                            missing affordance). */}
                        {(() => {
                          const street = selectedClient.address || selectedClient.street || selectedClient.address_line1;
                          const line2 = selectedClient.address_line2;
                          const city = selectedClient.city;
                          const state = selectedClient.state;
                          const zip = selectedClient.zipcode || selectedClient.zip || selectedClient.zip_code;
                          const hasAddress = !!(street || city || state || zip);
                          const singleLine = [street, line2, [city, state].filter(Boolean).join(', '), zip].filter(Boolean).join(' ');
                          const multiLine = [street, line2, [city, state].filter(Boolean).join(', ') + (zip ? ` ${zip}` : '')].filter(Boolean);
                          return (
                            <HoverCard openDelay={200} closeDelay={150}>
                              <HoverCardTrigger asChild>
                                <button
                                  type="button"
                                  data-testid="client-address-hover-trigger"
                                  className={`p-1 rounded transition-colors ${hasAddress ? 'text-teal-600 hover:bg-teal-50 hover:text-teal-700' : 'text-gray-300 cursor-help'}`}
                                  title={hasAddress ? 'Hover to see address' : 'No address on file'}
                                  aria-label="Client address"
                                >
                                  <HomeIcon className="h-4 w-4" />
                                </button>
                              </HoverCardTrigger>
                              <HoverCardContent
                                side="right"
                                align="start"
                                className="w-72"
                                data-testid="client-address-hover-content"
                              >
                                {hasAddress ? (
                                  <div className="space-y-2">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                      Address
                                    </div>
                                    <div className="text-sm text-gray-800 space-y-0.5">
                                      {multiLine.map((line, i) => (
                                        <div key={i}>{line}</div>
                                      ))}
                                    </div>
                                    <div className="pt-1">
                                      <button
                                        type="button"
                                        data-testid="client-address-copy-btn"
                                        className="w-full text-xs px-2 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 transition-colors"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            if (navigator?.clipboard?.writeText) {
                                              await navigator.clipboard.writeText(singleLine);
                                              toast.success('Address copied');
                                            } else {
                                              // Fallback for environments where the
                                              // async Clipboard API is unavailable.
                                              const ta = document.createElement('textarea');
                                              ta.value = singleLine;
                                              document.body.appendChild(ta);
                                              ta.select();
                                              document.execCommand('copy');
                                              document.body.removeChild(ta);
                                              toast.success('Address copied');
                                            }
                                          } catch (err) {
                                            toast.error('Failed to copy address');
                                          }
                                        }}
                                      >
                                        Copy address
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-500 italic">
                                    No address on file for this client.
                                  </div>
                                )}
                              </HoverCardContent>
                            </HoverCard>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {(user?.can_edit_client_status_insurance || hasPermission('edit_client_insurance') || isSuperAdmin(user)) ? (
                          <Select 
                            value={selectedClient.status || 'active'}
                            onValueChange={(value) => handleUpdateClientStatus(selectedClient.id, value)}
                          >
                            <SelectTrigger className="w-32 h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CLIENT_STATUSES.filter(s => {
                                if (s.value === 'archived' && !user.can_archive_clients && !isSuperAdmin(user)) return false;
                                // Hide inactive unless user is supervisor, super admin, or waitlist manager
                                if (s.value === 'inactive' && !user?.is_supervisor && !user?.is_waitlist_manager && !isSuperAdmin(user)) return false;
                                return true;
                              }).map(status => (
                                <SelectItem key={status.value} value={status.value}>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={getStatusBadgeColor(selectedClient.status)}>
                            {formatClientStatus(selectedClient.status)}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Inactive reason banner */}
                    {selectedClient.status === 'inactive' && selectedClient.inactive_reason && (
                      <div className="mx-0 mb-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200" data-testid="inactive-reason-banner">
                        <div className="flex items-start gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <div className="text-sm">
                            <p className="font-semibold text-red-800">Inactive Reason</p>
                            <p className="text-red-700 mt-0.5">{selectedClient.inactive_reason}</p>
                            {(selectedClient.inactivated_by || selectedClient.inactivated_at) && (
                              <p className="text-red-500 text-xs mt-1">
                                {selectedClient.inactivated_by && (() => {
                                  const setByUser = users?.find(u => u.id === selectedClient.inactivated_by);
                                  return setByUser ? `Set by ${setByUser.full_name || setByUser.first_name + ' ' + setByUser.last_name}` : '';
                                })()}
                                {selectedClient.inactivated_by && selectedClient.inactivated_at && ' \u2022 '}
                                {selectedClient.inactivated_at && new Date(selectedClient.inactivated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Two-column content area */}
                    {(() => {
                      // Prepare care team data - only include ACTIVE enrollments
                      const activeEnrollments = clientEnrollments.filter(e => 
                        (e.status === 'active' || e.status === 'assigned') && !e.disenrollment_pending
                      );
                      const providerGroups = {};
                      activeEnrollments.forEach(enrollment => {
                        const providerId = enrollment.provider_id;
                        if (!providerGroups[providerId]) {
                          const providerUser = users?.find(u => u.id === providerId);
                          const credentials = providerUser?.credentials || '';
                          const providerName = enrollment.provider_name || providerUser?.full_name || 'Unknown';
                          providerGroups[providerId] = {
                            provider_id: providerId,
                            provider_name: providerName,
                            provider_credentials: credentials,
                            enrollments: []
                          };
                        }
                        providerGroups[providerId].enrollments.push(enrollment);
                      });
                      const providerList = Object.values(providerGroups);
                      
                      return (
                        <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                          {/* Row 2: Gender/DOB/Client since | First Provider */}
                          <div className="text-sm text-gray-500 flex items-center gap-2 flex-wrap">
                            {selectedClient.gender && <span>{selectedClient.gender.charAt(0).toUpperCase() + selectedClient.gender.slice(1)}</span>}
                            {selectedClient.gender && selectedClient.date_of_birth && <span>·</span>}
                            {selectedClient.date_of_birth && (
                              <span>
                                {formatDateMMDDYYYY(selectedClient.date_of_birth)} ({calculateAge(selectedClient.date_of_birth)}y)
                              </span>
                            )}
                            {(selectedClient.gender || selectedClient.date_of_birth) && <span>·</span>}
                            <span className="flex items-center gap-1">
                              Client since {new Date(selectedClient.created_at).toLocaleDateString()}
                              {(isSuperAdmin(user) || userRoleName === 'admin') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-4 w-4 p-0 text-gray-400 hover:text-gray-600"
                                  onClick={() => {
                                    setEditingClientSinceDate(selectedClient.created_at ? new Date(selectedClient.created_at).toISOString().split('T')[0] : '');
                                    setShowEditClientSinceDialog(true);
                                  }}
                                  title="Edit client start date"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                              )}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="font-medium text-gray-600">Care Team:</span>
                            {providerList[0] ? (
                              <div className="group relative flex items-center gap-1 cursor-default">
                                <span className="font-medium text-gray-800">
                                  {providerList[0].provider_name}{providerList[0].provider_credentials ? `, ${providerList[0].provider_credentials}` : ''}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ({providerList[0].enrollments.length} service{providerList[0].enrollments.length !== 1 ? 's' : ''})
                                </span>
                                <div className="hidden group-hover:block absolute left-0 top-full mt-1 z-50 bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 min-w-[220px]">
                                  <div className="font-semibold mb-2 border-b border-gray-700 pb-1">{providerList[0].provider_name} - Services</div>
                                  <div className="space-y-1.5">
                                    {providerList[0].enrollments.map(enrollment => (
                                      <div key={enrollment.id} className="flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full ${enrollment.disenrollment_pending ? 'bg-amber-400' : enrollment.status === 'active' ? 'bg-green-400' : 'bg-blue-400'}`}></span>
                                        <span>{enrollment.service_name}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">No providers assigned</span>
                            )}
                          </div>
                          
                          {/* Row 3: Email/Phone | Second Provider */}
                          <div className="flex items-center gap-4 text-sm">
                            {selectedClient.email ? (
                              <a href={`mailto:${selectedClient.email}`} className="flex items-center gap-1 text-gray-600 hover:text-teal-600 transition-colors">
                                <Mail className="h-4 w-4" />
                                <span className="truncate max-w-[200px]">{selectedClient.email}</span>
                              </a>
                            ) : (
                              <span className="flex items-center gap-1 text-gray-400"><Mail className="h-4 w-4" /> No email</span>
                            )}
                            {selectedClient.phone ? (
                              <a href={`tel:${selectedClient.phone}`} className="flex items-center gap-1 text-gray-600 hover:text-teal-600 transition-colors">
                                <Phone className="h-4 w-4" />
                                <span>{selectedClient.phone}</span>
                              </a>
                            ) : (
                              <span className="flex items-center gap-1 text-gray-400"><Phone className="h-4 w-4" /> No phone</span>
                            )}
                            {selectedClient.phone_secondary && (
                              <a href={`tel:${selectedClient.phone_secondary}`} className="flex items-center gap-1 text-gray-600 hover:text-teal-600 transition-colors">
                                <Phone className="h-4 w-4" />
                                <span>{selectedClient.phone_secondary}</span>
                                <span className="text-xs text-gray-400">(alt)</span>
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm pl-6">
                            {providerList[1] && (
                              <div className="group relative flex items-center gap-1 cursor-default">
                                <span className="font-medium text-gray-800">
                                  {providerList[1].provider_name}{providerList[1].provider_credentials ? `, ${providerList[1].provider_credentials}` : ''}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ({providerList[1].enrollments.length} service{providerList[1].enrollments.length !== 1 ? 's' : ''})
                                </span>
                                <div className="hidden group-hover:block absolute left-0 top-full mt-1 z-50 bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 min-w-[220px]">
                                  <div className="font-semibold mb-2 border-b border-gray-700 pb-1">{providerList[1].provider_name} - Services</div>
                                  <div className="space-y-1.5">
                                    {providerList[1].enrollments.map(enrollment => (
                                      <div key={enrollment.id} className="flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full ${enrollment.disenrollment_pending ? 'bg-amber-400' : enrollment.status === 'active' ? 'bg-green-400' : 'bg-blue-400'}`}></span>
                                        <span>{enrollment.service_name}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Row 4: Labels | Third Provider */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <Tag className="h-4 w-4 text-gray-400" />
                            {(selectedClient.labels || []).map(labelId => {
                              const label = clientLabels.find(l => l.id === labelId);
                              if (!label) return null;
                              return (
                                <span
                                  key={labelId}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                  style={{ backgroundColor: label.color + 'cc' }}
                                >
                                  {label.name}
                                  <button
                                    type="button"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await axios.delete(`${API}/clients/${selectedClient.id}/labels/${labelId}`);
                                        setSelectedClient(prev => ({ ...prev, labels: (prev.labels || []).filter(l => l !== labelId) }));
                                        setClients(prev => prev.map(c => c.id === selectedClient.id ? { ...c, labels: (c.labels || []).filter(l => l !== labelId) } : c));
                                        toast.success('Label removed');
                                      } catch (error) { toast.error('Failed to remove label'); }
                                    }}
                                    className="hover:bg-white/20 rounded-full p-0.5"
                                    title="Remove label"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </span>
                              );
                            })}
                            {clientLabels.length > 0 && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button type="button" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 border border-dashed border-gray-300">
                                    <Plus className="h-3 w-3" />Add
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-48 p-2" align="start">
                                  <div className="space-y-1">
                                    {clientLabels.filter(label => !(selectedClient.labels || []).includes(label.id)).map(label => (
                                      <button
                                        key={label.id}
                                        type="button"
                                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-gray-100 text-left"
                                        onClick={async () => {
                                          try {
                                            await axios.post(`${API}/clients/${selectedClient.id}/labels/${label.id}`);
                                            setSelectedClient(prev => ({ ...prev, labels: [...(prev.labels || []), label.id] }));
                                            setClients(prev => prev.map(c => c.id === selectedClient.id ? { ...c, labels: [...(c.labels || []), label.id] } : c));
                                            toast.success(`Label "${label.name}" added`);
                                          } catch (error) { toast.error('Failed to add label'); }
                                        }}
                                      >
                                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: label.color }} />
                                        {label.name}
                                      </button>
                                    ))}
                                    {clientLabels.filter(l => !(selectedClient.labels || []).includes(l.id)).length === 0 && (
                                      <p className="text-xs text-gray-500 text-center py-2">All labels assigned</p>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                            {clientLabels.length === 0 && (selectedClient.labels || []).length === 0 && (
                              <span className="text-xs text-gray-400">No labels</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm pl-6">
                            {providerList[2] && (
                              <div className="group relative flex items-center gap-1 cursor-default">
                                <span className="font-medium text-gray-800">
                                  {providerList[2].provider_name}{providerList[2].provider_credentials ? `, ${providerList[2].provider_credentials}` : ''}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ({providerList[2].enrollments.length} service{providerList[2].enrollments.length !== 1 ? 's' : ''})
                                </span>
                                <div className="hidden group-hover:block absolute left-0 top-full mt-1 z-50 bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 min-w-[220px]">
                                  <div className="font-semibold mb-2 border-b border-gray-700 pb-1">{providerList[2].provider_name} - Services</div>
                                  <div className="space-y-1.5">
                                    {providerList[2].enrollments.map(enrollment => (
                                      <div key={enrollment.id} className="flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full ${enrollment.disenrollment_pending ? 'bg-amber-400' : enrollment.status === 'active' ? 'bg-green-400' : 'bg-blue-400'}`}></span>
                                        <span>{enrollment.service_name}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                            {/* Additional providers beyond 3 */}
                            {providerList.slice(3).map((group) => (
                              <div key={group.provider_id} className="group relative flex items-center gap-1 cursor-default">
                                <span className="font-medium text-gray-800">
                                  {group.provider_name}{group.provider_credentials ? `, ${group.provider_credentials}` : ''}
                                </span>
                                <span className="text-xs text-gray-500">({group.enrollments.length})</span>
                              </div>
                            ))}
                            {/* Add Provider Button */}
                            {hasPermission('manage_service_enrollments') && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={async () => {
                                  setSelectedProvidersForAssignment(selectedClient.assigned_providers || []);
                                  if (!providers || providers.length === 0) {
                                    try {
                                      const response = await axios.get(`${API}/providers`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                      setProviders(response.data);
                                    } catch (error) { console.error('Failed to fetch providers:', error); toast.error('Failed to load providers'); }
                                  }
                                  try {
                                    const servicesResponse = await axios.get(`${API}/treatment-planning/service-types`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                    setAvailableServices(servicesResponse.data);
                                  } catch (error) { console.error('Failed to fetch services:', error); }
                                  try {
                                    // Fetch with include_inactive=true so the dialog's
                                    // existingEnrollments mirrors the chart-header
                                    // `clientEnrollments` set (which also includes
                                    // past/pending records).  Without this the two
                                    // sources of truth drift apart and the same
                                    // client can show different enrollment lists in
                                    // the chart header vs the Edit Client dialog.
                                    const enrollmentsResponse = await axios.get(`${API}/clients/${selectedClient.id}/service-enrollments?include_inactive=true`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                    const enrollments = enrollmentsResponse.data;
                                    setExistingEnrollments(enrollments);
                                    // Also refresh the chart-header context so the
                                    // two panels are guaranteed to agree.
                                    if (enrollmentsCacheRef?.current) {
                                      enrollmentsCacheRef.current[selectedClient.id] = enrollments;
                                    }
                                    setClientEnrollments(enrollments);
                                    const assignments = {};
                                    enrollments.forEach(enrollment => {
                                      if (enrollment.status === 'active' || enrollment.status === 'assigned') {
                                        if (!assignments[enrollment.provider_id]) assignments[enrollment.provider_id] = [];
                                        assignments[enrollment.provider_id].push(enrollment.service_type);
                                      }
                                    });
                                    setProviderServiceAssignments(assignments);
                                  } catch (error) { console.error('Failed to fetch enrollments:', error); setExistingEnrollments([]); setProviderServiceAssignments({}); }
                                  setShowAssignProviders(true);
                                }}
                                className="h-6 px-2 text-teal-600"
                              >
                                <Plus className="h-3 w-3 mr-1" />Manage
                              </Button>
                            )}
                          </div>
                          
                          {/* Row 5: Insurance (with divider) */}
                          <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                            <CreditCard className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-600">Insurance:</span>
                            {(() => {
                              const activeInsurances = (selectedClientInsurances || []).filter(ins => ins.status === 'active').sort((a, b) => a.priority - b.priority);
                              if (activeInsurances.length === 0) return <span className="text-sm">Self Pay</span>;
                              const primary = activeInsurances[0];
                              const priorityLabels = {1: 'Primary', 2: 'Secondary', 3: 'Tertiary'};
                              return (
                                <div className="group relative flex items-center gap-2 cursor-default">
                                  <span className="text-sm">{primary.company}{primary.plan ? ` - ${primary.plan}` : ''}</span>
                                  {(() => {
                                    // Use client-level status (kept in sync by 271 processing)
                                    // falling back to insurance-level status
                                    const headerStatus = selectedClient?.primary_eligibility_status || primary.eligibility_status;
                                    if (!headerStatus || headerStatus === 'not_checked') return null;
                                    const { icon, color, bg, label } = getEligibilityDisplay(headerStatus);
                                    return (
                                      <span className={`inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full font-medium ${bg} ${color}`}>
                                        {icon} {label}
                                      </span>
                                    );
                                  })()}
                                  {activeInsurances.length > 1 && <span className="text-xs text-gray-500">+{activeInsurances.length - 1} more</span>}
                                  <div className="hidden group-hover:block absolute left-0 top-full mt-1 z-50 bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 min-w-[280px]">
                                    <div className="font-semibold mb-2 border-b border-gray-700 pb-1">Insurance Details</div>
                                    <div className="space-y-3">
                                      {activeInsurances.map((ins, idx) => (
                                        <div key={ins.id} className={idx > 0 ? 'pt-2 border-t border-gray-700' : ''}>
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="font-medium">{priorityLabels[ins.priority] || `#${ins.priority}`}</span>
                                            {(() => {
                                              const insStatus = ins.priority === 1
                                                ? (selectedClient?.primary_eligibility_status || ins.eligibility_status)
                                                : ins.eligibility_status;
                                              const { icon, label } = getEligibilityDisplay(insStatus);
                                              return (
                                                <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/10 text-white">
                                                  {icon} {label}
                                                </span>
                                              );
                                            })()}
                                          </div>
                                          <div className="text-gray-300">{ins.company}</div>
                                          {ins.plan && <div className="text-gray-400">Plan: {ins.plan}</div>}
                                          {ins.member_id && <div className="text-gray-400">Member ID: {ins.member_id}</div>}
                                          {ins.group_number && <div className="text-gray-400">Group: {ins.group_number}</div>}
                                          {/* Show eligibility dates inline in the popup */}
                                          {ins.priority === 1 && (selectedClient?.last_eligibility_dos || selectedClient?.last_eligibility_check) && (() => {
                                            const fmt = (d) => {
                                              if (!d) return null;
                                              const dt = new Date(String(d).substring(0,10) + 'T00:00:00');
                                              return isNaN(dt) ? null : dt.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                                            };
                                            return (
                                              <div className="text-gray-400 text-[10px] mt-1 space-y-0.5">
                                                {fmt(selectedClient.last_eligibility_dos) && (
                                                  <div className="flex items-center justify-between gap-3">
                                                    <span>Checking for:</span><span>{fmt(selectedClient.last_eligibility_dos)}</span>
                                                  </div>
                                                )}
                                                {fmt(selectedClient.last_eligibility_check) && (
                                                  <div className="flex items-center justify-between gap-3">
                                                    <span>Checked:</span><span>{fmt(selectedClient.last_eligibility_check)}</span>
                                                  </div>
                                                )}
                                                {fmt(selectedClient.primary_effective_date) && (
                                                  <div className="flex items-center justify-between gap-3">
                                                    <span>Coverage:</span>
                                                    <span>{fmt(selectedClient.primary_effective_date)}{selectedClient.primary_end_date ? ` → ${fmt(selectedClient.primary_end_date)}` : ''}</span>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                          <div className="pt-2 border-t border-gray-200">
                            {/* Empty cell to maintain grid alignment */}
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Schedule Next Button */}
                    {(() => {
                      const schedulers = getSchedulersForProvider(user.id);
                      if (schedulers.length === 0) return null;
                      return (
                        <div className="pt-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedNote({ client_id: selectedClient.id, client_name: formatClientName(selectedClient) });
                              setShowScheduleNextDialog(true);
                              setScheduleNextData({ timeframe: '2_weeks', customDate: '', notes: '' });
                            }}
                            className="text-purple-600 border-purple-300 hover:bg-purple-50"
                          >
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            Schedule Next Appointment
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>



              {/* Banner Alerts Display */}
              {(() => {
                const bannerAlerts = clientAlerts.filter(a => a.is_banner);
                if (bannerAlerts.length === 0) return null;
                
                return (
                  <div className="space-y-2 mb-4">
                    {bannerAlerts.map(alert => (
                      <Alert key={alert.id} className="bg-amber-50 border-amber-300">
                        <AlertDescription>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <Shield className="h-4 w-4 text-amber-700" />
                                <span className="text-xs font-semibold text-amber-800 uppercase">Banner Alert</span>
                              </div>
                              <p className="text-sm font-medium text-amber-900 whitespace-pre-wrap">{alert.content}</p>
                              <div className="flex items-center space-x-3 mt-1 text-xs text-amber-700">
                                <span>By: {alert.created_by_name}</span>
                                <span>•</span>
                                <span>{new Date(alert.created_at).toLocaleDateString()}</span>
                                {alert.tagged_users?.length > 0 && (
                                  <>
                                    <span>•</span>
                                    <span>Tagged: {alert.tagged_users.length} user(s)</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 ml-4">
                              {!alert.read_by?.[user.id] && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/alerts/${alert.id}/mark-read`);
                                      fetchClientAlerts(selectedClient.id);
                                      refreshAlerts();
                                      toast.success("Marked as read");
                                    } catch (error) {
                                      toast.error("Failed to mark as read");
                                    }
                                  }}
                                >
                                  Mark Read
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => {
                                  setClientDetailTab("alerts");
                                }}
                                title="View all alerts"
                              >
                                <Bell className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                );
              })()}

              {/* Authorization Warnings */}
              {(() => {
                const lowAuthWarnings = (clientAuthorizations || []).filter(auth => 
                  auth.status === 'low_units' || auth.status === 'warning' || auth.status === 'depleted' || auth.status === 'expired'
                );
                
                if (lowAuthWarnings.length === 0) return null;
                
                // Group by service type and only show most recent for each
                const warningsByServiceType = {};
                lowAuthWarnings.forEach(auth => {
                  const serviceTypeKey = auth.service_type;
                  if (!warningsByServiceType[serviceTypeKey] || 
                      new Date(auth.created_at) > new Date(warningsByServiceType[serviceTypeKey].created_at)) {
                    warningsByServiceType[serviceTypeKey] = auth;
                  }
                });
                
                const mostRecentWarnings = Object.values(warningsByServiceType);
                
                return (
                  <div className="space-y-2">
                    {mostRecentWarnings.map(auth => {
                      // Get service type name - try serviceTypes first since they should be loaded now
                      const serviceType = serviceTypes.find(st => st.id === auth.service_type);
                      const serviceTypeName = serviceType?.display_name || 
                                            serviceType?.name ||
                                            'Service';
                      
                      
                      return (
                        <Alert 
                          key={auth.id} 
                          className={
                            auth.status === 'depleted' || auth.status === 'expired' ? 'border-red-400 bg-red-50' :
                            auth.status === 'low_units' ? 'border-red-400 bg-red-50' :
                            'border-yellow-400 bg-yellow-50'
                          }
                        >
                          <AlertCircle className={
                            auth.status === 'depleted' || auth.status === 'expired' ? 'h-4 w-4 text-red-600' :
                            auth.status === 'low_units' ? 'h-4 w-4 text-red-600' :
                            'h-4 w-4 text-yellow-600'
                          } />
                          <AlertTitle className="font-semibold">
                            {auth.status === 'depleted' ? '⚫ Authorization Depleted' :
                             auth.status === 'expired' ? '⚫ Authorization Expired' :
                             auth.status === 'low_units' ? 'Low Authorization Units' :
                             '⚠️ Authorization Warning'}
                          </AlertTitle>
                          <AlertDescription>
                            <div className="flex items-center justify-between">
                              <div>
                                <strong>{serviceTypeName}</strong> (Auth #{auth.authorization_number})
                                {auth.status === 'depleted' ? (
                                  <span className="ml-2">has <strong className="text-red-600">no remaining units</strong>. New authorization required to schedule appointments.</span>
                                ) : auth.status === 'expired' ? (
                                  <span className="ml-2">expired on <strong>{fmtLocalDate(auth.end_date)}</strong>. New authorization required to schedule appointments.</span>
                                ) : auth.status === 'low_units' ? (
                                  <span className="ml-2">has only <strong className="text-red-600">{auth.remaining_units} units</strong> remaining (out of {auth.total_units}).</span>
                                ) : (
                                  <span className="ml-2">has <strong className="text-yellow-600">{auth.remaining_units} units</strong> remaining or expires in <strong>{auth.days_until_expiration} days</strong>.</span>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setClientDetailTab("authorizations");
                                }}
                              >
                                View Authorizations
                              </Button>
                            </div>
                          </AlertDescription>
                        </Alert>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Navigation Tabs for Client Details */}
              <Tabs value={clientDetailTab} onValueChange={(newTab) => {
                setClientDetailTab(newTab);
                // Force billing data refresh when switching to billing tab
                if (newTab === 'billing') {
                  setBillingRefreshKey(k => k + 1);
                }
                // Update URL with tab parameter using replaceState to avoid
                // triggering React Router navigation (which can close/reopen the dialog)
                if (selectedClient?.id) {
                  const url = new URL(window.location.href);
                  url.searchParams.set('tab', newTab);
                  window.history.replaceState({}, '', `/client/${selectedClient.id}?${url.searchParams.toString()}`);
                }
              }} className="w-full">
                <TabsList className="flex flex-wrap w-full h-auto gap-1 p-1">
                  <TabsTrigger value="overview" className="flex-shrink-0">Overview</TabsTrigger>
                  <TabsTrigger value="alerts" className="flex-shrink-0">
                    Alerts
                    {unreadAlertCounts && unreadAlertCounts[selectedClient?.id] > 0 && (
                      <Badge className="ml-1 bg-amber-500 text-white text-xs px-1.5 py-0.5">
                        {unreadAlertCounts && unreadAlertCounts[selectedClient?.id]}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="diagnoses" className="flex-shrink-0">Diagnoses</TabsTrigger>
                  <TabsTrigger value="medications" className="flex-shrink-0">Meds & Vitals</TabsTrigger>
                  <TabsTrigger value="contacts" className="flex-shrink-0">Contacts</TabsTrigger>
                  <TabsTrigger value="treatment-plans" className="flex-shrink-0">Plan</TabsTrigger>
                  <TabsTrigger value="authorizations" className="flex-shrink-0">Auths</TabsTrigger>
                  <TabsTrigger value="assessments" className="flex-shrink-0">Assessments</TabsTrigger>
                  <TabsTrigger value="groups" className="flex-shrink-0">Groups</TabsTrigger>
                  <TabsTrigger value="appointments" className="flex-shrink-0">Appts</TabsTrigger>
                  <TabsTrigger value="notes" className="flex-shrink-0">Documents</TabsTrigger>
                  {/* Supervision tab - visible to supervisors, admins, QA auditors, and providers (supervisees) */}
                  {(userRoleName === 'supervisor' || user?.is_supervisor || userRoleName === 'admin' || isSuperAdmin(user) || userRoleName === 'provider' || user?.can_qa_audit) && (
                    <TabsTrigger value="supervision" className="flex-shrink-0">
                      Supervision
                      {/* Show badge if provider has pending actions */}
                      {clientMDTReviews?.some(review => 
                        review.sections?.some(s => 
                          s.action_status === 'corrective_action_in_progress' && 
                          s.supervisor_signed_by && 
                          !s.clinician_signed_by &&
                          (s.assigned_clinician_id === user?.id || review.assigned_provider_id === user?.id)
                        )
                      ) && (
                        <Badge className="ml-1 bg-orange-500 text-white text-xs px-1.5 py-0.5">!</Badge>
                      )}
                    </TabsTrigger>
                  )}
                  {billingSettings.billing_enabled && (user?.is_billing_manager || user?.is_biller || isSuperAdmin(user)) && (
                    <TabsTrigger value="billing" className="flex-shrink-0" data-testid="client-chart-billing-tab">Billing</TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Clinical Summary</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-sm font-medium text-gray-500">Active Diagnoses</Label>
                            <p className="text-sm">{clientDiagnoses.filter(d => d.is_active === true).length} active</p>
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-gray-500">Current Medications</Label>
                            <p className="text-sm">{clientMedications.filter(m => m.is_active === true).length} active</p>
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-gray-500">Active Groups</Label>
                            <p className="text-sm">{selectedClient.group_memberships?.length || 0} active</p>
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-gray-500">Authorizations</Label>
                            <p className="text-sm">{(clientAuthorizations || []).filter(a => a.is_active).length} active</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-lg">Insurance Coverage</CardTitle>
                        {(user?.can_edit_client_status_insurance || hasPermission('edit_client_insurance') || isSuperAdmin(user)) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenInsuranceManagement(selectedClient)}
                            className="flex items-center"
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Manage Insurance
                          </Button>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {selectedClientInsurances.filter(ins => ins.status === 'active').length === 0 ? (
                            <div className="border rounded-lg p-3 bg-gray-50 border-gray-200">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-gray-700">Self Pay</span>
                              </div>
                            </div>
                          ) : (
                            selectedClientInsurances
                              .filter(ins => ins.status === 'active')
                              .sort((a, b) => a.priority - b.priority)
                              .map((insurance, insIdx) => {
                                const priorityLabels = {1: 'Primary', 2: 'Secondary', 3: 'Tertiary', 4: 'Quaternary'};
                                // Resolve member ID from decrypted top-level fields (array member_id is encrypted)
                                const memberIdFields = ['primary_member_id', 'secondary_member_id', 'tertiary_member_id'];
                                const resolvedMemberId = selectedClient[memberIdFields[Math.min(insIdx, 2)]]
                                  || insurance.member_id  // Fallback: read directly from insurances[] if top-level is empty
                                  || '';
                                // Eligibility data for this plan (use eligibilityDetail for primary, fallback for others)
                                const elig = eligibilityDetailMap[insIdx] || null;
                                const eligStatus = insIdx === 0
                                  ? (selectedClient.primary_eligibility_status)
                                  : (insurance.eligibility_status || null);
                                // Discrepancy for member ID/name
                                const memberDisc = elig?.discrepancies?.find(d =>
                                  ['Member ID', 'full_name', 'Name'].includes(d.field)
                                );
                                // Status colors
                                const { icon: eligIcon, color: eligColor, bg: eligBg, label: eligLabel } = getEligibilityDisplay(eligStatus);
                                const isVerified = eligStatus === 'verified' || eligStatus === 'active';
                                const isRejected = eligStatus === 'rejected';
                                const isPending  = ['pending','queued','submitted'].includes(eligStatus);
                                const isInactive = eligStatus === 'inactive';
                                // "In flight" = a 270 was actually submitted and we're waiting for 271
                                // 'pending' alone is NOT enough — it's also the old default for unchecked clients
                                const isInFlight = ['queued', 'submitted', 'processing'].includes(eligStatus);
                                // Helper: safe date formatter — returns null if date is invalid
                                const safeDate = (d) => {
                                  if (!d || d === 'Invalid Date' || d === 'null' || d === 'undefined') return null;
                                  // Strip time/timezone so we always parse just the date portion
                                  const dateOnly = String(d).split('T')[0].split(' ')[0];
                                  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null;
                                  const dt = new Date(dateOnly + 'T00:00:00');
                                  if (isNaN(dt.getTime())) return null;
                                  return dt.toLocaleDateString();
                                };

                                return (
                                  <div key={insurance.id} className="border rounded-lg overflow-hidden">
                                    {/* ── Row 1: Priority label + status badge ── */}
                                    <div className={`flex items-center justify-between px-3 py-2 ${
                                      isVerified ? 'bg-green-50 border-b border-green-100' :
                                      isRejected ? 'bg-red-50 border-b border-red-100' :
                                      isPending  ? 'bg-yellow-50 border-b border-yellow-100' :
                                      'bg-gray-50 border-b border-gray-100'
                                    }`}>
                                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {priorityLabels[insurance.priority] || `Priority ${insurance.priority}`} Insurance
                                        <span className="ml-1 text-gray-400 font-normal normal-case">(#{insurance.priority})</span>
                                      </span>
                                      <div className="flex items-center gap-1.5">
                                        {/* Yellow alert for verified-with-discrepancies */}
                                        {safeEligibilityDetail?.discrepancies?.length > 0 && isVerified && (
                                          <span
                                            className="text-amber-500 text-xs cursor-pointer"
                                            title={`${safeEligibilityDetail.discrepancies.length} discrepancy(ies) — payer data differs from EMR`}
                                            onClick={() => { setDiscrepancyData({ checkId: elig?.id, clientId: selectedClient.id, clientName: selectedClient.full_name, discrepancies: safeEligibilityDetail.discrepancies }); setShowDiscrepancyReview(true); }}
                                          >
                                            ⚠ Mismatch
                                          </span>
                                        )}
                                      <span
                                        className={`text-xs font-semibold px-2 py-0.5 rounded-full cursor-help inline-flex items-center gap-1 ${
                                          isVerified && safeEligibilityDetail?.discrepancies?.length > 0 ? 'bg-amber-100 text-amber-800' :
                                          isVerified ? 'bg-green-100 text-green-800' :
                                          isRejected ? 'bg-red-100 text-red-700' :
                                          isPending  ? 'bg-yellow-100 text-yellow-800' :
                                          isInactive ? 'bg-gray-200 text-gray-600' :
                                          'bg-gray-100 text-gray-500'
                                        }`}
                                        title={eligibilityTooltip(selectedClient, elig)}
                                      >
                                        {safeEligibilityDetail?.discrepancies?.length > 0 && isVerified ? (
                                          <>
                                            <AlertTriangle className="h-3 w-3" />
                                            <span>Verified with Discrepancies</span>
                                          </>
                                        ) : (
                                          <>
                                            {eligIcon}
                                            <span>
                                              {eligLabel}
                                              {isVerified && elig?.coverage_effective_date ? ` · Eff ${elig.coverage_effective_date}` : ''}
                                            </span>
                                          </>
                                        )}
                                      </span>
                                      </div>
                                    </div>

                                    {/* ── Row 2: Insurance name + member ID (left) — Date picker + Verify (right) ── */}
                                    <div className="px-3 py-2">
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                        <p className="font-semibold text-sm">{insurance.company}{insurance.plan ? ` — ${insurance.plan}` : ''}</p>
                                        <div className="flex items-center gap-1 mt-0.5">
                                            {resolvedMemberId ? (
                                              <span className="flex items-center gap-1">
                                                <span className="text-xs text-gray-500 font-mono">{resolvedMemberId}</span>
                                                <button
                                                  title="Copy member ID"
                                                  className="text-gray-300 hover:text-gray-600 transition-colors"
                                                  onClick={() => {
                                                    navigator.clipboard.writeText(resolvedMemberId);
                                                    toast.success('Member ID copied');
                                                  }}
                                                >
                                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                                </button>
                                                {elig?.payer_member_id && elig.payer_member_id !== resolvedMemberId &&
                                                  // Both payer_ids must be set AND match — null means unknown, don't show
                                                  elig.payer_id && insurance.payer_id &&
                                                  elig.payer_id === insurance.payer_id && (
                                                  <span className="text-xs text-amber-600 ml-1">(Payer: {elig.payer_member_id})</span>
                                                )}
                                              </span>
                                            ) : (
                                              <span className="text-xs text-gray-400 italic">No member ID on file</span>
                                            )}
                                            {memberDisc && (
                                              <span
                                                className="text-amber-500 cursor-help text-xs"
                                                title={`Payer has: "${memberDisc.payer}". Click Sync to update.`}
                                                onClick={() => {
                                                  setDiscrepancyData({ checkId: elig?.id, clientId: selectedClient.id, clientName: selectedClient.full_name, discrepancies: elig?.discrepancies || [] });
                                                  setShowDiscrepancyReview(true);
                                                }}
                                              >
                                                ⚠ Mismatch
                                              </span>
                                            )}
                                            {insurance.group_number && resolvedMemberId && (
                                              <span className="text-xs text-gray-400">· Group: {insurance.group_number}</span>
                                            )}
                                          </div>
                                          {safeDate(insurance.start_date) && (
                                            <p className="text-xs text-gray-400">
                                              Eff: {safeDate(insurance.start_date)}
                                              {safeDate(insurance.end_date) && (
                                                <span className={`ml-2 ${new Date(insurance.end_date + 'T00:00:00') < new Date() ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                                                  · Exp: {safeDate(insurance.end_date)}
                                                  {new Date(insurance.end_date + 'T00:00:00') < new Date() && ' (expired)'}
                                                </span>
                                              )}
                                            </p>
                                          )}
                                          {!safeDate(insurance.start_date) && safeDate(insurance.end_date) && (
                                            <p className={`text-xs ${new Date(insurance.end_date + 'T00:00:00') < new Date() ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                                              Exp: {safeDate(insurance.end_date)}
                                              {new Date(insurance.end_date + 'T00:00:00') < new Date() && ' (expired)'}
                                            </p>
                                          )}
                                          {/* Last checked timestamp — pulled from the per-plan eligibility
                                              detail (primary reads from elig map; secondary/tertiary falls
                                              back to insurance.eligibility_checked_at which is now set by
                                              the 271 persistence path). */}
                                          {(() => {
                                            const ts = elig?.response_received_at || elig?.response_date
                                              || (insIdx > 0 ? insurance.eligibility_checked_at : null)
                                              || (insIdx === 0 ? selectedClient?.last_eligibility_check : null);
                                            if (!ts) return null;
                                            let dateStr = null;
                                            let fullTs = null;
                                            try {
                                              const dt = new Date(ts);
                                              if (!isNaN(dt.getTime())) {
                                                dateStr = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
                                                fullTs = dt.toLocaleString();
                                              }
                                            } catch (e) { /* ignore */ }
                                            if (!dateStr) return null;
                                            return (
                                              <p
                                                className="text-xs text-gray-400 mt-0.5"
                                                title={fullTs || ''}
                                                data-testid={`insurance-last-checked-${insurance.id}`}
                                              >
                                                Last checked {dateStr}
                                              </p>
                                            );
                                          })()}
                                        </div>
                                        {/* Right column: Date picker + Verify button */}
                                        <div className="flex items-center gap-1.5 shrink-0">
                                          <input
                                            type="date"
                                            value={eligibilityCheckDOS}
                                            onChange={e => setEligibilityCheckDOS(e.target.value)}
                                            className="text-xs border border-gray-200 rounded px-1.5 py-0.5 h-6 text-gray-600 w-[120px]"
                                            title="Specific date of service — leave blank for general eligibility check (recommended)"
                                          />
                                          <button
                                            className="text-xs px-2 py-0.5 h-6 rounded bg-teal-600 hover:bg-teal-700 text-white font-medium"
                                            data-testid="verify-eligibility-btn"
                                            onClick={() => handleEligibilityCheck(selectedClient.id, selectedClient.full_name, { dateOfService: eligibilityCheckDOS || undefined, insuranceIndex: insIdx })}
                                          >
                                            Verify
                                          </button>
                                        </div>
                                      </div>

                                      {/* ── Row 3: Benefits summary (only when verified + data available) ── */}
                                      {isVerified && (elig?.copay != null || selectedClient?.eligibility_copay_amounts || elig?.deductible || selectedClient?.eligibility_deductible || elig?.coinsurance || selectedClient?.eligibility_coinsurance) && (
                                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-xs border-t border-gray-100 pt-2">
                                          {(() => {
                                            const copayAmt = elig?.copay ?? (selectedClient?.eligibility_copay_amounts ? Object.values(selectedClient.eligibility_copay_amounts)[0] : null);
                                            const dedAmt = elig?.deductible ?? selectedClient?.eligibility_deductible;
                                            const coinsAmt = elig?.coinsurance ?? selectedClient?.eligibility_coinsurance;
                                            return <>
                                              {copayAmt != null && <span><span className="text-gray-400 uppercase text-[10px]">Copay</span> <span className="font-semibold">${copayAmt}</span></span>}
                                              {dedAmt != null && (
                                                <span>
                                                  <span className="text-gray-400 uppercase text-[10px]">Ded</span>{' '}
                                                  <span className="font-semibold">${dedAmt}</span>
                                                  {safeEligibilityDetail?.deductible_remaining != null && (
                                                    <span className="text-green-700 ml-1">(${safeEligibilityDetail.deductible_remaining} rem)</span>
                                                  )}
                                                </span>
                                              )}
                                              {coinsAmt != null && <span><span className="text-gray-400 uppercase text-[10px]">Co-ins</span> <span className="font-semibold">{coinsAmt}%</span></span>}
                                            </>;
                                          })()}
                                        </div>
                                      )}

                                      {/* ── Row 4: Status feedback ── */}
                                      {isRejected && elig?.errors?.length > 0 && (
                                        <div className="mt-2 text-xs text-red-700 bg-red-50 rounded px-2 py-1 border border-red-100">
                                          {elig.errors[0]}
                                        </div>
                                      )}
                                      {isInFlight && (
                                        <div className="mt-2 flex items-center gap-2 text-xs text-blue-700 bg-blue-50 rounded px-2 py-1.5 border border-blue-100">
                                          <span className="inline-block w-2 h-2 rounded-full bg-blue-400 animate-pulse shrink-0" />
                                          <span>
                                            270 submitted — waiting for Availity response.
                                            {' '}<button
                                              className="underline font-medium hover:text-blue-900"
                                              onClick={() => handleDownload271Responses(selectedClient.id)}
                                            >
                                              Check now
                                            </button>
                                          </span>
                                        </div>
                                      )}

                                      {/* ── Row 5: Actions ── */}
                                      <div className="mt-2 pt-2 border-t border-gray-100">
                                        {/* Secondary actions: view links on the left, "Added by" on the right */}
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            {(insurance.card_front_url || insurance.card_back_url) && (
                                              <button
                                                className="text-xs text-blue-600 underline hover:text-blue-800"
                                                onClick={() => {
                                                  setViewingInsuranceCards({
                                                    type: priorityLabels[insurance.priority],
                                                    front: insurance.card_front_url || null,
                                                    back: insurance.card_back_url || null
                                                  });
                                                  setShowInsuranceCardView(true);
                                                }}
                                              >
                                                View Card
                                              </button>
                                            )}
                                            {(safeEligibilityDetail || selectedClient?.primary_eligibility_status) && insIdx === 0 && (
                                              <button
                                                className="text-xs text-blue-600 underline hover:text-blue-800"
                                                onClick={() => {
                                                  if (!safeEligibilityDetail) {
                                                    fetchEligibilityDetail(selectedClient.id).then(() => setShowEligibilityBenefits(true));
                                                  } else {
                                                    setShowEligibilityBenefits(true);
                                                  }
                                                }}
                                              >
                                                View Full Benefits
                                              </button>
                                            )}
                                            <button
                                              className="text-xs text-gray-500 underline hover:text-gray-700"
                                              onClick={() => {
                                                fetchEligibilityDetail(selectedClient.id);
                                                setShowEligibilityHistory(true);
                                              }}
                                            >
                                              View History
                                            </button>
                                            {selectedClient?.insurances?.filter(i => i.status === 'active').length > 1 && insIdx === 0 && (
                                              <button
                                                className="text-xs text-teal-600 underline hover:text-teal-800"
                                                onClick={async () => {
                                                  const active = (selectedClient.insurances || []).filter(i => i.status === 'active');
                                                  toast.info(`Submitting ${active.length} plan(s)...`, { duration: 3000 });
                                                  const results = await Promise.allSettled(
                                                    active.map((_, idx) =>
                                                      handleEligibilityCheck(selectedClient.id, selectedClient.full_name, { dateOfService: eligibilityCheckDOS || undefined, insuranceIndex: idx })
                                                    )
                                                  );
                                                  const succeeded = results.filter(r => r.status === 'fulfilled').length;
                                                  const failed = results.filter(r => r.status === 'rejected').length;
                                                  if (failed === 0) {
                                                    toast.success(`All ${succeeded} plan(s) submitted`);
                                                  } else {
                                                    toast.warning(`${succeeded} submitted, ${failed} failed`);
                                                  }
                                                }}
                                              >
                                                Check All Plans
                                              </button>
                                            )}
                                          </div>
                                          {/* Added-on provenance — moved to bottom-right so the
                                              eligibility box stays compact. Reads from the first
                                              history entry where action==='added', falling back
                                              to created_at for legacy records. */}
                                          {(() => {
                                            const addedEntry = Array.isArray(insurance.history)
                                              ? insurance.history.find(h => h?.action === 'added')
                                              : null;
                                            const rawTs = addedEntry?.timestamp || insurance.created_at;
                                            if (!rawTs) return null;
                                            let dateStr = null;
                                            let fullTs = null;
                                            try {
                                              const dt = new Date(rawTs);
                                              if (!isNaN(dt.getTime())) {
                                                dateStr = dt.toLocaleDateString();
                                                fullTs = dt.toLocaleString();
                                              }
                                            } catch (e) { /* ignore */ }
                                            if (!dateStr) return null;
                                            const who = addedEntry?.changed_by_name;
                                            return (
                                              <span
                                                className="text-xs text-gray-400 ml-auto shrink-0"
                                                title={fullTs || ''}
                                                data-testid={`insurance-added-on-${insurance.id}`}
                                              >
                                                Added {dateStr}{who ? ` by ${who}` : ''}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Live Eligibility Status Tracker */}
                    {activeEligibilityCheckId && (
                      <div className="mt-3">
                        <Suspense fallback={<div className="text-sm text-gray-400 p-2">Loading tracker...</div>}>
                          <EligibilityStatusTracker
                            checkId={activeEligibilityCheckId}
                            token={sessionStorage.getItem('token')}
                            onComplete={(check) => {
                              // Refresh client data when check completes
                              if (check?.parsed_data?.coverage_status) {
                                applyEligibilityResult(selectedClient?.id, {
                                  status: check.parsed_data.coverage_status,
                                  lastChecked: new Date().toISOString()
                                });
                              }
                              // Clear tracker after 30s so it doesn't clutter
                              setTimeout(() => setActiveEligibilityCheckId(null), 30000);
                            }}
                          />
                        </Suspense>
                      </div>
                    )}

                  </div>
                  
                  {/* Service History / Episodes of Care */}
                  <Card className="mt-4">
                    <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors py-3" onClick={() => setShowServiceHistory(!showServiceHistory)}>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <History className="h-5 w-5 text-teal-600" />
                          Service History
                          {clientEnrollments && clientEnrollments.length > 0 && (
                            <>
                              <Badge variant="outline" className="ml-2 text-xs">
                                {clientEnrollments.filter(e => (e.status === 'active' || e.status === 'assigned') && !e.disenrollment_pending).length} active
                              </Badge>
                              {clientEnrollments.filter(e => e.disenrollment_pending).length > 0 && (
                                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 bg-amber-50">
                                  {clientEnrollments.filter(e => e.disenrollment_pending).length} pending
                                </Badge>
                              )}
                            </>
                          )}
                        </CardTitle>
                        <ChevronDown className={`h-5 w-5 transition-transform ${showServiceHistory ? 'transform rotate-180' : ''}`} />
                      </div>
                    </CardHeader>
                    {showServiceHistory && (
                    <CardContent>
                      {/* Current Enrollments */}
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          Active Services
                        </h4>
                        {clientEnrollments && clientEnrollments.length > 0 ? (
                          <div className="space-y-3">
                            {clientEnrollments.filter(e => (e.status === 'active' || e.status === 'assigned') && !e.disenrollment_pending).map((enrollment, idx) => {
                              const provider = users.find(u => u.id === enrollment.provider_id);
                              const service = serviceTypes.find(s => s.id === enrollment.service_type_id);
                              return (
                                <div key={idx} className="flex items-start justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                                  <div>
                                    <p className="font-medium text-green-800">
                                      {service?.name || enrollment.service_name || 'Unknown Service'}
                                    </p>
                                    <p className="text-sm text-green-700">
                                      Provider: {provider?.full_name || 'Unassigned'}
                                      {provider?.credentials && `, ${provider.credentials}`}
                                    </p>
                                    <p className="text-xs text-green-600 mt-1">
                                      Started: {enrollment.start_date ? fmtLocalDate(enrollment.start_date) : enrollment.created_at ? new Date(enrollment.created_at).toLocaleDateString() : 'Unknown'}
                                    </p>
                                  </div>
                                  <Badge className="bg-green-100 text-green-700">Active</Badge>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm italic">No active service enrollments</p>
                        )}
                      </div>
                      
                      {/* Pending Disenrollment Services */}
                      {clientEnrollments && clientEnrollments.filter(e => e.disenrollment_pending).length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                          Pending Disenrollment
                          <Badge variant="outline" className="ml-1 text-xs text-amber-600 border-amber-300">
                            {clientEnrollments.filter(e => e.disenrollment_pending).length}
                          </Badge>
                        </h4>
                        <div className="space-y-3">
                          {clientEnrollments.filter(e => e.disenrollment_pending).map((enrollment, idx) => {
                            const provider = users.find(u => u.id === enrollment.provider_id);
                            const service = serviceTypes.find(s => s.id === enrollment.service_type_id);
                            const reasonLabel = 
                              enrollment.disenrollment_reason === 'treatment_completed' ? 'Successfully completed treatment' :
                              enrollment.disenrollment_reason === 'never_seen' ? 'Client declined / never seen' :
                              enrollment.disenrollment_reason === 'switched_service' ? 'Switched to different service' :
                              enrollment.disenrollment_reason === 'added_in_error' ? 'Added in error' :
                              enrollment.disenrollment_reason === 'client_discharged' ? 'Client discharged from practice' :
                              enrollment.disenrollment_reason === 'provider_left' ? 'Provider no longer with practice' :
                              enrollment.disenrollment_reason === 'insurance_change' ? 'Insurance/authorization change' :
                              enrollment.disenrollment_reason === 'other' ? 'Other' :
                              enrollment.disenrollment_reason || 'Pending';
                            return (
                              <div key={idx} className="flex items-start justify-between p-3 bg-amber-50 border-2 border-amber-300 rounded-lg">
                                <div>
                                  <p className="font-medium text-amber-800">
                                    {service?.name || enrollment.service_name || 'Unknown Service'}
                                  </p>
                                  <p className="text-sm text-amber-700">
                                    Provider: {provider?.full_name || 'Unassigned'}
                                    {provider?.credentials && `, ${provider.credentials}`}
                                  </p>
                                  <p className="text-xs text-amber-600 mt-1">
                                    Started: {enrollment.start_date ? fmtLocalDate(enrollment.start_date) : 'Unknown'}
                                  </p>
                                  <p className="text-xs text-amber-700 mt-1 font-medium">
                                    Reason: {reasonLabel}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <Badge className="bg-amber-200 text-amber-800 border-amber-400">
                                    Awaiting Discharge Note
                                  </Badge>
                                  <p className="text-xs text-amber-600 mt-1">
                                    Discharge form required
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      )}
                      
                      {/* Past Enrollments / Discharge History */}
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                          Past Services
                        </h4>
                        {clientEnrollments && clientEnrollments.filter(e => e.status === 'discharged' || e.status === 'discontinued' || e.status === 'completed' || e.status === 'ended' || e.status === 'cancelled').length > 0 ? (
                          <div className="space-y-3">
                            {clientEnrollments.filter(e => e.status === 'discharged' || e.status === 'discontinued' || e.status === 'completed' || e.status === 'ended' || e.status === 'cancelled').map((enrollment, idx) => {
                              const provider = users.find(u => u.id === enrollment.provider_id);
                              const service = serviceTypes.find(s => s.id === enrollment.service_type_id);
                              return (
                                <div key={idx} className="flex items-start justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                  <div>
                                    <p className="font-medium text-gray-700">
                                      {service?.name || enrollment.service_name || 'Unknown Service'}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      Provider: {provider?.full_name || 'Unknown'}
                                      {provider?.credentials && `, ${provider.credentials}`}
                                    </p>
                                    <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                      <p>Started: {enrollment.start_date ? fmtLocalDate(enrollment.start_date) : (enrollment.created_at ? fmtLocalDate(enrollment.created_at) : 'Unknown')}</p>
                                      {enrollment.end_date && (
                                        <p>Ended: {fmtLocalDate(enrollment.end_date)}</p>
                                      )}
                                      {(enrollment.disenrollment_reason || enrollment.discharge_reason) && (
                                        <p className="text-gray-600">Reason: {
                                          enrollment.disenrollment_reason === 'treatment_completed' ? 'Successfully completed treatment' :
                                          enrollment.disenrollment_reason === 'never_seen' ? 'Client declined / never seen' :
                                          enrollment.disenrollment_reason === 'switched_service' ? 'Switched to different service' :
                                          enrollment.disenrollment_reason === 'added_in_error' ? 'Added in error' :
                                          enrollment.disenrollment_reason === 'client_discharged' ? 'Client discharged from practice' :
                                          enrollment.disenrollment_reason === 'provider_left' ? 'Provider no longer with practice' :
                                          enrollment.disenrollment_reason === 'insurance_change' ? 'Insurance/authorization change' :
                                          enrollment.disenrollment_reason === 'other' ? 'Other' :
                                          enrollment.discharge_reason || enrollment.disenrollment_reason || 'Unknown'
                                        }</p>
                                      )}
                                    </div>
                                  </div>
                                  <Badge variant="outline" className={
                                    enrollment.status === 'completed' ? 'text-green-600 border-green-300 bg-green-50' : 'text-gray-500'
                                  }>
                                    {enrollment.status === 'completed' ? 'Successfully Completed' : 
                                     enrollment.status === 'discharged' ? 'Discharged' : 
                                     enrollment.status === 'ended' ? 'Ended' :
                                     enrollment.status === 'cancelled' ? 'Cancelled' : 'Discontinued'}
                                  </Badge>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm italic">No past service history</p>
                        )}
                      </div>
                      
                      {/* Client Status History */}
                      {selectedClient.status_history && selectedClient.status_history.length > 0 && (
                        <div className="mt-6 pt-4 border-t">
                          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-500" />
                            Status Changes
                          </h4>
                          <div className="space-y-2">
                            {selectedClient.status_history.slice().reverse().slice(0, 5).map((change, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className={
                                    change.new_status === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
                                    change.new_status === 'discharged' ? 'bg-gray-100 text-gray-600' :
                                    change.new_status === 'on_hold' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                    'bg-gray-50 text-gray-600'
                                  }>
                                    {change.new_status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                                  </Badge>
                                  {change.reason && <span className="text-gray-500">- {change.reason}</span>}
                                </div>
                                <span className="text-xs text-gray-400">
                                  {change.changed_at ? new Date(change.changed_at).toLocaleDateString() : ''}
                                </span>
                              </div>
                            ))}
                            {selectedClient.status_history.length > 5 && (
                              <p className="text-xs text-gray-400 text-center">
                                + {selectedClient.status_history.length - 5} more status changes
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                    )}
                  </Card>
                </TabsContent>
                <TabsContent value="diagnoses" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                  <DiagnosesTab
                    selectedClient={selectedClient}
                    clientDiagnoses={clientDiagnoses}
                    users={users}
                    currentUser={user}
                    setShowDiagnosisForm={setShowDiagnosisForm}
                    showArchivedDiagnoses={showArchivedDiagnoses}
                    setShowArchivedDiagnoses={setShowArchivedDiagnoses}
                    handleClientClick={handleClientClick}
                  />
                </TabsContent>
                <TabsContent value="medications" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                  <MedsVitalsTab
                    selectedClient={selectedClient}
                    clientMedications={clientMedications}
                    clientDiagnoses={clientDiagnoses}
                    setEditingMedication={setEditingMedication}
                    setShowMedicationForm={setShowMedicationForm}
                    medicationReferralRequests={medicationReferralRequests}
                    medicationReferralTemplate={medicationReferralTemplate}
                    setMedicationReferralClient={setMedicationReferralClient}
                    setMedicationReferralFormData={setMedicationReferralFormData}
                    setShowMedicationReferralDialog={setShowMedicationReferralDialog}
                    setSelectedReferralForDetails={setSelectedReferralForDetails}
                    setSelectedMedicationManagers={setSelectedMedicationManagers}
                    brandingSettings={brandingSettings}
                    openIPrescribe={openIPrescribe}
                    canAccessIPrescribe={canAccessIPrescribe}
                    vitalReadings={vitalReadings}
                    setVitalReadings={setVitalReadings}
                    showVitalForm={showVitalForm}
                    setShowVitalForm={setShowVitalForm}
                    vitalFormData={vitalFormData}
                    setVitalFormData={setVitalFormData}
                    vitalsTimeFilter={vitalsTimeFilter}
                    setVitalsTimeFilter={setVitalsTimeFilter}
                    vitalsChartView={vitalsChartView}
                    setVitalsChartView={setVitalsChartView}
                    handleClientClick={handleClientClick}
                    formatDateForFilename={formatDateForFilename}
                    generateDownloadFilename={generateDownloadFilename}
                  />
                </TabsContent>



                {/* Treatment Plans Tab */}
                <TabsContent value="treatment-plans" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                  {/* Import PANs from Assessment Banner */}
                  {(() => {
                    const storedPans = sessionStorage.getItem('pansFromAssessment');
                    if (storedPans) {
                      try {
                        const pansData = JSON.parse(storedPans);
                        // Only show if it's for the current client
                        if (pansData.clientId === selectedClient?.id) {
                          return (
                            <div className="bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300 rounded-lg p-4 mb-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-3xl">📋</span>
                                  <div>
                                    <h4 className="font-semibold text-purple-900">PANs Ready to Import from {pansData.sourceAssessment}</h4>
                                    <p className="text-sm text-purple-700">{pansData.pans.length} Prioritized Assessed Need{pansData.pans.length > 1 ? 's' : ''} identified:</p>
                                    <ul className="text-sm text-purple-800 mt-1 ml-4 list-disc">
                                      {pansData.pans.slice(0, 3).map((pan, idx) => (
                                        <li key={idx} className="truncate max-w-md">
                                          <strong>#{pan.priority}:</strong> {pan.text}
                                        </li>
                                      ))}
                                      {pansData.pans.length > 3 && (
                                        <li className="text-purple-600 italic">...and {pansData.pans.length - 3} more</li>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                                    onClick={() => {
                                      sessionStorage.removeItem('pansFromAssessment');
                                      // Force re-render
                                      setClientDetailTab('overview');
                                      setTimeout(() => setClientDetailTab('treatment-plans'), 0);
                                    }}
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Dismiss
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-purple-600 hover:bg-purple-700 text-white"
                                    onClick={async () => {
                                      // Open create treatment plan dialog with PANs pre-loaded
                                      setNewPlanProviderId(user?.id || '');
                                      setNewPlanStartDate(new Date().toISOString().split('T')[0]);
                                      setClientServiceEnrollments([]); // Reset enrollments
                                      
                                      // Fetch service types, available PANs, users, AND client service enrollments
                                      try {
                                        const requests = [
                                          axios.get(`${API}/treatment-planning/service-types`, {
                                            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                          }),
                                          axios.get(`${API}/treatment-planning/pans`, {
                                            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                          }),
                                          axios.get(`${API}/staff-list`, {
                                            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                          })
                                        ];
                                        
                                        // Also fetch client's service enrollments if we have a selected client.
                                        // Jul 2026 — non-fatal: a 403 (role missing view_service_enrollments)
                                        // must not break the whole form; fall back to unscoped service types.
                                        if (selectedClient?.id) {
                                          requests.push(
                                            axios.get(`${API}/clients/${selectedClient.id}/service-enrollments`, {
                                              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                            }).catch((enrErr) => {
                                              console.warn('[NewTreatmentPlan] enrollments fetch failed (continuing without):', enrErr.response?.status);
                                              return { data: [] };
                                            })
                                          );
                                        }
                                        
                                        const responses = await Promise.all(requests);
                                        const [servicesRes, pansRes, usersRes] = responses;
                                        
                                        setServiceTypes(servicesRes.data);
                                        setAvailablePANs(pansRes.data);
                                        setUsers(usersRes.data);
                                        
                                        // Store client's service enrollments if fetched
                                        if (responses.length > 3) {
                                          const enrollmentsRes = responses[3];
                                          void 0 && console.log('Client enrollments loaded for PANs import:', enrollmentsRes.data?.length);
                                          setClientServiceEnrollments(enrollmentsRes.data || []);
                                        }
                                        
                                        // Pre-populate PANs from assessment as custom PANs
                                        const importedPans = pansData.pans.map((pan, idx) => ({
                                          pan_id: `imported-${idx}`,
                                          pan_name: pan.text,
                                          category: 'emotional', // Default category
                                          isCustom: true,
                                          fromAssessment: true,
                                          priority: pan.priority,
                                          goals: [] // Initialize with empty goals array
                                        }));
                                        
                                        setPlanPANs(importedPans);
                                        setExpandedPANs(new Set(importedPans.map(p => p.pan_id)));
                                        setShowCreatePlanDialog(true);
                                        
                                        // Prefill medications and diagnoses from already-loaded context data
                                        if (selectedClient?.id) {
                                          const activeMeds = (clientMedications || []).filter(m => m.client_id === selectedClient.id && m.is_active === true);
                                          setTpMedications(activeMeds.map(m => ({
                                            name: m.medication_name || m.name || '',
                                            dosage: m.dosage || '',
                                            frequency: m.frequency || '',
                                            prescriber: m.prescriber || '',
                                            purpose: m.purpose || '',
                                            status: 'active'
                                          })));
                                          const activeDiags = (clientDiagnoses || []).filter(d => d.client_id === selectedClient.id && d.is_active !== false);
                                          setTpDiagnoses(activeDiags.map(d => ({
                                            code: d.code || d.diagnosis_code || '',
                                            description: d.description || d.diagnosis_description || d.diagnosis_name || '',
                                            status: d.status || 'active',
                                            is_primary: d.is_primary || false,
                                            assigned_by: d.assigned_by || '',
                                            provider_names: d.provider_names || [],
                                            assigned_by_name: d.assigned_by_name || ''
                                          })));
                                        }
                                        
                                        // Clear the stored PANs
                                        sessionStorage.removeItem('pansFromAssessment');
                                        
                                        toast.success(`${importedPans.length} PANs imported! Complete the treatment plan setup.`);
                                      } catch (error) {
                                        console.error('Error loading treatment plan data:', error);
                                        toast.error(`Failed to load form data: ${error.response?.status || ''} ${error.response?.data?.detail || error.message}`);
                                      }
                                    }}
                                  >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Create Treatment Plan with These PANs
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      } catch (e) {
                        console.error('Error parsing stored PANs:', e);
                      }
                    }
                    return null;
                  })()}
                  
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Treatment Plans</CardTitle>
                        <p className="text-sm text-gray-600 mt-1">Manage PANs, Goals, and Objectives by service</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowLegacyImport(true)}
                          title="Import a historical treatment plan from PDF"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Import Legacy
                        </Button>
                        <Button
                          size="sm"
                          onClick={async () => {
                            void 0 && console.log('New Treatment Plan button clicked');
                          // Set defaults when opening - default to current user if they're a provider
                          const defaultProviderId = (user?.is_provider || userRoleName === 'provider' || userRoleName === 'supervisor') ? user.id : '';
                          setNewPlanProviderId(defaultProviderId);
                          setNewPlanServiceType(''); // Clear - will be filtered by provider
                          setNewPlanStartDate(new Date().toISOString().split('T')[0]);
                          setPlanPANs([]);
                          setExpandedPANs(new Set());
                          setClientServiceEnrollments([]); // Reset enrollments
                          
                          // Fetch service types, PANs, users/providers, AND client service enrollments
                          try {
                            const requests = [
                              axios.get(`${API}/treatment-planning/service-types`, {
                                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                              }),
                              axios.get(`${API}/treatment-planning/pans`, {
                                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                              }),
                              axios.get(`${API}/staff-list`, {
                                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                              })
                            ];
                            
                            // Also fetch client's service enrollments if we have a selected client
                            if (selectedClient?.id) {
                              requests.push(
                                axios.get(`${API}/clients/${selectedClient.id}/service-enrollments`, {
                                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                })
                              );
                            }
                            
                            const responses = await Promise.all(requests);
                            const [servicesRes, pansRes, usersRes] = responses;
                            
                            setServiceTypes(servicesRes.data);
                            setAvailablePANs(pansRes.data);
                            setUsers(usersRes.data);
                            
                            // Store client's service enrollments if fetched
                            if (responses.length > 3) {
                              const enrollmentsRes = responses[3];
                              void 0 && console.log('Client enrollments loaded:', enrollmentsRes.data?.length);
                              setClientServiceEnrollments(enrollmentsRes.data || []);
                            }
                            
                            setShowCreatePlanDialog(true);
                            setPlanFieldsCollapsed(false);
                            
                            // Prefill medications and diagnoses from already-loaded context data
                            if (selectedClient?.id) {
                              const activeMeds = (clientMedications || []).filter(m => m.client_id === selectedClient.id && m.is_active === true);
                              setTpMedications(activeMeds.map(m => ({
                                name: m.medication_name || m.name || '',
                                dosage: m.dosage || '',
                                frequency: m.frequency || '',
                                prescriber: m.prescriber || '',
                                purpose: m.purpose || '',
                                status: 'active'
                              })));
                              const activeDiags = (clientDiagnoses || []).filter(d => d.client_id === selectedClient.id && d.is_active !== false);
                              setTpDiagnoses(activeDiags.map(d => ({
                                code: d.code || d.diagnosis_code || '',
                                description: d.description || d.diagnosis_description || d.diagnosis_name || '',
                                status: d.status || 'active',
                                is_primary: d.is_primary || false,
                                assigned_by: d.assigned_by || '',
                                provider_names: d.provider_names || [],
                                assigned_by_name: d.assigned_by_name || ''
                              })));
                            }
                            
                            void 0 && console.log('Dialog should now open with loaded data');
                          } catch (error) {
                            console.error('Error loading treatment plan data:', error);
                            toast.error(`Failed to load form data: ${error.response?.status || ''} ${error.response?.data?.detail || error.message}`);
                          }
                        }}
                        className="bg-teal-600 hover:bg-teal-700"
                        data-testid="legacy-new-treatment-plan-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        New Treatment Plan
                      </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {loadingTreatmentPlans ? (
                        <div className="text-center py-8">Loading treatment plans...</div>
                      ) : treatmentPlans.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                          <p className="text-lg mb-2">No Treatment Plans Yet</p>
                          <p className="text-sm">Click "New Treatment Plan" to create the first plan for this client</p>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          {/* Active Treatment Plans */}
                          {treatmentPlans.filter(p => p.status !== 'archived').map(plan => (
                            <Card key={plan.id} className="border-2">
                              <CardHeader className="bg-blue-50">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        className="text-gray-500 hover:text-gray-700 flex-shrink-0 -ml-1"
                                        onClick={() => setExpandedPlans(prev => {
                                          const next = new Set(prev);
                                          if (next.has(plan.id)) next.delete(plan.id);
                                          else next.add(plan.id);
                                          return next;
                                        })}
                                        data-testid={`expand-plan-${plan.id}`}
                                      >
                                        <ChevronDown className={`h-4 w-4 transition-transform ${expandedPlans.has(plan.id) ? 'rotate-180' : '-rotate-90'}`} />
                                      </button>
                                      <CardTitle className="text-base">
                                        {selectedClient && <span className="text-gray-600">{selectedClient.first_name} {selectedClient.last_name} &mdash; </span>}
                                        {(() => {
                                          const st = serviceTypes.find(st => st.id === plan.service_type);
                                          if (st) return st.display_name || st.name;
                                          // Backend enriches `service_type_name` when possible (supervisee / legacy plans).
                                          if (plan.service_type_name) return plan.service_type_name;
                                          const raw = plan.service_type;
                                          // Don't echo a raw UUID to the user; prefer a neutral label.
                                          if (!raw || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) {
                                            return 'Treatment Plan';
                                          }
                                          return raw.replace(/_/g, ' ').toUpperCase();
                                        })()}
                                        {plan.next_review_date && plan.status === 'active' && (() => {
                                          const reviewDate = new Date(plan.next_review_date + (plan.next_review_date.includes('T') ? '' : 'T12:00:00'));
                                          const today = new Date();
                                          const daysUntilReview = Math.ceil((reviewDate - today) / (1000 * 60 * 60 * 24));
                                          
                                          if (daysUntilReview < 0) {
                                            return <Badge className="ml-2 bg-red-500">Review Overdue</Badge>;
                                          } else if (daysUntilReview <= 14) {
                                            return <Badge className="ml-2 bg-orange-500">Review Due Soon</Badge>;
                                          }
                                          return null;
                                        })()}
                                      </CardTitle>
                                      {plan.doc_number && (
                                        <button
                                          type="button"
                                          className="font-mono text-[10px] text-gray-400 hover:text-gray-700 flex-shrink-0"
                                          title="Document number — click to copy"
                                          data-testid={`plan-doc-id-${plan.id}`}
                                          onClick={() => { navigator.clipboard?.writeText(plan.doc_number); toast.success('Document number copied to clipboard'); }}
                                        >
                                          {plan.doc_number}
                                        </button>
                                      )}
                                      
                                      {/* Delete button for DRAFT plans only */}
                                      {(() => {
                                        const isNotClientSigned = !plan.client_signed_at;
                                        const isDraft = plan.status === 'draft' || plan.status === 'needs_corrections';
                                        const canDeleteStatus = isDraft || (isNotClientSigned && !plan.provider_signed_at);
                                        const isOwnPlan = plan.assigned_provider_id === user?.id || plan.created_by === user?.id;
                                        const isAdminDel = user?.role?.toLowerCase() === 'admin';
                                        const userIsSuperAdmin = isSuperAdmin(user);
                                        const planProvider = users.find(u => u.id === plan.assigned_provider_id);
                                        const isSupervisorOfProvider = (user?.is_supervisor || user?.role?.toLowerCase() === 'supervisor') && planProvider?.supervisor_ids?.includes(user?.id);
                                        // Jun 2026 — admins/super admins can delete ANY plan
                                        // (backend permits it, incl. finalized, with audit log);
                                        // non-admins remain limited to unsigned drafts.
                                        const canDelete = (isAdminDel || userIsSuperAdmin)
                                          || (canDeleteStatus && (isOwnPlan || isSupervisorOfProvider));
                                        if (!canDelete) return null;
                                        return (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                                            title="Delete treatment plan"
                                            onClick={async () => {
                                              const isFinalizedPlan = plan.client_signed_at || plan.provider_signed_at || ['active', 'fully_signed', 'pending_signatures', 'sent_to_client_for_signature'].includes(plan.status);
                                              if (isFinalizedPlan) {
                                                const typed = window.prompt('⚠️ This plan is signed/finalized.\n\nDeleting it permanently removes the plan and ALL associated PANs, Goals, and Objectives. This cannot be undone.\n\nType DELETE to confirm:');
                                                if (typed !== 'DELETE') { if (typed !== null) toast.info('Deletion cancelled'); return; }
                                              } else if (!window.confirm('Delete Treatment Plan?\n\nThis will permanently delete this plan and all associated PANs, Goals, and Objectives.\n\nThis action cannot be undone. Continue?')) return;
                                              try {
                                                await axios.delete(`${API}/treatment-plans/${plan.id}`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                                toast.success('Treatment plan deleted');
                                                await refetchTreatmentPlans(selectedClient.id);
                                              } catch (error) {
                                                toast.error(error.response?.data?.detail || 'Failed to delete treatment plan');
                                              }
                                            }}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        );
                                      })()}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                                      <span><span className="font-medium text-gray-600">Provider:</span> {(() => { const prov = users.find(u => u.id === plan.assigned_provider_id); if (prov) return `${prov.first_name || ''} ${prov.last_name || ''}`.trim() + (prov.credentials ? `, ${prov.credentials}` : ''); return plan.assigned_provider_name || 'Unassigned'; })()}</span>
                                      {plan.visit_frequency && <span><span className="font-medium text-gray-600">Frequency:</span> {plan.visit_frequency.charAt(0).toUpperCase() + plan.visit_frequency.slice(1)}</span>}
                                      {plan.start_date && <span>Started {formatDateStringForDisplay(plan.start_date)}</span>}
                                      {plan.last_review_date && (
                                        <span data-testid={`plan-last-review-${plan.id}`} title="Date the last review was performed">
                                          Reviewed: {new Date(plan.last_review_date + (plan.last_review_date.includes('T') ? '' : 'T12:00:00')).toLocaleDateString()}
                                        </span>
                                      )}
                                      {plan.next_review_date ? (
                                        <span className="flex items-center gap-1">
                                          Next Review Due: {new Date(plan.next_review_date + (plan.next_review_date.includes('T') ? '' : 'T12:00:00')).toLocaleDateString()}
                                          <button
                                            className="text-blue-500 hover:text-blue-700 hover:underline"
                                            data-testid={`edit-review-date-${plan.id}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setReviewDateEditPlan(plan);
                                              setReviewDateEditValue(plan.next_review_date ? plan.next_review_date.split('T')[0] : '');
                                            }}
                                          >
                                            (edit)
                                          </button>
                                        </span>
                                      ) : (
                                        <button
                                          className="text-blue-500 hover:text-blue-700 hover:underline"
                                          data-testid={`set-review-date-${plan.id}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setReviewDateEditPlan(plan);
                                            setReviewDateEditValue('');
                                          }}
                                        >
                                          Set review date
                                        </button>
                                      )}
                                      {plan.version_number && <span className="font-medium text-gray-600">v{plan.version_number}</span>}
                                    </div>
                                  </div>
                                  <div className="flex gap-2 items-center flex-shrink-0">
                                    {/* Status Badge */}
                                    {plan.status === 'draft' && <Badge className="bg-gray-400">Draft</Badge>}
                                    {plan.status === 'needs_corrections' && <Badge className="bg-orange-500">Needs Corrections</Badge>}
                                    {plan.status === 'sent_to_client_for_signature' && <Badge className="bg-blue-500">Sent to Client</Badge>}
                                    {plan.status === 'pending_signatures' && (
                                      <>
                                        <Badge className="bg-yellow-500">Pending Signatures</Badge>
                                        {plan.provider_signed_at && <Badge variant="outline" className="text-xs">Provider</Badge>}
                                        {plan.supervisor_signed_at && <Badge variant="outline" className="text-xs">Supervisor</Badge>}
                                      </>
                                    )}
                                    {plan.status === 'fully_signed' && <Badge className="bg-green-500">Fully Signed</Badge>}
                                    {plan.status === 'active' && <Badge className="bg-green-600">Active</Badge>}
                                    {plan.portal_signature_completed && <Badge variant="outline" className="text-xs text-green-600 border-green-300">Portal Signed</Badge>}
                                    {plan.status === 'on_hold' && <Badge className="bg-orange-500">On Hold</Badge>}
                                    {plan.status === 'archived' && <Badge className="bg-gray-500">Archived</Badge>}

                                    {/* Primary CTA based on status */}
                                    {(plan.status === 'draft' || plan.status === 'active') && !plan.provider_signed_at && (
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="complete-plan-btn">
                                            <FileText className="h-4 w-4 mr-1" />
                                            Complete Plan
                                            <ChevronDown className="h-4 w-4 ml-1" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-56">
                                          <DropdownMenuItem onClick={() => { setSelectedTreatmentPlan(plan); setHoldFromClient(false); setShowTreatmentPlanDialog(true); }} className="cursor-pointer">
                                            <Send className="h-4 w-4 mr-2" /> Sign & Send to Client
                                          </DropdownMenuItem>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem onClick={async () => {
                                            try {
                                              toast.info('Generating PDF...');
                                              const response = await axios.get(`${API}/treatment-plans/${plan.id}/download-preview-pdf`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }, responseType: 'blob' });
                                              const blob = new Blob([response.data], { type: 'application/pdf' });
                                              const url = window.URL.createObjectURL(blob);
                                              const a = document.createElement('a'); a.href = url; a.download = filenameFromResponse(response, `Treatment_Plan_${selectedClient?.last_name || 'Client'}_${new Date().toISOString().split('T')[0]}.pdf`);
                                              document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
                                              toast.success('PDF downloaded');
                                            } catch (error) { toast.error(await pdfBlobErrDetail(error, 'Failed to download PDF')); }
                                          }} className="cursor-pointer">
                                            <Download className="h-4 w-4 mr-2" /> Download for Signature
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={async () => {
                                            // Feb 20 2026 — User reported plans
                                            // showing "signed" but with no
                                            // client signature on file.  Root
                                            // cause: this dropdown item sat
                                            // right under "Sign & Send to
                                            // Client" with a single-line
                                            // confirm dialog, so providers
                                            // clicked it intending to start the
                                            // in-system signing flow.  It does
                                            // NOT capture any signature image
                                            // — it ONLY records that signatures
                                            // were collected on a paper
                                            // printout outside the system.
                                            //
                                            // Strengthened the confirm to make
                                            // the no-digital-signature
                                            // consequence unambiguous, and
                                            // require the user to type
                                            // "PAPER" so a stray Enter on the
                                            // earlier confirm can't finalize.
                                            const typed = window.prompt(
                                              'FINALIZE WITH PAPER SIGNATURES?\n\n'
                                              + 'This is only for plans signed on PAPER (printed, wet-ink signed, then collected outside the system).\n\n'
                                              + 'It will NOT capture any digital signature image.  Anyone viewing this plan later will see "Signed on paper — no digital signature on file."\n\n'
                                              + 'If you meant to SIGN THE PLAN ELECTRONICALLY, click Cancel and use "Sign & Send to Client" instead.\n\n'
                                              + 'To confirm paper finalization, type PAPER below:'
                                            );
                                            if (typed !== 'PAPER') {
                                              toast.info('Paper finalization cancelled.');
                                              return;
                                            }
                                            try {
                                              await axios.post(`${API}/treatment-plans/${plan.id}/finalize-paper-signatures`, {}, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                              toast.success('Treatment plan finalized with paper signatures');
                                              await refetchTreatmentPlans(selectedClient.id);
                                            } catch (error) { toast.error(error.response?.data?.detail || 'Failed to finalize plan'); }
                                          }} className="cursor-pointer text-green-700" data-testid="finalize-paper-signatures-btn">
                                            <CheckCircle className="h-4 w-4 mr-2" /> Finalize (Paper Signatures)
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    )}

                                    {plan.status === 'needs_corrections' && (
                                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => { setSelectedTreatmentPlan(plan); setHoldFromClient(false); setShowTreatmentPlanDialog(true); }}>
                                        Resubmit
                                      </Button>
                                    )}

                                    {plan.status === 'pending_signatures' && (
                                      <>
                                        {!plan.provider_signed_at && (
                                          <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={async () => {
                                            if (!user.signature_image) { setSignaturePlanId(plan.id); setStaffSignatureType('provider'); setShowStaffSignatureDialog(true); return; }
                                            try {
                                              await axios.post(`${API}/treatment-plans/${plan.id}/sign-provider`, { signature: user.signature_image }, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                              toast.success('Provider signature recorded'); await refetchTreatmentPlans(selectedClient.id);
                                            } catch (error) { toast.error(error.response?.data?.detail || 'Failed to sign'); }
                                          }}>
                                            Sign as Provider
                                          </Button>
                                        )}
                                        {plan.requires_supervisor_signature && plan.provider_signed_at && !plan.supervisor_signed_at && (() => {
                                          const isAssignedProvider = plan.assigned_provider_id === user?.id;
                                          const isUserSupervisor = user?.is_supervisor || user?.role?.toLowerCase() === 'supervisor';
                                          const isAdminUser = user?.role?.toLowerCase() === 'admin' || isSuperAdmin(user);
                                          if (isAssignedProvider && !isAdminUser) return <span className="text-xs text-gray-500">Awaiting supervisor</span>;
                                          if (isUserSupervisor || isAdminUser) return (
                                            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={async () => {
                                              if (!user.signature_image) { setSignaturePlanId(plan.id); setStaffSignatureType('supervisor'); setShowStaffSignatureDialog(true); return; }
                                              try {
                                                await axios.post(`${API}/treatment-plans/${plan.id}/sign-supervisor`, { signature: user.signature_image }, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                                toast.success('Supervisor signature recorded'); await refetchTreatmentPlans(selectedClient.id); fetchNotifications();
                                              } catch (error) { toast.error(error.response?.data?.detail || 'Failed to sign'); }
                                            }}>
                                              Sign as Supervisor
                                            </Button>
                                          );
                                          return <span className="text-xs text-gray-500">Awaiting supervisor</span>;
                                        })()}
                                        {((plan.requires_supervisor_signature && plan.supervisor_signed_at) || (!plan.requires_supervisor_signature && plan.provider_signed_at)) && !plan.client_signed_at && (
                                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={async () => {
                                            try {
                                              await axios.post(`${API}/treatment-plans/${plan.id}/send-to-client`, {}, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                              toast.success('Treatment plan sent to client');
                                            } catch (error) {
                                              toast.error(error.response?.data?.detail || 'Failed to send to client');
                                              return;
                                            }
                                            // Post-success refetch — failures here must NOT
                                            // trigger the "Failed to send" toast (the actual
                                            // send already succeeded). Log silently and let
                                            // the next user-driven refetch reconcile.
                                            try { await refetchTreatmentPlans(selectedClient.id); }
                                            catch (e) { console.warn('Refetch after send-to-client failed (send succeeded):', e); }
                                          }}>
                                            Send to Client
                                          </Button>
                                        )}
                                        {/* Jun 2026 — Remind button.
                                            Surfaces when the plan has been sent to
                                            the client but the client hasn't signed
                                            yet.  Hidden once the client signs. */}
                                        {plan.sent_to_client_at && !plan.client_signed_at && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                            data-testid={`remind-treatment-plan-${plan.id}`}
                                            title={`Originally sent ${new Date(plan.sent_to_client_at).toLocaleString()}${plan.last_reminded_at ? ` · Last reminded ${new Date(plan.last_reminded_at).toLocaleString()}` : ''}`}
                                            onClick={async () => {
                                              try {
                                                const r = await axios.post(`${API}/treatment-plans/${plan.id}/remind-client`, {}, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                                toast.success(`Reminder sent (${r.data?.reminder_count || 1}×)`);
                                                try { await refetchTreatmentPlans(selectedClient.id); }
                                                catch (e) { console.warn('Refetch after reminder failed (send succeeded):', e); }
                                              } catch (error) {
                                                toast.error(error.response?.data?.detail || 'Failed to send reminder');
                                              }
                                            }}
                                          >
                                            Remind
                                            {(plan.reminder_history?.length || 0) > 0 && (
                                              <span className="ml-1 text-xs">×{plan.reminder_history.length}</span>
                                            )}
                                          </Button>
                                        )}
                                      </>
                                    )}

                                    {plan.status === 'fully_signed' && (
                                      <Button size="sm" variant="outline" onClick={async () => {
                                        try {
                                          await axios.post(`${API}/treatment-plans/${plan.id}/generate-pdf`, {}, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                          toast.success('PDF generated and saved'); await refetchTreatmentPlans(selectedClient.id);
                                        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to generate PDF'); }
                                      }}>
                                        Generate PDF
                                      </Button>
                                    )}

                                    {/* Hamburger menu - all secondary actions */}
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`plan-actions-menu-${plan.id}`}>
                                          <MoreVertical className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end" className="w-56">
                                        {/* For drafts/needs_corrections: single Edit option */}
                                        {canEditTreatmentPlan(plan) && (plan.status === 'draft' || plan.status === 'needs_corrections') && (
                                          <DropdownMenuItem data-testid="edit-treatment-plan-btn" onClick={async () => {
                                            setEditingPlanForMinorEdit(plan);
                                            // Jun 2026 — RESUME an in-progress major review on re-entry.
                                            // Deterministic via the persisted review_in_progress flag,
                                            // with a fallback inference for pre-flag records.
                                            const resumeMajorReview = plan.review_in_progress === true ||
                                              (plan.version_type === 'major_review' && !plan.provider_signature);
                                            if (resumeMajorReview) {
                                              const savedProgress = plan.previous_review_goal_progress || [];
                                              const goals = [];
                                              (plan.pans || []).forEach(pan => (pan.goals || []).forEach(goal => {
                                                const gid = goal.id || goal.goal_id;
                                                const saved = savedProgress.find(gp => gp.goal_id === gid) || {};
                                                goals.push({
                                                  goal_id: gid,
                                                  goal_text: goal.goal_text || goal.description || 'Unnamed goal',
                                                  pan_name: pan.pan_name,
                                                  progress_status: saved.progress_status || '',
                                                  progress_notes: saved.progress_notes || '',
                                                });
                                              }));
                                              setMajorReviewGoalProgress(goals);
                                              setMajorReviewNotes(plan.review_notes || '');
                                              setMajorReviewDate((plan.last_review_date || '').split('T')[0] || new Date().toLocaleDateString('en-CA'));
                                              setIsMajorReviewEdit(true);
                                              toast.info('Resuming major review in progress', { duration: 3000 });
                                            } else {
                                              setIsMajorReviewEdit(false);
                                            }
                                            setNewPlanProviderId(plan.assigned_provider_id || '');
                                            setNewPlanServiceType(plan.service_type || '');
                                            setNewPlanStartDate(plan.start_date || new Date().toISOString().split('T')[0]);
                                            setNewPlanVisitFrequency(plan.visit_frequency || '');
                                            const existingPANs = (plan.pans || []).map(p => ({ ...p, goals: (p.goals || []).map(g => ({ ...g, objectives: g.objectives || [] })) }));
                                            setPlanPANs(existingPANs);
                                            setExpandedPANs(new Set(existingPANs.map(p => p.pan_id)));
                                            const editClientId = plan.client_id || selectedClient?.id;
                                            if (editClientId) {
                                              if (plan.medications_snapshot?.length > 0) { setTpMedications(plan.medications_snapshot); }
                                              else { const activeMeds = (clientMedications || []).filter(m => m.client_id === editClientId && m.is_active === true); setTpMedications(activeMeds.map(m => ({ name: m.medication_name || m.name || '', dosage: m.dosage || '', frequency: m.frequency || '', prescriber: m.prescriber || '', purpose: m.purpose || '', status: 'active' }))); }
                                              if (plan.diagnoses_snapshot?.length > 0) { setTpDiagnoses(plan.diagnoses_snapshot); }
                                              else { const activeDiags = (clientDiagnoses || []).filter(d => d.client_id === editClientId && d.is_active !== false); setTpDiagnoses(activeDiags.map(d => ({ code: d.code || d.diagnosis_code || '', description: d.description || d.diagnosis_description || d.diagnosis_name || '', status: d.status || 'active', is_primary: d.is_primary || false, assigned_by: d.assigned_by || '' }))); }
                                            }
                                            setTpLevelOfCareNotes(plan.level_of_care_notes || '');
                                            setTpTraumaReports(plan.trauma_reports || 'None reported');
                                            if (plan.supports) { setTpSupportsNoneReported(plan.supports.none_reported || false); setTpSupports(plan.supports.entries || []); }
                                            else { setTpSupportsNoneReported(false); setTpSupports([]); }
                                            setShowCreatePlanDialog(true);
                                          }} className="cursor-pointer">
                                            <Pencil className="h-4 w-4 mr-2" />
                                            Edit Draft
                                          </DropdownMenuItem>
                                        )}
                                        {/* For active/signed plans: Minor Edit */}
                                        {canEditTreatmentPlan(plan) && plan.status !== 'draft' && plan.status !== 'needs_corrections' && (
                                          <DropdownMenuItem data-testid="minor-edit-treatment-plan-btn" onClick={() => {
                                            setPlanToEdit(plan);
                                            setShowEditTypeDialog(false);
                                            setIsMajorReviewEdit(false);
                                            setEditingPlanForMinorEdit(plan);
                                            setNewPlanProviderId(plan.assigned_provider_id || '');
                                            setNewPlanServiceType(plan.service_type || '');
                                            setNewPlanStartDate(plan.start_date || new Date().toISOString().split('T')[0]);
                                            setNewPlanVisitFrequency(plan.visit_frequency || '');
                                            const existingPANs = (plan.pans || []).map(p => ({ ...p, goals: (p.goals || []).map(g => ({ ...g, objectives: g.objectives || [] })) }));
                                            setPlanPANs(existingPANs);
                                            setExpandedPANs(new Set(existingPANs.map(p => p.pan_id)));
                                            const editClientId = plan.client_id || selectedClient?.id;
                                            if (editClientId) {
                                              if (plan.medications_snapshot?.length > 0) { setTpMedications(plan.medications_snapshot); }
                                              else { const activeMeds = (clientMedications || []).filter(m => m.client_id === editClientId && m.is_active === true); setTpMedications(activeMeds.map(m => ({ name: m.medication_name || m.name || '', dosage: m.dosage || '', frequency: m.frequency || '', prescriber: m.prescriber || '', purpose: m.purpose || '', status: 'active' }))); }
                                              if (plan.diagnoses_snapshot?.length > 0) { setTpDiagnoses(plan.diagnoses_snapshot); }
                                              else { const activeDiags = (clientDiagnoses || []).filter(d => d.client_id === editClientId && d.is_active !== false); setTpDiagnoses(activeDiags.map(d => ({ code: d.code || d.diagnosis_code || '', description: d.description || d.diagnosis_description || d.diagnosis_name || '', status: d.status || 'active', is_primary: d.is_primary || false, assigned_by: d.assigned_by || '' }))); }
                                            }
                                            setTpLevelOfCareNotes(plan.level_of_care_notes || '');
                                            setTpTraumaReports(plan.trauma_reports || 'None reported');
                                            if (plan.supports) { setTpSupportsNoneReported(plan.supports.none_reported || false); setTpSupports(plan.supports.entries || []); }
                                            else { setTpSupportsNoneReported(false); setTpSupports([]); }
                                            setShowCreatePlanDialog(true);
                                            toast.info('Minor Edit mode - changes won\'t require new signatures', { duration: 3000 });
                                          }} className="cursor-pointer">
                                            <Pencil className="h-4 w-4 mr-2" />
                                            Minor Edit
                                          </DropdownMenuItem>
                                        )}
                                        {/* For active/signed plans: Treatment Plan Review (Major Review) */}
                                        {canEditTreatmentPlan(plan) && plan.status === 'active' && (
                                          <DropdownMenuItem data-testid="major-review-treatment-plan-btn" onClick={async () => {
                                            const goals = [];
                                            if (plan?.pans) {
                                              for (const pan of plan.pans) {
                                                if (pan.goals) {
                                                  for (const goal of pan.goals) {
                                                    goals.push({ goal_id: goal.id || goal.goal_id, goal_text: goal.goal_text || goal.description || 'Unnamed goal', pan_name: pan.pan_name, progress_status: '', progress_notes: '' });
                                                  }
                                                }
                                              }
                                            }
                                            setMajorReviewGoalProgress(goals);
                                            setMajorReviewNotes('');
                                            setMajorReviewDate(new Date().toLocaleDateString('en-CA'));
                                            setIsMajorReviewEdit(true);
                                            setEditingPlanForMinorEdit(plan);
                                            setNewPlanProviderId(plan.assigned_provider_id || '');
                                            setNewPlanServiceType(plan.service_type || '');
                                            setNewPlanStartDate(plan.start_date || new Date().toISOString().split('T')[0]);
                                            setNewPlanVisitFrequency(plan.visit_frequency || '');
                                            const existingPANs = (plan.pans || []).map(p => ({
                                              pan_id: p.id || p.pan_id, pan_name: p.pan_name, is_custom: p.is_custom || false,
                                              goals: (p.goals || []).map(g => ({ goal_id: g.id || g.goal_id, goal_text: g.goal_text || g.description, target_date: g.target_date, objectives: (g.objectives || []).map(o => ({ objective_id: o.id || o.objective_id, objective_text: o.objective_text || o.description })) }))
                                            }));
                                            setPlanPANs(existingPANs);
                                            setExpandedPANs(new Set(existingPANs.map(p => p.pan_id)));
                                            const editClientId = plan.client_id || selectedClient?.id;
                                            if (editClientId) {
                                              if (plan.medications_snapshot?.length > 0) { setTpMedications(plan.medications_snapshot); }
                                              else { const activeMeds = (clientMedications || []).filter(m => m.client_id === editClientId && m.is_active === true); setTpMedications(activeMeds.map(m => ({ name: m.medication_name || m.name || '', dosage: m.dosage || '', frequency: m.frequency || '', prescriber: m.prescriber || '', purpose: m.purpose || '', status: 'active' }))); }
                                              if (plan.diagnoses_snapshot?.length > 0) { setTpDiagnoses(plan.diagnoses_snapshot); }
                                              else { const activeDiags = (clientDiagnoses || []).filter(d => d.client_id === editClientId && d.is_active !== false); setTpDiagnoses(activeDiags.map(d => ({ code: d.code || d.diagnosis_code || '', description: d.description || d.diagnosis_description || d.diagnosis_name || '', status: d.status || 'active', is_primary: d.is_primary || false, assigned_by: d.assigned_by || '' }))); }
                                            }
                                            setTpLevelOfCareNotes(plan.level_of_care_notes || '');
                                            setTpTraumaReports(plan.trauma_reports || 'None reported');
                                            if (plan.supports) { setTpSupportsNoneReported(plan.supports.none_reported || false); setTpSupports(plan.supports.entries || []); }
                                            else { setTpSupportsNoneReported(false); setTpSupports([]); }
                                            await new Promise(resolve => setTimeout(resolve, 50));
                                            setShowCreatePlanDialog(true);
                                            toast.info('Treatment Plan Review - creates a new version requiring signatures', { duration: 4000 });
                                          }} className="cursor-pointer text-orange-700">
                                            <RotateCcw className="h-4 w-4 mr-2" />
                                            Treatment Plan Review
                                          </DropdownMenuItem>
                                        )}
                                        {plan.status === 'draft' && (
                                          <DropdownMenuItem onClick={() => { setPlanSettingsToEdit({ id: plan.id, visit_frequency: plan.visit_frequency, start_date: plan.start_date, end_date: plan.end_date }); setShowEditPlanSettingsDialog(true); }} className="cursor-pointer">
                                            <Settings className="h-4 w-4 mr-2" /> Plan Settings
                                          </DropdownMenuItem>
                                        )}
                                        {plan.status === 'active' && plan.pdf_snapshot_path && (
                                          <DropdownMenuItem onClick={async () => {
                                            try {
                                              const response = await axios.get(`${API}/treatment-plans/${plan.id}/pdf`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }, responseType: 'blob' });
                                              const blob = new Blob([response.data], { type: 'application/pdf' });
                                              const url = window.URL.createObjectURL(blob);
                                              setDocumentBlobUrl(url);
                                              const planDate = plan.start_date ? new Date(plan.start_date) : new Date();
                                              const docType = `Treatment Plan Dated ${String(planDate.getMonth() + 1).padStart(2, '0')}-${String(planDate.getDate()).padStart(2, '0')}-${planDate.getFullYear()}`;
                                              setViewingDocument({ id: plan.pdf_snapshot_path, filename: generateDownloadFilename(selectedClient, docType), file_type: 'application/pdf', treatment_plan_id: plan.id });
                                            } catch (error) { toast.error(await pdfBlobErrDetail(error, 'Failed to load PDF')); }
                                          }} className="cursor-pointer">
                                            <FileText className="h-4 w-4 mr-2" /> View PDF
                                          </DropdownMenuItem>
                                        )}
                                        {/* Download for Signature — Feb 2026: now available to ALL users
                                            (provider, office, supervisor, admin) for ANY non-archived plan
                                            status. Uses /download-preview-pdf which generates a current
                                            preview regardless of signature state, so office staff can
                                            print and collect wet signatures even when the assigned
                                            provider isn't logged in. Previously this was only on the
                                            provider-only "Complete Plan" button for draft/active plans
                                            without a provider signature, which hid it from office staff
                                            and from anyone viewing a signed/pending plan. */}
                                        {plan.status !== 'archived' && (
                                          <DropdownMenuItem data-testid={`download-for-signature-kebab-${plan.id}`} onClick={async () => {
                                            try {
                                              toast.info('Generating PDF...');
                                              const response = await axios.get(`${API}/treatment-plans/${plan.id}/download-preview-pdf`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }, responseType: 'blob' });
                                              const blob = new Blob([response.data], { type: 'application/pdf' });
                                              const url = window.URL.createObjectURL(blob);
                                              const a = document.createElement('a'); a.href = url; a.download = filenameFromResponse(response, `Treatment_Plan_${selectedClient?.last_name || 'Client'}_${new Date().toISOString().split('T')[0]}.pdf`);
                                              document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
                                              toast.success('PDF downloaded');
                                            } catch (error) { toast.error(await pdfBlobErrDetail(error, 'Failed to download PDF')); }
                                          }} className="cursor-pointer">
                                            <Download className="h-4 w-4 mr-2" /> Download for Signature
                                          </DropdownMenuItem>
                                        )}
                                        {/* Download PDF — Feb 2026: broadened so any user can grab the
                                            signed/active PDF.  Prefer the immutable snapshot when
                                            present (active plans with pdf_snapshot_path), otherwise
                                            fall back to the on-the-fly preview so fully_signed plans
                                            (snapshot not yet generated) and pending_signatures plans
                                            can still be downloaded for filing/records.  Previously
                                            this item was gated to `active && pdf_snapshot_path`, so
                                            office staff couldn't download a signed plan that hadn't
                                            had its PDF generated yet. */}
                                        {(plan.status === 'active' || plan.status === 'fully_signed' || plan.status === 'pending_signatures') && (
                                          <DropdownMenuItem data-testid="download-treatment-plan-pdf" onClick={async () => {
                                            try {
                                              const useSnapshot = plan.status === 'active' && plan.pdf_snapshot_path;
                                              const endpoint = useSnapshot
                                                ? `${API}/treatment-plans/${plan.id}/pdf`
                                                : `${API}/treatment-plans/${plan.id}/download-preview-pdf`;
                                              if (!useSnapshot) toast.info('Generating PDF...');
                                              const response = await axios.get(endpoint, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }, responseType: 'blob' });
                                              const blob = new Blob([response.data], { type: 'application/pdf' });
                                              const url = window.URL.createObjectURL(blob);
                                              const planDate = plan.start_date ? new Date(plan.start_date) : new Date();
                                              const docType = `Treatment Plan Dated ${String(planDate.getMonth() + 1).padStart(2, '0')}-${String(planDate.getDate()).padStart(2, '0')}-${planDate.getFullYear()}`;
                                              const a = document.createElement('a'); a.href = url; a.download = filenameFromResponse(response, generateDownloadFilename(selectedClient, docType));
                                              document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
                                              toast.success('Treatment plan downloaded');
                                            } catch (error) { toast.error(await pdfBlobErrDetail(error, 'Failed to download PDF')); }
                                          }} className="cursor-pointer">
                                            <Download className="h-4 w-4 mr-2" /> Download PDF
                                          </DropdownMenuItem>
                                        )}
                                        {plan.status === 'sent_to_client_for_signature' && (
                                          <>
                                            <DropdownMenuItem onClick={async () => {
                                              try {
                                                toast.info('Generating PDF...');
                                                const response = await axios.get(`${API}/treatment-plans/${plan.id}/download-preview-pdf`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }, responseType: 'blob' });
                                                const blob = new Blob([response.data], { type: 'application/pdf' });
                                                const url = window.URL.createObjectURL(blob);
                                                const a = document.createElement('a'); a.href = url; a.download = filenameFromResponse(response, `Treatment_Plan_${selectedClient?.last_name || 'Client'}_${new Date().toISOString().split('T')[0]}.pdf`);
                                                document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
                                                toast.success('PDF downloaded');
                                              } catch (error) { toast.error(await pdfBlobErrDetail(error, 'Failed to download PDF')); }
                                            }} className="cursor-pointer">
                                              <Download className="h-4 w-4 mr-2" /> Download for Signature
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={async () => {
                                              if (!confirm('Recall treatment plan from client?')) return;
                                              try {
                                                await axios.post(`${API}/treatment-plans/${plan.id}/recall-from-client`, {}, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                                toast.success('Treatment plan recalled'); await refetchTreatmentPlans(selectedClient.id);
                                              } catch (error) { toast.error(error.response?.data?.detail || 'Failed to recall'); }
                                            }} className="cursor-pointer text-orange-700">
                                              <RotateCcw className="h-4 w-4 mr-2" /> Recall from Client
                                            </DropdownMenuItem>
                                          </>
                                        )}
                                        {plan.status === 'pending_signatures' && plan.requires_supervisor_signature && plan.provider_signed_at && !plan.supervisor_signed_at && (user?.is_supervisor || user?.role?.toLowerCase() === 'supervisor' || user?.role?.toLowerCase() === 'admin' || isSuperAdmin(user)) && (
                                          <DropdownMenuItem onClick={() => {
                                            const feedback = prompt('Please provide feedback for corrections:');
                                            if (feedback && feedback.trim()) {
                                              (async () => {
                                                try {
                                                  await axios.post(`${API}/treatment-plans/${plan.id}/request-corrections`, { feedback: feedback.trim() }, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                                  toast.success('Correction request sent'); await refetchTreatmentPlans(selectedClient.id); fetchNotifications();
                                                } catch (error) { toast.error(error.response?.data?.detail || 'Failed to request corrections'); }
                                              })();
                                            }
                                          }} className="cursor-pointer text-orange-700">
                                            <Pencil className="h-4 w-4 mr-2" /> Request Corrections
                                          </DropdownMenuItem>
                                        )}
                                        {plan.version_number && (
                                          <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={async () => {
                                              try {
                                                const response = await axios.get(`${API}/treatment-plans/${plan.id}/version-history`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                                setVersionHistory(response.data); setShowVersionHistoryDialog(true);
                                              } catch (error) { toast.error('Failed to load version history'); }
                                            }} className="cursor-pointer">
                                              <Clock className="h-4 w-4 mr-2" /> Version History
                                            </DropdownMenuItem>
                                          </>
                                        )}
                                        {plan.status === 'active' && (
                                          <DropdownMenuItem data-testid={`request-portal-sig-${plan.id}`} onClick={async () => {
                                            try {
                                              await axios.post(`${API}/treatment-plans/${plan.id}/request-portal-signature`, {}, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                              toast.success('Signature request sent to client portal'); await refetchTreatmentPlans(selectedClient.id);
                                            } catch (error) { toast.error(error.response?.data?.detail || 'Failed to send signature request'); }
                                          }} className="cursor-pointer">
                                            <Send className="h-4 w-4 mr-2" />
                                            {plan.portal_signature_completed ? 'Re-request Portal Signature' : plan.portal_signature_requested ? 'Resend Portal Signature' : 'Request Portal Signature'}
                                          </DropdownMenuItem>
                                        )}
                                        {(() => {
                                          const isSigned = plan.provider_signed_at || ['active', 'fully_signed'].includes(plan.status);
                                          const isArchived = plan.status === 'archived';
                                          const isOwnPlan = plan.assigned_provider_id === user?.id;
                                          const isAdminArch = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'super admin' || user?.is_super_admin;
                                          const planProv = users.find(u => u.id === plan.assigned_provider_id);
                                          const isSuperOfProv = user?.is_supervisor && planProv?.supervisor_ids?.includes(user?.id);
                                          const canArchive = isSigned && !isArchived && (isOwnPlan || isAdminArch || isSuperOfProv);
                                          if (!canArchive) return null;
                                          return (
                                            <>
                                              <DropdownMenuSeparator />
                                              <DropdownMenuItem data-testid="archive-treatment-plan-btn" onClick={async () => {
                                                if (!window.confirm('Archive this treatment plan? It will be moved to the Archived section.')) return;
                                                try {
                                                  await axios.post(`${API}/treatment-plans/${plan.id}/archive`, {}, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
                                                  toast.success('Treatment plan archived'); await refetchTreatmentPlans(selectedClient.id);
                                                } catch (error) {
                                                  if (error.response?.status === 400 && error.response?.data?.detail?.includes('already archived')) { toast.info('Already archived'); await refetchTreatmentPlans(selectedClient.id); }
                                                  else { toast.error(error.response?.data?.detail || 'Failed to archive'); }
                                                }
                                              }} className="cursor-pointer text-gray-600">
                                                <Archive className="h-4 w-4 mr-2" /> Archive Plan
                                              </DropdownMenuItem>
                                            </>
                                          );
                                        })()}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>

                                </div>
                              </CardHeader>
                              {expandedPlans.has(plan.id) && (
                              <CardContent className="pt-3 pb-3">
                                {/* Supervisor feedback banner for needs_corrections */}
                                {plan.status === 'needs_corrections' && plan.supervisor_feedback && (
                                  <div className="mb-3 p-3 bg-orange-50 border border-orange-300 rounded">
                                    <p className="text-sm font-semibold text-orange-900 mb-1">Supervisor Feedback:</p>
                                    <p className="text-sm text-orange-800">{plan.supervisor_feedback}</p>
                                    {plan.supervisor_feedback_at && (
                                      <p className="text-xs text-orange-600 mt-2">
                                        Requested on {new Date(plan.supervisor_feedback_at).toLocaleDateString()}
                                      </p>
                                    )}
                                  </div>
                                )}
                                {/* Sent to client info */}
                                {plan.status === 'sent_to_client_for_signature' && (
                                  <div className="mb-3 p-3 bg-blue-50 border border-blue-300 rounded">
                                    <p className="text-sm text-blue-800">
                                      Waiting for client to sign via their portal access.
                                      {plan.sent_to_client_at && <span className="text-xs text-blue-600 ml-2">Sent {new Date(plan.sent_to_client_at).toLocaleDateString()}</span>}
                                    </p>
                                  </div>
                                )}
                                {/* Display PANs, Goals, Objectives - Compact Layout */}
                                {plan.pans && plan.pans.length > 0 ? (
                                  <div className="space-y-3">
                                    {plan.pans.map((pan, panIndex) => {
                                      const panKey = `${plan.id}-${pan.id}`;
                                      const isPANExpanded = expandedPANs.has(panKey);
                                      const panNumber = pan.pan_number || (panIndex + 1);
                                      const isEditable = plan.status === 'draft' || plan.status === 'needs_corrections' || (plan.status === 'active' && !plan.provider_signed_at);
                                      
                                      return (
                                        <div key={pan.id} className="border rounded-md bg-white" data-testid={`pan-${pan.id}`}>
                                          {/* Compact PAN Header */}
                                          <div
                                            className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b cursor-pointer hover:bg-slate-100 transition-colors"
                                            onClick={() => {
                                              const newExpanded = new Set(expandedPANs);
                                              if (isPANExpanded) {
                                                newExpanded.delete(panKey);
                                              } else {
                                                newExpanded.add(panKey);
                                              }
                                              setExpandedPANs(newExpanded);
                                            }}
                                          >
                                            {isPANExpanded ? (
                                              <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                                            ) : (
                                              <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
                                            )}
                                            <span className="text-xs font-bold text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded shrink-0">
                                              PAN {panNumber}
                                            </span>
                                            <span className="font-semibold text-sm text-gray-900 truncate">{pan.pan_name}</span>
                                            {pan.is_safety_related && (
                                              <Badge className="bg-red-100 text-red-700 border border-red-200 text-[10px] px-1.5 py-0 shrink-0">Safety</Badge>
                                            )}
                                            <span className="text-xs text-gray-400 ml-auto shrink-0">
                                              {pan.goals?.filter(g => !g.is_closed).length || 0} goal{(pan.goals?.filter(g => !g.is_closed).length || 0) !== 1 ? 's' : ''}
                                            </span>
                                            {isEditable && (
                                              <div className="flex gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                                                <button
                                                  className="text-xs text-red-400 hover:text-red-600 hover:bg-red-50 px-1 py-0.5 rounded transition-colors"
                                                  onClick={async () => {
                                                    const goalCount = pan.goals?.length || 0;
                                                    if (!confirm(`Delete this PAN? This will also delete ${goalCount} goal(s) and all associated objectives.`)) return;
                                                    try {
                                                      await axios.delete(`${API}/client-pans/${pan.id}`, {
                                                        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                                      });
                                                      toast.success('PAN deleted');
                                                      await refetchTreatmentPlans(selectedClient.id);
                                                    } catch (error) {
                                                      console.error('Error deleting PAN:', error);
                                                      toast.error('Failed to delete PAN');
                                                    }
                                                  }}
                                                  title="Delete PAN"
                                                  data-testid={`delete-pan-btn-${pan.id}`}
                                                >
                                                  <Trash2 className="h-3 w-3 inline" />
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                          
                                          {/* PAN Content - Goals & Objectives */}
                                          {isPANExpanded && (
                                            <div className="px-3 py-2">
                                              {pan.goals && pan.goals.filter(g => !g.is_closed).length > 0 ? (
                                                <div className="space-y-2">
                                                  {pan.goals.filter(g => !g.is_closed).map((goal, goalIdx) => {
                                                    const goalNumber = goalIdx + 1;
                                                    
                                                    return (
                                                      <div key={goal.id} className="border-l-2 border-purple-400 pl-3 py-1" data-testid={`goal-${goal.id}`}>
                                                        {/* Goal Header - always visible */}
                                                        <div className="flex items-start justify-between gap-2">
                                                          <div className="flex-1 min-w-0">
                                                            <div className="flex items-baseline gap-1.5">
                                                              <span className="text-xs font-bold text-purple-700 shrink-0">Goal {goalNumber}:</span>
                                                              <span className="text-sm text-gray-900">{goal.goal_text}</span>
                                                            </div>
                                                            {/* Client's Goal Description - always visible */}
                                                            {(goal.client_goal_description || goal.goal_in_clients_words) && (
                                                              <p className="text-xs text-gray-500 italic mt-0.5 ml-0.5" data-testid={`client-goal-desc-${goal.id}`}>
                                                                Client's words: "{goal.client_goal_description || goal.goal_in_clients_words}"
                                                              </p>
                                                            )}
                                                            {/* Duration info - compact inline */}
                                                            {(goal.duration_days || goal.target_end_date) && (
                                                              <p className="text-[11px] text-purple-500 mt-0.5 ml-0.5">
                                                                {goal.duration_days && <span>{goal.duration_days} days</span>}
                                                                {goal.target_end_date && <span> &middot; Target: {new Date(goal.target_end_date).toLocaleDateString()}</span>}
                                                              </p>
                                                            )}
                                                            {/* Barriers & Strengths */}
                                                            {(goal.barriers || goal.client_barriers) && (
                                                              <p className="text-[11px] text-gray-500 mt-0.5 ml-0.5">
                                                                <span className="font-medium text-gray-600">Barriers:</span> {goal.barriers || goal.client_barriers}
                                                              </p>
                                                            )}
                                                            {(goal.strengths || goal.client_strengths) && (
                                                              <p className="text-[11px] text-gray-500 mt-0.5 ml-0.5">
                                                                <span className="font-medium text-gray-600">Strengths:</span> {goal.strengths || goal.client_strengths}
                                                              </p>
                                                            )}
                                                          </div>
                                                          {/* Goal action buttons - visible with labels */}
                                                          {isEditable && (
                                                            <div className="flex items-center gap-1 shrink-0" data-testid={`goal-actions-${goal.id}`}>
                                                              <button
                                                                className="text-xs text-blue-500 hover:text-blue-700 px-1.5 py-0.5 rounded hover:bg-blue-50 flex items-center gap-0.5"
                                                                onClick={() => {
                                                                  setGoalToEdit({...goal, treatment_plan_id: plan.id});
                                                                  setShowEditGoalDialog(true);
                                                                }}
                                                                title="Edit goal"
                                                              >
                                                                <Pencil className="h-3 w-3" /> Edit
                                                              </button>
                                                              {plan.status === 'draft' && (
                                                                <button
                                                                  className="text-xs text-red-400 hover:text-red-600 px-1.5 py-0.5 rounded hover:bg-red-100 flex items-center gap-0.5"
                                                                  onClick={async () => {
                                                                    const objCount = goal.objectives?.length || 0;
                                                                    if (!confirm(`Delete Goal ${goalIdx + 1}${objCount > 0 ? ` and its ${objCount} objective(s)` : ''}?`)) return;
                                                                    try {
                                                                      await axios.delete(`${API}/client-goals/${goal.id}`, {
                                                                        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                                                      });
                                                                      toast.success('Goal deleted');
                                                                      await refetchTreatmentPlans(selectedClient.id);
                                                                    } catch (error) {
                                                                      console.error('Error deleting goal:', error);
                                                                      toast.error('Failed to delete goal');
                                                                    }
                                                                  }}
                                                                  title="Delete goal"
                                                                >
                                                                  <Trash2 className="h-3 w-3" />
                                                                </button>
                                                              )}
                                                              {plan.status !== 'draft' && (
                                                                <button
                                                                  className="text-xs text-orange-400 hover:text-orange-600 px-1.5 py-0.5 rounded hover:bg-orange-50 flex items-center gap-0.5"
                                                                  onClick={() => {
                                                                    setGoalToClose({...goal, goalIndex: goalIdx + 1, objectivesCount: goal.objectives?.length || 0});
                                                                    setGoalCloseReason('');
                                                                    setGoalCloseNotes('');
                                                                    setShowCloseGoalDialog(true);
                                                                  }}
                                                                  title="Close goal"
                                                                >
                                                                  <Archive className="h-3 w-3" /> Close
                                                                </button>
                                                              )}
                                                            </div>
                                                          )}
                                                        </div>
                                                        
                                                        {/* Objectives - always visible */}
                                                        {goal.objectives && goal.objectives.filter(o => !o.is_closed).length > 0 && (
                                                          <div className="mt-1.5 ml-2 space-y-1.5">
                                                            {goal.objectives.filter(o => !o.is_closed).map((obj, objIdx) => (
                                                              <div key={obj.id} className="text-xs border-t border-gray-200 pt-1.5 px-1" data-testid={`objective-${obj.id}`}>
                                                                <div className="flex items-start justify-between gap-1">
                                                                  <div className="flex-1 min-w-0">
                                                                    <div className="flex items-baseline gap-1">
                                                                      <span className="font-bold text-blue-600 shrink-0">Objective #{objIdx + 1}</span>
                                                                      <span className="text-gray-800">{obj.objective_text}</span>
                                                                    </div>
                                                                    {/* Interventions */}
                                                                    {obj.interventions?.length > 0 && (
                                                                      <div className="mt-1 ml-4">
                                                                        {obj.interventions.map((intervention, intIdx) => (
                                                                          <div key={intIdx} className="flex items-baseline gap-1 text-[11px] text-gray-600 flex-wrap">
                                                                            <span className="text-teal-500 shrink-0">Intervention {intIdx + 1}:</span>
                                                                            <span className="font-medium text-teal-700">
                                                                              {typeof intervention === 'string' ? intervention : intervention.name}
                                                                            </span>
                                                                            {typeof intervention === 'object' && intervention.description && (
                                                                              <span className="text-gray-500">: {intervention.description}</span>
                                                                            )}
                                                                            {typeof intervention === 'object' && intervention.modality && (
                                                                              <span className="text-gray-400">Modality: {intervention.modality}</span>
                                                                            )}
                                                                            {typeof intervention === 'object' && intervention.frequency && (
                                                                              <span className="text-gray-400">Frequency: {intervention.frequency}</span>
                                                                            )}
                                                                          </div>
                                                                        ))}
                                                                      </div>
                                                                    )}
                                                                    {!obj.interventions?.length && obj.intervention && (
                                                                      <p className="text-[11px] text-gray-500 ml-4 mt-0.5">
                                                                        <span className="text-teal-500">&rarr;</span> {obj.intervention}
                                                                      </p>
                                                                    )}
                                                                    {/* Compact metadata row - only show treatment_modality/frequency if not already in interventions */}
                                                                    {(obj.treatment_modality || obj.frequency) && (
                                                                      <div className="flex gap-2 mt-0.5 ml-4 text-[11px] text-gray-400">
                                                                        {obj.treatment_modality && <span>{obj.treatment_modality}</span>}
                                                                        {obj.frequency && <span>&middot; {obj.frequency}</span>}
                                                                      </div>
                                                                    )}
                                                                    {obj.treatment_description && (
                                                                      <p className="text-[11px] text-gray-400 italic mt-0.5 ml-4">
                                                                        <span className="font-medium not-italic text-gray-500">Treatment Details:</span> {obj.treatment_description}
                                                                      </p>
                                                                    )}
                                                                  </div>
                                                                  {/* Objective action buttons */}
                                                                  {isEditable && (
                                                                    <div className="flex gap-1 shrink-0">
                                                                      <button
                                                                        className="text-xs text-blue-500 hover:text-blue-700 px-1.5 py-0.5 rounded hover:bg-blue-100 flex items-center gap-0.5"
                                                                        onClick={() => {
                                                                          setObjectiveToEdit({...obj, goal_id: goal.id, treatment_plan_id: plan.id});
                                                                          setShowEditObjectiveDialog(true);
                                                                        }}
                                                                        title="Edit objective"
                                                                      >
                                                                        <Pencil className="h-3 w-3" /> Edit
                                                                      </button>
                                                                      <button
                                                                        className="text-xs text-red-400 hover:text-red-600 px-1.5 py-0.5 rounded hover:bg-red-100 flex items-center gap-0.5"
                                                                        onClick={async () => {
                                                                          if (!confirm(`Delete Objective ${goalNumber}.${objIdx + 1}?`)) return;
                                                                          try {
                                                                            await axios.delete(`${API}/client-objectives/${obj.id}`, {
                                                                              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                                                            });
                                                                            toast.success('Objective deleted');
                                                                            await refetchTreatmentPlans(selectedClient.id);
                                                                          } catch (error) {
                                                                            toast.error('Failed to delete objective');
                                                                          }
                                                                        }}
                                                                        title="Delete objective"
                                                                      >
                                                                        <Trash2 className="h-3 w-3" />
                                                                      </button>
                                                                    </div>
                                                                  )}
                                                                </div>
                                                              </div>
                                                            ))}
                                                          </div>
                                                        )}
                                                        
                                                        {/* Inline Add Objective button */}
                                                        {isEditable && (
                                                          <button
                                                            className="text-[11px] text-blue-500 hover:text-blue-700 ml-2 mt-1 flex items-center gap-0.5 hover:bg-blue-50 px-1.5 py-0.5 rounded transition-colors"
                                                            onClick={async () => {
                                                              setSelectedTreatmentPlan(plan);
                                                              setSelectedGoalForObjective(goal);
                                                              try {
                                                                const response = await axios.get(`${API}/treatment-planning/goals/${goal.goal_id}/objectives`);
                                                                setAvailableObjectives(response.data);
                                                              } catch (e) {
                                                                setAvailableObjectives([]);
                                                              }
                                                              setShowAddObjectiveDialog(true);
                                                            }}
                                                            data-testid={`add-objective-btn-${goal.id}`}
                                                          >
                                                            <Plus className="h-3 w-3" /> Add Objective
                                                          </button>
                                                        )}
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              ) : (
                                                <p className="text-xs text-gray-400 py-1">No goals added yet</p>
                                              )}
                                              
                                              {/* Add Goal button - below goals list */}
                                              {isEditable && (
                                                <button
                                                  className="text-[11px] text-purple-500 hover:text-purple-700 mt-2 flex items-center gap-0.5 hover:bg-purple-50 px-1.5 py-0.5 rounded transition-colors"
                                                  onClick={async () => {
                                                    setSelectedTreatmentPlan(plan);
                                                    setSelectedPANForGoal(pan);
                                                    try {
                                                      const response = await axios.get(
                                                        `${API}/treatment-planning/pans/${pan.pan_id}/goals`,
                                                        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                                                      );
                                                      setAvailableGoals(response.data);
                                                      setShowAddGoalDialog(true);
                                                    } catch (error) {
                                                      console.error('Error fetching goals:', error);
                                                      toast.error('Failed to load goal templates');
                                                    }
                                                  }}
                                                  data-testid={`add-goal-btn-${pan.id}`}
                                                >
                                                  <Plus className="h-3 w-3" /> Add Goal
                                                </button>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="text-center text-gray-500 py-4">No PANs added yet. Click "+ Add PAN" below to identify treatment needs.</p>
                                )}
                                
                                {/* Add PAN button - only for draft/needs_corrections plans */}
                                {(plan.status === 'draft' || plan.status === 'needs_corrections') && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    data-testid={`add-pan-btn-${plan.id}`}
                                    onClick={async () => {
                                      setSelectedTreatmentPlan(plan);
                                      const response = await axios.get(`${API}/treatment-planning/pans`);
                                      setAvailablePANs(response.data);
                                      setShowAddPANDialog(true);
                                    }}
                                    className="w-full mt-2 border-dashed text-xs h-8"
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add PAN
                                  </Button>
                                )}

                                {/* ===== CLINICAL CONTEXT SECTION ===== */}
                                {(() => {
                                  const hasClinicalContext = plan.medications_snapshot?.length > 0 || 
                                    plan.diagnoses_snapshot?.length > 0 ||
                                    plan.level_of_care_notes || plan.trauma_reports || plan.supports;
                                  const notCapturedMsg = <p className="text-xs text-gray-400 italic">Not captured at time of plan creation</p>;
                                  
                                  return (
                                    <div className="mt-3">
                                      <div className="border rounded-md bg-white">
                                      {/* Clinical Context Header - matches PAN bar style */}
                                      <div
                                        className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b cursor-pointer hover:bg-slate-100 transition-colors rounded-t-md"
                                        onClick={() => setClinicalContextCollapsed(prev => {
                                          const next = new Set(prev);
                                          if (next.has(plan.id)) next.delete(plan.id);
                                          else next.add(plan.id);
                                          return next;
                                        })}
                                      >
                                        {clinicalContextCollapsed.has(plan.id) ? (
                                          <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
                                        ) : (
                                          <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                                        )}
                                        <span className="text-xs font-semibold text-gray-700">Clinical Context</span>
                                      </div>
                                      
                                      {!clinicalContextCollapsed.has(plan.id) && (
                                      <div className="p-3">
                                      <TreatmentPlanClinicalContextReadOnly
                                        plan={plan}
                                        formTemplates={formTemplates}
                                        notCapturedMsg={notCapturedMsg}
                                      />
                                      </div>
                                      )}
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* Version History Section - show for all non-draft plans */}
                                {plan.status !== 'draft' && (
                                  <div className="mt-4 pt-4 border-t">
                                    <button
                                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                                      onClick={async () => {
                                        const historyKey = plan.id;
                                        if (expandedVersionHistory.has(historyKey)) {
                                          // Collapse
                                          const newSet = new Set(expandedVersionHistory);
                                          newSet.delete(historyKey);
                                          setExpandedVersionHistory(newSet);
                                        } else {
                                          // Expand and fetch version history if not already loaded
                                          const newSet = new Set(expandedVersionHistory);
                                          newSet.add(historyKey);
                                          setExpandedVersionHistory(newSet);
                                          
                                          if (!versionHistoryData[historyKey]) {
                                            try {
                                              const response = await axios.get(
                                                `${API}/treatment-plans/${plan.id}/version-history`,
                                                {
                                                  headers: {
                                                    Authorization: `Bearer ${sessionStorage.getItem('token')}`
                                                  }
                                                }
                                              );
                                              setVersionHistoryData({
                                                ...versionHistoryData,
                                                [historyKey]: response.data
                                              });
                                            } catch (error) {
                                              console.error('Error fetching version history:', error);
                                              toast.error('Failed to load version history');
                                            }
                                          }
                                        }
                                      }}
                                    >
                                      {expandedVersionHistory.has(plan.id) ? (
                                        <ChevronUp className="h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4" />
                                      )}
                                      <History className="h-4 w-4" />
                                      Version History (v{plan.version_number || plan.version || '1.0'})
                                    </button>
                                    
                                    {expandedVersionHistory.has(plan.id) && versionHistoryData[plan.id] && (
                                      <div className="mt-3 space-y-2">
                                        {versionHistoryData[plan.id].map((version, idx) => (
                                          <div
                                            key={version.id}
                                            className={`p-3 rounded-lg border ${
                                              version.is_current
                                                ? 'bg-green-50 border-green-300'
                                                : 'bg-gray-50 border-gray-200'
                                            }`}
                                          >
                                            <div className="flex items-start justify-between">
                                              <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                  <span className="font-semibold text-sm">
                                                    Version {version.version_number}
                                                  </span>
                                                  {version.is_current && (
                                                    <Badge className="bg-green-600">Current</Badge>
                                                  )}
                                                  <span className="text-xs text-gray-500">
                                                    {new Date(version.created_at).toLocaleDateString()}
                                                  </span>
                                                </div>
                                                {version.review_notes && (
                                                  <p className="text-xs text-gray-600 mt-1">
                                                    {version.review_notes}
                                                  </p>
                                                )}
                                                {version.edit_history && version.edit_history.length > 0 && (
                                                  <div className="mt-2 space-y-1">
                                                    {version.edit_history.slice(-3).map((edit, editIdx) => (
                                                      <div key={editIdx} className="text-xs text-gray-500 flex items-start gap-2">
                                                        <span className="font-medium">
                                                          {edit.edit_type === 'minor' ? '📝 Minor Edit' : '🔄 Major Review'}
                                                        </span>
                                                        <span>•</span>
                                                        <span>{edit.notes}</span>
                                                        <span>•</span>
                                                        <span>{new Date(edit.edited_at).toLocaleDateString()}</span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                              {version.pdf_available && (
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="ml-2"
                                                  onClick={async () => {
                                                    try {
                                                      const response = await axios.get(
                                                        `${API}/treatment-plans/${version.id}/pdf`,
                                                        {
                                                          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
                                                          responseType: 'blob'
                                                        }
                                                      );
                                                      const blob = new Blob([response.data], { type: 'application/pdf' });
                                                      const url = window.URL.createObjectURL(blob);
                                                      setDocumentBlobUrl(url);
                                                      // Generate filename with version date
                                                      const versionDate = version.created_at ? new Date(version.created_at) : new Date();
                                                      const versionDocType = `Treatment Plan v${version.version_number} Dated ${String(versionDate.getMonth() + 1).padStart(2, '0')}-${String(versionDate.getDate()).padStart(2, '0')}-${versionDate.getFullYear()}`;
                                                      
                                                      setViewingDocument({
                                                        id: version.id,
                                                        filename: generateDownloadFilename(selectedClient, versionDocType),
                                                        file_type: 'application/pdf'
                                                      });
                                                    } catch (error) {
                                                      console.error('Failed to load PDF:', error);
                                                      toast.error('Failed to load PDF');
                                                    }
                                                  }}
                                                >
                                                  <FileText className="h-3 w-3 mr-1" />
                                                  View PDF
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Signatures — shows who signed and when, so supervisors can confirm
                                    supervisee signing at a glance without opening the PDF. */}
                                {(plan.provider_signed_at || plan.supervisor_signed_at || plan.client_signed_at) && (
                                  <div className="mt-4 pt-3 border-t border-gray-200" data-testid={`plan-signatures-${plan.id}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                      <FileText className="h-4 w-4 text-gray-500" />
                                      <h4 className="text-sm font-semibold text-gray-700">Signatures</h4>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                      {/* Provider / Supervisee */}
                                      {plan.provider_signed_at && (() => {
                                        const signerUser = users.find(u => u.id === (plan.provider_signed_by || plan.assigned_provider_id));
                                        const signerLabel = signerUser
                                          ? `${signerUser.first_name || ''} ${signerUser.last_name || ''}`.trim() + (signerUser.credentials ? `, ${signerUser.credentials}` : '')
                                          : (plan.provider_signed_by_name || plan.assigned_provider_name || 'Provider');
                                        return (
                                          <div className="border rounded-md p-2 bg-blue-50 border-blue-200" data-testid={`plan-sig-provider-${plan.id}`}>
                                            <div className="flex items-center justify-between mb-1">
                                              <span className="text-xs font-semibold text-blue-800">Provider{plan.requires_supervisor_signature ? ' (supervisee)' : ''}</span>
                                              <Badge variant="outline" className="text-[10px] h-4 border-blue-300 text-blue-700">Signed</Badge>
                                            </div>
                                            <p className="text-xs text-gray-800 truncate" title={signerLabel}>{signerLabel}</p>
                                            <p className="text-[11px] text-gray-500 mt-0.5">{new Date(plan.provider_signed_at).toLocaleString()}</p>
                                            {plan.provider_signature && (
                                              <div className="mt-1.5 bg-white border rounded p-1 flex items-center justify-center h-14 overflow-hidden">
                                                <img src={plan.provider_signature} alt="Provider signature" className="max-h-12 max-w-full object-contain" />
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      {/* Supervisor */}
                                      {plan.requires_supervisor_signature && (plan.supervisor_signed_at || plan.provider_signed_at) && (() => {
                                        const signerUser = plan.supervisor_signed_by ? users.find(u => u.id === plan.supervisor_signed_by) : null;
                                        const signerLabel = signerUser
                                          ? `${signerUser.first_name || ''} ${signerUser.last_name || ''}`.trim() + (signerUser.credentials ? `, ${signerUser.credentials}` : '')
                                          : (plan.supervisor_signed_by_name || 'Supervisor');
                                        const isSigned = !!plan.supervisor_signed_at;
                                        return (
                                          <div className={`border rounded-md p-2 ${isSigned ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200 border-dashed'}`} data-testid={`plan-sig-supervisor-${plan.id}`}>
                                            <div className="flex items-center justify-between mb-1">
                                              <span className={`text-xs font-semibold ${isSigned ? 'text-indigo-800' : 'text-gray-500'}`}>Supervisor</span>
                                              {isSigned
                                                ? <Badge variant="outline" className="text-[10px] h-4 border-indigo-300 text-indigo-700">Signed</Badge>
                                                : <Badge variant="outline" className="text-[10px] h-4 border-gray-300 text-gray-500">Pending</Badge>}
                                            </div>
                                            {isSigned ? (
                                              <>
                                                <p className="text-xs text-gray-800 truncate" title={signerLabel}>{signerLabel}</p>
                                                <p className="text-[11px] text-gray-500 mt-0.5">{new Date(plan.supervisor_signed_at).toLocaleString()}</p>
                                                {plan.supervisor_signature && (
                                                  <div className="mt-1.5 bg-white border rounded p-1 flex items-center justify-center h-14 overflow-hidden">
                                                    <img src={plan.supervisor_signature} alt="Supervisor signature" className="max-h-12 max-w-full object-contain" />
                                                  </div>
                                                )}
                                              </>
                                            ) : (
                                              <p className="text-[11px] text-gray-500 italic">Awaiting supervisor signature</p>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      {/* Client */}
                                      {(plan.client_signed_at || plan.provider_signed_at) && !plan.skip_client_signature && (
                                        <div className={`border rounded-md p-2 ${plan.client_signed_at ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200 border-dashed'}`} data-testid={`plan-sig-client-${plan.id}`}>
                                          <div className="flex items-center justify-between mb-1">
                                            <span className={`text-xs font-semibold ${plan.client_signed_at ? 'text-green-800' : 'text-gray-500'}`}>Client</span>
                                            {plan.client_signed_at
                                              ? (plan.paper_signatures
                                                  ? <Badge variant="outline" className="text-[10px] h-4 border-amber-400 text-amber-800 bg-amber-50" title="Signed on paper outside the system. No digital signature image captured.">Signed (Paper)</Badge>
                                                  : <Badge variant="outline" className="text-[10px] h-4 border-green-300 text-green-700">Signed</Badge>)
                                              : <Badge variant="outline" className="text-[10px] h-4 border-gray-300 text-gray-500">Pending</Badge>}
                                          </div>
                                          {plan.client_signed_at ? (
                                            <>
                                              <p className="text-xs text-gray-800 truncate">{selectedClient ? `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim() : 'Client'}</p>
                                              <p className="text-[11px] text-gray-500 mt-0.5">{new Date(plan.client_signed_at).toLocaleString()}</p>
                                              {/* Feb 20 2026 — when paper_signatures=true the plan was
                                                  finalized via the "Finalize (Paper Signatures)" action
                                                  which records NO digital signature image.  Show a clear
                                                  notice so the user knows why the signature box is empty,
                                                  with a "Capture digital signature" recovery action that
                                                  routes back into the in-system signing flow. */}
                                              {plan.client_signature ? (
                                                <div className="mt-1.5 bg-white border rounded p-1 flex items-center justify-center h-14 overflow-hidden">
                                                  <img src={plan.client_signature} alt="Client signature" className="max-h-12 max-w-full object-contain" />
                                                </div>
                                              ) : plan.paper_signatures ? (
                                                <p className="text-[11px] text-amber-800 italic mt-1.5 bg-amber-50 border border-amber-200 rounded px-1.5 py-1" data-testid={`plan-paper-signed-notice-${plan.id}`}>
                                                  Signed on paper — no digital signature image on file. The paper-signed PDF is attached to this plan.
                                                </p>
                                              ) : (
                                                <p className="text-[11px] text-amber-800 italic mt-1.5 bg-amber-50 border border-amber-200 rounded px-1.5 py-1">
                                                  No digital signature image on file — the plan was marked signed without capturing a signature.
                                                </p>
                                              )}
                                            </>
                                          ) : (
                                            <p className="text-[11px] text-gray-500 italic">Awaiting client signature</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Current Version Display - Always visible at bottom of card */}
                                {plan.version && plan.version > 1 && (
                                  <div className="mt-4 pt-3 border-t border-gray-200 bg-gradient-to-r from-teal-50 to-transparent rounded-b-lg px-3 py-2">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-teal-800 bg-teal-100 px-2 py-0.5 rounded">
                                          Version {plan.version}.0
                                        </span>
                                        <span className="text-xs text-gray-600">
                                          {plan.major_review_date ? (
                                            <>Major review on {new Date(plan.major_review_date).toLocaleDateString()}</>
                                          ) : plan.updated_at ? (
                                            <>Updated {new Date(plan.updated_at).toLocaleDateString()}</>
                                          ) : null}
                                        </span>
                                      </div>
                                      {plan.previous_version_id && (
                                        <button
                                          className="text-xs text-teal-700 hover:text-teal-900 underline"
                                          onClick={async () => {
                                            const historyKey = plan.id;
                                            if (!expandedVersionHistory.has(historyKey)) {
                                              const newSet = new Set(expandedVersionHistory);
                                              newSet.add(historyKey);
                                              setExpandedVersionHistory(newSet);
                                              
                                              if (!versionHistoryData[historyKey]) {
                                                try {
                                                  const response = await axios.get(
                                                    `${API}/treatment-plans/${plan.id}/version-history`,
                                                    {
                                                      headers: {
                                                        Authorization: `Bearer ${sessionStorage.getItem('token')}`
                                                      }
                                                    }
                                                  );
                                                  setVersionHistoryData({
                                                    ...versionHistoryData,
                                                    [historyKey]: response.data
                                                  });
                                                } catch (error) {
                                                  console.error('Error fetching version history:', error);
                                                }
                                              }
                                            }
                                          }}
                                        >
                                          View previous versions
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </CardContent>)}
                            </Card>
                          ))}
                          
                          {/* Archived Treatment Plans Section - Collapsed by default */}
                          {treatmentPlans.filter(p => p.status === 'archived').length > 0 && (
                            <div className="mt-8 pt-6 border-t-2 border-gray-200">
                              <button
                                className="w-full flex items-center gap-2 text-lg font-semibold text-gray-500 hover:text-gray-700 transition-colors"
                                onClick={() => setArchivedPlansExpanded(!archivedPlansExpanded)}
                              >
                                {archivedPlansExpanded ? (
                                  <ChevronUp className="h-5 w-5" />
                                ) : (
                                  <ChevronDown className="h-5 w-5" />
                                )}
                                <Archive className="h-5 w-5" />
                                Archived Treatment Plans ({treatmentPlans.filter(p => p.status === 'archived').length})
                              </button>
                              
                              {archivedPlansExpanded && (
                              <div className="space-y-4 mt-4">
                                {treatmentPlans.filter(p => p.status === 'archived').map(plan => (
                                  <Card key={plan.id} className="border border-gray-300 bg-gray-50 opacity-75">
                                    <CardHeader className="bg-gray-100 py-3">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <CardTitle className="text-base text-gray-600">
                                            {serviceTypes.find(st => st.id === plan.service_type)?.display_name || plan.service_type?.replace(/_/g, ' ').toUpperCase() || 'Treatment Plan'}
                                          </CardTitle>
                                          <p className="text-xs text-gray-500 mt-1">
                                            Provider: {users.find(u => u.id === plan.assigned_provider_id)?.full_name || 'Unknown'} |
                                            Archived: {plan.archived_at ? new Date(plan.archived_at).toLocaleDateString() : 'Unknown date'}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Badge className="bg-gray-500">Archived</Badge>
                                          {/* Restore button - same permissions as archive */}
                                          {(() => {
                                            const isOwnPlan = plan.assigned_provider_id === user?.id;
                                            const isAdmin = user?.role?.toLowerCase() === 'admin' || isSuperAdmin(user);
                                            const planProvider = users.find(u => u.id === plan.assigned_provider_id);
                                            const isSupervisorOfProvider = user?.is_supervisor && planProvider?.supervisor_ids?.includes(user?.id);
                                            
                                            const canRestore = isOwnPlan || isAdmin || isSupervisorOfProvider;
                                            
                                            if (!canRestore) return null;
                                            
                                            return (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-xs bg-white hover:bg-teal-50 border-teal-300 text-teal-700"
                                                title="Restore this treatment plan to active"
                                                onClick={async () => {
                                                  const confirmed = window.confirm(
                                                    `Restore Treatment Plan?\n\n` +
                                                    `This will:\n` +
                                                    `• Move this plan back to active status\n` +
                                                    `• Make it the current plan for this service\n\n` +
                                                    `Note: If there's already an active plan for this service, you'll need to archive it first.\n\n` +
                                                    `Continue?`
                                                  );
                                                  
                                                  if (confirmed) {
                                                    try {
                                                      await axios.post(
                                                        `${API}/treatment-plans/${plan.id}/restore`,
                                                        {},
                                                        {
                                                          headers: {
                                                            Authorization: `Bearer ${sessionStorage.getItem('token')}`
                                                          }
                                                        }
                                                      );
                                                      toast.success('Treatment plan restored to active');
                                                      await refetchTreatmentPlans(selectedClient.id);
                                                    } catch (error) {
                                                      console.error('Error restoring treatment plan:', error);
                                                      toast.error(error.response?.data?.detail || 'Failed to restore treatment plan');
                                                    }
                                                  }
                                                }}
                                              >
                                                <RotateCcw className="h-3 w-3 mr-1" />
                                                Restore
                                              </Button>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </CardHeader>
                                    <CardContent className="py-3">
                                      <p className="text-sm text-gray-500">
                                        This treatment plan has been archived and is no longer active.
                                        {plan.provider_signed_at && (
                                          <span className="block mt-1">Originally signed on {new Date(plan.provider_signed_at).toLocaleDateString()}</span>
                                        )}
                                      </p>
                                      <div className="flex gap-2 mt-2">
                                      {plan.pdf_snapshot_path ? (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={async () => {
                                            try {
                                              const response = await axios.get(
                                                `${API}/treatment-plans/${plan.id}/pdf`,
                                                {
                                                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
                                                  responseType: 'blob'
                                                }
                                              );
                                              const blob = new Blob([response.data], { type: 'application/pdf' });
                                              const url = window.URL.createObjectURL(blob);
                                              setDocumentBlobUrl(url);
                                              setViewingDocument({
                                                id: plan.id,
                                                filename: generateDownloadFilename(selectedClient, 'Archived Treatment Plan'),
                                                file_type: 'application/pdf'
                                              });
                                            } catch (error) {
                                              console.error('Failed to load archived plan PDF:', error);
                                              toast.error('Failed to load PDF');
                                            }
                                          }}
                                        >
                                          <FileText className="h-4 w-4 mr-1" />
                                          View Archived PDF
                                        </Button>
                                      ) : (
                                        <p className="text-xs text-gray-400 italic">
                                          No PDF available - this plan was archived before being signed
                                        </p>
                                      )}
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                  {/* Add/Edit Medication Dialog */}
                  {/* Add/Edit Medication Dialog - Extracted Component */}
                  <AddEditMedicationDialog
                    open={showMedicationForm}
                    onOpenChange={(open) => {
                      setShowMedicationForm(open);
                      if (!open) {
                        setEditingMedication(null);
                      }
                    }}
                    client={editingClient || selectedClient}
                    editingMedication={editingMedication}
                    onSuccess={async (clientId, options) => {
                      // Refresh medications list
                      try {
                        const medsRes = await axios.get(`${API}/clients/${clientId}/medications`);
                        const activeMeds = (medsRes.data || []).filter(m => m.is_active !== false);
                        
                        setClientMedications(prev => {
                          // Replace medications for this client, keep others
                          const otherMeds = prev.filter(m => m.client_id !== clientId);
                          return [...otherMeds, ...(medsRes.data || [])];
                        });
                        
                        // Also update intakeData.medications if intake wizard is open and editing this client
                        if (showIntakeWizard && editingClient && editingClient.id === clientId) {
                          handleIntakeFieldChange('medications', activeMeds.map(m => ({
                            id: m.id,
                            name: m.medication_name || m.name || '',
                            dosage: m.dosage || '',
                            frequency: m.frequency || '',
                            prescriber: m.prescriber || '',
                            is_active: true
                          })));
                        }
                        
                        toast.success('Medication saved successfully');
                      } catch (error) {
                        console.error('Failed to refresh medications:', error);
                      }
                      // Only navigate if not in note editing/creation context
                      if (!selectedNote && !showAddNote) {
                        handleClientClick(clientId, options);
                      }
                    }}
                  />


                <TabsContent value="authorizations" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                  <AuthorizationsTab
                    selectedClient={selectedClient}
                    clientAuthorizations={clientAuthorizations}
                    clientEnrollments={clientEnrollments}
                    enrollmentsLoaded={enrollmentsLoaded}
                    serviceTypes={serviceTypes}
                    setServiceTypes={setServiceTypes}
                    cptCodes={cptCodes}
                    providers={providers}
                    users={users}
                    appointments={appointments}
                    showAuthorizationForm={showAuthorizationForm}
                    setShowAuthorizationForm={setShowAuthorizationForm}
                    authorizationFormData={authorizationFormData}
                    setAuthorizationFormData={setAuthorizationFormData}
                    expandedAuthId={expandedAuthId}
                    setExpandedAuthId={setExpandedAuthId}
                    setEditingAppointment={setEditingAppointment}
                    setShowAppointmentForm={setShowAppointmentForm}
                    setClientDetailTab={setClientDetailTab}
                    handleClientClick={handleClientClick}
                  />
                </TabsContent>

                {/* Assessments Tab - Score Tracking */}
                <TabsContent value="assessments" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                  <AssessmentsTabContent 
                    clientId={selectedClient?.id}
                    token={sessionStorage.getItem('token')}
                  />
                </TabsContent>

                <TabsContent value="groups" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-lg">Group History</CardTitle>
                      <Button
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-700"
                        onClick={() => {
                          setRequestGroupData({
                            client_id: selectedClient.id,
                            group_id: '',
                            request_reason: ''
                          });
                          setShowRequestGroupDialog(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Request Group
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {/* Pending Admission Requests for This Client */}
                        {pendingGroupRequests.filter(r => r.client_id === selectedClient.id).length > 0 && (
                          <div>
                            <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                              <Clock className="h-5 w-5 text-orange-500" />
                              Pending Requests
                              <Badge className="bg-orange-500 text-white">
                                {pendingGroupRequests.filter(r => r.client_id === selectedClient.id).length}
                              </Badge>
                            </h4>
                            <div className="space-y-3">
                              {pendingGroupRequests
                                .filter(r => r.client_id === selectedClient.id)
                                .map(request => {
                                  const requestGroup = clientGroups.find(g => g.id === request.group_id);
                                  const requestedBy = users.find(u => u.id === request.requested_by);
                                  return (
                                    <div key={request.id} className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-lg">{requestGroup?.name || 'Unknown Group'}</span>
                                        <Badge className="bg-orange-100 text-orange-800">⏳ Awaiting Approval</Badge>
                                      </div>
                                      <div className="text-sm text-gray-600 space-y-1">
                                        <p>
                                          <strong>Requested by:</strong> {requestedBy?.full_name || 'Unknown'} on {new Date(request.created_at).toLocaleDateString()}
                                        </p>
                                        {request.request_reason && (
                                          <p><strong>Reason:</strong> {request.request_reason}</p>
                                        )}
                                        {requestGroup?.schedule_day && requestGroup?.start_time && (
                                          <p><strong>Schedule:</strong> {requestGroup.schedule_day}s at {formatTime12Hour(requestGroup.start_time)}</p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}

                        {/* Active Groups */}
                        {clientGroups.some(g => g.members?.some(m => m.client_id === selectedClient.id && m.status === 'active') && g.status === 'active') && (
                          <div>
                            <h4 className="font-semibold text-lg mb-3">Active Groups</h4>
                            <div className="space-y-3">
                              {clientGroups
                                .filter(g => g.members?.some(m => m.client_id === selectedClient.id && m.status === 'active') && g.status === 'active')
                                .map(group => {
                                  const member = group.members.find(m => m.client_id === selectedClient.id);
                                  return (
                                    <div key={group.id} className="p-4 bg-green-50 rounded-lg border border-green-200">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-lg">{group.name}</span>
                                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                                      </div>
                                      <div className="text-sm text-gray-600 space-y-1">
                                        {member.joined_date && (
                                          <p><strong>Start Date:</strong> {new Date(member.joined_date).toLocaleDateString()}</p>
                                        )}
                                        <p><strong>Sessions:</strong> {member.sessions_attended || 0} attended / {group.total_sessions || 'Ongoing'} total</p>
                                        {group.schedule_day && group.start_time && (
                                          <p><strong>Schedule:</strong> {group.schedule_day}s at {formatTime12Hour(group.start_time)}</p>
                                        )}
                                        {group.modality && (
                                          <p><strong>Modality:</strong> {group.modality === 'in_person' ? '📍 In-Person' : group.modality === 'telehealth' ? '💻 Telehealth' : '🔄 Hybrid'}</p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}

                        {/* Former Groups / Group History (all non-active memberships in grayscale) */}
                        {clientGroups.some(g => g.members?.some(m => m.client_id === selectedClient.id && m.status !== 'active')) && (
                          <div>
                            <h4 className="font-semibold text-lg mb-3 text-gray-700">Former Groups</h4>
                            <div className="space-y-3">
                              {clientGroups
                                .filter(g => g.members?.some(m => m.client_id === selectedClient.id && m.status !== 'active'))
                                .map(group => {
                                  const member = group.members.find(m => m.client_id === selectedClient.id);
                                  return (
                                    <div key={group.id} className="p-4 bg-gray-100 rounded-lg border border-gray-300">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-lg text-gray-700">{group.name}</span>
                                        <Badge className="bg-gray-300 text-gray-700 capitalize">
                                          {member.status === 'completed' ? '✓ Completed' : member.status === 'removed' ? 'Removed' : member.status}
                                        </Badge>
                                      </div>
                                      <div className="text-sm text-gray-600 space-y-1">
                                        {member.joined_date && (
                                          <p><strong>Start Date:</strong> {new Date(member.joined_date).toLocaleDateString()}</p>
                                        )}
                                        {member.exit_date && (
                                          <p><strong>{member.status === 'completed' ? 'Completion' : 'End'} Date:</strong> {new Date(member.exit_date).toLocaleDateString()}</p>
                                        )}
                                        <p><strong>Sessions:</strong> {member.sessions_attended || 0} attended / {member.total_sessions || group.total_sessions || 'N/A'} total</p>
                                        {member.exit_reason && (
                                          <p><strong>Reason:</strong> {member.exit_reason.replace(/_/g, ' ')}</p>
                                        )}
                                        {member.exit_notes && (
                                          <p className="text-xs bg-white p-2 rounded mt-1"><strong>Notes:</strong> {member.exit_notes}</p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}

                        {/* No groups and no pending requests */}
                        {!clientGroups.some(g => g.members?.some(m => m.client_id === selectedClient.id)) && 
                         !pendingGroupRequests.some(r => r.client_id === selectedClient.id) && (
                          <div className="text-center py-8">
                            <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                            <p className="text-gray-500 mb-3">No group history</p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-teal-600 text-teal-600 hover:bg-teal-50"
                              onClick={() => {
                                setRequestGroupData({
                                  client_id: selectedClient.id,
                                  group_id: '',
                                  request_reason: ''
                                });
                                setShowRequestGroupDialog(true);
                              }}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Request Group Placement
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="appointments" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-lg">Appointments</CardTitle>
                      <Button
                        size="sm"
                        onClick={() => {
                          // Pre-fill client and set provider
                          // If client has assigned providers, use the first one. Otherwise, use current user if they're a provider
                          const assignedProviders = selectedClient.assigned_providers || [];
                          const defaultProviderId = assignedProviders.length > 0 
                            ? assignedProviders[0] 
                            : (userRoleName === 'provider' ? user.id : '');
                            
                          // If we have a provider, get their default location and POS
                          const selectedProvider = users?.find(u => u.id === defaultProviderId);
                          
                          const now = new Date();
                          const startTime = formatLocalDateTime(now);
                          const endTime = formatLocalDateTime(new Date(now.getTime() + 60 * 60000)); // 1 hour later
                          
                          setAppointmentFormData({
                            client_id: selectedClient.id,
                            client_ids: [],
                            selected_group_id: '',
                            provider_id: defaultProviderId,
                            start_time: startTime,
                            end_time: endTime,
                            service_type: '',
                            appointment_type: 'client_session',
                            status: 'scheduled',
                            is_billable: true,
                            is_payroll_eligible: false,
                            units: 1,
                            location: selectedProvider?.default_location || '',
                            pos_code: selectedProvider?.default_pos_code || '',
                            notes: '',
                            cpt_code_id: '',
                            is_recurring: false,
                            recurrence_pattern: '',
                            recurrence_count: '',
                            recurrence_end_date: '',
                            selectedCptMeta: null
                          });
                          setEditingAppointment(null); // Ensure we're in create mode
                          setShowAppointmentForm(true);
                        }}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Schedule Appointment
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {/* Upcoming Appointments - Collapsible */}
                      <div className="mb-6">
                        <div 
                          className="flex items-center gap-2 cursor-pointer mb-3 hover:bg-gray-50 -mx-2 px-2 py-1 rounded"
                          onClick={() => setShowClientUpcomingAppts(!showClientUpcomingAppts)}
                        >
                          {showClientUpcomingAppts ? (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-500" />
                          )}
                          <h4 className="font-semibold text-gray-700">
                            Upcoming Appointments
                            <span className="ml-2 text-sm font-normal text-gray-500">
                              ({appointments.filter(apt => (apt.client_id === selectedClient.id || apt.client_ids?.includes(selectedClient.id)) && new Date(apt.start_time) >= new Date()).length})
                            </span>
                          </h4>
                        </div>
                        {showClientUpcomingAppts && (() => {
                          const upcomingList = appointments
                            .filter(apt => (apt.client_id === selectedClient.id || apt.client_ids?.includes(selectedClient.id)) && new Date(apt.start_time) >= new Date())
                            .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
                          const displayList = showAllUpcomingAppts ? upcomingList : upcomingList.slice(0, 5);
                          const hiddenCount = upcomingList.length - 5;
                          return (
                        <div className="space-y-2">
                          {displayList.map(apt => {
                              const startTime = new Date(apt.start_time);
                              const providerName = getProviderNames([apt.provider_id]);
                              return (
                                <div 
                                  key={apt.id} 
                                  className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                                >
                                  <div>
                                    <p className={`font-medium ${apt.google_meet_link ? 'text-[#10B981]' : ''}`}>{apt.service_type}</p>
                                    <p className={`text-sm ${apt.google_meet_link ? 'text-[#10B981]' : 'text-gray-600'}`}>
                                      {startTime.toLocaleDateString()} at {startTime.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                    </p>
                                    <p className={`text-xs ${apt.google_meet_link ? 'text-[#10B981]' : 'text-gray-500'}`}>Provider: {providerName}</p>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Badge className={getStatusBadgeColor(apt.status)}>{apt.status}</Badge>
                                    {/* Vitals Check Badge */}
                                    {apt.has_vitals_check && apt.vitals_check_provider_id === user?.id && (
                                      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 flex items-center gap-1">
                                        <Activity className="h-3 w-3" />
                                        Vitals
                                      </Badge>
                                    )}
                                    {apt.google_meet_link && (
                                      <Button size="sm" variant="outline" asChild onClick={(e) => e.stopPropagation()}>
                                        <a href={apt.google_meet_link} target="_blank" rel="noopener noreferrer">
                                          <Video className="h-3 w-3 mr-1" />
                                          Join
                                        </a>
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        
                                        // Check if user can edit this appointment
                                        if (!canEditAppointment(apt)) {
                                          toast.error('You do not have permission to edit this appointment');
                                          return;
                                        }
                                        
                                        // Find CPT code metadata
                                        const aptCptCode = [...cptCodes, ...cptBundles].find(c =>
                                          c.code === apt.service_type ||
                                          c.display_name === apt.service_type ||
                                          c.id === apt.cpt_code_id ||
                                          c.id === apt.cpt_bundle_id
                                        );
                                        
                                        setAppointmentFormData({
                                          client_id: apt.client_id || '',
                                          client_ids: apt.client_ids || [],
                                          selected_group_id: apt.selected_group_id || '',
                                          provider_id: apt.provider_id,
                                          start_time: formatLocalDateTime(apt.start_time),
                                          end_time: formatLocalDateTime(apt.end_time),
                                          service_type: apt.service_type,
                                          appointment_type: apt.appointment_type || 'client_session',
                                          status: apt.status || 'scheduled',
                                          is_billable: apt.is_billable !== undefined ? apt.is_billable : true,
                                          is_payroll_eligible: apt.is_payroll_eligible || false,
                                          units: apt.units || 1,
                                          location: apt.location || '',
                                          notes: apt.notes || '',
                                          cpt_code_id: apt.cpt_code_id || '',
                                          selectedCptMeta: aptCptCode ? {
                                            allows_multiple_units: aptCptCode.allows_multiple_units,
                                            minutes_per_unit: aptCptCode.minutes_per_unit || aptCptCode.duration_minutes || 60,
                                            code: aptCptCode.code || aptCptCode.display_name,
                                            is_group_session: aptCptCode.is_group_session || false
                                          } : null
                                        });
                                        setEditingAppointment(apt);
                                        setShowAppointmentForm(true);
                                      }}
                                      title="Edit appointment"
                                    >
                                      <Edit className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-gray-400 hover:text-gray-600 px-1"
                                      title="View change history"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setChangeHistoryEntity({
                                          type: 'appointment',
                                          id: apt.id,
                                          name: `${apt.service_type || 'Appointment'} — ${new Date(apt.start_time).toLocaleDateString()}`
                                        });
                                        setShowChangeHistory(true);
                                      }}
                                    >
                                      <History className="h-3 w-3" />
                                    </Button>

                                  </div>
                                </div>
                              );
                            })}
                          {upcomingList.length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-4">No upcoming appointments</p>
                          )}
                          {hiddenCount > 0 && !showAllUpcomingAppts && (
                            <Button 
                              variant="ghost" 
                              className="w-full text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={() => setShowAllUpcomingAppts(true)}
                            >
                              Show all {upcomingList.length} appointments
                            </Button>
                          )}
                          {showAllUpcomingAppts && upcomingList.length > 5 && (
                            <Button 
                              variant="ghost" 
                              className="w-full text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                              onClick={() => setShowAllUpcomingAppts(false)}
                            >
                              Show less
                            </Button>
                          )}
                        </div>
                          );
                        })()}
                      </div>

                      {/* Past Appointments - Collapsible */}
                      <div>
                        <div 
                          className="flex items-center gap-2 cursor-pointer mb-3 hover:bg-gray-50 -mx-2 px-2 py-1 rounded"
                          onClick={() => setShowClientPastAppts(!showClientPastAppts)}
                        >
                          {showClientPastAppts ? (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-500" />
                          )}
                          <h4 className="font-semibold text-gray-700">
                            Past Appointments
                            <span className="ml-2 text-sm font-normal text-gray-500">
                              ({appointments.filter(apt => (apt.client_id === selectedClient.id || apt.client_ids?.includes(selectedClient.id)) && new Date(apt.start_time) < new Date()).length})
                            </span>
                          </h4>
                        </div>
                        {showClientPastAppts && (() => {
                          const pastList = appointments
                            .filter(apt => (apt.client_id === selectedClient.id || apt.client_ids?.includes(selectedClient.id)) && new Date(apt.start_time) < new Date())
                            .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
                          const displayList = showAllPastAppts ? pastList : pastList.slice(0, 5);
                          const hiddenCount = pastList.length - 5;
                          return (
                        <div className="space-y-2">
                          {displayList.map(apt => {
                              const startTime = new Date(apt.start_time);
                              const providerName = getProviderNames([apt.provider_id]);
                              return (
                                <div 
                                  key={apt.id} 
                                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                >
                                  <div>
                                    <p className="font-medium">{apt.service_type}</p>
                                    <p className="text-sm text-gray-500">
                                      {startTime.toLocaleDateString()} at {startTime.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                    </p>
                                    <p className="text-xs text-gray-400">Provider: {providerName}</p>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Badge className={getStatusBadgeColor(apt.status)}>{apt.status}</Badge>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        
                                        // Check if user can edit this appointment
                                        if (!canEditAppointment(apt)) {
                                          toast.error('You do not have permission to edit this appointment');
                                          return;
                                        }
                                        
                                        // Find CPT code metadata
                                        const aptCptCode = [...cptCodes, ...cptBundles].find(c =>
                                          c.code === apt.service_type ||
                                          c.display_name === apt.service_type ||
                                          c.id === apt.cpt_code_id ||
                                          c.id === apt.cpt_bundle_id
                                        );
                                        
                                        setAppointmentFormData({
                                          client_id: apt.client_id || '',
                                          client_ids: apt.client_ids || [],
                                          selected_group_id: apt.selected_group_id || '',
                                          provider_id: apt.provider_id,
                                          start_time: formatLocalDateTime(apt.start_time),
                                          end_time: formatLocalDateTime(apt.end_time),
                                          service_type: apt.service_type,
                                          appointment_type: apt.appointment_type || 'client_session',
                                          status: apt.status || 'scheduled',
                                          is_billable: apt.is_billable !== undefined ? apt.is_billable : true,
                                          is_payroll_eligible: apt.is_payroll_eligible || false,
                                          units: apt.units || 1,
                                          location: apt.location || '',
                                          notes: apt.notes || '',
                                          cpt_code_id: apt.cpt_code_id || '',
                                          selectedCptMeta: aptCptCode ? {
                                            allows_multiple_units: aptCptCode.allows_multiple_units,
                                            minutes_per_unit: aptCptCode.minutes_per_unit || aptCptCode.duration_minutes || 60,
                                            code: aptCptCode.code || aptCptCode.display_name,
                                            is_group_session: aptCptCode.is_group_session || false
                                          } : null
                                        });
                                        setEditingAppointment(apt);
                                        setShowAppointmentForm(true);
                                      }}
                                      title="Edit appointment"
                                    >
                                      <Edit className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          {pastList.length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-4">No past appointments</p>
                          )}
                          {hiddenCount > 0 && !showAllPastAppts && (
                            <Button 
                              variant="ghost" 
                              className="w-full text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                              onClick={() => setShowAllPastAppts(true)}
                            >
                              Show all {pastList.length} appointments
                            </Button>
                          )}
                          {showAllPastAppts && pastList.length > 5 && (
                            <Button 
                              variant="ghost" 
                              className="w-full text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                              onClick={() => setShowAllPastAppts(false)}
                            >
                              Show less
                            </Button>
                          )}
                        </div>
                          );
                        })()}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>


                {/* Alerts Tab */}
                <TabsContent value="alerts" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                  <Suspense fallback={<div className="p-6 text-center text-sm text-gray-500">Loading alerts…</div>}>
                    <AlertsTab
                      selectedClient={selectedClient}
                      clientAlerts={clientAlerts}
                      loadingAlerts={loadingAlerts}
                      user={user}
                      setShowAlertForm={setShowAlertForm}
                      setEditingAlert={setEditingAlert}
                      setAlertFormData={setAlertFormData}
                      fetchClientAlerts={fetchClientAlerts}
                      fetchData={fetchData}
                    />
                  </Suspense>
                </TabsContent>

                <TabsContent value="notes" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                  <ClientChartDocumentsTab
                    selectedClient={selectedClient}
                    notes={clientNotes}
                    setNotes={setClientNotes}
                    clientDocuments={clientDocuments}
                    setClientDocuments={setClientDocuments}
                    formTypes={formTypes}
                    formTemplates={formTemplates}
                    users={users}
                    user={user}
                    hasPermission={hasPermission}
                    canLockNote={canLockNote}
                    canEditNote={canEditNote}
                    selectedClinicalNotes={selectedClinicalNotes}
                    setSelectedClinicalNotes={setSelectedClinicalNotes}
                    expandedNoteFormTypes={expandedNoteFormTypes}
                    setExpandedNoteFormTypes={setExpandedNoteFormTypes}
                    setShowUploadDocument={setShowUploadDocument}
                    setShowAddNote={setShowAddNote}
                    setSelectedNoteStatus={setSelectedNoteStatus}
                    setSelectedNoteProvider={setSelectedNoteProvider}
                    setSelectedNoteClients={setSelectedNoteClients}
                    setIsGroupNote={setIsGroupNote}
                    setNoteClientLocked={setNoteClientLocked}
                    setSelectedNote={setSelectedNote}
                    setShowNoteDetail={setShowNoteDetail}
                    openNote={openNote}
                    setDocumentBlobUrl={setDocumentBlobUrl}
                    setViewingDocument={setViewingDocument}
                    setEditingDocumentFormType={setEditingDocumentFormType}
                    setNewDocumentFormType={setNewDocumentFormType}
                    fetchData={fetchData}
                    refreshClientNotes={refreshClientNotes}
                    handleDeleteNoteWithUndo={handleDeleteNoteWithUndo}
                    handleDeleteDocumentWithUndo={handleDeleteDocumentWithUndo}
                    generateDocumentDownloadFilename={generateDocumentDownloadFilename}
                    formatDateStringForDisplay={formatDateStringForDisplay}
                    getStatusBadgeColor={getStatusBadgeColor}
                    formatStatusLabel={formatStatusLabel}
                    // Option A lazy-load hooks
                    clientNotesHasMore={clientNotesHasMore}
                    clientNotesTotalCount={clientNotesTotalCount}
                    onLoadAllClientNotes={loadAllClientNotes}
                  />
                </TabsContent>

                {/* Claims & Billing Tab - For Billers/Admins Only */}
                {(user?.is_biller || user?.is_billing_manager || isSuperAdmin(user)) && (
                  <TabsContent value="billing" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                    <Suspense fallback={<div className="p-6 text-center text-sm text-gray-500">Loading billing…</div>}>
                      <ClientChartBillingTab
                        clientBillingSummary={clientBillingSummary}
                        clientClaims={clientClaims}
                        clientPaymentHistory={clientPaymentHistory}
                        claimsSortColumn={claimsSortColumn}
                        claimsSortDirection={claimsSortDirection}
                        sortClientClaims={sortClientClaims}
                        expandedClaimVersions={expandedClaimVersions}
                        setExpandedClaimVersions={setExpandedClaimVersions}
                        setShowPostPaymentDialog={setShowPostPaymentDialog}
                        setSelectedClaimsForInvoice={setSelectedClaimsForInvoice}
                        setShowStatementDialog={setShowStatementDialog}
                        setShowInvoiceDialog={setShowInvoiceDialog}
                        billingEnabled={billingSettings.billing_enabled}
                        clientId={selectedClient?.id}
                        clientName={selectedClient?.full_name || `${selectedClient?.first_name || ''} ${selectedClient?.last_name || ''}`.trim()}
                        clientMrn={selectedClient?.mrn || ''}
                        apiUrl={API}
                        onDataRefresh={() => fetchClientBilling(selectedClient?.id)}
                        onPostPaymentForClaim={(claim, action) => {
                          // Route all three payment flows (payment / insurance / write_off)
                          // through the shared PostPaymentDialog. The dialog auto-detects
                          // insurance vs self-pay claims and pre-selects the right mode.
                          setPostPaymentTarget({ claim, initialAction: action });
                          setShowPostPaymentDialog(true);
                        }}
                      />
                    </Suspense>
                  </TabsContent>
                )}

                <TabsContent value="contacts" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-lg">Contacts</CardTitle>
                      <Button 
                        size="sm" 
                        onClick={() => setShowAddContact(true)}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Contact
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Emergency Contacts Section */}
                        {selectedClient.emergency_contacts && selectedClient.emergency_contacts.length > 0 && (
                          <div className="mb-6">
                            {selectedClient.emergency_contacts.map(contact => (
                              <div key={contact.id} className="p-4 bg-purple-50 rounded-lg border border-purple-200 mb-3">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-medium">{contact.name}</h4>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs bg-red-600 text-white px-2 py-1 rounded">EMERGENCY</span>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => openEditContact(contact)}
                                    >
                                      <Edit className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <Label className="text-xs text-gray-500">Relationship</Label>
                                    <p className="font-medium">{contact.relationship}</p>
                                  </div>
                                  {contact.phone && (
                                    <div>
                                      <Label className="text-xs text-gray-500">Phone</Label>
                                      <div>
                                        <a 
                                          href={`tel:${contact.phone}`} 
                                          className="text-teal-600 hover:text-teal-700 hover:underline block"
                                        >
                                          {contact.phone}
                                        </a>
                                      </div>
                                    </div>
                                  )}
                                  {contact.email && (
                                    <div>
                                      <Label className="text-xs text-gray-500">Email</Label>
                                      <div>
                                        <a 
                                          href={`mailto:${contact.email}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-teal-600 hover:text-teal-700 hover:underline block"
                                        >
                                          {contact.email}
                                        </a>
                                      </div>
                                    </div>
                                  )}
                                  <div>
                                    <Label className="text-xs text-gray-500">Release on File</Label>
                                    {(() => {
                                      // Check for active release linked to this contact
                                      const linkedRelease = (clientReleases || []).find(r => 
                                        r.linked_contact_id === contact.id && r.status === 'active'
                                      );
                                      const pendingRelease = (clientReleases || []).find(r => 
                                        r.linked_contact_id === contact.id && r.status === 'pending_signature'
                                      );
                                      
                                      if (linkedRelease) {
                                        return (
                                          <div 
                                            className="flex items-center gap-2 cursor-pointer hover:opacity-80"
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              try {
                                                const response = await axios.get(`${API}/releases/${linkedRelease.id}`);
                                                setViewingReleaseInline(response.data);
                                              } catch (error) {
                                                toast.error('Failed to load release');
                                              }
                                            }}
                                          >
                                            <Badge className="bg-green-600">
                                              📋 Active Release
                                            </Badge>
                                            {linkedRelease.is_expiring_soon && (
                                              <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                                                Expiring Soon
                                              </Badge>
                                            )}
                                          </div>
                                        );
                                      } else if (pendingRelease) {
                                        return (
                                          <div 
                                            className="flex items-center gap-2 cursor-pointer hover:opacity-80"
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              try {
                                                const response = await axios.get(`${API}/releases/${pendingRelease.id}`);
                                                setViewingReleaseInline(response.data);
                                              } catch (error) {
                                                toast.error('Failed to load release');
                                              }
                                            }}
                                          >
                                            <Badge className="bg-yellow-600">
                                              ⏳ Pending Signature
                                            </Badge>
                                          </div>
                                        );
                                      } else if (contact.is_minor_or_legal_guardian) {
                                        return (
                                          <div className="flex items-center space-x-2">
                                            <div className="w-3 h-3 rounded-full bg-green-500" />
                                            <span className="text-green-700">Minor/Legal Guardian</span>
                                          </div>
                                        );
                                      } else {
                                        return (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              // Pre-fill with contact info
                                              setReleaseFormData({
                                                direction: 'both',
                                                linked_contact_id: contact.id,
                                                recipient_name: contact.organization || `${contact.first_name} ${contact.last_name}`,
                                                contact_person: contact.organization ? `${contact.first_name} ${contact.last_name}` : '',
                                                address: contact.address || '',
                                                city: '',
                                                state: '',
                                                zip_code: '',
                                                phone: contact.phone || '',
                                                phone_ext: '',
                                                fax: '',
                                                email: contact.email || '',
                                                date_range_type: 'all',
                                                date_from: '',
                                                date_to: '',
                                                info_entire_medical: false,
                                                info_non_medical: false,
                                                info_progress_therapy: false,
                                                info_progress_psychiatric: false,
                                                info_assessments: false,
                                                info_psych_testing: false,
                                                purpose_assessment: false,
                                                purpose_continuity: true,
                                                purpose_substance: false,
                                                purpose_substance_description: '',
                                                purpose_treatment_summary: false,
                                                purpose_discharge_summary: false,
                                                purpose_school_records: false,
                                                purpose_attendance_only: false,
                                                purpose_insurance: false,
                                                purpose_other: false,
                                                purpose_other_description: '',
                                                expiration_type: 'default_90_days',
                                                expiration_date: '',
                                                expiration_event: ''
                                              });
                                              setShowCreateRelease(true);
                                            }}
                                          >
                                            <Plus className="h-3 w-3 mr-1" />
                                            Create Release
                                          </Button>
                                        );
                                      }
                                    })()}
                                  </div>
                                  {contact.discharge_date && (
                                    <div>
                                      <Label className="text-xs text-gray-500">Discharge Date</Label>
                                      <p>{fmtLocalDate(contact.discharge_date)}</p>
                                    </div>
                                  )}
                                  {contact.end_date && (
                                    <div>
                                      <Label className="text-xs text-gray-500">Release End Date</Label>
                                      <p>{fmtLocalDate(contact.end_date)}</p>
                                      {contact.auto_calculate_end_date && (
                                        <p className="text-xs text-gray-400">(Auto-calculated: Discharge + 90 days)</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Regular Contacts Section */}
                        {selectedClient.contacts && selectedClient.contacts.length > 0 && (
                          <div>
                            <h3 className="text-md font-semibold text-gray-700 mb-3">General Contacts</h3>
                            {selectedClient.contacts.map(contact => (
                          <div key={contact.id} className="p-4 bg-gray-50 rounded-lg border">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium">{contact.name}</h4>
                              <div className="flex space-x-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openEditContact(contact)}
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handleDeleteContact(selectedClient.id, contact.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              <div>
                                <Label className="text-xs text-gray-500">Relationship</Label>
                                <p className="font-medium">{{ pcp: 'PCP (Primary Care)', other_provider: 'Other Provider' }[contact.relationship] || contact.relationship}</p>
                              </div>
                              {contact.title_agency && (
                                <div>
                                  <Label className="text-xs text-gray-500">Title / Agency</Label>
                                  <p>{contact.title_agency}</p>
                                </div>
                              )}
                              {contact.phone && (
                                <div>
                                  <Label className="text-xs text-gray-500">Phone</Label>
                                  <div>
                                    <a 
                                      href={`tel:${contact.phone}`} 
                                      className="text-teal-600 hover:text-teal-700 hover:underline block"
                                    >
                                      {contact.phone}
                                    </a>
                                  </div>
                                </div>
                              )}
                              {contact.email && (
                                <div>
                                  <Label className="text-xs text-gray-500">Email</Label>
                                  <div>
                                    <a 
                                      href={`mailto:${contact.email}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-teal-600 hover:text-teal-700 hover:underline block"
                                    >
                                      {contact.email}
                                    </a>
                                  </div>
                                </div>
                              )}
                              {contact.fax && (
                                <div>
                                  <Label className="text-xs text-gray-500">Fax</Label>
                                  <p>{contact.fax}</p>
                                </div>
                              )}
                              {(contact.contact_address || contact.contact_city) && (
                                <div>
                                  <Label className="text-xs text-gray-500">Address</Label>
                                  <p>{[contact.contact_address, contact.contact_city, contact.contact_state, contact.contact_zipcode].filter(Boolean).join(', ')}</p>
                                </div>
                              )}
                              <div>
                                <Label className="text-xs text-gray-500">Release on File</Label>
                                {(() => {
                                  // Check for active release linked to this contact
                                  const linkedRelease = (clientReleases || []).find(r => 
                                    r.linked_contact_id === contact.id && r.status === 'active'
                                  );
                                  const pendingRelease = (clientReleases || []).find(r => 
                                    r.linked_contact_id === contact.id && r.status === 'pending_signature'
                                  );
                                  
                                  if (linkedRelease) {
                                    return (
                                      <div 
                                        className="flex items-center gap-2 cursor-pointer hover:opacity-80"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const response = await axios.get(`${API}/releases/${linkedRelease.id}`);
                                            setViewingReleaseInline(response.data);
                                          } catch (error) {
                                            toast.error('Failed to load release');
                                          }
                                        }}
                                      >
                                        <Badge className="bg-green-600">
                                          📋 Active
                                        </Badge>
                                      </div>
                                    );
                                  } else if (pendingRelease) {
                                    return (
                                      <Badge className="bg-yellow-600">
                                        ⏳ Pending
                                      </Badge>
                                    );
                                  } else if (contact.is_minor_or_legal_guardian) {
                                    return (
                                      <span className="text-green-700 text-sm">Minor/Guardian</span>
                                    );
                                  } else {
                                    return (
                                      <span className="text-red-600 text-sm">No Release</span>
                                    );
                                  }
                                })()}
                              </div>
                              {contact.discharge_date && (
                                <div>
                                  <Label className="text-xs text-gray-500">Discharge Date</Label>
                                  <p>{fmtLocalDate(contact.discharge_date)}</p>
                                </div>
                              )}
                              {contact.end_date && (
                                <div>
                                  <Label className="text-xs text-gray-500">Release End Date</Label>
                                  <p>{fmtLocalDate(contact.end_date)}</p>
                                  {contact.auto_calculate_end_date && (
                                    <p className="text-xs text-gray-400">(Auto-calculated: Discharge + 90 days)</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                          </div>
                        )}
                        
                        {/* No contacts message - show only if both arrays are empty */}
                        {(!selectedClient.emergency_contacts || selectedClient.emergency_contacts.length === 0) && 
                         (!selectedClient.contacts || selectedClient.contacts.length === 0) && (
                          <p className="text-sm text-gray-500 text-center py-4">No contacts added</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Releases of Information Section */}
                  {viewingReleaseInline ? (
                    /* Inline Release Detail View */
                    <Card className="mt-4">
                      <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <div className="flex items-center gap-3">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setViewingReleaseInline(null)}
                          >
                            ← Back to Releases
                          </Button>
                          <div>
                            <CardTitle className="text-lg">Release Details</CardTitle>
                            <p className="text-sm text-gray-500">{viewingReleaseInline.recipient_name || 'Release of Information'}</p>
                          </div>
                        </div>
                        <Badge className={`${
                          viewingReleaseInline.status === 'active' ? 'bg-green-600' :
                          viewingReleaseInline.status === 'pending_signature' ? 'bg-yellow-600' :
                          viewingReleaseInline.status === 'expired' ? 'bg-gray-500' :
                          viewingReleaseInline.status === 'revoked' ? 'bg-red-600' :
                          'bg-blue-600'
                        }`}>
                          {viewingReleaseInline.status === 'pending_signature' ? 'Pending Signature' : 
                           viewingReleaseInline.status.charAt(0).toUpperCase() + viewingReleaseInline.status.slice(1)}
                        </Badge>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Summary Info */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Direction:</span>
                              <span className="ml-2 font-medium">
                                {viewingReleaseInline.direction === 'disclose_to' ? '📤 Disclose To' :
                                 viewingReleaseInline.direction === 'obtain_from' ? '📥 Obtain From' : '🔄 Bidirectional'}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Recipient:</span>
                              <span className="ml-2 font-medium">{viewingReleaseInline.recipient_name || 'Not specified'}</span>
                            </div>
                            {viewingReleaseInline.contact_person && (
                              <div>
                                <span className="text-gray-500">Contact Person:</span>
                                <span className="ml-2">{viewingReleaseInline.contact_person}</span>
                              </div>
                            )}
                            {viewingReleaseInline.phone && (
                              <div>
                                <span className="text-gray-500">Phone:</span>
                                <span className="ml-2">{viewingReleaseInline.phone}</span>
                              </div>
                            )}
                            {viewingReleaseInline.fax && (
                              <div>
                                <span className="text-gray-500">Fax:</span>
                                <span className="ml-2">{viewingReleaseInline.fax}</span>
                              </div>
                            )}
                            {viewingReleaseInline.email && (
                              <div>
                                <span className="text-gray-500">Email:</span>
                                <span className="ml-2">{viewingReleaseInline.email}</span>
                              </div>
                            )}
                            <div>
                              <span className="text-gray-500">Created:</span>
                              <span className="ml-2">{viewingReleaseInline.created_at ? new Date(viewingReleaseInline.created_at).toLocaleDateString() : 'N/A'}</span>
                            </div>
                            {viewingReleaseInline.signed_at && (
                              <div>
                                <span className="text-gray-500">Signed:</span>
                                <span className="ml-2">{new Date(viewingReleaseInline.signed_at).toLocaleDateString()}</span>
                              </div>
                            )}
                            {viewingReleaseInline.computed_expiration_date ? (
                              <div>
                                <span className="text-gray-500">Expires:</span>
                                <span className="ml-2">{new Date(viewingReleaseInline.computed_expiration_date).toLocaleDateString()}</span>
                              </div>
                            ) : ['draft', 'pending_signature', 'active'].includes(viewingReleaseInline.status) && (
                              <div>
                                <span className="text-gray-500">Expires:</span>
                                <span className="ml-2">90 days after discharge</span>
                              </div>
                            )}
                            {viewingReleaseInline.initiated_by && (
                              <div>
                                <span className="text-gray-500">Initiated By:</span>
                                <span className="ml-2 capitalize">{viewingReleaseInline.initiated_by}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Address if available */}
                        {(viewingReleaseInline.address || viewingReleaseInline.city) && (
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-medium text-blue-900 mb-2">📍 Recipient Address</h4>
                            <p className="text-sm text-blue-800">
                              {viewingReleaseInline.address && <span>{viewingReleaseInline.address}<br/></span>}
                              {(viewingReleaseInline.city || viewingReleaseInline.state || viewingReleaseInline.zip_code) && (
                                <span>{[viewingReleaseInline.city, viewingReleaseInline.state, viewingReleaseInline.zip_code].filter(Boolean).join(', ')}</span>
                              )}
                            </p>
                          </div>
                        )}

                        {/* Purpose */}
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <h4 className="font-medium text-purple-900 mb-2">📋 Purpose of Disclosure</h4>
                          <div className="flex flex-wrap gap-2">
                            {viewingReleaseInline.purpose_continuity && <Badge variant="outline" className="bg-white">Continuity of Care</Badge>}
                            {viewingReleaseInline.purpose_assessment && <Badge variant="outline" className="bg-white">Assessment/Evaluation</Badge>}
                            {viewingReleaseInline.purpose_treatment_summary && <Badge variant="outline" className="bg-white">Treatment Summary</Badge>}
                            {viewingReleaseInline.purpose_discharge_summary && <Badge variant="outline" className="bg-white">Discharge Summary</Badge>}
                            {viewingReleaseInline.purpose_insurance && <Badge variant="outline" className="bg-white">Insurance/Billing</Badge>}
                            {viewingReleaseInline.purpose_school_records && <Badge variant="outline" className="bg-white">School Records</Badge>}
                            {viewingReleaseInline.purpose_payment && <Badge variant="outline" className="bg-white">Payment/Billing</Badge>}
                            {viewingReleaseInline.purpose_legal && <Badge variant="outline" className="bg-white">Legal Proceedings</Badge>}
                            {viewingReleaseInline.purpose_personal && <Badge variant="outline" className="bg-white">Personal Records</Badge>}
                            {viewingReleaseInline.purpose_other && (
                              <Badge variant="outline" className="bg-white">
                                Other{viewingReleaseInline.purpose_other_text ? `: ${viewingReleaseInline.purpose_other_text}` : (viewingReleaseInline.purpose_other_description ? `: ${viewingReleaseInline.purpose_other_description}` : '')}
                              </Badge>
                            )}
                            {!viewingReleaseInline.purpose_continuity && !viewingReleaseInline.purpose_assessment && !viewingReleaseInline.purpose_treatment_summary && !viewingReleaseInline.purpose_discharge_summary && !viewingReleaseInline.purpose_insurance && !viewingReleaseInline.purpose_school_records && !viewingReleaseInline.purpose_payment && !viewingReleaseInline.purpose_legal && !viewingReleaseInline.purpose_personal && !viewingReleaseInline.purpose_other && (
                              <span className="text-sm text-purple-700">No specific purpose indicated</span>
                            )}
                          </div>
                        </div>

                        {/* Information to be Disclosed */}
                        <div className="bg-green-50 p-4 rounded-lg">
                          <h4 className="font-medium text-green-900 mb-2">📄 Information to be Disclosed</h4>
                          <div className="flex flex-wrap gap-2">
                            {viewingReleaseInline.info_entire_medical && <Badge variant="outline" className="bg-white">Entire Medical Record</Badge>}
                            {viewingReleaseInline.info_treatment_summary && <Badge variant="outline" className="bg-white">Treatment Summary</Badge>}
                            {viewingReleaseInline.info_progress_therapy && <Badge variant="outline" className="bg-white">Progress Notes</Badge>}
                            {viewingReleaseInline.info_assessments && <Badge variant="outline" className="bg-white">Assessments</Badge>}
                            {viewingReleaseInline.info_discharge_summary && <Badge variant="outline" className="bg-white">Discharge Summary</Badge>}
                            {viewingReleaseInline.info_medications && <Badge variant="outline" className="bg-white">Medications</Badge>}
                            {viewingReleaseInline.info_lab_results && <Badge variant="outline" className="bg-white">Lab Results</Badge>}
                            {viewingReleaseInline.info_school_records && <Badge variant="outline" className="bg-white">School Records</Badge>}
                            {!viewingReleaseInline.info_entire_medical && !viewingReleaseInline.info_treatment_summary && !viewingReleaseInline.info_progress_therapy && !viewingReleaseInline.info_assessments && !viewingReleaseInline.info_discharge_summary && (
                              <span className="text-sm text-green-700">No specific information types selected</span>
                            )}
                          </div>
                        </div>

                        {/* Signature Info */}
                        {viewingReleaseInline.client_signature_name && (
                          <div className="bg-amber-50 p-4 rounded-lg">
                            <h4 className="font-medium text-amber-900 mb-2">✍️ Signature</h4>
                            <div className="text-sm text-amber-800 space-y-2">
                              <p><strong>Signed by:</strong> {viewingReleaseInline.client_signature_name}</p>
                              {viewingReleaseInline.signed_by_guardian && (
                                <p><strong>Guardian:</strong> {viewingReleaseInline.guardian_name} ({viewingReleaseInline.guardian_relationship})</p>
                              )}
                              <p><strong>Date:</strong> {viewingReleaseInline.client_signature_date ? new Date(viewingReleaseInline.client_signature_date).toLocaleString() : 'N/A'}</p>
                              {/* Display actual signature image */}
                              {viewingReleaseInline.client_signature && (
                                <div className="mt-3 pt-3 border-t border-amber-200">
                                  <p className="text-xs text-amber-600 mb-2">Client Signature:</p>
                                  <div className="bg-white p-2 rounded border border-amber-200 inline-block">
                                    <img 
                                      src={viewingReleaseInline.client_signature.startsWith('data:') 
                                        ? viewingReleaseInline.client_signature 
                                        : `data:image/png;base64,${viewingReleaseInline.client_signature}`}
                                      alt="Client Signature"
                                      className="max-h-24 max-w-full"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                  <Card className="mt-4">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Releases of Information</CardTitle>
                        <p className="text-sm text-gray-500">Manage authorizations for disclosure of client information</p>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => {
                          // Initialize the form data
                          setReleaseFormData({
                            direction: 'both',
                            linked_contact_id: '',
                            recipient_name: '',
                            contact_person: '',
                            address: '',
                            suite: '',
                            city: '',
                            state: '',
                            zip_code: '',
                            phone: '',
                            phone_ext: '',
                            fax: '',
                            email: '',
                            date_range_type: 'all',
                            date_from: '',
                            date_to: '',
                            info_entire_medical: false,
                            info_non_medical: false,
                            info_progress_therapy: false,
                            info_progress_psychiatric: false,
                            info_assessments: false,
                            info_psych_testing: false,
                            info_treatment_summary: false,
                            info_discharge_summary: false,
                            info_school_records: false,
                            info_attendance_only: false,
                            info_insurance_claims: false,
                            info_substance: false,
                            info_substance_description: '',
                            info_other: false,
                            info_other_description: '',
                            purpose_assessment: false,
                            purpose_continuity: false,
                            purpose_substance: false,
                            purpose_substance_description: '',
                            purpose_treatment_summary: false,
                            purpose_discharge_summary: false,
                            purpose_school_records: false,
                            purpose_attendance_only: false,
                            purpose_insurance: false,
                            purpose_other: false,
                            purpose_other_description: '',
                            expiration_type: 'default_90_days',
                            expiration_date: '',
                            expiration_event: '',
                            member_refused: false,
                            verbal_consent: false,
                            special_reason: ''
                          });
                          setSelectedRelease(null);
                          // Switch to the "release" tab in the client detail view
                          setClientDetailTab('release-form');
                        }}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        New Release
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {(clientReleases || []).length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No releases on file</p>
                      ) : (
                        <div className="space-y-4">
                          {/* Active & Pending Releases */}
                          {(() => {
                            // Mar 2026 — Drafts belong in the Active section
                            // (they are in-progress releases the clinician
                            // still needs to finish/sign), NOT the archive.
                            // Archive is reserved for terminal states
                            // (expired, revoked, rejected).
                            const activeReleases = (clientReleases || []).filter(r => 
                              r.status === 'active' || r.status === 'pending_signature' || r.status === 'draft'
                            );
                            const archivedReleases = (clientReleases || []).filter(r => 
                              r.status === 'expired' || r.status === 'revoked' || r.status === 'rejected'
                            );
                            
                            return (
                              <>
                                {/* Active Releases Section */}
                                {activeReleases.length > 0 && (
                                  <div className="space-y-3">
                                    {activeReleases.map(release => {
                                      const isPending = release.status === 'pending_signature';
                                      const isDraft = release.status === 'draft';
                                      const isEditable = isPending || isDraft;
                                      const infoTypes = [];
                                      if (release.info_entire_medical) infoTypes.push('Entire Medical Record');
                                      if (release.info_progress_therapy) infoTypes.push('Therapy Notes');
                                      if (release.info_progress_psychiatric) infoTypes.push('Psychiatric Notes');
                                      if (release.info_assessments) infoTypes.push('Assessments');
                                      if (release.info_psych_testing) infoTypes.push('Psych Testing');
                                      if (release.info_treatment_summary) infoTypes.push('Treatment Summary');
                                      if (release.info_discharge_summary) infoTypes.push('Discharge Summary');
                                      if (release.info_school_records) infoTypes.push('School Records');
                                      if (release.info_insurance_claims) infoTypes.push('Insurance Claims');
                                      if (release.info_substance) infoTypes.push('Substance Use');
                                      if (release.info_other) infoTypes.push('Other');
                                      const addressLine = [release.address, release.suite, [release.city, release.state].filter(Boolean).join(', '), release.zip_code].filter(Boolean).join(' • ');
                                      return (
                                      <div 
                                        key={release.id} 
                                        className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                                          release.status === 'active' 
                                            ? 'bg-green-50 border-green-200' 
                                            : isDraft
                                              ? 'bg-blue-50 border-blue-200'
                                              : 'bg-yellow-50 border-yellow-200'
                                        }`}
                                        onClick={async () => {
                                          void 0 && console.log('Release card clicked:', release.id);
                                          try {
                                            const response = await axios.get(`${API}/releases/${release.id}`);
                                            setViewingReleaseInline(response.data);
                                          } catch (error) {
                                            console.error('Error fetching release details:', error);
                                            setViewingReleaseInline(release);
                                          }
                                        }}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            <span className="text-2xl">
                                              {release.direction === 'disclose_to' ? '📤' : release.direction === 'obtain_from' ? '📥' : '🔄'}
                                            </span>
                                            <div>
                                              <h4 className="font-medium">
                                                {release.recipient_name
                                                  || (isDraft
                                                    ? <span className="italic text-blue-700">Unfinished draft — click Edit to add details</span>
                                                    : 'Unknown Recipient')}
                                              </h4>
                                              <p className="text-sm text-gray-600">
                                                {release.direction === 'disclose_to' ? 'Disclose To' : release.direction === 'obtain_from' ? 'Obtain From' : 'Bidirectional'}
                                                {release.linked_contact_id && ' • Linked to Contact'}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            {isEditable && (
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className={`h-8 w-8 p-0 ${
                                                  isDraft
                                                    ? 'text-blue-700 hover:text-blue-800 hover:bg-blue-100'
                                                    : 'text-yellow-700 hover:text-yellow-800 hover:bg-yellow-100'
                                                }`}
                                                title={isDraft ? 'Continue editing this draft' : 'Edit this pending release'}
                                                data-testid={`edit-pending-release-${release.id}`}
                                                onClick={async (e) => {
                                                  e.stopPropagation();
                                                  e.preventDefault();
                                                  try {
                                                    const response = await axios.get(`${API}/releases/${release.id}`);
                                                    const full = response.data || release;
                                                    setReleaseFormData(prev => ({ ...prev, ...full }));
                                                    setSelectedRelease(full);
                                                    setClientDetailTab('release-form');
                                                  } catch (error) {
                                                    console.error('Failed to load release for edit:', error);
                                                    toast.error('Failed to load release');
                                                  }
                                                }}
                                              >
                                                <Edit className="h-4 w-4" />
                                              </Button>
                                            )}
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                                              title="View release details"
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                try {
                                                  const response = await axios.get(`${API}/releases/${release.id}`);
                                                  setViewingReleaseInline(response.data);
                                                } catch (error) {
                                                  setViewingReleaseInline(release);
                                                }
                                              }}
                                            >
                                              <Eye className="h-4 w-4" />
                                            </Button>
                                            {release.pdf_document_id ? (
                                              // PDF already exists - show View button
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                                title="View PDF in chart"
                                                onClick={async (e) => {
                                                  e.stopPropagation();
                                                  e.preventDefault();
                                                  try {
                                                    // Find the document and open it
                                                    const doc = clientDocuments.find(d => d.id === release.pdf_document_id);
                                                    if (doc) {
                                                      setViewingDocument(doc);
                                                    } else {
                                                      // Fetch the document if not in local state
                                                      const response = await axios.get(`${API}/documents/${release.pdf_document_id}`);
                                                      setViewingDocument(response.data);
                                                    }
                                                  } catch (error) {
                                                    console.error('Failed to view PDF:', error);
                                                    toast.error('Failed to load PDF');
                                                  }
                                                }}
                                              >
                                                <FileText className="h-4 w-4" />
                                              </Button>
                                            ) : (
                                              // No PDF yet - show Generate button
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-8 w-8 p-0 text-gray-500 hover:text-teal-600 hover:bg-teal-50"
                                                title="Generate PDF and save to chart"
                                                onClick={async (e) => {
                                                  e.stopPropagation();
                                                  e.preventDefault();
                                                  try {
                                                    toast.loading('Generating PDF...', { id: 'release-pdf' });
                                                    const response = await axios.post(`${API}/releases/${release.id}/generate-pdf?save_to_chart=true`);
                                                    toast.success('PDF saved to client documents', { id: 'release-pdf' });
                                                    // Refresh documents and releases to get updated pdf_document_id
                                                    if (selectedClient) {
                                                      const docsRes = await axios.get(`${API}/clients/${selectedClient.id}/documents`);
                                                      setClientDocuments(docsRes.data);
                                                      // Refresh the releases list to get the pdf_document_id
                                                      const releasesRes = await axios.get(`${API}/clients/${selectedClient.id}/releases`);
                                                      // Update local state with refreshed releases
                                                      setClientContacts(prev => prev.map(c => ({
                                                        ...c,
                                                        releases: (releasesRes.data || []).filter(r => r.contact_id === c.id)
                                                      })));
                                                    }
                                                  } catch (error) {
                                                    console.error('Failed to generate PDF:', error);
                                                    toast.error(error.response?.data?.detail || 'Failed to generate PDF', { id: 'release-pdf' });
                                                  }
                                                }}
                                              >
                                                <FileText className="h-4 w-4" />
                                              </Button>
                                            )}
                                            {release.is_expiring_soon && release.status === 'active' && (
                                              <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                                                Expiring Soon
                                              </Badge>
                                            )}
                                            <Badge className={
                                              release.status === 'active'
                                                ? 'bg-green-600'
                                                : isDraft
                                                  ? 'bg-blue-600'
                                                  : 'bg-yellow-600'
                                            }>
                                              {release.status === 'pending_signature'
                                                ? 'Pending Signature'
                                                : isDraft
                                                  ? 'Draft'
                                                  : 'Active'}
                                            </Badge>
                                          </div>
                                        </div>
                                        {/* Contact info row */}
                                        {(release.contact_person || release.phone || release.fax || release.email || addressLine) && (
                                          <div className="mt-2 text-xs text-gray-700 flex flex-wrap gap-x-4 gap-y-1">
                                            {release.contact_person && <span>👤 {release.contact_person}</span>}
                                            {release.phone && <span>📞 {release.phone}{release.phone_ext ? ` ext. ${release.phone_ext}` : ''}</span>}
                                            {release.fax && <span>📠 Fax {release.fax}</span>}
                                            {release.email && <span>✉️ {release.email}</span>}
                                            {addressLine && <span>📍 {addressLine}</span>}
                                          </div>
                                        )}
                                        {/* Info types row */}
                                        {infoTypes.length > 0 && (
                                          <div className="mt-1 text-xs text-gray-600">
                                            <span className="font-medium">Records:</span> {infoTypes.slice(0, 4).join(', ')}{infoTypes.length > 4 ? ` +${infoTypes.length - 4} more` : ''}
                                          </div>
                                        )}
                                        <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-4">
                                          <span>Created: {new Date(release.created_at).toLocaleDateString()}</span>
                                          {release.signed_at && <span>Signed: {new Date(release.signed_at).toLocaleDateString()}</span>}
                                          {release.computed_expiration_date ? (
                                            <span>Expires: {new Date(release.computed_expiration_date).toLocaleDateString()}</span>
                                          ) : ['draft', 'pending_signature', 'active'].includes(release.status) && (
                                            <span>Expires: 90 days after discharge</span>
                                          )}
                                        </div>
                                      </div>
                                      );
                                    })}
                                  </div>
                                )}
                                
                                {activeReleases.length === 0 && archivedReleases.length > 0 && (
                                  <p className="text-sm text-gray-500 text-center py-2">No active releases</p>
                                )}

                                {/* Archived Releases Section (Collapsible) */}
                                {archivedReleases.length > 0 && (
                                  <div className="border-t pt-4">
                                    <button
                                      onClick={() => setShowArchivedReleases(!showArchivedReleases)}
                                      className="flex items-center gap-2 text-gray-600 hover:text-gray-800 w-full justify-between"
                                    >
                                      <div className="flex items-center gap-2">
                                        <Archive className="h-4 w-4" />
                                        <span className="text-sm font-medium">
                                          Archived Releases ({archivedReleases.length})
                                        </span>
                                      </div>
                                      <ChevronDown className={`h-4 w-4 transition-transform ${showArchivedReleases ? 'rotate-180' : ''}`} />
                                    </button>
                                    
                                    {showArchivedReleases && (
                                      <div className="mt-3 space-y-3">
                                        {archivedReleases.map(release => {
                                          const isDraft = release.status === 'draft';
                                          const infoTypes = [];
                                          if (release.info_entire_medical) infoTypes.push('Entire Medical Record');
                                          if (release.info_progress_therapy) infoTypes.push('Therapy Notes');
                                          if (release.info_progress_psychiatric) infoTypes.push('Psychiatric Notes');
                                          if (release.info_assessments) infoTypes.push('Assessments');
                                          if (release.info_psych_testing) infoTypes.push('Psych Testing');
                                          if (release.info_treatment_summary) infoTypes.push('Treatment Summary');
                                          if (release.info_discharge_summary) infoTypes.push('Discharge Summary');
                                          if (release.info_school_records) infoTypes.push('School Records');
                                          if (release.info_insurance_claims) infoTypes.push('Insurance Claims');
                                          if (release.info_substance) infoTypes.push('Substance Use');
                                          if (release.info_other) infoTypes.push('Other');
                                          const addressLine = [release.address, release.suite, [release.city, release.state].filter(Boolean).join(', '), release.zip_code].filter(Boolean).join(' • ');
                                          return (
                                          <div 
                                            key={release.id} 
                                            className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                                              release.status === 'expired'
                                                ? 'bg-gray-50 border-gray-200'
                                                : release.status === 'revoked' || release.status === 'rejected'
                                                ? 'bg-red-50 border-red-200'
                                                : isDraft
                                                ? 'bg-amber-50 border-amber-200'
                                                : 'bg-blue-50 border-blue-200'
                                            }`}
                                            onClick={async () => {
                                              try {
                                                const response = await axios.get(`${API}/releases/${release.id}`);
                                                setViewingReleaseInline(response.data);
                                              } catch (error) {
                                                setViewingReleaseInline(release);
                                              }
                                            }}
                                          >
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center gap-3">
                                                <span className="text-2xl opacity-50">
                                                  {release.direction === 'disclose_to' ? '📤' : release.direction === 'obtain_from' ? '📥' : '🔄'}
                                                </span>
                                                <div>
                                                  <h4 className="font-medium text-gray-600">{release.recipient_name || (isDraft ? <span className="italic text-amber-700">Unfinished draft — click Edit to add details</span> : 'Unknown Recipient')}</h4>
                                                  <p className="text-sm text-gray-500">
                                                    {release.direction === 'disclose_to' ? 'Disclose To' : release.direction === 'obtain_from' ? 'Obtain From' : 'Bidirectional'}
                                                  </p>
                                                </div>
                                              </div>
                                              <div className="flex items-center gap-2">
                                                {isDraft && (
                                                  <>
                                                    <Button
                                                      size="sm"
                                                      variant="ghost"
                                                      className="h-8 w-8 p-0 text-amber-700 hover:text-amber-800 hover:bg-amber-100"
                                                      title="Edit this draft"
                                                      data-testid={`edit-draft-release-${release.id}`}
                                                      onClick={async (e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        try {
                                                          // Load full release (server returns all fields)
                                                          const response = await axios.get(`${API}/releases/${release.id}`);
                                                          const full = response.data || release;
                                                          // Hydrate form with stored values, keeping safe defaults
                                                          setReleaseFormData(prev => ({
                                                            ...prev,
                                                            ...full,
                                                          }));
                                                          setSelectedRelease(full);
                                                          setClientDetailTab('release-form');
                                                        } catch (error) {
                                                          console.error('Failed to load release for edit:', error);
                                                          toast.error('Failed to load release');
                                                        }
                                                      }}
                                                    >
                                                      <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                      size="sm"
                                                      variant="ghost"
                                                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                      title="Delete this draft"
                                                      data-testid={`delete-draft-release-${release.id}`}
                                                      onClick={async (e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        if (!confirm(`Permanently delete this draft release${release.recipient_name ? ` for "${release.recipient_name}"` : ''}? This cannot be undone.`)) return;
                                                        try {
                                                          await axios.delete(`${API}/releases/${release.id}`);
                                                          setClientReleases(prev => prev.filter(r => r.id !== release.id));
                                                          toast.success('Draft release deleted');
                                                        } catch (error) {
                                                          console.error('Failed to delete draft:', error);
                                                          toast.error(error.response?.data?.detail || 'Failed to delete draft');
                                                        }
                                                      }}
                                                    >
                                                      <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                  </>
                                                )}
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  className="h-8 w-8 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                                  title="View release details"
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    try {
                                                      const response = await axios.get(`${API}/releases/${release.id}`);
                                                      setViewingReleaseInline(response.data);
                                                    } catch (error) {
                                                      setViewingReleaseInline(release);
                                                    }
                                                  }}
                                                >
                                                  <Eye className="h-4 w-4" />
                                                </Button>
                                                {release.pdf_document_id ? (
                                                  // PDF already exists - show View button
                                                  <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                                    title="View PDF in chart"
                                                    onClick={async (e) => {
                                                      e.stopPropagation();
                                                      e.preventDefault();
                                                      try {
                                                        const doc = clientDocuments.find(d => d.id === release.pdf_document_id);
                                                        if (doc) {
                                                          setViewingDocument(doc);
                                                        } else {
                                                          const response = await axios.get(`${API}/documents/${release.pdf_document_id}`);
                                                          setViewingDocument(response.data);
                                                        }
                                                      } catch (error) {
                                                        console.error('Failed to view PDF:', error);
                                                        toast.error('Failed to load PDF');
                                                      }
                                                    }}
                                                  >
                                                    <FileText className="h-4 w-4" />
                                                  </Button>
                                                ) : (
                                                  // No PDF yet - show Generate button
                                                  <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-8 w-8 p-0 text-gray-400 hover:text-teal-600 hover:bg-teal-50"
                                                    title="Generate PDF and save to chart"
                                                    onClick={async (e) => {
                                                      e.stopPropagation();
                                                      e.preventDefault();
                                                      try {
                                                        toast.loading('Generating PDF...', { id: 'release-pdf' });
                                                        const response = await axios.post(`${API}/releases/${release.id}/generate-pdf?save_to_chart=true`);
                                                        toast.success('PDF saved to client documents', { id: 'release-pdf' });
                                                        if (selectedClient) {
                                                          const docsRes = await axios.get(`${API}/clients/${selectedClient.id}/documents`);
                                                          setClientDocuments(docsRes.data);
                                                          const releasesRes = await axios.get(`${API}/clients/${selectedClient.id}/releases`);
                                                          setClientContacts(prev => prev.map(c => ({
                                                            ...c,
                                                            releases: (releasesRes.data || []).filter(r => r.contact_id === c.id)
                                                          })));
                                                        }
                                                      } catch (error) {
                                                        console.error('Failed to generate PDF:', error);
                                                        toast.error(error.response?.data?.detail || 'Failed to generate PDF', { id: 'release-pdf' });
                                                      }
                                                    }}
                                                  >
                                                    <FileText className="h-4 w-4" />
                                                  </Button>
                                                )}
                                                <Badge className={`${
                                                  release.status === 'expired'
                                                    ? 'bg-gray-500'
                                                    : release.status === 'revoked' || release.status === 'rejected'
                                                    ? 'bg-red-600'
                                                    : isDraft
                                                    ? 'bg-amber-600'
                                                    : 'bg-blue-600'
                                                }`}>
                                                  {release.status.charAt(0).toUpperCase() + release.status.slice(1)}
                                                </Badge>
                                              </div>
                                            </div>
                                            {/* Contact info row */}
                                            {(release.contact_person || release.phone || release.fax || release.email || addressLine) && (
                                              <div className="mt-2 text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
                                                {release.contact_person && <span>👤 {release.contact_person}</span>}
                                                {release.phone && <span>📞 {release.phone}{release.phone_ext ? ` ext. ${release.phone_ext}` : ''}</span>}
                                                {release.fax && <span>📠 Fax {release.fax}</span>}
                                                {release.email && <span>✉️ {release.email}</span>}
                                                {addressLine && <span>📍 {addressLine}</span>}
                                              </div>
                                            )}
                                            {/* Info types row */}
                                            {infoTypes.length > 0 && (
                                              <div className="mt-1 text-xs text-gray-500">
                                                <span className="font-medium">Records:</span> {infoTypes.slice(0, 4).join(', ')}{infoTypes.length > 4 ? ` +${infoTypes.length - 4} more` : ''}
                                              </div>
                                            )}
                                            <div className="mt-2 text-xs text-gray-400 flex flex-wrap gap-4">
                                              <span>Created: {new Date(release.created_at).toLocaleDateString()}</span>
                                              {release.signed_at && <span>Signed: {new Date(release.signed_at).toLocaleDateString()}</span>}
                                              {release.computed_expiration_date && (
                                                <span>Expired: {new Date(release.computed_expiration_date).toLocaleDateString()}</span>
                                              )}
                                            </div>
                                          </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  )}
                </TabsContent>

                {/* Supervision Tab - Internal oversight, NOT for clients */}
                {(userRoleName === 'supervisor' || user?.is_supervisor || userRoleName === 'admin' || isSuperAdmin(user) || userRoleName === 'provider' || user?.can_qa_audit) && (
                <TabsContent value="supervision" className={`${clientHeaderCollapsed ? 'min-h-[700px]' : 'min-h-[600px]'}`}>
                    <Suspense fallback={<PageLoader label="Loading Supervision..." />}>
                    <SupervisionTab
                      clientMDTReviews={clientMDTReviews}
                      setClientMDTReviews={setClientMDTReviews}
                      selectedClient={selectedClient}
                      user={user}
                      userRoleName={userRoleName}
                      providers={providers}
                      appointments={appointments}
                      clientDocuments={clientDocuments}
                      fetchMDTPendingActions={fetchMDTPendingActions}
                      openNote={openNote}
                      serviceTypes={serviceTypes}
                      setViewingDocument={setViewingDocument}
                    />
                    </Suspense>
                </TabsContent>
                )}

                <TabsContent value="release-form">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-xl">
                            {selectedRelease ? 'Edit Release of Information' : 'New Release of Information'}
                          </CardTitle>
                          <p className="text-sm text-gray-500 mt-1">Authorization for Use and/or Disclosure of Protected Health Information</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setClientDetailTab('contacts')}>
                          <ArrowLeft className="h-4 w-4 mr-2" />
                          Back to Contacts
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Client Info (read-only) */}
                      {selectedClient && (
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <h4 className="font-medium text-blue-900">Client Information</h4>
                          <p className="text-sm text-blue-800">
                            {selectedClient.full_name} | DOB: {selectedClient.date_of_birth ? formatDateMMDDYYYY(selectedClient.date_of_birth) : 'N/A'} | MRN: {formatMrn(selectedClient.mrn) || 'N/A'}
                          </p>
                        </div>
                      )}
                      
                      {/* Direction */}
                      <div>
                        <Label className="text-base font-semibold">Type of Release</Label>
                        <div className="grid grid-cols-3 gap-3 mt-2">
                          {[
                            { value: 'both', label: '🔄 Bidirectional', desc: 'Exchange in both directions' },
                            { value: 'disclose_to', label: '📤 Disclose To', desc: 'We share info TO another party' },
                            { value: 'obtain_from', label: '📥 Obtain From', desc: 'We request info FROM another party' }
                          ].map(option => (
                            <div 
                              key={option.value}
                              onClick={() => setReleaseFormData(prev => ({ ...prev, direction: option.value }))}
                              className={`p-3 border rounded-lg cursor-pointer transition-all ${
                                releaseFormData.direction === option.value 
                                  ? 'border-indigo-500 bg-indigo-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="font-medium text-sm">{option.label}</div>
                              <div className="text-xs text-gray-500 mt-1">{option.desc}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Recipient Info */}
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">
                          {releaseFormData.direction === 'obtain_from' ? 'Source Information' : 'Recipient Information'}
                        </h4>
                        
                        {/* Instructions for Client (Jun 2026) — free-text shown
                            in the portal so the client knows who the release
                            is for, without the provider needing contact
                            details on file. Mirrors the Documents tab
                            "Instructions for Client" send message. */}
                        <div className="mb-4">
                          <Label htmlFor="releaseClientInstructions">Instructions for Client (optional)</Label>
                          <textarea
                            id="releaseClientInstructions"
                            data-testid="release-client-instructions-textarea"
                            className="w-full min-h-[80px] p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 mt-1"
                            placeholder="e.g., Please fill out this release so we can speak with your teacher..."
                            value={releaseFormData.client_instructions || ''}
                            onChange={(e) => setReleaseFormData(prev => ({ ...prev, client_instructions: e.target.value }))}
                          />
                          <p className="text-xs text-gray-500 mt-1">Shown to the client in the portal when they open this release</p>
                        </div>
                        
                        {/* Link to existing contact (optional) */}
                        {selectedClient?.contacts && selectedClient.contacts.length > 0 && (
                          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                            <Label className="text-sm">Link to existing contact (optional)</Label>
                            <Select
                              value={releaseFormData.linked_contact_id || '__none__'}
                              onValueChange={(value) => {
                                if (value === '__none__') {
                                  setReleaseFormData(prev => ({ ...prev, linked_contact_id: '' }));
                                } else {
                                  const contact = selectedClient.contacts.find(c => c.id === value);
                                  if (contact) {
                                    setReleaseFormData(prev => ({
                                      ...prev,
                                      linked_contact_id: value,
                                      recipient_name: contact.name || contact.organization || '',
                                      contact_person: contact.contact_person || '',
                                      phone: contact.phone || '',
                                      fax: contact.fax || '',
                                      email: contact.email || '',
                                      address: contact.address || '',
                                      city: contact.city || '',
                                      state: contact.state || '',
                                      zip_code: contact.zip || ''
                                    }));
                                  }
                                }
                              }}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select a contact to auto-fill..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">-- No linked contact --</SelectItem>
                                {selectedClient.contacts.map(contact => (
                                  <SelectItem key={contact.id} value={contact.id}>
                                    {contact.name || contact.organization} {contact.relationship ? `(${contact.relationship})` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 mt-1">Selecting a contact will auto-fill the fields below</p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Organization/Person Name <span className="text-gray-400 text-xs">(client can complete)</span></Label>
                            <DebouncedInput
                              value={releaseFormData.recipient_name}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, recipient_name: e.target.value }))}
                              placeholder="Enter name"
                            />
                          </div>
                          <div>
                            <Label>Contact Person</Label>
                            <DebouncedInput
                              value={releaseFormData.contact_person}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                              placeholder="Contact name"
                            />
                          </div>
                          <div>
                            <Label>Phone</Label>
                            <DebouncedInput
                              value={releaseFormData.phone}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, phone: e.target.value }))}
                              placeholder="Phone number"
                            />
                          </div>
                          <div>
                            <Label>Fax</Label>
                            <DebouncedInput
                              value={releaseFormData.fax}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, fax: e.target.value }))}
                              placeholder="Fax number"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label>Email</Label>
                            <DebouncedInput
                              type="email"
                              value={releaseFormData.email}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, email: e.target.value }))}
                              placeholder="Email address"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label>Street Address</Label>
                            <DebouncedInput
                              value={releaseFormData.address || ''}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, address: e.target.value }))}
                              placeholder="123 Main Street"
                              data-testid="release-address-input"
                            />
                          </div>
                          <div>
                            <Label>Suite / Unit / Floor</Label>
                            <DebouncedInput
                              value={releaseFormData.suite || ''}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, suite: e.target.value }))}
                              placeholder="e.g., Suite 200"
                              data-testid="release-suite-input"
                            />
                          </div>
                          <div>
                            <Label>City</Label>
                            <DebouncedInput
                              value={releaseFormData.city || ''}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, city: e.target.value }))}
                              placeholder="City"
                              data-testid="release-city-input"
                            />
                          </div>
                          <div>
                            <Label>State</Label>
                            <DebouncedInput
                              value={releaseFormData.state || ''}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, state: e.target.value.toUpperCase().slice(0, 2) }))}
                              placeholder="e.g., MA"
                              maxLength={2}
                              data-testid="release-state-input"
                            />
                          </div>
                          <div>
                            <Label>ZIP Code</Label>
                            <DebouncedInput
                              value={releaseFormData.zip_code || ''}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                              placeholder="e.g., 02101"
                              data-testid="release-zip-input"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Information to Disclose */}
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">Information to be {releaseFormData.direction === 'obtain_from' ? 'Obtained' : 'Disclosed'}</h4>
                        <p className="text-sm text-gray-500 mb-3">Select the types of records to be released</p>
                        <div className="space-y-3">
                          {/* Main medical record option */}
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={releaseFormData.info_entire_medical}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setReleaseFormData(prev => ({
                                  ...prev,
                                  info_entire_medical: checked,
                                  // If entire record selected, clear other selections
                                  ...(checked ? {
                                    info_non_medical: false,
                                    info_progress_therapy: false,
                                    info_progress_psychiatric: false,
                                    info_assessments: false,
                                    info_psych_testing: false,
                                    info_treatment_summary: false,
                                    info_discharge_summary: false,
                                    info_school_records: false,
                                    info_attendance_only: false,
                                    info_insurance_claims: false
                                  } : {})
                                }));
                              }}
                              className="rounded border-gray-300 mt-1"
                            />
                            <span className="text-sm font-medium">Entire medical/psychiatric history (including diagnosis and treatment, excluding progress notes, not including HIV test results)</span>
                          </label>
                          
                          {releaseFormData.info_entire_medical && (
                            <p className="text-xs text-indigo-600 ml-6">✓ Includes all records below (except Drug/Alcohol and Other)</p>
                          )}
                          
                          <div className={`space-y-3 ${releaseFormData.info_entire_medical ? 'opacity-50 pointer-events-none' : ''}`}>
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={releaseFormData.info_non_medical}
                                onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_non_medical: e.target.checked }))}
                                disabled={releaseFormData.info_entire_medical}
                                className="rounded border-gray-300 mt-1"
                              />
                              <span className="text-sm">Entire non-medical record (excludes alcohol/drug abuse, HIV/AIDS info if applicable)</span>
                            </label>
                            
                            <div className="grid grid-cols-2 gap-3 pl-0">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={releaseFormData.info_progress_therapy}
                                  onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_progress_therapy: e.target.checked }))}
                                  disabled={releaseFormData.info_entire_medical}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">Progress Notes (therapy, groups)</span>
                              </label>
                              
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={releaseFormData.info_progress_psychiatric}
                                  onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_progress_psychiatric: e.target.checked }))}
                                  disabled={releaseFormData.info_entire_medical}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">Progress Notes (psychiatric)</span>
                              </label>
                              
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={releaseFormData.info_assessments}
                                  onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_assessments: e.target.checked }))}
                                  disabled={releaseFormData.info_entire_medical}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">Assessments</span>
                              </label>
                              
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={releaseFormData.info_psych_testing}
                                  onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_psych_testing: e.target.checked }))}
                                  disabled={releaseFormData.info_entire_medical}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">Psychological/Educational Testing</span>
                              </label>
                              
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={releaseFormData.info_treatment_summary}
                                  onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_treatment_summary: e.target.checked }))}
                                  disabled={releaseFormData.info_entire_medical}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">Treatment Summary & Treatment Plan</span>
                              </label>
                              
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={releaseFormData.info_discharge_summary}
                                  onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_discharge_summary: e.target.checked }))}
                                  disabled={releaseFormData.info_entire_medical}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">Discharge Summary</span>
                              </label>
                              
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={releaseFormData.info_school_records}
                                  onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_school_records: e.target.checked }))}
                                  disabled={releaseFormData.info_entire_medical}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">School Records</span>
                              </label>
                              
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={releaseFormData.info_attendance_only}
                                  onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_attendance_only: e.target.checked }))}
                                  disabled={releaseFormData.info_entire_medical}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">Attendance Records Only</span>
                              </label>
                              
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={releaseFormData.info_insurance_claims}
                                  onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_insurance_claims: e.target.checked }))}
                                  disabled={releaseFormData.info_entire_medical}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">Case management & insurance claims</span>
                              </label>
                            </div>
                          </div>
                          
                          {/* Drug/Alcohol - special 42 CFR Part 2 */}
                          <div className="border-t pt-3 mt-3">
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={releaseFormData.info_substance}
                                onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_substance: e.target.checked }))}
                                className="rounded border-gray-300 mt-1"
                              />
                              <div>
                                <span className="text-sm font-medium">Drug/Alcohol diagnosis, treatment or referral information</span>
                                <p className="text-xs text-amber-600">⚠️ 42 CFR Part 2: Requires description of what and how much is to be disclosed</p>
                              </div>
                            </label>
                            {releaseFormData.info_substance && (
                              <DebouncedInput
                                value={releaseFormData.info_substance_description || ''}
                                onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_substance_description: e.target.value }))}
                                placeholder="Describe what and how much to disclose..."
                                className="mt-2 ml-6"
                              />
                            )}
                          </div>
                          
                          {/* Other */}
                          <div className="border-t pt-3 mt-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={releaseFormData.info_other}
                                onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_other: e.target.checked }))}
                                className="rounded border-gray-300"
                              />
                              <span className="text-sm">Other (specify)</span>
                            </label>
                            {releaseFormData.info_other && (
                              <DebouncedInput
                                value={releaseFormData.info_other_description || ''}
                                onChange={(e) => setReleaseFormData(prev => ({ ...prev, info_other_description: e.target.value }))}
                                placeholder="Specify other information to disclose..."
                                className="mt-2 ml-6"
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Purpose of Disclosure */}
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">Purpose of Disclosure</h4>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { key: 'purpose_continuity', label: 'Continuity of Care' },
                            { key: 'purpose_assessment', label: 'Assessment/Evaluation' },
                            { key: 'purpose_treatment_summary', label: 'Treatment Summary' },
                            { key: 'purpose_discharge_summary', label: 'Discharge Summary' },
                            { key: 'purpose_insurance', label: 'Insurance/Billing' },
                            { key: 'purpose_school_records', label: 'School Records' },
                            { key: 'purpose_other', label: 'Other' }
                          ].map(purpose => (
                            <label key={purpose.key} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={releaseFormData[purpose.key]}
                                onChange={(e) => setReleaseFormData(prev => ({ ...prev, [purpose.key]: e.target.checked }))}
                                className="rounded border-gray-300"
                              />
                              <span className="text-sm">{purpose.label}</span>
                            </label>
                          ))}
                        </div>
                        {/* Other purpose text field */}
                        {releaseFormData.purpose_other && (
                          <div className="mt-3">
                            <DebouncedInput
                              placeholder="Please specify the purpose..."
                              value={releaseFormData.purpose_other_text || ''}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, purpose_other_text: e.target.value }))}
                              className="w-full"
                            />
                          </div>
                        )}
                      </div>

                      {/* Expiration */}
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">Expiration</h4>
                        <div className="space-y-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="expiration"
                              checked={releaseFormData.expiration_type === 'default_90_days'}
                              onChange={() => setReleaseFormData(prev => ({ ...prev, expiration_type: 'default_90_days' }))}
                            />
                            <span className="text-sm">90 days from discharge (default)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="expiration"
                              checked={releaseFormData.expiration_type === 'one_year'}
                              onChange={() => setReleaseFormData(prev => ({ ...prev, expiration_type: 'one_year' }))}
                            />
                            <span className="text-sm">1 year from discharge</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="expiration"
                              checked={releaseFormData.expiration_type === 'specific_date'}
                              onChange={() => setReleaseFormData(prev => ({ ...prev, expiration_type: 'specific_date' }))}
                            />
                            <span className="text-sm">Specific date</span>
                          </label>
                          {releaseFormData.expiration_type === 'specific_date' && (
                            <Input
                              type="date"
                              value={releaseFormData.expiration_date}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, expiration_date: e.target.value }))}
                              className="w-48 ml-6"
                            />
                          )}
                        </div>
                      </div>

                      {/* Legal Notices & Understanding Section */}
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <h4 className="font-semibold mb-3">Important Information</h4>
                        <div className="space-y-3 text-sm text-gray-700">
                          <p>
                            <strong>Revocation:</strong> I understand that I may revoke this consent at any time, in writing, except that information has already been shared as a result of the release. If I do not revoke it, this consent will expire as indicated above. If an expiration date is not specified, this authorization will expire 90 days from the date of discharge from this agency.
                          </p>
                          <p>
                            <strong>Health Information:</strong> Health information includes information collected from me, or created by my therapist or information received from my therapist by another health care provider, a health plan, my employer, or a health care clearinghouse.
                          </p>
                          <p>
                            <strong>Re-disclosure (42 CFR Part 2):</strong> Federal law prohibits the person or organization to whom disclosure is made from making any further disclosure of substance abuse treatment information unless further disclosure is expressly permitted by the written authorization of the person to whom it pertains or as otherwise permitted by 42 C.F.R. part 2.
                          </p>
                          <p>
                            <strong>Privacy Notice:</strong> I understand that the information disclosed pursuant to this authorization to the recipient may be subject to re-disclosure and is no longer protected by the federal standards for privacy of individually identifiable health information (45 C.F.R. Part 160 and 164) and state law.
                          </p>
                          <p>
                            <strong>Right to Inspect:</strong> I understand that I may inspect or obtain a copy of health information that will be used or disclosed pursuant to this authorization.
                          </p>
                          <p>
                            <strong>Voluntary:</strong> I understand that I may refuse to sign this authorization and that my refusal to sign will not affect my ability to obtain treatment, except when I am receiving health care solely for the purpose of creating information for disclosure to a third party.
                          </p>
                        </div>
                      </div>

                      {/* Special Circumstances */}
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">Special Circumstances</h4>
                        <div className="space-y-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={releaseFormData.member_refused}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, member_refused: e.target.checked }))}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">Member refused request and disclosure of records or information</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={releaseFormData.verbal_consent}
                              onChange={(e) => setReleaseFormData(prev => ({ ...prev, verbal_consent: e.target.checked }))}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">Client or parent/guardian provided verbal consent (if needed)</span>
                          </label>
                          {(releaseFormData.member_refused || releaseFormData.verbal_consent) && (
                            <div className="ml-6">
                              <Label className="text-sm">Please provide reason:</Label>
                              <DebouncedInput
                                value={releaseFormData.special_reason || ''}
                                onChange={(e) => setReleaseFormData(prev => ({ ...prev, special_reason: e.target.value }))}
                                placeholder="Explain the circumstances..."
                                className="mt-1"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Notice to Recipient */}
                      <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                        <h4 className="font-semibold mb-2 text-amber-800">Notice to Recipient of Information</h4>
                        <p className="text-sm text-amber-900">
                          The information has been disclosed to you from records the confidentiality of which may be protected by federal and/or state law. If the records are so protected, Federal Regulation (42 CFR part 2) prohibits you from making further disclosure of this information unless further disclosure is expressly permitted by the written consent of the person to whom it pertains, or as otherwise permitted by 42 CFR, part 2.
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button variant="outline" onClick={() => { setSelectedRelease(null); setClientDetailTab('contacts'); }}>
                          Cancel
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={async () => {
                            if (!selectedClient?.id) {
                              toast.error('No client selected');
                              return;
                            }
                            try {
                              let saved;
                              if (selectedRelease?.id) {
                                // Update existing draft / pending release
                                const response = await axios.put(`${API}/releases/${selectedRelease.id}`, releaseFormData);
                                saved = response.data;
                                setClientReleases(prev => prev.map(r => r.id === saved.id ? saved : r));
                                toast.success('Release updated');
                              } else {
                                const response = await axios.post(`${API}/releases`, {
                                  ...releaseFormData,
                                  client_id: selectedClient.id
                                });
                                saved = response.data;
                                setClientReleases(prev => [saved, ...prev]);
                                toast.success('Release saved as draft');
                              }
                              setSelectedRelease(null);
                              setClientDetailTab('contacts');
                            } catch (error) {
                              console.error('Error saving release:', error);
                              toast.error(error.response?.data?.detail || 'Failed to save release');
                            }
                          }}
                          data-testid="save-release-draft-btn"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          {selectedRelease?.id ? 'Save Changes' : 'Save as Draft'}
                        </Button>
                        <Button 
                          className="bg-indigo-600 hover:bg-indigo-700"
                          onClick={async () => {
                            if (!selectedClient?.id) {
                              toast.error('No client selected');
                              return;
                            }
                            try {
                              let releaseId;
                              if (selectedRelease?.id) {
                                // Update first, then send
                                const updateRes = await axios.put(`${API}/releases/${selectedRelease.id}`, releaseFormData);
                                releaseId = updateRes.data.id;
                                setClientReleases(prev => prev.map(r => r.id === releaseId ? updateRes.data : r));
                              } else {
                                const createResponse = await axios.post(`${API}/releases`, {
                                  ...releaseFormData,
                                  client_id: selectedClient.id
                                });
                                releaseId = createResponse.data.id;
                                setClientReleases(prev => [createResponse.data, ...prev]);
                              }
                              await axios.post(`${API}/releases/${releaseId}/send-for-signature`);
                              setClientReleases(prev => prev.map(r => r.id === releaseId ? {...r, status: 'pending_signature'} : r));
                              toast.success('Release sent to client for signature');
                              setSelectedRelease(null);
                              setClientDetailTab('contacts');
                            } catch (error) {
                              console.error('Error sending release:', error);
                              toast.error(error.response?.data?.detail || 'Failed to send release');
                            }
                          }}
                          data-testid="send-release-btn"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          {selectedRelease?.id ? 'Save & Send for Signature' : 'Save & Send for Signature'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

              </Tabs>

              {/* Additional Information, Appointments, and Recent Notes - Only show on Overview tab */}
              {clientDetailTab === 'overview' && (
                <>
              {/* Additional Information */}
              <Card>
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50 transition-colors py-3"
                  onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      Additional Information
                    </CardTitle>
                    <ChevronDown className={`h-5 w-5 transition-transform ${showAdditionalInfo ? 'transform rotate-180' : ''}`} />
                  </div>
                </CardHeader>
                {showAdditionalInfo && (
                  <CardContent>
                    {/* Edit button at the top */}
                    {(user?.can_edit_client_status_insurance || hasPermission('edit_client_insurance') || isSuperAdmin(user)) && (
                      <div className="flex justify-end mb-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            // Phase 3e.3a — delegate to V2 provider.
                            intakeWizard.open(selectedClient.id);
                            setShowClientDetail(false);
                          }}
                          title="Edit client information"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      </div>
                    )}
                    
                    {/* Address Information Section */}
                    <div className="border-b pb-6 mb-6">
                      <h4 className="font-semibold text-gray-700 mb-3">Address Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Mailing Address */}
                        <div>
                          <h5 className="text-sm font-medium text-gray-600 mb-2">📬 Mailing Address</h5>
                          <div className="text-sm space-y-1 text-gray-700">
                            {selectedClient.address_line1 || selectedClient.city || selectedClient.state || selectedClient.zipcode ? (
                              <>
                                {selectedClient.address_line1 && <p>{selectedClient.address_line1}</p>}
                                {selectedClient.address_line2 && <p>{selectedClient.address_line2}</p>}
                                {(selectedClient.city || selectedClient.state || selectedClient.zipcode) && (
                                  <p>
                                    {selectedClient.city}{selectedClient.city && selectedClient.state ? ', ' : ''}{selectedClient.state} {selectedClient.zipcode}
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-gray-400 italic">No mailing address on file</p>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Used for: Client correspondence, billing statements</p>
                        </div>
                        
                        {/* Insurance Address */}
                        <div>
                          <h5 className="text-sm font-medium text-gray-600 mb-2">🏥 Insurance Address (for claims)</h5>
                          <div className="text-sm space-y-1 text-gray-700">
                            {selectedClient.use_separate_insurance_address ? (
                              selectedClient.insurance_address_line1 || selectedClient.insurance_city || selectedClient.insurance_state || selectedClient.insurance_zipcode ? (
                                <>
                                  {selectedClient.insurance_address_line1 && <p>{selectedClient.insurance_address_line1}</p>}
                                  {selectedClient.insurance_address_line2 && <p>{selectedClient.insurance_address_line2}</p>}
                                  {(selectedClient.insurance_city || selectedClient.insurance_state || selectedClient.insurance_zipcode) && (
                                    <p>
                                      {selectedClient.insurance_city}{selectedClient.insurance_city && selectedClient.insurance_state ? ', ' : ''}{selectedClient.insurance_state} {selectedClient.insurance_zipcode}
                                    </p>
                                  )}
                                </>
                              ) : (
                                <p className="text-gray-400 italic">No insurance address on file</p>
                              )
                            ) : (
                              <p className="text-gray-600">
                                <span className="inline-block mr-1">↑</span> Same as mailing address
                              </p>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Used for: Insurance claims submission</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Dynamic content based on intake form config */}
                    {(() => {
                      // Fields shown elsewhere (don't duplicate in Additional Info)
                      const fieldsShownElsewhere = [
                        'first_name', 'middle_name', 'last_name', 'preferred_name',
                        'date_of_birth', 'ssn', 'email', 'phone', 'cell_phone',
                        'gender',
                        'primary_insurance', 'secondary_insurance', 'tertiary_insurance',
                        'primary_insurance_id', 'secondary_insurance_id', 'tertiary_insurance_id',
                        'emergency_contacts',
                        'status', 'assigned_providers', 'assigned_provider', 'preferred_location',  // Already shown at top of client detail
                        'assignment_type'  // From intake wizard, managed separately
                      ];
                      
                      if (!intakeFormConfig || !intakeFormConfig.steps) {
                        return null;
                      }
                      
                      return (
                        <div className="space-y-6">
                          {intakeFormConfig.steps.map(section => {
                            // Filter fields that should be shown in Additional Info
                            const fieldsToShow = section.fields.filter(field => 
                              field.visible && !fieldsShownElsewhere.includes(field.field_id)
                            );
                            
                            if (fieldsToShow.length === 0) return null;
                            
                            return (
                              <div key={section.step_id} className="border-b pb-4 last:border-b-0">
                                <h4 className="font-semibold text-gray-700 mb-3">{section.step_name}</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                  {fieldsToShow.map(field => {
                                    let fieldValue = selectedClient[field.field_id];
                                    let displayValue = fieldValue;
                                    let hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
                                    
                                    // Special handling for specific fields
                                    if (field.field_id === 'status') {
                                      displayValue = selectedClient.status;
                                      hasValue = !!selectedClient.status;
                                    } else if (field.field_id === 'preferred_location') {
                                      const preferredLocationId = selectedClient.preferred_location;
                                      if (preferredLocationId) {
                                        const location = (locations || []).find(loc => loc.id === preferredLocationId);
                                        displayValue = location ? location.name : preferredLocationId;
                                        hasValue = !!location;
                                      } else {
                                        hasValue = false;
                                      }
                                    } else if (field.field_id === 'assigned_providers') {
                                      const assignedProviders = selectedClient.assigned_providers || [];
                                      if (assignedProviders.length > 0) {
                                        const providerNames = assignedProviders.map(providerId => {
                                          const provider = (providers || []).find(p => p.id === providerId);
                                          return provider ? provider.full_name : null;
                                        }).filter(Boolean);
                                        if (providerNames.length > 0) {
                                          displayValue = providerNames.join(', ');
                                          hasValue = true;
                                        } else {
                                          hasValue = false;
                                        }
                                      } else {
                                        hasValue = false;
                                      }
                                    } else if (field.field_type === 'checkbox') {
                                      displayValue = fieldValue ? 'Yes' : 'No';
                                    } else if (field.field_type === 'date') {
                                      displayValue = formatDateMMDDYYYY(fieldValue) || fieldValue;
                                    } else if (field.field_type === 'multi-select') {
                                      // Handle multi-select fields (arrays)
                                      if (Array.isArray(fieldValue) && fieldValue.length > 0) {
                                        displayValue = fieldValue.join(', ');
                                        hasValue = true;
                                      } else {
                                        hasValue = false;
                                      }
                                    } else if (field.field_type === 'textarea') {
                                      // Textarea fields - preserve line breaks
                                      hasValue = fieldValue && fieldValue.trim().length > 0;
                                    }
                                    
                                    return (
                                      <div key={field.field_id} className={field.field_type === 'textarea' ? 'md:col-span-3' : ''}>
                                        <Label className="text-xs text-gray-500">{field.field_name}</Label>
                                        <p className={`${!hasValue ? "text-gray-400 italic" : ""} ${field.field_type === 'textarea' ? 'whitespace-pre-wrap' : ''}`}>
                                          {hasValue ? displayValue : "None provided"}
                                        </p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </CardContent>
                )}
              </Card>

              {/* Upcoming & Recent Appointments */}
              <Card>
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50 transition-colors py-3"
                  onClick={() => setShowOverviewAppointments(!showOverviewAppointments)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      Appointments
                      <Badge variant="outline" className="ml-2 text-xs">
                        {appointments.filter(apt => (apt.client_id === selectedClient?.id || apt.client_ids?.includes(selectedClient?.id)) && new Date(apt.start_time) >= new Date()).length} upcoming
                      </Badge>
                    </CardTitle>
                    <ChevronDown className={`h-5 w-5 transition-transform ${showOverviewAppointments ? 'transform rotate-180' : ''}`} />
                  </div>
                </CardHeader>
                {showOverviewAppointments && (
                <CardContent>
                  {/* Upcoming Appointments */}
                  <div className="mb-4">
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">Upcoming</h4>
                    <div className="space-y-2">
                      {appointments
                        .filter(apt => (apt.client_id === selectedClient.id || apt.client_ids?.includes(selectedClient.id)) && new Date(apt.start_time) >= new Date())
                        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                        .slice(0, 3)
                        .map(apt => {
                          const startTime = new Date(apt.start_time);
                          const providerName = getProviderNames([apt.provider_id]);
                          return (
                            <div key={apt.id} className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                              <div>
                                <p className="text-sm font-medium">{apt.service_type}</p>
                                <p className="text-xs text-gray-600">
                                  {startTime.toLocaleDateString()} at {startTime.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                </p>
                                <p className="text-xs text-gray-500">{providerName}</p>
                              </div>
                              <Badge className={getStatusBadgeColor(apt.status)}>{apt.status}</Badge>
                            </div>
                          );
                        })}
                      {appointments.filter(apt => (apt.client_id === selectedClient.id || apt.client_ids?.includes(selectedClient.id)) && new Date(apt.start_time) >= new Date()).length === 0 && (
                        <p className="text-sm text-gray-500">No upcoming appointments</p>
                      )}
                    </div>
                  </div>

                  {/* Recent Past Appointments */}
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">Recent Past</h4>
                    <div className="space-y-2">
                      {appointments
                        .filter(apt => (apt.client_id === selectedClient.id || apt.client_ids?.includes(selectedClient.id)) && new Date(apt.start_time) < new Date())
                        .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
                        .slice(0, 3)
                        .map(apt => {
                          const startTime = new Date(apt.start_time);
                          const providerName = getProviderNames([apt.provider_id]);
                          return (
                            <div key={apt.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <div>
                                <p className="text-sm font-medium">{apt.service_type}</p>
                                <p className="text-xs text-gray-500">
                                  {startTime.toLocaleDateString()} at {startTime.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                </p>
                                <p className="text-xs text-gray-400">{providerName}</p>
                              </div>
                              <Badge className={getStatusBadgeColor(apt.status)}>{apt.status}</Badge>
                            </div>
                          );
                        })}
                      {appointments.filter(apt => (apt.client_id === selectedClient.id || apt.client_ids?.includes(selectedClient.id)) && new Date(apt.start_time) < new Date()).length === 0 && (
                        <p className="text-sm text-gray-500">No past appointments</p>
                      )}
                    </div>
                  </div>
                </CardContent>
                )}
              </Card>

              {/* Recent Notes */}
              <Card>
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50 transition-colors py-3"
                  onClick={() => setShowOverviewNotes(!showOverviewNotes)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      Recent Clinical Notes
                      <Badge variant="outline" className="ml-2 text-xs">
                        {notes.filter(note => note.client_id === selectedClient?.id).length} total
                      </Badge>
                    </CardTitle>
                    <ChevronDown className={`h-5 w-5 transition-transform ${showOverviewNotes ? 'transform rotate-180' : ''}`} />
                  </div>
                </CardHeader>
                {showOverviewNotes && (
                <CardContent>
                  <div className="space-y-2">
                    {notes
                      .filter(note => note.client_id === selectedClient.id)
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .slice(0, 3)
                      .map(note => {
                        // Get display name - prioritize template_name from note, then title
                        const noteName = note.template_name || note.title || 'Clinical Note';
                        const providerName = note.provider_name || users.find(u => u.id === note.provider_id)?.full_name || '';
                        const dateOfService = formatDateStringForDisplay(note.date_of_service || note.created_at);
                        
                        return (
                          <div 
                            key={note.id} 
                            className="p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 border-l-4 border-blue-400"
                            onClick={() => openNote(note)}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm text-gray-800">{noteName}</span>
                              <Badge className={getStatusBadgeColor(note.status)}>{formatStatusLabel(note)}</Badge>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>{providerName}</span>
                              <span>DOS: {dateOfService}</span>
                            </div>
                          </div>
                        );
                      })}
                    {notes.filter(note => note.client_id === selectedClient.id).length === 0 && (
                      <p className="text-sm text-gray-500">No clinical notes documented</p>
                    )}
                  </div>
                </CardContent>
                )}
              </Card>
              </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Notification Manager Dialog - extracted to NotificationManagerDialog.jsx (Feb 2026) */}
      <NotificationManagerDialog
        open={showNotificationManager}
        setOpen={setShowNotificationManager}
        user={user}
        impersonatedUserId={impersonatedUserId}
        isAdmin={isAdmin}
        setEditingAppointment={setEditingAppointment}
        setShowAppointmentForm={setShowAppointmentForm}
        setActiveTab={setActiveTab}
        setSelectedClient={setSelectedClient}
        setShowClientDetail={setShowClientDetail}
        setClientDetailTab={setClientDetailTab}
        setBillingReviewClientId={setBillingReviewClientId}
        setShowBillingReviewDialog={setShowBillingReviewDialog}
        fetchAppointments={refreshAppointments}
      />
      {/* User Management Dialog */}
      <Dialog open={showUserList} onOpenChange={(open) => {
        if (open) {
          void 0 && console.log('User Management Dialog Opening');
          void 0 && console.log('Total users:', users?.length);
          void 0 && console.log('Active users:', users?.filter(u => u.is_active && !u.is_archived).length);
          void 0 && console.log('Sample user:', users?.[0]);
          // Fetch super admin count for "Last Man Standing" protection
          fetchSuperAdminCount();
        }
        setShowUserList(open);
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>User Management - Full Directory</DialogTitle>
            <AdminJumpToDropdown currentDialogId="users" isDirty={currentAdminDialogDirty} />
          </DialogHeader>
          
          <Tabs value={showArchivedUsers ? "archived" : "active"} onValueChange={(value) => {
            setShowArchivedUsers(value === "archived");
            // No need to refetch - we already have all users
          }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="active">Active Users</TabsTrigger>
              <TabsTrigger value="archived">Archived Users</TabsTrigger>
            </TabsList>
            
            <TabsContent value="active" className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Total Users Loaded: {users?.length || 0} | 
                  Active Users: {users?.filter(u => u.is_active && !u.is_archived).length || 0} |
                  {users?.length > 0 && (
                    <> Active: {users?.filter(u => u.is_active).length}, Inactive: {users?.filter(u => u.is_archived).length}</>
                  )}
                </div>
                <Button 
                  onClick={() => {
                    resetUserFormData();
                    fetchRoles();
                    fetchCptBundles();
                    if (!cptCodes || cptCodes.length === 0) {
                      // Static data - no global refresh needed // Ensure CPT codes are loaded
                    }
                    setShowUserForm(true);
                  }}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="add-user-from-list"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add New User
                </Button>
              </div>
              
              {/* Search and Filter Controls */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1">
                  <DebouncedInput
                    type="text"
                    placeholder="Search by name, email, or phone..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Role:</Label>
                  <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {roles?.map(role => (
                        <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(userSearchTerm || userRoleFilter !== 'all') && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setUserSearchTerm('');
                        setUserRoleFilter('all');
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                {/* Sort Mode */}
                <div className="flex items-center gap-1">
                  <Label className="text-sm whitespace-nowrap">Sort:</Label>
                  <Select value={userSortMode} onValueChange={setUserSortMode}>
                    <SelectTrigger className="w-36 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">A-Z by Name</SelectItem>
                      <SelectItem value="role">Role, then Name</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* View Mode Toggle */}
                <div className="flex items-center border rounded-lg">
                  <Button
                    variant={userViewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    className={userViewMode === 'list' ? 'bg-teal-600' : ''}
                    onClick={() => setUserViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={userViewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    className={userViewMode === 'grid' ? 'bg-teal-600' : ''}
                    onClick={() => setUserViewMode('grid')}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* List View */}
              {userViewMode === 'list' && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Phone</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Role</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {users?.filter(u => {
                        if (!u.is_active || u.is_archived) return false;
                        if (userRoleFilter !== 'all' && u.role_id !== userRoleFilter) return false;
                        if (userSearchTerm) {
                          const searchLower = userSearchTerm.toLowerCase();
                          const nameMatch = u.full_name?.toLowerCase().includes(searchLower) ||
                            `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(searchLower);
                          const emailMatch = u.email?.toLowerCase().includes(searchLower);
                          const phoneMatch = u.phone?.toLowerCase().includes(searchLower);
                          if (!nameMatch && !emailMatch && !phoneMatch) return false;
                        }
                        return true;
                      }).sort((a, b) => {
                        const nameA = (a.first_name || a.full_name || '').toLowerCase();
                        const nameB = (b.first_name || b.full_name || '').toLowerCase();
                        if (userSortMode === 'role') {
                          const roleA = (roles?.find(r => r.id === a.role_id)?.name || a.role || '').toLowerCase();
                          const roleB = (roles?.find(r => r.id === b.role_id)?.name || b.role || '').toLowerCase();
                          if (roleA !== roleB) return roleA.localeCompare(roleB);
                        }
                        return nameA.localeCompare(nameB);
                      }).map((userItem) => {
                        const displayName = userItem.full_name || 
                          (userItem.first_name && userItem.last_name ? `${userItem.first_name} ${userItem.last_name}`.trim() : null) ||
                          userItem.email || 'Unknown User';
                        return (
                          <tr key={userItem.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium flex items-center gap-2">
                                {displayName}
                                {userItem.disable_appointment_reminders && (
                                  <span
                                    className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border border-amber-300 bg-amber-100 text-amber-800"
                                    title="All automated appointment reminders are disabled for this provider"
                                    data-testid={`reminders-off-badge-row-${userItem.id}`}
                                  >
                                    <BellOff className="h-3 w-3" />
                                    Reminders OFF
                                  </span>
                                )}
                              </div>
                              {userItem.title && <div className="text-xs text-gray-500">{userItem.title}</div>}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{userItem.email}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{userItem.phone || '-'}</td>
                            <td className="px-4 py-3">
                              <Badge className={getRoleBadgeColor(roles?.find(r => r.id === userItem.role_id)?.name || userItem.role)}>
                                {roles?.find(r => r.id === userItem.role_id)?.name || userItem.role || 'No Role'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={userItem.is_active ? 'default' : 'secondary'} className={userItem.is_active ? 'bg-green-100 text-green-800' : ''}>
                                {userItem.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {/* 2FA button in list view */}
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="ghost" size="sm" title="2FA Options">
                                      <Shield className={`h-4 w-4 ${userItem.two_factor_enabled ? 'text-blue-600' : 'text-gray-400'}`} />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-56">
                                    <div className="space-y-2">
                                      <p className="text-sm font-medium">2FA Management</p>
                                      <p className="text-xs text-gray-500">{userItem.two_factor_enabled ? '2FA is enabled' : '2FA is not set up'}</p>
                                      {userItem.two_factor_enabled ? (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="w-full text-red-600 hover:text-red-700"
                                          onClick={() => {
                                            if (window.confirm(`Reset 2FA for ${userItem.full_name || userItem.email}? They will need to set it up again on next login.`)) {
                                              resetUser2FA(userItem.id);
                                            }
                                          }}
                                        >
                                          <RotateCcw className="h-4 w-4 mr-2" />
                                          Reset 2FA
                                        </Button>
                                      ) : (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="w-full"
                                          onClick={() => requireUser2FA(userItem.id)}
                                        >
                                          <Shield className="h-4 w-4 mr-2" />
                                          Require 2FA Setup
                                        </Button>
                                      )}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                  fetchRoles();
                                  fetchCptBundles();
                                  // Always refresh superadmin count when editing a user (for Last Man Standing protection)
                                  if (isSuperAdmin(userItem)) {
                                    fetchSuperAdminCount();
                                  }
                                  if (!cptCodes || cptCodes.length === 0) {
                                    // Static data - no global refresh needed
                                  }
                                  setEditingUser(userItem);
                                  setUserFormData({
                                    email: userItem.email || '',
                                    password: '',
                                    first_name: userItem.first_name || '',
                                    last_name: userItem.last_name || '',
                                    full_name: userItem.full_name || '',
                                    role_id: userItem.role_id || '',
                                    phone: userItem.phone || '',
                                    title: userItem.title || '',
                                    department: userItem.department || '',
                                    credentials: userItem.credentials || '',
                                    is_provider: userItem.is_provider || false,
                                    is_prescriber: userItem.is_prescriber || false,
                                    is_medication_manager: userItem.is_medication_manager || false,
                                    is_waitlist_manager: userItem.is_waitlist_manager || false,
                                    is_biller: userItem.is_biller || false,
                                    is_billing_manager: userItem.is_billing_manager || false,
                                    is_medical_records_manager: userItem.is_medical_records_manager || false,
                                    is_payroll_manager: userItem.is_payroll_manager || false,
                                    is_era_manager: userItem.is_era_manager || false,
                                    is_authorization_manager: userItem.is_authorization_manager || false,
                                    is_scheduler: userItem.is_scheduler || false,
                                    scheduling_for: userItem.scheduling_for || [],
                                    npi: userItem.npi || '',
                                    taxonomy_code: userItem.taxonomy_code || '',
                                    employee_id: userItem.employee_id || '',
                                    license_status: userItem.license_status || 'Licensed',
                                    medicare_approved: userItem.medicare_approved || false,
                                    supervisor_ids: userItem.supervisor_ids || [],
                                    assigned_billers: userItem.assigned_billers || [],
                                    assigned_cpt_codes: userItem.assigned_cpt_codes || [],
                                    assigned_cpt_bundles: userItem.assigned_cpt_bundles || [],
                                    assigned_pos_codes: userItem.assigned_pos_codes || [],
                                    assigned_locations: userItem.assigned_locations || [],
                                    default_location: userItem.default_location || null,
                                    default_pos_code: userItem.default_pos_code || null,
                                    default_telehealth_modality: userItem.default_telehealth_modality || null,
                                    default_template_id: userItem.default_template_id || '',
                                    custom_telehealth_url: userItem.custom_telehealth_url || '',
                                    meet_space_uri: userItem.meet_space_uri || null,
                                    meet_space_resource_name: userItem.meet_space_resource_name || null,
                                    meet_space_created_at: userItem.meet_space_created_at || null,
                                    disable_appointment_reminders: userItem.disable_appointment_reminders || false,
                                    can_archive_clients: userItem.can_archive_clients || false,
                                    can_archive_users: userItem.can_archive_users || false,
                                    can_generate_client_portal_links: userItem.can_generate_client_portal_links !== false,
                                    can_qa_audit: userItem.can_qa_audit || false,
                                    group_admin: userItem.group_admin || false,
                                    client_visibility: userItem.client_visibility || 'own_only',
                                    is_archived: userItem.is_archived || false,
                                    is_active: userItem.is_active !== false
                                  });
                                  setShowUserForm(true);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Grid View */}
              {userViewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {users?.filter(u => {
                  // First filter: active and not archived
                  if (!u.is_active || u.is_archived) return false;
                  
                  // Role filter
                  if (userRoleFilter !== 'all' && u.role_id !== userRoleFilter) return false;
                  
                  // Search filter
                  if (userSearchTerm) {
                    const searchLower = userSearchTerm.toLowerCase();
                    const nameMatch = u.full_name?.toLowerCase().includes(searchLower) ||
                      `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(searchLower);
                    const emailMatch = u.email?.toLowerCase().includes(searchLower);
                    const phoneMatch = u.phone?.toLowerCase().includes(searchLower);
                    if (!nameMatch && !emailMatch && !phoneMatch) return false;
                  }
                  
                  return true;
                }).sort((a, b) => {
                  const nameA = (a.first_name || a.full_name || '').toLowerCase();
                  const nameB = (b.first_name || b.full_name || '').toLowerCase();
                  if (userSortMode === 'role') {
                    const roleA = (roles?.find(r => r.id === a.role_id)?.name || a.role || '').toLowerCase();
                    const roleB = (roles?.find(r => r.id === b.role_id)?.name || b.role || '').toLowerCase();
                    if (roleA !== roleB) return roleA.localeCompare(roleB);
                  }
                  return nameA.localeCompare(nameB);
                }).map((userItem) => {
                  
                  const displayName = userItem.full_name || 
                    (userItem.first_name && userItem.last_name ? `${userItem.first_name} ${userItem.last_name}`.trim() : null) ||
                    userItem.email ||
                    'Unknown User';
                  
                  return (
                  <Card key={userItem.id} className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg">
                          {displayName}
                        </h3>
                        <Badge className={getRoleBadgeColor(roles?.find(r => r.id === userItem.role_id)?.name || userItem.role)}>
                          {roles?.find(r => r.id === userItem.role_id)?.name || userItem.role || 'No Role'}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center text-gray-600">
                          <Mail className="h-4 w-4 mr-2" />
                          {userItem.email}
                        </div>
                        
                        {userItem.phone && (
                          <div className="flex items-center text-gray-600">
                            <Phone className="h-4 w-4 mr-2" />
                            {userItem.phone}
                          </div>
                        )}
                        
                        {userItem.title && (
                          <div className="text-gray-600">
                            <strong>Title:</strong> {userItem.title}
                          </div>
                        )}
                        
                        {userItem.credentials && (
                          <div className="text-gray-600">
                            <strong>Credentials:</strong> {userItem.credentials}
                          </div>
                        )}
                        
                        {userItem.department && (
                          <div className="text-gray-600">
                            <strong>Department:</strong> {userItem.department}
                          </div>
                        )}
                        
                        {userItem.supervisor_ids && userItem.supervisor_ids.length > 0 && (
                          <div className="text-gray-600">
                            <strong>Supervisors:</strong> {
                              userItem.supervisor_ids.map(supId => {
                                const sup = users?.find(u => u.id === supId);
                                return sup ? `${sup.first_name} ${sup.last_name}` : 'Unknown';
                              }).join(', ')
                            }
                          </div>
                        )}
                        
                        <div className="text-xs text-gray-500">
                          Created: {new Date(userItem.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between pt-2 border-t">
                        <div className="flex items-center space-x-2">
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                          {/* 2FA Status Badge */}
                          <Badge 
                            className={userItem.two_factor_enabled ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}
                            title={userItem.two_factor_enabled ? '2FA Enabled' : '2FA Not Set Up'}
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            {userItem.two_factor_enabled ? '2FA' : 'No 2FA'}
                          </Badge>
                        </div>
                        <div className="flex space-x-2">
                          {/* 2FA Management Button */}
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" title="2FA Options">
                                <Shield className="h-3 w-3" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-56">
                              <div className="space-y-2">
                                <p className="text-sm font-medium">2FA Management</p>
                                {userItem.two_factor_enabled ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full text-red-600 hover:text-red-700"
                                    onClick={() => {
                                      if (window.confirm(`Reset 2FA for ${userItem.full_name || userItem.email}? They will need to set it up again on next login.`)) {
                                        resetUser2FA(userItem.id);
                                      }
                                    }}
                                  >
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                    Reset 2FA
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full"
                                    onClick={() => requireUser2FA(userItem.id)}
                                  >
                                    <Shield className="h-4 w-4 mr-2" />
                                    Require 2FA Setup
                                  </Button>
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              fetchRoles();
                              fetchCptBundles();
                              // Always refresh superadmin count when editing a user (for Last Man Standing protection)
                              if (isSuperAdmin(userItem)) {
                                fetchSuperAdminCount();
                              }
                              if (!cptCodes || cptCodes.length === 0) {
                                // Static data - no global refresh needed
                              }
                              setEditingUser(userItem);
                              setUserFormData({
                                email: userItem.email,
                                password: '',
                                first_name: userItem.first_name || '',
                                last_name: userItem.last_name || '',
                                full_name: userItem.full_name,
                                role_id: userItem.role_id || '',
                                phone: userItem.phone || '',
                                title: userItem.title || '',
                                department: userItem.department || '',
                                credentials: userItem.credentials || '',
                                is_provider: userItem.is_provider || false,
                                is_prescriber: userItem.is_prescriber || false,
                                is_medication_manager: userItem.is_medication_manager || false,
                                is_waitlist_manager: userItem.is_waitlist_manager || false,
                                is_biller: userItem.is_biller || false,
                                is_billing_manager: userItem.is_billing_manager || false,
                                is_medical_records_manager: userItem.is_medical_records_manager || false,
                                is_payroll_manager: userItem.is_payroll_manager || false,
                                    is_era_manager: userItem.is_era_manager || false,
                                is_authorization_manager: userItem.is_authorization_manager || false,
                                is_scheduler: userItem.is_scheduler || false,
                                scheduling_for: userItem.scheduling_for || [],
                                npi: userItem.npi || '',
                                license_status: userItem.license_status || 'Licensed',
                                medicare_approved: userItem.medicare_approved || false,
                                supervisor_ids: userItem.supervisor_ids || [],
                                assigned_billers: userItem.assigned_billers || [],
                                assigned_cpt_codes: userItem.assigned_cpt_codes || [],
                                assigned_cpt_bundles: userItem.assigned_cpt_bundles || [],
                                assigned_pos_codes: userItem.assigned_pos_codes || [],
                                assigned_locations: userItem.assigned_locations || [],
                                default_location: userItem.default_location || null,
                                default_pos_code: userItem.default_pos_code || null,
                                default_telehealth_modality: userItem.default_telehealth_modality || null,
                                default_template_id: userItem.default_template_id || '',
                                custom_telehealth_url: userItem.custom_telehealth_url || '',
                                meet_space_uri: userItem.meet_space_uri || null,
                                meet_space_resource_name: userItem.meet_space_resource_name || null,
                                meet_space_created_at: userItem.meet_space_created_at || null,
                                disable_appointment_reminders: userItem.disable_appointment_reminders || false,
                                can_archive_clients: userItem.can_archive_clients || false,
                                can_archive_users: userItem.can_archive_users || false,
                                can_generate_client_portal_links: userItem.can_generate_client_portal_links !== false,
                                can_qa_audit: userItem.can_qa_audit || false,
                                group_admin: userItem.group_admin || false,
                                client_visibility: userItem.client_visibility || 'own_only',
                                is_archived: userItem.is_archived || false
                              });
                              setShowUserForm(true);
                            }}
                            data-testid={`edit-user-${userItem.id}`}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                  )
                })}
              </div>
              )}
            </TabsContent>
            
            <TabsContent value="archived" className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Inactive Users: {users?.filter(u => u.is_archived).length || 0}
                </div>
              </div>
              
              {/* Search and Filter Controls for Archived */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1">
                  <DebouncedInput
                    type="text"
                    placeholder="Search archived users by name, email, or phone..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Role:</Label>
                  <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {roles?.map(role => (
                        <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(userSearchTerm || userRoleFilter !== 'all') && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setUserSearchTerm('');
                        setUserRoleFilter('all');
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {users?.filter(u => {
                  // First filter: archived
                  if (!u.is_archived) return false;
                  
                  // Role filter
                  if (userRoleFilter !== 'all' && u.role_id !== userRoleFilter) return false;
                  
                  // Search filter
                  if (userSearchTerm) {
                    const searchLower = userSearchTerm.toLowerCase();
                    const nameMatch = u.full_name?.toLowerCase().includes(searchLower) ||
                      `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(searchLower);
                    const emailMatch = u.email?.toLowerCase().includes(searchLower);
                    const phoneMatch = u.phone?.toLowerCase().includes(searchLower);
                    if (!nameMatch && !emailMatch && !phoneMatch) return false;
                  }
                  
                  return true;
                }).map((userItem) => (
                  <Card key={userItem.id} className="p-4 opacity-75">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg">{userItem.full_name}</h3>
                        <Badge className={getRoleBadgeColor(roles?.find(r => r.id === userItem.role_id)?.name || userItem.role)}>
                          {roles?.find(r => r.id === userItem.role_id)?.name || userItem.role || 'No Role'}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center text-gray-600">
                          <Mail className="h-4 w-4 mr-2" />
                          {userItem.email}
                        </div>
                        
                        {userItem.phone && (
                          <div className="flex items-center text-gray-600">
                            <Phone className="h-4 w-4 mr-2" />
                            {userItem.phone}
                          </div>
                        )}
                        
                        {userItem.title && (
                          <div className="text-gray-600">
                            <strong>Title:</strong> {userItem.title}
                          </div>
                        )}
                        
                        <div className="text-xs text-gray-500">
                          Created: {new Date(userItem.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between pt-2 border-t">
                        <Badge className="bg-red-100 text-red-800">Archived</Badge>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              // Always refresh superadmin count when editing a user (for Last Man Standing protection)
                              if (isSuperAdmin(userItem)) {
                                fetchSuperAdminCount();
                              }
                              setEditingUser(userItem);
                              setUserFormData({
                                email: userItem.email,
                                password: '',
                                first_name: userItem.first_name || '',
                                last_name: userItem.last_name || '',
                                full_name: userItem.full_name,
                                role_id: userItem.role_id || '',
                                phone: userItem.phone || '',
                                title: userItem.title || '',
                                department: userItem.department || '',
                                credentials: userItem.credentials || '',
                                is_provider: userItem.is_provider || false,
                                is_prescriber: userItem.is_prescriber || false,
                                is_medication_manager: userItem.is_medication_manager || false,
                                is_waitlist_manager: userItem.is_waitlist_manager || false,
                                is_biller: userItem.is_biller || false,
                                is_billing_manager: userItem.is_billing_manager || false,
                                is_medical_records_manager: userItem.is_medical_records_manager || false,
                                is_payroll_manager: userItem.is_payroll_manager || false,
                                    is_era_manager: userItem.is_era_manager || false,
                                is_authorization_manager: userItem.is_authorization_manager || false,
                                is_scheduler: userItem.is_scheduler || false,
                                scheduling_for: userItem.scheduling_for || [],
                                npi: userItem.npi || '',
                                license_status: userItem.license_status || 'Licensed',
                                medicare_approved: userItem.medicare_approved || false,
                                supervisor_ids: userItem.supervisor_ids || [],
                                assigned_billers: userItem.assigned_billers || [],
                                assigned_cpt_codes: userItem.assigned_cpt_codes || [],
                                assigned_cpt_bundles: userItem.assigned_cpt_bundles || [],
                                assigned_pos_codes: userItem.assigned_pos_codes || [],
                                assigned_locations: userItem.assigned_locations || [],
                                default_location: userItem.default_location || null,
                                default_pos_code: userItem.default_pos_code || null,
                                default_telehealth_modality: userItem.default_telehealth_modality || null,
                                default_template_id: userItem.default_template_id || '',
                                custom_telehealth_url: userItem.custom_telehealth_url || '',
                                meet_space_uri: userItem.meet_space_uri || null,
                                meet_space_resource_name: userItem.meet_space_resource_name || null,
                                meet_space_created_at: userItem.meet_space_created_at || null,
                                disable_appointment_reminders: userItem.disable_appointment_reminders || false,
                                can_archive_clients: userItem.can_archive_clients || false,
                                can_archive_users: userItem.can_archive_users || false,
                                can_generate_client_portal_links: userItem.can_generate_client_portal_links !== false,
                                can_qa_audit: userItem.can_qa_audit || false,
                                group_admin: userItem.group_admin || false,
                                client_visibility: userItem.client_visibility || 'own_only',
                                is_archived: userItem.is_archived || false
                              });
                              setShowUserForm(true);
                            }}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              
              {(!users || users.filter(u => u.is_archived).length === 0) && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No archived users</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Quick Schedule Dialog */}
      <Dialog open={showQuickSchedule} onOpenChange={setShowQuickSchedule}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Schedule Appointment - {selectedTimeSlot?.toLocaleDateString()} at {selectedTimeSlot?.toLocaleTimeString()}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAppointment} className="space-y-4">
            <div>
              <Label htmlFor="quick_client_id">Client</Label>
              <Select 
                value={appointmentFormData.client_id} 
                onValueChange={(value) => setAppointmentFormData(prev => ({...prev, client_id: value}))}
              >
                <SelectTrigger data-testid="quick-appointment-client">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    try {
                      const accessibleClients = getAccessibleClients(appointmentFormData.provider_id);
                      if (!accessibleClients || accessibleClients.length === 0) {
                        return <SelectItem value="_none" disabled>No clients available</SelectItem>;
                      }
                      return accessibleClients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {formatClientName(client)}
                        </SelectItem>
                      ));
                    } catch (error) {
                      console.error('Error loading clients:', error);
                      return <SelectItem value="_error" disabled>Error loading clients</SelectItem>;
                    }
                  })()}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="quick_provider_id">Provider *</Label>
              <Select 
                value={appointmentFormData.provider_id} 
                onValueChange={(value) => {
                  // Find the selected provider
                  const selectedProvider = users?.find(u => u.id === value);
                  
                  // Auto-populate default location and POS code if provider has them set
                  const updates = { provider_id: value };
                  if (selectedProvider?.default_location) {
                    updates.location = selectedProvider.default_location;
                  }
                  if (selectedProvider?.default_pos_code) {
                    updates.pos_code = selectedProvider.default_pos_code;
                  }
                  
                  setAppointmentFormData(prev => ({...prev, ...updates}));
                }}
                required
              >
                <SelectTrigger data-testid="quick-appointment-provider">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    try {
                      const accessibleProviders = getAccessibleProviders(appointmentFormData.appointment_type);
                      if (!accessibleProviders || accessibleProviders.length === 0) {
                        return <SelectItem value="_none" disabled>No providers available</SelectItem>;
                      }
                      return accessibleProviders.map(provider => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {formatProviderName(provider)}
                        </SelectItem>
                      ));
                    } catch (error) {
                      console.error('Error loading providers (quick):', error);
                      return <SelectItem value="_error" disabled>Error loading providers</SelectItem>;
                    }
                  })()}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="quick_service_type">Service Type (CPT Code) *</Label>
              <Select 
                value={appointmentFormData.cpt_code_id} 
                onValueChange={(value) => {
                  // Find the selected CPT code by ID
                  const selectedCpt = cptCodes.find(code => code.id === value);
                  if (selectedCpt) {
                    const serviceTypeDisplay = selectedCpt.display_name || selectedCpt.code;
                    if (appointmentFormData.start_time) {
                      // Auto-calculate end time based on CPT duration
                      const startTime = new Date(appointmentFormData.start_time);
                      const endTime = new Date(startTime.getTime() + selectedCpt.duration_minutes * 60000);
                      const endTimeString = endTime.toISOString().slice(0, 16);
                      setAppointmentFormData(prev => ({
                        ...prev, 
                        service_type: serviceTypeDisplay,
                        end_time: endTimeString,
                        cpt_code_id: selectedCpt.id
                      }));
                    } else {
                      setAppointmentFormData(prev => ({
                        ...prev, 
                        service_type: serviceTypeDisplay,
                        cpt_code_id: selectedCpt.id
                      }));
                    }
                  }
                }}
                required
                disabled={!appointmentFormData.provider_id}
              >
                <SelectTrigger data-testid="quick-service-type">
                  <SelectValue placeholder={appointmentFormData.provider_id ? "Select CPT code" : "Select provider first"} />
                </SelectTrigger>
                <SelectContent>
                  {getAccessibleCPTCodes(appointmentFormData.provider_id).map(code => (
                    <SelectItem key={code.id} value={code.id}>
                      {code.display_name || code.code} - {code.description} ({code.duration_minutes} min)
                    </SelectItem>
                  ))}
                  {getAccessibleCPTCodes(appointmentFormData.provider_id).length === 0 && appointmentFormData.provider_id && (
                    <SelectItem value="none" disabled>No CPT codes assigned to this provider</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {!appointmentFormData.provider_id && (
                <p className="text-xs text-gray-500 mt-1">Please select a provider first</p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quick_start_time">Start Time</Label>
                <Input
                  id="quick_start_time"
                  type="datetime-local"
                  value={appointmentFormData.start_time}
                  onChange={(e) => {
                    const newStartTime = e.target.value;
                    // If CPT code is selected, auto-calculate end time
                    if (appointmentFormData.cpt_code_id) {
                      const selectedCpt = cptCodes.find(code => code.id === appointmentFormData.cpt_code_id);
                      if (selectedCpt) {
                        const startTime = new Date(newStartTime);
                        const endTime = new Date(startTime.getTime() + selectedCpt.duration_minutes * 60000);
                        const endTimeString = endTime.toISOString().slice(0, 16);
                        setAppointmentFormData(prev => ({
                          ...prev, 
                          start_time: newStartTime,
                          end_time: endTimeString
                        }));
                        return;
                      }
                    }
                    // Otherwise just update start time
                    setAppointmentFormData(prev => ({...prev, start_time: newStartTime}));
                  }}
                  required
                  data-testid="quick-start-time"
                />
              </div>
              <div>
                <Label htmlFor="quick_end_time">End Time</Label>
                <Input
                  id="quick_end_time"
                  type="datetime-local"
                  value={appointmentFormData.end_time}
                  onChange={(e) => setAppointmentFormData(prev => ({...prev, end_time: e.target.value}))}
                  required
                  data-testid="quick-end-time"
                />
              </div>
            </div>
            
            {/* Recurring Appointment Options */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center space-x-2 mb-3">
                <input
                  type="checkbox"
                  id="is_recurring"
                  checked={appointmentFormData.is_recurring}
                  onChange={(e) => setAppointmentFormData(prev => ({
                    ...prev,
                    is_recurring: e.target.checked,
                    recurrence_pattern: e.target.checked ? 'weekly' : '',
                    recurrence_count: e.target.checked ? '12' : ''
                  }))}
                  className="h-4 w-4"
                />
                <Label htmlFor="is_recurring" className="font-semibold">Make this a recurring appointment</Label>
              </div>
              
              {appointmentFormData.is_recurring && (
                <div className="space-y-3 ml-6">
                  <div>
                    <Label htmlFor="recurrence_pattern">Repeat Pattern *</Label>
                    <Select 
                      value={appointmentFormData.recurrence_pattern} 
                      onValueChange={(value) => setAppointmentFormData(prev => ({...prev, recurrence_pattern: value}))}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select pattern" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                    {appointmentFormData.recurrence_pattern === 'monthly' && (() => {
                      const d = new Date(appointmentFormData.start_time);
                      if (isNaN(d.getTime())) return null;
                      const dayN = d.getDate();
                      const wd = d.toLocaleDateString('en-US', { weekday: 'long' });
                      const ord = Math.floor((dayN - 1) / 7) + 1;
                      const dim = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
                      const isLast = dayN + 7 > dim && ord >= 4;
                      const ordLabel = isLast ? 'Last' : ['1st', '2nd', '3rd', '4th', '5th'][ord - 1];
                      const mode = appointmentFormData.monthly_mode || 'day_of_month';
                      return (
                        <div className="mt-2">
                          <Select value={mode} onValueChange={(v) => setAppointmentFormData(prev => ({ ...prev, monthly_mode: v }))}>
                            <SelectTrigger data-testid="monthly-mode-select-legacy">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="day_of_month">On day {dayN} of each month</SelectItem>
                              <SelectItem value="nth_weekday">On the {ordLabel.toLowerCase()} {wd} of each month</SelectItem>
                            </SelectContent>
                          </Select>
                          {mode === 'day_of_month' && dayN > 28 && (
                            <p className="text-xs text-amber-600 mt-1" data-testid="monthly-clamp-hint-legacy">
                              Day {dayN}: months without that day will use their last day instead (e.g. Feb → Feb 28/29). Choose "{ordLabel.toLowerCase()} {wd}" to avoid this.
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Day of week selection for weekly/biweekly */}
                  {(appointmentFormData.recurrence_pattern === 'weekly' || appointmentFormData.recurrence_pattern === 'biweekly') && (
                    <div>
                      <Label>Repeat On (select days)</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => {
                          const isSelected = appointmentFormData.recurring_days.includes(index);
                          return (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                setAppointmentFormData(prev => {
                                  const days = [...prev.recurring_days];
                                  if (isSelected) {
                                    return { ...prev, recurring_days: days.filter(d => d !== index) };
                                  } else {
                                    return { ...prev, recurring_days: [...days, index].sort() };
                                  }
                                });
                              }}
                              className={`px-3 py-1 rounded text-sm border ${
                                isSelected 
                                  ? 'bg-teal-600 text-white border-teal-600' 
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-teal-400'
                              }`}
                            >
                              {day.slice(0, 3)}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {appointmentFormData.recurring_days.length === 0 
                          ? 'Select at least one day, or leave blank to use the start date\'s day' 
                          : `Will repeat on ${appointmentFormData.recurring_days.map(d => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d]).join(', ')}`
                        }
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="recurrence_count">Number of Occurrences</Label>
                      {(() => {
                        const recCap = ['block_time', 'supervision'].includes(appointmentFormData.appointment_type) ? 365 : 16;
                        return (
                      <Input
                        id="recurrence_count"
                        type="number"
                        min="1"
                        max={recCap}
                        value={appointmentFormData.recurrence_count}
                        onChange={(e) => {
                          const value = e.target.value ? Math.min(recCap, parseInt(e.target.value) || 1) : '';
                          setAppointmentFormData(prev => ({...prev, recurrence_count: value.toString(), recurrence_end_date: ''}));
                        }}
                        placeholder={`Max ${recCap}`}
                      />
                        );
                      })()}
                    </div>
                    <div>
                      <Label htmlFor="recurrence_end_date">Or End Date</Label>
                      <Input
                        id="recurrence_end_date"
                        type="date"
                        value={appointmentFormData.recurrence_end_date}
                        onChange={(e) => setAppointmentFormData(prev => ({...prev, recurrence_end_date: e.target.value, recurrence_count: ''}))}
                      />
                    </div>
                  </div>
                  
                  {!appointmentFormData.recurrence_count && !appointmentFormData.recurrence_end_date && (
                    <p className="text-xs text-red-500">Please specify either number of occurrences or an end date</p>
                  )}
                  
                  <p className="text-xs text-gray-500">
                    {appointmentFormData.recurrence_count && `Will create ${appointmentFormData.recurrence_count} appointments`}
                    {parseInt(appointmentFormData.recurrence_count) > 12 && (
                      <span className="text-amber-600"> • High session count - consider authorization limits</span>
                    )}
                    {appointmentFormData.recurrence_end_date && `Will repeat until ${new Date(appointmentFormData.recurrence_end_date).toLocaleDateString()}`}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowQuickSchedule(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                Schedule Appointment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Diagnosis Form Dialog */}
      {/* Add Diagnosis Dialog - Extracted Component */}
      <AddDiagnosisDialog
        open={showDiagnosisForm}
        onOpenChange={setShowDiagnosisForm}
        client={selectedClient}
        users={users}
        currentUser={user}
        onSuccess={async (clientId, options) => {
          // Refresh diagnoses list
          try {
            const diagnosesRes = await axios.get(`${API}/clients/${clientId}/diagnoses?include_archived=true`);
            setClientDiagnoses(prev => {
              // Replace diagnoses for this client, keep others
              const otherDiagnoses = prev.filter(d => d.client_id !== clientId);
              return [...otherDiagnoses, ...(diagnosesRes.data || [])];
            });
            toast.success('Diagnosis added successfully');
          } catch (error) {
            console.error('Failed to refresh diagnoses:', error);
          }
          // Only navigate if not in note editing context (selectedNote is set OR showAddNote is open)
          if (!selectedNote && !showAddNote) {
            handleClientClick(clientId, options);
          }
        }}
      />

      {/* User Archive Modal - Comprehensive Archive Workflow */}
      <UserArchiveModal
        isOpen={showArchiveModal}
        onClose={() => {
          setShowArchiveModal(false);
          setUserToArchive(null);
        }}
        user={userToArchive}
        providers={providers}
        onArchiveComplete={handleArchiveComplete}
      />


      {/* Alert Form Dialog */}
      <Dialog open={showAlertForm} onOpenChange={setShowAlertForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingAlert ? 'Edit Alert' : 'Add New Alert'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              if (editingAlert) {
                // Update existing alert
                await axios.put(`${API}/alerts/${editingAlert.id}`, alertFormData);
                toast.success("Alert updated successfully");
              } else {
                // Create new alert
                await axios.post(`${API}/clients/${selectedClient.id}/alerts`, alertFormData);
                toast.success("Alert created successfully");
              }
              setShowAlertForm(false);
              setEditingAlert(null);
              // Refresh client alerts immediately for instant UI update
              fetchClientAlerts(selectedClient.id);
              refreshAlerts(); // Refresh to update unread counts
            } catch (error) {
              if (error.response?.status === 409) {
                toast.error(error.response.data.detail || "This alert already exists for this client");
              } else {
                toast.error(editingAlert ? "Failed to update alert" : "Failed to create alert");
              }
            }
          }} className="space-y-4">
            <div>
              <Label htmlFor="alert-content">Alert Content *</Label>
              <DebouncedTextarea
                id="alert-content"
                value={alertFormData.content}
                onChange={(e) => setAlertFormData(prev => ({...prev, content: e.target.value}))}
                placeholder="Enter alert message..."
                rows={4}
                required
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-banner"
                checked={alertFormData.is_banner}
                onCheckedChange={(checked) => setAlertFormData(prev => ({...prev, is_banner: checked}))}
              />
              <Label htmlFor="is-banner" className="cursor-pointer">
                Make this a banner alert (prominently displayed)
              </Label>
            </div>
            
            <div>
              <Label htmlFor="tagged-users">Tag Users (Optional)</Label>
              <div className="space-y-2 mt-2 max-h-40 overflow-y-auto border rounded p-2">
                {users.filter(u => u.id !== user.id).map(tagUser => (
                  <div key={tagUser.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`tag-user-${tagUser.id}`}
                      checked={alertFormData.tagged_users.includes(tagUser.id)}
                      onCheckedChange={(checked) => {
                        setAlertFormData(prev => ({
                          ...prev,
                          tagged_users: checked
                            ? [...prev.tagged_users, tagUser.id]
                            : prev.tagged_users.filter(id => id !== tagUser.id)
                        }));
                      }}
                    />
                    <label htmlFor={`tag-user-${tagUser.id}`} className="text-sm cursor-pointer">
                      {tagUser.full_name} ({tagUser.role})
                    </label>
                  </div>
                ))}
              </div>
              {alertFormData.tagged_users.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {alertFormData.tagged_users.length} user(s) will be notified
                </p>
              )}
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowAlertForm(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingAlert ? 'Update Alert' : 'Create Alert'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* Note Detail Dialog — extracted VERBATIM to components/features/NoteDetailDialog.jsx
          (V2 Notes migration Phase 3A, Jun 2026). All state + handlers still live in this
          Dashboard and flow through the ctx bag below — legacy behavior unchanged. */}
      {showNoteDetail && (
        <NoteDetailDialog
          ctx={{
            API, NOTE_SIZE_PRESETS, allClientsForLookup, appointments, autosaveBlockedReason, availablePANs,
            availableTreatmentPlanGoals, calculateAge, canEditNote, canLockNote, clientAppointments, clientDiagnoses,
            clientGroups, clientMedications, clientMismatchInfo, clientNotes, clientVitals, clients,
            closeNoteWithCheck, confirmAction, cptCodes, editContextMenuPos, editedNoteConsent, editedNoteContent,
            // editingNote + fetchAppointments intentionally omitted — not bound in
            // Dashboard scope (dead references in the original inline block; the
            // component destructure yields undefined, matching the guarded usage).
            fetchTreatmentPlanGoals, formTemplates, formatDateMMDDYYYY, formatLocalDateTime,
            formatTimeTo12Hour, getAccessibleCPTCodes, getSignatureUrl, handleAddLinkedForm, handleCloseEditingNoteDiscard, handleCloseEditingNoteKeepEditing,
            handleCloseEditingNoteSaveAndClose, handleFieldChangeLifted, handleMismatchKeepEditing, handleMismatchMoveNote, handleMismatchSaveAndClose, handleNavigateToNote,
            handleSignNote, handleUnsignNote, isEditingNote, isSavingNote, linkedFormsAdded, linkedGoals,
            loadedSignatures, locations, noteCreationError, noteDialogSize, noteEditingAutosave, noteHasClientVisibilityDivider,
            noteJustSigned, noteLinkedAppointment, notes, openNote, organizations, posCodes,
            providers, refreshClientNotes, refreshNotes, refreshTodos, replaceClientVariables, returnToCreateNote,
            selectedClient, selectedNote, serviceTypes, setActiveTab, setAppointmentFormData, setAppointments,
            setAutosaveBlockedReason, setClientMedications, setClientNotes, setCorrectionsReason, setEditContextMenuPos, setEditedNoteConsent,
            setEditedNoteContent, setEditingAppointment, setEditingMedication, setIsEditingNote, setIsSavingNote, setLinkedFormsAdded,
            setLinkedGoals, setLoadedSignatures, setNewNoteFormData, setNewNoteFormDataBridge, setNotBillableComment, setNotBillableReason,
            setNotBillableTargetNote, setNoteCreationError, setNoteJustSigned, setNoteLinkedAppointment, setNotes, setReturnToCreateNote,
            setSelectedClient, setSelectedNote, setSelectedNoteClients, setSelectedNoteProvider, setSelectedNoteStatus, setSelectedTemplate,
            setSendToClientNote, setShowAddNote, setShowAllDiagnoses, setShowAppointmentForm, setShowCloseEditingNoteWarning, setShowCorrectionsDialog,
            setShowDiagnosisForm, setShowEditContextMenu, setShowLinkGoalsDialog, setShowMedicationForm, setShowNextStepsDialog, setShowNotBillableDialog,
            setShowNoteDetail, setShowNoteHistory, setShowReferencePanel, setShowSendToClientDialog, setShowSignatureContextMenu, setSignatureContextMenuPos,
            setSignatureContextMenuType, showAllDiagnoses, showCloseEditingNoteWarning, showEditContextMenu, showNoteDetail, showReferencePanel,
            showSignatureContextMenu, signatureContextMenuPos, signatureContextMenuType, updateNoteDialogSize, updateNoteStatusOptimistically, user,
            userRoleName, users,
          }}
        />
      )}

      {/* Signature Confirmation Dialog */}
      <SignatureConfirmDialog ctx={{
        API, refreshNotes, refreshTodos, selectedNote, setShowNoteDetail,
        setShowSignatureConfirm, setSignWithoutDate, setSignatureDate,
        showSignatureConfirm, signWithoutDate, signatureAction, signatureDate,
        updateNoteStatusOptimistically, user,
      }} />

      {/* Request Corrections Dialog */}
      <CorrectionsDialog ctx={{
        API, correctionsReason, refreshNotes, refreshTodos, selectedNote,
        setCorrectionsReason, setShowCorrectionsDialog, setShowNoteDetail,
        showCorrectionsDialog, user,
      }} />

      {/* Note History Panel */}
      <NoteHistoryPanel
        noteId={(() => {
          return selectedNote?.id;
        })()}
        isOpen={showNoteHistory}
        onClose={() => setShowNoteHistory(false)}
        onRestoreComplete={() => {
          refreshNotes(); refreshTodos();
          // Refresh the selected note
          if (selectedNote?.id) {
            axios.get(`${API}/notes/${selectedNote.id}`).then(res => {
              setSelectedNote(res.data);
            });
          }
        }}
        isNoteLocked={selectedNote?.status === 'locked' || selectedNote?.status === 'signed' || selectedNote?.is_locked}
      />

      {/* Send to Client Dialog — extracted to its own component so typing
          in the message textarea no longer triggers a full-App re-render. */}
      <SendToClientDialog
        open={showSendToClientDialog}
        onOpenChange={setShowSendToClientDialog}
        noteId={selectedNote?.id}
        alreadySigned={!!(selectedNote?.client_signature || selectedNote?.client_signed_at)}
        onSuccess={() => {
          refreshNotes();
          refreshTodos();
          setShowNoteDetail(false);
        }}
      />

      {/* Admin Override Dialog */}
      <Dialog open={showAdminOverrideDialog} onOpenChange={setShowAdminOverrideDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>⚠️ Admin Override - Signature Requirement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-900">
                <strong>Warning:</strong> You are bypassing required signatures. This should only be done in exceptional circumstances (e.g., client deceased, emergency processing).
              </p>
            </div>
            <div>
              <Label htmlFor="overrideReason">Reason for Override (required)</Label>
              <DebouncedTextarea
                id="overrideReason"
                className="w-full min-h-[120px] p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-red-500 mt-2"
                placeholder="Explain why signatures are being bypassed (e.g., client deceased, legal emergency, etc.)..."
                value={adminOverrideReason}
                onChange={(e) => setAdminOverrideReason(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAdminOverrideDialog(false);
                  setAdminOverrideReason('');
                  setOverrideTargetStatus(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                disabled={!adminOverrideReason.trim()}
                onClick={async () => {
                  try {
                    await axios.put(`${API}/notes/${selectedNote.id}`, {
                      status: overrideTargetStatus,
                      signature_override_reason: adminOverrideReason.trim(),
                      signature_override_by: user.id,
                      signature_override_at: new Date().toISOString()
                    });
                    toast.success('Status updated with admin override');
                    refreshNotes(); refreshTodos();
                    setShowAdminOverrideDialog(false);
                    setAdminOverrideReason('');
                    setOverrideTargetStatus(null);
                    setShowNoteDetail(false);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to update status');
                  }
                }}
              >
                ⚠️ Override & Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact dialogs — Add + Edit consolidated into a single
          ContactFormDialog component.  The two open booleans + handlers
          stay separate so existing call-sites work unchanged; the
          dialog body lives in one place now (Feb 2026 refactor). */}
      <ContactFormDialog
        open={showAddContact}
        onOpenChange={setShowAddContact}
        formData={contactFormData}
        setFormData={setContactFormData}
        onSubmit={handleCreateContact}
        onCancel={() => setShowAddContact(false)}
        editing={false}
      />
      <ContactFormDialog
        open={showEditContact}
        onOpenChange={setShowEditContact}
        formData={contactFormData}
        setFormData={setContactFormData}
        onSubmit={handleUpdateContact}
        onCancel={() => {
          setShowEditContact(false);
          setEditingContact(null);
        }}
        editing={true}
      />

      {/* Inactive Reason Dialog */}
      <Dialog open={showInactiveReasonDialog} onOpenChange={setShowInactiveReasonDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Client as Inactive</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Please provide a reason for marking this client as inactive. This will help with record-keeping and future reference.
            </p>
            <div>
              <Label htmlFor="inactive-reason">Reason *</Label>
              <DebouncedTextarea
                id="inactive-reason"
                className="w-full min-h-[100px] p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                value={inactiveReason}
                onChange={(e) => setInactiveReason(e.target.value)}
                placeholder="e.g., Client moved out of state, client's insurance no longer accepted, client requested pause in services..."
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowInactiveReasonDialog(false);
                  setInactiveClientId(null);
                  setInactiveReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirmInactive}
                className="bg-teal-600 hover:bg-teal-700"
              >
                Mark as Inactive
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Disenrollment Dialog - For multi-provider scenarios */}
      <Dialog open={showServiceDisenrollmentDialog} onOpenChange={(open) => {
        setShowServiceDisenrollmentDialog(open);
        if (!open) {
          setDischargeCheckInfo(null);
          setSelectedServicesToDisenroll([]);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-amber-500" />
              {dischargeCheckInfo?.has_multiple_providers 
                ? 'End Your Services Only'
                : 'Select Services to End'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {dischargeCheckInfo?.has_multiple_providers && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>{dischargeCheckInfo.client_name}</strong> has active services with other providers. 
                  You can only end your own service(s) - the client will remain active with other providers.
                </p>
              </div>
            )}
            
            {dischargeCheckInfo?.other_provider_enrollments?.length > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs font-medium text-blue-800 mb-2">Other active services (will continue):</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  {dischargeCheckInfo.other_provider_enrollments.map((e, idx) => (
                    <li key={idx}>• {e.service_name} with {e.provider_name}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {dischargeCheckInfo?.current_provider_enrollments?.length > 0 ? (
              <>
                <p className="text-sm text-gray-600">
                  Select which service(s) you want to end:
                </p>
                <div className="space-y-2">
                  {dischargeCheckInfo.current_provider_enrollments.map((enrollment) => (
                    <div 
                      key={enrollment.id} 
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedServicesToDisenroll.includes(enrollment.id)
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        setSelectedServicesToDisenroll(prev => 
                          prev.includes(enrollment.id)
                            ? prev.filter(id => id !== enrollment.id)
                            : [...prev, enrollment.id]
                        );
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{enrollment.service_name}</p>
                          <p className="text-xs text-gray-500">
                            Started: {enrollment.start_date 
                              ? fmtLocalDate(enrollment.start_date) 
                              : enrollment.created_at 
                                ? new Date(enrollment.created_at).toLocaleDateString()
                                : 'Unknown'}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedServicesToDisenroll.includes(enrollment.id)}
                          onChange={() => {}}
                          className="h-4 w-4 text-teal-600"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500 italic">
                You have no active services with this client to end.
              </p>
            )}
            
            <div className="flex justify-end space-x-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowServiceDisenrollmentDialog(false);
                  setDischargeCheckInfo(null);
                  setSelectedServicesToDisenroll([]);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-amber-600 hover:bg-amber-700"
                disabled={selectedServicesToDisenroll.length === 0}
                onClick={async () => {
                  // Initiate disenrollment for each selected service
                  try {
                    for (const enrollmentId of selectedServicesToDisenroll) {
                      await axios.post(
                        `${API}/service-enrollments/${enrollmentId}/initiate-disenrollment?reason=client_discharged&reason_notes=${encodeURIComponent('Initiated from client discharge flow')}`
                      );
                    }
                    toast.success(`Disenrollment initiated for ${selectedServicesToDisenroll.length} service(s). Please complete the discharge form(s).`);
                    setShowServiceDisenrollmentDialog(false);
                    setDischargeCheckInfo(null);
                    setSelectedServicesToDisenroll([]);
                    
                    // Refresh client enrollments via the centralized
                    // helper so the chart header and the Edit Client
                    // dialog stay in sync (both read `clientEnrollments`).
                    if (selectedClient?.id) {
                      invalidateEnrollmentCache(selectedClient.id);
                      await reloadClientEnrollments(selectedClient.id);
                    }
                  } catch (error) {
                    // Handle error - detail can be string or object (validation error)
                    const errorDetail = error.response?.data?.detail;
                    const errorMessage = typeof errorDetail === 'string' 
                      ? errorDetail 
                      : (errorDetail?.msg || 'Failed to initiate disenrollment');
                    toast.error(errorMessage);
                  }
                }}
              >
                End Selected Service(s)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Future Appointments Confirmation Dialog */}
      <Dialog open={showCancelFutureApptsDialog} onOpenChange={setShowCancelFutureApptsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-amber-500" />
              Future Appointments Found
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {cancelFutureApptsData && (
              <>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>{cancelFutureApptsData.clientName}</strong> has{' '}
                    <strong>{cancelFutureApptsData.futureApptsCount}</strong> future appointment
                    {cancelFutureApptsData.futureApptsCount > 1 ? 's' : ''} scheduled.
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  Would you like to cancel all future appointments for this client?
                </p>
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowCancelFutureApptsDialog(false);
                      setCancelFutureApptsData(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      // Proceed without cancelling appointments
                      try {
                        if (cancelFutureApptsData.status === 'archived') {
                          await handleArchiveClient(cancelFutureApptsData.clientId, cancelFutureApptsData.reason, false);
                        } else {
                          await axios.patch(`${API}/clients/${cancelFutureApptsData.clientId}/status`, {
                            status: cancelFutureApptsData.status,
                            discharge_reason: cancelFutureApptsData.reason,
                            cancel_future_appointments: false
                          });
                          toast.success(`Client moved to ${cancelFutureApptsData.status}`);
                          refreshClients({ syncOpenChart: true }); refreshStats();
                        }
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to update client status");
                      }
                      setShowCancelFutureApptsDialog(false);
                      setCancelFutureApptsData(null);
                    }}
                  >
                    Keep Appointments
                  </Button>
                  <Button
                    type="button"
                    className="bg-red-600 hover:bg-red-700"
                    onClick={async () => {
                      // Cancel all future appointments
                      try {
                        if (cancelFutureApptsData.status === 'archived') {
                          await handleArchiveClient(cancelFutureApptsData.clientId, cancelFutureApptsData.reason, true);
                        } else {
                          const response = await axios.patch(`${API}/clients/${cancelFutureApptsData.clientId}/status`, {
                            status: cancelFutureApptsData.status,
                            discharge_reason: cancelFutureApptsData.reason,
                            cancel_future_appointments: true
                          });
                          let message = `Client moved to ${cancelFutureApptsData.status}`;
                          if (response.data.appointments_cancelled > 0) {
                            message += ` (${response.data.appointments_cancelled} appointments cancelled)`;
                          }
                          toast.success(message);
                          refreshClients({ syncOpenChart: true }); refreshStats();
                        }
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to update client status");
                      }
                      setShowCancelFutureApptsDialog(false);
                      setCancelFutureApptsData(null);
                    }}
                  >
                    Cancel Appointments
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Billing Review Dialog */}
      <Dialog open={showBillingReviewDialog} onOpenChange={setShowBillingReviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Billing Review</DialogTitle>
            <DialogDescription>
              Review the client's outstanding balance before they can be archived.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Please ensure all billing has been processed and any outstanding balance has been collected or written off before completing this review.
              </p>
            </div>
            <div>
              <Label htmlFor="billing-notes">Notes (Optional)</Label>
              <DebouncedTextarea
                id="billing-notes"
                className="w-full min-h-[80px] p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                value={billingReviewNotes}
                onChange={(e) => setBillingReviewNotes(e.target.value)}
                placeholder="e.g., Final payment received, balance written off as charity care..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                onClick={() => handleCompleteBillingReview(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Check className="h-4 w-4 mr-2" />
                Balance Cleared - Ready for Archive
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleCompleteBillingReview(false)}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                Outstanding Balance - Not Ready for Archive
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowBillingReviewDialog(false);
                  setBillingReviewClientId(null);
                  setBillingReviewNotes('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Location Dialog */}
      <Dialog open={showAddLocation} onOpenChange={setShowAddLocation}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Location</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLocation} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location-name">Location Name</Label>
                <DebouncedInput 
                  id="location-name" 
                  placeholder="e.g., Main Office" 
                  value={locationFormData.name}
                  onChange={(e) => setLocationFormData({...locationFormData, name: e.target.value})}
                  required 
                />
              </div>
              <div>
                <Label htmlFor="location-type">Location Type</Label>
                <Select 
                  value={locationFormData.type}
                  onValueChange={(value) => setLocationFormData({...locationFormData, type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="main">Main Office</SelectItem>
                    <SelectItem value="satellite">Satellite Office</SelectItem>
                    <SelectItem value="remote">Remote/Telehealth</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="location-address1">Address Line 1</Label>
              <DebouncedInput 
                id="location-address1" 
                placeholder="Street address" 
                value={locationFormData.address_line1}
                onChange={(e) => setLocationFormData({...locationFormData, address_line1: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="location-address2">Address Line 2 (Optional)</Label>
              <DebouncedInput 
                id="location-address2" 
                placeholder="Suite, Unit, Building, Floor" 
                value={locationFormData.address_line2}
                onChange={(e) => setLocationFormData({...locationFormData, address_line2: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="location-city">City</Label>
                <DebouncedInput 
                  id="location-city" 
                  placeholder="City" 
                  value={locationFormData.city}
                  onChange={(e) => setLocationFormData({...locationFormData, city: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="location-state">State</Label>
                <DebouncedInput 
                  id="location-state" 
                  placeholder="CA" 
                  maxLength="2"
                  value={locationFormData.state}
                  onChange={(e) => setLocationFormData({...locationFormData, state: e.target.value.toUpperCase()})}
                />
              </div>
              <div>
                <Label htmlFor="location-zip">ZIP Code</Label>
                <DebouncedInput 
                  id="location-zip" 
                  placeholder="12345 or 12345-6789" 
                  value={locationFormData.zip_code}
                  onChange={(e) => setLocationFormData({...locationFormData, zip_code: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="location-phone">Phone</Label>
                <DebouncedInput 
                  id="location-phone" 
                  placeholder="(555) 123-4567" 
                  value={locationFormData.phone}
                  onChange={(e) => setLocationFormData({...locationFormData, phone: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="location-fax">Fax</Label>
                <DebouncedInput 
                  id="location-fax" 
                  placeholder="(555) 123-4568" 
                  value={locationFormData.fax}
                  onChange={(e) => setLocationFormData({...locationFormData, fax: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="location-license">License Number</Label>
                <DebouncedInput 
                  id="location-license" 
                  placeholder="License #" 
                  value={locationFormData.license}
                  onChange={(e) => setLocationFormData({...locationFormData, license: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location-license-expiry">License Expiry Date</Label>
                <Input 
                  id="location-license-expiry" 
                  type="date"
                  value={locationFormData.license_expiry}
                  onChange={(e) => setLocationFormData({...locationFormData, license_expiry: e.target.value})}
                />
              </div>
            </div>
            
            {/* Read-only: Display providers assigned to this location */}
            {editingLocation && locationProviders.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
                <Label className="text-sm font-medium mb-2 block">Providers at This Location</Label>
                <p className="text-xs text-gray-600 mb-3">
                  These providers have this location in their assigned locations. 
                  To modify, go to User Management and edit the provider's assigned locations.
                </p>
                <div className="space-y-1">
                  {locationProviders.map(provider => (
                    <div key={provider.id} className="flex items-center bg-white p-2 rounded border text-sm">
                      <span className="font-medium">{provider.full_name}{provider.credentials ? `, ${provider.credentials}` : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Create Treatment Plan from PANs - shown for Comprehensive Assessment when PANs are filled */}
            {selectedTemplate?.name?.includes('Comprehensive Assessment') && (
              (() => {
                // Check if any PANs are filled
                const pans = [
                  newNoteFormData['pan_1'],
                  newNoteFormData['pan_2'],
                  newNoteFormData['pan_3'],
                  newNoteFormData['pan_4'],
                  newNoteFormData['pan_5']
                ].filter(Boolean);
                
                if (pans.length > 0) {
                  return (
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 mt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">📋</span>
                          <div>
                            <h4 className="font-medium text-purple-900">Ready to Create Treatment Plan</h4>
                            <p className="text-sm text-purple-700">{pans.length} Prioritized Assessed Need{pans.length > 1 ? 's' : ''} identified</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={async () => {
                            // First save the assessment as draft
                            const client = allClientsForLookup.find(c => c.id === selectedNoteClients[0]);
                            let noteContent = {
                              template_id: selectedTemplate.id,
                              template_name: selectedTemplate.name
                            };
                            
                            if (selectedTemplate.form_fields) {
                              noteContent.fields = selectedTemplate.form_fields.map(field => ({
                                ...field,
                                value: newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : (field.value !== undefined ? field.value : '')
                              }));
                            }
                            
                            // Save assessment as draft - with manual service details
                            const manualSvcAssess = (!selectedAppointment || selectedAppointment === 'none') ? {
                              dos: newNoteFormData._manual_dos || getTodayLocalDate(),
                              start_time: newNoteFormData._manual_start_time || null,
                              end_time: newNoteFormData._manual_end_time || null,
                              units: newNoteFormData._manual_units || null,
                              cpt: newNoteFormData._manual_cpt || null,
                              cpt_code_id: newNoteFormData._manual_cpt_id || null,
                              pos_code: newNoteFormData._manual_pos_code || null,
                              modality: newNoteFormData._manual_modality || null
                            } : null;
                            setNoteCreationError(null);
                            const createAssessmentResponse = await handleCreateClinicalNote(
                              noteContent,
                              selectedTemplate.id,
                              selectedNoteProvider,
                              selectedNoteClients,
                              isGroupNote,
                              selectedAppointment,
                              'draft',
                              manualSvcAssess
                            );
                            
                            // CRITICAL: Check if assessment was actually created
                            if (!createAssessmentResponse) {
                              setNoteCreationError('Assessment save failed. Your work has NOT been saved. Please try again or copy your content.');
                              return; // Keep dialog open!
                            }
                            
                            // Store PANs to transfer to treatment plan
                            const pansToTransfer = [
                              { priority: 1, text: newNoteFormData['pan_1'] },
                              { priority: 2, text: newNoteFormData['pan_2'] },
                              { priority: 3, text: newNoteFormData['pan_3'] },
                              { priority: 4, text: newNoteFormData['pan_4'] },
                              { priority: 5, text: newNoteFormData['pan_5'] }
                            ].filter(p => p.text);
                            
                            // Store PANs in session storage for the treatment plan tab to pick up
                            sessionStorage.setItem('pansFromAssessment', JSON.stringify({
                              clientId: selectedNoteClients[0],
                              clientName: client?.full_name || 'Client',
                              pans: pansToTransfer,
                              sourceAssessment: 'Comprehensive Assessment'
                            }));
                            
                            // Close the create note dialog
                            setShowAddNote(false);
                            setSelectedTemplate(null);
                            setSelectedNoteProvider(null);
                            setSelectedNoteClients([]);
                            setNewNoteFormData({});
                            
                            // Set the client and switch to treatment plan tab
                            if (client) {
                              setSelectedClient(client);
                              setClientDetailTab('treatment-plans');
                              setShowClientDetail(true);
                              
                              // Show toast to guide user
                              toast.success(`Assessment saved! Opening Treatment Plan tab with ${pansToTransfer.length} PANs ready to import.`);
                            }
                          }}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Create Treatment Plan from PANs
                        </Button>
                      </div>
                    </div>
                  );
                }
                return null;
              })()
            )}
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowAddLocation(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                Add Location
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={showCreateTask} onOpenChange={(open) => {
        setShowCreateTask(open);
        if (open && selectedClient?.id && !taskFormData.client_id) {
          setTaskFormData(prev => ({...prev, client_id: selectedClient.id}));
          setTaskClientSearch(selectedClient.full_name || `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim());
        }
        if (!open) {
          setTaskClientSearch('');
          setTaskClientResults([]);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <DebouncedInput
                value={taskFormData.title}
                onChange={(e) => setTaskFormData({...taskFormData, title: e.target.value})}
                placeholder="Enter task title"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <DebouncedTextarea
                value={taskFormData.description}
                onChange={(e) => setTaskFormData({...taskFormData, description: e.target.value})}
                placeholder="Add details about this task..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select 
                  value={taskFormData.category} 
                  onValueChange={(value) => setTaskFormData({...taskFormData, category: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduling">Scheduling</SelectItem>
                    <SelectItem value="client_communication">Client Communication</SelectItem>
                    <SelectItem value="external_coordination">External Coordination</SelectItem>
                    <SelectItem value="administrative">Administrative</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select 
                  value={taskFormData.priority} 
                  onValueChange={(value) => setTaskFormData({...taskFormData, priority: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>Urgent</span></SelectItem>
                    <SelectItem value="normal"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-teal-500"></span>Normal</span></SelectItem>
                    <SelectItem value="low"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-gray-400"></span>Low</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To *</Label>
                <Select 
                  value={taskFormData.assigned_to} 
                  onValueChange={(value) => setTaskFormData({...taskFormData, assigned_to: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.filter(u => !u.is_archived).map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name} ({formatRoleName(u.role)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Related Client (Optional)</Label>
                <div className="relative">
                  <DebouncedInput
                    placeholder="Type to search clients..."
                    value={taskClientSearch}
                    onChange={async (e) => {
                      const val = e.target.value;
                      setTaskClientSearch(val);
                      if (val.length < 2) {
                        setTaskClientResults([]);
                        if (!val) setTaskFormData({...taskFormData, client_id: ''});
                        return;
                      }
                      setTaskClientSearching(true);
                      try {
                        const response = await axios.get(`${API}/clients?search=${encodeURIComponent(val)}&limit=10`);
                        const results = Array.isArray(response.data) ? response.data : (response.data?.clients || response.data?.results || []);
                        setTaskClientResults(results);
                      } catch (err) {
                        console.error('Client search failed:', err);
                      }
                      setTaskClientSearching(false);
                    }}
                    data-testid="task-client-search"
                  />
                  {taskFormData.client_id && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setTaskFormData({...taskFormData, client_id: ''});
                        setTaskClientSearch('');
                        setTaskClientResults([]);
                      }}
                      data-testid="task-client-clear"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {taskClientSearch.length >= 2 && !taskFormData.client_id && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {taskClientSearching ? (
                        <div className="p-3 text-sm text-gray-500">Searching...</div>
                      ) : taskClientResults.length > 0 ? (
                        taskClientResults.map(c => (
                          <div
                            key={c.id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                            onClick={() => {
                              setTaskFormData({...taskFormData, client_id: c.id});
                              setTaskClientSearch(c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim());
                              setTaskClientResults([]);
                            }}
                            data-testid={`task-client-option-${c.id}`}
                          >
                            {c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim()}
                            {c.status !== 'active' ? <span className="ml-2 text-gray-400">({formatClientStatus(c.status)})</span> : ''}
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-sm text-gray-500">No clients found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div>
              <Label>Due Date (Optional)</Label>
              <Input
                type="date"
                value={taskFormData.due_date}
                onChange={(e) => setTaskFormData({...taskFormData, due_date: e.target.value})}
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCreateTask(false)}>Cancel</Button>
              <Button 
                onClick={async () => {
                  if (!taskFormData.title || !taskFormData.assigned_to) {
                    toast.error('Please fill in required fields');
                    return;
                  }
                  
                  try {
                    const response = await axios.post(`${API}/tasks`, taskFormData);
                    const newTaskId = response.data.id;
                    
                    // Build optimistic task object
                    const assignedUser = users.find(u => u.id === taskFormData.assigned_to);
                    const relatedClient = clients.find(c => c.id === taskFormData.client_id);
                    const now = new Date().toISOString();
                    
                    const optimisticTask = {
                      id: newTaskId,
                      title: taskFormData.title,
                      description: taskFormData.description,
                      category: taskFormData.category,
                      priority: taskFormData.priority,
                      status: 'pending',
                      assigned_to: taskFormData.assigned_to,
                      assigned_to_name: assignedUser?.full_name,
                      assigned_by: user.id,
                      assigned_by_name: user.full_name,
                      client_id: taskFormData.client_id || null,
                      client_name: relatedClient?.full_name || null,
                      due_date: taskFormData.due_date || null,
                      created_at: now,
                      updated_at: now,
                      comments: [],
                      comment_count: 0,
                    };
                    
                    // Optimistically add to the appropriate list(s)
                    if (taskFormData.assigned_to === user.id) {
                      // Self-assigned: add to "assigned to me" list only
                      setTasks(prev => [optimisticTask, ...(prev || [])]);
                    } else {
                      // Assigned to someone else: add to "I assigned" list
                      setTasksAssignedByMe(prev => [optimisticTask, ...(prev || [])]);
                    }
                    
                    toast.success('Task created successfully');
                    setShowCreateTask(false);
                    setTaskFormData({
                      title: '',
                      description: '',
                      category: 'other',
                      priority: 'normal',
                      assigned_to: '',
                      client_id: '',
                      due_date: ''
                    });
                    setTaskClientSearch('');
                    setTaskClientResults([]);
                  } catch (error) {
                    toast.error('Failed to create task');
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Create Task
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog */}
      <TaskDetailsDialog ctx={{
        API,
        clients,
        emailVerifyMode,
        setEmailVerifyMode,
        emailVerifyNewEmail,
        setEmailVerifyNewEmail,
        isEditingTask,
        setIsEditingTask,
        refreshAlerts,
        refreshTodos,
        selectedTask,
        setSelectedTask,
        setShowTaskDetail,
        showTaskDetail,
        setCompletionDialogNote,
        setCompletionDialogTask,
        taskComment,
        setTaskComment,
        taskEditData,
        setTaskEditData,
        setTasks,
        setTasksAssignedByMe,
        updateTaskStatusOptimistic,
        user,
      }} />

      {/* ── Task Completion Confirmation Dialog (Feb 2026) ───────────
          When marking a task complete, ask the assignee what they
          did so the assigner gets a meaningful confirmation instead
          of a bare "X completed: Y".  Optional but encouraged. */}
      <TaskCompletionNoteDialog ctx={{
        setShowTaskDetail,
        completionDialogTask,
        setCompletionDialogTask,
        completionDialogNote,
        setCompletionDialogNote,
        completionDialogSubmitting,
        setCompletionDialogSubmitting,
        updateTaskStatusOptimistic,
      }} />

      {/* Change Request Detail Dialog */}
      <Dialog open={showChangeRequestDetail} onOpenChange={setShowChangeRequestDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="change-request-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Badge className={`${
                selectedChangeRequest?.status === 'pending' ? 'bg-blue-500' : 
                selectedChangeRequest?.status === 'approved' ? 'bg-green-500' : 
                selectedChangeRequest?.status === 'denied' ? 'bg-red-500' : 'bg-gray-500'
              } text-white`}>
                {selectedChangeRequest?.status?.toUpperCase() || 'PENDING'}
              </Badge>
              Change Request
            </DialogTitle>
            <DialogDescription>
              Review and take action on this change request
            </DialogDescription>
          </DialogHeader>
          
          {selectedChangeRequest && (
            <div className="space-y-4">
              {/* Client & Requester Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Client</p>
                  <p className="font-semibold">{selectedChangeRequest.client_name}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Requested By</p>
                  <p className="font-semibold">{selectedChangeRequest.requested_by_name}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(selectedChangeRequest.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              
              {/* Category */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-blue-600 mb-1">Category</p>
                <p className="font-semibold text-blue-900 capitalize">
                  {selectedChangeRequest.field_category?.replace(/_/g, ' ') || 'General'}
                </p>
              </div>
              
              {/* Requested Changes */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-gray-700">Requested Changes</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => {
                      const textToCopy = selectedChangeRequest.proposed_value || 
                        selectedChangeRequest.reason || 
                        (selectedChangeRequest.changes?.map(c => `${c.field_name}: ${c.new_value || c.proposed_value}`).join('\n')) ||
                        'No details';
                      navigator.clipboard.writeText(textToCopy);
                      toast.success('Copied to clipboard');
                    }}
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </Button>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  {selectedChangeRequest.proposed_value ? (
                    <p className="text-sm whitespace-pre-wrap">{selectedChangeRequest.proposed_value}</p>
                  ) : selectedChangeRequest.changes && selectedChangeRequest.changes.length > 0 ? (
                    <div className="space-y-2">
                      {selectedChangeRequest.changes.map((change, idx) => (
                        <div key={idx} className="border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                          <p className="text-xs text-gray-500 capitalize">{change.field_name?.replace(/_/g, ' ')}</p>
                          <div className="flex items-center gap-2 text-sm">
                            {change.old_value && (
                              <>
                                <span className="text-red-600 line-through">{change.old_value}</span>
                                <span className="text-gray-400">→</span>
                              </>
                            )}
                            <span className="text-green-600 font-medium">{change.new_value || change.proposed_value || '(not specified)'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No details provided</p>
                  )}
                </div>
              </div>
              
              {/* Reason for request */}
              {selectedChangeRequest.reason && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Reason</p>
                  <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                    <p className="text-sm whitespace-pre-wrap text-amber-900">{selectedChangeRequest.reason}</p>
                  </div>
                </div>
              )}
              
              {/* Review Info (if reviewed) */}
              {selectedChangeRequest.reviewed_by_name && (
                <div className={`p-3 rounded-lg ${
                  selectedChangeRequest.status === 'approved' ? 'bg-green-50' : 'bg-red-50'
                }`}>
                  <p className="text-xs text-gray-500 mb-1">
                    {selectedChangeRequest.status === 'approved' ? 'Approved' : 'Denied'} By
                  </p>
                  <p className="font-semibold">{selectedChangeRequest.reviewed_by_name}</p>
                  {selectedChangeRequest.reviewed_at && (
                    <p className="text-xs text-gray-500">
                      {new Date(selectedChangeRequest.reviewed_at).toLocaleString()}
                    </p>
                  )}
                  {selectedChangeRequest.review_reason && (
                    <p className="text-sm mt-2 italic">"{selectedChangeRequest.review_reason}"</p>
                  )}
                </div>
              )}
              
              {/* Action Buttons (only for pending requests and authorized users) */}
              {selectedChangeRequest.status === 'pending' && 
               (isSuperAdmin(user) || user?.is_biller || user?.is_scheduler || 
                ['office', 'office staff', 'office_staff'].includes(user?.role?.toLowerCase())) && (
                <div className="space-y-3 pt-4 border-t">
                  {/* Edit Client Button - Opens Full Intake Wizard */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={async () => {
                      if (selectedChangeRequest.client_id) {
                        // Close this dialog first
                        setShowChangeRequestDetail(false);
                        // Phase 3e.2 — delegate to V2 provider (its open() doesn't navigate).
                        intakeWizard.open(selectedChangeRequest.client_id);
                        toast.info(`Make your changes, save, then find the change request in your To-Do list to approve/deny.`);
                      } else {
                        toast.error('Client not found');
                      }
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Open Client to Edit
                  </Button>
                  
                  {/* Approve/Deny Buttons */}
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/change-requests/${selectedChangeRequest.id}/approve`);
                          toast.success('Change request approved');
                          setShowChangeRequestDetail(false);
                          refreshTodos(); refreshAlerts();
                        } catch (error) {
                          console.error('Failed to approve:', error);
                          toast.error('Failed to approve change request');
                        }
                      }}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
                      onClick={async () => {
                        const reason = window.prompt('Please provide a reason for denying this request:');
                        if (reason) {
                          try {
                            await axios.put(`${API}/change-requests/${selectedChangeRequest.id}/deny`, {
                              reason: reason
                            });
                            toast.success('Change request denied');
                            setShowChangeRequestDetail(false);
                            refreshTodos(); refreshAlerts();
                          } catch (error) {
                            console.error('Failed to deny:', error);
                            toast.error('Failed to deny change request');
                          }
                        }
                      }}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Deny
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Dismiss Button (for already reviewed requests) */}
              {selectedChangeRequest.status !== 'pending' && (
                <div className="pt-4 border-t">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/change-requests/${selectedChangeRequest.id}/dismiss`);
                        toast.success('Dismissed from your task list');
                        setShowChangeRequestDetail(false);
                        refreshTodos(); refreshAlerts();
                      } catch (error) {
                        console.error('Failed to dismiss:', error);
                        toast.error('Failed to dismiss');
                      }
                    }}
                  >
                    Dismiss from Task List
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Global Edit Client Dialog - accessible from any tab */}
      <Dialog open={showEditClientForm} onOpenChange={setShowEditClientForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>Edit Client - {editingClient?.full_name}</DialogTitle>
              {/* Quick-jump to Manage Insurance — saves scrolling for the
                  most-used action in the edit-client flow. */}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowEditClientInsurance(true)}
                className="shrink-0"
                data-testid="edit-client-manage-insurance-shortcut"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Manage Insurance
              </Button>
            </div>
          </DialogHeader>
          
          {/* Permission notice for non-admin users */}
          {!isSuperAdmin(user) && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
              <p className="text-blue-800">
                <strong>Note:</strong> Some fields may be disabled based on your permissions.
              </p>
            </div>
          )}
          
          <form onSubmit={handleUpdateClient} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="global-edit-client-name">Full Name</Label>
                <DebouncedInput
                  id="global-edit-client-name"
                  value={clientFormData.full_name || ''}
                  onChange={(e) => setClientFormData({...clientFormData, full_name: e.target.value})}
                  disabled={editingClient && !canEditFieldCategory('demographics')}
                  required
                />
              </div>
              <div>
                <Label htmlFor="global-edit-client-email">Email</Label>
                <DebouncedInput
                  id="global-edit-client-email"
                  type="email"
                  value={clientFormData.email || ''}
                  onChange={(e) => setClientFormData({...clientFormData, email: e.target.value})}
                  disabled={editingClient && !canEditFieldCategory('demographics')}
                />
              </div>
              <div>
                <Label htmlFor="global-edit-client-phone">Phone</Label>
                <DebouncedInput
                  id="global-edit-client-phone"
                  value={clientFormData.phone || ''}
                  onChange={(e) => setClientFormData({...clientFormData, phone: e.target.value})}
                  disabled={editingClient && !canEditFieldCategory('demographics')}
                />
              </div>
              <div>
                <Label htmlFor="global-edit-client-dob">Date of Birth</Label>
                <Input
                  id="global-edit-client-dob"
                  type="date"
                  value={clientFormData.date_of_birth || ''}
                  onChange={(e) => setClientFormData({...clientFormData, date_of_birth: e.target.value})}
                  disabled={editingClient && !canEditFieldCategory('demographics')}
                />
              </div>
            </div>
            
            {/* Communication Preferences */}
            {/* Preferred Pharmacy Section */}
            <div className="space-y-3 border-t pt-4">
              <Label className="text-sm font-semibold">Preferred Pharmacy</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="pharmacy-name" className="text-xs text-gray-500">Pharmacy Name</Label>
                  <DebouncedInput
                    id="pharmacy-name"
                    value={clientFormData.pharmacy_name || ''}
                    onChange={(e) => setClientFormData({...clientFormData, pharmacy_name: e.target.value})}
                    placeholder="CVS, Walgreens, etc."
                  />
                </div>
                <div>
                  <Label htmlFor="pharmacy-phone" className="text-xs text-gray-500">Phone</Label>
                  <DebouncedInput
                    id="pharmacy-phone"
                    value={clientFormData.pharmacy_phone || ''}
                    onChange={(e) => setClientFormData({...clientFormData, pharmacy_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label htmlFor="pharmacy-fax" className="text-xs text-gray-500">Fax</Label>
                  <DebouncedInput
                    id="pharmacy-fax"
                    value={clientFormData.pharmacy_fax || ''}
                    onChange={(e) => setClientFormData({...clientFormData, pharmacy_fax: e.target.value})}
                    placeholder="(555) 123-4568"
                  />
                </div>
                <div>
                  <Label htmlFor="pharmacy-address" className="text-xs text-gray-500">Address</Label>
                  <DebouncedInput
                    id="pharmacy-address"
                    value={clientFormData.pharmacy_address || ''}
                    onChange={(e) => setClientFormData({...clientFormData, pharmacy_address: e.target.value})}
                    placeholder="123 Main St, City, ST"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Communication Preferences</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="global-edit-email-consent"
                  checked={clientFormData.email_consent}
                  onCheckedChange={(checked) => setClientFormData({...clientFormData, email_consent: checked})}
                />
                <Label htmlFor="global-edit-email-consent" className="text-sm font-normal cursor-pointer">
                  Email reminders and notifications
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="global-edit-sms-consent"
                  checked={clientFormData.sms_consent}
                  onCheckedChange={(checked) => setClientFormData({...clientFormData, sms_consent: checked})}
                />
                <Label htmlFor="global-edit-sms-consent" className="text-sm font-normal cursor-pointer">
                  SMS/Text message reminders
                </Label>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => {
                  setChangeHistoryEntity({
                    type: 'client',
                    id: editingClient?.id,
                    name: editingClient?.full_name || 'Client'
                  });
                  setShowChangeHistory(true);
                }}
              >
                <History className="h-4 w-4 mr-1" />
                View Change History
              </Button>
              <div className="flex space-x-2">
                <Button type="button" variant="outline" onClick={() => {
                  setShowEditClientForm(false);
                  setEditingClient(null);
                }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                  Update Client
                </Button>
              </div>
            </div>
          </form>

          {/* Feb 27, 2026 — External Reviewer grants live on the
              client chart, not the user record (option `a` from the
              design discussion).  Surface the grant UI here at the
              bottom of the Edit Client dialog where admins already
              manage chart-level metadata. */}
          {editingClient?.id && (
            <div className="mt-4 pt-4 border-t">
              <ExternalReviewersPanel
                clientId={editingClient.id}
                users={users}
                canManage={
                  isSuperAdmin(user)
                  || user?.is_supervisor
                  || user?.is_billing_manager
                }
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Note from Template Dialog - Lazy mounted to avoid ~4000 lines of JSX evaluation when closed */}
      {showAddNote && (
      <Dialog modal={false} allowOverlayClose={user?.is_billing_manager || user?.role === 'super admin' || user?.role === 'office'} open={showAddNote} onOpenChange={(open) => {
        if (!open) {
          // Check if there are unsaved changes before closing
          const hasUnsavedChanges = isNoteFormDirtyRef.current;
          const hasFormContent = newNoteFormData && Object.keys(newNoteFormData).length > 0;
          const hasDraft = !!autosaveNoteId;
          
          // If user has typed something (dirty) OR has an unsaved draft with content
          if ((hasUnsavedChanges || hasDraft) && hasFormContent && selectedTemplate) {
            // Show the close warning modal
            // Use setTimeout to break out of Radix Dialog's event handling
            setTimeout(() => {
              setShowCloseNoteWarning(true);
            }, 0);
            return; // Don't close yet - wait for user to choose an option
          }
          
          // Save backup before clearing state — captures content even if never saved
          if (hasFormContent && selectedTemplate) {
            saveUnsavedDraftBackup();
          }
          
          // Close the dialog
          setShowAddNote(false);
          setShowCreateNoteRefPanel(false);
          setTimeout(() => {
            setNoteDialogHeaderCollapsed(false);
            setNoteClientLocked(false);
            setCurriculumSessionData(null);
            setSelectedNoteGroupId(null);
            isNoteFormDirtyRef.current = false;
            initialFormDataRef.current = null;
            setNewNoteFormData({});
            setSelectedTemplate(null);
            setSelectedNoteClients([]);
            setSelectedNoteProvider(null);
            setAutosaveNoteId(null);
            setLastAutosave(null);
            const url = new URL(window.location.href);
            if (url.searchParams.has('draftNote')) {
              url.searchParams.delete('draftNote');
              window.history.replaceState({}, '', url.toString());
            }
          }, 0);
        } else {
          setShowAddNote(true);
          // Reset linked goals when opening to ensure new notes start fresh
          setLinkedGoals([]);
          
          // Auto-select logged-in user as the provider if they're a provider
          // This enables lazy auto-save to work immediately
          // Check is_provider flag OR if user exists in providers list (regardless of role)
          if (user?.id && !selectedNoteProvider) {
            const isUserProvider = user?.is_provider || providers?.some(p => p.id === user.id);
            if (isUserProvider) {
              setSelectedNoteProvider(user.id);
              void 0 && console.log('[AddNote] Auto-selected current user as provider:', user.id);
            } else if (providers?.length === 1) {
              // If there's only one provider, auto-select them
              setSelectedNoteProvider(providers[0].id);
              void 0 && console.log('[AddNote] Auto-selected only available provider:', providers[0].id);
            }
          }
          
          // Auto-select logged-in user's default template if they have one
          if (user?.default_template_id && formTemplates?.length > 0) {
            const defaultTemplate = formTemplates.find(t => t.id === user.default_template_id);
            if (defaultTemplate && defaultTemplate.is_active !== false) {
              // Fetch fresh template data
              axios.get(`${API}/form-templates/${defaultTemplate.id}`)
                .then(response => {
                  const freshTemplate = response.data;
                  setSelectedTemplate(freshTemplate);
                  setIsGroupNote(freshTemplate.is_group_template || false);
                  
                  // Initialize form data with defaults
                  const initialFormData = {};
                  if (freshTemplate.form_fields) {
                    freshTemplate.form_fields.forEach(field => {
                      // Initialize multi-select radio fields as empty arrays
                      if (field.type === 'radio' && field.allowMultiple === true) {
                        initialFormData[field.id] = field.default_value ? 
                          (Array.isArray(field.default_value) ? field.default_value : [field.default_value]) : [];
                      } else if (field.type === 'checkbox_group' || field.type === 'multiselect') {
                        // These are always arrays
                        initialFormData[field.id] = field.default_value ? 
                          (Array.isArray(field.default_value) ? field.default_value : [field.default_value]) : [];
                      } else if (field.default_value !== undefined && field.default_value !== null && field.default_value !== '') {
                        if (field.type === 'date' && field.default_value === 'today') {
                          initialFormData[field.id] = new Date().toISOString().split('T')[0];
                        } else if (field.type === 'date' && field.default_value === 'custom' && field.custom_default_date) {
                          initialFormData[field.id] = field.custom_default_date;
                        } else if (field.type === 'time' && field.default_value === 'now') {
                          initialFormData[field.id] = new Date().toTimeString().slice(0, 5);
                        } else if (field.type === 'time' && field.default_value === 'custom' && field.custom_default_time) {
                          initialFormData[field.id] = field.custom_default_time;
                        } else {
                          initialFormData[field.id] = field.default_value;
                        }
                      }
                    });
                  }
                  setNewNoteFormData(initialFormData);
                  // Reset dirty tracking - initialFormDataRef will be set by the useEffect watching selectedTemplate
                  setTimeout(() => {
                    isNoteFormDirtyRef.current = false;
                    initialFormDataRef.current = JSON.stringify(initialFormData);
                  }, 0);
                })
                .catch(err => {
                  console.error('Error loading default template:', err);
                  setSelectedTemplate(defaultTemplate); // Fall back to cached version
                });
            }
          }
        }
      }}>
        <DialogContent 
          className={`max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden`}
          style={showCreateNoteRefPanel 
            ? { width: '53vw', left: '27.5vw', transform: 'translate(-50%, -50%)' } 
            : { width: NOTE_SIZE_PRESETS[noteDialogSize]?.width || '95vw' }}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
          {/* Close Warning Modal - Simple div-based modal that works */}
          <CloseNoteWarningModal ctx={{
            autosaveNoteId, handleCloseNoteDiscard, handleCloseNoteSaveAndClose,
            setShowCloseNoteWarning, showCloseNoteWarning,
          }} />
          <DialogHeader className="pb-2 flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg">Create Note from Template</DialogTitle>
              <div className="flex items-center gap-2">
                {/* Note Size Presets */}
                {!showCreateNoteRefPanel && (
                  <div className="flex items-center bg-gray-100 rounded-md p-0.5" data-testid="create-note-size-presets">
                    {Object.entries(NOTE_SIZE_PRESETS).map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => updateNoteDialogSize(key)}
                        className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                          noteDialogSize === key 
                            ? 'bg-white text-teal-700 shadow-sm' 
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                        title={`${preset.label} width`}
                        data-testid={`create-note-size-${key}`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                )}
                {/* Reference Note Toggle */}
              <Button 
                size="sm" 
                variant={showCreateNoteRefPanel ? "default" : "outline"}
                className={`h-8 px-3 text-xs ${showCreateNoteRefPanel ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}
                onClick={() => setShowCreateNoteRefPanel(!showCreateNoteRefPanel)}
                data-testid="create-note-reference-toggle"
                title="View another note side-by-side for reference"
              >
                <Columns2 className="h-3.5 w-3.5 mr-1" />
                Reference
              </Button>
              </div>
              {/* Autosave Status Indicator - Shows all states including errors */}
              {(() => {
                const status = noteEditingAutosave?.saveStatus;
                const lastSaved = noteEditingAutosave?.lastSavedAt;
                const forceSave = noteEditingAutosave?.forceSave;
                
                // Error state - RED with alert
                if (status === 'error') {
                  return (
                    <div className="flex items-center gap-2 text-sm">
                      <div className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-md border border-red-300">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="font-medium">Save Failed!</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => forceSave?.()}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Retry
                      </Button>
                    </div>
                  );
                }
                
                // Offline cached state - AMBER with cloud-off
                if (status === 'offline_cached') {
                  return (
                    <div className="flex items-center gap-2 text-sm">
                      <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-md border border-amber-300">
                        <WifiOff className="h-4 w-4" />
                        <span className="font-medium">Offline - Not synced</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 text-xs border-amber-300 text-amber-600 hover:bg-amber-50"
                        onClick={() => forceSave?.()}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Retry
                      </Button>
                    </div>
                  );
                }
                
                // Session expired
                if (status === 'session_expired') {
                  return (
                    <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-md border border-orange-300 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="font-medium">Session Expired - Please login</span>
                    </div>
                  );
                }
                
                // Conflict state
                if (status === 'conflict') {
                  return (
                    <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-md border border-purple-300 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="font-medium">Version Conflict - Reload note</span>
                    </div>
                  );
                }
                
                // Saving state
                if (status === 'saving') {
                  return (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-teal-500 rounded-full"></div>
                      <span>Saving...</span>
                    </div>
                  );
                }
                
                // Saved state
                if (status === 'saved' && lastSaved) {
                  return (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>Saved {new Date(lastSaved).toLocaleTimeString()}</span>
                    </div>
                  );
                }
                
                // Legacy fallback for backwards compatibility
                if (isAutosaving) {
                  return (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-teal-500 rounded-full"></div>
                      <span>Saving...</span>
                    </div>
                  );
                }
                
                if (lastAutosave) {
                  return (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>Saved {lastAutosave.toLocaleTimeString()}</span>
                    </div>
                  );
                }
                
                return null;
              })()}
            </div>
          </DialogHeader>
          
          {/* CRITICAL: Note Creation Error Banner - Shows when save fails */}
          {/* STICKY: Fixed to top of dialog so it's visible even when scrolled to bottom of long note */}
          {noteCreationError && (
            <div className="sticky top-0 z-50 bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-4 flex-shrink-0 shadow-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5 animate-pulse" />
                <div className="flex-1">
                  <h3 className="font-bold text-red-800 text-lg">NOTE NOT SAVED!</h3>
                  <p className="text-red-700 mt-1">{noteCreationError}</p>
                  <p className="text-red-600 text-sm mt-2">
                    Your work is still here. Please copy your text below before closing, or try saving again.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-400 text-red-700 hover:bg-red-100"
                      onClick={() => {
                        // Build header info for recovery context
                        const client = selectedNoteClients?.length > 0 
                          ? allClientsForLookup.find(c => c.id === selectedNoteClients[0])
                          : null;
                        const clientName = client?.full_name || 'Unknown Client';
                        const clientMRN = client?.mrn || 'No MRN';
                        const dateStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
                        const templateName = selectedTemplate?.name || 'Unknown Template';
                        const providerName = users?.find(u => u.id === selectedNoteProvider)?.full_name || 'Unknown Provider';
                        
                        // Build full recovery text with header
                        const headerText = `=== NOTE RECOVERY DATA ===
Client: ${clientName}
MRN: ${clientMRN}
Date: ${dateStr}
Template: ${templateName}
Provider: ${providerName}
===========================

`;
                        // Copy all form data to clipboard
                        const formDataText = Object.entries(newNoteFormData)
                          .filter(([key, value]) => value && !key.startsWith('_'))
                          .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)
                          .join('\n\n');
                        
                        navigator.clipboard.writeText(headerText + formDataText);
                        toast.success('Note content copied to clipboard (includes client name & date)');
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy All Content
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-400 text-red-700 hover:bg-red-100"
                      onClick={() => setNoteCreationError(null)}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Collapsible Header Section */}
          {selectedTemplate && noteDialogHeaderCollapsed ? (
            /* Collapsed Summary Bar */
            <div 
              className="bg-gray-50 border rounded-lg p-3 mb-3 flex-shrink-0 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setNoteDialogHeaderCollapsed(false)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-medium text-gray-700">{selectedTemplate.name}</span>
                  <span className="text-gray-500">|</span>
                  <span className="text-gray-600">
                    👤 {(() => {
                      const client = findClientById(selectedNoteClients[0]) || selectedClient;
                      return client ? formatClientName(client) : 'No client selected';
                    })()}
                  </span>
                  <span className="text-gray-500">|</span>
                  <span className="text-gray-600">
                    🩺 {(() => {
                      const provider = users.find(u => u.id === selectedNoteProvider);
                      return provider ? provider.full_name : 'No provider selected';
                    })()}
                  </span>
                  <span className="text-gray-500">|</span>
                  <span className="text-gray-600">
                    {(() => {
                      if (selectedAppointment && selectedAppointment !== 'none') {
                        const apt = appointments.find(a => a.id === selectedAppointment);
                        if (apt) {
                          const aptDate = new Date(apt.start_time);
                          return `Appt: ${aptDate.toLocaleDateString()} ${aptDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                        }
                      }
                      return 'No appointment linked';
                    })()}
                  </span>
                </div>
                <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-800">
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Expand
                </Button>
              </div>
            </div>
          ) : (
            /* Expanded Header - Full Options - Now scrollable for smaller screens */
            <div className="flex-shrink-0 max-h-[50vh] overflow-y-auto border-b pb-4 mb-2">
              {/* Client Header - Show if client is pre-selected from client chart */}
              {/* Only show this if noteClientLocked is false - otherwise we show in the Select Client section */}
              {selectedNoteClients.length === 1 && !isGroupNote && !noteClientLocked && (() => {
                const preSelectedClient = findClientById(selectedNoteClients[0]);
                if (preSelectedClient) {
                  return (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 mb-4 flex-shrink-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-blue-900">
                            Creating note for: {formatClientName(preSelectedClient)}
                          </p>
                          <p className="text-sm text-blue-700">
                            MRN: #{(preSelectedClient.mrn || 'N/A').replace(/^MRN-/i, '')} | DOB: {preSelectedClient.date_of_birth ? formatDateMMDDYYYY(preSelectedClient.date_of_birth) : 'N/A'}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedNoteClients([])}
                          title="Clear client selection"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
          
          <div className="space-y-4">
            {/* Copay reminder for providers (Jun 2026) */}
            {!isGroupNote && selectedNoteClients.length === 1 && (
              <CopayReminderBanner clientId={selectedNoteClients[0]} />
            )}
            {/* Row 1: Client (left) | Provider (right) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Client Selection */}
              <div>
                <Label>{isGroupNote ? 'Select Group Members *' : 'Select Client *'}</Label>
                {noteClientLocked && selectedNoteClients.length === 1 ? (
                  // Locked client display - when opened from client chart
                  <div className="mt-1 p-2 bg-gray-100 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-teal-600" />
                      <span className="font-medium text-sm">
                        {findClientById(selectedNoteClients[0])?.full_name || selectedClient?.full_name || 'Unknown Client'}
                      </span>
                      <span className="text-xs text-gray-500">
                        (MRN: #{(findClientById(selectedNoteClients[0])?.mrn || selectedClient?.mrn || 'N/A').replace('MRN-', '')})
                      </span>
                      <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded ml-auto">
                        🔒 From Chart
                      </span>
                    </div>
                  </div>
                ) : !isGroupNote ? (
                  // Single client dropdown for individual notes
                  <Select 
                    value={typeof selectedNoteClients[0] === 'object' ? selectedNoteClients[0]?.id : (selectedNoteClients[0] || '')} 
                    onValueChange={(value) => setSelectedNoteClients([value])}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Choose a client..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.filter(c => !c.is_archived).map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.full_name} (MRN: #{client.mrn?.replace('MRN-', '') || 'N/A'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  // Multiple client selection for group notes
                  <div className="space-y-2 mt-1">
                    {/* Quick Add from Existing Group */}
                    <div className="p-2 bg-teal-50 rounded-lg border border-teal-200">
                      <Label className="text-xs font-medium text-teal-700">Quick Add from Group</Label>
                      <Select onValueChange={(groupId) => {
                        const group = clientGroups.find(g => g.id === groupId);
                        if (group && group.member_ids) {
                          const newMembers = group.member_ids.filter(id => !selectedNoteClients.includes(id));
                          setSelectedNoteClients([...selectedNoteClients, ...newMembers]);
                        }
                      }}>
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Select a group..." />
                        </SelectTrigger>
                        <SelectContent>
                          {clientGroups.filter(g => g.status === 'active' || g.is_active === true || (g.is_active !== false && g.status !== 'archived')).map(group => (
                            <SelectItem key={group.id} value={group.id}>
                              {group.name} ({group.member_ids?.length || 0} members)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Select onValueChange={(value) => {
                      if (!selectedNoteClients.includes(value)) {
                        setSelectedNoteClients([...selectedNoteClients, value]);
                      }
                    }}>
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Add individual clients..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients
                          .filter(c => !c.is_archived && !selectedNoteClients.includes(c.id))
                          .map(client => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.full_name} (MRN: #{client.mrn?.replace('MRN-', '') || 'N/A'})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Selected clients list */}
                    {selectedNoteClients.length > 0 && (
                      <div className="border rounded p-2 space-y-1 max-h-24 overflow-y-auto">
                        <p className="text-xs font-semibold text-gray-600">Selected ({selectedNoteClients.length}):</p>
                        {selectedNoteClients.map(clientId => {
                          const client = allClientsForLookup.find(c => c.id === clientId);
                          return client ? (
                            <div key={clientId} className="flex items-center justify-between bg-gray-50 p-1 rounded text-xs">
                              <span>{client.full_name}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-5 w-5 p-0"
                                onClick={() => setSelectedNoteClients(selectedNoteClients.filter(id => id !== clientId))}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Provider Selection */}
              <div>
                <Label>Select Provider <span className="font-normal text-gray-500">(responsible for this note)</span> *</Label>
                <Select 
                  value={selectedNoteProvider || ''} 
                  onValueChange={(value) => {
                    setSelectedNoteProvider(value);
                    
                    // ONLY auto-select the provider's default template if NO template has been explicitly chosen yet
                    // This prevents overwriting the user's template selection when they change providers
                    if (!selectedTemplate) {
                      const selectedProviderUser = users.find(u => u.id === value);
                      if (selectedProviderUser?.default_template_id && formTemplates?.length > 0) {
                        const providerDefaultTemplate = formTemplates.find(t => t.id === selectedProviderUser.default_template_id);
                        if (providerDefaultTemplate && providerDefaultTemplate.is_active !== false) {
                          // Fetch fresh template data
                          axios.get(`${API}/form-templates/${providerDefaultTemplate.id}`)
                            .then(response => {
                              const freshTemplate = response.data;
                              setSelectedTemplate(freshTemplate);
                              setIsGroupNote(freshTemplate.is_group_template || false);
                              
                              // Initialize form data with defaults
                              const initialFormData = {};
                              if (freshTemplate.form_fields) {
                                freshTemplate.form_fields.forEach(field => {
                                  // Initialize multi-select radio fields as empty arrays
                                  if (field.type === 'radio' && field.allowMultiple === true) {
                                    initialFormData[field.id] = field.default_value ? 
                                      (Array.isArray(field.default_value) ? field.default_value : [field.default_value]) : [];
                                  } else if (field.type === 'checkbox_group' || field.type === 'multiselect') {
                                    // These are always arrays
                                    initialFormData[field.id] = field.default_value ? 
                                      (Array.isArray(field.default_value) ? field.default_value : [field.default_value]) : [];
                                  } else if (field.default_value !== undefined && field.default_value !== null && field.default_value !== '') {
                                    if (field.type === 'date' && field.default_value === 'today') {
                                      initialFormData[field.id] = new Date().toISOString().split('T')[0];
                                    } else if (field.type === 'date' && field.default_value === 'custom' && field.custom_default_date) {
                                      initialFormData[field.id] = field.custom_default_date;
                                    } else if (field.type === 'time' && field.default_value === 'now') {
                                      initialFormData[field.id] = new Date().toTimeString().slice(0, 5);
                                    } else if (field.type === 'time' && field.default_value === 'custom' && field.custom_default_time) {
                                      initialFormData[field.id] = field.custom_default_time;
                                    } else {
                                      initialFormData[field.id] = field.default_value;
                                    }
                                  }
                                });
                              }
                              setNewNoteFormData(initialFormData);
                              // Reset dirty tracking
                              setTimeout(() => {
                                isNoteFormDirtyRef.current = false;
                                initialFormDataRef.current = JSON.stringify(initialFormData);
                              }, 0);
                            })
                            .catch(err => {
                              console.error('Error loading provider default template:', err);
                              setSelectedTemplate(providerDefaultTemplate);
                            });
                        }
                      }
                    }
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={
                      isSuperAdmin(user) || userRoleName === 'office staff' 
                        ? "Choose a provider..." 
                        : "Select yourself or a supervisee..."
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      // Filter providers based on current user's role AND client assignment
                      let availableProviders = [];
                      
                      const userRole = user?.role?.toLowerCase();
                      
                      // Get client's assigned providers if client is selected
                      const selectedClientForProvider = selectedNoteClients.length === 1 ? allClientsForLookup.find(c => c.id === selectedNoteClients[0]) : null;
                      const clientAssignedProviderIds = selectedClientForProvider?.assigned_providers || [];
                      
                      if (isSuperAdmin(user) || userRole === 'admin' || userRole === 'office staff' || userRole === 'office' || userRole === 'reception') {
                        // Admin/Super Admin/Office/Reception: Show all users who are designated as providers
                        availableProviders = users.filter(u => 
                          u.is_provider && !u.is_archived
                        );
                        
                        // If client is selected, further filter to only show client's assigned providers
                        if (selectedClientForProvider && clientAssignedProviderIds.length > 0) {
                          availableProviders = availableProviders.filter(p => 
                            clientAssignedProviderIds.includes(p.id)
                          );
                        }
                      } else if (userRole === 'provider' || userRole === 'intern' || userRole === 'supervisor') {
                        // Provider/Supervisor: Show themselves (if is_provider) + their supervisees (if is_provider)
                        if (user?.is_provider) {
                          availableProviders = [user]; // Start with themselves
                        }
                        
                        // Add supervisees who are providers
                        const supervisees = users.filter(u => 
                          u.supervisor_ids?.includes(user.id) && !u.is_archived && u.is_provider
                        );
                        availableProviders = [...availableProviders, ...supervisees];
                        
                        // If client is selected, further filter to only show client's assigned providers
                        if (selectedClientForProvider && clientAssignedProviderIds.length > 0) {
                          availableProviders = availableProviders.filter(p => 
                            clientAssignedProviderIds.includes(p.id)
                          );
                        }
                      }
                      
                      if (availableProviders.length === 0) {
                        if (selectedClientForProvider && clientAssignedProviderIds.length === 0) {
                          return (
                            <div className="p-2 text-sm text-amber-600">
                              No providers assigned to this client.
                            </div>
                          );
                        }
                        return (
                          <div className="p-2 text-sm text-red-600">
                            No providers available.
                          </div>
                        );
                      }
                      
                      return availableProviders.map(provider => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {provider.full_name}{provider.credentials ? `, ${provider.credentials}` : ''}
                          {provider.id === user?.id ? ' (Me)' : ''}
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Row 2: Form Template (left) | Link Appointment (right) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Form Template Selection */}
              <div>
                <Label>Select Form Template</Label>
                {/* TEMPLATE LOCK: Disable template changes once note has been saved (has autosaveNoteId) */}
                {autosaveNoteId ? (
                  <div className="mt-1">
                    <div className="flex items-center gap-2 p-2 border rounded-md bg-gray-50">
                      <Lock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium">{selectedTemplate?.name || 'Unknown Template'}</span>
                      {selectedTemplate?.is_group_template && <span className="text-xs text-gray-500">(Group)</span>}
                    </div>
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <Lock className="h-3 w-3" />
                      Template cannot be changed after note content is saved.
                    </p>
                  </div>
                ) : (
                <Popover open={templateSearchOpen} onOpenChange={setTemplateSearchOpen} modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={templateSearchOpen}
                      className="w-full justify-between font-normal mt-1"
                      data-testid="template-search-trigger"
                    >
                      {selectedTemplate ? (
                        <span className="truncate">{selectedTemplate.name} {selectedTemplate.is_group_template && '(Group)'}</span>
                      ) : (
                        <span className="text-gray-500">Search and select template...</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[600px] p-0" align="start" style={{ zIndex: 9999 }}>
                    <Command>
                      <CommandInput placeholder="Search templates..." />
                      <CommandList>
                        <CommandEmpty>No templates found.</CommandEmpty>
                        <CommandGroup>
                          {(() => {
                            const allTemplates = formTemplates || [];
                            const activeTemplates = allTemplates
                              .filter(template => 
                                template.is_active !== false && 
                                template.available_in_quick_create !== false &&
                                !template.is_group_template // Hide group templates in individual note creation
                              )
                              .sort((a, b) => {
                                // Progress notes first (check name or form_type)
                                const aIsProgress = (a.name || '').toLowerCase().includes('progress') || 
                                                   (a.form_type || '').toLowerCase().includes('progress');
                                const bIsProgress = (b.name || '').toLowerCase().includes('progress') || 
                                                   (b.form_type || '').toLowerCase().includes('progress');
                                
                                if (aIsProgress && !bIsProgress) return -1;
                                if (!aIsProgress && bIsProgress) return 1;
                                
                                // Within same group, sort alphabetically
                                return (a.name || '').localeCompare(b.name || '');
                              });
                            
                            return activeTemplates.map(template => (
                              <CommandItem
                                key={template.id}
                                value={template.name}
                                onSelect={async () => {
                                  // CHECK FOR DIRTY STATE: If user has typed in form or note exists, show warning
                                  const hasDirtyData = isNoteFormDirtyRef.current || autosaveNoteId;
                                  const isChangingTemplate = selectedTemplate && selectedTemplate.id !== template.id;
                                  
                                  void 0 && console.log('[TemplateChange] onSelect:', {
                                    templateId: template.id,
                                    templateName: template.name,
                                    hasDirtyData,
                                    isNoteFormDirtyRef: isNoteFormDirtyRef.current,
                                    autosaveNoteId,
                                    selectedTemplate: selectedTemplate?.name,
                                    selectedTemplateId: selectedTemplate?.id,
                                    isChangingTemplate,
                                    willShowWarning: hasDirtyData && isChangingTemplate
                                  });
                                  
                                  if (hasDirtyData && isChangingTemplate) {
                                    // Store the pending template and show warning modal
                                    void 0 && console.log('[TemplateChange] Dirty data detected, showing warning', { hasDirtyData, isChangingTemplate, autosaveNoteId });
                                    setPendingTemplateChange(template);
                                    setShowTemplateChangeWarning(true);
                                    setTemplateSearchOpen(false);
                                    return;
                                  }
                                  
                                  // Fetch fresh template data
                                  let freshTemplate;
                                  try {
                                    const response = await axios.get(`${API}/form-templates/${template.id}`);
                                    freshTemplate = response.data;
                                  } catch (error) {
                                    console.error('Error fetching fresh template:', error);
                                    freshTemplate = template;
                                  }
                                  setSelectedTemplate(freshTemplate);
                                  setLinkedGoals([]);
                                  
                                  // Reset dirty flag when changing to new template normally
                                  isNoteFormDirtyRef.current = false;
                                  
                                  // Fetch available PANs if Comprehensive Assessment
                                  if (freshTemplate?.name?.includes('Comprehensive Assessment')) {
                                    try {
                                      const pansRes = await axios.get(`${API}/treatment-planning/pans`, {
                                        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                      });
                                      setAvailablePANs(pansRes.data);
                                    } catch (error) {
                                      console.error('Error fetching PANs:', error);
                                    }
                                  }
                                  
                                  // Initialize form data
                                  const initialFormData = {};
                                  if (freshTemplate && freshTemplate.form_fields) {
                                    freshTemplate.form_fields.forEach(field => {
                                      if (field.default_value !== undefined && field.default_value !== null && field.default_value !== '') {
                                        // Handle date default values
                                        if (field.type === 'date' && field.default_value === 'today') {
                                          initialFormData[field.id] = new Date().toISOString().split('T')[0];
                                        } else if (field.type === 'date' && field.default_value === 'custom' && field.custom_default_date) {
                                          initialFormData[field.id] = field.custom_default_date;
                                        } else if (field.type === 'time' && field.default_value === 'now') {
                                          initialFormData[field.id] = new Date().toTimeString().slice(0, 5);
                                        } else if (field.type === 'time' && field.default_value === 'custom' && field.custom_default_time) {
                                          initialFormData[field.id] = field.custom_default_time;
                                        } else {
                                          initialFormData[field.id] = field.default_value;
                                        }
                                      }
                                      
                                      // Auto-fill from curriculum session data if available
                                      if (curriculumSessionData?.session_data && field.auto_fill_from) {
                                        const sessionData = curriculumSessionData.session_data;
                                        switch (field.auto_fill_from) {
                                          case 'curriculum_session_number':
                                            initialFormData[field.id] = `Session ${curriculumSessionData.current_session_number} of ${curriculumSessionData.total_sessions}`;
                                            break;
                                          case 'curriculum_session_title':
                                            initialFormData[field.id] = sessionData.title || '';
                                            break;
                                          case 'curriculum_session_goal':
                                            initialFormData[field.id] = sessionData.goal || '';
                                            break;
                                          case 'curriculum_session_objectives':
                                            initialFormData[field.id] = Array.isArray(sessionData.objectives) 
                                              ? sessionData.objectives.join('\n') 
                                              : sessionData.objectives || '';
                                            break;
                                          case 'curriculum_session_content':
                                            initialFormData[field.id] = sessionData.content || '';
                                            break;
                                          case 'curriculum_session_interventions':
                                            // For multi-select, set as array of selected values
                                            if (Array.isArray(sessionData.interventions)) {
                                              initialFormData[field.id] = sessionData.interventions;
                                            }
                                            break;
                                          case 'curriculum_session_homework':
                                            initialFormData[field.id] = sessionData.homework || '';
                                            break;
                                        }
                                      }
                                    });
                                  }
                                  setNewNoteFormData(initialFormData);
                                  
                                  // IMPORTANT: Reset dirty tracking AFTER setting form data with defaults
                                  // This must be done with setTimeout to ensure it runs after the useEffect
                                  setTimeout(() => {
                                    isNoteFormDirtyRef.current = false;
                                    initialFormDataRef.current = JSON.stringify(initialFormData);
                                    void 0 && console.log('[TemplateChange] Reset dirty tracking after initializing form with defaults');
                                  }, 0);
                                  
                                  // Set group note mode
                                  setIsGroupNote(freshTemplate.is_group_template || false);
                                  if (freshTemplate.is_group_template) {
                                    setSelectedNoteClients([]);
                                  }
                                  
                                  setTemplateSearchOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${selectedTemplate?.id === template.id ? 'opacity-100' : 'opacity-0'}`}
                                />
                                {template.name} {template.is_group_template && '(Group)'}
                              </CommandItem>
                            ));
                          })()}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                )}
              </div>
              
              {/* Link Appointment - moved into Row 2 */}
              {selectedNoteClients.length > 0 && !isGroupNote ? (
              <div>
                <Label>Link to Appointment <span className="font-normal text-gray-500">(optional - pre-fills location, POS & modality)</span></Label>
                <AppointmentLinkSelect
                  value={selectedAppointment || ''}
                  appointments={appointments}
                  clientGroups={clientGroups}
                  API={API}
                  clientIds={selectedNoteClients}
                  groupAware={false}
                  triggerClassName="mt-1"
                  placeholder="No appointment - or choose one..."
                  noneLabel="No appointment"
                  testIdPrefix="new-note-appt"
                  renderLabel={(apt) => {
                    const aptClient = allClientsForLookup.find(c => c.id === apt.client_id);
                    const aptProvider = users.find(u => u.id === apt.provider_id);
                    const d = new Date(apt.start_time);
                    const dateStr = d.toLocaleDateString();
                    const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    const aptLocation = apt.location_id ? locations.find(l => l.id === apt.location_id) : null;
                    const linkedNotes = notes.filter(note => note.appointment_id === apt.id);
                    const hasLinkedNotes = linkedNotes.length > 0;
                    const locStr = aptLocation ? ` @ ${aptLocation.name}` : '';
                    const linkedStr = hasLinkedNotes
                      ? ` (${linkedNotes.length} note${linkedNotes.length > 1 ? 's' : ''} linked)`
                      : '';
                    return `${dateStr} ${timeStr} - ${aptClient?.full_name} with ${aptProvider?.full_name}${locStr}${linkedStr}`;
                  }}
                  onChange={(value, apt) => {
                    setSelectedAppointment(value || null);
                    // Pre-fill consent affirmation from appointment data
                    if (value && apt) {
                      const posObj = (getEffectivePosCodes(organizations, posCodes)).find(p => p.code === apt.pos_code);
                      setTelehealthConsent({
                        provider_location: apt.location_id || null,
                        pos_code: apt.pos_code || null,
                        pos_description: posObj?.description || apt.pos_code_description || null,
                        modality: apt.telehealth_modality === 'audio_visual' ? 'audio_visual' :
                                  apt.telehealth_modality === 'audio_only' ? 'audio_only' :
                                  apt.telehealth_modifier === '93' ? 'audio_only' :
                                  apt.telehealth_modifier === '95' ? 'audio_visual' :
                                  apt.pos_code && ['10', '02'].includes(apt.pos_code) ? 'audio_visual' : 'in_person'
                      });
                    }
                  }}
                />
              </div>
              ) : (
              <div />
              )}
            </div>
            
            {/* Manual DOS, Times, and CPT - Show when NO appointment is linked */}
            {selectedNoteClients.length > 0 && (!selectedAppointment || selectedAppointment === 'none') && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <Label className="font-medium text-amber-900 text-sm">Service Details</Label>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex flex-wrap items-end gap-2">
                  {/* Date of Service */}
                  <div className="w-[110px]">
                    <Label className="text-xs text-gray-600">DOS</Label>
                    <Input
                      type="date"
                      value={newNoteFormData._manual_dos || getTodayLocalDate()}
                      onChange={(e) => setNewNoteFormData(prev => ({...prev, _manual_dos: e.target.value}))}
                      className="h-8 text-xs px-1"
                    />
                  </div>
                  {/* Start Time */}
                  <div className="w-[130px]">
                    <Label className="text-xs text-gray-600">Start</Label>
                    <TimeInput
                      value={newNoteFormData._manual_start_time || ''}
                      onChange={(newStartTime) => {
                        setNewNoteFormData(prev => {
                          const newData = {...prev, _manual_start_time: newStartTime};
                          // Auto-calculate end time if we have CPT and units
                          if (prev._manual_cpt_id && newStartTime && prev._manual_units) {
                            const selectedCpt = getAccessibleCPTCodes(selectedNoteProvider).find(c => c.id === prev._manual_cpt_id);
                            if (selectedCpt) {
                              const minutesPerUnit = selectedCpt.minutes_per_unit || selectedCpt.duration_minutes || 15;
                              const totalMinutes = minutesPerUnit * (prev._manual_units || 1);
                              const [hours, mins] = newStartTime.split(':').map(Number);
                              const startDate = new Date();
                              startDate.setHours(hours, mins, 0, 0);
                              const endDate = new Date(startDate.getTime() + totalMinutes * 60000);
                              newData._manual_end_time = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
                            }
                          }
                          return newData;
                        });
                      }}
                      className="h-8 text-xs"
                      data-testid="note-start-time"
                    />
                  </div>
                  {/* End Time */}
                  <div className="w-[130px]">
                    <Label className="text-xs text-gray-600">End</Label>
                    <TimeInput
                      value={newNoteFormData._manual_end_time || ''}
                      onChange={(newEndTime) => {
                        setNewNoteFormData(prev => {
                          const newData = {...prev, _manual_end_time: newEndTime};
                          // Auto-calculate units if we have CPT and start time
                          if (prev._manual_cpt_id && prev._manual_start_time && newEndTime) {
                            const selectedCpt = getAccessibleCPTCodes(selectedNoteProvider).find(c => c.id === prev._manual_cpt_id);
                            if (selectedCpt) {
                              const minutesPerUnit = selectedCpt.minutes_per_unit || selectedCpt.duration_minutes || 15;
                              const [startH, startM] = prev._manual_start_time.split(':').map(Number);
                              const [endH, endM] = newEndTime.split(':').map(Number);
                              const startMins = startH * 60 + startM;
                              const endMins = endH * 60 + endM;
                              const totalMinutes = endMins - startMins;
                              if (totalMinutes > 0) {
                                newData._manual_units = Math.max(1, Math.round(totalMinutes / minutesPerUnit));
                              }
                            }
                          }
                          return newData;
                        });
                      }}
                      className="h-8 text-xs"
                      data-testid="note-end-time"
                    />
                  </div>
                  {/* Units */}
                  <div className="w-[60px]">
                    <Label className="text-xs text-gray-600">Units</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newNoteFormData._manual_units || 1}
                      onChange={(e) => {
                        const newUnits = parseInt(e.target.value) || 1;
                        setNewNoteFormData(prev => {
                          const newData = {...prev, _manual_units: newUnits};
                          // Auto-calculate end time if we have CPT and start time
                          if (prev._manual_cpt_id && prev._manual_start_time) {
                            const selectedCpt = getAccessibleCPTCodes(selectedNoteProvider).find(c => c.id === prev._manual_cpt_id);
                            if (selectedCpt) {
                              const minutesPerUnit = selectedCpt.minutes_per_unit || selectedCpt.duration_minutes || 15;
                              const totalMinutes = minutesPerUnit * newUnits;
                              const [hours, mins] = prev._manual_start_time.split(':').map(Number);
                              const startDate = new Date();
                              startDate.setHours(hours, mins, 0, 0);
                              const endDate = new Date(startDate.getTime() + totalMinutes * 60000);
                              newData._manual_end_time = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
                            }
                          }
                          return newData;
                        });
                      }}
                      disabled={(() => {
                        if (!newNoteFormData._manual_cpt_id) return false;
                        const selectedCpt = getAccessibleCPTCodes(selectedNoteProvider).find(c => c.id === newNoteFormData._manual_cpt_id);
                        return selectedCpt && !selectedCpt.allows_multiple_units;
                      })()}
                      className="h-8 text-xs px-1"
                    />
                  </div>
                  {/* CPT Code */}
                  <div className="min-w-[220px] flex-1">
                    <Label className="text-xs text-gray-600">CPT</Label>
                    <CptCombobox
                      testId="note-cpt"
                      placeholder="Select..."
                      valueMode="id"
                      value={newNoteFormData._manual_cpt_id || ''}
                      codes={(() => {
                        // Sort by code, then by description so visually
                        // similar variants (e.g. all 10 ``90791``s) cluster
                        // together.  Keeps the dropdown predictable when a
                        // clinician has multiple variants of the same code
                        // assigned via different bundles.
                        const codes = getAccessibleCPTCodes(selectedNoteProvider);
                        return [...codes].sort((a, b) => {
                          const aCode = (a.code || '').toString();
                          const bCode = (b.code || '').toString();
                          if (aCode !== bCode) return aCode.localeCompare(bCode);
                          return (a.description || '').localeCompare(b.description || '');
                        });
                      })()}
                      onChange={(value, selectedCpt) => {
                        setNewNoteFormData(prev => {
                          const newData = {...prev, _manual_cpt_id: value, _manual_cpt: selectedCpt?.code || null};
                          // Set default units and recalculate end time
                          if (selectedCpt) {
                            if (!selectedCpt.allows_multiple_units) {
                              newData._manual_units = 1;
                            }
                            // Recalculate end time if start time exists
                            if (prev._manual_start_time) {
                              const minutesPerUnit = selectedCpt.minutes_per_unit || selectedCpt.duration_minutes || 15;
                              const units = newData._manual_units || prev._manual_units || 1;
                              const totalMinutes = minutesPerUnit * units;
                              const [hours, mins] = prev._manual_start_time.split(':').map(Number);
                              const startDate = new Date();
                              startDate.setHours(hours, mins, 0, 0);
                              const endDate = new Date(startDate.getTime() + totalMinutes * 60000);
                              newData._manual_end_time = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
                            }
                          }
                          return newData;
                        });
                      }}
                    />
                  </div>
                  {/* POS */}
                  <div className="min-w-[150px]">
                    <Label className="text-xs text-gray-600">POS</Label>
                    <Select
                      value={newNoteFormData._manual_pos_code || ''}
                      onValueChange={(value) => {
                        const selectedProv = providers?.find(p => p.id === selectedNoteProvider) || users?.find(u => u.id === selectedNoteProvider);
                        setNewNoteFormData(prev => {
                          const newData = {...prev, _manual_pos_code: value};
                          if (value === '02' || value === '10') {
                            // Telehealth — prefill modality from provider default
                            if (!prev._manual_modality) {
                              newData._manual_modality = selectedProv?.default_telehealth_modality || 'audio_visual';
                            }
                          } else {
                            newData._manual_modality = '';
                          }
                          return newData;
                        });
                        // Sync to consent
                        const posObj = (getEffectivePosCodes(organizations, posCodes)).find(p => p.code === value);
                        setTelehealthConsent(prev => {
                          const updated = {
                            ...prev,
                            pos_code: value,
                            pos_description: posObj?.description || ''
                          };
                          if (value === '02' || value === '10') {
                            if (!prev.modality) {
                              updated.modality = selectedProv?.default_telehealth_modality || 'audio_visual';
                            }
                            if (!prev.provider_location && selectedProv?.default_location) {
                              updated.provider_location = selectedProv.default_location;
                            }
                          }
                          return updated;
                        });
                      }}
                    >
                      <SelectTrigger className="h-8 text-sm" data-testid="note-create-pos-selector">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const selectedProvider = providers?.find(p => p.id === selectedNoteProvider) || users?.find(u => u.id === selectedNoteProvider);
                          const providerPosCodes = selectedProvider?.assigned_pos_codes || [];
                          const allPosCodes = getEffectivePosCodes(organizations, posCodes);
                          const available = providerPosCodes.length > 0
                            ? allPosCodes.filter(p => providerPosCodes.includes(p.code))
                            : allPosCodes;
                          return available.map(pos => (
                            <SelectItem key={pos.code} value={pos.code}>
                              {pos.code} - {pos.name || pos.description}
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Modality - always reserve space; only populate for telehealth POS
                      so the row layout stays stable when modality toggles in/out. */}
                  <div className="min-w-[140px]">
                    {(newNoteFormData._manual_pos_code === '02' || newNoteFormData._manual_pos_code === '10') ? (
                      <>
                        <Label className="text-xs text-gray-600">Modality</Label>
                        <Select
                          value={newNoteFormData._manual_modality || 'audio_visual'}
                          onValueChange={(value) => {
                            setNewNoteFormData(prev => ({...prev, _manual_modality: value}));
                            // Sync to consent (keep derived keys consistent)
                            setTelehealthConsent(prev => ({
                              ...prev,
                              modality: value,
                              modifier: value === 'audio_only' ? '93' : value === 'audio_visual' ? '95' : null,
                              session_modality: value === 'audio_only' ? 'phone' : value === 'audio_visual' ? 'video' : 'in_person'
                            }));
                          }}
                        >
                          <SelectTrigger className="h-8 text-sm" data-testid="note-create-modality-selector">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="audio_visual">Audio-Visual (95)</SelectItem>
                            <SelectItem value="audio_only">Audio Only (93)</SelectItem>
                          </SelectContent>
                        </Select>
                      </>
                    ) : (
                      // Invisible placeholder — same height as the visible Modality field
                      // so the row doesn't shift when POS changes to/from telehealth.
                      <div aria-hidden="true" className="invisible">
                        <Label className="text-xs text-gray-600">Modality</Label>
                        <div className="h-8" />
                      </div>
                    )}
                  </div>
                  {/* Create & Link Appointment — saves draft first, then creates appointment */}
                  {newNoteFormData._manual_cpt && (!selectedAppointment || selectedAppointment === 'none') && (
                    <div className="flex items-end">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs gap-1 border-blue-300 text-blue-700 hover:bg-blue-50 whitespace-nowrap"
                        data-testid="create-link-appointment-from-new-note"
                        disabled={isSavingNote || !selectedTemplate || !selectedNoteProvider || selectedNoteClients.length === 0}
                        onClick={async () => {
                          if (isSavingNote) return;
                          setIsSavingNote(true);
                          try {
                            // Step 1: Build note content (same as Save as Draft)
                            const getClientDataValue = (field) => {
                              const client = allClientsForLookup.find(c => c.id === selectedNoteClients[0]);
                              if (!client) return '';
                              switch(field.type) {
                                case 'client_name': return client.full_name || '';
                                case 'client_dob': return client.date_of_birth || client.dob || '';
                                default: return '';
                              }
                            };
                            let noteContent = {
                              template_id: selectedTemplate.id,
                              template_name: selectedTemplate.name
                            };
                            if (selectedTemplate.form_fields && selectedTemplate.form_fields.length > 0) {
                              noteContent.fields = selectedTemplate.form_fields.map(field => ({
                                ...field,
                                value: field.type?.startsWith('client_')
                                  ? getClientDataValue(field)
                                  : (newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : (field.value !== undefined ? field.value : ''))
                              }));
                            } else if (selectedTemplate.template_data && selectedTemplate.template_data.sections) {
                              noteContent.sections = selectedTemplate.template_data.sections.map(section => ({
                                name: section.name,
                                fields: (section.fields || []).map(field => ({
                                  ...field,
                                  value: field.type?.startsWith('client_')
                                    ? getClientDataValue(field)
                                    : (newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : (field.value !== undefined ? field.value : ''))
                                }))
                              }));
                            }
                            const manualSvc = {
                              dos: newNoteFormData._manual_dos || getTodayLocalDate(),
                              start_time: newNoteFormData._manual_start_time || null,
                              end_time: newNoteFormData._manual_end_time || null,
                              units: newNoteFormData._manual_units || null,
                              cpt: newNoteFormData._manual_cpt || null,
                              cpt_code_id: newNoteFormData._manual_cpt_id || null,
                              pos_code: newNoteFormData._manual_pos_code || null,
                              modality: newNoteFormData._manual_modality || null
                            };

                            // Step 2: Create draft note
                            const createResponse = await handleCreateClinicalNote(
                              noteContent,
                              selectedTemplate.id,
                              selectedNoteProvider,
                              selectedNoteClients,
                              isGroupNote,
                              'none',
                              'draft',
                              manualSvc
                            );
                            if (!createResponse) {
                              toast.error('Failed to save draft note');
                              return;
                            }
                            const noteId = createResponse.id || createResponse.note_id;

                            // Step 3: Create & link appointment (with conflict detection)
                            const createLinkedAppt = async (force = false) => {
                              const url = `${API}/notes/${noteId}/create-linked-appointment${force ? '?force=true' : ''}`;
                              return axios.post(url);
                            };
                            try {
                              const apptResp = await createLinkedAppt(false);
                              toast.success('Draft saved, appointment created & linked');
                              // Feb 27, 2026 — Optimistically push the new
                              // appointment onto the global state so it
                              // shows up on the calendar / past
                              // appointments list right away.  Same
                              // rationale as the note-header button —
                              // fetchAppointments() uses a tight
                              // today-centered window that may not
                              // include the note's DOS.
                              if (apptResp?.data?.appointment) {
                                setAppointments(prev => {
                                  const apt = apptResp.data.appointment;
                                  if (!apt.id) return prev;
                                  if (prev.some(a => a.id === apt.id)) return prev;
                                  return [apt, ...prev];
                                });
                              }
                              if (typeof fetchAppointments === 'function') fetchAppointments();
                            } catch (apptErr) {
                              const status = apptErr.response?.status;
                              const detail = apptErr.response?.data?.detail;
                              if (status === 409 && detail && typeof detail === 'object' && detail.can_force) {
                                if (window.confirm(`${detail.message}\n\nCreate the appointment anyway?`)) {
                                  try {
                                    const apptResp2 = await createLinkedAppt(true);
                                    toast.success('Draft saved, appointment created & linked (conflict overridden)');
                                    if (apptResp2?.data?.appointment) {
                                      setAppointments(prev => {
                                        const apt = apptResp2.data.appointment;
                                        if (!apt.id) return prev;
                                        if (prev.some(a => a.id === apt.id)) return prev;
                                        return [apt, ...prev];
                                      });
                                    }
                                    if (typeof fetchAppointments === 'function') fetchAppointments();
                                  } catch (err2) {
                                    const d2 = err2.response?.data?.detail;
                                    toast.error(typeof d2 === 'object' ? (d2.message || 'Failed to create appointment') : (d2 || 'Failed to create appointment'));
                                    // Note was saved; leave user in the note
                                  }
                                } else {
                                  toast.info('Draft saved. Appointment not created.');
                                }
                              } else if (status === 409 && detail && typeof detail === 'object') {
                                // Non-bypassable (CPT same-day rule)
                                toast.error(detail.message || 'Scheduling conflict — appointment not created');
                              } else {
                                toast.error(typeof detail === 'object' ? (detail?.message || 'Failed to create appointment') : (detail || 'Failed to create appointment'));
                              }
                            }

                            // Step 4: Close Create dialog, open the note for editing
                            setShowAddNote(false);
                            setSelectedTemplate(null);
                            setSelectedNoteProvider(null);
                            setSelectedNoteClients([]);
                            setIsGroupNote(false);
                            setSelectedAppointment(null);
                            setTemplateSearchTerm('');
                            setNewNoteFormData({});

                            // Open the note for editing
                            if (typeof openNote === 'function') {
                              await openNote({id: noteId});
                              setIsEditingNote(true);
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to create appointment');
                          } finally {
                            setIsSavingNote(false);
                          }
                        }}
                      >
                        <CalendarPlus className="h-3.5 w-3.5" />
                        Create & Link Appt
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              </div>
            )}
            
            {/* Collapse button - show when template is selected */}
            {selectedTemplate && (
              <div className="flex justify-end pt-2">
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => setNoteDialogHeaderCollapsed(true)}
                >
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Collapse Options
                </Button>
              </div>
            )}
          </div>
          </div>
          )}
          
          {/* Form Preview Section - Always visible, scrollable */}
          <div className="flex-1 overflow-y-auto min-h-0 pr-2">
            {selectedTemplate && (
              <div className="border rounded-lg p-4">
                {/* Session Location & Consent Affirmation - Only for Progress Notes WITHOUT consent_affirmation field in template */}
                {selectedNoteClients.length > 0 && selectedTemplate && 
                 selectedTemplate.form_type?.toLowerCase().includes('progress') &&
                 // Exclude no-show/cancellation forms - no consent needed when client didn't attend
                 !selectedTemplate.name?.toLowerCase().includes('no show') &&
                 !selectedTemplate.name?.toLowerCase().includes('cancellation form') &&
                 // Check both old (form_fields) and new (template_data.sections) structures for consent_affirmation
                 !selectedTemplate.form_fields?.some(f => f.type === 'consent_affirmation') &&
                 !selectedTemplate.template_data?.sections?.some(section => 
                   section.fields?.some(f => f.type === 'consent_affirmation')
                 ) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Building className="h-5 w-5 text-blue-600" />
                      <Label className="font-medium text-blue-900">Consent Affirmation</Label>
                      {telehealthConsent?.pos_code && (
                        <span className="bg-green-200 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium">
                          ✓ Confirmed
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      {/* Provider Location */}
                      <div>
                        <Label className="text-sm text-gray-700 mb-1 block">Provider Location</Label>
                        <Select
                          value={telehealthConsent?.provider_location || ''}
                          onValueChange={(value) => setTelehealthConsent(prev => ({
                            ...prev,
                            provider_location: value
                          }))}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select location..." />
                          </SelectTrigger>
                          <SelectContent>
                            {[...(locations || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(loc => (
                              <SelectItem key={loc.id} value={loc.id}>
                                <div className="flex items-center gap-2">
                                  <Building className="h-3 w-3" />
                                  {loc.name}
                                </div>
                              </SelectItem>
                            ))}
                            <SelectItem value="home">
                              <div className="flex items-center gap-2">
                                <Home className="h-3 w-3" />
                                Provider's Home
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Place of Service (POS) */}
                      <div>
                        <Label className="text-sm text-gray-700 mb-1 block">Place of Service</Label>
                        <Select
                          value={telehealthConsent?.pos_code || ''}
                          onValueChange={(value) => {
                            const posObj = (getEffectivePosCodes(organizations, posCodes)).find(p => p.code === value);
                            setTelehealthConsent(prev => ({
                              ...prev,
                              pos_code: value,
                              pos_description: posObj?.description || ''
                            }));
                            // Sync back to Service Details header
                            setNewNoteFormData(prev => {
                              const newData = {...prev, _manual_pos_code: value};
                              if (value === '02' || value === '10') {
                                if (!prev._manual_modality) {
                                  const selectedProv = providers?.find(p => p.id === selectedNoteProvider) || users?.find(u => u.id === selectedNoteProvider);
                                  newData._manual_modality = selectedProv?.default_telehealth_modality || 'audio_visual';
                                }
                              } else {
                                newData._manual_modality = '';
                              }
                              return newData;
                            });
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select POS..." />
                          </SelectTrigger>
                          <SelectContent>
                            {getEffectivePosCodes(organizations, posCodes).map(pos => (
                              <SelectItem key={pos.code} value={pos.code}>
                                <div className="flex items-center gap-2">
                                  {['10', '02'].includes(pos.code) ? (
                                    <Monitor className="h-3 w-3 text-blue-600" />
                                  ) : pos.code === '03' ? (
                                    <GraduationCap className="h-3 w-3 text-orange-600" />
                                  ) : (
                                    <Building className="h-3 w-3 text-green-600" />
                                  )}
                                  {pos.code} - {pos.description}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Modality - Only for telehealth POS codes */}
                      <div>
                        <Label className="text-sm text-gray-700 mb-1 block">Session Modality</Label>
                        <Select
                          value={telehealthConsent?.modality || ''}
                          onValueChange={(value) => {
                            setTelehealthConsent(prev => ({
                              ...prev,
                              modality: value,
                              modifier: value === 'audio_only' ? '93' : value === 'audio_visual' ? '95' : null,
                              session_modality: value === 'audio_only' ? 'phone' : value === 'audio_visual' ? 'video' : 'in_person'
                            }));
                            // Sync back to Service Details header
                            setNewNoteFormData(prev => ({...prev, _manual_modality: value}));
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select modality..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="in_person">
                              <div className="flex items-center gap-2">
                                <Users className="h-3 w-3 text-green-600" />
                                In-Person
                              </div>
                            </SelectItem>
                            <SelectItem value="audio_visual">
                              <div className="flex items-center gap-2">
                                <Monitor className="h-3 w-3 text-blue-600" />
                                Audio/Visual (Video)
                              </div>
                            </SelectItem>
                            <SelectItem value="audio_only">
                              <div className="flex items-center gap-2">
                                <Phone className="h-3 w-3 text-purple-600" />
                                Audio Only (Phone)
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Consent language — single source of truth via backend */}
                    <ConsentLivePreview
                      consentData={telehealthConsent}
                      token={sessionStorage.getItem('token')}
                    />
                  </div>
                )}
                
                {/* Template name already shown in collapsed header - skip duplicate here */}
                
                {(() => {
                  // Get fields - handle both old (form_fields) and new (template_data.sections) structures
                  let fieldsToRender = [];
                  let hasSections = false;
                  let sectionsData = [];
                  
                  // Priority: form_fields (edited by form builder) > template_data.sections (legacy)
                  // This ensures edits made in the form builder take effect
                  if (selectedTemplate.form_fields && selectedTemplate.form_fields.length > 0) {
                    // Use form_fields - this is what the form builder edits
                    fieldsToRender = selectedTemplate.form_fields;
                  } else if (selectedTemplate.template_data && selectedTemplate.template_data.sections) {
                    // Fallback to sections structure (legacy templates)
                    hasSections = true;
                    sectionsData = selectedTemplate.template_data.sections;
                  }
                  
                  const renderField = (field, index, allFields = []) => {
                    // Check conditional field logic - hide if condition not met
                    if (field.conditionalField) {
                      const parentField = allFields.find(f => f.id === field.conditionalField);
                      if (parentField) {
                        // During note creation, values are in newNoteFormData, not in field.value
                        const parentValue = newNoteFormData[parentField.id] || parentField.value || '';
                        const operator = field.conditionalOperator || 'equals';
                        const targetValue = field.conditionalValue;
                        
                        let shouldShow = false;
                        if (operator === 'equals') {
                          shouldShow = Array.isArray(parentValue) ? parentValue.includes(targetValue) : parentValue === targetValue;
                        } else if (operator === 'not_equals') {
                          shouldShow = Array.isArray(parentValue) ? !parentValue.includes(targetValue) : parentValue !== targetValue;
                        } else if (operator === 'contains') {
                          shouldShow = Array.isArray(parentValue) ? parentValue.includes(targetValue) : String(parentValue || '').includes(targetValue);
                        } else if (operator === 'not_empty') {
                          shouldShow = Array.isArray(parentValue) ? parentValue.length > 0 : !!parentValue;
                        } else {
                          shouldShow = targetValue ? (Array.isArray(parentValue) ? parentValue.includes(targetValue) : parentValue === targetValue) : !!parentValue;
                        }
                        
                        if (!shouldShow) {
                          return null; // Don't render this field
                        }
                      } else {
                        // Parent field not found, hide conditional field
                        return null;
                      }
                    }
                    
                    // Style for background color only - width is controlled by parent grid
                    const fieldStyle = {
                      backgroundColor: field.backgroundColor || 'transparent',
                      padding: field.backgroundColor ? '12px' : '0',
                      borderRadius: field.backgroundColor ? '6px' : '0',
                      boxSizing: 'border-box'
                    };

                    // create_task field (Feb 2026 — reschedule-request
                    // flow).  Mounts the dedicated widget that
                    // captures title + assignee + due_date + note;
                    // backend processor spawns the real Task on
                    // note save.  Placed BEFORE the client_* and
                    // simpleField branches so it always wins for
                    // this type.
                    if (field.type === 'create_task') {
                      return (
                        <div key={index} style={fieldStyle}>
                          <CreateTaskFieldWidget
                            field={field}
                            value={newNoteFormData[field.id] || {}}
                            onChange={(next) => setNewNoteFormData(prev => ({
                              ...(prev || {}),
                              [field.id]: next,
                            }))}
                            client={selectedClient}
                            readOnly={false}
                          />
                        </div>
                      );
                    }
                    
                    // Check if this is a client data auto-fill field
                    if (field.type && field.type.startsWith('client_')) {
                      // Render read-only pre-populated field
                      if (field.type === 'client_name') {
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <Input value={selectedClient?.full_name || 'Client'} disabled className="bg-gray-100" />
                          </div>
                        );
                      }
                      if (field.type === 'client_dob') {
                        const dob = selectedClient?.date_of_birth || selectedClient?.dob;
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <Input value={dob ? new Date(dob).toLocaleDateString() : 'N/A'} disabled className="bg-gray-100" />
                          </div>
                        );
                      }
                      if (field.type === 'client_age') {
                        const dob = selectedClient?.date_of_birth || selectedClient?.dob;
                        const age = dob ? Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000)) : 'N/A';
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <Input value={age} disabled className="bg-gray-100" />
                          </div>
                        );
                      }
                      if (field.type === 'client_email') {
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <Input value={selectedClient?.email || 'N/A'} disabled className="bg-gray-100" />
                          </div>
                        );
                      }
                      if (field.type === 'client_phone') {
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <Input value={selectedClient?.phone || 'N/A'} disabled className="bg-gray-100" />
                          </div>
                        );
                      }
                      if (field.type === 'client_address') {
                        const address = selectedClient ? 
                          [
                            selectedClient.address || selectedClient.street || selectedClient.address_line1,
                            selectedClient.address_line2,
                            selectedClient.city, 
                            selectedClient.state, 
                            selectedClient.zipcode || selectedClient.zip || selectedClient.zip_code
                          ].filter(Boolean).join(', ') : 'N/A';
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <Input value={address} disabled className="bg-gray-100" />
                          </div>
                        );
                      }

                      if (field.type === 'client_intake_date') {
                        const intakeDate = selectedClient?.intake_completed_at || selectedClient?.created_at || 'N/A';
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <Input value={intakeDate !== 'N/A' ? new Date(intakeDate).toLocaleDateString() : 'N/A'} disabled className="bg-gray-100" />
                          </div>
                        );
                      }
                      if (field.type === 'client_last_appointment') {
                        const clientAppts = appointments.filter(a => a.client_id === selectedClient?.id && a.status === 'completed');
                        const lastAppt = clientAppts.sort((a, b) => new Date(b.start_time) - new Date(a.start_time))[0];
                        const lastApptDate = lastAppt ? new Date(lastAppt.start_time).toLocaleDateString() : 'No appointments';
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <Input value={lastApptDate} disabled className="bg-gray-100" />
                          </div>
                        );
                      }
                      if (field.type === 'client_next_appointment') {
                        const futureAppts = appointments.filter(a => 
                          a.client_id === selectedClient?.id && 
                          new Date(a.start_time) > new Date() &&
                          a.status !== 'cancelled' && a.status !== 'client_cancelled'
                        );
                        const nextAppt = futureAppts.sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];
                        const nextApptDate = nextAppt ? new Date(nextAppt.start_time).toLocaleDateString() + ' ' + new Date(nextAppt.start_time).toLocaleTimeString() : 'No upcoming appointments';
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <Input value={nextApptDate} disabled className="bg-gray-100" />
                          </div>
                        );
                      }

                      if (field.type === 'client_diagnoses') {
                        // Get the selected client for diagnoses - check multiple sources
                        const clientId = selectedNoteClients[0];
                        const diagnosesClient = allClientsForLookup?.find(c => c.id === clientId) 
                          || clients?.find(c => c.id === clientId) 
                          || (selectedClient?.id === clientId ? selectedClient : null);
                        
                        // All diagnoses for this client
                        const allClientDiagnoses = clientDiagnoses?.filter(d => d.client_id === clientId) || [];
                        
                        // Filter by selected provider (default) or show all
                        const providerDiagnoses = allClientDiagnoses.filter(d => 
                          d.assigned_by === selectedNoteProvider || 
                          d.provider_ids?.includes(selectedNoteProvider)
                        );
                        const hasOtherProviderDx = allClientDiagnoses.length > providerDiagnoses.length;
                        
                        // Use toggle state to determine which diagnoses to show
                        const filteredDiagnoses = showAllDiagnoses ? allClientDiagnoses : providerDiagnoses;
                        
                        // Helper to get user name
                        const getDiagUserName = (userId, cachedName) => {
                          if (cachedName) return cachedName;
                          const foundUser = users.find(u => u.id === userId);
                          return foundUser ? (foundUser.full_name || `${foundUser.first_name || ''} ${foundUser.last_name || ''}`.trim()) : 'Unknown';
                        };
                        
                        return (
                          <div key={index} style={fieldStyle}>
                            <div className="flex items-center justify-between mb-1">
                              <Label>{field.label}</Label>
                              {/* Always show toggle - allows filtering by selected provider */}
                              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={showAllDiagnoses}
                                  onChange={(e) => setShowAllDiagnoses(e.target.checked)}
                                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 h-3.5 w-3.5"
                                  data-testid="show-all-diagnoses-toggle"
                                />
                                <span>Show all providers {hasOtherProviderDx ? `(${allClientDiagnoses.length})` : ''}</span>
                              </label>
                            </div>
                            <div className="border rounded p-2 bg-blue-50">
                              {!showAllDiagnoses && hasOtherProviderDx && (
                                <p className="text-xs text-gray-500 mb-2 italic">
                                  Showing {providerDiagnoses.length} diagnosis(es) for selected provider. 
                                  {allClientDiagnoses.length - providerDiagnoses.length} more from other providers.
                                </p>
                              )}
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b bg-gray-100">
                                    <th className="text-left p-1">Code</th>
                                    <th className="text-left p-1">Description</th>
                                    <th className="text-left p-1">Tag</th>
                                    <th className="text-left p-1">Date</th>
                                    <th className="text-left p-1">Provider(s)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredDiagnoses.length > 0 ? (
                                    filteredDiagnoses.map((diag, idx) => (
                                      <tr key={idx} className={showAllDiagnoses && diag.assigned_by !== selectedNoteProvider && !diag.provider_ids?.includes(selectedNoteProvider) ? 'bg-gray-50 text-gray-500' : ''}>
                                        <td className="p-1">{diag.code}</td>
                                        <td className="p-1">{diag.description}</td>
                                        <td className="p-1">
                                          {diag.diagnosis_tag ? (
                                            <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${
                                              diag.diagnosis_tag === 'differential' ? 'bg-purple-100 text-purple-700' :
                                              diag.diagnosis_tag === 'working' ? 'bg-amber-100 text-amber-700' :
                                              diag.diagnosis_tag === 'rule_out' ? 'bg-red-100 text-red-700' :
                                              diag.diagnosis_tag === 'provisional' ? 'bg-sky-100 text-sky-700' :
                                              'bg-gray-100 text-gray-600'
                                            }`}>
                                              {diag.diagnosis_tag === 'differential' ? 'Diff' : diag.diagnosis_tag === 'working' ? 'Wrk' : diag.diagnosis_tag === 'rule_out' ? 'R/O' : diag.diagnosis_tag === 'provisional' ? 'Prov' : diag.diagnosis_tag}
                                            </span>
                                          ) : '-'}
                                        </td>
                                        <td className="p-1">{diag.start_date ? fmtLocalDate(diag.start_date) : 'N/A'}</td>
                                        <td className="p-1">{diag.provider_names?.length > 0 ? diag.provider_names.join(', ') : getDiagUserName(diag.assigned_by, diag.assigned_by_name)}</td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr><td colSpan="5" className="p-1 text-center text-gray-500">
                                      {allClientDiagnoses.length > 0 
                                        ? 'No diagnoses for this provider. Check "Show all providers" to see others.' 
                                        : 'No diagnoses on file'}
                                    </td></tr>
                                  )}
                                </tbody>
                              </table>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="mt-2 border-teal-300 text-teal-700 hover:bg-teal-50"
                                disabled={!diagnosesClient}
                                data-testid="add-diagnosis-from-note-template-btn"
                                onClick={() => {
                                  void 0 && console.log('[AddDiagnosis Template] Button clicked, diagnosesClient:', diagnosesClient);
                                  if (diagnosesClient) {
                                    void 0 && console.log('[AddDiagnosis Template] Setting selectedClient:', diagnosesClient.id, diagnosesClient.full_name);
                                    setSelectedClient(diagnosesClient);
                                    setTimeout(() => {
                                      void 0 && console.log('[AddDiagnosis Template] Setting showDiagnosisForm to true');
                                      setShowDiagnosisForm(true);
                                    }, 50);
                                  } else {
                                    toast.error('Unable to add diagnosis - client data not loaded. Please refresh the page.');
                                  }
                                }}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Add Diagnosis
                              </Button>
                            </div>
                          </div>
                        );
                      }
                      
                      // Client Diagnoses Selector - interactive multi-select
                      if (field.type === 'client_diagnoses_selector') {
                        // Get the selected client - check multiple sources
                        const clientId = selectedNoteClients[0];
                        const selectorClient = allClientsForLookup?.find(c => c.id === clientId) 
                          || clients?.find(c => c.id === clientId) 
                          || (selectedClient?.id === clientId ? selectedClient : null);
                        void 0 && console.log('[client_diagnoses_selector] Looking up client:', clientId, 'Found:', selectorClient?.full_name || 'NOT FOUND');
                        const availableDiagnoses = clientDiagnoses?.filter(d => d.client_id === clientId && d.is_active) || [];
                        const fieldKey = field.id;
                        const selectedDiagnosisIds = newNoteFormData[fieldKey] || [];
                        
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <div className="border rounded p-3 bg-teal-50">
                              <p className="text-xs text-gray-600 mb-2">Select diagnoses to include in this note:</p>
                              <div className="border rounded bg-white p-2 max-h-48 overflow-y-auto">
                                {availableDiagnoses.length > 0 ? (
                                  availableDiagnoses.map((dx) => (
                                    <label key={dx.id} className="flex items-center gap-2 py-1.5 px-2 hover:bg-gray-50 cursor-pointer rounded">
                                      <input
                                        type="checkbox"
                                        checked={selectedDiagnosisIds.includes(dx.id)}
                                        onChange={(e) => {
                                          setNewNoteFormData(prev => {
                                            const current = prev[fieldKey] || [];
                                            if (e.target.checked) {
                                              return { ...prev, [fieldKey]: [...current, dx.id] };
                                            } else {
                                              return { ...prev, [fieldKey]: current.filter(id => id !== dx.id) };
                                            }
                                          });
                                        }}
                                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                      />
                                      <span className="text-sm flex-1 flex items-center gap-1">
                                        <code className="font-mono text-xs bg-gray-100 px-1 rounded mr-1">{dx.code}</code>
                                        {dx.description}
                                        {dx.diagnosis_tag && (
                                          <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${
                                            dx.diagnosis_tag === 'differential' ? 'bg-purple-100 text-purple-700' :
                                            dx.diagnosis_tag === 'working' ? 'bg-amber-100 text-amber-700' :
                                                        dx.diagnosis_tag === 'rule_out' ? 'bg-red-100 text-red-700' :
                                            dx.diagnosis_tag === 'provisional' ? 'bg-sky-100 text-sky-700' :
                                            'bg-gray-100 text-gray-600'
                                          }`}>
                                            {dx.diagnosis_tag === 'differential' ? 'Diff' : dx.diagnosis_tag === 'working' ? 'Wrk' : dx.diagnosis_tag === 'rule_out' ? 'R/O' : dx.diagnosis_tag === 'provisional' ? 'Prov' : dx.diagnosis_tag}
                                          </span>
                                        )}
                                      </span>
                                      {dx.is_primary && <span className="text-xs bg-teal-100 text-teal-700 px-1 rounded">Primary</span>}
                                    </label>
                                  ))
                                ) : (
                                  <p className="text-sm text-gray-400 italic py-2">No active diagnoses on file</p>
                                )}
                              </div>
                              {selectedDiagnosisIds.length > 0 && (
                                <p className="text-xs text-teal-600 mt-2">{selectedDiagnosisIds.length} diagnosis(es) selected</p>
                              )}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="mt-2 border-teal-300 text-teal-700 hover:bg-teal-50"
                                disabled={!selectorClient}
                                data-testid="add-diagnosis-from-create-note-btn"
                                onClick={() => {
                                  if (selectorClient) {
                                    setSelectedClient(selectorClient);
                                    setTimeout(() => {
                                      setShowDiagnosisForm(true);
                                    }, 0);
                                  } else {
                                    toast.error('Unable to add diagnosis - client data not loaded. Please refresh the page.');
                                  }
                                }}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Add New Diagnosis
                              </Button>
                            </div>
                          </div>
                        );
                      }
                      
                      if (field.type === 'client_medications') {
                        // Handler to discontinue a medication from within the note
                        const handleDiscontinueMed = async (med) => {
                          if (!window.confirm(`Discontinue ${med.medication_name}?`)) return;
                          try {
                            await axios.patch(`${API}/medications/${med.id}/discontinue`);
                            toast.success(`${med.medication_name} discontinued`);
                            // Refresh medications list
                            if (selectedClient?.id) {
                              const medsResponse = await axios.get(`${API}/clients/${selectedClient.id}/medications`);
                              setClientMedications(prev => {
                                const otherMeds = prev.filter(m => m.client_id !== selectedClient.id);
                                return [...otherMeds, ...(medsResponse.data || [])];
                              });
                            }
                          } catch (error) {
                            console.error('Failed to discontinue medication:', error);
                            toast.error('Failed to discontinue medication');
                          }
                        };
                        
                        const activeMeds = clientMedications?.filter(m => m.is_active === true) || [];
                        
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <div className="border rounded p-2 bg-gray-100">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b">
                                    <th className="text-left p-1">Medication</th>
                                    <th className="text-left p-1">Dosage</th>
                                    <th className="text-left p-1">Frequency</th>
                                    <th className="text-left p-1">Prescriber</th>
                                    <th className="text-left p-1">Purpose</th>
                                    <th className="text-center p-1 w-16">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {activeMeds.length > 0 ? (
                                    activeMeds.map((med, idx) => (
                                      <tr key={idx}>
                                        <td className="p-1">{med.medication_name}</td>
                                        <td className="p-1">{med.dosage}</td>
                                        <td className="p-1">{med.frequency}</td>
                                        <td className="p-1">{med.prescriber || 'N/A'}</td>
                                        <td className="p-1">{med.purpose || '-'}</td>
                                        <td className="p-1 text-center">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 px-1.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => handleDiscontinueMed(med)}
                                            title="Discontinue medication"
                                          >
                                            <X className="h-3 w-3" />
                                          </Button>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr><td colSpan="6" className="p-1 text-center text-gray-500">No medications on file</td></tr>
                                  )}
                                </tbody>
                              </table>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="mt-2 text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
                                disabled={!selectedClient}
                                onClick={() => {
                                  if (selectedClient) {
                                    setShowMedicationForm(true);
                                  } else {
                                    toast.error('Unable to add medication - client data not loaded. Please refresh the page.');
                                  }
                                }}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add Medication
                              </Button>
                            </div>
                          </div>
                        );
                      }
                      if (field.type === 'client_allergies') {
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <DebouncedTextarea 
                              value={selectedClient?.allergies || 'No known allergies'} 
                              disabled 
                              className="bg-gray-100" 
                              rows={2}
                            />
                          </div>
                        );
                      }
                      if (field.type === 'client_insurance') {
                        return (
                          <div key={index} style={fieldStyle}>
                            <Label>{field.label}</Label>
                            <div className="border rounded p-2 bg-gray-100">
                              {selectedClient?.insurances && selectedClient.insurances.length > 0 ? (
                                selectedClient.insurances.map((ins, idx) => (
                                  <div key={idx} className="text-xs mb-2">
                                    <div><strong>{ins.priority}:</strong> {ins.insurance_company}</div>
                                    <div>Member ID: {ins.member_id || 'N/A'}</div>
                                    <div>Group: {ins.group_number || 'N/A'}</div>
                                  </div>
                                ))
                              ) : (
                                <p className="text-xs text-gray-500">No insurance on file</p>
                              )}
                            </div>
                          </div>
                        );
                      }
                      // Default for other client data fields
                      return (
                        <div key={index} style={fieldStyle}>
                          <Label>{field.label}</Label>
                          <Input value="[Auto-populated from client record]" disabled className="bg-gray-100" />
                        </div>
                      );
                    }
                    
                    // Clinical Components - Treatment Plan Goals
                    if (field.type === 'treatment_plan_goals') {
                      return (
                        <div key={index} style={{...fieldStyle, width: '100%'}}>
                          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Target className="h-5 w-5 text-purple-600" />
                              <div>
                                <Label className="font-medium text-purple-900">Treatment Plan Goals</Label>
                                {linkedGoals.length > 0 && linkedGoals[0].provider_name && (
                                  <p className="text-xs text-purple-600 italic">(developed by {linkedGoals[0].provider_name})</p>
                                )}
                              </div>
                              {linkedGoals.length > 0 && (
                                <span className="bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full text-xs font-medium">
                                  {linkedGoals.length} linked
                                </span>
                              )}
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="ml-auto border-purple-300 text-purple-700 hover:bg-purple-100"
                                onClick={() => {
                                  // Open link goals dialog
                                  if (selectedNoteClients.length > 0) {
                                    fetchTreatmentPlanGoals(selectedNoteClients[0]);
                                    setShowLinkGoalsDialog(true);
                                  } else {
                                    toast.error('Please select a client first');
                                  }
                                }}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Link Goals
                              </Button>
                            </div>
                            {linkedGoals.length > 0 ? (
                              <div className="space-y-2">
                                {linkedGoals.map((goal, gIdx) => (
                                  <div key={goal.goal_id || gIdx} className="bg-white rounded border border-purple-200 p-3">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1 space-y-1">
                                        {/* Provider who developed the goal */}
                                        {goal.provider_name && (
                                          <div className="text-xs text-purple-600 mb-1">
                                            <span className="font-medium">Developed by:</span> {goal.provider_name}
                                          </div>
                                        )}
                                        
                                        {/* PAN */}
                                        {goal.pan_name && (
                                          <div className="text-sm">
                                            <span className="font-semibold text-purple-800">PAN #{goal.pan_number || (gIdx + 1)}:</span>{' '}
                                            <span className="text-gray-800">{goal.pan_name}</span>
                                          </div>
                                        )}
                                        
                                        {/* Goal */}
                                        <div className="text-sm">
                                          <span className="font-semibold text-purple-800">Goal #{gIdx + 1}:</span>{' '}
                                          <span className="text-gray-800">{goal.goal_text}</span>
                                        </div>
                                        
                                        {/* Goal in Client's Words */}
                                        {goal.goal_in_clients_words && (
                                          <div className="text-sm">
                                            <span className="font-semibold text-purple-800">Goal in Client's Words:</span>{' '}
                                            <span className="text-gray-800 italic">"{goal.goal_in_clients_words}"</span>
                                          </div>
                                        )}
                                        
                                        {/* Objectives with intervention */}
                                        {goal.objectives && goal.objectives.length > 0 && (
                                          <div className="text-sm">
                                            <div className="mt-1 pl-3 border-l-2 border-purple-200 space-y-2">
                                              {goal.objectives.map((obj, objIdx) => (
                                                <div key={obj.objective_id || objIdx} className="text-xs text-gray-600">
                                                  <span className="font-bold text-blue-600">Objective #{objIdx + 1}:</span> {obj.objective_text}
                                                  {obj.responsible_party && (
                                                    <span className="text-gray-400 ml-1">({obj.responsible_party})</span>
                                                  )}
                                                  {obj.interventions?.length > 0 && (
                                                    <div className="mt-1 pl-3 space-y-0.5">
                                                      {obj.interventions.map((intervention, intIdx) => (
                                                        <div key={intIdx} className="text-gray-500 flex items-baseline gap-1 flex-wrap">
                                                          <span className="text-teal-600 font-medium">Intervention #{intIdx + 1}:</span>
                                                          <span className="font-medium text-teal-700">{typeof intervention === 'string' ? intervention : intervention.name}</span>
                                                          {typeof intervention === 'object' && intervention.description && (
                                                            <span className="text-gray-500">: {intervention.description}</span>
                                                          )}
                                                          {typeof intervention === 'object' && intervention.modality && (
                                                            <span className="text-gray-400">Modality: {intervention.modality}</span>
                                                          )}
                                                          {typeof intervention === 'object' && intervention.frequency && (
                                                            <span className="text-gray-400">Frequency: {intervention.frequency}</span>
                                                          )}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                  {!obj.interventions?.length && obj.intervention && (
                                                    <div className="text-gray-400 ml-1">• {obj.intervention}</div>
                                                  )}
                                                  {obj.treatment_description && (
                                                    <div className="text-gray-400 mt-0.5">
                                                      <span className="font-medium text-gray-500">Treatment Details:</span> {obj.treatment_description}
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Service Type badge */}
                                        {goal.service_type && (
                                          <div className="mt-2">
                                            <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                                              {goal.service_type}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                                        onClick={() => setLinkedGoals(prev => prev.filter(g => g.goal_id !== goal.goal_id))}
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-purple-700 italic">
                                Click "Link Goals" to pull in goals from active treatment plans.
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    }
                    
                    // Clinical Components - Consent Affirmation
                    if (field.type === 'consent_affirmation') {
                      return (
                        <div key={index} style={{...fieldStyle, width: '100%'}}>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Building className="h-5 w-5 text-blue-600" />
                              <Label className="font-medium text-blue-900">Consent Affirmation</Label>
                              {telehealthConsent?.pos_code && telehealthConsent?.modality && (
                                <span className="bg-green-200 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium">
                                  ✓ Confirmed
                                </span>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-3 gap-4 mb-4">
                              <div>
                                <Label className="text-sm text-gray-700 mb-1 block">Provider Location</Label>
                                <Select
                                  value={telehealthConsent?.provider_location || ''}
                                  onValueChange={(value) => setTelehealthConsent(prev => ({
                                    ...prev,
                                    provider_location: value
                                  }))}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select location..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[...(locations || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(loc => (
                                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                    ))}
                                    <SelectItem value="home">Provider's Home</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-sm text-gray-700 mb-1 block">Place of Service</Label>
                                <Select
                                  value={telehealthConsent?.pos_code || ''}
                                  onValueChange={(value) => {
                                    const posObj = (getEffectivePosCodes(organizations, posCodes)).find(p => p.code === value);
                                    setTelehealthConsent(prev => ({
                                      ...prev,
                                      pos_code: value,
                                      pos_description: posObj?.description || ''
                                    }));
                                  }}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select POS..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getEffectivePosCodes(organizations, posCodes).map(pos => (
                                      <SelectItem key={pos.code} value={pos.code}>{pos.code} - {pos.description}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-sm text-gray-700 mb-1 block">Session Modality</Label>
                                <Select
                                  value={telehealthConsent?.modality || ''}
                                  onValueChange={(value) => setTelehealthConsent(prev => ({
                                    ...prev,
                                    modality: value,
                                    modifier: value === 'audio_only' ? '93' : value === 'audio_visual' ? '95' : null,
                                    session_modality: value === 'audio_only' ? 'phone' : value === 'audio_visual' ? 'video' : 'in_person'
                                  }))}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select modality..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="in_person">In-Person</SelectItem>
                                    <SelectItem value="audio_visual">Audio/Visual (Video)</SelectItem>
                                    <SelectItem value="audio_only">Audio Only (Phone)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            {/* Consent language — single source of truth via backend */}
                            <ConsentLivePreview
                              consentData={telehealthConsent}
                              token={sessionStorage.getItem('token')}
                            />
                          </div>
                        </div>
                      );
                    }
                    
                    // Special handling for heading, static_text, divider, spacer - no label needed
                    if (field.type === 'heading') {
                      return (
                        <div 
                          key={index} 
                          style={{
                            ...fieldStyle,
                            color: field.fontColor || 'inherit',
                            textAlign: field.textAlign || 'left'
                          }} 
                          className={`${field.isBold !== false ? 'font-bold' : ''} ${field.isItalic ? 'italic' : ''} ${field.isUnderline ? 'underline' : ''} ${field.headingLevel === 'h1' ? 'text-2xl' : field.headingLevel === 'h3' ? 'text-base' : 'text-xl'}`}>
                          {field.label}
                        </div>
                      );
                    }
                    
                    if (field.type === 'static_text') {
                      // Get the first client for variable replacement
                      const clientForVariables = allClientsForLookup.find(c => c.id === selectedNoteClients[0]);
                      // Static text can be stored in field.content OR field.label
                      const rawText = field.content || field.label || '';
                      const processedContent = replaceClientVariables(rawText, clientForVariables);
                      
                      // Build style object for container (alignment and base font size)
                      const containerStyle = {
                        textAlign: field.textAlign || 'left',
                        fontSize: field.fontSize ? `${field.fontSize}px` : '14px'
                      };
                      
                      // Check if content contains HTML tags (from rich text editor)
                      const hasHtmlTags = /<[^>]+>/.test(processedContent);
                      
                      return (
                        <div key={index} style={fieldStyle}>
                          {hasHtmlTags ? (
                            // Render as HTML for rich text content - use prose for consistent line spacing
                            <div 
                              className="prose prose-sm max-w-none" 
                              style={containerStyle}
                              dangerouslySetInnerHTML={{ __html: processedContent }}
                            />
                          ) : (
                            // Render as plain text for legacy content - use prose for consistent line spacing
                            <div 
                              className="prose prose-sm max-w-none" 
                              style={{
                                ...containerStyle,
                                fontWeight: field.fontWeight || 'normal',
                                fontStyle: field.fontStyle || 'normal',
                                textDecoration: field.textDecoration || 'none',
                                color: field.textColor || '#000000',
                                whiteSpace: 'pre-wrap'
                              }}
                            >
                              {processedContent}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    if (field.type === 'divider') {
                      return (
                        <div key={index} style={fieldStyle}>
                          <hr style={{ borderTopWidth: `${field.thickness}px`, borderColor: field.color }} />
                        </div>
                      );
                    }
                    
                    if (field.type === 'client_visibility_divider') {
                      return (
                        <div key={index} style={fieldStyle} className="my-6 flex items-center justify-center">
                          <div className="flex-1 border-t-2 border-dashed border-teal-400"></div>
                          <span className="px-4 text-sm text-teal-600 font-medium whitespace-nowrap">--- CLIENT PORTAL / SUMMARY START ---</span>
                          <div className="flex-1 border-t-2 border-dashed border-teal-400"></div>
                        </div>
                      );
                    }
                    
                    if (field.type === 'spacer') {
                      return (
                        <div key={index} style={fieldStyle}>
                          <div style={{ height: `${field.height}px` }} />
                        </div>
                      );
                    }
                    
                    // Section header - styled section divider
                    if (field.type === 'section_header') {
                      const sectionColor = field.backgroundColor || '#2563eb';
                      return (
                        <div key={index} style={{ ...fieldStyle, width: '100%', minHeight: '40px' }} className="mt-6 mb-3">
                          {field.label ? (
                            <div 
                              className="text-white px-4 py-2 rounded-lg shadow-sm"
                              style={{ backgroundColor: sectionColor }}
                            >
                              <h3 className="text-base font-semibold">{field.label}</h3>
                            </div>
                          ) : (
                            <div 
                              className="border-b-2 min-h-[8px]"
                              style={{ borderColor: sectionColor }}
                            />
                          )}
                        </div>
                      );
                    }
                    
                    // create_task field (Feb 2026) — must short-circuit
                    // BEFORE the generic labeled wrapper below because
                    // the CreateTaskFieldWidget renders its own
                    // title/assignee/due-date/note UI and supplies its
                    // own header — wrapping it in a generic `<Label>`
                    // adds duplicate text.  Branch parity with the
                    // section-template renderField at line 62302.
                    if (field.type === 'create_task') {
                      return (
                        <div key={index} style={fieldStyle}>
                          <CreateTaskFieldWidget
                            field={field}
                            value={newNoteFormData[field.id] || {}}
                            onChange={(next) => handleNewNoteFieldChange(field.id, next)}
                            client={selectedClient}
                            readOnly={false}
                          />
                        </div>
                      );
                    }
                    
                    // Regular editable fields with labels
                    return (
                      <div key={index} style={fieldStyle}>
                        {field.type !== 'checkbox' && (
                          <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
                        )}
                        {field.type === 'text' && (
                          <DebouncedInput 
                            value={newNoteFormData[field.id] || ''} 
                            onChange={(e) => handleNewNoteFieldChange(field.id, e.target.value)}
                            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`} 
                          />
                        )}
                        {field.type === 'email' && (
                          <DebouncedInput 
                            type="email" 
                            value={newNoteFormData[field.id] || ''} 
                            onChange={(e) => handleNewNoteFieldChange(field.id, e.target.value)}
                            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`} 
                          />
                        )}
                        {field.type === 'phone' && (
                          <DebouncedInput 
                            type="tel" 
                            value={newNoteFormData[field.id] || ''} 
                            onChange={(e) => handleNewNoteFieldChange(field.id, e.target.value)}
                            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`} 
                          />
                        )}
                        {field.type === 'date' && (
                          <Input 
                            type="date" 
                            value={newNoteFormData[field.id] || ''} 
                            onChange={(e) => handleNewNoteFieldChange(field.id, e.target.value)}
                          />
                        )}
                        {field.type === 'time' && (
                          <Input 
                            type="time" 
                            value={newNoteFormData[field.id] || ''} 
                            onChange={(e) => handleNewNoteFieldChange(field.id, e.target.value)}
                          />
                        )}
                        {field.type === 'number' && (
                          <Input 
                            type="number" 
                            value={newNoteFormData[field.id] || ''} 
                            onChange={(e) => handleNewNoteFieldChange(field.id, e.target.value)}
                            min={field.min}
                            max={field.max}
                            step={field.step || 1}
                          />
                        )}
                        {field.type === 'rating' && (
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => handleNewNoteFieldChange(field.id, star)}
                                className={`text-2xl ${(newNoteFormData[field.id] || 0) >= star ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
                              >
                                ★
                              </button>
                            ))}
                            <span className="ml-2 text-sm text-gray-500">
                              {newNoteFormData[field.id] ? `${newNoteFormData[field.id]}/5` : 'Not rated'}
                            </span>
                          </div>
                        )}
                        {field.type === 'textarea' && (
                          (() => {
                            // Check if this is a PAN field
                            const isPanField = field.id?.startsWith('pan_') && field.label?.includes('Prioritized Need');
                            
                            if (isPanField) {
                              const currentValue = newNoteFormData[field.id] || '';
                              const isCustomValue = currentValue && !availablePANs.some(p => p.name === currentValue);
                              
                              return (
                                <div className="space-y-2">
                                  <Select 
                                    value={isCustomValue ? '__custom__' : currentValue}
                                    onValueChange={(value) => {
                                      if (value === '__custom__') {
                                        // Clear to allow custom input
                                        handleNewNoteFieldChange(field.id, '');
                                      } else if (value === '__clear__') {
                                        handleNewNoteFieldChange(field.id, '');
                                      } else {
                                        handleNewNoteFieldChange(field.id, value);
                                      }
                                    }}
                                  >
                                    <SelectTrigger className={currentValue ? 'border-green-300 bg-green-50' : ''}>
                                      <SelectValue placeholder="Select a PAN or write custom..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__clear__">
                                        <span className="text-gray-400">-- Clear selection --</span>
                                      </SelectItem>
                                      <SelectItem value="__custom__">
                                        <span className="text-blue-600 font-medium">✏️ Write custom PAN...</span>
                                      </SelectItem>
                                      {/* Group PANs by category */}
                                      {['safety', 'emotional', 'behavioral', 'relational', 'cognitive', 'developmental'].map(category => {
                                        const categoryPans = availablePANs.filter(p => p.category === category);
                                        if (categoryPans.length === 0) return null;
                                        return (
                                          <div key={category}>
                                            <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 uppercase">
                                              {category}
                                            </div>
                                            {categoryPans.map((pan) => (
                                              <SelectItem key={pan.id} value={pan.name}>
                                                {pan.is_safety_related && '⚠️ '}{pan.name}
                                              </SelectItem>
                                            ))}
                                          </div>
                                        );
                                      })}
                                    </SelectContent>
                                  </Select>
                                  {/* Show custom input if custom selected or value doesn't match any PAN */}
                                  {(isCustomValue || currentValue === '') && (
                                    <DebouncedTextarea 
                                      value={currentValue}
                                      onChange={(e) => handleNewNoteFieldChange(field.id, e.target.value)}
                                      placeholder="Write a custom prioritized assessed need..."
                                      rows={2}
                                      className="border-blue-200 focus:border-blue-400"
                                    />
                                  )}
                                  {currentValue && !isCustomValue && (
                                    <p className="text-xs text-green-600">✓ Selected from library</p>
                                  )}
                                </div>
                              );
                            }
                            
                            // Regular textarea for non-PAN fields - use DebouncedTextarea for performance
                            const rowCount = parseInt(field.rows) || 4;
                            return (
                              <DebouncedTextarea 
                                value={newNoteFormData[field.id] || ''} 
                                onChange={(e) => handleNewNoteFieldChange(field.id, e.target.value)}
                                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`} 
                                rows={rowCount}
                                debounceMs={100}
                                className="resize-y"
                                style={rowCount === 1 ? { minHeight: 'auto' } : {}}
                                onInput={(e) => {
                                  e.target.style.height = 'auto';
                                  e.target.style.height = e.target.scrollHeight + 'px';
                                }}
                              />
                            );
                          })()
                        )}
                        {field.type === 'select' && (
                          <Select 
                            value={newNoteFormData[field.id] || ''} 
                            onValueChange={(value) => handleNewNoteFieldChange(field.id, value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {(field.options || []).map((option, idx) => (
                                <SelectItem key={idx} value={typeof option === 'object' ? (option.value || option.label) : option}>
                                  {getOptionLabel(option)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {field.type === 'pan_select' && (
                          (() => {
                            const currentValue = newNoteFormData[field.id] || '';
                            const isCustomValue = currentValue && !availablePANs.some(p => p.name === currentValue);
                            
                            return (
                              <div className="space-y-2">
                                <Select 
                                  value={isCustomValue ? '__custom__' : currentValue}
                                  onValueChange={(value) => {
                                    if (value === '__custom__') {
                                      handleNewNoteFieldChange(field.id, '');
                                    } else if (value === '__clear__') {
                                      handleNewNoteFieldChange(field.id, '');
                                    } else {
                                      handleNewNoteFieldChange(field.id, value);
                                    }
                                  }}
                                >
                                  <SelectTrigger className={currentValue && !isCustomValue ? 'border-green-300 bg-green-50' : ''}>
                                    <SelectValue placeholder="Select a PAN or write custom..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__clear__">
                                      <span className="text-gray-400">-- Clear selection --</span>
                                    </SelectItem>
                                    <SelectItem value="__custom__">
                                      <span className="text-blue-600 font-medium">✏️ Write custom need...</span>
                                    </SelectItem>
                                    <div className="border-t my-1"></div>
                                    {availablePANs.map((pan) => (
                                      <SelectItem key={pan.id} value={pan.name}>
                                        {pan.is_safety_related && '⚠️ '}{pan.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {(isCustomValue || currentValue === '') && (
                                  <DebouncedTextarea 
                                    value={currentValue}
                                    onChange={(e) => handleNewNoteFieldChange(field.id, e.target.value)}
                                    placeholder="Write a custom prioritized need..."
                                    rows={2}
                                    className="border-blue-200 focus:border-blue-400"
                                  />
                                )}
                                {currentValue && !isCustomValue && (
                                  <p className="text-xs text-green-600">✓ Selected from PAN library</p>
                                )}
                                {isCustomValue && currentValue && (
                                  <p className="text-xs text-blue-600">✏️ Custom entry</p>
                                )}
                              </div>
                            );
                          })()
                        )}
                        {field.type === 'radio' && (
                          <div className="grid gap-3 grid-cols-1 mt-1 w-full">
                            {(field.options || []).map((option, idx) => {
                              const optValue = typeof option === 'object' ? (option.value || option.label) : option;
                              const optLabel = typeof option === 'object' ? option.label : option;
                              const currentValue = newNoteFormData[field.id];
                              
                              // Handle both single and multi-select modes
                              const isMultiSelect = field.allowMultiple === true;
                              
                              // Force string comparison to avoid type mismatches
                              const stringOptValue = String(optValue);
                              const currentValuesArray = isMultiSelect 
                                ? (Array.isArray(currentValue) ? currentValue.map(String) : (currentValue ? [String(currentValue)] : []))
                                : [];
                              
                              const isSelected = isMultiSelect 
                                ? currentValuesArray.includes(stringOptValue)
                                : (String(currentValue) === stringOptValue);
                              
                              return (
                                <div key={idx} className="flex items-start space-x-3 min-w-0">
                                  <input
                                    type={isMultiSelect ? "checkbox" : "radio"}
                                    id={`${field.id}-${idx}`}
                                    name={field.id}
                                    checked={isSelected}
                                    onChange={() => {
                                      if (isMultiSelect) {
                                        // Multi-select: toggle in array with string values
                                        const newValues = isSelected 
                                          ? currentValuesArray.filter(v => v !== stringOptValue)
                                          : [...currentValuesArray, stringOptValue];
                                        handleNewNoteFieldChange(field.id, newValues);
                                      } else {
                                        // Single select: set value
                                        handleNewNoteFieldChange(field.id, optValue);
                                      }
                                    }}
                                    className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600"
                                  />
                                  <label 
                                    htmlFor={`${field.id}-${idx}`}
                                    className={`text-base md:text-lg font-normal cursor-pointer leading-snug break-words whitespace-normal min-w-0 flex-1 ${isSelected ? 'text-blue-700 font-medium' : 'text-gray-700'}`}
                                  >
                                    {optLabel}
                                  </label>
                                </div>
                              );
                            })}
                            
                            {/* Linked Form Banner - appears when trigger condition is met */}
                            {(() => {
                              const currentFieldValue = newNoteFormData[field.id];
                              const linkedFormConfig = selectedTemplate?.linked_forms?.find(lf => 
                                lf.trigger_field_id === field.id && lf.trigger_value === currentFieldValue
                              );
                              
                              if (linkedFormConfig) {
                                const isAdded = linkedFormsAdded[linkedFormConfig.form_name];
                                return (
                                  <div className={`w-full mt-3 p-3 rounded-lg border-l-4 ${isAdded ? 'bg-green-50 border-green-500' : 'bg-amber-50 border-amber-500'}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg">{isAdded ? '✅' : '⚠️'}</span>
                                        <span className="text-sm font-medium">
                                          {isAdded 
                                            ? `${linkedFormConfig.form_name} added to chart`
                                            : `Based on this answer, please complete a ${linkedFormConfig.form_name}`
                                          }
                                        </span>
                                      </div>
                                      {!isAdded && linkedFormConfig.linked_template_id && selectedNoteClients.length > 0 && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-300"
                                          onClick={async () => {
                                            try {
                                              // Get the linked template
                                              const templateResponse = await axios.get(`${API}/form-templates/${linkedFormConfig.linked_template_id}`);
                                              const template = templateResponse.data;
                                              
                                              // Prepare initial content from template fields
                                              const initialContent = {
                                                template_id: template.id,
                                                template_name: template.name,
                                                fields: template.form_fields?.map(f => ({
                                                  ...f,
                                                  value: f.type === 'checkbox_group' || f.type === 'multiselect' ? [] : ''
                                                })) || []
                                              };
                                              
                                              // Create the note for the client
                                              const noteData = {
                                                client_id: selectedNoteClients[0],
                                                template_id: template.id,
                                                template_name: template.name,
                                                content: JSON.stringify(initialContent),
                                                status: 'draft',
                                                provider_id: selectedNoteProvider || user.id,
                                                billable: template.billable || false,
                                                requires_supervisor_signature: template.requires_supervisor_signature || false
                                              };
                                              
                                              const response = await axios.post(`${API}/notes`, noteData);
                                              const newNote = response.data;
                                              
                                              // Create a task if configured (non-blocking - don't fail if task creation fails)
                                              if (linkedFormConfig.create_task) {
                                                try {
                                                  const client = allClientsForLookup.find(c => c.id === selectedNoteClients[0]);
                                                  const assignedTo = selectedNoteProvider || user?.id;
                                                  
                                                  if (assignedTo) {
                                                    const taskData = {
                                                      title: `Complete ${linkedFormConfig.form_name} for ${client?.full_name || 'Client'}`,
                                                      description: `This form was triggered from the Comprehensive Assessment. Please complete and sign.`,
                                                      category: 'documentation',
                                                      assigned_to: assignedTo,
                                                      client_id: selectedNoteClients[0],
                                                      due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                                                      priority: 'medium'
                                                    };
                                                    await axios.post(`${API}/tasks`, taskData);
                                                  } else {
                                                    console.warn('No provider selected, skipping task creation');
                                                  }
                                                } catch (taskError) {
                                                  console.error('Task creation failed (note was still added):', taskError);
                                                  // Don't throw - the note was added successfully
                                                }
                                              }
                                              
                                              // Track that this linked form was added
                                              setLinkedFormsAdded(prev => ({
                                                ...prev,
                                                [linkedFormConfig.form_name]: newNote.id
                                              }));
                                              
                                              toast.success(`${linkedFormConfig.form_name} added to client's chart${linkedFormConfig.create_task ? ' and task created' : ''}`);
                                              refreshNotes(); refreshTodos();
                                            } catch (error) {
                                              console.error('Error adding linked form:', error);
                                              console.error('Error response:', error.response);
                                              console.error('Error data:', error.response?.data);
                                              const errorMsg = typeof error.response?.data?.detail === 'string' 
                                                ? error.response.data.detail 
                                                : (error.message || 'Unknown error');
                                              toast.error(`Failed to add ${linkedFormConfig.form_name}: ${errorMsg}`);
                                            }
                                          }}
                                        >
                                          <Plus className="h-3 w-3 mr-1" />
                                          Add to Chart
                                        </Button>
                                      )}
                                      {!isAdded && !linkedFormConfig.linked_template_id && (
                                        <span className="text-xs text-amber-600 italic">Template not yet configured</span>
                                      )}
                                      {isAdded && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="bg-green-100 hover:bg-green-200 text-green-800 border-green-300"
                                          onClick={async () => {
                                            const linkedNoteId = linkedFormsAdded[linkedFormConfig.form_name];
                                            // Fetch the latest note data
                                            try {
                                              const response = await axios.get(`${API}/notes/${linkedNoteId}`);
                                              const linkedNote = response.data;
                                              
                                              // Store current Create Note dialog state so we can return
                                              setReturnToCreateNote({
                                                template: selectedTemplate,
                                                clients: selectedNoteClients,
                                                provider: selectedNoteProvider,
                                                status: selectedNoteStatus,
                                                formData: newNoteFormData,
                                                linkedFormsAdded: linkedFormsAdded,
                                                parentTemplateName: selectedTemplate?.name || 'Comprehensive Assessment'
                                              });
                                              
                                              setShowAddNote(false); // Close the create dialog
                                              // Open the linked note with proper content initialization
                                              openNote(linkedNote);
                                            } catch (error) {
                                              console.error('Error fetching linked note:', error);
                                              toast.error('Could not open the linked form');
                                            }
                                          }}
                                        >
                                          <ExternalLink className="h-3 w-3 mr-1" />
                                          View Form
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                        {field.type === 'checkbox' && (
                          <div className="flex items-center space-x-3">
                            <input 
                              type="checkbox" 
                              id={`create-note-checkbox-${field.id}`}
                              className="w-5 h-5 flex-shrink-0"
                              checked={newNoteFormData[field.id] === true || newNoteFormData[field.id] === 'true'}
                              onChange={(e) => handleNewNoteFieldChange(field.id, e.target.checked)}
                            />
                            <Label htmlFor={`create-note-checkbox-${field.id}`} className="text-base md:text-lg cursor-pointer">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                          </div>
                        )}
                        {field.type === 'checkbox_group' && (
                          <div className={`grid gap-3 w-full ${
                            field.checkboxColumns === '3' ? 'grid-cols-1 sm:grid-cols-3' :
                            field.checkboxColumns === '2' ? 'grid-cols-1 sm:grid-cols-2' :
                            'grid-cols-1'
                          }`}>
                            {(field.options || []).map((option, idx) => {
                              const optValue = typeof option === 'object' ? (option.value || option.label) : option;
                              const currentValues = Array.isArray(newNoteFormData[field.id]) ? newNoteFormData[field.id] : [];
                              return (
                                <div key={idx} className="flex items-start space-x-3">
                                  <input 
                                    type="checkbox" 
                                    id={`${field.id}-${idx}`} 
                                    className="mt-1 h-5 w-5 flex-shrink-0"
                                    checked={currentValues.includes(optValue) || currentValues.includes(getOptionLabel(option))}
                                    onChange={(e) => {
                                      let newValues = [...currentValues];
                                      if (e.target.checked) {
                                        newValues.push(optValue);
                                      } else {
                                        newValues = newValues.filter(v => v !== optValue && v !== getOptionLabel(option));
                                      }
                                      handleNewNoteFieldChange(field.id, newValues);
                                    }}
                                  />
                                  <label 
                                    htmlFor={`${field.id}-${idx}`} 
                                    className="text-sm sm:text-base font-normal cursor-pointer leading-relaxed"
                                    style={{ wordBreak: 'break-word' }}
                                  >
                                    {getOptionLabel(option)}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {(field.type === 'multiselect' || field.type === 'multi-select') && (
                          <div className="relative">
                            {(() => {
                              const currentValues = Array.isArray(newNoteFormData[field.id]) ? newNoteFormData[field.id] : [];
                              const options = field.options || [];
                              
                              if (options.length === 0) {
                                return <div className="text-gray-400 text-sm italic">No options configured</div>;
                              }
                              
                              return (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      role="combobox"
                                      className="w-full justify-between text-left font-normal h-auto min-h-[40px] py-2"
                                    >
                                      <span className="flex flex-wrap gap-1">
                                        {currentValues.length === 0 ? (
                                          <span className="text-gray-500">Select options...</span>
                                        ) : currentValues.length <= 3 ? (
                                          currentValues.map((val, i) => (
                                            <span key={i} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                                              {val}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="text-gray-700">{currentValues.length} selected</span>
                                        )}
                                      </span>
                                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-[400px] p-0 max-h-[300px] overflow-y-auto" align="start">
                                    <div className="p-2 space-y-1">
                                      {options.map((option, idx) => (
                                        <div 
                                          key={idx} 
                                          className="flex items-start space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                                          onClick={() => {
                                            let newValues = [...currentValues];
                                            if (newValues.includes(option)) {
                                              newValues = newValues.filter(v => v !== option);
                                            } else {
                                              newValues.push(option);
                                            }
                                            handleNewNoteFieldChange(field.id, newValues);
                                          }}
                                        >
                                          <input 
                                            type="checkbox" 
                                            checked={currentValues.includes(option)}
                                            onChange={() => {}}
                                            className="h-4 w-4 mt-0.5 shrink-0"
                                          />
                                          <span className="text-sm break-words whitespace-normal">{getOptionLabel(option)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              );
                            })()}
                          </div>
                        )}
                        
                        {/* Group Attendance Field */}
                        {field.type === 'group_attendance' && isGroupNote && (
                          <div className="border rounded-lg p-3 bg-gray-50">
                            <div className="space-y-2">
                              {selectedNoteClients.map(clientId => {
                                const client = allClientsForLookup.find(c => c.id === clientId);
                                if (!client) return null;
                                const attendanceData = newNoteFormData[field.id] || {};
                                return (
                                  <div key={clientId} className="flex items-center justify-between p-2 bg-white rounded border">
                                    <span className="font-medium text-sm">{formatClientName(client)}</span>
                                    <Select
                                      value={attendanceData[clientId] || ''}
                                      onValueChange={(value) => {
                                        handleNewNoteFieldChange(field.id, {
                                          ...attendanceData,
                                          [clientId]: value
                                        });
                                      }}
                                    >
                                      <SelectTrigger className="w-40">
                                        <SelectValue placeholder="Select status" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {(field.options || ['Present', 'Absent - Excused', 'Absent - Unexcused', 'Late']).map(option => (
                                          <SelectItem key={option} value={option}>
                                            {option === 'Present' && '✓ '}{option}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                );
                              })}
                              {selectedNoteClients.length === 0 && (
                                <p className="text-sm text-gray-400 text-center py-4">
                                  Add group members above to track attendance
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Group Member Notes Field */}
                        {field.type === 'group_member_notes' && isGroupNote && (
                          <div className="space-y-3">
                            {selectedNoteClients.map(clientId => {
                              const client = allClientsForLookup.find(c => c.id === clientId);
                              if (!client) return null;
                              const memberNotes = newNoteFormData[field.id] || {};
                              return (
                                <div key={clientId} className="border rounded-lg p-3">
                                  <Label className="text-sm font-medium text-purple-700 mb-2 block">
                                    {formatClientName(client)}
                                  </Label>
                                  <DebouncedTextarea
                                    value={memberNotes[clientId] || ''}
                                    onChange={(e) => {
                                      handleNewNoteFieldChange(field.id, {
                                        ...memberNotes,
                                        [clientId]: e.target.value
                                      });
                                    }}
                                    placeholder={field.placeholder || `Progress notes for ${client.first_name}...`}
                                    rows={3}
                                    className="text-sm"
                                  />
                                </div>
                              );
                            })}
                            {selectedNoteClients.length === 0 && (
                              <p className="text-sm text-gray-400 text-center py-4">
                                Add group members above to write individual notes
                              </p>
                            )}
                          </div>
                        )}
                        
                        {field.type === 'signature' && (
                          <SignatureDisplay
                            value={newNoteFormData[field.id]}
                            label={null}
                            required={field.required}
                            users={users}
                            currentUser={user}
                            onChange={(val) => handleNewNoteFieldChange(field.id, val)}
                            disabled={false}
                            testId={`signature-field-${field.id}`}
                            loadedSignatures={loadedSignatures}
                            setLoadedSignatures={setLoadedSignatures}
                            getSignatureUrl={getSignatureUrl}
                          />
                        )}
                        {/* Send Assessment — quick-send picker while writing a new note (audit fix, Jun 2026) */}
                        {field.type === 'send_assessment' && (
                          <div className="p-3 rounded-lg border border-purple-200 bg-purple-50/50 space-y-1"
                            data-testid={`new-note-send-assessment-${field.id}`}>
                            <p className="text-xs text-gray-600">
                              {field.description || 'Optionally send an assessment to this client (email + portal). Not required to sign or submit this note.'}
                            </p>
                            {selectedNoteClients?.length === 1 ? (
                              <AssessmentQuickSend clientId={selectedNoteClients[0]} />
                            ) : (
                              <p className="text-xs text-gray-400 italic">Select a single client to send an assessment.</p>
                            )}
                          </div>
                        )}
                        {/* File Upload — staff-side upload while writing a new note (audit fix, Jun 2026) */}
                        {field.type === 'file' && (() => {
                          const fileClientId = selectedNoteClients?.length === 1 ? selectedNoteClients[0] : null;
                          return (
                            <div className="mt-1" data-testid={`new-note-file-field-${field.id}`}>
                              {!fileClientId && (
                                <p className="text-xs text-gray-400 italic mb-1">Select a single client to upload a file.</p>
                              )}
                              <FormFileUploadField
                                value={newNoteFormData[field.id]}
                                disabled={!fileClientId}
                                testId={`new-note-file-upload-${field.id}`}
                                onView={async (info) => {
                                  try {
                                    const r = await axios.get(`${API}/documents/${info.document_id}/download?inline=true`, { responseType: 'blob' });
                                    const ct = r.headers['content-type'] || '';
                                    if (ct.includes('application/json')) {
                                      const payload = JSON.parse(await r.data.text());
                                      if (payload?.download_url) { window.open(payload.download_url, '_blank'); return; }
                                    }
                                    window.open(window.URL.createObjectURL(new Blob([r.data], { type: ct || 'application/pdf' })), '_blank');
                                  } catch { toast.error('Could not open file'); }
                                }}
                                onChange={(v) => handleNewNoteFieldChange(field.id, v)}
                                uploadFile={async (file) => {
                                  const fd = new FormData();
                                  fd.append('file', file);
                                  fd.append('description', `Uploaded via note field "${field.label || field.id}"`);
                                  const r = await axios.post(`${API}/clients/${fileClientId}/documents`, fd, {
                                    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
                                  });
                                  return { document_id: r.data.id, filename: r.data.filename || file.name };
                                }}
                              />
                            </div>
                          );
                        })()}
                        {field.type === 'matrix' && (
                          <div className="space-y-2">
                            <div className="border rounded overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-100">
                                  <tr>
                                    {!field.hide_row_labels && <th className="border p-2 text-left font-semibold"></th>}
                                    {(field.columnHeaders || []).map((header, colIdx) => (
                                      <th key={colIdx} className="border p-2 text-left font-semibold">{header}</th>
                                    ))}
                                    <th className="border p-2 w-10"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const matrixData = newNoteFormData[field.id] || {};
                                    const extraRows = matrixData._extraRows || [];
                                    const baseRows = matrixBaseRows(field);
                                    const allRows = [...baseRows, ...extraRows];
                                    
                                    return allRows.map((row, rowIdx) => {
                                      const isExtraRow = rowIdx >= baseRows.length;
                                      return (
                                        <tr key={rowIdx}>
                                          {!field.hide_row_labels && (
                                          <td className="border p-2 font-medium bg-gray-50">
                                            {isExtraRow ? (
                                              <DebouncedInput 
                                                className="h-7 text-xs font-medium"
                                                value={row}
                                                onChange={(e) => {
                                                  const newExtraRows = [...extraRows];
                                                  newExtraRows[rowIdx - baseRows.length] = e.target.value;
                                                  const updatedMatrix = { ...matrixData, _extraRows: newExtraRows };
                                                  handleNewNoteFieldChange(field.id, updatedMatrix);
                                                }}
                                                placeholder="Row label"
                                              />
                                            ) : row}
                                          </td>
                                          )}
                                          {(field.columnHeaders || []).map((col, colIdx) => {
                                            const cellKey = `${rowIdx}-${colIdx}`;
                                            const cellValue = matrixCellValue(field, matrixData, rowIdx, colIdx) || '';
                                            const columnType = field.columnTypes?.[colIdx] || field.cellType || 'text';
                                            
                                            return (
                                              <td key={colIdx} className="border p-1">
                                                {columnType === 'dropdown' ? (
                                                  <Select 
                                                    value={cellValue}
                                                    onValueChange={(value) => {
                                                      const updatedMatrix = { ...matrixData, [cellKey]: value };
                                                      handleNewNoteFieldChange(field.id, updatedMatrix);
                                                    }}
                                                  >
                                                    <SelectTrigger className="h-8 text-xs">
                                                      <SelectValue placeholder="Select" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      {(field.columnDropdownOptions?.[colIdx]?.split(',') || field.dropdownOptions || []).map((option, optIdx) => (
                                                        <SelectItem key={optIdx} value={option.trim()}>{option.trim()}</SelectItem>
                                                      ))}
                                                    </SelectContent>
                                                  </Select>
                                                ) : columnType === 'checkbox' ? (
                                                  <div className="flex justify-center">
                                                    <input
                                                      type="checkbox"
                                                      checked={cellValue === 'true' || cellValue === true}
                                                      onChange={(e) => {
                                                        const updatedMatrix = { ...matrixData, [cellKey]: e.target.checked };
                                                        handleNewNoteFieldChange(field.id, updatedMatrix);
                                                      }}
                                                      className="w-4 h-4"
                                                    />
                                                  </div>
                                                ) : (
                                                  <DebouncedInput 
                                                    className="h-8 text-xs" 
                                                    value={cellValue}
                                                    onChange={(e) => {
                                                      const updatedMatrix = { ...matrixData, [cellKey]: e.target.value };
                                                      handleNewNoteFieldChange(field.id, updatedMatrix);
                                                    }}
                                                    placeholder="Enter text" 
                                                  />
                                                )}
                                              </td>
                                            );
                                          })}
                                          <td className="border p-1">
                                            {isExtraRow && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const newExtraRows = extraRows.filter((_, idx) => idx !== (rowIdx - baseRows.length));
                                                  const updatedMatrix = { ...matrixData, _extraRows: newExtraRows };
                                                  // Remove cell data for this row
                                                  (field.columnHeaders || []).forEach((_, colIdx) => {
                                                    delete updatedMatrix[`${rowIdx}-${colIdx}`];
                                                  });
                                                  handleNewNoteFieldChange(field.id, updatedMatrix);
                                                }}
                                                className="text-red-600 hover:text-red-800 p-1"
                                                title="Remove row"
                                              >
                                                ✕
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    });
                                  })()}
                                </tbody>
                              </table>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const matrixData = newNoteFormData[field.id] || {};
                                const extraRows = matrixData._extraRows || [];
                                const updatedMatrix = { 
                                  ...matrixData, 
                                  _extraRows: [...extraRows, `Additional Row ${extraRows.length + 1}`]
                                };
                                handleNewNoteFieldChange(field.id, updatedMatrix);
                              }}
                              className="w-full"
                            >
              <Plus className="h-4 w-4 mr-2" />
                              Add Row
                            </Button>
                          </div>
                        )}
                        {field.type === 'service_dropdown' && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <div 
                                className="border rounded-md p-2 min-h-[38px] cursor-pointer bg-white hover:bg-gray-50"
                                data-testid="service-dropdown-multiselect"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex flex-wrap gap-1">
                                    {(Array.isArray(newNoteFormData[field.id]) ? newNoteFormData[field.id] : []).length > 0 ? (
                                      (newNoteFormData[field.id] || []).map((val, idx) => {
                                        const service = serviceTypes.find(st => st.id === val);
                                        return (
                                          <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {service?.display_name || service?.name || val}
                                            <button
                                              type="button"
                                              className="ml-1 hover:text-blue-900"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const newValues = (newNoteFormData[field.id] || []).filter(v => v !== val);
                                                handleNewNoteFieldChange(field.id, newValues);
                                              }}
                                            >
                                              ×
                                            </button>
                                          </span>
                                        );
                                      })
                                    ) : (
                                      <span className="text-gray-400 text-sm">
                                        {serviceTypes?.length === 0 ? 'Loading services...' : 'Select services...'}
                                      </span>
                                    )}
                                  </div>
                                  <ChevronDown className="h-4 w-4 text-gray-400" />
                                </div>
                              </div>
                            </PopoverTrigger>
                            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2 max-h-60 overflow-y-auto" align="start">
                              {serviceTypes.filter(st => st.is_active !== false).length > 0 ? (
                                serviceTypes.filter(st => st.is_active !== false).map((service) => (
                                  <div key={service.id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                    onClick={() => {
                                      const currentValues = Array.isArray(newNoteFormData[field.id]) ? newNoteFormData[field.id] : [];
                                      const newValues = currentValues.includes(service.id)
                                        ? currentValues.filter(v => v !== service.id)
                                        : [...currentValues, service.id];
                                      handleNewNoteFieldChange(field.id, newValues);
                                    }}
                                  >
                                    <Checkbox
                                      id={`${field.id}-${service.id}`}
                                      checked={(Array.isArray(newNoteFormData[field.id]) ? newNoteFormData[field.id] : []).includes(service.id)}
                                      onCheckedChange={(checked) => {
                                        const currentValues = Array.isArray(newNoteFormData[field.id]) ? newNoteFormData[field.id] : [];
                                        const newValues = checked
                                          ? [...currentValues, service.id]
                                          : currentValues.filter(v => v !== service.id);
                                        handleNewNoteFieldChange(field.id, newValues);
                                      }}
                                    />
                                    <Label htmlFor={`${field.id}-${service.id}`} className="flex-1 font-normal cursor-pointer">
                                      {service.display_name || service.name}
                                    </Label>
                                  </div>
                                ))
                              ) : (
                                <div className="text-gray-400 text-sm p-2">
                                  {serviceTypes?.length === 0 
                                    ? 'No services loaded. Please refresh or check Admin → Service Types.' 
                                    : 'No active services available'}
                                </div>
                              )}
                            </PopoverContent>
                          </Popover>
                        )}
                        {field.type === 'location_dropdown' && (
                          <Select 
                            value={newNoteFormData[field.id] || ''} 
                            onValueChange={(value) => handleNewNoteFieldChange(field.id, value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a location" />
                            </SelectTrigger>
                            <SelectContent>
                              {[...(locations || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((location) => (
                                <SelectItem key={location.id} value={location.name}>{location.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {field.type === 'provider_dropdown' && (
                          <Select 
                            value={newNoteFormData[field.id] || ''} 
                            onValueChange={(value) => handleNewNoteFieldChange(field.id, value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a provider" />
                            </SelectTrigger>
                            <SelectContent>
                              {[...(users || [])].filter(u => u.is_provider).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')).map((provider) => (
                                <SelectItem key={provider.id} value={provider.id}>
                                  {formatProviderName(provider)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {field.type === 'group_dropdown' && (
                          <Select 
                            value={newNoteFormData[field.id] || ''} 
                            onValueChange={(value) => handleNewNoteFieldChange(field.id, value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a group" />
                            </SelectTrigger>
                            <SelectContent>
                              {(() => {
                                // Debug: log what we have
                                void 0 && console.log('[DEBUG] clientGroups:', clientGroups?.length, 'groups');
                                if (clientGroups?.length > 0) {
                                  void 0 && console.log('[DEBUG] First group:', JSON.stringify(clientGroups[0]));
                                  void 0 && console.log('[DEBUG] Filter test - status:', clientGroups[0]?.status, 'is_active:', clientGroups[0]?.is_active);
                                }
                                const activeGroups = (clientGroups || []).filter(g => 
                                  g.status === 'active' || g.is_active === true || (g.is_active !== false && !g.status)
                                );
                                void 0 && console.log('[DEBUG] Active groups after filter:', activeGroups.length);
                                
                                if (activeGroups.length > 0) {
                                  return activeGroups.map((group) => (
                                    <SelectItem key={group.id} value={group.name}>
                                      {group.name}
                                    </SelectItem>
                                  ));
                                } else {
                                  return (
                                    <div className="px-2 py-1.5 text-sm text-gray-500">
                                      {!clientGroups ? 'Loading groups...' : clientGroups.length === 0 ? 'No groups in system' : `No active groups (${clientGroups.length} total, check console)`}
                                    </div>
                                  );
                                }
                              })()}
                            </SelectContent>
                          </Select>
                        )}
                        {field.type === 'current_provider_name' && (() => {
                          const prov = users?.find(u => u.id === selectedNoteProvider) || user;
                          return (
                            <Input 
                              value={`${prov?.full_name || `${prov?.first_name || ''} ${prov?.last_name || ''}`.trim() || 'N/A'}${prov?.credentials ? `, ${prov.credentials}` : ''}`} 
                              disabled 
                              className="bg-gray-100"
                            />
                          );
                        })()}
                        {field.type === 'current_user_name' && (
                          <Input 
                            value={`${user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'N/A'}${user?.credentials ? `, ${user.credentials}` : ''}`} 
                            disabled 
                            className="bg-gray-100"
                          />
                        )}
                        {field.type === 'current_date' && (
                          <Input 
                            value={new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} 
                            disabled 
                            className="bg-gray-100"
                          />
                        )}
                      </div>
                    );
                  };
                  
                  // Main rendering - use modular FormFieldRenderer
                  // Build context with all real App.js data
                  // For selectedClient, prefer the fully-loaded selectedClient from ClientsContext
                  // if it matches the note's client, otherwise fall back to clients.find()
                  const noteClientIdForContext = selectedNoteClients?.[0];
                  const fullSelectedClientForContext = selectedClient?.id === noteClientIdForContext 
                    ? selectedClient  // Use fully-loaded client with all fields
                    : allClientsForLookup.find(c => c.id === noteClientIdForContext);
                  
                  // Debug: Log when client data might be missing for prefills
                  if (noteClientIdForContext && !fullSelectedClientForContext) {
                    console.warn('[CreateNote] Client not found for prefills:', {
                      noteClientId: noteClientIdForContext,
                      selectedClientId: selectedClient?.id,
                      selectedClientMatches: selectedClient?.id === noteClientIdForContext,
                      allClientsCount: allClientsForLookup?.length,
                      clientInLookup: !!allClientsForLookup.find(c => c.id === noteClientIdForContext)
                    });
                  }
                  
                  const formFieldContext = {
                    // Client data - use fully loaded client if available
                    selectedClient: fullSelectedClientForContext,
                    clients: allClientsForLookup,  // Use full client list for lookups, not filtered list
                    selectedNoteClients: selectedNoteClients,
                    clientDiagnoses: clientDiagnoses,
                    clientMedications: clientMedications || [],
                    clientVitals: clientVitals || [],
                    
                    // User data
                    user: user,
                    currentUser: user,
                    users: users,
                    
                    // Appointments - use client-specific appointments if available
                    appointments: clientAppointments.length > 0 ? clientAppointments : appointments,
                    
                    // Form data
                    newNoteFormData: newNoteFormData,
                    formTemplates: formTemplates,
                    
                    // Treatment Plan data
                    linkedGoals: linkedGoals,
                    treatmentPlanGoals: availableTreatmentPlanGoals || [],
                    availablePANs: availablePANs || [],
                    
                    // Telehealth/Consent
                    telehealthConsent: telehealthConsent,
                    setTelehealthConsent: setTelehealthConsent,
                    organizations: organizations,
                    posCodes: organizations?.[0]?.pos_codes || [],
                    
                    // Dropdown data
                    serviceTypes: serviceTypes,
                    locations: locations,
                    providers: users?.filter(u => u.is_provider) || [],
                    allGroups: clientGroups || [],
                    
                    // Group note data
                    isGroupNote: isGroupNote || false,
                    groupMembers: typeof groupMembers !== 'undefined' ? groupMembers : [],
                    groupAttendance: typeof groupAttendance !== 'undefined' ? groupAttendance : {},
                    groupMemberNotes: typeof groupMemberNotes !== 'undefined' ? groupMemberNotes : {},
                    
                    // Note state
                    selectedNote: null,
                    isEditMode: false,
                    disabled: false,
                    
                    // Callbacks
                    setNewNoteFormData: setNewNoteFormData,
                    setSelectedClient: setSelectedClient,
                    setShowDiagnosisForm: setShowDiagnosisForm,
                    setShowMedicationForm: setShowMedicationForm,
                    setEditingMedication: setEditingMedication,
                    setShowLinkGoalsDialog: setShowLinkGoalsDialog,
                    fetchTreatmentPlanGoals: fetchTreatmentPlanGoals,
                    setLinkedGoals: setLinkedGoals,
                    setGroupAttendance: typeof setGroupAttendance !== 'undefined' ? setGroupAttendance : () => {},
                    setGroupMemberNotes: typeof setGroupMemberNotes !== 'undefined' ? setGroupMemberNotes : () => {},
                    
                    // Signature helpers
                    loadedSignatures: loadedSignatures,
                    setLoadedSignatures: setLoadedSignatures,
                    getSignatureUrl: getSignatureUrl,
                  };
                  
                  return (
                    <FormFieldRenderer
                      fields={fieldsToRender}
                      sections={hasSections ? sectionsData : null}
                      contextValue={formFieldContext}
                      onChange={setNewNoteFormData}
                      disabled={false}
                    />
                  );
                })()}
              </div>
            )}
            
            {/* Create Treatment Plan from PANs - shown for Comprehensive Assessment when PANs are filled */}
            {selectedTemplate?.name?.includes('Comprehensive Assessment') && (
              (() => {
                // Check if any PANs are filled
                const pans = [
                  newNoteFormData['pan_1'],
                  newNoteFormData['pan_2'],
                  newNoteFormData['pan_3'],
                  newNoteFormData['pan_4'],
                  newNoteFormData['pan_5']
                ].filter(Boolean);
                
                if (pans.length > 0) {
                  return (
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 mt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">📋</span>
                          <div>
                            <h4 className="font-medium text-purple-900">Ready to Create Treatment Plan</h4>
                            <p className="text-sm text-purple-700">{pans.length} Prioritized Assessed Need{pans.length > 1 ? 's' : ''} identified</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={async () => {
                            // First save the assessment as draft
                            const client = allClientsForLookup.find(c => c.id === selectedNoteClients[0]);
                            let noteContent = {
                              template_id: selectedTemplate.id,
                              template_name: selectedTemplate.name
                            };
                            
                            if (selectedTemplate.form_fields) {
                              noteContent.fields = selectedTemplate.form_fields.map(field => ({
                                ...field,
                                value: newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : (field.value !== undefined ? field.value : '')
                              }));
                            }
                            
                            // Save assessment as draft - with manual service details
                            const manualSvcAssess = (!selectedAppointment || selectedAppointment === 'none') ? {
                              dos: newNoteFormData._manual_dos || getTodayLocalDate(),
                              start_time: newNoteFormData._manual_start_time || null,
                              end_time: newNoteFormData._manual_end_time || null,
                              units: newNoteFormData._manual_units || null,
                              cpt: newNoteFormData._manual_cpt || null,
                              cpt_code_id: newNoteFormData._manual_cpt_id || null,
                              pos_code: newNoteFormData._manual_pos_code || null,
                              modality: newNoteFormData._manual_modality || null
                            } : null;
                            setNoteCreationError(null);
                            const createAssessmentResponse = await handleCreateClinicalNote(
                              noteContent,
                              selectedTemplate.id,
                              selectedNoteProvider,
                              selectedNoteClients,
                              isGroupNote,
                              selectedAppointment,
                              'draft',
                              manualSvcAssess
                            );
                            
                            // CRITICAL: Check if assessment was actually created
                            if (!createAssessmentResponse) {
                              setNoteCreationError('Assessment save failed. Your work has NOT been saved. Please try again or copy your content.');
                              return; // Keep dialog open!
                            }
                            
                            // Store PANs to transfer to treatment plan
                            const pansToTransfer = [
                              { priority: 1, text: newNoteFormData['pan_1'] },
                              { priority: 2, text: newNoteFormData['pan_2'] },
                              { priority: 3, text: newNoteFormData['pan_3'] },
                              { priority: 4, text: newNoteFormData['pan_4'] },
                              { priority: 5, text: newNoteFormData['pan_5'] }
                            ].filter(p => p.text);
                            
                            // Store PANs in session storage for the treatment plan tab to pick up
                            sessionStorage.setItem('pansFromAssessment', JSON.stringify({
                              clientId: selectedNoteClients[0],
                              clientName: client?.full_name || 'Client',
                              pans: pansToTransfer,
                              sourceAssessment: 'Comprehensive Assessment'
                            }));
                            
                            // Close the create note dialog
                            setShowAddNote(false);
                            setSelectedTemplate(null);
                            setSelectedNoteProvider(null);
                            setSelectedNoteClients([]);
                            setNewNoteFormData({});
                            
                            // Set the client and switch to treatment plan tab
                            if (client) {
                              setSelectedClient(client);
                              setClientDetailTab('treatment-plans');
                              setShowClientDetail(true);
                              
                              // Show toast to guide user
                              toast.success(`Assessment saved! Opening Treatment Plan tab with ${pansToTransfer.length} PANs ready to import.`);
                            }
                          }}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Create Treatment Plan from PANs
                        </Button>
                      </div>
                    </div>
                  );
                }
                return null;
              })()
            )}
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setShowAddNote(false);
                setSelectedTemplate(null);
                setSelectedNoteProvider(null);
                setSelectedNoteClients([]);
                setIsGroupNote(false);
                setSelectedAppointment(null);
                setTemplateSearchTerm('');
                setNewNoteFormData({}); // Reset form data
                setLinkedGoals([]); // Reset linked goals
                setNoteClientLocked(false); // Reset client lock
              }}>
                Cancel
              </Button>
              <Button 
                className={autosaveBlockedReason 
                  ? "bg-red-600 hover:bg-red-700 text-white" 
                  : "bg-gray-600 hover:bg-gray-700"}
                disabled={
                  isSavingNote ||
                  !selectedTemplate || 
                  !selectedNoteProvider || 
                  selectedNoteClients.length === 0 ||
                  (isGroupNote && selectedNoteClients.length < 2) ||
                  !!autosaveBlockedReason
                }
                title={autosaveBlockedReason || undefined}
                onClick={async () => {
                  if (isSavingNote) return; // Prevent double-click
                  setIsSavingNote(true);
                  
                  try {
                    // Helper function to get client data field value
                    const getClientDataValue = (field) => {
                      const client = allClientsForLookup.find(c => c.id === selectedNoteClients[0]); // Use first client for group notes
                      if (!client) return '';
                      
                      switch(field.type) {
                      case 'client_name': return client.full_name || '';
                      case 'client_dob': return client.date_of_birth || client.dob || '';
                      case 'client_age': 
                        const dob1 = client.date_of_birth || client.dob;
                        return dob1 ? Math.floor((new Date() - new Date(dob1)) / (365.25 * 24 * 60 * 60 * 1000)).toString() : '';
                      case 'client_email': return client.email || '';
                      case 'client_phone': return client.phone || '';
                      case 'client_address': 
                        return [client.address || client.street || client.address_line1, client.address_line2, client.city, client.state, client.zipcode || client.zip || client.zip_code].filter(Boolean).join(', ') || '';
                      case 'client_allergies':
                        return client.allergies || '';
                      case 'client_intake_date':
                        return client.intake_date || client.created_at || '';
                      case 'client_diagnoses':
                        // Snapshot diagnoses at note creation time
                        const clientDx = clientDiagnoses.filter(d => d.client_id === selectedNoteClients[0] && d.is_active);
                        return JSON.stringify(clientDx.map(dx => ({
                          id: dx.id,
                          code: dx.code || dx.diagnosis_code,
                          description: dx.description || dx.diagnosis_description,
                          start_date: dx.start_date,
                          diagnosis_tag: dx.diagnosis_tag,
                          assigned_by_name: dx.assigned_by_name || 'Unknown',
                          provider_names: dx.provider_names || [],
                          is_primary: dx.is_primary
                        })));
                      case 'client_diagnoses_selector':
                        // For selector, only include the selected diagnoses from newNoteFormData
                        const dxSelectorSelectedIds = newNoteFormData[field.id] || [];
                        const dxSelectorClientDx = clientDiagnoses.filter(d => 
                          d.client_id === selectedNoteClients[0] && 
                          d.is_active && 
                          dxSelectorSelectedIds.includes(d.id)
                        );
                        return JSON.stringify(dxSelectorClientDx.map(dx => ({
                          id: dx.id,
                          code: dx.code || dx.diagnosis_code,
                          description: dx.description || dx.diagnosis_description,
                          start_date: dx.start_date,
                          diagnosis_tag: dx.diagnosis_tag,
                          assigned_by_name: dx.assigned_by_name || 'Unknown',
                          provider_names: dx.provider_names || [],
                          is_primary: dx.is_primary
                        })));
                      case 'client_medications':
                        // Snapshot medications at note creation time
                        const clientMeds = clientMedications.filter(m => m.client_id === selectedNoteClients[0] && m.is_active === true);
                        return JSON.stringify(clientMeds.map(med => ({
                          id: med.id,
                          medication_name: med.medication_name,
                          dosage: med.dosage,
                          frequency: med.frequency,
                          start_date: med.start_date,
                          prescriber: med.prescriber,
                          prescribed_by_name: med.prescribed_by_name || 'Unknown',
                          purpose: med.purpose
                        })));
                      case 'client_insurance':
                        // Snapshot insurance info
                        const clientIns = clientInsurances?.filter(i => i.client_id === selectedNoteClients[0] && i.is_active) || [];
                        return JSON.stringify(clientIns.map(ins => ({
                          id: ins.id,
                          company_name: ins.company_name || ins.insurance_company,
                          policy_number: ins.policy_number,
                          group_number: ins.group_number,
                          is_primary: ins.is_primary
                        })));
                      case 'client_providers':
                        // Snapshot assigned providers
                        const assignedProviders = (client.assigned_providers || []).map(providerId => {
                          const provider = users?.find(u => u.id === providerId);
                          return provider ? { id: provider.id, name: provider.full_name || `${provider.first_name || ''} ${provider.last_name || ''}`.trim() } : null;
                        }).filter(Boolean);
                        return JSON.stringify(assignedProviders);
                      case 'client_last_appointment':
                        // Get most recent past appointment
                        const pastAppts = appointments?.filter(a => a.client_id === selectedNoteClients[0] && new Date(a.start_time) < new Date()).sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
                        return pastAppts?.[0]?.start_time || '';
                      case 'client_next_appointment':
                        // Get next upcoming appointment
                        const futureAppts = appointments?.filter(a => a.client_id === selectedNoteClients[0] && new Date(a.start_time) >= new Date()).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
                        return futureAppts?.[0]?.start_time || '';
                      default: return '';
                    }
                  };
                  
                  // Save as draft - same logic as current save button
                  let noteContent = {
                    template_id: selectedTemplate.id,
                    template_name: selectedTemplate.name
                  };
                  
                  // Priority: form_fields > template_data.sections (for consistency with rendering)
                  if (selectedTemplate.form_fields && selectedTemplate.form_fields.length > 0) {
                    noteContent.fields = selectedTemplate.form_fields.map(field => ({
                      ...field,
                      value: field.type?.startsWith('client_') 
                        ? getClientDataValue(field)
                        : (newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : (field.value !== undefined ? field.value : ''))
                    }));
                  } else if (selectedTemplate.template_data && selectedTemplate.template_data.sections) {
                    noteContent.sections = selectedTemplate.template_data.sections.map(section => ({
                      name: section.name,
                      fields: (section.fields || []).map(field => ({
                        ...field,
                        value: field.type?.startsWith('client_') 
                          ? getClientDataValue(field)
                          : (newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : (field.value !== undefined ? field.value : ''))
                      }))
                    }));
                  }
                  
                  // Prepare manual service details if no appointment linked
                  const manualServiceDetails = (!selectedAppointment || selectedAppointment === 'none') ? {
                    dos: newNoteFormData._manual_dos || getTodayLocalDate(),
                    start_time: newNoteFormData._manual_start_time || null,
                    end_time: newNoteFormData._manual_end_time || null,
                    units: newNoteFormData._manual_units || null,
                    cpt: newNoteFormData._manual_cpt || null,
                    cpt_code_id: newNoteFormData._manual_cpt_id || null,
                    pos_code: newNoteFormData._manual_pos_code || null,
                    modality: newNoteFormData._manual_modality || null
                  } : null;
                  
                  // Create note as draft
                  setNoteCreationError(null); // Clear any previous error
                  const createResponse = await handleCreateClinicalNote(
                    noteContent, 
                    selectedTemplate.id, 
                    selectedNoteProvider,
                    selectedNoteClients,
                    isGroupNote,
                    selectedAppointment,
                    'draft',  // Force draft status
                    manualServiceDetails
                  );
                  
                  // CRITICAL: Check if note was actually created
                  if (!createResponse) {
                    setNoteCreationError('Draft save failed. Your work has NOT been saved. Please try again or copy your content.');
                    return; // Keep dialog open!
                  }
                  
                  // SUCCESS - now close dialog
                  setNoteCreationError(null);
                  setShowAddNote(false);
                  setSelectedTemplate(null);
                  setSelectedNoteProvider(null);
                  setSelectedNoteClients([]);
                  setIsGroupNote(false);
                  setSelectedAppointment(null);
                  setTemplateSearchTerm('');
                  setSelectedNoteStatus('draft');
                  setNewNoteFormData({});
                  } catch (error) {
                    console.error('Error saving draft:', error);
                    setNoteCreationError(`Draft save failed: ${error.response?.data?.detail || error.message}. Your work has NOT been saved.`);
                    // Keep dialog open!
                  } finally {
                    setIsSavingNote(false);
                  }
                }}
              >
                {isSavingNote ? 'Saving...' : (autosaveBlockedReason || 'Save as Draft')}
              </Button>
              <Button 
                className={autosaveBlockedReason 
                  ? "bg-red-600 hover:bg-red-700 text-white" 
                  : "bg-teal-600 hover:bg-teal-700"}
                disabled={
                  isSavingNote ||
                  !selectedTemplate || 
                  !selectedNoteProvider || 
                  selectedNoteClients.length === 0 ||
                  (isGroupNote && selectedNoteClients.length < 2) ||
                  !!autosaveBlockedReason
                }
                title={autosaveBlockedReason || undefined}
                onClick={async () => {
                  if (isSavingNote) return; // Prevent double-click
                  setIsSavingNote(true);
                  
                  // Check signature requirements
                  const requiresProviderSig = selectedTemplate.requires_provider_signature !== false;
                  const requiresClientSig = selectedTemplate.require_client_signature || false;
                  
                  // If only client signature is needed (no provider signature required)
                  if (!requiresProviderSig && requiresClientSig) {
                    // Helper function to get client data field value
                    const getClientDataValue = (field) => {
                      const client = allClientsForLookup.find(c => c.id === selectedNoteClients[0]);
                      if (!client) return '';
                      
                      switch(field.type) {
                        case 'client_name': return client.full_name || '';
                        case 'client_dob': return client.date_of_birth || client.dob || '';
                        case 'client_age': 
                          const dob2 = client.date_of_birth || client.dob;
                          return dob2 ? Math.floor((new Date() - new Date(dob2)) / (365.25 * 24 * 60 * 60 * 1000)).toString() : '';
                        case 'client_email': return client.email || '';
                        case 'client_phone': return client.phone || '';
                        case 'client_address': 
                          return [client.address || client.street || client.address_line1, client.address_line2, client.city, client.state, client.zipcode || client.zip || client.zip_code].filter(Boolean).join(', ') || '';
                        case 'client_allergies':
                          return client.allergies || '';
                        case 'client_intake_date':
                          return client.intake_date || client.created_at || '';
                        case 'client_diagnoses':
                          const clientDx = clientDiagnoses.filter(d => d.client_id === selectedNoteClients[0] && d.is_active);
                          return JSON.stringify(clientDx.map(dx => ({
                            id: dx.id,
                            code: dx.code || dx.diagnosis_code,
                            description: dx.description || dx.diagnosis_description,
                            start_date: dx.start_date,
                            diagnosis_tag: dx.diagnosis_tag,
                            assigned_by_name: dx.assigned_by_name || 'Unknown',
                            provider_names: dx.provider_names || [],
                            is_primary: dx.is_primary
                          })));
                        case 'client_diagnoses_selector':
                          const dxSelectedIds = newNoteFormData[field.id] || [];
                          const dxSelectedClientDx = clientDiagnoses.filter(d => 
                            d.client_id === selectedNoteClients[0] && 
                            d.is_active && 
                            dxSelectedIds.includes(d.id)
                          );
                          return JSON.stringify(dxSelectedClientDx.map(dx => ({
                            id: dx.id,
                            code: dx.code || dx.diagnosis_code,
                            description: dx.description || dx.diagnosis_description,
                            start_date: dx.start_date,
                            diagnosis_tag: dx.diagnosis_tag,
                            assigned_by_name: dx.assigned_by_name || 'Unknown',
                            provider_names: dx.provider_names || [],
                            is_primary: dx.is_primary
                          })));
                        case 'client_medications':
                          const clientMeds = clientMedications.filter(m => m.client_id === selectedNoteClients[0] && m.is_active === true);
                          return JSON.stringify(clientMeds.map(med => ({
                            id: med.id,
                            medication_name: med.medication_name,
                            dosage: med.dosage,
                            frequency: med.frequency,
                            start_date: med.start_date,
                            prescriber: med.prescriber,
                            prescribed_by_name: med.prescribed_by_name || 'Unknown',
                            purpose: med.purpose
                          })));
                        case 'client_insurance':
                          const clientIns = clientInsurances?.filter(i => i.client_id === selectedNoteClients[0] && i.is_active) || [];
                          return JSON.stringify(clientIns.map(ins => ({
                            id: ins.id,
                            company_name: ins.company_name || ins.insurance_company,
                            policy_number: ins.policy_number,
                            group_number: ins.group_number,
                            is_primary: ins.is_primary
                          })));
                        case 'client_vitals':
                          // Get most recent vitals for this client
                          const recentVitals = clientVitals && clientVitals.length > 0 
                            ? clientVitals.sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0]
                            : null;
                          if (recentVitals) {
                            return JSON.stringify({
                              recorded_at: recentVitals.recorded_at,
                              recorded_by_name: recentVitals.recorded_by_name,
                              blood_pressure_systolic: recentVitals.blood_pressure_systolic,
                              blood_pressure_diastolic: recentVitals.blood_pressure_diastolic,
                              pulse: recentVitals.pulse,
                              oxygen_saturation: recentVitals.oxygen_saturation,
                              respiratory_rate: recentVitals.respiratory_rate,
                              temperature: recentVitals.temperature,
                              height_feet: recentVitals.height_feet,
                              height_inches: recentVitals.height_inches,
                              weight: recentVitals.weight,
                              notes: recentVitals.notes
                            });
                          }
                          return '';
                        case 'client_providers':
                          const assignedProviders = (client.assigned_providers || []).map(providerId => {
                            const provider = users?.find(u => u.id === providerId);
                            return provider ? { id: provider.id, name: provider.full_name || `${provider.first_name || ''} ${provider.last_name || ''}`.trim() } : null;
                          }).filter(Boolean);
                          return JSON.stringify(assignedProviders);
                        case 'client_last_appointment':
                          const pastAppts = appointments?.filter(a => a.client_id === selectedNoteClients[0] && new Date(a.start_time) < new Date()).sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
                          return pastAppts?.[0]?.start_time || '';
                        case 'client_next_appointment':
                          const futureAppts = appointments?.filter(a => a.client_id === selectedNoteClients[0] && new Date(a.start_time) >= new Date()).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
                          return futureAppts?.[0]?.start_time || '';
                        default: return '';
                      }
                    };
                    
                    // Just create as draft and show send to client dialog
                    let noteContent = {
                      template_id: selectedTemplate.id,
                      template_name: selectedTemplate.name
                    };
                    
                    // Priority: form_fields > template_data.sections (for consistency with rendering)
                    if (selectedTemplate.form_fields && selectedTemplate.form_fields.length > 0) {
                      noteContent.fields = selectedTemplate.form_fields.map(field => ({
                        ...field,
                        value: field.type?.startsWith('client_') 
                          ? getClientDataValue(field)
                          : (newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : (field.value !== undefined ? field.value : ''))
                      }));
                    } else if (selectedTemplate.template_data && selectedTemplate.template_data.sections) {
                      noteContent.sections = selectedTemplate.template_data.sections.map(section => ({
                        name: section.name,
                        fields: (section.fields || []).map(field => ({
                          ...field,
                          value: field.type?.startsWith('client_') 
                            ? getClientDataValue(field)
                            : (newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : (field.value !== undefined ? field.value : ''))
                        }))
                      }));
                    }
                    
                    // Prepare manual service details if no appointment linked
                    const manualServiceDetails = (!selectedAppointment || selectedAppointment === 'none') ? {
                      dos: newNoteFormData._manual_dos || getTodayLocalDate(),
                      start_time: newNoteFormData._manual_start_time || null,
                      end_time: newNoteFormData._manual_end_time || null,
                      units: newNoteFormData._manual_units || null,
                      cpt: newNoteFormData._manual_cpt || null,
                      cpt_code_id: newNoteFormData._manual_cpt_id || null
                    } : null;
                    
                    try {
                      setNoteCreationError(null); // Clear any previous error
                      const createResponse = await handleCreateClinicalNote(
                        noteContent, 
                        selectedTemplate.id, 
                        selectedNoteProvider,
                        selectedNoteClients,
                        isGroupNote,
                        selectedAppointment,
                        'draft',
                        manualServiceDetails
                      );
                      
                      // CRITICAL: Check if note was actually created
                      if (!createResponse || !createResponse.id) {
                        setNoteCreationError('Note creation failed. Your work has NOT been saved. Please try again or copy your content.');
                        return; // Keep dialog open!
                      }
                      
                      // Refresh data in background
                      refreshNotes(); refreshTodos();
                      
                      // FIXED: Use the SPECIFIC note ID returned, not "most recent note"
                      // This prevents race conditions where another user's note could be fetched
                      setSelectedNote(createResponse);
                      setShowSendToClientDialog(true);
                      
                      setNoteCreationError(null); // Clear error on success
                      setShowAddNote(false);
                      setSelectedTemplate(null);
                      setSelectedNoteProvider(null);
                      setSelectedNoteClients([]);
                      setIsGroupNote(false);
                      setSelectedAppointment(null);
                      setTemplateSearchTerm('');
                      setNewNoteFormData({});
                    } catch (error) {
                      console.error('Error creating note:', error);
                      setNoteCreationError(`Note creation failed: ${error.response?.data?.detail || error.message}. Your work has NOT been saved.`);
                      // Keep dialog open - don't close!
                    } finally {
                      setIsSavingNote(false);
                    }
                    return;
                  }
                  
                  // Original "Save & Sign" logic for provider signature
                  // Helper function to get client data field value
                  const getClientDataValue = (field) => {
                    const client = allClientsForLookup.find(c => c.id === selectedNoteClients[0]);
                    if (!client) return '';
                    
                    switch(field.type) {
                      case 'client_name': return client.full_name || '';
                      case 'client_dob': return client.date_of_birth || client.dob || '';
                      case 'client_age': 
                        const dob3 = client.date_of_birth || client.dob;
                        return dob3 ? Math.floor((new Date() - new Date(dob3)) / (365.25 * 24 * 60 * 60 * 1000)).toString() : '';
                      case 'client_email': return client.email || '';
                      case 'client_phone': return client.phone || '';
                      case 'client_address': 
                        return [client.address || client.street || client.address_line1, client.address_line2, client.city, client.state, client.zipcode || client.zip || client.zip_code].filter(Boolean).join(', ') || '';
                      case 'client_allergies':
                        return client.allergies || '';
                      case 'client_intake_date':
                        return client.intake_date || client.created_at || '';
                      case 'client_diagnoses':
                        // Snapshot diagnoses at note creation time
                        const clientDx = clientDiagnoses.filter(d => d.client_id === selectedNoteClients[0] && d.is_active);
                        return JSON.stringify(clientDx.map(dx => ({
                          id: dx.id,
                          code: dx.code || dx.diagnosis_code,
                          description: dx.description || dx.diagnosis_description,
                          start_date: dx.start_date,
                          assigned_by_name: dx.assigned_by_name || 'Unknown',
                          provider_names: dx.provider_names || [],
                          is_primary: dx.is_primary
                        })));
                      case 'client_diagnoses_selector':
                        // For selector, only include the selected diagnoses from newNoteFormData
                        const dxSelectorSelectedIds = newNoteFormData[field.id] || [];
                        const dxSelectorClientDx = clientDiagnoses.filter(d => 
                          d.client_id === selectedNoteClients[0] && 
                          d.is_active && 
                          dxSelectorSelectedIds.includes(d.id)
                        );
                        return JSON.stringify(dxSelectorClientDx.map(dx => ({
                          id: dx.id,
                          code: dx.code || dx.diagnosis_code,
                          description: dx.description || dx.diagnosis_description,
                          start_date: dx.start_date,
                          assigned_by_name: dx.assigned_by_name || 'Unknown',
                          provider_names: dx.provider_names || [],
                          is_primary: dx.is_primary
                        })));
                      case 'client_medications':
                        // Snapshot medications at note creation time
                        const clientMeds = clientMedications.filter(m => m.client_id === selectedNoteClients[0] && m.is_active === true);
                        return JSON.stringify(clientMeds.map(med => ({
                          id: med.id,
                          medication_name: med.medication_name,
                          dosage: med.dosage,
                          frequency: med.frequency,
                          start_date: med.start_date,
                          prescriber: med.prescriber,
                          prescribed_by_name: med.prescribed_by_name || 'Unknown',
                          purpose: med.purpose
                        })));
                      case 'client_insurance':
                        // Snapshot insurance info
                        const clientIns = clientInsurances?.filter(i => i.client_id === selectedNoteClients[0] && i.is_active) || [];
                        return JSON.stringify(clientIns.map(ins => ({
                          id: ins.id,
                          company_name: ins.company_name || ins.insurance_company,
                          policy_number: ins.policy_number,
                          group_number: ins.group_number,
                          is_primary: ins.is_primary
                        })));
                      case 'client_vitals':
                        // Get most recent vitals for this client
                        const latestVitals = clientVitals && clientVitals.length > 0 
                          ? clientVitals.sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0]
                          : null;
                        if (latestVitals) {
                          return JSON.stringify({
                            recorded_at: latestVitals.recorded_at,
                            recorded_by_name: latestVitals.recorded_by_name,
                            blood_pressure_systolic: latestVitals.blood_pressure_systolic,
                            blood_pressure_diastolic: latestVitals.blood_pressure_diastolic,
                            pulse: latestVitals.pulse,
                            oxygen_saturation: latestVitals.oxygen_saturation,
                            respiratory_rate: latestVitals.respiratory_rate,
                            temperature: latestVitals.temperature,
                            height_feet: latestVitals.height_feet,
                            height_inches: latestVitals.height_inches,
                            weight: latestVitals.weight,
                            notes: latestVitals.notes
                          });
                        }
                        return '';
                      case 'client_providers':
                        // Snapshot assigned providers
                        const assignedProviders = (client.assigned_providers || []).map(providerId => {
                          const provider = users?.find(u => u.id === providerId);
                          return provider ? { id: provider.id, name: provider.full_name || `${provider.first_name || ''} ${provider.last_name || ''}`.trim() } : null;
                        }).filter(Boolean);
                        return JSON.stringify(assignedProviders);
                      case 'client_last_appointment':
                        // Get most recent past appointment
                        const pastAppts = appointments?.filter(a => a.client_id === selectedNoteClients[0] && new Date(a.start_time) < new Date()).sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
                        return pastAppts?.[0]?.start_time || '';
                      case 'client_next_appointment':
                        // Get next upcoming appointment
                        const futureAppts = appointments?.filter(a => a.client_id === selectedNoteClients[0] && new Date(a.start_time) >= new Date()).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
                        return futureAppts?.[0]?.start_time || '';
                      default: return '';
                    }
                  };
                  
                  // Collect form data and create note
                  // Handle both old (form_fields) and new (template_data.sections) template structures
                  let noteContent = {
                    template_id: selectedTemplate.id,
                    template_name: selectedTemplate.name
                  };
                  
                  void 0 && console.log('[DEBUG] Creating note with formData:', newNoteFormData);
                  void 0 && console.log('[DEBUG] Template structure:', selectedTemplate.template_data || selectedTemplate.form_fields);
                  
                  // Priority: form_fields > template_data.sections (for consistency with rendering)
                  if (selectedTemplate.form_fields && selectedTemplate.form_fields.length > 0) {
                    // form_fields structure (preferred - what form builder edits)
                    noteContent.fields = selectedTemplate.form_fields.map(field => ({
                      ...field,
                      value: field.type?.startsWith('client_') 
                        ? getClientDataValue(field)
                        : (newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : (field.value !== undefined ? field.value : ''))
                    }));
                  } else if (selectedTemplate.template_data && selectedTemplate.template_data.sections) {
                    // Legacy template structure with sections - fallback
                    noteContent.sections = selectedTemplate.template_data.sections.map(section => ({
                      name: section.name,
                      fields: (section.fields || []).map(field => ({
                        ...field,
                        value: field.type?.startsWith('client_') 
                          ? getClientDataValue(field)
                          : (newNoteFormData[field.id] !== undefined ? newNoteFormData[field.id] : (field.value !== undefined ? field.value : ''))
                      }))
                    }));
                  }
                  
                  void 0 && console.log('[DEBUG] Final note content being saved:', noteContent);
                  
                  // Prepare manual service details if no appointment linked
                  const manualServiceDetails = (!selectedAppointment || selectedAppointment === 'none') ? {
                    dos: newNoteFormData._manual_dos || getTodayLocalDate(),
                    start_time: newNoteFormData._manual_start_time || null,
                    end_time: newNoteFormData._manual_end_time || null,
                    units: newNoteFormData._manual_units || null,
                    cpt: newNoteFormData._manual_cpt || null,
                    cpt_code_id: newNoteFormData._manual_cpt_id || null,
                    pos_code: newNoteFormData._manual_pos_code || null,
                    modality: newNoteFormData._manual_modality || null
                  } : null;
                  
                  try {
                    setNoteCreationError(null); // Clear any previous error
                    // Create the note first
                    const createdNote = await handleCreateClinicalNote(
                      noteContent, 
                      selectedTemplate.id, 
                      selectedNoteProvider,
                      selectedNoteClients,
                      isGroupNote,
                      selectedAppointment,
                      'draft',  // Create as draft first
                      manualServiceDetails
                    );
                    
                    // CRITICAL: Check if note was actually created
                    if (!createdNote || !createdNote.id) {
                      setNoteCreationError('Note creation failed. Your work has NOT been saved. Please try again or copy your content.');
                      return; // Keep dialog open!
                    }
                    
                    // Get the provider's saved signature or use the one from the form
                    let signatureImage = null;
                    
                    // Check if there's a signature field in the form with a captured signature
                    const signatureFieldId = Object.keys(newNoteFormData).find(key => 
                      newNoteFormData[key] && 
                      typeof newNoteFormData[key] === 'string' && 
                      newNoteFormData[key].startsWith('data:image')
                    );
                    
                    if (signatureFieldId) {
                      signatureImage = newNoteFormData[signatureFieldId];
                    } else if (user.signature) {
                      // Use the provider's saved signature
                      signatureImage = user.signature;
                    }
                    
                    // Sign the note as provider
                    const signatureData = {
                      provider_signed_by: user.id,
                      provider_signed_at: new Date().toISOString(),
                      ...(signatureImage && { provider_signature: signatureImage })
                    };
                    
                    await axios.put(`${API}/notes/${createdNote.id}`, signatureData);
                    toast.success('Note created and signed!');
                    
                    // Refresh data
                    refreshNotes(); refreshTodos();
                    
                    // Show advancement dialog
                    const updatedNote = await axios.get(`${API}/notes/${createdNote.id}`);
                    setSignedNoteForAdvancement(updatedNote.data);
                    setShowSignatureAdvanceDialog(true);
                    
                    // SUCCESS - now close dialog and reset
                    setNoteCreationError(null);
                    setShowAddNote(false);
                    setSelectedTemplate(null);
                    setSelectedNoteProvider(null);
                    setSelectedNoteClients([]);
                    setIsGroupNote(false);
                    setSelectedAppointment(null);
                    setTemplateSearchTerm('');
                    setSelectedNoteStatus('draft');
                    setNewNoteFormData({});
                    
                  } catch (error) {
                    console.error('Error creating/signing note:', error);
                    setNoteCreationError(`Note creation failed: ${error.response?.data?.detail || error.message}. Your work has NOT been saved.`);
                    // Keep dialog open - don't close!
                  } finally {
                    setIsSavingNote(false);
                  }
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                {isSavingNote 
                  ? 'Saving...'
                  : autosaveBlockedReason
                    ? autosaveBlockedReason
                    : selectedTemplate && !selectedTemplate.requires_provider_signature && selectedTemplate.require_client_signature 
                      ? 'Send to Client for Signature' 
                      : selectedTemplate && selectedTemplate.requires_provider_signature === false && !selectedTemplate.require_client_signature
                        ? 'Complete'
                        : 'Save & Sign'}
              </Button>
            </div>
          </div>
          </div>
          {/* Reference Note Side Panel for Create Note */}
          {showCreateNoteRefPanel && (
            <ReferenceNotePanel
              selectedNote={{ client_id: selectedNoteClients?.[0], client_ids: selectedNoteClients, is_group_note: isGroupNote }}
              notes={notes}
              users={users}
              formTemplates={formTemplates}
              clientDiagnoses={clientDiagnoses}
              locations={locations}
              onClose={() => setShowCreateNoteRefPanel(false)}
              onApplyField={(fieldId, value, label) => {
                // For create note, match by field id or label against selected template
                const templateFields = selectedTemplate?.form_fields || [];
                const match = templateFields.find(f => f.id === fieldId) || templateFields.find(f => (f.label || f.field_name) === label);
                if (match) {
                  setNewNoteFormData(prev => ({ ...prev, [match.id]: value }));
                } else {
                  const text = Array.isArray(value) ? value.join(', ') : String(value || '');
                  navigator.clipboard.writeText(text).catch(() => {});
                  toast.info(`No matching field found — copied to clipboard`);
                }
              }}
              onApplyAll={(fields) => {
                const templateFields = selectedTemplate?.form_fields || [];
                const updates = {};
                fields.forEach(({ id, value, label }) => {
                  const match = templateFields.find(f => f.id === id) || templateFields.find(f => (f.label || f.field_name) === label);
                  if (match) updates[match.id] = value;
                });
                if (Object.keys(updates).length > 0) {
                  setNewNoteFormData(prev => ({ ...prev, ...updates }));
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Link Treatment Plan Goals Dialog */}
      <LinkGoalsDialog ctx={{
        availableTreatmentPlanGoals, linkedGoals, loadingTreatmentPlanGoals,
        setLinkedGoals, setSelectedNote, setShowLinkGoalsDialog, showLinkGoalsDialog,
      }} />

      {/* Add/Edit Group Dialog - Extracted Component */}
      <AddEditGroupDialog
        open={showAddGroup}
        onOpenChange={(open) => {
          setShowAddGroup(open);
          if (!open && editingGroup) {
            // Update URL to remove groupId when closing
            setEditingGroup(null);
            navigate(`/dashboard?view=groups`, { replace: true });
          }
        }}
        editingGroup={editingGroup}
        setEditingGroup={setEditingGroup}
        groupFormData={groupFormData}
        setGroupFormData={setGroupFormData}
        clients={allClientsForLookup}
        providers={providers}
        cptCodes={cptCodes}
        curricula={curricula}
        locations={locations}
        appointments={appointments}
        serviceTypes={serviceTypes}
        handleCreateClientGroup={handleCreateClientGroup}
        handleUpdateClientGroup={handleUpdateClientGroup}
        handleDeleteClientGroup={handleDeleteClientGroup}
        handleScheduleGroupSessions={handleScheduleGroupSessions}
        handleAddClientToGroup={handleAddClientToGroup}
        handleRemoveClientFromGroup={handleRemoveClientFromGroup}
        handleReactivateMember={handleReactivateMember}
        formatClientName={formatClientName}
        groupClientSearch={groupClientSearch}
        setGroupClientSearch={setGroupClientSearch}
        addToFutureAppointments={addToFutureAppointments}
        setAddToFutureAppointments={setAddToFutureAppointments}
        setSelectedGroupForMembers={setSelectedGroupForMembers}
        setShowFormerMembers={setShowFormerMembers}
        setShowAddGroup={setShowAddGroup}
        setAppointmentFormData={setAppointmentFormData}
        setEditingAppointment={setEditingAppointment}
        setShowAppointmentForm={setShowAppointmentForm}
      />

      {/* Group Members Management Dialog */}
      {/* Group Members Dialog */}
      <GroupMembersDialog
        open={showGroupMembers}
        onOpenChange={handleCloseGroupMembers}
        selectedGroup={selectedGroup}
        setSelectedGroup={setSelectedGroup}
        clients={clients}
        allClientsForLookup={allClientsForLookup}
        setClientGroups={setClientGroups}
        groupMemberSearch={groupMemberSearch}
        setGroupMemberSearch={setGroupMemberSearch}
        brandingSettings={brandingSettings}
        locations={locations}
      />

      {/* Edit Location Dialog — extracted to LocationDialogs.jsx
          (Feb 2026 refactor).  Parent retains all state ownership. */}
      <EditLocationDialog
        open={showEditLocation}
        onOpenChange={setShowEditLocation}
        editingLocation={editingLocation}
        formData={locationFormData}
        setFormData={setLocationFormData}
        onSubmit={handleUpdateLocation}
        onCancel={() => setShowEditLocation(false)}
      />

      {/* Location Provider Management Dialog */}
      <Dialog open={showLocationProviders} onOpenChange={setShowLocationProviders}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Providers at {editingLocation?.name}</DialogTitle>
          </DialogHeader>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded mb-4">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> This is a read-only view. To assign or remove providers from this location, 
              go to <strong>User Management</strong> and edit the provider's "Assigned Locations" field.
            </p>
          </div>
          <div>
            <h4 className="font-medium mb-3">Providers Working at This Location</h4>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {locationProviders.length > 0 ? (
                locationProviders.map(provider => (
                  <div key={provider.id} className="flex items-center p-3 bg-gray-50 rounded border">
                    <div className="flex-1">
                      <p className="font-medium">{provider.full_name}{provider.credentials ? `, ${provider.credentials}` : ''}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No providers assigned to this location</p>
              )}
            </div>
          </div>
          
          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setShowLocationProviders(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Locations Dialog — extracted to LocationDialogs.jsx
          (Feb 2026 refactor).  Parent retains all state ownership.
          Five callbacks + locations array + providerCounts are wired
          in so the row-actions can drive the existing state machine
          for Set-Primary / View-Providers / Edit / Archive / Delete. */}
      <ManageLocationsDialog
        open={showManageLocations}
        onOpenChange={setShowManageLocations}
        apiBase={API}
        locations={locations}
        setLocations={setLocations}
        providers={providers}
        locationProviderCounts={locationProviderCounts}
        setEditingLocation={setEditingLocation}
        setLocationFormData={setLocationFormData}
        setLocationProviders={setLocationProviders}
        setShowLocationProviders={setShowLocationProviders}
        setShowEditLocation={setShowEditLocation}
        setShowAddLocation={setShowAddLocation}
        adminJumpDropdown={
          <AdminJumpToDropdown
            currentDialogId="locations"
            isDirty={currentAdminDialogDirty}
          />
        }
      />

      {/* Deleted Documents Manager Dialog */}
      <Dialog open={showDeletedDocuments} onOpenChange={setShowDeletedDocuments}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-500" />
              Document Recovery
            </DialogTitle>
          </DialogHeader>
          <DeletedDocumentsManager clients={clients} user={user} />
        </DialogContent>
      </Dialog>

      {/* Manage POS Codes Dialog */}
      <Dialog open={showManagePOS} onOpenChange={setShowManagePOS}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Manage POS (Place of Service) Codes</DialogTitle>
            <AdminJumpToDropdown currentDialogId="pos" isDirty={currentAdminDialogDirty} />
          </DialogHeader>
          
          {/* Add New POS Code */}
          <div className="mb-6 p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Add New POS Code</h4>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label>POS Code (for billing)</Label>
                <DebouncedInput
                  placeholder="e.g., 02"
                  value={newPOSCode}
                  onChange={(e) => setNewPOSCode(e.target.value)}
                  maxLength={2}
                />
              </div>
              <div>
                <Label>Description</Label>
                <DebouncedInput
                  placeholder="e.g., Telehealth Outside the Home"
                  value={newPOSLocation}
                  onChange={(e) => setNewPOSLocation(e.target.value)}
                />
              </div>
            </div>
            <Button
              size="sm"
              onClick={async () => {
                if (!newPOSCode || !newPOSLocation) {
                  toast.error('Please fill in both fields');
                  return;
                }
                try {
                  const currentPOS = getEffectivePosCodes(organizations, posCodes);
                  const newEntry = {code: newPOSCode, description: newPOSLocation};
                  
                  await axios.patch(`${API}/organizations/${organizations[0].id}`, {
                    pos_codes: [...currentPOS, newEntry]
                  });
                  
                  toast.success('POS code added');
                  setNewPOSCode('');
                  setNewPOSLocation('');
                  refreshNotes(); refreshClients(); refreshStats();
                } catch (error) {
                  toast.error('Failed to add POS code');
                }
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add POS Code
            </Button>
          </div>
          
          {/* List of POS Codes */}
          <div className="space-y-2">
            <h4 className="font-medium mb-2">Current POS Codes ({getEffectivePosCodes(organizations, posCodes).length})</h4>
            {getEffectivePosCodes(organizations, posCodes).map((pos, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                <div>
                  <span className="font-mono font-semibold text-blue-600">{pos.code}</span>
                  <span className="text-sm ml-2">- {pos.description}</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                  onClick={async () => {
                    if (window.confirm(`Delete POS code "${pos.code} - ${pos.description}"?`)) {
                      try {
                        const currentPOS = getEffectivePosCodes(organizations, posCodes);
                        const updatedPOS = currentPOS.filter((_, i) => i !== idx);
                        await axios.patch(`${API}/organizations/${organizations[0].id}`, {
                          pos_codes: updatedPOS
                        });
                        toast.success('POS code deleted');
                        refreshNotes(); refreshClients(); refreshStats();
                      } catch (error) {
                        toast.error('Failed to delete POS code');
                      }
                    }
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
            
            {getEffectivePosCodes(organizations, posCodes).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No POS codes configured. Add your first one above!
              </div>
            )}
          </div>
          
          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setShowManagePOS(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Firm Calendar Dialog */}
      <Dialog open={showFirmCalendarDialog} onOpenChange={setShowFirmCalendarDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-sky-600" />
              Firm Calendar
            </DialogTitle>
            <AdminJumpToDropdown currentDialogId="firmCalendar" isDirty={currentAdminDialogDirty} />
          </DialogHeader>
          <Suspense fallback={<PageLoader label="Loading Firm Calendar..." />}>
            <FirmCalendarAdmin />
          </Suspense>
        </DialogContent>
      </Dialog>


      {/* Manage Licenses Dialog */}
      {/* Auto-Submit Schedule Admin Dialog — recurring submission of
          closed claim batches to NEHEN/Availity (admin/billing-mgr only). */}
      <Dialog open={showAutoSubmitSchedule} onOpenChange={setShowAutoSubmitSchedule}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Auto-Submit Schedule</DialogTitle>
            <AdminJumpToDropdown currentDialogId="autoSubmitSchedule" isDirty={currentAdminDialogDirty} />
          </DialogHeader>
          <Suspense fallback={<div className="text-center py-8 text-gray-500">Loading…</div>}>
            <AutoSubmitScheduleSettings />
          </Suspense>
        </DialogContent>
      </Dialog>

      <Dialog open={showManageLicenses} onOpenChange={setShowManageLicenses}>
        <DialogContent className="max-w-4xl">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Manage Licenses & Certifications</DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add License
              </Button>
              <AdminJumpToDropdown currentDialogId="licenses" isDirty={currentAdminDialogDirty} />
            </div>
          </DialogHeader>          
          <div className="space-y-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-medium">State Healthcare License - CA</h4>
                  <p className="text-sm text-gray-600">License: CA-12345</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                  <Button size="sm" variant="outline">
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-gray-500">Issue Date</Label>
                  <p>01/15/2023</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Expiry Date</Label>
                  <p>12/31/2025</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-medium">DEA Registration</h4>
                  <p className="text-sm text-gray-600">DEA: AB1234567</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className="bg-orange-100 text-orange-800">Expiring Soon</Badge>
                  <Button size="sm" variant="outline">
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-gray-500">Issue Date</Label>
                  <p>06/30/2022</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Expiry Date</Label>
                  <p>06/30/2025</p>
                </div>
              </div>
            </Card>
          </div>
          
          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setShowManageLicenses(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Organization Configuration Dialog */}
      <Dialog open={showOrgConfig} onOpenChange={setShowOrgConfig}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Organization Configuration</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Primary Organization */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">Primary Organization</h3>
              </div>
              
              {organizations?.[0] ? (
                <Card className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-lg">{organizations[0].name}</h4>
                      <p className="text-sm text-gray-600 capitalize">{organizations[0].organization_type || 'Healthcare Practice'}</p>
                      {organizations[0].address && (
                        <p className="text-sm text-gray-500">
                          {organizations[0].address}
                          {organizations[0].city && `, ${organizations[0].city}`}
                          {organizations[0].state && `, ${organizations[0].state}`}
                          {organizations[0].zip_code && ` ${organizations[0].zip_code}`}
                        </p>
                      )}
                      {organizations[0].tax_id && <p className="text-sm text-gray-500">Tax ID: {organizations[0].tax_id}</p>}
                      {organizations[0].taxonomy_code && <p className="text-sm text-gray-500">Taxonomy: {organizations[0].taxonomy_code}</p>}
                      <div className="mt-2">
                        <Label className="text-xs text-gray-500">NPIs ({organizations[0].npis?.length || 0})</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {organizations[0].npis?.map((npi, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">{npi}</Badge>
                          ))}
                          {(!organizations[0].npis || organizations[0].npis.length === 0) && (
                            <span className="text-xs text-gray-400">No NPIs assigned</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setOrganizationFormData({
                            name: organizations[0].name || '',
                            organization_type: organizations[0].organization_type || '',
                            address: organizations[0].address || '',
                            city: organizations[0].city || '',
                            state: organizations[0].state || '',
                            zip_code: organizations[0].zip_code || '',
                            phone: organizations[0].phone || '',
                            fax: organizations[0].fax || '',
                            email: organizations[0].email || '',
                            website: organizations[0].website || '',
                            tax_id: organizations[0].tax_id || '',
                            taxonomy_code: organizations[0].taxonomy_code || '',
                            npis: organizations[0].npis || [],
                            availity_submitter_id: organizations[0].availity_submitter_id || '',
                            isa_sender_id: organizations[0].isa_sender_id || '',
                            nehen_isa_sender_id: organizations[0].nehen_isa_sender_id || ''
                          });
                          setEditingOrganization(organizations[0]);
                          setShowEditOrganization(true);
                        }}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </Card>
              ) : (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <p className="text-gray-500 mb-4">No organization configured</p>
                  <p className="text-sm text-gray-400 mb-4">Create your organization to get started</p>
                  <Button
                    onClick={() => setShowAddOrganization(true)}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Create Organization
                  </Button>
                </div>
              )}
            </div>

            {/* Payments — admin-configurable public payment link
                (Feb 2026).  Lets the practice point statement "Pay
                Now" buttons at ANY URL — Square Online Checkout,
                PayPal.me, Venmo, custom payments page — without
                requiring the Square API key to be configured.  When
                set, this overrides the Square API path entirely. */}
            {organizations?.[0] && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium">Payments</h3>
                </div>
                <Card className="p-4 space-y-3">
                  <div>
                    <Label htmlFor="org-payment-link" className="text-sm font-medium">
                      Public Payment Link
                    </Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Paste the URL you want patients to land on when they click <strong>Pay Now</strong> in their statement email. Works with Square&apos;s public checkout link, PayPal.me, Venmo, or any other payments page. Leave blank to fall back to the Square Checkout API.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        id="org-payment-link"
                        type="url"
                        placeholder="https://squareup.com/pay/your-business"
                        defaultValue={organizations[0].payment_link || ''}
                        onChange={(e) => {
                          window.__pendingPaymentLink = e.target.value;
                        }}
                        data-testid="org-payment-link-input"
                      />
                      <Button
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-700 whitespace-nowrap"
                        onClick={async () => {
                          const newLink = (window.__pendingPaymentLink ?? organizations[0].payment_link ?? '').trim();
                          try {
                            // Empty string clears the override (the
                            // backend update filters out null, so we
                            // send "" to actually overwrite).  The
                            // statement builder treats empty as "no
                            // override — call Square API".
                            await axios.patch(
                              `${API}/organizations/${organizations[0].id}`,
                              { payment_link: newLink },
                              { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } },
                            );
                            toast.success(newLink ? 'Payment link saved' : 'Payment link cleared');
                            if (typeof fetchOrganizations === 'function') await fetchOrganizations();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to save payment link');
                          }
                        }}
                        data-testid="org-payment-link-save"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setShowOrgConfig(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Terms of Use Editor Dialog */}
      <Dialog open={showTosEditor} onOpenChange={(open) => {
        if (!open) {
          setShowTosEditor(false);
        } else {
          // Load current TOS when opening
          (async () => {
            try {
              const response = await axios.get(`${API}/admin/portal-legal-documents`, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
              });
              if (response.data.tos) {
                setTosEditorContent(response.data.tos.content || '');
                setTosEditorVersion(response.data.tos.version || '');
              } else {
                setTosEditorContent('');
                setTosEditorVersion(new Date().toISOString().split('T')[0]);
              }
            } catch (error) {
              console.error('Error loading TOS:', error);
              setTosEditorVersion(new Date().toISOString().split('T')[0]);
            }
          })();
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Terms of Use</DialogTitle>
            <DialogDescription>
              Update the Terms of Use shown to clients. When you change the version, clients who accepted an older version will be prompted to re-accept.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Version (YYYY-MM-DD format)</Label>
              <DebouncedInput
                value={tosEditorVersion}
                onChange={(e) => setTosEditorVersion(e.target.value)}
                placeholder="2026-02-03"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Changing this will require clients to re-accept the terms</p>
            </div>
            
            <div>
              <Label>Terms of Use Content (HTML supported)</Label>
              <DebouncedTextarea
                value={tosEditorContent}
                onChange={(e) => setTosEditorContent(e.target.value)}
                placeholder="Enter the Terms of Use content..."
                className="mt-1 min-h-[400px] font-mono text-sm"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowTosEditor(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!tosEditorVersion || !tosEditorContent) {
                  toast.error('Version and content are required');
                  return;
                }
                setSavingLegalDoc(true);
                try {
                  await axios.post(`${API}/admin/portal-legal-documents/tos`, {
                    version: tosEditorVersion,
                    content: tosEditorContent
                  }, {
                    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                  });
                  toast.success('Terms of Use updated successfully');
                  setShowTosEditor(false);
                } catch (error) {
                  toast.error('Failed to save Terms of Use');
                } finally {
                  setSavingLegalDoc(false);
                }
              }}
              disabled={savingLegalDoc}
            >
              {savingLegalDoc ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notice of Privacy Practices Editor Dialog */}
      <Dialog open={showNppEditor} onOpenChange={(open) => {
        if (!open) {
          setShowNppEditor(false);
        } else {
          // Load current NPP when opening
          (async () => {
            try {
              const response = await axios.get(`${API}/admin/portal-legal-documents`, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
              });
              if (response.data.npp) {
                setNppEditorContent(response.data.npp.content || '');
                setNppEditorVersion(response.data.npp.version || '');
              } else {
                setNppEditorContent('');
                setNppEditorVersion(new Date().toISOString().split('T')[0]);
              }
            } catch (error) {
              console.error('Error loading NPP:', error);
              setNppEditorVersion(new Date().toISOString().split('T')[0]);
            }
          })();
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Notice of Privacy Practices</DialogTitle>
            <DialogDescription>
              Update the Notice of Privacy Practices (NPP) that clients can view from the portal.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Version (YYYY-MM-DD format)</Label>
              <DebouncedInput
                value={nppEditorVersion}
                onChange={(e) => setNppEditorVersion(e.target.value)}
                placeholder="2026-02-03"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Notice of Privacy Practices Content (HTML supported)</Label>
              <DebouncedTextarea
                value={nppEditorContent}
                onChange={(e) => setNppEditorContent(e.target.value)}
                placeholder="Enter the Notice of Privacy Practices content..."
                className="mt-1 min-h-[400px] font-mono text-sm"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowNppEditor(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!nppEditorVersion || !nppEditorContent) {
                  toast.error('Version and content are required');
                  return;
                }
                setSavingLegalDoc(true);
                try {
                  await axios.post(`${API}/admin/portal-legal-documents/npp`, {
                    version: nppEditorVersion,
                    content: nppEditorContent
                  }, {
                    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                  });
                  toast.success('Notice of Privacy Practices updated successfully');
                  setShowNppEditor(false);
                } catch (error) {
                  toast.error('Failed to save Notice of Privacy Practices');
                } finally {
                  setSavingLegalDoc(false);
                }
              }}
              disabled={savingLegalDoc}
            >
              {savingLegalDoc ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Insurance Management Dialog */}
      <ManageInsuranceDialog ctx={{
        API,
        insurances,
        fetchInsurances,
        setEditingInsurance,
        setInsuranceFormData,
        setShowAddInsurance,
        setShowEditInsurance,
        setMigratingInsurance,
        showPayerAuditLog,
        setShowPayerAuditLog,
        insuranceViewMode,
        setInsuranceViewMode,
        showArchivedInsurances,
        setShowArchivedInsurances,
        showManageInsurance,
        setShowManageInsurance,
        headerExtras: (<AdminJumpToDropdown currentDialogId="insurance" isDirty={currentAdminDialogDirty} />),
        refreshNotes,
        refreshClients,
        refreshStats,
      }} />

      {/* Manage Groups Dialog */}
      {/* Curriculum Library Dialog - Extracted Component */}
      <CurriculumLibraryDialog
        open={showCurriculumLibrary}
        onOpenChange={setShowCurriculumLibrary}
        curricula={curricula}
        setCurricula={setCurricula}
        fetchCurricula={fetchCurricula}
        canEdit={isSuperAdmin(user) || userRoleName === 'admin' || user?.group_admin || isGroupFacilitator(user, clientGroups)}
      />

      {/* Insurance migration dialog — admin tool for retiring an insurance carrier
          by reassigning its current client roster to either another carrier or self-pay.
          Optionally archives the source after migration. Wired from the Migrate icon
          in both card and list views of the Insurances admin page. */}
      {migratingInsurance && (
        <Suspense fallback={null}>
          <InsuranceMigrationDialog
            open={!!migratingInsurance}
            onOpenChange={(o) => { if (!o) setMigratingInsurance(null); }}
            source={migratingInsurance}
            allInsurances={insurances || []}
            onComplete={() => fetchInsurances()}
          />
        </Suspense>
      )}

      {/* Group Note Creation Dialog - For creating individual notes per group member */}
      <GroupNoteCreationDialog
        open={showGroupNoteDialog}
        onOpenChange={(open) => {
          setShowGroupNoteDialog(open);
          if (!open) {
            // Reset mode when closing
            setGroupNoteMode('create');
            setGroupNoteExistingNotes([]);
          }
        }}
        appointment={groupNoteAppointment}
        group={groupNoteGroup}
        clients={allClientsForLookup?.length > 0 ? allClientsForLookup : clients}
        findClientById={findClientById}
        templates={formTemplates}
        currentUser={user}
        curriculumSession={groupNoteCurriculumSession}
        existingNotes={groupNoteExistingNotes}
        mode={groupNoteMode}
        locations={locations}
        posCodes={posCodes}
        brandingSettings={brandingSettings}
        organizations={organizations}
        curricula={curricula}
        setCurricula={setCurricula}
        canEditCurriculum={isSuperAdmin(user) || userRoleName === 'admin' || user?.group_admin || isGroupFacilitator(user, clientGroups)}
        onNotesCreated={() => {
          refreshAppointments(true);
          setShowGroupNoteDialog(false);
          setGroupNoteMode('create');
          setGroupNoteExistingNotes([]);
        }}
        onRefresh={() => refreshAppointments(true)}
      />

      {/* Group Attendance Snapshot */}
      <GroupAttendanceSnapshot
        open={showAttendanceSnapshot}
        onOpenChange={(open) => {
          setShowAttendanceSnapshot(open);
          if (!open) {
            setAttendanceSnapshotGroup(null);
            // Update URL to remove history mode
            navigate(`/dashboard?view=groups`, { replace: true });
          }
        }}
        group={attendanceSnapshotGroup}
        clients={allClientsForLookup}
        locations={locations}
        brandingSettings={brandingSettings}
      />

      {/* Change History Modal */}
      <ChangeHistoryModal
        open={showChangeHistory}
        onOpenChange={setShowChangeHistory}
        entityType={changeHistoryEntity.type}
        entityId={changeHistoryEntity.id}
        entityName={changeHistoryEntity.name}
      />

      {/* Survey Dashboard — lazy-mounted only when opened. */}
      {showSurveyDashboard && (
        <Suspense fallback={null}>
          <SurveyDashboard
            open={true}
            onOpenChange={setShowSurveyDashboard}
            adminJumpToDropdown={<AdminJumpToDropdown currentDialogId="clientSurveys" isDirty={currentAdminDialogDirty} />}
          />
        </Suspense>
      )}

      {/* Break Time - Self-contained with Konami code + subtle footer link */}
      <BreakTime />

      {/* Organization dialogs — Add + Edit consolidated into a single
          OrganizationFormDialog (Feb 2026 refactor).  Add omits the
          Practice Address block; Edit includes it with full Medicare
          PECOS guidance.  Wire format and submit handlers unchanged. */}
      <OrganizationFormDialog
        open={showAddOrganization}
        onOpenChange={setShowAddOrganization}
        formData={organizationFormData}
        setFormData={setOrganizationFormData}
        onSubmit={handleCreateOrganization}
        onCancel={() => setShowAddOrganization(false)}
        editing={false}
      />
      <OrganizationFormDialog
        open={showEditOrganization}
        onOpenChange={setShowEditOrganization}
        formData={organizationFormData}
        setFormData={setOrganizationFormData}
        onSubmit={handleUpdateOrganization}
        onCancel={() => setShowEditOrganization(false)}
        editing={true}
      />

      {/* Add Insurance Dialog */}
      {/* ── SHARED Insurance Company form fields ──────────────────────
          Renders the union of all fields used by both the Add and
          Edit dialogs.  Prior to Feb 2026 these were two separate
          ~340-line copies that drifted: the Add form had timely-
          filing fields the Edit form lacked; the Edit form had per-
          clearinghouse payer-ID fields the Add form lacked.  A single
          source means a new field gets added once and shows up in
          both dialogs automatically.

          The `mode` prop ('create' | 'edit') only affects:
            • testid / id prefix ('ins-' vs 'edit-ins-')
          State + setter (`insuranceFormData` / `setInsuranceFormData`)
          and submit handlers stay in the parent dialog. */}
      {/* Insurance Add/Edit dialogs — share a single field renderer
          so they can never drift again. The IIFE binds the renderer
          locally (no `globalThis` round-trip) so even if React
          re-orders sibling renders this can't desync between the two
          Dialogs.

          NOTE: the previous implementation assigned the renderer to
          `globalThis.__insuranceFormFieldsRenderer` in a sibling JSX
          expression and then called it from both Dialogs. That worked
          but was fragile — if the assignment block ever rendered
          AFTER one of the Dialogs (e.g. a future refactor that
          conditionally mounts the assignment), the Dialog would crash
          with "undefined is not a function". A user reported being
          unable to find the new "Include provider address" toggle in
          the Edit dialog (Feb 2026); root cause was either deploy
          lag or the brittle global pattern. Local closure is safer. */}
      <InsuranceFormDialogs ctx={{
        API,
        insuranceFormData,
        setInsuranceFormData,
        insurances,
        setInsurances,
        editingInsurance,
        setEditingInsurance,
        showAddInsurance,
        setShowAddInsurance,
        showEditInsurance,
        setShowEditInsurance,
        fetchInsurances,
      }} />

      {/* Manage CPT Codes Dialog */}
      <Dialog open={showManageCptCodes} onOpenChange={(open) => {
        setShowManageCptCodes(open);
        if (open) {
          fetchCptBundles();
          checkCptCodeUsage(); // Check which CPT codes have appointments
          // Make sure CPT codes are loaded for bundle display
          if (!cptCodes || cptCodes.length === 0) {
            // Static data - no global refresh needed
          }
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Manage CPT Codes & Bundles</DialogTitle>
            <AdminJumpToDropdown currentDialogId="cpt" isDirty={currentAdminDialogDirty} />
          </DialogHeader>
          
          <Tabs value={cptDialogTab} onValueChange={(value) => {
            setCptDialogTab(value);
            if (value === "bundles") {
              fetchCptBundles();
            }
          }} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="codes">CPT Codes</TabsTrigger>
              <TabsTrigger value="bundles">Bundles</TabsTrigger>
            </TabsList>
            
            <TabsContent value="codes" className="space-y-6">
              {/* Top row - Filters and Action Buttons */}
              <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <h3 className="text-lg font-medium">CPT Codes</h3>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={cptCodeFilter === 'active' ? 'default' : 'outline'}
                        onClick={() => setCptCodeFilter('active')}
                      >
                        Active ({cptCodes?.filter(c => c.is_active !== false).length || 0})
                      </Button>
                      <Button
                        size="sm"
                        variant={cptCodeFilter === 'archived' ? 'default' : 'outline'}
                        onClick={() => setCptCodeFilter('archived')}
                      >
                        Archived ({cptCodes?.filter(c => c.is_active === false).length || 0})
                      </Button>
                      <Button
                        size="sm"
                        variant={cptCodeFilter === 'all' ? 'default' : 'outline'}
                        onClick={() => setCptCodeFilter('all')}
                      >
                        All ({cptCodes?.length || 0})
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/cpt-codes/template/csv`, {
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'cpt_codes_template.csv');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success("Template downloaded - see examples inside!");
                      } catch (error) {
                        toast.error("Failed to download template");
                      }
                    }}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download Template
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/cpt-codes/export/csv`, {
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'cpt_codes.csv');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success("CPT codes exported successfully");
                      } catch (error) {
                        toast.error("Failed to export CSV");
                      }
                    }}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Export Current
                  </Button>
                  <Dialog open={showCptImportDialog} onOpenChange={setShowCptImportDialog}>
                    <DialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Upload CSV
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Import CPT Codes from CSV</DialogTitle>
                        <DialogDescription>
                          Choose how to handle existing CPT codes when uploading
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-3">
                          <Label>Import Mode</Label>
                          <div className="space-y-2">
                            <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                              <input
                                type="radio"
                                name="import-mode"
                                value="create_and_update"
                                checked={cptImportMode === 'create_and_update'}
                                onChange={(e) => setCptImportMode(e.target.value)}
                                className="mt-1"
                              />
                              <div>
                                <p className="font-semibold">Create & Update (Recommended)</p>
                                <p className="text-xs text-gray-600">Create new CPT codes and update existing ones with new data (including pricing)</p>
                              </div>
                            </label>
                            <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                              <input
                                type="radio"
                                name="import-mode"
                                value="update_only"
                                checked={cptImportMode === 'update_only'}
                                onChange={(e) => setCptImportMode(e.target.value)}
                                className="mt-1"
                              />
                              <div>
                                <p className="font-semibold">Update Only</p>
                                <p className="text-xs text-gray-600">Only update existing CPT codes, skip new ones</p>
                              </div>
                            </label>
                            <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                              <input
                                type="radio"
                                name="import-mode"
                                value="create_only"
                                checked={cptImportMode === 'create_only'}
                                onChange={(e) => setCptImportMode(e.target.value)}
                                className="mt-1"
                              />
                              <div>
                                <p className="font-semibold">Create Only</p>
                                <p className="text-xs text-gray-600">Only create new CPT codes, skip existing ones</p>
                              </div>
                            </label>
                          </div>
                        </div>
                        <div>
                          <Button
                            onClick={() => document.getElementById('cpt-csv-upload').click()}
                            className="w-full"
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Select CSV File
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <input
                    id="cpt-csv-upload"
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('mode', cptImportMode);
                      
                      try {
                        const response = await axios.post(`${API}/cpt-codes/import/csv?mode=${cptImportMode}`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        
                        const { created, updated, skipped, errors } = response.data;
                        let message = `CSV Import Complete:\n✅ Created: ${created}\n🔄 Updated: ${updated}\n⏭️ Skipped: ${skipped}`;
                        if (errors.length > 0) {
                          message += `\n❌ Errors: ${errors.length}\n${errors.slice(0, 5).join('\n')}`;
                          if (errors.length > 5) message += `\n... and ${errors.length - 5} more`;
                        }
                        
                        if (errors.length > 0) {
                          toast.error(message, { duration: 10000 });
                        } else {
                          toast.success(message);
                        }
                        
                        // Static data - no global refresh needed // Refresh CPT codes
                        setShowCptImportDialog(false);
                        e.target.value = ''; // Reset file input
                      } catch (error) {
                        console.error('CSV upload error:', error);
                        toast.error(error.response?.data?.detail || "Failed to upload CSV");
                        e.target.value = ''; // Reset file input
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={() => setShowCptForm(true)}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add CPT Code
                  </Button>
                </div>
              </div>
              
              {/* Second row - Search and Sort */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 flex-1">
                  <Search className="h-4 w-4 text-gray-400" />
                  <DebouncedInput
                    placeholder="Search CPT codes by code, name, description, or category..."
                    value={cptCodeSearch}
                    onChange={(e) => setCptCodeSearch(e.target.value)}
                    className="flex-1"
                  />
                </div>
                <Select value={cptCodeSort} onValueChange={setCptCodeSort}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="code">Sort by Code</SelectItem>
                    <SelectItem value="name">Sort by Name</SelectItem>
                    <SelectItem value="duration">Sort by Duration</SelectItem>
                    <SelectItem value="category">Sort by Category</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            
            <div className="space-y-3">
              {cptCodes
                ?.filter(code => {
                  // Status filter
                  if (cptCodeFilter === 'active' && code.is_active === false) return false;
                  if (cptCodeFilter === 'archived' && code.is_active !== false) return false;
                  
                  // Search filter
                  if (cptCodeSearch) {
                    const searchTerm = cptCodeSearch.toLowerCase();
                    const searchableText = [
                      code.code,
                      code.display_name,
                      code.description,
                      code.category
                    ].filter(Boolean).join(' ').toLowerCase();
                    
                    if (!searchableText.includes(searchTerm)) return false;
                  }
                  
                  return true;
                })
                ?.sort((a, b) => {
                  switch (cptCodeSort) {
                    case 'code':
                      return (a.code || '').localeCompare(b.code || '');
                    case 'name':
                      return (a.display_name || a.code || '').localeCompare(b.display_name || b.code || '');
                    case 'duration':
                      return (a.minutes_per_unit || a.duration_minutes || 0) - (b.minutes_per_unit || b.duration_minutes || 0);
                    case 'category':
                      return (a.category || '').localeCompare(b.category || '');
                    default:
                      return 0;
                  }
                })
                .map(code => (
                <Card key={code.id} className={`p-4 ${!code.is_active ? 'bg-gray-100 opacity-60' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-lg">{code.display_name || code.code} - {code.description}</h4>
                        {!code.is_active && (
                          <Badge variant="secondary" className="text-xs">Archived</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        Duration: {code.minutes_per_unit || code.duration_minutes} min/unit | 
                        {code.base_price ? ` Price: $${code.base_price.toFixed(2)} |` : ' No price set |'}
                        {code.min_duration && code.max_duration ? ` Valid Range: ${code.min_duration}-${code.max_duration} min |` : ''}
                        {code.allows_multiple_units ? ' Multiple units allowed' : ' Fixed duration'}
                        {(code.min_age !== null && code.min_age !== undefined) || (code.max_age !== null && code.max_age !== undefined) ? ' | Age: ' : ''}
                        {code.min_age !== null && code.min_age !== undefined && code.max_age !== null && code.max_age !== undefined ? `${code.min_age}-${code.max_age} years` : ''}
                        {code.min_age !== null && code.min_age !== undefined && (code.max_age === null || code.max_age === undefined) ? `${code.min_age}+ years` : ''}
                        {(code.min_age === null || code.min_age === undefined) && code.max_age !== null && code.max_age !== undefined ? `up to ${code.max_age} years` : ''}
                      </p>
                      {code.assigned_users?.length > 0 && (
                        <div className="mt-2">
                          <Label className="text-xs text-gray-500">Assigned to ({code.assigned_users.length})</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {code.assigned_users.map((userId, idx) => {
                              const user = users.find(u => u.id === userId);
                              return (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {user?.full_name || userId}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          // Set up editing state for CPT code
                          setCptFormData({
                            code: code.code,
                            display_name: code.display_name || '',
                            description: code.description,
                            category: code.category || '',
                            bundle_ids: code.bundle_ids || [],
                            billing_type: code.billing_type || 'insurance',
                            base_price: code.base_price || null,
                            // Self-Pay Pricing
                            self_pay_discount_type: code.self_pay_discount_type || 'none',
                            self_pay_discount_percent: code.self_pay_discount_percent || null,
                            self_pay_discount_amount: code.self_pay_discount_amount || null,
                            self_pay_rate: code.self_pay_rate || null,
                            units: code.units || 1,
                            duration_minutes: code.duration_minutes || 45,
                            minutes_per_unit: code.minutes_per_unit || 45,
                            allows_multiple_units: code.allows_multiple_units !== undefined ? code.allows_multiple_units : true,
                            is_group_session: code.is_group_session || false,
                            default_group_id: code.default_group_id || '',
                            default_template_id: code.default_template_id || '',
                            min_duration: code.min_duration || null,
                            max_duration: code.max_duration || null,
                            min_age: code.min_age || null,
                            max_age: code.max_age || null,
                            pos_codes: code.pos_codes || [],
                            modifiers: code.modifiers || [],
                            color: code.color || '#3B82F6',
                            is_addon_code: code.is_addon_code || false,
                            requires_primary_codes: code.requires_primary_codes || [],
                            requires_authorization: code.requires_authorization || false,
                            allow_authorization: code.allow_authorization || false,
                            allows_overlap: code.allows_overlap || false
                          });
                          setEditingCptCode(code);
                          setShowCptForm(true);
                        }}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          // Duplicate - copy all fields but don't set editingCptCode
                          setCptFormData({
                            code: '', // Clear code so they enter a new one
                            display_name: code.display_name || '',
                            description: code.description,
                            category: code.category || '',
                            bundle_ids: code.bundle_ids || [],
                            billing_type: code.billing_type || 'insurance',
                            base_price: code.base_price || null,
                            // Self-Pay Pricing
                            self_pay_discount_type: code.self_pay_discount_type || 'none',
                            self_pay_discount_percent: code.self_pay_discount_percent || null,
                            self_pay_discount_amount: code.self_pay_discount_amount || null,
                            self_pay_rate: code.self_pay_rate || null,
                            units: code.units || 1,
                            duration_minutes: code.duration_minutes || 45,
                            minutes_per_unit: code.minutes_per_unit || 45,
                            allows_multiple_units: code.allows_multiple_units !== undefined ? code.allows_multiple_units : true,
                            is_group_session: code.is_group_session || false,
                            min_duration: code.min_duration || null,
                            max_duration: code.max_duration || null,
                            min_age: code.min_age || null,
                            max_age: code.max_age || null,
                            pos_codes: code.pos_codes || [],
                            modifiers: code.modifiers || [],
                            color: code.color || '#3B82F6',
                            is_addon_code: code.is_addon_code || false,
                            requires_primary_codes: code.requires_primary_codes || [],
                            allows_overlap: code.allows_overlap || false
                          });
                          setEditingCptCode(null); // Important: don't set editing, this is a new code
                          setShowCptForm(true);
                          toast.info(`Duplicating ${code.code} - enter a new code`);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Duplicate
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleArchiveCptCode(code.id, code.is_active)}
                      >
                        {code.is_active ? (
                          <>
                            <Archive className="h-4 w-4 mr-1" />
                            Archive
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Activate
                          </>
                        )}
                      </Button>
                      {(() => {
                        const usage = cptCodeUsage[code.id];
                        const hasAppointments = usage && usage.appointment_count > 0;
                        
                        return (
                          <div className="relative group">
                            <Button 
                              size="sm" 
                              variant="destructive"
                              disabled={hasAppointments}
                              onClick={() => handleDeleteCptCode(code.id, code.display_name || code.code)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                            {hasAppointments && (
                              <div className="absolute bottom-full mb-2 right-0 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                ⚠️ Cannot delete: {usage.appointment_count} appointment(s) use this code. Use Archive instead to preserve history.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </Card>
              ))}
              
              {(!cptCodes || cptCodes.length === 0) && (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">No CPT codes configured</p>
                  <Button
                    onClick={() => setShowCptForm(true)}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Your First CPT Code
                  </Button>
                </div>
              )}
            </div>
            </TabsContent>
            
            <TabsContent value="bundles" className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">CPT Code Bundles</h3>
                <Button
                  size="sm"
                  onClick={() => {
                    setBundleFormData({ name: '', description: '', cpt_code_ids: [] });
                    setEditingBundle(null);
                    setShowBundleForm(true);
                  }}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create Bundle
                </Button>
              </div>
              
              <div className="space-y-3">
                {cptBundles?.map(bundle => {
                  void 0 && console.log("Rendering bundle:", bundle.name, "with CPT IDs:", bundle.cpt_code_ids);
                  void 0 && console.log("Available CPT codes:", cptCodes?.length, "codes");
                  return (
                    <Card key={bundle.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-lg">{bundle.name}</h4>
                          <p className="text-sm text-gray-600">{bundle.description}</p>
                          <div className="mt-2">
                            <Label className="text-xs text-gray-500">CPT Codes ({bundle.cpt_code_ids?.length || 0})</Label>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {bundle.cpt_code_ids?.map((codeId) => {
                                const code = cptCodes.find(c => c.id === codeId);
                                void 0 && console.log("Looking for code ID:", codeId, "Found:", code);
                                return code ? (
                                  <Badge key={codeId} variant="outline" className="text-xs">
                                    {code.code}
                                  </Badge>
                                ) : (
                                  <Badge key={codeId} variant="outline" className="text-xs bg-red-100">
                                    ID: {codeId.substring(0, 8)}...
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setBundleFormData({
                                name: bundle.name,
                                description: bundle.description,
                                cpt_code_ids: bundle.cpt_code_ids || []
                              });
                              setEditingBundle(bundle);
                              setShowBundleForm(true);
                            }}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              if (window.confirm(`Delete bundle "${bundle.name}"?`)) {
                                handleDeleteBundle(bundle.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
                
                {(!cptBundles || cptBundles.length === 0) && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">No bundles created yet</p>
                    <Button
                      onClick={() => {
                        setBundleFormData({ name: '', description: '', cpt_code_ids: [] });
                        setEditingBundle(null);
                        setShowBundleForm(true);
                      }}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Create Your First Bundle
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setShowManageCptCodes(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit CPT Code Dialog */}
      <Dialog open={showCptForm} onOpenChange={setShowCptForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCptCode ? 'Edit CPT Code' : 'Add CPT Code'}</DialogTitle>
            <DialogDescription>
              {editingCptCode ? 'Update the CPT code details below' : 'Enter the details for the new CPT code'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCptCode} className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="cpt_code">CPT Code *</Label>
                <DebouncedInput
                  id="cpt_code"
                  value={cptFormData.code}
                  onChange={(e) => setCptFormData(prev => ({...prev, code: e.target.value}))}
                  placeholder="e.g., 90837"
                  required
                  disabled={!!editingCptCode}
                />
              </div>
              <div>
                <Label htmlFor="cpt_display_name">Display Name</Label>
                <DebouncedInput
                  id="cpt_display_name"
                  value={cptFormData.display_name || ''}
                  onChange={(e) => setCptFormData(prev => ({...prev, display_name: e.target.value}))}
                  placeholder="e.g., Individual Therapy 60min"
                />
              </div>
              <div>
                <Label htmlFor="cpt_category">Category</Label>
                <Select
                  value={cptFormData.category || ''}
                  onValueChange={(value) => setCptFormData(prev => ({...prev, category: value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="evaluation">Evaluation</SelectItem>
                    <SelectItem value="individual_therapy">Individual Therapy</SelectItem>
                    <SelectItem value="group_therapy">Group Therapy</SelectItem>
                    <SelectItem value="family_therapy">Family Therapy</SelectItem>
                    <SelectItem value="testing">Testing</SelectItem>
                    <SelectItem value="crisis">Crisis</SelectItem>
                    <SelectItem value="medication">Medication Management</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="cpt_description">Description</Label>
              <DebouncedTextarea
                id="cpt_description"
                value={cptFormData.description || ''}
                onChange={(e) => setCptFormData(prev => ({...prev, description: e.target.value}))}
                placeholder="Describe this CPT code..."
                rows={2}
              />
            </div>

            {/* Duration & Units */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm">Duration & Units</h4>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="cpt_duration" className="flex items-center gap-1">
                    Default Appt Length
                    <span className="text-gray-400 cursor-help" title="Total appointment duration in minutes. Used for scheduling and display.">ⓘ</span>
                  </Label>
                  <Input
                    id="cpt_duration"
                    type="number"
                    value={cptFormData.duration_minutes || 45}
                    onChange={(e) => setCptFormData(prev => ({...prev, duration_minutes: parseInt(e.target.value) || 45}))}
                  />
                  <p className="text-xs text-gray-500 mt-1">Total minutes for appointment</p>
                </div>
                <div>
                  <Label htmlFor="cpt_min_per_unit" className="flex items-center gap-1">
                    Minutes Per Unit
                    <span className="text-gray-400 cursor-help" title="Minutes per billing unit. Used to calculate duration when multiple units are billed (e.g., 2 units × 45 min = 90 min appointment).">ⓘ</span>
                  </Label>
                  <Input
                    id="cpt_min_per_unit"
                    type="number"
                    value={cptFormData.minutes_per_unit || 45}
                    onChange={(e) => setCptFormData(prev => ({...prev, minutes_per_unit: parseInt(e.target.value) || 45}))}
                  />
                  <p className="text-xs text-gray-500 mt-1">For billing unit calculations</p>
                </div>
                <div>
                  <Label htmlFor="cpt_units" className="flex items-center gap-1">
                    Default Units
                    <span className="text-gray-400 cursor-help" title="Number of billing units to default to when scheduling this CPT code.">ⓘ</span>
                  </Label>
                  <Input
                    id="cpt_units"
                    type="number"
                    value={cptFormData.units || 1}
                    onChange={(e) => setCptFormData(prev => ({...prev, units: parseInt(e.target.value) || 1}))}
                  />
                  <p className="text-xs text-gray-500 mt-1">Billing units per session</p>
                </div>
                <div className="flex items-end pb-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cpt_multiple_units"
                      checked={cptFormData.allows_multiple_units !== false}
                      onCheckedChange={(checked) => setCptFormData(prev => ({...prev, allows_multiple_units: checked}))}
                    />
                    <Label htmlFor="cpt_multiple_units" className="text-sm flex items-center gap-1">
                      Allow Multiple Units
                      <span className="text-gray-400 cursor-help" title="If checked, providers can adjust the number of units when scheduling (e.g., for extended sessions).">ⓘ</span>
                    </Label>
                  </div>
                </div>
                <div className="flex items-end pb-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cpt_allows_overlap"
                      checked={cptFormData.allows_overlap === true}
                      onCheckedChange={(checked) => setCptFormData(prev => ({...prev, allows_overlap: checked === true}))}
                      data-testid="cpt-allows-overlap-checkbox"
                    />
                    <Label htmlFor="cpt_allows_overlap" className="text-sm flex items-center gap-1">
                      Allow Overlapping Appointments
                      <span className="text-gray-400 cursor-help" title="If checked, appointments with this CPT code can overlap other appointments whose CPT code ALSO allows overlapping. Conflict detection only skips when both codes allow it.">ⓘ</span>
                    </Label>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cpt_min_duration" className="flex items-center gap-1">
                    Minimum Duration
                    <span className="text-gray-400 cursor-help" title="Optional validation: appointments shorter than this will trigger a warning to use a different CPT code.">ⓘ</span>
                  </Label>
                  <Input
                    id="cpt_min_duration"
                    type="number"
                    value={cptFormData.min_duration || ''}
                    onChange={(e) => setCptFormData(prev => ({...prev, min_duration: e.target.value ? parseInt(e.target.value) : null}))}
                    placeholder="No minimum"
                  />
                  <p className="text-xs text-gray-500 mt-1">Warn if appointment is shorter</p>
                </div>
                <div>
                  <Label htmlFor="cpt_max_duration" className="flex items-center gap-1">
                    Maximum Duration
                    <span className="text-gray-400 cursor-help" title="Optional validation: appointments longer than this will trigger a warning to use a different CPT code.">ⓘ</span>
                  </Label>
                  <Input
                    id="cpt_max_duration"
                    type="number"
                    value={cptFormData.max_duration || ''}
                    onChange={(e) => setCptFormData(prev => ({...prev, max_duration: e.target.value ? parseInt(e.target.value) : null}))}
                    placeholder="No maximum"
                  />
                  <p className="text-xs text-gray-500 mt-1">Warn if appointment is longer</p>
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm">Pricing</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cpt_billing_type">Billing Type</Label>
                  <Select
                    value={cptFormData.billing_type || 'insurance'}
                    onValueChange={(value) => setCptFormData(prev => ({...prev, billing_type: value}))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="insurance">Insurance</SelectItem>
                      <SelectItem value="self_pay">Self Pay Only</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="cpt_base_price">Base Price ($)</Label>
                  <Input
                    id="cpt_base_price"
                    type="number"
                    step="0.01"
                    value={cptFormData.base_price || ''}
                    onChange={(e) => setCptFormData(prev => ({...prev, base_price: e.target.value ? parseFloat(e.target.value) : null}))}
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div className="pt-2 border-t">
                <Label className="text-sm font-medium">Self-Pay Pricing</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <Label htmlFor="cpt_self_pay_type" className="text-xs">Discount Type</Label>
                    <Select
                      value={cptFormData.self_pay_discount_type || 'none'}
                      onValueChange={(value) => setCptFormData(prev => ({...prev, self_pay_discount_type: value}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Discount</SelectItem>
                        <SelectItem value="percentage">Percentage Off</SelectItem>
                        <SelectItem value="fixed_amount">Fixed Amount Off</SelectItem>
                        <SelectItem value="fixed_rate">Fixed Rate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {cptFormData.self_pay_discount_type === 'percentage' && (
                    <div>
                      <Label htmlFor="cpt_self_pay_percent" className="text-xs">Discount %</Label>
                      <Input
                        id="cpt_self_pay_percent"
                        type="number"
                        value={cptFormData.self_pay_discount_percent || ''}
                        onChange={(e) => setCptFormData(prev => ({...prev, self_pay_discount_percent: e.target.value ? parseFloat(e.target.value) : null}))}
                        placeholder="e.g., 20"
                      />
                    </div>
                  )}
                  {cptFormData.self_pay_discount_type === 'fixed_amount' && (
                    <div>
                      <Label htmlFor="cpt_self_pay_amount" className="text-xs">Discount Amount ($)</Label>
                      <Input
                        id="cpt_self_pay_amount"
                        type="number"
                        step="0.01"
                        value={cptFormData.self_pay_discount_amount || ''}
                        onChange={(e) => setCptFormData(prev => ({...prev, self_pay_discount_amount: e.target.value ? parseFloat(e.target.value) : null}))}
                        placeholder="e.g., 25.00"
                      />
                    </div>
                  )}
                  {cptFormData.self_pay_discount_type === 'fixed_rate' && (
                    <div>
                      <Label htmlFor="cpt_self_pay_rate" className="text-xs">Fixed Rate ($)</Label>
                      <Input
                        id="cpt_self_pay_rate"
                        type="number"
                        step="0.01"
                        value={cptFormData.self_pay_rate || ''}
                        onChange={(e) => setCptFormData(prev => ({...prev, self_pay_rate: e.target.value ? parseFloat(e.target.value) : null}))}
                        placeholder="e.g., 150.00"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Age Restrictions */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm">Age Restrictions (Optional)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cpt_min_age">Minimum Age</Label>
                  <Input
                    id="cpt_min_age"
                    type="number"
                    value={cptFormData.min_age || ''}
                    onChange={(e) => setCptFormData(prev => ({...prev, min_age: e.target.value ? parseInt(e.target.value) : null}))}
                    placeholder="No minimum"
                  />
                </div>
                <div>
                  <Label htmlFor="cpt_max_age">Maximum Age</Label>
                  <Input
                    id="cpt_max_age"
                    type="number"
                    value={cptFormData.max_age || ''}
                    onChange={(e) => setCptFormData(prev => ({...prev, max_age: e.target.value ? parseInt(e.target.value) : null}))}
                    placeholder="No maximum"
                  />
                </div>
              </div>
            </div>

            {/* POS Codes & Modifiers */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm">Place of Service & Modifiers</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cpt_pos">POS Codes</Label>
                  <DebouncedInput
                    id="cpt_pos"
                    value={Array.isArray(cptFormData.pos_codes) ? cptFormData.pos_codes.join(', ') : (cptFormData.pos_codes || '')}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Keep raw string while typing, only split into array when there's content
                      if (val.includes(',')) {
                        // User has typed commas, store as array but preserve trailing comma for typing
                        const parts = val.split(',').map(s => s.trim());
                        // If last character is comma or space after comma, keep an empty slot
                        if (val.endsWith(',') || val.endsWith(', ')) {
                          setCptFormData(prev => ({...prev, pos_codes: parts}));
                        } else {
                          setCptFormData(prev => ({...prev, pos_codes: parts.filter(Boolean)}));
                        }
                      } else {
                        // Single value or empty
                        setCptFormData(prev => ({...prev, pos_codes: val.trim() ? [val.trim()] : []}));
                      }
                    }}
                    onBlur={(e) => {
                      // Clean up on blur - remove empty entries
                      const cleaned = (cptFormData.pos_codes || []).filter(s => s && s.trim());
                      setCptFormData(prev => ({...prev, pos_codes: cleaned}));
                    }}
                    placeholder="e.g., 11, 02 (comma-separated)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Common: 11 (Office), 02 (Telehealth)</p>
                </div>
                <div>
                  <Label htmlFor="cpt_modifiers">Modifiers</Label>
                  <DebouncedInput
                    id="cpt_modifiers"
                    value={Array.isArray(cptFormData.modifiers) ? cptFormData.modifiers.join(', ') : (cptFormData.modifiers || '')}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.includes(',')) {
                        const parts = val.split(',').map(s => s.trim());
                        if (val.endsWith(',') || val.endsWith(', ')) {
                          setCptFormData(prev => ({...prev, modifiers: parts}));
                        } else {
                          setCptFormData(prev => ({...prev, modifiers: parts.filter(Boolean)}));
                        }
                      } else {
                        setCptFormData(prev => ({...prev, modifiers: val.trim() ? [val.trim()] : []}));
                      }
                    }}
                    onBlur={(e) => {
                      const cleaned = (cptFormData.modifiers || []).filter(s => s && s.trim());
                      setCptFormData(prev => ({...prev, modifiers: cleaned}));
                    }}
                    placeholder="e.g., 95, GT (comma-separated)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Common: 95 (Telehealth), GT (Interactive)</p>
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm">Options</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cpt_group_session"
                    checked={cptFormData.is_group_session || false}
                    onCheckedChange={(checked) => setCptFormData(prev => ({...prev, is_group_session: checked}))}
                  />
                  <Label htmlFor="cpt_group_session" className="text-sm">Group Session</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cpt_requires_auth"
                    checked={cptFormData.requires_authorization || false}
                    onCheckedChange={(checked) => setCptFormData(prev => ({...prev, requires_authorization: checked, allow_authorization: checked ? false : prev.allow_authorization}))}
                  />
                  <Label htmlFor="cpt_requires_auth" className="text-sm" title="Scheduling is blocked unless a valid auth is found / picked.">Requires Authorization</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cpt_allow_auth"
                    checked={cptFormData.allow_authorization || false}
                    disabled={cptFormData.requires_authorization}
                    onCheckedChange={(checked) => setCptFormData(prev => ({...prev, allow_authorization: checked}))}
                  />
                  <Label htmlFor="cpt_allow_auth" className="text-sm" title="Auth picker is shown and units deduct if one is picked, but scheduling is NOT blocked without an auth.">Allow Authorization (optional)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cpt_addon_code"
                    checked={cptFormData.is_addon_code || false}
                    onCheckedChange={(checked) => setCptFormData(prev => ({...prev, is_addon_code: checked}))}
                  />
                  <Label htmlFor="cpt_addon_code" className="text-sm">Add-on Code</Label>
                </div>
              </div>
              
              {/* Default Group for Group Sessions */}
              {cptFormData.is_group_session && (
                <div className="pt-2 border-t">
                  <Label htmlFor="cpt_default_group">Default Group</Label>
                  <Select
                    value={cptFormData.default_group_id || '_none'}
                    onValueChange={(value) => setCptFormData(prev => ({...prev, default_group_id: value === '_none' ? null : value}))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select default group (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No default group</SelectItem>
                      {clientGroups?.filter(g => g.is_active).map(group => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Auto-populate this group when scheduling</p>
                </div>
              )}
              
              {/* Add-on Code Requirements */}
              {cptFormData.is_addon_code && (
                <div className="pt-2 border-t">
                  <Label htmlFor="cpt_requires_primary">Requires Primary CPT Codes</Label>
                  <Select
                    value=""
                    onValueChange={(value) => {
                      if (value && !cptFormData.requires_primary_codes?.includes(value)) {
                        setCptFormData(prev => ({
                          ...prev, 
                          requires_primary_codes: [...(prev.requires_primary_codes || []), value]
                        }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Add required primary code..." />
                    </SelectTrigger>
                    <SelectContent>
                      {cptCodes?.filter(c => !c.is_addon_code && c.code !== cptFormData.code).map(code => (
                        <SelectItem key={code.id} value={code.id}>
                          {code.code} - {code.display_name || code.description || ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {cptFormData.requires_primary_codes?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {cptFormData.requires_primary_codes.map(codeId => {
                        const code = cptCodes?.find(c => c.id === codeId);
                        return (
                          <Badge key={codeId} variant="secondary" className="flex items-center gap-1">
                            {code?.code || codeId}
                            <button
                              type="button"
                              onClick={() => setCptFormData(prev => ({
                                ...prev,
                                requires_primary_codes: prev.requires_primary_codes?.filter(id => id !== codeId) || []
                              }))}
                              className="ml-1 hover:text-red-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Default Note Template */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm">Default Note Template</h4>
              <div>
                <Label htmlFor="cpt_default_template">Auto-create Note Template</Label>
                <Select
                  value={cptFormData.default_template_id || '_none'}
                  onValueChange={(value) => setCptFormData(prev => ({...prev, default_template_id: value === '_none' ? null : value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select template (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No default template</SelectItem>
                    {formTemplates?.filter(t => !t.is_archived).map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Automatically create this note type when an appointment is scheduled with this CPT</p>
              </div>
            </div>

            {/* Bundles & Color */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bundles</Label>
                <Select
                  value=""
                  onValueChange={(value) => {
                    if (value && !cptFormData.bundle_ids?.includes(value)) {
                      setCptFormData(prev => ({
                        ...prev, 
                        bundle_ids: [...(prev.bundle_ids || []), value]
                      }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Add to bundle..." />
                  </SelectTrigger>
                  <SelectContent>
                    {cptBundles?.map(bundle => (
                      <SelectItem key={bundle.id} value={bundle.id}>
                        {bundle.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {cptFormData.bundle_ids?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {cptFormData.bundle_ids.map(bundleId => {
                      const bundle = cptBundles?.find(b => b.id === bundleId);
                      return (
                        <Badge key={bundleId} variant="outline" className="flex items-center gap-1">
                          {bundle?.name || bundleId}
                          <button
                            type="button"
                            onClick={() => setCptFormData(prev => ({
                              ...prev,
                              bundle_ids: prev.bundle_ids?.filter(id => id !== bundleId) || []
                            }))}
                            className="ml-1 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="cpt_color">Calendar Color</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="cpt_color"
                    type="color"
                    value={cptFormData.color || '#3B82F6'}
                    onChange={(e) => setCptFormData(prev => ({...prev, color: e.target.value}))}
                    className="w-16 h-10 p-1"
                  />
                  <span className="text-sm text-gray-500">Used for calendar display</span>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => {
                setShowCptForm(false);
                setEditingCptCode(null);
              }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                {editingCptCode ? 'Update CPT Code' : 'Create CPT Code'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Create/Edit Bundle Dialog */}
      <Dialog open={showBundleForm} onOpenChange={setShowBundleForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBundle ? 'Edit Bundle' : 'Create New Bundle'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateBundle} className="space-y-4">
            <div>
              <Label htmlFor="bundle_name">Bundle Name *</Label>
              <DebouncedInput
                id="bundle_name"
                value={bundleFormData.name}
                onChange={(e) => setBundleFormData(prev => ({...prev, name: e.target.value}))}
                required
                placeholder="e.g., Outpatient Therapy"
              />
            </div>

            <div>
              <Label htmlFor="bundle_description">Description</Label>
              <DebouncedTextarea
                id="bundle_description"
                value={bundleFormData.description}
                onChange={(e) => setBundleFormData(prev => ({...prev, description: e.target.value}))}
                placeholder="Brief description of this bundle..."
                rows={2}
              />
            </div>

            <div className="space-y-3">
              <Label className="text-base font-semibold">CPT Codes in Bundle</Label>
              <p className="text-sm text-gray-600">Select CPT codes to include in this bundle</p>
              
              <div className="max-h-64 overflow-y-auto p-4 border rounded-lg space-y-2">
                {cptCodes?.map((code) => (
                  <label key={code.id} className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={bundleFormData.cpt_code_ids.includes(code.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setBundleFormData(prev => ({
                            ...prev,
                            cpt_code_ids: [...prev.cpt_code_ids, code.id]
                          }));
                        } else {
                          setBundleFormData(prev => ({
                            ...prev,
                            cpt_code_ids: prev.cpt_code_ids.filter(id => id !== code.id)
                          }));
                        }
                      }}
                      className="rounded"
                    />
                    <span>{code.code} - {code.description}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowBundleForm(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                {editingIntakeBundle ? 'Update Bundle' : 'Create Bundle'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>



      {/* Forms & Types Management Dialog — extracted to FormsAdminDialogs.jsx (V2 Forms migration) */}
      <FormsManagementDialog ctx={{
        showFormsManagementDialog, setShowFormsManagementDialog,
        formsManagementTab, setFormsManagementTab,
        formTemplates, formTypes,
        fetchFormTemplates,
        setFormTemplates, setFormTypes,
        openFormBuilder: (template) => {
          if (template) {
            setEditingFormTemplate(template);
            setFormTemplateData({
              name: template.name,
              description: template.description || '',
              form_type: template.form_type || '',
              form_fields: template.form_fields || [],
              billable: template.billable || false,
              requires_provider_signature: template.requires_provider_signature !== false,
              requires_supervisor_signature: template.requires_supervisor_signature || false,
              allow_client_signature: template.allow_client_signature || false,
              require_client_signature: template.require_client_signature || false,
              is_scored_assessment: template.is_scored_assessment || false,
              is_group_template: template.is_group_template || false,
              available_in_quick_create: template.available_in_quick_create !== false,
              allow_external_download: template.allow_external_download || false,
              download_requires_signature: template.download_requires_signature !== false,
              allow_print_for_signature: template.allow_print_for_signature || false,
              allow_send_to_client: template.allow_send_to_client || false,
              scoring_config: template.scoring_config || { max_score: 0, severity_ranges: [], clinical_alerts: [] },
              status_notifications: template.status_notifications || [],
              is_active: template.is_active !== false
            });
            setActiveFormFields(template.form_fields || []);
          } else {
            setEditingFormTemplate(null);
            setFormTemplateData({ name: '', description: '', form_type: '', form_fields: [], is_active: true });
            setActiveFormFields([]);
          }
          staticTextEditorCache.current.clear();
          setShowFormTemplateBuilder(true);
        },
        duplicateFormTemplate,
        handleDeleteFormTemplate,
        setEditingFormType, setFormTypeFormData, setShowManageFormTypes,
        handleDeleteFormType,
        handleMoveFormTypeUp, handleMoveFormTypeDown,
        headerExtras: <AdminJumpToDropdown currentDialogId="forms" isDirty={currentAdminDialogDirty} />,
      }} />

      {/* Manage All Form Types Dialog */}
      <Dialog open={showManageFormTypesList} onOpenChange={setShowManageFormTypesList}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-start justify-between">
            <DialogTitle>Manage Form Types - Reorder & Edit</DialogTitle>
            <AdminJumpToDropdown currentDialogId="formTypes" isDirty={currentAdminDialogDirty} />
          </DialogHeader>
          
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Use the arrows to reorder categories. Lower numbers appear first in lists.
            </p>
            
            {formTypes.map((type, index) => (
              <div key={type.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                <div className="flex items-center space-x-3 flex-1">
                  <span className="text-sm font-bold text-gray-600 w-8">{type.sort_order}</span>
                  <div>
                    <div className="font-medium">{type.name}</div>
                    {type.description && (
                      <div className="text-xs text-gray-500">{type.description}</div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleMoveFormTypeUp(type.id)}
                    disabled={index === 0}
                    title="Move Up"
                  >
                    ↑
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleMoveFormTypeDown(type.id)}
                    disabled={index === formTypes.length - 1}
                    title="Move Down"
                  >
                    ↓
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingFormType(type);
                      setFormTypeFormData({
                        name: type.name,
                        description: type.description || '',
                        sort_order: type.sort_order
                      });
                      setShowManageFormTypes(true);
                      setShowManageFormTypesList(false);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600"
                    onClick={() => {
                      if (window.confirm(`Delete "${type.name}"? This won't affect existing forms.`)) {
                        handleDeleteFormType(type.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            {formTypes.length === 0 && (
              <p className="text-center text-gray-500 py-8">No form types yet</p>
            )}
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowManageFormTypesList(false)}
              >
                Close
              </Button>
              <Button
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => {
                  setEditingFormType(null);
                  setFormTypeFormData({ name: '', description: '', sort_order: formTypes.length + 1 });
                  setShowManageFormTypes(true);
                  setShowManageFormTypesList(false);
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New Form Type
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Intake Bundles Dialog */}
      <Dialog open={showManageIntakeBundles} onOpenChange={(open) => {
        setShowManageIntakeBundles(open);
        if (!open) {
          setEditingIntakeBundle(null);
          setIntakeBundleFormData({
            name: '',
            service_type_ids: [],
            trigger_cpt_code_ids: [],
            age_min: null,
            age_max: null,
            documents: []
          });
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex flex-row items-start justify-between">
            <div>
              <DialogTitle>
                {editingIntakeBundle === null && intakeBundleFormData.name === '' 
                  ? 'Manage Intake Bundles' 
                  : editingIntakeBundle 
                    ? 'Edit Intake Bundle' 
                    : 'Create Intake Bundle'}
              </DialogTitle>
              <p className="text-sm text-gray-600 mt-2">
                Bundle multiple forms and documents for automated client intake workflows
              </p>
            </div>
            <AdminJumpToDropdown currentDialogId="intakeBundles" isDirty={currentAdminDialogDirty} />
          </DialogHeader>
          
          {/* Show list view when not editing/creating */}
          {editingIntakeBundle === null && intakeBundleFormData.name === '' ? (
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-600">{intakeBundles.length} bundle(s) configured</p>
                <Button
                  size="sm"
                  className="bg-teal-600 hover:bg-teal-700"
                  onClick={() => {
                    setIntakeBundleFormData({
                      name: '',
                      service_type_ids: [],
                      trigger_cpt_code_ids: [],
                      age_min: null,
                      age_max: null,
                      documents: []
                    });
                    setEditingIntakeBundle(false); // Set to false (not null) to show form
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create New Bundle
                </Button>
              </div>
              
              {intakeBundles.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Folder className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No intake bundles configured</p>
                  <p className="text-sm mt-2">Create bundles to automate client intake workflows</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {intakeBundles.map((bundle) => (
                    <div key={bundle.id} className="border rounded-lg p-4 bg-white hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-base">{bundle.name}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {bundle.documents?.length || 0} document(s)
                            </span>
                            {bundle.assessments?.length > 0 && (
                              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded">
                                {bundle.assessments.length} assessment(s)
                              </span>
                            )}
                            {bundle.service_type_ids?.length > 0 && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                {bundle.service_type_ids.length} service(s)
                              </span>
                            )}
                            {(bundle.trigger_cpt_code_ids?.length > 0 || bundle.trigger_cpt_codes?.length > 0) && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                {(bundle.trigger_cpt_code_ids?.length || bundle.trigger_cpt_codes?.length || 0)} CPT trigger(s)
                              </span>
                            )}
                            {(bundle.age_min || bundle.age_max) && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                Age: {bundle.age_min || '0'} - {bundle.age_max || '∞'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingIntakeBundle(bundle);
                              // Ensure backwards compatibility - migrate old trigger_cpt_codes to trigger_cpt_code_ids
                              const formData = {
                                ...bundle,
                                trigger_cpt_code_ids: bundle.trigger_cpt_code_ids || [],
                                // If we only have old format, we can't auto-migrate without knowing the CPT IDs
                                // So we keep both for now and let user update
                              };
                              setIntakeBundleFormData(formData);
                            }}
                          >
                            <Edit2 className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => {
                              // Duplicate the bundle - copy all fields but clear the ID and rename
                              const duplicatedBundle = {
                                ...bundle,
                                id: undefined, // Clear ID so a new one is generated
                                name: `${bundle.name} (Copy)`,
                                trigger_cpt_code_ids: bundle.trigger_cpt_code_ids || [],
                                // Copy documents but clear their IDs
                                documents: (bundle.documents || []).map(doc => ({
                                  ...doc,
                                  id: undefined
                                }))
                              };
                              setEditingIntakeBundle(null); // Not editing, creating new
                              setIntakeBundleFormData(duplicatedBundle);
                              toast.info('Bundle duplicated - make your changes and save');
                            }}
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Duplicate
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={async () => {
                              if (confirm(`Delete bundle "${bundle.name}"?`)) {
                                try {
                                  await axios.delete(`${API}/intake-bundles/${bundle.id}`);
                                  setIntakeBundles(prev => prev.filter(b => b.id !== bundle.id));
                                  toast.success('Bundle deleted');
                                } catch (error) {
                                  toast.error('Failed to delete bundle');
                                }
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
          <>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            {/* Back button when in form mode */}
            <Button
              size="sm"
              variant="ghost"
              className="mb-2"
              onClick={() => {
                setEditingIntakeBundle(null);
                setIntakeBundleFormData({
                  name: '',
                  service_type_ids: [],
                  trigger_cpt_code_ids: [],
                  age_min: null,
                  age_max: null,
                  documents: []
                });
              }}
            >
              ← Back to List
            </Button>
            
            {/* Bundle Name */}
            <div>
              <Label htmlFor="bundle_name">Bundle Name *</Label>
              <DebouncedInput
                id="bundle_name"
                value={intakeBundleFormData.name}
                onChange={(e) => setIntakeBundleFormData(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Adult Intake Package"
                className="mt-1"
              />
            </div>

            {/* Trigger Configuration */}
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">Trigger Configuration</Label>
                <p className="text-xs text-gray-600 mt-1">Choose when this bundle should be automatically assigned</p>
              </div>

              {/* Service Types */}
              <div>
                <Label>Service Types</Label>
                <p className="text-xs text-gray-500 mb-2">Assign this bundle when client enrolls in selected services</p>
                <div className="border rounded p-3 max-h-40 overflow-y-auto space-y-2">
                  {serviceTypes.map(service => (
                    <div key={service.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`service_${service.id}`}
                        checked={intakeBundleFormData.service_type_ids.includes(service.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setIntakeBundleFormData(prev => ({
                              ...prev,
                              service_type_ids: [...prev.service_type_ids, service.id]
                            }));
                          } else {
                            setIntakeBundleFormData(prev => ({
                              ...prev,
                              service_type_ids: prev.service_type_ids.filter(id => id !== service.id)
                            }));
                          }
                        }}
                      />
                      <Label htmlFor={`service_${service.id}`} className="text-sm cursor-pointer">
                        {service.name}
                      </Label>
                    </div>
                  ))}
                  {serviceTypes.length === 0 && (
                    <p className="text-sm text-gray-500">No services available</p>
                  )}
                </div>
              </div>

              {/* CPT Code Triggers */}
              <div>
                <Label>CPT Code Triggers</Label>
                <p className="text-xs text-gray-500 mb-2">Select specific CPT codes that will trigger this intake bundle. Each CPT code entry is treated individually.</p>
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {cptCodes.map((cpt) => (
                    <div key={cpt.id} className="flex items-start space-x-2">
                      <Checkbox
                        id={`cpt-trigger-${cpt.id}`}
                        checked={intakeBundleFormData.trigger_cpt_code_ids?.includes(cpt.id)}
                        onCheckedChange={(checked) => {
                          setIntakeBundleFormData(prev => ({
                            ...prev,
                            trigger_cpt_code_ids: checked
                              ? [...(prev.trigger_cpt_code_ids || []), cpt.id]
                              : (prev.trigger_cpt_code_ids || []).filter(id => id !== cpt.id)
                          }));
                        }}
                      />
                      <label 
                        htmlFor={`cpt-trigger-${cpt.id}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        <span className="font-medium">{cpt.code}</span>
                        <span className="text-gray-500 ml-2">- {cpt.display_name || cpt.description || cpt.category}</span>
                      </label>
                    </div>
                  ))}
                </div>
                {intakeBundleFormData.trigger_cpt_code_ids?.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    {intakeBundleFormData.trigger_cpt_code_ids.length} CPT code(s) selected
                  </p>
                )}
              </div>
            </div>

            {/* Age Range (Optional) */}
            <div>
              <Label className="text-sm font-semibold">Age Range (Optional)</Label>
              <p className="text-xs text-gray-500 mb-2">Only assign to clients within this age range</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="age_min">Minimum Age</Label>
                  <Input
                    id="age_min"
                    type="number"
                    value={intakeBundleFormData.age_min || ''}
                    onChange={(e) => setIntakeBundleFormData(prev => ({
                      ...prev,
                      age_min: e.target.value ? parseInt(e.target.value) : null
                    }))}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div>
                  <Label htmlFor="age_max">Maximum Age</Label>
                  <Input
                    id="age_max"
                    type="number"
                    value={intakeBundleFormData.age_max || ''}
                    onChange={(e) => setIntakeBundleFormData(prev => ({
                      ...prev,
                      age_max: e.target.value ? parseInt(e.target.value) : null
                    }))}
                    placeholder="120"
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Documents/Forms */}
            <div>
              <Label className="text-sm font-semibold">Documents in Bundle</Label>
              <p className="text-xs text-gray-500 mb-2">Select forms to include in this bundle</p>
              
              {/* Add Document Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const newDoc = {
                    template_id: '',
                    template_name: '',
                    auto_send_to_client: false,
                    required_for_provider: true
                  };
                  setIntakeBundleFormData(prev => ({
                    ...prev,
                    documents: [...prev.documents, newDoc]
                  }));
                }}
                className="mb-3"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Document
              </Button>

              {/* Document List */}
              <div className="space-y-3">
                {intakeBundleFormData.documents.map((doc, idx) => (
                  <div key={idx} className="border rounded p-3 bg-gray-50 space-y-3">
                    <div className="flex justify-between items-start">
                      <Label className="text-sm font-medium">Document #{idx + 1}</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setIntakeBundleFormData(prev => ({
                            ...prev,
                            documents: prev.documents.filter((_, i) => i !== idx)
                          }));
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>

                    <div>
                      <Label className="text-xs">Form Template</Label>
                      {/* Feb 2026: replaced plain <Select> with a searchable
                          Popover+Command picker.  Two complaints: (1) the
                          template list wasn't alphabetized so finding a form
                          in a 100+ template library was painful, (2) no
                          search.  Now alphabetized + typeahead. */}
                      <Popover
                        open={intakeBundleDocPickerOpenIdx === idx}
                        onOpenChange={(open) => setIntakeBundleDocPickerOpenIdx(open ? idx : null)}
                        modal={true}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            aria-expanded={intakeBundleDocPickerOpenIdx === idx}
                            className="w-full justify-between font-normal mt-1"
                            data-testid={`intake-bundle-doc-${idx}-trigger`}
                          >
                            {doc.template_id ? (
                              <span className="truncate">
                                {formTemplates.find(t => t.id === doc.template_id)?.name || doc.template_name || 'Selected'}
                              </span>
                            ) : (
                              <span className="text-gray-500">Search and select form template…</span>
                            )}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[520px] p-0" align="start" style={{ zIndex: 9999 }}>
                          <Command>
                            <CommandInput placeholder="Search templates…" data-testid={`intake-bundle-doc-${idx}-search`} />
                            <CommandList>
                              <CommandEmpty>No templates found.</CommandEmpty>
                              <CommandGroup>
                                {formTemplates
                                  .filter(t => !t.is_archived)
                                  .slice()
                                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                  .map(template => (
                                    <CommandItem
                                      key={template.id}
                                      value={template.name}
                                      data-testid={`intake-bundle-doc-${idx}-option-${template.id}`}
                                      onSelect={() => {
                                        const updatedDocs = [...intakeBundleFormData.documents];
                                        updatedDocs[idx] = {
                                          ...updatedDocs[idx],
                                          template_id: template.id,
                                          template_name: template.name || ''
                                        };
                                        setIntakeBundleFormData(prev => ({ ...prev, documents: updatedDocs }));
                                        setIntakeBundleDocPickerOpenIdx(null);
                                      }}
                                    >
                                      {template.name}
                                    </CommandItem>
                                  ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="flex items-center space-x-4 text-xs">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <Checkbox
                          checked={doc.auto_send_to_client}
                          onCheckedChange={(checked) => {
                            const updatedDocs = [...intakeBundleFormData.documents];
                            updatedDocs[idx] = {...updatedDocs[idx], auto_send_to_client: checked};
                            setIntakeBundleFormData(prev => ({...prev, documents: updatedDocs}));
                          }}
                        />
                        <span>Auto-send to client portal</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <Checkbox
                          checked={doc.required_for_provider}
                          onCheckedChange={(checked) => {
                            const updatedDocs = [...intakeBundleFormData.documents];
                            updatedDocs[idx] = {...updatedDocs[idx], required_for_provider: checked};
                            setIntakeBundleFormData(prev => ({...prev, documents: updatedDocs}));
                          }}
                        />
                        <span>Required for provider</span>
                      </label>
                    </div>
                  </div>
                ))}
                
                {intakeBundleFormData.documents.length === 0 && (
                  <div className="text-center py-6 text-gray-500 border rounded bg-gray-50">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">No documents added yet</p>
                    <p className="text-xs">Click "Add Document" to include forms</p>
                  </div>
                )}
              </div>
            </div>

            {/* Assessments in Bundle — Jun 2026: scored assessments
                (PHQ-9 etc.) sent to the client portal with the packet;
                auto-scored + PDF filed to chart on completion. */}
            <div>
              <Label>Assessments in Bundle</Label>
              <p className="text-xs text-gray-500 mb-2">
                Scored assessments the client completes in the portal when the packet is sent (results are scored automatically and a PDF is filed to the chart)
              </p>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setIntakeBundleFormData(prev => ({
                    ...prev,
                    assessments: [...(prev.assessments || []), { template_id: '', template_name: '' }]
                  }));
                }}
                className="mb-3"
                data-testid="intake-bundle-add-assessment-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Assessment
              </Button>

              <div className="space-y-3">
                {(intakeBundleFormData.assessments || []).map((asmt, idx) => (
                  <div key={idx} className="border rounded p-3 bg-teal-50/50 space-y-2">
                    <div className="flex justify-between items-start">
                      <Label className="text-sm font-medium">Assessment #{idx + 1}</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setIntakeBundleFormData(prev => ({
                            ...prev,
                            assessments: (prev.assessments || []).filter((_, i) => i !== idx)
                          }));
                        }}
                        data-testid={`intake-bundle-asmt-${idx}-remove`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                    <Popover
                      open={intakeBundleAsmtPickerOpenIdx === idx}
                      onOpenChange={(open) => setIntakeBundleAsmtPickerOpenIdx(open ? idx : null)}
                      modal={true}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={intakeBundleAsmtPickerOpenIdx === idx}
                          className="w-full justify-between font-normal"
                          data-testid={`intake-bundle-asmt-${idx}-trigger`}
                        >
                          {asmt.template_id ? (
                            <span className="truncate">
                              {formTemplates.find(t => t.id === asmt.template_id)?.name || asmt.template_name || 'Selected'}
                            </span>
                          ) : (
                            <span className="text-gray-500">Search and select assessment…</span>
                          )}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[520px] p-0" align="start" style={{ zIndex: 9999 }}>
                        <Command>
                          <CommandInput placeholder="Search assessments…" data-testid={`intake-bundle-asmt-${idx}-search`} />
                          <CommandList>
                            <CommandEmpty>No scored assessments found.</CommandEmpty>
                            <CommandGroup>
                              {formTemplates
                                .filter(t => t.is_scored_assessment && !t.is_archived)
                                .slice()
                                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                .map(template => (
                                  <CommandItem
                                    key={template.id}
                                    value={template.name}
                                    data-testid={`intake-bundle-asmt-${idx}-option-${template.id}`}
                                    onSelect={() => {
                                      setIntakeBundleFormData(prev => {
                                        const updated = [...(prev.assessments || [])];
                                        updated[idx] = { template_id: template.id, template_name: template.name || '' };
                                        return { ...prev, assessments: updated };
                                      });
                                      setIntakeBundleAsmtPickerOpenIdx(null);
                                    }}
                                  >
                                    {template.name}
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer Actions - only show when in form mode */}
          <div className="flex justify-end space-x-2 border-t pt-4 px-6 pb-4">
            <Button
              variant="outline"
              onClick={() => {
                setEditingIntakeBundle(null);
                setIntakeBundleFormData({
                  name: '',
                  service_type_ids: [],
                  trigger_cpt_code_ids: [],
                  age_min: null,
                  age_max: null,
                  documents: []
                });
              }}
            >
              ← Back to List
            </Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700"
              onClick={async () => {
                try {
                  if (!intakeBundleFormData.name.trim()) {
                    toast.error('Please enter a bundle name');
                    return;
                  }
                  
                  if (intakeBundleFormData.documents.length === 0) {
                    toast.error('Please add at least one document to the bundle');
                    return;
                  }

                  // Validate all documents have templates selected
                  if (intakeBundleFormData.documents.some(doc => !doc.template_id)) {
                    toast.error('Please select a template for all documents');
                    return;
                  }

                  if ((intakeBundleFormData.assessments || []).some(a => !a.template_id)) {
                    toast.error('Please select a template for all assessments');
                    return;
                  }

                  if (editingIntakeBundle) {
                    await axios.put(`${API}/intake-bundles/${editingIntakeBundle.id}`, intakeBundleFormData);
                    toast.success('Bundle updated successfully');
                  } else {
                    await axios.post(`${API}/intake-bundles`, intakeBundleFormData);
                    toast.success('Bundle created successfully');
                  }

                  // Refresh data
                  const response = await axios.get(`${API}/intake-bundles`);
                  setIntakeBundles(response.data);

                  // Go back to list
                  setEditingIntakeBundle(null);
                  setIntakeBundleFormData({
                    name: '',
                    service_type_ids: [],
                    trigger_cpt_code_ids: [],
                    age_min: null,
                    age_max: null,
                    documents: []
                  });
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to save bundle');
                }
              }}
            >
              {editingIntakeBundle ? 'Update Bundle' : 'Create Bundle'}
            </Button>
          </div>
          </>
          )}
        </DialogContent>
      </Dialog>

      {/* Form Type Management Dialog — extracted to FormsAdminDialogs.jsx (V2 Forms migration) */}
      <FormTypeDialog ctx={{
        showManageFormTypes, setShowManageFormTypes,
        editingFormType, setEditingFormType,
        formTypeFormData, setFormTypeFormData,
        handleCreateFormType,
      }} />

      <Dialog open={showManageRoles} onOpenChange={setShowManageRoles}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Roles & Permissions Management</DialogTitle>
            <AdminJumpToDropdown currentDialogId="roles" isDirty={currentAdminDialogDirty} />
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">Manage custom roles and assign permissions</p>
              <Button 
                onClick={() => {
                  setEditingRole(null);
                  setRoleFormData({ name: '', description: '', permissions: [], visible_tabs: [] });
                  setShowAddEditRole(true);
                }}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Role
              </Button>
            </div>

            <div className="grid gap-4">
              {(roles || []).map((role) => {
                const legacyProtectedRole = ['super admin', 'admin', 'supervisor', 'office', 'provider', 'intern', 'client'].includes((role.name || '').toLowerCase()) || role.is_default;
                return (
                <Card key={role.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{formatRoleName(role.name)}</h3>
                          {legacyProtectedRole && (
                            <Badge variant="secondary" className="text-xs">System Role</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          <Badge variant="outline" className="text-xs">
                            {(role.name?.toLowerCase() === 'super admin') 
                              ? 'All permissions' 
                              : `${role.permissions?.length || 0} permissions`}
                          </Badge>
                          {role.name !== 'Client' && (
                            <Badge variant="outline" className="text-xs bg-blue-50">
                              {role.visible_tabs?.length || 0} tabs ({role.visible_tabs?.length === 0 ? 'all visible' : role.visible_tabs?.join(', ')})
                            </Badge>
                          )}
                          {role.name === 'Client' && (
                            <Badge variant="outline" className="text-xs bg-gray-100">
                              Client Portal tabs
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(role.name || '').toLowerCase() === 'super admin' ? (
                          <span className="text-xs text-gray-500 inline-flex items-center gap-1" title="Managed by the platform — cannot be edited, renamed, or deleted">
                            <Lock className="h-3 w-3" />
                            Managed by platform
                          </span>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingRole(role);
                                setRoleFormData({
                                  name: role.name,
                                  description: role.description,
                                  permissions: [...new Set([...(role.permissions || []), ...(role.locked_permissions || [])])],
                                  visible_tabs: role.visible_tabs || []
                                });
                                setShowAddEditRole(true);
                              }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            {!legacyProtectedRole && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to delete the role "${role.name}"?`)) {
                                    handleDeleteRole(role.id);
                                  }
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Role Dialog */}
      <Dialog open={showAddEditRole} onOpenChange={setShowAddEditRole}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateRole} className="space-y-4">
            <div>
              <Label htmlFor="role_name">Role Name *</Label>
              <DebouncedInput
                id="role_name"
                value={roleFormData.name}
                onChange={(e) => setRoleFormData(prev => ({...prev, name: e.target.value}))}
                required
                disabled={!!editingRole?.system_protected}
                placeholder="e.g., Billing Manager"
              />
              {editingRole?.system_protected && (
                <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  System role — the name is locked and the role can&apos;t be deleted. Permissions and tabs remain editable.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="role_description">Description</Label>
              <DebouncedTextarea
                id="role_description"
                value={roleFormData.description}
                onChange={(e) => setRoleFormData(prev => ({...prev, description: e.target.value}))}
                placeholder="Brief description of this role..."
                rows={2}
              />
            </div>

            <div className="space-y-3">
              <Label className="text-base font-semibold">Permissions</Label>
              <p className="text-sm text-gray-600">Select the permissions for this role</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto p-4 border rounded-lg">
                {Object.entries(availablePermissions).map(([category, perms]) => (
                  <div key={category} className="space-y-2">
                    <h4 className="font-semibold text-sm capitalize border-b pb-1">
                      {category.replace(/_/g, ' ')}
                    </h4>
                    <div className="space-y-1.5 pl-2">
                      {perms.map((perm) => {
                        const permLocked = !!editingRole?.system_protected && (editingRole?.locked_permissions || []).includes(perm);
                        return (
                        <label key={perm} className={`flex items-center space-x-2 text-sm ${permLocked ? 'opacity-70' : ''}`} title={permLocked ? 'Required for this system role — cannot be removed' : undefined}>
                          <input
                            type="checkbox"
                            checked={permLocked || roleFormData.permissions.includes(perm)}
                            disabled={permLocked}
                            onChange={(e) => {
                              if (permLocked) return;
                              if (e.target.checked) {
                                setRoleFormData(prev => ({
                                  ...prev,
                                  permissions: [...prev.permissions, perm]
                                }));
                              } else {
                                setRoleFormData(prev => ({
                                  ...prev,
                                  permissions: prev.permissions.filter(p => p !== perm)
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-xs inline-flex items-center gap-1">
                            {perm.replace(/_/g, ' ')}
                            {permLocked && <Lock className="h-2.5 w-2.5 text-gray-400" />}
                          </span>
                        </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tab Visibility Section - not applicable for Client role */}
            {roleFormData.name !== 'Client' && (
            <div className="space-y-3">
              <Label className="text-base font-semibold">Tab Visibility</Label>
              <p className="text-sm text-gray-600">Select which tabs users with this role can see</p>
              
              <div className="p-4 border rounded-lg space-y-4">
                {/* Always visible tabs */}
                <div className="pb-3 border-b">
                  <p className="text-xs font-medium text-gray-500 mb-2">ALWAYS VISIBLE</p>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <input type="checkbox" checked disabled className="rounded opacity-50" />
                      <span>Clients</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <input type="checkbox" checked disabled className="rounded opacity-50" />
                      <span>Tasks</span>
                    </div>
                  </div>
                </div>
                
                {/* Configurable tabs */}
                <div className="pb-3 border-b">
                  <p className="text-xs font-medium text-gray-500 mb-2">CONFIGURABLE BY ROLE</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { id: 'appointments', label: 'Schedule' },
                      { id: 'groups', label: 'Groups' },
                      { id: 'cpt', label: 'CPT Codes' },
                      { id: 'eligibility', label: 'Eligibility' },
                      { id: 'client_bills', label: 'Client Bills' },
                    ].map((tab) => (
                      <label 
                        key={tab.id} 
                        className="flex items-center space-x-2 p-2 border rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={roleFormData.visible_tabs?.includes(tab.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRoleFormData(prev => ({
                                ...prev,
                                visible_tabs: [...(prev.visible_tabs || []), tab.id]
                              }));
                            } else {
                              setRoleFormData(prev => ({
                                ...prev,
                                visible_tabs: (prev.visible_tabs || []).filter(t => t !== tab.id)
                              }));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm font-medium">{tab.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* User-level controlled tabs */}
                <div className="pb-3 border-b">
                  <p className="text-xs font-medium text-gray-500 mb-2">CONTROLLED AT USER LEVEL</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm text-gray-500">
                    <div className="p-2 bg-gray-50 rounded">Meds Management</div>
                    <div className="p-2 bg-gray-50 rounded">Billing</div>
                    <div className="p-2 bg-gray-50 rounded">Waitlist</div>
                    <div className="p-2 bg-gray-50 rounded">Records</div>
                    <div className="p-2 bg-gray-50 rounded">Auths</div>
                  </div>
                </div>
                
                {/* Super Admin only tabs */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">SUPER ADMIN ONLY</p>
                  <div className="grid grid-cols-3 gap-2 text-sm text-gray-500">
                    <div className="p-2 bg-gray-50 rounded">Batch</div>
                    <div className="p-2 bg-gray-50 rounded">ERA</div>
                    <div className="p-2 bg-gray-50 rounded">Claims</div>
                  </div>
                </div>
              </div>
            </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddEditRole(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                {editingRole ? 'Update Role' : 'Create Role'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Insurance Management Dialog (Extracted Component) */}
      <ClientInsuranceDialog
        isOpen={showInsuranceManagement}
        onClose={() => { setShowInsuranceManagement(false); setInsuranceDialogPrefill(null); }}
        client={managingInsuranceForClient}
        insuranceCompanies={insurances}
        onUpdate={(optimisticInsurances) => handleInsuranceUpdate(managingInsuranceForClient?.id, optimisticInsurances)}
        onEligibilityCheck={handleEligibilityCheck}
        prefillData={insuranceDialogPrefill}
        onPrefillConsumed={() => setInsuranceDialogPrefill(null)}
      />

      {/* Insurance Card View - Universal Component */}
      <InsuranceCardViewer
        isOpen={showInsuranceCardView}
        onClose={setShowInsuranceCardView}
        cardData={viewingInsuranceCards}
      />

      {/* Edit Client Since Date Dialog (for backdating imported clients) */}
      <Dialog open={showEditClientSinceDialog} onOpenChange={setShowEditClientSinceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Client Start Date</DialogTitle>
            <p className="text-sm text-gray-600">
              Update the "Client since" date for backdating imported clients
            </p>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!editingClientSinceDate) {
              toast.error('Please select a date');
              return;
            }
            try {
              // Store as ISO string with noon time to avoid timezone shift issues
              // Using T12:00:00 ensures the date doesn't shift when displayed in any timezone
              const dateToStore = `${editingClientSinceDate}T12:00:00.000Z`;
              
              // Update the client's created_at date
              await axios.put(`${API}/clients/${selectedClient.id}`, {
                created_at: dateToStore
              }, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
              });
              
              // Update local state
              setSelectedClient(prev => ({
                ...prev,
                created_at: dateToStore
              }));
              
              toast.success('Client start date updated successfully');
              setShowEditClientSinceDialog(false);
            } catch (error) {
              console.error('Error updating client start date:', error);
              toast.error(error.response?.data?.detail || 'Failed to update client start date');
            }
          }}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="client_since_date">Client Start Date</Label>
                <Input
                  id="client_since_date"
                  type="date"
                  value={editingClientSinceDate}
                  onChange={(e) => setEditingClientSinceDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  When did this client first become your client?
                </p>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowEditClientSinceDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Client Insurance Dialog */}
      <Dialog open={showEditClientInsurance} onOpenChange={setShowEditClientInsurance}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit {editingInsuranceType?.charAt(0).toUpperCase() + editingInsuranceType?.slice(1)} Insurance
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              const updateData = {};
              const prefix = editingInsuranceType;
              
              // Map form data to client fields with appropriate prefix
              updateData[`${prefix}_insurance`] = clientInsuranceFormData.insurance_name;
              updateData[`${prefix}_member_id`] = clientInsuranceFormData.member_id;
              updateData[`${prefix}_group_number`] = clientInsuranceFormData.group_number;
              updateData[`${prefix}_relationship_to_insured`] = clientInsuranceFormData.relationship_to_insured;
              updateData[`${prefix}_insured_name`] = clientInsuranceFormData.insured_name;
              updateData[`${prefix}_insured_ssn`] = clientInsuranceFormData.insured_ssn;
              updateData[`${prefix}_insured_dob`] = clientInsuranceFormData.insured_dob;
              updateData[`${prefix}_effective_date`] = clientInsuranceFormData.effective_date;
              updateData[`${prefix}_end_date`] = clientInsuranceFormData.end_date;
              
              await axios.put(`${API}/clients/${selectedClient.id}`, updateData);
              toast.success('Insurance updated successfully');
              setShowEditClientInsurance(false);
              handleClientClick(selectedClient.id); // Refresh client data
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Failed to update insurance');
            }
          }}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="insurance_name">Insurance Company Name *</Label>
                <DebouncedInput
                  id="insurance_name"
                  value={clientInsuranceFormData.insurance_name}
                  onChange={(e) => setClientInsuranceFormData(prev => ({...prev, insurance_name: e.target.value}))}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="member_id">Member ID</Label>
                  <DebouncedInput
                    id="member_id"
                    value={clientInsuranceFormData.member_id}
                    onChange={(e) => setClientInsuranceFormData(prev => ({...prev, member_id: e.target.value}))}
                  />
                </div>
                <div>
                  <Label htmlFor="group_number">Group Number</Label>
                  <DebouncedInput
                    id="group_number"
                    value={clientInsuranceFormData.group_number}
                    onChange={(e) => setClientInsuranceFormData(prev => ({...prev, group_number: e.target.value}))}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="relationship_to_insured">Relationship to Insured</Label>
                <Select
                  value={clientInsuranceFormData.relationship_to_insured}
                  onValueChange={(value) => setClientInsuranceFormData(prev => ({...prev, relationship_to_insured: value}))}
                >
                  <SelectTrigger id="relationship_to_insured">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Self</SelectItem>
                    <SelectItem value="spouse">Spouse</SelectItem>
                    <SelectItem value="child">Child</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {clientInsuranceFormData.relationship_to_insured !== 'self' && (
                <>
                  <div>
                    <Label htmlFor="insured_name">Insured Person's Name</Label>
                    <DebouncedInput
                      id="insured_name"
                      value={clientInsuranceFormData.insured_name}
                      onChange={(e) => setClientInsuranceFormData(prev => ({...prev, insured_name: e.target.value}))}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="insured_ssn">Insured's SSN</Label>
                      <DebouncedInput
                        id="insured_ssn"
                        value={clientInsuranceFormData.insured_ssn}
                        onChange={(e) => setClientInsuranceFormData(prev => ({...prev, insured_ssn: e.target.value}))}
                        placeholder="XXX-XX-XXXX"
                      />
                    </div>
                    <div>
                      <Label htmlFor="insured_dob">Insured's Date of Birth</Label>
                      <Input
                        id="insured_dob"
                        type="date"
                        value={clientInsuranceFormData.insured_dob}
                        onChange={(e) => setClientInsuranceFormData(prev => ({...prev, insured_dob: e.target.value}))}
                      />
                    </div>
                  </div>
                </>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="effective_date">Effective Date</Label>
                  <Input
                    id="effective_date"
                    type="date"
                    value={clientInsuranceFormData.effective_date}
                    onChange={(e) => setClientInsuranceFormData(prev => ({...prev, effective_date: e.target.value}))}
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={clientInsuranceFormData.end_date}
                    onChange={(e) => setClientInsuranceFormData(prev => ({...prev, end_date: e.target.value}))}
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button type="button" variant="outline" onClick={() => setShowEditClientInsurance(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send Statement Dialog — Feb 2026: shared `StatementDialog`
          component, used by both the patient-chart Billing tab (here)
          and the V2 Client Bills tab.  The legacy 418-line inline
          implementation that used to live below was deleted after
          parity verification — see git history for the prior
          version. */}
      <StatementDialog
        open={showStatementDialog}
        onClose={() => {
          setShowStatementDialog(false);
          setStatementPreview(null);
          setStatementCcEmails('');
          setStatementHistory(null);
          setShowStatementHistory(false);
        }}
        client={selectedClient}
      />

      {/* Client Eligibility History Dialog — moved to EligibilityTab.jsx */}

      {/* Eligibility Full Benefits Dialog */}
      <Dialog open={showEligibilityBenefits} onOpenChange={setShowEligibilityBenefits}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Full Benefits — {safeEligibilityDetail?.payer_name || selectedClient?.full_name}</DialogTitle>
            <DialogDescription>
              {safeEligibilityDetail?.date_of_service ? `For date of service: ${safeEligibilityDetail.date_of_service}` :
               safeEligibilityDetail?.coverage_effective_date ? `Coverage from ${safeEligibilityDetail.coverage_effective_date}${safeEligibilityDetail?.coverage_termination_date ? ` to ${safeEligibilityDetail.coverage_termination_date}` : ''}` :
               'Latest eligibility response'}
            </DialogDescription>
          </DialogHeader>
          {safeEligibilityDetail ? (
            safeEligibilityDetail.parsed_data ? (
              <Parsed271ViewInline
                data={safeEligibilityDetail.parsed_data}
                check={safeEligibilityDetail}
                onAddOtherPayerToChart={handleAddOtherPayerToChart}
              />
            ) : (
              <div className="space-y-4 py-2 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  {safeEligibilityDetail.payer_name && <div><p className="text-xs text-gray-400">Payer</p><p className="font-medium">{safeEligibilityDetail.payer_name}</p></div>}
                  {safeEligibilityDetail.payer_subscriber_name && <div><p className="text-xs text-gray-400">Subscriber</p><p className="font-medium">{safeEligibilityDetail.payer_subscriber_name}</p></div>}
                  {safeEligibilityDetail.payer_subscriber_id && <div><p className="text-xs text-gray-400">Member ID (Payer)</p><p className="font-mono font-medium">{safeEligibilityDetail.payer_subscriber_id}</p></div>}
                  {safeEligibilityDetail.coverage_status && <div><p className="text-xs text-gray-400">Coverage</p><p className={`font-medium capitalize ${safeEligibilityDetail.coverage_status === 'active' ? 'text-green-700' : 'text-red-600'}`}>{safeEligibilityDetail.coverage_status}</p></div>}
                  {safeEligibilityDetail.coverage_effective_date && <div><p className="text-xs text-gray-400">Effective</p><p className="font-medium">{safeEligibilityDetail.coverage_effective_date}</p></div>}
                  {safeEligibilityDetail.coverage_termination_date && <div><p className="text-xs text-gray-400">Termination</p><p className="font-medium text-red-600">{safeEligibilityDetail.coverage_termination_date}</p></div>}
                </div>
                {(safeEligibilityDetail.copay != null || safeEligibilityDetail.deductible != null || safeEligibilityDetail.coinsurance != null || selectedClient?.eligibility_copay_amounts) && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Benefits</p>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(selectedClient?.eligibility_copay_amounts || {}).map(([svc, amt]) => (
                        <div key={svc} className="bg-gray-50 rounded p-2 text-center">
                          <p className="text-[10px] text-gray-400 uppercase">{svc} Copay</p>
                          <p className="font-semibold">${amt}</p>
                        </div>
                      ))}
                      {safeEligibilityDetail.deductible != null && <div className="bg-gray-50 rounded p-2 text-center"><p className="text-[10px] text-gray-400 uppercase">Deductible</p><p className="font-semibold">${safeEligibilityDetail.deductible}</p></div>}
                      {safeEligibilityDetail.coinsurance != null && <div className="bg-gray-50 rounded p-2 text-center"><p className="text-[10px] text-gray-400 uppercase">Co-Ins</p><p className="font-semibold">{safeEligibilityDetail.coinsurance}%</p></div>}
                    </div>
                  </div>
                )}
                {safeEligibilityDetail.discrepancies?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">Discrepancies</p>
                    {safeEligibilityDetail.discrepancies.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 mb-1">
                        <span className="font-medium capitalize">{d.field?.replace(/_/g,' ')}:</span>
                        <span>On file: <strong>{d.submitted}</strong></span>
                        <span className="text-gray-400">→</span>
                        <span>Payer: <strong>{d.payer}</strong></span>
                      </div>
                    ))}
                    <button className="text-xs text-blue-600 underline mt-1" onClick={() => { setDiscrepancyData({ checkId: safeEligibilityDetail.id, clientId: selectedClient?.id, clientName: selectedClient?.full_name, discrepancies: safeEligibilityDetail.discrepancies }); setShowDiscrepancyReview(true); setShowEligibilityBenefits(false); }}>Review &amp; Sync</button>
                  </div>
                )}
                {safeEligibilityDetail.suggested_insurance?.name && (
                  <div data-testid="eligibility-suggested-insurance-banner" className="bg-amber-50 border border-amber-300 rounded p-2.5">
                    <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1.5">
                      <Lightbulb className="h-3.5 w-3.5" />
                      Possible plan mismatch
                    </p>
                    <p className="text-xs text-amber-900">
                      {safeEligibilityDetail.suggested_insurance.reason || `The payer's response references a different plan: ${safeEligibilityDetail.suggested_insurance.name}`}
                    </p>
                    <p className="text-[11px] text-amber-700 mt-1">
                      Check this client's primary insurance against what the payer has on file.
                      {safeEligibilityDetail.suggested_insurance.payer_id && ` (Suggested payer ID: ${safeEligibilityDetail.suggested_insurance.payer_id})`}
                    </p>
                  </div>
                )}
                {safeEligibilityDetail.errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded p-2">
                    <p className="text-xs font-semibold text-red-700 mb-1">Payer Errors</p>
                    {safeEligibilityDetail.errors.map((e, i) => <p key={i} className="text-xs text-red-600">• {e}</p>)}
                  </div>
                )}
                <p className="text-xs text-gray-400 pt-1">
                  Received: {safeEligibilityDetail.response_date ? new Date(safeEligibilityDetail.response_date).toLocaleString() : safeEligibilityDetail.checked_at ? new Date(safeEligibilityDetail.checked_at).toLocaleString() : '—'}
                  {safeEligibilityDetail.control_number && ` · #${safeEligibilityDetail.control_number}`}
                </p>
              </div>
            )
          ) : (
            <p className="text-sm text-gray-500 py-4 text-center">No eligibility data yet. Run a check first.</p>
          )}
          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              size="sm"
              data-testid="view-history-from-benefits-btn"
              onClick={() => {
                if (selectedClient?.id) {
                  fetchEligibilityDetail(selectedClient.id);
                }
                setShowEligibilityHistory(true);
              }}
            >
              <History className="h-4 w-4 mr-1.5" />
              View History
            </Button>
            <Button variant="outline" onClick={() => setShowEligibilityBenefits(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Eligibility History Dialog */}
      <Dialog open={showEligibilityHistory} onOpenChange={setShowEligibilityHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Eligibility History — {selectedClient?.full_name}</DialogTitle>
            <DialogDescription>All eligibility checks for this client, newest first.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {eligibilityDetailLoading && <p className="text-sm text-gray-500 text-center py-4">Loading…</p>}
            {!eligibilityDetailLoading && safeEligibilityHistory.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No eligibility checks found.</p>
            )}
            {safeEligibilityHistory.map((check, i) => (
              <div key={check.id || i} className={`text-sm rounded-lg p-3 border ${
                check.status === 'completed' ? 'bg-green-50 border-green-200' :
                check.status === 'rejected' ? 'bg-red-50 border-red-200' :
                check.status === 'completed_with_errors' ? 'bg-amber-50 border-amber-200' :
                'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold">{check.insurance_name || check.payer_name || 'Unknown payer'}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    check.status === 'completed' ? 'bg-green-100 text-green-800' :
                    check.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    check.status === 'completed_with_errors' ? 'bg-amber-100 text-amber-800' :
                    'bg-gray-100 text-gray-600'
                  }`}>{(() => { const { label } = getEligibilityDisplay(check.coverage_status || check.status); return label; })()}</span>
                </div>
                <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5">
                  {/* When the check was submitted — shows local time so
                      the user can see "ran at 2:37pm today" at a glance.
                      The raw field may be a pure date string for very
                      old rows, so we fall back to date-only formatting
                      when no time component is present. */}
                  {check.checked_at && (() => {
                    const raw = check.checked_at;
                    const hasTime = String(raw).includes('T') || String(raw).includes(':');
                    const d = new Date(hasTime ? raw : raw + 'T00:00:00');
                    if (isNaN(d.getTime())) return null;
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    const dateStr = `${mm}/${dd}/${yyyy}`;
                    const timeStr = hasTime
                      ? ` ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
                      : '';
                    return <span>Submitted: <strong>{dateStr}{timeStr}</strong></span>;
                  })()}
                  {/* What date it was checking for (DOS override) */}
                  {check.date_of_service && (
                    <span className="text-blue-700 font-medium">Checking for: {new Date(check.date_of_service + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
                  )}
                  {/* Response received date — now shows local time too,
                      helps spot delayed responses vs. realtime ones. */}
                  {check.response_date && check.response_date !== check.checked_at && (() => {
                    const d = new Date(check.response_date);
                    if (isNaN(d.getTime())) return null;
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    const hh = String(d.getHours()).padStart(2, '0');
                    const mi = String(d.getMinutes()).padStart(2, '0');
                    return <span>Response: <strong>{`${mm}/${dd}/${yyyy} ${hh}:${mi}`}</strong></span>;
                  })()}
                  {/* Coverage window the payer confirmed */}
                  {check.coverage_effective_date && (
                    <span className="text-green-700">
                      Coverage: {new Date(check.coverage_effective_date + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                      {check.coverage_termination_date && ` → ${new Date(check.coverage_termination_date + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`}
                    </span>
                  )}
                  {check.copay != null && <span>Copay: ${check.copay}</span>}
                  {check.deductible != null && <span>Ded: ${check.deductible}</span>}
                  {check.control_number && <span className="font-mono text-gray-400">#{check.control_number}</span>}
                  <button
                    className="text-blue-600 hover:text-blue-800 underline font-medium"
                    data-testid={`view-270-btn-${check.id}`}
                    onClick={() => handleView270(check.id)}
                  >
                    View 270
                  </button>
                  {['completed', 'completed_with_errors', 'rejected'].includes(check.status) && (
                    <button
                      className="text-green-600 hover:text-green-800 underline font-medium"
                      data-testid={`view-271-btn-${check.id}`}
                      onClick={() => handleView271Details(check.id)}
                    >
                      View 271
                    </button>
                  )}
                </div>
                {check.suggested_insurance?.name && (
                  <div data-testid={`eligibility-suggested-insurance-row-${check.id}`} className="mt-1.5 text-xs flex items-start gap-1.5 text-amber-800">
                    <Lightbulb className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Possible plan mismatch:</strong> payer references{' '}
                      <strong>{check.suggested_insurance.name}</strong> — verify the client's plan on file.
                    </span>
                  </div>
                )}
                {check.errors?.length > 0 && (
                  <div className="mt-1.5 text-xs text-red-700">
                    {check.errors.map((e, j) => <div key={j}>• {e}</div>)}
                  </div>
                )}
                {check.has_discrepancies && (
                  <button
                    className="mt-1.5 text-xs text-blue-600 underline"
                    onClick={() => {
                      setDiscrepancyData({ checkId: check.id, clientId: selectedClient?.id, clientName: selectedClient?.full_name, discrepancies: check.discrepancies || [] });
                      setShowDiscrepancyReview(true);
                      setShowEligibilityHistory(false);
                    }}
                  >
                    {check.discrepancies?.length} discrepancy(ies) — Review &amp; Sync
                  </button>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEligibilityHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 270 EDI Payload Preview Dialog */}
      <Dialog open={show270Preview} onOpenChange={setShow270Preview}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>270 EDI Payload Preview</DialogTitle>
            <DialogDescription>
              {preview270Meta.insurance_name && `${preview270Meta.insurance_name} — `}
              Control #{preview270Meta.control_number || '—'}
              {preview270Meta.payer_id && ` — Payer ID: ${preview270Meta.payer_id}`}
            </DialogDescription>
          </DialogHeader>
          {loading270Preview ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-sm text-gray-500">Generating 270 payload...</p>
            </div>
          ) : (
            <pre className="bg-gray-900 text-green-400 text-xs p-4 rounded-lg overflow-x-auto whitespace-pre-wrap font-mono max-h-[50vh] overflow-y-auto" data-testid="preview-270-content">
              {preview270Content || 'No content generated.'}
            </pre>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="copy-270-btn" onClick={handleCopy270} disabled={!preview270Content}>Copy</Button>
            <Button variant="outline" size="sm" data-testid="download-270-file-btn" onClick={handleDownload270File} disabled={!preview270Content}>Download .x12</Button>
            <Button variant="outline" onClick={() => setShow270Preview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 271 Parsed Details Dialog (Client Chart) */}
      <Dialog open={show271Details} onOpenChange={setShow271Details}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              271 Response Details
            </DialogTitle>
            <DialogDescription>
              {details271?.insurance_name && `${details271.insurance_name} — `}
              Control #{details271?.control_number || '—'}
            </DialogDescription>
          </DialogHeader>
          {loading271Details ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <p className="text-sm text-gray-500">Loading 271 details...</p>
            </div>
          ) : (details271?.parsed_data || details271?.raw_271) ? (
            <Tabs defaultValue="parsed" className="w-full">
              <TabsList className="grid grid-cols-2 w-full max-w-sm">
                <TabsTrigger value="parsed" data-testid="271-tab-parsed">Parsed Summary</TabsTrigger>
                <TabsTrigger value="raw" data-testid="271-tab-raw">
                  Raw 271{details271?.raw_271 && (
                    <span className="ml-1.5 text-[10px] text-gray-500">
                      ({(details271.raw_271.length / 1024).toFixed(1)} KB)
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="parsed" className="mt-4">
                {details271?.parsed_data ? (
                  <Parsed271ViewInline
                    data={details271.parsed_data}
                    check={details271}
                    onAddOtherPayerToChart={handleAddOtherPayerToChart}
                  />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No parsed 271 data — switch to <strong>Raw 271</strong> to see the X12 response.
                  </div>
                )}
              </TabsContent>
              <TabsContent value="raw" className="mt-4">
                {details271?.raw_271 ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        Raw X12 271 payload — exactly what the payer returned. Each segment ends with <code className="font-mono">~</code>.
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="271-copy-raw"
                        onClick={() => {
                          navigator.clipboard.writeText(details271.raw_271);
                          toast.success('271 copied to clipboard');
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                    <pre
                      className="bg-gray-900 text-green-300 text-[11px] font-mono p-3 rounded max-h-[60vh] overflow-auto whitespace-pre-wrap break-all"
                      data-testid="271-raw-payload"
                    >
                      {/* Render one segment per line — payers ship the
                          271 as one long string with `~` segment
                          terminators, which is unreadable in a <pre>.
                          Splitting + rejoining preserves the wire form
                          while making it scannable.  */}
                      {details271.raw_271
                        .replace(/\r/g, '')
                        .split('~')
                        .filter(seg => seg.trim())
                        .map(seg => seg + '~')
                        .join('\n')}
                    </pre>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No raw 271 stored on this check.
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No parsed 271 data available for this check.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow271Details(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Eligibility Discrepancy Review & Sync Modal */}
      <Dialog open={showDiscrepancyReview} onOpenChange={setShowDiscrepancyReview}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Payer Discrepancies</DialogTitle>
            <DialogDescription>
              The payer returned data that differs from {discrepancyData?.clientName}'s record.
              Review each field and sync if the payer's version is correct.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {(discrepancyData?.discrepancies || []).map((disc, i) => (
              <div key={i} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold capitalize">{disc.field?.replace(/_/g, ' ')}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    disc.severity === 'high' ? 'bg-red-100 text-red-700' :
                    disc.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{disc.severity || 'low'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-xs text-gray-500 mb-1">On File</div>
                    <div className="font-mono text-xs">{disc.submitted || '—'}</div>
                  </div>
                  <div className="bg-blue-50 rounded p-2">
                    <div className="text-xs text-blue-500 mb-1">Payer Says</div>
                    <div className="font-mono text-xs font-semibold">{disc.payer || '—'}</div>
                  </div>
                </div>
                <button
                  className="w-full text-xs bg-blue-600 hover:bg-blue-700 text-white rounded px-3 py-1.5 font-medium transition-colors"
                  data-testid={`accept-discrepancy-btn-${i}`}
                  onClick={async () => {
                    try {
                      // The new parser stamps `field_key` on each
                      // discrepancy so the backend can route the update
                      // to the right column (and split-address dicts).
                      // Older checks only have `field` (human label) —
                      // backend's _LEGACY_FIELD_LABEL_TO_KEY handles
                      // those, so we send whatever we have.
                      await axios.post(`${API}/eligibility/${discrepancyData.checkId}/sync-field`, {
                        field: disc.field,
                        field_key: disc.field_key,
                        value: disc.payer,
                        payer_data: disc.payer_data,
                      });
                      setDiscrepancyData(prev => prev ? {
                        ...prev,
                        discrepancies: prev.discrepancies.filter((_, j) => j !== i)
                      } : null);
                      // Refetch eligibility everywhere — chart header,
                      // Eligibility tab, schedule badge — so the badge
                      // flips from "Eligible with discrepancies" to
                      // plain "Eligible" the moment the last open
                      // discrepancy is resolved.
                      if (selectedClient?.id) handleClientClick(selectedClient.id);
                      try { window.dispatchEvent(new CustomEvent('eligibility:refresh', { detail: { clientId: discrepancyData?.clientId, checkId: discrepancyData?.checkId } })); } catch (e) {}
                    } catch (err) {
                      alert(`Sync failed: ${err.response?.data?.detail || err.message}`);
                    }
                  }}
                >
                  Sync &quot;{disc.field?.replace(/_/g, ' ')}&quot; to Member Profile
                </button>
                <button
                  className="w-full text-xs bg-white hover:bg-amber-50 text-amber-700 border border-amber-300 rounded px-3 py-1.5 font-medium transition-colors mt-1.5"
                  data-testid={`decline-discrepancy-btn-${i}`}
                  onClick={async () => {
                    // Decline = "we see the difference but don't want
                    // to update our record right now". Backend marks
                    // the disc with resolution='dismissed' (stays
                    // visible in history) and once every high-severity
                    // disc is resolved, flips eligibility back to
                    // plain "Eligible" so the workflow unblocks.
                    try {
                      await axios.post(
                        `${API}/eligibility/${discrepancyData.checkId}/discrepancies/dismiss`,
                        {
                          field: disc.field,
                          field_key: disc.field_key,
                        }
                      );
                      setDiscrepancyData(prev => prev ? {
                        ...prev,
                        discrepancies: prev.discrepancies.filter((_, j) => j !== i)
                      } : null);
                      if (selectedClient?.id) handleClientClick(selectedClient.id);
                      try { window.dispatchEvent(new CustomEvent('eligibility:refresh', { detail: { clientId: discrepancyData?.clientId, checkId: discrepancyData?.checkId } })); } catch (e) {}
                      toast.success('Discrepancy dismissed');
                    } catch (err) {
                      toast.error(`Dismiss failed: ${err.response?.data?.detail || err.message}`);
                    }
                  }}
                >
                  Decline — keep our record as-is
                </button>
              </div>
            ))}
            {discrepancyData?.discrepancies?.length === 0 && (
              <p className="text-sm text-center text-gray-500 py-4">All discrepancies resolved.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscrepancyReview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shared Post Payment Dialog — handles Self-Pay, Manual Insurance Payment, and Write-Off.
          Only renders when a specific claim was targeted (per-claim action). The top-of-tab
          "Post Payment" button (no claim target) opens ClientPostPaymentDialog instead.
          Conditionally mounted so the lazy chunk only downloads when actually opened. */}
      {showPostPaymentDialog && !!postPaymentTarget?.claim && (
        <Suspense fallback={null}>
          <PostPaymentDialog
            open={true}
            onOpenChange={(next) => {
              setShowPostPaymentDialog(next);
              if (!next) setPostPaymentTarget(null);
            }}
            claim={postPaymentTarget?.claim}
            initialAction={postPaymentTarget?.initialAction}
            onSaved={() => fetchClientBilling(selectedClient?.id)}
          />
        </Suspense>
      )}

      {/* Client-level multi-claim "Post Payment" — opens from the top button
          on the Client Chart Billing tab. Insurance or self-pay; distributes
          across multiple open balances; overflow goes to escrow. */}
      {showPostPaymentDialog && !postPaymentTarget?.claim && (
        <Suspense fallback={null}>
          <ClientPostPaymentDialog
            open={true}
            onOpenChange={(next) => {
              setShowPostPaymentDialog(next);
              if (!next) setPostPaymentTarget(null);
            }}
            client={selectedClient}
            claims={clientClaims || []}
            escrowBalance={clientBillingSummary?.financials?.escrow_balance || 0}
            onSaved={() => fetchClientBilling(selectedClient?.id)}
          />
        </Suspense>
      )}


      {/* Bulk Communications Dialog */}
      <Dialog open={showBulkCommunicationsDialog} onOpenChange={setShowBulkCommunicationsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-teal-600" />
              Send Bulk Communication
            </DialogTitle>
            <DialogDescription>
              Send messages to multiple clients based on filters. Messages go to client portal inbox.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Filters */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm">Filter Recipients</h3>
              
              {/* Status Filter */}
              <div>
                <Label className="text-sm">Client Status</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {['active', 'waitlist', 'inactive'].map(status => (
                    <label key={status} className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={bulkCommFilters.statuses.includes(status)}
                        onChange={(e) => {
                          const newStatuses = e.target.checked
                            ? [...bulkCommFilters.statuses, status]
                            : bulkCommFilters.statuses.filter(s => s !== status);
                          const newFilters = { ...bulkCommFilters, statuses: newStatuses };
                          setBulkCommFilters(newFilters);
                          fetchBulkCommPreview(newFilters);
                        }}
                        className="rounded"
                      />
                      <span className="capitalize">{status}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Provider Filter */}
              <div>
                <Label className="text-sm">Assigned Provider</Label>
                <Select
                  value={bulkCommFilters.provider_ids[0] || 'all'}
                  onValueChange={(value) => {
                    const newFilters = {
                      ...bulkCommFilters,
                      provider_ids: value === 'all' ? [] : [value]
                    };
                    setBulkCommFilters(newFilters);
                    fetchBulkCommPreview(newFilters);
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All Providers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Providers</SelectItem>
                    {[...users].filter(u => u.role === 'provider' || u.role === 'supervisor' || u.is_provider === true).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')).map(provider => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {formatProviderName(provider)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Service Type Filter */}
              <div>
                <Label className="text-sm">Service Type</Label>
                <Select
                  value={bulkCommFilters.service_type_ids[0] || 'all'}
                  onValueChange={(value) => {
                    const newFilters = {
                      ...bulkCommFilters,
                      service_type_ids: value === 'all' ? [] : [value]
                    };
                    setBulkCommFilters(newFilters);
                    fetchBulkCommPreview(newFilters);
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All Services" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {serviceTypes.map(st => (
                      <SelectItem key={st.id} value={st.id}>
                        {st.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Label Filter */}
              <div>
                <Label className="text-sm">Client Label</Label>
                <Select
                  value={bulkCommFilters.label_ids[0] || 'all'}
                  onValueChange={(value) => {
                    const newFilters = {
                      ...bulkCommFilters,
                      label_ids: value === 'all' ? [] : [value]
                    };
                    setBulkCommFilters(newFilters);
                    fetchBulkCommPreview(newFilters);
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All Labels" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Labels</SelectItem>
                    {clientLabels.map(label => (
                      <SelectItem key={label.id} value={label.id}>
                        {label.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Preview Recipients */}
              <div className="p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">Recipients Preview</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => fetchBulkCommPreview(bulkCommFilters)}
                    disabled={loadingBulkCommPreview}
                  >
                    <RefreshCw className={`h-3 w-3 ${loadingBulkCommPreview ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                {bulkCommPreview ? (
                  <div className="space-y-1 text-sm">
                    <p className="font-semibold text-lg text-teal-600">{bulkCommPreview.total_recipients} recipients</p>
                    <p className="text-gray-600">{bulkCommPreview.email_enabled_count} with email</p>
                    <p className="text-gray-600">{bulkCommPreview.sms_enabled_count} with SMS enabled</p>
                    {bulkCommPreview.preview_truncated && (
                      <p className="text-xs text-amber-600">Showing first 100 of {bulkCommPreview.total_recipients}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Select filters to preview recipients</p>
                )}
              </div>
            </div>
            
            {/* Right Column - Message */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm">Compose Message</h3>
              
              {/* Template Selection */}
              {messageTemplates.length > 0 && (
                <div>
                  <Label className="text-sm">Use Template</Label>
                  <Select
                    value=""
                    onValueChange={(templateId) => {
                      const template = messageTemplates.find(t => t.id === templateId);
                      if (template) {
                        setBulkCommSubject(template.subject);
                        setBulkCommMessage(template.message);
                      }
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {messageTemplates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Subject */}
              <div>
                <Label className="text-sm">Subject *</Label>
                <DebouncedInput
                  value={bulkCommSubject}
                  onChange={(e) => setBulkCommSubject(e.target.value)}
                  placeholder="Message subject..."
                  className="h-9"
                />
              </div>
              
              {/* Message */}
              <div>
                <Label className="text-sm">Message *</Label>
                <DebouncedTextarea
                  value={bulkCommMessage}
                  onChange={(e) => setBulkCommMessage(e.target.value)}
                  placeholder="Type your message here..."
                  rows={6}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Available merge fields: {'{client_name}'}, {'{first_name}'}, {'{last_name}'}
                </p>
              </div>
              
              {/* Delivery Options */}
              <div className="space-y-2 p-3 bg-gray-50 rounded-lg border">
                <Label className="text-sm font-medium">Delivery Options</Label>
                
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="rounded"
                  />
                  <span className="text-sm">Client Portal Inbox</span>
                  <Badge variant="secondary" className="text-xs">Always</Badge>
                </label>
                
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={bulkCommSendEmail}
                    onChange={(e) => setBulkCommSendEmail(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Also send via Email</span>
                  {bulkCommPreview && (
                    <span className="text-xs text-gray-500">({bulkCommPreview.email_enabled_count} recipients)</span>
                  )}
                </label>
                
                <label className="flex items-center gap-2 opacity-50">
                  <input
                    type="checkbox"
                    checked={false}
                    disabled
                    className="rounded"
                  />
                  <span className="text-sm">Also send via SMS</span>
                  <Badge variant="outline" className="text-xs">Coming Soon</Badge>
                </label>
                
                {bulkCommSendEmail && (
                  <p className="text-xs text-gray-500 mt-2 pl-5">
                    Emails sent from no-reply address. Recipients will be informed that replies are not monitored.
                  </p>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowBulkCommunicationsDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={sendBulkCommunication}
              disabled={sendingBulkComm || !bulkCommSubject.trim() || !bulkCommMessage.trim() || !bulkCommPreview?.total_recipients}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {sendingBulkComm ? (
                <>Sending...</>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to {bulkCommPreview?.total_recipients || 0} Recipients
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Communications History Dialog */}
      <Dialog open={showBulkCommHistoryDialog} onOpenChange={setShowBulkCommHistoryDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Communication History
            </DialogTitle>
            <DialogDescription>
              View previously sent bulk communications
            </DialogDescription>
          </DialogHeader>
          
          {bulkCommHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No bulk communications sent yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bulkCommHistory.map((comm) => (
                <div key={comm.id} className="p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium">{comm.subject}</h4>
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">{comm.message}</p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="font-medium text-teal-600">{comm.recipient_count} recipients</p>
                      {comm.send_email && (
                        <p className="text-xs text-gray-500">{comm.email_sent_count} emails sent</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                    <span>Sent by {comm.sent_by_name}</span>
                    <span>{new Date(comm.sent_at).toLocaleString()}</span>
                    <div className="flex gap-1">
                      <Badge variant="secondary">Portal</Badge>
                      {comm.send_email && <Badge variant="secondary">Email</Badge>}
                      {comm.send_sms && <Badge variant="secondary">SMS</Badge>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => setShowBulkCommHistoryDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Templates Dialog */}
      <Dialog open={showMessageTemplatesDialog} onOpenChange={setShowMessageTemplatesDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Message Templates
            </DialogTitle>
            <DialogDescription>
              Create reusable templates for bulk communications
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Template Form */}
            <div className="p-4 border rounded-lg bg-gray-50">
              <h4 className="font-medium mb-3">{editingTemplate ? 'Edit Template' : 'Create New Template'}</h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Template Name *</Label>
                  <DebouncedInput
                    value={templateFormData.name}
                    onChange={(e) => setTemplateFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Holiday Closure Notice"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-sm">Subject *</Label>
                  <DebouncedInput
                    value={templateFormData.subject}
                    onChange={(e) => setTemplateFormData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Message subject..."
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-sm">Message *</Label>
                  <DebouncedTextarea
                    value={templateFormData.message}
                    onChange={(e) => setTemplateFormData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Type your template message..."
                    rows={4}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available merge fields: {'{client_name}'}, {'{first_name}'}, {'{last_name}'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveMessageTemplate} size="sm" className="bg-teal-600 hover:bg-teal-700">
                    {editingTemplate ? 'Update Template' : 'Save Template'}
                  </Button>
                  {editingTemplate && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingTemplate(null);
                        setTemplateFormData({ name: '', subject: '', message: '' });
                      }}
                    >
                      Cancel Edit
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Existing Templates */}
            <div>
              <h4 className="font-medium mb-2">Saved Templates ({messageTemplates.length})</h4>
              {messageTemplates.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No templates created yet</p>
              ) : (
                <div className="space-y-2">
                  {messageTemplates.map(template => (
                    <div key={template.id} className="p-3 border rounded-lg flex items-start justify-between hover:bg-gray-50">
                      <div className="flex-1">
                        <p className="font-medium">{template.name}</p>
                        <p className="text-sm text-gray-600">{template.subject}</p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">{template.message}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingTemplate(template);
                            setTemplateFormData({
                              name: template.name,
                              subject: template.subject,
                              message: template.message
                            });
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteMessageTemplate(template.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => setShowMessageTemplatesDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Labels Management Dialog */}
      <Dialog open={showClientLabelsDialog} onOpenChange={setShowClientLabelsDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-start justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Manage Client Labels
              </DialogTitle>
              <DialogDescription>
                Create and manage labels to organize and categorize clients
              </DialogDescription>
            </div>
            <AdminJumpToDropdown currentDialogId="labels" isDirty={currentAdminDialogDirty} />
          </DialogHeader>

          <div className="space-y-6">
            {/* Add New Label Form */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <Label className="font-medium">Add New Label</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Label Name *</Label>
                  <DebouncedInput
                    value={newLabelData.name}
                    onChange={(e) => setNewLabelData(prev => ({...prev, name: e.target.value}))}
                    placeholder="e.g., Self-Pay, High-Risk"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select 
                    value={newLabelData.category}
                    onValueChange={(value) => setNewLabelData(prev => ({...prev, category: value}))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="clinical">Clinical</SelectItem>
                      <SelectItem value="administrative">Administrative</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Color</Label>
                  <div className="flex flex-wrap gap-2 mt-1 items-center">
                    {['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280'].map(color => (
                      <button
                        key={color}
                        type="button"
                        className={`w-6 h-6 rounded-full border-2 shadow-sm hover:scale-110 transition-transform ${newLabelData.color === color ? 'border-gray-800 ring-2 ring-offset-1 ring-gray-400' : 'border-white'}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setNewLabelData(prev => ({...prev, color}))}
                      />
                    ))}
                    {/* Custom color picker */}
                    <div className="relative">
                      <input
                        type="color"
                        value={newLabelData.color}
                        onChange={(e) => setNewLabelData(prev => ({...prev, color: e.target.value}))}
                        className="w-6 h-6 rounded-full cursor-pointer border-2 border-dashed border-gray-400 hover:border-gray-600"
                        title="Pick custom color"
                      />
                    </div>
                    <span className="text-xs text-gray-400 ml-1">or pick custom</span>
                  </div>
                </div>
                <div className="flex items-end">
                  <Button
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={async () => {
                      if (!newLabelData.name.trim()) {
                        toast.error('Please enter a label name');
                        return;
                      }
                      
                      try {
                        await axios.post(`${API}/client-labels`, newLabelData);
                        toast.success('Label created');
                        fetchClientLabels();
                        setNewLabelData({ name: '', category: 'general', color: '#3b82f6' });
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to create label');
                      }
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Label
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Existing Labels */}
            <div>
              <Label className="font-medium mb-2 block">All Labels ({clientLabels.length})</Label>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {clientLabels.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No labels created yet</p>
                ) : (
                  clientLabels.map(label => (
                    <div key={label.id} className="flex items-center justify-between p-2 bg-white border rounded-lg">
                      <div className="flex items-center gap-2">
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: label.color }}
                        >
                          {label.name}
                        </span>
                        <span className="text-xs text-gray-500 capitalize">{label.category}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-red-600 hover:text-red-800 hover:bg-red-50"
                        onClick={async () => {
                          if (window.confirm(`Delete label "${label.name}"? It will be removed from all clients.`)) {
                            try {
                              await axios.delete(`${API}/client-labels/${label.id}`);
                              toast.success('Label deleted');
                              fetchClientLabels();
                            } catch (error) {
                              toast.error('Failed to delete label');
                            }
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientLabelsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Member Removal Reason Dialog */}
      <Dialog open={showRemovalDialog} onOpenChange={(open) => {
        setShowRemovalDialog(open);
        if (!open) {
          setRemoveFromFutureStartDate(new Date().toISOString().split('T')[0]);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Member from Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Please provide a reason for removing this member from the group.
            </p>
            
            <div>
              <Label htmlFor="removal_reason">Removal Reason *</Label>
              <Select value={removalReason} onValueChange={setRemovalReason} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed_successfully">Completed Successfully</SelectItem>
                  <SelectItem value="did_not_return">Did Not Return</SelectItem>
                  <SelectItem value="transferred">Transferred to Another Group</SelectItem>
                  <SelectItem value="discharged">Client Discharged</SelectItem>
                  <SelectItem value="behavioral_issues">Behavioral Issues</SelectItem>
                  <SelectItem value="schedule_conflict">Schedule Conflict</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="removal_notes">Additional Notes</Label>
              <DebouncedTextarea
                id="removal_notes"
                value={removalNotes}
                onChange={(e) => setRemovalNotes(e.target.value)}
                className="w-full min-h-[100px] p-2 border rounded"
                placeholder="Add any additional details about the removal..."
              />
            </div>
            
            {/* Last session date */}
            <div className="border-t pt-4">
              <Label htmlFor="remove_start_date" className="text-sm font-medium">
                Last Session Date:
              </Label>
              <p className="text-xs text-gray-500 mb-2">
                The member's final session in this group. They will be removed from all sessions after this date.
              </p>
              <Input
                id="remove_start_date"
                type="date"
                value={removeFromFutureStartDate}
                onChange={(e) => setRemoveFromFutureStartDate(e.target.value)}
                className="w-48"
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowRemovalDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmRemoval} className="bg-red-600 hover:bg-red-700">
                Remove Member
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Attendee to Single Appointment Dialog */}
      <Dialog open={showAddAttendeeDialog} onOpenChange={(open) => {
        setShowAddAttendeeDialog(open);
        if (!open) {
          setAddAttendeeSearch('');
          setAddAttendeeAppointment(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-600" />
              Add Attendee to Session
            </DialogTitle>
            <DialogDescription>
              Add a drop-in or guest to this specific session only. They won't be added to the group roster.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current attendees */}
            {addAttendeeAppointment && (
              <div>
                <Label className="text-sm font-medium">Current Attendees</Label>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(addAttendeeAppointment.client_ids || []).map(clientId => {
                    const client = allClientsForLookup.find(c => c.id === clientId);
                    return client ? (
                      <div key={clientId} className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                        <span>{client.full_name}</span>
                        <button
                          className="ml-1 hover:text-red-600"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/appointments/${addAttendeeAppointment.id}/remove-attendee`, null, {
                                params: { client_id: clientId }
                              });
                              toast.success(`Removed ${client.full_name} from session`);
                              
                              // Update the appointment in local state
                              setAddAttendeeAppointment(prev => ({
                                ...prev,
                                client_ids: prev.client_ids.filter(id => id !== clientId)
                              }));
                              
                              // Refresh appointments
                              refreshAppointments(true); refreshStats();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to remove attendee');
                            }
                          }}
                          title="Remove from this session"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : null;
                  })}
                  {(!addAttendeeAppointment.client_ids || addAttendeeAppointment.client_ids.length === 0) && (
                    <span className="text-sm text-gray-500">No attendees yet</span>
                  )}
                </div>
              </div>
            )}
            
            {/* Search to add new attendee */}
            <div>
              <Label className="text-sm font-medium">Add Client</Label>
              <DebouncedInput
                placeholder="Search clients by name..."
                value={addAttendeeSearch}
                onChange={(e) => setAddAttendeeSearch(e.target.value)}
                className="mt-1"
              />
              
              {addAttendeeSearch && (
                <div className="mt-2 max-h-48 overflow-y-auto border rounded">
                  {(allClientsForLookup || clients)
                    ?.filter(c => 
                      c.full_name?.toLowerCase().includes(addAttendeeSearch.toLowerCase()) &&
                      !addAttendeeAppointment?.client_ids?.includes(c.id)
                    )
                    .slice(0, 10)
                    .map(client => (
                      <div
                        key={client.id}
                        className="px-3 py-2 hover:bg-green-50 cursor-pointer flex items-center justify-between"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/appointments/${addAttendeeAppointment.id}/add-attendee`, null, {
                              params: { client_id: client.id }
                            });
                            toast.success(`Added ${client.full_name} to session`);
                            
                            // Update the appointment in local state
                            setAddAttendeeAppointment(prev => ({
                              ...prev,
                              client_ids: [...(prev.client_ids || []), client.id]
                            }));
                            
                            setAddAttendeeSearch('');
                            
                            // Refresh appointments
                            refreshAppointments(true); refreshStats();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to add attendee');
                          }
                        }}
                      >
                        <span>{client.full_name}</span>
                        <UserPlus className="h-4 w-4 text-green-600" />
                      </div>
                    ))}
                  {(allClientsForLookup || clients)?.filter(c => 
                    c.full_name?.toLowerCase().includes(addAttendeeSearch.toLowerCase()) &&
                    !addAttendeeAppointment?.client_ids?.includes(c.id)
                  ).length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">No matching clients found</div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAttendeeDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Former Members Dialog */}
      <GroupFormerMembersDialog
        open={showFormerMembers}
        onOpenChange={setShowFormerMembers}
        selectedGroupForMembers={selectedGroupForMembers}
        clients={allClientsForLookup}
        onReactivate={editingGroup ? handleReactivateMember : null}
      />

      {/* Change Request Dialog */}
      <Suspense fallback={null}>
        <ChangeRequestDialog
          open={showChangeRequestDialog}
          onOpenChange={setShowChangeRequestDialog}
          changeRequestSection={changeRequestSection}
          changeRequestReason={changeRequestReason}
          setChangeRequestReason={setChangeRequestReason}
          editingClient={editingClient}
        />
      </Suspense>

      {/* Request Group Dialog */}
      <GroupRequestDialog
        open={showRequestGroupDialog}
        onOpenChange={setShowRequestGroupDialog}
        requestGroupData={requestGroupData}
        setRequestGroupData={setRequestGroupData}
        clients={allClientsForLookup}
        clientGroups={clientGroups}
        onSuccess={fetchPendingGroupRequests}
        selectedClient={selectedClient}
      />

      {/* Group Admission Approval Dialog */}
      <GroupApprovalDialog
        open={showGroupApprovalDialog}
        onOpenChange={setShowGroupApprovalDialog}
        groupApprovalRequest={groupApprovalRequest}
        groupApprovalData={groupApprovalData}
        setGroupApprovalData={setGroupApprovalData}
        setGroupApprovalRequest={setGroupApprovalRequest}
        clients={allClientsForLookup}
        clientGroups={clientGroups}
        onSuccess={() => {
          // Optimistically update the groups to show the new member immediately
          if (groupApprovalRequest) {
            const clientId = groupApprovalRequest.client_id;
            const groupId = groupApprovalRequest.group_id;
            const startDate = groupApprovalData?.start_date || new Date().toISOString().split('T')[0];
            
            // Find the client info for display
            const approvedClient = allClientsForLookup.find(c => c.id === clientId);
            
            // Optimistically update clientGroups to include the new member
            setClientGroups(prevGroups => prevGroups.map(group => {
              if (group.id === groupId) {
                // Check if member already exists as active
                const existingMember = group.members?.find(m => m.client_id === clientId);
                if (existingMember && existingMember.status === 'active') return group;
                
                const clientDetails = approvedClient ? {
                  id: approvedClient.id,
                  full_name: approvedClient.full_name,
                  mrn: approvedClient.mrn
                } : null;
                
                let updatedMembers;
                if (existingMember && existingMember.status !== 'active') {
                  // Reactivate existing removed member
                  updatedMembers = (group.members || []).map(m =>
                    m.client_id === clientId
                      ? { ...m, status: 'active', joined_date: startDate, exit_date: null, exit_reason: null, exit_notes: null, client_details: clientDetails, client_name: clientDetails?.full_name }
                      : m
                  );
                } else {
                  // Add new member
                  const newMember = {
                    client_id: clientId,
                    status: 'active',
                    joined_date: startDate,
                    sessions_attended: 0,
                    total_sessions: group.total_sessions || 0,
                    client_details: clientDetails,
                    client_name: clientDetails?.full_name
                  };
                  updatedMembers = [...(group.members || []), newMember];
                }
                
                return {
                  ...group,
                  members: updatedMembers,
                  client_ids: [...new Set([...(group.client_ids || []), clientId])]
                };
              }
              return group;
            }));
            
            // Optimistically update the client's assigned_providers if group has a facilitator
            const targetGroup = clientGroups.find(g => g.id === groupId);
            if (targetGroup?.facilitator_id && targetGroup?.service_type_id) {
              setClients(prevClients => prevClients.map(client => {
                if (client.id === clientId) {
                  const updatedProviders = [...new Set([...(client.assigned_providers || []), targetGroup.facilitator_id])];
                  return { ...client, assigned_providers: updatedProviders };
                }
                return client;
              }));
              
              // Also update allClientsForLookup
              setAllClientsForLookup(prevClients => prevClients.map(client => {
                if (client.id === clientId) {
                  const updatedProviders = [...new Set([...(client.assigned_providers || []), targetGroup.facilitator_id])];
                  return { ...client, assigned_providers: updatedProviders };
                }
                return client;
              }));
            }
          }
          
          // Also do a background refresh to ensure data consistency
          refreshNotes(); refreshClients(); refreshStats();
          fetchPendingGroupRequests();
        }}
      />

      {/* Medication Referral Dialog — extracted Feb 2026.
          See /app/frontend/src/components/features/MedicationReferralDialog.jsx
          The inline 1,253-line block this replaced is now mounted as
          the <MedicationReferralDialog/> component below. */}
      <MedicationReferralDialog
        open={showMedicationReferralDialog}
        setShowMedicationReferralDialog={setShowMedicationReferralDialog}
        medicationReferralClient={medicationReferralClient}
        medicationReferralFormData={medicationReferralFormData}
        setMedicationReferralFormData={setMedicationReferralFormData}
        selectedMedicationManagers={selectedMedicationManagers}
        setSelectedMedicationManagers={setSelectedMedicationManagers}
        medicationReferralTemplate={medicationReferralTemplate}
        fetchMedicationReferralTemplate={fetchMedicationReferralTemplate}
        generatingAISummary={generatingAISummary}
        setGeneratingAISummary={setGeneratingAISummary}
        setClientMedications={setClientMedications}
        setSelectedClient={setSelectedClient}
        setShowMedicationForm={setShowMedicationForm}
        setShowDiagnosisForm={setShowDiagnosisForm}
        selectedNote={selectedNote}
        setSelectedNote={setSelectedNote}
        setActiveFormFields={setActiveFormFields}
        isRefreshingDiagnoses={isRefreshingDiagnoses}
        setIsRefreshingDiagnoses={setIsRefreshingDiagnoses}
        users={users}
        user={user}
        medicationManagerUsers={medicationManagerUsers}
        clientMedications={clientMedications}
        clientDiagnoses={clientDiagnoses}
        clientEligibility={clientEligibility}
        allClientsForLookup={allClientsForLookup}
        selectedTemplate={selectedTemplate}
      />

      {/* Custom Prompt Dialog (replaces browser prompt for styling) */}
      <Dialog open={customPromptDialog.open} onOpenChange={() => setCustomPromptDialog({ open: false, title: '', placeholder: '', onSubmit: null })}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{customPromptDialog.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <DebouncedTextarea
              id="custom-prompt-input"
              placeholder={customPromptDialog.placeholder}
              rows={4}
              className="w-full"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  const value = e.target.value;
                  if (customPromptDialog.onSubmit) {
                    customPromptDialog.onSubmit(value || '');
                    setCustomPromptDialog({ open: false, title: '', placeholder: '', onSubmit: null });
                  }
                }
              }}
            />
            <p className="text-sm text-gray-500">Press Ctrl+Enter to submit</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setCustomPromptDialog({ open: false, title: '', placeholder: '', onSubmit: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const value = document.getElementById('custom-prompt-input').value;
                if (customPromptDialog.onSubmit) {
                  customPromptDialog.onSubmit(value || '');
                  setCustomPromptDialog({ open: false, title: '', placeholder: '', onSubmit: null });
                }
              }}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Referral Details Dialog */}
      <MedicationReferralDetailsDialog ctx={{
        API,
        allClientsForLookup,
        editingReferralFormData,
        fetchMedicationReferralRequests,
        formTemplates,
        formatClientName,
        medicationReferralTemplate,
        selectedReferralForDetails,
        serviceRequestTemplates,
        setEditingReferralFormData,
        setSelectedReferralForDetails,
        user,
      }} />

      {/* Medication Manager Dashboard Dialog */}
      <Dialog open={showMedicationManagerDashboard} onOpenChange={setShowMedicationManagerDashboard}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">💊 Medication Manager Dashboard</DialogTitle>
            <DialogDescription>
              Review and manage medication referral requests assigned to you
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {medicationReferralRequests.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>No medication referral requests at this time</p>
              </div>
            ) : (
              <>
                {/* Section 1: Pending Referrals */}
                {(() => {
                  const pendingRequests = medicationReferralRequests.filter(r => r.status === 'pending');
                  if (pendingRequests.length === 0) return null;
                  
                  return (
                    <div>
                      <h3 className="text-lg font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                        <Badge className="bg-yellow-600 text-white">{pendingRequests.length}</Badge>
                        Pending Referrals
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">New referrals awaiting your review</p>
                      <div className="space-y-3">
                        {pendingRequests.map((request) => {
                          const requestClient = allClientsForLookup.find(c => c.id === request.client_id);
                          const template = formTemplates.find(t => t.id === request.template_id);
                          
                          return (
                            <Card key={request.id} className="border-2 border-yellow-300 bg-yellow-50">
                              <CardHeader>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <button
                                      onClick={() => setSelectedReferralForDetails(request)}
                                      className="text-lg font-semibold text-purple-900 hover:underline cursor-pointer"
                                    >
                                      {requestClient ? formatClientName(requestClient) : request.client_name}
                                    </button>
                                    {requestClient?.mrn && (
                                      <span className="text-sm text-gray-600 ml-2">MRN: {formatMrn(requestClient.mrn)}</span>
                                    )}
                                    {request.doc_number && (
                                      <span className="text-sm text-gray-600 ml-2">
                                        Doc ID: <span className="font-mono text-teal-700 bg-teal-50 px-1 py-0.5 rounded">{request.doc_number}</span>
                                      </span>
                                    )}
                                    <p className="text-sm text-gray-600 mt-1">
                                      <strong>Requested by:</strong> {request.requested_by_name} • {new Date(request.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => {
                                      setSelectedMedReferralProvider('');
                                      setSelectedMedReferralService('');
                                      setShowApprovalProviderDropdown(request.id);
                                    }}
                                  >
                                    <Check className="h-4 w-4 mr-1" />
                                    Approve
                                  </Button>
                                  
                                  {showApprovalProviderDropdown === request.id && (
                                    <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border">
                                      {/* Step 1: Provider Selection */}
                                      <div>
                                        <Label className="text-xs text-gray-600 mb-1">Assign Provider *</Label>
                                        <Select
                                          value={selectedMedReferralProvider}
                                          onValueChange={(value) => {
                                            setSelectedMedReferralProvider(value);
                                            setSelectedMedReferralService(''); // Reset service when provider changes
                                          }}
                                        >
                                          <SelectTrigger className="w-[280px] h-8">
                                            <SelectValue placeholder="Select provider or waitlist..." />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="waitlist">Add to Med Management Waitlist</SelectItem>
                                            {medicationManagerUsers.length === 0 ? (
                                              <div className="px-2 py-1 text-sm text-gray-500">No medication providers available</div>
                                            ) : (
                                              medicationManagerUsers.map(mgr => (
                                                <SelectItem key={mgr.id} value={mgr.id}>
                                                  {mgr.full_name || `${mgr.first_name} ${mgr.last_name}`}
                                                </SelectItem>
                                              ))
                                            )}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      
                                      {/* Step 2: Service Selection (only when provider selected, not waitlist) */}
                                      {selectedMedReferralProvider && selectedMedReferralProvider !== 'waitlist' && (
                                        <div>
                                          <Label className="text-xs text-gray-600 mb-1">Service Enrollment (Optional)</Label>
                                          <Select
                                            value={selectedMedReferralService || ''}
                                            onValueChange={setSelectedMedReferralService}
                                          >
                                            <SelectTrigger className="w-[280px] h-8">
                                              <SelectValue placeholder="Select a service type..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="none">No service enrollment</SelectItem>
                                              {serviceTypes && serviceTypes.map(service => (
                                                <SelectItem key={service.id} value={service.id}>
                                                  {service.name}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      )}
                                      
                                      {/* Action Buttons */}
                                      <div className="flex gap-2 mt-1">
                                        <Button
                                          size="sm"
                                          className="bg-green-600 hover:bg-green-700"
                                          disabled={!selectedMedReferralProvider}
                                          onClick={async () => {
                                            try {
                                              const params = selectedMedReferralProvider !== 'waitlist' 
                                                ? `?assigned_provider_id=${selectedMedReferralProvider}` 
                                                : '';
                                              await axios.put(`${API}/medication-referral-requests/${request.id}/approve${params}`);
                                              
                                              // Create service enrollment if provider and service selected
                                              if (selectedMedReferralProvider !== 'waitlist' && 
                                                  selectedMedReferralService && 
                                                  selectedMedReferralService !== 'none') {
                                                try {
                                                  await axios.post(`${API}/clients/${request.client_id}/service-enrollments`, {
                                                    provider_id: selectedMedReferralProvider,
                                                    service_type: selectedMedReferralService,
                                                    status: 'active'
                                                  });
                                                } catch (enrollError) {
                                                  console.warn('Failed to create service enrollment:', enrollError);
                                                }
                                              }
                                              
                                              toast.success(selectedMedReferralProvider === 'waitlist' 
                                                ? "Approved and added to waitlist!" 
                                                : "Referral approved with provider assigned!");
                                              setShowApprovalProviderDropdown(null);
                                              setSelectedMedReferralProvider('');
                                              setSelectedMedReferralService('');
                                              fetchMedicationReferralRequests();
                                              refreshClients(); refreshStats();
                                            } catch (error) {
                                              console.error("Failed to approve:", error);
                                              toast.error("Failed to approve referral");
                                            }
                                          }}
                                        >
                                          Confirm
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => {
                                            setShowApprovalProviderDropdown(null);
                                            setSelectedMedReferralProvider('');
                                            setSelectedMedReferralService('');
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                  
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-red-600 text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                      setCustomPromptDialog({
                                        open: true,
                                        title: "Deny Referral",
                                        placeholder: "Please provide a reason for denying this referral...",
                                        onSubmit: async (reason) => {
                                          try {
                                            await axios.put(`${API}/medication-referral-requests/${request.id}/deny`, {
                                              review_notes: reason
                                            });
                                            toast.success("Referral denied");
                                            fetchMedicationReferralRequests();
                                          } catch (error) {
                                            console.error("Failed to deny:", error);
                                            toast.error("Failed to deny referral");
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    <XCircle className="h-4 w-4 mr-1" />
                                    Deny
                                  </Button>
                                  
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-orange-600 text-orange-600 hover:bg-orange-50"
                                    onClick={() => {
                                      setCustomPromptDialog({
                                        open: true,
                                        title: "Request More Information",
                                        placeholder: "What additional information do you need from the provider?",
                                        onSubmit: async (question) => {
                                          try {
                                            await axios.put(`${API}/medication-referral-requests/${request.id}/request-more-info`, {
                                              more_info_question: question
                                            });
                                            toast.success("Information requested - returned to provider");
                                            fetchMedicationReferralRequests();
                                          } catch (error) {
                                            console.error("Failed to request info:", error);
                                            toast.error("Failed to request information");
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    <MessageSquare className="h-4 w-4 mr-1" />
                                    Request Info
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Section 2: Needs More Information - MY Referrals that need response */}
                {(() => {
                  // Only show referrals where I am the requester and they need more info
                  const myNeedsInfoRequests = medicationReferralRequests.filter(r => 
                    r.status === 'needs_more_info' && r.requested_by_id === user?.id
                  );
                  if (myNeedsInfoRequests.length === 0) return null;
                  
                  return (
                    <div>
                      <h3 className="text-lg font-semibold text-orange-900 mb-3 flex items-center gap-2">
                        <Badge className="bg-orange-600 text-white">{myNeedsInfoRequests.length}</Badge>
                        My Referrals - Response Needed
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">The medication team needs more information from you</p>
                      <div className="space-y-3">
                        {myNeedsInfoRequests.map((request) => {
                          const requestClient = allClientsForLookup.find(c => c.id === request.client_id);
                          
                          return (
                            <Card key={request.id} className="border-2 border-orange-300 bg-orange-50">
                              <CardHeader>
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <button
                                      onClick={() => setSelectedReferralForDetails(request)}
                                      className="text-lg font-semibold text-purple-900 hover:underline cursor-pointer"
                                    >
                                      {requestClient ? formatClientName(requestClient) : request.client_name}
                                    </button>
                                    {requestClient?.mrn && (
                                      <span className="text-sm text-gray-600 ml-2">MRN: {formatMrn(requestClient.mrn)}</span>
                                    )}
                                    <p className="text-sm text-gray-600 mt-1">
                                      <strong>Reviewed by:</strong> {request.reviewed_by_name} • {new Date(request.reviewed_at || request.created_at).toLocaleDateString()}
                                    </p>
                                    {request.more_info_question && (
                                      <div className="mt-2 p-3 bg-orange-100 rounded-lg border border-orange-200">
                                        <p className="text-sm text-orange-800">
                                          <MessageSquare className="inline h-4 w-4 mr-1" />
                                          <strong>Question from med team:</strong>
                                        </p>
                                        <p className="text-sm text-orange-900 mt-1">{request.more_info_question}</p>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex gap-2 ml-4">
                                    <Button
                                      size="sm"
                                      className="bg-orange-600 hover:bg-orange-700"
                                      onClick={() => {
                                        setCustomPromptDialog({
                                          open: true,
                                          title: "Respond to Question",
                                          placeholder: "Enter your response to the medication team's question...",
                                          onSubmit: async (answer) => {
                                            if (!answer || answer.trim() === '') {
                                              toast.error("Please provide a response");
                                              return;
                                            }
                                            try {
                                              await axios.put(`${API}/medication-referral-requests/${request.id}/answer`, {
                                                more_info_answer: answer
                                              });
                                              toast.success("Response submitted - referral resubmitted for review");
                                              fetchMedicationReferralRequests();
                                            } catch (error) {
                                              console.error("Failed to submit answer:", error);
                                              const errorMsg = error.response?.data?.detail || error.message || "Failed to submit response";
                                              toast.error(errorMsg);
                                            }
                                          }
                                        });
                                      }}
                                    >
                                      <MessageSquare className="h-4 w-4 mr-1" />
                                      Respond
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setSelectedReferralForDetails(request)}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </CardHeader>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Section 3: Denied Referrals (last 30 days only) */}
                {(() => {
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                  
                  const deniedRequests = medicationReferralRequests.filter(r => 
                    r.status === 'denied' && 
                    new Date(r.reviewed_at || r.created_at) >= thirtyDaysAgo
                  );
                  
                  if (deniedRequests.length === 0) return null;
                  
                  return (
                    <div>
                      <h3 className="text-lg font-semibold text-red-900 mb-3 flex items-center gap-2">
                        <Badge className="bg-red-600 text-white">{deniedRequests.length}</Badge>
                        Recently Denied Referrals
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">Denied within the last 30 days</p>
                      <div className="space-y-3">
                        {deniedRequests.map((request) => {
                          const requestClient = allClientsForLookup.find(c => c.id === request.client_id);
                          
                          return (
                            <Card key={request.id} className="border-2 border-red-300 bg-red-50">
                              <CardHeader>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <button
                                      onClick={() => setSelectedReferralForDetails(request)}
                                      className="text-lg font-semibold text-purple-900 hover:underline cursor-pointer"
                                    >
                                      {requestClient ? formatClientName(requestClient) : request.client_name}
                                    </button>
                                    {requestClient?.mrn && (
                                      <span className="text-sm text-gray-600 ml-2">MRN: {formatMrn(requestClient.mrn)}</span>
                                    )}
                                    <p className="text-sm text-gray-600 mt-1">
                                      <strong>Requested by:</strong> {request.requested_by_name} • {new Date(request.created_at).toLocaleDateString()}
                                    </p>
                                    {request.review_notes && (
                                      <p className="text-sm text-red-700 mt-1">
                                        <strong>Reason:</strong> {request.review_notes}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </CardHeader>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Attendance Tracking Dialog */}
      <AttendanceTrackingDialog
        open={showAttendanceDialog}
        onOpenChange={(open) => {
          setShowAttendanceDialog(open);
          if (!open) {
            setDirectAttendanceGroup(null);
            // Update URL to remove attendance mode
            navigate(`/dashboard?view=groups`, { replace: true });
          }
        }}
        attendanceAppointment={attendanceAppointment}
        attendanceData={attendanceData}
        setAttendanceData={setAttendanceData}
        clientGroups={clientGroups}
        clients={allClientsForLookup}
        onSaveAttendance={handleSaveAttendance}
        directGroup={directAttendanceGroup}
        sessionDate={directAttendanceDate}
        setSessionDate={setDirectAttendanceDate}
      />

      {/* Assign Providers Dialog - Enhanced with Service Selection */}
      <Suspense fallback={null}>
        <AssignProvidersDialog
          open={showAssignProviders}
          onOpenChange={setShowAssignProviders}
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          editingClient={editingClient}
          providers={providers}
          users={users}
          serviceTypes={serviceTypes}
          availableServices={availableServices}
          existingEnrollments={existingEnrollments}
          setExistingEnrollments={setExistingEnrollments}
          selectedProvidersForAssignment={selectedProvidersForAssignment}
          setSelectedProvidersForAssignment={setSelectedProvidersForAssignment}
          providerServiceAssignments={providerServiceAssignments}
          setProviderServiceAssignments={setProviderServiceAssignments}
          resetSelections={() => {
            setSelectedProvidersForAssignment([]);
            setProviderServiceAssignments({});
            setAvailableServices([]);
            setExistingEnrollments([]);
          }}
          showIntakeWizard={showIntakeWizard}
          handleIntakeFieldChange={handleIntakeFieldChange}
          clientFormData={clientFormData}
          setClientFormData={setClientFormData}
          setPendingServiceEnrollments={setPendingServiceEnrollments}
          setClientEnrollments={setClientEnrollments}
          invalidateEnrollmentCache={invalidateEnrollmentCache}
          enrollmentsCacheRef={enrollmentsCacheRef}
          reloadClientEnrollments={reloadClientEnrollments}
          refreshNotes={refreshNotes}
          refreshClients={refreshClients}
          refreshStats={refreshStats}
          confirmAction={confirmAction}
        />
      </Suspense>

      {/* Transfer Service Dialog */}
      <Dialog open={showTransferServiceDialog} onOpenChange={setShowTransferServiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer Service to Another Provider</DialogTitle>
            <DialogDescription>
              Transfer {transferEnrollment?.service_name} from {transferEnrollment?.provider_name} to a different provider.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-provider-select">Select New Provider</Label>
              <Select value={newProviderForTransfer} onValueChange={setNewProviderForTransfer}>
                <SelectTrigger id="new-provider-select">
                  <SelectValue placeholder="Choose provider..." />
                </SelectTrigger>
                <SelectContent>
                  {providers
                    .filter(p => p.id !== transferEnrollment?.provider_id)
                    .map(provider => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {provider.full_name} {provider.credentials ? `(${provider.credentials})` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
              <p className="text-amber-800">
                <strong>Note:</strong> This will discontinue the current enrollment and create a new one with the selected provider. 
                The service history and treatment plan due dates will be preserved.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowTransferServiceDialog(false);
                setTransferEnrollment(null);
                setNewProviderForTransfer('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!newProviderForTransfer) {
                  toast.error('Please select a provider');
                  return;
                }
                
                try {
                  // Discontinue the old enrollment
                  await axios.post(`${API}/service-enrollments/${transferEnrollment.id}/discontinue`, {
                    reason: `Transferred to provider ${providers.find(p => p.id === newProviderForTransfer)?.full_name}`
                  }, {
                    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                  });
                  
                  // Create new enrollment with new provider
                  await axios.post(`${API}/clients/${transferEnrollment.client_id}/service-enrollments`, {
                    provider_id: newProviderForTransfer,
                    service_type: transferEnrollment.service_type,
                    visit_frequency: transferEnrollment.visit_frequency || 'weekly',
                    notes: `Transferred from ${transferEnrollment.provider_name}`
                  }, {
                    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                  });
                  
                  toast.success('Service transferred successfully');
                  
                  // Refresh enrollments via the centralized helper so
                  // both the chart header (`clientEnrollments`) and
                  // the Edit Client "Manage" dialog (`existingEnrollments`)
                  // stay in sync after the transfer.
                  invalidateEnrollmentCache(selectedClient.id);
                  await reloadClientEnrollments(selectedClient.id);
                  
                  // Close dialog
                  setShowTransferServiceDialog(false);
                  setTransferEnrollment(null);
                  setNewProviderForTransfer('');
                  
                } catch (error) {
                  console.error('Error transferring service:', error);
                  toast.error(error.response?.data?.detail || 'Failed to transfer service');
                }
              }}
              disabled={!newProviderForTransfer}
            >
              Transfer Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog open={!!viewingDocument} onOpenChange={(open) => {
        if (!open) {
          setViewingDocument(null);
          if (documentBlobUrl) {
            window.URL.revokeObjectURL(documentBlobUrl);
            setDocumentBlobUrl(null);
          }
          setDocumentZoom(100);
        }
      }}>
        <DialogContent className="w-[95vw] h-[95vh] max-w-none p-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b pr-14">
            {/* Document Title and Navigation - pr-14 gives space for close button */}
            <div className="flex flex-row items-center justify-between mb-4">
              <DialogTitle className="flex-1">{viewingDocument?.filename}</DialogTitle>
              <div className="flex items-center space-x-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDocumentZoom(Math.max(25, documentZoom - 25))}
                disabled={documentZoom <= 25}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium w-16 text-center">{documentZoom}%</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDocumentZoom(Math.min(300, documentZoom + 25))}
                disabled={documentZoom >= 300}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDocumentZoom(100)}
              >
                Reset
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/documents/${viewingDocument.id}/download`, {
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    // Use consistent filename convention
                    const downloadFilename = generateDocumentDownloadFilename(viewingDocument, selectedClient);
                    link.setAttribute('download', downloadFilename);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast.success('Document downloaded');
                  } catch (error) {
                    toast.error('Failed to download document');
                  }
                }}
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
            </div>
            </div>
            
            {/* Document Navigation */}
            {viewingDocument && clientDocuments && clientDocuments.length > 1 && (() => {
              // Sort documents by most recent first
              const sortedDocs = [...clientDocuments].sort((a, b) => {
                const dateA = new Date(a.uploaded_at || a.created_at);
                const dateB = new Date(b.uploaded_at || b.created_at);
                return dateB - dateA;
              });
              
              // Find current document index
              const currentIndex = sortedDocs.findIndex(d => d.id === viewingDocument.id);
              
              const handleNavigateToDoc = async (doc) => {
                try {
                  // Revoke old blob URL
                  if (documentBlobUrl) {
                    window.URL.revokeObjectURL(documentBlobUrl);
                  }
                  
                  // Fetch new document
                  const response = await axios.get(`${API}/documents/${doc.id}/download?inline=true`, {
                    responseType: 'blob'
                  });
                  // Use the response content type if available, otherwise fall back to document's file_type
                  const contentType = response.headers['content-type'] || doc.file_type || 'application/octet-stream';
                  const blob = new Blob([response.data], { type: contentType });
                  const url = window.URL.createObjectURL(blob);
                  setDocumentBlobUrl(url);
                  setViewingDocument(doc);
                  setDocumentZoom(100);
                  void 0 && console.log('[Document] Navigated to:', doc.filename, 'type:', contentType);
                } catch (error) {
                  toast.error('Failed to load document');
                }
              };
              
              return (
                <div className="mt-3 pt-3 border-t">
                  {/* Single Navigation Row */}
                  <div className="flex items-center justify-between text-sm">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={currentIndex === 0}
                      onClick={() => handleNavigateToDoc(sortedDocs[currentIndex - 1])}
                      className="gap-1"
                    >
                      <ArrowLeft className="h-3 w-3" />
                      Previous
                    </Button>
                    <span className="text-gray-600 font-medium">
                      {currentIndex + 1} of {sortedDocs.length} Documents
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={currentIndex === sortedDocs.length - 1}
                      onClick={() => handleNavigateToDoc(sortedDocs[currentIndex + 1])}
                      className="gap-1"
                    >
                      Next
                      <ArrowRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })()}
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-gray-100 p-4 flex justify-center">
            {documentBlobUrl && (
              <div 
                className="bg-white shadow-lg"
                style={{ 
                  width: viewingDocument?.file_type?.includes('pdf') ? '100%' : `${Math.max(documentZoom, 80)}%`,
                  maxWidth: viewingDocument?.file_type?.includes('pdf') ? 'none' : '1200px',
                  minHeight: '100%'
                }}
              >
                {viewingDocument?.file_type?.includes('pdf') ? (
                  <iframe
                    src={`${documentBlobUrl}#page=1&zoom=${documentZoom}`}
                    className="w-full border-0"
                    style={{ height: 'calc(95vh - 180px)', minHeight: '600px' }}
                    title={viewingDocument?.filename}
                  />
                ) : viewingDocument?.file_type?.includes('image') ? (
                  <img 
                    src={documentBlobUrl} 
                    alt={viewingDocument?.filename}
                    className="w-full h-auto"
                    onError={(e) => {
                      console.error('[Document] Image failed to load:', viewingDocument?.filename, 'blob URL:', documentBlobUrl);
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="text-center py-8 text-red-600"><p>Failed to render image.</p><p class="text-sm text-gray-500 mt-2">The file may be corrupted or in an unsupported format.</p></div>';
                    }}
                  />
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Preview not available for this file type.</p>
                    <p className="text-sm text-gray-500 mt-2">File type: {viewingDocument?.file_type}</p>
                    <Button 
                      className="mt-4"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = documentBlobUrl;
                        link.download = viewingDocument?.filename || 'download';
                        link.click();
                      }}
                    >
                      Download File
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDocument} onOpenChange={(open) => {
        setShowUploadDocument(open);
        if (!open) {
          setDocumentDescription('');
          setDocumentFormType('');
          setSelectedFiles([]);
          setDuplicateFileWarnings([]);
          setSuggestedCategory(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Form Type (Optional - applies to all files)
              </label>
              <Select
                value={documentFormType}
                onValueChange={setDocumentFormType}
                disabled={uploadingDocument}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  {(formTypes || [])
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(type => (
                      <SelectItem key={type.id} value={type.name}>
                        {type.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Categorize these documents for organization
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Select File(s) (Max 100MB each)
              </label>
              <Input
                type="file"
                onChange={handleFileSelect}
                disabled={uploadingDocument}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                multiple
              />
              <p className="text-xs text-gray-500 mt-1">
                You can select multiple files at once. Supported formats: PDF, Word, Images, Text
              </p>
              {selectedFiles.length > 0 && (
                <div className="mt-2 p-3 bg-green-50 rounded border border-green-200">
                  <p className="text-sm font-medium text-green-700 mb-2">
                    ✓ {selectedFiles.length} file(s) selected:
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between text-xs text-green-700">
                        <span className="truncate flex-1">{file.name}</span>
                        <span className="ml-2 text-green-600">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-green-600 mt-2">
                    Total: {(selectedFiles.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}
              {/* Duplicate File Warning */}
              {duplicateFileWarnings.length > 0 && (
                <div className="mt-2 p-3 bg-amber-50 rounded border border-amber-300">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Duplicate file(s) detected:
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-amber-700 border-amber-400 hover:bg-amber-100"
                      onClick={() => {
                        // Remove duplicate files from selectedFiles
                        const duplicateSet = new Set(duplicateFileWarnings.map(f => f.toLowerCase()));
                        const filteredFiles = selectedFiles.filter(file => 
                          !duplicateSet.has(file.name.toLowerCase())
                        );
                        setSelectedFiles(filteredFiles);
                        setDuplicateFileWarnings([]);
                        toast.success(`Removed ${duplicateFileWarnings.length} duplicate file(s)`, { id: 'skip-duplicates' });
                      }}
                    >
                      Skip Duplicates
                    </Button>
                  </div>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {duplicateFileWarnings.map((filename, index) => (
                      <div key={index} className="text-xs text-amber-700 flex items-center justify-between">
                        <span>• {filename}</span>
                        <button
                          className="text-amber-600 hover:text-amber-800 text-xs underline ml-2"
                          onClick={() => {
                            // Remove this specific file
                            const filteredFiles = selectedFiles.filter(file => 
                              file.name.toLowerCase() !== filename.toLowerCase()
                            );
                            setSelectedFiles(filteredFiles);
                            setDuplicateFileWarnings(prev => prev.filter(f => f !== filename));
                            toast.success(`Removed "${filename}"`, { id: 'skip-file' });
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 mt-2">
                    These file(s) already exist in this client's chart. Click "Skip Duplicates" to remove them or upload anyway to create copies.
                  </p>
                </div>
              )}
              {/* Auto-suggested Category Notice */}
              {suggestedCategory && documentFormType === suggestedCategory && (
                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs text-blue-700 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Category auto-suggested from filename. You can change it above if needed.
                  </p>
                </div>
              )}
            </div>
            {uploadingDocument && (
              <div className="text-sm text-gray-500">Uploading {selectedFiles.length} file(s)...</div>
            )}
          </div>

      {/* Checking Duplicates Loading Dialog */}
      <Dialog open={checkingDuplicates} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" hideCloseButton>
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-600 mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Checking for Duplicates</h3>
            <p className="text-sm text-gray-600 text-center">
              Please wait while we verify this client doesn't already exist in the system...
            </p>
          </div>
        </DialogContent>
      </Dialog>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadDocument(false);
                setDocumentDescription('');
                setDocumentFormType('');
                setSelectedFiles([]);
                setDuplicateFileWarnings([]);
                setSuggestedCategory(null);
              }}
              disabled={uploadingDocument}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmUpload}
              disabled={uploadingDocument || selectedFiles.length === 0}
              className="min-w-[140px]"
            >
              {uploadingDocument ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </span>
              ) : (
                `Upload ${selectedFiles.length} Document(s)`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Document Form Type Dialog */}
      <Dialog open={!!editingDocumentFormType} onOpenChange={(open) => !open && setEditingDocumentFormType(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Document Name</label>
              <DebouncedInput
                value={editingDocumentFormType?.editFilename ?? editingDocumentFormType?.filename ?? ''}
                onChange={(e) => setEditingDocumentFormType(prev => ({...prev, editFilename: e.target.value}))}
                placeholder="Enter document name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Folder / Category</label>
              <Select
                value={newDocumentFormType}
                onValueChange={setNewDocumentFormType}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category (or leave blank for Other)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No category (Other Notes & Documents)</SelectItem>
                  {(formTypes || [])
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(type => (
                      <SelectItem key={type.id} value={type.name}>
                        {type.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditingDocumentFormType(null);
                setNewDocumentFormType('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const formTypeValue = newDocumentFormType === '__none__' ? '' : newDocumentFormType;
                const newFilename = editingDocumentFormType?.editFilename || editingDocumentFormType?.filename;
                
                try {
                  // Update both filename and form_type
                  await axios.put(`${API}/documents/${editingDocumentFormType.id}`, {
                    filename: newFilename,
                    form_type: formTypeValue
                  });
                  
                  // Update local state
                  setClientDocuments(prev => prev.map(d => 
                    d.id === editingDocumentFormType.id 
                      ? {...d, filename: newFilename, form_type: formTypeValue}
                      : d
                  ));
                  
                  toast.success('Document updated successfully');
                  setEditingDocumentFormType(null);
                  setNewDocumentFormType('');
                } catch (error) {
                  console.error('Failed to update document:', error);
                  toast.error('Failed to update document');
                }
              }}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Client Warning Dialog */}
      <Dialog open={showDuplicateWarning} onOpenChange={(open) => {
        if (!open) {
          setRevealedSSNs({}); // Clear revealed SSNs when closing
        }
        setShowDuplicateWarning(open);
      }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Potential Duplicate Client Detected
            </DialogTitle>
            <DialogDescription>
              We found {intakePotentialDuplicates.length} existing client{intakePotentialDuplicates.length > 1 ? 's' : ''} with similar information.
              Please review carefully before proceeding.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {intakePotentialDuplicates.map((dup, index) => {
              // Determine badge color based on confidence
              const confidenceColors = {
                exact: 'bg-red-600 text-white',
                strong: 'bg-orange-600 text-white',
                likely: 'bg-yellow-600 text-white',
                possible: 'bg-blue-600 text-white'
              };
              const bgColors = {
                exact: 'bg-red-50 border-red-300',
                strong: 'bg-orange-50 border-orange-300',
                likely: 'bg-yellow-50 border-yellow-300',
                possible: 'bg-blue-50 border-blue-300'
              };
              
              return (
                <div key={dup.id} className={`border rounded-lg p-4 ${bgColors[dup.confidence] || 'bg-yellow-50 border-yellow-300'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <Badge className={confidenceColors[dup.confidence] || 'bg-yellow-600'}>
                          {dup.confidence_label || 'Potential Match'}
                        </Badge>
                        <h4 className="font-semibold text-lg">{dup.full_name}</h4>
                        <Badge variant="outline" className="capitalize">
                          {dup.status?.replace(/_/g, ' ') || 'Unknown'}
                        </Badge>
                        {dup.mrn && (
                          <span className="text-sm text-gray-600">
                            {dup.mrn.startsWith('REF-') ? 'Ref' : 'MRN'}: #{dup.mrn.replace(/MRN-|REF-/, '')}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        <div>
                          <span className="text-gray-600">DOB:</span> {dup.date_of_birth || 'Not provided'}
                        </div>
                        <div>
                          <span className="text-gray-600">Phone:</span> {dup.phone || 'Not provided'}
                        </div>
                        <div>
                          <span className="text-gray-600">Email:</span> {dup.email || 'Not provided'}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">SSN:</span>
                          {dup.ssn_masked ? (
                            <>
                              <span className="font-mono">
                                {revealedSSNs[dup.id] || dup.ssn_masked}
                              </span>
                              {!revealedSSNs[dup.id] && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-xs"
                                  onClick={async () => {
                                    try {
                                      const response = await axios.get(`${API}/clients/${dup.id}/reveal-ssn`);
                                      if (response.data.ssn) {
                                        setRevealedSSNs(prev => ({
                                          ...prev,
                                          [dup.id]: response.data.ssn
                                        }));
                                      }
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Unable to reveal SSN');
                                    }
                                  }}
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Reveal
                                </Button>
                              )}
                              {revealedSSNs[dup.id] && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => {
                                    setRevealedSSNs(prev => {
                                      const newState = { ...prev };
                                      delete newState[dup.id];
                                      return newState;
                                    });
                                  }}
                                >
                                  <EyeOff className="h-3 w-3 mr-1" />
                                  Hide
                                </Button>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400">Not on file</span>
                          )}
                        </div>
                      </div>
                      
                      <div className={`rounded p-2 ${dup.confidence === 'exact' ? 'bg-red-100' : dup.confidence === 'strong' ? 'bg-orange-100' : 'bg-yellow-100'}`}>
                        <p className="text-sm font-medium text-gray-900">
                          Why this matched:
                        </p>
                        <ul className="list-disc list-inside text-sm text-gray-700">
                          {dup.match_reasons.map((reason, i) => (
                            <li key={i}>{reason}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <div className="ml-4 flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          // Open this client's chart
                          try {
                            const response = await axios.get(`${API}/clients/${dup.id}`);
                            setSelectedClient(response.data);
                            setClientDetailTab("overview");
                            setShowClientDetail(true);
                            setShowDuplicateWarning(false);
                            setRevealedSSNs({});
                          } catch (error) {
                            toast.error("Failed to open client");
                          }
                        }}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View Chart
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          // Use this existing client instead
                          setShowDuplicateWarning(false);
                          setRevealedSSNs({});
                          setPendingClientData(null);
                          setIntakePotentialDuplicates([]);
                          // Phase 3e.3a — delegate to V2 provider.
                          intakeWizard.open(dup.id);
                          toast.info("Opened existing client for editing");
                        }}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Use This Client
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2 border-t pt-4">
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                If this is truly a <strong>different person</strong> with the same details, you can create a new record.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Tip: Adding SSN helps distinguish between people with the same name and DOB.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setShowDuplicateWarning(false);
                setRevealedSSNs({});
                setPendingClientData(null);
                setIntakePotentialDuplicates([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setRevealedSSNs({});
                handleCreateClientAnyway();
              }}
              className="bg-yellow-600 hover:bg-yellow-700"
              disabled={isCreatingClient}
            >
              {isCreatingClient ? 'Creating...' : 'Create New Client Anyway'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Delete Document Confirmation Dialog */}
      <AlertDialog open={!!documentToDelete} onOpenChange={(open) => !open && setDocumentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{documentToDelete?.filename}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await axios.delete(`${API}/documents/${documentToDelete.id}`);
                  toast.success('Document deleted');
                  
                  // Refresh documents list
                  const response = await axios.get(`${API}/clients/${selectedClient.id}/documents`);
                  setClientDocuments(response.data);
                  
                  setDocumentToDelete(null);
                } catch (error) {
                  console.error('Error deleting document:', error);
                  toast.error('Failed to delete document');
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin Navigation - Unsaved Changes Warning */}
      <AlertDialog open={showUnsavedChangesWarning} onOpenChange={setShowUnsavedChangesWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Unsaved Changes
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save them before navigating to {pendingAdminNavigation?.label}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelAdminNavigation}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => confirmAdminNavigation(false)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Discard Changes
            </Button>
            <AlertDialogAction
              onClick={() => {
                // User should save manually first, then navigate
                setShowUnsavedChangesWarning(false);
                toast.info('Please save your changes first, then navigate again.');
              }}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Save First
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Signature Pad Dialog - First Time Signing */}
      <Dialog open={showSignaturePad} onOpenChange={setShowSignaturePad}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Your Signature</DialogTitle>
            <DialogDescription>
              Draw your signature below. This will be saved to your profile and used for all future signatures.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-gray-300 rounded-lg bg-white">
              <SignatureCanvas
                ref={sigPadRef}
                canvasProps={{
                  className: 'signature-canvas w-full h-48',
                  style: { width: '100%', height: '192px' }
                }}
                backgroundColor="white"
              />
            </div>
            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={handleClearSignaturePad}>
                Clear
              </Button>
              <div className="space-x-2">
                <Button variant="outline" onClick={() => setShowSignaturePad(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveSignature} className="bg-teal-600 hover:bg-teal-700">
                  Save & Sign Note
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Next Steps Dialog - After Signing */}
      <NextStepsDialog ctx={{
        API, formTemplates, getSchedulersForProvider, nextStepsCptBlocked,
        refreshNotes, refreshTodos, selectedNote, setNotBillableComment,
        setNotBillableReason, setNotBillableTargetNote, setScheduleNextData,
        setSelectedNote, setShowNextStepsDialog, setShowNotBillableDialog,
        setShowNoteDetail, setShowScheduleNextDialog, showNextStepsDialog,
        updateNoteStatusOptimistically, user,
      }} />

      {/* Schedule Next Appointment Dialog */}
      <Dialog open={showScheduleNextDialog} onOpenChange={setShowScheduleNextDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-purple-600" />
              Schedule Next Appointment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Send a scheduling request to your scheduler for <strong>{selectedNote?.client_name || selectedClient?.first_name + ' ' + selectedClient?.last_name || 'this client'}</strong>.
            </p>
            
            <div>
              <Label>Schedule in</Label>
              <Select 
                value={scheduleNextData.timeframe} 
                onValueChange={(value) => setScheduleNextData(prev => ({ ...prev, timeframe: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1_week">1 Week</SelectItem>
                  <SelectItem value="2_weeks">2 Weeks</SelectItem>
                  <SelectItem value="3_weeks">3 Weeks</SelectItem>
                  <SelectItem value="1_month">1 Month</SelectItem>
                  <SelectItem value="6_weeks">6 Weeks</SelectItem>
                  <SelectItem value="2_months">2 Months</SelectItem>
                  <SelectItem value="3_months">3 Months</SelectItem>
                  <SelectItem value="prn">PRN (As Needed)</SelectItem>
                  <SelectItem value="custom">Custom Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {scheduleNextData.timeframe === 'custom' && (
              <div>
                <Label>Target Date</Label>
                <Input
                  type="date"
                  value={scheduleNextData.customDate}
                  onChange={(e) => setScheduleNextData(prev => ({ ...prev, customDate: e.target.value }))}
                  className="mt-1"
                />
              </div>
            )}
            
            <div>
              <Label>Notes for Scheduler (optional)</Label>
              <DebouncedTextarea
                value={scheduleNextData.notes}
                onChange={(e) => setScheduleNextData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="e.g., Client prefers morning appointments, follow up on medication adjustment..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            {/* Show which scheduler will receive this */}
            {(() => {
              const schedulers = getSchedulersForProvider(user.id);
              return schedulers.length > 0 ? (
                <div className="bg-purple-50 p-3 rounded-lg text-sm">
                  <span className="text-purple-700">
                    📤 This request will be sent to: <strong>{schedulers.map(s => s.full_name || `${s.first_name} ${s.last_name}`).join(', ')}</strong>
                  </span>
                </div>
              ) : null;
            })()}
            
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowScheduleNextDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={async () => {
                  try {
                    // Get the scheduler(s) for this provider
                    const schedulers = getSchedulersForProvider(user.id);
                    if (schedulers.length === 0) {
                      toast.error('No scheduler assigned to you');
                      return;
                    }
                    
                    // Get client info from selectedNote or selectedClient
                    const clientName = selectedNote?.client_name || 
                      (selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Client');
                    const clientId = selectedNote?.client_id || selectedClient?.id;
                    
                    // Calculate target date based on timeframe
                    let targetDate = '';
                    const today = new Date();
                    const timeframeMap = {
                      '1_week': 7,
                      '2_weeks': 14,
                      '3_weeks': 21,
                      '1_month': 30,
                      '6_weeks': 42,
                      '2_months': 60,
                      '3_months': 90
                    };
                    
                    if (scheduleNextData.timeframe === 'custom') {
                      targetDate = scheduleNextData.customDate;
                    } else if (scheduleNextData.timeframe === 'prn') {
                      targetDate = 'PRN';
                    } else {
                      const days = timeframeMap[scheduleNextData.timeframe] || 14;
                      const target = new Date(today.getTime() + days * 24 * 60 * 60 * 1000);
                      targetDate = target.toISOString().split('T')[0];
                    }
                    
                    const timeframeLabel = {
                      '1_week': '1 week',
                      '2_weeks': '2 weeks',
                      '3_weeks': '3 weeks',
                      '1_month': '1 month',
                      '6_weeks': '6 weeks',
                      '2_months': '2 months',
                      '3_months': '3 months',
                      'prn': 'PRN (As Needed)',
                      'custom': `by ${scheduleNextData.customDate}`
                    }[scheduleNextData.timeframe];
                    
                    // Create a task for each scheduler
                    for (const scheduler of schedulers) {
                      await axios.post(`${API}/tasks`, {
                        title: `Schedule appointment for ${clientName}`,
                        description: `**Scheduling Request from ${user.full_name || user.first_name}**\n\n` +
                          `Client: ${clientName}\n` +
                          `Schedule in: ${timeframeLabel}\n` +
                          `Target date: ${targetDate === 'PRN' ? 'As needed' : targetDate}\n` +
                          `${scheduleNextData.notes ? `\nNotes: ${scheduleNextData.notes}` : ''}`,
                        priority: 'medium',
                        status: 'pending',
                        assigned_to: scheduler.id,
                        created_by: user.id,
                        client_id: clientId,
                        task_type: 'scheduling_request',
                        due_date: targetDate === 'PRN' ? null : targetDate
                      });
                    }
                    
                    toast.success(`Scheduling request sent to ${schedulers.length > 1 ? 'schedulers' : schedulers[0].full_name || 'scheduler'}`);
                    setShowScheduleNextDialog(false);
                    refreshNotes(); refreshClients(); refreshStats();
                  } catch (error) {
                    console.error('Error creating scheduling task:', error);
                    toast.error('Failed to send scheduling request');
                  }
                }}
              >
                Send Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Export Dialog */}
      <Dialog open={showClientExportDialog} onOpenChange={setShowClientExportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Export Clients</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Filters row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="export-status">Filter by Status</Label>
                <Select 
                  value={clientExportFilters.status} 
                  onValueChange={(value) => setClientExportFilters({...clientExportFilters, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="discharged">Discharged</SelectItem>
                    <SelectItem value="waitlist">Waitlist</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="export-provider">Filter by Provider</Label>
                <Select 
                  value={clientExportFilters.providerId} 
                  onValueChange={(value) => setClientExportFilters({...clientExportFilters, providerId: value})}
                >
                  <SelectTrigger data-testid="export-provider-filter">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Providers</SelectItem>
                    {(providers || users?.filter(u => ['provider','supervisor'].includes(u.role?.toLowerCase())))
                      ?.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
                      .map(p => (
                        <SelectItem key={p.id} value={p.id}>{formatProviderName(p)}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="export-start-date">Start Date (optional)</Label>
                <Input
                  id="export-start-date"
                  type="date"
                  value={clientExportFilters.startDate}
                  onChange={(e) => setClientExportFilters({...clientExportFilters, startDate: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="export-end-date">End Date (optional)</Label>
                <Input
                  id="export-end-date"
                  type="date"
                  value={clientExportFilters.endDate}
                  onChange={(e) => setClientExportFilters({...clientExportFilters, endDate: e.target.value})}
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-500">Date filters apply to client creation date. All columns will be included in the export.</p>
          </div>
          
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={() => setShowClientExportDialog(false)}>
              Cancel
            </Button>
            <Button
              data-testid="export-clients-btn"
              onClick={async () => {
                try {
                  const params = new URLSearchParams();
                  if (clientExportFilters.status !== 'all') {
                    params.append('status', clientExportFilters.status);
                  }
                  if (clientExportFilters.providerId !== 'all') {
                    params.append('provider_id', clientExportFilters.providerId);
                  }
                  if (clientExportFilters.startDate) {
                    params.append('start_date', clientExportFilters.startDate);
                  }
                  if (clientExportFilters.endDate) {
                    params.append('end_date', clientExportFilters.endDate);
                  }
                  
                  const response = await axios.get(`${API}/clients/export/csv?${params.toString()}`, {
                    responseType: 'blob'
                  });
                  
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `clients_export_${new Date().toISOString().slice(0,10)}.csv`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  
                  toast.success("Clients exported successfully!");
                  setShowClientExportDialog(false);
                } catch (error) {
                  console.error('Export error:', error);
                  toast.error(error.response?.data?.detail || "Failed to export clients");
                }
              }}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Download className="h-4 w-4 mr-1" />
              Export All Columns
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Routing Rules Manager Dialog - Full Screen */}
      <Dialog open={showRoutingRulesManager} onOpenChange={setShowRoutingRulesManager}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Routing Rules Manager</DialogTitle>
                <DialogDescription>
                  Manage signature routing rules, priorities, and detect conflicts
                </DialogDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    setValidatingRules(true);
                    try {
                      const response = await axios.post(`${API}/routing-rules/validate`);
                      setRuleConflicts(response.data);
                      toast.success('Validation complete');
                    } catch (error) {
                      toast.error('Validation failed');
                    } finally {
                      setValidatingRules(false);
                    }
                  }}
                  disabled={validatingRules}
                >
                  {validatingRules ? 'Validating...' : '🔍 Detect Conflicts'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="backfill-routing-btn"
                  onClick={async () => {
                    setRunningBackfill(true);
                    try {
                      const res = await axios.post(`${API}/routing-rules/backfill-notes`, { dry_run: true });
                      setBackfillResult(res.data);
                    } catch (error) {
                      toast.error('Backfill preview failed');
                    } finally {
                      setRunningBackfill(false);
                    }
                  }}
                  disabled={runningBackfill}
                >
                  {runningBackfill ? 'Scanning...' : 'Backfill Notes'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="audit-routing-btn"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/routing-rules/audit`);
                      const broken = res.data?.broken_rules || [];
                      if (broken.length === 0) {
                        toast.success(`All ${res.data?.total_rules || 0} rules look healthy — no zombies`);
                      } else {
                        const summary = broken.map(r =>
                          `• ${r.rule_name}: ${r.issues.join('; ')}`
                        ).join('\n');
                        alert(`Found ${broken.length} broken rule(s) that match nobody in production:\n\n${summary}\n\nOpen each in the list below to fix the missing selections.`);
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Audit failed');
                    }
                  }}
                >
                  🧟 Audit Zombies
                </Button>
                <AdminJumpToDropdown currentDialogId="routing" isDirty={currentAdminDialogDirty} />
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingRoutingRule(null);
                    setRoutingRuleFormData({
                      rule_name: '',
                      description: '',
                      priority: routingRules.length,
                      scope: 'global',
                      provider_id: null,
                      template_id: null,
                      conditions: [],
                      require_signature: true,
                      route_to: 'first_supervisor',
                      specific_supervisor_id: null,
                      is_active: true
                    });
                    setShowRoutingRuleForm(true);
                    setShowRoutingRulesManager(false); // Close manager when opening form
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Rule
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Conflict Detection Results - Expanded */}
            {ruleConflicts && (
              <div className="space-y-3">
                {ruleConflicts.conflicts?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                      <span>⚠️ Conflicts Found ({ruleConflicts.conflicts.length})</span>
                    </h4>
                    {ruleConflicts.conflicts.map((conflict, idx) => (
                      <div key={idx} className="bg-white rounded p-3 mb-2 text-sm">
                        <div className="flex items-start gap-2">
                          <Badge className={`${conflict.severity === 'high' ? 'bg-red-600' : conflict.severity === 'medium' ? 'bg-orange-600' : 'bg-yellow-600'}`}>
                            {conflict.severity}
                          </Badge>
                          <div className="flex-1">
                            <p className="text-red-700 mb-1">{conflict.description}</p>
                            <p className="text-red-600 text-xs">💡 {conflict.suggestion}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {ruleConflicts.redundancies?.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-800 mb-3">ℹ️ Redundant Rules ({ruleConflicts.redundancies.length})</h4>
                    {ruleConflicts.redundancies.map((item, idx) => (
                      <div key={idx} className="bg-white rounded p-3 mb-2 text-sm">
                        <p className="text-yellow-700 mb-1">{item.description}</p>
                        <p className="text-yellow-600 text-xs">💡 {item.suggestion}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {ruleConflicts.conflicts?.length === 0 && ruleConflicts.redundancies?.length === 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <span className="text-green-700 font-semibold">✅ No conflicts or redundancies detected!</span>
                  </div>
                )}
              </div>
            )}

            {/* Backfill Results */}
            {backfillResult && (
              <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">
                    {backfillResult.dry_run ? 'Backfill Preview (Dry Run)' : 'Backfill Complete'}
                  </h4>
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <div className="bg-white rounded p-2 text-center">
                      <p className="text-lg font-bold">{backfillResult.total_notes}</p>
                      <p className="text-xs text-gray-500">Notes Scanned</p>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <p className="text-lg font-bold text-orange-600">{backfillResult.would_update}</p>
                      <p className="text-xs text-gray-500">{backfillResult.dry_run ? 'Would Update' : 'Updated'}</p>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <p className="text-lg font-bold text-gray-500">{backfillResult.skipped}</p>
                      <p className="text-xs text-gray-500">Skipped</p>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <p className="text-lg font-bold text-red-500">{backfillResult.errors}</p>
                      <p className="text-xs text-gray-500">Errors</p>
                    </div>
                  </div>
                  
                  {backfillResult.updates?.length > 0 && (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      <p className="text-xs font-semibold text-blue-700 mb-1">Notes to update:</p>
                      {backfillResult.updates.map((u, idx) => (
                        <div key={idx} className="bg-white rounded p-2 text-xs flex items-center justify-between">
                          <span>{u.client} - {u.provider} ({u.current_status})</span>
                          <span className="text-blue-600 font-medium">
                            {u.assigned_supervisor}
                            {u.assigned_supervisor_2 && <span className="text-purple-600 ml-1">+ {u.assigned_supervisor_2}</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {backfillResult.dry_run && backfillResult.would_update > 0 && (
                    <Button
                      size="sm"
                      className="mt-3 bg-orange-600 hover:bg-orange-700"
                      data-testid="apply-backfill-btn"
                      onClick={async () => {
                        setRunningBackfill(true);
                        try {
                          const res = await axios.post(`${API}/routing-rules/backfill-notes`, { dry_run: false });
                          setBackfillResult(res.data);
                          toast.success(`Updated ${res.data.would_update} notes`);
                          refreshNotes(); refreshClients(); refreshStats();
                        } catch (error) {
                          toast.error('Backfill failed');
                        } finally {
                          setRunningBackfill(false);
                        }
                      }}
                      disabled={runningBackfill}
                    >
                      {runningBackfill ? 'Applying...' : `Apply to ${backfillResult.would_update} Notes`}
                    </Button>
                  )}
                  
                  {!backfillResult.dry_run && (
                    <p className="text-sm text-green-700 mt-2 font-medium">
                      Done! {backfillResult.would_update} notes now have supervisor signature requirements.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Rules List - Full Detail */}
            {routingRules.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg mb-2">No routing rules configured yet</p>
                <p className="text-sm">Click "Add New Rule" to create your first rule</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">All Rules ({routingRules.length})</h3>
                  <p className="text-sm text-gray-600">Sorted by priority (highest first)</p>
                </div>
                
                {routingRules
                  .sort((a, b) => b.priority - a.priority)
                  .map((rule) => (
                  <div
                    key={rule.id}
                    className={`border rounded-lg p-4 ${rule.is_active ? 'bg-white' : 'bg-gray-50 opacity-70'}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="font-semibold text-lg">{rule.rule_name}</h3>
                          <Badge variant="outline" className="font-mono">Priority: {rule.priority}</Badge>
                          {rule.scope === 'provider_specific' && (
                            <Badge className="bg-purple-100 text-purple-700">Provider-Specific</Badge>
                          )}
                          {rule.scope === 'template_specific' && (
                            <Badge className="bg-blue-100 text-blue-700">Template-Specific</Badge>
                          )}
                          {!rule.is_active && (
                            <Badge variant="outline" className="bg-gray-200">Inactive</Badge>
                          )}
                        </div>
                        
                        {rule.description && (
                          <p className="text-sm text-gray-600 mb-3">{rule.description}</p>
                        )}
                        
                        {/* Conditions */}
                        <div className="bg-blue-50 rounded p-3 mb-2">
                          <span className="text-sm font-semibold text-blue-900">IF (Conditions):</span>
                          <div className="ml-4 mt-2 space-y-1">
                            {rule.conditions.length === 0 ? (
                              <p className="text-sm text-gray-500">No conditions (always applies)</p>
                            ) : (
                              rule.conditions.map((cond, idx) => (
                                <div key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                                  <span className="font-mono bg-white px-2 py-1 rounded text-xs">{cond.field}</span>
                                  <span className="font-semibold">{cond.operator}</span>
                                  <span className="font-mono bg-white px-2 py-1 rounded text-xs font-semibold">{cond.value}</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="bg-green-50 rounded p-3">
                          <span className="text-sm font-semibold text-green-900">THEN (Actions):</span>
                          <div className="ml-4 mt-2 text-sm text-gray-700">
                            <p>• Require Signature: <strong>{rule.require_signature ? 'Yes' : 'No'}</strong></p>
                            {rule.require_signature && (
                              <p>• Route To: <strong className="capitalize">{rule.route_to.replace(/_/g, ' ')}</strong></p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingRoutingRule(rule);
                            setRoutingRuleFormData(rule);
                            setShowRoutingRuleForm(true);
                            setShowRoutingRulesManager(false);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={async () => {
                            if (window.confirm(`Delete rule "${rule.rule_name}"?\n\nThis action cannot be undone.`)) {
                              try {
                                await axios.delete(`${API}/routing-rules/${rule.id}`);
                                toast.success('Rule deleted');
                                fetchRoutingRules();
                              } catch (error) {
                                toast.error('Failed to delete rule');
                              }
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Routing Rule Form Dialog */}
      <Dialog open={showRoutingRuleForm} onOpenChange={setShowRoutingRuleForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRoutingRule ? 'Edit Routing Rule' : 'Create Routing Rule'}</DialogTitle>
            <DialogDescription>
              Configure when notes require supervisor signature and how they should be routed
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Natural Language Input */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Label className="mb-2 block">✨ Quick Rule with AI (Optional)</Label>
              <p className="text-xs text-gray-600 mb-2">Describe your rule in plain English, and AI will help build it</p>
              <div className="flex gap-2">
                <DebouncedInput
                  placeholder="e.g., 'if provider unlicensed and insurance is medicare then route to first supervisor'"
                  value={naturalLanguageRule}
                  onChange={(e) => setNaturalLanguageRule(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={async () => {
                    if (!naturalLanguageRule.trim()) {
                      toast.error('Please enter a rule description');
                      return;
                    }
                    setParsingRule(true);
                    try {
                      const response = await axios.post(`${API}/routing-rules/parse-natural-language`, {
                        text: naturalLanguageRule
                      });
                      
                      // Populate ALL fields from the parsed response
                      setRoutingRuleFormData({
                        ...routingRuleFormData,
                        rule_name: response.data.rule_name || routingRuleFormData.rule_name,
                        description: response.data.description || routingRuleFormData.description,
                        scope: response.data.scope || routingRuleFormData.scope,
                        provider_id: response.data.provider_id || routingRuleFormData.provider_id,
                        template_id: response.data.template_id || routingRuleFormData.template_id,
                        conditions: response.data.conditions || routingRuleFormData.conditions,
                        require_signature: response.data.require_signature ?? routingRuleFormData.require_signature,
                        route_to: response.data.route_to || routingRuleFormData.route_to,
                        specific_supervisor_id: response.data.specific_supervisor_id || routingRuleFormData.specific_supervisor_id,
                        priority: response.data.priority || routingRuleFormData.priority
                      });
                      
                      toast.success('Rule parsed! Review and adjust below');
                    } catch (error) {
                      toast.error('Failed to parse rule');
                    } finally {
                      setParsingRule(false);
                    }
                  }}
                  disabled={parsingRule}
                >
                  {parsingRule ? 'Parsing...' : '✨ Parse'}
                </Button>
              </div>
            </div>

            {/* Manual Form */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rule Name *</Label>
                <DebouncedInput
                  value={routingRuleFormData.rule_name}
                  onChange={(e) => setRoutingRuleFormData({...routingRuleFormData, rule_name: e.target.value})}
                  placeholder="Unlicensed Provider Rule"
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Input
                  type="number"
                  value={routingRuleFormData.priority}
                  onChange={(e) => setRoutingRuleFormData({...routingRuleFormData, priority: parseInt(e.target.value)})}
                />
                <p className="text-xs text-gray-500 mt-1">Higher numbers = higher priority</p>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <DebouncedTextarea
                value={routingRuleFormData.description}
                onChange={(e) => setRoutingRuleFormData({...routingRuleFormData, description: e.target.value})}
                placeholder="Explain when this rule applies..."
                rows={2}
              />
            </div>

            {/* Scope Selection */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Scope</Label>
                <Select
                  value={routingRuleFormData.scope}
                  onValueChange={(value) => setRoutingRuleFormData({
                    ...routingRuleFormData,
                    scope: value,
                    provider_id: value === 'provider_specific' ? routingRuleFormData.provider_id : null,
                    template_id: value === 'template_specific' ? routingRuleFormData.template_id : null
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global</SelectItem>
                    <SelectItem value="provider_specific">Provider-Specific</SelectItem>
                    <SelectItem value="template_specific">Template-Specific</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {routingRuleFormData.scope === 'provider_specific' && (
                <div>
                  <Label>Provider</Label>
                  <Select
                    value={routingRuleFormData.provider_id || ''}
                    onValueChange={(value) => setRoutingRuleFormData({...routingRuleFormData, provider_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...users].filter(u => u.role === 'provider' || u.is_provider === true).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')).map(provider => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {formatProviderName(provider)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {routingRuleFormData.scope === 'template_specific' && (
                <div>
                  <Label>Template</Label>
                  <Select
                    value={routingRuleFormData.template_id || ''}
                    onValueChange={(value) => setRoutingRuleFormData({...routingRuleFormData, template_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      {formTemplates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Conditions Builder */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">IF (Conditions)</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setRoutingRuleFormData({
                      ...routingRuleFormData,
                      conditions: [...routingRuleFormData.conditions, {field: 'license_status', operator: 'equals', value: ''}]
                    });
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Condition
                </Button>
              </div>

              {routingRuleFormData.conditions.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No conditions yet. Click "Add Condition" to start.</p>
              ) : (
                <div className="space-y-3">
                  {routingRuleFormData.conditions.map((condition, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-4">
                        <Select
                          value={condition.field}
                          onValueChange={(value) => {
                            const newConditions = [...routingRuleFormData.conditions];
                            newConditions[idx].field = value;
                            setRoutingRuleFormData({...routingRuleFormData, conditions: newConditions});
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="license_status">Provider License Status</SelectItem>
                            <SelectItem value="medicare_approved">Provider Medicare Approved</SelectItem>
                            <SelectItem value="provider_id">Specific Provider</SelectItem>
                            <SelectItem value="provider_role">Provider Role</SelectItem>
                            <SelectItem value="billing_type">Insurance Billing Type</SelectItem>
                            <SelectItem value="insurance_company">Insurance Company</SelectItem>
                            <SelectItem value="template_requires_signature">Template Requires Signature</SelectItem>
                            <SelectItem value="template_billable">Template is Billable</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="col-span-3">
                        <Select
                          value={condition.operator}
                          onValueChange={(value) => {
                            const newConditions = [...routingRuleFormData.conditions];
                            newConditions[idx].operator = value;
                            setRoutingRuleFormData({...routingRuleFormData, conditions: newConditions});
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="equals">equals</SelectItem>
                            <SelectItem value="not_equals">not equals</SelectItem>
                            <SelectItem value="contains">contains</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="col-span-4">
                        {condition.field === 'license_status' ? (
                          <Select
                            value={condition.value}
                            onValueChange={(value) => {
                              const newConditions = [...routingRuleFormData.conditions];
                              newConditions[idx].value = value;
                              setRoutingRuleFormData({...routingRuleFormData, conditions: newConditions});
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Licensed">Licensed</SelectItem>
                              <SelectItem value="Unlicensed">Unlicensed</SelectItem>
                              <SelectItem value="Intern">Intern</SelectItem>
                              <SelectItem value="Provisionally Licensed">Provisionally Licensed</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : condition.field === 'medicare_approved' ? (
                          <Select
                            value={condition.value}
                            onValueChange={(value) => {
                              const newConditions = [...routingRuleFormData.conditions];
                              newConditions[idx].value = value;
                              setRoutingRuleFormData({...routingRuleFormData, conditions: newConditions});
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="true">Yes</SelectItem>
                              <SelectItem value="false">No</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (condition.field === 'template_billable' || condition.field === 'template_requires_signature') ? (
                          <Select
                            value={condition.value}
                            onValueChange={(value) => {
                              const newConditions = [...routingRuleFormData.conditions];
                              newConditions[idx].value = value;
                              setRoutingRuleFormData({...routingRuleFormData, conditions: newConditions});
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="true">Yes (billable / requires signature)</SelectItem>
                              <SelectItem value="false">No (non-billable / no signature)</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : condition.field === 'provider_role' ? (
                          <Select
                            value={condition.value}
                            onValueChange={(value) => {
                              const newConditions = [...routingRuleFormData.conditions];
                              newConditions[idx].value = value;
                              setRoutingRuleFormData({...routingRuleFormData, conditions: newConditions});
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="provider">Provider</SelectItem>
                              <SelectItem value="supervisor">Supervisor</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="biller">Biller</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : condition.field === 'provider_id' ? (
                          <Select
                            value={condition.value}
                            onValueChange={(value) => {
                              const newConditions = [...routingRuleFormData.conditions];
                              newConditions[idx].value = value;
                              setRoutingRuleFormData({...routingRuleFormData, conditions: newConditions});
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select provider" />
                            </SelectTrigger>
                            <SelectContent>
                              {/* Use existing `users` list already loaded
                                  for the rest of the admin page — filtered
                                  to only clinical staff. */}
                              {users
                                .filter(u => ['provider', 'supervisor', 'admin'].includes(u.role))
                                .map(u => (
                                  <SelectItem key={u.id} value={u.id}>
                                    {u.full_name || u.email} {u.role !== 'provider' ? `(${formatRoleName(u.role)})` : ''}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <DebouncedInput
                            value={condition.value}
                            onChange={(e) => {
                              const newConditions = [...routingRuleFormData.conditions];
                              newConditions[idx].value = e.target.value;
                              setRoutingRuleFormData({...routingRuleFormData, conditions: newConditions});
                            }}
                            placeholder="Enter value..."
                          />
                        )}
                      </div>

                      <div className="col-span-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => {
                            const newConditions = routingRuleFormData.conditions.filter((_, i) => i !== idx);
                            setRoutingRuleFormData({...routingRuleFormData, conditions: newConditions});
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="border rounded-lg p-4">
              <Label className="text-base font-semibold mb-3 block">THEN (Actions)</Label>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={routingRuleFormData.require_signature}
                    onCheckedChange={(checked) => setRoutingRuleFormData({...routingRuleFormData, require_signature: checked})}
                  />
                  <Label>Require Supervisor Signature</Label>
                </div>

                {routingRuleFormData.require_signature && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Route To</Label>
                      <Select
                        value={routingRuleFormData.route_to}
                        onValueChange={(value) => setRoutingRuleFormData({...routingRuleFormData, route_to: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="first_supervisor">First Available Supervisor</SelectItem>
                          <SelectItem value="all_supervisors">All Supervisors</SelectItem>
                          <SelectItem value="specific_supervisor">Specific Supervisor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {routingRuleFormData.route_to === 'specific_supervisor' && (
                      <div>
                        <Label>Supervisor</Label>
                        <Select
                          value={routingRuleFormData.specific_supervisor_id || ''}
                          onValueChange={(value) => setRoutingRuleFormData({...routingRuleFormData, specific_supervisor_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select supervisor" />
                          </SelectTrigger>
                          <SelectContent>
                            {[...users].filter(u => u.role === 'provider' || u.is_provider === true || u.is_supervisor === true).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')).map(supervisor => (
                              <SelectItem key={supervisor.id} value={supervisor.id}>
                                {supervisor.full_name || `${supervisor.first_name} ${supervisor.last_name}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="mt-3 flex items-start gap-2">
                          <input
                            type="checkbox"
                            id="require-primary-sup-too"
                            className="mt-1"
                            checked={!!routingRuleFormData.require_primary_supervisor_too}
                            onChange={(e) => setRoutingRuleFormData({
                              ...routingRuleFormData,
                              require_primary_supervisor_too: e.target.checked,
                            })}
                          />
                          <label htmlFor="require-primary-sup-too" className="text-xs text-gray-600 leading-snug">
                            Also require the provider's primary supervisor to sign.
                            <div className="text-gray-400">
                              Off by default — the named supervisor above is the only required signer.
                              Turn on if you need dual sign-off (specific + primary).
                            </div>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Switch
                    checked={routingRuleFormData.is_active}
                    onCheckedChange={(checked) => setRoutingRuleFormData({...routingRuleFormData, is_active: checked})}
                  />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => {
              setShowRoutingRuleForm(false);
              // Reopen manager if user wants to go back
              if (window.confirm('Go back to Rules Manager?')) {
                setShowRoutingRulesManager(true);
              }
            }}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!routingRuleFormData.rule_name) {
                  toast.error('Please provide a rule name');
                  return;
                }
                // Client-side guardrails — match the new backend
                // validation so the admin gets an immediate error
                // instead of a backend 400. This is what caused the
                // "Bernice Rule" to save with provider_id=null and
                // silently match no one in production.
                if (routingRuleFormData.scope === 'provider_specific' && !routingRuleFormData.provider_id) {
                  toast.error('Pick a provider from the dropdown — rule scope is "provider_specific"');
                  return;
                }
                if (routingRuleFormData.scope === 'template_specific' && !routingRuleFormData.template_id) {
                  toast.error('Pick a template from the dropdown — rule scope is "template_specific"');
                  return;
                }
                if (routingRuleFormData.route_to === 'specific_supervisor' && !routingRuleFormData.specific_supervisor_id) {
                  toast.error('Pick a supervisor — routing is set to "specific supervisor"');
                  return;
                }

                try {
                  if (editingRoutingRule) {
                    await axios.put(`${API}/routing-rules/${editingRoutingRule.id}`, routingRuleFormData);
                    toast.success('Rule updated');
                  } else {
                    await axios.post(`${API}/routing-rules`, routingRuleFormData);
                    toast.success('Rule created');
                  }
                  setShowRoutingRuleForm(false);
                  fetchRoutingRules();
                  setNaturalLanguageRule('');
                  // Reopen manager to see changes
                  setShowRoutingRulesManager(true);
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to save rule');
                }
              }}
            >
              {editingRoutingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Preview Dialog */}
      <Dialog open={showClientPreview} onOpenChange={handleCloseWaitlistPreview}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Client Information Preview</DialogTitle>
          </DialogHeader>
          {/* Loading State */}
          {previewClientLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600 mb-3"></div>
              <p className="text-gray-600">Loading Client...</p>
            </div>
          )}
          {!previewClientLoading && previewClient && (
            <div className="space-y-4">
              {/* Urgent Banner - Show if client is marked urgent */}
              {previewClient.waitlist_urgent && (
                <div className="p-4 bg-red-100 rounded-lg border-2 border-red-500">
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">🚨</span>
                    <div className="flex-1">
                      <p className="font-bold text-red-800 text-lg">URGENT WAITLIST CLIENT</p>
                      {previewClient.waitlist_urgent_reason && (
                        <p className="text-sm text-red-700 mt-1">
                          <span className="font-semibold">Reason:</span> {previewClient.waitlist_urgent_reason}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-red-600">
                        {previewClient.waitlist_urgent_marked_by_name && (
                          <span>
                            <span className="font-semibold">Marked by:</span> {previewClient.waitlist_urgent_marked_by_name}
                          </span>
                        )}
                        {previewClient.waitlist_urgent_marked_at && (
                          <span>
                            <span className="font-semibold">When:</span> {new Date(previewClient.waitlist_urgent_marked_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Key Info Summary - Name, Age, Gender, Service, Location, Language */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500 text-xs uppercase">Name</span>
                    <p className="font-semibold text-gray-900">{previewClient.full_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs uppercase">Age</span>
                    <p className="font-semibold text-gray-900">
                      {previewClient.date_of_birth ? (
                        (() => {
                          const dob = new Date(previewClient.date_of_birth);
                          const today = new Date();
                          let age = today.getFullYear() - dob.getFullYear();
                          const m = today.getMonth() - dob.getMonth();
                          if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
                          return `${age} years`;
                        })()
                      ) : 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs uppercase">Gender</span>
                    <p className="font-semibold text-gray-900">{previewClient.gender || 'Not provided'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs uppercase">Requested Service(s)</span>
                    <p className="font-semibold text-gray-900">
                      {previewClient.service_type_ids?.length > 0 
                        ? previewClient.service_type_ids.map(id => 
                            serviceTypes.find(st => st.id === id)?.name || id
                          ).join(', ')
                        : previewClient.service_requested || previewClient.service_type || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs uppercase">Preferred Location</span>
                    <p className="font-semibold text-gray-900">
                      {previewClient.preferred_location 
                        ? (locations.find(l => l.id === previewClient.preferred_location)?.name
                          || (/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(previewClient.preferred_location)
                            ? 'Unknown location (removed)'
                            : previewClient.preferred_location))
                        : 'No preference'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs uppercase">Language</span>
                    <p className="font-semibold text-gray-900">{previewClient.language || 'English'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs uppercase">Preferred Mode</span>
                    <p className="font-semibold text-gray-900">
                      {(() => {
                        const m = previewClient.preferred_mode || previewClient.preferred_modality || previewClient.modality_preference;
                        return m
                          ? String(m).replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                          : 'No preference';
                      })()}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Waitlist Status Info */}
              {(previewClient.status === 'waitlist' || previewClient.status === 'pending_intake') && (
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <h3 className="font-semibold text-sm text-orange-800 mb-2">Waitlist Status</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Status:</span>{' '}
                      <span className="font-medium">{previewClient.status === 'waitlist' ? 'On Waitlist' : 'Pending Approval'}</span>
                    </div>
                    {previewClient.waitlist_added_at && (
                      <div>
                        <span className="text-gray-600">Added:</span>{' '}
                        <span className="font-medium">{new Date(previewClient.waitlist_added_at).toLocaleDateString()}</span>
                      </div>
                    )}
                    {previewClient.created_at && !previewClient.waitlist_added_at && (
                      <div>
                        <span className="text-gray-600">Submitted:</span>{' '}
                        <span className="font-medium">{new Date(previewClient.created_at).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600">Contact Count:</span>{' '}
                      <span className="font-medium">{previewClient.waitlist_contact_count || 0}</span>
                    </div>
                    {previewClient.last_waitlist_contact_at && (
                      <div>
                        <span className="text-gray-600">Last Contact:</span>{' '}
                        <span className="font-medium">{new Date(previewClient.last_waitlist_contact_at).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Corrections Requested Info - Show when corrections were requested */}
              {(previewClient.status === 'corrections_requested' || previewClient.last_correction_notes) && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <h3 className="font-semibold text-sm text-amber-800 mb-2">Additional Info Requested</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600 font-medium">What was requested:</span>
                      <p className="text-gray-900 whitespace-pre-wrap mt-1 bg-white p-2 rounded border border-amber-100">
                        {previewClient.last_correction_notes || previewClient.corrections_notes || previewClient.correction_reason || 'Additional information or corrections were requested from the client.'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-amber-700">
                      {previewClient.last_correction_requested_by_name && (
                        <span>
                          <span className="font-semibold">Requested by:</span> {previewClient.last_correction_requested_by_name}
                        </span>
                      )}
                      {previewClient.last_correction_requested_at && (
                        <span>
                          <span className="font-semibold">When:</span> {new Date(previewClient.last_correction_requested_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Referral Source */}
              {previewClient.referral_source && (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-sm text-gray-700 mb-1">Referral Source</h3>
                  <p className="text-gray-900 text-sm">{previewClient.referral_source}</p>
                </div>
              )}

              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Reason for Referral</h3>
                <p className="text-gray-900 whitespace-pre-wrap">
                  {previewClient.reason_for_referral || 'Not provided'}
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Symptoms</h3>
                {previewClient.symptoms_checklist && previewClient.symptoms_checklist.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {previewClient.symptoms_checklist.map((symptom, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {symptom}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No symptoms specified</p>
                )}
              </div>
              
              {previewClient.client_preferences && (
                <div>
                  <h3 className="font-semibold text-sm text-gray-700 mb-2">Client Preferences</h3>
                  <p className="text-gray-900 whitespace-pre-wrap">{previewClient.client_preferences}</p>
                </div>
              )}
              
              {/* Contact History */}
              {previewClient.waitlist_notes && (
                <div>
                  <h3 className="font-semibold text-sm text-gray-700 mb-2">Contact History</h3>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">
                      {previewClient.waitlist_notes}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between mt-4 pt-4 border-t">
            <Button 
              variant="outline"
              onClick={async () => {
                try {
                  // For waitlist/pending clients, open the intake wizard (edit dialog)
                  // For active clients, open the full client chart
                  if (['pending_intake', 'corrections_requested', 'waitlist'].includes(previewClient.status)) {
                    // Use URL-aware handler for waitlist edit
                    handleWaitlistEdit(previewClient.id);
                  } else {
                    const response = await axios.get(`${API}/clients/${previewClient.id}`);
                    setSelectedClient(response.data);
                    setShowClientPreview(false);
                    setShowClientDetail(true);
                  }
                } catch (error) {
                  toast.error("Failed to open client profile");
                }
              }}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {['pending_intake', 'corrections_requested', 'waitlist'].includes(previewClient?.status) ? 'Edit Intake' : 'Open Full Profile'}
            </Button>
            <Button onClick={() => handleCloseWaitlistPreview(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request More Info Dialog */}
      <Dialog open={showRequestInfoDialog} onOpenChange={setShowRequestInfoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Additional Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="info-request-notes">What additional information do you need from the client?</Label>
              <DebouncedTextarea
                id="info-request-notes"
                className="mt-2 min-h-[120px]"
                placeholder="Please provide details about what information or corrections are needed..."
                value={infoRequestNotes}
                onChange={(e) => setInfoRequestNotes(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRequestInfoDialog(false);
                setInfoRequestNotes('');
                setSelectedClientForAction(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={async () => {
                if (infoRequestNotes.trim() && selectedClientForAction) {
                  try {
                    await axios.post(`${API}/clients/${selectedClientForAction.id}/request-corrections`, {
                      correction_notes: infoRequestNotes
                    });
                    toast.success("Information request sent to client");
                    setShowRequestInfoDialog(false);
                    setInfoRequestNotes('');
                    setSelectedClientForAction(null);
                    refreshNotes(); refreshTodos();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Failed to send request");
                  }
                }
              }}
              disabled={!infoRequestNotes.trim()}
            >
              Send Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deny Request Dialog */}
      <Dialog open={showDenyDialog} onOpenChange={setShowDenyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deny Service Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Please provide a reason for denying this request. The client will be notified via email.
            </p>
            <div>
              <Label htmlFor="deny-reason">Reason for Denial</Label>
              <DebouncedTextarea
                id="deny-reason"
                className="mt-2 min-h-[120px]"
                placeholder="Explain why this service request is being denied..."
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDenyDialog(false);
                setDenyReason('');
                setSelectedClientForAction(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                if (denyReason.trim() && selectedClientForAction) {
                  try {
                    // Move to inactive status (with deny reason stored in notes)
                    await axios.patch(`${API}/clients/${selectedClientForAction.id}/status`, {
                      status: 'inactive'
                    });
                    
                    // Update client with inactive reason (denial)
                    await axios.put(`${API}/clients/${selectedClientForAction.id}`, {
                      ...selectedClientForAction,
                      inactive_reason: denyReason,
                      inactive_at: new Date().toISOString()
                    });
                    
                    // Send email notification
                    if (selectedClientForAction.email) {
                      await axios.post(`${API}/send-email`, {
                        to_email: selectedClientForAction.email,
                        subject: "Service Request Update",
                        body_text: `Hello ${selectedClientForAction.full_name},\n\nWe have reviewed your service request and unfortunately we are unable to provide services at this time.\n\nReason: ${denyReason}\n\nIf you have questions, please contact us directly.\n\nBest regards,\nCareVaultPro Team`
                      });
                    }
                    toast.success("Request denied - client marked as inactive");
                    setShowDenyDialog(false);
                    setDenyReason('');
                    setSelectedClientForAction(null);
                    refreshNotes(); refreshClients(); refreshStats();
                  } catch (error) {
                    toast.error("Failed to deny request");
                  }
                }
              }}
              disabled={!denyReason.trim()}
            >
              Deny Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Urgent Escalation Dialog - Select Supervisors to Notify */}
      <Dialog open={showUrgentEscalationDialog} onOpenChange={setShowUrgentEscalationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">🚨 Mark Client as Urgent</DialogTitle>
          </DialogHeader>
          {urgentEscalationClient && (
            <div className="space-y-4">
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="font-semibold text-red-900">{urgentEscalationClient.full_name}</p>
                {urgentEscalationClient.symptoms_checklist?.some(s => urgentSymptoms.includes(s)) && (
                  <p className="text-sm text-red-700 mt-1">
                    ⚠️ Concerning symptoms reported: {urgentEscalationClient.symptoms_checklist.filter(s => urgentSymptoms.includes(s)).join(', ')}
                  </p>
                )}
              </div>
              
              <div>
                <Label className="font-medium">Reason for Urgent Flag</Label>
                <DebouncedTextarea
                  value={urgentReason}
                  onChange={(e) => setUrgentReason(e.target.value)}
                  placeholder="Describe why this client needs urgent attention..."
                  rows={2}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="font-medium mb-2 block">Notify Supervisors/Admins</Label>
                <p className="text-xs text-gray-500 mb-2">Select who should be notified about this urgent case:</p>
                <div className="max-h-40 overflow-y-auto space-y-2 border rounded p-2">
                  {supervisorsList.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No supervisors found</p>
                  ) : (
                    supervisorsList.map(supervisor => (
                      <label key={supervisor.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedSupervisorsForUrgent.includes(supervisor.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSupervisorsForUrgent(prev => [...prev, supervisor.id]);
                            } else {
                              setSelectedSupervisorsForUrgent(prev => prev.filter(id => id !== supervisor.id));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{supervisor.full_name || supervisor.email}</span>
                        <span className="text-xs text-gray-500">({supervisor.role})</span>
                      </label>
                    ))
                  )}
                </div>
                {supervisorsList.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 text-xs"
                    onClick={() => setSelectedSupervisorsForUrgent(supervisorsList.map(u => u.id))}
                  >
                    Select All
                  </Button>
                )}
              </div>
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowUrgentEscalationDialog(false)} className="flex-1">
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={selectedSupervisorsForUrgent.length === 0}
              onClick={async () => {
                try {
                  // Mark client as urgent with full tracking info
                  await axios.patch(`${API}/clients/${urgentEscalationClient.id}`, {
                    waitlist_urgent: true,
                    waitlist_urgent_reason: urgentReason || 'Marked urgent by staff',
                    waitlist_urgent_marked_at: new Date().toISOString(),
                    waitlist_urgent_marked_by: user?.id,
                    waitlist_urgent_marked_by_name: user?.full_name || user?.email
                  });
                  
                  // Notify selected supervisors
                  for (const supervisorId of selectedSupervisorsForUrgent) {
                    await axios.post(`${API}/notifications/create`, {
                      title: "🚨 Urgent Waitlist Client",
                      message: `${urgentEscalationClient.full_name} has been marked URGENT. Reason: ${urgentReason || 'Requires immediate review'}`,
                      type: "urgent_waitlist",
                      user_id: supervisorId,
                      client_id: urgentEscalationClient.id,
                      link: `/waitlist?highlight=${urgentEscalationClient.id}`
                    });
                  }
                  
                  toast.success(`Marked urgent - ${selectedSupervisorsForUrgent.length} supervisor(s) notified`);
                  setShowUrgentEscalationDialog(false);
                  setUrgentEscalationClient(null);
                  setSelectedSupervisorsForUrgent([]);
                  setUrgentReason('');
                  refreshClients(); refreshStats();
                } catch (error) {
                  console.error("Failed to mark as urgent:", error.response?.data || error.message);
                  toast.error(`Failed to mark as urgent: ${error.response?.data?.detail || error.message}`);
                }
              }}
            >
              🚨 Mark Urgent & Notify ({selectedSupervisorsForUrgent.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activate with Provider Assignment Dialog */}
      <Dialog open={showActivateWithProviderDialog} onOpenChange={setShowActivateWithProviderDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign Provider & Activate Client</DialogTitle>
            <DialogDescription>
              Assign a provider and optional service enrollment to <span className="font-semibold">{clientToActivate?.full_name}</span> before activating their account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="provider-select">Assign Provider *</Label>
              <Select 
                value={selectedProviderForActivation} 
                onValueChange={(value) => {
                  setSelectedProviderForActivation(value);
                  // Reset service selection when provider changes
                  setSelectedServiceForActivation('');
                }}
              >
                <SelectTrigger id="provider-select" className="mt-2">
                  <SelectValue placeholder="Select a provider..." />
                </SelectTrigger>
                <SelectContent>
                  {providers && providers.length > 0 ? (
                    providers.map(provider => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {formatProviderName(provider)}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>No providers available</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {/* Service Enrollment (Optional) */}
            {selectedProviderForActivation && (
              <div>
                <Label htmlFor="service-select">Service Enrollment (Optional)</Label>
                <p className="text-xs text-gray-500 mb-2">Assign a service type to create an enrollment for billing</p>
                <Select 
                  value={selectedServiceForActivation || ''} 
                  onValueChange={setSelectedServiceForActivation}
                >
                  <SelectTrigger id="service-select" className="mt-1">
                    <SelectValue placeholder="Select a service type (optional)..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No service enrollment</SelectItem>
                    {serviceTypes && serviceTypes.map(service => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowActivateWithProviderDialog(false);
                setClientToActivate(null);
                setSelectedProviderForActivation('');
                setSelectedServiceForActivation('');
              }}
            >
              Cancel
            </Button>
            <Button 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={async () => {
                if (selectedProviderForActivation && clientToActivate) {
                  try {
                    // Update status to active
                    await axios.patch(`${API}/clients/${clientToActivate.id}/status`, {
                      status: 'active'
                    });
                    
                    // Assign provider
                    await axios.patch(`${API}/clients/${clientToActivate.id}`, {
                      assigned_providers: [selectedProviderForActivation]
                    });
                    
                    // Create service enrollment if selected
                    if (selectedServiceForActivation && selectedServiceForActivation !== 'none') {
                      try {
                        await axios.post(`${API}/clients/${clientToActivate.id}/service-enrollments`, {
                          provider_id: selectedProviderForActivation,
                          service_type: selectedServiceForActivation,
                          status: 'active'
                        });
                      } catch (enrollError) {
                        console.warn('Failed to create service enrollment:', enrollError);
                        // Don't fail the whole operation for enrollment errors
                      }
                    }
                    
                    toast.success(`${clientToActivate.full_name} activated and assigned to provider`);
                    
                    // Optimistic update: Remove client from waitlist immediately
                    const activatedClientId = clientToActivate.id;
                    setClients(prevClients => 
                      prevClients.map(c => 
                        c.id === activatedClientId 
                          ? { ...c, status: 'active', assigned_providers: [selectedProviderForActivation] }
                          : c
                      )
                    );
                    
                    setShowActivateWithProviderDialog(false);
                    setClientToActivate(null);
                    setSelectedProviderForActivation('');
                    setSelectedServiceForActivation('');
                    
                    // Background refresh to sync with server
                    refreshClients(); refreshStats();
                  } catch (error) {
                    toast.error("Failed to activate client");
                  }
                }
              }}
              disabled={!selectedProviderForActivation}
            >
              Assign & Activate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Portal Link Generated Modal */}
      <Dialog open={showPortalLinkModal} onOpenChange={setShowPortalLinkModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Client Portal Link Generated</DialogTitle>
            <DialogDescription>
              Share this secure link with {generatedPortalLink?.client_name}
            </DialogDescription>
          </DialogHeader>
          
          {generatedPortalLink && (
            <div className="space-y-4">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-teal-900">Client Information</Label>
                    <p className="text-sm text-teal-800 mt-1">{generatedPortalLink.client_name}</p>
                    <p className="text-xs text-teal-600">{generatedPortalLink.client_email}</p>
                  </div>
                  <Badge className="bg-teal-600 text-white">
                    Expires in {generatedPortalLink.expires_in_hours}hrs
                  </Badge>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2">Portal Link</Label>
                <div className="flex items-center space-x-2 mt-1">
                  <Input 
                    value={generatedPortalLink.magic_link}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedPortalLink.magic_link);
                      toast.success('Link copied to clipboard!');
                    }}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Expires: {new Date(generatedPortalLink.expires_at).toLocaleString()}
                </p>
              </div>
              
              <Alert className="bg-blue-50 border-blue-200">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-sm text-blue-800">
                  <strong>Email Integration Coming Soon:</strong> Currently, you can copy and share this link manually. Email delivery will be available in a future update.
                </AlertDescription>
              </Alert>
              
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    window.open(generatedPortalLink.magic_link, '_blank');
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Preview Portal
                </Button>
                <Button onClick={() => setShowPortalLinkModal(false)}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Staff to Thread Dialog */}
      <Dialog open={showForwardDialog} onOpenChange={setShowForwardDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Staff to Thread</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Add a staff member to this conversation with <span className="font-semibold">{(threadToForward || selectedStaffThread)?.client_name}</span>. They will be able to see all messages and reply.
            </p>
            
            {/* Warning about visibility */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                <strong>Note:</strong> The added staff member will see the entire conversation history.
              </p>
            </div>
            
            {/* Show current participants */}
            {(() => {
              const thread = threadToForward || selectedStaffThread;
              const sharedWith = thread?.shared_with || [];
              if (sharedWith.length === 0) return null;
              
              const sharedUsers = sharedWith
                .map(id => users.find(u => u.id === id))
                .filter(Boolean)
                .map(u => u.full_name || `${u.first_name} ${u.last_name}`.trim());
              
              if (sharedUsers.length === 0) return null;
              
              return (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Currently on this thread:</p>
                  <p className="text-sm font-medium">{sharedUsers.join(', ')}</p>
                </div>
              );
            })()}
            
            <div>
              <Label htmlFor="forwardTo">Add staff member: *</Label>
              <Select value={forwardToUserId} onValueChange={setForwardToUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter(u => {
                      // Exclude current user
                      if (u.id === user?.id) return false;
                      // Exclude clients
                      if (u.role === 'client') return false;
                      // Exclude users already on the thread
                      const thread = threadToForward || selectedStaffThread;
                      const sharedWith = thread?.shared_with || [];
                      if (sharedWith.includes(u.id)) return false;
                      // Exclude the original recipient
                      if (u.id === thread?.recipient_id) return false;
                      return true;
                    })
                    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
                    .map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name} ({formatRoleName(u.role)})
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="forwardNote">Internal note for staff (not visible to client)</Label>
              <DebouncedTextarea
                id="forwardNote"
                value={forwardNote}
                onChange={(e) => setForwardNote(e.target.value)}
                className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 mt-2"
                placeholder="E.g., 'Can you help with this billing question?' or 'FYI - related to your patient'"
                rows="3"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForwardDialog(false);
                  setForwardToUserId('');
                  setForwardNote('');
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={forwardStaffMessage} 
                disabled={forwardingMessage || !forwardToUserId}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {forwardingMessage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Users className="h-4 w-4 mr-2" />
                    Add to Thread
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Staff New Message Dialog */}
      <Dialog open={showStaffNewMessageDialog} onOpenChange={(open) => {
        setShowStaffNewMessageDialog(open);
        if (!open) {
          setMessageRecipientType('client');
          setClientSearchQuery('');
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Message</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={async (e) => {
            e.preventDefault();
            
            // Handle Staff Announcement
            if (messageRecipientType === 'all_staff') {
              if (!isSuperAdmin(user)) {
                toast.error('Only administrators can send staff announcements');
                return;
              }
              if (!staffNewMessageSubject.trim() || !staffNewMessageText.trim()) {
                toast.error('Please fill in subject and message');
                return;
              }
              
              setSendingStaffNewMessage(true);
              try {
                const params = new URLSearchParams({
                  subject: staffNewMessageSubject,
                  message: staffNewMessageText
                });
                
                const response = await axios.post(`${API}/staff/announcements?${params.toString()}`);
                toast.success(response.data.message || 'Announcement sent to all staff');
                setShowStaffNewMessageDialog(false);
                setStaffNewMessageSubject('');
                setStaffNewMessageText('');
                setMessageRecipientType('client');
                fetchStaffAnnouncements();
              } catch (error) {
                console.error('Error sending announcement:', error);
                toast.error('Failed to send announcement');
              } finally {
                setSendingStaffNewMessage(false);
              }
              return;
            }
            
            // Check if broadcast (All Clients)
            if (staffNewMessageClientId === 'ALL_CLIENTS') {
              if (!is_admin_user || !(isSuperAdmin(user))) {
                toast.error('Only administrators can send broadcast messages');
                return;
              }
              setShowBroadcastConfirmation(true);
              return;
            }
            
            // Validate based on recipient type
            if (messageRecipientType === 'client') {
              if (!staffNewMessageClientId || !staffNewMessageSubject.trim() || !staffNewMessageText.trim()) {
                toast.error('Please fill in all required fields');
                return;
              }
            } else {
              // Staff to staff
              if (!staffToStaffRecipientId || !staffToStaffSubject.trim() || !staffToStaffMessage.trim()) {
                toast.error('Please fill in all required fields');
                return;
              }
            }
            
            setSendingStaffNewMessage(true);
            try {
              if (messageRecipientType === 'client') {
                const params = new URLSearchParams({
                  client_id: staffNewMessageClientId,
                  subject: staffNewMessageSubject,
                  message: staffNewMessageText,
                  topic: staffNewMessageTopic
                });
                
                await axios.post(`${API}/staff/messages/new?${params.toString()}`);
                toast.success('Message sent to client');
              } else {
                const params = new URLSearchParams({
                  recipient_id: staffToStaffRecipientId,
                  subject: staffToStaffSubject,
                  message: staffToStaffMessage
                });
                
                await axios.post(`${API}/staff/messages/to-staff?${params.toString()}`);
                toast.success('Message sent successfully');
              }
              
              setShowStaffNewMessageDialog(false);
              setStaffNewMessageClientId('');
              setStaffNewMessageSubject('');
              setStaffNewMessageText('');
              setStaffNewMessageTopic('general');
              setStaffToStaffRecipientId('');
              setStaffToStaffSubject('');
              setStaffToStaffMessage('');
              setClientSearchQuery('');
              fetchStaffMessageThreads(false);
            } catch (error) {
              console.error('Error sending message:', error);
              const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
              toast.error(`Failed to send message: ${detail}`);
            } finally {
              setSendingStaffNewMessage(false);
            }
          }} className="space-y-4">
            
            {/* Client/Staff Toggle */}
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <Label>Send to:</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={messageRecipientType === 'client' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMessageRecipientType('client')}
                  className={messageRecipientType === 'client' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                >
                  Client
                </Button>
                <Button
                  type="button"
                  variant={messageRecipientType === 'staff' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMessageRecipientType('staff')}
                  className={messageRecipientType === 'staff' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                >
                  Staff Member
                </Button>
                {isSuperAdmin(user) && (
                  <Button
                    type="button"
                    variant={messageRecipientType === 'all_staff' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMessageRecipientType('all_staff')}
                    className={messageRecipientType === 'all_staff' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                  >
                    All Staff (Announcement)
                  </Button>
                )}
              </div>
            </div>

            {messageRecipientType === 'all_staff' ? (
              /* Staff Announcement Form */
              <>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <AlertCircle className="h-4 w-4 inline mr-1" />
                    This announcement will be sent to all staff members. It's read-only - staff cannot reply.
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="announcementSubject">Subject: *</Label>
                  <DebouncedInput
                    id="announcementSubject"
                    value={staffNewMessageSubject}
                    onChange={(e) => setStaffNewMessageSubject(e.target.value)}
                    placeholder="e.g., Office Closure Notice, New Policy Update"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="announcementMessage">Message: *</Label>
                  <DebouncedTextarea
                    id="announcementMessage"
                    value={staffNewMessageText}
                    onChange={(e) => setStaffNewMessageText(e.target.value)}
                    className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 mt-1"
                    placeholder="Type your announcement..."
                    rows="5"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowStaffNewMessageDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={sendingStaffNewMessage || !staffNewMessageSubject.trim() || !staffNewMessageText.trim()}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {sendingStaffNewMessage ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Announcement
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : messageRecipientType === 'client' ? (
              <>
                {/* Searchable Client Selector */}
                <div>
                  <Label htmlFor="staffMsgClient">To (Client): *</Label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by name or MRN..."
                      value={clientSearchQuery}
                      onChange={(e) => {
                        setClientSearchQuery(e.target.value);
                        // Clear selection when user starts typing again
                        if (staffNewMessageClientId) {
                          setStaffNewMessageClientId('');
                        }
                      }}
                      onFocus={() => fetchAccessibleClients()}
                      className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    {clientSearchQuery.length >= 2 && !staffNewMessageClientId && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {/* Broadcast option for admins */}
                        {(isSuperAdmin(user)) && (
                          <div
                            className="p-3 hover:bg-teal-50 cursor-pointer border-b flex items-center gap-2"
                            onClick={() => {
                              setStaffNewMessageClientId('ALL_CLIENTS');
                              setClientSearchQuery('📢 All Clients (Broadcast)');
                            }}
                          >
                            <span className="text-lg">📢</span>
                            <div>
                              <div className="font-semibold text-teal-600">All Clients (Broadcast)</div>
                              <div className="text-xs text-gray-500">Send to {broadcastClientCount} clients</div>
                            </div>
                          </div>
                        )}
                        
                        {/* Filtered client list */}
                        {accessibleClients
                          .filter(c => {
                            const search = clientSearchQuery.toLowerCase();
                            return (
                              c.last_name?.toLowerCase().includes(search) ||
                              c.first_name?.toLowerCase().includes(search) ||
                              c.full_name?.toLowerCase().includes(search) ||
                              c.mrn && mrnMatches(c.mrn, search)
                            );
                          })
                          .slice(0, 50)
                          .map(c => {
                            const displayName = (c.first_name && c.last_name) 
                              ? `${c.last_name}, ${c.first_name}` 
                              : c.full_name || 'Unknown Client';
                            return (
                            <div
                              key={c.id}
                              className="p-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setStaffNewMessageClientId(c.id);
                                const cleanMrn = c.mrn ? c.mrn.replace(/^MRN-/i, '') : 'N/A';
                                setClientSearchQuery(`${displayName} (MRN: ${cleanMrn})`);
                              }}
                            >
                              <div className="font-medium">{displayName}</div>
                              <div className="text-xs text-gray-500">MRN: {c.mrn ? c.mrn.replace(/^MRN-/i, '') : 'N/A'}</div>
                            </div>
                          )})
                        }
                        
                        {accessibleClients.filter(c => {
                          const search = clientSearchQuery.toLowerCase();
                          return (
                            c.last_name?.toLowerCase().includes(search) ||
                            c.first_name?.toLowerCase().includes(search) ||
                            c.full_name?.toLowerCase().includes(search) ||
                            c.mrn && mrnMatches(c.mrn, search)
                          );
                        }).length === 0 && (
                          <div className="p-4 text-center text-gray-500">No clients found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="staffMsgTopic">Topic:</Label>
                  <Select value={staffNewMessageTopic} onValueChange={setStaffNewMessageTopic}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Question</SelectItem>
                      <SelectItem value="appointment">Appointment Related</SelectItem>
                      <SelectItem value="billing">Billing Question</SelectItem>
                      <SelectItem value="medical">Medical Question</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="staffMsgSubject">Subject: *</Label>
                  <DebouncedInput
                    id="staffMsgSubject"
                    value={staffNewMessageSubject}
                    onChange={(e) => setStaffNewMessageSubject(e.target.value)}
                    placeholder="E.g., Follow-up on your appointment"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="staffMsgText">Message: *</Label>
                  <DebouncedTextarea
                    id="staffMsgText"
                    value={staffNewMessageText}
                    onChange={(e) => setStaffNewMessageText(e.target.value)}
                    className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Type your message here..."
                    rows="6"
                    required
                  />
                </div>
              </>
            ) : (
              <>
                {/* Staff Recipient Selector */}
                <div>
                  <Label htmlFor="staffToStaffRecipient">To (Staff Member): *</Label>
                  <Select value={staffToStaffRecipientId} onValueChange={(value) => {
                    setStaffToStaffRecipientId(value);
                    const selectedUser = users.find(u => u.id === value);
                    if (selectedUser) {
                      setStaffToStaffRecipientName(selectedUser.full_name);
                    }
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select staff member" />
                    </SelectTrigger>
                    <SelectContent>
                      {users
                        .filter(u => u.id !== user?.id && u.role !== 'client')
                        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
                        .map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.full_name} ({formatRoleName(u.role)})
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="staffToStaffSubject">Subject: *</Label>
                  <DebouncedInput
                    id="staffToStaffSubject"
                    value={staffToStaffSubject}
                    onChange={(e) => setStaffToStaffSubject(e.target.value)}
                    placeholder="Message subject"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="staffToStaffMessage">Message: *</Label>
                  <DebouncedTextarea
                    id="staffToStaffMessage"
                    value={staffToStaffMessage}
                    onChange={(e) => setStaffToStaffMessage(e.target.value)}
                    className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Type your message here..."
                    rows="6"
                    required
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowStaffNewMessageDialog(false);
                  setStaffNewMessageClientId('');
                  setStaffNewMessageSubject('');
                  setStaffNewMessageText('');
                  setStaffNewMessageTopic('general');
                  setStaffToStaffRecipientId('');
                  setStaffToStaffSubject('');
                  setStaffToStaffMessage('');
                  setClientSearchQuery('');
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={sendingStaffNewMessage}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {sendingStaffNewMessage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Broadcast Confirmation Dialog */}
      <Dialog open={showBroadcastConfirmation} onOpenChange={setShowBroadcastConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Broadcast Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="font-semibold text-yellow-800">⚠️ You're about to send a message to ALL clients</p>
              <p className="text-sm text-yellow-700 mt-2">
                This will send <span className="font-bold">{broadcastClientCount} individual messages</span> to all active clients.
              </p>
            </div>
            
            <div className="text-sm text-gray-600">
              <p><strong>Subject:</strong> [Broadcast] {staffNewMessageSubject}</p>
              <p className="mt-2"><strong>Message preview:</strong></p>
              <p className="mt-1 p-2 bg-gray-50 rounded border">{staffNewMessageText.substring(0, 200)}{staffNewMessageText.length > 200 ? '...' : ''}</p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowBroadcastConfirmation(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={sendBroadcastMessage}
                disabled={sendingStaffNewMessage}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {sendingStaffNewMessage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Yes, Send to {broadcastClientCount} Clients
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recall Message Dialog */}
      <Dialog open={showRecallDialog} onOpenChange={setShowRecallDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recall Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              You're about to recall this message. This action cannot be undone.
            </p>
            
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <p className="font-semibold text-blue-800">What happens:</p>
              <ul className="mt-2 space-y-1 text-blue-700 text-xs">
                <li>• If <strong>unread</strong>: Message will be completely deleted</li>
                <li>• If <strong>already read</strong>: Content replaced with recall notice</li>
              </ul>
            </div>
            
            <div>
              <Label htmlFor="recallReason">Reason for recall: *</Label>
              <DebouncedTextarea
                id="recallReason"
                value={recallReason}
                onChange={(e) => setRecallReason(e.target.value)}
                className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 mt-2"
                placeholder="E.g., Sent to wrong client, incorrect information, etc."
                rows="3"
                required
              />
              <p className="text-xs text-gray-500 mt-1">This reason is for internal records only</p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowRecallDialog(false);
                  setRecallReason('');
                  setRecallThreadId(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={recallStaffMessage}
                disabled={recallingMessage || !recallReason.trim()}
                className="bg-red-600 hover:bg-red-700"
              >
                {recallingMessage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Recalling...
                  </>
                ) : (
                  <>
                    Recall Message
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Staff to Staff Message Dialog */}
      <Dialog open={showStaffToStaffMessageDialog} onOpenChange={setShowStaffToStaffMessageDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Message to {staffToStaffRecipientName}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!staffToStaffSubject.trim() || !staffToStaffMessage.trim()) {
              toast.error('Please fill in all fields');
              return;
            }
            
            setSendingStaffToStaffMessage(true);
            try {
              const params = new URLSearchParams({
                recipient_id: staffToStaffRecipientId,
                subject: staffToStaffSubject,
                message: staffToStaffMessage
              });
              
              await axios.post(`${API}/staff/messages/to-staff?${params.toString()}`);
              toast.success('Message sent successfully');
              setShowStaffToStaffMessageDialog(false);
              setStaffToStaffSubject('');
              setStaffToStaffMessage('');
            } catch (error) {
              console.error('Error sending message:', error);
              const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
              toast.error(`Failed to send message: ${detail}`);
            } finally {
              setSendingStaffToStaffMessage(false);
            }
          }} className="space-y-4">
            <div>
              <Label htmlFor="staffToStaffSubject">Subject: *</Label>
              <DebouncedInput
                id="staffToStaffSubject"
                value={staffToStaffSubject}
                onChange={(e) => setStaffToStaffSubject(e.target.value)}
                placeholder="Message subject"
                required
              />
            </div>

            <div>
              <Label htmlFor="staffToStaffMessage">Message: *</Label>
              <DebouncedTextarea
                id="staffToStaffMessage"
                value={staffToStaffMessage}
                onChange={(e) => setStaffToStaffMessage(e.target.value)}
                className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Type your message here..."
                rows="6"
                required
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowStaffToStaffMessageDialog(false);
                  setStaffToStaffSubject('');
                  setStaffToStaffMessage('');
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={sendingStaffToStaffMessage}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {sendingStaffToStaffMessage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Message from Chart Dialog */}
      <Dialog open={showMessageFromChartDialog} onOpenChange={setShowMessageFromChartDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Message to {messageFromChartClientName}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!staffNewMessageSubject.trim() || !staffNewMessageText.trim()) {
              toast.error('Please fill in all required fields');
              return;
            }
            
            setSendingStaffNewMessage(true);
            try {
              const params = new URLSearchParams({
                client_id: messageFromChartClientId,
                subject: staffNewMessageSubject,
                message: staffNewMessageText,
                topic: staffNewMessageTopic
              });
              
              await axios.post(`${API}/staff/messages/new?${params.toString()}`);
              toast.success('Message sent to client');
              setShowMessageFromChartDialog(false);
              setStaffNewMessageSubject('');
              setStaffNewMessageText('');
              setStaffNewMessageTopic('general');
              fetchStaffMessageThreads(false);
            } catch (error) {
              console.error('Error sending message:', error);
              const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
              toast.error(`Failed to send message: ${detail}`);
            } finally {
              setSendingStaffNewMessage(false);
            }
          }} className="space-y-4">
            <div>
              <Label htmlFor="chartMsgTopic">Topic:</Label>
              <Select value={staffNewMessageTopic} onValueChange={setStaffNewMessageTopic}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General Question</SelectItem>
                  <SelectItem value="appointment">Appointment Related</SelectItem>
                  <SelectItem value="billing">Billing Question</SelectItem>
                  <SelectItem value="medical">Medical Question</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="chartMsgSubject">Subject: *</Label>
              <DebouncedInput
                id="chartMsgSubject"
                value={staffNewMessageSubject}
                onChange={(e) => setStaffNewMessageSubject(e.target.value)}
                placeholder="E.g., Follow-up on your appointment"
                required
              />
            </div>

            <div>
              <Label htmlFor="chartMsgText">Message: *</Label>
              <DebouncedTextarea
                id="chartMsgText"
                value={staffNewMessageText}
                onChange={(e) => setStaffNewMessageText(e.target.value)}
                className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Type your message here..."
                rows="6"
                required
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowMessageFromChartDialog(false);
                  setStaffNewMessageSubject('');
                  setStaffNewMessageText('');
                  setStaffNewMessageTopic('general');
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={sendingStaffNewMessage}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {sendingStaffNewMessage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Archived Messages Search Dialog */}
      <Dialog open={showArchivedMessagesDialog} onOpenChange={setShowArchivedMessagesDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Archived Message Search</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Search for messages that have been archived after 180 days of inactivity.
            </p>

            {/* Search Filters */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="archivedClientName">Client Name</Label>
                <input
                  id="archivedClientName"
                  type="text"
                  value={archivedSearchClientName}
                  onChange={(e) => setArchivedSearchClientName(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Search by client name..."
                />
              </div>

              <div>
                <Label htmlFor="archivedStaffName">Staff Name</Label>
                <input
                  id="archivedStaffName"
                  type="text"
                  value={archivedSearchStaffName}
                  onChange={(e) => setArchivedSearchStaffName(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Search by staff name..."
                />
              </div>

              <div>
                <Label htmlFor="archivedStartDate">Start Date</Label>
                <input
                  id="archivedStartDate"
                  type="date"
                  value={archivedSearchStartDate}
                  onChange={(e) => setArchivedSearchStartDate(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                />
              </div>

              <div>
                <Label htmlFor="archivedEndDate">End Date</Label>
                <input
                  id="archivedEndDate"
                  type="date"
                  value={archivedSearchEndDate}
                  onChange={(e) => setArchivedSearchEndDate(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="archivedKeyword">Keyword Search</Label>
                <input
                  id="archivedKeyword"
                  type="text"
                  value={archivedSearchKeyword}
                  onChange={(e) => setArchivedSearchKeyword(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Search message content..."
                />
              </div>
            </div>

            {/* Search Button */}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setArchivedSearchClientName('');
                  setArchivedSearchStaffName('');
                  setArchivedSearchStartDate('');
                  setArchivedSearchEndDate('');
                  setArchivedSearchKeyword('');
                  setArchivedMessageResults([]);
                }}
              >
                Clear Filters
              </Button>
              <Button
                onClick={async () => {
                  setSearchingArchived(true);
                  try {
                    const params = new URLSearchParams();
                    if (archivedSearchClientName) params.append('client_name', archivedSearchClientName);
                    if (archivedSearchStaffName) params.append('staff_name', archivedSearchStaffName);
                    if (archivedSearchStartDate) params.append('start_date', new Date(archivedSearchStartDate).toISOString());
                    if (archivedSearchEndDate) params.append('end_date', new Date(archivedSearchEndDate).toISOString());
                    if (archivedSearchKeyword) params.append('keyword', archivedSearchKeyword);

                    const response = await fetch(`${BACKEND_URL}/api/admin/archived-messages/search?${params.toString()}`, {
                      headers: {
                        'Authorization': `Bearer ${sessionStorage.getItem('token')}`
                      }
                    });

                    if (!response.ok) {
                      throw new Error('Failed to search archived messages');
                    }

                    const data = await response.json();
                    setArchivedMessageResults(data.threads || []);
                    
                    if (data.threads.length === 0) {
                      toast.info('No archived messages found matching your search criteria');
                    } else {
                      toast.success(`Found ${data.threads.length} archived message(s)`);
                    }
                  } catch (error) {
                    console.error('Error searching archived messages:', error);
                    toast.error('Failed to search archived messages');
                  } finally {
                    setSearchingArchived(false);
                  }
                }}
                disabled={searchingArchived}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {searchingArchived ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
            </div>

            {/* Search Results */}
            {archivedMessageResults.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold mb-3">Search Results ({archivedMessageResults.length})</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {archivedMessageResults.map((thread) => (
                    <div
                      key={thread.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${BACKEND_URL}/api/admin/archived-messages/${thread.id}`, {
                            headers: {
                              'Authorization': `Bearer ${sessionStorage.getItem('token')}`
                            }
                          });

                          if (!response.ok) {
                            throw new Error('Failed to fetch archived thread');
                          }

                          const data = await response.json();
                          setSelectedArchivedThread(data.thread);
                          setArchivedThreadMessages(data.messages || []);
                          setShowArchivedMessagesDialog(false);
                        } catch (error) {
                          console.error('Error fetching archived thread:', error);
                          toast.error('Failed to load archived message thread');
                        }
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold">{thread.client_name}</span>
                            <span className="text-gray-500">→</span>
                            <span className="text-gray-600">{thread.recipient_name}</span>
                          </div>
                          <div className="text-sm font-medium text-gray-900 mb-1">
                            {thread.subject}
                          </div>
                          <div className="text-sm text-gray-500">
                            {thread.last_message_preview}
                          </div>
                          <div className="text-xs text-gray-400 mt-2">
                            Created: {formatMessageTimestamp(thread.created_at, { hour: undefined, minute: undefined })}
                            {thread.archived_at && ` • Archived: ${formatMessageTimestamp(thread.archived_at, { hour: undefined, minute: undefined })}`}
                          </div>
                        </div>
                        <Badge variant="secondary" className="ml-2">
                          Archived
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Archived Thread View Dialog */}
      {selectedArchivedThread && (
        <Dialog open={!!selectedArchivedThread} onOpenChange={(open) => {
          if (!open) {
            setSelectedArchivedThread(null);
            setArchivedThreadMessages([]);
            setShowArchivedMessagesDialog(true);
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Archived Message Thread</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 flex-1 overflow-y-auto">
              {/* Thread Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold">Client:</span> {selectedArchivedThread.client_name}
                  </div>
                  <div>
                    <span className="font-semibold">Staff:</span> {selectedArchivedThread.recipient_name}
                  </div>
                  <div>
                    <span className="font-semibold">Subject:</span> {selectedArchivedThread.subject}
                  </div>
                  <div>
                    <span className="font-semibold">Topic:</span> {selectedArchivedThread.topic}
                  </div>
                  <div>
                    <span className="font-semibold">Created:</span> {formatMessageTimestamp(selectedArchivedThread.created_at)}
                  </div>
                  <div>
                    <span className="font-semibold">Archived:</span> {selectedArchivedThread.archived_at ? formatMessageTimestamp(selectedArchivedThread.archived_at) : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="space-y-3">
                <h3 className="font-semibold">Message History</h3>
                {archivedThreadMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-4 rounded-lg ${
                      msg.sender_type === 'client' 
                        ? 'bg-blue-50 border-l-4 border-blue-500' 
                        : 'bg-green-50 border-l-4 border-green-500'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold">
                        {msg.sender_name}
                        <span className="text-xs text-gray-500 ml-2">
                          ({msg.sender_type})
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatMessageTimestamp(msg.created_at)}
                      </span>
                    </div>
                    <div className="text-sm whitespace-pre-wrap">
                      {msg.message}
                    </div>
                    {msg.is_recalled && (
                      <div className="mt-2 text-xs text-red-600 italic">
                        This message was recalled
                        {msg.recall_reason && `: ${msg.recall_reason}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedArchivedThread(null);
                  setArchivedThreadMessages([]);
                  setShowArchivedMessagesDialog(true);
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Search
              </Button>

              <Button
                onClick={async () => {
                  if (!window.confirm('Are you sure you want to restore this message thread? It will become visible in the staff messaging area again.')) {
                    return;
                  }

                  try {
                    const response = await fetch(`${BACKEND_URL}/api/admin/archived-messages/${selectedArchivedThread.id}/restore`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${sessionStorage.getItem('token')}`
                      }
                    });

                    if (!response.ok) {
                      throw new Error('Failed to restore message thread');
                    }

                    toast.success('Message thread restored successfully');
                    
                    // Remove from results
                    setArchivedMessageResults(prev => prev.filter(t => t.id !== selectedArchivedThread.id));
                    
                    // Close dialog and go back to search
                    setSelectedArchivedThread(null);
                    setArchivedThreadMessages([]);
                    setShowArchivedMessagesDialog(true);
                  } catch (error) {
                    console.error('Error restoring message thread:', error);
                    toast.error('Failed to restore message thread');
                  }
                }}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Restore Thread
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* CPT Conflict Rule Dialog */}
      <Dialog open={showConflictRuleDialog} onOpenChange={setShowConflictRuleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingConflictRule ? 'Edit' : 'Create'} CPT Conflict Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rule Name *</Label>
              <DebouncedInput
                value={conflictRuleForm.rule_name}
                onChange={(e) => setConflictRuleForm({...conflictRuleForm, rule_name: e.target.value})}
                placeholder="e.g., Only one group session per day"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <DebouncedTextarea
                value={conflictRuleForm.description}
                onChange={(e) => setConflictRuleForm({...conflictRuleForm, description: e.target.value})}
                placeholder="Explain why these CPT codes conflict..."
                rows={2}
              />
            </div>
            
            <div>
              <Label>Conflicting CPT Codes *</Label>
              <p className="text-xs text-gray-500 mb-2">
                Select CPT codes that cannot be scheduled on the same day for a client
              </p>
              <Select
                value=""
                onValueChange={(value) => {
                  if (value && !conflictRuleForm.cpt_codes.includes(value)) {
                    setConflictRuleForm({
                      ...conflictRuleForm, 
                      cpt_codes: [...conflictRuleForm.cpt_codes, value]
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select CPT codes to add..." />
                </SelectTrigger>
                <SelectContent>
                  {/* Show unique CPT codes only - group by code and show all descriptions */}
                  {[...new Map(cptCodes.map(cpt => [cpt.code, cpt])).values()]
                    .filter(cpt => !conflictRuleForm.cpt_codes.includes(cpt.code))
                    .sort((a, b) => a.code.localeCompare(b.code))
                    .map(cpt => {
                      // Get all descriptions for this code
                      const allDescriptions = cptCodes
                        .filter(c => c.code === cpt.code)
                        .map(c => c.description || c.display_name)
                        .filter(Boolean);
                      const uniqueDescriptions = [...new Set(allDescriptions)];
                      const displayDesc = uniqueDescriptions.length > 1 
                        ? `${uniqueDescriptions[0]} (+${uniqueDescriptions.length - 1} more)` 
                        : (uniqueDescriptions[0] || 'No description');
                      return (
                        <SelectItem key={cpt.code} value={cpt.code}>
                          {cpt.code} - {displayDesc}
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
              
              {conflictRuleForm.cpt_codes.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {conflictRuleForm.cpt_codes.map((code) => {
                    // Get all descriptions for this code
                    const matchingCpts = cptCodes.filter(c => c.code === code);
                    const descriptions = [...new Set(matchingCpts.map(c => c.description || c.display_name).filter(Boolean))];
                    return (
                      <div key={code} className="flex items-center gap-1 bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm">
                        <span className="font-medium">{code}</span>
                        {descriptions.length > 0 && (
                          <span className="text-xs" title={descriptions.join(', ')}>
                            - {descriptions.length > 1 ? `${descriptions[0]} (+${descriptions.length - 1})` : descriptions[0]}
                          </span>
                        )}
                        <button
                          onClick={() => {
                            setConflictRuleForm({
                              ...conflictRuleForm,
                              cpt_codes: conflictRuleForm.cpt_codes.filter(c => c !== code)
                            });
                          }}
                          className="ml-1 text-orange-600 hover:text-orange-800"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="border rounded-lg p-3 space-y-2">
              <Label htmlFor="max_per_day">Maximum Appointments Per Day</Label>
              <Input
                id="max_per_day"
                type="number"
                min={1}
                max={10}
                value={conflictRuleForm.max_per_day || 1}
                onChange={(e) => setConflictRuleForm({...conflictRuleForm, max_per_day: parseInt(e.target.value) || 1})}
                className="w-24"
              />
              <p className="text-xs text-gray-500">
                Set to 1 for "only one per day" rules. For example, set to 1 for IHT to allow only 1 IHT session per client per day.
              </p>
            </div>
            
            <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded">
              <input
                type="checkbox"
                id="can_override"
                checked={conflictRuleForm.can_override || false}
                onChange={(e) => setConflictRuleForm({...conflictRuleForm, can_override: e.target.checked})}
                className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <Label htmlFor="can_override" className="cursor-pointer">
                Allow users to override this rule
                <p className="text-xs text-gray-500 font-normal mt-1">
                  If checked, users will see a warning but can choose to proceed anyway. If unchecked, scheduling will be blocked completely.
                </p>
              </Label>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowConflictRuleDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!conflictRuleForm.rule_name || conflictRuleForm.cpt_codes.length < 1) {
                    toast.error('Please provide rule name and at least 1 CPT code');
                    return;
                  }
                  
                  try {
                    if (editingConflictRule) {
                      await axios.put(`${API}/cpt-conflict-rules/${editingConflictRule.id}`, conflictRuleForm);
                      toast.success('Conflict rule updated successfully');
                    } else {
                      await axios.post(`${API}/cpt-conflict-rules`, conflictRuleForm);
                      toast.success('Conflict rule created successfully');
                    }
                    setShowConflictRuleDialog(false);
                    fetchRules();
                  } catch (error) {
                    toast.error('Failed to save conflict rule');
                  }
                }}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {editingConflictRule ? 'Update' : 'Create'} Rule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Insurance-CPT Restriction Dialog */}
      <Dialog open={showRestrictionDialog} onOpenChange={setShowRestrictionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRestriction ? 'Edit' : 'Create'} Insurance-CPT Restriction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Insurance Providers * (Select multiple)</Label>
              <p className="text-xs text-gray-500 mb-2">
                Select all insurance providers that do NOT cover the CPT codes below
              </p>
              <Select
                value=""
                onValueChange={(value) => {
                  if (value && !restrictionForm.insurance_ids.includes(value)) {
                    setRestrictionForm({
                      ...restrictionForm, 
                      insurance_ids: [...restrictionForm.insurance_ids, value]
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select insurances to add..." />
                </SelectTrigger>
                <SelectContent>
                  {insurances
                    .filter(ins => !restrictionForm.insurance_ids.includes(ins.id))
                    .map(ins => (
                      <SelectItem key={ins.id} value={ins.id}>
                        {ins.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              
              {restrictionForm.insurance_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {restrictionForm.insurance_ids.map((insId) => {
                    const insInfo = insurances.find(i => i.id === insId);
                    return (
                      <div key={insId} className="flex items-center gap-1 bg-red-100 text-red-800 px-2 py-1 rounded text-sm">
                        <span>{insInfo?.name || insId}</span>
                        <button
                          onClick={() => {
                            setRestrictionForm({
                              ...restrictionForm,
                              insurance_ids: restrictionForm.insurance_ids.filter(id => id !== insId)
                            });
                          }}
                          className="ml-1 text-red-600 hover:text-red-800"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div>
              <Label>Specific Plans (Optional)</Label>
              <p className="text-xs text-gray-500 mb-2">
                Leave empty to apply to ALL plans. Or select specific plans to restrict.
              </p>
              
              {restrictionForm.insurance_ids.length > 0 ? (
                <>
                  <Select
                    value=""
                    onValueChange={(value) => {
                      if (value && !restrictionForm.specific_plans.includes(value)) {
                        setRestrictionForm({
                          ...restrictionForm,
                          specific_plans: [...restrictionForm.specific_plans, value]
                        });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select plans to restrict..." />
                    </SelectTrigger>
                    <SelectContent>
                      {restrictionForm.insurance_ids.flatMap(insId => {
                        const insurance = insurances.find(i => i.id === insId);
                        if (!insurance || !insurance.plans || insurance.plans.length === 0) {
                          return [(
                            <SelectItem key={`${insId}-no-plans`} value={`_no_plans_${insId}`} disabled>
                              {insurance?.name || 'Unknown'} - No plans configured
                            </SelectItem>
                          )];
                        }
                        return insurance.plans
                          .filter(plan => {
                            const planName = typeof plan === 'string' ? plan : plan.name;
                            return !restrictionForm.specific_plans.includes(planName);
                          })
                          .map(plan => {
                            const planName = typeof plan === 'string' ? plan : plan.name;
                            return (
                              <SelectItem key={`${insId}-${planName}`} value={planName}>
                                {insurance.name} - {planName}
                              </SelectItem>
                            );
                          });
                      })}
                    </SelectContent>
                  </Select>
                  
                  {restrictionForm.specific_plans.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {restrictionForm.specific_plans.map((plan) => (
                        <div key={plan} className="flex items-center gap-1 bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm">
                          <span>{plan}</span>
                          <button
                            onClick={() => {
                              setRestrictionForm({
                                ...restrictionForm,
                                specific_plans: restrictionForm.specific_plans.filter(p => p !== plan)
                              });
                            }}
                            className="ml-1 text-purple-600 hover:text-purple-800"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400 italic">Select insurance companies first to see available plans</p>
              )}
            </div>
            
            <div>
              <Label>Description</Label>
              <DebouncedTextarea
                value={restrictionForm.description}
                onChange={(e) => setRestrictionForm({...restrictionForm, description: e.target.value})}
                placeholder="Explain the restriction..."
                rows={2}
              />
            </div>
            
            <div>
              <Label>Restricted CPT Codes *</Label>
              <p className="text-xs text-gray-500 mb-2">
                Select CPT codes that are NOT allowed for this insurance
              </p>
              <Select
                value=""
                onValueChange={(value) => {
                  if (value && !restrictionForm.cpt_codes.includes(value)) {
                    setRestrictionForm({
                      ...restrictionForm, 
                      cpt_codes: [...restrictionForm.cpt_codes, value]
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select CPT codes to restrict..." />
                </SelectTrigger>
                <SelectContent>
                  {cptCodes
                    .filter(cpt => !restrictionForm.cpt_codes.includes(cpt.code))
                    .map(cpt => (
                      <SelectItem key={cpt.id} value={cpt.code}>
                        {cpt.code} - {cpt.display_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              
              {restrictionForm.cpt_codes.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {restrictionForm.cpt_codes.map((code) => {
                    const cptInfo = cptCodes.find(c => c.code === code);
                    return (
                      <div key={code} className="flex items-center gap-1 bg-red-100 text-red-800 px-2 py-1 rounded text-sm">
                        <span>{code}</span>
                        {cptInfo && <span className="text-xs">- {cptInfo.display_name}</span>}
                        <button
                          onClick={() => {
                            setRestrictionForm({
                              ...restrictionForm,
                              cpt_codes: restrictionForm.cpt_codes.filter(c => c !== code)
                            });
                          }}
                          className="ml-1 text-red-600 hover:text-red-800"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded">
              <input
                type="checkbox"
                id="allow_direct_billing"
                checked={restrictionForm.allow_direct_billing || false}
                onChange={(e) => setRestrictionForm({...restrictionForm, allow_direct_billing: e.target.checked})}
                className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <Label htmlFor="allow_direct_billing" className="cursor-pointer">
                Allow non-billable scheduling (record-keeping only)
                <p className="text-xs text-gray-500 font-normal mt-1">
                  If checked, users can schedule this service for record-keeping, but it will be marked as non-billable. If unchecked, scheduling will be blocked completely.
                </p>
              </Label>
            </div>
            
            <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded">
              <input
                type="checkbox"
                id="allow_secondary_fallback"
                checked={restrictionForm.allow_secondary_fallback !== false}
                onChange={(e) => setRestrictionForm({...restrictionForm, allow_secondary_fallback: e.target.checked})}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <Label htmlFor="allow_secondary_fallback" className="cursor-pointer">
                Allow secondary insurance fallback
                <p className="text-xs text-gray-500 font-normal mt-1">
                  If checked and client has secondary insurance, system will check if secondary covers this service when primary doesn't. If unchecked, service will be blocked regardless of secondary coverage.
                </p>
              </Label>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowRestrictionDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (restrictionForm.insurance_ids.length === 0 || restrictionForm.cpt_codes.length === 0) {
                    toast.error('Please select at least one insurance and one CPT code');
                    return;
                  }
                  
                  try {
                    if (editingRestriction) {
                      await axios.put(`${API}/insurance-cpt-restrictions/${editingRestriction.id}`, restrictionForm);
                      toast.success('Restriction updated successfully');
                    } else {
                      await axios.post(`${API}/insurance-cpt-restrictions`, restrictionForm);
                      toast.success('Restriction created successfully');
                    }
                    setShowRestrictionDialog(false);
                    fetchRules();
                  } catch (error) {
                    toast.error('Failed to save restriction');
                  }
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                {editingRestriction ? 'Update' : 'Create'} Restriction
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rules Management Dialog */}
      <Dialog open={showRulesManagementDialog} onOpenChange={setShowRulesManagementDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-start justify-between">
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Insurance/CPT Conflict Rules Configuration
            </DialogTitle>
            <AdminJumpToDropdown currentDialogId="rulesManagement" isDirty={currentAdminDialogDirty} />
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* CPT Conflict Rules Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                    CPT Conflict Rules
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Prevent scheduling of conflicting services on the same day
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingConflictRule(null);
                    setConflictRuleForm({
                      rule_name: '',
                      cpt_codes: [],
                      description: '',
                      can_override: false,
                      max_per_day: 1,
                      is_active: true
                    });
                    setShowConflictRuleDialog(true);
                  }}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Conflict Rule
                </Button>
              </CardHeader>
              <CardContent>
                {cptConflictRules.length === 0 ? (
                  <p className="text-sm text-gray-500">No conflict rules configured yet.</p>
                ) : (
                  <div className="space-y-2">
                    {cptConflictRules.map((rule) => (
                      <div key={rule.id} className="p-3 border rounded-lg flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold">{rule.rule_name}</p>
                          <p className="text-sm text-gray-600">{rule.description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            CPT Codes: {rule.cpt_codes.join(', ')}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingConflictRule(rule);
                              setConflictRuleForm(rule);
                              setShowConflictRuleDialog(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              if (window.confirm('Delete this conflict rule?')) {
                                try {
                                  await axios.delete(`${API}/cpt-conflict-rules/${rule.id}`);
                                  toast.success('Conflict rule deleted');
                                  fetchRules();
                                } catch (error) {
                                  toast.error('Failed to delete rule');
                                }
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Insurance Restrictions Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-600" />
                    Insurance-CPT Restrictions
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Block specific CPT codes for certain insurance providers
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingRestriction(null);
                    setRestrictionForm({
                      insurance_ids: [],
                      specific_plans: [],
                      cpt_codes: [],
                      description: '',
                      allow_direct_billing: false,
                      allow_secondary_fallback: true,
                      is_active: true
                    });
                    setShowRestrictionDialog(true);
                  }}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Restriction
                </Button>
              </CardHeader>
              <CardContent>
                {insuranceCptRestrictions.length === 0 ? (
                  <p className="text-sm text-gray-500">No insurance restrictions configured yet.</p>
                ) : (
                  <div className="space-y-2">
                    {insuranceCptRestrictions.map((restriction) => {
                      const insuranceIds = restriction.insurance_ids || [restriction.insurance_id].filter(Boolean);
                      // Map each ID -> human label. If we can't find it in the
                      // insurances list (hard-deleted payer), show a red
                      // "Removed insurance" chip + short ID suffix so the admin
                      // can still identify / clean the orphan link.
                      const missingIds = insuranceIds.filter(id => !insurances.find(i => i.id === id));
                      const insuranceNames = insuranceIds
                        .map(id => {
                          const ins = insurances.find(i => i.id === id);
                          if (ins) return ins.name || ins.payer_name || 'Unnamed Insurance';
                          return `Removed Insurance (${(id || '').slice(0, 8)}…)`;
                        })
                        .join(', ');

                      const planInfo = restriction.specific_plans && restriction.specific_plans.length > 0
                        ? ` (Plans: ${restriction.specific_plans.join(', ')})`
                        : ' (All Plans)';

                      return (
                        <div key={restriction.id} className="p-3 border rounded-lg flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-semibold">
                              {insuranceNames || 'Unknown Insurance'}{planInfo}
                            </p>
                            {missingIds.length > 0 && (
                              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 inline-block" data-testid="orphan-insurance-warning">
                                ⚠ {missingIds.length} referenced insurance{missingIds.length > 1 ? 's have' : ' has'} been removed. Edit this restriction to clean up, or delete it.
                              </p>
                            )}
                            <p className="text-sm text-gray-600">{restriction.description}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Restricted CPT Codes: {restriction.cpt_codes.join(', ')}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingRestriction(restriction);
                                // Ensure backward compatibility
                                const formData = {
                                  ...restriction,
                                  insurance_ids: restriction.insurance_ids || [restriction.insurance_id].filter(Boolean),
                                  specific_plans: restriction.specific_plans || [],
                                  allow_secondary_fallback: restriction.allow_secondary_fallback !== false // Default to true
                                };
                                setRestrictionForm(formData);
                                setShowRestrictionDialog(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                if (window.confirm('Delete this restriction?')) {
                                try {
                                  await axios.delete(`${API}/insurance-cpt-restrictions/${restriction.id}`);
                                  toast.success('Restriction deleted');
                                  fetchRules();
                                } catch (error) {
                                  toast.error('Failed to delete restriction');
                                }
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CPT Billing Rules — Modifier add / override / replace
                rules applied at claim submission time.  Jun 2026.
                Backend model: CPTBillingRule. */}
            <Card data-testid="admin-cpt-billing-rules-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Wrench className="h-5 w-5 text-indigo-600" />
                    CPT Billing Rules
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Add or override modifiers on specific CPTs by payer and/or credential at claim generation.
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingCptBillingRule(null);
                    setCptBillingRuleForm({
                      rule_name: '',
                      description: '',
                      cpt_code: '',
                      cpt_code_ids: [],
                      insurance_name: '',
                      provider_credential: '',
                      action_type: 'modify',
                      add_modifiers: [],
                      add_modifiers_position: 'append',
                      remove_modifiers: [],
                      override_modifiers: [],
                      replacement_cpt_code: '',
                      block_reason: '',
                      priority: 0,
                      notes: '',
                      is_active: true,
                    });
                    setShowCptBillingRuleDialog(true);
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="admin-cpt-billing-rule-add"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Billing Rule
                </Button>
              </CardHeader>
              <CardContent>
                {(!cptBillingRules || cptBillingRules.length === 0) ? (
                  <p className="text-sm text-gray-500">No CPT billing rules configured yet.</p>
                ) : (
                  <div className="space-y-2">
                    {cptBillingRules.map((rule) => (
                      <div key={rule.id} className="p-3 border rounded-lg flex justify-between items-start" data-testid={`admin-cpt-billing-rule-row-${rule.id}`}>
                        <div className="flex-1">
                          <p className="font-semibold">{rule.rule_name}</p>
                          {rule.description && <p className="text-sm text-gray-600">{rule.description}</p>}
                          <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-700">
                            {rule.cpt_code && <span className="px-2 py-0.5 bg-slate-100 rounded font-mono">CPT: {rule.cpt_code}</span>}
                            {rule.insurance_name && <span className="px-2 py-0.5 bg-slate-100 rounded">Payer: {rule.insurance_name}</span>}
                            {rule.provider_credential && <span className="px-2 py-0.5 bg-slate-100 rounded">Credential: {rule.provider_credential}</span>}
                            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded">Action: {rule.action_type}</span>
                            {(rule.add_modifiers || []).length > 0 && (
                              <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded font-mono">+{rule.add_modifiers.join(', ')}</span>
                            )}
                            {(rule.override_modifiers || []).length > 0 && (
                              <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded font-mono">override → {rule.override_modifiers.join(', ')}</span>
                            )}
                            {(rule.remove_modifiers || []).length > 0 && (
                              <span className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded font-mono">−{rule.remove_modifiers.join(', ')}</span>
                            )}
                            {rule.replacement_cpt_code && (
                              <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded font-mono">→ CPT {rule.replacement_cpt_code}</span>
                            )}
                            <span className="px-2 py-0.5 bg-gray-50 rounded">Priority: {rule.priority || 0}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingCptBillingRule(rule);
                              setCptBillingRuleForm({
                                rule_name: rule.rule_name || '',
                                description: rule.description || '',
                                cpt_code: rule.cpt_code || '',
                                // Merge legacy singular into the multi-select array.
                                cpt_code_ids: rule.cpt_code_ids && rule.cpt_code_ids.length > 0
                                  ? rule.cpt_code_ids
                                  : (rule.cpt_code_id ? [rule.cpt_code_id] : []),
                                insurance_name: rule.insurance_name || '',
                                provider_credential: rule.provider_credential || '',
                                action_type: rule.action_type || 'modify',
                                add_modifiers: rule.add_modifiers || [],
                                add_modifiers_position: rule.add_modifiers_position || 'append',
                                remove_modifiers: rule.remove_modifiers || [],
                                override_modifiers: rule.override_modifiers || [],
                                replacement_cpt_code: rule.replacement_cpt_code || '',
                                block_reason: rule.block_reason || '',
                                priority: rule.priority || 0,
                                notes: rule.notes || '',
                                is_active: rule.is_active !== false,
                              });
                              setShowCptBillingRuleDialog(true);
                            }}
                            data-testid={`admin-cpt-billing-rule-edit-${rule.id}`}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              if (window.confirm(`Deactivate billing rule "${rule.rule_name}"?`)) {
                                try {
                                  await axios.delete(`${API}/billing/rules/cpt/${rule.id}`);
                                  toast.success('Billing rule deactivated');
                                  fetchRules();
                                } catch (error) {
                                  toast.error('Failed to deactivate rule');
                                }
                              }
                            }}
                            data-testid={`admin-cpt-billing-rule-delete-${rule.id}`}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* CPT Billing Rule Editor — Add / Edit modal */}
      <Dialog open={showCptBillingRuleDialog} onOpenChange={setShowCptBillingRuleDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingCptBillingRule ? 'Edit' : 'Add'} CPT Billing Rule</DialogTitle>
            <DialogDescription>
              Mutates the claim line at submission time when ALL conditions match.
              Leave a condition blank to match anything.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Rule Name *</Label>
              <Input
                value={cptBillingRuleForm.rule_name}
                onChange={(e) => setCptBillingRuleForm({ ...cptBillingRuleForm, rule_name: e.target.value })}
                placeholder="e.g., Add U1 modifier for 90837 on Medicaid (nurses)"
                data-testid="cpt-billing-rule-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input
                value={cptBillingRuleForm.description}
                onChange={(e) => setCptBillingRuleForm({ ...cptBillingRuleForm, description: e.target.value })}
                placeholder="Optional human-readable explanation"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">CPT Targeting</Label>
                {/* Feb 2026 — Per-variant picker (multi-select).
                    Lists every CPT variant in the catalog so the
                    user can target one OR more specific variants.
                    Leaving the list empty AND ``cpt_code`` blank
                    matches every CPT (universal rule); leaving the
                    list empty WITH ``cpt_code`` set matches all
                    variants that share that billing code (legacy).
                    Picking 1+ variants restricts to exactly those. */}
                {(() => {
                  const ids = cptBillingRuleForm.cpt_code_ids || [];
                  const variantById = {};
                  for (const c of (cptCodes || [])) variantById[c.id] = c;
                  // Sort the catalog by code then by display_name so
                  // the picker is browsable.
                  const sortedCpts = [...(cptCodes || [])].sort((a, b) => {
                    const ca = (a.code || '').localeCompare(b.code || '');
                    if (ca !== 0) return ca;
                    return (a.display_name || a.description || '').localeCompare(b.display_name || b.description || '');
                  });
                  const labelFor = (v) => {
                    const code = v.code || '';
                    const name = v.display_name || v.description || '';
                    return name ? `${code} — ${name}` : `${code}`;
                  };
                  const buttonLabel =
                    ids.length === 0
                      ? (cptBillingRuleForm.cpt_code ? `All variants of ${cptBillingRuleForm.cpt_code}` : 'Any CPT (applies to all)')
                      : ids.length === 1
                        ? labelFor(variantById[ids[0]] || { code: ids[0] })
                        : `${ids.length} variants selected`;
                  return (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-between font-normal h-9 text-left"
                          data-testid="cpt-billing-rule-cpt-picker"
                        >
                          <span className="truncate">{buttonLabel}</span>
                          <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[420px] p-0" align="start">
                        <div className="p-2 border-b text-xs text-gray-500 flex items-center justify-between">
                          <span>
                            {ids.length === 0
                              ? 'No variants selected (applies to all of ' + (cptBillingRuleForm.cpt_code || 'any code') + ')'
                              : `${ids.length} variant${ids.length === 1 ? '' : 's'} selected`}
                          </span>
                          {ids.length > 0 && (
                            <button
                              type="button"
                              className="text-indigo-600 hover:underline"
                              onClick={() =>
                                setCptBillingRuleForm({ ...cptBillingRuleForm, cpt_code_ids: [] })
                              }
                              data-testid="cpt-billing-rule-cpt-clear"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <div className="max-h-72 overflow-y-auto py-1">
                          {sortedCpts.length === 0 ? (
                            <div className="px-3 py-4 text-xs text-gray-500">
                              No CPT codes available — add them in Setup → CPT Codes first.
                            </div>
                          ) : (
                            sortedCpts.map((v) => {
                              const checked = ids.includes(v.id);
                              return (
                                <label
                                  key={v.id}
                                  className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 cursor-pointer"
                                  data-testid={`cpt-billing-rule-cpt-option-${v.id}`}
                                >
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(next) => {
                                      const nextIds = next
                                        ? Array.from(new Set([...ids, v.id]))
                                        : ids.filter((i) => i !== v.id);
                                      // Also stamp the bare cpt_code so
                                      // rules round-trip cleanly even if
                                      // someone later clears the variant
                                      // list and wants the legacy "all
                                      // variants of <code>" behavior.
                                      const inferredCode = nextIds.length > 0
                                        ? (variantById[nextIds[0]]?.code || cptBillingRuleForm.cpt_code)
                                        : cptBillingRuleForm.cpt_code;
                                      setCptBillingRuleForm({
                                        ...cptBillingRuleForm,
                                        cpt_code_ids: nextIds,
                                        cpt_code: inferredCode,
                                      });
                                    }}
                                  />
                                  <span className="font-mono text-gray-700">{v.code}</span>
                                  {(v.display_name || v.description) && (
                                    <span className="text-gray-600 truncate">
                                      — {v.display_name || (v.description || '').slice(0, 60)}
                                    </span>
                                  )}
                                </label>
                              );
                            })
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  );
                })()}
                {/* Helper text — clarifies the precedence rules. */}
                <p className="text-[10px] text-gray-500 mt-1 leading-tight">
                  {(cptBillingRuleForm.cpt_code_ids || []).length > 0
                    ? 'Rule applies to the selected variants only.'
                    : cptBillingRuleForm.cpt_code
                    ? `Rule applies to all variants of ${cptBillingRuleForm.cpt_code}.`
                    : 'Rule applies to every CPT.'}
                </p>
              </div>
              <div>
                <Label className="text-xs">Payer</Label>
                <Select
                  value={cptBillingRuleForm.insurance_name || '__any__'}
                  onValueChange={(v) => setCptBillingRuleForm({ ...cptBillingRuleForm, insurance_name: v === '__any__' ? '' : v })}
                >
                  <SelectTrigger data-testid="cpt-billing-rule-payer-select">
                    <SelectValue placeholder="Any payer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Any payer</SelectItem>
                    {/* Legacy free-text value not matching a configured payer */}
                    {cptBillingRuleForm.insurance_name
                      && !insurances.some((i) => i.name === cptBillingRuleForm.insurance_name) && (
                      <SelectItem value={cptBillingRuleForm.insurance_name}>
                        {cptBillingRuleForm.insurance_name} (legacy text)
                      </SelectItem>
                    )}
                    {[...new Map(insurances.filter((i) => i.is_active !== false && i.name).map((i) => [i.name, i])).values()]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((i) => (
                        <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Credential</Label>
                <Select
                  value={cptBillingRuleForm.provider_credential || '__any__'}
                  onValueChange={(v) => setCptBillingRuleForm({ ...cptBillingRuleForm, provider_credential: v === '__any__' ? '' : v })}
                >
                  <SelectTrigger data-testid="cpt-billing-rule-credential-select">
                    <SelectValue placeholder="Any credential" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Any credential</SelectItem>
                    {(() => {
                      // Existing credentials pulled from staff profiles
                      // (supports comma-separated multi-credential values).
                      const creds = [...new Set((users || [])
                        .flatMap((u) => String(u.credentials || '').split(','))
                        .map((c) => c.trim())
                        .filter(Boolean))]
                        .sort((a, b) => a.localeCompare(b));
                      const current = (cptBillingRuleForm.provider_credential || '').trim();
                      return (
                        <>
                          {/* Legacy free-text value not matching any staff credential */}
                          {current && !creds.some((c) => c.toLowerCase() === current.toLowerCase()) && (
                            <SelectItem value={current}>{current} (legacy text)</SelectItem>
                          )}
                          {creds.map((c) => (
                            <SelectItem key={c} value={c} data-testid={`cpt-billing-rule-credential-option-${c}`}>{c}</SelectItem>
                          ))}
                        </>
                      );
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Action</Label>
                <select
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  value={cptBillingRuleForm.action_type}
                  onChange={(e) => setCptBillingRuleForm({ ...cptBillingRuleForm, action_type: e.target.value })}
                  data-testid="cpt-billing-rule-action-select"
                >
                  <option value="modify">Modify (add / override / remove modifiers)</option>
                  <option value="replace_cpt">Replace CPT entirely</option>
                  <option value="block">Block (warn + prevent claim submission)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Priority (higher = applied later, wins ties)</Label>
                <Input
                  type="number"
                  value={cptBillingRuleForm.priority}
                  onChange={(e) => setCptBillingRuleForm({ ...cptBillingRuleForm, priority: parseInt(e.target.value || '0', 10) })}
                />
              </div>
            </div>

            {cptBillingRuleForm.action_type === 'modify' && (
              <>
                <div>
                  <Label className="text-xs">Add Modifiers (comma-separated)</Label>
                  <Input
                    value={(cptBillingRuleForm.add_modifiers || []).join(',')}
                    onChange={(e) => setCptBillingRuleForm({
                      ...cptBillingRuleForm,
                      add_modifiers: e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean),
                    })}
                    placeholder="e.g., U1, GT"
                    data-testid="cpt-billing-rule-add-modifiers-input"
                  />
                  {/* Feb 2026 — Position toggle.  Default "append"
                      keeps existing rules byte-identical; "prepend"
                      puts rule-added modifiers BEFORE the credential
                      modifier (e.g., HA → HO → 95 instead of HO → 95
                      → HA), which some payers require for qualifier
                      modifiers. */}
                  <div className="mt-2 flex items-center gap-3 text-xs">
                    <span className="text-gray-600">Position:</span>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="add-modifiers-position"
                        value="append"
                        checked={(cptBillingRuleForm.add_modifiers_position || 'append') === 'append'}
                        onChange={() => setCptBillingRuleForm({ ...cptBillingRuleForm, add_modifiers_position: 'append' })}
                        data-testid="cpt-billing-rule-add-mods-position-append"
                      />
                      <span>After credential modifier (default)</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="add-modifiers-position"
                        value="prepend"
                        checked={cptBillingRuleForm.add_modifiers_position === 'prepend'}
                        onChange={() => setCptBillingRuleForm({ ...cptBillingRuleForm, add_modifiers_position: 'prepend' })}
                        data-testid="cpt-billing-rule-add-mods-position-prepend"
                      />
                      <span>Before credential modifier (first)</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {cptBillingRuleForm.add_modifiers_position === 'prepend'
                      ? 'Inserts these modifiers at the FRONT — they appear before HO/HN/AJ/etc. on the claim.'
                      : 'Appended on top of credential-derived modifiers (SA/HM/etc.).'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs">Override Modifiers (replaces everything else)</Label>
                  <Input
                    value={(cptBillingRuleForm.override_modifiers || []).join(',')}
                    onChange={(e) => setCptBillingRuleForm({
                      ...cptBillingRuleForm,
                      override_modifiers: e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean),
                    })}
                    placeholder="Leave blank unless you want to replace the entire modifier set"
                  />
                </div>
                <div>
                  <Label className="text-xs">Remove Modifiers</Label>
                  <Input
                    value={(cptBillingRuleForm.remove_modifiers || []).join(',')}
                    onChange={(e) => setCptBillingRuleForm({
                      ...cptBillingRuleForm,
                      remove_modifiers: e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean),
                    })}
                    placeholder="e.g., GT"
                  />
                </div>
              </>
            )}

            {cptBillingRuleForm.action_type === 'replace_cpt' && (
              <div>
                <Label className="text-xs">Replacement CPT Code *</Label>
                <Input
                  value={cptBillingRuleForm.replacement_cpt_code}
                  onChange={(e) => setCptBillingRuleForm({ ...cptBillingRuleForm, replacement_cpt_code: e.target.value.toUpperCase() })}
                  placeholder="e.g., H2019"
                />
              </div>
            )}

            {cptBillingRuleForm.action_type === 'block' && (
              <div>
                <Label className="text-xs">Block Reason *</Label>
                <Input
                  value={cptBillingRuleForm.block_reason}
                  onChange={(e) => setCptBillingRuleForm({ ...cptBillingRuleForm, block_reason: e.target.value })}
                  placeholder="Shown to the biller when claim is held"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                checked={cptBillingRuleForm.is_active}
                onCheckedChange={(v) => setCptBillingRuleForm({ ...cptBillingRuleForm, is_active: !!v })}
              />
              <Label className="text-xs">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCptBillingRuleDialog(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!cptBillingRuleForm.rule_name?.trim()) {
                  toast.error('Rule name is required');
                  return;
                }
                if (cptBillingRuleForm.action_type === 'block' && !cptBillingRuleForm.block_reason?.trim()) {
                  toast.error('Block reason is required for "block" action');
                  return;
                }
                if (cptBillingRuleForm.action_type === 'replace_cpt' && !cptBillingRuleForm.replacement_cpt_code?.trim()) {
                  toast.error('Replacement CPT code is required for "replace_cpt" action');
                  return;
                }
                try {
                  if (editingCptBillingRule) {
                    await axios.put(`${API}/billing/rules/cpt/${editingCptBillingRule.id}`, cptBillingRuleForm);
                    toast.success('Billing rule updated');
                  } else {
                    await axios.post(`${API}/billing/rules/cpt`, cptBillingRuleForm);
                    toast.success('Billing rule created');
                  }
                  setShowCptBillingRuleDialog(false);
                  fetchRules();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to save billing rule');
                }
              }}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="cpt-billing-rule-save-button"
            >
              {editingCptBillingRule ? 'Update' : 'Create'} Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTreatmentPlanDialog} onOpenChange={setShowTreatmentPlanDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Sign & Send Treatment Plan</DialogTitle>
            <DialogDescription>
              Sign this treatment plan and send it to the client for their signature. By default, it will be automatically sent after all staff signatures are complete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start space-x-2">
              <Checkbox 
                id="hold-from-client"
                checked={holdFromClient}
                onCheckedChange={setHoldFromClient}
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="hold-from-client"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Hold - Don't Send to Client Yet
                </Label>
                <p className="text-xs text-muted-foreground">
                  Check this if you need to coordinate with the client before sending for their signature (e.g., call them first, schedule appointment, etc.)
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTreatmentPlanDialog(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={async () => {
                let postSucceeded = false;
                try {
                  await axios.post(
                    `${API}/treatment-plans/${selectedTreatmentPlan.id}/complete`,
                    { hold_from_client: holdFromClient },
                    {
                      headers: {
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`
                      },
                      // Treatment plan completion can do a lot of work
                      // server-side (PAN/goal validation, diagnoses
                      // snapshot, supervisor fan-out, client portal
                      // hand-off). Give it a comfortable budget so a slow
                      // run doesn't fire the catch block while the backend
                      // is still working — that path showed the user a
                      // misleading "Failed to sign plan" toast even
                      // though the signing actually completed.
                      timeout: 120000,
                    }
                  );
                  postSucceeded = true;
                  toast.success(holdFromClient
                    ? 'Treatment plan signed (held from client)'
                    : 'Treatment plan signed and sent'
                  );
                  setShowTreatmentPlanDialog(false);
                  if (selectedClient?.id) {
                    await refetchTreatmentPlans(selectedClient.id);
                  }
                } catch (error) {
                  console.error('Error completing plan:', error);
                  if (postSucceeded) {
                    // Backend already accepted the sign — refresh problem
                    // only. Don't scare the user into re-clicking.
                    toast.warning('Plan signed — refresh failed. Reload the page to see the latest status.');
                    setShowTreatmentPlanDialog(false);
                  } else {
                    toast.error(error.response?.data?.detail || 'Failed to sign plan');
                  }
                }
              }}
            >
              <FileText className="h-4 w-4 mr-1" />
              Sign and Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* =================== TREATMENT PLANNING DIALOGS =================== */}
      
      {/* Duplicate Treatment Plan Detected Dialog */}
      <Dialog open={!!duplicatePlanDetected} onOpenChange={(open) => !open && setDuplicatePlanDetected(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="h-5 w-5" />
              Active Treatment Plan Exists
            </DialogTitle>
            <DialogDescription>
              {duplicatePlanDetected?.message || 'An active treatment plan already exists for this client with this provider and service type.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              To make changes, please use <strong>Minor Edit</strong> or <strong>Major Review</strong> on the existing plan.
            </p>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Service Type: {duplicatePlanDetected?.serviceType}</p>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setDuplicatePlanDetected(null)}
            >
              Cancel
            </Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700"
              onClick={async () => {
                // Close dialogs and navigate to the existing plan
                setDuplicatePlanDetected(null);
                setShowCreatePlanDialog(false);
                
                // Find and select the existing plan
                if (duplicatePlanDetected?.existingPlanId) {
                  const existingPlan = treatmentPlans.find(p => p.id === duplicatePlanDetected.existingPlanId);
                  if (existingPlan) {
                    setSelectedTreatmentPlan(existingPlan);
                    toast.info('Showing existing treatment plan. Use Minor Edit or Major Review to make changes.');
                  } else {
                    // Refetch plans and try to find it
                    await refetchTreatmentPlans(selectedClient?.id);
                    toast.info('Please select the existing treatment plan to edit it.');
                  }
                }
              }}
            >
              <FileText className="h-4 w-4 mr-2" />
              Go to Existing Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Treatment Plan Dialog - Streamlined */}
      <Dialog open={showCreatePlanDialog} onOpenChange={async (open) => {
        // Only allow opening via this handler, not closing (closing handled by X button and Cancel)
        if (open) {
        void 0 && console.log('Dialog onOpenChange called with open:', open);
        setShowCreatePlanDialog(open);
          void 0 && console.log('Dialog is opening - about to set defaults and fetch data');
          
          // Check if we're editing an existing plan (minor edit mode)
          // NOTE: PANs are now pre-loaded before opening the dialog in minor edit mode
          const isEditing = !!editingPlanForMinorEdit;
          
          if (!isEditing) {
            // Set defaults when opening for NEW plan
            // Default to current user if they're a provider (consistent with calendar behavior)
            const defaultProviderId = (user?.is_provider || userRoleName === 'provider' || userRoleName === 'supervisor') ? user.id : '';
            setNewPlanProviderId(defaultProviderId);
            setNewPlanServiceType(''); // Clear service type - will be filtered by provider
            setNewPlanStartDate(new Date().toISOString().split('T')[0]);
            setPlanPANs([]);
            setExpandedPANs(new Set());
          }
          // For minor edit, PANs are already loaded by the minor edit click handler
          
          setShowInlinePANSelector(false);
          setShowInlinePANGoalSelector(null);
          setShowInlineObjectiveForm(null);
          setClientServiceEnrollments([]); // Reset enrollments
          
          // Fetch service types, PANs, and client's service enrollments when dialog opens
          // In edit mode, use the plan's client_id; otherwise use selectedClient
          const clientIdForFetch = editingPlanForMinorEdit?.client_id || selectedClient?.id;
          
          try {
            void 0 && console.log('Starting to fetch service types, PANs, and client enrollments...');
            void 0 && console.log('API base URL:', API);
            void 0 && console.log('Token exists:', !!sessionStorage.getItem('token'));
            void 0 && console.log('Client ID for fetch:', clientIdForFetch);
            void 0 && console.log('Is editing:', isEditing);
            
            const requests = [
              axios.get(`${API}/treatment-planning/service-types`, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
              }),
              axios.get(`${API}/treatment-planning/pans`, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
              })
            ];
            
            // Also fetch client's service enrollments if we have a client ID
            if (clientIdForFetch) {
              requests.push(
                axios.get(`${API}/clients/${clientIdForFetch}/service-enrollments`, {
                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                })
              );
              // Also fetch treatment plans to check for duplicates
              requests.push(
                axios.get(`${API}/clients/${clientIdForFetch}/treatment-plans`, {
                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                })
              );
            }
            
            const responses = await Promise.all(requests);
            const [servicesRes, pansRes] = responses;
            
            void 0 && console.log('API Response - Service types:', servicesRes.data);
            void 0 && console.log('API Response - Service types count:', servicesRes.data.length);
            void 0 && console.log('API Response - PANs count:', pansRes.data.length);
            
            setServiceTypes(servicesRes.data);
            setAvailablePANs(pansRes.data);
            
            // Store client's service enrollments if fetched
            if (responses.length > 2) {
              const enrollmentsRes = responses[2];
              void 0 && console.log('API Response - Client enrollments:', enrollmentsRes.data);
              setClientServiceEnrollments(enrollmentsRes.data || []);
            }
            
            // Store treatment plans for duplicate checking
            if (responses.length > 3) {
              const plansRes = responses[3];
              void 0 && console.log('API Response - Treatment plans:', plansRes.data?.length || 0);
              setTreatmentPlans(plansRes.data || []);
            }
            
            setTpMetadataLoaded(true);
          } catch (error) {
            console.error('Error loading data:', error);
            console.error('Error details:', error.response?.data);
            toast.error('Failed to load form data');
          }
          
          // Prefill medications and diagnoses from already-loaded context data
          // (same pattern as the form builder's client_diagnoses / client_medications fields)
          void 0 && console.log('[TP Prefill] clientIdForFetch:', clientIdForFetch);
          void 0 && console.log('[TP Prefill] selectedClient:', selectedClient?.id, selectedClient?.full_name);
          void 0 && console.log('[TP Prefill] clientMedications count:', clientMedications?.length, 'sample:', clientMedications?.[0]);
          void 0 && console.log('[TP Prefill] clientDiagnoses count:', clientDiagnoses?.length, 'sample:', clientDiagnoses?.[0]);
          if (clientIdForFetch) {
            const activeMeds = (clientMedications || []).filter(m => m.client_id === clientIdForFetch && m.is_active === true);
            void 0 && console.log('[TP Prefill] activeMeds after filter:', activeMeds.length);
            setTpMedications(activeMeds.map(m => ({
              name: m.medication_name || m.name || '',
              dosage: m.dosage || '',
              frequency: m.frequency || '',
              prescriber: m.prescriber || '',
              purpose: m.purpose || '',
              status: 'active'
            })));
            const activeDiags = (clientDiagnoses || []).filter(d => d.client_id === clientIdForFetch && d.is_active !== false);
            void 0 && console.log('[TP Prefill] activeDiags after filter:', activeDiags.length);
            setTpDiagnoses(activeDiags.map(d => ({
              code: d.code || d.diagnosis_code || '',
              description: d.description || d.diagnosis_description || d.diagnosis_name || '',
              status: d.status || 'active',
              is_primary: d.is_primary || false,
              assigned_by: d.assigned_by || '',
              provider_names: d.provider_names || [],
              assigned_by_name: d.assigned_by_name || ''
            })));
          }
        } else {
          // Ignore close from outside click - only X button and Cancel can close
          return;
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0 [&>button[class*='absolute']]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <div ref={treatmentPlanDialogRef} className="overflow-y-auto max-h-[90vh] p-6">
          {/* Custom close button with confirmation */}
          <button
            type="button"
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10"
            onClick={() => {
              const hasData = planPANs.length > 0 || newPlanProviderId || newPlanServiceType;
              if (hasData) {
                if (confirm('You have unsaved changes. Are you sure you want to close? Your draft will be lost.')) {
                  setShowCreatePlanDialog(false);
                  setEditingPlanForMinorEdit(null);
                  setIsMajorReviewEdit(false);
                  setNewPlanServiceType('');
                  setNewPlanProviderId('');
                  setNewPlanVisitFrequency('');
                  setNewPlanStartDate('');
                  setPlanPANs([]);
                  setPendingInterventions({});
                }
              } else {
                setShowCreatePlanDialog(false);
                setEditingPlanForMinorEdit(null);
                setIsMajorReviewEdit(false);
              }
            }}
          >
            <X className="h-4 w-4" />
          </button>
          <DialogHeader>
            <DialogTitle>{editingPlanForMinorEdit ? (isMajorReviewEdit ? 'Major Review - Treatment Plan' : 'Edit Treatment Plan (Minor Edit)') : 'Create Treatment Plan'}</DialogTitle>
            <DialogDescription>
              {editingPlanForMinorEdit 
                ? (isMajorReviewEdit ? 'Create a new version of this treatment plan. This requires new signatures from all parties.' : 'Make minor edits to this treatment plan. Changes will increment the version number but not require new signatures.')
                : 'Create a new treatment plan with service type, provider assignment, and PANs'}
            </DialogDescription>
          </DialogHeader>
          {/* Minor Edit info banner */}
          {editingPlanForMinorEdit && !isMajorReviewEdit && editingPlanForMinorEdit.status !== 'draft' && editingPlanForMinorEdit.status !== 'needs_corrections' && (
            <div className="mx-1 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800" data-testid="minor-edit-banner">
              <p className="font-medium mb-1">Minor Edit Mode</p>
              <p className="text-blue-700 text-xs">Use this for typos, spelling corrections, or small clarifications that don't change meaning. Existing signatures will be preserved.</p>
              <p className="text-blue-700 text-xs mt-1">Changes to goals, objectives, interventions, or clinical content require a <span className="font-semibold">Treatment Plan Review</span> instead.</p>
            </div>
          )}
          <div className="space-y-4 py-4">
            {!planFieldsCollapsed && (
            <>
            {/* Provider & Service Type - Side by side */}
            <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="assigned-provider">Assigned Provider *</Label>
              <Select
                value={newPlanProviderId || ''}
                onValueChange={(value) => {
                  setNewPlanProviderId(value);
                  setNewPlanServiceType(''); // Reset service type when provider changes
                }}
              >
                <SelectTrigger id="assigned-provider">
                  <SelectValue placeholder="Select provider first" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Get providers who have active enrollments with this client
                    const providersWithEnrollments = clientServiceEnrollments
                      .filter(e => e.status === 'assigned' || e.status === 'active')
                      .map(e => e.provider_id)
                      .filter((v, i, a) => a.indexOf(v) === i); // unique
                    
                    // In edit mode, always include the currently assigned provider
                    const currentProviderId = editingPlanForMinorEdit?.assigned_provider_id;
                    if (currentProviderId && !providersWithEnrollments.includes(currentProviderId)) {
                      providersWithEnrollments.push(currentProviderId);
                    }
                    
                    // Get all providers with enrollments
                    let eligibleProviders = users.filter(u => 
                      u.is_provider && providersWithEnrollments.includes(u.id)
                    );
                    
                    // Apply permission-based filtering (same logic as calendar)
                    // Admin/Office can see all providers with enrollments
                    // Providers can only see themselves (and supervisees if they're a supervisor)
                    // BUT: In edit mode, always allow viewing the current provider
                    if (!isSuperAdmin(user) && userRoleName !== 'office') {
                      if (userRoleName === 'provider' || user?.is_provider) {
                        // Get this user's supervisees
                        const superviseeIds = users
                          .filter(u => u.supervisor_ids && u.supervisor_ids.includes(user.id))
                          .map(u => u.id);
                        
                        // Filter to only show: themselves + supervisees (if any) + current provider in edit mode
                        eligibleProviders = eligibleProviders.filter(p => 
                          p.id === user.id || 
                          superviseeIds.includes(p.id) ||
                          (editingPlanForMinorEdit && p.id === currentProviderId)
                        );
                      }
                    }
                    
                    if (eligibleProviders.length === 0) {
                      return (
                        <SelectItem value="none" disabled>
                          No providers available for this client
                        </SelectItem>
                      );
                    }
                    
                    // Sort: current user first, then alphabetically
                    eligibleProviders.sort((a, b) => {
                      if (a.id === user?.id) return -1;
                      if (b.id === user?.id) return 1;
                      return (a.first_name + ' ' + a.last_name).localeCompare(b.first_name + ' ' + b.last_name);
                    });
                    
                    return eligibleProviders.map(provider => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {formatProviderName(provider)}
                        {provider.id === user?.id ? ' (You)' : ''}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {isSuperAdmin(user) || userRoleName === 'office' 
                  ? 'All providers with active enrollments for this client' 
                  : 'Providers you can create treatment plans for'}
              </p>
            </div>
            
            {/* Service Type Selection */}
            <div>
              <Label htmlFor="service-type">Service Type *</Label>
              <Select
                value={newPlanServiceType || ''}
                onValueChange={async (value) => {
                  setNewPlanServiceType(value);
                  
                  // Auto-fill start date from matching service enrollment if available
                  if (clientServiceEnrollments && clientServiceEnrollments.length > 0) {
                    const matchingEnrollment = clientServiceEnrollments.find(e => 
                      e.service_type === value && e.start_date
                    );
                    if (matchingEnrollment?.start_date) {
                      void 0 && console.log('[Treatment Plan] Auto-filling start date from enrollment:', matchingEnrollment.start_date);
                      // Extract just the date part if it's a full timestamp
                      const enrollmentDate = matchingEnrollment.start_date.split('T')[0];
                      setNewPlanStartDate(enrollmentDate);
                    }
                  }
                  
                  // Check for existing active treatment plan with this provider + service combination
                  if (selectedClient?.id && newPlanProviderId && value) {
                    try {
                      void 0 && console.log('[Duplicate Check] Checking for existing plans...');
                      void 0 && console.log('[Duplicate Check] Provider:', newPlanProviderId);
                      void 0 && console.log('[Duplicate Check] Service Type:', value);
                      void 0 && console.log('[Duplicate Check] Available plans:', treatmentPlans.length);
                      
                      // Check if there's already an active plan for this combination
                      const existingPlans = treatmentPlans.filter(plan => {
                        const providerMatch = plan.assigned_provider_id === newPlanProviderId;
                        const serviceMatch = plan.service_type === value;
                        // is_current_version might be null/undefined for older plans - treat as current if not explicitly false
                        const isCurrent = plan.is_current_version !== false;
                        const isActiveStatus = ['active', 'pending_signatures', 'draft', 'sent_to_client_for_signature'].includes(plan.status);
                        
                        void 0 && console.log(`[Duplicate Check] Plan ${plan.id}: provider=${providerMatch}, service=${serviceMatch}, current=${isCurrent}, activeStatus=${isActiveStatus}`);
                        
                        return providerMatch && serviceMatch && isCurrent && isActiveStatus;
                      });
                      
                      void 0 && console.log('[Duplicate Check] Found existing plans:', existingPlans.length);
                      
                      if (existingPlans.length > 0) {
                        const existingPlan = existingPlans[0];
                        // Find the service name from enrollments
                        const enrollment = clientServiceEnrollments.find(e => e.service_type === value);
                        const serviceName = enrollment?.service_name || value;
                        setDuplicatePlanDetected({
                          existingPlanId: existingPlan.id,
                          serviceType: serviceName,
                          message: `An active treatment plan already exists for this client with this provider and service type.`
                        });
                      }
                    } catch (error) {
                      console.error('Error checking for duplicate plans:', error);
                    }
                  }
                }}
                disabled={!newPlanProviderId}
              >
                <SelectTrigger id="service-type">
                  <SelectValue placeholder={newPlanProviderId ? "Select service type" : "Select provider first"}>
                    {newPlanServiceType && (() => {
                      // Find matching enrollment to get display name
                      const matchingEnrollment = clientServiceEnrollments?.find(e => e.service_type === newPlanServiceType);
                      if (matchingEnrollment) {
                        return matchingEnrollment.service_name || newPlanServiceType?.replace(/_/g, ' ');
                      }
                      // Check if it looks like a UUID (contains dashes and is 36 chars)
                      if (newPlanServiceType?.length === 36 && newPlanServiceType?.includes('-')) {
                        const anyServiceType = serviceTypes?.find(st => st.id === newPlanServiceType);
                        if (anyServiceType) return anyServiceType.name;
                        return 'Unknown Service Type';
                      }
                      // Just format the service type name nicely
                      return newPlanServiceType?.replace(/_/g, ' ');
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    if (!newPlanProviderId) {
                      return (
                        <SelectItem value="none" disabled>
                          Please select a provider first
                        </SelectItem>
                      );
                    }
                    
                    // Get service types from this provider's enrollments with this client
                    const providerEnrollments = clientServiceEnrollments.filter(
                      e => e.provider_id === newPlanProviderId && 
                           (e.status === 'assigned' || e.status === 'active')
                    );
                    
                    // In edit mode, include the current service type even if no enrollment exists
                    const currentServiceType = editingPlanForMinorEdit?.service_type;
                    const hasCurrentServiceType = currentServiceType && 
                      !providerEnrollments.some(e => e.service_type === currentServiceType);
                    
                    if (providerEnrollments.length === 0 && !hasCurrentServiceType) {
                      return (
                        <SelectItem value="none" disabled>
                          No service enrollments for this provider
                        </SelectItem>
                      );
                    }
                    
                    // Get unique service type names from enrollments
                    const enrolledServiceTypes = providerEnrollments.map(e => e.service_type);
                    
                    // Build options list
                    const options = [];
                    
                    // Add current service type first if editing and not in enrollments
                    if (hasCurrentServiceType) {
                      // Look up the service type name from serviceTypes array
                      const currentServiceTypeObj = serviceTypes?.find(st => st.id === currentServiceType);
                      const displayName = currentServiceTypeObj?.name || currentServiceTypeObj?.display_name || 
                        (currentServiceType?.length === 36 && currentServiceType?.includes('-') 
                          ? 'Unknown Service Type' 
                          : currentServiceType?.replace(/_/g, ' '));
                      options.push(
                        <SelectItem key="current" value={currentServiceType}>
                          {displayName} (current)
                        </SelectItem>
                      );
                    }
                    
                    // Add enrolled service types
                    providerEnrollments.forEach(enrollment => {
                      options.push(
                        <SelectItem key={enrollment.id || enrollment.service_type} value={enrollment.service_type}>
                          {enrollment.service_name || enrollment.service_type?.replace(/_/g, ' ').toUpperCase() || enrollment.service_type}
                        </SelectItem>
                      );
                    });
                    
                    return options;
                  })()}
                </SelectContent>
              </Select>
              {newPlanServiceType && (
                <p className="text-xs text-teal-600 mt-1">
                  Active enrollment found
                </p>
              )}
            </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="visit-frequency">Visit Frequency *</Label>
                <Select
                  value={newPlanVisitFrequency || ''}
                  onValueChange={(value) => setNewPlanVisitFrequency(value)}
                >
                  <SelectTrigger id="visit-frequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="more_than_weekly">More than once a week</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="bi-weekly">Bi-weekly (Every 2 weeks)</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="as-needed">As Needed</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  How often client is seen
                </p>
              </div>
              
              <div>
                <Label htmlFor="start-date">Service Start Date *</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={newPlanStartDate || ''}
                  onChange={(e) => setNewPlanStartDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Date client began this service (intake date)
                </p>
              </div>
            </div>
            
            {/* Legacy Plan Checkbox */}
            <div className="flex items-center space-x-2 px-2 py-1.5 bg-amber-50/60 border border-amber-100 rounded text-xs">
              <Checkbox
                id="legacy-plan"
                checked={isLegacyPlan}
                onCheckedChange={(checked) => setIsLegacyPlan(checked)}
                className="h-3.5 w-3.5"
              />
              <Label htmlFor="legacy-plan" className="text-xs cursor-pointer text-amber-800">
                Legacy Plan - Skip Signature Requirement
              </Label>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setPlanFieldsCollapsed(true)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-teal-600 transition-colors"
              >
                <ChevronUp className="h-3.5 w-3.5" />
                <span>Hide Details</span>
              </button>
            </div>
            </>
            )}

            {planFieldsCollapsed && (
              <div className="flex items-center justify-between px-2 py-1.5 bg-gray-50 rounded border text-xs text-gray-500">
                <div className="flex items-center gap-2 truncate">
                  {newPlanProviderId && (
                    <span className="font-medium text-gray-700">{users.find(u => u.id === newPlanProviderId)?.first_name} {users.find(u => u.id === newPlanProviderId)?.last_name}</span>
                  )}
                  {newPlanServiceType && <span className="text-gray-300">|</span>}
                  {newPlanServiceType && (
                    <span>{clientServiceEnrollments?.find(e => e.service_type === newPlanServiceType)?.service_name || newPlanServiceType?.replace(/_/g, ' ')}</span>
                  )}
                  {newPlanVisitFrequency && <span className="text-gray-300">|</span>}
                  {newPlanVisitFrequency && <span>{newPlanVisitFrequency?.replace(/_/g, ' ')}</span>}
                </div>
                <button
                  type="button"
                  onClick={() => setPlanFieldsCollapsed(false)}
                  className="flex items-center gap-1 text-gray-400 hover:text-teal-600 transition-colors shrink-0 ml-2"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                  <span>Show Details</span>
                </button>
              </div>
            )}
            
            {/* PANs Section */}
            {isMajorReviewEdit && (
              <div className="bg-orange-50 p-2.5 rounded-lg text-xs text-orange-800 border border-orange-200">
                <p className="font-semibold mb-1">Major Review Mode</p>
                <p>Update goal statuses inline below each goal. Goals marked "Met" or "Discontinued" will be archived. You can also add new PANs/goals or archive existing ones.</p>
                <div className="mt-2 flex items-center gap-2">
                  <Label className="text-xs font-semibold text-orange-900 whitespace-nowrap">Date of review</Label>
                  <Input
                    type="date"
                    className="h-8 w-44 bg-white text-gray-900"
                    data-testid="major-review-date-input"
                    value={majorReviewDate}
                    onChange={(e) => setMajorReviewDate(e.target.value)}
                  />
                  <span className="text-[11px] text-orange-700">Next review is calculated from this date.</span>
                </div>
              </div>
            )}
            <div ref={panSectionRef} className="border-t pt-4">
              {planPANs.length === 0 ? (
                <Label className="text-sm text-gray-500">PANs (Prioritized Assessed Needs)</Label>
              ) : (
                <div className="space-y-4">
                  {planPANs.map((pan, panIdx) => (
                    <Card key={panIdx} className="border border-teal-200">
                      <div className="flex items-center gap-2 px-3 py-2 bg-teal-50 border-b border-teal-200 rounded-t-lg">
                        <span className="text-xs font-semibold text-teal-700">PAN {panIdx + 1}</span>
                        <span className="text-sm font-medium text-gray-900">{pan.pan_name}</span>
                        {pan.goals && pan.goals.length > 0 && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {pan.goals.length} goal{pan.goals.length > 1 ? 's' : ''}
                          </Badge>
                        )}
                        {isMajorReviewEdit && (
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm(`Archive PAN "${pan.pan_name}"? It will not carry forward to the new version.`)) {
                                setPlanPANs(planPANs.filter((_, idx) => idx !== panIdx));
                              }
                            }}
                            className="text-orange-500 hover:text-orange-700 text-xs flex items-center gap-0.5"
                            title="Archive this PAN"
                            data-testid={`archive-pan-${panIdx}`}
                          >
                            <Archive className="h-3 w-3" /> Archive
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setPlanPANs(planPANs.filter((_, idx) => idx !== panIdx));
                          }}
                          className="ml-auto text-red-400 hover:text-red-600"
                          title="Remove PAN"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      
                      <CardContent className="p-3 space-y-3">
                        {/* Goal Section */}
                        <div>
                          {/* Check if we're in inline edit mode for this PAN's goals */}
                          {showInlinePANGoalSelector === panIdx ? (
                            <div className="border border-dashed border-gray-200 rounded p-2">
                              {/* Show inline goal selector for adding/replacing goals */}
                              {!selectedGoalTemplate && !goalCustomization.isCustomGoal && goalCustomization.customized_text === '' ? (
                                <div className="space-y-2">
                                  <Label className="text-xs font-semibold block">Select a Goal Template or Create Custom</Label>
                                  
                                  {/* Create Custom Goal Option */}
                                  <div
                                    className="cursor-pointer hover:bg-teal-50 transition-colors border border-dashed border-teal-400 rounded px-2 py-1.5"
                                    onClick={() => {
                                      setSelectedGoalTemplate(null);
                                      setGoalCustomization({
                                        customized_text: '',
                                        client_goal_description: '',
                                        client_barriers: '',
                                        client_strengths: '',
                                        duration_days: 90,
                                        custom_duration: '',
                                        notes: '',
                                        isCustomGoal: true
                                      });
                                    }}
                                  >
                                    <div className="flex items-center gap-1.5 text-teal-700">
                                      <Plus className="h-3 w-3" />
                                      <p className="text-xs font-medium">Create Custom Goal (No Template)</p>
                                    </div>
                                  </div>
                                  
                                  {/* Goal Templates */}
                                  {availableGoals.length > 0 && (
                                    <>
                                      <Label className="text-[11px] text-gray-500 block">OR select from templates:</Label>
                                      <div className="space-y-1 max-h-[200px] overflow-y-auto">
                                        {availableGoals.map(goal => (
                                          <div
                                            key={goal.id}
                                            className="cursor-pointer hover:bg-blue-50 transition-colors border-l-2 border-blue-500 px-2 py-1.5 rounded-r text-xs"
                                            onClick={() => {
                                              setSelectedGoalTemplate(goal);
                                              setGoalCustomization({
                                                customized_text: goal.goal_text,
                                                client_goal_description: '',
                                                client_barriers: '',
                                                client_strengths: '',
                                                duration_days: 90,
                                                custom_duration: '',
                                                notes: ''
                                              });
                                            }}
                                          >
                                            {goal.goal_text}
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                  
                                  <button
                                    type="button"
                                    className="text-xs text-gray-500 hover:text-gray-700"
                                    onClick={() => setShowInlinePANGoalSelector(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (selectedGoalTemplate || goalCustomization.isCustomGoal || goalCustomization.customized_text.trim() !== '') ? (
                                /* Goal Customization Form - compact inline */
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        className="text-xs text-gray-400 hover:text-gray-600"
                                        onClick={() => {
                                          setSelectedGoalTemplate(null);
                                          setGoalCustomization({
                                            customized_text: '',
                                            client_goal_description: '',
                                            client_barriers: '',
                                            client_strengths: '',
                                            duration_days: 90,
                                            custom_duration: '',
                                            notes: '',
                                            isCustomGoal: false
                                          });
                                        }}
                                      >
                                        ← Back
                                      </button>
                                      {selectedGoalTemplate && (
                                        <span className="text-[11px] text-gray-500 truncate max-w-[300px]">
                                          Template: {selectedGoalTemplate.goal_text}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <Label className="text-xs">Goal Text *</Label>
                                    <DebouncedTextarea
                                      value={goalCustomization.customized_text || ''}
                                      onChange={(e) => setGoalCustomization(prev => ({...prev, customized_text: e.target.value}))}
                                      placeholder="Clinical/professional goal statement..."
                                      rows={2}
                                      className="mt-1 text-sm"
                                    />
                                  </div>
                                  
                                  <div>
                                    <Label className="text-xs">Client's Goal (in their words)</Label>
                                    <DebouncedTextarea
                                      value={goalCustomization.client_goal_description || ''}
                                      onChange={(e) => setGoalCustomization(prev => ({...prev, client_goal_description: e.target.value}))}
                                      placeholder="How does the client describe this goal?"
                                      rows={2}
                                      className="mt-1 text-sm"
                                    />
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <Label className="text-xs">Barriers</Label>
                                      <DebouncedTextarea
                                        value={goalCustomization.client_barriers}
                                        onChange={(e) => setGoalCustomization(prev => ({...prev, client_barriers: e.target.value}))}
                                        placeholder="Barriers to achieving goal"
                                        rows={2}
                                        className="mt-1 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Strengths</Label>
                                      <DebouncedTextarea
                                        value={goalCustomization.client_strengths}
                                        onChange={(e) => setGoalCustomization(prev => ({...prev, client_strengths: e.target.value}))}
                                        placeholder="Client strengths"
                                        rows={2}
                                        className="mt-1 text-sm"
                                      />
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <Label className="text-xs">Duration *</Label>
                                      <Select
                                        value={goalCustomization.duration_days === 'custom' ? 'custom' : String(goalCustomization.duration_days)}
                                        onValueChange={(value) => {
                                          if (value === 'custom') {
                                            setGoalCustomization(prev => ({...prev, duration_days: 'custom', custom_duration: ''}));
                                          } else {
                                            setGoalCustomization(prev => ({...prev, duration_days: parseInt(value), custom_duration: ''}));
                                          }
                                        }}
                                      >
                                        <SelectTrigger className="h-8 text-xs">
                                          <SelectValue placeholder="Select duration" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="30">30 days</SelectItem>
                                          <SelectItem value="60">60 days</SelectItem>
                                          <SelectItem value="90">90 days</SelectItem>
                                          <SelectItem value="180">180 days</SelectItem>
                                          <SelectItem value="365">365 days</SelectItem>
                                          <SelectItem value="custom">Custom</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    {goalCustomization.duration_days === 'custom' && (
                                      <div>
                                        <Label className="text-xs">Custom Days</Label>
                                        <Input
                                          type="number"
                                          value={goalCustomization.custom_duration}
                                          onChange={(e) => setGoalCustomization(prev => ({...prev, custom_duration: e.target.value}))}
                                          placeholder="Days"
                                          className="h-8 text-xs"
                                        />
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="flex gap-2 pt-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setShowInlinePANGoalSelector(null);
                                        setSelectedGoalTemplate(null);
                                        setGoalCustomization({
                                          customized_text: '',
                                          client_goal_description: '',
                                          client_barriers: '',
                                          client_strengths: '',
                                          duration_days: 90,
                                          custom_duration: '',
                                          notes: '',
                                          isCustomGoal: false
                                        });
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={() => {
                                        if (!goalCustomization.customized_text.trim()) {
                                          toast.error('Please enter goal text');
                                          return;
                                        }
                                        if (!goalCustomization.duration_days || (goalCustomization.duration_days === 'custom' && !goalCustomization.custom_duration)) {
                                          toast.error('Please select a duration');
                                          return;
                                        }
                                        
                                        const goalData = {
                                          goal_text: goalCustomization.customized_text.trim(),
                                          client_goal_description: goalCustomization.client_goal_description?.trim() || '',
                                          client_barriers: goalCustomization.client_barriers?.trim() || '',
                                          client_strengths: goalCustomization.client_strengths?.trim() || '',
                                          duration_days: goalCustomization.duration_days === 'custom' 
                                            ? parseInt(goalCustomization.custom_duration) 
                                            : goalCustomization.duration_days,
                                          notes: goalCustomization.notes?.trim() || '',
                                          template_id: selectedGoalTemplate?.id || null,
                                          objectives: []
                                        };
                                        
                                        const updatedPANs = [...planPANs];
                                        if (goalCustomization.editingExistingGoalIdx !== undefined) {
                                          // Editing existing goal - preserve its objectives
                                          const existingGoal = updatedPANs[panIdx].goals[goalCustomization.editingExistingGoalIdx];
                                          goalData.objectives = existingGoal?.objectives || [];
                                          updatedPANs[panIdx].goals = updatedPANs[panIdx].goals.map((g, gIdx) =>
                                            gIdx === goalCustomization.editingExistingGoalIdx ? goalData : g
                                          );
                                        } else {
                                          // Adding new goal - append to existing goals
                                          updatedPANs[panIdx].goals = [...(updatedPANs[panIdx].goals || []), goalData];
                                        }
                                        setPlanPANs(updatedPANs);
                                        toast.success(goalCustomization.editingExistingGoalIdx !== undefined ? 'Goal updated' : 'Goal added to plan');
                                        
                                        setShowInlinePANGoalSelector(null);
                                        setSelectedGoalTemplate(null);
                                        setGoalCustomization({
                                          customized_text: '',
                                          client_goal_description: '',
                                          client_barriers: '',
                                          client_strengths: '',
                                          duration_days: 90,
                                          custom_duration: '',
                                          notes: '',
                                          isCustomGoal: false
                                        });
                                      }}
                                    >
                                      Add Goal
                                    </Button>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ) : (!pan.goals || pan.goals.length === 0) ? (
                            <div className="border-2 border-dashed border-gray-200 rounded p-3">
                              {/* Default: Show "No goal set" message with Add Goal button */}
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">No goal set for this PAN</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    setSelectedPANForGoal({ ...pan, index: panIdx });
                                    setShowInlinePANGoalSelector(panIdx);
                                    
                                    try {
                                      const response = await axios.get(
                                        `${API}/treatment-planning/pans/${pan.pan_id}/goals`,
                                        {
                                          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                        }
                                      );
                                      setAvailableGoals(response.data);
                                    } catch (error) {
                                      console.error('Error fetching goals:', error);
                                      toast.error('Failed to load goal templates');
                                    }
                                  }}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add Goal
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between mb-1">
                                <Label className="text-xs font-medium text-gray-700">Goals</Label>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  data-testid={`add-goal-btn-pan-${panIdx}`}
                                  onClick={async () => {
                                    setSelectedPANForGoal({ ...pan, index: panIdx });
                                    setShowInlinePANGoalSelector(panIdx);
                                    setGoalCustomization({
                                      customized_text: '',
                                      client_goal_description: '',
                                      client_barriers: '',
                                      client_strengths: '',
                                      duration_days: 90,
                                      custom_duration: '',
                                      notes: '',
                                      isCustomGoal: false
                                    });
                                    setSelectedGoalTemplate(null);
                                    try {
                                      const response = await axios.get(
                                        `${API}/treatment-planning/pans/${pan.pan_id}/goals`,
                                        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                                      );
                                      setAvailableGoals(response.data);
                                    } catch (error) {
                                      console.error('Error fetching goals:', error);
                                      toast.error('Failed to load goal templates');
                                    }
                                  }}
                                  className="h-6 text-xs"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add Goal
                                </Button>
                              </div>
                              {pan.goals.map((goal, goalIdx) => (
                                <Card key={goalIdx} className="border-blue-200 bg-blue-50">
                                  <CardContent className="p-3">
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex-1">
                                        <p className="text-sm font-medium"><span className="text-xs font-bold text-blue-700 mr-1.5">Goal #{goalIdx + 1}:</span>{goal.goal_text}</p>
                                        {goal.client_goal_description && (
                                          <p className="text-xs text-gray-500 italic mt-0.5">Client's words: "{goal.client_goal_description}"</p>
                                        )}
                                        {(goal.client_barriers || goal.client_strengths) && (
                                          <div className="flex gap-3 mt-1 text-xs text-gray-600">
                                            {goal.client_barriers && (
                                              <span><strong>Barriers:</strong> {goal.client_barriers}</span>
                                            )}
                                            {goal.client_strengths && (
                                              <span><strong>Strengths:</strong> {goal.client_strengths}</span>
                                            )}
                                          </div>
                                        )}
                                        {goal.duration_days && newPlanStartDate && (
                                          <p className="text-xs text-teal-600 mt-1 font-medium">
                                            Duration: {goal.duration_days} days | Expected End: {
                                              (() => {
                                                const startDate = new Date(newPlanStartDate);
                                                const endDate = new Date(startDate);
                                                endDate.setDate(endDate.getDate() + goal.duration_days);
                                                return endDate.toLocaleDateString();
                                              })()
                                            }
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex gap-1 ml-2">
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            // Pre-populate with existing goal data for editing
                                            setSelectedPANForGoal({ ...pan, index: panIdx });
                                            setGoalCustomization({
                                              customized_text: goal.goal_text || '',
                                              client_goal_description: goal.client_goal_description || '',
                                              client_barriers: goal.client_barriers || '',
                                              client_strengths: goal.client_strengths || '',
                                              duration_days: goal.duration_days || 90,
                                              custom_duration: '',
                                              notes: goal.notes || '',
                                              isCustomGoal: true, // Treat as custom so it shows the edit form
                                              editingExistingGoalIdx: goalIdx // Track that we're editing, not creating
                                            });
                                            setShowInlinePANGoalSelector(panIdx);
                                            
                                            // Fetch available goals for this PAN
                                            try {
                                              const response = await axios.get(
                                                `${API}/treatment-planning/pans/${pan.pan_id}/goals`,
                                                {
                                                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                                }
                                              );
                                              setAvailableGoals(response.data);
                                            } catch (error) {
                                              console.error('Error fetching goals:', error);
                                              toast.error('Failed to load goal templates');
                                            }
                                          }}
                                          className="text-blue-600 hover:text-blue-800"
                                          title="Edit goal"
                                        >
                                          <Edit className="h-3 w-3" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updatedPANs = [...planPANs];
                                            updatedPANs[panIdx].goals = [];
                                            setPlanPANs(updatedPANs);
                                          }}
                                          className="text-red-600 hover:text-red-800"
                                          title="Remove goal"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </div>
                                    </div>
                                    
                                    {/* Inline Goal Status (Major Review only) */}
                                    {isMajorReviewEdit && (() => {
                                      const goalId = goal.goal_id || goal.id;
                                      const progressEntry = majorReviewGoalProgress.find(g => g.goal_id === goalId);
                                      const progressIdx = majorReviewGoalProgress.findIndex(g => g.goal_id === goalId);
                                      const isArchived = progressEntry?.progress_status === 'met' || progressEntry?.progress_status === 'discontinued';
                                      return (
                                        <div className={`mt-2 p-2 rounded-lg border ${isArchived ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
                                          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                                            <div>
                                              <label className="text-[11px] font-medium text-gray-600 block mb-0.5">Goal Status</label>
                                              <select
                                                value={progressEntry?.progress_status || ''}
                                                onChange={(e) => {
                                                  const status = e.target.value;
                                                  const updated = [...majorReviewGoalProgress];
                                                  if (progressIdx >= 0) {
                                                    updated[progressIdx].progress_status = status;
                                                  } else {
                                                    updated.push({
                                                      goal_id: goalId,
                                                      goal_text: goal.goal_text,
                                                      pan_name: pan.pan_name,
                                                      progress_status: status,
                                                      progress_notes: ''
                                                    });
                                                  }
                                                  setMajorReviewGoalProgress(updated);
                                                }}
                                                className="w-full p-1.5 border rounded text-xs bg-white"
                                                data-testid={`goal-status-${panIdx}-${goalIdx}`}
                                              >
                                                <option value="">No change</option>
                                                <option value="met">Met / Completed</option>
                                                <option value="partial">Partial Progress</option>
                                                <option value="ongoing">Ongoing - Continue</option>
                                                <option value="no_progress">No Progress</option>
                                                <option value="discontinued">Discontinued</option>
                                                <option value="modified">Modified for Next Period</option>
                                              </select>
                                            </div>
                                            <div>
                                              <label className="text-[11px] font-medium text-gray-600 block mb-0.5">Progress Notes</label>
                                              <input
                                                type="text"
                                                value={progressEntry?.progress_notes || ''}
                                                onChange={(e) => {
                                                  const updated = [...majorReviewGoalProgress];
                                                  if (progressIdx >= 0) {
                                                    updated[progressIdx].progress_notes = e.target.value;
                                                  } else {
                                                    updated.push({
                                                      goal_id: goalId,
                                                      goal_text: goal.goal_text,
                                                      pan_name: pan.pan_name,
                                                      progress_status: '',
                                                      progress_notes: e.target.value
                                                    });
                                                  }
                                                  setMajorReviewGoalProgress(updated);
                                                }}
                                                placeholder="Brief note..."
                                                className="w-full p-1.5 border rounded text-xs"
                                                data-testid={`goal-notes-${panIdx}-${goalIdx}`}
                                              />
                                            </div>
                                            {isArchived && (
                                              <Badge className="bg-red-100 text-red-700 text-[10px] whitespace-nowrap h-fit">
                                                Will archive
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                    
                                    {/* Objectives Section */}
                                    <div className="mt-3 pt-2 border-t border-blue-200">
                                      <div className="flex items-center justify-between mb-2">
                                        <Label className="text-xs font-medium text-gray-700">Objectives</Label>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => {
                                            setEditingObjectiveIndex(null); // Reset - adding new, not editing
                                            setShowInlineObjectiveForm(panIdx + '-' + goalIdx);
                                            // Clear any pre-existing pending interventions
                                            setPendingInterventions(prev => {
                                              const updated = { ...prev };
                                              delete updated[`${panIdx}-${goalIdx}`];
                                              return updated;
                                            });
                                            // Scroll to objective form within dialog container
                                            setTimeout(() => {
                                              if (objectiveFormRef.current && treatmentPlanDialogRef.current) {
                                                const dialogContent = treatmentPlanDialogRef.current;
                                                const objForm = objectiveFormRef.current;
                                                const offsetTop = objForm.offsetTop - 20;
                                                dialogContent.scrollTo({ top: offsetTop, behavior: 'smooth' });
                                              }
                                            }, 100);
                                          }}
                                          className="h-6 text-xs"
                                        >
                                          <Plus className="h-3 w-3 mr-1" />
                                          Add Objective
                                        </Button>
                                      </div>
                                      
                                      {(!goal.objectives || goal.objectives.length === 0) ? (
                                        <p className="text-xs text-gray-500 italic">No objectives added yet</p>
                                      ) : (
                                        <div className="space-y-1">
                                          {goal.objectives.map((objective, objIdx) => (
                                            <div key={objIdx} className="bg-white p-2 rounded border">
                                              <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                  <p className="text-xs font-medium">
                                                    <span className="text-blue-600 font-bold">Objective #{objIdx + 1}:</span>
                                                    {objective.responsible_party && (
                                                      <span className="text-gray-700"> {objective.responsible_party} will</span>
                                                    )}
                                                    {' '}{objective.objective_text}
                                                  </p>
                                                  {/* Display multiple interventions (new format) */}
                                                  {objective.interventions?.length > 0 && (
                                                    <div className="mt-1.5 ml-2 space-y-1">
                                                      {objective.interventions.map((intervention, intIdx) => (
                                                        <div key={intIdx} className="text-xs border-l-2 border-teal-300 pl-2">
                                                          <p className="font-medium text-teal-700">
                                                            <span className="text-teal-500">Intervention #{intIdx + 1}:</span>{' '}
                                                            {typeof intervention === 'string' ? intervention : intervention.name}
                                                          </p>
                                                          {typeof intervention === 'object' && intervention.description && (
                                                            <p className="text-gray-500 text-[11px] italic">{intervention.description}</p>
                                                          )}
                                                          {typeof intervention === 'object' && (intervention.modality || intervention.frequency) && (
                                                            <p className="text-gray-400 text-[11px]">
                                                              {intervention.modality && `(${intervention.modality})`}
                                                              {intervention.modality && intervention.frequency && ' - '}
                                                              {intervention.frequency && intervention.frequency}
                                                            </p>
                                                          )}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                  {/* Fallback for legacy single intervention format */}
                                                  {!objective.interventions?.length && objective.intervention && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                      <strong>Intervention:</strong> {objective.intervention}
                                                    </p>
                                                  )}
                                                  {objective.treatment_description && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                      <strong>Treatment:</strong> {objective.treatment_description}
                                                    </p>
                                                  )}
                                                  <div className="flex gap-3 mt-1 text-xs text-gray-600">
                                                    {objective.treatment_modality && (
                                                      <span><strong>Modality:</strong> {objective.treatment_modality}</span>
                                                    )}
                                                    {objective.frequency && (
                                                      <span><strong>Frequency:</strong> {objective.frequency}</span>
                                                    )}
                                                  </div>
                                                </div>
                                                <div className="flex gap-1 ml-2">
                                                  <button
                                                    type="button"
                                                    data-testid={`edit-objective-btn-${panIdx}-${goalIdx}-${objIdx}`}
                                                    onClick={() => {
                                                      // Pre-populate the inline form with existing objective data
                                                      setEditingObjectiveIndex(objIdx);
                                                      setShowInlineObjectiveForm(panIdx + '-' + goalIdx);
                                                      // Pre-populate pending interventions with existing ones
                                                      const key = `${panIdx}-${goalIdx}`;
                                                      setPendingInterventions(prev => ({
                                                        ...prev,
                                                        [key]: objective.interventions ? objective.interventions.map((int, i) => ({
                                                          id: (typeof int === 'object' ? int.id : null) || Date.now().toString() + i,
                                                          name: typeof int === 'string' ? int : int.name || '',
                                                          description: typeof int === 'object' ? int.description || '' : '',
                                                          modality: typeof int === 'object' ? int.modality || '' : '',
                                                          frequency: typeof int === 'object' ? int.frequency || '' : '',
                                                        })) : []
                                                      }));
                                                      // Set form data via DOM after render
                                                      setTimeout(() => {
                                                        const rpEl = document.getElementById(`responsible-party-${panIdx}-${goalIdx}`);
                                                        if (rpEl) rpEl.value = objective.responsible_party || 'client';
                                                        const textEl = document.getElementById(`objective-text-${panIdx}-${goalIdx}`);
                                                        if (textEl) {
                                                          textEl.value = objective.objective_text || '';
                                                          textEl.style.height = 'auto';
                                                          textEl.style.height = textEl.scrollHeight + 'px';
                                                        }
                                                        const descEl = document.getElementById(`treatment-description-${panIdx}-${goalIdx}`);
                                                        if (descEl) {
                                                          descEl.value = objective.treatment_description || '';
                                                          descEl.style.height = 'auto';
                                                          descEl.style.height = descEl.scrollHeight + 'px';
                                                        }
                                                      }, 50);
                                                      // Scroll to objective form
                                                      setTimeout(() => {
                                                        if (objectiveFormRef.current && treatmentPlanDialogRef.current) {
                                                          const dialogContent = treatmentPlanDialogRef.current;
                                                          const objForm = objectiveFormRef.current;
                                                          const offsetTop = objForm.offsetTop - 20;
                                                          dialogContent.scrollTo({ top: offsetTop, behavior: 'smooth' });
                                                        }
                                                      }, 150);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800"
                                                    title="Edit objective"
                                                  >
                                                    <Edit className="h-3 w-3" />
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      // Use functional update with proper immutability
                                                      setPlanPANs(prevPANs => 
                                                        prevPANs.map((pan, pIdx) => 
                                                          pIdx === panIdx
                                                            ? {
                                                                ...pan,
                                                                goals: pan.goals.map((goal, gIdx) =>
                                                                  gIdx === goalIdx
                                                                    ? {
                                                                        ...goal,
                                                                        objectives: goal.objectives.filter((_, idx) => idx !== objIdx)
                                                                      }
                                                                    : goal
                                                                )
                                                              }
                                                            : pan
                                                        )
                                                      );
                                                    }}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Remove objective"
                                                  >
                                                    <X className="h-3 w-3" />
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      
                                      {/* Inline Objective Form */}
                                      {showInlineObjectiveForm === (panIdx + '-' + goalIdx) && (
                                        <div ref={objectiveFormRef} className="mt-2 p-3 bg-gray-50 rounded border space-y-2">
                                          <Label className="text-xs font-semibold block mb-2">{editingObjectiveIndex !== null ? 'Edit Objective' : 'Add New Objective'}</Label>
                                          
                                          <div>
                                            <Label className="text-xs">Responsible Party *</Label>
                                            <Select
                                              key={`rp-select-${panIdx}-${goalIdx}-${editingObjectiveIndex ?? 'new'}`}
                                              defaultValue={editingObjectiveIndex !== null && goal.objectives[editingObjectiveIndex]?.responsible_party ? goal.objectives[editingObjectiveIndex].responsible_party : 'client'}
                                              onValueChange={(value) => {
                                                document.getElementById(`responsible-party-${panIdx}-${goalIdx}`).value = value;
                                              }}
                                            >
                                              <SelectTrigger className="h-8 text-xs">
                                                <SelectValue placeholder="Select responsible party" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="client">Client</SelectItem>
                                                <SelectItem value="provider">Provider/Therapist</SelectItem>
                                                <SelectItem value="family">Family/Caregiver</SelectItem>
                                                <SelectItem value="school">School/Teacher</SelectItem>
                                                <SelectItem value="community">Community Support</SelectItem>
                                                <SelectItem value="other">Other</SelectItem>
                                              </SelectContent>
                                            </Select>
                                            <input type="hidden" id={`responsible-party-${panIdx}-${goalIdx}`} value={editingObjectiveIndex !== null && goal.objectives[editingObjectiveIndex]?.responsible_party ? goal.objectives[editingObjectiveIndex].responsible_party : 'client'} />
                                          </div>
                                          
                                          <div>
                                            <Label className="text-xs">What They Will Do *</Label>
                                            <DebouncedTextarea
                                              placeholder="Describe what the responsible party will do..."
                                              id={`objective-text-${panIdx}-${goalIdx}`}
                                              rows={3}
                                              className="text-xs resize-none overflow-hidden"
                                              onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                            />
                                          </div>
                                          
                                          {/* Interventions Section - Inline Selector */}
                                          <div className="border rounded p-2 bg-gray-50">
                                            <Label className="text-xs font-semibold block mb-1.5">Interventions</Label>
                                            
                                            {/* Selected interventions - editable inline */}
                                            {(pendingInterventions[`${panIdx}-${goalIdx}`] || []).length > 0 && (
                                              <div className="space-y-1.5 mb-2">
                                                {(pendingInterventions[`${panIdx}-${goalIdx}`] || []).map((intervention, intIdx) => (
                                                  <div key={intervention.id || intIdx} className="p-2 bg-white border rounded text-xs space-y-1.5">
                                                    <div className="flex items-center justify-between">
                                                      <input
                                                        type="text"
                                                        value={intervention.name || ''}
                                                        onChange={(e) => {
                                                          setPendingInterventions(prev => {
                                                            const key = `${panIdx}-${goalIdx}`;
                                                            const updated = [...(prev[key] || [])];
                                                            updated[intIdx] = { ...updated[intIdx], name: e.target.value };
                                                            return { ...prev, [key]: updated };
                                                          });
                                                        }}
                                                        className="font-medium text-teal-700 text-xs bg-transparent border-none focus:outline-none focus:ring-0 flex-1"
                                                        placeholder="Intervention name..."
                                                      />
                                                      <button 
                                                        type="button" 
                                                        className="text-red-400 hover:text-red-600 ml-2 shrink-0"
                                                        onClick={() => {
                                                          setPendingInterventions(prev => ({
                                                            ...prev,
                                                            [`${panIdx}-${goalIdx}`]: (prev[`${panIdx}-${goalIdx}`] || []).filter((_, i) => i !== intIdx)
                                                          }));
                                                        }}
                                                      >
                                                        <X className="h-3 w-3" />
                                                      </button>
                                                    </div>
                                                    <DebouncedTextarea
                                                      value={intervention.description || ''}
                                                      onChange={(e) => {
                                                        setPendingInterventions(prev => {
                                                          const key = `${panIdx}-${goalIdx}`;
                                                          const updated = [...(prev[key] || [])];
                                                          updated[intIdx] = { ...updated[intIdx], description: e.target.value };
                                                          return { ...prev, [key]: updated };
                                                        });
                                                      }}
                                                      placeholder="Intervention description / full text..."
                                                      rows={2}
                                                      className="w-full text-[11px] text-gray-700 bg-gray-50 border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-500 resize-none"
                                                    />
                                                    <div className="grid grid-cols-2 gap-2">
                                                      <select
                                                        value={intervention.modality || ''}
                                                        onChange={(e) => {
                                                          setPendingInterventions(prev => {
                                                            const key = `${panIdx}-${goalIdx}`;
                                                            const updated = [...(prev[key] || [])];
                                                            updated[intIdx] = { ...updated[intIdx], modality: e.target.value };
                                                            return { ...prev, [key]: updated };
                                                          });
                                                        }}
                                                        className="text-xs text-gray-600 bg-white border rounded px-2 py-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
                                                      >
                                                        <option value="">Modality...</option>
                                                        <option value="Individual">Individual</option>
                                                        <option value="Group">Group</option>
                                                        <option value="Family">Family</option>
                                                        <option value="Couples">Couples</option>
                                                        <option value="Telehealth">Telehealth</option>
                                                        <option value="In-person">In-person</option>
                                                      </select>
                                                      <select
                                                        value={intervention.frequency || ''}
                                                        onChange={(e) => {
                                                          setPendingInterventions(prev => {
                                                            const key = `${panIdx}-${goalIdx}`;
                                                            const updated = [...(prev[key] || [])];
                                                            updated[intIdx] = { ...updated[intIdx], frequency: e.target.value };
                                                            return { ...prev, [key]: updated };
                                                          });
                                                        }}
                                                        className="text-xs text-gray-600 bg-white border rounded px-2 py-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
                                                      >
                                                        <option value="">Frequency...</option>
                                                        <option value="Each session">Each session</option>
                                                        <option value="Weekly">Weekly</option>
                                                        <option value="Bi-weekly">Bi-weekly</option>
                                                        <option value="Monthly">Monthly</option>
                                                        <option value="As needed">As needed</option>
                                                        <option value="PRN">PRN</option>
                                                      </select>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                            
                                            {/* Inline intervention selector */}
                                            {inlineInterventionSelector === `${panIdx}-${goalIdx}` ? (
                                              <div className="border border-dashed border-gray-200 rounded p-2 space-y-2">
                                                {/* Search */}
                                                <div className="relative">
                                                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
                                                  <input
                                                    type="text"
                                                    placeholder="Search interventions..."
                                                    value={interventionSearchQuery}
                                                    onChange={(e) => setInterventionSearchQuery(e.target.value)}
                                                    className="w-full pl-7 pr-2 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                                                    autoFocus
                                                  />
                                                </div>
                                                
                                                {/* Custom intervention option */}
                                                <div
                                                  className="cursor-pointer hover:bg-teal-50 border border-dashed border-teal-400 rounded px-2 py-1.5"
                                                  onClick={() => {
                                                    const key = `${panIdx}-${goalIdx}`;
                                                    setPendingInterventions(prev => ({
                                                      ...prev,
                                                      [key]: [...(prev[key] || []), {
                                                        id: Date.now().toString(),
                                                        name: '',
                                                        modality: null,
                                                        frequency: null,
                                                        isCustom: true
                                                      }]
                                                    }));
                                                    setInlineInterventionSelector(null);
                                                    setInterventionSearchQuery('');
                                                  }}
                                                >
                                                  <div className="flex items-center gap-1.5 text-teal-700">
                                                    <Plus className="h-3 w-3" />
                                                    <span className="text-xs font-medium">Custom Intervention</span>
                                                  </div>
                                                </div>
                                                
                                                {/* Library items */}
                                                {interventionCategoriesLoading ? (
                                                  <p className="text-xs text-gray-400 text-center py-2">Loading...</p>
                                                ) : (
                                                  <div className="max-h-[200px] overflow-y-auto space-y-0.5">
                                                    {interventionCategories
                                                      .flatMap(cat => cat.interventions.map(int => ({ ...int, categoryName: cat.category.name, categoryIcon: cat.category.icon })))
                                                      .filter(int => !interventionSearchQuery || int.name.toLowerCase().includes(interventionSearchQuery.toLowerCase()) || (int.description && int.description.toLowerCase().includes(interventionSearchQuery.toLowerCase())))
                                                      .map(intervention => {
                                                        const isAlreadyAdded = (pendingInterventions[`${panIdx}-${goalIdx}`] || []).some(p => p.name === intervention.name);
                                                        return (
                                                          <div
                                                            key={intervention.id}
                                                            className={`px-2 py-1.5 rounded text-xs cursor-pointer transition-colors ${
                                                              isAlreadyAdded ? 'bg-teal-50 text-teal-700' : 'hover:bg-blue-50'
                                                            }`}
                                                            onClick={() => {
                                                              if (isAlreadyAdded) return;
                                                              const key = `${panIdx}-${goalIdx}`;
                                                              setPendingInterventions(prev => ({
                                                                ...prev,
                                                                [key]: [...(prev[key] || []), {
                                                                  id: Date.now().toString(),
                                                                  name: intervention.name,
                                                                  description: intervention.description,
                                                                  modality: null,
                                                                  frequency: null
                                                                }]
                                                              }));
                                                              setInlineInterventionSelector(null);
                                                              setInterventionSearchQuery('');
                                                            }}
                                                          >
                                                            <div className="flex items-center gap-1.5">
                                                              {isAlreadyAdded ? (
                                                                <Check className="h-3 w-3 text-teal-600 shrink-0" />
                                                              ) : (
                                                                <span className="text-[10px] shrink-0">{intervention.categoryIcon}</span>
                                                              )}
                                                              <span className={`font-medium ${isAlreadyAdded ? '' : 'text-gray-900'}`}>{intervention.name}</span>
                                                            </div>
                                                            {intervention.description && (
                                                              <p className="text-[10px] text-gray-400 ml-5 mt-0.5 line-clamp-1">{intervention.description}</p>
                                                            )}
                                                          </div>
                                                        );
                                                      })
                                                    }
                                                  </div>
                                                )}
                                                
                                                <div className="flex justify-end">
                                                  <button
                                                    type="button"
                                                    className="text-xs text-gray-500 hover:text-gray-700"
                                                    onClick={() => {
                                                      setInlineInterventionSelector(null);
                                                      setInterventionSearchQuery('');
                                                    }}
                                                  >
                                                    Done
                                                  </button>
                                                </div>
                                              </div>
                                            ) : (
                                              <button
                                                type="button"
                                                className="text-[11px] text-teal-600 hover:text-teal-800 flex items-center gap-0.5 hover:bg-teal-50 px-1.5 py-0.5 rounded transition-colors"
                                                onClick={async () => {
                                                  setInlineInterventionSelector(`${panIdx}-${goalIdx}`);
                                                  setInterventionSearchQuery('');
                                                  if (interventionCategories.length === 0) {
                                                    setInterventionCategoriesLoading(true);
                                                    try {
                                                      const response = await axios.get(`${API}/intervention-library/grouped`);
                                                      setInterventionCategories(response.data || []);
                                                    } catch (err) {
                                                      console.error('Failed to fetch interventions:', err);
                                                    } finally {
                                                      setInterventionCategoriesLoading(false);
                                                    }
                                                  }
                                                }}
                                              >
                                                <Plus className="h-3 w-3" /> Add Intervention
                                              </button>
                                            )}
                                          </div>
                                          
                                          <div>
                                            <Label className="text-xs">Treatment Description (Optional)</Label>
                                            <DebouncedTextarea
                                              placeholder="Additional treatment notes..."
                                              id={`treatment-description-${panIdx}-${goalIdx}`}
                                              rows={3}
                                              className="text-xs resize-none overflow-hidden"
                                              onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                            />
                                          </div>
                                          
                                          <div className="flex gap-2 pt-2">
                                            <Button
                                              type="button"
                                              size="sm"
                                              onClick={() => {
                                                try {
                                                  const responsibleParty = document.getElementById(`responsible-party-${panIdx}-${goalIdx}`).value;
                                                  const objectiveText = document.getElementById(`objective-text-${panIdx}-${goalIdx}`).value;
                                                  
                                                  // Get interventions from React state (new library-based approach)
                                                  const interventions = pendingInterventions[`${panIdx}-${goalIdx}`] || [];
                                                  
                                                  const treatmentDescription = document.getElementById(`treatment-description-${panIdx}-${goalIdx}`).value;
                                                  
                                                  // Validate required fields
                                                  if (!responsibleParty) {
                                                    toast.error('Please select a responsible party');
                                                    return;
                                                  }
                                                  if (!objectiveText.trim()) {
                                                    toast.error('Please describe what they will do');
                                                    return;
                                                  }
                                                  
                                                  const isEditing = editingObjectiveIndex !== null;
                                                  
                                                  // Create new objective with interventions array (each with modality/frequency)
                                                  const newObjective = {
                                                    responsible_party: responsibleParty.trim(),
                                                    objective_text: objectiveText.trim(),
                                                    interventions: interventions.length > 0 ? interventions : null,
                                                    intervention: interventions.length > 0 
                                                      ? interventions.map(i => i.name).join(', ') 
                                                      : null,
                                                    treatment_description: treatmentDescription.trim() || null,
                                                  };
                                                  
                                                  // Use functional update with proper immutability
                                                  setPlanPANs(prevPANs => {
                                                    if (!prevPANs[panIdx]) {
                                                      toast.error(`PAN at index ${panIdx} not found`);
                                                      return prevPANs;
                                                    }
                                                    
                                                    if (!prevPANs[panIdx].goals || !prevPANs[panIdx].goals[goalIdx]) {
                                                      toast.error(`Goal at index ${goalIdx} not found`);
                                                      return prevPANs;
                                                    }
                                                    
                                                    if (isEditing) {
                                                      // Replace existing objective at editingObjectiveIndex
                                                      return prevPANs.map((pan, pIdx) => 
                                                        pIdx === panIdx
                                                          ? {
                                                              ...pan,
                                                              goals: pan.goals.map((goal, gIdx) =>
                                                                gIdx === goalIdx
                                                                  ? {
                                                                      ...goal,
                                                                      objectives: goal.objectives.map((obj, oIdx) =>
                                                                        oIdx === editingObjectiveIndex ? newObjective : obj
                                                                      )
                                                                    }
                                                                  : goal
                                                              )
                                                            }
                                                          : pan
                                                      );
                                                    } else {
                                                      // Adding new - check for duplicates
                                                      const existingObjectives = prevPANs[panIdx].goals[goalIdx].objectives || [];
                                                      const isDuplicate = existingObjectives.some(obj => 
                                                        obj.objective_text.toLowerCase().trim() === objectiveText.toLowerCase().trim()
                                                      );
                                                      if (isDuplicate) {
                                                        toast.error('This objective already exists for this goal');
                                                        return prevPANs;
                                                      }
                                                      
                                                      return prevPANs.map((pan, pIdx) => 
                                                        pIdx === panIdx
                                                          ? {
                                                              ...pan,
                                                              goals: pan.goals.map((goal, gIdx) =>
                                                                gIdx === goalIdx
                                                                  ? {
                                                                      ...goal,
                                                                      objectives: [...(goal.objectives || []), newObjective]
                                                                    }
                                                                  : goal
                                                              )
                                                            }
                                                          : pan
                                                      );
                                                    }
                                                  });
                                                  
                                                  // Clear the pending interventions for this form
                                                  setPendingInterventions(prev => {
                                                    const updated = { ...prev };
                                                    delete updated[`${panIdx}-${goalIdx}`];
                                                    return updated;
                                                  });
                                                  
                                                  setShowInlineObjectiveForm(null);
                                                  setEditingObjectiveIndex(null);
                                                  
                                                  // Clear the text area
                                                  const textArea = document.getElementById(`objective-text-${panIdx}-${goalIdx}`);
                                                  if (textArea) {
                                                    textArea.value = '';
                                                  }
                                                  
                                                  toast.success(isEditing ? 'Objective updated' : 'Objective added');
                                                } catch (error) {
                                                  console.error('Error saving objective:', error);
                                                  toast.error('Failed to save objective: ' + error.message);
                                                }
                                              }}
                                              className="bg-blue-600 hover:bg-blue-700 text-xs h-7"
                                            >
                                              {editingObjectiveIndex !== null ? (
                                                <><Edit className="h-3 w-3 mr-1" /> Update Objective</>
                                              ) : (
                                                <><Plus className="h-3 w-3 mr-1" /> Save Objective</>
                                              )}
                                            </Button>
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              onClick={() => {
                                                // Clear pending interventions when cancelling
                                                setPendingInterventions(prev => {
                                                  const updated = { ...prev };
                                                  delete updated[`${panIdx}-${goalIdx}`];
                                                  return updated;
                                                });
                                                setShowInlineObjectiveForm(null);
                                                setEditingObjectiveIndex(null);
                                              }}
                                              className="text-xs h-7"
                                            >
                                              Cancel
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              
            </div>
          {/* Inline PAN Selector */}
          {showInlinePANSelector && (
            <div ref={inlinePANSelectorRef} className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold mb-3 block">Select a PAN</Label>
              
              {/* Search Input */}
              <div className="mb-3">
                <DebouncedInput
                  placeholder="Search PANs by name or description..."
                  value={panSearchQuery}
                  onChange={(e) => setPANSearchQuery(e.target.value)}
                  className="mb-2"
                />
                <Select
                  value={panCategoryFilter || 'all'}
                  onValueChange={(value) => setPANCategoryFilter(value === 'all' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="safety">Safety</SelectItem>
                    <SelectItem value="emotional">Emotional</SelectItem>
                    <SelectItem value="behavioral">Behavioral</SelectItem>
                    <SelectItem value="relational">Relational</SelectItem>
                    <SelectItem value="cognitive">Cognitive</SelectItem>
                    <SelectItem value="physical">Physical</SelectItem>
                    <SelectItem value="developmental">Developmental</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {/* Create Custom PAN Option */}
                <Card
                  className="cursor-pointer hover:bg-blue-50 border-blue-200 border-dashed transition-colors"
                  onClick={() => {
                    setShowCustomPANInput(true);
                    setCustomPANName('');
                    setCustomPANDescription('');
                    setCustomPANCategory('other');
                  }}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 text-blue-600">
                      <Plus className="h-4 w-4" />
                      <span className="font-medium text-sm">Create Custom PAN</span>
                      <span className="text-xs text-gray-500">- Define a specific need not in the list</span>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Custom PAN Input Form */}
                {showCustomPANInput && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-3 space-y-2">
                      <div>
                        <Label htmlFor="inline-custom-pan-name" className="text-xs">Custom PAN Name *</Label>
                        <DebouncedInput
                          id="inline-custom-pan-name"
                          placeholder="e.g., Chronic pain management, Housing instability..."
                          value={customPANName}
                          onChange={(e) => setCustomPANName(e.target.value)}
                          className="bg-white h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inline-custom-pan-desc" className="text-xs">Description (optional)</Label>
                        <DebouncedInput
                          id="inline-custom-pan-desc"
                          placeholder="Brief description of this need..."
                          value={customPANDescription}
                          onChange={(e) => setCustomPANDescription(e.target.value)}
                          className="bg-white h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Category</Label>
                        <Select value={customPANCategory} onValueChange={setCustomPANCategory}>
                          <SelectTrigger className="bg-white h-8 text-sm">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="safety">Safety</SelectItem>
                            <SelectItem value="emotional">Emotional</SelectItem>
                            <SelectItem value="behavioral">Behavioral</SelectItem>
                            <SelectItem value="relational">Relational</SelectItem>
                            <SelectItem value="cognitive">Cognitive</SelectItem>
                            <SelectItem value="physical">Physical</SelectItem>
                            <SelectItem value="developmental">Developmental</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => {
                            if (!customPANName.trim()) {
                              toast.error('Please enter a PAN name');
                              return;
                            }
                            // Add custom PAN to plan
                            const customPAN = {
                              pan_id: `custom-${Date.now()}`,
                              pan_name: customPANName.trim(),
                              description: customPANDescription.trim() || null,
                              category: customPANCategory || 'other',
                              is_custom: true,
                              goals: []
                            };
                            setPlanPANs([...planPANs, customPAN]);
                            setShowCustomPANInput(false);
                            setShowInlinePANSelector(false);
                            setCustomPANName('');
                            setCustomPANDescription('');
                            setCustomPANCategory('');
                            toast.success(`Custom PAN "${customPANName}" added`);
                          }}
                        >
                          Add Custom PAN
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => {
                            setShowCustomPANInput(false);
                            setCustomPANName('');
                            setCustomPANDescription('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {(() => {
                  let filteredPANs = availablePANs.filter(pan => !planPANs.find(p => p.pan_id === pan.id));
                  
                  // Filter by category
                  if (panCategoryFilter) {
                    filteredPANs = filteredPANs.filter(pan => pan.category === panCategoryFilter);
                  }
                  
                  // Filter by search query
                  if (panSearchQuery) {
                    const query = panSearchQuery.toLowerCase();
                    filteredPANs = filteredPANs.filter(pan => 
                      pan.name.toLowerCase().includes(query) ||
                      (pan.description && pan.description.toLowerCase().includes(query))
                    );
                  }
                  
                  if (filteredPANs.length === 0) {
                    return (
                      <div className="text-center py-4 text-gray-500">
                        <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No PANs found matching your criteria</p>
                        <p className="text-xs">Try adjusting your search or filter</p>
                      </div>
                    );
                  }
                  
                  return filteredPANs.map(pan => (
                    <Card
                      key={pan.id}
                      className="cursor-pointer hover:bg-teal-50 transition-colors"
                      onClick={() => {
                        // Check for duplicate PANs
                        const isDuplicate = planPANs.some(p => p.pan_id === pan.id);
                        if (isDuplicate) {
                          toast.error(`PAN "${pan.name}" is already added to this treatment plan`);
                          return;
                        }
                        
                        setPlanPANs([...planPANs, { pan_id: pan.id, pan_name: pan.name, goals: [] }]);
                        setShowInlinePANSelector(false);
                        setPANSearchQuery('');
                        setPANCategoryFilter('');
                        toast.success(`PAN "${pan.name}" added`);
                      }}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-sm">{pan.name}</h4>
                            <Badge variant="outline" className="text-xs mt-1">
                              {pan.category}
                            </Badge>
                            {pan.is_safety_related && (
                              <Badge className="bg-red-600 text-xs ml-1 mt-1">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Safety
                              </Badge>
                            )}
                            {pan.description && (
                              <p className="text-xs text-gray-600 mt-1">
                                {pan.description}
                              </p>
                            )}
                          </div>
                          <Plus className="h-4 w-4 text-teal-600 flex-shrink-0 ml-2" />
                        </div>
                      </CardContent>
                    </Card>
                  ));
                })()}
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowInlinePANSelector(false);
                    setPANSearchQuery('');
                    setPANCategoryFilter('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Add PAN Button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setShowInlinePANSelector(true);
              setTimeout(() => {
                if (panSectionRef.current) {
                  panSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 150);
            }}
            className="w-full mt-2"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add PAN
          </Button>

          </div>

          {/* ===== CLINICAL CONTEXT (dynamic from form template) ===== */}
          <TreatmentPlanClinicalContext
            formTemplates={formTemplates}
            tpDiagnoses={tpDiagnoses}
            setTpDiagnoses={setTpDiagnoses}
            tpMedications={tpMedications}
            setTpMedications={setTpMedications}
            tpLevelOfCareNotes={tpLevelOfCareNotes}
            setTpLevelOfCareNotes={setTpLevelOfCareNotes}
            tpTraumaReports={tpTraumaReports}
            setTpTraumaReports={setTpTraumaReports}
            tpSupportsNoneReported={tpSupportsNoneReported}
            setTpSupportsNoneReported={setTpSupportsNoneReported}
            tpSupports={tpSupports}
            setTpSupports={setTpSupports}
            collapsed={tpClinicalContextCollapsed}
            setCollapsed={setTpClinicalContextCollapsed}
          />
          {/* ===== END CLINICAL CONTEXT ===== */}

          {/* Dialog Footer - Cancel/Create at bottom */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => {
                const hasData = planPANs.length > 0 || newPlanProviderId || newPlanServiceType;
                if (hasData) {
                  if (!confirm('You have unsaved changes. Are you sure you want to close? Your draft will be lost.')) return;
                }
                setShowCreatePlanDialog(false);
                setNewPlanServiceType('');
                setNewPlanProviderId('');
                setNewPlanVisitFrequency('');
                setNewPlanStartDate('');
                setEditingPlanForMinorEdit(null);
                setIsMajorReviewEdit(false);
                setPlanPANs([]);
                setPendingInterventions({});
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (editingPlanForMinorEdit) {
                  try {
                    const changes = {
                      service_type: newPlanServiceType,
                      assigned_provider_id: newPlanProviderId,
                      visit_frequency: newPlanVisitFrequency,
                      start_date: newPlanStartDate,
                      pans: planPANs
                    };
                    
                    if (isMajorReviewEdit) {
                      // Filter out goals marked met/discontinued (they get archived)
                      const archivedGoalIds = new Set(
                        majorReviewGoalProgress
                          .filter(g => g.progress_status === 'met' || g.progress_status === 'discontinued')
                          .map(g => g.goal_id)
                      );
                      const filteredPANs = planPANs.map(pan => ({
                        ...pan,
                        goals: (pan.goals || []).filter(goal => {
                          const gid = goal.goal_id || goal.id;
                          return !archivedGoalIds.has(gid);
                        })
                      })).filter(pan => pan.goals.length > 0); // Remove PANs with no remaining goals
                      
                      const changesWithFilteredPANs = { ...changes, pans: filteredPANs };
                      
                      // Major Review: creates new version with goal progress
                      const response = await axios.post(
                        `${API}/treatment-plans/${editingPlanForMinorEdit.id}/major-review`,
                        {
                          review_notes: majorReviewNotes || 'Treatment plan review and update',
                          changes: changesWithFilteredPANs,
                          goal_progress: majorReviewGoalProgress.filter(g => g.progress_status),
                          // Date the review was done (local date, YYYY-MM-DD) —
                          // backend anchors last/next review dates on this.
                          review_date: majorReviewDate || new Date().toLocaleDateString('en-CA')
                        },
                        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                      );
                      // Also save clinical context metadata on the NEW plan
                      if (response.data?.new_plan_id) {
                        await axios.patch(
                          `${API}/treatment-plans/${response.data.new_plan_id}/metadata`,
                          {
                            medications_snapshot: tpMedications,
                            diagnoses_snapshot: tpDiagnoses,
                            level_of_care_notes: tpLevelOfCareNotes,
                            trauma_reports: tpTraumaReports,
                            supports: { none_reported: tpSupportsNoneReported, entries: tpSupports }
                          },
                          { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                        );
                      }
                      const copiedData = response.data.copied_data;
                      const copySummary = copiedData
                        ? ` Copied: ${copiedData.pans} PAN(s), ${copiedData.goals} goal(s), ${copiedData.objectives} objective(s).`
                        : '';
                      toast.success(`Version ${response.data.new_version} created!${copySummary}`, { duration: 5000 });
                    } else {
                      // Minor Edit
                      const isDraft = editingPlanForMinorEdit.status === 'draft' || editingPlanForMinorEdit.status === 'needs_corrections';
                      await axios.post(
                        `${API}/treatment-plans/${editingPlanForMinorEdit.id}/minor-edit`,
                        { changes, notes: isDraft ? 'Draft edit via treatment plan editor' : 'Minor edit via treatment plan editor' },
                        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                      );
                      await axios.patch(
                        `${API}/treatment-plans/${editingPlanForMinorEdit.id}/metadata`,
                        {
                          medications_snapshot: tpMedications,
                          diagnoses_snapshot: tpDiagnoses,
                          level_of_care_notes: tpLevelOfCareNotes,
                          trauma_reports: tpTraumaReports,
                          supports: { none_reported: tpSupportsNoneReported, entries: tpSupports }
                        },
                        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                      );
                      toast.success(isDraft ? 'Draft saved successfully' : 'Treatment plan updated (minor edit)');
                    }
                    
                    setShowCreatePlanDialog(false);
                    setEditingPlanForMinorEdit(null);
                    setIsMajorReviewEdit(false);
                    setMajorReviewGoalProgress([]);
                    setMajorReviewNotes('');
                    await refetchTreatmentPlans(selectedClient.id);
                  } catch (error) {
                    console.error('Error saving edit:', error);
                    toast.error(error.response?.data?.detail || 'Failed to save changes');
                  }
                  return;
                }
                if (!newPlanServiceType || !newPlanProviderId) {
                  toast.error('Please select both a provider and service type');
                  return;
                }
                if (!newPlanVisitFrequency) {
                  toast.error('Please select a visit frequency');
                  return;
                }
                try {
                  const payload = {
                    service_type: newPlanServiceType,
                    assigned_provider_id: newPlanProviderId,
                    visit_frequency: newPlanVisitFrequency,
                    start_date: newPlanStartDate || new Date().toISOString().split('T')[0],
                    is_legacy_plan: isLegacyPlan
                  };
                  const response = await axios.post(
                    `${API}/clients/${selectedClient.id}/treatment-plans`,
                    payload,
                    { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                  );
                  const planId = response.data.id || response.data.plan_id;
                  for (let i = 0; i < planPANs.length; i++) {
                    const pan = planPANs[i];
                    try {
                      let panResponse;
                      if (pan.is_custom) {
                        panResponse = await axios.post(
                          `${API}/treatment-plans/${planId}/pans/custom`,
                          { pan_name: pan.pan_name, description: pan.description || null, category: pan.category || 'other', is_custom: true, pan_number: i + 1 },
                          { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                        );
                      } else {
                        panResponse = await axios.post(
                          `${API}/treatment-plans/${planId}/pans`,
                          { pan_id: pan.pan_id, pan_number: i + 1 },
                          { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                        );
                      }
                      const addedPanId = panResponse.data?.client_pan_id || panResponse.data?.id;
                      if (addedPanId && pan.goals && pan.goals.length > 0) {
                        for (const goal of pan.goals) {
                          try {
                            const goalResponse = await axios.post(
                              `${API}/client-pans/${addedPanId}/goals`,
                              {
                                client_pan_id: addedPanId,
                                goal_id: goal.goal_id || goal.template_id || null,
                                goal_text: goal.customized_text || goal.goal_text,
                                client_goal_description: goal.client_goal_description || '',
                                client_barriers: goal.client_barriers || '',
                                client_strengths: goal.client_strengths || '',
                                duration_days: goal.duration_days || 90,
                                is_custom: goal.is_custom || !goal.template_id
                              },
                              { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                            );
                            
                            // Save objectives for this goal
                            const clientGoalId = goalResponse.data?.client_goal_id;
                            if (clientGoalId && goal.objectives && goal.objectives.length > 0) {
                              for (const obj of goal.objectives) {
                                try {
                                  await axios.post(
                                    `${API}/client-goals/${clientGoalId}/objectives`,
                                    {
                                      objective_text: obj.objective_text,
                                      responsible_party: obj.responsible_party || null,
                                      intervention: obj.intervention || null,
                                      interventions: obj.interventions || null,
                                      treatment_description: obj.treatment_description || null,
                                      treatment_modality: obj.treatment_modality || null,
                                      frequency: obj.frequency || null
                                    },
                                    { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                                  );
                                } catch (objError) {
                                  console.error('Failed to add objective:', objError);
                                  toast.error(`Failed to add objective to goal`);
                                }
                              }
                            }
                          } catch (goalError) {
                            toast.error(`Failed to add goal to PAN ${pan.pan_name}`);
                          }
                        }
                      }
                    } catch (panError) {
                      toast.error(`Failed to add PAN: ${pan.pan_name}`);
                    }
                  }
                  // Save plan-level metadata (medications, diagnoses, supports, etc.)
                  try {
                    await axios.patch(
                      `${API}/treatment-plans/${planId}/metadata`,
                      {
                        medications_snapshot: tpMedications,
                        diagnoses_snapshot: tpDiagnoses,
                        level_of_care_notes: tpLevelOfCareNotes,
                        trauma_reports: tpTraumaReports,
                        supports: {
                          none_reported: tpSupportsNoneReported,
                          entries: tpSupports.filter(s => s.role || s.contact_and_title || s.services_provided || s.release_signed).map(({ isCustomRole, ...rest }) => rest)
                        }
                      },
                      { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                    );
                  } catch (metaError) {
                    console.error('Failed to save plan metadata:', metaError);
                    toast.error('Plan created but some metadata failed to save');
                  }
                  toast.success('Treatment plan created successfully');
                  setShowCreatePlanDialog(false);
                  setNewPlanServiceType('');
                  setNewPlanProviderId('');
                  setNewPlanVisitFrequency('');
                  setNewPlanStartDate('');
                  setIsLegacyPlan(false);
                  setPlanPANs([]);
                  setExpandedPANs(new Set());
                  setTpMedications([]);
                  setTpDiagnoses([]);
                  setTpLevelOfCareNotes('');
                  setTpTraumaReports('None reported');
                  setTpSupportsNoneReported(false);
                  setTpSupports([]);
                  setTpMetadataLoaded(false);
                  await refetchTreatmentPlans(selectedClient.id);
                } catch (error) {
                  if (error.response?.status === 409 && error.response?.data?.detail?.error === 'duplicate_plan') {
                    const detail = error.response.data.detail;
                    setDuplicatePlanDetected({
                      existingPlanId: detail.existing_plan_id,
                      serviceType: detail.service_type,
                      message: detail.message
                    });
                  } else {
                    const errorMessage = typeof error.response?.data?.detail === 'string' 
                      ? error.response?.data?.detail 
                      : error.response?.data?.detail?.message || `Failed to create treatment plan: ${error.message}`;
                    toast.error(errorMessage);
                  }
                }
              }}
              disabled={!newPlanServiceType || !newPlanProviderId || !newPlanVisitFrequency || !newPlanStartDate}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {editingPlanForMinorEdit 
                ? (isMajorReviewEdit ? 'Save Major Review' : (editingPlanForMinorEdit.status === 'draft' || editingPlanForMinorEdit.status === 'needs_corrections' ? 'Save Draft' : 'Save Minor Edit')) 
                : 'Create Treatment Plan'}
            </Button>
          </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add PAN Dialog */}
      <Dialog open={showAddPANDialog} onOpenChange={setShowAddPANDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add PAN to Treatment Plan</DialogTitle>
            <p className="text-sm text-gray-600">
              Select a Prioritized Assessed Need from the list below
            </p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Search Input */}
            <div>
              <Label>Search PANs</Label>
              <DebouncedInput
                placeholder="Search by name or description..."
                value={panSearchQuery}
                onChange={(e) => setPANSearchQuery(e.target.value)}
                className="mb-2"
              />
            </div>
            
            {/* Category Filter */}
            <div>
              <Label>Filter by Category</Label>
              <Select
                value={panCategoryFilter || 'all'}
                onValueChange={(value) => setPANCategoryFilter(value === 'all' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="safety">Safety</SelectItem>
                  <SelectItem value="emotional">Emotional</SelectItem>
                  <SelectItem value="behavioral">Behavioral</SelectItem>
                  <SelectItem value="relational">Relational</SelectItem>
                  <SelectItem value="cognitive">Cognitive</SelectItem>
                  <SelectItem value="physical">Physical</SelectItem>
                  <SelectItem value="developmental">Developmental</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* PAN List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {/* Custom PAN Option */}
              <Card
                className="cursor-pointer hover:bg-blue-50 border-blue-200 border-dashed transition-colors"
                onClick={() => setShowCustomPANInput(true)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-blue-600">
                    <Plus className="h-5 w-5" />
                    <span className="font-medium">Create Custom PAN</span>
                    <span className="text-sm text-gray-500">- Define a specific need not in the list</span>
                  </div>
                </CardContent>
              </Card>
              
              {/* Custom PAN Input Form */}
              {showCustomPANInput && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 space-y-3">
                    <div>
                      <Label htmlFor="custom-pan-name">Custom PAN Name *</Label>
                      <DebouncedInput
                        id="custom-pan-name"
                        placeholder="e.g., Chronic pain management, Housing instability..."
                        value={customPANName}
                        onChange={(e) => setCustomPANName(e.target.value)}
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="custom-pan-description">Description (optional)</Label>
                      <DebouncedInput
                        id="custom-pan-description"
                        placeholder="Brief description of this need..."
                        value={customPANDescription}
                        onChange={(e) => setCustomPANDescription(e.target.value)}
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select
                        value={customPANCategory}
                        onValueChange={setCustomPANCategory}
                      >
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="safety">Safety</SelectItem>
                          <SelectItem value="emotional">Emotional</SelectItem>
                          <SelectItem value="behavioral">Behavioral</SelectItem>
                          <SelectItem value="relational">Relational</SelectItem>
                          <SelectItem value="cognitive">Cognitive</SelectItem>
                          <SelectItem value="physical">Physical</SelectItem>
                          <SelectItem value="developmental">Developmental</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        onClick={async () => {
                          if (!customPANName.trim()) {
                            toast.error('Please enter a PAN name');
                            return;
                          }
                          
                          try {
                            if (selectedTreatmentPlan?.id) {
                              // Adding custom PAN to existing plan
                              await axios.post(
                                `${API}/treatment-plans/${selectedTreatmentPlan.id}/pans/custom`,
                                { 
                                  pan_name: customPANName.trim(),
                                  description: customPANDescription.trim() || null,
                                  category: customPANCategory || 'other',
                                  client_id: selectedClient.id
                                },
                                {
                                  headers: {
                                    Authorization: `Bearer ${sessionStorage.getItem('token')}`
                                  }
                                }
                              );
                              
                              toast.success(`Custom PAN "${customPANName}" added to treatment plan`);
                              setShowAddPANDialog(false);
                              setShowCustomPANInput(false);
                              setCustomPANName('');
                              setCustomPANDescription('');
                              setCustomPANCategory('');
                              setPANCategoryFilter('');
                              
                              // Reload treatment plans
                              await refetchTreatmentPlans(selectedClient.id);
                            } else {
                              // Building new plan - add to local state
                              const customPanId = `custom_${Date.now()}`;
                              setPlanPANs([...planPANs, { 
                                pan_id: customPanId, 
                                pan_name: customPANName.trim(),
                                is_custom: true,
                                description: customPANDescription.trim() || null,
                                category: customPANCategory || 'other',
                                goals: [] 
                              }]);
                              toast.success(`Custom PAN "${customPANName}" added`);
                              setShowAddPANDialog(false);
                              setShowCustomPANInput(false);
                              setCustomPANName('');
                              setCustomPANDescription('');
                              setCustomPANCategory('');
                              setPANCategoryFilter('');
                              setPANSearchQuery('');
                            }
                          } catch (error) {
                            console.error('Error adding custom PAN:', error);
                            toast.error(error.response?.data?.detail || 'Failed to add custom PAN');
                          }
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Custom PAN
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setShowCustomPANInput(false);
                          setCustomPANName('');
                          setCustomPANDescription('');
                          setCustomPANCategory('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Predefined PANs */}
              {(() => {
                let filteredPANs = availablePANs;
                
                // Filter by category
                if (panCategoryFilter) {
                  filteredPANs = filteredPANs.filter(pan => pan.category === panCategoryFilter);
                }
                
                // Filter by search query
                if (panSearchQuery) {
                  const query = panSearchQuery.toLowerCase();
                  filteredPANs = filteredPANs.filter(pan => 
                    pan.name.toLowerCase().includes(query) ||
                    (pan.description && pan.description.toLowerCase().includes(query))
                  );
                }
                
                if (filteredPANs.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No PANs found matching your criteria</p>
                      <p className="text-sm">Try adjusting your search or filter</p>
                    </div>
                  );
                }
                
                return filteredPANs.map(pan => (
                  <Card
                    key={pan.id}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={async () => {
                      // Check if we're adding to an existing plan or building a new one
                      if (selectedTreatmentPlan?.id) {
                        // Adding to existing plan
                        try {
                          await axios.post(
                            `${API}/treatment-plans/${selectedTreatmentPlan.id}/pans`,
                            { 
                              pan_id: pan.id,
                              client_id: selectedClient.id
                            },
                            {
                              headers: {
                                Authorization: `Bearer ${sessionStorage.getItem('token')}`
                              }
                            }
                          );
                          
                          toast.success(`PAN "${pan.name}" added to treatment plan`);
                          setShowAddPANDialog(false);
                          setPANCategoryFilter('');
                          
                          // Reload treatment plans
                          await refetchTreatmentPlans(selectedClient.id);
                        } catch (error) {
                          console.error('Error adding PAN:', error);
                          toast.error(error.response?.data?.detail || 'Failed to add PAN');
                        }
                      } else {
                        // Building new plan in dialog - check for duplicates
                        const isDuplicate = planPANs.some(p => p.pan_id === pan.id);
                        if (isDuplicate) {
                          toast.error(`PAN "${pan.name}" is already added to this treatment plan`);
                          return;
                        }
                        
                        setPlanPANs([...planPANs, { pan_id: pan.id, pan_name: pan.name, goals: [] }]);
                        toast.success(`PAN "${pan.name}" added`);
                        setShowAddPANDialog(false);
                        setPANCategoryFilter('');
                        setPANSearchQuery('');
                      }
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{pan.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {pan.category}
                            </Badge>
                            {pan.is_safety_related && (
                              <Badge className="bg-red-600 text-xs">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Safety
                              </Badge>
                            )}
                          </div>
                          {pan.description && (
                            <p className="text-sm text-gray-600 mt-1">
                              {pan.description}
                            </p>
                          )}
                        </div>
                        <Plus className="h-5 w-5 text-teal-600 flex-shrink-0 ml-2" />
                      </div>
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddPANDialog(false);
                setPANCategoryFilter('');
                setPANSearchQuery('');
                setShowCustomPANInput(false);
                setCustomPANName('');
                setCustomPANDescription('');
                setCustomPANCategory('');
              }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Goal Dialog */}
      <Dialog open={showAddGoalDialog} onOpenChange={setShowAddGoalDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Goal to PAN</DialogTitle>
            <DialogDescription>
              PAN: <span className="font-semibold">{selectedPANForGoal?.pan_name}</span>
              <br />
              <span className="text-orange-600 text-xs">Note: Each PAN should have only 1 active goal. Multiple objectives can be added to each goal.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Template Goals */}
            <div>
              <Label className="text-base font-semibold">Select from Template Goals</Label>
              <p className="text-xs text-gray-500 mb-3">Click a goal to add it to this PAN</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableGoals.length === 0 ? (
                  <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded">
                    No template goals available for this PAN. You can create a custom goal below.
                  </p>
                ) : (
                  availableGoals.map(goal => (
                    <Card
                      key={goal.id}
                      className="cursor-pointer hover:bg-teal-50 transition-colors border-l-4 border-teal-500"
                      onClick={() => {
                        // Open customization dialog for this goal template
                        setSelectedGoalTemplate(goal);
                        setGoalCustomization({
                          customized_text: goal.goal_text, // Pre-fill with template text
                          client_goal_description: '',
                          client_barriers: '',
                          client_strengths: '',
                          duration_days: 90,
                          custom_duration: '',
                          notes: ''
                        });
                        setShowCustomizeGoalDialog(true);
                      }}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between">
                          <p className="text-sm flex-1">{goal.goal_text}</p>
                          <Plus className="h-5 w-5 text-teal-600 flex-shrink-0 ml-2" />
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>

            {/* Custom Goal */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Or Create Custom Goal</Label>
              <div className="space-y-3 mt-3">
                <div>
                  <Label htmlFor="custom-goal-text">Goal Description *</Label>
                  <DebouncedTextarea
                    id="custom-goal-text"
                    value={customGoalText || ''}
                    onChange={(e) => setCustomGoalText(e.target.value)}
                    placeholder="Enter the treatment goal..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="client-goal-desc">Client's Goal (in their own words) *</Label>
                  <DebouncedInput
                    id="client-goal-desc"
                    value={clientGoalDescription || ''}
                    onChange={(e) => setClientGoalDescription(e.target.value)}
                    placeholder="How does the client describe this goal?"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="goal-barriers">Barriers</Label>
                    <DebouncedInput
                      id="goal-barriers"
                      value={goalBarriers || ''}
                      onChange={(e) => setGoalBarriers(e.target.value)}
                      placeholder="Challenges to overcome..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="goal-strengths">Strengths</Label>
                    <DebouncedInput
                      id="goal-strengths"
                      value={goalStrengths || ''}
                      onChange={(e) => setGoalStrengths(e.target.value)}
                      placeholder="Client strengths/resources..."
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="custom-goal-duration">Goal Duration * (Required)</Label>
                    <Select
                      value={goalDuration === 'custom' ? 'custom' : String(goalDuration || '90')}
                      onValueChange={(value) => {
                        if (value === 'custom') {
                          setGoalDuration('custom');
                          setCustomGoalDuration('');
                        } else {
                          setGoalDuration(parseInt(value));
                          setCustomGoalDuration('');
                        }
                      }}
                    >
                      <SelectTrigger id="custom-goal-duration">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 days (1 month)</SelectItem>
                        <SelectItem value="60">60 days (2 months)</SelectItem>
                        <SelectItem value="90">90 days (3 months)</SelectItem>
                        <SelectItem value="180">180 days (6 months)</SelectItem>
                        <SelectItem value="365">365 days (1 year)</SelectItem>
                        <SelectItem value="custom">Custom duration</SelectItem>
                      </SelectContent>
                    </Select>
                    {goalDuration === 'custom' && (
                      <Input
                        type="number"
                        min="1"
                        placeholder="Enter custom days"
                        value={customGoalDuration || ''}
                        onChange={(e) => setCustomGoalDuration(e.target.value)}
                        className="mt-2"
                      />
                    )}
                    {selectedTreatmentPlan?.start_date && goalDuration !== 'custom' && goalDuration && (
                      <p className="text-xs text-teal-600 mt-1 font-medium">
                        Duration: {goalDuration} days | Expected End: {
                          (() => {
                            const startDate = new Date(selectedTreatmentPlan.start_date);
                            const endDate = new Date(startDate);
                            endDate.setDate(endDate.getDate() + goalDuration);
                            return endDate.toLocaleDateString();
                          })()
                        }
                      </p>
                    )}
                    {selectedTreatmentPlan?.start_date && goalDuration === 'custom' && customGoalDuration && (
                      <p className="text-xs text-teal-600 mt-1 font-medium">
                        Duration: {customGoalDuration} days | Expected End: {
                          (() => {
                            const startDate = new Date(selectedTreatmentPlan.start_date);
                            const endDate = new Date(startDate);
                            endDate.setDate(endDate.getDate() + parseInt(customGoalDuration));
                            return endDate.toLocaleDateString();
                          })()
                        }
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    if (!customGoalText?.trim()) {
                      toast.error('Please enter a goal description');
                      return;
                    }
                    
                    if (!clientGoalDescription?.trim()) {
                      toast.error('Please enter the goal in client\'s own words');
                      return;
                    }
                    
                    // Validate duration
                    let finalDuration = goalDuration;
                    if (finalDuration === 'custom') {
                      if (!customGoalDuration || parseInt(customGoalDuration) < 1) {
                        toast.error('Please enter a valid custom duration (minimum 1 day)');
                        return;
                      }
                      finalDuration = parseInt(customGoalDuration);
                    }
                    
                    if (!finalDuration || finalDuration < 1) {
                      toast.error('Please select a valid duration');
                      return;
                    }
                    
                    const confirmAdd = confirm(
                      'Each PAN should only have 1 active goal. Adding this custom goal will set any existing goals for this PAN as inactive.\n\n' +
                      'Do you want to continue?'
                    );
                    
                    if (!confirmAdd) {
                      return;
                    }
                    
                    try {
                      void 0 && console.log('Creating custom goal...');
                      void 0 && console.log('  selectedPANForGoal:', selectedPANForGoal);
                      void 0 && console.log('  selectedClient:', selectedClient);
                      void 0 && console.log('  selectedTreatmentPlan:', selectedTreatmentPlan);
                      void 0 && console.log('  Payload:', {
                        goal_text: customGoalText,
                        client_goal_description: clientGoalDescription,
                        barriers: goalBarriers,
                        strengths: goalStrengths,
                        duration_days: finalDuration,
                        notes: goalNotes,
                        client_id: selectedClient?.id,
                        treatment_plan_id: selectedTreatmentPlan?.id,
                        set_as_active: true
                      });
                      
                      await axios.post(
                        `${API}/client-pans/${selectedPANForGoal.id}/goals`,
                        {
                          goal_text: customGoalText,
                          client_goal_description: clientGoalDescription,
                          barriers: goalBarriers,
                          strengths: goalStrengths,
                          duration_days: finalDuration,
                          notes: goalNotes,
                          client_id: selectedClient.id,
                          treatment_plan_id: selectedTreatmentPlan.id,
                          set_as_active: true // Signal backend to deactivate other goals
                        },
                        {
                          headers: {
                            Authorization: `Bearer ${sessionStorage.getItem('token')}`
                          }
                        }
                      );
                      
                      toast.success('Custom goal created (previous goals set inactive)');
                      setShowAddGoalDialog(false);
                      setCustomGoalText('');
                      setClientGoalDescription('');
                      setGoalBarriers('');
                      setGoalStrengths('');
                      setGoalDuration(90);
                      setCustomGoalDuration('');
                      setGoalNotes('');
                      await refetchTreatmentPlans(selectedClient.id);
                    } catch (error) {
                      console.error('❌ Error creating custom goal:', error);
                      console.error('Error response:', error.response);
                      console.error('Error status:', error.response?.status);
                      console.error('Error detail:', error.response?.data);
                      toast.error(error.response?.data?.detail || `Failed to create custom goal: ${error.response?.status || 'Unknown error'}`);
                    }
                  }}
                  className="bg-teal-600 hover:bg-teal-700 w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Save Goal
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-end border-t pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddGoalDialog(false);
                setCustomGoalText('');
                setClientGoalDescription('');
                setGoalBarriers('');
                setGoalStrengths('');
                setGoalDuration(90);
                setCustomGoalDuration('');
                setGoalNotes('');
              }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customize Goal Dialog */}
      <Dialog open={showCustomizeGoalDialog} onOpenChange={setShowCustomizeGoalDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedGoalTemplate ? 'Save Goal' : 'Save Goal'}
            </DialogTitle>
            <DialogDescription>
              {selectedGoalTemplate 
                ? 'Personalize this goal template with client-specific details, barriers, and strengths'
                : 'Create a custom goal tailored to this client\'s unique needs and circumstances'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedGoalTemplate && (
              <div>
                <Label className="text-sm font-medium">Original Template Goal</Label>
                <div className="p-3 bg-gray-50 rounded border text-sm text-gray-700 mb-3">
                  {selectedGoalTemplate.goal_text}
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="customized-text">
                Goal Text *
              </Label>
              <DebouncedTextarea
                id="customized-text"
                value={goalCustomization.customized_text}
                onChange={(e) => setGoalCustomization(prev => ({...prev, customized_text: e.target.value}))}
                placeholder={selectedGoalTemplate 
                  ? "Clinical/professional goal statement..."
                  : "Write a specific, measurable goal for this client..."
                }
                rows={3}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="client-description">Client's Goal (in their own words) *</Label>
              <DebouncedTextarea
                id="client-description"
                value={goalCustomization.client_goal_description || ''}
                onChange={(e) => setGoalCustomization(prev => ({...prev, client_goal_description: e.target.value}))}
                placeholder="How does the client describe this goal in their own words?"
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="barriers">Client Barriers</Label>
                <DebouncedTextarea
                  id="barriers"
                  value={goalCustomization.client_barriers}
                  onChange={(e) => setGoalCustomization(prev => ({...prev, client_barriers: e.target.value}))}
                  placeholder="What barriers might prevent achieving this goal?"
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="strengths">Client Strengths</Label>
                <DebouncedTextarea
                  id="strengths"
                  value={goalCustomization.client_strengths}
                  onChange={(e) => setGoalCustomization(prev => ({...prev, client_strengths: e.target.value}))}
                  placeholder="What client strengths support this goal?"
                  rows={3}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="duration">Goal Duration * (Required)</Label>
                <Select
                  value={goalCustomization.duration_days === 'custom' ? 'custom' : String(goalCustomization.duration_days)}
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      setGoalCustomization(prev => ({...prev, duration_days: 'custom', custom_duration: ''}));
                    } else {
                      setGoalCustomization(prev => ({...prev, duration_days: parseInt(value), custom_duration: ''}));
                    }
                  }}
                >
                  <SelectTrigger id="duration">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 days (1 month)</SelectItem>
                    <SelectItem value="60">60 days (2 months)</SelectItem>
                    <SelectItem value="90">90 days (3 months)</SelectItem>
                    <SelectItem value="180">180 days (6 months)</SelectItem>
                    <SelectItem value="365">365 days (1 year)</SelectItem>
                    <SelectItem value="custom">Custom duration</SelectItem>
                  </SelectContent>
                </Select>
                {goalCustomization.duration_days === 'custom' && (
                  <Input
                    type="number"
                    min="1"
                    placeholder="Enter custom days"
                    value={goalCustomization.custom_duration}
                    onChange={(e) => setGoalCustomization(prev => ({...prev, custom_duration: e.target.value}))}
                    className="mt-2"
                  />
                )}
                {(newPlanStartDate || selectedTreatmentPlan?.start_date) && goalCustomization.duration_days !== 'custom' && goalCustomization.duration_days && (
                  <p className="text-xs text-teal-600 mt-1 font-medium">
                    Duration: {goalCustomization.duration_days} days | Expected End: {
                      (() => {
                        const startDate = new Date(newPlanStartDate || selectedTreatmentPlan.start_date);
                        const endDate = new Date(startDate);
                        endDate.setDate(endDate.getDate() + goalCustomization.duration_days);
                        return endDate.toLocaleDateString();
                      })()
                    }
                  </p>
                )}
                {(newPlanStartDate || selectedTreatmentPlan?.start_date) && goalCustomization.duration_days === 'custom' && goalCustomization.custom_duration && (
                  <p className="text-xs text-teal-600 mt-1 font-medium">
                    Duration: {goalCustomization.custom_duration} days | Expected End: {
                      (() => {
                        const startDate = new Date(newPlanStartDate || selectedTreatmentPlan.start_date);
                        const endDate = new Date(startDate);
                        endDate.setDate(endDate.getDate() + parseInt(goalCustomization.custom_duration));
                        return endDate.toLocaleDateString();
                      })()
                    }
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="goal-notes">Additional Notes</Label>
                <DebouncedInput
                  id="goal-notes"
                  value={goalCustomization.notes}
                  onChange={(e) => setGoalCustomization(prev => ({...prev, notes: e.target.value}))}
                  placeholder="Any additional notes or context"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-between border-t pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowCustomizeGoalDialog(false);
                setSelectedGoalTemplate(null);
                setGoalCustomization({
                  customized_text: '',
                  client_barriers: '',
                  client_strengths: '',
                  duration_days: 90,
                  custom_duration: '',
                  notes: ''
                });
              }}
            >
              Cancel
            </Button>
            
            <Button
              onClick={async () => {
                if (!goalCustomization.customized_text.trim()) {
                  toast.error('Please provide customized goal text');
                  return;
                }
                
                if (!goalCustomization.client_goal_description?.trim()) {
                  toast.error('Please enter the goal in client\'s own words');
                  return;
                }
                
                // Validate duration
                let finalDuration = goalCustomization.duration_days;
                if (finalDuration === 'custom') {
                  if (!goalCustomization.custom_duration || parseInt(goalCustomization.custom_duration) < 1) {
                    toast.error('Please enter a valid custom duration (minimum 1 day)');
                    return;
                  }
                  finalDuration = parseInt(goalCustomization.custom_duration);
                }
                
                if (!finalDuration || finalDuration < 1) {
                  toast.error('Please select a valid duration');
                  return;
                }
                
                try {
                  const goalData = {
                    goal_id: selectedGoalTemplate?.id || null,  // Handle custom goals with no template
                    goal_text: goalCustomization.customized_text,
                    client_goal_description: goalCustomization.client_goal_description,
                    barriers: goalCustomization.client_barriers,
                    strengths: goalCustomization.client_strengths,
                    duration_days: finalDuration,
                    notes: goalCustomization.notes,
                    client_id: selectedClient.id
                  };
                  
                  // Check if adding to existing plan or building new one
                  if (selectedPANForGoal.index !== undefined) {
                    // Building new plan - check if PAN already has an active goal
                    const updatedPANs = [...planPANs];
                    const existingGoals = updatedPANs[selectedPANForGoal.index].goals || [];
                    
                    if (existingGoals.length > 0) {
                      const confirmReplace = confirm(
                        `This PAN already has ${existingGoals.length} goal(s). Each PAN should only have 1 active goal.\n\n` +
                        'Would you like to replace the existing goal with this new one?'
                      );
                      
                      if (!confirmReplace) {
                        return;
                      }
                      
                      // Replace existing goals with new one
                      updatedPANs[selectedPANForGoal.index].goals = [{ ...goalData, objectives: [] }];
                    } else {
                      // Add first goal to PAN
                      updatedPANs[selectedPANForGoal.index].goals = [{ ...goalData, objectives: [] }];
                    }
                    
                    setPlanPANs(updatedPANs);
                    toast.success('Goal added (1 active goal per PAN)');
                  } else {
                    // Adding to existing plan - check for existing active goals
                    const confirmAdd = confirm(
                      'Each PAN should only have 1 active goal. Adding this goal will set any existing goals for this PAN as inactive.\n\n' +
                      'Do you want to continue?'
                    );
                    
                    if (!confirmAdd) {
                      return;
                    }
                    
                    await axios.post(
                      `${API}/client-pans/${selectedPANForGoal.id}/goals`,
                      { 
                        ...goalData,
                        treatment_plan_id: selectedTreatmentPlan.id,
                        set_as_active: true // Signal backend to deactivate other goals
                      },
                      {
                        headers: {
                          Authorization: `Bearer ${sessionStorage.getItem('token')}`
                        }
                      }
                    );
                    
                    toast.success('Goal added (previous goals set inactive)');
                    await refetchTreatmentPlans(selectedClient.id);
                  }
                  
                  // Close dialogs and reset
                  setShowCustomizeGoalDialog(false);
                  setShowAddGoalDialog(false);
                  setShowInlinePANGoalSelector(null);
                  setSelectedGoalTemplate(null);
                  setGoalCustomization({
                    customized_text: '',
                    client_goal_description: '',
                    client_barriers: '',
                    client_strengths: '',
                    duration_days: 90,
                    custom_duration: '',
                    notes: ''
                  });
                } catch (error) {
                  console.error('Error adding customized goal:', error);
                  toast.error(error.response?.data?.detail || 'Failed to add customized goal');
                }
              }}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Save className="h-4 w-4 mr-2" />
              Add Customized Goal
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Document Rules Dialog */}
      <Dialog open={showManageRulesDialog} onOpenChange={setShowManageRulesDialog}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <div>
              <DialogTitle>Manage Document Deadline Rules</DialogTitle>
              <DialogDescription>
                View, edit, and manage all document deadline rules in your system
              </DialogDescription>
            </div>
            <AdminJumpToDropdown currentDialogId="documentDeadlines" isDirty={currentAdminDialogDirty} />
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">{documentRules.length} rules configured</p>
              <Button
                size="sm"
                onClick={() => {
                  setEditingRule(null);
                  setShowRuleDialog(true);
                }}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Rule
              </Button>
            </div>
            
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium">Rule Name</th>
                    <th className="text-left p-3 font-medium">Service Type</th>
                    <th className="text-left p-3 font-medium">Document Type</th>
                    <th className="text-left p-3 font-medium">Timing</th>
                    <th className="text-left p-3 font-medium">Age / Insurance</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documentRules.map((rule, index) => (
                    <tr key={rule.id} className={`border-b hover:bg-gray-50 ${!rule.is_active ? 'opacity-60' : ''}`}>
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{rule.rule_name}</p>
                          <p className="text-xs text-gray-500">Priority: {rule.priority}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <p className="text-sm">
                          {rule.service_type === 'all_services' 
                            ? 'All Services' 
                            : serviceTypes.find(st => st.id === rule.service_type)?.display_name || rule.service_type}
                        </p>
                      </td>
                      <td className="p-3">
                        <p className="text-sm">{rule.document_type}</p>
                        <p className="text-xs text-gray-500">{rule.document_category}</p>
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="text-sm font-medium">{rule.timing?.trigger}</p>
                          {rule.timing?.frequency_days && (
                            <p className="text-xs text-gray-500">{rule.timing.frequency_days} days</p>
                          )}
                          {rule.timing?.description && (
                            <p className="text-xs text-teal-600">{rule.timing.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="space-y-1">
                          {/* Age criteria */}
                          {(rule.conditions?.age_min !== null || rule.conditions?.age_max !== null) ? (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                              Age: {rule.conditions?.age_min || 0}-{rule.conditions?.age_max || '∞'}
                            </Badge>
                          ) : (
                            <span className="text-xs text-gray-400">All ages</span>
                          )}
                          {/* Insurance criteria */}
                          {rule.conditions?.insurance_plans?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {rule.conditions.insurance_plans.slice(0, 2).map(plan => (
                                <Badge key={plan} variant="secondary" className="text-xs">
                                  {plan}
                                </Badge>
                              ))}
                              {rule.conditions.insurance_plans.length > 2 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{rule.conditions.insurance_plans.length - 2} more
                                </Badge>
                              )}
                            </div>
                          ) : rule.conditions?.insurance_types?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {rule.conditions.insurance_types.slice(0, 2).map(type => (
                                <Badge key={type} variant="secondary" className="text-xs">
                                  {type}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">All insurances</span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        {rule.is_active ? (
                          <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>
                        ) : (
                          <Badge variant="destructive">Inactive</Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingRule(rule);
                              setShowRuleDialog(true);
                            }}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-800"
                            onClick={async () => {
                              if (confirm(`Are you sure you want to delete "${rule.rule_name}"?`)) {
                                try {
                                  await axios.delete(`${API}/document-deadline-rules/${rule.id}`, {
                                    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                  });
                                  toast.success('Rule deleted successfully');
                                  await fetchDocumentRules(true);
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Failed to delete rule');
                                }
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {documentRules.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No document deadline rules found</p>
                <p className="text-sm">Add your first rule to get started</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowManageRulesDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Deadline Rule Dialog */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Document Deadline Rule' : 'Add Document Deadline Rule'}</DialogTitle>
          </DialogHeader>
          <form className="space-y-4 py-4" onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            
            const ruleData = {
              rule_name: formData.get('rule_name'),
              service_type: formData.get('service_type'),
              document_type: formData.get('document_type'),
              document_category: formData.get('document_category'),
              timing: {
                trigger: formData.get('trigger'),
                frequency_days: formData.get('trigger') === 'recurring' ? parseInt(formData.get('frequency_days')) : null,
                session_count: formData.get('trigger') === 'after_sessions' ? parseInt(formData.get('session_count')) : null,
                description: formData.get('timing_description')
              },
              conditions: {
                visit_frequencies: Array.from(formData.getAll('visit_frequencies')),
                insurance_plans: Array.from(formData.getAll('insurance_plans')),
                age_min: formData.get('age_min') ? parseInt(formData.get('age_min')) : null,
                age_max: formData.get('age_max') ? parseInt(formData.get('age_max')) : null,
                sub_group: formData.get('sub_group'),
                additional_conditions: formData.get('additional_conditions')
              },
              notes: formData.get('notes'),
              priority: formData.get('priority'),
              is_active: formData.get('is_active') === 'on'
            };
            
            try {
              if (editingRule) {
                await axios.put(
                  `${API}/document-deadline-rules/${editingRule.id}`,
                  ruleData,
                  { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                );
                toast.success('Rule updated successfully');
              } else {
                await axios.post(
                  `${API}/document-deadline-rules`,
                  ruleData,
                  { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                );
                toast.success('Rule created successfully');
              }
              
              setShowRuleDialog(false);
              await fetchDocumentRules(true);
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Failed to save rule');
            }
          }}>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="rule_name">Rule Name *</Label>
                <Input
                  id="rule_name"
                  name="rule_name"
                  defaultValue={editingRule?.rule_name}
                  placeholder="e.g., Outpatient Therapy - IAP Review (Weekly)"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="service_type">Service Type *</Label>
                <Select name="service_type" defaultValue={editingRule?.service_type} required>
                  <SelectTrigger id="service_type">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_services">All Services (Universal)</SelectItem>
                    {serviceTypes.map(st => (
                      <SelectItem key={st.id} value={st.id}>{st.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="document_type">Document Type *</Label>
                <Input
                  id="document_type"
                  name="document_type"
                  defaultValue={editingRule?.document_type}
                  placeholder="e.g., Individualized Action Plan"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="document_category">Document Category</Label>
                <Select name="document_category" defaultValue={editingRule?.document_category || 'general'}>
                  <SelectTrigger id="document_category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="assessment">Assessment</SelectItem>
                    <SelectItem value="treatment_plan">Treatment Plan</SelectItem>
                    <SelectItem value="notes">Progress Notes</SelectItem>
                    <SelectItem value="medical">Medical</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select name="priority" defaultValue={editingRule?.priority || 'medium'}>
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Timing</Label>
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div>
                  <Label htmlFor="trigger">When Due *</Label>
                  <Select name="trigger" defaultValue={editingRule?.timing?.trigger || 'recurring'} required>
                    <SelectTrigger id="trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admission">At Admission</SelectItem>
                      <SelectItem value="recurring">Recurring</SelectItem>
                      <SelectItem value="after_sessions">After X Sessions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="frequency_days">Frequency (Days)</Label>
                  <Input
                    id="frequency_days"
                    name="frequency_days"
                    type="number"
                    defaultValue={editingRule?.timing?.frequency_days}
                    placeholder="90"
                  />
                  <p className="text-xs text-gray-500 mt-1">For recurring rules</p>
                </div>
                
                <div>
                  <Label htmlFor="session_count">Session Count</Label>
                  <Input
                    id="session_count"
                    name="session_count"
                    type="number"
                    defaultValue={editingRule?.timing?.session_count}
                    placeholder="8"
                  />
                  <p className="text-xs text-gray-500 mt-1">For session-based rules</p>
                </div>
              </div>
              
              <div className="mt-3">
                <Label htmlFor="timing_description">Description</Label>
                <Input
                  id="timing_description"
                  name="timing_description"
                  defaultValue={editingRule?.timing?.description}
                  placeholder="e.g., every 3 months (90 days)"
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Conditions</Label>
              <div className="space-y-3 mt-3">
                <div>
                  <Label>Visit Frequencies (select all that apply)</Label>
                  <div className="flex flex-wrap gap-4 mt-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="visit_frequencies"
                        value="more_than_weekly"
                        defaultChecked={editingRule?.conditions?.visit_frequencies?.includes('more_than_weekly')}
                      />
                      More than once a week
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="visit_frequencies"
                        value="weekly"
                        defaultChecked={editingRule?.conditions?.visit_frequencies?.includes('weekly')}
                      />
                      Weekly
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="visit_frequencies"
                        value="bi-weekly"
                        defaultChecked={editingRule?.conditions?.visit_frequencies?.includes('bi-weekly')}
                      />
                      Bi-weekly
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="visit_frequencies"
                        value="monthly"
                        defaultChecked={editingRule?.conditions?.visit_frequencies?.includes('monthly')}
                      />
                      Monthly
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave unchecked for all frequencies</p>
                </div>
                
                <div>
                  <Label htmlFor="age_groups">Age Criteria</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div>
                      <Label className="text-xs text-gray-500">Min Age</Label>
                      <Input
                        id="age_min"
                        name="age_min"
                        type="number"
                        min="0"
                        max="120"
                        defaultValue={editingRule?.conditions?.age_min || ''}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Max Age</Label>
                      <Input
                        id="age_max"
                        name="age_max"
                        type="number"
                        min="0"
                        max="120"
                        defaultValue={editingRule?.conditions?.age_max || ''}
                        placeholder="120"
                      />
                    </div>
                    <div className="flex items-end pb-2">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById('age_min').value = '0';
                            document.getElementById('age_max').value = '20';
                          }}
                        >
                          ≤20 (CANS)
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById('age_min').value = '21';
                            document.getElementById('age_max').value = '120';
                          }}
                        >
                          21+
                        </Button>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave blank for all ages. For CANS, use ages 0-20.</p>
                </div>
                
                <div>
                  <Label>Insurance Plans (select all that apply)</Label>
                  <div className="border rounded-lg p-3 mt-2 max-h-48 overflow-y-auto bg-gray-50">
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-600 mb-2">MassHealth & Related Plans:</p>
                      {['MassHealth', 'MBHP', 'Fallon 365', 'Tufts Health Together', 'BMC HealthNet', 'Neighborhood Health Plan', 'Health New England'].map(plan => (
                        <label key={plan} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            name="insurance_plans"
                            value={plan}
                            defaultChecked={editingRule?.conditions?.insurance_plans?.includes(plan) || editingRule?.conditions?.insurance_types?.includes(plan)}
                          />
                          {plan}
                        </label>
                      ))}
                      <p className="text-xs font-medium text-gray-600 mt-3 mb-2">Commercial Plans:</p>
                      {['Blue Cross Blue Shield', 'Harvard Pilgrim', 'Tufts Health Plan', 'Aetna', 'Cigna', 'United Healthcare'].map(plan => (
                        <label key={plan} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            name="insurance_plans"
                            value={plan}
                            defaultChecked={editingRule?.conditions?.insurance_plans?.includes(plan) || editingRule?.conditions?.insurance_types?.includes(plan)}
                          />
                          {plan}
                        </label>
                      ))}
                      <p className="text-xs font-medium text-gray-600 mt-3 mb-2">Government:</p>
                      {['Medicare', 'Medicaid', 'Tricare'].map(plan => (
                        <label key={plan} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            name="insurance_plans"
                            value={plan}
                            defaultChecked={editingRule?.conditions?.insurance_plans?.includes(plan) || editingRule?.conditions?.insurance_types?.includes(plan)}
                          />
                          {plan}
                        </label>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave unchecked for all insurance plans</p>
                </div>
                
                <div>
                  <Label htmlFor="sub_group">Sub-Group</Label>
                  <Input
                    id="sub_group"
                    name="sub_group"
                    defaultValue={editingRule?.conditions?.sub_group}
                    placeholder="e.g., Med: AntiPsychotics"
                  />
                </div>
                
                <div>
                  <Label htmlFor="additional_conditions">Additional Conditions</Label>
                  <Textarea
                    id="additional_conditions"
                    name="additional_conditions"
                    defaultValue={editingRule?.conditions?.additional_conditions}
                    placeholder="Any other conditions..."
                    rows={2}
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                defaultValue={editingRule?.notes}
                placeholder="Additional notes about this rule..."
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                defaultChecked={editingRule?.is_active !== false}
                className="h-4 w-4"
              />
              <Label htmlFor="is_active">Active (rule is enforced)</Label>
            </div>
            
            <div className="flex justify-between border-t pt-4">
              {editingRule && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to deactivate this rule?')) {
                      try {
                        await axios.delete(`${API}/document-deadline-rules/${editingRule.id}`, {
                          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                        });
                        toast.success('Rule deactivated');
                        setShowRuleDialog(false);
                        await fetchDocumentRules(true);
                      } catch (error) {
                        toast.error('Failed to deactivate rule');
                      }
                    }
                  }}
                >
                  Deactivate
                </Button>
              )}
              <div className="flex gap-2 ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowRuleDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {editingRule ? 'Update' : 'Create'} Rule
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Service Types Dialog */}
      <Dialog open={showManageServiceTypesDialog} onOpenChange={setShowManageServiceTypesDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <div>
              <DialogTitle>Manage Service Types</DialogTitle>
              <DialogDescription>
                View, edit, and manage all service types in your organization
              </DialogDescription>
            </div>
            <AdminJumpToDropdown currentDialogId="serviceTypes" isDirty={currentAdminDialogDirty} />
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">{serviceTypes.length} service types</p>
              <Button
                size="sm"
                onClick={() => {
                  setEditingServiceType(null);
                  setServiceTypeFormData({
                    name: '',
                    display_name: '',
                    short_name: '',
                    description: '',
                    required_documents: [],
                    requirements: '',
                    notes: '',
                    sort_order: serviceTypes.length + 1
                  });
                  setShowServiceTypeDialog(true);
                }}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Service Type
              </Button>
            </div>
            
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium">Service Type</th>
                    <th className="text-left p-3 font-medium">Description</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceTypes.map((serviceType, index) => (
                    <tr key={serviceType.id} className={`border-b hover:bg-gray-50 ${!serviceType.is_active ? 'opacity-60' : ''}`}>
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{serviceType.display_name}</p>
                          <p className="text-xs text-gray-500">{serviceType.name}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <p className="text-sm text-gray-600 max-w-xs truncate">{serviceType.description}</p>
                      </td>
                      <td className="p-3">
                        {serviceType.is_active ? (
                          <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>
                        ) : (
                          <Badge variant="destructive">Inactive</Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingServiceType(serviceType);
                              setServiceTypeFormData({
                                name: serviceType.name,
                                display_name: serviceType.display_name,
                                short_name: serviceType.short_name || '',
                                description: serviceType.description || '',
                                required_documents: serviceType.required_documents || [],
                                requirements: serviceType.requirements || '',
                                notes: serviceType.notes || '',
                                sort_order: serviceType.sort_order || index,
                                is_active: serviceType.is_active !== false,
                                requires_discharge: serviceType.requires_discharge !== false
                              });
                              setShowServiceTypeDialog(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-800"
                            onClick={async () => {
                              if (confirm(`Are you sure you want to ${serviceType.is_active ? 'deactivate' : 'activate'} "${serviceType.display_name}"?`)) {
                                try {
                                  await axios.patch(
                                    `${API}/treatment-planning/service-types/${serviceType.id}`,
                                    { is_active: !serviceType.is_active },
                                    { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                                  );
                                  toast.success(`Service type ${serviceType.is_active ? 'deactivated' : 'activated'} successfully`);
                                  await fetchServiceTypes();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Failed to update service type');
                                }
                              }
                            }}
                          >
                            {serviceType.is_active ? <Trash2 className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {serviceTypes.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No service types found</p>
                <p className="text-sm">Add your first service type to get started</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowManageServiceTypesDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Type Management Dialog */}
      <Dialog open={showServiceTypeDialog} onOpenChange={setShowServiceTypeDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingServiceType ? 'Edit Service Type' : 'Add Service Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="st-name">Service Name *</Label>
                <DebouncedInput
                  id="st-name"
                  value={serviceTypeFormData.name}
                  onChange={(e) => setServiceTypeFormData(prev => ({...prev, name: e.target.value}))}
                  placeholder="e.g., Outpatient Therapy – Adult"
                />
              </div>
              
              <div>
                <Label htmlFor="st-display-name">Display Name *</Label>
                <DebouncedInput
                  id="st-display-name"
                  value={serviceTypeFormData.display_name}
                  onChange={(e) => setServiceTypeFormData(prev => ({...prev, display_name: e.target.value}))}
                  placeholder="How it appears in dropdowns"
                />
              </div>
              
              <div>
                <Label htmlFor="st-short-name">Short Name / Code</Label>
                <DebouncedInput
                  id="st-short-name"
                  value={serviceTypeFormData.short_name}
                  onChange={(e) => setServiceTypeFormData(prev => ({...prev, short_name: e.target.value}))}
                  placeholder="e.g., OT Adult, IHT"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="st-description">Description</Label>
              <DebouncedTextarea
                id="st-description"
                value={serviceTypeFormData.description}
                onChange={(e) => setServiceTypeFormData(prev => ({...prev, description: e.target.value}))}
                placeholder="Brief description of this service type"
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="st-requirements">Provider Requirements</Label>
              <DebouncedTextarea
                id="st-requirements"
                value={serviceTypeFormData.requirements}
                onChange={(e) => setServiceTypeFormData(prev => ({...prev, requirements: e.target.value}))}
                placeholder="e.g., Licensed therapist required, LADC certification"
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="st-required-docs">Required Documents (one per line)</Label>
              <DebouncedTextarea
                id="st-required-docs"
                value={serviceTypeFormData.required_documents.join('\n')}
                onChange={(e) => setServiceTypeFormData(prev => ({
                  ...prev, 
                  required_documents: e.target.value.split('\n').filter(d => d.trim())
                }))}
                placeholder="Treatment Plan&#10;Progress Notes&#10;Discharge Summary"
                rows={4}
              />
            </div>
            
            <div>
              <Label htmlFor="st-notes">Additional Notes</Label>
              <DebouncedTextarea
                id="st-notes"
                value={serviceTypeFormData.notes}
                onChange={(e) => setServiceTypeFormData(prev => ({...prev, notes: e.target.value}))}
                placeholder="Any additional notes or guidelines"
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="st-active"
                checked={serviceTypeFormData.is_active}
                onChange={(e) => setServiceTypeFormData(prev => ({...prev, is_active: e.target.checked}))}
                className="h-4 w-4"
              />
              <Label htmlFor="st-active">Active (available for use)</Label>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <input
                type="checkbox"
                id="st-requires-discharge"
                checked={serviceTypeFormData.requires_discharge !== false}
                onChange={(e) => setServiceTypeFormData(prev => ({...prev, requires_discharge: e.target.checked}))}
                className="h-4 w-4"
              />
              <div>
                <Label htmlFor="st-requires-discharge" className="font-medium text-amber-800">Requires Discharge Note</Label>
                <p className="text-xs text-amber-600">When enabled, clients enrolled in this service must have a signed discharge form before being marked as discharged. Disable for services like evaluations that don't require formal discharge.</p>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between border-t pt-4">
            {editingServiceType && (
              <Button
                variant="destructive"
                onClick={async () => {
                  if (window.confirm('Are you sure you want to deactivate this service type? It cannot be deleted if used in active treatment plans.')) {
                    try {
                      await axios.delete(`${API}/treatment-planning/service-types/${editingServiceType.id}`, {
                        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                      });
                      toast.success('Service type deactivated');
                      setShowServiceTypeDialog(false);
                      await fetchServiceTypes();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to deactivate service type');
                    }
                  }
                }}
              >
                Deactivate
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button
                variant="outline"
                onClick={() => setShowServiceTypeDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!serviceTypeFormData.name || !serviceTypeFormData.display_name) {
                    toast.error('Please fill in required fields');
                    return;
                  }
                  
                  try {
                    if (editingServiceType) {
                      await axios.put(
                        `${API}/treatment-planning/service-types/${editingServiceType.id}`,
                        serviceTypeFormData,
                        {
                          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                        }
                      );
                      toast.success('Service type updated successfully');
                    } else {
                      await axios.post(
                        `${API}/treatment-planning/service-types`,
                        serviceTypeFormData,
                        {
                          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                        }
                      );
                      toast.success('Service type created successfully');
                    }
                    
                    setShowServiceTypeDialog(false);
                    await fetchServiceTypes();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to save service type');
                  }
                }}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {editingServiceType ? 'Update' : 'Create'} Service Type
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Objective Dialog */}
      <Dialog open={showAddObjectiveDialog} onOpenChange={setShowAddObjectiveDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Objective to Goal</DialogTitle>
            <p className="text-sm text-gray-600">
              Goal: <span className="font-semibold">{selectedGoalForObjective?.goal_text}</span>
            </p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Template Objectives */}
            <div>
              <Label className="text-base font-semibold">Select from Template Objectives</Label>
              <p className="text-xs text-gray-500 mb-3">Click an objective to add it to this goal</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableObjectives.length === 0 ? (
                  <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded">
                    No template objectives available for this goal. You can create a custom objective below.
                  </p>
                ) : (
                  availableObjectives.map(obj => (
                    <Card
                      key={obj.id}
                      className="cursor-pointer hover:bg-blue-50 transition-colors"
                      onClick={async () => {
                        try {
                          await axios.post(
                            `${API}/client-goals/${selectedGoalForObjective.id}/objectives`,
                            {
                              objective_id: obj.id,
                              objective_text: obj.objective_text,
                              intervention: obj.interventions?.length > 0 ? obj.interventions.map(i => typeof i === 'string' ? i : i.name).join(', ') : obj.intervention,
                              interventions: obj.interventions || (obj.intervention ? [obj.intervention] : []),
                              frequency: obj.frequency,
                              client_id: selectedClient.id,
                              treatment_plan_id: selectedTreatmentPlan.id
                            },
                            {
                              headers: {
                                Authorization: `Bearer ${sessionStorage.getItem('token')}`
                              }
                            }
                          );
                          
                          toast.success('Objective added successfully');
                          setShowAddObjectiveDialog(false);
                          await refetchTreatmentPlans(selectedClient.id);
                        } catch (error) {
                          console.error('Error adding objective:', error);
                          toast.error(error.response?.data?.detail || 'Failed to add objective');
                        }
                      }}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium">{obj.objective_text}</p>
                            {obj.interventions?.length > 0 && (
                              <div className="mt-2 pl-2 border-l-2 border-teal-200 space-y-1">
                                <span className="text-xs text-gray-500 font-medium">Interventions:</span>
                                {obj.interventions.map((intervention, intIdx) => (
                                  <div key={intIdx} className="text-xs text-gray-600 pl-2">
                                    <span className="font-medium">• {typeof intervention === 'string' ? intervention : intervention.name}</span>
                                    {typeof intervention === 'object' && intervention.description && (
                                      <span className="text-gray-400 text-xs italic ml-1">{intervention.description}</span>
                                    )}
                                    {typeof intervention === 'object' && (intervention.modality || intervention.frequency) && (
                                      <span className="text-gray-400 ml-1 text-xs">
                                        ({[intervention.modality, intervention.frequency].filter(Boolean).join(', ')})
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {!obj.interventions?.length && obj.intervention && (
                              <div className="text-xs text-gray-600 mt-1">• {obj.intervention}</div>
                            )}
                          </div>
                          <Plus className="h-5 w-5 text-blue-600 flex-shrink-0 ml-2" />
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>

            {/* Custom Objective */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Or Create Custom Objective</Label>
              <div className="space-y-3 mt-3">
                <div>
                  <Label htmlFor="custom-responsible-party" className="text-xs">Responsible Party *</Label>
                  <Select
                    value={objectiveFormData.responsible_party}
                    onValueChange={(value) => setObjectiveFormData(prev => ({...prev, responsible_party: value}))}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select responsible party" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="provider">Provider/Therapist</SelectItem>
                      <SelectItem value="family">Family/Caregiver</SelectItem>
                      <SelectItem value="school">School/Teacher</SelectItem>
                      <SelectItem value="community">Community Support</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="custom-objective-text" className="text-xs">What They Will Do *</Label>
                  <DebouncedTextarea
                    id="custom-objective-text"
                    value={objectiveFormData.objective_text}
                    onChange={(e) => setObjectiveFormData(prev => ({...prev, objective_text: e.target.value}))}
                    placeholder="Describe what the responsible party will do..."
                    rows={2}
                    className="resize-none overflow-hidden text-xs"
                    onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                  />
                </div>
                
                {/* Inline Interventions Section */}
                <div>
                  <Label className="text-xs font-semibold">Interventions</Label>
                  
                  {/* List added interventions with editable fields */}
                  {addObjectiveInterventions.length > 0 && (
                    <div className="space-y-1.5 mt-2 mb-2">
                      {addObjectiveInterventions.map((intervention, idx) => (
                        <div key={intervention.id || idx} className="p-2 bg-white border rounded text-xs space-y-1.5">
                          <div className="flex items-center justify-between">
                            <input
                              type="text"
                              value={intervention.name || ''}
                              onChange={(e) => {
                                setAddObjectiveInterventions(prev => prev.map((int, i) =>
                                  i === idx ? { ...int, name: e.target.value } : int
                                ));
                              }}
                              className="font-medium text-teal-700 text-xs bg-transparent border-none focus:outline-none focus:ring-0 flex-1"
                              placeholder="Intervention name..."
                            />
                            <button 
                              type="button" 
                              className="text-red-400 hover:text-red-600 ml-2 shrink-0"
                              onClick={() => {
                                setAddObjectiveInterventions(prev => prev.filter((_, i) => i !== idx));
                              }}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                          <DebouncedTextarea
                            value={intervention.description || ''}
                            onChange={(e) => {
                              setAddObjectiveInterventions(prev => prev.map((int, i) =>
                                i === idx ? { ...int, description: e.target.value } : int
                              ));
                            }}
                            placeholder="Intervention description / full text..."
                            rows={2}
                            className="w-full text-[11px] text-gray-700 bg-gray-50 border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-500 resize-none"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <select
                              value={intervention.modality || ''}
                              onChange={(e) => {
                                setAddObjectiveInterventions(prev => prev.map((int, i) =>
                                  i === idx ? { ...int, modality: e.target.value } : int
                                ));
                              }}
                              className="text-xs text-gray-600 bg-white border rounded px-2 py-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
                            >
                              <option value="">Modality...</option>
                              <option value="Individual">Individual</option>
                              <option value="Group">Group</option>
                              <option value="Family">Family</option>
                              <option value="Couples">Couples</option>
                              <option value="Telehealth">Telehealth</option>
                              <option value="In-person">In-person</option>
                            </select>
                            <select
                              value={intervention.frequency || ''}
                              onChange={(e) => {
                                setAddObjectiveInterventions(prev => prev.map((int, i) =>
                                  i === idx ? { ...int, frequency: e.target.value } : int
                                ));
                              }}
                              className="text-xs text-gray-600 bg-white border rounded px-2 py-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
                            >
                              <option value="">Frequency...</option>
                              <option value="Each session">Each session</option>
                              <option value="Weekly">Weekly</option>
                              <option value="Bi-weekly">Bi-weekly</option>
                              <option value="Monthly">Monthly</option>
                              <option value="As needed">As needed</option>
                              <option value="PRN">PRN</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Inline intervention selector */}
                  {showAddObjectiveInterventionLibrary ? (
                    <div className="border border-dashed border-gray-200 rounded p-2 space-y-2 mt-2">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search interventions..."
                          value={interventionSearchQuery}
                          onChange={(e) => setInterventionSearchQuery(e.target.value)}
                          className="w-full pl-7 pr-2 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                          autoFocus
                        />
                      </div>
                      
                      {/* Custom intervention option */}
                      <div
                        className="cursor-pointer hover:bg-teal-50 border border-dashed border-teal-400 rounded px-2 py-1.5"
                        onClick={() => {
                          setAddObjectiveInterventions(prev => [...prev, {
                            id: Date.now().toString(),
                            name: '',
                            description: '',
                            modality: '',
                            frequency: '',
                            isCustom: true
                          }]);
                          setShowAddObjectiveInterventionLibrary(false);
                          setInterventionSearchQuery('');
                        }}
                      >
                        <div className="flex items-center gap-1.5 text-teal-700">
                          <Plus className="h-3 w-3" />
                          <span className="text-xs font-medium">Custom Intervention</span>
                        </div>
                      </div>

                      {/* Library items */}
                      {interventionCategoriesLoading ? (
                        <p className="text-xs text-gray-400 text-center py-2">Loading...</p>
                      ) : (
                        <div className="max-h-[200px] overflow-y-auto space-y-0.5">
                          {interventionCategories
                            .flatMap(cat => cat.interventions.map(int => ({ ...int, categoryName: cat.category.name })))
                            .filter(int => !interventionSearchQuery || int.name.toLowerCase().includes(interventionSearchQuery.toLowerCase()) || (int.description && int.description.toLowerCase().includes(interventionSearchQuery.toLowerCase())))
                            .map(intervention => {
                              const isAlreadyAdded = addObjectiveInterventions.some(p => p.name === intervention.name);
                              return (
                                <div
                                  key={intervention.id}
                                  className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer transition-colors ${isAlreadyAdded ? 'bg-teal-50 text-teal-600 opacity-60' : 'hover:bg-gray-50'}`}
                                  onClick={() => {
                                    if (isAlreadyAdded) return;
                                    setAddObjectiveInterventions(prev => [...prev, {
                                      id: intervention.id,
                                      name: intervention.name,
                                      description: intervention.description || '',
                                      modality: intervention.modality || '',
                                      frequency: intervention.frequency || '',
                                    }]);
                                    setShowAddObjectiveInterventionLibrary(false);
                                    setInterventionSearchQuery('');
                                  }}
                                >
                                  {isAlreadyAdded ? (
                                    <Check className="h-3 w-3 text-teal-500 shrink-0" />
                                  ) : (
                                    <Plus className="h-3 w-3 text-gray-400 shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium">{intervention.name}</span>
                                    {intervention.description && (
                                      <span className="text-gray-400 ml-1 truncate">— {intervention.description}</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}

                      <button
                        type="button"
                        className="text-xs text-gray-500 hover:text-gray-700 mt-1"
                        onClick={() => {
                          setShowAddObjectiveInterventionLibrary(false);
                          setInterventionSearchQuery('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 w-full border-dashed text-xs h-7"
                      onClick={async () => {
                        try {
                          setInterventionCategoriesLoading(true);
                          const response = await axios.get(`${API}/intervention-library/grouped`, {
                            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                          });
                          setInterventionCategories(response.data);
                        } catch (error) {
                          console.error('Error loading interventions:', error);
                          toast.error('Failed to load intervention library');
                        } finally {
                          setInterventionCategoriesLoading(false);
                        }
                        setShowAddObjectiveInterventionLibrary(true);
                      }}
                    >
                      <Plus className="h-3 w-3 mr-1" /> Browse Interventions
                    </Button>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="objective-treatment-desc" className="text-xs">Treatment Details (Optional)</Label>
                  <DebouncedTextarea
                    id="objective-treatment-desc"
                    value={objectiveFormData.treatment_description}
                    onChange={(e) => setObjectiveFormData(prev => ({...prev, treatment_description: e.target.value}))}
                    placeholder="Additional notes about the treatment approach..."
                    rows={2}
                    className="text-xs"
                  />
                </div>
                
                <Button
                  onClick={async () => {
                    if (!objectiveFormData.responsible_party) {
                      toast.error('Please select a responsible party');
                      return;
                    }
                    if (!objectiveFormData.objective_text?.trim()) {
                      toast.error('Please describe what they will do');
                      return;
                    }
                    
                    const interventionString = addObjectiveInterventions.length > 0 
                      ? addObjectiveInterventions.map(i => typeof i === 'string' ? i : i.name).join(', ')
                      : null;
                    
                    try {
                      await axios.post(
                        `${API}/client-goals/${selectedGoalForObjective.id}/objectives`,
                        {
                          responsible_party: objectiveFormData.responsible_party,
                          objective_text: objectiveFormData.objective_text,
                          intervention: interventionString,
                          interventions: addObjectiveInterventions,
                          treatment_description: objectiveFormData.treatment_description,
                          client_id: selectedClient.id,
                          treatment_plan_id: selectedTreatmentPlan.id
                        },
                        {
                          headers: {
                            Authorization: `Bearer ${sessionStorage.getItem('token')}`
                          }
                        }
                      );
                      
                      toast.success('Objective created successfully');
                      setShowAddObjectiveDialog(false);
                      setObjectiveFormData({
                        responsible_party: 'client',
                        objective_text: '',
                        intervention: '',
                        custom_intervention: '',
                        treatment_description: ''
                      });
                      setAddObjectiveInterventions([]);
                      await refetchTreatmentPlans(selectedClient.id);
                    } catch (error) {
                      console.error('Error creating objective:', error);
                      toast.error(error.response?.data?.detail || 'Failed to create objective');
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700 w-full text-xs h-8"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Save Objective
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-end border-t pt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowAddObjectiveDialog(false);
                setCustomObjectiveText('');
                setObjectiveIntervention('');
                setObjectiveFrequency('');
                setObjectiveModality('');
                setAddObjectiveInterventions([]);
              }}
              className="text-xs h-8"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Objective Dialog */}
      <Dialog open={showEditObjectiveDialog} onOpenChange={(open) => {
        setShowEditObjectiveDialog(open);
        if (!open) {
          setObjectiveToEdit(null);
          setEditingInterventionIdx(null); // Reset intervention editing state
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Objective</DialogTitle>
            <DialogDescription>
              Update the objective details, interventions, and treatment approach
            </DialogDescription>
          </DialogHeader>
          {objectiveToEdit && (
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-responsible-party">Responsible Party *</Label>
                <Select
                  value={objectiveToEdit.responsible_party || 'client'}
                  onValueChange={(value) => setObjectiveToEdit(prev => ({...prev, responsible_party: value}))}
                >
                  <SelectTrigger id="edit-responsible-party">
                    <SelectValue placeholder="Select responsible party" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="provider">Provider/Therapist</SelectItem>
                    <SelectItem value="family">Family/Caregiver</SelectItem>
                    <SelectItem value="school">School/Teacher</SelectItem>
                    <SelectItem value="community">Community Support</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="edit-objective-text">What They Will Do *</Label>
                <DebouncedTextarea
                  id="edit-objective-text"
                  value={objectiveToEdit.objective_text || ''}
                  onChange={(e) => setObjectiveToEdit(prev => ({...prev, objective_text: e.target.value}))}
                  placeholder="Describe what the responsible party will do..."
                  rows={3}
                  className="mt-1 resize-none overflow-hidden"
                  onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                />
              </div>

              {/* Interventions Section */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-semibold">Interventions</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-teal-600 border-teal-300 hover:bg-teal-50"
                    onClick={() => setShowEditInterventionLibrary(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Browse Intervention Library
                  </Button>
                </div>
                
                {/* List existing interventions */}
                <div className="space-y-2">
                  {(objectiveToEdit.interventions || []).length === 0 && !objectiveToEdit.intervention ? (
                    <p className="text-sm text-gray-500 italic py-2">No interventions added yet. Click "Browse Intervention Library" to add interventions.</p>
                  ) : (
                    <>
                      {(objectiveToEdit.interventions || []).map((intervention, idx) => (
                        <div key={idx} className="p-2 bg-white border rounded text-sm">
                          {editingInterventionIdx === idx ? (
                            /* Editing mode - show editable fields */
                            <div className="space-y-2">
                              <div>
                                <Label className="text-xs text-gray-500">Intervention Name</Label>
                                <DebouncedInput
                                  value={typeof intervention === 'string' ? intervention : intervention.name || ''}
                                  onChange={(e) => {
                                    setObjectiveToEdit(prev => ({
                                      ...prev,
                                      interventions: prev.interventions.map((int, i) => 
                                        i === idx 
                                          ? typeof int === 'string' 
                                            ? e.target.value 
                                            : { ...int, name: e.target.value }
                                          : int
                                      )
                                    }));
                                  }}
                                  className="mt-1"
                                  placeholder="Intervention name"
                                />
                              </div>
                              {typeof intervention === 'object' && (
                                <div>
                                  <Label className="text-xs text-gray-500">Description (optional)</Label>
                                  <DebouncedTextarea
                                    value={intervention.description || ''}
                                    onChange={(e) => {
                                      setObjectiveToEdit(prev => ({
                                        ...prev,
                                        interventions: prev.interventions.map((int, i) => 
                                          i === idx ? { ...int, description: e.target.value } : int
                                        )
                                      }));
                                    }}
                                    className="mt-1"
                                    rows={2}
                                    placeholder="Additional details about this intervention..."
                                  />
                                </div>
                              )}
                              <div className="flex gap-2 pt-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  className="bg-teal-600 hover:bg-teal-700 h-7 text-xs"
                                  onClick={() => setEditingInterventionIdx(null)}
                                >
                                  Done
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-gray-500 h-7 text-xs"
                                  onClick={() => setEditingInterventionIdx(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            /* Display mode - show intervention with edit/delete buttons */
                            <div className="flex items-start justify-between">
                              <div 
                                className="flex-1 cursor-pointer hover:bg-gray-50 rounded p-1 -m-1"
                                onClick={() => setEditingInterventionIdx(idx)}
                                title="Click to edit this intervention"
                              >
                                <span className="font-medium text-teal-700">
                                  {typeof intervention === 'string' ? intervention : intervention.name}
                                </span>
                                {typeof intervention === 'object' && intervention.description && (
                                  <p className="text-gray-600 text-xs mt-0.5 italic">{intervention.description}</p>
                                )}
                                {typeof intervention === 'object' && (intervention.modality || intervention.frequency) && (
                                  <span className="text-gray-500 text-xs ml-2">
                                    ({[intervention.modality, intervention.frequency].filter(Boolean).join(', ')})
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-1 ml-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-6 w-6 p-0"
                                  onClick={() => setEditingInterventionIdx(idx)}
                                  title="Edit intervention"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                  onClick={() => {
                                    setObjectiveToEdit(prev => ({
                                      ...prev,
                                      interventions: prev.interventions.filter((_, i) => i !== idx)
                                    }));
                                  }}
                                  title="Remove intervention"
                                >
                                  ×
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      {(!objectiveToEdit.interventions || objectiveToEdit.interventions.length === 0) && objectiveToEdit.intervention && (
                        <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                          <span className="font-medium">Legacy intervention:</span> {objectiveToEdit.intervention}
                          <p className="text-xs mt-1">Use the Intervention Library to add new interventions.</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="edit-treatment-desc">Treatment Description (Optional)</Label>
                <DebouncedTextarea
                  id="edit-treatment-desc"
                  value={objectiveToEdit.treatment_description || ''}
                  onChange={(e) => setObjectiveToEdit(prev => ({...prev, treatment_description: e.target.value}))}
                  placeholder="Additional notes about the treatment approach..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter className="border-t pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditObjectiveDialog(false);
                setObjectiveToEdit(null);
              }}
              disabled={isSavingObjective}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!objectiveToEdit.objective_text?.trim()) {
                  toast.error('Please describe what they will do');
                  return;
                }
                setIsSavingObjective(true);
                try {
                  // Build the intervention string for backward compatibility
                  const interventions = objectiveToEdit.interventions || [];
                  const interventionString = interventions.length > 0 
                    ? interventions.map(i => typeof i === 'string' ? i : i.name).join(', ')
                    : objectiveToEdit.intervention;
                  
                  await axios.patch(
                    `${API}/client-objectives/${objectiveToEdit.id}`,
                    {
                      objective_text: objectiveToEdit.objective_text,
                      responsible_party: objectiveToEdit.responsible_party,
                      interventions: interventions,
                      intervention: interventionString,
                      treatment_description: objectiveToEdit.treatment_description
                    },
                    {
                      headers: {
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`
                      }
                    }
                  );
                  toast.success('Objective updated successfully');
                  setShowEditObjectiveDialog(false);
                  setObjectiveToEdit(null);
                  await refetchTreatmentPlans(selectedClient.id);
                } catch (error) {
                  console.error('Error updating objective:', error);
                  toast.error(error.response?.data?.detail || 'Failed to update objective');
                } finally {
                  setIsSavingObjective(false);
                }
              }}
              disabled={isSavingObjective}
            >
              {isSavingObjective ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Goal Dialog */}
      <Dialog open={showEditGoalDialog} onOpenChange={(open) => {
        setShowEditGoalDialog(open);
        if (!open) {
          setGoalToEdit(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Goal</DialogTitle>
            <DialogDescription>
              Update the goal details, barriers, and strengths for this client
            </DialogDescription>
          </DialogHeader>
          {goalToEdit && (
            <div className="space-y-4 py-4">
              {/* Show original template if exists */}
              {goalToEdit.template_goal_text && goalToEdit.template_goal_text !== goalToEdit.goal_text && (
                <div>
                  <Label className="text-sm font-medium">Original Template Goal</Label>
                  <div className="p-3 bg-gray-50 rounded border text-sm text-gray-700 mb-3">
                    {goalToEdit.template_goal_text}
                  </div>
                </div>
              )}
              
              <div>
                <Label htmlFor="edit-goal-text">Goal Text *</Label>
                <DebouncedTextarea
                  id="edit-goal-text"
                  value={goalToEdit.goal_text || ''}
                  onChange={(e) => setGoalToEdit(prev => ({...prev, goal_text: e.target.value}))}
                  placeholder="Clinical/professional goal statement..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="edit-client-description">Client's Goal (in their own words) *</Label>
                <DebouncedTextarea
                  id="edit-client-description"
                  value={goalToEdit.client_goal_description || ''}
                  onChange={(e) => setGoalToEdit(prev => ({...prev, client_goal_description: e.target.value}))}
                  placeholder="How does the client describe this goal in their own words?"
                  rows={2}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-barriers">Client Barriers</Label>
                  <DebouncedTextarea
                    id="edit-barriers"
                    value={goalToEdit.barriers || ''}
                    onChange={(e) => setGoalToEdit(prev => ({...prev, barriers: e.target.value}))}
                    placeholder="What barriers might prevent achieving this goal?"
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-strengths">Client Strengths</Label>
                  <DebouncedTextarea
                    id="edit-strengths"
                    value={goalToEdit.strengths || ''}
                    onChange={(e) => setGoalToEdit(prev => ({...prev, strengths: e.target.value}))}
                    placeholder="What client strengths support this goal?"
                    rows={3}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-duration">Goal Duration * (Required)</Label>
                  <Select
                    value={goalToEdit._durationSelect || (goalToEdit.duration_days ? ([30, 60, 90, 180, 365].includes(goalToEdit.duration_days) ? String(goalToEdit.duration_days) : 'custom') : '')}
                    onValueChange={(value) => {
                      if (value === 'custom') {
                        setGoalToEdit(prev => ({...prev, _durationSelect: 'custom', duration_days: prev.duration_days || ''}));
                      } else {
                        setGoalToEdit(prev => ({...prev, _durationSelect: value, duration_days: parseInt(value)}));
                      }
                    }}
                  >
                    <SelectTrigger id="edit-duration">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 days (1 month)</SelectItem>
                      <SelectItem value="60">60 days (2 months)</SelectItem>
                      <SelectItem value="90">90 days (3 months)</SelectItem>
                      <SelectItem value="180">180 days (6 months)</SelectItem>
                      <SelectItem value="365">365 days (1 year)</SelectItem>
                      <SelectItem value="custom">Custom duration</SelectItem>
                    </SelectContent>
                  </Select>
                  {(goalToEdit._durationSelect === 'custom' || (goalToEdit.duration_days && ![30, 60, 90, 180, 365].includes(goalToEdit.duration_days))) && (
                    <Input
                      type="number"
                      min="1"
                      placeholder="Enter custom days"
                      value={goalToEdit.duration_days || ''}
                      onChange={(e) => setGoalToEdit(prev => ({...prev, duration_days: parseInt(e.target.value) || null}))}
                      className="mt-2"
                    />
                  )}
                  {goalToEdit.duration_days && selectedTreatmentPlan?.start_date && (
                    <p className="text-xs text-teal-600 mt-1 font-medium">
                      Duration: {goalToEdit.duration_days} days | Expected End: {
                        (() => {
                          const startDate = new Date(selectedTreatmentPlan.start_date);
                          const endDate = new Date(startDate);
                          endDate.setDate(endDate.getDate() + goalToEdit.duration_days);
                          return endDate.toLocaleDateString();
                        })()
                      }
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="edit-goal-notes">Additional Notes</Label>
                  <DebouncedInput
                    id="edit-goal-notes"
                    value={goalToEdit.notes || ''}
                    onChange={(e) => setGoalToEdit(prev => ({...prev, notes: e.target.value}))}
                    placeholder="Any additional notes or context"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="border-t pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditGoalDialog(false);
                setGoalToEdit(null);
              }}
              disabled={isSavingGoal}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!goalToEdit.goal_text?.trim()) {
                  toast.error('Please provide goal text');
                  return;
                }
                setIsSavingGoal(true);
                try {
                  await axios.patch(
                    `${API}/client-goals/${goalToEdit.id}`,
                    {
                      goal_text: goalToEdit.goal_text,
                      client_goal_description: goalToEdit.client_goal_description,
                      barriers: goalToEdit.barriers,
                      strengths: goalToEdit.strengths,
                      duration_days: goalToEdit.duration_days,
                      notes: goalToEdit.notes
                    },
                    {
                      headers: {
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`
                      }
                    }
                  );
                  toast.success('Goal updated successfully');
                  setShowEditGoalDialog(false);
                  setGoalToEdit(null);
                  await refetchTreatmentPlans(selectedClient.id);
                } catch (error) {
                  console.error('Error updating goal:', error);
                  toast.error(error.response?.data?.detail || 'Failed to update goal');
                } finally {
                  setIsSavingGoal(false);
                }
              }}
              disabled={isSavingGoal}
            >
              {isSavingGoal ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={showVersionHistoryDialog} onOpenChange={setShowVersionHistoryDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Treatment Plan Version History</DialogTitle>
            <DialogDescription>
              View all versions of this treatment plan
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4 max-h-[60vh] overflow-y-auto">
            {versionHistory.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No version history available</p>
            ) : (
              versionHistory.map((version, idx) => (
                <Card key={version.id} className={version.is_current ? 'border-2 border-blue-500' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={version.is_current ? 'bg-blue-600' : 'bg-gray-500'}>
                            Version {version.version_number}
                          </Badge>
                          {version.is_current && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                              Current
                            </Badge>
                          )}
                          <Badge variant="outline" className="capitalize">
                            {version.status}
                          </Badge>
                        </div>
                        
                        <div className="text-sm text-gray-600 space-y-1">
                          <p><strong>Created:</strong> {new Date(version.created_at).toLocaleString()}</p>
                          <p><strong>Created by:</strong> {version.created_by}</p>
                          {version.signed_at && (
                            <p><strong>Signed:</strong> {new Date(version.signed_at).toLocaleString()}</p>
                          )}
                          {version.review_notes && (
                            <p className="mt-2 italic">"{version.review_notes}"</p>
                          )}
                        </div>

                        {version.edit_history && version.edit_history.length > 0 && (
                          <details className="mt-3">
                            <summary className="text-sm font-medium cursor-pointer text-blue-600 hover:text-blue-800">
                              View Edit History ({version.edit_history.length} {version.edit_history.length === 1 ? 'edit' : 'edits'})
                            </summary>
                            <div className="mt-2 space-y-2 pl-4 border-l-2 border-gray-200">
                              {version.edit_history.map((edit, editIdx) => (
                                <div key={editIdx} className="text-xs text-gray-600">
                                  <p><strong>v{edit.version}</strong> - {edit.edit_type}</p>
                                  <p>{new Date(edit.edited_at).toLocaleString()}</p>
                                  {edit.notes && <p className="italic">"{edit.notes}"</p>}
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                      
                      {version.pdf_available && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              const response = await axios.get(
                                `${API}/treatment-plans/${version.id}/pdf`,
                                {
                                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
                                  responseType: 'blob'
                                }
                              );
                              const blob = new Blob([response.data], { type: 'application/pdf' });
                              const url = window.URL.createObjectURL(blob);
                              window.open(url, '_blank');
                            } catch (error) {
                              console.error('Error loading PDF:', error);
                              toast.error('Failed to load PDF');
                            }
                          }}
                        >
                          View PDF
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowVersionHistoryDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Plan Settings Dialog */}
      <Dialog open={showEditPlanSettingsDialog} onOpenChange={(open) => {
        setShowEditPlanSettingsDialog(open);
        if (!open) setPlanSettingsToEdit(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Treatment Plan Settings</DialogTitle>
            <DialogDescription>
              Update visit frequency and dates. Note: Changing frequency will recalculate the review due date.
            </DialogDescription>
          </DialogHeader>
          {planSettingsToEdit && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Visit Frequency *</Label>
                <Select
                  value={planSettingsToEdit.visit_frequency || ''}
                  onValueChange={(value) => setPlanSettingsToEdit(prev => ({...prev, visit_frequency: value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="more_than_weekly">More than once a week</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="bi-weekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="as-needed">As Needed</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Review due date will be recalculated based on frequency
                </p>
              </div>

              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={planSettingsToEdit.start_date || ''}
                  onChange={(e) => setPlanSettingsToEdit(prev => ({...prev, start_date: e.target.value}))}
                />
              </div>

              <div>
                <Label>End Date (Optional)</Label>
                <Input
                  type="date"
                  value={planSettingsToEdit.end_date || ''}
                  onChange={(e) => setPlanSettingsToEdit(prev => ({...prev, end_date: e.target.value}))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditPlanSettingsDialog(false);
                setPlanSettingsToEdit(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                try {
                  await axios.patch(
                    `${API}/treatment-plans/${planSettingsToEdit.id}/settings`,
                    {
                      visit_frequency: planSettingsToEdit.visit_frequency,
                      start_date: planSettingsToEdit.start_date,
                      end_date: planSettingsToEdit.end_date
                    },
                    {
                      headers: {
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`
                      }
                    }
                  );
                  toast.success('Plan settings updated successfully');
                  setShowEditPlanSettingsDialog(false);
                  setPlanSettingsToEdit(null);
                  await refetchTreatmentPlans(selectedClient.id);
                } catch (error) {
                  console.error('Error updating plan settings:', error);
                  toast.error(error.response?.data?.detail || 'Failed to update plan settings');
                }
              }}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Review Date Dialog */}
      <Dialog open={!!reviewDateEditPlan} onOpenChange={(open) => { if (!open) { setReviewDateEditPlan(null); setReviewDateEditValue(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Review Date</DialogTitle>
            <DialogDescription>
              Set the next review due date for this treatment plan.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="review-date-input">Next Review Date</Label>
            <Input
              id="review-date-input"
              type="date"
              value={reviewDateEditValue}
              onChange={(e) => setReviewDateEditValue(e.target.value)}
              className="mt-1"
              data-testid="review-date-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReviewDateEditPlan(null); setReviewDateEditValue(''); }}>
              Cancel
            </Button>
            <Button
              disabled={!reviewDateEditValue}
              onClick={async () => {
                try {
                  await axios.patch(
                    `${API}/treatment-plans/${reviewDateEditPlan.id}/review-date`,
                    { next_review_date: reviewDateEditValue },
                    { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
                  );
                  toast.success('Review date updated');
                  setReviewDateEditPlan(null);
                  setReviewDateEditValue('');
                  // Targeted refresh — only refetch treatment plans for the current client
                  if (selectedClient?.id) {
                    refetchTreatmentPlans(selectedClient.id);
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to update review date');
                }
              }}
              data-testid="save-review-date-btn"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Goal Dialog */}
      <Dialog open={showCloseGoalDialog} onOpenChange={(open) => {
        setShowCloseGoalDialog(open);
        if (!open) {
          setGoalToClose(null);
          setGoalCloseReason('');
          setGoalCloseNotes('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Close/Retire Goal</DialogTitle>
            <DialogDescription>
              Document why this goal is being closed. This information will be saved for clinical records and compliance.
            </DialogDescription>
          </DialogHeader>
          {goalToClose && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-sm font-medium">Goal {goalToClose.goalIndex}</p>
                <p className="text-sm text-gray-600 mt-1">{goalToClose.goal_text}</p>
                {goalToClose.objectivesCount > 0 && (
                  <p className="text-xs text-orange-600 mt-2">
                    ⚠️ This will also close {goalToClose.objectivesCount} associated {goalToClose.objectivesCount === 1 ? 'objective' : 'objectives'}
                  </p>
                )}
              </div>

              <div>
                <Label>Closure Reason *</Label>
                <Select
                  value={goalCloseReason}
                  onValueChange={setGoalCloseReason}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason for closing goal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">✅ Goal Completed/Met</SelectItem>
                    <SelectItem value="deferred">⏸️ Deferred to Future Treatment</SelectItem>
                    <SelectItem value="client_declined">🚫 Client Declined</SelectItem>
                    <SelectItem value="guardian_declined">🚫 Guardian/Parent Declined</SelectItem>
                    <SelectItem value="not_met">❌ Goal Not Met</SelectItem>
                    <SelectItem value="no_longer_appropriate">⚠️ No Longer Appropriate</SelectItem>
                    <SelectItem value="replaced">🔄 Replaced with Different Goal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Additional Notes (Optional)</Label>
                <DebouncedTextarea
                  value={goalCloseNotes}
                  onChange={(e) => setGoalCloseNotes(e.target.value)}
                  placeholder="Add any additional details about this closure..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCloseGoalDialog(false);
                setGoalToClose(null);
                setGoalCloseReason('');
                setGoalCloseNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!goalCloseReason) {
                  toast.error('Please select a closure reason');
                  return;
                }

                try {
                  await axios.patch(
                    `${API}/client-goals/${goalToClose.id}/close`,
                    {
                      close_reason: goalCloseReason,
                      close_notes: goalCloseNotes
                    },
                    {
                      headers: {
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`
                      }
                    }
                  );
                  toast.success('Goal closed successfully');
                  setShowCloseGoalDialog(false);
                  setGoalToClose(null);
                  setGoalCloseReason('');
                  setGoalCloseNotes('');
                  await refetchTreatmentPlans(selectedClient.id);
                } catch (error) {
                  console.error('Error closing goal:', error);
                  toast.error(error.response?.data?.detail || 'Failed to close goal');
                }
              }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Close Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Signature Dialog */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Client Signature</DialogTitle>
            <DialogDescription>
              Please sign below to acknowledge and consent to this treatment plan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-gray-300 rounded-lg bg-white">
              <SignatureCanvas
                ref={(ref) => { if (ref) setClientSignature(ref); }}
                canvasProps={{
                  width: 600,
                  height: 200,
                  className: 'signature-canvas'
                }}
              />
            </div>
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  if (clientSignature) {
                    clientSignature.clear();
                  }
                }}
              >
                Clear Signature
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSignatureDialog(false);
                    setClientSignature(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={async () => {
                    if (!clientSignature || clientSignature.isEmpty()) {
                      toast.error('Please provide a signature');
                      return;
                    }
                    
                    try {
                      const signatureData = clientSignature.toDataURL();
                      
                      await axios.post(
                        `${API}/treatment-plans/${signaturePlanId}/sign-client`,
                        { signature: signatureData },
                        {
                          headers: {
                            Authorization: `Bearer ${sessionStorage.getItem('token')}`
                          }
                        }
                      );
                      
                      toast.success('Client signature recorded successfully');
                      setShowSignatureDialog(false);
                      setClientSignature(null);
                      setSignaturePlanId(null);
                      await refetchTreatmentPlans(selectedClient.id);
                    } catch (error) {
                      console.error('Error signing:', error);
                      toast.error(error.response?.data?.detail || 'Failed to record signature');
                    }
                  }}
                >
                  Sign Treatment Plan
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* iframe-safe confirm dialog (replaces native window.confirm which
          Emergent's preview sandbox silently suppresses).  Driven by
          confirmAction(...) — returns a promise resolving to true/false. */}
      <AlertDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          // Closing the dialog without clicking either button counts as Cancel.
          if (!open && confirmState.resolver) _closeConfirm(false);
        }}
      >
        <AlertDialogContent data-testid="iframe-safe-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState.title}</AlertDialogTitle>
            <AlertDialogDescription style={{ whiteSpace: 'pre-line' }}>
              {confirmState.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="iframe-safe-confirm-cancel"
              onClick={() => _closeConfirm(false)}
            >
              {confirmState.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="iframe-safe-confirm-ok"
              className={confirmState.destructive ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
              onClick={() => _closeConfirm(true)}
            >
              {confirmState.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Staff Signature Dialog (Provider/Supervisor) */}
      <Dialog open={showStaffSignatureDialog} onOpenChange={setShowStaffSignatureDialog}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>
              {staffSignatureType === 'provider' ? 'Provider Signature' : 'Supervisor Signature (if required)'}
            </DialogTitle>
            <DialogDescription>
              {!user.signature_image ? (
                <>
                  Draw your signature below. This will be saved to your profile and used for all future signatures.
                  Your signature indicates your review and approval of this plan.
                </>
              ) : (
                <>
                  Please sign the treatment plan below. Your signature indicates your review and approval of this plan.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Show saved signature option if user has one */}
            {user.signature_image && (
              <div className="border-2 border-blue-300 rounded-lg bg-blue-50 p-4">
                <p className="text-sm font-medium text-blue-900 mb-2">Your Saved Signature:</p>
                <img src={user.signature_image} alt="Saved signature" className="h-20 border border-blue-200 bg-white" />
                <Button
                  size="sm"
                  className="mt-2 bg-blue-600 hover:bg-blue-700"
                  onClick={async () => {
                    try {
                      const endpoint = staffSignatureType === 'provider' 
                        ? `/treatment-plans/${signaturePlanId}/sign-provider`
                        : `/treatment-plans/${signaturePlanId}/sign-supervisor`;
                      
                      await axios.post(
                        `${API}${endpoint}`,
                        { signature: user.signature_image },
                        {
                          headers: {
                            Authorization: `Bearer ${sessionStorage.getItem('token')}`
                          }
                        }
                      );
                      
                      toast.success(`${staffSignatureType === 'provider' ? 'Provider' : 'Supervisor'} signature recorded successfully`);
                      setShowStaffSignatureDialog(false);
                      setStaffSignature(null);
                      setStaffSignatureType(null);
                      setSignaturePlanId(null);
                      
                      // Refresh treatment plans and notifications
                      if (selectedClient?.id) {
                        await refetchTreatmentPlans(selectedClient.id);
                      }
                      if (staffSignatureType === 'supervisor') {
                        fetchNotifications();
                      }
                    } catch (error) {
                      console.error('Error signing:', error);
                      toast.error(error.response?.data?.detail || 'Failed to sign treatment plan');
                    }
                  }}
                >
                  Use Saved Signature
                </Button>
              </div>
            )}
            
            <p className="text-sm text-gray-600">Or draw a new signature below:</p>
            
            <div className="border-2 border-gray-300 rounded-lg bg-white">
              <SignatureCanvas
                ref={(ref) => { if (ref) setStaffSignature(ref); }}
                canvasProps={{
                  width: 600,
                  height: 200,
                  className: 'signature-canvas'
                }}
              />
            </div>
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  if (staffSignature) {
                    staffSignature.clear();
                  }
                }}
              >
                Clear Signature
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowStaffSignatureDialog(false);
                    setStaffSignature(null);
                    setStaffSignatureType(null);
                    setSignaturePlanId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={async () => {
                    if (!staffSignature || staffSignature.isEmpty()) {
                      toast.error('Please provide a signature');
                      return;
                    }
                    
                    let signSucceeded = false;
                    try {
                      const signatureData = staffSignature.toDataURL();
                      
                      // If user doesn't have a saved signature, save this one to their profile
                      if (!user.signature_image) {
                        try {
                          await axios.put(
                            `${API}/users/${user.id}`,
                            { signature_image: signatureData },
                            {
                              headers: {
                                Authorization: `Bearer ${sessionStorage.getItem('token')}`
                              }
                            }
                          );
                          // Update local user object
                          setUser({ ...user, signature_image: signatureData });
                          void 0 && console.log('Signature saved to profile');
                        } catch (error) {
                          console.error('Failed to save signature to profile:', error);
                          // Continue anyway - we can still sign the plan
                        }
                      }
                      
                      const endpoint = staffSignatureType === 'provider' 
                        ? `/treatment-plans/${signaturePlanId}/sign-provider`
                        : `/treatment-plans/${signaturePlanId}/sign-supervisor`;
                      
                      await axios.post(
                        `${API}${endpoint}`,
                        { signature: signatureData },
                        {
                          headers: {
                            Authorization: `Bearer ${sessionStorage.getItem('token')}`
                          },
                          // See saved-signature handler above — the global 30s
                          // axios ceiling caused a false "Failed to sign" toast
                          // on slow signs that actually succeeded server-side.
                          timeout: 120000,
                        }
                      );
                      signSucceeded = true;
                      
                      toast.success(`${staffSignatureType === 'provider' ? 'Provider' : 'Supervisor'} signature recorded successfully`);
                      setShowStaffSignatureDialog(false);
                      setStaffSignature(null);
                      setStaffSignatureType(null);
                      setSignaturePlanId(null);
                      
                      // Refresh treatment plans and notifications
                      if (selectedClient?.id) {
                        await refetchTreatmentPlans(selectedClient.id);
                      }
                      if (staffSignatureType === 'supervisor') {
                        fetchNotifications(); // Refresh notifications for supervisor
                      }
                    } catch (error) {
                      console.error('Error signing:', error);
                      if (signSucceeded) {
                        toast.warning('Signature recorded — refresh failed. Reload the page to see the latest status.');
                      } else {
                        toast.error(error.response?.data?.detail || 'Failed to sign treatment plan');
                      }
                    }
                  }}
                >
                  Sign Treatment Plan
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Treatment Plan Type Dialog */}
      <Dialog open={showEditTypeDialog} onOpenChange={setShowEditTypeDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Treatment Plan</DialogTitle>
            <DialogDescription>
              Choose how you'd like to update this treatment plan
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Minor Edit Option */}
            <Card
              className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-purple-300"
              onClick={async () => {
                setShowEditTypeDialog(false);
                setIsMajorReviewEdit(false);
                // Set the plan being edited for minor edit
                setEditingPlanForMinorEdit(planToEdit);
                // Pre-populate form fields from the existing plan
                setNewPlanProviderId(planToEdit.assigned_provider_id || '');
                setNewPlanServiceType(planToEdit.service_type || '');
                setNewPlanStartDate(planToEdit.start_date || new Date().toISOString().split('T')[0]);
                setNewPlanVisitFrequency(planToEdit.visit_frequency || '');
                // CRITICAL: Pre-load the PANs BEFORE opening the dialog
                // React state updates are async, so editingPlanForMinorEdit won't be set yet
                // Transform existing PANs to match the expected format (pan_id, pan_name, goals)
                const existingPANs = (planToEdit.pans || []).map(pan => ({
                  pan_id: pan.id || pan.pan_id,
                  pan_name: pan.pan_name,
                  is_custom: pan.is_custom || false,
                  goals: (pan.goals || []).map(goal => ({
                    goal_id: goal.id || goal.goal_id,
                    goal_text: goal.goal_text || goal.description,
                    target_date: goal.target_date,
                    objectives: (goal.objectives || []).map(obj => ({
                      objective_id: obj.id || obj.objective_id,
                      objective_text: obj.objective_text || obj.description
                    }))
                  }))
                }));
                setPlanPANs(existingPANs);
                // Also expand all PANs to show their content
                setExpandedPANs(new Set(existingPANs.map(p => p.pan_id)));
                // Small delay to ensure state updates are processed before dialog opens
                await new Promise(resolve => setTimeout(resolve, 50));
                // Open the create/edit plan dialog
                setShowCreatePlanDialog(true);
                // Prefill medications and diagnoses from context for minor edits too
                const editClientId = planToEdit.client_id || selectedClient?.id;
                if (editClientId) {
                  // If the plan already has snapshots, use those; otherwise pull from context
                  if (planToEdit.medications_snapshot?.length > 0) {
                    setTpMedications(planToEdit.medications_snapshot);
                  } else {
                    const activeMeds = (clientMedications || []).filter(m => m.client_id === editClientId && m.is_active === true);
                    setTpMedications(activeMeds.map(m => ({
                      name: m.medication_name || m.name || '',
                      dosage: m.dosage || '',
                      frequency: m.frequency || '',
                      prescriber: m.prescriber || '',
                      purpose: m.purpose || '',
                      status: 'active'
                    })));
                  }
                  if (planToEdit.diagnoses_snapshot?.length > 0) {
                    setTpDiagnoses(planToEdit.diagnoses_snapshot);
                  } else {
                    const activeDiags = (clientDiagnoses || []).filter(d => d.client_id === editClientId && d.is_active !== false);
                    setTpDiagnoses(activeDiags.map(d => ({
                      code: d.code || d.diagnosis_code || '',
                      description: d.description || d.diagnosis_description || d.diagnosis_name || '',
                      status: d.status || 'active',
                      is_primary: d.is_primary || false,
                      assigned_by: d.assigned_by || '',
                      provider_names: d.provider_names || [],
                      assigned_by_name: d.assigned_by_name || ''
                    })));
                  }
                }
                
                // Load level of care, trauma reports, and supports
                setTpLevelOfCareNotes(planToEdit.level_of_care_notes || '');
                setTpTraumaReports(planToEdit.trauma_reports || 'None reported');
                if (planToEdit.supports) {
                  setTpSupportsNoneReported(planToEdit.supports.none_reported || false);
                  setTpSupports(planToEdit.supports.entries || []);
                } else {
                  setTpSupportsNoneReported(false);
                  setTpSupports([]);
                }
                
                toast.info('Minor Edit mode - make your changes and save', { duration: 4000 });
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Pencil className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">Minor Edit</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      For typos, spelling corrections, grammar fixes, or small clarifications
                    </p>
                    <div className="bg-blue-50 p-2 rounded text-xs space-y-1">
                      <p className="font-medium text-blue-900">✓ No re-signing required</p>
                      <p className="text-blue-700">✓ Increments version (e.g., 1.0 → 1.1)</p>
                      <p className="text-blue-700">✓ Keeps existing signatures</p>
                      <p className="text-blue-700">✓ PDF automatically updated</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      <strong>Use when:</strong> The meaning or intent doesn't change
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Major Review Option */}
            <Card
              className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-orange-300"
              onClick={async (e) => {
                // Prevent double-click issues
                if (e.detail > 1) return;
                
                // Initialize goal progress with the plan's goals
                const goals = [];
                if (planToEdit?.pans) {
                  for (const pan of planToEdit.pans) {
                    if (pan.goals) {
                      for (const goal of pan.goals) {
                        goals.push({
                          goal_id: goal.id || goal.goal_id,
                          goal_text: goal.goal_text || goal.description || 'Unnamed goal',
                          pan_name: pan.pan_name,
                          progress_status: '',
                          progress_notes: ''
                        });
                      }
                    }
                  }
                }
                
                setMajorReviewGoalProgress(goals);
                setMajorReviewNotes('');
                setMajorReviewDate(new Date().toLocaleDateString('en-CA'));
                setIsMajorReviewEdit(true);
                setShowEditTypeDialog(false);
                // Open the same editor as minor edit but with major review flag
                setEditingPlanForMinorEdit(planToEdit);
                setNewPlanProviderId(planToEdit.assigned_provider_id || '');
                setNewPlanServiceType(planToEdit.service_type || '');
                setNewPlanStartDate(planToEdit.start_date || new Date().toISOString().split('T')[0]);
                setNewPlanVisitFrequency(planToEdit.visit_frequency || '');
                const existingPANs = (planToEdit.pans || []).map(pan => ({
                  pan_id: pan.id || pan.pan_id,
                  pan_name: pan.pan_name,
                  is_custom: pan.is_custom || false,
                  goals: (pan.goals || []).map(goal => ({
                    goal_id: goal.id || goal.goal_id,
                    goal_text: goal.goal_text || goal.description,
                    target_date: goal.target_date,
                    objectives: (goal.objectives || []).map(obj => ({
                      objective_id: obj.id || obj.objective_id,
                      objective_text: obj.objective_text || obj.description
                    }))
                  }))
                }));
                setPlanPANs(existingPANs);
                setExpandedPANs(new Set(existingPANs.map(p => p.pan_id)));
                const editClientId = planToEdit.client_id || selectedClient?.id;
                if (editClientId) {
                  if (planToEdit.medications_snapshot?.length > 0) {
                    setTpMedications(planToEdit.medications_snapshot);
                  } else {
                    const activeMeds = (clientMedications || []).filter(m => m.client_id === editClientId && m.is_active === true);
                    setTpMedications(activeMeds.map(m => ({
                      name: m.medication_name || m.name || '',
                      dosage: m.dosage || '',
                      frequency: m.frequency || '',
                      prescriber: m.prescriber || '',
                      purpose: m.purpose || '',
                      status: 'active'
                    })));
                  }
                  if (planToEdit.diagnoses_snapshot?.length > 0) {
                    setTpDiagnoses(planToEdit.diagnoses_snapshot);
                  } else {
                    const activeDiags = (clientDiagnoses || []).filter(d => d.client_id === editClientId && d.is_active !== false);
                    setTpDiagnoses(activeDiags.map(d => ({
                      code: d.code || d.diagnosis_code || '',
                      description: d.description || d.diagnosis_description || d.diagnosis_name || '',
                      status: d.status || 'active',
                      is_primary: d.is_primary || false,
                      assigned_by: d.assigned_by || '',
                      provider_names: d.provider_names || [],
                      assigned_by_name: d.assigned_by_name || ''
                    })));
                  }
                }
                setTpLevelOfCareNotes(planToEdit.level_of_care_notes || '');
                setTpTraumaReports(planToEdit.trauma_reports || 'None reported');
                if (planToEdit.supports) {
                  setTpSupportsNoneReported(planToEdit.supports.none_reported || false);
                  setTpSupports(planToEdit.supports.entries || []);
                } else {
                  setTpSupportsNoneReported(false);
                  setTpSupports([]);
                }
                await new Promise(resolve => setTimeout(resolve, 50));
                setShowCreatePlanDialog(true);
                toast.info('Major Review mode - update goals, add new ones, and document progress', { duration: 5000 });
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <RefreshCw className="h-5 w-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">Major Review</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      For significant changes, new goals, different approaches, or meaning changes
                    </p>
                    <div className="bg-orange-50 p-2 rounded text-xs space-y-1">
                      <p className="font-medium text-orange-900">⚠️ Requires new signatures</p>
                      <p className="text-orange-700">• Creates new major version (e.g., 1.x → 2.0)</p>
                      <p className="text-orange-700">• Links to previous version for history</p>
                      <p className="text-orange-700">• Full signature workflow restart</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      <strong>Use when:</strong> Treatment approach, goals, or clinical direction changes
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditTypeDialog(false);
                  setPlanToEdit(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Legacy Treatment Plan Import Modal */}
      <LegacyTreatmentPlanImport
        isOpen={showLegacyImport}
        onClose={() => setShowLegacyImport(false)}
        client={selectedClient}
        providers={users}
        clientEnrollments={clientEnrollments}
        serviceTypes={serviceTypes}
        onImportSuccess={() => {
          // Refresh treatment plans after import
          if (selectedClient?.id) {
            axios.get(`${API}/clients/${selectedClient.id}/treatment-plans`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            }).then(res => setTreatmentPlans(res.data)).catch(console.error);
          }
        }}
      />

      {/* Intervention Library Selector Modal */}
      <InterventionLibrarySelector
        isOpen={showInterventionLibrary}
        onClose={() => {
          setShowInterventionLibrary(false);
          setInterventionLibraryTarget(null);
        }}
        selectedInterventions={
          interventionLibraryTarget 
            ? (pendingInterventions[`${interventionLibraryTarget.panIdx}-${interventionLibraryTarget.goalIdx}`] || [])
            : []
        }
        onSave={(interventions) => {
          if (interventionLibraryTarget) {
            setPendingInterventions(prev => ({
              ...prev,
              [`${interventionLibraryTarget.panIdx}-${interventionLibraryTarget.goalIdx}`]: interventions
            }));
          }
        }}
      />

      {/* Intervention Library Selector for Edit Objective Dialog */}
      <InterventionLibrarySelector
        isOpen={showEditInterventionLibrary}
        onClose={() => setShowEditInterventionLibrary(false)}
        selectedInterventions={objectiveToEdit?.interventions || []}
        title="Select Interventions for Objective"
        onSave={(interventions) => {
          setObjectiveToEdit(prev => ({
            ...prev,
            interventions: interventions
          }));
          setShowEditInterventionLibrary(false);
        }}
      />
      </Suspense>

      {/* System Status Overlay - Admin debug panel (Ctrl+Shift+D) */}
      <SystemStatusOverlay 
        user={user}
        staticDataLoaded={staticDataLoaded}
        dataHealth={dataHealth}
        wsConnected={wsConnected}
        onRefresh={() => {
          setStaticDataLoaded(false);
          setDataHealth({
            staticData: 'pending',
            dynamicData: 'pending',
            retryCount: 0,
            failedEndpoints: [],
            staticDetails: {},
            dynamicDetails: {}
          });
          window.location.reload();
        }}
      />

    </div>
    </ChartActionsProvider>
  );
};


// ── Parsed 271 View (inline for App.js usage) ──────────────────────────
const EB_STATUS_MAP = {
  '1': 'Active Coverage', '2': 'Active (Full Risk Capitation)', '3': 'Active (Capitated)',
  '6': 'Inactive', 'A': 'Co-Insurance', 'B': 'Co-Payment', 'C': 'Deductible',
  'D': 'Benefit Description', 'E': 'Exclusions', 'F': 'Limitations',
  'G': 'Out of Pocket (Stop Loss)', 'H': 'Unlimited', 'I': 'Non-Covered',
  'Y': 'Spend Down', 'R': 'Other/Additional Payor',
};
const TIME_MAP = { '22': 'Service Year', '23': 'Calendar Year', '24': 'YTD', '25': 'Contract', '27': 'Visit', '29': 'Remaining', '33': 'Month' };
const LEVEL_MAP = { 'IND': 'Individual', 'FAM': 'Family', 'EMP': 'Employee Only', 'ESP': 'Employee + Spouse', 'ECH': 'Employee + Children' };
const f$ = v => v != null ? `$${Number(v).toFixed(2)}` : '—';
const fPct = v => v != null ? `${(Number(v) * 100).toFixed(0)}%` : null;
// June 2026 — delegates to `utils/insuranceDates.fmtLocalDate`
// which centrally defends against the YYYY-MM-DD UTC-shift bug.
// Format kept identical to original ("Jun 1, 2026" short form).
const fDt = d => fmtLocalDate(d, { month: 'short', day: 'numeric', year: 'numeric' }) || '—';

// Behavioral-health-relevant EB03 service-type codes used as the
// default "MH focus" filter on the client-chart 271 viewer. Mirrors
// EligibilityTab's MH_FOCUS_CODES.
const APP_MH_FOCUS_CODES = new Set([
  '30', '1', '96', '98',
  'A4', 'A5', 'A6', 'A7', 'A8', 'MH',
  'AI', 'AJ', 'AK',
  'BB', 'BC', 'BD',
  'CE', 'CF', 'CG', 'CH', 'CI', 'CJ',
  'RT',
]);

function Parsed271ViewInline({ data, check, onAddOtherPayerToChart }) {
  const benefits = data.service_benefits || [];
  const errors = data.errors || [];
  const discrepancies = data.discrepancies || [];
  const [ebFilter, setEbFilter] = React.useState('mh');
  const filteredBenefits = React.useMemo(() => {
    if (ebFilter === 'all') return benefits;
    if (ebFilter === 'active') return benefits.filter(b => b.status === '1' || b.status === '6');
    return benefits.filter(b => {
      const codes = (b.service_types && b.service_types.length > 0)
        ? b.service_types
        : (b.service_type ? [b.service_type] : []);
      return codes.some(c => APP_MH_FOCUS_CODES.has(c));
    });
  }, [benefits, ebFilter]);
  const grouped = {};
  filteredBenefits.forEach(b => { const k = b.service_name || b.service_type || 'General'; if (!grouped[k]) grouped[k] = []; grouped[k].push(b); });
  const coverageColor = data.coverage_status === 'active' ? 'text-green-700 bg-green-50 border-green-200' : data.coverage_status === 'inactive' ? 'text-red-700 bg-red-50 border-red-200' : 'text-gray-700 bg-gray-50 border-gray-200';

  return (
    <div className="space-y-4" data-testid="parsed-271-view">
      {/* Coverage Banner */}
      <div className={`rounded-lg border p-3 ${coverageColor}`}>
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          <span className="font-semibold text-base capitalize">{data.coverage_status || 'Unknown'}</span>
          {data.payer_name && <span className="text-sm opacity-75">— {data.payer_name}</span>}
        </div>
        <div className="flex gap-4 mt-1 text-xs">
          {data.coverage_effective_date && <span>Effective: {fDt(data.coverage_effective_date)}</span>}
          {data.coverage_termination_date && <span>Term: {fDt(data.coverage_termination_date)}</span>}
        </div>
      </div>

      {/* Subscriber Info */}
      {(data.payer_subscriber_name || data.payer_subscriber_id) && (
        <div className="rounded-lg border border-gray-200 p-3">
          <div className="text-sm font-medium text-gray-700 mb-2">Subscriber (Policyholder)</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {data.payer_subscriber_name && <div><span className="text-gray-500">Name:</span> <span className="font-medium">{data.payer_subscriber_name}</span></div>}
            {data.payer_subscriber_id && <div><span className="text-gray-500">Member ID:</span> <span className="font-mono font-medium">{data.payer_subscriber_id}</span></div>}
            {data.payer_subscriber_dob && <div><span className="text-gray-500">DOB:</span> <span className="font-medium">{fDt(data.payer_subscriber_dob)}</span></div>}
            {data.payer_subscriber_address && <div className="col-span-2"><span className="text-gray-500">Address:</span> <span className="font-medium">{data.payer_subscriber_address}</span></div>}
          </div>
        </div>
      )}

      {/* Other / Additional Active Coverage (Loop 2120C in the 271).
          MassHealth is the most common source — they advertise any
          commercial coverage they know about so the biller can confirm
          the chart's COB lineup is right.  Keeping this read-only on
          purpose: we don't auto-write these into client.insurances
          since the user wants to review and add them deliberately. */}
      {Array.isArray(data.other_payers) && data.other_payers.length > 0 && (
        <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-3" data-testid="other-active-coverage">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-4 w-4 text-purple-700" />
            <span className="text-sm font-medium text-purple-900">
              Other Active Coverage on File with Payer ({data.other_payers.length})
            </span>
          </div>
          <p className="text-[11px] text-purple-700/80 mb-2">
            The responding payer reported these additional plans for this patient.
            Confirm the chart has them on file before billing.
          </p>
          <div className="space-y-2">
            {data.other_payers.map((p, idx) => {
              const addr = [
                p.address_line1, p.address_line2, p.city,
                p.state, p.zip,
              ].filter(Boolean).join(', ');
              return (
                <div
                  key={idx}
                  className="rounded bg-white border border-purple-100 p-2 text-xs"
                  data-testid={`other-payer-${idx}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                        <span className="font-semibold text-gray-900">{p.name || 'Unknown payer'}</span>
                        {p.payer_id && <span className="font-mono text-gray-500">Payer ID: {p.payer_id}</span>}
                        {p.member_id && <span className="font-mono text-gray-500">Member ID: {p.member_id}</span>}
                        {p.group_number && <span className="font-mono text-gray-500">Group: {p.group_number}</span>}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-gray-600 mt-0.5">
                        {p.plan_name && <span>{p.plan_name}</span>}
                        {p.effective_date && <span>Effective: {fDt(p.effective_date)}</span>}
                        {p.termination_date && <span>Term: {fDt(p.termination_date)}</span>}
                        {addr && <span className="text-gray-500">{addr}</span>}
                      </div>
                    </div>
                    {onAddOtherPayerToChart && (
                      <button
                        type="button"
                        data-testid={`add-other-payer-${idx}-btn`}
                        onClick={() => onAddOtherPayerToChart(p)}
                        className="shrink-0 text-[11px] px-2 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors"
                        title="Open the Add Insurance form prefilled with this payer's info"
                      >
                        + Add to chart
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MassHealth ACO sub-loops: PCP, ACO contact, Behavioral Health carve-out.
          MassHealth ACO Model B 271s attach these under EB*L / EB*MC / EB*W
          sub-loops (NM1*P3 / NM1*13 + N3 / N4 / PER*IC*TE + MSG). The
          parser captures them on `primary_care_provider`, `managed_care_coordinator`,
          and `carveouts[]`; this block surfaces them in the View 271 dialog
          so the biller can see who to call for authorization without
          digging into the raw EDI. */}
      {(data.primary_care_provider || data.managed_care_coordinator || (Array.isArray(data.carveouts) && data.carveouts.length > 0)) && (
        <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-3" data-testid="eb-subloops-section">
          <div className="text-sm font-medium text-violet-900">Care Coordination & Carve-outs</div>
          {data.primary_care_provider && (
            <div className="bg-white rounded border border-violet-100 p-3" data-testid="eb-pcp">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold">Primary Care Provider</div>
                  <div className="text-sm font-semibold text-gray-900">{data.primary_care_provider.name}</div>
                  {data.primary_care_provider.plan_label && (
                    <div className="text-[11px] text-gray-500">{data.primary_care_provider.plan_label}</div>
                  )}
                </div>
                {data.primary_care_provider.npi && (
                  <Badge variant="outline" className="font-mono text-[10px] shrink-0">NPI {data.primary_care_provider.npi}</Badge>
                )}
              </div>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-[12px] text-gray-700">
                {data.primary_care_provider.address_line1 && (
                  <div>{data.primary_care_provider.address_line1}{data.primary_care_provider.address_line2 ? `, ${data.primary_care_provider.address_line2}` : ''}</div>
                )}
                {(data.primary_care_provider.city || data.primary_care_provider.state) && (
                  <div>{[data.primary_care_provider.city, data.primary_care_provider.state, data.primary_care_provider.zip].filter(Boolean).join(', ')}</div>
                )}
                {data.primary_care_provider.phone && (
                  <div className="font-mono">📞 {data.primary_care_provider.phone}</div>
                )}
              </div>
              {Array.isArray(data.primary_care_provider.messages) && data.primary_care_provider.messages.length > 0 && (
                <ul className="mt-1.5 list-disc pl-5 text-[11px] text-gray-600 space-y-0.5">
                  {data.primary_care_provider.messages.map((m, i) => (<li key={i}>{m}</li>))}
                </ul>
              )}
            </div>
          )}
          {data.managed_care_coordinator && (
            <div className="bg-white rounded border border-violet-100 p-3" data-testid="eb-managed-care">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold">ACO / Managed Care Contact</div>
                  <div className="text-sm font-semibold text-gray-900">{data.managed_care_coordinator.name}</div>
                  {data.managed_care_coordinator.plan_label && (
                    <div className="text-[11px] text-gray-500">{data.managed_care_coordinator.plan_label}</div>
                  )}
                </div>
                {data.managed_care_coordinator.phone && (
                  <Badge variant="outline" className="font-mono text-[10px] shrink-0">📞 {data.managed_care_coordinator.phone}</Badge>
                )}
              </div>
              {(data.managed_care_coordinator.address_line1 || data.managed_care_coordinator.city) && (
                <div className="mt-1 text-[12px] text-gray-700">
                  {data.managed_care_coordinator.address_line1}
                  {data.managed_care_coordinator.city ? ` · ${[data.managed_care_coordinator.city, data.managed_care_coordinator.state, data.managed_care_coordinator.zip].filter(Boolean).join(', ')}` : ''}
                </div>
              )}
              {Array.isArray(data.managed_care_coordinator.messages) && data.managed_care_coordinator.messages.length > 0 && (
                <ul className="mt-1.5 list-disc pl-5 text-[11px] text-gray-600 space-y-0.5">
                  {data.managed_care_coordinator.messages.map((m, i) => (<li key={i}>{m}</li>))}
                </ul>
              )}
            </div>
          )}
          {Array.isArray(data.carveouts) && data.carveouts.map((co, idx) => (
            <div key={idx} className="bg-white rounded border border-amber-200 p-3" data-testid={`eb-carveout-${idx}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">
                    Carve-out{co.plan_label ? ` · ${co.plan_label}` : ''}
                  </div>
                  <div className="text-sm font-semibold text-gray-900">{co.name}</div>
                </div>
                {co.phone && (
                  <Badge className="bg-amber-500 text-white font-mono text-[10px] shrink-0">📞 {co.phone}</Badge>
                )}
              </div>
              {(co.address_line1 || co.city) && (
                <div className="mt-1 text-[12px] text-gray-700">
                  {co.address_line1}
                  {co.city ? ` · ${[co.city, co.state, co.zip].filter(Boolean).join(', ')}` : ''}
                </div>
              )}
              {Array.isArray(co.messages) && co.messages.length > 0 && (
                <ul className="mt-1.5 list-disc pl-5 text-[11px] text-gray-600 space-y-0.5">
                  {co.messages.map((m, i) => (<li key={i}>{m}</li>))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Financial Summary */}
      {(data.copay_amounts && Object.keys(data.copay_amounts).length > 0) || data.deductible_individual != null || data.coinsurance_percent != null || data.out_of_pocket_max != null || data.spend_down != null ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3">
          <div className="text-sm font-medium text-blue-800 mb-2">Financial Summary</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {data.copay_amounts && Object.entries(data.copay_amounts).map(([svc, amt]) => (
              <div key={svc} className="bg-white rounded p-2 border border-blue-100">
                <div className="text-[10px] text-gray-500 uppercase">Co-pay</div>
                <div className="text-sm font-semibold">{f$(amt)}</div>
                <div className="text-[10px] text-gray-400">{svc}</div>
              </div>
            ))}
            {data.deductible_individual != null && <div className="bg-white rounded p-2 border border-blue-100"><div className="text-[10px] text-gray-500 uppercase">Deductible (Individual)</div><div className="text-sm font-semibold">{f$(data.deductible_individual)}</div></div>}
            {data.deductible_family != null && <div className="bg-white rounded p-2 border border-blue-100"><div className="text-[10px] text-gray-500 uppercase">Deductible (Family)</div><div className="text-sm font-semibold">{f$(data.deductible_family)}</div></div>}
            {data.deductible_remaining != null && <div className="bg-white rounded p-2 border border-blue-100"><div className="text-[10px] text-gray-500 uppercase">Deductible Remaining</div><div className="text-sm font-semibold text-amber-700">{f$(data.deductible_remaining)}</div></div>}
            {data.coinsurance_percent != null && <div className="bg-white rounded p-2 border border-blue-100"><div className="text-[10px] text-gray-500 uppercase">Co-insurance</div><div className="text-sm font-semibold">{fPct(data.coinsurance_percent)}</div></div>}
            {data.out_of_pocket_max != null && <div className="bg-white rounded p-2 border border-blue-100"><div className="text-[10px] text-gray-500 uppercase">Out-of-Pocket Max</div><div className="text-sm font-semibold">{f$(data.out_of_pocket_max)}</div></div>}
            {data.out_of_pocket_remaining != null && <div className="bg-white rounded p-2 border border-blue-100"><div className="text-[10px] text-gray-500 uppercase">OOP Remaining</div><div className="text-sm font-semibold text-amber-700">{f$(data.out_of_pocket_remaining)}</div></div>}
            {data.spend_down != null && <div className="bg-white rounded p-2 border border-blue-100"><div className="text-[10px] text-gray-500 uppercase">Spend Down</div><div className="text-sm font-semibold text-purple-700">{f$(data.spend_down)}</div></div>}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 inline mr-1" />
          No financial details returned by payer. This payer may only report active/inactive coverage status.
        </div>
      )}

      {/* All Service Benefits */}
      {benefits.length > 0 && (
        <div className="rounded-lg border border-gray-200 p-3">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div className="text-sm font-medium text-gray-700">
              Benefit Segments
              <span className="text-gray-500 font-normal ml-1">
                ({filteredBenefits.length}
                {filteredBenefits.length !== benefits.length && ` of ${benefits.length}`}
                {' '}EB lines)
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs" data-testid="eb-filter-group-chart">
              <span className="text-gray-500 mr-1">Show:</span>
              {[
                { key: 'mh', label: 'MH focus', title: 'Mental health, psychiatric, substance abuse + main coverage' },
                { key: 'active', label: 'Active', title: 'Only Active / Inactive coverage segments' },
                { key: 'all', label: 'All', title: 'Every EB segment, no filter' },
              ].map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setEbFilter(opt.key)}
                  title={opt.title}
                  className={`px-2 py-0.5 rounded border transition-colors ${
                    ebFilter === opt.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`eb-filter-chart-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {filteredBenefits.length === 0 ? (
            <div className="text-xs text-gray-500 italic py-2">
              No EB segments match the current filter. Switch to <strong>All</strong> to see all {benefits.length} lines.
            </div>
          ) : (
          <div className="space-y-3">
            {Object.entries(grouped).map(([serviceName, items]) => (
              <div key={serviceName} className="border-l-2 border-gray-300 pl-3">
                <div className="text-xs font-semibold text-gray-600 mb-1">{serviceName}</div>
                <div className="space-y-1">
                  {items.map((b, idx) => {
                    const statusLabel = b.status_description || EB_STATUS_MAP[b.status] || b.status;
                    const timePeriod = TIME_MAP[b.time_period] || (b.time_period ? `Period ${b.time_period}` : '');
                    const coverageLevel = LEVEL_MAP[b.coverage_level] || b.coverage_level || '';
                    const inNet = b.in_network === 'Y' ? 'In-Network' : b.in_network === 'N' ? 'Out-of-Network' : '';
                    // Show ALL EB03 service types this segment covered
                    // (Medicare often bundles 12+ codes per EB, e.g.
                    // "30^42^45^A4^MH"). Each gets its readable label
                    // so billers don't have to know X12 by heart.
                    const allServiceTypes = (b.service_types && b.service_types.length > 1)
                      ? b.service_types.map((code, i) => ({
                          code,
                          name: (b.service_names && b.service_names[i]) || `Service Type ${code}`,
                        }))
                      : [];
                    return (
                      <div key={idx} className="bg-gray-50 rounded px-2 py-1.5 text-xs">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          <span className={`font-medium ${b.status === '1' ? 'text-green-700' : b.status === '6' || b.status === 'I' || b.status === 'E' ? 'text-red-700' : 'text-gray-700'}`}>{statusLabel}</span>
                          {coverageLevel && <span className="text-gray-500">{coverageLevel}</span>}
                          {timePeriod && <span className="text-gray-400">({timePeriod})</span>}
                          {b.amount != null && <span className="font-mono font-semibold">{f$(b.amount)}</span>}
                          {b.percent != null && <span className="font-mono font-semibold">{fPct(b.percent)}</span>}
                          {inNet && <span className={`text-[10px] px-1 border rounded ${b.in_network === 'Y' ? 'border-green-300 text-green-700' : 'border-red-300 text-red-700'}`}>{inNet}</span>}
                          {b.insurance_type && <span className="text-[10px] px-1 border rounded border-blue-300 text-blue-700">{b.insurance_type}</span>}
                          {b.plan_coverage_description && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 font-medium" title="Plan Coverage Description (EB05)" data-testid={`eb-plan-description-${idx}`}>{b.plan_coverage_description}</span>}
                        </div>
                        {allServiceTypes.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1" data-testid={`eb-service-types-${idx}`}>
                            {allServiceTypes.map((st) => (
                              <span
                                key={st.code}
                                className="inline-flex items-center gap-1 rounded bg-white px-1.5 py-0.5 border border-gray-200 text-[10px]"
                                title={`X12 EB03 code ${st.code}`}
                              >
                                <span className="font-mono text-gray-500">{st.code}</span>
                                <span className="text-gray-700">{st.name}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {benefits.length === 0 && (
        <div className="rounded-lg border border-gray-200 p-3 text-sm text-gray-500">
          No EB (benefit) segments found in the 271 response.
        </div>
      )}

      {/* Discrepancies */}
      {discrepancies.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="text-sm font-medium text-amber-800 mb-2">Discrepancies ({discrepancies.length})</div>
          <div className="space-y-1">
            {discrepancies.map((d, i) => (
              <div key={i} className="text-xs flex items-start gap-2">
                <span className={`text-[10px] px-1 border rounded shrink-0 ${d.severity === 'high' ? 'border-red-400 text-red-700' : 'border-amber-400 text-amber-700'}`}>{d.severity || 'info'}</span>
                <span><strong>{d.field}:</strong> Sent "{d.sent}" → Payer returned "{d.received}"{d.suggestion && <span className="text-gray-500 italic"> — {d.suggestion}</span>}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <div className="text-sm font-medium text-red-800 mb-1">Errors / Rejections</div>
          {errors.map((e, i) => <div key={i} className="text-xs text-red-700 mt-1">{e}</div>)}
        </div>
      )}

      {/* Audit */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-400 border-t pt-2">
        {check?.request_sent_at && <span>Sent: {new Date(check.request_sent_at).toLocaleString()}</span>}
        {check?.response_received_at && <span>Received: {new Date(check.response_received_at).toLocaleString()}</span>}
        {check?.latency_seconds != null && <span>Latency: {check.latency_seconds < 60 ? `${check.latency_seconds}s` : `${Math.round(check.latency_seconds / 60)}m`}</span>}
        {check?.control_number && <span>Control #: {check.control_number}</span>}
        {check?.payer_id_used && <span>Payer ID: {check.payer_id_used}</span>}
      </div>
    </div>
  );
}

// Route wrapper components

// Top-level auth loading gate - shows HIPAA loading screen until auth state is known
const AppContent = () => {
  const { loading } = useAuth();
  
  // Block all rendering until we know auth state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
        <p className="text-gray-600">Verifying HIPAA Session...</p>
      </div>
    );
  }
  
  // Platform-admin "Enter Tenant" impersonation: when active, the admin
  // domain must render the tenant EMR instead of the Platform Portal.
  const impersonationActive = (() => {
    try {
      const info = JSON.parse(sessionStorage.getItem('impersonation') || 'null');
      if (!info || !sessionStorage.getItem('token')) return false;
      if (info.until && info.until < Date.now()) {
        sessionStorage.removeItem('impersonation');
        sessionStorage.removeItem('token');
        return false;
      }
      return true;
    } catch { return false; }
  })();

  // Auth state is known, render routes
  return (
    <Router>
      <IntakeWizardProvider
        onSaved={(clientId) => {
          // Phase 3e.1 — broadcast a window event so route-local
          // lists (Waitlist, Calendar, Clients) can re-fetch.  This
          // replaces the per-layout `handleIntakeSaved` that used to
          // live in DashboardLayoutV2.  Both legacy and V2 listeners
          // get notified.
          try {
            window.dispatchEvent(new CustomEvent('v2:intake-saved', {
              detail: { clientId },
            }));
          } catch (_) { /* noop */ }
        }}
      >
      <ImpersonationBanner />
      {window.location.hostname.startsWith('admin.') && !impersonationActive ? (
        <Routes>
          <Route path="*" element={<Suspense fallback={<PageLoader label="Loading Platform Portal..." />}><PlatformPortal /></Suspense>} />
        </Routes>
      ) : (
      <Routes>
        <Route path="/print/note/:noteId" element={<PrintNoteView />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/portal" element={<Suspense fallback={<PageLoader label="Loading Portal..." />}><PublicPortal /></Suspense>} />
        <Route path="/client-portal" element={<Suspense fallback={<PageLoader label="Loading Client Portal..." />}><ClientPortal /></Suspense>} />
        <Route path="/platform" element={<Suspense fallback={<PageLoader label="Loading Platform Portal..." />}><PlatformPortal /></Suspense>} />
        <Route path="/p/:tenantSlug/portal" element={<Suspense fallback={<PageLoader label="Loading Portal..." />}><TenantPublicPortal /></Suspense>} />
        <Route path="/communication-preferences/:token" element={<CommunicationPreferences />} />
        <Route path="/waitlist/:action/:token" element={<Suspense fallback={<PageLoader label="Loading..." />}><WaitlistSelfService /></Suspense>} />
        <Route path="/accept-clients/:token" element={<Suspense fallback={<PageLoader label="Loading..." />}><AcceptClientsPage /></Suspense>} />
        <Route path="/activeclient" element={<ProtectedRoute><ActiveClientWrapper /></ProtectedRoute>} />
        <Route path="/client/:clientId" element={<ProtectedRoute><ClientChartWrapper /></ProtectedRoute>} />
        <Route path="/note/:noteId" element={<ProtectedRoute><NoteFullScreenWrapper /></ProtectedRoute>} />
        <Route path="/v2/note/:noteId" element={<ProtectedRoute><V2NoteHost /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        {/* V2 route-split shell — Feb 2026 strangler migration.
            See /app/frontend/src/layouts/DashboardLayoutV2.jsx */}
        <Route path="/v2" element={<ProtectedRoute><DashboardLayoutV2 /></ProtectedRoute>}>
          {/* Landing on /v2 with no child route redirects to the
              first sidebar entry the user can actually access.  Most
              practice users see Waitlist; admins see everything but
              Waitlist is still the safest default. */}
          {/* Mar 2026 — Default V2 landing changed from /v2/waitlist
              to /v2/clients per user feedback ("client management
              should be the default tab"). */}
          <Route index element={<Navigate to="/v2/clients" replace />} />
          <Route path="authorizations" element={<AuthorizationsRoute />} />
          <Route path="batches" element={<BatchRoute />} />
          <Route path="billing" element={<BillingRoute />} />
          <Route path="calendar" element={<CalendarRoute />} />
          <Route path="claims" element={<ClaimsWorkshopRoute />} />
          <Route path="era" element={<ERAManagementRoute />} />
          <Route path="client-bills" element={<ClientBillsRoute />} />
          <Route path="admin/locations" element={<LocationsAdminRoute />} />
          <Route path="admin/service-types" element={<ServiceTypesAdminRoute />} />
          <Route path="admin/pos-codes" element={<POSCodesAdminRoute />} />
          <Route path="admin/client-labels" element={<ClientLabelsAdminRoute />} />
          <Route path="admin/chart-reviews" element={<ChartReviewConfigRoute />} />
          <Route path="admin/cpt-codes" element={<CPTCodesAdminRoute />} />
          <Route path="admin/insurance" element={<ManageInsuranceAdminRoute />} />
          <Route path="admin/forms" element={<FormsAdminRoute />} />
          <Route path="eligibility" element={<EligibilityRoute />} />
          <Route path="groups" element={<GroupsRoute />} />
          <Route path="medical-records" element={<MedicalRecordsRoute />} />
          <Route path="medication-management" element={<MedicationManagementRoute />} />
          <Route path="payroll" element={<PayrollRoute />} />
          <Route path="reports" element={<ReportsRoute />} />
          <Route path="tasks" element={<TasksRoute />} />
          {/* Sep 2026 — Supervision & Treatment Plans are TABS on the task
              list now (user request); old standalone URLs redirect. */}
          <Route path="supervision" element={<Navigate to="/v2/tasks?tab=supervision" replace />} />
          <Route path="treatment-plans" element={<Navigate to="/v2/tasks?tab=treatment-plans" replace />} />
          <Route path="waitlist" element={<WaitlistRoute />} />
          {/* Mar 2026 — V2 migration: Active Clients (read-only,
              strangler-pattern — does not touch the legacy
              Client Management tab). */}
          <Route path="clients" element={<ActiveClientsRoute />} />
          {/* Mar 2026 — V2 migration: Staff/Provider directory. */}
          <Route path="directory" element={<StaffDirectoryRoute />} />
          {/* Mar 2026 — admin diagnostic for billing-ledger desync. */}
          {/* Feb 2026 — V2 Client Chart Dialog (strangler-fig Phase 1A).
              Both /v2/clients and /v2/clients/:clientId mount the same
              ActiveClientsRoute component.  When the :clientId param is
              present, the route renders the V2 Client Chart Dialog as
              an overlay on top of the list — exactly mirroring the
              legacy /client/:id → Dashboard → Dialog pattern, but
              sized larger (95vw × 95vh) per the user's request.  Each
              tab inside the dialog is a placeholder linking back to
              the legacy chart until migrated in Phase 2+. */}
          <Route path="clients/:clientId" element={<ActiveClientsRoute />} />
          <Route path="admin/claim-investigate" element={<AdminClaimInvestigateRoute />} />
          {/* Feb 2026 — V2 Admin · EDI Settings.  Per-clearinghouse
              identifier overrides (NEHEN + Availity) for X12 envelopes
              and TriZetto SFTP folder paths.  DB-first with env-var
              fallback.  Secrets stay in env / Secret Manager. */}
          <Route path="admin/card-on-file" element={<CardOnFileAdminRoute />} />
          <Route path="admin/statement-settings" element={<StatementSettingsRoute />} />
          <Route path="admin/platform-billing" element={<PlatformBillingRoute />} />
          <Route path="admin/billing-directory" element={<BillingDirectoryAdminRoute />} />
          <Route path="admin/document-deadline-rules" element={<DocumentDeadlineRulesRoute />} />
          <Route path="admin/appointment-reminders" element={<AppointmentRemindersRoute />} />
          <Route path="admin/conflict-rules" element={<ConflictRulesAdminRoute />} />
          <Route path="admin/document-reminders" element={<DocumentReminderSettingsRoute />} />
          <Route path="admin/routing-rules" element={<RoutingRulesAdminRoute />} />
          {/* Jun 2026 — V2 Admin hub (Phase 1).  Native landing page
              with the same five inner-tab structure as the legacy
              Admin tab; Setup cards deep-link back to legacy dialogs
              via URL params handled in App.js.  Subsequent phases
              will add dedicated V2 routes per dialog (CPT, Locations,
              etc.) and drop the deep-link bounce. */}
          <Route path="admin" element={<AdminRoute />} />
          {/* Jun 2026 — V2 Admin · Client Surveys.  Replaces the
              modal-from-Admin-tab jump (legacy setShowSurveyDashboard).
              Admin-gated; closes back to /v2/admin. */}
          <Route path="admin/surveys" element={<SurveyDashboardRoute />} />
          {/* Jun 2026 — V2 Admin · User Management (read-only v1).
              Mutations bounce to legacy via sessionStorage intent.
              Admin-gated. */}
          <Route path="admin/users" element={<UsersAdminRoute />} />
          {/* Mar 2026 — V2 Admin · Roles & Permissions Management.
              1-for-1 port of the legacy `showManageRoles` /
              `showAddEditRole` dialog pair.  Admin-gated. */}
          <Route path="admin/roles" element={<RolesAdminRoute />} />
          {/* Mar 2026 — V2 Admin · Note Signature Behavior. */}
          <Route path="admin/note-signatures" element={<NoteSignatureSettingsRoute />} />
          <Route path="admin/firm-calendar" element={<FirmCalendarAdminRoute />} />
          <Route path="admin/auto-submit-schedule" element={<AutoSubmitScheduleRoute />} />
          {/* Jun 2026 — V2 Admin Batch 2: standalone extractions of the
              legacy inline dialogs (Payment Reminders, Client Portal,
              Eligibility Settings, Waitlist Communication). */}
          <Route path="admin/payment-reminders" element={<PaymentRemindersRoute />} />
          <Route path="admin/client-portal" element={<ClientPortalSettingsRoute />} />
          <Route path="admin/eligibility-settings" element={<EligibilitySettingsRoute />} />
          <Route path="admin/waitlist-communication" element={<WaitlistCommunicationRoute />} />
          {/* Jun 2026 — consolidated card: Payment Reminders + Card on File
              + Statement Settings. Old routes stay live for deep links. */}
          <Route path="admin/client-billing" element={<ClientBillingSettingsRoute />} />
          {/* Jun 2026 — V2 Admin Batch 3: Intake Bundles (full CRUD) +
              Manage Licenses (placeholder — legacy was mock-only). */}
          <Route path="admin/intake-bundles" element={<IntakeBundlesRoute />} />
          <Route path="admin/licenses" element={<ManageLicensesRoute />} />
          <Route path="admin/system-settings" element={<SystemSettingsRoute />} />
          <Route path="admin/organization" element={<OrganizationSettingsRoute />} />
          <Route path="admin/internal-appointment-types" element={<InternalApptTypesAdminRoute />} />
          <Route path="admin/note-automation" element={<NoteAutomationRoute />} />
          <Route path="admin/client-demographics" element={<ClientDemographicsRoute />} />
          <Route path="admin/branding" element={<BrandingRouteV2 />} />
          <Route path="admin/access-security" element={<AccessSecurityRoute />} />
          <Route path="admin/security-audit" element={<SecurityAuditRoute />} />
          <Route path="admin/messaging-tools" element={<MessagingToolsRoute />} />
          <Route path="admin/intake-form" element={<IntakeFormConfigRoute />} />
          {/* Jun 2026 — V2 Profile (own + teammates).  Replaces the
              legacy ProfileModal jump from the user dropdown / staff
              directory.  Hard-refresh-safe; closes back to /v2. */}
          <Route path="profile" element={<ProfileRoute />} />
          <Route path="profile/:userId" element={<ProfileRoute />} />
        </Route>
        <Route path="/batch-history" element={<ProtectedRoute><Suspense fallback={<PageLoader label="Loading Batch History..." />}><BatchHistoryPage /></Suspense></ProtectedRoute>} />
        <Route path="/claims-workshop" element={<ProtectedRoute><Suspense fallback={<PageLoader label="Loading Claims Workshop..." />}><ClaimsWorkshop /></Suspense></ProtectedRoute>} />
        <Route path="/atlas" element={<ProtectedRoute><Suspense fallback={<PageLoader label="Loading Atlas..." />}><CareVaultAtlas /></Suspense></ProtectedRoute>} />
        <Route path="/admin/legacy-import" element={<ProtectedRoute><Suspense fallback={<PageLoader label="Loading Migration Tool..." />}><LegacyImportAdmin /></Suspense></ProtectedRoute>} />
        <Route path="/help" element={<ProtectedRoute><Suspense fallback={<PageLoader label="Loading Admin Help Center..." />}><HelpModule /></Suspense></ProtectedRoute>} />
        <Route path="/break" element={<BreakPage />} />
        <Route path="/sandbox/form-fields" element={<ProtectedRoute><FormFieldRendererSandbox /></ProtectedRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      )}
      
      <Toaster />
      </IntakeWizardProvider>
    </Router>
  );
};

const LoginRoute = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [betaEnabled, setBetaEnabled] = useState(null); // null = loading, true/false = loaded
  const [betaAccess, setBetaAccess] = useState(() => localStorage.getItem('beta_access') === 'true');

  // Get redirect path from query params
  const searchParams = new URLSearchParams(location.search);
  const redirectPath = searchParams.get('redirect');

  // Check beta status on mount
  React.useEffect(() => {
    const checkBetaStatus = async () => {
      try {
        const response = await axios.get(`${API}/beta/status`);
        setBetaEnabled(response.data.beta_enabled);
      } catch (e) {
        // If can't check beta status, assume it's disabled
        setBetaEnabled(false);
      }
    };
    checkBetaStatus();
  }, []);

  React.useEffect(() => {
    if (user) {
      // Check if this was an intentional logout - if so, ignore redirect and go to dashboard
      const wasIntentionalLogout = sessionStorage.getItem('intentionalLogout') === 'true';
      sessionStorage.removeItem('intentionalLogout'); // Clear the flag
      
      // Only use redirect path if it wasn't an intentional logout
      const targetPath = (!wasIntentionalLogout && redirectPath) ? decodeURIComponent(redirectPath) : '/dashboard';
      navigate(targetPath, { replace: true });
    }
  }, [user, navigate, redirectPath]);

  // Still loading beta status
  if (betaEnabled === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  // If user is logged in, redirect to intended path or dashboard
  if (user) {
    // Check if this was an intentional logout - if so, ignore redirect
    const wasIntentionalLogout = sessionStorage.getItem('intentionalLogout') === 'true';
    const targetPath = (!wasIntentionalLogout && redirectPath) ? decodeURIComponent(redirectPath) : '/dashboard';
    // Don't clear the flag here - the useEffect will handle it
    return <Navigate to={targetPath} replace />;
  }

  // If beta is enabled and user hasn't passed the gate yet, show beta gate
  if (betaEnabled && !betaAccess) {
    return <BetaGate onValidCode={() => setBetaAccess(true)} />;
  }

  // Show normal login
  return <Login />;
};

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  
  // If not logged in, redirect to login with the intended path as a query param
  if (!user) {
    const intendedPath = location.pathname + location.search;
    // Encode the intended path and pass as query parameter
    const encodedPath = encodeURIComponent(intendedPath);
    return <Navigate to={`/login?redirect=${encodedPath}`} replace />;
  }
  
  return children;
};

// Wrapper component for fullscreen client chart (uses sessionStorage instead of URL param)
const ActiveClientWrapper = () => {
  const clientId = sessionStorage.getItem('activeClientId');
  
  if (!clientId) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Dashboard fullscreenClientId={clientId} />;
};

// Legacy wrapper - keep for backwards compatibility but redirect
const ClientChartWrapper = () => {
  const { clientId } = useParams();
  // Pass clientId to Dashboard to load on mount
  return <Dashboard initialClientId={clientId} />;
};

// Wrapper component for fullscreen note view
const NoteFullScreenWrapper = () => {
  const { noteId } = useParams();
  return <Dashboard fullscreenNoteId={noteId} />;
};


// Main App Component
// Communication Preferences Page (public, accessed via token in unsubscribe link)
function CommunicationPreferences() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await axios.get(`${API}/public/communication-preferences/${token}`);
        setPreferences(response.data);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Unable to load preferences. The link may have expired.'));
      } finally {
        setLoading(false);
      }
    };
    fetchPreferences();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API}/public/communication-preferences/${token}`, {
        email_consent: preferences.email_consent,
        sms_consent: preferences.sms_consent,
        communication_opt_out: preferences.communication_opt_out
      });
      setSuccess('Your preferences have been saved.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save preferences.'));
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    if (!window.confirm('Are you sure you want to unsubscribe from all communications?')) return;
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API}/public/unsubscribe/${token}`);
      setPreferences({ ...preferences, communication_opt_out: true, email_consent: false, sms_consent: false });
      setSuccess('You have been unsubscribed from all communications.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to unsubscribe.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto text-teal-600" />
          <p className="mt-2 text-gray-600">Loading preferences...</p>
        </div>
      </div>
    );
  }

  if (error && !preferences) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md mx-4">
          <CardHeader>
            <CardTitle className="text-red-600">Unable to Load Preferences</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">{error}</p>
            <p className="mt-4 text-sm text-gray-500">Please contact our office if you need to update your communication preferences.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <Card className="w-full max-w-lg mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{preferences?.company_name || 'Communication Preferences'}</CardTitle>
          <p className="text-gray-600 mt-2">
            Hi {preferences?.client_name}, manage how we contact you.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
          )}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">{success}</div>
          )}

          {preferences?.communication_opt_out ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
              <p className="font-semibold text-yellow-800">You are currently unsubscribed from all communications.</p>
              <p className="text-sm text-yellow-700 mt-1">To receive appointment reminders and important updates, please update your preferences below.</p>
            </div>
          ) : null}

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">📧 Email Communications</p>
                <p className="text-sm text-gray-500">Appointment reminders, confirmations, and important updates</p>
              </div>
              <Switch
                checked={preferences?.email_consent || false}
                onCheckedChange={(checked) => setPreferences({ ...preferences, email_consent: checked, communication_opt_out: false })}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">📱 SMS/Text Messages</p>
                <p className="text-sm text-gray-500">Quick reminders sent to your phone</p>
              </div>
              <Switch
                checked={preferences?.sms_consent || false}
                onCheckedChange={(checked) => setPreferences({ ...preferences, sms_consent: checked, communication_opt_out: false })}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button onClick={handleSave} disabled={saving} className="flex-1">
              {saving ? <Loader className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Preferences
            </Button>
          </div>

          <div className="border-t pt-4 mt-4">
            <button
              onClick={handleUnsubscribeAll}
              disabled={saving}
              className="w-full text-center text-sm text-red-600 hover:text-red-800 underline"
            >
              Unsubscribe from all communications
            </button>
          </div>

          <p className="text-xs text-gray-400 text-center">
            Note: Even if you unsubscribe, we may still contact you for important account-related matters.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}


function App() {
  // Suppress ResizeObserver errors globally
  React.useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return;
      }
      originalError.apply(console, args);
    };
    
    return () => {
      console.error = originalError;
    };
  }, []);

  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <StaticDataProvider>
            <UIProvider>
              <NotificationsProvider>
                <ClientsProvider>
                  <AppointmentsProvider>
                    <NotesProvider>
                      <ImpersonationProvider>
                        <AppContent />
                        <DateCalculatorPopover />
                      </ImpersonationProvider>
                    </NotesProvider>
                  </AppointmentsProvider>
                </ClientsProvider>
              </NotificationsProvider>
            </UIProvider>
          </StaticDataProvider>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

// Route Components

// Client Medical Records Requests Component
const ClientMedicalRecordsRequests = ({ clientId, API }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [clientId]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API}/medical-records/requests`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const clientRequests = response.data.requests.filter(r => r.client_id === clientId);
      setRequests(clientRequests);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted: { color: 'bg-yellow-100 text-yellow-800', label: 'Submitted' },
      under_review: { color: 'bg-blue-100 text-blue-800', label: 'Under Review' },
      pending_provider_review: { color: 'bg-purple-100 text-purple-800', label: 'Provider Review' },
      provider_reviewed: { color: 'bg-indigo-100 text-indigo-800', label: 'Provider Reviewed' },
      pending_client_info: { color: 'bg-orange-100 text-orange-800', label: 'Awaiting Client Info' },
      approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
      payment_required: { color: 'bg-blue-100 text-blue-800', label: 'Payment Required' },
      paid: { color: 'bg-teal-100 text-teal-800', label: 'Paid' },
      records_ready: { color: 'bg-green-100 text-green-800', label: 'Ready' },
      completed: { color: 'bg-gray-100 text-gray-800', label: 'Completed' },
      denied: { color: 'bg-red-100 text-red-800', label: 'Denied' }
    };
    
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
        <p className="text-gray-600 text-sm">Loading requests...</p>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
        <p>No medical records requests found for this client</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {(() => {
        const now = new Date();
        const ninetyDaysAgo = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
        
        // Separate active and historical requests
        const activeRequests = requests.filter(request => {
          const isActive = !['completed', 'denied'].includes(request.status);
          const createdAt = new Date(request.created_at);
          const isRecent = createdAt > ninetyDaysAgo;
          return isActive || (['completed', 'denied'].includes(request.status) && isRecent);
        });
        
        const historicalRequests = requests.filter(request => {
          const isCompleted = ['completed', 'denied'].includes(request.status);
          const createdAt = new Date(request.created_at);
          const isOld = createdAt <= ninetyDaysAgo;
          return isCompleted && !isOld; // Within 90 days but completed/denied
        });
        
        const renderRequest = (request) => {
          // Format date safely
          let submittedDate = 'N/A';
          try {
            if (request.created_at) {
              submittedDate = new Date(request.created_at).toLocaleString();
            }
          } catch (e) {
            console.error('Invalid date:', request.created_at);
          }

          return (
            <div key={request.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">
                        Request #{request.id.substring(0, 8).toUpperCase()}
                      </span>
                      {getStatusBadge(request.status)}
                    </div>
                    <p className="text-sm text-gray-600">
                      Submitted: {submittedDate}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Record Types:</span>
                  <p className="text-gray-600">{request.record_types.join(', ').replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Delivery Method:</span>
                  <p className="text-gray-600 capitalize">
                    {request.delivery_method?.replace(/_/g, ' ') || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Date Range:</span>
                  <p className="text-gray-600">
                    {request.date_range_all ? 'All Dates' : `${request.date_range_start || ''} to ${request.date_range_end || ''}`}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Requester:</span>
                  <p className="text-gray-600 capitalize">{request.requester_type}</p>
                </div>
              </div>

              {request.filter_by_provider && (
                <div className="mt-2 text-sm">
                  <span className="font-medium text-gray-700">Provider Filter: </span>
                  <span className="text-gray-600">{request.selected_provider_id || 'Specified'}</span>
                </div>
              )}

              {request.filter_by_service_type && request.selected_service_types?.length > 0 && (
                <div className="mt-2">
                  <span className="font-medium text-gray-700 text-sm">Service Filters: </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {request.selected_service_types.map((service, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {request.denial_reason && (
                <div className="mt-3 bg-red-50 border-l-4 border-red-500 p-2">
                  <p className="text-sm text-red-700">
                    <strong>Denied:</strong> {request.denial_reason}
                  </p>
                </div>
              )}

              {request.info_requested_message && (
                <div className="mt-3 bg-yellow-50 border-l-4 border-yellow-500 p-2">
                  <p className="text-sm text-yellow-800">
                    <strong>Info Requested:</strong> {request.info_requested_message}
                  </p>
                </div>
              )}
            </div>
          );
        };
        
        return (
          <>
            {/* Active Requests */}
            {activeRequests.length > 0 && (
              <div className="space-y-3">
                {activeRequests.map(renderRequest)}
              </div>
            )}
            
            {/* Historical Requests (Past 90 Days) */}
            {historicalRequests.length > 0 && (
              <div className="mt-6 border-t pt-4">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 w-full"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                  Request History (Past 90 Days)
                </button>
                
                {showHistory && (
                  <div className="mt-4 space-y-2">
                    {historicalRequests.map(request => (
                      <div key={request.id} className="p-3 border rounded bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">{request.request_type}</p>
                            <p className="text-xs text-gray-500">
                              Requested: {new Date(request.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge className={
                            request.status === 'completed' ? 'bg-green-100 text-green-700' : 
                            'bg-red-100 text-red-700'
                          }>
                            {request.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        );
      })()}
      
      {/* Close Note Warning Modal is now rendered inside DialogContent above */}
      {/* Close Editing Note Warning Modal is now rendered inside DialogContent above */}
      
      {/* Template Change Warning Dialog - Using Portal to ensure visibility above other dialogs */}
      <AlertDialog open={showTemplateChangeWarning} onOpenChange={setShowTemplateChangeWarning}>
        <AlertDialogContent className="z-[200]">
          <AlertDialogHeader>
            <AlertDialogTitle>Change Form Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Your current note has been auto-saved as a draft. Changing the template will start a new note.
              <br /><br />
              Your existing work will be preserved and can be found in your Drafts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleKeepCurrentNote}>
              Keep Current Note
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleStartNewNoteWithTemplate} className="bg-blue-600 hover:bg-blue-700">
              Start New Note
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default App;

